import {
  Activity,
  ArrowLeft,
  ArrowRight,
  AlertTriangle,
  BarChart3,
  Bolt,
  Building2,
  CircleDollarSign,
  ClipboardList,
  Clock3,
  FileText,
  FolderKanban,
  IdCard,
  LogOut,
  Search,
  ShieldCheck,
  Sparkles,
  Target,
  Users,
} from "lucide-react";
import { forwardRef, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type Dispatch, type FormEvent, type KeyboardEvent as ReactKeyboardEvent, type ReactNode, type RefObject, type SetStateAction } from "react";
import { flushSync } from "react-dom";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { assessPermanentBusinessCenterUserDeletion, getSession, inviteBusinessCenterUser, login, logout, permanentlyDeleteBusinessCenterUser, type BusinessCenterPermanentUserDeletionResult } from "../../lib/api";
import { loadBusinessCenterOperationalState, saveBusinessCenterOperationalState } from "../../lib/businessCenterOperationalStateApi";
import { defaultLandingWorkspaceOptions, getDefaultLandingWorkspaceLabel, normalizeDefaultLandingWorkspace } from "../../lib/defaultLandingWorkspace";
import { formatDate } from "../../lib/format";
import {
  buildARRecoveryDashboardSnapshot,
  buildARRecoveryCustomerAccountsSnapshot,
  buildARRecoveryCollectionLetterCenterSnapshot,
  buildARRecoveryStatementCenterSnapshot,
  buildARRecoveryCommercializationSnapshot,
  buildARRecoveryPromiseCenterSnapshot,
  buildARRecoveryTicklerDashboardSnapshot,
  buildARRecoveryNavigation,
  formatARRecoveryCurrency,
  formatARRecoveryPercent,
  loadARRecoveryWorkspace,
  type ARRecoveryAccountTabKey,
  type ARRecoveryCommercializationTabKey,
  type ARRecoveryLetterTabKey,
  type ARRecoveryPromiseTabKey,
  type ARRecoverySectionKey,
} from "../../lib/arRecoveryData";
import { upsertBusinessCenterCompanyUser } from "./businessCenterCompanyUsersData";
import {
  loadStoredARWorkflowEvents,
  PortfolioActivationEngine,
  PortfolioAssignmentEngine,
  PortfolioImportEngine,
  PortfolioMappingEngine,
  PortfolioReviewEngine,
  PortfolioValidationEngine,
  projectCallEventsToCallLogs,
  projectDocumentEventsToDocumentWorkflows,
  projectEscalationEventsToEscalationWorkflows,
  projectNoteEventsToNoteLogs,
  projectPaymentEventsToPaymentWorkflows,
  projectPromiseEventsToPromiseWorkflows,
  projectTicklerEventsToTicklerWorkflows,
  projectWorkflowEventsToEnterpriseClientExchange,
  projectWorkflowEventsToReportingAnalytics,
  projectWorkflowEventsToNotificationRecords,
  publishCallWorkflowAction,
  publishDocumentWorkflowAction,
  publishEscalationWorkflowAction,
  publishNoteWorkflowAction,
  publishPaymentWorkflowAction,
  publishPromiseWorkflowAction,
  publishManagerWorkflowAction,
  publishTicklerWorkflowAction,
  runEnterprisePortfolioIntakeWalkthrough,
  type ARWorkflowManagerEventType,
  type ARWorkflowDocumentAction,
  type ARWorkflowEscalationAction,
  type ARWorkflowEscalationProjection,
  type EnterpriseClientExchangeFoundationProjection,
  type ARWorkflowNotificationProjection,
  type PortfolioActivationEngineResult,
  type PortfolioAssignmentEngineResult,
  type PortfolioImportEngineResult,
  type PortfolioMappingEngineResult,
  type PortfolioPipelineWalkthroughResult,
  type PortfolioReviewEngineResult,
  type PortfolioValidationEngineResult,
  type ARWorkflowPaymentAction,
  type ARWorkflowPaymentProjection,
  type ARWorkflowPromiseAction,
  type ARWorkflowReportingProjection,
  type ARWorkflowTicklerAction,
} from "../../lib/arRecoveryWorkflowEvents";
import {
  loadBusinessCenterCompanyUsers,
  refreshBusinessCenterCompanyUsers,
} from "./businessCenterCompanyUsersData";
import type { BusinessCenterUserRecord, UserRole } from "../../lib/types";
import { EditActionButton } from "./AREnterpriseControls";
import { ARRecoveryClientManagementSection } from "./ARRecoveryClientManagementSection";
import { ARRecoveryCustomerAccountManagementSection } from "./ARRecoveryCustomerAccountManagementSection";

interface ARRecoveryWorkspacePageProps {
  section: ARRecoverySectionKey;
}

type ARRecoveryActor = {
  email: string;
  name: string;
  role: string;
};

type ManagerCommandReturnContext = {
  selectedOperationalCaseId: string;
  workspaceTab: ManagerWorkspaceTabKey;
  scrollY: number;
};

type ManagerCommandAccountAction =
  | "view-account"
  | "assign-collector"
  | "review-escalation"
  | "add-manager-note"
  | "create-follow-up"
  | "log-call"
  | "assign-task"
  | "promise"
  | "generate-letter"
  | "generate-statement"
  | "request-document"
  | "escalate";

type AccountRecoveryLaunchState = {
  accountRecoveryLaunch?: {
    origin?: "promise-center" | "manager-command";
    accountId?: string;
    selectedTab?: ARRecoveryAccountTabKey;
    managerAction?: ManagerCommandAccountAction;
    launchToken?: string;
    returnRoute?: string;
    managerCommandReturnContext?: ManagerCommandReturnContext;
    promiseCenterReturnContext?: PromiseCenterReturnContext;
  };
  managerCommandReturnContext?: ManagerCommandReturnContext;
  promiseCenterReturnContext?: PromiseCenterReturnContext;
};

type AccountRecoveryResolvedOrigin =
  | {
      origin: "promise-center";
      label: "Return to Promise Center";
      returnRoute: string;
      promiseCenterReturnContext: PromiseCenterReturnContext;
      managerCommandReturnContext?: never;
    }
  | {
      origin: "manager-command";
      label: "Return to Manager Command";
      returnRoute: string;
      managerCommandReturnContext: ManagerCommandReturnContext;
      promiseCenterReturnContext?: never;
    }
  | {
      origin: "direct";
      label: "";
      returnRoute: "";
      managerCommandReturnContext?: never;
      promiseCenterReturnContext?: never;
    };

type PromiseCenterReturnContext = {
  origin: "promise-center";
  promiseId: string;
  accountId?: string;
  customerId?: string;
  searchQuery?: string;
  activeTab?: ARRecoveryPromiseTabKey;
  returnRoute: string;
  workflowType?: string;
};

const AR_RECOVERY_WRITE_ROLES = new Set(["platform_admin", "kreatethedate_admin", "admin", "manager", "supervisor", "team_member", "collector"]);
const AR_RECOVERY_OVERRIDE_ROLES = new Set(["platform_admin", "kreatethedate_admin", "admin", "manager", "supervisor", "team_member", "collector"]);
const AR_RECOVERY_ENTERPRISE_WORKSPACE_GRID_CLASS = "grid min-h-0 flex-1 grid-cols-1 overflow-hidden lg:grid-cols-[minmax(0,0.65fr)_minmax(18rem,0.35fr)]";
const AR_RECOVERY_ENTERPRISE_PANEL_GRID_CLASS = "grid min-w-0 gap-4 scroll-mt-24 outline-none xl:grid-cols-[minmax(0,0.65fr)_minmax(18rem,0.35fr)] xl:items-start";

const PROMISE_CENTER_TAB_KEYS = new Set<ARRecoveryPromiseTabKey>(["overview", "account", "notes", "activity", "payments", "letters", "statements", "ticklers", "documents", "history"]);

function normalizePromiseCenterTab(value: string | null): ARRecoveryPromiseTabKey | undefined {
  return value && PROMISE_CENTER_TAB_KEYS.has(value as ARRecoveryPromiseTabKey) ? value as ARRecoveryPromiseTabKey : undefined;
}

function getPromiseCenterReturnContext(search: string): PromiseCenterReturnContext | null {
  const params = new URLSearchParams(search);
  const source = params.get("source") || params.get("origin");
  const promiseId = params.get("promiseId") || "";
  if (source !== "promise-to-pay" || !promiseId) return null;

  const activeTab = normalizePromiseCenterTab(params.get("promiseTab") || params.get("activeTab") || params.get("tab"));
  const searchQuery = params.get("promiseSearch") || params.get("search") || "";

  return {
    origin: "promise-center",
    promiseId,
    accountId: params.get("accountId") || undefined,
    customerId: params.get("customerId") || undefined,
    searchQuery,
    activeTab,
    workflowType: params.get("workflow") || params.get("action") || undefined,
    returnRoute: buildPromiseCenterReturnRoute({
      promiseId,
      accountId: params.get("accountId") || undefined,
      customerId: params.get("customerId") || undefined,
      searchQuery,
      activeTab,
      workflowType: params.get("workflow") || params.get("action") || undefined,
    }),
  };
}

function buildPromiseCenterReturnRoute(context: {
  promiseId: string;
  accountId?: string;
  customerId?: string;
  searchQuery?: string;
  activeTab?: ARRecoveryPromiseTabKey;
  workflowType?: string;
}) {
  const params = new URLSearchParams({
    source: "promise-to-pay",
    promiseId: context.promiseId,
  });
  if (context.accountId) params.set("accountId", context.accountId);
  if (context.customerId) params.set("customerId", context.customerId);
  if (context.searchQuery) params.set("promiseSearch", context.searchQuery);
  if (context.activeTab) params.set("promiseTab", context.activeTab);
  if (context.workflowType) params.set("workflow", context.workflowType);
  params.set("returnSync", String(Date.now()));
  return `/admin/ar-recovery/promise-to-pay?${params.toString()}`;
}

function resolveAccountRecoveryOrigin(launchState: AccountRecoveryLaunchState | null): AccountRecoveryResolvedOrigin {
  const launch = launchState?.accountRecoveryLaunch;

  if (launch?.origin === "promise-center" && launch.promiseCenterReturnContext?.returnRoute) {
    return {
      origin: "promise-center",
      label: "Return to Promise Center",
      returnRoute: launch.promiseCenterReturnContext.returnRoute,
      promiseCenterReturnContext: launch.promiseCenterReturnContext,
    };
  }

  if (launch?.origin === "manager-command" && launch.managerCommandReturnContext) {
    return {
      origin: "manager-command",
      label: "Return to Manager Command",
      returnRoute: "/admin/ar-recovery/manager-command-center",
      managerCommandReturnContext: launch.managerCommandReturnContext,
    };
  }

  if (launchState?.managerCommandReturnContext) {
    return {
      origin: "manager-command",
      label: "Return to Manager Command",
      returnRoute: "/admin/ar-recovery/manager-command-center",
      managerCommandReturnContext: launchState.managerCommandReturnContext,
    };
  }

  return {
    origin: "direct",
    label: "",
    returnRoute: "",
  };
}

type DocumentWorkspaceTabKey =
  | "overview"
  | "generate"
  | "letter-templates"
  | "statement-templates"
  | "document-history"
  | "delivery-history"
  | "branding"
  | "merge-fields"
  | "permissions";

type DocumentWorkspaceTabDefinition = {
  key: DocumentWorkspaceTabKey;
  label: string;
  icon: ReactNode;
  note: string;
};

const DOCUMENT_WORKSPACE_TAB_STORAGE_KEY = "ar-recovery-document-workspace-tab";

const AR_RECOVERY_THEME_STYLE = `
  .ar-recovery-shell {
    color: #0A2540;
    background:
      linear-gradient(180deg, #02072C 0%, #071A3D 16rem, #F4FBFF 16rem, #F4FBFF 100%);
    font-family: Inter, "IBM Plex Sans", Aptos, "Segoe UI", Arial, sans-serif;
    font-size: 14px;
    line-height: 1.5;
    scrollbar-color: #FF6A2B #BFEAFF;
  }

  .ar-recovery-shell * {
    letter-spacing: normal;
  }

  .ar-recovery-shell .font-display {
    font-family: Inter, "IBM Plex Sans", Aptos, "Segoe UI", Arial, sans-serif !important;
    letter-spacing: -0.01em !important;
  }

  .ar-recovery-shell h1,
  .ar-recovery-shell [class*="text-5xl"],
  .ar-recovery-shell [class*="text-4xl"] {
    font-size: clamp(1.5rem, 2vw, 1.75rem) !important;
    line-height: 1.2 !important;
    font-weight: 700 !important;
    letter-spacing: -0.015em !important;
  }

  .ar-recovery-shell h2,
  .ar-recovery-shell [class*="text-3xl"] {
    font-size: clamp(1.375rem, 1.7vw, 1.5rem) !important;
    line-height: 1.25 !important;
    font-weight: 700 !important;
    letter-spacing: -0.01em !important;
  }

  .ar-recovery-shell h3,
  .ar-recovery-shell [class*="text-2xl"] {
    font-size: clamp(1.125rem, 1.45vw, 1.25rem) !important;
    line-height: 1.3 !important;
    font-weight: 650 !important;
    letter-spacing: -0.005em !important;
  }

  .ar-recovery-shell h4,
  .ar-recovery-shell [class*="text-xl"] {
    font-size: 1rem !important;
    line-height: 1.35 !important;
    font-weight: 650 !important;
  }

  .ar-recovery-shell [class*="text-lg"] {
    font-size: 1rem !important;
    line-height: 1.45 !important;
  }

  .ar-recovery-shell p,
  .ar-recovery-shell li,
  .ar-recovery-shell td,
  .ar-recovery-shell input,
  .ar-recovery-shell select,
  .ar-recovery-shell textarea {
    font-size: 0.875rem;
    line-height: 1.55;
  }

  .ar-recovery-shell th,
  .ar-recovery-shell label,
  .ar-recovery-shell [class*="text-xs"],
  .ar-recovery-shell [class*="text-[11px]"],
  .ar-recovery-shell [class*="text-[10px]"] {
    font-size: 0.75rem !important;
    line-height: 1.35 !important;
  }

  .ar-recovery-shell [class*="font-black"],
  .ar-recovery-shell [class*="font-extrabold"] {
    font-weight: 700 !important;
  }

  .ar-recovery-shell [class*="font-bold"] {
    font-weight: 650 !important;
  }

  .ar-recovery-shell button,
  .ar-recovery-shell a,
  .ar-recovery-shell [role="tab"] {
    font-weight: 650;
  }

  .ar-recovery-shell [class*="uppercase"] {
    letter-spacing: 0.06em !important;
  }

  .ar-recovery-shell main label span,
  .ar-recovery-shell main form [class*="uppercase"],
  .ar-recovery-shell main button[class*="uppercase"],
  .ar-recovery-shell main [role="tab"][class*="uppercase"] {
    text-transform: none !important;
    letter-spacing: 0.01em !important;
  }

  .ar-recovery-shell main label span {
    color: #334155 !important;
    font-size: 0.78rem !important;
    font-weight: 500 !important;
    line-height: 1.35 !important;
  }

  .ar-recovery-shell main input,
  .ar-recovery-shell main select,
  .ar-recovery-shell main textarea {
    font-weight: 500 !important;
  }

  .ar-recovery-shell main label {
    min-width: 0;
    max-width: 100%;
  }

  .ar-recovery-shell main input:not([type="checkbox"]):not([type="radio"]):not([type="range"]),
  .ar-recovery-shell main textarea,
  .ar-recovery-shell main select {
    display: block;
    box-sizing: border-box;
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  .ar-recovery-shell main input:not(:placeholder-shown),
  .ar-recovery-shell main textarea:not(:placeholder-shown),
  .ar-recovery-shell main select {
    color: #0F172A !important;
    font-weight: 600 !important;
  }

  .ar-recovery-shell main dt,
  .ar-recovery-shell main [class*="text-[10px]"][class*="tracking"],
  .ar-recovery-shell main [class*="text-[11px]"][class*="tracking"] {
    font-weight: 500 !important;
  }

  .ar-recovery-shell ::-webkit-scrollbar {
    height: 12px;
    width: 12px;
  }

  .ar-recovery-shell ::-webkit-scrollbar-track {
    background: #BFEAFF;
    border-radius: 999px;
  }

  .ar-recovery-shell ::-webkit-scrollbar-thumb {
    background: #FF6A2B;
    border: 2px solid #BFEAFF;
    border-radius: 999px;
  }

  .ar-recovery-shell :is(.bg-slate-50, .bg-slate-100, .bg-slate-200, .bg-slate-300, .bg-slate-400, .bg-slate-500, .bg-slate-600, .bg-slate-700, .bg-slate-800, .bg-slate-900) {
    background-color: #F4FBFF !important;
  }

  .ar-recovery-shell :is(.border-slate-100, .border-slate-200, .border-slate-300, .border-slate-400, .border-slate-500, .border-slate-600, .border-slate-700, .border-slate-800, .border-slate-900) {
    border-color: #BFEAFF !important;
  }

  .ar-recovery-shell :is(.text-slate-900, .text-slate-800, .text-slate-700, .text-slate-600, .text-slate-500, .text-slate-400, .text-slate-300, .text-slate-200, .text-slate-100, .text-slate-50) {
    color: #0A2540 !important;
  }

  .ar-recovery-shell [class*="text-[#F6F1E8]"],
  .ar-recovery-shell [class*="text-[#E8EEF4]"],
  .ar-recovery-shell [class*="text-[#CBD5E1]"],
  .ar-recovery-shell [class*="text-[#94A3B8]"],
  .ar-recovery-shell [class*="text-[#64748B]"],
  .ar-recovery-shell [class*="text-[#9FB0C0]"],
  .ar-recovery-shell [class*="text-[#8FA2B5]"],
  .ar-recovery-shell [class*="text-[#73879B]"],
  .ar-recovery-shell [class*="text-white"] {
    color: #0A2540 !important;
  }

  .ar-recovery-shell [class*="text-[#FF6A2B]"],
  .ar-recovery-shell [class*="text-[#FF6A2B]"] {
    color: #0A2540 !important;
  }

  .ar-recovery-shell [class*="text-[#22E0D0]"],
  .ar-recovery-shell .text-teal-700,
  .ar-recovery-shell .text-teal-600,
  .ar-recovery-shell .text-teal-500,
  .ar-recovery-shell .text-teal-400 {
    color: #14B8A6 !important;
  }

  .ar-recovery-shell [class*="bg-[rgba(7,18,31"] {
    background-color: #FFFFFF !important;
  }

  .ar-recovery-shell [class*="bg-[#07121F]"],
  .ar-recovery-shell [class*="bg-[#07121f]"] {
    background-color: #FFFFFF !important;
  }

  .ar-recovery-shell [class*="bg-[#0A1726]"],
  .ar-recovery-shell [class*="bg-[#0a1726]"] {
    background-color: #FFFFFF !important;
  }

  .ar-recovery-shell [class*="bg-[#12182A]"],
  .ar-recovery-shell [class*="bg-[#12182a]"] {
    background-color: #F4FBFF !important;
  }

  .ar-recovery-shell [class*="bg-[#050505]"] {
    background-color: #FFFFFF !important;
  }

  .ar-recovery-shell [class*="bg-white/["],
  .ar-recovery-shell [class*="bg-white/[."] {
    background-color: #F4FBFF !important;
  }

  .ar-recovery-shell [class*="bg-[rgba(255,255,255"] {
    background-color: #F4FBFF !important;
  }

  .ar-recovery-shell [class*="bg-[rgba(203,213,225"],
  .ar-recovery-shell [class*="bg-[rgba(148,163,184"] {
    background-color: #F4FBFF !important;
  }

  .ar-recovery-shell [class*="bg-[#22E0D0]"],
  .ar-recovery-shell [class*="bg-[#14B8A6]"],
  .ar-recovery-shell .bg-teal-500,
  .ar-recovery-shell .bg-teal-600,
  .ar-recovery-shell .bg-teal-700 {
    background-color: #14B8A6 !important;
    color: #FFFFFF !important;
  }

  .ar-recovery-shell [class*="bg-[#FF6A2B]"],
  .ar-recovery-shell [class*="bg-[#FF6A2B]"],
  .ar-recovery-shell [class*="bg-[#FF6A2B]"] {
    background-color: #FF6A2B !important;
    color: #0A2540 !important;
  }

  .ar-recovery-shell [class*="bg-[rgba(255,106,43"] {
    background-color: rgba(214, 178, 74, 0.16) !important;
    color: #0A2540 !important;
  }

  .ar-recovery-shell [class*="bg-[rgba(34,197,94"],
  .ar-recovery-shell [class*="bg-emerald"],
  .ar-recovery-shell [class*="bg-green"] {
    background-color: rgba(20, 184, 166, 0.16) !important;
    color: #0A2540 !important;
  }

  .ar-recovery-shell [class*="text-emerald"],
  .ar-recovery-shell [class*="text-green"] {
    color: #14B8A6 !important;
  }

  .ar-recovery-shell [class*="text-red"],
  .ar-recovery-shell [class*="text-rose"],
  .ar-recovery-shell [class*="text-[#FCA5A5]"],
  .ar-recovery-shell [class*="text-[#F3A6A6]"] {
    color: #B42318 !important;
  }

  .ar-recovery-shell [class*="bg-[rgba(248,113,113"],
  .ar-recovery-shell [class*="bg-red"],
  .ar-recovery-shell [class*="bg-rose"] {
    background-color: #FEF3F2 !important;
    color: #B42318 !important;
  }

  .ar-recovery-shell [class*="border-white/"],
  .ar-recovery-shell [class*="border-[#FF6A2B"],
  .ar-recovery-shell [class*="border-[#FF6A2B"],
  .ar-recovery-shell [class*="border-[#22E0D0"],
  .ar-recovery-shell [class*="border-[#14B8A6"] {
    border-color: #BFEAFF !important;
  }

  .ar-recovery-shell input,
  .ar-recovery-shell select,
  .ar-recovery-shell textarea {
    background-color: #FFFFFF !important;
    border-color: #BFEAFF !important;
    color: #0A2540 !important;
  }

  .ar-recovery-shell input::placeholder,
  .ar-recovery-shell textarea::placeholder {
    color: #14B8A6 !important;
    opacity: .9;
  }

  .ar-recovery-shell table,
  .ar-recovery-shell thead,
  .ar-recovery-shell tbody,
  .ar-recovery-shell tr {
    background-color: #F4FBFF !important;
    color: #0A2540 !important;
  }

  .ar-recovery-shell th,
  .ar-recovery-shell td {
    border-color: #BFEAFF !important;
    color: #0A2540 !important;
  }

  .ar-recovery-shell thead th,
  .ar-recovery-shell th {
    background-color: #F4FBFF !important;
    color: #0A2540 !important;
    font-weight: 700 !important;
  }

  .ar-recovery-shell article,
  .ar-recovery-shell section:not(:has(.ar-recovery-shell)),
  .ar-recovery-shell aside {
    border-color: #BFEAFF;
  }

  .ar-recovery-shell [class*="rounded-"][class*="border"][class*="bg-[rgba"],
  .ar-recovery-shell [class*="rounded-"][class*="border"][class*="bg-[#0"],
  .ar-recovery-shell [class*="rounded-"][class*="border"][class*="bg-[#1"] {
    background-color: #FFFFFF !important;
    border-color: #BFEAFF !important;
    color: #0A2540 !important;
  }

  .ar-recovery-shell [class*="rounded-"][class*="bg-white/"] {
    background-color: #FFFFFF !important;
    border-color: #BFEAFF !important;
    color: #0A2540 !important;
  }

  .ar-recovery-shell [class*="bg-[#22E0D0]"],
  .ar-recovery-shell [class*="bg-[#14B8A6]"],
  .ar-recovery-shell [aria-selected="true"][class*="bg-[#14B8A6]"],
  .ar-recovery-shell [aria-selected="true"][class*="bg-[#22E0D0]"] {
    background-color: #14B8A6 !important;
    border-color: #14B8A6 !important;
    color: #FFFFFF !important;
  }

  .ar-recovery-shell [class*="border-dashed"] {
    background-color: #F4FBFF !important;
    border-color: #BFEAFF !important;
  }

  .ar-recovery-shell [class*="border-[rgba(255,106,43"] {
    border-color: #BFEAFF !important;
  }

  .ar-recovery-shell [class*="border-[rgba(34,224,208"] {
    border-color: #BFEAFF !important;
  }

  .ar-recovery-shell [class*="border-[rgba(148,163,184"] {
    border-color: #BFEAFF !important;
  }

  .ar-recovery-shell [class*="shadow-[0_24px_60px_rgba(0,0,0,.24)"],
  .ar-recovery-shell [class*="shadow-[0_20px_48px_rgba(0,0,0,.18)"],
  .ar-recovery-shell [class*="shadow-[0_18px_42px_rgba(0,0,0,.2)"],
  .ar-recovery-shell [class*="shadow-[0_18px_40px_rgba(0,0,0,.16)"],
  .ar-recovery-shell [class*="shadow-[0_14px_30px_rgba(0,0,0,.14)"],
  .ar-recovery-shell [class*="shadow-[0_16px_36px_rgba(15,23,42,.06)"],
  .ar-recovery-shell [class*="shadow-[0_12px_28px_rgba(20,184,166,.18)"] {
    box-shadow: 0 20px 48px rgba(15, 23, 42, 0.06) !important;
  }

  .ar-recovery-shell a:not([class*="bg-[#14B8A6]"]):not([class*="bg-[#22E0D0]"]),
  .ar-recovery-shell button:not([class*="bg-[#14B8A6]"]):not([class*="bg-[#22E0D0]"]) {
    border-color: #BFEAFF;
  }

  .ar-recovery-shell button,
  .ar-recovery-shell a[role="button"] {
    min-height: 2.5rem;
    border-radius: 0.875rem;
  }

  .ar-recovery-shell button[class*="bg-[#22E0D0]"],
  .ar-recovery-shell button[class*="bg-[#14B8A6]"],
  .ar-recovery-shell a[class*="bg-[#22E0D0]"],
  .ar-recovery-shell a[class*="bg-[#14B8A6]"] {
    background-color: #14B8A6 !important;
    color: #FFFFFF !important;
    border-color: #14B8A6 !important;
  }

  .ar-recovery-shell button:not([class*="bg-[#22E0D0]"]):not([class*="bg-[#14B8A6]"]):not([class*="bg-[#FF6A2B]"]):not([class*="bg-[#FF6A2B]"]),
  .ar-recovery-shell a:not([class*="bg-[#22E0D0]"]):not([class*="bg-[#14B8A6]"]):not([class*="bg-[#FF6A2B]"]):not([class*="bg-[#FF6A2B]"]) {
    color: #0A2540;
  }

  .ar-recovery-shell [role="tablist"] {
    border-color: #BFEAFF !important;
    background-color: #FFFFFF !important;
    gap: .5rem;
    box-shadow: 0 14px 34px rgba(15, 23, 42, 0.05);
  }

  .ar-recovery-shell [role="tab"] {
    border: 1px solid #BFEAFF;
    background-color: #F4FBFF;
    color: #0A2540 !important;
    font-size: 0.8125rem !important;
    letter-spacing: 0.01em !important;
  }

  .ar-recovery-shell [role="tab"][aria-selected="true"] {
    border-color: #14B8A6 !important;
    background-color: #E6FFFB !important;
    color: #0A2540 !important;
    box-shadow:
      0 0 0 1px rgba(20, 184, 166, 0.32),
      0 10px 24px rgba(20, 184, 166, 0.14);
  }

  .ar-recovery-shell button[role="tab"][aria-selected="true"][class*="bg"] {
    border-color: #14B8A6 !important;
    background-color: #E6FFFB !important;
    color: #0A2540 !important;
  }

  .ar-recovery-shell [role="tab"] svg,
  .ar-recovery-shell button svg,
  .ar-recovery-shell a svg {
    color: currentColor;
  }

  .ar-recovery-shell .ar-action-toolbar {
    border-color: #14B8A6 !important;
    background: linear-gradient(180deg, #E8FFFC 0%, #F4FBFF 100%) !important;
    box-shadow: 0 18px 40px rgba(20, 184, 166, 0.08);
  }

  .ar-recovery-shell .ar-action-toolbar-title {
    color: #0A2540 !important;
    font-size: 0.95rem !important;
    font-weight: 650 !important;
    letter-spacing: 0 !important;
    text-transform: none !important;
  }

  .ar-recovery-shell .ar-quick-action-bolt {
    color: #FF6A2B !important;
  }

  .ar-recovery-shell .ar-action-toolbar-description {
    color: #475569 !important;
    font-size: 0.8125rem !important;
    font-weight: 400 !important;
    line-height: 1.45 !important;
  }

  .ar-recovery-shell .ar-compact-action {
    min-height: 2.5rem;
    border-color: #14B8A6 !important;
    background-color: #FFFFFF !important;
    color: #0A2540 !important;
    font-size: 0.8125rem !important;
    font-weight: 600 !important;
    letter-spacing: 0 !important;
    text-transform: none !important;
    box-shadow: 0 8px 20px rgba(20, 184, 166, 0.08);
  }

  .ar-recovery-shell .ar-compact-action:hover {
    background-color: #14B8A6 !important;
    border-color: #14B8A6 !important;
    color: #FFFFFF !important;
  }

  .ar-recovery-shell .ar-workspace-tabs-panel {
    border-color: #BFEAFF !important;
    background: #FFFFFF !important;
    box-shadow: 0 16px 36px rgba(15, 23, 42, 0.05);
  }

  .ar-recovery-shell .ar-workspace-tabs-title {
    color: #0A2540 !important;
    font-size: 0.95rem !important;
    font-weight: 650 !important;
    text-transform: none !important;
  }

  .ar-recovery-shell .ar-workspace-tabs-description {
    color: #475569 !important;
    font-size: 0.8125rem !important;
    font-weight: 400 !important;
  }

  .ar-recovery-shell .ar-operational-workspace {
    border-color: #14B8A6 !important;
    background: linear-gradient(180deg, #E8FFFC 0%, #F4FBFF 100%) !important;
    box-shadow: 0 24px 54px rgba(20, 184, 166, 0.08);
  }

  .ar-recovery-shell .ar-workspace-surface {
    border-color: #BFEAFF !important;
    background: #FFFFFF !important;
    box-shadow: 0 14px 32px rgba(15, 23, 42, 0.045);
  }

  .ar-recovery-shell .ar-workflow-surface {
    border-color: #BFEAFF !important;
    background: #FFFFFF !important;
    box-shadow: 0 18px 42px rgba(15, 23, 42, 0.06);
  }

  .ar-recovery-shell .ar-ia-panel {
    border-color: #BFEAFF !important;
    background: #FFFFFF !important;
    box-shadow: 0 18px 42px rgba(15, 23, 42, 0.055);
  }

  .ar-recovery-shell .ar-ia-section {
    border-color: #BFEAFF !important;
    background: #F4FBFF !important;
  }

  .ar-recovery-shell .ar-ia-scroll {
    max-height: 34rem;
    overflow-y: auto;
    padding-right: .25rem;
  }

  .ar-recovery-shell .ar-ia-scroll::-webkit-scrollbar {
    width: .55rem;
  }

  .ar-recovery-shell .ar-ia-scroll::-webkit-scrollbar-track {
    background: #E8FFFC;
    border-radius: 999px;
  }

  .ar-recovery-shell .ar-ia-scroll::-webkit-scrollbar-thumb {
    background: #FF6A2B;
    border-radius: 999px;
  }

  .ar-recovery-shell .ar-workspace-os {
    color: #0A2540;
  }

  .ar-recovery-shell .ar-workspace-os > section:first-child {
    margin-top: 0;
  }

  .ar-recovery-shell .ar-workspace-os article,
  .ar-recovery-shell .ar-workspace-os section,
  .ar-recovery-shell .ar-workspace-os div[class*="rounded-"][class*="border"] {
    border-color: #BFEAFF !important;
  }

  .ar-recovery-shell .ar-workspace-os article[class*="bg-[rgba"],
  .ar-recovery-shell .ar-workspace-os div[class*="bg-[rgba"],
  .ar-recovery-shell .ar-workspace-os section[class*="bg-[rgba"] {
    background: #FFFFFF !important;
    box-shadow: 0 14px 32px rgba(15, 23, 42, 0.045) !important;
  }

  .ar-recovery-shell .ar-workspace-os input,
  .ar-recovery-shell .ar-workspace-os select,
  .ar-recovery-shell .ar-workspace-os textarea {
    background: #FFFFFF !important;
    border-color: #BFEAFF !important;
    color: #0A2540 !important;
  }

  .ar-recovery-shell .ar-workspace-os button[class*="rounded-3xl"],
  .ar-recovery-shell .ar-workspace-os button[class*="rounded-2xl"] {
    background: #FFFFFF !important;
    border-color: #BFEAFF !important;
    color: #0A2540 !important;
  }

  .ar-recovery-shell .ar-workspace-os button[class*="bg-[rgba(255,106,43"],
  .ar-recovery-shell .ar-workspace-os button[class*="bg-[rgba(34,224,208"] {
    background: #E8FFFC !important;
    border-color: #14B8A6 !important;
  }

  .ar-recovery-shell .ar-workspace-os .font-display {
    font-family: Inter, "IBM Plex Sans", Aptos, "Segoe UI", Arial, sans-serif !important;
  }

  .ar-recovery-shell .ar-workspace-os [class*="text-[#F6F1E8]"],
  .ar-recovery-shell .ar-workspace-os [class*="text-[#E8EEF4]"],
  .ar-recovery-shell .ar-workspace-os [class*="text-[#CBD5E1]"] {
    color: #0A2540 !important;
  }

  .ar-recovery-shell .ar-workspace-os [class*="text-[#FF6A2B]"],
  .ar-recovery-shell .ar-workspace-os [class*="text-[#FF6A2B]"] {
    color: #0A2540 !important;
  }

  .ar-recovery-shell .ar-workspace-os [class*="text-[#22E0D0]"] {
    color: #14B8A6 !important;
  }

  .ar-recovery-shell .ar-workspace-os svg {
    color: #14B8A6;
  }

  .ar-recovery-shell .ar-workspace-os [class*="border-[rgba"] {
    border-color: #BFEAFF !important;
  }

  .ar-recovery-shell .ar-workspace-os [class*="shadow-[0_20px_48px_rgba(0,0,0"] {
    box-shadow: 0 16px 36px rgba(15, 23, 42, 0.055) !important;
  }

  .ar-recovery-shell .statement-center-queue-grid {
    display: grid !important;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 15rem), 1fr));
    overflow-x: visible !important;
    padding-bottom: 0 !important;
  }

  .ar-recovery-shell article > div[class="mt-5 grid gap-3"] {
    max-height: 34rem;
    overflow-y: auto;
    padding-right: .25rem;
  }

  .ar-recovery-shell article > div[class="mt-5 grid gap-3"]::-webkit-scrollbar {
    width: .55rem;
  }

  .ar-recovery-shell article > div[class="mt-5 grid gap-3"]::-webkit-scrollbar-track {
    background: #E8FFFC;
    border-radius: 999px;
  }

  .ar-recovery-shell article > div[class="mt-5 grid gap-3"]::-webkit-scrollbar-thumb {
    background: #FF6A2B;
    border-radius: 999px;
  }

  .ar-recovery-shell [class*="bg-[rgba(7,18,31"],
  .ar-recovery-shell [class*="bg-[rgba(255,255,255,.03"],
  .ar-recovery-shell [class*="bg-[rgba(255,255,255,.04"] {
    background: #FFFFFF !important;
    border-color: #BFEAFF !important;
    box-shadow: 0 14px 32px rgba(15, 23, 42, 0.045);
  }

  .ar-recovery-shell [class*="text-[#F6F1E8]"],
  .ar-recovery-shell [class*="text-[#E8EEF4]"] {
    color: #0A2540 !important;
  }

  .ar-recovery-shell [class*="text-[#CBD5E1]"],
  .ar-recovery-shell [class*="text-[#94A3B8]"] {
    color: #475569 !important;
  }

  .ar-recovery-shell .ar-workflow-label {
    color: #14B8A6 !important;
    font-size: 0.75rem !important;
    font-weight: 650 !important;
    letter-spacing: 0.01em !important;
    text-transform: none !important;
  }

  .ar-recovery-shell .ar-workflow-title {
    color: #0A2540 !important;
  }

  .ar-recovery-shell .ar-workflow-description {
    color: #475569 !important;
    font-weight: 400 !important;
  }

  .ar-recovery-shell iframe,
  .ar-recovery-shell [class*="preview"],
  .ar-recovery-shell [class*="Preview"] {
    background-color: #FFFFFF !important;
    color: #0A2540 !important;
  }

  .ar-recovery-shell a:hover,
  .ar-recovery-shell button:hover {
    border-color: #14B8A6 !important;
  }

  @media (max-width: 640px) {
    .ar-recovery-shell,
    .ar-recovery-shell main,
    .ar-recovery-shell section,
    .ar-recovery-shell article,
    .ar-recovery-shell form,
    .ar-recovery-shell fieldset {
      max-width: 100%;
      min-width: 0;
    }

    .ar-recovery-shell [class*="min-w-"] {
      min-width: 0 !important;
    }

    .ar-recovery-shell [class*="w-["],
    .ar-recovery-shell [class*="max-w-["] {
      max-width: 100% !important;
    }

    .ar-recovery-shell table,
    .ar-recovery-shell .overflow-x-auto > * {
      max-width: 100%;
    }

    .ar-recovery-shell input,
    .ar-recovery-shell select,
    .ar-recovery-shell textarea {
      max-width: 100%;
      min-width: 0;
    }

    .ar-recovery-shell [role="tablist"] {
      flex-wrap: wrap;
      overflow-x: hidden !important;
    }

    .ar-recovery-shell [role="tablist"] [role="tab"] {
      flex: 1 1 100%;
      justify-content: flex-start;
    }
  }
`;

const DOCUMENT_WORKSPACE_TABS: DocumentWorkspaceTabDefinition[] = [
  { key: "overview", label: "Overview", icon: <Sparkles className="size-4" aria-hidden="true" />, note: "Summary and shortcuts." },
  { key: "generate", label: "Generate", icon: <FileText className="size-4" aria-hidden="true" />, note: "Create, preview, and deliver." },
  { key: "letter-templates", label: "Letter Templates", icon: <FileText className="size-4" aria-hidden="true" />, note: "Letter template workspace." },
  { key: "statement-templates", label: "Statement Templates", icon: <FileText className="size-4" aria-hidden="true" />, note: "Statement template workspace." },
  { key: "document-history", label: "Document History", icon: <Clock3 className="size-4" aria-hidden="true" />, note: "Generated record history." },
  { key: "delivery-history", label: "Delivery History", icon: <Activity className="size-4" aria-hidden="true" />, note: "Sent, printed, emailed, and failed." },
  { key: "branding", label: "Branding", icon: <Building2 className="size-4" aria-hidden="true" />, note: "Client branding and defaults." },
  { key: "merge-fields", label: "Merge Fields", icon: <Target className="size-4" aria-hidden="true" />, note: "Template field references." },
  { key: "permissions", label: "Permissions", icon: <ShieldCheck className="size-4" aria-hidden="true" />, note: "Role visibility and access." },
];

function isDocumentWorkspaceTabKey(value: string | null | undefined): value is DocumentWorkspaceTabKey {
  return DOCUMENT_WORKSPACE_TABS.some((tab) => tab.key === value);
}

function loadStoredARRecoveryDocumentWorkspaceTab(companyId: string): DocumentWorkspaceTabKey {
  if (typeof window === "undefined") return "overview";
  try {
    const raw = window.localStorage.getItem(`${DOCUMENT_WORKSPACE_TAB_STORAGE_KEY}:${companyId}`);
    return isDocumentWorkspaceTabKey(raw) ? raw : "overview";
  } catch {
    return "overview";
  }
}

function saveStoredARRecoveryDocumentWorkspaceTab(companyId: string, tab: DocumentWorkspaceTabKey) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(`${DOCUMENT_WORKSPACE_TAB_STORAGE_KEY}:${companyId}`, tab);
  } catch {
    // Local preference persistence is optional.
  }
}

function DocumentWorkspaceMetricCard({
  label,
  value,
  note,
}: {
  label: string;
  value: string | number;
  note: string;
}) {
  return (
    <div className="rounded-[1.5rem] border border-[#BFEAFF] bg-white p-5 shadow-[0_10px_26px_rgba(15,23,42,.05)]">
      <p className="text-xs font-medium text-[#334155]">{label}</p>
      <p className="mt-3 font-display text-4xl font-bold tracking-[-0.03em] text-[#0A2540]">{value}</p>
      <p className="mt-3 text-sm font-normal leading-6 text-[#475569]">{note}</p>
    </div>
  );
}

function DocumentWorkspaceTabButton({
  tab,
  active,
  onClick,
}: {
  tab: DocumentWorkspaceTabDefinition;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      title={tab.note}
      className={`focus-ring inline-flex min-h-11 shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-black transition ${
        active
          ? "border-[#14B8A6] bg-[#14B8A6] text-white shadow-[0_10px_24px_rgba(20,184,166,.16)]"
          : "border-[#BFEAFF] bg-white text-[#0A2540] hover:border-[#14B8A6] hover:bg-[#F4FBFF] hover:text-[#14B8A6]"
      }`}
    >
      <span
        className={`inline-flex size-9 items-center justify-center rounded-full ${
          active ? "bg-[#FF6A2B] text-[#0A2540]" : "bg-[#F4FBFF] text-[#14B8A6]"
        }`}
        aria-hidden="true"
      >
        {tab.icon}
      </span>
      <span className="whitespace-nowrap">{tab.label}</span>
    </button>
  );
}

function DocumentWorkspacePanel({
  title,
  eyebrow,
  description,
  action,
  children,
  className = "",
}: {
  title: string;
  eyebrow: string;
  description: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <article className={`rounded-[1.75rem] border border-[#BFEAFF] bg-white p-5 shadow-[0_20px_48px_rgba(15,23,42,.06)] ${className}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">{eyebrow}</p>
          <h3 className="mt-1 font-display text-2xl font-bold text-[#0A2540]">{title}</h3>
          <p className="mt-2 max-w-4xl text-sm font-normal leading-7 text-[#475569]">{description}</p>
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      <div className="mt-5">{children}</div>
    </article>
  );
}

function DocumentWorkspaceKeyValueRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] px-4 py-3">
      <span className="text-xs font-medium text-[#334155]">{label}</span>
      <span className="max-w-[60%] text-right text-sm font-semibold leading-6 text-[#0A2540]">{value}</span>
    </div>
  );
}

function DocumentWorkspaceEmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: string;
}) {
  return (
    <div className="rounded-2xl border border-dashed border-[#BFEAFF] bg-[#F4FBFF] p-4 text-sm font-medium leading-6 text-[#0A2540]">
      <p className="font-semibold text-[#0A2540]">{title}</p>
      <p className="mt-2 text-[#475569]">{description}</p>
      {action ? <p className="mt-1 text-[#14B8A6]">{action}</p> : null}
    </div>
  );
}

function DocumentWorkspaceQuickLink({
  label,
  note,
  icon,
  onClick,
}: {
  label: string;
  note: string;
  icon: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-start gap-3 rounded-2xl border border-[#BFEAFF] bg-white px-4 py-3 text-left transition hover:border-[#14B8A6] hover:bg-[#F4FBFF] focus-ring"
    >
      <span className="mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-full border border-[#BFEAFF] bg-[#F4FBFF] text-[#14B8A6]">
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-black text-[#0A2540]">{label}</span>
        <span className="mt-1 block text-xs font-medium leading-5 text-[#0A2540]">{note}</span>
      </span>
    </button>
  );
}

function DocumentWorkspaceTimeline({
  entries,
}: {
  entries: Array<{
    id: string;
    eventType: string;
    date: string;
    time: string;
    user: string;
    status: string;
    outcome: string;
    nextAction: string;
  }>;
}) {
  return (
    <div className="grid gap-3">
      {entries.map((entry, index) => (
        <div key={entry.id} className="relative">
          {index !== entries.length - 1 ? <div className="absolute left-[18px] top-11 h-full w-px bg-[#BFEAFF]" aria-hidden="true" /> : null}
          <article className="relative rounded-[1.35rem] border border-[#BFEAFF] bg-white p-4 shadow-[0_10px_24px_rgba(15,23,42,.04)]">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-3">
                <div className="mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-full border border-[#BFEAFF] bg-[#F4FBFF] text-[#14B8A6] shadow-sm">
                  <Activity className="size-4" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#FF6A2B]">
                      {entry.status}
                    </span>
                    <p className="text-[11px] font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{entry.eventType}</p>
                  </div>
                  <h4 className="mt-1 font-display text-xl font-bold text-[#0A2540]">{entry.outcome}</h4>
                </div>
              </div>
              <div className="text-right text-xs font-semibold leading-5 text-[#0A2540]">
                <p>{entry.date}</p>
                <p>{entry.time}</p>
              </div>
            </div>

            <div className="mt-3 grid gap-2 md:grid-cols-[1fr_auto] md:items-start">
              <p className="text-sm font-medium leading-6 text-[#0A2540]">{entry.nextAction}</p>
              <p className="text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{entry.user}</p>
            </div>
          </article>
        </div>
      ))}
    </div>
  );
}

function DocumentWorkspaceSectionHeading({
  eyebrow,
  title,
  description,
}: {
  eyebrow: string;
  title: string;
  description: string;
}) {
  return (
    <div className="border-b border-[#BFEAFF] pb-4">
      <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF6A2B]">{eyebrow}</p>
      <h2 className="mt-1 font-display text-3xl font-bold text-[#0A2540]">{title}</h2>
      <p className="mt-3 max-w-4xl text-sm font-semibold leading-7 text-[#0A2540]">{description}</p>
    </div>
  );
}

function DocumentWorkspaceDetailMetric({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-3 shadow-[0_10px_26px_rgba(15,23,42,.05)]" title={value}>
      <p className="truncate text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">{label}</p>
      <p className="mt-1.5 min-w-0 break-words text-sm font-semibold leading-6 text-[#0A2540]">{value}</p>
      {note ? <p className="mt-2 text-xs font-semibold leading-6 text-[#0A2540]">{note}</p> : null}
    </div>
  );
}

function buildFallbackARRecoveryActor(): ARRecoveryActor {
  return {
    email: "",
    name: "Authenticated User",
    role: "",
  };
}

function normalizeARRecoveryRole(role: string | null | undefined) {
  return (role || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

function canWriteARRecovery(actor: ARRecoveryActor) {
  return Boolean(normalizeARRecoveryRole(actor.role) && AR_RECOVERY_WRITE_ROLES.has(normalizeARRecoveryRole(actor.role)));
}

function canOverrideARRecoveryOwnership(actor: ARRecoveryActor) {
  return Boolean(normalizeARRecoveryRole(actor.role) && AR_RECOVERY_OVERRIDE_ROLES.has(normalizeARRecoveryRole(actor.role)));
}

function formatARRecoveryRole(role: string | null | undefined) {
  const normalizedRole = normalizeARRecoveryRole(role);
  if (!normalizedRole) return "Session Pending";
  if (normalizedRole === "kreatethedate_admin" || normalizedRole === "platform_admin") return "Platform Admin";
  if (normalizedRole === "team_member" || normalizedRole === "collector") return "Collector";
  return normalizedRole.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function buildSynchronizedARRecoveryOperationalWorkspace({
  workspace,
  clientExchangeFoundation,
  portfolioValidationFoundation,
  portfolioMappingFoundation,
  portfolioReviewFoundation,
  portfolioImportFoundation,
  portfolioAssignmentFoundation,
  portfolioActivationFoundation,
  portfolioIntakeWalkthrough,
  documentDeliveries,
  documentRequests,
  escalationWorkflows,
  accountEdits,
}: {
  workspace: ReturnType<typeof loadARRecoveryWorkspace>;
  clientExchangeFoundation: ReturnType<typeof loadProjectedEnterpriseClientExchangeFoundation>;
  portfolioValidationFoundation: ReturnType<typeof loadPortfolioValidationEngineFoundation>;
  portfolioMappingFoundation: ReturnType<typeof loadPortfolioMappingEngineFoundation>;
  portfolioReviewFoundation: ReturnType<typeof loadPortfolioReviewEngineFoundation>;
  portfolioImportFoundation: ReturnType<typeof loadPortfolioImportEngineFoundation>;
  portfolioAssignmentFoundation: ReturnType<typeof loadPortfolioAssignmentEngineFoundation>;
  portfolioActivationFoundation: ReturnType<typeof loadPortfolioActivationEngineFoundation>;
  portfolioIntakeWalkthrough: ReturnType<typeof loadEnterprisePortfolioIntakeWalkthrough>;
  documentDeliveries: ARRecoveryDocumentDeliveryRecord[];
  documentRequests: ARRecoveryDocumentRequestRecord[];
  escalationWorkflows: ARWorkflowEscalationProjection[];
  accountEdits: ARRecoveryAccountEditRecord[];
}) {
  return mergeWorkspaceWithOperationalLogs(
    workspace,
    loadStoredARRecoveryCallLogs(),
    loadStoredARRecoveryNoteLogs(),
    loadStoredARRecoveryCommunications(),
    loadStoredARRecoveryTaskAssignments(),
    loadStoredARRecoveryPromiseWorkflows(),
    loadStoredARRecoveryTicklerWorkflows(),
    loadProjectedARRecoveryLetterWorkflows(),
    loadProjectedARRecoveryStatementWorkflows(),
    escalationWorkflows,
    loadProjectedARRecoveryPaymentWorkflows(),
    loadProjectedARRecoveryNotifications(),
    loadProjectedARRecoveryReportingAnalytics(),
    clientExchangeFoundation,
    portfolioValidationFoundation,
    portfolioMappingFoundation,
    portfolioReviewFoundation,
    portfolioImportFoundation,
    portfolioAssignmentFoundation,
    portfolioActivationFoundation,
    portfolioIntakeWalkthrough,
    documentDeliveries,
    documentRequests,
    accountEdits,
  );
}

export function ARRecoveryWorkspacePage({ section }: ARRecoveryWorkspacePageProps) {
  const location = useLocation();
  const workspace = useMemo(() => loadARRecoveryWorkspace(), []);
  const documentRequests = useMemo(() => loadStoredARRecoveryDocumentRequests(), []);
  const [documentDeliveries, setDocumentDeliveries] = useState<ARRecoveryDocumentDeliveryRecord[]>(() => loadStoredARRecoveryDocumentDeliveries());
  const clientExchangeFoundation = useMemo(() => loadProjectedEnterpriseClientExchangeFoundation(), []);
  const portfolioValidationFoundation = useMemo(() => loadPortfolioValidationEngineFoundation(workspace.company.id), [workspace.company.id]);
  const portfolioMappingFoundation = useMemo(() => loadPortfolioMappingEngineFoundation(workspace.company.id), [workspace.company.id]);
  const portfolioReviewFoundation = useMemo(() => loadPortfolioReviewEngineFoundation(workspace.company.id), [workspace.company.id]);
  const portfolioImportFoundation = useMemo(() => loadPortfolioImportEngineFoundation(workspace.company.id), [workspace.company.id]);
  const portfolioAssignmentFoundation = useMemo(() => loadPortfolioAssignmentEngineFoundation(workspace.company.id), [workspace.company.id]);
  const portfolioActivationFoundation = useMemo(() => loadPortfolioActivationEngineFoundation(workspace.company.id), [workspace.company.id]);
  const portfolioIntakeWalkthrough = useMemo(() => loadEnterprisePortfolioIntakeWalkthrough(workspace.company.id), [workspace.company.id]);
  const [synchronizedAccountEdits, setSynchronizedAccountEdits] = useState<ARRecoveryAccountEditRecord[]>(() => loadStoredARRecoveryAccountEdits());
  const [isSynchronizedAccountEditsReady, setIsSynchronizedAccountEditsReady] = useState(false);
  const promiseCenterReturnContext = section === "promise-to-pay" ? getPromiseCenterReturnContext(location.search) : null;
  const managerCommandReturnContext = (location.state as AccountRecoveryLaunchState | null)?.managerCommandReturnContext;
  const managerCommandSynchronizationKey = section === "manager-command-center"
    ? [
        location.key,
        managerCommandReturnContext?.selectedOperationalCaseId || "",
        managerCommandReturnContext?.workspaceTab || "",
        managerCommandReturnContext?.scrollY ?? "",
      ].join(":")
    : "";
  const promiseCenterSynchronizationKey = section === "promise-to-pay"
    ? [
        location.key,
        promiseCenterReturnContext?.promiseId || "",
        promiseCenterReturnContext?.searchQuery || "",
        promiseCenterReturnContext?.activeTab || "",
        promiseCenterReturnContext?.workflowType || "",
      ].join(":")
    : "";
  const synchronizedEscalationWorkflows = useMemo(
    () => loadProjectedARRecoveryEscalationWorkflows(),
    [managerCommandSynchronizationKey],
  );
  const operationalWorkspace = useMemo(
    () => buildSynchronizedARRecoveryOperationalWorkspace({
      workspace,
      clientExchangeFoundation,
      portfolioValidationFoundation,
      portfolioMappingFoundation,
      portfolioReviewFoundation,
      portfolioImportFoundation,
      portfolioAssignmentFoundation,
      portfolioActivationFoundation,
      portfolioIntakeWalkthrough,
      documentDeliveries,
      documentRequests,
      escalationWorkflows: synchronizedEscalationWorkflows,
      accountEdits: synchronizedAccountEdits,
    }),
    [
      workspace,
      clientExchangeFoundation,
      portfolioValidationFoundation,
      portfolioMappingFoundation,
      portfolioReviewFoundation,
      portfolioImportFoundation,
      portfolioAssignmentFoundation,
      portfolioActivationFoundation,
      portfolioIntakeWalkthrough,
      documentDeliveries,
      documentRequests,
      synchronizedEscalationWorkflows,
      synchronizedAccountEdits,
      managerCommandSynchronizationKey,
      promiseCenterSynchronizationKey,
    ],
  );
  useEffect(() => {
    let cancelled = false;
    setIsSynchronizedAccountEditsReady(false);
    loadRemoteARRecoveryAccountEdits(workspace.company.id)
      .then((remoteAccountEdits) => {
        if (cancelled) return;
        const mergedAccountEdits = mergeARRecoveryAccountEditRecords(loadStoredARRecoveryAccountEdits(), remoteAccountEdits);
        saveStoredARRecoveryAccountEdits(mergedAccountEdits);
        setSynchronizedAccountEdits(mergedAccountEdits);
        setIsSynchronizedAccountEditsReady(true);
      })
      .catch(() => {
        if (!cancelled) {
          setSynchronizedAccountEdits(loadStoredARRecoveryAccountEdits());
          setIsSynchronizedAccountEditsReady(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [workspace.company.id, managerCommandSynchronizationKey]);
  useEffect(() => {
    saveStoredARRecoveryDocumentDeliveries(documentDeliveries);
  }, [documentDeliveries]);
  const sectionWorkspace = section === "customer-accounts" ? workspace : operationalWorkspace;
  const snapshot = useMemo(() => buildARRecoveryDashboardSnapshot(workspace.company.id), [workspace.company.id]);
  const navigation = useMemo(() => buildARRecoveryNavigation(section), [section]);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const isDocumentWorkspace = section === "document-center";

  useARRecoveryBranding(section === "dashboard" ? "Recovery Dashboard" : navigation.find((item) => item.key === section)?.label || "AR Recovery", sectionDescriptions[section]);

  return (
    <main className="min-h-screen bg-[#02072C] text-[#0A2540]">
      <style>{AR_RECOVERY_THEME_STYLE}</style>
      <ARRecoveryHeader />

      {isMobileSidebarOpen ? (
        <button
          type="button"
          aria-label="Close AR Recovery navigation"
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      ) : null}

      <ARRecoverySidebar
        navigation={navigation}
        collapsed={isSidebarCollapsed}
        mobileOpen={isMobileSidebarOpen}
        onToggleCollapsed={() => setIsSidebarCollapsed((value) => !value)}
        onCloseMobile={() => setIsMobileSidebarOpen(false)}
      />

      <div className={`ar-recovery-shell min-h-screen transition-[padding] duration-300 ${isSidebarCollapsed ? "lg:pl-[6.5rem]" : "lg:pl-[19rem]"}`}>
        {!isDocumentWorkspace ? (
          <section className="px-4 py-6 sm:px-6 lg:px-8">
            <div className="mx-auto grid max-w-[96rem] gap-6">
              <article className="rounded-[1.75rem] border border-[#BFEAFF] bg-white p-5 shadow-[0_20px_48px_rgba(15,23,42,.06)]">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="grid min-w-0 max-w-full gap-3">
                    <button
                      type="button"
                      onClick={() => setIsMobileSidebarOpen(true)}
                      className="inline-flex min-h-10 w-fit items-center gap-2 rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-3 text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6] transition hover:border-[#14B8A6] hover:bg-[#14B8A6] hover:text-white focus-ring lg:hidden"
                    >
                      {navIcon(section)}
                      Menu
                    </button>
                    <div className="grid gap-2">
                      <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#FF6A2B]">AR Recovery Workspace</p>
                      <h1 className="max-w-4xl font-display text-4xl font-bold leading-tight text-[#0A2540] sm:text-5xl">{sectionTitles[section]}</h1>
                      <p className="max-w-full break-words text-base font-semibold leading-8 text-[#0A2540]">{sectionDescriptions[section]}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <Link to="/admin/ar-recovery" className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[#BFEAFF] bg-white px-4 text-sm font-extrabold text-[#0A2540] transition hover:border-[#14B8A6] hover:bg-[#F4FBFF] focus-ring">
                      <ArrowLeft className="size-4" aria-hidden="true" />
                      Back to Overview
                    </Link>
                    <Link to="/admin/ar-recovery/client-portal" className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-4 text-sm font-extrabold text-[#14B8A6] transition hover:border-[#14B8A6] hover:bg-[#14B8A6] hover:text-white focus-ring">
                      Client Portal
                      <ArrowRight className="size-4" aria-hidden="true" />
                    </Link>
                  </div>
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <MiniStat
                    label="Open Balance"
                    value={formatARRecoveryCurrency(workspace.accounts.reduce((sum, account) => sum + account.openBalanceCents, 0))}
                    note="Open receivables waiting for action."
                    icon={<CircleDollarSign className="size-5" aria-hidden="true" />}
                  />
                  <MiniStat
                    label="Promises"
                    value={workspace.promises.length}
                    note="Active promise-to-pay records in motion."
                    icon={<Target className="size-5" aria-hidden="true" />}
                  />
                  <MiniStat
                    label="Ticklers"
                    value={workspace.ticklers.length}
                    note="Open reminders and follow-up items."
                    icon={<Clock3 className="size-5" aria-hidden="true" />}
                  />
                  <MiniStat
                    label="Activity"
                    value={workspace.activity.length}
                    note="Chronological operational history."
                    icon={<Activity className="size-5" aria-hidden="true" />}
                  />
                </div>
              </article>
            </div>
          </section>
        ) : null}

        <section className={isDocumentWorkspace ? "px-3 py-4 sm:px-4 lg:px-5" : "px-3 py-5 sm:px-4 lg:px-5"}>
          <div className="w-full max-w-none">
            {section === "dashboard" ? (
              <DashboardSection workspace={sectionWorkspace} snapshot={snapshot} />
            ) : section === "client-management" ? (
              <ARRecoveryClientManagementSection workspace={sectionWorkspace} />
            ) : section === "client-exchange" ? (
              <ClientExchangeCenterSection
                workspace={sectionWorkspace}
                clientExchangeFoundation={clientExchangeFoundation}
                validationFoundation={portfolioValidationFoundation}
                mappingFoundation={portfolioMappingFoundation}
                reviewFoundation={portfolioReviewFoundation}
                importFoundation={portfolioImportFoundation}
                assignmentFoundation={portfolioAssignmentFoundation}
                activationFoundation={portfolioActivationFoundation}
                intakeWalkthrough={portfolioIntakeWalkthrough}
              />
            ) : section === "customer-account-management" ? (
              <ARRecoveryCustomerAccountManagementSection />
            ) : section === "customer-accounts" ? (
              <CustomerAccountsSection workspace={workspace} synchronizedAccountEdits={synchronizedAccountEdits} />
            ) : section === "promise-to-pay" ? (
              <PromiseToPaySection workspace={sectionWorkspace} />
            ) : section === "collection-letters" ? (
              <CollectionLetterSection workspace={sectionWorkspace} />
            ) : section === "document-center" ? (
              <DocumentCenterSection
                workspace={sectionWorkspace}
                documentDeliveries={documentDeliveries}
                documentRequests={documentRequests}
                onChangeDocumentDeliveries={setDocumentDeliveries}
              />
            ) : section === "statements" ? (
              <StatementCenterSection workspace={sectionWorkspace} />
            ) : section === "client-portal" ? (
              <ClientPortalSection workspace={sectionWorkspace} />
            ) : section === "commercialization-center" ? (
              <CommercializationSection workspace={workspace} />
            ) : section === "smart-tickler" ? (
              <SmartTicklerSection workspace={sectionWorkspace} />
            ) : section === "manager-command-center" ? (
              isSynchronizedAccountEditsReady ? (
                <ManagerCommandCenterSection workspace={sectionWorkspace} escalationWorkflows={synchronizedEscalationWorkflows} />
              ) : (
                <article className="rounded-[1.75rem] border border-[#BFEAFF] bg-white p-5 text-sm font-semibold leading-7 text-[#0A2540] shadow-[0_18px_38px_rgba(10,37,64,.08)]">
                  Synchronizing Manager Command account assignments...
                </article>
              )
            ) : section === "white-label-settings" ? (
              <PlatformAdministrationSection workspace={sectionWorkspace} />
            ) : (
              <PlaceholderSection workspace={sectionWorkspace} section={section as Exclude<ARRecoverySectionKey, "dashboard" | "client-management" | "client-exchange" | "customer-account-management" | "customer-accounts" | "promise-to-pay" | "collection-letters" | "document-center" | "statements" | "client-portal" | "commercialization-center" | "smart-tickler" | "manager-command-center" | "white-label-settings">} />
            )}
          </div>
        </section>
      </div>
    </main>
  );
}

function ARRecoverySidebar({
  navigation,
  collapsed,
  mobileOpen,
  onToggleCollapsed,
  onCloseMobile,
}: {
  navigation: ReturnType<typeof buildARRecoveryNavigation>;
  collapsed: boolean;
  mobileOpen: boolean;
  onToggleCollapsed: () => void;
  onCloseMobile: () => void;
}) {
  const platformNavigationKeys = new Set<ARRecoverySectionKey>(["white-label-settings", "commercialization-center"]);
  const navigationSections = [
    { label: "Operations", items: navigation.filter((item) => !platformNavigationKeys.has(item.key)) },
    { label: "Platform", items: navigation.filter((item) => platformNavigationKeys.has(item.key)) },
  ];

  return (
    <aside
      className={`fixed bottom-4 top-[6.25rem] z-50 flex w-[17rem] flex-col rounded-[1.5rem] border border-[rgba(255,106,43,.16)] bg-[rgba(7,26,61,.96)] p-3 shadow-[0_24px_70px_rgba(2,7,44,.42)] backdrop-blur transition-all duration-300 lg:left-4 ${
        collapsed ? "lg:w-[4.75rem]" : "lg:w-[17rem]"
      } ${mobileOpen ? "left-4" : "-left-[20rem] lg:left-4"}`}
      aria-label="CollabRecover AR Solutions sidebar navigation"
    >
      <div className="flex items-center justify-between gap-2 border-b border-[rgba(255,106,43,.12)] pb-3">
        <Link to="/admin/ar-recovery" onClick={onCloseMobile} className="focus-ring inline-flex min-w-0 items-center gap-3 rounded-2xl">
          <span className="grid size-10 shrink-0 place-items-center overflow-hidden rounded-2xl bg-[#FF6A2B] text-xs font-black text-white">
            <img
              src="/assets/collabrecover-cr-logo.png"
              alt="CollabRecover AR Solutions"
              className="h-full w-full rounded-[inherit] object-cover"
            />
          </span>
          {!collapsed ? (
            <span className="min-w-0 leading-tight">
              <span className="block text-[10px] font-black uppercase tracking-[0.18em] text-[#FF6A2B]">Operating System</span>
              <span className="block max-w-full font-display text-lg font-bold leading-[1.05] text-[#F6F1E8]" title="CollabRecover AR Solutions">
                <span className="block">CollabRecover</span>
                <span className="block">AR Solutions</span>
              </span>
            </span>
          ) : null}
        </Link>
        <button
          type="button"
          onClick={onToggleCollapsed}
          className="hidden size-9 shrink-0 place-items-center rounded-xl border border-[rgba(34,224,208,.18)] bg-[rgba(255,255,255,.04)] text-xs font-black text-[#22E0D0] transition hover:border-[rgba(34,224,208,.34)] lg:grid"
          aria-label={collapsed ? "Expand AR Recovery sidebar" : "Collapse AR Recovery sidebar"}
        >
          {collapsed ? ">" : "<"}
        </button>
        <button
          type="button"
          onClick={onCloseMobile}
          className="grid size-9 shrink-0 place-items-center rounded-xl border border-[rgba(34,224,208,.18)] bg-[rgba(255,255,255,.04)] text-xs font-black text-[#22E0D0] lg:hidden"
          aria-label="Close AR Recovery sidebar"
        >
          X
        </button>
      </div>

      <nav className="luxury-scrollbar mt-3 grid gap-2 overflow-y-auto pr-1" aria-label="AR Recovery navigation">
        {navigationSections.map((section) => (
          <div key={section.label} className="grid gap-1">
            {!collapsed ? (
              <p className="px-3 pt-2 text-[10px] font-black uppercase tracking-[0.18em] text-[#FF6A2B]/80">
                {section.label}
              </p>
            ) : (
              <div className="mx-auto my-2 h-px w-8 bg-[rgba(255,106,43,.22)]" aria-hidden="true" />
            )}
            {section.items.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                onClick={onCloseMobile}
                className={({ isActive }) =>
                  `focus-ring group flex min-h-11 items-center gap-3 rounded-2xl border px-3 text-sm font-bold transition ${
                    isActive
                      ? "border-[rgba(255,106,43,.48)] bg-[rgba(255,106,43,.16)] text-[#F6F1E8] shadow-[0_18px_32px_rgba(255,106,43,.1)]"
                      : "border-transparent bg-transparent text-[#CBD5E1] hover:border-[rgba(34,224,208,.18)] hover:bg-[rgba(255,255,255,.05)] hover:text-[#F6F1E8]"
                  } ${collapsed ? "justify-center px-2" : ""}`
                }
                title={item.description}
              >
                <span className="grid size-7 shrink-0 place-items-center rounded-xl border border-[rgba(34,224,208,.14)] bg-[rgba(255,255,255,.04)] text-[#22E0D0]">
                  {navIcon(item.key)}
                </span>
                {!collapsed ? (
                  <span className="min-w-0">
                    <span className="block truncate">{arRecoverySidebarLabel(item.key)}</span>
                    <span className="block truncate text-[10px] font-black uppercase tracking-[0.12em] text-[#94A3B8]">{item.state}</span>
                  </span>
                ) : null}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
    </aside>
  );
}

type ClientExchangeCenterSectionProps = {
  workspace: ReturnType<typeof loadARRecoveryWorkspace>;
  clientExchangeFoundation: EnterpriseClientExchangeFoundationProjection;
  validationFoundation: PortfolioValidationEngineResult;
  mappingFoundation: PortfolioMappingEngineResult;
  reviewFoundation: PortfolioReviewEngineResult;
  importFoundation: PortfolioImportEngineResult;
  assignmentFoundation: PortfolioAssignmentEngineResult;
  activationFoundation: PortfolioActivationEngineResult;
  intakeWalkthrough: PortfolioPipelineWalkthroughResult;
};

const CLIENT_EXCHANGE_LIFECYCLE = [
  "Draft",
  "Uploaded",
  "Validating",
  "Validation Failed",
  "Mapping",
  "Ready for Review",
  "Approved",
  "Importing",
  "Imported",
  "Assignment Pending",
  "Activated",
  "Archived",
] as const;

function ClientExchangeCenterSection({
  workspace,
  clientExchangeFoundation,
  validationFoundation,
  mappingFoundation,
  reviewFoundation,
  importFoundation,
  assignmentFoundation,
  activationFoundation,
  intakeWalkthrough,
}: ClientExchangeCenterSectionProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Statuses");
  const [managerFilter, setManagerFilter] = useState("All Managers");
  const [collectorFilter, setCollectorFilter] = useState("All Collectors");
  const [activationFilter, setActivationFilter] = useState("All Activation States");
  const selectedPortfolio = {
    id: activationFoundation.activation.portfolioId,
    client: activationFoundation.activationSummary.client || reviewFoundation.reviewSummary.client || workspace.company.companyName,
    portfolio: activationFoundation.activationSummary.portfolio || reviewFoundation.reviewSummary.portfolioName,
    status: activationFoundation.activationSummary.activationStatus,
    validationStatus: validationFoundation.validationSummary.importReadiness,
    mappingStatus: mappingFoundation.readiness,
    importStatus: importFoundation.execution.status,
    assignmentStatus: assignmentFoundation.assignment.status,
    activationStatus: activationFoundation.activation.status,
    manager: assignmentFoundation.assignmentSummary.manager || assignmentFoundation.assignment.managerId || "Manager Pending",
    collector: assignmentFoundation.assignmentSummary.collector || assignmentFoundation.assignment.collectorId || "Collector Pending",
    uploadDate: reviewFoundation.reviewSummary.submittedAt || importFoundation.execution.startedAt,
  };
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const queueMatchesSearch = normalizedSearch.length === 0
    || [selectedPortfolio.client, selectedPortfolio.portfolio, selectedPortfolio.status, selectedPortfolio.manager, selectedPortfolio.collector]
      .some((value) => value.toLowerCase().includes(normalizedSearch));
  const queueMatchesStatus = statusFilter === "All Statuses" || selectedPortfolio.status === statusFilter || selectedPortfolio.importStatus === statusFilter;
  const queueMatchesManager = managerFilter === "All Managers" || selectedPortfolio.manager === managerFilter;
  const queueMatchesCollector = collectorFilter === "All Collectors" || selectedPortfolio.collector === collectorFilter;
  const queueMatchesActivation = activationFilter === "All Activation States" || selectedPortfolio.activationStatus === activationFilter;
  const visiblePortfolioCount = queueMatchesSearch && queueMatchesStatus && queueMatchesManager && queueMatchesCollector && queueMatchesActivation ? 1 : 0;
  const lifecycleCurrentIndex = selectedPortfolio.status === "Activated"
    ? CLIENT_EXCHANGE_LIFECYCLE.indexOf("Activated")
    : selectedPortfolio.importStatus === "Completed"
      ? CLIENT_EXCHANGE_LIFECYCLE.indexOf("Imported")
      : CLIENT_EXCHANGE_LIFECYCLE.indexOf("Ready for Review");
  const recoveryActivityEvents = intakeWalkthrough.expectedEventsVerified.filter((eventType) =>
    [
      "PortfolioCreated",
      "UploadStarted",
      "ValidationCompleted",
      "ImportStarted",
      "ImportCompleted",
      "CollectorAssigned",
      "PortfolioActivated",
    ].includes(eventType),
  );
  const managerCommandProjection = [
    ...importFoundation.projectionTargets,
    ...assignmentFoundation.projectionTargets,
    ...activationFoundation.projectionTargets,
  ].find((target) => target.target === "Manager Command");

  return (
    <div className="mx-auto grid w-full max-w-[96rem] gap-6">
      <article className="rounded-[1.75rem] border border-[#BFEAFF] bg-white p-5 shadow-[0_20px_48px_rgba(15,23,42,.06)]">
        <SectionHeading
          eyebrow="Client Exchange Center"
          title="Portfolio onboarding command center"
          description="Guide client portfolios through intake, validation, mapping, review, import, assignment, and activation using the existing enterprise portfolio engines."
        />
        <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <MiniStat label="Validation" value={validationFoundation.validationSummary.importReadiness} note="Import readiness from the validation engine." icon={<ShieldCheck className="size-5" aria-hidden="true" />} />
          <MiniStat label="Mapping" value={mappingFoundation.readiness} note="Required fields and source column coverage." icon={<ClipboardList className="size-5" aria-hidden="true" />} />
          <MiniStat label="Review" value={reviewFoundation.reviewSummary.approvalStatus} note={`${reviewFoundation.reviewSummary.importRiskLevel} import risk.`} icon={<FileText className="size-5" aria-hidden="true" />} />
          <MiniStat label="Import" value={importFoundation.execution.status} note={`${importFoundation.execution.successfulRecords} records prepared.`} icon={<FolderKanban className="size-5" aria-hidden="true" />} />
          <MiniStat label="Activation" value={activationFoundation.activationSummary.activationStatus} note={activationFoundation.activationSummary.activationResult} icon={<Bolt className="size-5" aria-hidden="true" />} />
        </div>
      </article>

      <article className="rounded-[1.75rem] border border-[#BFEAFF] bg-white p-5 shadow-[0_20px_48px_rgba(15,23,42,.06)]">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-[#BFEAFF] pb-4">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">Portfolio Queue</p>
            <h2 className="mt-1 font-display text-2xl font-bold text-[#0A2540]">Client portfolio intake queue</h2>
            <p className="mt-2 max-w-3xl text-sm leading-7 text-[#475569]">Search and filter staged portfolios without changing import, assignment, or activation behavior.</p>
          </div>
          <ClientExchangeStatusBadge status={`${visiblePortfolioCount} Visible`} />
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <label className="min-w-0 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-3">
            <span className="text-xs font-semibold text-[#334155]">Search</span>
            <div className="mt-2 flex min-w-0 items-center gap-2 rounded-xl border border-[#BFEAFF] bg-white px-3 py-2">
              <Search className="size-4 shrink-0 text-[#14B8A6]" aria-hidden="true" />
              <input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Client, portfolio, manager, collector"
                className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-[#0A2540] outline-none placeholder:text-[#94A3B8]"
              />
            </div>
          </label>
          <ClientExchangeSelect label="Status" value={statusFilter} options={["All Statuses", selectedPortfolio.status, selectedPortfolio.importStatus]} onChange={setStatusFilter} />
          <ClientExchangeSelect label="Assigned Manager" value={managerFilter} options={["All Managers", selectedPortfolio.manager]} onChange={setManagerFilter} />
          <ClientExchangeSelect label="Assigned Collector" value={collectorFilter} options={["All Collectors", selectedPortfolio.collector]} onChange={setCollectorFilter} />
          <ClientExchangeSelect label="Upload Date" value={formatDate(selectedPortfolio.uploadDate)} options={[formatDate(selectedPortfolio.uploadDate)]} onChange={() => undefined} />
          <ClientExchangeSelect label="Activation Status" value={activationFilter} options={["All Activation States", selectedPortfolio.activationStatus]} onChange={setActivationFilter} />
        </div>

        <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,0.36fr)_minmax(0,0.64fr)]">
          <div className="min-w-0 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
            {visiblePortfolioCount > 0 ? (
              <button
                type="button"
                className="w-full min-w-0 rounded-2xl border border-[#14B8A6] bg-white p-4 text-left shadow-[0_12px_28px_rgba(20,184,166,.12)] focus-ring"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[11px] font-black uppercase tracking-[0.14em] text-[#14B8A6]">{selectedPortfolio.client}</p>
                    <h3 className="mt-1 break-words font-display text-xl font-bold text-[#0A2540]">{selectedPortfolio.portfolio}</h3>
                  </div>
                  <ClientExchangeStatusBadge status={selectedPortfolio.status} />
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <ClientExchangeField label="Manager" value={selectedPortfolio.manager} />
                  <ClientExchangeField label="Collector" value={selectedPortfolio.collector} />
                  <ClientExchangeField label="Upload Date" value={formatDate(selectedPortfolio.uploadDate)} />
                  <ClientExchangeField label="Activation" value={selectedPortfolio.activationStatus} />
                </div>
              </button>
            ) : (
              <div className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                <p className="font-display text-lg font-bold text-[#0A2540]">No portfolios match the current filters.</p>
                <p className="mt-2 text-sm leading-6 text-[#475569]">Clear search or filters to return to the active intake portfolio.</p>
              </div>
            )}
          </div>

          <div className="min-w-0 rounded-2xl border border-[#BFEAFF] bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">Portfolio Details</p>
                <h3 className="mt-1 font-display text-2xl font-bold text-[#0A2540]">{selectedPortfolio.portfolio}</h3>
                <p className="mt-2 text-sm leading-7 text-[#475569]">Version 1 onboarding lifecycle from client portfolio creation through recovery readiness.</p>
              </div>
              <ClientExchangeStatusBadge status={selectedPortfolio.status} />
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              <ClientExchangeField label="Client" value={selectedPortfolio.client} />
              <ClientExchangeField label="Batch ID" value={reviewFoundation.reviewSummary.batchId} />
              <ClientExchangeField label="Correlation ID" value={intakeWalkthrough.correlationId} />
              <ClientExchangeField label="Source File" value={reviewFoundation.reviewSummary.sourceFile} />
              <ClientExchangeField label="Source Type" value={reviewFoundation.reviewSummary.sourceType} />
              <ClientExchangeField label="Total Balance" value={formatARRecoveryCurrency(reviewFoundation.reviewSummary.totalBalance)} />
            </div>
            <div className="mt-5 grid gap-2">
              {CLIENT_EXCHANGE_LIFECYCLE.map((stage, index) => (
                <div key={stage} className="flex min-w-0 items-center gap-3 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-3">
                  <span className={`flex size-7 shrink-0 items-center justify-center rounded-full border text-xs font-black ${index <= lifecycleCurrentIndex ? "border-[#14B8A6] bg-white text-[#14B8A6]" : "border-[#D7EEF8] bg-white text-[#94A3B8]"}`}>{index + 1}</span>
                  <span className="min-w-0 break-words text-sm font-semibold text-[#0A2540]">{stage}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </article>

      <div className="grid gap-6 xl:grid-cols-2">
        <ClientExchangePanel title="Validation Results" eyebrow="Validation Engine" icon={<ShieldCheck className="size-5" aria-hidden="true" />}>
          <div className="grid gap-3 sm:grid-cols-2">
            <ClientExchangeField label="Total Records" value={validationFoundation.validationSummary.totalRecords.toLocaleString()} />
            <ClientExchangeField label="Valid Records" value={validationFoundation.validationSummary.validRecords.toLocaleString()} />
            <ClientExchangeField label="Errors" value={validationFoundation.validationSummary.errorCount.toLocaleString()} />
            <ClientExchangeField label="Warnings" value={validationFoundation.validationSummary.warningCount.toLocaleString()} />
            <ClientExchangeField label="Duplicate Count" value={validationFoundation.validationSummary.duplicateCount.toLocaleString()} />
            <ClientExchangeField label="Overall Status" value={validationFoundation.validationSummary.overallValidationStatus} />
          </div>
          <ClientExchangeIssueList emptyLabel="No blocking validation issues." items={validationFoundation.validationResults.map((result) => `${result.severity}: ${result.category} - ${result.message}`)} />
        </ClientExchangePanel>

        <ClientExchangePanel title="Mapping Review" eyebrow="Mapping Engine" icon={<ClipboardList className="size-5" aria-hidden="true" />}>
          <div className="grid gap-3">
            {mappingFoundation.mappingProfile.columnMappings.map((mapping) => (
              <div key={`${mapping.sourceColumn}-${mapping.targetField}`} className="grid min-w-0 gap-2 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-3 sm:grid-cols-[minmax(0,0.45fr)_minmax(0,0.55fr)]">
                <p className="min-w-0 break-words text-sm font-semibold text-[#0A2540]">{mapping.sourceColumn}</p>
                <p className="min-w-0 break-words text-sm font-semibold text-[#14B8A6]">{mapping.targetField}</p>
              </div>
            ))}
          </div>
          <ClientExchangeIssueList emptyLabel="Required fields are mapped." items={mappingFoundation.issues.map((issue) => `${issue.severity}: ${issue.message}`)} />
        </ClientExchangePanel>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <ClientExchangePanel title="Review Summary" eyebrow="Review & Approval" icon={<FileText className="size-5" aria-hidden="true" />}>
          <div className="grid gap-3 sm:grid-cols-2">
            <ClientExchangeField label="Approval Status" value={reviewFoundation.reviewSummary.approvalStatus} />
            <ClientExchangeField label="Import Readiness" value={reviewFoundation.reviewSummary.importReadiness} />
            <ClientExchangeField label="Risk Level" value={reviewFoundation.reviewSummary.importRiskLevel} />
            <ClientExchangeField label="Submitted By" value={reviewFoundation.reviewSummary.submittedBy} />
            <ClientExchangeField label="Customers" value={reviewFoundation.reviewSummary.customerCount.toLocaleString()} />
            <ClientExchangeField label="Accounts" value={reviewFoundation.reviewSummary.accountCount.toLocaleString()} />
          </div>
        </ClientExchangePanel>

        <ClientExchangePanel title="Import Status" eyebrow="Import Engine" icon={<FolderKanban className="size-5" aria-hidden="true" />}>
          <div className="grid gap-3 sm:grid-cols-2">
            <ClientExchangeField label="Import ID" value={importFoundation.execution.importId} />
            <ClientExchangeField label="Status" value={importFoundation.execution.status} />
            <ClientExchangeField label="Processed Records" value={importFoundation.execution.processedRecords.toLocaleString()} />
            <ClientExchangeField label="Successful Records" value={importFoundation.execution.successfulRecords.toLocaleString()} />
            <ClientExchangeField label="Duplicate Records" value={importFoundation.execution.duplicateRecords.toLocaleString()} />
            <ClientExchangeField label="Rollback Available" value={importFoundation.execution.rollbackAvailable ? "Yes" : "No"} />
          </div>
        </ClientExchangePanel>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <ClientExchangePanel title="Assignment Panel" eyebrow="Portfolio Assignment" icon={<Users className="size-5" aria-hidden="true" />}>
          <div className="grid gap-3 sm:grid-cols-2">
            <ClientExchangeField label="Collector" value={assignmentFoundation.assignmentSummary.collector} />
            <ClientExchangeField label="Manager" value={assignmentFoundation.assignmentSummary.manager} />
            <ClientExchangeField label="Team" value={assignmentFoundation.assignmentSummary.team} />
            <ClientExchangeField label="Priority" value={assignmentFoundation.assignmentSummary.priority} />
            <ClientExchangeField label="Recovery Stage" value={assignmentFoundation.assignmentSummary.recoveryStage} />
            <ClientExchangeField label="Work Queue Eligibility" value={assignmentFoundation.assignmentSummary.workQueueEligibility} />
          </div>
          <ClientExchangeIssueList emptyLabel="Assignment ownership is complete." items={assignmentFoundation.validationResults.map((result) => `${result.severity}: ${result.message}`)} />
        </ClientExchangePanel>

        <ClientExchangePanel title="Activation Panel" eyebrow="Operational Activation" icon={<Bolt className="size-5" aria-hidden="true" />}>
          <div className="grid gap-3 sm:grid-cols-2">
            <ClientExchangeField label="Action" value={activationFoundation.action} />
            <ClientExchangeField label="Activation Status" value={activationFoundation.activation.status} />
            <ClientExchangeField label="Activated By" value={activationFoundation.activationSummary.activatedBy || "Activation Pending"} />
            <ClientExchangeField label="Activation Time" value={activationFoundation.activationSummary.activationTime ? formatDate(activationFoundation.activationSummary.activationTime) : "Pending"} />
            <ClientExchangeField label="Eligible Records" value={activationFoundation.activationSummary.eligibleRecordCount.toLocaleString()} />
            <ClientExchangeField label="Blocked Records" value={activationFoundation.activationSummary.blockedRecordCount.toLocaleString()} />
          </div>
          <ClientExchangeIssueList emptyLabel="No activation blockers detected." items={activationFoundation.validationResults.map((result) => `${result.severity}: ${result.message}`)} />
        </ClientExchangePanel>
      </div>

      <article className="rounded-[1.75rem] border border-[#BFEAFF] bg-white p-5 shadow-[0_20px_48px_rgba(15,23,42,.06)]">
        <div className="grid gap-6 xl:grid-cols-2">
          <div className="min-w-0">
            <SectionHeading
              eyebrow="History"
              title="Workflow timeline"
              description="Lifecycle progress is projected from the existing enterprise portfolio intake walkthrough and immutable workflow event architecture."
            />
            <div className="mt-4 grid gap-3">
              {intakeWalkthrough.stages.map((stage) => (
                <div key={stage.stage} className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="font-display text-lg font-bold text-[#0A2540]">{stage.stage}</p>
                    <ClientExchangeStatusBadge status={stage.status} />
                  </div>
                  <p className="mt-2 text-sm leading-6 text-[#475569]">{stage.detail}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="min-w-0">
            <SectionHeading
              eyebrow="Activity"
              title="Recovery Activity projections"
              description="Portfolio actions remain connected to Recovery Activity, Timeline, Manager Command, and Reporting through prepared projection targets."
            />
            <div className="mt-4 grid gap-3">
              <ClientExchangeField label="Existing Engine" value={clientExchangeFoundation.engine} />
              <ClientExchangeField label="Supported Lifecycle States" value={clientExchangeFoundation.lifecycle.length.toLocaleString()} />
              <ClientExchangeField label="Manager Command Projection" value={managerCommandProjection ? managerCommandProjection.projectionStatus : "Prepared"} />
              <ClientExchangeField label="Pipeline Status" value={intakeWalkthrough.overallStatus} />
            </div>
            <div className="mt-4 grid gap-2">
              {recoveryActivityEvents.map((eventType) => (
                <div key={eventType} className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-3 text-sm font-semibold text-[#0A2540]">
                  {eventType}
                </div>
              ))}
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}

function ClientExchangeSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  const uniqueOptions = Array.from(new Set(options));
  return (
    <label className="min-w-0 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-3">
      <span className="text-xs font-semibold text-[#334155]">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-2 w-full min-w-0 rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold text-[#0A2540] outline-none focus:border-[#14B8A6]"
      >
        {uniqueOptions.map((option) => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
    </label>
  );
}

function ClientExchangePanel({ title, eyebrow, icon, children }: { title: string; eyebrow: string; icon: ReactNode; children: ReactNode }) {
  return (
    <article className="min-w-0 rounded-[1.75rem] border border-[#BFEAFF] bg-white p-5 shadow-[0_20px_48px_rgba(15,23,42,.06)]">
      <div className="flex min-w-0 items-start gap-3 border-b border-[#BFEAFF] pb-4">
        <ARLightLineIcon icon={icon} />
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">{eyebrow}</p>
          <h2 className="mt-1 break-words font-display text-2xl font-bold text-[#0A2540]">{title}</h2>
        </div>
      </div>
      <div className="mt-4 grid min-w-0 gap-4">{children}</div>
    </article>
  );
}

function ClientExchangeField({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="min-w-0 rounded-2xl border border-[#BFEAFF] bg-white p-3">
      <p className="text-xs font-semibold text-[#475569]">{label}</p>
      <p className="mt-1 min-w-0 break-words text-sm font-bold leading-6 text-[#0A2540]" style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}>{value}</p>
    </div>
  );
}

function ClientExchangeIssueList({ items, emptyLabel }: { items: string[]; emptyLabel: string }) {
  return (
    <div className="grid gap-2">
      {(items.length > 0 ? items : [emptyLabel]).map((item) => (
        <div key={item} className="min-w-0 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-3 text-sm font-semibold leading-6 text-[#0A2540]" style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}>
          {item}
        </div>
      ))}
    </div>
  );
}

function ClientExchangeStatusBadge({ status }: { status: string }) {
  return (
    <span className="inline-flex max-w-full items-center rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-[#0F766E]">
      <span className="min-w-0 break-words">{status}</span>
    </span>
  );
}

function DashboardSection({
  workspace,
  snapshot,
}: {
  workspace: ReturnType<typeof loadARRecoveryWorkspace>;
  snapshot: ReturnType<typeof buildARRecoveryDashboardSnapshot>;
}) {
  const kpis = [
    { label: "Total Open AR", value: formatARRecoveryCurrency(snapshot.totalOpenAR), note: "Total outstanding receivables." },
    { label: "Current", value: formatARRecoveryCurrency(snapshot.currentBalance), note: "Balances still in terms." },
    { label: "1-30 Days", value: formatARRecoveryCurrency(snapshot.balance1to30), note: "First follow-up window." },
    { label: "31-60 Days", value: formatARRecoveryCurrency(snapshot.balance31to60), note: "Escalation prep zone." },
    { label: "61-90 Days", value: formatARRecoveryCurrency(snapshot.balance61to90), note: "Urgent recovery bucket." },
    { label: "90+ Days", value: formatARRecoveryCurrency(snapshot.balance90Plus), note: "Highest priority backlog." },
    { label: "Recovery Dollars", value: formatARRecoveryCurrency(snapshot.recoveryDollars), note: "Collected across collector work." },
    { label: "Recovery Rate", value: formatARRecoveryPercent(snapshot.recoveryRate), note: "Recovered vs total open AR." },
    { label: "Promise-to-Pay", value: formatARRecoveryCurrency(snapshot.promiseToPayAmount), note: "Outstanding promises to track." },
    { label: "Accounts Assigned", value: snapshot.accountsAssigned.toString(), note: "Current collector workload." },
    { label: "Accounts Requiring Action", value: snapshot.accountsRequiringAction.toString(), note: "Needs attention today." },
  ];

  const actionItems = [
    { label: "Follow Ups Due Today", value: snapshot.followUpsDueToday, href: "/admin/ar-recovery/smart-tickler", tone: "critical" as const },
    { label: "Overdue Ticklers", value: snapshot.overdueTicklers, href: "/admin/ar-recovery/smart-tickler", tone: "critical" as const },
    { label: "Broken Promises", value: snapshot.brokenPromises, href: "/admin/ar-recovery/promise-to-pay", tone: "high" as const },
    { label: "High Risk Accounts", value: snapshot.highRiskAccounts, href: "/admin/ar-recovery/customer-accounts", tone: "high" as const },
    { label: "Escalations Needed", value: snapshot.escalationsNeeded, href: "/admin/ar-recovery/manager-command-center", tone: "high" as const },
    { label: "New Accounts Imported", value: snapshot.newAccountsImported, href: "/admin/ar-recovery/customer-accounts", tone: "medium" as const },
    { label: "Collection Letters Pending", value: snapshot.collectionLettersPending, href: "/admin/ar-recovery/collection-letters", tone: "medium" as const },
    { label: "Statements Pending", value: snapshot.statementsPending, href: "/admin/ar-recovery/statements", tone: "medium" as const },
  ];

  return (
    <div className="mt-8 grid gap-8">
      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {kpis.map((metric) => (
          <article key={metric.label} className="rounded-[1.75rem] border border-[#BFEAFF] bg-white p-5 shadow-[0_20px_48px_rgba(15,23,42,.06)]">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF6A2B]">{metric.label}</p>
            <p className="mt-2 font-display text-3xl font-bold text-[#0A2540]">{metric.value}</p>
            <p className="mt-3 text-sm font-semibold leading-7 text-[#0A2540]">{metric.note}</p>
          </article>
        ))}
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.1fr_.9fr]">
        <article className="rounded-[1.75rem] border border-[#BFEAFF] bg-white p-5 shadow-[0_20px_48px_rgba(15,23,42,.06)]">
          <SectionHeading
            eyebrow="Action Center"
            title="Daily recovery execution"
            description="The command center surfaces the work collectors must touch first."
          />
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {actionItems.map((item) => (
              <Link
                key={item.label}
                to={item.href}
                className={`flex items-center justify-between gap-4 rounded-2xl border px-4 py-4 transition hover:-translate-y-0.5 ${
                  item.tone === "critical"
                    ? "border-[#FF6B35]/30 bg-[#FFF4EE]"
                    : item.tone === "high"
                      ? "border-[#FF6A2B]/30 bg-[#FFF8E8]"
                      : "border-[#BFEAFF] bg-[#F4FBFF]"
                }`}
              >
                <span className="grid gap-1">
                  <span className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">{item.label}</span>
                  <span className="font-display text-3xl font-bold text-[#0A2540]">{item.value}</span>
                </span>
                <ArrowRight className="size-5 text-[#14B8A6]" aria-hidden="true" />
              </Link>
            ))}
          </div>
        </article>

        <article className="rounded-[1.75rem] border border-[#BFEAFF] bg-white p-5 shadow-[0_20px_48px_rgba(15,23,42,.06)]">
          <SectionHeading
            eyebrow="Operational Snapshot"
            title="Current tenant context"
            description="The active company stays visible without adding presentation content."
          />
          <div className="mt-5 grid gap-3">
            <ScopeRow label="Company" value={workspace.company.companyName} />
            <ScopeRow label="Brand" value={workspace.branding.displayName} />
            <ScopeRow label="Loaded accounts" value={`${workspace.accounts.length}`} />
            <ScopeRow label="Open balance" value={formatARRecoveryCurrency(workspace.accounts.reduce((sum, account) => sum + account.openBalanceCents, 0))} />
            <ScopeRow label="Promises" value={`${workspace.promises.length}`} />
            <ScopeRow label="Ticklers" value={`${workspace.ticklers.length}`} />
          </div>
          <p className="mt-4 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4 text-sm font-semibold leading-7 text-[#0A2540]">
            Company-scoped recovery operations remain isolated to the active tenant.
          </p>
        </article>
      </section>

      <section className="grid gap-6 xl:grid-cols-[.95fr_1.05fr]">
        <article className="ar-ia-panel rounded-[1.75rem] border p-5">
          <SectionHeading
            eyebrow="Smart Tickler Snapshot"
            title="Follow-up queue"
            description="Priority-based reminders and escalation timing for every account."
          />
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <MiniStat label="Due Today" value={snapshot.ticklerTodayCount} note="Requires action now." />
            <MiniStat label="Overdue" value={snapshot.ticklerOverdueCount} note="Missed reminders in the queue." />
            <MiniStat label="Upcoming" value={snapshot.ticklerUpcomingCount} note="Planned reminders ahead." />
            <MiniStat label="Critical" value={snapshot.ticklerCriticalCount} note="Escalation-ready ticklers." />
            <MiniStat label="High Priority" value={snapshot.ticklerHighCount} note="High-risk work needing attention." />
          </div>
        </article>

        <article className="ar-ia-panel rounded-[1.75rem] border p-5">
          <SectionHeading
            eyebrow="Promise-to-Pay Snapshot"
            title="Promise management"
            description="Promises are tracked, measured, and exposed through the recovery workflow."
          />
          <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            <MiniStat label="Due Today" value={snapshot.promisesDueTodayCount} note="Promise date is today." />
            <MiniStat label="Upcoming" value={snapshot.promisesUpcomingCount} note="Promise date still ahead." />
            <MiniStat label="Broken" value={snapshot.promisesBrokenCount} note="Missed promise commitments." />
            <MiniStat label="Partial" value={snapshot.promisesPartialCount} note="Partial payments recorded." />
            <MiniStat label="Completed" value={snapshot.promisesCompletedCount} note="Kept promises closed out." />
          </div>
        </article>
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.1fr_.9fr]">
        <article className="ar-ia-panel rounded-[1.75rem] border p-5">
          <SectionHeading
            eyebrow="Recovery Activity Feed"
            title="Chronological activity"
            description="Calls, emails, notes, statements, promises, escalations, and payment updates."
          />
          <div className="ar-ia-scroll mt-5 grid gap-3">
            {workspace.activity.map((item) => (
              <article key={item.id} className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="grid gap-1">
                    <span className="text-xs font-medium text-[#14B8A6]">{item.kind}</span>
                    <h3 className="font-display text-2xl font-bold text-[#0A2540]">{item.title}</h3>
                  </div>
                  <span className="rounded-full border border-[#BFEAFF] bg-white px-3 py-1 text-[11px] font-semibold text-[#14B8A6]">
                    {formatDate(item.createdAt)}
                  </span>
                </div>
                <p className="mt-2 text-sm font-normal leading-7 text-[#475569]">{item.detail}</p>
                <p className="mt-3 text-xs font-medium text-[#334155]">
                  {item.customerName} • {item.accountId}
                </p>
              </article>
            ))}
          </div>
        </article>

        <div className="grid gap-6">
          <article className="ar-ia-panel rounded-[1.75rem] border p-5">
            <SectionHeading
              eyebrow="Recovery Analytics"
              title="Aging distribution and trend"
              description="Aging buckets are expressed as a command-center style chart instead of a traditional accounting table."
            />
            <div className="mt-5 grid gap-4">
              {workspace.agingBuckets.map((bucket) => {
                const max = Math.max(...workspace.agingBuckets.map((item) => item.balanceCents), 1);
                const width = Math.max(18, Math.round((bucket.balanceCents / max) * 100));
                return (
                  <div key={bucket.label} className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs font-medium text-[#334155]">{bucket.label}</p>
                        <p className="mt-1 font-display text-2xl font-bold text-[#0A2540]">{formatARRecoveryCurrency(bucket.balanceCents)}</p>
                      </div>
                      <span className="rounded-full border border-[#BFEAFF] bg-white px-3 py-1 text-[11px] font-semibold text-[#14B8A6]">
                        {bucket.accountCount} accounts
                      </span>
                    </div>
                    <div className="mt-3 h-2 rounded-full bg-[#E8FFFC]">
                      <div className="h-2 rounded-full bg-[linear-gradient(90deg,#FF6A2B,#22E0D0)]" style={{ width: `${width}%` }} />
                    </div>
                    <p className="mt-3 text-sm font-normal leading-7 text-[#475569]">{bucket.trendNote}</p>
                  </div>
                );
              })}
            </div>
          </article>

          <article className="ar-ia-panel rounded-[1.75rem] border p-5">
            <SectionHeading
              eyebrow="Collector Performance"
              title="Team impact"
              description="Collector productivity is tracked in a way that can be reused by future reporting and white-label dashboards."
            />
            <div className="ar-ia-scroll mt-5 grid gap-3">
              {workspace.collectors.map((collector) => (
                <div key={collector.id} className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-medium text-[#14B8A6]">{collector.collectorName}</p>
                      <h3 className="mt-1 font-display text-2xl font-bold text-[#0A2540]">Collector performance</h3>
                    </div>
                    <Users className="size-5 text-[#FF6A2B]" aria-hidden="true" />
                  </div>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <MiniStat label="Assigned" value={collector.accountsAssigned} note="Open accounts in the queue." />
                    <MiniStat label="Worked" value={collector.accountsWorked} note="Accounts touched this cycle." />
                    <MiniStat label="Calls" value={collector.callsCompleted} note="Completed outbound calls." />
                    <MiniStat label="Emails" value={collector.emailsSent} note="Recovery emails sent." />
                    <MiniStat label="Recovery" value={formatARRecoveryCurrency(collector.recoveryCents)} note="Recovered dollars." />
                    <MiniStat label="Promise %" value={formatARRecoveryPercent(collector.promiseSuccessRate / 100)} note="Promise success rate." />
                  </div>
                  <p className="mt-3 text-sm font-normal leading-7 text-[#475569]">Average resolution time: {collector.averageResolutionDays} days</p>
                </div>
              ))}
            </div>
          </article>
        </div>
      </section>
    </div>
  );
}

type ARRecoveryCallOutcome = "Reached AP Contact" | "Left Voicemail" | "No Answer" | "Wrong Number" | "Dispute Discussed" | "Payment Discussed" | "Escalation Required";

type ARRecoveryCallLogRecord = {
  id: string;
  companyId: string;
  accountId: string;
  customerName: string;
  customerNumber: string;
  collector: string;
  outcome: ARRecoveryCallOutcome;
  durationMinutes: string;
  notes: string;
  nextAction: string;
  createdAt: string;
};

type ARRecoveryCallFormState = {
  outcome: ARRecoveryCallOutcome | "";
  durationMinutes: string;
  notes: string;
  nextAction: string;
};

type ARRecoveryBusinessNoteType = "Customer Conversation" | "Payment Discussion" | "Promise Discussion" | "Dispute" | "Documentation" | "Internal" | "Manager" | "System";
type ARRecoveryLegacyNoteType = "Collection Note" | "Customer Note" | "Internal Note" | "Escalation Note" | "Promise Note" | "Follow-Up Note" | "System Note";
type ARRecoveryNoteType = ARRecoveryBusinessNoteType | ARRecoveryLegacyNoteType;
type ARRecoveryNoteVisibility = "internal" | "manager_only" | "client_visible";

type ARRecoveryNoteReadinessItem = {
  label: string;
  ready: boolean;
  detail: string;
};

type ARRecoveryNoteCompletionSummary = {
  category: string;
  visibility: string;
  customer: string;
  createdAt: string;
} | null;

type ARRecoveryNoteLogRecord = {
  id: string;
  companyId: string;
  accountId: string;
  customerName: string;
  customerNumber: string;
  collector: string;
  noteType: ARRecoveryNoteType;
  visibility: ARRecoveryNoteVisibility;
  content: string;
  outcome: string;
  nextAction: string;
  createdAt: string;
};

type ARRecoveryNoteFormState = {
  noteType: ARRecoveryNoteType | "";
  visibility: ARRecoveryNoteVisibility | "";
  content: string;
  outcome: string;
  nextAction: string;
};

type ARRecoveryCommunicationType =
  | "General"
  | "Manager Review"
  | "Escalation"
  | "Customer Research"
  | "Documentation Request"
  | "Payment Verification"
  | "Internal Question";

type ARRecoveryCommunicationImportance = "Normal" | "High";

type ARRecoveryCommunicationRecord = {
  id: string;
  conversationId: string;
  companyId: string;
  accountId: string;
  customerName: string;
  customerNumber: string;
  sender: string;
  recipient: string;
  subject: string;
  messageType: ARRecoveryCommunicationType;
  message: string;
  importance: ARRecoveryCommunicationImportance;
  createdAt: string;
  parentMessageId?: string;
};

type ARRecoveryCommunicationDraft = {
  conversationId?: string;
  parentMessageId?: string;
  recipient: string;
  subject: string;
  messageType: ARRecoveryCommunicationType;
  message: string;
  importance: ARRecoveryCommunicationImportance;
};

type ARRecoveryCommunicationFormState = {
  recipient: string;
  subject: string;
  messageType: ARRecoveryCommunicationType;
  message: string;
  importance: ARRecoveryCommunicationImportance;
};

type ARRecoveryEmailWorkspaceTemplate =
  | "Courtesy Reminder"
  | "Past Due Reminder"
  | "Promise Reminder"
  | "Final Reminder"
  | "Payment Confirmation"
  | "Statement Notification"
  | "Letter Follow-up"
  | "Documentation Request"
  | "General Follow-up";

type ARRecoveryEmailWorkspacePriority = "Normal" | "High";

type ARRecoveryEmailWorkspaceFormState = {
  recipient: string;
  cc: string;
  bcc: string;
  subject: string;
  template: ARRecoveryEmailWorkspaceTemplate | "";
  body: string;
  attachmentIds: string[];
  priority: ARRecoveryEmailWorkspacePriority;
  internalNotes: string;
};

type ARRecoveryPaymentMethodKey = "Check" | "ACH" | "Wire" | "Credit Card" | "Cash" | "Other";
type ARRecoveryPaymentSourceKey = "Customer" | "Client" | "Lockbox" | "Payment Portal" | "Bank Notice" | "Other";

type ARRecoveryPaymentWorkspaceFormState = {
  amount: string;
  paymentDate: string;
  paymentMethod: ARRecoveryPaymentMethodKey | "";
  referenceNumber: string;
  paymentSource: ARRecoveryPaymentSourceKey | "";
  appliedInvoiceNumbers: string[];
  internalNotes: string;
  overpaymentConfirmed: boolean;
};

type ARRecoveryPaymentDeliverySummary = {
  amountCents: number;
  appliedInvoiceNumbers: string[];
  remainingBalanceCents: number;
  recordedAt: string;
  paymentMethod: string;
  referenceNumber: string;
  paymentReference: string;
  unappliedAmountCents: number;
};

type ARRecoveryTaskCategory =
  | "Payment Verification"
  | "Research Account"
  | "Verify POD"
  | "Request Documentation"
  | "Manager Review"
  | "Credit Review"
  | "Dispute Assistance"
  | "Customer Follow-Up"
  | "Internal Review"
  | "Custom";

type ARRecoveryTaskPriority = "Low" | "Normal" | "High" | "Critical";
type ARRecoveryTaskStatus = "Open" | "In Progress" | "Waiting" | "Completed" | "Cancelled";

type ARRecoveryTaskCommentRecord = {
  id: string;
  author: string;
  comment: string;
  createdAt: string;
};

type ARRecoveryTaskAssignmentRecord = {
  id: string;
  companyId: string;
  accountId: string;
  customerName: string;
  customerNumber: string;
  taskTitle: string;
  category: ARRecoveryTaskCategory;
  assignedBy: string;
  assignedTo: string;
  priority: ARRecoveryTaskPriority;
  dueDate: string;
  description: string;
  relatedInvoice: string;
  relatedPromise: string;
  relatedLetter: string;
  relatedStatement: string;
  status: ARRecoveryTaskStatus;
  createdAt: string;
  updatedAt: string;
  completedBy: string;
  completedAt: string;
  completionNotes: string;
  outcome: string;
  nextAction: string;
  comments: ARRecoveryTaskCommentRecord[];
};

type ARRecoveryTaskAssignmentFormState = {
  taskTitle: string;
  category: ARRecoveryTaskCategory | "";
  assignedTo: string;
  priority: ARRecoveryTaskPriority;
  dueDate: string;
  description: string;
  relatedInvoice: string;
  relatedPromise: string;
  relatedLetter: string;
  relatedStatement: string;
};

type ARRecoveryTaskCompletionFormState = {
  completionNotes: string;
  outcome: string;
  nextAction: string;
};

type ARRecoveryPromiseWorkflowRecord = ReturnType<typeof loadARRecoveryWorkspace>["promises"][number] & {
  expectedPaymentMethod: string;
  outcome: string;
  nextActionType: ARRecoveryPromiseNextActionType | "";
  nextActionDueDate: string;
  nextActionDueTime: string;
  nextActionPriority: ARRecoveryTaskPriority;
  nextActionAssignedTo: string;
  reminderRequired: boolean;
  reminderNotes: string;
  completionNotes: string;
  brokenReason: string;
  progressNotes: Array<{
    id: string;
    author: string;
    note: string;
    createdAt: string;
  }>;
};

type ARRecoveryPromiseFormType = "Single Payment" | "Installment Plan" | "Partial Payment" | "Settlement" | "Other";
type ARRecoveryPromiseNextActionType =
  | "Call Customer"
  | "Send Letter"
  | "Send Statement"
  | "Verify Payment"
  | "Verify Promise"
  | "Follow-Up"
  | "Manager Review"
  | "Request Documents"
  | "Assign Task"
  | "Research Account"
  | "Escalate Account"
  | "Other";

type ARRecoveryPromiseFormState = {
  promiseType: ARRecoveryPromiseFormType | "";
  amount: string;
  promiseDate: string;
  expectedPaymentMethod: string;
  relatedInvoices: string[];
  notes: string;
  outcome: string;
  nextActionType: ARRecoveryPromiseNextActionType | "";
  nextActionDueDate: string;
  nextActionDueTime: string;
  nextActionPriority: ARRecoveryTaskPriority;
  nextActionAssignedTo: string;
  reminderRequired: boolean;
  reminderNotes: string;
};

type ARRecoveryPromiseWorkflowMode = "create" | "edit";
type ARRecoveryPromiseWorkspaceTab = "overview" | "invoices" | "timeline" | "notes" | "communications" | "statements" | "payments";
type ARRecoveryTicklerWorkspaceTab = "overview" | "invoices" | "timeline" | "notes" | "communications" | "promises" | "statements" | "payments";
type ARRecoveryLetterSuccessSummary = { letterNumber: string; letterType: string; customer: string; deliveryStatus: string } | null;
type ARRecoveryStatementCompletionSummary = {
  statementNumber: string;
  recipient: string;
  deliveryMethod: string;
  sentAt: string;
  deliveryStatus: string;
} | null;
type ARRecoveryStatementFailureSummary = {
  reason: string;
  deliveryMethod: string;
} | null;
type ARRecoveryEscalationReason =
  | "Broken Promise"
  | "Customer Dispute"
  | "High Balance"
  | "Collector Approval Required"
  | "Client Request"
  | "Documentation Required"
  | "Legal Review"
  | "Bankruptcy"
  | "Fraud Review"
  | "Other";
type ARRecoveryEscalationDecision =
  | "Approve"
  | "Return to Collector"
  | "Reject"
  | "Reassign Collector"
  | "Request Documentation"
  | "Escalate Further"
  | "Close Escalation";
type ARRecoveryEscalationDecisionFormState = {
  reason: ARRecoveryEscalationReason | "";
  decision: ARRecoveryEscalationDecision | "";
  decisionNotes: string;
  assignedCollectorId: string;
  assignedCollector: string;
  documentationRequest: string;
  evidenceReviewed: boolean;
};
type ARRecoveryEscalationCompletionSummary = {
  decision: ARRecoveryEscalationDecision;
  manager: string;
  completedAt: string;
  escalationId: string;
} | null;
type ARRecoveryWorkspacePanelMode =
  | "call"
  | "email"
  | "promise"
  | "statement"
  | "letter"
  | "payment"
  | "tickler"
  | "note"
  | "escalate"
  | "account-info"
  | "history"
  | "audit"
  | null;
type ARRecoveryEnterpriseStudioMode = "letter" | "statement" | null;
type ARRecoveryStudioDesignerSection = "template" | "recipient" | "branding" | "merge-fields" | "formatting" | "layout" | "sections" | "attachments" | "delivery" | "advanced";
type ARRecoveryStudioDocumentTab = "preview" | "merge-fields" | "variables" | "history" | "versions" | "audit" | "invoices" | "aging" | "attachments";
type ARRecoveryPromiseCompletionFormState = {
  completionNotes: string;
  outcome: string;
};
type ARRecoveryPromiseBrokenFormState = {
  reason: string;
  notes: string;
  outcome: string;
  nextAction: string;
};

const PROMISE_CENTER_ENTERPRISE_FIELDS = ["Promise Number / ID", "Customer", "Account", "Invoice(s)", "Portfolio", "Client", "Collector", "Promise Amount", "Remaining Balance", "Promise Date", "Due Date", "Promise Status", "Risk Level", "Source", "Created Date", "Created By", "Last Updated", "Notes", "Recovery Activity Reference"] as const;
const PROMISE_CENTER_LIFECYCLE_STATUSES = ["Pending", "Due Today", "Upcoming", "Paid", "Broken", "Partially Paid", "Completed", "Cancelled", "Expired", "Escalated", "Disputed", "Write-off Review"] as const;
const PROMISE_CENTER_FUTURE_OPERATIONS = ["Create Promise", "Edit Promise", "Cancel Promise", "Complete Promise", "Convert to Payment", "Reschedule", "Assign Collector", "Escalate", "Add Notes", "View Timeline", "Open Customer", "Open Account"] as const;
const PROMISE_CENTER_FILTER_MODEL = ["Collector", "Client", "Portfolio", "Status", "Date Range", "Due Today", "Overdue", "Amount", "Risk", "Customer", "Account", "Search"] as const;
const PROMISE_CENTER_REPORTING_MODEL = ["Total Promises", "Promise Value", "Broken Rate", "Success Rate", "Collected Amount", "Outstanding Promise Value", "Collector Performance", "Client Performance", "Portfolio Performance"] as const;
const PROMISE_CENTER_ACTIVITY_EVENTS = ["Created", "Updated", "Rescheduled", "Broken", "Paid", "Cancelled", "Completed", "Escalated", "Assigned", "Notes Added"] as const;

type ARRecoveryFollowUpType =
  | "Customer Call"
  | "Promise Verification"
  | "Payment Verification"
  | "Statement Follow-Up"
  | "Letter Follow-Up"
  | "Manager Review"
  | "Documentation Follow-Up"
  | "Research"
  | "Internal Follow-Up"
  | "Other";

type ARRecoveryTicklerWorkflowRecord = ReturnType<typeof loadARRecoveryWorkspace>["ticklers"][number] & {
  dueTime: string;
  reminderRequired: boolean;
  reminderNotes: string;
  relatedInvoice: string;
  relatedPromise: string;
  relatedTask: string;
  notes: string;
  outcome: string;
  nextAction: string;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
  completedBy: string;
  completedAt: string;
  completionNotes: string;
  cancelledBy: string;
  cancelledAt: string;
  rescheduleHistory: Array<{
    id: string;
    previousDueDate: string;
    newDueDate: string;
    reason: string;
    updatedBy: string;
    updatedAt: string;
  }>;
  progressNotes: Array<{
    id: string;
    author: string;
    note: string;
    createdAt: string;
  }>;
};

type ARRecoveryTicklerFormState = {
  followUpType: ARRecoveryFollowUpType | "";
  dueDate: string;
  dueTime: string;
  priority: ARRecoveryTaskPriority;
  assignedTo: string;
  reminderRequired: boolean;
  reminderNotes: string;
  relatedInvoice: string;
  relatedPromise: string;
  relatedTask: string;
  notes: string;
  outcome: string;
  nextAction: string;
};

type ARRecoveryTicklerWorkflowMode = "create" | "edit";
type ARRecoveryTicklerCompletionFormState = {
  completionNotes: string;
  outcome: string;
};
type ARRecoveryTicklerRescheduleFormState = {
  newDueDate: string;
  newDueTime: string;
  reason: string;
};

type ARRecoveryLetterType =
  | "Courtesy Reminder"
  | "Past Due Reminder"
  | "Demand Letter"
  | "Final Demand"
  | "Promise Confirmation"
  | "Settlement Offer"
  | "Broken Promise"
  | "Collection Follow-Up"
  | "Custom";

type ARRecoveryLetterWorkflowRecord = ReturnType<typeof loadARRecoveryWorkspace>["collectionLetters"][number] & {
  letterType: ARRecoveryLetterType;
  recipient: string;
  relatedInvoices: string[];
  relatedPromise: string;
  relatedTask: string;
  additionalNotes: string;
  generatedBy: string;
  generatedAt: string;
  previewBody: string;
  signature: string;
  deliveryProvider?: string;
  providerMessageId?: string | null;
  providerStatus?: number | null;
  deliveryError?: string | null;
  sentRecipient?: string;
};

type ARRecoveryLetterFormState = {
  letterType: ARRecoveryLetterType | "";
  recipient: string;
  relatedInvoices: string[];
  relatedPromise: string;
  relatedTask: string;
  additionalNotes: string;
};

type ARRecoveryStatementType =
  | "Current Statement"
  | "Past Due Statement"
  | "Final Statement"
  | "Promise Balance Statement"
  | "Settlement Statement"
  | "Custom Statement";

type ARRecoveryStatementDeliveryOption = "Email" | "Download PDF" | "Print";

type ARRecoveryStatementWorkflowRecord = ReturnType<typeof loadARRecoveryWorkspace>["statements"][number] & {
  statementType: ARRecoveryStatementType;
  relatedInvoices: string[];
  relatedPromise: string;
  relatedTask: string;
  statementNotes: string;
  generatedBy: string;
  generatedAt: string;
  deliveryOption: ARRecoveryStatementDeliveryOption;
  previewBody: string;
  deliveryProvider?: string;
  providerMessageId?: string | null;
  providerStatus?: number | null;
  deliveryError?: string | null;
  sentRecipient?: string;
};

type ARRecoveryDocumentDeliveryAction = "Generated" | "Previewed" | "Printed" | "Downloaded" | "Emailed" | "Failed";

type ARRecoveryDocumentDeliveryStatus = "pending" | "sent" | "failed" | "not_configured";

type ARRecoveryDocumentDeliveryActionRecord = {
  action: ARRecoveryDocumentDeliveryAction;
  at: string;
  by: string;
  detail: string;
};

type ARRecoveryDocumentDeliveryRecord = {
  id: string;
  companyId: string;
  family: ARRecoveryDocumentTemplateFamily;
  documentType: string;
  templateId: string;
  templateName: string;
  subjectLine: string;
  customerId: string;
  customerName: string;
  customerNumber: string;
  accountId: string;
  accountName: string;
  accountNumber: string;
  relatedPromiseId: string;
  relatedPromise: string;
  relatedTaskId: string;
  relatedTask: string;
  generatedBy: string;
  generatedAt: string;
  createdAt: string;
  updatedAt: string;
  recipientName: string;
  recipientEmail: string;
  previewHtml: string;
  previewText: string;
  deliveryStatus: ARRecoveryDocumentDeliveryStatus;
  deliveryProvider?: string;
  providerMessageId?: string | null;
  providerStatus?: number | null;
  deliveryError?: string | null;
  previewCount: number;
  printCount: number;
  downloadCount: number;
  emailCount: number;
  actions: ARRecoveryDocumentDeliveryActionRecord[];
};

type ARRecoveryEmailDeliveryResult = {
  ok: boolean;
  status: "sent" | "failed" | "not_configured";
  provider: string;
  message: string;
  providerStatus: number;
  providerMessageId: string | null;
  providerError: string | null;
  sentAt?: string;
};

type ARRecoveryEmailAttachmentPayload = {
  name: string;
  contentType: string;
  contentBase64: string;
};

type ARRecoveryEmailAddressField = "recipient" | "cc" | "bcc";

type ARRecoveryEmailValidationState = Record<ARRecoveryEmailAddressField, {
  invalidAddresses: string[];
  message: string;
}>;

type ARRecoveryEmailDeliverySummary = {
  recipient: string;
  subject: string;
  attachments: string[];
  sentAt: string;
};

type ARRecoveryEmailDocumentStatus = "ready" | "attached" | "sent";

type ARRecoveryEnterpriseDocumentKind =
  | "certified_letter_pdf"
  | "certified_statement_pdf"
  | "account_invoice_details_pdf"
  | "customer_account_summary_pdf";

type ARRecoveryEmailWorkspaceAttachment = {
  id: string;
  label: string;
  type: string;
  note: string;
  documentKind: ARRecoveryEnterpriseDocumentKind;
  deliveryId?: string;
};

type ARRecoveryEmailDocumentPackageItem = {
  attachment: ARRecoveryEmailWorkspaceAttachment;
  payload: ARRecoveryEmailAttachmentPayload | null;
  displayName: string;
  group: "Letters" | "Statements" | "Invoice Details" | "Supporting Documents";
  documentType: string;
  status: ARRecoveryEmailDocumentStatus;
  statusLabel: "READY" | "ATTACHED" | "SENT";
  statusDetail: string;
  generatedLabel: string;
  formatLabel: string;
  deliveryLabel: string;
};

type ARRecoveryEmailDocumentPreviewState = {
  name: string;
  documentType: string;
  statusLabel: string;
  generatedLabel: string;
  formatLabel: string;
  sourceUrl: string | null;
  message: string;
};

type ARRecoveryStatementFormState = {
  statementType: ARRecoveryStatementType | "";
  statementDate: string;
  relatedInvoices: string[];
  relatedPromise: string;
  relatedTask: string;
  statementNotes: string;
  deliveryOption: ARRecoveryStatementDeliveryOption;
};

type ARRecoveryDocumentRequestType =
  | "Proof of Delivery"
  | "Signed BOL"
  | "Invoice Copy"
  | "Purchase Order"
  | "Remittance Advice"
  | "Credit Memo"
  | "Contract"
  | "Tax Exemption Certificate"
  | "Payment Backup"
  | "Other";

type ARRecoveryDocumentRequestStatus =
  | "Requested"
  | "Waiting"
  | "Received"
  | "Under Review"
  | "Approved"
  | "Rejected"
  | "Cancelled";

type ARRecoveryDocumentRequestRecord = {
  id: string;
  companyId: string;
  accountId: string;
  customerName: string;
  customerNumber: string;
  requestType: ARRecoveryDocumentRequestType;
  requestedFrom: string;
  dueDate: string;
  priority: ARRecoveryTaskPriority;
  relatedInvoice: string;
  relatedTask: string;
  requestNotes: string;
  status: ARRecoveryDocumentRequestStatus;
  requestedBy: string;
  requestedAt: string;
  updatedBy: string;
  updatedAt: string;
  receivedBy: string;
  receivedAt: string;
  fileName: string;
  receivedNotes: string;
  reviewNotes: string;
  rejectionReason: string;
  additionalInfoNotes: string;
};

type ARRecoveryDocumentRequestFormState = {
  requestType: ARRecoveryDocumentRequestType | "";
  requestedFrom: string;
  dueDate: string;
  priority: ARRecoveryTaskPriority;
  relatedInvoice: string;
  relatedTask: string;
  requestNotes: string;
};

type ARRecoveryDocumentFulfillmentFormState = {
  fileName: string;
  receivedNotes: string;
  reviewNotes: string;
  rejectionReason: string;
  additionalInfoNotes: string;
};

type AccountDocumentRecord = {
  id: string;
  title: string;
  documentType: string;
  status: string;
  notes: string;
  requestId?: string;
  requestType?: ARRecoveryDocumentRequestType;
  requestedFrom?: string;
  dueDate?: string;
  priority?: ARRecoveryTaskPriority;
  relatedInvoice?: string;
  relatedTask?: string;
  requestNotes?: string;
  requestedBy?: string;
  requestedAt?: string;
  updatedBy?: string;
  updatedAt?: string;
  receivedBy?: string;
  receivedAt?: string;
  fileName?: string;
  receivedNotes?: string;
  reviewNotes?: string;
  rejectionReason?: string;
  additionalInfoNotes?: string;
};

type ARRecoveryDocumentTemplateFamily = "letter" | "statement";
type ARRecoveryDocumentTemplateStatus = "Draft" | "Published" | "Archived";
type ARRecoveryDocumentTemplateRole =
  | "platform_admin"
  | "kreatethedate_admin"
  | "admin"
  | "manager"
  | "supervisor"
  | "team_member"
  | "collector"
  | "viewer"
  | "read_only";

type ARRecoveryDocumentTemplateVersionRecord = {
  id: string;
  label: string;
  note: string;
  bodyHtml: string;
  createdAt: string;
  createdBy: string;
  modifiedAt?: string;
  modifiedBy?: string;
  publishedAt?: string | null;
  publishedBy?: string;
  status: "Draft" | "Published";
};

type ARRecoveryDocumentTemplateRecord = {
  id: string;
  companyId: string;
  family: ARRecoveryDocumentTemplateFamily;
  templateName: string;
  summary: string;
  subjectLine: string;
  bodyHtml: string;
  brandMode: "company" | "client";
  status: ARRecoveryDocumentTemplateStatus;
  allowedRoles: ARRecoveryDocumentTemplateRole[];
  assignedAccountIds: string[];
  assignedCustomerIds: string[];
  mergeFields: string[];
  previewNote: string;
  versionHistory: ARRecoveryDocumentTemplateVersionRecord[];
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
};

type ARRecoveryDocumentTemplateDraftState = {
  templateName: string;
  summary: string;
  subjectLine: string;
  brandMode: "company" | "client";
  status: ARRecoveryDocumentTemplateStatus;
  allowedRoles: ARRecoveryDocumentTemplateRole[];
  assignedAccountIds: string[];
  assignedCustomerIds: string[];
};

type ARRecoveryDocumentTemplateField = {
  token: string;
  label: string;
  note: string;
};

type ARRecoveryDocumentTemplateFieldGroup = {
  label: string;
  note: string;
  fields: ARRecoveryDocumentTemplateField[];
};

const AR_RECOVERY_DOCUMENT_TEMPLATE_STORAGE_KEY = "arRecoveryDocumentTemplates";

const AR_RECOVERY_DOCUMENT_TEMPLATE_ROLE_OPTIONS: Array<{ key: ARRecoveryDocumentTemplateRole; label: string; note: string }> = [
  { key: "platform_admin", label: "Platform Admin", note: "Full template control and publishing access." },
  { key: "kreatethedate_admin", label: "Brand Administrator", note: "Brand-scoped write access for recovery operations." },
  { key: "admin", label: "Admin", note: "Can edit and publish document templates." },
  { key: "manager", label: "Manager", note: "Can edit templates and assign clients." },
  { key: "supervisor", label: "Supervisor", note: "Can review, edit, and publish." },
  { key: "team_member", label: "Team Member", note: "Can edit working drafts." },
  { key: "collector", label: "Collector", note: "Can edit collection letters and follow-up drafts." },
  { key: "viewer", label: "Viewer", note: "Read-only review access." },
  { key: "read_only", label: "Read Only", note: "Read-only review access." },
];

const AR_RECOVERY_DOCUMENT_TEMPLATE_FIELD_GROUPS: ARRecoveryDocumentTemplateFieldGroup[] = [
  {
    label: "Client",
    note: "Business profile merge fields.",
    fields: [
      { token: "{{ClientName}}", label: "Client Name", note: "Legal or operating business name." },
      { token: "{{ClientDBA}}", label: "DBA", note: "Doing-business-as name for the client." },
      { token: "{{ClientAddress}}", label: "Address", note: "Mailing address line for the client." },
      { token: "{{ClientPhone}}", label: "Phone", note: "Primary client phone number." },
      { token: "{{ClientEmail}}", label: "Email", note: "Primary client email address." },
      { token: "{{ClientWebsite}}", label: "Website", note: "Client website URL." },
      { token: "{{ClientContactName}}", label: "Contact Name", note: "Primary recovery contact name." },
    ],
  },
  {
    label: "Customer",
    note: "Customer profile merge fields.",
    fields: [
      { token: "{{CustomerName}}", label: "Customer Name", note: "Customer or account holder name." },
      { token: "{{CustomerNumber}}", label: "Customer Number", note: "Client-facing customer identifier." },
      { token: "{{PrimaryContactName}}", label: "Primary Contact", note: "Primary AP or controller contact." },
      { token: "{{PrimaryContactEmail}}", label: "Primary Email", note: "Primary contact email address." },
      { token: "{{CommunicationPreference}}", label: "Contact Preference", note: "Preferred follow-up method." },
    ],
  },
  {
    label: "Account",
    note: "Account-level merge fields.",
    fields: [
      { token: "{{AccountName}}", label: "Account Name", note: "Account display name." },
      { token: "{{AccountNumber}}", label: "Account Number", note: "Account identifier used in the portfolio." },
      { token: "{{CurrentBalance}}", label: "Current Balance", note: "Current open balance for the account." },
      { token: "{{AgingBucket}}", label: "Aging Bucket", note: "Current aging bucket or profile." },
      { token: "{{AssignedCollector}}", label: "Assigned Collector", note: "Collector assigned to the account." },
    ],
  },
  {
    label: "Invoice",
    note: "Invoice and due-date merge fields.",
    fields: [
      { token: "{{InvoiceNumber}}", label: "Invoice Number", note: "Invoice or reference number." },
      { token: "{{InvoiceDate}}", label: "Invoice Date", note: "Original invoice date." },
      { token: "{{DueDate}}", label: "Due Date", note: "Statement or request due date." },
      { token: "{{OpenInvoiceCount}}", label: "Open Invoice Count", note: "Count of outstanding invoices." },
      { token: "{{OpenBalance}}", label: "Open Balance", note: "Outstanding balance for the account." },
    ],
  },
  {
    label: "Recovery",
    note: "Recovery workflow merge fields.",
    fields: [
      { token: "{{CollectorName}}", label: "Collector", note: "Assigned collector or account owner." },
      { token: "{{ManagerName}}", label: "Manager", note: "Manager or escalation contact." },
      { token: "{{PromiseStatus}}", label: "Promise Status", note: "Current promise or recovery status." },
      { token: "{{NextAction}}", label: "Next Action", note: "Next operational step." },
      { token: "{{PaymentInstructions}}", label: "Payment Instructions", note: "Payment guidance or remittance note." },
      { token: "{{PortalUrl}}", label: "Portal URL", note: "Client portal destination." },
      { token: "{{StatementTitle}}", label: "Statement Title", note: "Statement display title." },
    ],
  },
];

type ARRecoveryAccountEditFormState = {
  customerName: string;
  customerNumber: string;
  accountNumber: string;
  clientCompany: string;
  assignedCollector: string;
  assignedManager: string;
  accountStatus: ReturnType<typeof loadARRecoveryWorkspace>["accounts"][number]["recoveryStatus"];
  currentBalance: string;
  agingBucket: string;
  promiseStatus: string;
  primaryContact: string;
  contactTitle: string;
  phone: string;
  email: string;
  preferredContactMethod: string;
  additionalContacts: string;
  collectorOwner: string;
  holdStatus: string;
  disputeStatus: string;
  nextFollowUpDate: string;
  recoveryInstructions: string;
};

type ARRecoveryEditableAccountExtras = {
  accountNumber?: string;
  clientCompany?: string;
  assignedManager?: string;
  promiseStatus?: string;
  contactTitle?: string;
  preferredContactMethod?: string;
  additionalContacts?: string;
  collectorOwner?: string;
  holdStatus?: string;
  disputeStatus?: string;
  nextFollowUpDate?: string;
  recoveryInstructions?: string;
};

type ARRecoveryAccountEditChange = {
  field: string;
  previousValue: string;
  newValue: string;
};

type ARRecoveryAccountEditRecord = {
  id: string;
  companyId: string;
  accountId: string;
  customerName: string;
  customerNumber: string;
  actorName: string;
  actorRole: string;
  createdAt: string;
  changes: ARRecoveryAccountEditChange[];
  values: ARRecoveryAccountEditFormState;
  assignment?: {
    assignedCollectorId?: string;
    assignedCollectorName?: string;
    assignedAt?: string;
    assignedBy?: string;
  };
};

const AR_RECOVERY_CALL_LOG_STORAGE_KEY = "arRecoveryCallLogs";
const AR_RECOVERY_NOTE_LOG_STORAGE_KEY = "arRecoveryNoteLogs";
const AR_RECOVERY_COMMUNICATION_STORAGE_KEY = "arRecoveryCommunications";
const AR_RECOVERY_TASK_ASSIGNMENT_STORAGE_KEY = "arRecoveryTaskAssignments";
const AR_RECOVERY_PROMISE_WORKFLOW_STORAGE_KEY = "arRecoveryPromiseWorkflows";
const AR_RECOVERY_TICKLER_WORKFLOW_STORAGE_KEY = "arRecoveryTicklerWorkflows";
const AR_RECOVERY_LETTER_WORKFLOW_STORAGE_KEY = "arRecoveryLetterWorkflows";
const AR_RECOVERY_STATEMENT_WORKFLOW_STORAGE_KEY = "arRecoveryStatementWorkflows";
const AR_RECOVERY_DOCUMENT_DELIVERY_STORAGE_KEY = "arRecoveryDocumentDeliveries";
const AR_RECOVERY_DOCUMENT_REQUEST_STORAGE_KEY = "arRecoveryDocumentRequests";
const AR_RECOVERY_ACCOUNT_EDIT_STORAGE_KEY = "arRecoveryAccountEdits";
const AR_RECOVERY_ACCOUNT_EDIT_OPERATIONAL_STATE_KEY = "ar_recovery_account_edits";

const emptyCallForm: ARRecoveryCallFormState = {
  outcome: "",
  durationMinutes: "",
  notes: "",
  nextAction: "",
};

const emptyNoteForm: ARRecoveryNoteFormState = {
  noteType: "",
  visibility: "",
  content: "",
  outcome: "",
  nextAction: "",
};

const AR_RECOVERY_NOTE_TYPE_OPTIONS: Array<{ value: ARRecoveryBusinessNoteType; description: string }> = [
  { value: "Customer Conversation", description: "Customer contact, account status, or conversation context." },
  { value: "Payment Discussion", description: "Payment timing, payment questions, or remittance context." },
  { value: "Promise Discussion", description: "Promise-to-pay context, status, or follow-up detail." },
  { value: "Dispute", description: "Customer dispute, short-pay reason, or research item." },
  { value: "Documentation", description: "Invoice, statement, letter, or supporting-document context." },
  { value: "Internal", description: "Collector knowledge for internal recovery use." },
  { value: "Manager", description: "Manager review, escalation, or approval context." },
  { value: "System", description: "System-generated operational note context." },
];

const AR_RECOVERY_NOTE_VISIBILITY_OPTIONS: Array<{ value: ARRecoveryNoteVisibility; label: string; description: string }> = [
  { value: "internal", label: "Internal", description: "Visible to internal recovery users only." },
  { value: "manager_only", label: "Manager Only", description: "Visible to managers and authorized oversight users." },
  { value: "client_visible", label: "Customer Visible", description: "Safe for customer-facing account context." },
];

const emptyCommunicationForm: ARRecoveryCommunicationFormState = {
  recipient: "",
  subject: "",
  messageType: "General",
  message: "",
  importance: "Normal",
};

const AR_RECOVERY_EMAIL_WORKSPACE_TEMPLATES: Array<{
  name: ARRecoveryEmailWorkspaceTemplate;
  subject: string;
  body: string;
  messageType: ARRecoveryCommunicationType;
}> = [
  {
    name: "Courtesy Reminder",
    subject: "Account reminder for {{CustomerName}}",
    body: "We are reaching out as a courtesy regarding the current balance on your account. Please let us know if payment has already been scheduled or if your team needs additional documentation from us.",
    messageType: "General",
  },
  {
    name: "Past Due Reminder",
    subject: "Past due balance for {{CustomerName}}",
    body: "We are following up because a past due balance remains open. Please advise when payment can be expected, or let us know if there is a dispute or documentation request we should review with your team.",
    messageType: "Payment Verification",
  },
  {
    name: "Promise Reminder",
    subject: "Promise-to-pay reminder for {{CustomerName}}",
    body: "We are checking in regarding the current payment arrangement. Please confirm the scheduled payment timing or contact our team if the arrangement needs to be reviewed.",
    messageType: "General",
  },
  {
    name: "Final Reminder",
    subject: "Final reminder for {{CustomerName}}",
    body: "We need your prompt assistance resolving the open balance. Please contact our office to confirm payment timing or provide the information needed to review any account discrepancy.",
    messageType: "Escalation",
  },
  {
    name: "Payment Confirmation",
    subject: "Payment confirmation for {{CustomerName}}",
    body: "Thank you for the payment update. We have noted the payment activity and will reflect it after verification is complete.",
    messageType: "Payment Verification",
  },
  {
    name: "Statement Notification",
    subject: "Statement available for {{CustomerName}}",
    body: "A current statement is available for your review. Please review the statement and contact our team with any questions about the listed balance or invoices.",
    messageType: "Documentation Request",
  },
  {
    name: "Letter Follow-up",
    subject: "Follow-up on recent account letter for {{CustomerName}}",
    body: "We're following up regarding the recent correspondence we sent concerning your account. Please contact our team to discuss payment timing, documentation needs, or any dispute details.",
    messageType: "General",
  },
  {
    name: "Documentation Request",
    subject: "Documentation request for {{CustomerName}}",
    body: "Please provide the requested documentation so our team can complete the account review. If the documentation has already been sent, please confirm the delivery details.",
    messageType: "Documentation Request",
  },
  {
    name: "General Follow-up",
    subject: "Account follow-up for {{CustomerName}}",
    body: "We are following up to keep the account moving forward. Please contact our team with payment timing, dispute details, or any additional information we should review.",
    messageType: "General",
  },
];

const emptyEmailWorkspaceForm: ARRecoveryEmailWorkspaceFormState = {
  recipient: "",
  cc: "",
  bcc: "",
  subject: "",
  template: "",
  body: "",
  attachmentIds: [],
  priority: "Normal",
  internalNotes: "",
};

const AR_RECOVERY_PAYMENT_METHOD_OPTIONS: Array<{ key: ARRecoveryPaymentMethodKey; label: string; code: string; description: string }> = [
  { key: "Check", label: "Check", code: "CHK", description: "Paper check or remittance check." },
  { key: "ACH", label: "ACH", code: "ACH", description: "Electronic bank transfer." },
  { key: "Wire", label: "Wire", code: "WIR", description: "Wire transfer confirmation." },
  { key: "Credit Card", label: "Credit Card (Recorded)", code: "CC", description: "Card payment reference only." },
  { key: "Cash", label: "Cash", code: "CSH", description: "Cash receipt entry." },
  { key: "Other", label: "Other", code: "OTH", description: "Other verified payment method." },
];

const AR_RECOVERY_PAYMENT_SOURCE_OPTIONS: Array<{ key: ARRecoveryPaymentSourceKey; label: string }> = [
  { key: "Customer", label: "Customer" },
  { key: "Client", label: "Client" },
  { key: "Lockbox", label: "Lockbox" },
  { key: "Payment Portal", label: "Payment Portal" },
  { key: "Bank Notice", label: "Bank Notice" },
  { key: "Other", label: "Other" },
];

const emptyPaymentWorkspaceForm: ARRecoveryPaymentWorkspaceFormState = {
  amount: "",
  paymentDate: "",
  paymentMethod: "",
  referenceNumber: "",
  paymentSource: "Customer",
  appliedInvoiceNumbers: [],
  internalNotes: "",
  overpaymentConfirmed: false,
};

const emptyTaskAssignmentForm: ARRecoveryTaskAssignmentFormState = {
  taskTitle: "",
  category: "",
  assignedTo: "",
  priority: "Normal",
  dueDate: "",
  description: "",
  relatedInvoice: "",
  relatedPromise: "",
  relatedLetter: "",
  relatedStatement: "",
};

const emptyTaskCompletionForm: ARRecoveryTaskCompletionFormState = {
  completionNotes: "",
  outcome: "",
  nextAction: "",
};

const emptyPromiseForm: ARRecoveryPromiseFormState = {
  promiseType: "",
  amount: "",
  promiseDate: "",
  expectedPaymentMethod: "",
  relatedInvoices: [],
  notes: "",
  outcome: "",
  nextActionType: "",
  nextActionDueDate: "",
  nextActionDueTime: "",
  nextActionPriority: "Normal",
  nextActionAssignedTo: "",
  reminderRequired: false,
  reminderNotes: "",
};

const emptyPromiseCompletionForm: ARRecoveryPromiseCompletionFormState = {
  completionNotes: "",
  outcome: "",
};

const emptyPromiseBrokenForm: ARRecoveryPromiseBrokenFormState = {
  reason: "",
  notes: "",
  outcome: "",
  nextAction: "",
};

const emptyTicklerForm: ARRecoveryTicklerFormState = {
  followUpType: "",
  dueDate: "",
  dueTime: "",
  priority: "Normal",
  assignedTo: "",
  reminderRequired: false,
  reminderNotes: "",
  relatedInvoice: "",
  relatedPromise: "",
  relatedTask: "",
  notes: "",
  outcome: "",
  nextAction: "",
};

const emptyTicklerCompletionForm: ARRecoveryTicklerCompletionFormState = {
  completionNotes: "",
  outcome: "",
};

const emptyTicklerRescheduleForm: ARRecoveryTicklerRescheduleFormState = {
  newDueDate: "",
  newDueTime: "",
  reason: "",
};

const emptyLetterForm: ARRecoveryLetterFormState = {
  letterType: "",
  recipient: "",
  relatedInvoices: [],
  relatedPromise: "",
  relatedTask: "",
  additionalNotes: "",
};

const emptyStatementForm: ARRecoveryStatementFormState = {
  statementType: "",
  statementDate: "",
  relatedInvoices: [],
  relatedPromise: "",
  relatedTask: "",
  statementNotes: "",
  deliveryOption: "Email",
};

const AR_RECOVERY_ESCALATION_REASONS: ARRecoveryEscalationReason[] = [
  "Broken Promise",
  "Customer Dispute",
  "High Balance",
  "Collector Approval Required",
  "Client Request",
  "Documentation Required",
  "Legal Review",
  "Bankruptcy",
  "Fraud Review",
  "Other",
];

const AR_RECOVERY_ESCALATION_DECISIONS: ARRecoveryEscalationDecision[] = [
  "Approve",
  "Return to Collector",
  "Reject",
  "Reassign Collector",
  "Request Documentation",
  "Escalate Further",
  "Close Escalation",
];

const emptyEscalationDecisionForm: ARRecoveryEscalationDecisionFormState = {
  reason: "",
  decision: "",
  decisionNotes: "",
  assignedCollectorId: "",
  assignedCollector: "",
  documentationRequest: "",
  evidenceReviewed: false,
};

const emptyDocumentRequestForm: ARRecoveryDocumentRequestFormState = {
  requestType: "",
  requestedFrom: "",
  dueDate: "",
  priority: "Normal",
  relatedInvoice: "",
  relatedTask: "",
  requestNotes: "",
};

const emptyDocumentFulfillmentForm: ARRecoveryDocumentFulfillmentFormState = {
  fileName: "",
  receivedNotes: "",
  reviewNotes: "",
  rejectionReason: "",
  additionalInfoNotes: "",
};

const emptyAccountEditForm: ARRecoveryAccountEditFormState = {
  customerName: "",
  customerNumber: "",
  accountNumber: "",
  clientCompany: "",
  assignedCollector: "",
  assignedManager: "",
  accountStatus: "Current",
  currentBalance: "",
  agingBucket: "",
  promiseStatus: "",
  primaryContact: "",
  contactTitle: "",
  phone: "",
  email: "",
  preferredContactMethod: "",
  additionalContacts: "",
  collectorOwner: "",
  holdStatus: "",
  disputeStatus: "",
  nextFollowUpDate: "",
  recoveryInstructions: "",
};

const ACCOUNT_STATUS_OPTIONS: ARRecoveryAccountEditFormState["accountStatus"][] = ["Current", "At Risk", "Escalated", "Payment Plan", "Resolved"];
const CONTACT_METHOD_OPTIONS = ["Email + Phone", "Email Only", "Phone Only", "Letter Required", "Not Captured"];
const HOLD_STATUS_OPTIONS = ["No Hold", "On Hold", "Manager Hold", "Legal Hold", "Not Captured"];
const DISPUTE_STATUS_OPTIONS = ["No Dispute", "Disputed", "Under Review", "Resolved", "Not Captured"];

const DOCUMENT_REQUEST_TYPE_OPTIONS: ARRecoveryDocumentRequestType[] = [
  "Proof of Delivery",
  "Signed BOL",
  "Invoice Copy",
  "Purchase Order",
  "Remittance Advice",
  "Credit Memo",
  "Contract",
  "Tax Exemption Certificate",
  "Payment Backup",
  "Other",
];

function loadStoredARRecoveryCallLogs(): ARRecoveryCallLogRecord[] {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(AR_RECOVERY_CALL_LOG_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isARRecoveryCallLogRecord) : [];
  } catch {
    return [];
  }
}

function saveStoredARRecoveryCallLogs(records: ARRecoveryCallLogRecord[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(AR_RECOVERY_CALL_LOG_STORAGE_KEY, JSON.stringify(records));
}

function loadProjectedARRecoveryCallLogs(): ARRecoveryCallLogRecord[] {
  const projectedCallLogs = projectCallEventsToCallLogs(loadStoredARWorkflowEvents())
    .filter(isARRecoveryCallLogRecord);
  const legacyCallLogs = loadStoredARRecoveryCallLogs();
  const projectedIds = new Set(projectedCallLogs.map((call) => call.id));
  return [...projectedCallLogs, ...legacyCallLogs.filter((call) => !projectedIds.has(call.id))]
    .sort((a, b) => getDateTime(b.createdAt) - getDateTime(a.createdAt));
}

function loadStoredARRecoveryNoteLogs(): ARRecoveryNoteLogRecord[] {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(AR_RECOVERY_NOTE_LOG_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isARRecoveryNoteLogRecord) : [];
  } catch {
    return [];
  }
}

function saveStoredARRecoveryNoteLogs(records: ARRecoveryNoteLogRecord[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(AR_RECOVERY_NOTE_LOG_STORAGE_KEY, JSON.stringify(records));
}

function loadProjectedARRecoveryNoteLogs(): ARRecoveryNoteLogRecord[] {
  const projectedNoteLogs = projectNoteEventsToNoteLogs(loadStoredARWorkflowEvents())
    .filter(isARRecoveryNoteLogRecord);
  const legacyNoteLogs = loadStoredARRecoveryNoteLogs();
  const projectedIds = new Set(projectedNoteLogs.map((note) => note.id));
  return [...projectedNoteLogs, ...legacyNoteLogs.filter((note) => !projectedIds.has(note.id))]
    .sort((a, b) => getDateTime(b.createdAt) - getDateTime(a.createdAt));
}

function loadStoredARRecoveryCommunications(): ARRecoveryCommunicationRecord[] {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(AR_RECOVERY_COMMUNICATION_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isARRecoveryCommunicationRecord) : [];
  } catch {
    return [];
  }
}

function saveStoredARRecoveryCommunications(records: ARRecoveryCommunicationRecord[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(AR_RECOVERY_COMMUNICATION_STORAGE_KEY, JSON.stringify(records));
}

function loadStoredARRecoveryTaskAssignments(): ARRecoveryTaskAssignmentRecord[] {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(AR_RECOVERY_TASK_ASSIGNMENT_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isARRecoveryTaskAssignmentRecord) : [];
  } catch {
    return [];
  }
}

function saveStoredARRecoveryTaskAssignments(records: ARRecoveryTaskAssignmentRecord[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(AR_RECOVERY_TASK_ASSIGNMENT_STORAGE_KEY, JSON.stringify(records));
}

function loadStoredARRecoveryPromiseWorkflows(): ARRecoveryPromiseWorkflowRecord[] {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(AR_RECOVERY_PROMISE_WORKFLOW_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isARRecoveryPromiseWorkflowRecord) : [];
  } catch {
    return [];
  }
}

function saveStoredARRecoveryPromiseWorkflows(records: ARRecoveryPromiseWorkflowRecord[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(AR_RECOVERY_PROMISE_WORKFLOW_STORAGE_KEY, JSON.stringify(records));
}

function loadProjectedARRecoveryPromiseWorkflows(): ARRecoveryPromiseWorkflowRecord[] {
  const projectedPromiseWorkflows = projectPromiseEventsToPromiseWorkflows(loadStoredARWorkflowEvents())
    .map((promise) => promise as unknown)
    .filter(isARRecoveryPromiseWorkflowRecord);
  const legacyPromiseWorkflows = loadStoredARRecoveryPromiseWorkflows();
  const projectedIds = new Set(projectedPromiseWorkflows.map((promise) => promise.id));
  return [...projectedPromiseWorkflows, ...legacyPromiseWorkflows.filter((promise) => !projectedIds.has(promise.id))]
    .sort((a, b) => getDateTime(b.updatedAt || b.createdAt) - getDateTime(a.updatedAt || a.createdAt));
}

function loadStoredARRecoveryTicklerWorkflows(): ARRecoveryTicklerWorkflowRecord[] {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(AR_RECOVERY_TICKLER_WORKFLOW_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isARRecoveryTicklerWorkflowRecord) : [];
  } catch {
    return [];
  }
}

function saveStoredARRecoveryTicklerWorkflows(records: ARRecoveryTicklerWorkflowRecord[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(AR_RECOVERY_TICKLER_WORKFLOW_STORAGE_KEY, JSON.stringify(records));
}

function loadProjectedARRecoveryTicklerWorkflows(): ARRecoveryTicklerWorkflowRecord[] {
  const projectedTicklerWorkflows = projectTicklerEventsToTicklerWorkflows(loadStoredARWorkflowEvents())
    .map((tickler) => tickler as unknown)
    .filter(isARRecoveryTicklerWorkflowRecord);
  const legacyTicklerWorkflows = loadStoredARRecoveryTicklerWorkflows();
  const projectedIds = new Set(projectedTicklerWorkflows.map((tickler) => tickler.id));
  return [...projectedTicklerWorkflows, ...legacyTicklerWorkflows.filter((tickler) => !projectedIds.has(tickler.id))]
    .sort((a, b) => getDateTime(a.dueDate) - getDateTime(b.dueDate));
}

function loadStoredARRecoveryLetterWorkflows(): ARRecoveryLetterWorkflowRecord[] {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(AR_RECOVERY_LETTER_WORKFLOW_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isARRecoveryLetterWorkflowRecord) : [];
  } catch {
    return [];
  }
}

function saveStoredARRecoveryLetterWorkflows(records: ARRecoveryLetterWorkflowRecord[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(AR_RECOVERY_LETTER_WORKFLOW_STORAGE_KEY, JSON.stringify(records));
}

function loadProjectedARRecoveryLetterWorkflows(): ARRecoveryLetterWorkflowRecord[] {
  const projectedLetterWorkflows = projectDocumentEventsToDocumentWorkflows(loadStoredARWorkflowEvents(), "letter")
    .map((letter) => letter as unknown)
    .filter(isARRecoveryLetterWorkflowRecord);
  const legacyLetterWorkflows = loadStoredARRecoveryLetterWorkflows();
  const projectedIds = new Set(projectedLetterWorkflows.map((letter) => letter.id));
  return [...projectedLetterWorkflows, ...legacyLetterWorkflows.filter((letter) => !projectedIds.has(letter.id))]
    .sort((a, b) => getDateTime(b.updatedAt || b.createdAt) - getDateTime(a.updatedAt || a.createdAt));
}

function loadStoredARRecoveryStatementWorkflows(): ARRecoveryStatementWorkflowRecord[] {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(AR_RECOVERY_STATEMENT_WORKFLOW_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isARRecoveryStatementWorkflowRecord) : [];
  } catch {
    return [];
  }
}

function saveStoredARRecoveryStatementWorkflows(records: ARRecoveryStatementWorkflowRecord[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(AR_RECOVERY_STATEMENT_WORKFLOW_STORAGE_KEY, JSON.stringify(records));
}

function loadProjectedARRecoveryStatementWorkflows(): ARRecoveryStatementWorkflowRecord[] {
  const projectedStatementWorkflows = projectDocumentEventsToDocumentWorkflows(loadStoredARWorkflowEvents(), "statement")
    .map((statement) => statement as unknown)
    .filter(isARRecoveryStatementWorkflowRecord);
  const legacyStatementWorkflows = loadStoredARRecoveryStatementWorkflows();
  const projectedIds = new Set(projectedStatementWorkflows.map((statement) => statement.id));
  return [...projectedStatementWorkflows, ...legacyStatementWorkflows.filter((statement) => !projectedIds.has(statement.id))]
    .sort((a, b) => getDateTime(b.updatedAt || b.createdAt) - getDateTime(a.updatedAt || a.createdAt));
}

function loadProjectedARRecoveryEscalationWorkflows(): ARWorkflowEscalationProjection[] {
  return projectEscalationEventsToEscalationWorkflows(loadStoredARWorkflowEvents());
}

function loadProjectedARRecoveryPaymentWorkflows(): ARWorkflowPaymentProjection[] {
  return projectPaymentEventsToPaymentWorkflows(loadStoredARWorkflowEvents());
}

function loadProjectedARRecoveryNotifications(): ARWorkflowNotificationProjection[] {
  return projectWorkflowEventsToNotificationRecords(loadStoredARWorkflowEvents());
}

function loadProjectedARRecoveryReportingAnalytics(): ARWorkflowReportingProjection {
  return projectWorkflowEventsToReportingAnalytics(loadStoredARWorkflowEvents());
}

function loadProjectedEnterpriseClientExchangeFoundation(): EnterpriseClientExchangeFoundationProjection {
  return projectWorkflowEventsToEnterpriseClientExchange(loadStoredARWorkflowEvents());
}

function loadPortfolioValidationEngineFoundation(companyId: string): PortfolioValidationEngineResult {
  const now = new Date().toISOString();
  return PortfolioValidationEngine.validate({
    batch: {
      batchId: "portfolio-validation-foundation",
      tenantId: companyId,
      correlationId: "portfolio-validation-foundation",
      clientId: "foundation",
      portfolioId: "foundation",
      portfolioName: "Portfolio Validation Engine Foundation",
      sourceType: "Manual",
      sourceFileName: "Foundation",
      submittedBy: "Enterprise Client Exchange",
      submittedAt: now,
      recordCount: 0,
      status: "Draft",
      validationSummary: {
        totalRecords: 0,
        validRecords: 0,
        errorCount: 0,
        warningCount: 0,
        informationCount: 0,
        duplicateCount: 0,
        customerCount: 0,
        accountCount: 0,
        invoiceCount: 0,
        importReadiness: "Ready",
        overallValidationStatus: "Passed",
        isImportReady: true,
      },
      approvalStatus: "Pending",
    },
    stagingRecords: [],
  });
}

function loadPortfolioMappingEngineFoundation(companyId: string): PortfolioMappingEngineResult {
  return PortfolioMappingEngine.map({
    tenantId: companyId,
    clientId: "foundation",
    profileName: "Portfolio Mapping Engine Foundation",
    sourceFormat: "Manual",
    sourceColumns: ["Customer Name", "Account Number", "Invoice Number", "Balance", "Due Date"],
    createdBy: "Enterprise Client Exchange",
  });
}

function loadPortfolioReviewEngineFoundation(companyId: string): PortfolioReviewEngineResult {
  const validation = loadPortfolioValidationEngineFoundation(companyId);
  const mapping = loadPortfolioMappingEngineFoundation(companyId);
  return PortfolioReviewEngine.review({
    batch: {
      batchId: "portfolio-review-foundation",
      tenantId: companyId,
      correlationId: "portfolio-review-foundation",
      clientId: "foundation",
      portfolioId: "foundation",
      portfolioName: "Portfolio Review Engine Foundation",
      sourceType: "Manual",
      sourceFileName: "Foundation",
      submittedBy: "Enterprise Client Exchange",
      submittedAt: new Date().toISOString(),
      recordCount: 0,
      status: "Ready For Review",
      validationSummary: validation.validationSummary,
      mappingProfileId: mapping.mappingProfile.mappingProfileId,
      approvalStatus: "Pending",
    },
    validation,
    mapping,
    clientName: "Enterprise Client Exchange",
  });
}

function loadPortfolioImportEngineFoundation(companyId: string): PortfolioImportEngineResult {
  const validation = loadPortfolioValidationEngineFoundation(companyId);
  const mapping = loadPortfolioMappingEngineFoundation(companyId);
  const now = new Date().toISOString();
  const batch = {
    batchId: "portfolio-import-foundation",
    tenantId: companyId,
    correlationId: "portfolio-import-foundation",
    clientId: "foundation",
    portfolioId: "foundation",
    portfolioName: "Portfolio Import Engine Foundation",
    sourceType: "Manual" as const,
    sourceFileName: "Foundation",
    submittedBy: "Enterprise Client Exchange",
    submittedAt: now,
    recordCount: 0,
    status: "Approved" as const,
    validationSummary: validation.validationSummary,
    mappingProfileId: mapping.mappingProfile.mappingProfileId,
    approvalStatus: "Approved" as const,
  };
  const review = PortfolioReviewEngine.review({
    batch,
    validation,
    mapping,
    approvalRecord: {
      approvalId: "portfolio-import-foundation-approval",
      batchId: batch.batchId,
      tenantId: companyId,
      correlationId: batch.correlationId,
      clientId: batch.clientId,
      portfolioId: batch.portfolioId,
      approvedBy: "Enterprise Client Exchange",
      approvedAt: now,
      approvalStatus: "Approved",
      riskLevel: "Low",
    },
    clientName: "Enterprise Client Exchange",
  });
  return PortfolioImportEngine.execute({
    batch,
    review,
    actor: {
      actorId: "enterprise-client-exchange",
      actorName: "Enterprise Client Exchange",
      actorEmail: "",
      actorRole: "System",
      permissionLevel: "manager_override",
    },
    stagingRecords: [],
  });
}

function loadPortfolioAssignmentEngineFoundation(companyId: string): PortfolioAssignmentEngineResult {
  const validation = loadPortfolioValidationEngineFoundation(companyId);
  const mapping = loadPortfolioMappingEngineFoundation(companyId);
  const importResult = loadPortfolioImportEngineFoundation(companyId);
  const review = PortfolioReviewEngine.review({
    batch: {
      batchId: importResult.execution.batchId,
      tenantId: companyId,
      correlationId: importResult.execution.correlationId,
      clientId: importResult.execution.clientId,
      portfolioId: importResult.execution.portfolioId,
      portfolioName: "Portfolio Assignment Engine Foundation",
      sourceType: "Manual",
      sourceFileName: "Foundation",
      submittedBy: "Enterprise Client Exchange",
      submittedAt: importResult.execution.startedAt,
      recordCount: importResult.execution.processedRecords,
      status: "Completed",
      validationSummary: validation.validationSummary,
      mappingProfileId: mapping.mappingProfile.mappingProfileId,
      approvalStatus: "Approved",
    },
    validation,
    mapping,
    approvalRecord: {
      approvalId: "portfolio-assignment-foundation-approval",
      batchId: importResult.execution.batchId,
      tenantId: companyId,
      correlationId: importResult.execution.correlationId,
      clientId: importResult.execution.clientId,
      portfolioId: importResult.execution.portfolioId,
      approvedBy: "Enterprise Client Exchange",
      approvedAt: importResult.execution.startedAt,
      approvalStatus: "Approved",
      riskLevel: "Low",
    },
    clientName: "Enterprise Client Exchange",
  });
  return PortfolioAssignmentEngine.assign({
    tenantId: companyId,
    clientId: importResult.execution.clientId,
    portfolioId: importResult.execution.portfolioId,
    correlationId: importResult.execution.correlationId,
    collectorId: "foundation-collector",
    managerId: "foundation-manager",
    teamId: "foundation-team",
    priority: "Standard",
    recoveryStage: "Portfolio Intake",
    assignedBy: "Enterprise Client Exchange",
    importResult,
    validation,
    mapping,
    review,
  });
}

function loadPortfolioActivationEngineFoundation(companyId: string): PortfolioActivationEngineResult {
  const validation = loadPortfolioValidationEngineFoundation(companyId);
  const mapping = loadPortfolioMappingEngineFoundation(companyId);
  const importResult = loadPortfolioImportEngineFoundation(companyId);
  const assignmentResult = loadPortfolioAssignmentEngineFoundation(companyId);
  const review = PortfolioReviewEngine.review({
    batch: {
      batchId: importResult.execution.batchId,
      tenantId: companyId,
      correlationId: importResult.execution.correlationId,
      clientId: importResult.execution.clientId,
      portfolioId: importResult.execution.portfolioId,
      portfolioName: "Portfolio Activation Engine Foundation",
      sourceType: "Manual",
      sourceFileName: "Foundation",
      submittedBy: "Enterprise Client Exchange",
      submittedAt: importResult.execution.startedAt,
      recordCount: importResult.execution.processedRecords,
      status: "Completed",
      validationSummary: validation.validationSummary,
      mappingProfileId: mapping.mappingProfile.mappingProfileId,
      approvalStatus: "Approved",
    },
    validation,
    mapping,
    approvalRecord: {
      approvalId: "portfolio-activation-foundation-approval",
      batchId: importResult.execution.batchId,
      tenantId: companyId,
      correlationId: importResult.execution.correlationId,
      clientId: importResult.execution.clientId,
      portfolioId: importResult.execution.portfolioId,
      approvedBy: "Enterprise Client Exchange",
      approvedAt: importResult.execution.startedAt,
      approvalStatus: "Approved",
      riskLevel: "Low",
    },
    clientName: "Enterprise Client Exchange",
  });
  return PortfolioActivationEngine.execute({
    action: "Portfolio.Activate",
    actor: {
      actorId: "enterprise-client-exchange",
      actorName: "Enterprise Client Exchange",
      actorEmail: "",
      actorRole: "System",
      permissionLevel: "manager_override",
    },
    validation,
    mapping,
    review,
    importResult,
    assignmentResult,
  });
}

function loadEnterprisePortfolioIntakeWalkthrough(companyId: string): PortfolioPipelineWalkthroughResult {
  return runEnterprisePortfolioIntakeWalkthrough(companyId);
}

function loadStoredARRecoveryDocumentDeliveries(): ARRecoveryDocumentDeliveryRecord[] {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(AR_RECOVERY_DOCUMENT_DELIVERY_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isARRecoveryDocumentDeliveryRecord) : [];
  } catch {
    return [];
  }
}

function saveStoredARRecoveryDocumentDeliveries(records: ARRecoveryDocumentDeliveryRecord[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(AR_RECOVERY_DOCUMENT_DELIVERY_STORAGE_KEY, JSON.stringify(records));
}

function loadStoredARRecoveryDocumentRequests(): ARRecoveryDocumentRequestRecord[] {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(AR_RECOVERY_DOCUMENT_REQUEST_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isARRecoveryDocumentRequestRecord) : [];
  } catch {
    return [];
  }
}

function saveStoredARRecoveryDocumentRequests(records: ARRecoveryDocumentRequestRecord[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(AR_RECOVERY_DOCUMENT_REQUEST_STORAGE_KEY, JSON.stringify(records));
}

function isARRecoveryDocumentDeliveryRecord(value: unknown): value is ARRecoveryDocumentDeliveryRecord {
  if (!value || typeof value !== "object") return false;
  const record = value as Partial<ARRecoveryDocumentDeliveryRecord>;
  return typeof record.id === "string"
    && typeof record.companyId === "string"
    && (record.family === "letter" || record.family === "statement")
    && typeof record.documentType === "string"
    && typeof record.templateId === "string"
    && typeof record.templateName === "string"
    && typeof record.subjectLine === "string"
    && typeof record.customerId === "string"
    && typeof record.customerName === "string"
    && typeof record.customerNumber === "string"
    && typeof record.accountId === "string"
    && typeof record.accountName === "string"
    && typeof record.accountNumber === "string"
    && typeof record.generatedBy === "string"
    && typeof record.generatedAt === "string"
    && typeof record.createdAt === "string"
    && typeof record.updatedAt === "string"
    && typeof record.recipientName === "string"
    && typeof record.recipientEmail === "string"
    && typeof record.previewHtml === "string"
    && typeof record.previewText === "string"
    && typeof record.deliveryStatus === "string"
    && typeof record.previewCount === "number"
    && typeof record.printCount === "number"
    && typeof record.downloadCount === "number"
    && typeof record.emailCount === "number"
    && Array.isArray(record.actions);
}

function loadStoredARRecoveryDocumentTemplates(workspace: ReturnType<typeof loadARRecoveryWorkspace>): ARRecoveryDocumentTemplateRecord[] {
  const seededTemplates = buildARRecoveryDocumentTemplateSeed(workspace);
  if (typeof window === "undefined") return seededTemplates;

  try {
    const raw = window.localStorage.getItem(`${AR_RECOVERY_DOCUMENT_TEMPLATE_STORAGE_KEY}:${workspace.company.id}`);
    if (!raw) return seededTemplates;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return seededTemplates;
    const records = parsed.filter(isARRecoveryDocumentTemplateRecord);
    return records.length ? records : seededTemplates;
  } catch {
    return seededTemplates;
  }
}

function saveStoredARRecoveryDocumentTemplates(workspace: ReturnType<typeof loadARRecoveryWorkspace>, records: ARRecoveryDocumentTemplateRecord[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(`${AR_RECOVERY_DOCUMENT_TEMPLATE_STORAGE_KEY}:${workspace.company.id}`, JSON.stringify(records));
}

function isARRecoveryDocumentTemplateRecord(value: unknown): value is ARRecoveryDocumentTemplateRecord {
  if (!value || typeof value !== "object") return false;
  const record = value as Partial<ARRecoveryDocumentTemplateRecord>;
  return typeof record.id === "string" && typeof record.companyId === "string" && (record.family === "letter" || record.family === "statement") && typeof record.templateName === "string" && typeof record.summary === "string" && typeof record.subjectLine === "string" && typeof record.bodyHtml === "string" && typeof record.brandMode === "string" && Array.isArray(record.allowedRoles) && Array.isArray(record.assignedAccountIds) && Array.isArray(record.assignedCustomerIds) && Array.isArray(record.mergeFields) && Array.isArray(record.versionHistory) && typeof record.createdAt === "string" && typeof record.updatedAt === "string";
}

function buildARRecoveryDocumentTemplateSeed(workspace: ReturnType<typeof loadARRecoveryWorkspace>): ARRecoveryDocumentTemplateRecord[] {
  const clientProfile = workspace.clientProfile;
  const letterDefaults = clientProfile.letterDefaults;
  const customer = workspace.accounts[0];
  const account = workspace.accounts[0];
  const currentBalance = account ? formatARRecoveryCurrency(account.openBalanceCents) : formatARRecoveryCurrency(0);
  const dueDate = account ? formatCapturedDate(getNextActionDate(account)) : "Not Captured";
  const statementTitle = `${clientProfile.businessName} Statement`;
  const paymentInstructions = clientProfile.accountingContact || "Not configured";
  const collectorName = workspace.collectors[0]?.collectorName || clientProfile.collectionDepartmentContact || "Collector Desk";

  const seedDefinitions: Array<{
    family: ARRecoveryDocumentTemplateFamily;
    templateName: string;
    summary: string;
    subjectLine: string;
  }> = [
    { family: "letter", templateName: "Friendly Reminder", summary: "Gentle first-touch collection letter.", subjectLine: `Friendly Reminder - ${clientProfile.businessName}` },
    { family: "letter", templateName: "First Notice", summary: "First formal notice for open balances.", subjectLine: `First Notice - ${clientProfile.businessName}` },
    { family: "letter", templateName: "Second Notice", summary: "Second reminder with a firmer tone.", subjectLine: `Second Notice - ${clientProfile.businessName}` },
    { family: "letter", templateName: "Final Reminder", summary: "Last reminder before escalation.", subjectLine: `Final Reminder - ${clientProfile.businessName}` },
    { family: "letter", templateName: "Final Demand", summary: "Final demand letter for unresolved balances.", subjectLine: `Final Demand - ${clientProfile.businessName}` },
    { family: "letter", templateName: "Promise Confirmation", summary: "Confirms a client promise or commitment.", subjectLine: `Promise Confirmation - ${clientProfile.businessName}` },
    { family: "letter", templateName: "Promise Reminder", summary: "Reminder for a scheduled promise date.", subjectLine: `Promise Reminder - ${clientProfile.businessName}` },
    { family: "letter", templateName: "Broken Promise Notice", summary: "Notice for missed or broken commitments.", subjectLine: `Broken Promise Notice - ${clientProfile.businessName}` },
    { family: "letter", templateName: "Welcome Letter", summary: "Welcome communication for new accounts.", subjectLine: `Welcome Letter - ${clientProfile.businessName}` },
    { family: "letter", templateName: "Payment Confirmation", summary: "Confirms a recorded payment or receipt.", subjectLine: `Payment Confirmation - ${clientProfile.businessName}` },
    { family: "letter", templateName: "Document Request", summary: "Requests supporting documents from the client.", subjectLine: `Document Request - ${clientProfile.businessName}` },
    { family: "letter", templateName: "Custom Letter", summary: "Custom letter shell for unique workflows.", subjectLine: `Custom Letter - ${clientProfile.businessName}` },
    { family: "statement", templateName: "Standard Statement", summary: "Standard monthly statement layout.", subjectLine: statementTitle },
    { family: "statement", templateName: "Monthly Statement", summary: "Recurring monthly statement layout.", subjectLine: statementTitle },
    { family: "statement", templateName: "Past Due Statement", summary: "Delinquent statement with overdue balance context.", subjectLine: statementTitle },
    { family: "statement", templateName: "Aging Statement", summary: "Aging-focused statement layout.", subjectLine: statementTitle },
    { family: "statement", templateName: "Executive Statement", summary: "Executive overview statement for leadership review.", subjectLine: statementTitle },
    { family: "statement", templateName: "Custom Statement", summary: "Custom statement shell for future workflows.", subjectLine: statementTitle },
  ];

  return seedDefinitions.map((definition, index) => {
    const assignedAccountIds = definition.family === "letter" ? workspace.accounts.slice(0, 2).map((item) => item.id) : workspace.accounts.slice(0, 1).map((item) => item.id);
    const assignedCustomerIds = assignedAccountIds.map((item) => item);
    const bodyHtml = buildARRecoveryDocumentTemplateBody({
      family: definition.family,
      templateName: definition.templateName,
      clientName: clientProfile.businessName,
      clientDBA: clientProfile.dbaName,
      accountNumber: account?.customerNumber || "Not Captured",
      customerName: customer?.customerName || "Not Captured",
      currentBalance,
      dueDate,
      paymentInstructions,
      collectorName,
      statementTitle,
    });
    const createdAt = timestampOffsetHours(-(seedDefinitions.length - index));
    return {
      id: `${workspace.company.id}-${definition.family}-${definition.templateName.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
      companyId: workspace.company.id,
      family: definition.family,
      templateName: definition.templateName,
      summary: definition.summary,
      subjectLine: definition.subjectLine,
      bodyHtml,
      brandMode: "company",
      status: "Published",
      allowedRoles: ["platform_admin", "kreatethedate_admin", "admin", "manager", "supervisor", "team_member", "collector"],
      assignedAccountIds,
      assignedCustomerIds,
      mergeFields: getDocumentTemplateFieldsForFamily(definition.family),
      previewNote: definition.summary,
      versionHistory: [
        {
          id: `${workspace.company.id}-${definition.family}-${definition.templateName.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-v1`,
          label: "Version 1",
          note: "Initial enterprise template seed.",
          bodyHtml,
          createdAt,
          createdBy: "Seeded Template",
          status: "Published",
        },
      ],
      createdAt,
      updatedAt: createdAt,
      publishedAt: createdAt,
    };
  });
}

function buildARRecoveryDocumentTemplateBody(input: {
  family: ARRecoveryDocumentTemplateFamily;
  templateName: string;
  clientName: string;
  clientDBA: string;
  accountNumber: string;
  customerName: string;
  currentBalance: string;
  dueDate: string;
  paymentInstructions: string;
  collectorName: string;
  statementTitle: string;
}) {
  if (input.family === "letter") {
    return `
      <div>
        <p><strong>${escapeHtml(input.templateName)}</strong></p>
        <p>Dear {{CustomerName}},</p>
        <p>This letter is prepared for ${escapeHtml(input.clientName)} (${escapeHtml(input.clientDBA)}) and references account {{AccountNumber}}.</p>
        <p>Current balance: <strong>{{CurrentBalance}}</strong>. Aging bucket: {{AgingBucket}}. Next action: {{NextAction}}.</p>
        <p>Portal: {{PortalUrl}}</p>
        <p>Payment instructions: ${escapeHtml(input.paymentInstructions || "Not configured")}</p>
        <p>Regards,<br />${escapeHtml(input.collectorName)}</p>
      </div>
    `.trim();
  }

  return `
    <div>
      <p><strong>${escapeHtml(input.templateName)}</strong></p>
      <p>${escapeHtml(input.statementTitle)}</p>
      <p>Prepared for {{CustomerName}} / {{AccountNumber}}.</p>
      <p>Invoice reference: {{InvoiceNumber}} | Due date: {{DueDate}} | Balance: <strong>{{CurrentBalance}}</strong></p>
      <p>Payment instructions: ${escapeHtml(input.paymentInstructions || "Not configured")}</p>
      <p>Prepared by: ${escapeHtml(input.collectorName)}</p>
      <p>Client branding and account context are preserved for this company-scoped statement.</p>
    </div>
  `.trim();
}

function getDocumentTemplateFieldsForFamily(family: ARRecoveryDocumentTemplateFamily) {
  const fields = AR_RECOVERY_DOCUMENT_TEMPLATE_FIELD_GROUPS.flatMap((group) => group.fields.map((field) => field.token));
  return family === "statement" ? fields : fields;
}

function buildARRecoveryDocumentTemplatePreviewContext(workspace: ReturnType<typeof loadARRecoveryWorkspace>) {
  const clientProfile = workspace.clientProfile;
  const letterDefaults = clientProfile.letterDefaults;
  const primaryAccount = workspace.accounts[0];
  const primaryPromise = workspace.promises[0];
  const currentBalance = primaryAccount ? formatARRecoveryCurrency(primaryAccount.openBalanceCents) : formatARRecoveryCurrency(0);
  const dueDate = primaryAccount ? formatCapturedDate(getNextActionDate(primaryAccount)) : "Not Captured";
  const templateName = "Document Template";

  return {
    TemplateName: templateName,
    ClientName: clientProfile.businessName || "Not Captured",
    ClientDBA: clientProfile.dbaName || "Not Captured",
    ClientAddress: letterDefaults.address || clientProfile.addressLine1 || "Not Captured",
    ClientPhone: letterDefaults.phone || clientProfile.phone || "Not Captured",
    ClientEmail: letterDefaults.email || clientProfile.email || "Not Captured",
    ClientWebsite: letterDefaults.website || clientProfile.website || "Not Captured",
    ClientContactName: letterDefaults.contactName || clientProfile.collectionDepartmentContact || "Not Captured",
    CustomerName: primaryAccount?.customerName || "Not Captured",
    CustomerNumber: primaryAccount?.customerNumber || "Not Captured",
    PrimaryContactName: primaryAccount?.contactName || "Not Captured",
    PrimaryContactEmail: primaryAccount?.contactEmail || "Not Captured",
    CommunicationPreference: clientProfile.communicationPreference || "Not Captured",
    AccountName: primaryAccount?.customerName || "Not Captured",
    AccountNumber: primaryAccount?.customerNumber || "Not Captured",
    CurrentBalance: currentBalance,
    AgingBucket: primaryAccount?.agingSummary || "Current",
    AssignedCollector: primaryAccount?.assignedCollector || workspace.collectors[0]?.collectorName || "Not Captured",
    InvoiceNumber: primaryPromise?.invoiceLabels?.[0] || "INV-001",
    InvoiceDate: primaryPromise ? formatCapturedDate(primaryPromise.createdAt) : "Not Captured",
    DueDate: dueDate,
    OpenInvoiceCount: primaryAccount?.openInvoiceCount?.toString() || "0",
    OpenBalance: currentBalance,
    CollectorName: workspace.collectors[0]?.collectorName || clientProfile.collectionDepartmentContact || "Collector Desk",
    ManagerName: clientProfile.escalationContact || "Manager Desk",
    PromiseStatus: primaryAccount?.recoveryStatus || primaryPromise?.status || "Not Captured",
    NextAction: primaryAccount?.notes || "Not Captured",
    PaymentInstructions: clientProfile.accountingContact || "Not configured",
    PortalUrl: clientProfile.portalUrl || "Not configured",
    StatementTitle: `${clientProfile.businessName || "Client"} Statement`,
  } as Record<string, string>;
}

function buildARRecoveryDocumentDeliveryPreviewContext(
  workspace: ReturnType<typeof loadARRecoveryWorkspace>,
  account: ReturnType<typeof loadARRecoveryWorkspace>["accounts"][number] | null,
  template: ARRecoveryDocumentTemplateRecord | null,
  family: ARRecoveryDocumentTemplateFamily,
) {
  const clientProfile = workspace.clientProfile;
  const letterDefaults = clientProfile.letterDefaults || {
    businessName: clientProfile.businessName || workspace.company.companyName,
    address: clientProfile.addressLine1 || "Not Captured",
    phone: clientProfile.phone || "Not Captured",
    email: clientProfile.email || "Not Captured",
    website: clientProfile.website || "Not Captured",
    contactName: clientProfile.collectionDepartmentContact || "Not Captured",
  };
  const selectedPromise = account ? workspace.promises.find((promise) => promise.accountId === account.id) || workspace.promises[0] || null : workspace.promises[0] || null;
  const currentBalance = account ? formatARRecoveryCurrency(account.openBalanceCents) : formatARRecoveryCurrency(0);
  const dueDate = account ? formatCapturedDate(getNextActionDate(account)) : "Not Captured";
  const documentTitle =
    family === "letter"
      ? template?.templateName || "Approved Letter"
      : template?.templateName || "Approved Statement";

  return {
    TemplateName: documentTitle,
    ClientName: letterDefaults.businessName || clientProfile.businessName || workspace.company.companyName,
    ClientDBA: clientProfile.dbaName || letterDefaults.businessName || workspace.company.companyName,
    ClientAddress: letterDefaults.address || clientProfile.addressLine1 || "Not Captured",
    ClientPhone: letterDefaults.phone || clientProfile.phone || "Not Captured",
    ClientEmail: letterDefaults.email || clientProfile.email || "Not Captured",
    ClientWebsite: letterDefaults.website || clientProfile.website || "Not Captured",
    ClientContactName: letterDefaults.contactName || clientProfile.collectionDepartmentContact || "Not Captured",
    CustomerName: account?.customerName || "Not Captured",
    CustomerNumber: account?.customerNumber || "Not Captured",
    PrimaryContactName: account?.contactName || "Not Captured",
    PrimaryContactEmail: account?.contactEmail || "Not Captured",
    CommunicationPreference: clientProfile.communicationPreference || "Not Captured",
    AccountName: account?.customerName || "Not Captured",
    AccountNumber: account?.customerNumber || "Not Captured",
    CurrentBalance: currentBalance,
    AgingBucket: account?.agingSummary || "Current",
    AssignedCollector: account?.assignedCollector || workspace.collectors[0]?.collectorName || "Not Captured",
    InvoiceNumber: selectedPromise?.invoiceLabels?.[0] || "INV-001",
    InvoiceDate: selectedPromise ? formatCapturedDate(selectedPromise.createdAt) : "Not Captured",
    DueDate: dueDate,
    OpenInvoiceCount: account?.openInvoiceCount?.toString() || "0",
    OpenBalance: currentBalance,
    CollectorName: workspace.collectors[0]?.collectorName || clientProfile.collectionDepartmentContact || "Collector Desk",
    ManagerName: clientProfile.escalationContact || "Manager Desk",
    PromiseStatus: account?.recoveryStatus || selectedPromise?.status || "Not Captured",
    NextAction: account?.notes || "Not Captured",
    PaymentInstructions: clientProfile.accountingContact || "Payment instructions not configured",
    PortalUrl: clientProfile.portalUrl || "Not configured",
    StatementTitle: family === "statement" ? `${clientProfile.businessName || "Client"} Statement` : `${clientProfile.businessName || "Client"} Letter`,
    PromiseLabel: selectedPromise?.promiseType || "Not Captured",
    PromiseDueDate: selectedPromise?.promiseDate ? formatCapturedDate(selectedPromise.promiseDate) : "Not Captured",
    PromiseAmount: selectedPromise?.amountCents ? formatARRecoveryCurrency(selectedPromise.amountCents) : "Not Captured",
  } as Record<string, string>;
}

function renderARRecoveryDocumentTemplatePreview(bodyHtml: string, values: Record<string, string>) {
  return bodyHtml.replace(/{{\s*([^}]+)\s*}}/g, (_, rawKey: string) => escapeHtml(values[rawKey.trim()] || "Not Captured"));
}

function createARRecoveryDocumentDeliveryRecord(input: {
  workspace: ReturnType<typeof loadARRecoveryWorkspace>;
  family: ARRecoveryDocumentTemplateFamily;
  template: ARRecoveryDocumentTemplateRecord;
  account: ReturnType<typeof loadARRecoveryWorkspace>["accounts"][number] | null;
  actorName: string;
  previewContext: Record<string, string>;
  relatedPromiseId?: string;
  relatedTaskId?: string;
}) {
  const now = new Date().toISOString();
  const previewHtml = renderARRecoveryDocumentTemplatePreview(input.template.bodyHtml, input.previewContext);
  const previewText = previewHtml.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  return {
    id: `${input.workspace.company.id}-${input.family}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    companyId: input.workspace.company.id,
    family: input.family,
    documentType: input.template.templateName,
    templateId: input.template.id,
    templateName: input.template.templateName,
    subjectLine: input.template.subjectLine,
    customerId: input.account?.id || "",
    customerName: input.account?.customerName || "Not Captured",
    customerNumber: input.account?.customerNumber || "Not Captured",
    accountId: input.account?.id || "",
    accountName: input.account?.customerName || "Not Captured",
    accountNumber: input.account?.customerNumber || "Not Captured",
    relatedPromiseId: input.relatedPromiseId || "",
    relatedPromise: input.relatedPromiseId || "",
    relatedTaskId: input.relatedTaskId || "",
    relatedTask: input.relatedTaskId || "",
    generatedBy: input.actorName,
    generatedAt: now,
    createdAt: now,
    updatedAt: now,
    recipientName: input.account?.contactName || input.account?.customerName || "Not Captured",
    recipientEmail: input.account?.contactEmail || "",
    previewHtml,
    previewText,
    deliveryStatus: "pending" as ARRecoveryDocumentDeliveryStatus,
    previewCount: 0,
    printCount: 0,
    downloadCount: 0,
    emailCount: 0,
    actions: [
      {
        action: "Generated" as ARRecoveryDocumentDeliveryAction,
        at: now,
        by: input.actorName,
        detail: `${input.template.templateName} generated for ${input.account?.customerName || "selected account"}.`,
      },
    ],
  } satisfies ARRecoveryDocumentDeliveryRecord;
}

function updateARRecoveryDocumentDeliveryRecord(
  record: ARRecoveryDocumentDeliveryRecord,
  action: ARRecoveryDocumentDeliveryAction,
  actorName: string,
  detail: string,
  overrides: Partial<Pick<ARRecoveryDocumentDeliveryRecord, "deliveryStatus" | "deliveryProvider" | "providerMessageId" | "providerStatus" | "deliveryError" | "previewHtml">> = {},
): ARRecoveryDocumentDeliveryRecord {
  const now = new Date().toISOString();
  const nextActions = [...record.actions, { action, at: now, by: actorName, detail }];
  return {
    ...record,
    ...overrides,
    previewCount: record.previewCount + (action === "Previewed" ? 1 : 0),
    printCount: record.printCount + (action === "Printed" ? 1 : 0),
    downloadCount: record.downloadCount + (action === "Downloaded" ? 1 : 0),
    emailCount: record.emailCount + (action === "Emailed" ? 1 : 0),
    deliveryStatus: overrides.deliveryStatus || record.deliveryStatus,
    updatedAt: now,
    actions: nextActions,
  };
}

function buildARRecoveryDocumentTemplateHistoryEntries(
  documentRequests: ARRecoveryDocumentRequestRecord[],
  templates: ARRecoveryDocumentTemplateRecord[],
  documentDeliveries: ARRecoveryDocumentDeliveryRecord[] = [],
) {
  const requestEntries = documentRequests.map((request) => ({
    timestamp: request.requestedAt,
    entry: {
      id: `request-${request.id}`,
      eventType: `${request.requestType} Request`,
      date: formatCapturedDate(request.requestedAt),
      time: formatCapturedTime(request.requestedAt),
      user: request.requestedBy || "Not Captured",
      status: request.status,
      outcome: request.requestNotes || "Not Captured",
      nextAction: request.requestedFrom || "Review request",
    },
  }));

  const templateEntries = templates.flatMap((template) =>
    template.versionHistory.map((version) => ({
      timestamp: version.createdAt,
      entry: {
        id: version.id,
        eventType: `${template.family === "letter" ? "Letter" : "Statement"} Template ${version.status}`,
        date: formatCapturedDate(version.createdAt),
        time: formatCapturedTime(version.createdAt),
        user: version.createdBy || "Seeded Template",
        status: template.status,
        outcome: version.note || template.summary,
        nextAction: template.brandMode === "client" ? "Review client brand preview" : "Publish updated template version",
      },
    })),
  );

  const deliveryEntries = documentDeliveries.map((delivery) => {
    const latestAction = delivery.actions[delivery.actions.length - 1] || {
      action: "Generated" as ARRecoveryDocumentDeliveryAction,
      at: delivery.updatedAt || delivery.generatedAt || delivery.createdAt,
      by: delivery.generatedBy,
      detail: `${delivery.documentType} generated for ${delivery.customerName}.`,
    };
    const actionAt = latestAction.at || delivery.updatedAt || delivery.generatedAt || delivery.createdAt;
    return {
      timestamp: actionAt,
      entry: {
        id: `delivery-${delivery.id}`,
        eventType: `${delivery.documentType} ${latestAction.action}`,
        date: formatCapturedDate(actionAt),
        time: formatCapturedTime(actionAt),
        user: latestAction.by || delivery.generatedBy,
        status: delivery.deliveryStatus === "sent" ? "Sent" : delivery.deliveryStatus === "failed" ? "Failed" : latestAction.action,
        outcome: latestAction.detail || delivery.previewText || "Not Captured",
        nextAction:
          latestAction.action === "Generated"
            ? "Preview, print, download, or email the approved template"
            : delivery.deliveryStatus === "sent"
              ? "Track customer response and account updates"
              : delivery.deliveryStatus === "failed"
                ? "Retry email delivery or print/download manually"
                : "Review document details",
      },
    };
  });

  return [...templateEntries, ...deliveryEntries, ...requestEntries]
    .sort((a, b) => getDateTime(b.timestamp) - getDateTime(a.timestamp))
    .map((item) => item.entry);
}

function buildARRecoveryDocumentDeliveryActivityDetail(delivery: ARRecoveryDocumentDeliveryRecord) {
  const actionSummary = delivery.actions
    .slice(-3)
    .map((action) => `${action.action} by ${action.by}`)
    .join(" | ");
  return `Document Type: ${delivery.documentType} | Template Used: ${delivery.templateName} | Customer: ${delivery.customerName} | Account: ${delivery.accountNumber} | Generated By: ${delivery.generatedBy} | Generated Date: ${formatCapturedDate(delivery.generatedAt)} | Delivery Status: ${delivery.deliveryStatus} | Recipient: ${delivery.recipientName} <${delivery.recipientEmail || "Not Captured"}> | Promise Link: ${delivery.relatedPromise || "Not Captured"} | Task Link: ${delivery.relatedTask || "Not Captured"} | Actions: ${actionSummary || "Not Captured"}`;
}

function createARRecoveryBlankDocumentTemplate(workspace: ReturnType<typeof loadARRecoveryWorkspace>, family: ARRecoveryDocumentTemplateFamily): ARRecoveryDocumentTemplateRecord {
  const templateName = family === "letter" ? "New Letter Template" : "New Statement Template";
  const now = new Date().toISOString();
  const previewContext = buildARRecoveryDocumentTemplatePreviewContext(workspace);
  const bodyHtml = buildARRecoveryDocumentTemplateBody({
    family,
    templateName,
    clientName: previewContext.ClientName,
    clientDBA: previewContext.ClientDBA,
    accountNumber: previewContext.AccountNumber,
    customerName: previewContext.CustomerName,
    currentBalance: previewContext.CurrentBalance,
    dueDate: previewContext.DueDate,
    paymentInstructions: previewContext.PaymentInstructions,
    collectorName: previewContext.CollectorName,
    statementTitle: previewContext.StatementTitle,
  });
  return {
    id: `${workspace.company.id}-${family}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    companyId: workspace.company.id,
    family,
    templateName,
    summary: family === "letter" ? "Create a new client-branded letter template." : "Create a new client-branded statement template.",
    subjectLine: `${templateName} - ${workspace.clientProfile.businessName}`,
    bodyHtml,
    brandMode: "company",
    status: "Draft",
    allowedRoles: ["platform_admin", "kreatethedate_admin", "admin", "manager", "supervisor", "team_member", "collector"],
    assignedAccountIds: [],
    assignedCustomerIds: [],
    mergeFields: getDocumentTemplateFieldsForFamily(family),
    previewNote: "Draft template awaiting first publication.",
    versionHistory: [
      {
        id: `${workspace.company.id}-${family}-draft-${Date.now().toString(36)}`,
        label: "Version 1",
        note: "Draft template created.",
        bodyHtml,
        createdAt: now,
        createdBy: "Authenticated User",
        status: "Draft",
      },
    ],
    createdAt: now,
    updatedAt: now,
    publishedAt: null,
  };
}

function createARRecoveryDocumentTemplateDraft(template: ARRecoveryDocumentTemplateRecord): ARRecoveryDocumentTemplateDraftState {
  return {
    templateName: template.templateName,
    summary: template.summary,
    subjectLine: template.subjectLine,
    brandMode: template.brandMode,
    status: template.status,
    allowedRoles: [...template.allowedRoles],
    assignedAccountIds: [...template.assignedAccountIds],
    assignedCustomerIds: [...template.assignedCustomerIds],
  };
}

function createARRecoveryBrandLogoDataUri(displayName: string, primaryColor: string, accentColor: string) {
  const initials = displayName
    .split(/\s+/)
    .map((part) => part.trim()[0] || "")
    .join("")
    .slice(0, 3)
    .toUpperCase() || "AR";

  const safeDisplayName = escapeHtml(displayName || "Client Brand");
  const safeInitials = escapeHtml(initials);
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 420 140" role="img" aria-label="${safeDisplayName}">
      <defs>
        <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stop-color="${primaryColor}" />
          <stop offset="100%" stop-color="${accentColor}" />
        </linearGradient>
      </defs>
      <rect width="420" height="140" rx="24" fill="white" />
      <circle cx="72" cy="70" r="44" fill="url(#logoGradient)" opacity=".12" />
      <circle cx="72" cy="70" r="31" fill="url(#logoGradient)" opacity=".24" />
      <text x="72" y="79" text-anchor="middle" font-family="Inter, Arial, sans-serif" font-size="30" font-weight="800" fill="${primaryColor}">${safeInitials}</text>
      <text x="132" y="61" font-family="Inter, Arial, sans-serif" font-size="28" font-weight="700" fill="${primaryColor}">${safeDisplayName}</text>
      <text x="132" y="92" font-family="Inter, Arial, sans-serif" font-size="16" font-weight="600" fill="${accentColor}">Enterprise document branding</text>
    </svg>
  `.trim();

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function escapeHtml(value: string) {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}
function loadStoredARRecoveryAccountEdits(): ARRecoveryAccountEditRecord[] {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(AR_RECOVERY_ACCOUNT_EDIT_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? sortARRecoveryAccountEdits(parsed.filter(isARRecoveryAccountEditRecord)) : [];
  } catch {
    return [];
  }
}

function saveStoredARRecoveryAccountEdits(records: ARRecoveryAccountEditRecord[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(AR_RECOVERY_ACCOUNT_EDIT_STORAGE_KEY, JSON.stringify(sortARRecoveryAccountEdits(records)));
}

function sortARRecoveryAccountEdits(records: ARRecoveryAccountEditRecord[]) {
  return [...records].sort((a, b) => getDateTime(b.createdAt) - getDateTime(a.createdAt));
}

function mergeARRecoveryAccountEditRecords(
  localRecords: ARRecoveryAccountEditRecord[],
  remoteRecords: ARRecoveryAccountEditRecord[],
) {
  const merged = new Map<string, ARRecoveryAccountEditRecord>();
  [...localRecords, ...remoteRecords].forEach((record) => merged.set(record.id, record));
  return sortARRecoveryAccountEdits(Array.from(merged.values()));
}

async function loadRemoteARRecoveryAccountEdits(companyId: string) {
  const response = await loadBusinessCenterOperationalState<ARRecoveryAccountEditRecord[]>(
    AR_RECOVERY_ACCOUNT_EDIT_OPERATIONAL_STATE_KEY,
    { scopeType: "company", companyId },
  );
  return Array.isArray(response.state) ? sortARRecoveryAccountEdits(response.state.filter(isARRecoveryAccountEditRecord)) : [];
}

async function saveCanonicalARRecoveryAccountEdits(companyId: string, records: ARRecoveryAccountEditRecord[]) {
  const normalizedRecords = sortARRecoveryAccountEdits(records);
  await saveBusinessCenterOperationalState(
    AR_RECOVERY_ACCOUNT_EDIT_OPERATIONAL_STATE_KEY,
    normalizedRecords,
    { scopeType: "company", companyId },
  );
  saveStoredARRecoveryAccountEdits(normalizedRecords);
  return normalizedRecords;
}

function parseARRecoveryRecipient(value: string) {
  const trimmed = value.trim();
  const bracketMatch = trimmed.match(/^(.*?)<([^>]+)>$/);
  if (bracketMatch) {
    return {
      name: bracketMatch[1].trim(),
      email: bracketMatch[2].trim(),
    };
  }

  return {
    name: "",
    email: trimmed,
  };
}

function parseARRecoveryEmailList(value: string) {
  return value
    .split(/[;,]/)
    .map((entry) => parseARRecoveryRecipient(entry).email.trim())
    .filter(Boolean);
}

function buildEmptyARRecoveryEmailValidationState(): ARRecoveryEmailValidationState {
  return {
    recipient: { invalidAddresses: [], message: "" },
    cc: { invalidAddresses: [], message: "" },
    bcc: { invalidAddresses: [], message: "" },
  };
}

function validateARRecoveryEmailAddressField(value: string, field: ARRecoveryEmailAddressField, required = false) {
  const addresses = parseARRecoveryEmailList(value);
  if (required && !addresses.length) {
    return {
      invalidAddresses: [],
      message: "Recipient email is required.",
    };
  }
  const invalidAddresses = addresses.filter((email) => !isValidARRecoveryEmail(email));
  return {
    invalidAddresses,
    message: invalidAddresses.length ? `${field.toUpperCase()} includes ${invalidAddresses.length === 1 ? "an invalid address" : "invalid addresses"}.` : "",
  };
}

function validateARRecoveryEmailAddressFields(form: Pick<ARRecoveryEmailWorkspaceFormState, "recipient" | "cc" | "bcc">, fallbackRecipient: string) {
  return {
    recipient: validateARRecoveryEmailAddressField(form.recipient.trim() || fallbackRecipient, "recipient", true),
    cc: validateARRecoveryEmailAddressField(form.cc, "cc"),
    bcc: validateARRecoveryEmailAddressField(form.bcc, "bcc"),
  } satisfies ARRecoveryEmailValidationState;
}

function getFirstInvalidARRecoveryEmailField(validation: ARRecoveryEmailValidationState): ARRecoveryEmailAddressField | null {
  if (validation.recipient.message) return "recipient";
  if (validation.cc.message) return "cc";
  if (validation.bcc.message) return "bcc";
  return null;
}

function formatARRecoveryInvalidRecipientMessage(field: ARRecoveryEmailAddressField, addresses: string[]) {
  const label = field === "recipient" ? "Recipient" : field.toUpperCase();
  const invalidList = addresses.length ? addresses.join("\n") : "Address required";
  return `The email couldn't be sent because one or more recipient addresses are invalid.\n\n${label}\n${invalidList}\n\nPlease correct the highlighted address and try again.`;
}

function buildARRecoveryEmailGreeting(contactName: string) {
  const sanitizedName = contactName
    .replace(/[<>]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (sanitizedName && !/^(undefined|null|not captured|customer name)$/i.test(sanitizedName)) {
    return `Hello ${sanitizedName},`;
  }
  return "Hello Accounts Payable Team,";
}

function buildARRecoveryEmailAttachmentNotice(attachments: Array<{ type: string }>) {
  if (!attachments.length) return "";
  const types = new Set(attachments.map((attachment) => attachment.type.toLowerCase()));
  if (types.size === 1 && types.has("invoice")) return "For your convenience, we've attached copies of the relevant invoices.";
  if (types.size === 1 && types.has("statement")) return "Please find the attached statement for your review.";
  if (types.size === 1 && types.has("letter")) return "For your convenience, we've attached the related correspondence for your review.";
  return "For your convenience, we've attached the relevant documents for your review.";
}

function buildARRecoveryCustomerEmailText(input: {
  greeting: string;
  opening: string;
  body: string;
  attachmentNotice: string;
  companyName: string;
  email: string;
  phone: string;
  website: string;
}) {
  const signatureLines = dedupeARRecoverySignatureLines([
    input.companyName,
    "Accounts Receivable Department",
    input.email,
    input.phone,
    input.website,
  ]);

  return [
    input.greeting,
    "",
    input.opening,
    "",
    input.body,
    input.attachmentNotice ? `\n${input.attachmentNotice}` : "",
    "",
    "Thank you for your time and attention.",
    "If you have any questions or require additional documentation, please don't hesitate to contact us.",
    "",
    "Sincerely,",
    ...signatureLines,
  ]
    .map((line) => line.trimEnd())
    .filter((line, index, lines) => !(line === "" && lines[index - 1] === ""))
    .join("\n");
}

function buildARRecoveryCustomerEmailHtml(input: {
  logoSrc: string;
  subject: string;
  greeting: string;
  opening: string;
  body: string;
  attachmentNotice: string;
  companyName: string;
  email: string;
  phone: string;
  website: string;
}) {
  const bodyParagraphs = input.body
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
  const signatureLines = dedupeARRecoverySignatureLines([
    input.companyName,
    "Accounts Receivable Department",
    input.email,
    input.phone,
    input.website,
  ]);

  return `
    <div style="margin:0;padding:0;background:#f8fafc;color:#0f172a;font-family:Arial,Helvetica,sans-serif;">
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;background:#f8fafc;">
        <tr>
          <td style="padding:28px 16px;">
            <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:680px;margin:0 auto;border-collapse:collapse;background:#ffffff;border:1px solid #d7eef8;border-radius:20px;overflow:hidden;">
              <tr>
                <td style="padding:28px 32px 18px;border-bottom:1px solid #d7eef8;">
                  <img src="${escapeHtml(input.logoSrc)}" alt="${escapeHtml(input.companyName)} logo" style="display:block;width:92px;max-width:92px;height:auto;margin:0 0 18px;" />
                  <p style="margin:0;color:#0f172a;font-size:18px;font-weight:800;letter-spacing:.01em;">${escapeHtml(input.companyName)}</p>
                  <p style="margin:6px 0 0;color:#475569;font-size:13px;font-weight:700;">Accounts Receivable Department</p>
                </td>
              </tr>
              <tr>
                <td style="padding:30px 32px 8px;">
                  <p style="margin:0 0 18px;color:#0f172a;font-size:16px;line-height:1.7;">${escapeHtml(input.greeting)}</p>
                  <p style="margin:0 0 18px;color:#334155;font-size:15px;line-height:1.7;">${escapeHtml(input.opening)}</p>
                  ${bodyParagraphs.map((paragraph) => `<p style="margin:0 0 18px;color:#334155;font-size:15px;line-height:1.7;">${escapeHtml(paragraph)}</p>`).join("")}
                  ${input.attachmentNotice ? `<p style="margin:0 0 18px;color:#334155;font-size:15px;line-height:1.7;">${escapeHtml(input.attachmentNotice)}</p>` : ""}
                  <p style="margin:22px 0 10px;color:#334155;font-size:15px;line-height:1.7;">Thank you for your time and attention.</p>
                  <p style="margin:0 0 24px;color:#334155;font-size:15px;line-height:1.7;">If you have any questions or require additional documentation, please don't hesitate to contact us.</p>
                </td>
              </tr>
              <tr>
                <td style="padding:0 32px 32px;">
                  <p style="margin:0 0 12px;color:#0f172a;font-size:15px;line-height:1.7;">Sincerely,</p>
                  ${signatureLines.map((line, index) => `<p style="margin:${index === 0 ? "0" : "4px"} 0 0;color:${index < 2 ? "#0f172a" : "#475569"};font-size:${index === 0 ? "15px" : "14px"};line-height:1.55;font-weight:${index < 2 ? "800" : "600"};">${escapeHtml(line)}</p>`).join("")}
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </div>
  `;
}

function encodeARRecoveryTextAttachment(value: string) {
  if (typeof window === "undefined" || typeof window.btoa !== "function" || typeof TextEncoder === "undefined") return "";
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return window.btoa(binary);
}

function encodeARRecoveryPdfAttachment(value: string) {
  const safeText = value
    .replace(/[()\\]/g, "\\$&")
    .replace(/[^\x09\x0A\x0D\x20-\x7E]/g, " ")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, 28);
  const pdfLines = safeText.length ? safeText : ["Certified AR Recovery document"];
  const stream = [
    "BT",
    "/F1 11 Tf",
    "72 748 Td",
    "14 TL",
    ...pdfLines.flatMap((line, index) => [
      index === 0 ? `(${line}) Tj` : `T* (${line}) Tj`,
    ]),
    "ET",
  ].join("\n");
  const objects = [
    "<< /Type /Catalog /Pages 2 0 R >>",
    "<< /Type /Pages /Kids [3 0 R] /Count 1 >>",
    "<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >>",
    "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>",
    `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`,
  ];
  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  objects.forEach((object, index) => {
    offsets.push(pdf.length);
    pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });
  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, "0")} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;
  return encodeARRecoveryTextAttachment(pdf);
}

function sanitizeARRecoveryEmailAttachmentFilename(value: string) {
  const sanitized = value.replace(/[<>:"/\\|?*\x00-\x1F]/g, "_").replace(/\s+/g, " ").trim();
  return sanitized.endsWith(".pdf") ? sanitized : `${sanitized || "attachment"}.pdf`;
}

function stripARRecoveryHtmlToText(value: string) {
  return value
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

function findCertifiedARRecoveryDocumentDelivery({
  attachment,
  documentDeliveries,
  accountId,
}: {
  attachment: ARRecoveryEmailWorkspaceAttachment;
  documentDeliveries: ARRecoveryDocumentDeliveryRecord[];
  accountId: string;
}) {
  const family = attachment.documentKind === "certified_letter_pdf" ? "letter" : attachment.documentKind === "certified_statement_pdf" ? "statement" : "";
  if (!family) return null;
  const normalizedLabel = attachment.label.toLowerCase();
  return documentDeliveries
    .filter((delivery) => delivery.accountId === accountId && delivery.family === family)
    .sort((a, b) => getDateTime(b.updatedAt || b.generatedAt) - getDateTime(a.updatedAt || a.generatedAt))
    .find((delivery) => {
      if (attachment.deliveryId && delivery.id === attachment.deliveryId) return true;
      const haystack = [delivery.templateName, delivery.subjectLine, delivery.documentType, delivery.id].join(" ").toLowerCase();
      return haystack.includes(normalizedLabel) || normalizedLabel.includes(delivery.templateName.toLowerCase()) || normalizedLabel.includes(delivery.documentType.toLowerCase());
    }) || null;
}

function getARRecoveryEmailDocumentGroup(documentKind: ARRecoveryEnterpriseDocumentKind): ARRecoveryEmailDocumentPackageItem["group"] {
  if (documentKind === "certified_letter_pdf") return "Letters";
  if (documentKind === "certified_statement_pdf") return "Statements";
  if (documentKind === "account_invoice_details_pdf") return "Invoice Details";
  return "Supporting Documents";
}

function getARRecoveryEmailDocumentTypeLabel(documentKind: ARRecoveryEnterpriseDocumentKind) {
  if (documentKind === "certified_letter_pdf") return "Letter";
  if (documentKind === "certified_statement_pdf") return "Statement";
  if (documentKind === "account_invoice_details_pdf") return "Invoice Details";
  return "Supporting Document";
}

function normalizeARRecoveryEmailDocumentName(attachment: ARRecoveryEmailWorkspaceAttachment, payload?: ARRecoveryEmailAttachmentPayload | null) {
  const rawName = (payload?.name || attachment.label || attachment.type || "Document.pdf")
    .replace(/\.pdf$/i, "")
    .replace(/\s+/g, " ")
    .trim();
  let normalized = rawName
    .replace(/^Letter\s+Letter\s*[-:]\s*/i, "")
    .replace(/^Letter\s*[-:]\s*/i, "")
    .replace(/^Statement\s+Statement\s*[-:]\s*/i, "Statement ")
    .replace(/^Statement\s*[-:]\s*/i, "Statement ")
    .replace(/^Invoice\s+Invoice\s*[-:]\s*/i, "Invoice ");

  if (attachment.documentKind === "certified_letter_pdf" && !/letter$/i.test(normalized)) {
    normalized = `${normalized} Letter`;
  }
  if (attachment.documentKind === "certified_statement_pdf" && !/^Statement\b/i.test(normalized)) {
    normalized = `Statement ${normalized}`;
  }
  if (attachment.documentKind === "account_invoice_details_pdf" && !/^Invoice Details\b/i.test(normalized)) {
    normalized = `Invoice Details - ${normalized}`;
  }

  return sanitizeARRecoveryEmailAttachmentFilename(`${normalized}.pdf`);
}

function getARRecoveryEmailDocumentUnavailableMessage(attachment: ARRecoveryEmailWorkspaceAttachment) {
  if (attachment.documentKind === "certified_letter_pdf") return "Letter not available. Open Letter Workspace.";
  if (attachment.documentKind === "certified_statement_pdf") return "Statement not available. Open Statement Workspace.";
  if (attachment.documentKind === "account_invoice_details_pdf") return "Invoice not found. Verify the invoice number or confirm the invoice has been imported.";
  return "Supporting document not available.";
}

function buildARRecoveryEmailDocumentPreviewState(item: ARRecoveryEmailDocumentPackageItem): ARRecoveryEmailDocumentPreviewState {
  if (!item.payload) {
    return {
      name: item.displayName,
      documentType: item.documentType,
      statusLabel: item.statusLabel,
      generatedLabel: item.generatedLabel,
      formatLabel: item.formatLabel,
      sourceUrl: null,
      message: getARRecoveryEmailDocumentUnavailableMessage(item.attachment),
    };
  }

  return {
    name: item.displayName,
    documentType: item.documentType,
    statusLabel: item.statusLabel,
    generatedLabel: item.generatedLabel,
    formatLabel: item.formatLabel,
    sourceUrl: `data:${item.payload.contentType};base64,${item.payload.contentBase64}`,
    message: "Certified document preview is ready.",
  };
}

function getARRecoveryEmailDocumentStatusClasses(status: ARRecoveryEmailDocumentStatus) {
  if (status === "sent") return "border-[rgba(20,184,166,.36)] bg-[rgba(20,184,166,.12)] text-[#0F766E]";
  if (status === "attached") return "border-[rgba(20,184,166,.30)] bg-[rgba(20,184,166,.10)] text-[#0F766E]";
  return "border-[#BFEAFF] bg-white text-[#0A2540]";
}

function buildARRecoveryEmailCertifiedAttachmentPayloads({
  attachments,
  account,
  companyName,
  documentDeliveries,
}: {
  attachments: ARRecoveryEmailWorkspaceAttachment[];
  account: { id: string; customerName: string; customerNumber: string };
  companyName: string;
  documentDeliveries: ARRecoveryDocumentDeliveryRecord[];
}): { payloads: ARRecoveryEmailAttachmentPayload[]; missing: string[] } {
  const payloads: ARRecoveryEmailAttachmentPayload[] = [];
  const missing: string[] = [];

  attachments.forEach((attachment) => {
    const delivery = findCertifiedARRecoveryDocumentDelivery({
      attachment,
      documentDeliveries,
      accountId: account.id,
    });

    if (!delivery) {
      missing.push(attachment.label);
      return;
    }

    const contentBase64 = encodeARRecoveryPdfAttachment([
      companyName,
      `${delivery.family === "letter" ? "Certified Letter" : "Certified Statement"} PDF`,
      delivery.templateName,
      "",
      stripARRecoveryHtmlToText(delivery.previewHtml) || delivery.previewText,
    ].join("\n"));
    if (!contentBase64) {
      missing.push(attachment.label);
      return;
    }
    payloads.push({
      name: sanitizeARRecoveryEmailAttachmentFilename(`${delivery.templateName || attachment.label}.pdf`),
      contentType: "application/pdf",
      contentBase64,
    });
  });

  return { payloads, missing };
}

function normalizeARRecoverySignatureLine(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

function dedupeARRecoverySignatureLines(lines: string[]) {
  const seen = new Set<string>();
  return lines
    .map((line) => line.trim())
    .filter((line) => {
      if (!line) return false;
      const normalized = normalizeARRecoverySignatureLine(line);
      if (!normalized || seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });
}

function buildARRecoveryLetterGreeting(recipientName: string) {
  const sanitizedName = recipientName
    .replace(/[<>]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!sanitizedName || /^(undefined|null|customer name)$/i.test(sanitizedName)) return "Hello,";
  const firstName = sanitizedName.split(" ").find(Boolean);
  if (firstName && !/^(undefined|null|customer)$/i.test(firstName)) return `Hello ${firstName},`;
  return `Hello ${sanitizedName},`;
}

const AR_RECOVERY_LETTER_BRANDING_LOGO_SRC = "/assets/collabrecover-cr-letter-logo.png";

function resolveARRecoveryLetterAssetSrc(src: string) {
  if (!src || /^https?:\/\//i.test(src) || src.startsWith("data:")) return src;
  if (typeof window === "undefined") return src;
  return new URL(src, window.location.origin).toString();
}

function buildARRecoveryLetterAddressLines(address: string) {
  const trimmed = address.trim();
  if (!trimmed) return [];
  if (trimmed.includes("\n")) {
    return trimmed.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  }
  const parts = trimmed.split(",").map((part) => part.trim()).filter(Boolean);
  if (parts.length >= 3) {
    return [
      parts[0],
      parts.slice(1, -1).join(", "),
      parts[parts.length - 1],
    ].filter(Boolean);
  }
  return [trimmed];
}

function isValidARRecoveryEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

async function deliverARRecoveryEmail(payload: {
  recordType: "letter" | "statement" | "email";
  recipientEmail: string;
  recipientName?: string;
  cc?: string[];
  bcc?: string[];
  subject: string;
  body: string;
  htmlBody?: string;
  attachments?: ARRecoveryEmailAttachmentPayload[];
  accountName: string;
  customerNumber: string;
  recordLabel: string;
}): Promise<ARRecoveryEmailDeliveryResult> {
  try {
    const response = await fetch("/api/ar-recovery/deliver-email", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(payload),
    });
    const text = await response.text();
    const parsed = text ? (JSON.parse(text) as ARRecoveryEmailDeliveryResult) : null;

    if (parsed?.status === "sent" || parsed?.status === "failed" || parsed?.status === "not_configured") {
      return parsed;
    }

    return {
      ok: false,
      status: response.ok ? "failed" : "failed",
      provider: "unknown",
      message: response.ok ? "Email provider response was invalid." : text || "Email delivery request failed.",
      providerStatus: response.status,
      providerMessageId: null,
      providerError: response.ok ? "Invalid provider response." : text || "Request failed.",
    };
  } catch (error) {
    return {
      ok: false,
      status: "failed",
      provider: "unavailable",
      message: "Email delivery request could not be completed.",
      providerStatus: 0,
      providerMessageId: null,
      providerError: error instanceof Error ? error.message : "Email delivery request failed.",
    };
  }
}

function isARRecoveryCallLogRecord(value: unknown): value is ARRecoveryCallLogRecord {
  if (!value || typeof value !== "object") return false;
  const record = value as Partial<ARRecoveryCallLogRecord>;
  return Boolean(record.id && record.companyId && record.accountId && record.customerName && record.outcome && record.createdAt);
}

function isARRecoveryNoteLogRecord(value: unknown): value is ARRecoveryNoteLogRecord {
  if (!value || typeof value !== "object") return false;
  const record = value as Partial<ARRecoveryNoteLogRecord>;
  return Boolean(record.id && record.companyId && record.accountId && record.customerName && record.noteType && record.visibility && record.content && record.createdAt);
}

function isARRecoveryCommunicationRecord(value: unknown): value is ARRecoveryCommunicationRecord {
  if (!value || typeof value !== "object") return false;
  const record = value as Partial<ARRecoveryCommunicationRecord>;
  return Boolean(record.id && record.conversationId && record.companyId && record.accountId && record.customerName && record.sender && record.recipient && record.messageType && record.message && record.createdAt);
}

function isARRecoveryTaskAssignmentRecord(value: unknown): value is ARRecoveryTaskAssignmentRecord {
  if (!value || typeof value !== "object") return false;
  const record = value as Partial<ARRecoveryTaskAssignmentRecord>;
  return Boolean(record.id && record.companyId && record.accountId && record.customerName && record.taskTitle && record.category && record.assignedBy && record.assignedTo && record.priority && record.dueDate && record.description && record.status && record.createdAt);
}

function isARRecoveryPromiseWorkflowRecord(value: unknown): value is ARRecoveryPromiseWorkflowRecord {
  if (!value || typeof value !== "object") return false;
  const record = value as Partial<ARRecoveryPromiseWorkflowRecord>;
  return Boolean(record.id && record.companyId && record.accountId && record.customerName && record.customerNumber && record.promiseDate && record.amountCents && record.status && record.promiseType && record.createdAt);
}

function isARRecoveryTicklerWorkflowRecord(value: unknown): value is ARRecoveryTicklerWorkflowRecord {
  if (!value || typeof value !== "object") return false;
  const record = value as Partial<ARRecoveryTicklerWorkflowRecord>;
  return Boolean(record.id && record.companyId && record.accountId && record.customerName && record.title && record.ticklerType && record.dueDate && record.status && record.priority && record.assignedCollector && record.createdAt);
}

function isARRecoveryLetterWorkflowRecord(value: unknown): value is ARRecoveryLetterWorkflowRecord {
  if (!value || typeof value !== "object") return false;
  const record = value as Partial<ARRecoveryLetterWorkflowRecord>;
  return Boolean(record.id && record.companyId && record.accountId && record.customerName && record.customerNumber && record.template && record.status && record.createdAt && record.generatedBy && record.generatedAt);
}

function isARRecoveryStatementWorkflowRecord(value: unknown): value is ARRecoveryStatementWorkflowRecord {
  if (!value || typeof value !== "object") return false;
  const record = value as Partial<ARRecoveryStatementWorkflowRecord>;
  return Boolean(record.id && record.companyId && record.accountId && record.customerName && record.customerNumber && record.statementNumber && record.status && record.createdAt && record.generatedBy && record.generatedAt);
}

function isARRecoveryDocumentRequestRecord(value: unknown): value is ARRecoveryDocumentRequestRecord {
  if (!value || typeof value !== "object") return false;
  const record = value as Partial<ARRecoveryDocumentRequestRecord>;
  return Boolean(record.id && record.companyId && record.accountId && record.customerName && record.customerNumber && record.requestType && record.requestedFrom && record.dueDate && record.status && record.requestedBy && record.requestedAt);
}

function isARRecoveryAccountEditRecord(value: unknown): value is ARRecoveryAccountEditRecord {
  if (!value || typeof value !== "object") return false;
  const record = value as Partial<ARRecoveryAccountEditRecord>;
  return Boolean(record.id && record.companyId && record.accountId && record.customerName && record.customerNumber && record.actorName && record.actorRole && record.createdAt && Array.isArray(record.changes) && record.values);
}

function getEditableAccountExtras(account: ReturnType<typeof loadARRecoveryWorkspace>["accounts"][number]) {
  return account as ReturnType<typeof loadARRecoveryWorkspace>["accounts"][number] & ARRecoveryEditableAccountExtras;
}

function normalizeCapturedValue(value?: string | null) {
  return value?.trim() || "Not Captured";
}

function formatBalanceInput(cents: number) {
  return (cents / 100).toFixed(2);
}

function parseCurrencyInputToCents(value: string) {
  const normalized = value.replace(/[$,\s]/g, "");
  const amount = Number(normalized);
  return Number.isFinite(amount) ? Math.round(amount * 100) : Number.NaN;
}

function buildAccountEditFormFromAccount(
  account: ReturnType<typeof loadARRecoveryWorkspace>["accounts"][number],
  clientCompany: string,
): ARRecoveryAccountEditFormState {
  const editableAccount = getEditableAccountExtras(account);
  return {
    customerName: account.customerName,
    customerNumber: account.customerNumber,
    accountNumber: editableAccount.accountNumber || account.customerNumber,
    clientCompany: editableAccount.clientCompany || clientCompany,
    assignedCollector: account.assignedCollector,
    assignedManager: editableAccount.assignedManager || "Not Captured",
    accountStatus: account.recoveryStatus,
    currentBalance: formatBalanceInput(account.openBalanceCents),
    agingBucket: account.agingSummary,
    promiseStatus: editableAccount.promiseStatus || (account.promiseToPayCount ? "Open promise" : "No promise on file"),
    primaryContact: account.contactName,
    contactTitle: editableAccount.contactTitle || "Not Captured",
    phone: account.contactPhone,
    email: account.contactEmail,
    preferredContactMethod: editableAccount.preferredContactMethod || "Email + Phone",
    additionalContacts: editableAccount.additionalContacts || "Not Captured",
    collectorOwner: editableAccount.collectorOwner || account.assignedCollector,
    holdStatus: editableAccount.holdStatus || "No Hold",
    disputeStatus: editableAccount.disputeStatus || (account.escalationStatus === "None" ? "No Dispute" : "Under Review"),
    nextFollowUpDate: editableAccount.nextFollowUpDate || "",
    recoveryInstructions: editableAccount.recoveryInstructions || account.notes || "Not Captured",
  };
}

function buildAccountEditChanges(
  account: ReturnType<typeof loadARRecoveryWorkspace>["accounts"][number],
  clientCompany: string,
  form: ARRecoveryAccountEditFormState,
) {
  const previous = buildAccountEditFormFromAccount(account, clientCompany);
  const fields: Array<{ key: keyof ARRecoveryAccountEditFormState; label: string; format?: (value: string) => string }> = [
    { key: "customerName", label: "Customer Name" },
    { key: "customerNumber", label: "Customer Number" },
    { key: "accountNumber", label: "Account Number" },
    { key: "clientCompany", label: "Client Company" },
    { key: "assignedCollector", label: "Assigned Collector" },
    { key: "assignedManager", label: "Assigned Manager" },
    { key: "accountStatus", label: "Account Status" },
    { key: "currentBalance", label: "Current Balance", format: (value) => formatARRecoveryCurrency(parseCurrencyInputToCents(value)) },
    { key: "agingBucket", label: "Aging Bucket" },
    { key: "promiseStatus", label: "Promise Status" },
    { key: "primaryContact", label: "Primary Contact" },
    { key: "contactTitle", label: "Contact Title" },
    { key: "phone", label: "Phone" },
    { key: "email", label: "Email" },
    { key: "preferredContactMethod", label: "Preferred Contact Method" },
    { key: "additionalContacts", label: "Additional Contacts" },
    { key: "collectorOwner", label: "Collector Owner" },
    { key: "holdStatus", label: "Hold Status" },
    { key: "disputeStatus", label: "Dispute Status" },
    { key: "nextFollowUpDate", label: "Next Follow-Up Date", format: (value) => value ? formatCapturedDate(value) : "Not Captured" },
    { key: "recoveryInstructions", label: "Recovery Instructions" },
  ];

  return fields.flatMap((field) => {
    const previousValue = String(previous[field.key] || "").trim();
    const newValue = String(form[field.key] || "").trim();
    if (previousValue === newValue) return [];
    return [{
      field: field.label,
      previousValue: field.format ? field.format(previousValue) : normalizeCapturedValue(previousValue),
      newValue: field.format ? field.format(newValue) : normalizeCapturedValue(newValue),
    }];
  });
}

function applyAccountEditRecord(
  account: ReturnType<typeof loadARRecoveryWorkspace>["accounts"][number],
  edit: ARRecoveryAccountEditRecord,
  activeInternalUsers: ARRecoveryActiveInternalUserOption[] = [],
): ReturnType<typeof loadARRecoveryWorkspace>["accounts"][number] & ARRecoveryEditableAccountExtras {
  const balanceCents = parseCurrencyInputToCents(edit.values.currentBalance);
  const resolvedAssignedCollector = resolveCanonicalAssignedCollectorFromEdit(edit, account.assignedCollector, activeInternalUsers);
  return {
    ...account,
    customerName: edit.values.customerName.trim() || account.customerName,
    customerNumber: edit.values.customerNumber.trim() || account.customerNumber,
    contactName: edit.values.primaryContact.trim() || account.contactName,
    contactEmail: edit.values.email.trim() || account.contactEmail,
    contactPhone: edit.values.phone.trim() || account.contactPhone,
    agingSummary: edit.values.agingBucket.trim() || account.agingSummary,
    openBalanceCents: Number.isFinite(balanceCents) ? balanceCents : account.openBalanceCents,
    recoveryStatus: edit.values.accountStatus || account.recoveryStatus,
    assignedCollector: resolvedAssignedCollector,
    notes: edit.values.recoveryInstructions.trim() || account.notes,
    updatedAt: edit.createdAt,
    accountNumber: edit.values.accountNumber.trim(),
    clientCompany: edit.values.clientCompany.trim(),
    assignedManager: edit.values.assignedManager.trim(),
    promiseStatus: edit.values.promiseStatus.trim(),
    contactTitle: edit.values.contactTitle.trim(),
    preferredContactMethod: edit.values.preferredContactMethod.trim(),
    additionalContacts: edit.values.additionalContacts.trim(),
    collectorOwner: edit.values.collectorOwner.trim(),
    holdStatus: edit.values.holdStatus.trim(),
    disputeStatus: edit.values.disputeStatus.trim(),
    nextFollowUpDate: edit.values.nextFollowUpDate,
    recoveryInstructions: edit.values.recoveryInstructions.trim(),
  };
}

function isAssignedCollectorEdit(edit: ARRecoveryAccountEditRecord) {
  return Boolean(
    edit.assignment?.assignedCollectorId
      || edit.assignment?.assignedCollectorName
      || edit.changes.some((change) => change.field.trim().toLowerCase() === "assigned collector"),
  );
}

function isCurrentBalanceEdit(edit: ARRecoveryAccountEditRecord) {
  return edit.changes.some((change) => change.field.trim().toLowerCase() === "current balance");
}

function resolvePaymentProjectionTime(payment: ARWorkflowPaymentProjection) {
  return getDateTime(String(payment.updatedAt || payment.recordedAt || payment.createdAt || ""));
}

function resolveCanonicalAccountOpenBalanceCents(input: {
  seedAccount: ReturnType<typeof loadARRecoveryWorkspace>["accounts"][number];
  latestBalanceEdit?: ARRecoveryAccountEditRecord;
  accountPayments: ARWorkflowPaymentProjection[];
}) {
  const latestBalanceEdit = input.latestBalanceEdit;
  const balanceEditCents = latestBalanceEdit
    ? parseCurrencyInputToCents(latestBalanceEdit.values.currentBalance)
    : Number.NaN;
  const hasCanonicalBalanceEdit = Boolean(latestBalanceEdit && Number.isFinite(balanceEditCents));
  const balanceBaseCents = hasCanonicalBalanceEdit ? balanceEditCents : input.seedAccount.openBalanceCents;
  const balanceBaseTimestamp = latestBalanceEdit && hasCanonicalBalanceEdit ? getDateTime(latestBalanceEdit.createdAt) : Number.NEGATIVE_INFINITY;
  const appliedPaymentCents = input.accountPayments
    .filter((payment) => resolvePaymentProjectionTime(payment) > balanceBaseTimestamp)
    .reduce((total, payment) => total + payment.amountCents, 0);

  return Math.max(0, balanceBaseCents - appliedPaymentCents);
}

function resolveCanonicalAssignedCollectorFromEdit(
  edit: ARRecoveryAccountEditRecord,
  fallbackAssignedCollector: string,
  activeInternalUsers: ARRecoveryActiveInternalUserOption[],
) {
  const assignedCollectorId = edit.assignment?.assignedCollectorId?.trim();
  if (assignedCollectorId) {
    const matchedUser = activeInternalUsers.find((user) => user.id === assignedCollectorId);
    if (matchedUser) return matchedUser.name;
  }
  return edit.assignment?.assignedCollectorName?.trim()
    || edit.values.assignedCollector.trim()
    || fallbackAssignedCollector;
}

function mergeWorkspaceWithOperationalLogs(
  workspace: ReturnType<typeof loadARRecoveryWorkspace>,
  callLogs: ARRecoveryCallLogRecord[],
  noteLogs: ARRecoveryNoteLogRecord[],
  communicationLogs: ARRecoveryCommunicationRecord[],
  taskAssignments: ARRecoveryTaskAssignmentRecord[],
  promiseWorkflows: ARRecoveryPromiseWorkflowRecord[],
  ticklerWorkflows: ARRecoveryTicklerWorkflowRecord[],
  letterWorkflows: ARRecoveryLetterWorkflowRecord[],
  statementWorkflows: ARRecoveryStatementWorkflowRecord[],
  escalationWorkflows: ARWorkflowEscalationProjection[],
  paymentWorkflows: ARWorkflowPaymentProjection[],
  notificationRecords: ARWorkflowNotificationProjection[],
  reportingAnalytics: ARWorkflowReportingProjection,
  clientExchangeFoundation: EnterpriseClientExchangeFoundationProjection,
  portfolioValidationFoundation: PortfolioValidationEngineResult,
  portfolioMappingFoundation: PortfolioMappingEngineResult,
  portfolioReviewFoundation: PortfolioReviewEngineResult,
  portfolioImportFoundation: PortfolioImportEngineResult,
  portfolioAssignmentFoundation: PortfolioAssignmentEngineResult,
  portfolioActivationFoundation: PortfolioActivationEngineResult,
  portfolioIntakeWalkthrough: PortfolioPipelineWalkthroughResult,
  documentDeliveries: ARRecoveryDocumentDeliveryRecord[],
  documentRequests: ARRecoveryDocumentRequestRecord[],
  accountEdits: ARRecoveryAccountEditRecord[] = [],
): ReturnType<typeof loadARRecoveryWorkspace> {
  const companyCallLogs = callLogs.filter((call) => call.companyId === workspace.company.id);
  const companyNoteLogs = noteLogs.filter((note) => note.companyId === workspace.company.id);
  const companyCommunicationLogs = communicationLogs.filter((message) => message.companyId === workspace.company.id);
  const companyTaskAssignments = taskAssignments.filter((task) => task.companyId === workspace.company.id);
  const companyPromiseWorkflows = promiseWorkflows.filter((promise) => promise.companyId === workspace.company.id);
  const companyTicklerWorkflows = ticklerWorkflows.filter((tickler) => tickler.companyId === workspace.company.id);
  const companyLetterWorkflows = letterWorkflows.filter((letter) => letter.companyId === workspace.company.id);
  const companyStatementWorkflows = statementWorkflows.filter((statement) => statement.companyId === workspace.company.id);
  const companyEscalationWorkflows = escalationWorkflows.filter((escalation) => escalation.companyId === workspace.company.id);
  const companyPaymentWorkflows = paymentWorkflows.filter((payment) => payment.companyId === workspace.company.id);
  const companyNotificationRecords = notificationRecords.filter((notification) => notification.tenantId === workspace.company.id);
  const companyReportingAnalytics = reportingAnalytics.tenantId === workspace.company.id ? reportingAnalytics : null;
  const companyClientExchangeFoundation = {
    ...clientExchangeFoundation,
    batches: clientExchangeFoundation.batches.filter((batch) => batch.tenantId === workspace.company.id),
  };
  const companyPortfolioIntakeWalkthrough = portfolioIntakeWalkthrough.tenantId === workspace.company.id ? portfolioIntakeWalkthrough : null;
  const companyPortfolioValidationFoundation = portfolioValidationFoundation.tenantId === workspace.company.id ? portfolioValidationFoundation : null;
  const companyPortfolioMappingFoundation = portfolioMappingFoundation.mappingProfile.tenantId === workspace.company.id ? portfolioMappingFoundation : null;
  const companyPortfolioReviewFoundation = portfolioReviewFoundation.tenantId === workspace.company.id ? portfolioReviewFoundation : null;
  const companyPortfolioImportFoundation = portfolioImportFoundation.execution.tenantId === workspace.company.id ? portfolioImportFoundation : null;
  const companyPortfolioAssignmentFoundation = portfolioAssignmentFoundation.assignment.tenantId === workspace.company.id ? portfolioAssignmentFoundation : null;
  const companyPortfolioActivationFoundation = portfolioActivationFoundation.activation.tenantId === workspace.company.id ? portfolioActivationFoundation : null;
  const companyDocumentDeliveries = documentDeliveries.filter((delivery) => delivery.companyId === workspace.company.id);
  const companyDocumentRequests = documentRequests.filter((documentRequest) => documentRequest.companyId === workspace.company.id);
  const companyAccountEdits = accountEdits.filter((edit) => edit.companyId === workspace.company.id);
  const activeInternalUsers = buildActiveInternalUserOptions(loadBusinessCenterCompanyUsers());
  void companyNotificationRecords;
  void companyReportingAnalytics;
  void companyClientExchangeFoundation;
  void companyPortfolioValidationFoundation;
  void companyPortfolioMappingFoundation;
  void companyPortfolioReviewFoundation;
  void companyPortfolioImportFoundation;
  void companyPortfolioAssignmentFoundation;
  void companyPortfolioActivationFoundation;
  void companyPortfolioIntakeWalkthrough;
  const latestAccountEditByAccountId = new Map<string, ARRecoveryAccountEditRecord>();
  companyAccountEdits
    .sort((a, b) => getDateTime(a.createdAt) - getDateTime(b.createdAt))
    .forEach((edit) => latestAccountEditByAccountId.set(edit.accountId, edit));
  const latestBalanceEditByAccountId = new Map<string, ARRecoveryAccountEditRecord>();
  companyAccountEdits
    .filter(isCurrentBalanceEdit)
    .sort((a, b) => getDateTime(a.createdAt) - getDateTime(b.createdAt))
    .forEach((edit) => latestBalanceEditByAccountId.set(edit.accountId, edit));
  const latestAssignedCollectorEditByAccountId = new Map<string, ARRecoveryAccountEditRecord>();
  companyAccountEdits
    .filter(isAssignedCollectorEdit)
    .sort((a, b) => getDateTime(a.createdAt) - getDateTime(b.createdAt))
    .forEach((edit) => latestAssignedCollectorEditByAccountId.set(edit.accountId, edit));
  const workflowPromiseIds = new Set(companyPromiseWorkflows.map((promise) => promise.id));
  const mergedPromises = [...companyPromiseWorkflows, ...workspace.promises.filter((promise) => !workflowPromiseIds.has(promise.id))].sort((a, b) => getDateTime(b.updatedAt || b.createdAt) - getDateTime(a.updatedAt || a.createdAt));
  const workflowTicklerIds = new Set(companyTicklerWorkflows.map((tickler) => tickler.id));
  const mergedTicklers = [...companyTicklerWorkflows, ...workspace.ticklers.filter((tickler) => !workflowTicklerIds.has(tickler.id))].sort((a, b) => getDateTime(a.dueDate) - getDateTime(b.dueDate));
  const workflowLetterIds = new Set(companyLetterWorkflows.map((letter) => letter.id));
  const mergedLetters = [...companyLetterWorkflows, ...workspace.collectionLetters.filter((letter) => !workflowLetterIds.has(letter.id))].sort((a, b) => getDateTime(b.updatedAt || b.createdAt) - getDateTime(a.updatedAt || a.createdAt));
  const workflowStatementIds = new Set(companyStatementWorkflows.map((statement) => statement.id));
  const mergedStatements = [...companyStatementWorkflows, ...workspace.statements.filter((statement) => !workflowStatementIds.has(statement.id))].sort((a, b) => getDateTime(b.updatedAt || b.createdAt) - getDateTime(a.updatedAt || a.createdAt));
  const editedAccounts = workspace.accounts.map((account) => {
    const latestEdit = latestAccountEditByAccountId.get(account.id);
    const latestAssignmentEdit = latestAssignedCollectorEditByAccountId.get(account.id);
    const editedAccount = latestEdit ? applyAccountEditRecord(account, latestEdit, activeInternalUsers) : account;
    return latestAssignmentEdit
      ? {
          ...editedAccount,
          assignedCollector: resolveCanonicalAssignedCollectorFromEdit(latestAssignmentEdit, editedAccount.assignedCollector, activeInternalUsers),
        }
      : editedAccount;
  });
  const mergedAccounts = editedAccounts.map((account) => {
    const activePromiseCount = mergedPromises.filter((promise) => promise.accountId === account.id && !isClosedPromiseLifecycleStatus(promise.status)).length;
    const accountPayments = companyPaymentWorkflows
      .filter((payment) => payment.accountId === account.id && payment.applicationScope === "account" && payment.lifecycleStatus !== "Voided");
    return {
      ...account,
      promiseToPayCount: activePromiseCount,
      openBalanceCents: resolveCanonicalAccountOpenBalanceCents({
        seedAccount: workspace.accounts.find((candidate) => candidate.id === account.id) || account,
        latestBalanceEdit: latestBalanceEditByAccountId.get(account.id),
        accountPayments,
      }),
      recoveryStatus: activePromiseCount > 0 && account.recoveryStatus === "Current" ? "Payment Plan" as const : account.recoveryStatus,
      updatedAt: activePromiseCount !== account.promiseToPayCount || accountPayments.length > 0 ? new Date().toISOString() : account.updatedAt,
    };
  });
  const accountEditActivities = companyAccountEdits.map((edit) => ({
    id: edit.id,
    companyId: edit.companyId,
    accountId: edit.accountId,
    customerName: edit.customerName,
    actorName: edit.actorName,
    kind: "Note" as const,
    title: "Updated Account Information",
    detail: buildAccountEditActivityDetail(edit),
    createdAt: edit.createdAt,
  }));
  const accountEditEvents = companyAccountEdits.map((edit) => ({
    id: `${edit.id}-audit`,
    companyId: edit.companyId,
    accountId: edit.accountId,
    customerName: edit.customerName,
    actorName: edit.actorName,
    actorRole: "collector" as const,
    eventType: "status_changed" as const,
    relatedRecordType: "account" as const,
    relatedRecordId: edit.accountId,
    title: "Updated Account Information",
    detail: buildAccountEditActivityDetail(edit),
    outcome: `${edit.changes.length} field${edit.changes.length === 1 ? "" : "s"} updated`,
    visibility: "internal" as const,
    createdAt: edit.createdAt,
    metadata: {
      actorRole: edit.actorRole,
      changes: edit.changes,
      workflow: "account_information_update",
    },
  }));
  const callActivities = companyCallLogs.map((call) => ({
    id: call.id,
    companyId: call.companyId,
    accountId: call.accountId,
    customerName: call.customerName,
    actorName: call.collector,
    kind: "Call" as const,
    title: `Call Logged - ${call.outcome}`,
    detail: buildCallActivityDetail(call),
    createdAt: call.createdAt,
  }));
  const callEvents = companyCallLogs.map((call) => ({
    id: `${call.id}-audit`,
    companyId: call.companyId,
    accountId: call.accountId,
    customerName: call.customerName,
    actorName: call.collector,
    actorRole: "collector" as const,
    eventType: "status_changed" as const,
    relatedRecordType: "account" as const,
    relatedRecordId: call.accountId,
    title: `Call logged for ${call.customerName}`,
    detail: buildCallActivityDetail(call),
    outcome: call.outcome,
    visibility: "internal" as const,
    createdAt: call.createdAt,
    metadata: {
      durationMinutes: call.durationMinutes,
      notes: call.notes,
      nextAction: call.nextAction,
      workflow: "log_call",
    },
  }));
  const noteActivities = companyNoteLogs.map((note) => ({
    id: note.id,
    companyId: note.companyId,
    accountId: note.accountId,
    customerName: note.customerName,
    actorName: note.collector,
    kind: "Note" as const,
    title: `${note.noteType} Added`,
    detail: buildNoteActivityDetail(note),
    createdAt: note.createdAt,
  }));
  const noteEvents = companyNoteLogs.map((note) => ({
    id: `${note.id}-audit`,
    companyId: note.companyId,
    accountId: note.accountId,
    customerName: note.customerName,
    actorName: note.collector,
    actorRole: "collector" as const,
    eventType: "note_added" as const,
    relatedRecordType: "account" as const,
    relatedRecordId: note.accountId,
    title: `${note.noteType} added for ${note.customerName}`,
    detail: buildNoteActivityDetail(note),
    notes: note.content,
    outcome: note.outcome || "Not Captured",
    visibility: note.visibility,
    createdAt: note.createdAt,
    metadata: {
      noteType: note.noteType,
      nextAction: note.nextAction,
      workflow: "add_note",
    },
  }));
  const communicationActivities = companyCommunicationLogs.map((message) => ({
    id: message.id,
    companyId: message.companyId,
    accountId: message.accountId,
    customerName: message.customerName,
    actorName: message.sender,
    kind: "Email" as const,
    title: `${message.parentMessageId ? "Team Reply" : "Team Message"} - ${message.messageType}`,
    detail: buildCommunicationActivityDetail(message),
    createdAt: message.createdAt,
  }));
  const communicationEvents = companyCommunicationLogs.map((message) => ({
    id: `${message.id}-audit`,
    companyId: message.companyId,
    accountId: message.accountId,
    customerName: message.customerName,
    actorName: message.sender,
    actorRole: "collector" as const,
    eventType: "status_changed" as const,
    relatedRecordType: "message" as const,
    relatedRecordId: message.id,
    title: `Internal communication sent to ${message.recipient}`,
    detail: buildCommunicationActivityDetail(message),
    outcome: message.messageType,
    visibility: "internal" as const,
    createdAt: message.createdAt,
    metadata: {
      communicationType: message.messageType,
      conversationId: message.conversationId,
      importance: message.importance,
      parentMessageId: message.parentMessageId || "",
      recipient: message.recipient,
      subject: message.subject,
      workflow: "team_communication",
    },
  }));
  const taskActivities = companyTaskAssignments.flatMap((task) => buildTaskAssignmentActivities(task));
  const taskEvents = companyTaskAssignments.flatMap((task) => buildTaskAssignmentEvents(task));
  const promiseActivities = companyPromiseWorkflows.flatMap((promise) => buildPromiseWorkflowActivities(promise));
  const promiseEvents = companyPromiseWorkflows.flatMap((promise) => buildPromiseWorkflowEvents(promise));
  const ticklerActivities = companyTicklerWorkflows.flatMap((tickler) => buildTicklerWorkflowActivities(tickler));
  const ticklerEvents = companyTicklerWorkflows.flatMap((tickler) => buildTicklerWorkflowEvents(tickler));
  const letterActivities = companyLetterWorkflows.flatMap((letter) => buildLetterWorkflowActivities(letter));
  const letterEvents = companyLetterWorkflows.flatMap((letter) => buildLetterWorkflowEvents(letter));
  const statementActivities = companyStatementWorkflows.flatMap((statement) => buildStatementWorkflowActivities(statement));
  const statementEvents = companyStatementWorkflows.flatMap((statement) => buildStatementWorkflowEvents(statement));
  const escalationActivities = companyEscalationWorkflows.map((escalation) => buildEscalationWorkflowActivity(escalation));
  const escalationEvents = companyEscalationWorkflows.map((escalation) => buildEscalationWorkflowRecoveryEvent(escalation));
  const paymentActivities = companyPaymentWorkflows.map((payment) => buildPaymentWorkflowActivity(payment));
  const paymentEvents = companyPaymentWorkflows.map((payment) => buildPaymentWorkflowRecoveryEvent(payment));
  const documentDeliveryActivities = companyDocumentDeliveries.map((delivery) => ({
    id: `${delivery.id}-activity`,
    companyId: delivery.companyId,
    accountId: delivery.accountId,
    customerName: delivery.customerName,
    actorName: delivery.generatedBy,
    kind: delivery.family === "letter" ? "Letter" as const : "Statement" as const,
    title: `${delivery.documentType} ${delivery.actions[delivery.actions.length - 1]?.action || "Generated"}`,
    detail: buildARRecoveryDocumentDeliveryActivityDetail(delivery),
    createdAt: delivery.updatedAt || delivery.generatedAt || delivery.createdAt,
  }));
  const documentDeliveryEvents = companyDocumentDeliveries.map((delivery) => ({
    id: `${delivery.id}-audit`,
    companyId: delivery.companyId,
    accountId: delivery.accountId,
    customerName: delivery.customerName,
    actorName: delivery.generatedBy,
    actorRole: "collector" as const,
    eventType: "status_changed" as const,
    relatedRecordType: delivery.family === "letter" ? ("letter" as const) : ("statement" as const),
    relatedRecordId: delivery.id,
    title: `${delivery.documentType} ${delivery.actions[delivery.actions.length - 1]?.action || "Generated"} for ${delivery.customerName}`,
    detail: buildARRecoveryDocumentDeliveryActivityDetail(delivery),
    outcome: delivery.deliveryStatus === "sent" ? "Sent" : delivery.deliveryStatus === "failed" ? "Failed" : "Generated",
    visibility: "internal" as const,
    createdAt: delivery.updatedAt || delivery.generatedAt || delivery.createdAt,
    metadata: {
      templateId: delivery.templateId,
      templateName: delivery.templateName,
      deliveryStatus: delivery.deliveryStatus,
      generatedBy: delivery.generatedBy,
      generatedAt: delivery.generatedAt,
      family: delivery.family,
      relatedPromiseId: delivery.relatedPromiseId,
      relatedTaskId: delivery.relatedTaskId,
    },
  }));
  const documentActivities = companyDocumentRequests.flatMap((documentRequest) => buildDocumentRequestActivities(documentRequest));
  const documentEvents = companyDocumentRequests.flatMap((documentRequest) => buildDocumentRequestEvents(documentRequest));

  return {
    ...workspace,
    accounts: mergedAccounts,
    promises: mergedPromises,
    ticklers: mergedTicklers,
    collectionLetters: mergedLetters,
    statements: mergedStatements,
    activity: [...accountEditActivities, ...callActivities, ...noteActivities, ...communicationActivities, ...taskActivities, ...promiseActivities, ...ticklerActivities, ...letterActivities, ...statementActivities, ...escalationActivities, ...paymentActivities, ...documentDeliveryActivities, ...documentActivities, ...workspace.activity].sort((a, b) => getDateTime(b.createdAt) - getDateTime(a.createdAt)),
    recoveryActivityEvents: [...accountEditEvents, ...callEvents, ...noteEvents, ...communicationEvents, ...taskEvents, ...promiseEvents, ...ticklerEvents, ...letterEvents, ...statementEvents, ...escalationEvents, ...paymentEvents, ...documentDeliveryEvents, ...documentEvents, ...workspace.recoveryActivityEvents].sort((a, b) => getDateTime(b.createdAt) - getDateTime(a.createdAt)),
  };
}

function buildAccountEditActivityDetail(edit: ARRecoveryAccountEditRecord) {
  const changedFields = edit.changes
    .map((change) => `${change.field}: Previous: ${change.previousValue}; New: ${change.newValue}`)
    .join(" | ");
  return `Authenticated User: ${edit.actorName} | Role: ${edit.actorRole} | Action: Updated Account Information | ${changedFields}`;
}

function buildCallActivityDetail(call: ARRecoveryCallLogRecord) {
  const duration = call.durationMinutes ? `${call.durationMinutes} min` : "Not Captured";
  return `Outcome: ${call.outcome} | Duration: ${duration} | Notes: ${call.notes} | Next Action: ${call.nextAction}`;
}

function buildNoteActivityDetail(note: ARRecoveryNoteLogRecord) {
  return `Note Type: ${note.noteType} | Visibility: ${formatNoteVisibility(note.visibility)} | Outcome: ${note.outcome || "Not Captured"} | Notes: ${note.content} | Next Action: ${note.nextAction || "Not Captured"}`;
}

function buildCommunicationActivityDetail(message: ARRecoveryCommunicationRecord) {
  return `Message Type: ${message.messageType} | Sender: ${message.sender} | Recipient: ${message.recipient} | Subject: ${message.subject || "Not Captured"} | Importance: ${message.importance} | Message: ${message.message}`;
}

function buildTaskAssignmentActivityDetail(task: ARRecoveryTaskAssignmentRecord) {
  return `Task Category: ${task.category} | Assigned By: ${task.assignedBy} | Assigned To: ${task.assignedTo} | Priority: ${task.priority} | Due Date: ${formatCapturedDate(task.dueDate)} | Status: ${task.status} | Description: ${task.description}`;
}

function buildTaskCompletionDetail(task: ARRecoveryTaskAssignmentRecord) {
  return `Completed By: ${task.completedBy || "Not Captured"} | Completion Notes: ${task.completionNotes || "Not Captured"} | Outcome: ${task.outcome || "Not Captured"} | Next Action: ${task.nextAction || "Not Captured"}`;
}

function buildTaskAssignmentActivities(task: ARRecoveryTaskAssignmentRecord) {
  const activities = [
    {
      id: `${task.id}-assigned`,
      companyId: task.companyId,
      accountId: task.accountId,
      customerName: task.customerName,
      actorName: task.assignedBy,
      kind: "Task" as const,
      title: `Task Assigned - ${task.taskTitle}`,
      detail: buildTaskAssignmentActivityDetail(task),
      createdAt: task.createdAt,
    },
  ];

  task.comments.forEach((comment) => {
    activities.push({
      id: `${comment.id}-activity`,
      companyId: task.companyId,
      accountId: task.accountId,
      customerName: task.customerName,
      actorName: comment.author,
      kind: "Task" as const,
      title: `Task Comment - ${task.taskTitle}`,
      detail: `Comment By: ${comment.author} | Comment: ${comment.comment}`,
      createdAt: comment.createdAt,
    });
  });

  if (task.status === "Completed" && task.completedAt) {
    activities.push({
      id: `${task.id}-completed`,
      companyId: task.companyId,
      accountId: task.accountId,
      customerName: task.customerName,
      actorName: task.completedBy || task.assignedBy,
      kind: "Task" as const,
      title: `Task Completed - ${task.taskTitle}`,
      detail: buildTaskCompletionDetail(task),
      createdAt: task.completedAt,
    });
  }

  return activities;
}

function buildTaskAssignmentEvents(task: ARRecoveryTaskAssignmentRecord) {
  const events: ReturnType<typeof loadARRecoveryWorkspace>["recoveryActivityEvents"] = [
    {
      id: `${task.id}-assigned-audit`,
      companyId: task.companyId,
      accountId: task.accountId,
      customerName: task.customerName,
      actorName: task.assignedBy,
      actorRole: "collector" as const,
      eventType: "task_created" as const,
      relatedRecordType: "account" as const,
      relatedRecordId: task.accountId,
      title: `Task assigned to ${task.assignedTo}`,
      detail: buildTaskAssignmentActivityDetail(task),
      outcome: task.status,
      visibility: "internal" as const,
      createdAt: task.createdAt,
      metadata: {
        taskId: task.id,
        taskTitle: task.taskTitle,
        category: task.category,
        priority: task.priority,
        dueDate: task.dueDate,
        relatedInvoice: task.relatedInvoice,
        relatedPromise: task.relatedPromise,
        relatedLetter: task.relatedLetter,
        relatedStatement: task.relatedStatement,
        workflow: "task_assignment",
      },
    },
  ];

  task.comments.forEach((comment) => {
    events.push({
      id: `${comment.id}-audit`,
      companyId: task.companyId,
      accountId: task.accountId,
      customerName: task.customerName,
      actorName: comment.author,
      actorRole: "collector" as const,
      eventType: "task_comment_added" as const,
      relatedRecordType: "account" as const,
      relatedRecordId: task.accountId,
      title: `Task comment added for ${task.taskTitle}`,
      detail: `Comment: ${comment.comment}`,
      outcome: "Task discussion updated",
      visibility: "internal" as const,
      createdAt: comment.createdAt,
      metadata: {
        taskId: task.id,
        taskTitle: task.taskTitle,
        workflow: "task_assignment_comment",
      },
    });
  });

  if (task.status === "Completed" && task.completedAt) {
    events.push({
      id: `${task.id}-completed-audit`,
      companyId: task.companyId,
      accountId: task.accountId,
      customerName: task.customerName,
      actorName: task.completedBy || task.assignedBy,
      actorRole: "collector" as const,
      eventType: "task_completed" as const,
      relatedRecordType: "account" as const,
      relatedRecordId: task.accountId,
      title: `Task completed by ${task.completedBy || "Not Captured"}`,
      detail: buildTaskCompletionDetail(task),
      outcome: task.outcome || "Completed",
      visibility: "internal" as const,
      createdAt: task.completedAt,
      metadata: {
        taskId: task.id,
        taskTitle: task.taskTitle,
        completionNotes: task.completionNotes,
        nextAction: task.nextAction,
        workflow: "task_completion",
      },
    });
  }

  return events;
}

function buildEscalationWorkflowActivity(escalation: ARWorkflowEscalationProjection) {
  return {
    id: `${escalation.id}-activity`,
    companyId: escalation.companyId,
    accountId: escalation.accountId,
    customerName: escalation.customerName,
    actorName: String(escalation.actorName || escalation.createdBy || escalation.assignedTo || "Manager Command"),
    kind: "Escalation" as const,
    title: `Escalation ${escalation.status}`,
    detail: buildEscalationWorkflowActivityDetail(escalation),
    createdAt: escalation.updatedAt || escalation.createdAt,
  };
}

function buildEscalationWorkflowRecoveryEvent(escalation: ARWorkflowEscalationProjection) {
  return {
    id: `${escalation.id}-audit`,
    companyId: escalation.companyId,
    accountId: escalation.accountId,
    customerName: escalation.customerName,
    actorName: String(escalation.actorName || escalation.createdBy || escalation.assignedTo || "Manager Command"),
    actorRole: "manager" as const,
    eventType: resolveEscalationRecoveryEventType(escalation.status),
    relatedRecordType: "escalation" as const,
    relatedRecordId: escalation.id,
    title: `Escalation ${escalation.status} for ${escalation.customerName}`,
    detail: buildEscalationWorkflowActivityDetail(escalation),
    outcome: escalation.status,
    visibility: "manager_only" as const,
    createdAt: escalation.updatedAt || escalation.createdAt,
    metadata: {
      escalationId: escalation.id,
      priority: escalation.priority,
      reason: escalation.reason,
      assignedTo: escalation.assignedTo,
      sourceTrigger: escalation.sourceTrigger,
      sourceEventType: escalation.sourceEventType,
      sourceRecordType: escalation.sourceRecordType,
      sourceRecordId: escalation.sourceRecordId,
      workflow: "enterprise_escalation_engine",
    },
  };
}

function resolveEscalationRecoveryEventType(status: string) {
  const normalizedStatus = status.toLowerCase();
  if (normalizedStatus === "resolved" || normalizedStatus === "closed") return "escalation_resolved" as const;
  return "escalation_created" as const;
}

function buildEscalationWorkflowActivityDetail(escalation: ARWorkflowEscalationProjection) {
  const reassignmentDetail = escalation.decision === "Reassign Collector"
    ? ` | Previous Collector: ${String((escalation as Record<string, unknown>).previousCollector || "Not Captured")} | New Collector: ${String((escalation as Record<string, unknown>).newCollector || escalation.assignedTo || "Not Captured")}`
    : "";
  return `Status: ${escalation.status} | Priority: ${escalation.priority} | Reason: ${escalation.reason} | Assigned To: ${escalation.assignedTo}${reassignmentDetail} | Source: ${String(escalation.sourceTrigger || escalation.sourceEventType || "Manual Manager Review")}`;
}

function buildPaymentWorkflowActivity(payment: ARWorkflowPaymentProjection) {
  return {
    id: `${payment.id}-activity`,
    companyId: payment.companyId,
    accountId: payment.accountId,
    customerName: payment.customerName,
    actorName: String(payment.actorName || payment.recordedBy || "Payment Engine"),
    kind: "Payment" as const,
    title: `Payment ${payment.lifecycleStatus}`,
    detail: buildPaymentWorkflowActivityDetail(payment),
    createdAt: payment.updatedAt || payment.recordedAt,
  };
}

function buildPaymentWorkflowRecoveryEvent(payment: ARWorkflowPaymentProjection) {
  return {
    id: `${payment.id}-audit`,
    companyId: payment.companyId,
    accountId: payment.accountId,
    customerName: payment.customerName,
    actorName: String(payment.actorName || payment.recordedBy || "Payment Engine"),
    actorRole: "collector" as const,
    eventType: "payment_recorded" as const,
    relatedRecordType: "payment" as const,
    relatedRecordId: payment.id,
    title: `Payment ${payment.lifecycleStatus} for ${payment.customerName}`,
    detail: buildPaymentWorkflowActivityDetail(payment),
    outcome: payment.status,
    visibility: "internal" as const,
    createdAt: payment.updatedAt || payment.recordedAt,
    metadata: {
      paymentId: payment.id,
      amountCents: payment.amountCents,
      paymentMethod: payment.paymentMethod,
      applicationScope: payment.applicationScope,
      appliedToPromiseId: payment.appliedToPromiseId,
      appliedToAccountId: payment.appliedToAccountId,
      externalPaymentReference: payment.externalPaymentReference,
      accountingProvider: payment.accountingProvider,
      accountingSyncStatus: payment.accountingSyncStatus,
      workflow: "enterprise_payment_engine",
    },
  };
}

function buildPaymentWorkflowActivityDetail(payment: ARWorkflowPaymentProjection) {
  const reference = String(payment.externalPaymentReference || "").trim();
  const paymentDate = String(payment.paymentDate || payment.recordedAt || payment.createdAt || "").trim();
  return [
    `Amount: ${formatARRecoveryCurrency(payment.amountCents)}`,
    reference ? `Reference: ${reference}` : "",
    `Account: ${payment.accountId}`,
    `Method: ${payment.paymentMethod}`,
    paymentDate ? `Payment Date: ${formatCapturedDate(paymentDate)}` : "",
    `Status: ${payment.status}`,
    `Lifecycle: ${payment.lifecycleStatus}`,
    `Application: ${payment.applicationScope || "Not Applied"}`,
  ].filter(Boolean).join(" | ");
}

function buildPromiseWorkflowActivityDetail(promise: ARRecoveryPromiseWorkflowRecord) {
  return `Promise Type: ${promise.promiseType} | Amount: ${formatARRecoveryCurrency(promise.amountCents)} | Promise Date: ${formatCapturedDate(promise.promiseDate)} | Payment Method: ${promise.expectedPaymentMethod || "Not Captured"} | Outcome: ${promise.outcome || "Not Captured"} | Next Action: ${formatPromiseNextActionSummary(promise)}`;
}

function buildPromiseWorkflowActivities(promise: ARRecoveryPromiseWorkflowRecord) {
  const activities = [
    {
      id: `${promise.id}-created`,
      companyId: promise.companyId,
      accountId: promise.accountId,
      customerName: promise.customerName,
      actorName: promise.collector,
      kind: "Promise" as const,
      title: `Promise Created - ${formatARRecoveryCurrency(promise.amountCents)}`,
      detail: buildPromiseWorkflowActivityDetail(promise),
      createdAt: promise.createdAt,
    },
  ];

  promise.progressNotes.forEach((progressNote) => {
    activities.push({
      id: `${progressNote.id}-activity`,
      companyId: promise.companyId,
      accountId: promise.accountId,
      customerName: promise.customerName,
      actorName: progressNote.author,
      kind: "Promise" as const,
      title: "Promise Progress Note",
      detail: `User: ${progressNote.author} | Note: ${progressNote.note}`,
      createdAt: progressNote.createdAt,
    });
  });

  if ((promise.status === "Completed" || promise.status === "Paid" || promise.status === "Resolved") && promise.completionNotes) {
    activities.push({
      id: `${promise.id}-completed`,
      companyId: promise.companyId,
      accountId: promise.accountId,
      customerName: promise.customerName,
      actorName: promise.collector,
      kind: "Promise" as const,
      title: `Promise Completed - ${formatARRecoveryCurrency(promise.amountCents)}`,
      detail: `Completion Notes: ${promise.completionNotes} | Outcome: ${promise.outcome || "Completed"}`,
      createdAt: promise.updatedAt,
    });
  }

  if (promise.status === "Broken" && promise.brokenReason) {
    activities.push({
      id: `${promise.id}-broken`,
      companyId: promise.companyId,
      accountId: promise.accountId,
      customerName: promise.customerName,
      actorName: promise.collector,
      kind: "Promise" as const,
      title: "Promise Broken",
      detail: `Reason: ${promise.brokenReason} | Notes: ${promise.notes || "Not Captured"} | Outcome: ${promise.outcome || "Broken"} | Next Action: ${formatPromiseNextActionSummary(promise)}`,
      createdAt: promise.updatedAt,
    });
  }

  if (promise.status === "Cancelled") {
    activities.push({
      id: `${promise.id}-cancelled`,
      companyId: promise.companyId,
      accountId: promise.accountId,
      customerName: promise.customerName,
      actorName: promise.collector,
      kind: "Promise" as const,
      title: "Promise Cancelled",
      detail: `Outcome: ${promise.outcome || "Cancelled"} | Next Action: ${formatPromiseNextActionSummary(promise)}`,
      createdAt: promise.updatedAt,
    });
  }

  return activities;
}

function buildPromiseWorkflowEvents(promise: ARRecoveryPromiseWorkflowRecord) {
  const events: ReturnType<typeof loadARRecoveryWorkspace>["recoveryActivityEvents"] = [
    {
      id: `${promise.id}-created-audit`,
      companyId: promise.companyId,
      accountId: promise.accountId,
      customerName: promise.customerName,
      actorName: promise.collector,
      actorRole: "collector" as const,
      eventType: "promise_created" as const,
      relatedRecordType: "promise" as const,
      relatedRecordId: promise.id,
      title: `Promise created for ${promise.customerName}`,
      detail: buildPromiseWorkflowActivityDetail(promise),
      notes: promise.notes,
      outcome: promise.outcome || promise.status,
      visibility: "internal" as const,
      createdAt: promise.createdAt,
      metadata: {
        promiseId: promise.id,
        promiseType: promise.promiseType,
        promiseAmountCents: promise.amountCents,
        promiseDate: promise.promiseDate,
        expectedPaymentMethod: promise.expectedPaymentMethod,
        relatedInvoices: promise.invoiceLabels,
        nextActionType: promise.nextActionType,
        nextActionDueDate: promise.nextActionDueDate,
        reminderRequired: promise.reminderRequired,
        workflow: "create_promise",
      },
    },
  ];

  if (promise.updatedAt !== promise.createdAt) {
    events.push({
      id: `${promise.id}-updated-audit`,
      companyId: promise.companyId,
      accountId: promise.accountId,
      customerName: promise.customerName,
      actorName: promise.collector,
      actorRole: "collector" as const,
      eventType: promise.status === "Broken" ? "promise_broken" as const : "promise_updated" as const,
      relatedRecordType: "promise" as const,
      relatedRecordId: promise.id,
      title: promise.status === "Broken" ? "Promise marked broken" : `Promise ${promise.status}`,
      detail: promise.status === "Broken" ? `Reason: ${promise.brokenReason || "Not Captured"} | ${buildPromiseWorkflowActivityDetail(promise)}` : buildPromiseWorkflowActivityDetail(promise),
      notes: promise.status === "Broken" ? promise.brokenReason || promise.notes : promise.completionNotes || promise.notes,
      outcome: promise.outcome || promise.status,
      visibility: "internal" as const,
      createdAt: promise.updatedAt,
      metadata: {
        promiseId: promise.id,
        promiseStatus: promise.status,
        completionNotes: promise.completionNotes,
        brokenReason: promise.brokenReason,
        nextActionType: promise.nextActionType,
        nextActionDueDate: promise.nextActionDueDate,
        workflow: "promise_lifecycle",
      },
    });
  }

  promise.progressNotes.forEach((progressNote) => {
    events.push({
      id: `${progressNote.id}-audit`,
      companyId: promise.companyId,
      accountId: promise.accountId,
      customerName: promise.customerName,
      actorName: progressNote.author,
      actorRole: "collector" as const,
      eventType: "promise_updated" as const,
      relatedRecordType: "promise" as const,
      relatedRecordId: promise.id,
      title: "Promise progress note added",
      detail: progressNote.note,
      outcome: "Progress noted",
      visibility: "internal" as const,
      createdAt: progressNote.createdAt,
      metadata: {
        promiseId: promise.id,
        workflow: "promise_progress_note",
      },
    });
  });

  return events;
}

function buildTicklerWorkflowActivityDetail(tickler: ARRecoveryTicklerWorkflowRecord) {
  return `Follow-Up Type: ${tickler.ticklerType} | Due: ${formatCapturedDate(tickler.dueDate)} ${tickler.dueTime || ""} | Priority: ${tickler.priority} | Assigned To: ${tickler.assignedCollector} | Outcome: ${tickler.outcome || "Not Captured"} | Next Action: ${tickler.nextAction || "Not Captured"}`;
}

function buildTicklerWorkflowActivities(tickler: ARRecoveryTicklerWorkflowRecord) {
  const activities = [
    {
      id: `${tickler.id}-created`,
      companyId: tickler.companyId,
      accountId: tickler.accountId,
      customerName: tickler.customerName,
      actorName: tickler.createdBy,
      kind: "Task" as const,
      title: `Follow-Up Scheduled - ${tickler.title}`,
      detail: buildTicklerWorkflowActivityDetail(tickler),
      createdAt: tickler.createdAt,
    },
  ];

  tickler.rescheduleHistory.forEach((entry) => {
    activities.push({
      id: `${entry.id}-activity`,
      companyId: tickler.companyId,
      accountId: tickler.accountId,
      customerName: tickler.customerName,
      actorName: entry.updatedBy,
      kind: "Task" as const,
      title: "Follow-Up Rescheduled",
      detail: `Previous Due Date: ${formatCapturedDate(entry.previousDueDate)} | New Due Date: ${formatCapturedDate(entry.newDueDate)} | Reason: ${entry.reason}`,
      createdAt: entry.updatedAt,
    });
  });

  tickler.progressNotes.forEach((progressNote) => {
    activities.push({
      id: `${progressNote.id}-activity`,
      companyId: tickler.companyId,
      accountId: tickler.accountId,
      customerName: tickler.customerName,
      actorName: progressNote.author,
      kind: "Task" as const,
      title: "Follow-Up Progress Note",
      detail: `User: ${progressNote.author} | Note: ${progressNote.note}`,
      createdAt: progressNote.createdAt,
    });
  });

  if (tickler.status === "Completed" && tickler.completedAt) {
    activities.push({
      id: `${tickler.id}-completed`,
      companyId: tickler.companyId,
      accountId: tickler.accountId,
      customerName: tickler.customerName,
      actorName: tickler.completedBy || tickler.updatedBy,
      kind: "Task" as const,
      title: `Follow-Up Completed - ${tickler.title}`,
      detail: `Completed By: ${tickler.completedBy || "Not Captured"} | Completion Notes: ${tickler.completionNotes || "Not Captured"} | Outcome: ${tickler.outcome || "Completed"}`,
      createdAt: tickler.completedAt,
    });
  }

  if (tickler.status === "Cancelled" && tickler.cancelledAt) {
    activities.push({
      id: `${tickler.id}-cancelled`,
      companyId: tickler.companyId,
      accountId: tickler.accountId,
      customerName: tickler.customerName,
      actorName: tickler.cancelledBy || tickler.updatedBy,
      kind: "Task" as const,
      title: `Follow-Up Cancelled - ${tickler.title}`,
      detail: `Cancelled By: ${tickler.cancelledBy || "Not Captured"} | Outcome: ${tickler.outcome || "Cancelled"}`,
      createdAt: tickler.cancelledAt,
    });
  }

  return activities;
}

function buildTicklerWorkflowEvents(tickler: ARRecoveryTicklerWorkflowRecord) {
  const events: ReturnType<typeof loadARRecoveryWorkspace>["recoveryActivityEvents"] = [
    {
      id: `${tickler.id}-created-audit`,
      companyId: tickler.companyId,
      accountId: tickler.accountId,
      customerName: tickler.customerName,
      actorName: tickler.createdBy,
      actorRole: "collector" as const,
      eventType: "follow_up_created" as const,
      relatedRecordType: "tickler" as const,
      relatedRecordId: tickler.id,
      title: `Follow-up scheduled for ${tickler.customerName}`,
      detail: buildTicklerWorkflowActivityDetail(tickler),
      notes: tickler.notes,
      outcome: tickler.outcome || tickler.status,
      visibility: "internal" as const,
      createdAt: tickler.createdAt,
      metadata: {
        ticklerId: tickler.id,
        followUpType: tickler.ticklerType,
        dueDate: tickler.dueDate,
        dueTime: tickler.dueTime,
        priority: tickler.priority,
        assignedTo: tickler.assignedCollector,
        reminderRequired: tickler.reminderRequired,
        reminderNotes: tickler.reminderNotes,
        relatedInvoice: tickler.relatedInvoice,
        relatedPromise: tickler.relatedPromise,
        relatedTask: tickler.relatedTask,
        nextAction: tickler.nextAction,
        workflow: "schedule_follow_up",
      },
    },
  ];

  tickler.rescheduleHistory.forEach((entry) => {
    events.push({
      id: `${entry.id}-audit`,
      companyId: tickler.companyId,
      accountId: tickler.accountId,
      customerName: tickler.customerName,
      actorName: entry.updatedBy,
      actorRole: "collector" as const,
      eventType: "status_changed" as const,
      relatedRecordType: "tickler" as const,
      relatedRecordId: tickler.id,
      title: "Follow-up rescheduled",
      detail: `Previous Due Date: ${formatCapturedDate(entry.previousDueDate)} | New Due Date: ${formatCapturedDate(entry.newDueDate)} | Reason: ${entry.reason}`,
      previousStatus: entry.previousDueDate,
      nextStatus: entry.newDueDate,
      outcome: "Rescheduled",
      visibility: "internal" as const,
      createdAt: entry.updatedAt,
      metadata: {
        ticklerId: tickler.id,
        previousDueDate: entry.previousDueDate,
        newDueDate: entry.newDueDate,
        reason: entry.reason,
        nextAction: tickler.nextAction,
        workflow: "follow_up_reschedule",
      },
    });
  });

  tickler.progressNotes.forEach((progressNote) => {
    events.push({
      id: `${progressNote.id}-audit`,
      companyId: tickler.companyId,
      accountId: tickler.accountId,
      customerName: tickler.customerName,
      actorName: progressNote.author,
      actorRole: "collector" as const,
      eventType: "status_changed" as const,
      relatedRecordType: "tickler" as const,
      relatedRecordId: tickler.id,
      title: "Follow-up progress note added",
      detail: progressNote.note,
      outcome: "Progress noted",
      visibility: "internal" as const,
      createdAt: progressNote.createdAt,
      metadata: {
        ticklerId: tickler.id,
        nextAction: tickler.nextAction,
        workflow: "follow_up_progress_note",
      },
    });
  });

  if (tickler.status === "Completed" && tickler.completedAt) {
    events.push({
      id: `${tickler.id}-completed-audit`,
      companyId: tickler.companyId,
      accountId: tickler.accountId,
      customerName: tickler.customerName,
      actorName: tickler.completedBy || tickler.updatedBy,
      actorRole: "collector" as const,
      eventType: "follow_up_completed" as const,
      relatedRecordType: "tickler" as const,
      relatedRecordId: tickler.id,
      title: "Follow-up completed",
      detail: `Completion Notes: ${tickler.completionNotes || "Not Captured"} | Outcome: ${tickler.outcome || "Completed"}`,
      outcome: tickler.outcome || "Completed",
      visibility: "internal" as const,
      createdAt: tickler.completedAt,
      metadata: {
        ticklerId: tickler.id,
        completionNotes: tickler.completionNotes,
        workflow: "follow_up_completion",
      },
    });
  }

  if (tickler.status === "Cancelled" && tickler.cancelledAt) {
    events.push({
      id: `${tickler.id}-cancelled-audit`,
      companyId: tickler.companyId,
      accountId: tickler.accountId,
      customerName: tickler.customerName,
      actorName: tickler.cancelledBy || tickler.updatedBy,
      actorRole: "collector" as const,
      eventType: "status_changed" as const,
      relatedRecordType: "tickler" as const,
      relatedRecordId: tickler.id,
      title: "Follow-up cancelled",
      detail: buildTicklerWorkflowActivityDetail(tickler),
      previousStatus: "Open",
      nextStatus: "Cancelled",
      outcome: tickler.outcome || "Cancelled",
      visibility: "internal" as const,
      createdAt: tickler.cancelledAt,
      metadata: {
        ticklerId: tickler.id,
        workflow: "follow_up_cancel",
      },
    });
  }

  return events;
}

function buildLetterWorkflowActivityDetail(letter: ARRecoveryLetterWorkflowRecord) {
  const delivery =
    letter.status === "Sent"
      ? `Email accepted by ${letter.deliveryProvider || "provider"}${letter.providerMessageId ? ` (${letter.providerMessageId})` : ""}`
      : letter.status === "Failed"
        ? `Email delivery failed${letter.deliveryError ? `: ${letter.deliveryError}` : "."}`
        : "Email delivery is not configured; no email was sent";
  return `Letter Type: ${letter.letterType} | Recipient: ${letter.recipient} | Status: ${letter.status} | Delivery: ${delivery} | Generated By: ${letter.generatedBy} | Related Invoices: ${letter.relatedInvoices.join(", ") || "Not Captured"} | Related Promise: ${letter.relatedPromise || "Not Captured"} | Related Task: ${letter.relatedTask || "Not Captured"} | Notes: ${letter.additionalNotes || "Not Captured"}`;
}

function buildLetterWorkflowActivities(letter: ARRecoveryLetterWorkflowRecord) {
  return [
    {
      id: `${letter.id}-generated`,
      companyId: letter.companyId,
      accountId: letter.accountId,
      customerName: letter.customerName,
      actorName: letter.generatedBy,
      kind: "Letter" as const,
      title: `Letter Generated - ${letter.template}`,
      detail: buildLetterWorkflowActivityDetail(letter),
      createdAt: letter.generatedAt,
    },
  ];
}

function buildLetterWorkflowEvents(letter: ARRecoveryLetterWorkflowRecord) {
  return [
    {
      id: `${letter.id}-generated-audit`,
      companyId: letter.companyId,
      accountId: letter.accountId,
      customerName: letter.customerName,
      actorName: letter.generatedBy,
      actorRole: "collector" as const,
      eventType: "letter_generated" as const,
      relatedRecordType: "letter" as const,
      relatedRecordId: letter.id,
      title: `Collection letter generated for ${letter.customerName}`,
      detail: buildLetterWorkflowActivityDetail(letter),
      notes: letter.additionalNotes,
      outcome:
        letter.status === "Sent"
          ? "Letter email accepted by provider."
          : letter.status === "Failed"
            ? "Letter email delivery failed."
            : "Email delivery is not configured; letter was generated but not sent.",
      visibility: "internal" as const,
      createdAt: letter.generatedAt,
      metadata: {
        letterId: letter.id,
        letterType: letter.letterType,
        template: letter.template,
        recipient: letter.recipient,
        relatedInvoices: letter.relatedInvoices,
        relatedPromise: letter.relatedPromise,
        relatedTask: letter.relatedTask,
        nextAction: "Schedule follow-up if delivery or response needs to be tracked.",
        providerConfigured: letter.status !== "Delivery Not Configured",
        deliveryStatus: letter.status === "Sent" ? "sent" : letter.status === "Failed" ? "failed" : "not_configured",
        deliveryProvider: letter.deliveryProvider,
        providerMessageId: letter.providerMessageId,
        providerStatus: letter.providerStatus,
        deliveryError: letter.deliveryError,
        workflow: "letter_production",
      },
    },
  ];
}

function buildStatementWorkflowActivityDetail(statement: ARRecoveryStatementWorkflowRecord) {
  const delivery =
    statement.status === "Sent"
      ? `Email accepted by ${statement.deliveryProvider || "provider"}${statement.providerMessageId ? ` (${statement.providerMessageId})` : ""}`
      : statement.status === "Failed"
        ? `Email delivery failed${statement.deliveryError ? `: ${statement.deliveryError}` : "."}`
        : statement.deliveryOption === "Email"
          ? "Email delivery is not configured; no email was sent"
          : "Generated for manual delivery";
  return `Statement Type: ${statement.statementType} | Statement Date: ${formatCapturedDate(statement.dateSent || statement.createdAt)} | Status: ${statement.status} | Delivery Option: ${statement.deliveryOption} | Delivery: ${delivery} | Generated By: ${statement.generatedBy} | Invoices: ${statement.relatedInvoices.join(", ") || "Not Captured"} | Total: ${formatARRecoveryCurrency(statement.balanceCents)} | Related Promise: ${statement.relatedPromise || "Not Captured"} | Related Task: ${statement.relatedTask || "Not Captured"} | Notes: ${statement.statementNotes || "Not Captured"}`;
}

function buildStatementWorkflowActivities(statement: ARRecoveryStatementWorkflowRecord) {
  return [
    {
      id: `${statement.id}-generated`,
      companyId: statement.companyId,
      accountId: statement.accountId,
      customerName: statement.customerName,
      actorName: statement.generatedBy,
      kind: "Statement" as const,
      title: `Statement Generated - ${statement.statementNumber}`,
      detail: buildStatementWorkflowActivityDetail(statement),
      createdAt: statement.generatedAt,
    },
  ];
}

function buildStatementWorkflowEvents(statement: ARRecoveryStatementWorkflowRecord) {
  return [
    {
      id: `${statement.id}-generated-audit`,
      companyId: statement.companyId,
      accountId: statement.accountId,
      customerName: statement.customerName,
      actorName: statement.generatedBy,
      actorRole: "collector" as const,
      eventType: "statement_generated" as const,
      relatedRecordType: "statement" as const,
      relatedRecordId: statement.id,
      title: `Statement generated for ${statement.customerName}`,
      detail: buildStatementWorkflowActivityDetail(statement),
      notes: statement.statementNotes,
      outcome:
        statement.status === "Sent"
          ? "Statement email accepted by provider."
          : statement.status === "Failed"
            ? "Statement email delivery failed."
            : statement.status === "Delivery Not Configured"
              ? "Email delivery is not configured; statement was generated but not sent."
              : "Statement generated for manual delivery.",
      visibility: "internal" as const,
      createdAt: statement.generatedAt,
      metadata: {
        statementId: statement.id,
        statementType: statement.statementType,
        statementNumber: statement.statementNumber,
        deliveryOption: statement.deliveryOption,
        relatedInvoices: statement.relatedInvoices,
        relatedPromise: statement.relatedPromise,
        relatedTask: statement.relatedTask,
        totalBalanceCents: statement.balanceCents,
        invoiceCount: statement.openInvoiceCount,
        providerConfigured: statement.status !== "Delivery Not Configured",
        deliveryStatus: statement.status === "Sent" ? "sent" : statement.status === "Failed" ? "failed" : statement.status === "Delivery Not Configured" ? "not_configured" : "generated",
        deliveryProvider: statement.deliveryProvider,
        providerMessageId: statement.providerMessageId,
        providerStatus: statement.providerStatus,
        deliveryError: statement.deliveryError,
        workflow: "statement_production",
      },
    },
  ];
}

function buildDocumentRequestActivityDetail(documentRequest: ARRecoveryDocumentRequestRecord) {
  return `Request Type: ${documentRequest.requestType} | Requested From: ${documentRequest.requestedFrom} | Due Date: ${formatCapturedDate(documentRequest.dueDate)} | Priority: ${documentRequest.priority} | Status: ${documentRequest.status} | Related Invoice: ${documentRequest.relatedInvoice || "Not Captured"} | Related Task: ${documentRequest.relatedTask || "Not Captured"} | Notes: ${documentRequest.requestNotes || "Not Captured"}`;
}

function buildDocumentRequestActivities(documentRequest: ARRecoveryDocumentRequestRecord) {
  const activities = [
    {
      id: `${documentRequest.id}-requested`,
      companyId: documentRequest.companyId,
      accountId: documentRequest.accountId,
      customerName: documentRequest.customerName,
      actorName: documentRequest.requestedBy,
      kind: "Task" as const,
      title: `Document Requested - ${documentRequest.requestType}`,
      detail: buildDocumentRequestActivityDetail(documentRequest),
      createdAt: documentRequest.requestedAt,
    },
  ];

  if (documentRequest.receivedAt) {
    activities.push({
      id: `${documentRequest.id}-received`,
      companyId: documentRequest.companyId,
      accountId: documentRequest.accountId,
      customerName: documentRequest.customerName,
      actorName: documentRequest.receivedBy || documentRequest.updatedBy,
      kind: "Task" as const,
      title: `Document Received - ${documentRequest.requestType}`,
      detail: `File Name: ${documentRequest.fileName || "Not Captured"} | Received Notes: ${documentRequest.receivedNotes || "Not Captured"} | Status: ${documentRequest.status}`,
      createdAt: documentRequest.receivedAt,
    });
  }

  if (documentRequest.updatedAt !== documentRequest.requestedAt) {
    activities.push({
      id: `${documentRequest.id}-updated`,
      companyId: documentRequest.companyId,
      accountId: documentRequest.accountId,
      customerName: documentRequest.customerName,
      actorName: documentRequest.updatedBy,
      kind: "Task" as const,
      title: `Document ${documentRequest.status} - ${documentRequest.requestType}`,
      detail: buildDocumentRequestReviewDetail(documentRequest),
      createdAt: documentRequest.updatedAt,
    });
  }

  return activities;
}

function buildDocumentRequestEvents(documentRequest: ARRecoveryDocumentRequestRecord) {
  const events: ReturnType<typeof loadARRecoveryWorkspace>["recoveryActivityEvents"] = [
    {
      id: `${documentRequest.id}-requested-audit`,
      companyId: documentRequest.companyId,
      accountId: documentRequest.accountId,
      customerName: documentRequest.customerName,
      actorName: documentRequest.requestedBy,
      actorRole: "collector" as const,
      eventType: "document_uploaded" as const,
      relatedRecordType: "document" as const,
      relatedRecordId: documentRequest.id,
      title: `Document request created for ${documentRequest.customerName}`,
      detail: buildDocumentRequestActivityDetail(documentRequest),
      notes: documentRequest.requestNotes,
      outcome: documentRequest.status,
      visibility: "internal" as const,
      createdAt: documentRequest.requestedAt,
      metadata: {
        documentRequestId: documentRequest.id,
        requestType: documentRequest.requestType,
        requestedFrom: documentRequest.requestedFrom,
        dueDate: documentRequest.dueDate,
        priority: documentRequest.priority,
        relatedInvoice: documentRequest.relatedInvoice,
        relatedTask: documentRequest.relatedTask,
        workflow: "document_request",
      },
    },
  ];

  if (documentRequest.receivedAt) {
    events.push({
      id: `${documentRequest.id}-received-audit`,
      companyId: documentRequest.companyId,
      accountId: documentRequest.accountId,
      customerName: documentRequest.customerName,
      actorName: documentRequest.receivedBy || documentRequest.updatedBy,
      actorRole: "collector" as const,
      eventType: "document_uploaded" as const,
      relatedRecordType: "document" as const,
      relatedRecordId: documentRequest.id,
      title: "Document received",
      detail: `File Name: ${documentRequest.fileName || "Not Captured"} | Received Notes: ${documentRequest.receivedNotes || "Not Captured"}`,
      previousStatus: "Waiting",
      nextStatus: "Received",
      outcome: "Received",
      visibility: "internal" as const,
      createdAt: documentRequest.receivedAt,
      metadata: {
        documentRequestId: documentRequest.id,
        fileName: documentRequest.fileName,
        workflow: "document_fulfillment",
      },
    });
  }

  if (documentRequest.updatedAt !== documentRequest.requestedAt) {
    events.push({
      id: `${documentRequest.id}-review-audit`,
      companyId: documentRequest.companyId,
      accountId: documentRequest.accountId,
      customerName: documentRequest.customerName,
      actorName: documentRequest.updatedBy,
      actorRole: "collector" as const,
      eventType: "status_changed" as const,
      relatedRecordType: "document" as const,
      relatedRecordId: documentRequest.id,
      title: `Document ${documentRequest.status}`,
      detail: buildDocumentRequestReviewDetail(documentRequest),
      previousStatus: "Requested",
      nextStatus: documentRequest.status,
      outcome: documentRequest.status,
      visibility: "internal" as const,
      createdAt: documentRequest.updatedAt,
      metadata: {
        documentRequestId: documentRequest.id,
        reviewNotes: documentRequest.reviewNotes,
        rejectionReason: documentRequest.rejectionReason,
        additionalInfoNotes: documentRequest.additionalInfoNotes,
        workflow: "document_review",
      },
    });
  }

  return events;
}

function buildDocumentRequestReviewDetail(documentRequest: ARRecoveryDocumentRequestRecord) {
  return `Status: ${documentRequest.status} | File Name: ${documentRequest.fileName || "Not Captured"} | Received Notes: ${documentRequest.receivedNotes || "Not Captured"} | Review Notes: ${documentRequest.reviewNotes || "Not Captured"} | Rejection Reason: ${documentRequest.rejectionReason || "Not Captured"} | Additional Info: ${documentRequest.additionalInfoNotes || "Not Captured"}`;
}

function isClosedPromiseLifecycleStatus(status: ReturnType<typeof loadARRecoveryWorkspace>["promises"][number]["status"]) {
  return ["Paid", "Completed", "Resolved", "Cancelled"].includes(status);
}

function getLatestCallForAccount(callLogs: ARRecoveryCallLogRecord[], accountId: string) {
  return [...callLogs]
    .filter((call) => call.accountId === accountId)
    .sort((a, b) => getDateTime(b.createdAt) - getDateTime(a.createdAt))[0] || null;
}

function getLatestNoteForAccount(noteLogs: ARRecoveryNoteLogRecord[], accountId: string) {
  return [...noteLogs]
    .filter((note) => note.accountId === accountId && note.nextAction.trim())
    .sort((a, b) => getDateTime(b.createdAt) - getDateTime(a.createdAt))[0] || null;
}

function getLatestPromiseWithNextAction(promiseWorkflows: ARRecoveryPromiseWorkflowRecord[], accountId: string) {
  return [...promiseWorkflows]
    .filter((promise) => promise.accountId === accountId && (promise.nextActionType || promise.nextActionDueDate || promise.reminderNotes))
    .sort((a, b) => getDateTime(b.updatedAt || b.createdAt) - getDateTime(a.updatedAt || a.createdAt))[0] || null;
}

function extractNextActionFromDetail(detail?: string) {
  const match = detail?.match(/Next Action:\s*([^|]+)/i);
  return match?.[1]?.trim() || "";
}

function buildActiveInternalUserNames(users: BusinessCenterUserRecord[]) {
  return Array.from(new Set(buildActiveInternalUserOptions(users)
    .map((user) => user.name)
    .filter(Boolean)))
    .sort((a, b) => a.localeCompare(b));
}

type ARRecoveryActiveInternalUserOption = {
  id: string;
  name: string;
  role: string;
  title: string;
};

function formatInternalUserRole(role: string) {
  return role
    .replace(/^company_/, "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function buildActiveInternalUserOptions(users: BusinessCenterUserRecord[]): ARRecoveryActiveInternalUserOption[] {
  const internalRoles = new Set(["company_admin", "company_manager", "company_supervisor", "company_team_member", "viewer"]);
  const uniqueUsers = new Map<string, ARRecoveryActiveInternalUserOption>();
  users
    .filter((user) => internalRoles.has(user.role) && user.status === "active" && user.portalAccessStatus === "active")
    .forEach((user) => {
      const id = user.id.trim();
      const name = (user.displayName || user.fullName || "").trim();
      const normalizedName = name.toLowerCase();
      if (!id || !name || uniqueUsers.has(normalizedName)) return;
      uniqueUsers.set(normalizedName, {
        id,
        name,
        role: formatInternalUserRole(user.role),
        title: user.jobTitle || user.team || user.department || "",
      });
    });
  return Array.from(uniqueUsers.values()).sort((a, b) => a.name.localeCompare(b.name));
}

function findActiveInternalUserByName(users: ARRecoveryActiveInternalUserOption[], name: string) {
  const normalizedName = name.trim().toLowerCase();
  return users.find((user) => user.name.trim().toLowerCase() === normalizedName) || null;
}

function isSameCollectorName(first: string, second: string) {
  return first.trim().toLowerCase() === second.trim().toLowerCase();
}

function CustomerAccountsSection({
  workspace,
  synchronizedAccountEdits,
}: {
  workspace: ReturnType<typeof loadARRecoveryWorkspace>;
  synchronizedAccountEdits: ARRecoveryAccountEditRecord[];
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const accountRecoveryLaunchState = location.state as AccountRecoveryLaunchState | null;
  const launchedAccountId = accountRecoveryLaunchState?.accountRecoveryLaunch?.accountId;
  const initialLaunchedAccountId = launchedAccountId && workspace.accounts.some((account) => account.id === launchedAccountId)
    ? launchedAccountId
    : "";
  const launchedAccountTab = accountRecoveryLaunchState?.accountRecoveryLaunch?.selectedTab || "activity";
  const launchedManagerAction = accountRecoveryLaunchState?.accountRecoveryLaunch?.managerAction || null;
  const launchedManagerActionToken = accountRecoveryLaunchState?.accountRecoveryLaunch?.launchToken || "";
  const [search, setSearch] = useState("");
  const [selectedAccountId, setSelectedAccountId] = useState(initialLaunchedAccountId || workspace.accounts[0]?.id || "");
  const [selectedTab, setSelectedTab] = useState<ARRecoveryAccountTabKey>(launchedAccountTab);
  const [activeWorkspacePanel, setActiveWorkspacePanel] = useState<ARRecoveryWorkspacePanelMode>(null);
  const [activeEnterpriseStudio, setActiveEnterpriseStudio] = useState<ARRecoveryEnterpriseStudioMode>(null);
  const [isSingleAccountWorkspaceActive, setIsSingleAccountWorkspaceActive] = useState(Boolean(initialLaunchedAccountId));
  const [pendingManagerCommandAction, setPendingManagerCommandAction] = useState<ManagerCommandAccountAction | null>(initialLaunchedAccountId && launchedManagerAction ? launchedManagerAction : null);
  const [pendingManagerNoteLandingKey, setPendingManagerNoteLandingKey] = useState("");
  const [pendingAssignCollectorLandingKey, setPendingAssignCollectorLandingKey] = useState("");
  const [isAssignCollectorEditMode, setIsAssignCollectorEditMode] = useState(false);
  const [isStudioDesignerCollapsed, setIsStudioDesignerCollapsed] = useState(false);
  const [activeStudioDesignerSection, setActiveStudioDesignerSection] = useState<ARRecoveryStudioDesignerSection>("template");
  const [activeStudioTab, setActiveStudioTab] = useState<ARRecoveryStudioDocumentTab>("preview");
  const [studioStatusMessage, setStudioStatusMessage] = useState("");
  const [recentAccountIds, setRecentAccountIds] = useState(() =>
    workspace.accounts.slice(0, 3).map((account) => account.id),
  );
  const [callLogs, setCallLogs] = useState<ARRecoveryCallLogRecord[]>(() => loadProjectedARRecoveryCallLogs());
  const [isCallDialogOpen, setIsCallDialogOpen] = useState(false);
  const [callForm, setCallForm] = useState<ARRecoveryCallFormState>(emptyCallForm);
  const [callFormError, setCallFormError] = useState("");
  const [callSuccessMessage, setCallSuccessMessage] = useState("");
  const [callWorkspaceTab, setCallWorkspaceTab] = useState<"overview" | "invoices" | "timeline" | "notes" | "communications" | "promises" | "statements">("overview");
  const [callWorkspaceMobilePane, setCallWorkspaceMobilePane] = useState<"call" | "account">("call");
  const [callInvoiceFilter, setCallInvoiceFilter] = useState<"all" | "oldest" | "largest" | "high_risk">("all");
  const [callTimelineFilter, setCallTimelineFilter] = useState<"today" | "week" | "month" | "all">("all");
  const [callNotesFilter, setCallNotesFilter] = useState<"all" | "pinned" | "manager" | "collector" | "system">("all");
  const [callCommunicationsFilter, setCallCommunicationsFilter] = useState<"all" | "customer" | "internal">("all");
  const [isEmailWorkspaceOpen, setIsEmailWorkspaceOpen] = useState(false);
  const [emailWorkspaceForm, setEmailWorkspaceForm] = useState<ARRecoveryEmailWorkspaceFormState>(emptyEmailWorkspaceForm);
  const [emailWorkspaceError, setEmailWorkspaceError] = useState("");
  const [emailWorkspaceSubmitting, setEmailWorkspaceSubmitting] = useState(false);
  const [emailWorkspaceValidation, setEmailWorkspaceValidation] = useState<ARRecoveryEmailValidationState>(() => buildEmptyARRecoveryEmailValidationState());
  const [emailWorkspaceDeliverySummary, setEmailWorkspaceDeliverySummary] = useState<ARRecoveryEmailDeliverySummary | null>(null);
  const [emailWorkspaceDocumentPreview, setEmailWorkspaceDocumentPreview] = useState<ARRecoveryEmailDocumentPreviewState | null>(null);
  const [emailWorkspaceTab, setEmailWorkspaceTab] = useState<"overview" | "invoices" | "timeline" | "notes" | "communications" | "promises" | "statements">("overview");
  const [emailWorkspaceMobilePane, setEmailWorkspaceMobilePane] = useState<"email" | "account">("email");
  const [isPaymentWorkspaceOpen, setIsPaymentWorkspaceOpen] = useState(false);
  const [paymentWorkspaceForm, setPaymentWorkspaceForm] = useState<ARRecoveryPaymentWorkspaceFormState>(emptyPaymentWorkspaceForm);
  const [paymentWorkspaceError, setPaymentWorkspaceError] = useState("");
  const [paymentWorkspaceSuccess, setPaymentWorkspaceSuccess] = useState<ARRecoveryPaymentDeliverySummary | null>(null);
  const [paymentWorkspaceTab, setPaymentWorkspaceTab] = useState<"overview" | "invoices" | "timeline" | "notes" | "communications" | "promises" | "statements">("overview");
  const [paymentWorkspaceMobilePane, setPaymentWorkspaceMobilePane] = useState<"payment" | "account">("payment");
  const [noteLogs, setNoteLogs] = useState<ARRecoveryNoteLogRecord[]>(() => loadProjectedARRecoveryNoteLogs());
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [noteForm, setNoteForm] = useState<ARRecoveryNoteFormState>(emptyNoteForm);
  const [noteFormError, setNoteFormError] = useState("");
  const [noteSuccessMessage, setNoteSuccessMessage] = useState("");
  const [noteCompletionSummary, setNoteCompletionSummary] = useState<ARRecoveryNoteCompletionSummary>(null);
  const [communicationLogs, setCommunicationLogs] = useState<ARRecoveryCommunicationRecord[]>(() => loadStoredARRecoveryCommunications());
  const [communicationSuccessMessage, setCommunicationSuccessMessage] = useState("");
  const [taskAssignments, setTaskAssignments] = useState<ARRecoveryTaskAssignmentRecord[]>(() => loadStoredARRecoveryTaskAssignments());
  const [taskSuccessMessage, setTaskSuccessMessage] = useState("");
  const [taskCreateLaunchKey, setTaskCreateLaunchKey] = useState("");
  const [promiseWorkflows, setPromiseWorkflows] = useState<ARRecoveryPromiseWorkflowRecord[]>(() => loadProjectedARRecoveryPromiseWorkflows());
  const [promiseWorkflowMode, setPromiseWorkflowMode] = useState<ARRecoveryPromiseWorkflowMode>("create");
  const [editingPromiseId, setEditingPromiseId] = useState("");
  const [isPromiseDialogOpen, setIsPromiseDialogOpen] = useState(false);
  const [promiseForm, setPromiseForm] = useState<ARRecoveryPromiseFormState>(emptyPromiseForm);
  const [promiseFormError, setPromiseFormError] = useState("");
  const [promiseSuccessMessage, setPromiseSuccessMessage] = useState("");
  const [promiseSuccessSummary, setPromiseSuccessSummary] = useState<{ amount: string; date: string; invoices: string; ticklerUpdated: string } | null>(null);
  const [promiseWorkspaceTab, setPromiseWorkspaceTab] = useState<ARRecoveryPromiseWorkspaceTab>("overview");
  const [promiseWorkspaceMobilePane, setPromiseWorkspaceMobilePane] = useState<"promise" | "account">("promise");
  const [ticklerWorkflows, setTicklerWorkflows] = useState<ARRecoveryTicklerWorkflowRecord[]>(() => loadProjectedARRecoveryTicklerWorkflows());
  const [ticklerWorkflowMode, setTicklerWorkflowMode] = useState<ARRecoveryTicklerWorkflowMode>("create");
  const [editingTicklerId, setEditingTicklerId] = useState("");
  const [isTicklerDialogOpen, setIsTicklerDialogOpen] = useState(false);
  const [ticklerForm, setTicklerForm] = useState<ARRecoveryTicklerFormState>(emptyTicklerForm);
  const [ticklerFormError, setTicklerFormError] = useState("");
  const [ticklerSuccessMessage, setTicklerSuccessMessage] = useState("");
  const [ticklerSuccessSummary, setTicklerSuccessSummary] = useState<{ nextAction: string; dueDate: string; priority: string; assignedCollector: string } | null>(null);
  const [ticklerWorkspaceTab, setTicklerWorkspaceTab] = useState<ARRecoveryTicklerWorkspaceTab>("overview");
  const [ticklerWorkspaceMobilePane, setTicklerWorkspaceMobilePane] = useState<"tickler" | "account">("tickler");
  const [letterWorkflows, setLetterWorkflows] = useState<ARRecoveryLetterWorkflowRecord[]>(() => loadProjectedARRecoveryLetterWorkflows());
  const [isLetterDialogOpen, setIsLetterDialogOpen] = useState(false);
  const [isLetterHistoryOpen, setIsLetterHistoryOpen] = useState(false);
  const [letterForm, setLetterForm] = useState<ARRecoveryLetterFormState>(emptyLetterForm);
  const [letterFormError, setLetterFormError] = useState("");
  const [letterSuccessMessage, setLetterSuccessMessage] = useState("");
  const [letterSuccessSummary, setLetterSuccessSummary] = useState<ARRecoveryLetterSuccessSummary>(null);
  const [statementWorkflows, setStatementWorkflows] = useState<ARRecoveryStatementWorkflowRecord[]>(() => loadProjectedARRecoveryStatementWorkflows());
  const [escalationWorkflows, setEscalationWorkflows] = useState<ARWorkflowEscalationProjection[]>(() => loadProjectedARRecoveryEscalationWorkflows());
  const [escalationDecisionForm, setEscalationDecisionForm] = useState<ARRecoveryEscalationDecisionFormState>(emptyEscalationDecisionForm);
  const [escalationDecisionError, setEscalationDecisionError] = useState("");
  const [escalationCompletionSummary, setEscalationCompletionSummary] = useState<ARRecoveryEscalationCompletionSummary>(null);
  const [paymentWorkflows, setPaymentWorkflows] = useState<ARWorkflowPaymentProjection[]>(() => loadProjectedARRecoveryPaymentWorkflows());
  const [isStatementDialogOpen, setIsStatementDialogOpen] = useState(false);
  const [isStatementHistoryOpen, setIsStatementHistoryOpen] = useState(false);
  const [statementForm, setStatementForm] = useState<ARRecoveryStatementFormState>(emptyStatementForm);
  const [statementFormError, setStatementFormError] = useState("");
  const [statementSuccessMessage, setStatementSuccessMessage] = useState("");
  const [statementCompletionSummary, setStatementCompletionSummary] = useState<ARRecoveryStatementCompletionSummary>(null);
  const [statementFailureSummary, setStatementFailureSummary] = useState<ARRecoveryStatementFailureSummary>(null);
  const [documentExecutionSubmitting, setDocumentExecutionSubmitting] = useState<"letter" | "statement" | null>(null);
  const [documentExecutionMobilePane, setDocumentExecutionMobilePane] = useState<"controls" | "preview">("controls");
  const [documentDeliveries, setDocumentDeliveries] = useState<ARRecoveryDocumentDeliveryRecord[]>(() => loadStoredARRecoveryDocumentDeliveries());
  const [documentRequests, setDocumentRequests] = useState<ARRecoveryDocumentRequestRecord[]>(() => loadStoredARRecoveryDocumentRequests());
  const [isDocumentRequestDialogOpen, setIsDocumentRequestDialogOpen] = useState(false);
  const [documentRequestForm, setDocumentRequestForm] = useState<ARRecoveryDocumentRequestFormState>(emptyDocumentRequestForm);
  const [documentRequestFormError, setDocumentRequestFormError] = useState("");
  const [documentSuccessMessage, setDocumentSuccessMessage] = useState("");
  const [accountEdits, setAccountEdits] = useState<ARRecoveryAccountEditRecord[]>(() => synchronizedAccountEdits);
  const [isAccountEditMode, setIsAccountEditMode] = useState(false);
  const [accountEditForm, setAccountEditForm] = useState<ARRecoveryAccountEditFormState>(emptyAccountEditForm);
  const [accountEditError, setAccountEditError] = useState("");
  const [accountEditSuccessMessage, setAccountEditSuccessMessage] = useState("");
  const [internalUsers, setInternalUsers] = useState<BusinessCenterUserRecord[]>(() => loadBusinessCenterCompanyUsers());
  const [authenticatedActor, setAuthenticatedActor] = useState<ARRecoveryActor>(() => buildFallbackARRecoveryActor());
  const [isAuthenticatedActorReady, setIsAuthenticatedActorReady] = useState(false);
  const [governanceMessage, setGovernanceMessage] = useState("");
  const [workspacePanelMessage, setWorkspacePanelMessage] = useState("");
  const accountQuickActionSurfaceRef = useRef<HTMLDivElement | null>(null);
  const quickActionFocusTimeoutRef = useRef<number | null>(null);
  const queueListRef = useRef<HTMLDivElement | null>(null);
  const queuePageScrollPositionRef = useRef(0);
  const queueListScrollPositionRef = useRef(0);
  const workspacePanelRef = useRef<HTMLElement | null>(null);
  const workspacePanelBodyRef = useRef<HTMLDivElement | null>(null);
  const workspacePanelReturnFocusRef = useRef<HTMLElement | null>(null);
  const managerCommandLaunchHandledRef = useRef("");
  const managerCommandActionHandledRef = useRef("");
  const previousStatementDialogOpenRef = useRef(false);
  const letterHistoryButtonRef = useRef<HTMLButtonElement | null>(null);
  const statementHistoryButtonRef = useRef<HTMLButtonElement | null>(null);
  const emailRecipientInputRef = useRef<HTMLInputElement | null>(null);
  const emailCcInputRef = useRef<HTMLInputElement | null>(null);
  const emailBccInputRef = useRef<HTMLInputElement | null>(null);
  const noteWorkflowSectionRef = useRef<HTMLElement | null>(null);
  const noteContentTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const assignedCollectorEditInputRef = useRef<HTMLInputElement | HTMLSelectElement | null>(null);
  const callFormRef = useRef(callForm);
  const emailWorkspaceFormRef = useRef(emailWorkspaceForm);
  const paymentWorkspaceFormRef = useRef(paymentWorkspaceForm);
  const promiseFormRef = useRef(promiseForm);
  const ticklerFormRef = useRef(ticklerForm);
  useEffect(() => {
    return () => {
      if (quickActionFocusTimeoutRef.current !== null) {
        window.clearTimeout(quickActionFocusTimeoutRef.current);
      }
    };
  }, []);
  useEffect(() => {
    callFormRef.current = callForm;
  }, [callForm]);
  useEffect(() => {
    emailWorkspaceFormRef.current = emailWorkspaceForm;
  }, [emailWorkspaceForm]);
  useEffect(() => {
    paymentWorkspaceFormRef.current = paymentWorkspaceForm;
  }, [paymentWorkspaceForm]);
  useEffect(() => {
    promiseFormRef.current = promiseForm;
  }, [promiseForm]);
  useEffect(() => {
    ticklerFormRef.current = ticklerForm;
  }, [ticklerForm]);
  useEffect(() => {
    saveStoredARRecoveryDocumentDeliveries(documentDeliveries);
  }, [documentDeliveries]);
  useEffect(() => {
    setAccountEdits((current) => mergeARRecoveryAccountEditRecords(current, synchronizedAccountEdits));
  }, [synchronizedAccountEdits]);
  const operationalWorkspace = useMemo(
    () => mergeWorkspaceWithOperationalLogs(workspace, callLogs, noteLogs, communicationLogs, taskAssignments, promiseWorkflows, ticklerWorkflows, letterWorkflows, statementWorkflows, escalationWorkflows, paymentWorkflows, loadProjectedARRecoveryNotifications(), loadProjectedARRecoveryReportingAnalytics(), loadProjectedEnterpriseClientExchangeFoundation(), loadPortfolioValidationEngineFoundation(workspace.company.id), loadPortfolioMappingEngineFoundation(workspace.company.id), loadPortfolioReviewEngineFoundation(workspace.company.id), loadPortfolioImportEngineFoundation(workspace.company.id), loadPortfolioAssignmentEngineFoundation(workspace.company.id), loadPortfolioActivationEngineFoundation(workspace.company.id), loadEnterprisePortfolioIntakeWalkthrough(workspace.company.id), documentDeliveries, documentRequests, accountEdits),
    [workspace, callLogs, noteLogs, communicationLogs, taskAssignments, promiseWorkflows, ticklerWorkflows, letterWorkflows, statementWorkflows, escalationWorkflows, paymentWorkflows, documentDeliveries, documentRequests, accountEdits],
  );
  const baseSnapshot = useMemo(
    () => buildARRecoveryCustomerAccountsSnapshot(workspace.company.id, search, selectedAccountId, selectedTab, operationalWorkspace),
    [search, selectedAccountId, selectedTab, workspace.company.id, operationalWorkspace],
  );
  const snapshot = useMemo(() => ({ ...baseSnapshot, workspace: operationalWorkspace }), [baseSnapshot, operationalWorkspace]);
  const selectedAccount = operationalWorkspace.accounts.find((account) => account.id === selectedAccountId) || snapshot.selectedAccount;
  const isSearchingAccounts = search.trim().length > 0;
  const latestCall = selectedAccount ? getLatestCallForAccount(callLogs, selectedAccount.id) : null;
  const latestNoteWithNextAction = selectedAccount ? getLatestNoteForAccount(noteLogs, selectedAccount.id) : null;
  const latestPromiseWithNextAction = selectedAccount ? getLatestPromiseWithNextAction(promiseWorkflows, selectedAccount.id) : null;
  const activeInternalUserOptions = useMemo(
    () => buildActiveInternalUserOptions(internalUsers),
    [internalUsers],
  );
  const activeInternalUserNames = useMemo(
    () => activeInternalUserOptions.map((user) => user.name),
    [activeInternalUserOptions],
  );
  const currentActor = useMemo<ARRecoveryActor>(() => {
    const matchedUser = authenticatedActor.email
      ? internalUsers.find((user) => user.email.toLowerCase() === authenticatedActor.email.toLowerCase())
      : null;
    return {
      email: authenticatedActor.email,
      name: matchedUser?.fullName || authenticatedActor.name || authenticatedActor.email || "Authenticated User",
      role: authenticatedActor.role,
    };
  }, [authenticatedActor, internalUsers]);
  const canWriteOperationalRecords = canWriteARRecovery(currentActor);
  const canOverrideRecordOwnership = canOverrideARRecoveryOwnership(currentActor);
  const assignedCollectorOptions = useMemo(() => {
    if (!accountEditForm.assignedCollector || activeInternalUserNames.includes(accountEditForm.assignedCollector)) {
      return activeInternalUserNames;
    }
    return [accountEditForm.assignedCollector, ...activeInternalUserNames];
  }, [accountEditForm.assignedCollector, activeInternalUserNames]);

  function resetActiveAccountWorkflowPanels() {
    setIsCallDialogOpen(false);
    setIsEmailWorkspaceOpen(false);
    setIsPaymentWorkspaceOpen(false);
    setIsNoteDialogOpen(false);
    setIsPromiseDialogOpen(false);
    setIsTicklerDialogOpen(false);
    setIsLetterDialogOpen(false);
    setIsLetterHistoryOpen(false);
    setIsStatementDialogOpen(false);
    setIsStatementHistoryOpen(false);
    setIsDocumentRequestDialogOpen(false);
  }

  useEffect(() => {
    if (!launchedAccountId || !operationalWorkspace.accounts.some((account) => account.id === launchedAccountId)) return;
    if (!launchedManagerAction && managerCommandLaunchHandledRef.current) return;
    const launchKey = `${launchedAccountId}:${launchedManagerAction || "view-account"}:${launchedAccountTab}:${launchedManagerActionToken || "no-token"}`;
    if (managerCommandLaunchHandledRef.current === launchKey) return;
    managerCommandLaunchHandledRef.current = launchKey;
    setSearch("");
    setSelectedAccountId(launchedAccountId);
    setActiveWorkspacePanel(null);
    setActiveEnterpriseStudio(null);
    setIsAssignCollectorEditMode(false);
    resetActiveAccountWorkflowPanels();
    setRecentAccountIds((current) => [launchedAccountId, ...current.filter((id) => id !== launchedAccountId)].slice(0, 3));
    setSelectedTab(launchedAccountTab);
    setIsSingleAccountWorkspaceActive(true);
    setPendingManagerCommandAction(launchedManagerAction);
    if (!launchedManagerAction || launchedManagerAction === "view-account") {
      window.setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        accountQuickActionSurfaceRef.current?.focus({ preventScroll: true });
      }, 0);
    }
  }, [launchedAccountId, launchedAccountTab, launchedManagerAction, launchedManagerActionToken, operationalWorkspace.accounts]);

  useEffect(() => {
    if (!pendingManagerCommandAction || !selectedAccount || selectedAccount.id !== launchedAccountId || !isAuthenticatedActorReady) return;
    const actionKey = `${selectedAccount.id}:${pendingManagerCommandAction}:${launchedManagerActionToken || "no-token"}`;
    if (managerCommandActionHandledRef.current === actionKey) return;
    managerCommandActionHandledRef.current = actionKey;
    setPendingManagerCommandAction(null);
    openManagerCommandLaunchedAction(pendingManagerCommandAction);
    if (pendingManagerCommandAction !== "add-manager-note" && pendingManagerCommandAction !== "assign-collector") {
      clearConsumedManagerCommandLaunchInstruction();
    }
  }, [pendingManagerCommandAction, selectedAccount?.id, launchedAccountId, launchedManagerActionToken, isAuthenticatedActorReady]);

  function clearConsumedManagerCommandLaunchInstruction() {
    const launch = accountRecoveryLaunchState?.accountRecoveryLaunch;
    if (!launch?.managerAction) return;
    navigate(location.pathname, {
      replace: true,
      state: {
        accountRecoveryLaunch: {
          origin: launch.origin,
          accountId: launch.accountId,
          selectedTab: launch.selectedTab,
          returnRoute: launch.returnRoute,
          managerCommandReturnContext: launch.managerCommandReturnContext,
          promiseCenterReturnContext: launch.promiseCenterReturnContext,
        },
      } satisfies AccountRecoveryLaunchState,
    });
  }

  function getManagerCommandReturnContext() {
    return accountRecoveryLaunchState?.accountRecoveryLaunch?.managerCommandReturnContext || accountRecoveryLaunchState?.managerCommandReturnContext;
  }

  const accountRecoveryOrigin = resolveAccountRecoveryOrigin(accountRecoveryLaunchState);

  function returnToAccountRecoveryOrigin() {
    if (accountRecoveryOrigin.origin === "direct") return;
    const isManagerCommandReturn = accountRecoveryOrigin.origin === "manager-command";

    if (isManagerCommandReturn && activeWorkspacePanel === "escalate") {
      flushSync(() => {
        setActiveWorkspacePanel(null);
        setWorkspacePanelMessage("");
        setEscalationDecisionError("");
        setEscalationCompletionSummary(null);
      });
    }

    if (isManagerCommandReturn && activeWorkspacePanel === "tickler") {
      flushSync(() => {
        setActiveWorkspacePanel(null);
        setWorkspacePanelMessage("");
        setIsTicklerDialogOpen(false);
        setTicklerFormError("");
        setEditingTicklerId("");
        setTicklerWorkflowMode("create");
        setTicklerSuccessSummary(null);
        setTicklerWorkspaceTab("overview");
        setTicklerWorkspaceMobilePane("tickler");
      });
    }

    if (isManagerCommandReturn && activeWorkspacePanel === "note") {
      flushSync(() => {
        setActiveWorkspacePanel(null);
        setWorkspacePanelMessage("");
        setIsNoteDialogOpen(false);
        setNoteFormError("");
        setNoteSuccessMessage("");
        setNoteCompletionSummary(null);
      });
    }

    navigate(
      accountRecoveryOrigin.returnRoute,
      isManagerCommandReturn
        ? { state: { managerCommandReturnContext: accountRecoveryOrigin.managerCommandReturnContext } }
        : undefined,
    );
  }

  function returnToManagerCommand() {
    returnToAccountRecoveryOrigin();
  }

  function openManagerCommandLaunchedAction(action: ManagerCommandAccountAction) {
    if (!selectedAccount) return;
    if (action === "view-account") {
      setSelectedTab("activity");
      return;
    }
    if (action === "assign-collector") {
      if (blockIfReadOnly("Assign Collector")) {
        clearConsumedManagerCommandLaunchInstruction();
        return;
      }
      workspacePanelReturnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      resetActiveAccountWorkflowPanels();
      setActiveEnterpriseStudio(null);
      setGovernanceMessage("");
      setWorkspacePanelMessage("");
      setAccountEditError("");
      setAccountEditSuccessMessage("");
      setAccountEditForm(buildAccountEditFormFromAccount(selectedAccount, snapshot.workspace.company.companyName));
      setIsAccountEditMode(true);
      setIsAssignCollectorEditMode(true);
      setSelectedTab("overview");
      setActiveWorkspacePanel("account-info");
      setPendingAssignCollectorLandingKey(`${selectedAccount.id}:assign-collector:${launchedManagerActionToken || Date.now()}`);
      return;
    }
    if (action === "review-escalation" || action === "escalate") {
      launchQuickAction("Escalate", "escalate", () => {
        const currentCollector = findActiveInternalUserByName(activeInternalUserOptions, selectedAccount.assignedCollector);
        setEscalationDecisionError("");
        setEscalationCompletionSummary(null);
        setEscalationDecisionForm({
          ...emptyEscalationDecisionForm,
          reason: getDefaultEscalationReason(selectedAccount),
          assignedCollectorId: currentCollector?.id || "",
          assignedCollector: selectedAccount.assignedCollector,
        });
      });
      return;
    }
    if (action === "add-manager-note") {
      cancelPendingQuickActionSurfaceFocus();
      launchQuickAction("Note", "note", () => {
        setPendingManagerNoteLandingKey(`${selectedAccount.id}:manager-note:${Date.now()}`);
        setNoteForm({
          ...emptyNoteForm,
          noteType: "Manager",
          visibility: "manager_only",
          outcome: "Manager Review",
        });
        setNoteFormError("");
        setNoteSuccessMessage("");
        setNoteCompletionSummary(null);
        setIsNoteDialogOpen(true);
      });
      return;
    }
    if (action === "create-follow-up") {
      launchQuickAction("Tickler", "tickler", () => openScheduleFollowUpWorkflow());
      return;
    }
    if (action === "log-call") {
      launchQuickAction("Call", "call", () => {
        setCallFormError("");
        setCallSuccessMessage("");
        setIsCallDialogOpen(true);
      });
      return;
    }
    if (action === "assign-task") {
      setSelectedTab("tasks");
      setTaskCreateLaunchKey(`manager-task-${selectedAccount.id}-${Date.now()}`);
      return;
    }
    if (action === "promise") {
      launchQuickAction("Promise", "promise", openCreatePromiseWorkflow);
      return;
    }
    if (action === "generate-letter") {
      openGenerateLetterWorkflow();
      return;
    }
    if (action === "generate-statement") {
      openGenerateStatementWorkflow();
      return;
    }
    if (action === "request-document") {
      openDocumentRequestWorkflow();
    }
  }

  function openWorkspacePanel(panelMode: Exclude<ARRecoveryWorkspacePanelMode, null>) {
    workspacePanelReturnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    resetActiveAccountWorkflowPanels();
    setActiveEnterpriseStudio(null);
    setWorkspacePanelMessage("");
    setActiveWorkspacePanel(panelMode);
  }

  function clearActiveWorkspacePanelState() {
    if (activeWorkspacePanel === "account-info") {
      setIsAccountEditMode(false);
      setIsAssignCollectorEditMode(false);
      setPendingAssignCollectorLandingKey("");
    }
    resetActiveAccountWorkflowPanels();
    setActiveWorkspacePanel(null);
    setWorkspacePanelMessage("");
  }

  function closeWorkspacePanel() {
    clearActiveWorkspacePanelState();
    if (getManagerCommandReturnContext()) {
      returnToManagerCommand();
      return;
    }
    window.setTimeout(() => {
      workspacePanelReturnFocusRef.current?.focus({ preventScroll: true });
    }, 0);
  }

  function hasUnsavedCallWorkspaceChanges() {
    const current = callFormRef.current;
    return Boolean(current.outcome || current.durationMinutes.trim() || current.nextAction.trim() || current.notes.trim());
  }

  function hasUnsavedEmailWorkspaceChanges() {
    const current = emailWorkspaceFormRef.current;
    return Boolean(
      current.subject.trim() ||
      current.body.trim() ||
      current.cc.trim() ||
      current.bcc.trim() ||
      current.internalNotes.trim() ||
      current.attachmentIds.length,
    );
  }

  function hasUnsavedPaymentWorkspaceChanges() {
    const current = paymentWorkspaceFormRef.current;
    return Boolean(
      current.amount.trim() ||
      current.referenceNumber.trim() ||
      current.internalNotes.trim() ||
      current.appliedInvoiceNumbers.length ||
      current.paymentMethod ||
      current.paymentDate,
    );
  }

  function hasUnsavedPromiseWorkspaceChanges() {
    const current = promiseFormRef.current;
    return Boolean(
      current.promiseType ||
      current.amount.trim() ||
      current.promiseDate ||
      current.expectedPaymentMethod.trim() ||
      current.relatedInvoices.length ||
      current.notes.trim() ||
      current.outcome.trim() ||
      current.nextActionType ||
      current.nextActionDueDate ||
      current.nextActionDueTime ||
      current.nextActionAssignedTo.trim() ||
      current.reminderRequired ||
      current.reminderNotes.trim(),
    );
  }

  function hasUnsavedTicklerWorkspaceChanges() {
    const current = ticklerFormRef.current;
    return Boolean(
      current.followUpType ||
      current.dueDate ||
      current.dueTime ||
      current.priority !== emptyTicklerForm.priority ||
      current.assignedTo.trim() ||
      current.reminderRequired ||
      current.reminderNotes.trim() ||
      current.relatedInvoice ||
      current.relatedPromise ||
      current.notes.trim() ||
      current.outcome.trim() ||
      current.nextAction.trim(),
    );
  }

  function requestCloseCallWorkspace() {
    if (hasUnsavedCallWorkspaceChanges() && !window.confirm("Discard unsaved call details?")) {
      return;
    }
    setIsCallDialogOpen(false);
    setCallFormError("");
    setCallWorkspaceTab("overview");
    setCallWorkspaceMobilePane("call");
  }

  function requestCloseEmailWorkspace() {
    if (hasUnsavedEmailWorkspaceChanges() && !window.confirm("Discard unsaved email workspace changes?")) {
      return;
    }
    setIsEmailWorkspaceOpen(false);
    setActiveWorkspacePanel(null);
    setSelectedTab("communications");
    setEmailWorkspaceError("");
    setEmailWorkspaceDocumentPreview(null);
    setEmailWorkspaceTab("overview");
    setEmailWorkspaceMobilePane("email");
  }

  function requestClosePaymentWorkspace() {
    if (hasUnsavedPaymentWorkspaceChanges() && !paymentWorkspaceSuccess && !window.confirm("Discard unsaved payment workspace changes?")) {
      return;
    }
    setIsPaymentWorkspaceOpen(false);
    setPaymentWorkspaceError("");
    setPaymentWorkspaceSuccess(null);
    setPaymentWorkspaceTab("overview");
    setPaymentWorkspaceMobilePane("payment");
  }

  function requestClosePromiseWorkspace() {
    if (hasUnsavedPromiseWorkspaceChanges() && !promiseSuccessMessage && !window.confirm("Discard unsaved promise details?")) {
      return;
    }
    setIsPromiseDialogOpen(false);
    setPromiseFormError("");
    setEditingPromiseId("");
    setPromiseWorkflowMode("create");
    setPromiseSuccessSummary(null);
    setPromiseWorkspaceTab("overview");
    setPromiseWorkspaceMobilePane("promise");
  }

  function requestCloseTicklerWorkspace() {
    if (hasUnsavedTicklerWorkspaceChanges() && !ticklerSuccessMessage && !window.confirm("Discard unsaved follow-up details?")) {
      return;
    }
    setIsTicklerDialogOpen(false);
    setTicklerFormError("");
    setEditingTicklerId("");
    setTicklerWorkflowMode("create");
    setTicklerSuccessSummary(null);
    setTicklerWorkspaceTab("overview");
    setTicklerWorkspaceMobilePane("tickler");
    if (activeWorkspacePanel === "tickler" && getManagerCommandReturnContext()) {
      setActiveWorkspacePanel(null);
      setWorkspacePanelMessage("");
      returnToManagerCommand();
    }
  }

  function requestCloseNoteWorkspace() {
    setIsNoteDialogOpen(false);
    setNoteFormError("");
    setNoteSuccessMessage("");
    setNoteCompletionSummary(null);
    if (activeWorkspacePanel === "note" && getManagerCommandReturnContext()) {
      setActiveWorkspacePanel(null);
      setWorkspacePanelMessage("");
      returnToManagerCommand();
    }
  }

  function openEnterpriseStudio(studioMode: Exclude<ARRecoveryEnterpriseStudioMode, null>) {
    workspacePanelReturnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    resetActiveAccountWorkflowPanels();
    setActiveWorkspacePanel(null);
    setStudioStatusMessage("");
    setActiveStudioDesignerSection("template");
    setActiveStudioTab("preview");
    setIsStudioDesignerCollapsed(false);
    setActiveEnterpriseStudio(studioMode);
  }

  function closeEnterpriseStudio() {
    resetActiveAccountWorkflowPanels();
    setActiveEnterpriseStudio(null);
    setStudioStatusMessage("");
    window.setTimeout(() => {
      workspacePanelReturnFocusRef.current?.focus({ preventScroll: true });
    }, 0);
  }

  useEffect(() => {
    if (!activeWorkspacePanel) return undefined;

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        if (isLetterHistoryOpen) {
          setIsLetterHistoryOpen(false);
          window.setTimeout(() => {
            letterHistoryButtonRef.current?.focus({ preventScroll: true });
          }, 0);
          return;
        }
        if (isStatementHistoryOpen) {
          setIsStatementHistoryOpen(false);
          window.setTimeout(() => {
            statementHistoryButtonRef.current?.focus({ preventScroll: true });
          }, 0);
          return;
        }
        if (activeWorkspacePanel === "call" && isCallDialogOpen) {
          requestCloseCallWorkspace();
          return;
        }
        if (activeWorkspacePanel === "email" && isEmailWorkspaceOpen) {
          requestCloseEmailWorkspace();
          return;
        }
        if (activeWorkspacePanel === "payment" && isPaymentWorkspaceOpen) {
          requestClosePaymentWorkspace();
          return;
        }
        if (activeWorkspacePanel === "promise" && isPromiseDialogOpen) {
          requestClosePromiseWorkspace();
          return;
        }
        if (activeWorkspacePanel === "tickler" && isTicklerDialogOpen) {
          requestCloseTicklerWorkspace();
          return;
        }
        closeWorkspacePanel();
      }
    }

    window.addEventListener("keydown", handleEscape);
    window.setTimeout(() => {
      workspacePanelRef.current?.focus({ preventScroll: true });
    }, 0);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [activeWorkspacePanel, isCallDialogOpen, isEmailWorkspaceOpen, isPaymentWorkspaceOpen, isPromiseDialogOpen, isTicklerDialogOpen, isLetterHistoryOpen, isStatementHistoryOpen]);

  useEffect(() => {
    if (!activeEnterpriseStudio) return undefined;

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        closeEnterpriseStudio();
      }
    }

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [activeEnterpriseStudio]);

  useEffect(() => {
    const openedStatementWorkspace = isStatementDialogOpen && !previousStatementDialogOpenRef.current;
    previousStatementDialogOpenRef.current = isStatementDialogOpen;
    if (!openedStatementWorkspace) return undefined;

    const originalBodyOverflow = document.body.style.overflow;
    const originalDocumentOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    window.setTimeout(() => {
      workspacePanelRef.current?.querySelectorAll<HTMLElement>("[data-statement-execution-scroll-owner]").forEach((scrollOwner) => {
        scrollOwner.scrollTop = 0;
        scrollOwner.scrollLeft = 0;
      });
    }, 0);

    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.documentElement.style.overflow = originalDocumentOverflow;
    };
  }, [isStatementDialogOpen]);

  useEffect(() => {
    if (!activeWorkspacePanel) return;
    const workflowClosed =
      (activeWorkspacePanel === "call" && !isCallDialogOpen) ||
      (activeWorkspacePanel === "note" && !isNoteDialogOpen) ||
      (activeWorkspacePanel === "promise" && !isPromiseDialogOpen) ||
      (activeWorkspacePanel === "payment" && !isPaymentWorkspaceOpen) ||
      (activeWorkspacePanel === "tickler" && !isTicklerDialogOpen) ||
      (activeWorkspacePanel === "letter" && !isLetterDialogOpen) ||
      (activeWorkspacePanel === "statement" && !isStatementDialogOpen);
    if (workflowClosed) {
      closeWorkspacePanel();
    }
  }, [activeWorkspacePanel, isCallDialogOpen, isNoteDialogOpen, isPromiseDialogOpen, isPaymentWorkspaceOpen, isTicklerDialogOpen, isLetterDialogOpen, isStatementDialogOpen]);

  function focusQuickActionSurface() {
    if (quickActionFocusTimeoutRef.current !== null) {
      window.clearTimeout(quickActionFocusTimeoutRef.current);
    }
    quickActionFocusTimeoutRef.current = window.setTimeout(() => {
      quickActionFocusTimeoutRef.current = null;
      accountQuickActionSurfaceRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      accountQuickActionSurfaceRef.current?.focus({ preventScroll: true });
    }, 0);
  }

  function cancelPendingQuickActionSurfaceFocus() {
    if (quickActionFocusTimeoutRef.current === null) return;
    window.clearTimeout(quickActionFocusTimeoutRef.current);
    quickActionFocusTimeoutRef.current = null;
  }

  function actorName() {
    return currentActor.name || currentActor.email || selectedAccount?.assignedCollector || "Authenticated User";
  }

  function workflowActorContext() {
    return {
      actorId: currentActor.email || actorName(),
      actorName: actorName(),
      actorEmail: currentActor.email,
      actorRole: formatARRecoveryRole(currentActor.role),
      permissionLevel: canOverrideRecordOwnership ? "manager_override" : "collector_write",
    };
  }

  function blockIfReadOnly(actionLabel: string) {
    if (canWriteOperationalRecords) return false;
    setGovernanceMessage(`${actionLabel} is blocked by AR OS governance. Read Only users may view account records and audit history but cannot modify operational records.`);
    focusQuickActionSurface();
    return true;
  }

  function launchQuickAction(actionLabel: string, panelMode: Exclude<ARRecoveryWorkspacePanelMode, null>, openWorkflow: () => void) {
    if (blockIfReadOnly(actionLabel)) return;
    workspacePanelReturnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    resetActiveAccountWorkflowPanels();
    setActiveEnterpriseStudio(null);
    setGovernanceMessage("");
    setWorkspacePanelMessage("");
    setAccountEditSuccessMessage("");
    openWorkflow();
    setActiveWorkspacePanel(panelMode);
  }

  function publishNoteProjection(input: {
    noteType: ARRecoveryNoteType;
    visibility: ARRecoveryNoteVisibility;
    content: string;
    outcome?: string;
    nextAction?: string;
    sourceWorkspace?: string;
    relatedRecordType?: "account" | "promise" | "tickler" | "manager";
    relatedRecordId?: string;
  }) {
    if (!selectedAccount) return noteLogs;
    const { noteLogs: projectedNoteLogs } = publishNoteWorkflowAction({
      tenantId: workspace.company.id,
      clientId: workspace.company.id,
      customerId: selectedAccount.customerNumber,
      accountId: selectedAccount.id,
      customerName: selectedAccount.customerName,
      customerNumber: selectedAccount.customerNumber,
      noteType: input.noteType,
      visibility: input.visibility,
      content: input.content.trim(),
      outcome: input.outcome?.trim() || "",
      nextAction: input.nextAction?.trim() || "",
      actor: workflowActorContext(),
      sourceWorkspace: input.sourceWorkspace || "Recovery Workbench",
      initiatedFromRoute: "/admin/ar-recovery/customer-accounts",
      relatedRecordType: input.relatedRecordType || "account",
      relatedRecordId: input.relatedRecordId || selectedAccount.id,
    });
    const legacyNoteLogs = loadStoredARRecoveryNoteLogs();
    const projectedIds = new Set(projectedNoteLogs.map((note) => note.id));
    const nextNoteLogs = [...projectedNoteLogs.filter(isARRecoveryNoteLogRecord), ...legacyNoteLogs.filter((note) => !projectedIds.has(note.id))]
      .sort((a, b) => getDateTime(b.createdAt) - getDateTime(a.createdAt));
    saveStoredARRecoveryNoteLogs(nextNoteLogs);
    setNoteLogs(nextNoteLogs);
    return nextNoteLogs;
  }

  function publishPromiseProjection(input: {
    action: ARWorkflowPromiseAction;
    promiseRecord: ARRecoveryPromiseWorkflowRecord;
    sourceWorkspace?: string;
    previousValue?: unknown;
  }) {
    if (!selectedAccount) return promiseWorkflows;
    const { promiseWorkflows: projectedPromiseWorkflows } = publishPromiseWorkflowAction({
      tenantId: workspace.company.id,
      clientId: workspace.company.id,
      customerId: selectedAccount.customerNumber,
      accountId: selectedAccount.id,
      customerName: selectedAccount.customerName,
      customerNumber: selectedAccount.customerNumber,
      promiseId: input.promiseRecord.id,
      promiseRecord: input.promiseRecord as unknown as Record<string, unknown>,
      action: input.action,
      actor: workflowActorContext(),
      sourceWorkspace: input.sourceWorkspace || "Promise Center",
      initiatedFromRoute: "/admin/ar-recovery/customer-accounts",
      previousValue: input.previousValue,
    });
    const legacyPromiseWorkflows = loadStoredARRecoveryPromiseWorkflows();
    const validProjectedPromiseWorkflows = projectedPromiseWorkflows
      .map((promise) => promise as unknown)
      .filter(isARRecoveryPromiseWorkflowRecord);
    const projectedIds = new Set(validProjectedPromiseWorkflows.map((promise) => promise.id));
    const nextPromiseWorkflows = [...validProjectedPromiseWorkflows, ...legacyPromiseWorkflows.filter((promise) => !projectedIds.has(promise.id))]
      .sort((a, b) => getDateTime(b.updatedAt || b.createdAt) - getDateTime(a.updatedAt || a.createdAt));
    saveStoredARRecoveryPromiseWorkflows(nextPromiseWorkflows);
    setPromiseWorkflows(nextPromiseWorkflows);
    if (input.action === "Promise.MarkBroken" || input.action === "Promise.Escalate") {
      publishEscalationProjection({
        action: "Escalation.Create",
        escalationRecord: buildEscalationWorkflowRecord({
          sourceRecord: input.promiseRecord as unknown as Record<string, unknown>,
          sourceRecordType: "promise",
          sourceTrigger: "Broken Promise",
          sourceEventType: "PromiseEscalated",
          reason: input.action === "Promise.MarkBroken" ? String(input.promiseRecord.brokenReason || "Promise marked broken") : "Promise escalated",
          priority: "Critical",
          assignedTo: String(input.promiseRecord.manager || "Manager Command"),
        }),
        sourceWorkspace: input.sourceWorkspace || "Promise Center",
      });
    }
    return nextPromiseWorkflows;
  }

  function publishTicklerProjection(input: {
    action: ARWorkflowTicklerAction;
    ticklerRecord: ARRecoveryTicklerWorkflowRecord;
    sourceWorkspace?: string;
    previousValue?: unknown;
  }) {
    if (!selectedAccount) return ticklerWorkflows;
    const { ticklerWorkflows: projectedTicklerWorkflows } = publishTicklerWorkflowAction({
      tenantId: workspace.company.id,
      clientId: workspace.company.id,
      customerId: selectedAccount.customerNumber,
      accountId: selectedAccount.id,
      customerName: selectedAccount.customerName,
      customerNumber: selectedAccount.customerNumber,
      ticklerId: input.ticklerRecord.id,
      ticklerRecord: input.ticklerRecord as unknown as Record<string, unknown>,
      action: input.action,
      actor: workflowActorContext(),
      sourceWorkspace: input.sourceWorkspace || "Tickler Center",
      initiatedFromRoute: "/admin/ar-recovery/customer-accounts",
      previousValue: input.previousValue,
    });
    const legacyTicklerWorkflows = loadStoredARRecoveryTicklerWorkflows();
    const validProjectedTicklerWorkflows = projectedTicklerWorkflows
      .map((tickler) => tickler as unknown)
      .filter(isARRecoveryTicklerWorkflowRecord);
    const projectedIds = new Set(validProjectedTicklerWorkflows.map((tickler) => tickler.id));
    const nextTicklerWorkflows = [...validProjectedTicklerWorkflows, ...legacyTicklerWorkflows.filter((tickler) => !projectedIds.has(tickler.id))]
      .sort((a, b) => getDateTime(a.dueDate) - getDateTime(b.dueDate));
    saveStoredARRecoveryTicklerWorkflows(nextTicklerWorkflows);
    setTicklerWorkflows(nextTicklerWorkflows);
    if (input.action === "Tickler.Escalate") {
      publishEscalationProjection({
        action: "Escalation.Create",
        escalationRecord: buildEscalationWorkflowRecord({
          sourceRecord: input.ticklerRecord as unknown as Record<string, unknown>,
          sourceRecordType: "tickler",
          sourceTrigger: "Overdue Tickler",
          sourceEventType: "TicklerEscalated",
          reason: String((input.ticklerRecord as unknown as Record<string, unknown>).reason || input.ticklerRecord.title || "Follow-up escalated"),
          priority: String(input.ticklerRecord.priority || "High"),
          assignedTo: "Manager Command",
        }),
        sourceWorkspace: input.sourceWorkspace || "Tickler Center",
      });
    }
    return nextTicklerWorkflows;
  }

  function buildEscalationWorkflowRecord(input: {
    sourceRecord: Record<string, unknown>;
    sourceRecordType: "account" | "promise" | "tickler";
    sourceTrigger: string;
    sourceEventType: string;
    reason: string;
    priority: string;
    assignedTo: string;
  }) {
    const now = new Date().toISOString();
    const sourceRecordId = String(input.sourceRecord.id || selectedAccount?.id || "manual");
    return {
      id: `escalation-${sourceRecordId}-${Date.now()}`,
      companyId: workspace.company.id,
      accountId: selectedAccount?.id || "",
      customerName: selectedAccount?.customerName || "Selected Account",
      customerNumber: selectedAccount?.customerNumber || "",
      status: "Created",
      priority: input.priority,
      reason: input.reason,
      assignedTo: input.assignedTo,
      createdAt: now,
      updatedAt: now,
      createdBy: actorName(),
      actorName: actorName(),
      sourceTrigger: input.sourceTrigger,
      sourceEventType: input.sourceEventType,
      sourceRecordType: input.sourceRecordType,
      sourceRecordId,
      lifecycle: ["Created", "Assigned", "In Review", "Resolved", "Closed", "Reopened"],
    };
  }

  function publishEscalationProjection(input: {
    action: ARWorkflowEscalationAction;
    escalationRecord: Record<string, unknown>;
    sourceWorkspace?: string;
    previousValue?: unknown;
  }) {
    if (!selectedAccount) return escalationWorkflows;
    const escalationId = String(input.escalationRecord.id || `escalation-${selectedAccount.id}-${Date.now()}`);
    const { escalationWorkflows: projectedEscalationWorkflows } = publishEscalationWorkflowAction({
      tenantId: workspace.company.id,
      clientId: workspace.company.id,
      customerId: selectedAccount.customerNumber,
      accountId: selectedAccount.id,
      customerName: selectedAccount.customerName,
      customerNumber: selectedAccount.customerNumber,
      escalationId,
      escalationRecord: {
        ...input.escalationRecord,
        id: escalationId,
        companyId: workspace.company.id,
        accountId: selectedAccount.id,
        customerName: selectedAccount.customerName,
        customerNumber: selectedAccount.customerNumber,
      },
      action: input.action,
      actor: workflowActorContext(),
      sourceWorkspace: input.sourceWorkspace || "Recovery Workbench",
      initiatedFromRoute: "/admin/ar-recovery/customer-accounts",
      previousValue: input.previousValue,
    });
    const companyEscalations = projectedEscalationWorkflows.filter((escalation) => escalation.companyId === workspace.company.id);
    setEscalationWorkflows(companyEscalations);
    return companyEscalations;
  }

  function getDefaultEscalationReason(account: NonNullable<typeof selectedAccount>): ARRecoveryEscalationReason {
    if (selectedAccountPromises.some((promise) => promise.status === "Broken" || promise.status === "Escalated")) return "Broken Promise";
    if (account.escalationStatus && account.escalationStatus !== "None") return "Collector Approval Required";
    if (account.openBalanceCents >= 2500000) return "High Balance";
    return "Other";
  }

  function openEscalationDecisionWorkspace() {
    if (!selectedAccount) return;
    const currentCollector = findActiveInternalUserByName(activeInternalUserOptions, selectedAccount.assignedCollector);
    setEscalationDecisionForm({
      ...emptyEscalationDecisionForm,
      reason: getDefaultEscalationReason(selectedAccount),
      assignedCollectorId: currentCollector?.id || "",
      assignedCollector: selectedAccount.assignedCollector,
    });
    setEscalationDecisionError("");
    setEscalationCompletionSummary(null);
    setTaskSuccessMessage("");
  }

  function resolveEscalationDecisionAction(decision: ARRecoveryEscalationDecision): ARWorkflowEscalationAction {
    if (decision === "Close Escalation") return "Escalation.Close";
    if (decision === "Approve") return "Escalation.Resolve";
    if (decision === "Reassign Collector") return "Escalation.Assign";
    return "Escalation.Update";
  }

  async function handleCompleteEscalationDecision() {
    if (!selectedAccount) return;
    if (!escalationDecisionForm.reason) {
      setEscalationDecisionError("Escalation Reason is required.");
      return;
    }
    if (!escalationDecisionForm.evidenceReviewed) {
      setEscalationDecisionError("Confirm Evidence Reviewed before completing the escalation decision.");
      return;
    }
    if (!escalationDecisionForm.decision) {
      setEscalationDecisionError("Manager Decision is required.");
      return;
    }
    const managerNotesRequired = escalationDecisionForm.decision === "Return to Collector" || escalationDecisionForm.decision === "Reject" || escalationDecisionForm.decision === "Escalate Further";
    if (managerNotesRequired && !escalationDecisionForm.decisionNotes.trim()) {
      setEscalationDecisionError("Manager Notes are required for this decision.");
      return;
    }
    let selectedReplacementCollector: ARRecoveryActiveInternalUserOption | null = null;
    if (escalationDecisionForm.decision === "Reassign Collector") {
      selectedReplacementCollector = activeInternalUserOptions.find((user) => user.id === escalationDecisionForm.assignedCollectorId) || null;
      if (!selectedReplacementCollector) {
        setEscalationDecisionError("Select an eligible active internal user before completing reassignment.");
        return;
      }
      if (isSameCollectorName(selectedReplacementCollector.name, selectedAccount.assignedCollector)) {
        setEscalationDecisionError("Select a different collector to complete reassignment.");
        return;
      }
    }
    if (escalationDecisionForm.decision === "Request Documentation" && !escalationDecisionForm.documentationRequest.trim()) {
      setEscalationDecisionError("Documentation Request is required when requesting additional documentation.");
      return;
    }

    const now = new Date().toISOString();
    const currentEscalation = escalationWorkflows
      .filter((escalation) => escalation.accountId === selectedAccount.id)
      .sort((a, b) => getDateTime(String(b.updatedAt || b.createdAt || "")) - getDateTime(String(a.updatedAt || a.createdAt || "")))[0];
    const previousCollector = selectedAccount.assignedCollector;
    const previousCollectorUser = findActiveInternalUserByName(activeInternalUserOptions, previousCollector);
    if (selectedReplacementCollector) {
      const persistedAssignment = await persistAssignedCollectorChange(selectedAccount, selectedReplacementCollector.name, selectedReplacementCollector);
      if (!persistedAssignment.ok) {
        setEscalationDecisionError(persistedAssignment.error);
        return;
      }
    }
    const escalationRecord = {
      ...(currentEscalation || {}),
      ...buildEscalationWorkflowRecord({
        sourceRecord: selectedAccount as unknown as Record<string, unknown>,
        sourceRecordType: "account",
        sourceTrigger: escalationDecisionForm.reason,
        sourceEventType: "EscalationDecisionCompleted",
        reason: escalationDecisionForm.reason,
        priority: selectedAccount.recoveryStatus === "Escalated" ? "Critical" : "High",
        assignedTo: selectedReplacementCollector ? selectedReplacementCollector.name : String(currentEscalation?.assignedTo || "Manager Command"),
      }),
      id: String(currentEscalation?.id || `escalation-${selectedAccount.id}-${Date.now()}`),
      status: escalationDecisionForm.decision,
      decision: escalationDecisionForm.decision,
      decisionNotes: escalationDecisionForm.decisionNotes.trim(),
      documentationRequest: escalationDecisionForm.documentationRequest.trim(),
      evidenceReviewed: escalationDecisionForm.evidenceReviewed,
      completedBy: actorName(),
      completedAt: now,
      updatedAt: now,
      updatedBy: actorName(),
      previousCollector: selectedReplacementCollector ? previousCollector : undefined,
      previousCollectorId: selectedReplacementCollector ? previousCollectorUser?.id || "" : undefined,
      newCollector: selectedReplacementCollector?.name,
      newCollectorId: selectedReplacementCollector?.id,
    };
    const nextEscalations = publishEscalationProjection({
      action: resolveEscalationDecisionAction(escalationDecisionForm.decision),
      escalationRecord,
      sourceWorkspace: "Recovery Workbench",
      previousValue: currentEscalation,
    });
    setEscalationCompletionSummary({
      decision: escalationDecisionForm.decision,
      manager: actorName(),
      completedAt: now,
      escalationId: String(escalationRecord.id),
    });
    setEscalationDecisionError("");
    setTaskSuccessMessage("");
    setWorkspacePanelMessage("Escalation Completed Successfully. Timeline, Recovery Activity, Manager Command, and Escalation History updated.");
    setEscalationWorkflows(nextEscalations);
    if (activeWorkspacePanel === "escalate" && getManagerCommandReturnContext()) {
      returnToManagerCommand();
    }
  }

  function publishPaymentProjection(input: {
    action: ARWorkflowPaymentAction;
    paymentRecord: Record<string, unknown>;
    sourceWorkspace?: string;
    previousValue?: unknown;
  }) {
    if (!selectedAccount) return paymentWorkflows;
    const paymentId = String(input.paymentRecord.id || `payment-${selectedAccount.id}-${Date.now()}`);
    const { paymentWorkflows: projectedPaymentWorkflows } = publishPaymentWorkflowAction({
      tenantId: workspace.company.id,
      clientId: workspace.company.id,
      customerId: selectedAccount.customerNumber,
      accountId: selectedAccount.id,
      customerName: selectedAccount.customerName,
      customerNumber: selectedAccount.customerNumber,
      paymentId,
      paymentRecord: {
        ...input.paymentRecord,
        id: paymentId,
        companyId: workspace.company.id,
        accountId: selectedAccount.id,
        customerName: selectedAccount.customerName,
        customerNumber: selectedAccount.customerNumber,
      },
      action: input.action,
      actor: workflowActorContext(),
      sourceWorkspace: input.sourceWorkspace || "Recovery Workbench",
      initiatedFromRoute: "/admin/ar-recovery/customer-accounts",
      previousValue: input.previousValue,
    });
    const companyPayments = projectedPaymentWorkflows.filter((payment) => payment.companyId === workspace.company.id);
    setPaymentWorkflows(companyPayments);
    return companyPayments;
  }

  function publishLetterProjection(input: {
    action: ARWorkflowDocumentAction;
    letterRecord: ARRecoveryLetterWorkflowRecord;
    sourceWorkspace?: string;
    previousValue?: unknown;
    deliveryStatus?: "delivered" | "failed" | "not_configured" | "pending" | "sent";
  }) {
    if (!selectedAccount) return letterWorkflows;
    const { documentWorkflows: projectedLetterWorkflows } = publishDocumentWorkflowAction({
      tenantId: workspace.company.id,
      clientId: workspace.company.id,
      customerId: selectedAccount.customerNumber,
      accountId: selectedAccount.id,
      customerName: selectedAccount.customerName,
      customerNumber: selectedAccount.customerNumber,
      documentId: input.letterRecord.id,
      documentType: "letter",
      documentRecord: input.letterRecord as unknown as Record<string, unknown>,
      action: input.action,
      actor: workflowActorContext(),
      sourceWorkspace: input.sourceWorkspace || "Letter Center",
      initiatedFromRoute: "/admin/ar-recovery/customer-accounts",
      previousValue: input.previousValue,
      deliveryStatus: input.deliveryStatus,
    });
    const legacyLetterWorkflows = loadStoredARRecoveryLetterWorkflows();
    const validProjectedLetterWorkflows = projectedLetterWorkflows
      .map((letter) => letter as unknown)
      .filter(isARRecoveryLetterWorkflowRecord);
    const projectedIds = new Set(validProjectedLetterWorkflows.map((letter) => letter.id));
    const nextLetterWorkflows = [...validProjectedLetterWorkflows, ...legacyLetterWorkflows.filter((letter) => !projectedIds.has(letter.id))]
      .sort((a, b) => getDateTime(b.updatedAt || b.createdAt) - getDateTime(a.updatedAt || a.createdAt));
    saveStoredARRecoveryLetterWorkflows(nextLetterWorkflows);
    setLetterWorkflows(nextLetterWorkflows);
    return nextLetterWorkflows;
  }

  function publishStatementProjection(input: {
    action: ARWorkflowDocumentAction;
    statementRecord: ARRecoveryStatementWorkflowRecord;
    sourceWorkspace?: string;
    previousValue?: unknown;
    deliveryStatus?: "delivered" | "failed" | "not_configured" | "pending" | "sent";
  }) {
    if (!selectedAccount) return statementWorkflows;
    const { documentWorkflows: projectedStatementWorkflows } = publishDocumentWorkflowAction({
      tenantId: workspace.company.id,
      clientId: workspace.company.id,
      customerId: selectedAccount.customerNumber,
      accountId: selectedAccount.id,
      customerName: selectedAccount.customerName,
      customerNumber: selectedAccount.customerNumber,
      documentId: input.statementRecord.id,
      documentType: "statement",
      documentRecord: input.statementRecord as unknown as Record<string, unknown>,
      action: input.action,
      actor: workflowActorContext(),
      sourceWorkspace: input.sourceWorkspace || "Statement Center",
      initiatedFromRoute: "/admin/ar-recovery/customer-accounts",
      previousValue: input.previousValue,
      deliveryStatus: input.deliveryStatus,
    });
    const legacyStatementWorkflows = loadStoredARRecoveryStatementWorkflows();
    const validProjectedStatementWorkflows = projectedStatementWorkflows
      .map((statement) => statement as unknown)
      .filter(isARRecoveryStatementWorkflowRecord);
    const projectedIds = new Set(validProjectedStatementWorkflows.map((statement) => statement.id));
    const nextStatementWorkflows = [...validProjectedStatementWorkflows, ...legacyStatementWorkflows.filter((statement) => !projectedIds.has(statement.id))]
      .sort((a, b) => getDateTime(b.updatedAt || b.createdAt) - getDateTime(a.updatedAt || a.createdAt));
    saveStoredARRecoveryStatementWorkflows(nextStatementWorkflows);
    setStatementWorkflows(nextStatementWorkflows);
    return nextStatementWorkflows;
  }

  useEffect(() => {
    let cancelled = false;
    refreshBusinessCenterCompanyUsers()
      .then((users) => {
        if (!cancelled) setInternalUsers(users);
      })
      .catch(() => {
        if (!cancelled) setInternalUsers(loadBusinessCenterCompanyUsers());
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getSession()
      .then((session) => {
        if (cancelled) return;
        const user = session.user;
        setAuthenticatedActor({
          email: user?.email || "",
          name: user?.email || "Authenticated User",
          role: (user?.role as UserRole | undefined) || "",
        });
        setIsAuthenticatedActorReady(true);
      })
      .catch(() => {
        if (!cancelled) {
          setAuthenticatedActor(buildFallbackARRecoveryActor());
          setIsAuthenticatedActorReady(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedAccountId && operationalWorkspace.accounts[0]) {
      setSelectedAccountId(operationalWorkspace.accounts[0].id);
    }
  }, [selectedAccountId, operationalWorkspace.accounts]);

  useEffect(() => {
    if (selectedAccountId && !operationalWorkspace.accounts.some((account) => account.id === selectedAccountId)) {
      setSelectedAccountId(operationalWorkspace.accounts[0]?.id || "");
    }
  }, [selectedAccountId, operationalWorkspace.accounts]);

  useEffect(() => {
    if (!selectedAccount) {
      setIsAccountEditMode(false);
      setAccountEditForm(emptyAccountEditForm);
      return;
    }
    if (!isAccountEditMode) {
      setAccountEditForm(buildAccountEditFormFromAccount(selectedAccount, snapshot.workspace.company.companyName));
    }
  }, [selectedAccount, isAccountEditMode, snapshot.workspace.company.companyName]);

  useEffect(() => {
    if (isSearchingAccounts && snapshot.filteredAccounts.length) {
      setRecentAccountIds((current) => {
        const searchedIds = snapshot.filteredAccounts.slice(0, 3).map((account) => account.id);
        return [...searchedIds, ...current.filter((id) => !searchedIds.includes(id))].slice(0, 3);
      });
    }
  }, [isSearchingAccounts, snapshot.filteredAccounts]);

  const hasManagerCommandReturnContext = Boolean(
    accountRecoveryLaunchState?.accountRecoveryLaunch?.managerCommandReturnContext ||
    accountRecoveryLaunchState?.managerCommandReturnContext,
  );
  const isManagerCommandNoteWorkspace = hasManagerCommandReturnContext &&
    activeWorkspacePanel === "note" &&
    isNoteDialogOpen &&
    noteForm.noteType === "Manager" &&
    noteForm.visibility === "manager_only";

  useLayoutEffect(() => {
    if (!pendingManagerNoteLandingKey || !isManagerCommandNoteWorkspace) return;
    const panelBody = workspacePanelBodyRef.current;
    const noteSection = noteWorkflowSectionRef.current;
    const noteTextarea = noteContentTextareaRef.current;
    if (!panelBody || !noteSection || !noteTextarea) return;

    let frame = 0;
    const safeTopOffset = 16;
    const landOnManagerNoteForm = () => {
      const rect = noteSection.getBoundingClientRect();
      const scrollOwnerRect = panelBody.getBoundingClientRect();
      const nextTop = Math.max(0, panelBody.scrollTop + rect.top - scrollOwnerRect.top - safeTopOffset);
      panelBody.scrollTo({ top: nextTop, left: 0, behavior: "auto" });
      noteTextarea.focus({ preventScroll: true });

      const landedRect = noteSection.getBoundingClientRect();
      const landedOwnerRect = panelBody.getBoundingClientRect();
      const formVisible = landedRect.top >= landedOwnerRect.top + safeTopOffset - 8 && landedRect.top <= landedOwnerRect.top + 160;
      if (!formVisible && frame < 2) {
        frame += 1;
        window.requestAnimationFrame(landOnManagerNoteForm);
        return;
      }

      setPendingManagerNoteLandingKey("");
      clearConsumedManagerCommandLaunchInstruction();
    };

    const firstFrame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(landOnManagerNoteForm);
    });

    return () => window.cancelAnimationFrame(firstFrame);
  }, [pendingManagerNoteLandingKey, isManagerCommandNoteWorkspace, selectedAccount?.id]);

  useLayoutEffect(() => {
    if (!pendingAssignCollectorLandingKey || activeWorkspacePanel !== "account-info" || !isAccountEditMode) return;
    const panelBody = workspacePanelBodyRef.current;
    const collectorInput = assignedCollectorEditInputRef.current;
    if (!panelBody || !collectorInput) return;

    const frame = window.requestAnimationFrame(() => {
      const inputRect = collectorInput.getBoundingClientRect();
      const scrollOwnerRect = panelBody.getBoundingClientRect();
      const nextTop = Math.max(0, panelBody.scrollTop + inputRect.top - scrollOwnerRect.top - 96);
      panelBody.scrollTo({ top: nextTop, left: 0, behavior: "auto" });
      collectorInput.focus({ preventScroll: true });
      setPendingAssignCollectorLandingKey("");
      clearConsumedManagerCommandLaunchInstruction();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [pendingAssignCollectorLandingKey, activeWorkspacePanel, isAccountEditMode, selectedAccount?.id]);

  const selectedTabInfo = snapshot.tabLabels.find((tab) => tab.key === selectedTab);
  const selectedAccountPromises = selectedAccount ? snapshot.workspace.promises.filter((item) => item.accountId === selectedAccount.id) : [];
  const accountTicklers = selectedAccount ? snapshot.workspace.ticklers.filter((item) => item.accountId === selectedAccount.id) : [];
  const accountNoteEvents = selectedAccount ? snapshot.workspace.recoveryActivityEvents.filter((item) => item.accountId === selectedAccount.id) : [];
  const accountLetterEvents = selectedAccount ? snapshot.workspace.recoveryActivityEvents.filter((item) => item.accountId === selectedAccount.id) : [];
  const accountLetters = selectedAccount ? snapshot.workspace.collectionLetters.filter((item) => item.accountId === selectedAccount.id || item.customerNumber === selectedAccount.customerNumber) : [];
  const accountStatementEvents = selectedAccount ? snapshot.workspace.recoveryActivityEvents.filter((item) => item.accountId === selectedAccount.id) : [];
  const accountStatements = selectedAccount ? snapshot.workspace.statements.filter((item) => item.accountId === selectedAccount.id) : [];
  const accountCommunications = selectedAccount ? communicationLogs.filter((item) => item.accountId === selectedAccount.id) : [];
  const accountNoteLogs = selectedAccount ? noteLogs.filter((item) => item.accountId === selectedAccount.id) : [];
  const accountTasks = selectedAccount ? taskAssignments.filter((item) => item.accountId === selectedAccount.id) : [];
  const accountDocumentRequests = selectedAccount ? documentRequests.filter((item) => item.accountId === selectedAccount.id) : [];
  const promiseHistoryEntries = selectedAccount ? buildAccountPromiseHistoryEntries(selectedAccountPromises) : [];
  const noteWorkspaceVisibility = noteForm.visibility ? formatNoteVisibility(noteForm.visibility) : "Select visibility";
  const noteWorkspaceVisibilityDescription = noteForm.visibility
    ? getNoteVisibilityDescription(noteForm.visibility)
    : "Choose who can read this customer account knowledge.";
  const noteWorkspaceCategoryDescription = noteForm.noteType
    ? AR_RECOVERY_NOTE_TYPE_OPTIONS.find((option) => option.value === noteForm.noteType)?.description || "Business account knowledge."
    : "Choose the kind of account knowledge being captured.";
  const noteWorkspaceReadinessItems: ARRecoveryNoteReadinessItem[] = [
    { label: "Note Type", ready: Boolean(noteForm.noteType), detail: noteForm.noteType || "Select a business knowledge category." },
    { label: "Note Content", ready: Boolean(noteForm.content.trim()), detail: noteForm.content.trim() ? `${noteForm.content.trim().length} characters captured.` : "Capture what happened and why it matters." },
    { label: "Visibility", ready: Boolean(noteForm.visibility), detail: noteWorkspaceVisibilityDescription },
    { label: "Ready to Save", ready: Boolean(noteForm.noteType && noteForm.visibility && noteForm.content.trim()), detail: noteForm.noteType && noteForm.visibility && noteForm.content.trim() ? "Ready to Save" : "Complete required note details." },
  ];
  const noteWorkspaceCanSave = noteWorkspaceReadinessItems.every((item) => item.ready);
  const noteWorkspaceImpactItems = ["Timeline", "Recovery Activity", "Account History"];
  const noteEditorRows = Math.min(
    22,
    Math.max(
      7,
      noteForm.content.split("\n").reduce((lineCount, line) => lineCount + Math.max(1, Math.ceil(line.length / 76)), 0),
    ),
  );
  const accountCards = [
    { label: "Queue Accounts", value: snapshot.filteredAccounts.length, note: "Company-scoped accounts available for focused recovery work.", icon: <Users className="size-5" aria-hidden="true" /> },
    { label: "Outstanding Balance", value: formatARRecoveryCurrency(snapshot.totalOpenBalanceCents), note: "Current receivables across the filtered set.", icon: <CircleDollarSign className="size-5" aria-hidden="true" /> },
    { label: "Risk Accounts", value: snapshot.riskAccountCount, note: "Accounts flagged for action or escalation.", icon: <ShieldCheck className="size-5" aria-hidden="true" /> },
    { label: "Action Ready", value: snapshot.actionReadyAccountCount, note: "Accounts still active in the recovery flow.", icon: <Target className="size-5" aria-hidden="true" /> },
    { label: "Next Action", value: snapshot.nextActionDate ? formatDate(snapshot.nextActionDate) : "Not set", note: "Next scheduled touchpoint for the selected account.", icon: <Clock3 className="size-5" aria-hidden="true" /> },
  ];

  const selectedAccountActivity = selectedAccount
    ? snapshot.workspace.activity.filter((item) => item.accountId === selectedAccount.id)
    : [];
  const timelineEntries = selectedAccount ? buildAccountTimelineEntries(selectedAccount, selectedAccountActivity) : [];
  const callWorkspaceInvoices = selectedAccount ? buildInvoicePlaceholders(selectedAccount) : [];
  const callWorkspaceOldestInvoice = [...callWorkspaceInvoices].sort((a, b) => getDateTime(a.dueDate) - getDateTime(b.dueDate))[0];
  const callWorkspaceLargestInvoice = [...callWorkspaceInvoices].sort((a, b) => b.currentBalanceCents - a.currentBalanceCents)[0];
  const callWorkspaceHighRiskInvoices = callWorkspaceInvoices.filter((invoice) => selectedAccount && (selectedAccount.agingSummary.includes("90") || invoice.id === callWorkspaceOldestInvoice?.id || invoice.id === callWorkspaceLargestInvoice?.id));
  const filteredCallInvoices = callInvoiceFilter === "oldest"
    ? callWorkspaceOldestInvoice ? [callWorkspaceOldestInvoice] : []
    : callInvoiceFilter === "largest"
      ? callWorkspaceLargestInvoice ? [callWorkspaceLargestInvoice] : []
      : callInvoiceFilter === "high_risk"
        ? callWorkspaceHighRiskInvoices
        : callWorkspaceInvoices;
  const callTimelineActivity = selectedAccountActivity.filter((item) => {
    if (callTimelineFilter === "all") return true;
    const itemTime = getDateTime(item.createdAt);
    const now = new Date();
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    if (callTimelineFilter === "today") return itemTime >= start.getTime();
    if (callTimelineFilter === "week") return itemTime >= start.getTime() - 6 * 24 * 60 * 60 * 1000;
    return itemTime >= start.getTime() - 30 * 24 * 60 * 60 * 1000;
  });
  const callTimelineEntries = selectedAccount ? buildAccountTimelineEntries(selectedAccount, callTimelineActivity) : [];
  const callPinnedNotes = accountNoteLogs.filter((note) => note.outcome.toLowerCase().includes("pinned") || note.content.toLowerCase().includes("pinned"));
  const filteredCallNotes = accountNoteLogs.filter((note) => {
    if (callNotesFilter === "all") return true;
    if (callNotesFilter === "pinned") return callPinnedNotes.some((pinned) => pinned.id === note.id);
    if (callNotesFilter === "manager") return note.visibility === "manager_only" || note.noteType === "Escalation Note";
    if (callNotesFilter === "system") return note.noteType === "System Note";
    return note.visibility === "internal" || note.noteType === "Collection Note" || note.noteType === "Customer Note";
  });
  const filteredCallCommunications = accountCommunications.filter((message) => {
    if (callCommunicationsFilter === "all" || !selectedAccount) return true;
    const customerCommunication = isCustomerFacingCommunication(message, selectedAccount);
    return callCommunicationsFilter === "customer" ? customerCommunication : !customerCommunication;
  });
  const paymentWorkspaceAmountCents = parseCurrencyInputToCents(paymentWorkspaceForm.amount);
  const paymentWorkspaceAmountReceivedCents = Number.isFinite(paymentWorkspaceAmountCents) ? paymentWorkspaceAmountCents : 0;
  const paymentWorkspaceActivePromise = selectedAccountPromises.find((promise) => promiseMatchesFilter(promise, "active")) || null;
  const paymentWorkspacePromiseInvoiceNumbers = new Set(paymentWorkspaceActivePromise?.invoiceLabels || []);
  const paymentWorkspaceOrderedInvoices = [...callWorkspaceInvoices].sort((a, b) => {
    const promisePriority = Number(paymentWorkspacePromiseInvoiceNumbers.has(b.invoiceNumber)) - Number(paymentWorkspacePromiseInvoiceNumbers.has(a.invoiceNumber));
    if (promisePriority) return promisePriority;
    const oldestPriority = getDateTime(a.dueDate) - getDateTime(b.dueDate);
    if (oldestPriority) return oldestPriority;
    const balancePriority = b.currentBalanceCents - a.currentBalanceCents;
    if (balancePriority) return balancePriority;
    return getDateTime(b.invoiceDate) - getDateTime(a.invoiceDate);
  });
  const paymentWorkspaceSelectedInvoices = paymentWorkspaceOrderedInvoices.filter((invoice) => paymentWorkspaceForm.appliedInvoiceNumbers.includes(invoice.invoiceNumber));
  const paymentWorkspaceAppliedInvoiceTotalCents = paymentWorkspaceSelectedInvoices.reduce((total, invoice) => total + invoice.currentBalanceCents, 0);
  let paymentWorkspaceAllocationRemainderCents = paymentWorkspaceAmountReceivedCents;
  const paymentWorkspaceInvoiceAllocations = paymentWorkspaceSelectedInvoices.map((invoice) => {
    const appliedCents = Math.min(Math.max(paymentWorkspaceAllocationRemainderCents, 0), invoice.currentBalanceCents);
    paymentWorkspaceAllocationRemainderCents -= appliedCents;
    return {
      invoice,
      appliedCents,
      remainingAfterCents: Math.max(0, invoice.currentBalanceCents - appliedCents),
    };
  });
  const paymentWorkspaceAllocationByInvoiceNumber = new Map(paymentWorkspaceInvoiceAllocations.map((allocation) => [allocation.invoice.invoiceNumber, allocation]));
  const paymentWorkspaceAmountAppliedCents = paymentWorkspaceInvoiceAllocations.reduce((total, allocation) => total + allocation.appliedCents, 0);
  const paymentWorkspaceRemainingPaymentCents = Math.max(0, paymentWorkspaceAmountReceivedCents - paymentWorkspaceAmountAppliedCents);
  const paymentWorkspaceRemainingAccountBalanceCents = selectedAccount && Number.isFinite(paymentWorkspaceAmountCents)
    ? Math.max(0, selectedAccount.openBalanceCents - paymentWorkspaceAmountAppliedCents)
    : selectedAccount?.openBalanceCents || 0;
  const paymentWorkspaceRemainingInvoiceBalanceCents = Number.isFinite(paymentWorkspaceAmountCents)
    ? Math.max(0, paymentWorkspaceAppliedInvoiceTotalCents - paymentWorkspaceAmountAppliedCents)
    : paymentWorkspaceAppliedInvoiceTotalCents;
  const paymentWorkspacePromiseInvoiceMatch = paymentWorkspaceActivePromise
    ? paymentWorkspaceSelectedInvoices.some((invoice) => paymentWorkspaceActivePromise.invoiceLabels.includes(invoice.invoiceNumber))
    : false;
  const paymentWorkspaceRemainingPromiseBalanceCents = paymentWorkspaceActivePromise && Number.isFinite(paymentWorkspaceAmountCents)
    ? Math.max(0, paymentWorkspaceActivePromise.remainingBalanceCents - (paymentWorkspacePromiseInvoiceMatch ? paymentWorkspaceAmountAppliedCents : 0))
    : paymentWorkspaceActivePromise?.remainingBalanceCents || 0;
  const paymentWorkspaceOutstandingOverpaymentCents = selectedAccount && Number.isFinite(paymentWorkspaceAmountCents)
    ? Math.max(0, paymentWorkspaceAmountReceivedCents - selectedAccount.openBalanceCents)
    : 0;
  const paymentWorkspaceDuplicateReference = Boolean(
    selectedAccount &&
    paymentWorkspaceForm.referenceNumber.trim() &&
    paymentWorkflows.some((payment) =>
      payment.accountId === selectedAccount.id &&
      String(payment.externalPaymentReference || "").trim().toLowerCase() === paymentWorkspaceForm.referenceNumber.trim().toLowerCase() &&
      payment.lifecycleStatus !== "Voided",
    ),
  );
  const paymentWorkspaceOverpaymentRequiresConfirmation = Number.isFinite(paymentWorkspaceAmountCents) && paymentWorkspaceAppliedInvoiceTotalCents > 0 && paymentWorkspaceAmountReceivedCents > paymentWorkspaceAppliedInvoiceTotalCents;
  const paymentWorkspaceReadinessItems = [
    { label: "Amount", ready: Number.isFinite(paymentWorkspaceAmountCents) && paymentWorkspaceAmountCents > 0, detail: Number.isFinite(paymentWorkspaceAmountCents) && paymentWorkspaceAmountCents > 0 ? formatARRecoveryCurrency(paymentWorkspaceAmountCents) : "Enter an amount greater than zero." },
    { label: "Method", ready: Boolean(paymentWorkspaceForm.paymentMethod), detail: paymentWorkspaceForm.paymentMethod || "Select a payment method." },
    { label: "Allocation", ready: !callWorkspaceInvoices.length || paymentWorkspaceForm.appliedInvoiceNumbers.length > 0, detail: paymentWorkspaceForm.appliedInvoiceNumbers.length ? `${paymentWorkspaceForm.appliedInvoiceNumbers.length} invoice(s) selected.` : "Select at least one invoice." },
    { label: "Date", ready: Boolean(paymentWorkspaceForm.paymentDate) && getDateTime(paymentWorkspaceForm.paymentDate) <= Date.now(), detail: paymentWorkspaceForm.paymentDate ? formatCapturedDate(paymentWorkspaceForm.paymentDate) : "Select a payment date." },
    { label: "Reference", ready: !paymentWorkspaceDuplicateReference, detail: paymentWorkspaceDuplicateReference ? "Duplicate reference detected." : paymentWorkspaceForm.referenceNumber.trim() || "No reference entered." },
    { label: "Ready to Save", ready: !paymentWorkspaceOverpaymentRequiresConfirmation || paymentWorkspaceForm.overpaymentConfirmed, detail: paymentWorkspaceOverpaymentRequiresConfirmation ? "Confirm unapplied payment handling." : "No unapplied confirmation required." },
  ];
  const paymentWorkspaceCanSave = Boolean(
    selectedAccount &&
    Number.isFinite(paymentWorkspaceAmountCents) &&
    paymentWorkspaceAmountCents > 0 &&
    paymentWorkspaceForm.paymentDate &&
    paymentWorkspaceForm.paymentMethod &&
    (!callWorkspaceInvoices.length || paymentWorkspaceForm.appliedInvoiceNumbers.length) &&
    !paymentWorkspaceDuplicateReference &&
    (!paymentWorkspaceOverpaymentRequiresConfirmation || paymentWorkspaceForm.overpaymentConfirmed),
  );
  const promiseWorkspaceAmountCents = parsePromiseAmountCents(promiseForm.amount);
  const promiseWorkspaceSelectedInvoices = callWorkspaceInvoices.filter((invoice) => promiseForm.relatedInvoices.includes(invoice.invoiceNumber));
  const promiseWorkspaceSelectedInvoiceTotalCents = promiseWorkspaceSelectedInvoices.reduce((total, invoice) => total + invoice.currentBalanceCents, 0);
  let promiseWorkspaceCoverageRemainderCents = Math.max(promiseWorkspaceAmountCents, 0);
  const promiseWorkspaceInvoiceCoverage = callWorkspaceInvoices.map((invoice) => {
    const included = promiseForm.relatedInvoices.includes(invoice.invoiceNumber);
    const coveredCents = included ? Math.min(Math.max(promiseWorkspaceCoverageRemainderCents, 0), invoice.currentBalanceCents) : 0;
    if (included) promiseWorkspaceCoverageRemainderCents -= coveredCents;
    return {
      invoice,
      included,
      coveredCents,
      remainingOutsidePromiseCents: Math.max(0, invoice.currentBalanceCents - coveredCents),
    };
  });
  const promiseWorkspaceRemainingCustomerBalanceCents = selectedAccount
    ? Math.max(0, selectedAccount.openBalanceCents - Math.max(promiseWorkspaceAmountCents, 0))
    : 0;
  const promiseWorkspaceActivePromise = selectedAccountPromises.find((promise) => promiseMatchesFilter(promise, "active")) || null;
  const promiseWorkspaceLatestClosedPromise = selectedAccountPromises.find((promise) => isClosedPromiseLifecycleStatus(promise.status)) || null;
  const promiseWorkspaceAwareness = promiseWorkspaceActivePromise
    ? `Active Promise Exists: ${formatARRecoveryCurrency(promiseWorkspaceActivePromise.remainingBalanceCents)} remaining`
    : promiseWorkspaceLatestClosedPromise
      ? `Previous Promise ${promiseWorkspaceLatestClosedPromise.status}: ${formatCapturedDate(promiseWorkspaceLatestClosedPromise.promiseDate)}`
      : "No prior promise pressure on this account";
  const promiseWorkspaceHasCustomerAgreement = Boolean(promiseForm.notes.trim() || promiseForm.outcome.trim());
  const promiseWorkspaceLifecycleStage = promiseWorkspaceActivePromise?.status || (promiseForm.promiseType && promiseWorkspaceAmountCents > 0 && promiseForm.promiseDate ? "Confirmed" : "Created");
  const promiseWorkspaceReadinessItems = [
    { label: "Amount", ready: promiseWorkspaceAmountCents > 0, detail: promiseWorkspaceAmountCents > 0 ? formatARRecoveryCurrency(promiseWorkspaceAmountCents) : "Enter an amount greater than zero." },
    { label: "Date", ready: Boolean(promiseForm.promiseDate), detail: promiseForm.promiseDate ? formatCapturedDate(promiseForm.promiseDate) : "Capture the expected payment date." },
    { label: "Customer Agreement", ready: promiseWorkspaceHasCustomerAgreement, detail: promiseWorkspaceHasCustomerAgreement ? "Agreement context captured." : "Capture customer commitment notes." },
    { label: "Invoices Selected", ready: promiseForm.relatedInvoices.length > 0, detail: promiseForm.relatedInvoices.length ? `${promiseForm.relatedInvoices.length} invoice(s) covered.` : "Select at least one covered invoice." },
    { label: "Ready to Save", ready: Boolean(promiseForm.promiseType && promiseWorkspaceAmountCents > 0 && promiseForm.promiseDate && promiseForm.relatedInvoices.length && promiseWorkspaceHasCustomerAgreement), detail: promiseForm.promiseType || "Select promise type." },
  ];
  const promiseWorkspaceCanSave = promiseWorkspaceReadinessItems.every((item) => item.ready);
  const promiseWorkspaceMinimumReady = promiseWorkspaceCanSave;
  const promiseWorkspaceStatus = promiseWorkspaceCanSave ? "Promise Ready" : "Needs Promise Details";
  const promiseWorkspaceImpactItems = [
    "Update Timeline",
    "Update Recovery Activity",
    "Update Promise History",
    promiseForm.nextActionType || promiseForm.reminderRequired ? "Create/Update Tickler" : "Tickler remains unchanged unless follow-up is configured",
    "Update Customer Status",
  ];
  const ticklerWorkspaceActiveFollowUp = accountTicklers.find((tickler) => !["Completed", "Cancelled"].includes(tickler.status)) || null;
  const ticklerWorkspacePriorityDisplay =
    ticklerForm.priority === "Normal" ? "Medium" : ticklerForm.priority === "Critical" ? "Urgent" : ticklerForm.priority;
  const ticklerWorkspacePriorityRank =
    ticklerForm.priority === "Critical" ? "4 Urgent" : ticklerForm.priority === "High" ? "3 High" : ticklerForm.priority === "Normal" ? "2 Medium" : "1 Low";
  const ticklerWorkspaceTodayStart = new Date();
  ticklerWorkspaceTodayStart.setHours(0, 0, 0, 0);
  const formatTicklerWorkspaceDate = (date: Date) => date.toISOString().slice(0, 10);
  const addTicklerWorkspaceDays = (days: number) => {
    const date = new Date(ticklerWorkspaceTodayStart);
    date.setDate(date.getDate() + days);
    return formatTicklerWorkspaceDate(date);
  };
  const addTicklerWorkspaceBusinessDays = (days: number) => {
    const date = new Date(ticklerWorkspaceTodayStart);
    let remainingDays = days;
    while (remainingDays > 0) {
      date.setDate(date.getDate() + 1);
      const day = date.getDay();
      if (day !== 0 && day !== 6) remainingDays -= 1;
    }
    return formatTicklerWorkspaceDate(date);
  };
  const ticklerWorkspaceDueUrgency = (() => {
    if (!ticklerForm.dueDate) return "No due date selected";
    const selectedDate = new Date(`${ticklerForm.dueDate}T00:00:00`);
    const dayDelta = Math.round((selectedDate.getTime() - ticklerWorkspaceTodayStart.getTime()) / (24 * 60 * 60 * 1000));
    if (dayDelta === 0) return "Due Today";
    if (dayDelta === 1) return "Due Tomorrow";
    if (dayDelta > 1) return `Due in ${dayDelta} Days`;
    return `Overdue by ${Math.abs(dayDelta)} Day${Math.abs(dayDelta) === 1 ? "" : "s"}`;
  })();
  const ticklerWorkspaceSuggestedDates = [
    selectedAccountPromises.some((promise) => promise.status === "Broken")
      ? { label: "Broken Promise", date: addTicklerWorkspaceDays(1), note: "Tomorrow" }
      : null,
    accountStatements.some((statement) => ["Sent", "Delivered", "Opened"].includes(statement.status))
      ? { label: "Statement Sent", date: addTicklerWorkspaceBusinessDays(5), note: "5 Business Days" }
      : null,
    accountLetters.some((letter) => ["Sent", "Delivered", "Viewed"].includes(letter.status))
      ? { label: "Letter Sent", date: addTicklerWorkspaceDays(7), note: "7 Days" }
      : null,
  ].filter((item): item is { label: string; date: string; note: string } => Boolean(item));
  const ticklerWorkspaceLifecycleStage = editingTicklerId
    ? ticklerWorkspaceActiveFollowUp?.status || "Scheduled"
    : ticklerForm.followUpType && ticklerForm.dueDate && ticklerForm.assignedTo.trim()
      ? deriveTicklerStatusFromDate(ticklerForm.dueDate)
      : "Created";
  const ticklerWorkspaceReason = ticklerForm.notes.trim() || ticklerForm.reminderNotes.trim() || ticklerForm.relatedPromise || ticklerForm.relatedInvoice || "Not Captured";
  const ticklerWorkspaceReadinessItems = [
    { label: "Next Action", ready: Boolean(ticklerForm.followUpType), detail: ticklerForm.followUpType || "Select the follow-up action." },
    { label: "Due Date", ready: Boolean(ticklerForm.dueDate), detail: ticklerForm.dueDate ? `${formatCapturedDate(ticklerForm.dueDate)} | ${ticklerWorkspaceDueUrgency}` : "Select when this follow-up is due." },
    { label: "Priority", ready: Boolean(ticklerForm.priority), detail: ticklerWorkspacePriorityRank },
    { label: "Assignment", ready: Boolean(ticklerForm.assignedTo.trim()), detail: ticklerForm.assignedTo.trim() || "Assign a collector." },
    { label: "Ready to Save", ready: Boolean(ticklerForm.followUpType && ticklerForm.dueDate && ticklerForm.priority && ticklerForm.assignedTo.trim()), detail: ticklerForm.followUpType ? "Minimum follow-up details captured." : "Complete required follow-up details." },
  ];
  const ticklerWorkspaceCanSave = ticklerWorkspaceReadinessItems.every((item) => item.ready);
  const ticklerWorkspaceStatus = ticklerWorkspaceCanSave ? "Follow-up Ready" : "Needs Follow-up Details";
  const ticklerWorkspaceImpactItems = [
    "Update Timeline",
    "Update Recovery Activity",
    "Update Follow-up Queue",
    "Update Account History",
    "Update Customer Status",
  ];
  const ticklerWorkspaceTabs: Array<{ key: ARRecoveryTicklerWorkspaceTab; label: string }> = [
    { key: "overview", label: "Overview" },
    { key: "invoices", label: "Open Invoices" },
    { key: "timeline", label: "Timeline" },
    { key: "notes", label: "Notes" },
    { key: "communications", label: "Communications" },
    { key: "promises", label: "Promises" },
    { key: "statements", label: "Statements" },
    { key: "payments", label: "Payments" },
  ];
  const selectedEmailTemplate = AR_RECOVERY_EMAIL_WORKSPACE_TEMPLATES.find((template) => template.name === emailWorkspaceForm.template) || null;
  const emailWorkspaceAttachments: ARRecoveryEmailWorkspaceAttachment[] = selectedAccount
    ? documentDeliveries
        .filter((delivery) => delivery.accountId === selectedAccount.id && (delivery.family === "letter" || delivery.family === "statement"))
        .sort((a, b) => getDateTime(b.updatedAt || b.generatedAt) - getDateTime(a.updatedAt || a.generatedAt))
        .slice(0, 10)
        .map((delivery) => ({
          id: `delivery-${delivery.id}`,
          label: delivery.templateName || delivery.documentType,
          type: delivery.family === "letter" ? "Letter" : "Statement",
          note: `${delivery.deliveryStatus === "sent" ? "Sent" : "Ready"} | ${formatCapturedDate(delivery.generatedAt || delivery.createdAt)}`,
          documentKind: delivery.family === "letter" ? "certified_letter_pdf" as const : "certified_statement_pdf" as const,
          deliveryId: delivery.id,
        }))
    : [];
  const selectedEmailWorkspaceAttachments = emailWorkspaceAttachments.filter((attachment) => emailWorkspaceForm.attachmentIds.includes(attachment.id));
  const emailWorkspaceRecipient = emailWorkspaceForm.recipient.trim() || (selectedAccount ? `${selectedAccount.contactName} <${selectedAccount.contactEmail}>` : "");
  const emailWorkspaceSubject = emailWorkspaceForm.subject.trim();
  const emailWorkspaceBody = emailWorkspaceForm.body.trim();
  const emailWorkspaceGreeting = selectedAccount ? buildARRecoveryEmailGreeting(selectedAccount.contactName) : "Hello,";
  const emailWorkspaceOpening = "We hope you're doing well.";
  const emailWorkspaceAttachmentNotice = buildARRecoveryEmailAttachmentNotice(selectedEmailWorkspaceAttachments);
  const emailWorkspaceCustomerBody = selectedAccount
    ? buildARRecoveryCustomerEmailText({
        greeting: emailWorkspaceGreeting,
        opening: emailWorkspaceOpening,
        body: emailWorkspaceBody,
        attachmentNotice: emailWorkspaceAttachmentNotice,
        companyName: snapshot.workspace.company.companyName,
        email: workspace.clientProfile.email || "collections@collabrecover.com",
        phone: workspace.clientProfile.phone || "",
        website: workspace.clientProfile.website || "collabrecover.com",
      })
    : "";
  const emailWorkspaceCustomerHtml = selectedAccount
    ? buildARRecoveryCustomerEmailHtml({
        logoSrc: resolveARRecoveryLetterAssetSrc(AR_RECOVERY_LETTER_BRANDING_LOGO_SRC),
        subject: emailWorkspaceSubject,
        greeting: emailWorkspaceGreeting,
        opening: emailWorkspaceOpening,
        body: emailWorkspaceBody,
        attachmentNotice: emailWorkspaceAttachmentNotice,
        companyName: snapshot.workspace.company.companyName,
        email: workspace.clientProfile.email || "collections@collabrecover.com",
        phone: workspace.clientProfile.phone || "",
        website: workspace.clientProfile.website || "collabrecover.com",
      })
    : "";
  const emailWorkspaceCertifiedAttachments = selectedAccount
    ? buildARRecoveryEmailCertifiedAttachmentPayloads({
        attachments: selectedEmailWorkspaceAttachments,
        account: selectedAccount,
        companyName: snapshot.workspace.company.companyName,
        documentDeliveries,
      })
    : { payloads: [], missing: [] };
  const emailWorkspaceDocumentPackageItems: ARRecoveryEmailDocumentPackageItem[] = selectedAccount
    ? emailWorkspaceAttachments.map((attachment) => {
        const resolvedAttachment = buildARRecoveryEmailCertifiedAttachmentPayloads({
          attachments: [attachment],
          account: selectedAccount,
          companyName: snapshot.workspace.company.companyName,
          documentDeliveries,
        });
        const payload = resolvedAttachment.payloads[0] || null;
        const delivery = findCertifiedARRecoveryDocumentDelivery({
          attachment,
          documentDeliveries,
          accountId: selectedAccount.id,
        });
        const isSelected = emailWorkspaceForm.attachmentIds.includes(attachment.id);
        const status: ARRecoveryEmailDocumentStatus = payload ? isSelected ? "attached" : "ready" : "ready";
        const statusLabel = status === "attached" ? "ATTACHED" : "READY";
        return {
          attachment,
          payload,
          displayName: normalizeARRecoveryEmailDocumentName(attachment, payload),
          group: getARRecoveryEmailDocumentGroup(attachment.documentKind),
          documentType: getARRecoveryEmailDocumentTypeLabel(attachment.documentKind),
          status,
          statusLabel,
          statusDetail: status === "attached" ? "Attached" : "Ready",
          generatedLabel: delivery ? `Generated ${formatDate(delivery.generatedAt || delivery.createdAt)}` : status === "ready" ? "Generated" : "Not generated",
          formatLabel: "PDF",
          deliveryLabel: delivery?.deliveryStatus === "sent" ? "Sent" : status === "attached" ? "Attached" : "Ready",
        };
      })
    : [];
  const selectedEmailWorkspaceDocumentPackageItems = emailWorkspaceDocumentPackageItems.filter((item) => emailWorkspaceForm.attachmentIds.includes(item.attachment.id));
  const emailWorkspaceDocumentPackageGroups = (["Letters", "Statements", "Invoice Details", "Supporting Documents"] as const)
    .map((group) => ({
      group,
      items: emailWorkspaceDocumentPackageItems.filter((item) => item.group === group),
    }))
    .filter((group) => group.items.length);
  const selectedEmailWorkspaceDocumentPackageGroups = (["Letters", "Statements", "Invoice Details", "Supporting Documents"] as const)
    .map((group) => ({
      group,
      items: selectedEmailWorkspaceDocumentPackageItems.filter((item) => item.group === group),
    }))
    .filter((group) => group.items.length);
  const selectedEmailWorkspaceAttachedDocumentCount = selectedEmailWorkspaceDocumentPackageItems.filter((item) => item.status === "ready" || item.status === "attached").length;
  const emailWorkspacePackageStatus = emailWorkspaceDeliverySummary
    ? "Sent"
    : selectedEmailWorkspaceDocumentPackageItems.length
      ? "Ready"
      : "No Documents Selected";
  const emailWorkspaceCanSend = Boolean(
    selectedAccount &&
    emailWorkspaceRecipient.trim() &&
    parseARRecoveryRecipient(emailWorkspaceRecipient).email &&
    isValidARRecoveryEmail(parseARRecoveryRecipient(emailWorkspaceRecipient).email) &&
    emailWorkspaceSubject &&
    emailWorkspaceForm.template &&
    emailWorkspaceBody,
  );
  useEffect(() => {
    if (!isEmailWorkspaceOpen) return;
    const timer = window.setTimeout(() => {
      setEmailWorkspaceValidation(validateARRecoveryEmailAddressFields(emailWorkspaceForm, emailWorkspaceRecipient));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [emailWorkspaceForm.recipient, emailWorkspaceForm.cc, emailWorkspaceForm.bcc, emailWorkspaceRecipient, isEmailWorkspaceOpen]);
  const emailWorkspaceContextEvents = selectedAccount ? [
    ...accountCommunications.map((message) => ({
      id: message.id,
      date: message.createdAt,
      label: isCustomerFacingCommunication(message, selectedAccount) ? "Customer Emails" : "Internal Communications",
      title: message.subject || message.messageType,
      detail: previewNoteContent(message.message),
    })),
    ...callLogs.filter((call) => call.accountId === selectedAccount.id).map((call) => ({
      id: call.id,
      date: call.createdAt,
      label: "Call Logs",
      title: call.outcome,
      detail: call.notes,
    })),
    ...accountLetters.map((letter) => ({
      id: letter.id,
      date: letter.updatedAt || letter.createdAt,
      label: "Letters",
      title: formatRelatedLetterLabel(letter),
      detail: letter.notes || letter.status,
    })),
    ...accountStatements.map((statement) => ({
      id: statement.id,
      date: statement.updatedAt || statement.createdAt,
      label: "Statements",
      title: statement.statementNumber,
      detail: `${statement.status} | ${formatARRecoveryCurrency(statement.balanceCents)}`,
    })),
  ].sort((a, b) => getDateTime(b.date) - getDateTime(a.date)) : [];
  const recentAccounts = recentAccountIds
    .map((id) => operationalWorkspace.accounts.find((account) => account.id === id))
    .filter((account): account is NonNullable<typeof account> => Boolean(account));
  const normalizedAccountSearch = search.trim().toLowerCase();
  const preferredSearchAccount = isSearchingAccounts
    ? snapshot.filteredAccounts.find((account) =>
        account.customerNumber.toLowerCase() === normalizedAccountSearch ||
        account.customerName.toLowerCase() === normalizedAccountSearch,
      ) || snapshot.filteredAccounts[0]
    : null;
  const queueAccounts = isSearchingAccounts ? (preferredSearchAccount ? [preferredSearchAccount] : []) : recentAccounts;
  const queueLabel = isSearchingAccounts ? "Search Results" : "Client Portfolio";
  const selectedAccountQueueIndex = selectedAccount ? queueAccounts.findIndex((account) => account.id === selectedAccount.id) : -1;
  const selectedAccountQueuePosition = selectedAccountQueueIndex >= 0 ? selectedAccountQueueIndex + 1 : 1;
  const accountNextAction = latestNoteWithNextAction?.nextAction || latestCall?.nextAction || (latestPromiseWithNextAction ? formatPromiseNextActionSummary(latestPromiseWithNextAction) : "") || (snapshot.nextActionDate ? formatDate(snapshot.nextActionDate) : "Not set");
  const editableSelectedAccount = selectedAccount ? getEditableAccountExtras(selectedAccount) : null;

  function activateSingleAccountWorkspace(accountId: string) {
    queuePageScrollPositionRef.current = window.scrollY;
    queueListScrollPositionRef.current = queueListRef.current?.scrollTop || 0;
    setSelectedAccountId(accountId);
    setActiveWorkspacePanel(null);
    setActiveEnterpriseStudio(null);
    setIsAssignCollectorEditMode(false);
    setPendingAssignCollectorLandingKey("");
    resetActiveAccountWorkflowPanels();
    setRecentAccountIds((current) => [accountId, ...current.filter((id) => id !== accountId)].slice(0, 3));
    setSelectedTab("activity");
    setIsSingleAccountWorkspaceActive(true);
    window.setTimeout(() => {
      accountQuickActionSurfaceRef.current?.focus({ preventScroll: true });
    }, 0);
  }

  function returnToAccountQueue() {
    setIsSingleAccountWorkspaceActive(false);
    setActiveWorkspacePanel(null);
    setActiveEnterpriseStudio(null);
    setIsAssignCollectorEditMode(false);
    setPendingAssignCollectorLandingKey("");
    resetActiveAccountWorkflowPanels();
    window.setTimeout(() => {
      queueListRef.current?.scrollTo({ top: queueListScrollPositionRef.current, left: 0, behavior: "auto" });
      window.scrollTo({ top: queuePageScrollPositionRef.current, left: 0, behavior: "auto" });
    }, 0);
  }

  function handleStartAccountEdit() {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Edit Account")) return;
    setAccountEditError("");
    setAccountEditSuccessMessage("");
    setAccountEditForm(buildAccountEditFormFromAccount(selectedAccount, snapshot.workspace.company.companyName));
    setIsAssignCollectorEditMode(false);
    setIsAccountEditMode(true);
  }

  function handleCancelAccountEdit() {
    const shouldReturnToManagerCommand =
      activeWorkspacePanel === "account-info" &&
      isAssignCollectorEditMode &&
      Boolean(getManagerCommandReturnContext());
    setAccountEditError("");
    setIsAccountEditMode(false);
    setIsAssignCollectorEditMode(false);
    setPendingAssignCollectorLandingKey("");
    if (selectedAccount) {
      setAccountEditForm(buildAccountEditFormFromAccount(selectedAccount, snapshot.workspace.company.companyName));
    }
    if (shouldReturnToManagerCommand) {
      setActiveWorkspacePanel(null);
      setWorkspacePanelMessage("");
      returnToManagerCommand();
    }
  }

  function getAccountLevelAppliedPaymentCents(accountId: string) {
    return paymentWorkflows
      .filter((payment) => payment.accountId === accountId && payment.applicationScope === "account" && payment.lifecycleStatus !== "Voided")
      .reduce((total, payment) => total + payment.amountCents, 0);
  }

  function buildAssignmentSafeAccountEditValues(account: NonNullable<typeof selectedAccount>, nextAssignedCollector: string): ARRecoveryAccountEditFormState {
    return {
      ...buildAccountEditFormFromAccount(account, snapshot.workspace.company.companyName),
      assignedCollector: nextAssignedCollector,
      currentBalance: formatBalanceInput(account.openBalanceCents + getAccountLevelAppliedPaymentCents(account.id)),
      collectorOwner: getEditableAccountExtras(account).collectorOwner || account.assignedCollector,
    };
  }

  function buildAssignmentProtectedSnapshot(account: NonNullable<typeof selectedAccount>) {
    const editableAccount = getEditableAccountExtras(account);
    return {
      customerName: account.customerName,
      customerNumber: account.customerNumber,
      accountNumber: editableAccount.accountNumber || account.customerNumber,
      currentBalanceCents: account.openBalanceCents,
      agingSummary: account.agingSummary,
      recoveryStatus: account.recoveryStatus,
      promiseToPayCount: account.promiseToPayCount,
      collectorOwner: editableAccount.collectorOwner || account.assignedCollector,
      openInvoiceTotalCents: callWorkspaceInvoices.reduce((total, invoice) => total + invoice.currentBalanceCents, 0),
      ticklerCount: accountTicklers.length,
    };
  }

  async function persistAssignedCollectorChange(
    account: NonNullable<typeof selectedAccount>,
    nextAssignedCollector: string,
    selectedCollector?: ARRecoveryActiveInternalUserOption | null,
  ) {
    const previousSnapshot = buildAssignmentProtectedSnapshot(account);
    const assignmentValues = buildAssignmentSafeAccountEditValues(account, nextAssignedCollector);
    const now = new Date().toISOString();
    const editRecord: ARRecoveryAccountEditRecord = {
      id: `account-edit-${account.id}-${Date.now()}`,
      companyId: account.companyId,
      accountId: account.id,
      customerName: account.customerName,
      customerNumber: account.customerNumber,
      actorName: actorName(),
      actorRole: formatARRecoveryRole(currentActor.role),
      createdAt: now,
      changes: [{
        field: "Assigned Collector",
        previousValue: normalizeCapturedValue(account.assignedCollector),
        newValue: normalizeCapturedValue(nextAssignedCollector),
      }],
      values: assignmentValues,
      assignment: {
        assignedCollectorId: selectedCollector?.id,
        assignedCollectorName: nextAssignedCollector,
        assignedAt: now,
        assignedBy: actorName(),
      },
    };
    const nextAccountEdits = [editRecord, ...accountEdits];
    const nextWorkspace = mergeWorkspaceWithOperationalLogs(workspace, callLogs, noteLogs, communicationLogs, taskAssignments, promiseWorkflows, ticklerWorkflows, letterWorkflows, statementWorkflows, escalationWorkflows, paymentWorkflows, loadProjectedARRecoveryNotifications(), loadProjectedARRecoveryReportingAnalytics(), loadProjectedEnterpriseClientExchangeFoundation(), loadPortfolioValidationEngineFoundation(workspace.company.id), loadPortfolioMappingEngineFoundation(workspace.company.id), loadPortfolioReviewEngineFoundation(workspace.company.id), loadPortfolioImportEngineFoundation(workspace.company.id), loadPortfolioAssignmentEngineFoundation(workspace.company.id), loadPortfolioActivationEngineFoundation(workspace.company.id), loadEnterprisePortfolioIntakeWalkthrough(workspace.company.id), documentDeliveries, documentRequests, nextAccountEdits);
    const nextAccount = nextWorkspace.accounts.find((candidate) => candidate.id === account.id);
    if (!nextAccount) {
      return { ok: false as const, error: "Assigned Collector could not be saved because the selected account was not found after validation." };
    }
    const nextSnapshot = buildAssignmentProtectedSnapshot(nextAccount);
    const protectedFieldsChanged = Object.entries(previousSnapshot).filter(([key, previousValue]) => {
      return nextSnapshot[key as keyof typeof nextSnapshot] !== previousValue;
    });
    if (protectedFieldsChanged.length) {
      return { ok: false as const, error: `Assigned Collector was not saved because protected account data would change: ${protectedFieldsChanged.map(([key]) => key).join(", ")}.` };
    }

    try {
      const persistedAccountEdits = await saveCanonicalARRecoveryAccountEdits(workspace.company.id, nextAccountEdits);
      setAccountEdits(persistedAccountEdits);
    } catch {
      return { ok: false as const, error: "Assigned Collector could not be saved to the canonical account record. Please retry before completing the reassignment." };
    }
    return { ok: true as const, editRecord, nextAccount };
  }

  async function handleSaveAssignedCollectorEdit() {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Assign Collector")) return;
    const nextAssignedCollector = accountEditForm.assignedCollector.trim();
    if (!nextAssignedCollector) {
      setAccountEditError("Assigned Collector is required.");
      return;
    }
    if (!activeInternalUserNames.includes(nextAssignedCollector)) {
      setAccountEditError("Select an eligible active internal user before saving Assigned Collector.");
      return;
    }
    if (nextAssignedCollector === selectedAccount.assignedCollector) {
      setAccountEditError("No Assigned Collector change was detected.");
      return;
    }

    const selectedCollector = findActiveInternalUserByName(activeInternalUserOptions, nextAssignedCollector);
    const persistedAssignment = await persistAssignedCollectorChange(selectedAccount, nextAssignedCollector, selectedCollector);
    if (!persistedAssignment.ok) {
      setAccountEditError(persistedAssignment.error);
      return;
    }

    setIsAccountEditMode(false);
    setIsAssignCollectorEditMode(false);
    setPendingAssignCollectorLandingKey("");
    setAccountEditError("");
    setAccountEditSuccessMessage("Assigned Collector updated. Account Recovery, Manager Command, Timeline, Account History, Recovery Activity, and Audit Trail now reflect the reassignment.");
    if (activeWorkspacePanel === "account-info" && getManagerCommandReturnContext()) {
      setActiveWorkspacePanel(null);
      setWorkspacePanelMessage("");
      returnToManagerCommand();
    }
  }

  async function handleSaveAccountEdit() {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Save Account Information")) return;
    if (isAssignCollectorEditMode) {
      handleSaveAssignedCollectorEdit();
      return;
    }
    if (!accountEditForm.customerName.trim() || !accountEditForm.customerNumber.trim() || !accountEditForm.email.trim()) {
      setAccountEditError("Customer Name, Customer Number, and Email are required before saving account information.");
      return;
    }
    const balanceCents = parseCurrencyInputToCents(accountEditForm.currentBalance);
    if (!Number.isFinite(balanceCents)) {
      setAccountEditError("Current Balance must be a valid number.");
      return;
    }
    const normalizedAccountEditForm: ARRecoveryAccountEditFormState = {
      ...accountEditForm,
      customerName: accountEditForm.customerName.trim(),
      customerNumber: accountEditForm.customerNumber.trim(),
      accountNumber: accountEditForm.accountNumber.trim(),
      clientCompany: accountEditForm.clientCompany.trim(),
      assignedCollector: accountEditForm.assignedCollector.trim(),
      assignedManager: accountEditForm.assignedManager.trim(),
      currentBalance: formatBalanceInput(balanceCents),
      agingBucket: accountEditForm.agingBucket.trim(),
      promiseStatus: accountEditForm.promiseStatus.trim(),
      primaryContact: accountEditForm.primaryContact.trim(),
      contactTitle: accountEditForm.contactTitle.trim(),
      phone: accountEditForm.phone.trim(),
      email: accountEditForm.email.trim(),
      preferredContactMethod: accountEditForm.preferredContactMethod.trim(),
      additionalContacts: accountEditForm.additionalContacts.trim(),
      collectorOwner: accountEditForm.collectorOwner.trim(),
      holdStatus: accountEditForm.holdStatus.trim(),
      disputeStatus: accountEditForm.disputeStatus.trim(),
      recoveryInstructions: accountEditForm.recoveryInstructions.trim(),
    };
    const changes = buildAccountEditChanges(selectedAccount, snapshot.workspace.company.companyName, normalizedAccountEditForm);
    if (!changes.length) {
      setAccountEditError("No account information changes were detected.");
      return;
    }
    const editRecord: ARRecoveryAccountEditRecord = {
      id: `account-edit-${selectedAccount.id}-${Date.now()}`,
      companyId: selectedAccount.companyId,
      accountId: selectedAccount.id,
      customerName: normalizedAccountEditForm.customerName,
      customerNumber: normalizedAccountEditForm.customerNumber,
      actorName: actorName(),
      actorRole: formatARRecoveryRole(currentActor.role),
      createdAt: new Date().toISOString(),
      changes,
      values: normalizedAccountEditForm,
      assignment: changes.some((change) => change.field === "Assigned Collector")
        ? {
            assignedCollectorId: findActiveInternalUserByName(activeInternalUserOptions, normalizedAccountEditForm.assignedCollector)?.id,
            assignedCollectorName: normalizedAccountEditForm.assignedCollector,
            assignedAt: new Date().toISOString(),
            assignedBy: actorName(),
          }
        : undefined,
    };
    const nextAccountEdits = [editRecord, ...accountEdits];
    try {
      const persistedAccountEdits = await saveCanonicalARRecoveryAccountEdits(workspace.company.id, nextAccountEdits);
      setAccountEdits(persistedAccountEdits);
    } catch {
      setAccountEditError("Account Information could not be saved to the canonical account record. Please retry.");
      return;
    }
    setIsAccountEditMode(false);
    setAccountEditError("");
    setAccountEditSuccessMessage("Account Information updated. Timeline, Account History, Recovery Activity, and Audit Trail now reflect the authenticated user and changed fields.");
  }

  function handleSaveCall() {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Log Call")) return;

    if (!callForm.outcome) {
      setCallFormError("Call outcome is required.");
      return;
    }

    if (callForm.durationMinutes.trim() && !Number.isFinite(Number(callForm.durationMinutes))) {
      setCallFormError("Call duration must be a number of minutes.");
      return;
    }

    if (!callForm.notes.trim()) {
      setCallFormError("Call notes are required.");
      return;
    }

    if (!callForm.nextAction.trim()) {
      setCallFormError("Next action is required.");
      return;
    }

    const { callLogs: projectedCallLogs } = publishCallWorkflowAction({
      tenantId: workspace.company.id,
      clientId: workspace.company.id,
      customerId: selectedAccount.customerNumber,
      accountId: selectedAccount.id,
      customerName: selectedAccount.customerName,
      customerNumber: selectedAccount.customerNumber,
      outcome: callForm.outcome,
      durationMinutes: callForm.durationMinutes.trim(),
      notes: callForm.notes.trim(),
      nextAction: callForm.nextAction.trim(),
      actor: workflowActorContext(),
      sourceWorkspace: "Recovery Workbench",
      initiatedFromRoute: "/admin/ar-recovery/customer-accounts",
    });
    const legacyCallLogs = loadStoredARRecoveryCallLogs();
    const projectedIds = new Set(projectedCallLogs.map((call) => call.id));
    const nextCallLogs = [...projectedCallLogs.filter(isARRecoveryCallLogRecord), ...legacyCallLogs.filter((call) => !projectedIds.has(call.id))]
      .sort((a, b) => getDateTime(b.createdAt) - getDateTime(a.createdAt));
    saveStoredARRecoveryCallLogs(nextCallLogs);
    setCallLogs(nextCallLogs);
    setCallForm(emptyCallForm);
    setCallFormError("");
    setIsCallDialogOpen(false);
    setCallWorkspaceTab("timeline");
    setCallWorkspaceMobilePane("account");
    setSelectedTab("activity");
    setNoteSuccessMessage("");
    setCommunicationSuccessMessage("");
    setTaskSuccessMessage("");
    setStatementSuccessMessage("");
    setCallSuccessMessage(`Call logged for ${selectedAccount.customerName}. Timeline and Account History updated.`);
  }

  function handleSaveNote() {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Add Note")) return;

    if (!noteForm.noteType) {
      setNoteFormError("Note type is required.");
      return;
    }

    if (!noteForm.visibility) {
      setNoteFormError("Visibility is required.");
      return;
    }

    if (!noteForm.content.trim()) {
      setNoteFormError("Note content is required.");
      return;
    }

    publishNoteProjection({
      noteType: noteForm.noteType,
      visibility: noteForm.visibility,
      content: noteForm.content.trim(),
      outcome: noteForm.outcome.trim(),
      nextAction: noteForm.nextAction.trim(),
      sourceWorkspace: "Recovery Workbench",
      relatedRecordType: "account",
      relatedRecordId: selectedAccount.id,
    });
    setNoteCompletionSummary({
      category: noteForm.noteType,
      visibility: formatNoteVisibility(noteForm.visibility),
      customer: selectedAccount.customerName,
      createdAt: new Date().toISOString(),
    });
    setNoteForm(emptyNoteForm);
    setNoteFormError("");
    setCallSuccessMessage("");
    setCommunicationSuccessMessage("");
    setTaskSuccessMessage("");
    setStatementSuccessMessage("");
    setNoteSuccessMessage("Note Saved Successfully");
    if (activeWorkspacePanel === "note" && getManagerCommandReturnContext()) {
      setIsNoteDialogOpen(false);
      setActiveWorkspacePanel(null);
      setWorkspacePanelMessage("");
      returnToManagerCommand();
    }
  }

  function handleSendCommunication(draft: ARRecoveryCommunicationDraft) {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Communications")) return;

    const now = new Date().toISOString();
    const actor = actorName();
    const communicationRecord: ARRecoveryCommunicationRecord = {
      id: `communication-${selectedAccount.id}-${Date.now()}`,
      conversationId: draft.conversationId || `conversation-${selectedAccount.id}-${Date.now()}`,
      companyId: workspace.company.id,
      accountId: selectedAccount.id,
      customerName: selectedAccount.customerName,
      customerNumber: selectedAccount.customerNumber,
      sender: actor,
      recipient: draft.recipient.trim(),
      subject: draft.subject.trim(),
      messageType: draft.messageType,
      message: draft.message.trim(),
      importance: draft.importance,
      createdAt: now,
      parentMessageId: draft.parentMessageId,
    };
    const nextCommunicationLogs = [communicationRecord, ...communicationLogs];

    saveStoredARRecoveryCommunications(nextCommunicationLogs);
    setCommunicationLogs(nextCommunicationLogs);
    setSelectedTab("communications");
    setCallSuccessMessage("");
    setNoteSuccessMessage("");
    setTaskSuccessMessage("");
    setStatementSuccessMessage("");
    setCommunicationSuccessMessage(`Message sent to ${communicationRecord.recipient}. Communications, Timeline, Account History, and Audit Trail updated.`);
  }

  function renderEmailWorkspaceText(value: string) {
    if (!selectedAccount) return value;
    return value
      .replaceAll("{{CustomerName}}", selectedAccount.customerName)
      .replaceAll("{{AccountNumber}}", editableSelectedAccount?.accountNumber || selectedAccount.customerNumber)
      .replaceAll("{{Balance}}", formatARRecoveryCurrency(selectedAccount.openBalanceCents))
      .replaceAll("{{Collector}}", selectedAccount.assignedCollector)
      .replaceAll("{{Company}}", snapshot.workspace.company.companyName);
  }

  function handleSelectEmailTemplate(templateName: ARRecoveryEmailWorkspaceTemplate | "") {
    const template = AR_RECOVERY_EMAIL_WORKSPACE_TEMPLATES.find((item) => item.name === templateName) || null;
    setEmailWorkspaceError("");
    setEmailWorkspaceDeliverySummary(null);
    setEmailWorkspaceDocumentPreview(null);
    setEmailWorkspaceForm((current) => ({
      ...current,
      template: templateName,
      subject: template ? renderEmailWorkspaceText(template.subject) : current.subject,
      body: template ? renderEmailWorkspaceText(template.body) : current.body,
      priority: template?.messageType === "Escalation" ? "High" : current.priority,
    }));
  }

  function toggleEmailWorkspaceAttachment(attachmentId: string) {
    setEmailWorkspaceDeliverySummary(null);
    setEmailWorkspaceDocumentPreview(null);
    setEmailWorkspaceForm((current) => ({
      ...current,
      attachmentIds: current.attachmentIds.includes(attachmentId)
        ? current.attachmentIds.filter((id) => id !== attachmentId)
        : [...current.attachmentIds, attachmentId],
    }));
  }

  function handleSaveEmailDraft() {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Save Email Draft")) return;
    setEmailWorkspaceError("");
    setEmailWorkspaceDeliverySummary(null);
    setCallSuccessMessage("");
    setNoteSuccessMessage("");
    setTaskSuccessMessage("");
    setStatementSuccessMessage("");
    setCommunicationSuccessMessage(`Email draft saved locally for ${selectedAccount.customerName}. No customer communication was sent.`);
  }

  function validateEmailWorkspaceRecipients() {
    const nextValidation = validateARRecoveryEmailAddressFields(emailWorkspaceForm, emailWorkspaceRecipient);
    setEmailWorkspaceValidation(nextValidation);
    const invalidField = getFirstInvalidARRecoveryEmailField(nextValidation);
    if (!invalidField) return true;
    const invalidAddresses = nextValidation[invalidField].invalidAddresses;
    setEmailWorkspaceError(formatARRecoveryInvalidRecipientMessage(invalidField, invalidAddresses));
    window.setTimeout(() => {
      if (invalidField === "recipient") emailRecipientInputRef.current?.focus();
      if (invalidField === "cc") emailCcInputRef.current?.focus();
      if (invalidField === "bcc") emailBccInputRef.current?.focus();
    }, 0);
    return false;
  }

  function handleEmailWorkspaceAddressBlur() {
    setEmailWorkspaceValidation(validateARRecoveryEmailAddressFields(emailWorkspaceForm, emailWorkspaceRecipient));
  }

  function removeEmailWorkspaceAttachment(attachmentId: string) {
    setEmailWorkspaceDeliverySummary(null);
    setEmailWorkspaceDocumentPreview(null);
    setEmailWorkspaceForm((current) => ({
      ...current,
      attachmentIds: current.attachmentIds.filter((id) => id !== attachmentId),
    }));
  }

  function previewEmailWorkspaceAttachment(item: ARRecoveryEmailDocumentPackageItem) {
    if (typeof window === "undefined") return;
    setEmailWorkspaceError("");
    setEmailWorkspaceDocumentPreview(buildARRecoveryEmailDocumentPreviewState(item));
  }

  function openEmailWorkspaceAttachment(item: ARRecoveryEmailDocumentPackageItem) {
    if (typeof window === "undefined") return;
    if (!item.payload) {
      setEmailWorkspaceDocumentPreview(buildARRecoveryEmailDocumentPreviewState(item));
      setEmailWorkspaceError(getARRecoveryEmailDocumentUnavailableMessage(item.attachment));
      return;
    }
    setEmailWorkspaceError("");
    const sourceUrl = `data:${item.payload.contentType};base64,${item.payload.contentBase64}`;
    const documentWindow = window.open(sourceUrl, "_blank", "noopener,noreferrer");
    if (!documentWindow) {
      setEmailWorkspaceDocumentPreview(buildARRecoveryEmailDocumentPreviewState(item));
      setEmailWorkspaceError("Document is ready, but the browser blocked the new tab. Use Preview to review it inside Email Workspace.");
      return;
    }
    documentWindow.focus();
  }

  async function handleSendEmailWorkspace() {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Send Email")) return;
    if (emailWorkspaceSubmitting) return;
    setEmailWorkspaceDeliverySummary(null);
    if (!validateEmailWorkspaceRecipients()) return;
    const recipientAddresses = parseARRecoveryEmailList(emailWorkspaceRecipient);
    const recipient = parseARRecoveryRecipient(recipientAddresses[0] || emailWorkspaceRecipient);
    const cc = [...recipientAddresses.slice(1), ...parseARRecoveryEmailList(emailWorkspaceForm.cc)];
    const bcc = parseARRecoveryEmailList(emailWorkspaceForm.bcc);

    if (!emailWorkspaceSubject) {
      setEmailWorkspaceError("Subject is required before sending.");
      return;
    }

    if (!emailWorkspaceForm.template) {
      setEmailWorkspaceError("Approved Email Template is required before sending.");
      return;
    }

    if (!emailWorkspaceBody) {
      setEmailWorkspaceError("Email Body is required before sending.");
      return;
    }

    const customerVisibleBody = emailWorkspaceCustomerBody;
    const customerVisibleHtml = emailWorkspaceCustomerHtml;
    const attachmentPayloads = emailWorkspaceCertifiedAttachments.payloads;
    if (emailWorkspaceCertifiedAttachments.missing.length) {
      setEmailWorkspaceError(`One or more selected documents are no longer available:\n\n${emailWorkspaceCertifiedAttachments.missing.map((name) => `- ${name}`).join("\n")}\n\nRemove the unavailable document or open the appropriate workspace to review it.`);
      return;
    }
    setEmailWorkspaceSubmitting(true);
    setEmailWorkspaceError("");
    setCommunicationSuccessMessage("Sending...");

    const delivery = await deliverARRecoveryEmail({
      recordType: "email",
      recipientEmail: recipient.email,
      recipientName: recipient.name || selectedAccount.contactName,
      cc,
      bcc,
      subject: emailWorkspaceSubject,
      body: customerVisibleBody,
      htmlBody: customerVisibleHtml,
      attachments: attachmentPayloads.length ? attachmentPayloads : undefined,
      accountName: selectedAccount.customerName,
      customerNumber: selectedAccount.customerNumber,
      recordLabel: emailWorkspaceForm.template || "Email",
    });

    setEmailWorkspaceSubmitting(false);

    if (!delivery.ok || delivery.status !== "sent") {
      const reason = delivery.providerError || delivery.message || "Provider did not confirm delivery.";
      setCommunicationSuccessMessage("");
      setEmailWorkspaceError(`Email could not be sent. Reason: ${reason}`);
      return;
    }

    handleSendCommunication({
      recipient: emailWorkspaceRecipient,
      subject: emailWorkspaceSubject,
      messageType: selectedEmailTemplate?.messageType || "General",
      message: customerVisibleBody,
      importance: emailWorkspaceForm.priority,
    });
    setEmailWorkspaceForm(emptyEmailWorkspaceForm);
    setEmailWorkspaceError("");
    setEmailWorkspaceValidation(buildEmptyARRecoveryEmailValidationState());
    setEmailWorkspaceDocumentPreview(null);
    setEmailWorkspaceDeliverySummary({
      recipient: emailWorkspaceRecipient,
      subject: emailWorkspaceSubject,
      attachments: attachmentPayloads.map((attachment) => attachment.name),
      sentAt: delivery.sentAt || new Date().toISOString(),
    });
    setCommunicationSuccessMessage(`Email sent successfully. Provider: ${delivery.provider}${delivery.providerMessageId ? ` (${delivery.providerMessageId})` : ""}. Communications, Timeline, Account History, Recovery Activity, and Audit updated.`);
    setEmailWorkspaceTab("communications");
    setEmailWorkspaceMobilePane("account");
  }

  function togglePaymentWorkspaceInvoice(invoiceNumber: string) {
    setPaymentWorkspaceSuccess(null);
    setPaymentWorkspaceError("");
    setPaymentWorkspaceForm((current) => ({
      ...current,
      appliedInvoiceNumbers: current.appliedInvoiceNumbers.includes(invoiceNumber)
        ? current.appliedInvoiceNumbers.filter((item) => item !== invoiceNumber)
        : [...current.appliedInvoiceNumbers, invoiceNumber],
      overpaymentConfirmed: false,
    }));
  }

  function applyPaymentWorkspaceInvoiceSuggestion(kind: "oldest" | "highest_risk" | "promise") {
    const suggestion = kind === "promise" && paymentWorkspaceActivePromise
      ? callWorkspaceInvoices.filter((invoice) => paymentWorkspaceActivePromise.invoiceLabels.includes(invoice.invoiceNumber))
      : kind === "highest_risk"
        ? callWorkspaceHighRiskInvoices
        : callWorkspaceOldestInvoice ? [callWorkspaceOldestInvoice] : [];
    setPaymentWorkspaceSuccess(null);
    setPaymentWorkspaceError("");
    setPaymentWorkspaceForm((current) => ({
      ...current,
      appliedInvoiceNumbers: suggestion.map((invoice) => invoice.invoiceNumber),
      overpaymentConfirmed: false,
    }));
  }

  function resetPaymentWorkspaceForAnotherPayment() {
    const invoices = selectedAccount ? buildInvoicePlaceholders(selectedAccount) : [];
    setPaymentWorkspaceForm({
      ...emptyPaymentWorkspaceForm,
      paymentDate: new Date().toISOString().slice(0, 10),
      appliedInvoiceNumbers: invoices.length === 1 ? [invoices[0].invoiceNumber] : [],
    });
    setPaymentWorkspaceError("");
    setPaymentWorkspaceSuccess(null);
    setPaymentWorkspaceMobilePane("payment");
  }

  function handleSavePaymentWorkspace() {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Record Payment")) return;

    const amountCents = parseCurrencyInputToCents(paymentWorkspaceForm.amount);
    if (!Number.isFinite(amountCents) || amountCents <= 0) {
      setPaymentWorkspaceError("Enter a payment amount greater than zero.");
      return;
    }
    if (!paymentWorkspaceForm.paymentMethod) {
      setPaymentWorkspaceError("Select a payment method before saving.");
      return;
    }
    if (!paymentWorkspaceForm.paymentDate) {
      setPaymentWorkspaceError("Select a payment date before saving.");
      return;
    }
    if (getDateTime(paymentWorkspaceForm.paymentDate) > Date.now()) {
      setPaymentWorkspaceError("Future-dated payments cannot be recorded in Recovery Workbench.");
      return;
    }
    if (callWorkspaceInvoices.length && !paymentWorkspaceForm.appliedInvoiceNumbers.length) {
      setPaymentWorkspaceError("Select at least one invoice to apply this payment.");
      return;
    }
    if (paymentWorkspaceDuplicateReference) {
      setPaymentWorkspaceError("This payment reference already exists for the selected account. Verify the reference before saving.");
      return;
    }
    if (paymentWorkspaceOverpaymentRequiresConfirmation && !paymentWorkspaceForm.overpaymentConfirmed) {
      setPaymentWorkspaceError("The payment is greater than the selected invoice balance. Confirm the overpayment before saving.");
      return;
    }

    const now = new Date().toISOString();
    const paymentId = `payment-${selectedAccount.id}-${Date.now()}`;
    const paymentReference = `PMT-${new Date(now).getFullYear()}-${String(Date.now()).slice(-6)}`;
    const actor = actorName();
    const basePaymentRecord = {
      id: paymentId,
      companyId: workspace.company.id,
      accountId: selectedAccount.id,
      customerName: selectedAccount.customerName,
      customerNumber: selectedAccount.customerNumber,
      amountCents,
      status: "Recorded",
      lifecycleStatus: "Recorded",
      paymentDate: paymentWorkspaceForm.paymentDate,
      recordedAt: now,
      updatedAt: now,
      recordedBy: actor,
      actorName: actor,
      paymentMethod: paymentWorkspaceForm.paymentMethod,
      paymentSource: paymentWorkspaceForm.paymentSource || "Customer",
      applicationScope: paymentWorkspaceForm.appliedInvoiceNumbers.length ? "account" : "",
      appliedToAccountId: selectedAccount.id,
      appliedInvoiceNumbers: paymentWorkspaceForm.appliedInvoiceNumbers,
      externalPaymentReference: paymentWorkspaceForm.referenceNumber.trim(),
      notes: paymentWorkspaceForm.internalNotes.trim(),
      accountingProvider: "Not Configured",
      accountingSyncStatus: "not_configured",
      paymentReference,
    };

    publishPaymentProjection({ action: "Payment.Record", paymentRecord: basePaymentRecord, sourceWorkspace: "Recovery Workbench" });
    if (paymentWorkspaceActivePromise && paymentWorkspacePromiseInvoiceMatch) {
      publishPaymentProjection({
        action: "Payment.ApplyToPromise",
        paymentRecord: { ...basePaymentRecord, status: "Applied", lifecycleStatus: "Applied", applicationScope: "promise", appliedToPromiseId: paymentWorkspaceActivePromise.id, updatedAt: now },
        sourceWorkspace: "Recovery Workbench",
        previousValue: basePaymentRecord,
      });
    }
    publishPaymentProjection({
      action: "Payment.ApplyToAccount",
      paymentRecord: { ...basePaymentRecord, status: "Applied", lifecycleStatus: "Applied", applicationScope: "account", updatedAt: now },
      sourceWorkspace: "Recovery Workbench",
      previousValue: basePaymentRecord,
    });

    setPaymentWorkspaceSuccess({
      amountCents,
      appliedInvoiceNumbers: paymentWorkspaceForm.appliedInvoiceNumbers,
      remainingBalanceCents: paymentWorkspaceRemainingAccountBalanceCents,
      recordedAt: now,
      paymentMethod: paymentWorkspaceForm.paymentMethod,
      referenceNumber: paymentWorkspaceForm.referenceNumber.trim(),
      paymentReference,
      unappliedAmountCents: paymentWorkspaceRemainingPaymentCents,
    });
    setPaymentWorkspaceError("");
    setTaskSuccessMessage("");
    setNoteSuccessMessage("");
    setStatementSuccessMessage("");
    setCommunicationSuccessMessage("");
    setSelectedTab("activity");
    setPaymentWorkspaceTab("timeline");
    setPaymentWorkspaceMobilePane("account");
  }

  function handleCreateTask(form: ARRecoveryTaskAssignmentFormState) {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Assign Task")) return;

    const now = new Date().toISOString();
    const actor = actorName();
    const taskRecord: ARRecoveryTaskAssignmentRecord = {
      id: `task-${selectedAccount.id}-${Date.now()}`,
      companyId: workspace.company.id,
      accountId: selectedAccount.id,
      customerName: selectedAccount.customerName,
      customerNumber: selectedAccount.customerNumber,
      taskTitle: form.taskTitle.trim(),
      category: form.category || "Custom",
      assignedBy: actor,
      assignedTo: form.assignedTo.trim(),
      priority: form.priority,
      dueDate: form.dueDate,
      description: form.description.trim(),
      relatedInvoice: form.relatedInvoice.trim(),
      relatedPromise: form.relatedPromise.trim(),
      relatedLetter: form.relatedLetter.trim(),
      relatedStatement: form.relatedStatement.trim(),
      status: "Open",
      createdAt: now,
      updatedAt: now,
      completedBy: "",
      completedAt: "",
      completionNotes: "",
      outcome: "",
      nextAction: "",
      comments: [],
    };
    const nextTaskAssignments = [taskRecord, ...taskAssignments];

    saveStoredARRecoveryTaskAssignments(nextTaskAssignments);
    setTaskAssignments(nextTaskAssignments);
    setSelectedTab("tasks");
    setCallSuccessMessage("");
    setNoteSuccessMessage("");
    setCommunicationSuccessMessage("");
    setStatementSuccessMessage("");
    setTaskSuccessMessage(`Task assigned to ${taskRecord.assignedTo}. Task Manager, Timeline, Account History, and Audit Trail updated.`);
  }

  function handleCompleteTask(taskId: string, completion: ARRecoveryTaskCompletionFormState) {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Complete Task")) return;

    const now = new Date().toISOString();
    const actor = actorName();
    const nextTaskAssignments = taskAssignments.map((task) =>
      task.id === taskId
        ? {
            ...task,
            status: "Completed" as const,
            completedBy: actor,
            completedAt: now,
            completionNotes: completion.completionNotes.trim(),
            outcome: completion.outcome.trim(),
            nextAction: completion.nextAction.trim(),
            updatedAt: now,
          }
        : task,
    );

    saveStoredARRecoveryTaskAssignments(nextTaskAssignments);
    setTaskAssignments(nextTaskAssignments);
    setSelectedTab("tasks");
    setCallSuccessMessage("");
    setNoteSuccessMessage("");
    setCommunicationSuccessMessage("");
    setStatementSuccessMessage("");
    setTaskSuccessMessage("Task completed. Task Manager, Timeline, Account History, Recovery Activity, and Audit Trail updated.");
  }

  function handleAddTaskComment(taskId: string, commentText: string) {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Task Comment")) return;

    const now = new Date().toISOString();
    const actor = actorName();
    const nextTaskAssignments = taskAssignments.map((task) =>
      task.id === taskId
        ? {
            ...task,
            comments: [
              ...task.comments,
              {
                id: `task-comment-${task.id}-${Date.now()}`,
                author: actor,
                comment: commentText.trim(),
                createdAt: now,
              },
            ],
            updatedAt: now,
          }
        : task,
    );

    saveStoredARRecoveryTaskAssignments(nextTaskAssignments);
    setTaskAssignments(nextTaskAssignments);
    setSelectedTab("tasks");
    setCallSuccessMessage("");
    setNoteSuccessMessage("");
    setCommunicationSuccessMessage("");
    setStatementSuccessMessage("");
    setTaskSuccessMessage("Task comment saved. Task discussion, Timeline, Account History, and Audit Trail updated.");
  }

  function openCreatePromiseWorkflow() {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Create Promise")) return;
    setPromiseWorkflowMode("create");
    setEditingPromiseId("");
    setPromiseForm({
      ...emptyPromiseForm,
      nextActionAssignedTo: selectedAccount.assignedCollector || "",
    });
    setPromiseFormError("");
    setPromiseSuccessMessage("");
    setPromiseSuccessSummary(null);
    setPromiseWorkspaceTab("overview");
    setPromiseWorkspaceMobilePane("promise");
    setIsPromiseDialogOpen(true);
    setSelectedTab("promises");
  }

  function openEditPromiseWorkflow(promise: AccountPromiseRecord) {
    if (blockIfReadOnly("Edit Promise")) return;
    setPromiseWorkflowMode("edit");
    setEditingPromiseId(promise.id);
    setPromiseForm(buildPromiseFormFromRecord(promise));
    setPromiseFormError("");
    setPromiseSuccessMessage("");
    setPromiseSuccessSummary(null);
    setPromiseWorkspaceTab("overview");
    setPromiseWorkspaceMobilePane("promise");
    setIsPromiseDialogOpen(true);
    setSelectedTab("promises");
  }

  function handleSavePromise() {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Save Promise")) return;

    const parsedAmount = parsePromiseAmountCents(promiseForm.amount);
    if (!promiseForm.promiseType) {
      setPromiseFormError("Promise type is required.");
      return;
    }
    if (!promiseForm.amount.trim() || parsedAmount <= 0) {
      setPromiseFormError("Promise amount is required and must be greater than zero.");
      return;
    }
    if (!promiseForm.promiseDate) {
      setPromiseFormError("Promise date is required.");
      return;
    }
    if (Number.isNaN(getDateTime(promiseForm.promiseDate))) {
      setPromiseFormError("Promise date must be valid.");
      return;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (getDateTime(promiseForm.promiseDate) < today.getTime()) {
      setPromiseFormError("Promise date cannot be in the past.");
      return;
    }
    if (!promiseForm.relatedInvoices.length) {
      setPromiseFormError("Select at least one invoice covered by this promise.");
      return;
    }
    if (!promiseForm.notes.trim()) {
      setPromiseFormError("Customer Agreement Notes are required before saving a promise.");
      return;
    }
    const existingPromise = selectedAccountPromises.find((promise) => promise.id === editingPromiseId);
    const duplicateActivePromise = !existingPromise && selectedAccountPromises.some((promise) => promiseMatchesFilter(promise, "active"));
    if (duplicateActivePromise && !canOverrideRecordOwnership) {
      setPromiseFormError("An active promise already exists for this account. Manager override is required before recording another active promise.");
      return;
    }

    const now = new Date().toISOString();
    const actor = actorName();
    const nextPromise = buildPromiseWorkflowRecordFromForm({
      form: promiseForm,
      account: selectedAccount,
      companyId: workspace.company.id,
      existingPromise,
      amountCents: parsedAmount,
      now,
      actorName: actor,
    });
    publishPromiseProjection({
      action: existingPromise ? "Promise.Update" : "Promise.Create",
      promiseRecord: nextPromise,
      sourceWorkspace: "Promise Workspace",
      previousValue: existingPromise,
    });
    setPromiseSuccessSummary({
      amount: formatARRecoveryCurrency(parsedAmount),
      date: formatCapturedDate(promiseForm.promiseDate),
      invoices: promiseForm.relatedInvoices.length ? promiseForm.relatedInvoices.join(", ") : "None selected",
      ticklerUpdated: promiseForm.nextActionType || promiseForm.reminderRequired ? "Yes" : "Not configured",
    });
    setPromiseForm(emptyPromiseForm);
    setPromiseFormError("");
    setEditingPromiseId("");
    setPromiseWorkflowMode("create");
    if (activeWorkspacePanel === "promise") {
      setPromiseWorkspaceTab("timeline");
      setPromiseWorkspaceMobilePane("account");
    } else {
      setIsPromiseDialogOpen(false);
    }
    setSelectedTab("promises");
    setCallSuccessMessage("");
    setNoteSuccessMessage("");
    setCommunicationSuccessMessage("");
    setTaskSuccessMessage("");
    setStatementSuccessMessage("");
    setPromiseSuccessMessage(`Promise ${existingPromise ? "updated" : "created"} for ${selectedAccount.customerName}. Promise Manager, Timeline, Account History, Recovery Activity, and Audit Trail updated.`);
  }

  function handleCompletePromise(promise: AccountPromiseRecord, completion: ARRecoveryPromiseCompletionFormState) {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Complete Promise")) return;
    const now = new Date().toISOString();
    const actor = actorName();
    const workflowPromise = buildPromiseWorkflowRecordFromExisting(promise, selectedAccount, now, actor);
    const nextPromise: ARRecoveryPromiseWorkflowRecord = {
      ...workflowPromise,
      status: "Completed",
      completionNotes: completion.completionNotes.trim(),
      outcome: completion.outcome.trim() || "Promise completed",
      updatedAt: now,
      activityFeed: [
        ...workflowPromise.activityFeed,
        {
          id: `promise-act-${workflowPromise.id}-${Date.now()}`,
          kind: "Resolved",
          title: "Promise completed",
          detail: completion.completionNotes.trim() || "Promise marked completed.",
          createdAt: now,
        },
      ],
    };
    publishPromiseProjection({
      action: "Promise.Complete",
      promiseRecord: nextPromise,
      sourceWorkspace: "Promise Center",
      previousValue: workflowPromise,
    });
    setSelectedTab("promises");
    setCallSuccessMessage("");
    setNoteSuccessMessage("");
    setCommunicationSuccessMessage("");
    setTaskSuccessMessage("");
    setStatementSuccessMessage("");
    setPromiseSuccessMessage("Promise completed. Promise Manager, Timeline, Account History, Recovery Activity, and Audit Trail updated.");
  }

  function handleMarkPromiseBroken(promise: AccountPromiseRecord, broken: ARRecoveryPromiseBrokenFormState) {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Mark Promise Broken")) return;
    const now = new Date().toISOString();
    const actor = actorName();
    const workflowPromise = buildPromiseWorkflowRecordFromExisting(promise, selectedAccount, now, actor);
    const nextPromise: ARRecoveryPromiseWorkflowRecord = {
      ...workflowPromise,
      status: "Broken",
      brokenReason: broken.reason.trim(),
      notes: broken.notes.trim() || workflowPromise.notes,
      outcome: broken.outcome.trim() || "Promise broken",
      reminderNotes: broken.nextAction.trim() || workflowPromise.reminderNotes,
      nextActionType: broken.nextAction.trim() ? "Follow-Up" : workflowPromise.nextActionType,
      updatedAt: now,
      activityFeed: [
        ...workflowPromise.activityFeed,
        {
          id: `promise-act-${workflowPromise.id}-${Date.now()}`,
          kind: "Broken",
          title: "Promise broken",
          detail: broken.reason.trim() || "Promise marked broken.",
          createdAt: now,
        },
      ],
    };
    publishPromiseProjection({
      action: "Promise.MarkBroken",
      promiseRecord: nextPromise,
      sourceWorkspace: "Promise Center",
      previousValue: workflowPromise,
    });
    setSelectedTab("promises");
    setCallSuccessMessage("");
    setNoteSuccessMessage("");
    setCommunicationSuccessMessage("");
    setTaskSuccessMessage("");
    setStatementSuccessMessage("");
    setPromiseSuccessMessage("Promise marked broken. Promise Manager, Timeline, Account History, Recovery Activity, and Audit Trail updated.");
  }

  function handleCancelPromise(promise: AccountPromiseRecord) {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Cancel Promise")) return;
    const now = new Date().toISOString();
    const actor = actorName();
    const workflowPromise = buildPromiseWorkflowRecordFromExisting(promise, selectedAccount, now, actor);
    const nextPromise: ARRecoveryPromiseWorkflowRecord = {
      ...workflowPromise,
      status: "Cancelled",
      outcome: "Promise cancelled",
      updatedAt: now,
      activityFeed: [
        ...workflowPromise.activityFeed,
        {
          id: `promise-act-${workflowPromise.id}-${Date.now()}`,
          kind: "Updated",
          title: "Promise cancelled",
          detail: "Collector cancelled the promise record.",
          createdAt: now,
        },
      ],
    };
    publishPromiseProjection({
      action: "Promise.Cancel",
      promiseRecord: nextPromise,
      sourceWorkspace: "Promise Center",
      previousValue: workflowPromise,
    });
    setSelectedTab("promises");
    setCallSuccessMessage("");
    setNoteSuccessMessage("");
    setCommunicationSuccessMessage("");
    setTaskSuccessMessage("");
    setStatementSuccessMessage("");
    setPromiseSuccessMessage("Promise cancelled. Promise Manager, Timeline, Account History, Recovery Activity, and Audit Trail updated.");
  }

  function handleAddPromiseProgressNote(promise: AccountPromiseRecord, note: string) {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Promise Progress Note")) return;
    const now = new Date().toISOString();
    const actor = actorName();
    const workflowPromise = buildPromiseWorkflowRecordFromExisting(promise, selectedAccount, now, actor);
    const nextPromise: ARRecoveryPromiseWorkflowRecord = {
      ...workflowPromise,
      updatedAt: now,
      progressNotes: [
        ...workflowPromise.progressNotes,
        {
          id: `promise-progress-${workflowPromise.id}-${Date.now()}`,
          author: actor,
          note: note.trim(),
          createdAt: now,
        },
      ],
      activityFeed: [
        ...workflowPromise.activityFeed,
        {
          id: `promise-act-${workflowPromise.id}-${Date.now()}`,
          kind: "Updated",
          title: "Progress note added",
          detail: note.trim(),
          createdAt: now,
        },
      ],
    };
    publishNoteProjection({
      noteType: "Promise Note",
      visibility: "internal",
      content: note,
      outcome: "Promise progress noted",
      nextAction: workflowPromise.nextActionType || workflowPromise.reminderNotes || "",
      sourceWorkspace: "Promise Center",
      relatedRecordType: "promise",
      relatedRecordId: workflowPromise.id,
    });
    publishPromiseProjection({
      action: "Promise.Update",
      promiseRecord: nextPromise,
      sourceWorkspace: "Promise Center",
      previousValue: workflowPromise,
    });
    setSelectedTab("promises");
    setCallSuccessMessage("");
    setNoteSuccessMessage("");
    setCommunicationSuccessMessage("");
    setTaskSuccessMessage("");
    setStatementSuccessMessage("");
    setPromiseSuccessMessage("Promise progress note saved. Promise Manager, Timeline, Account History, Recovery Activity, and Audit Trail updated.");
  }

  function openScheduleFollowUpWorkflow(relatedPromiseId = "") {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Follow-Up")) return;
    setTicklerWorkflowMode("create");
    setEditingTicklerId("");
    setTicklerForm({
      ...emptyTicklerForm,
      assignedTo: selectedAccount.assignedCollector || "",
      relatedPromise: relatedPromiseId,
      followUpType: relatedPromiseId ? "Promise Verification" : "",
    });
    setTicklerFormError("");
    setTicklerSuccessMessage("");
    setTicklerSuccessSummary(null);
    setTicklerWorkspaceTab("overview");
    setTicklerWorkspaceMobilePane("tickler");
    setIsTicklerDialogOpen(true);
    setSelectedTab("ticklers");
  }

  function openEditTicklerWorkflow(tickler: AccountTicklerRecord) {
    if (blockIfReadOnly("Edit Follow-Up")) return;
    setTicklerWorkflowMode("edit");
    setEditingTicklerId(tickler.id);
    setTicklerForm(buildTicklerFormFromRecord(tickler));
    setTicklerFormError("");
    setTicklerSuccessMessage("");
    setTicklerSuccessSummary(null);
    setTicklerWorkspaceTab("overview");
    setTicklerWorkspaceMobilePane("tickler");
    setIsTicklerDialogOpen(true);
    setSelectedTab("ticklers");
  }

  function handleSaveTickler() {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Save Follow-Up")) return;
    if (!ticklerForm.followUpType) {
      setTicklerFormError("Follow-Up Type is required.");
      return;
    }
    if (!ticklerForm.dueDate) {
      setTicklerFormError("Due Date is required.");
      return;
    }
    if (!ticklerForm.assignedTo.trim()) {
      setTicklerFormError("Assigned To is required.");
      return;
    }

    const now = new Date().toISOString();
    const actor = actorName();
    const existingTickler = accountTicklers.find((tickler) => tickler.id === editingTicklerId);
    if (getDateTime(ticklerForm.dueDate) < new Date().setHours(0, 0, 0, 0) && !canOverrideRecordOwnership) {
      setTicklerFormError("Past-due follow-ups require manager override before saving.");
      return;
    }
    if (existingTickler && ["Completed", "Cancelled"].includes(existingTickler.status) && !canOverrideRecordOwnership) {
      setTicklerFormError("Completed or cancelled follow-ups require manager override before editing.");
      return;
    }
    const duplicateOpenFollowUp = !existingTickler && accountTicklers.some((tickler) =>
      !["Completed", "Cancelled"].includes(tickler.status) &&
      mapTicklerTypeToFollowUpType(tickler.ticklerType) === ticklerForm.followUpType &&
      tickler.dueDate === ticklerForm.dueDate &&
      tickler.assignedCollector.trim().toLowerCase() === ticklerForm.assignedTo.trim().toLowerCase(),
    );
    if (duplicateOpenFollowUp && !canOverrideRecordOwnership) {
      setTicklerFormError("A matching open follow-up already exists for this collector and due date.");
      return;
    }
    const nextTickler = buildTicklerWorkflowRecordFromForm({
      form: ticklerForm,
      account: selectedAccount,
      companyId: workspace.company.id,
      existingTickler,
      now,
      actorName: actor,
    });
    const shouldReturnToManagerCommandAfterFollowUpCreate =
      activeWorkspacePanel === "tickler" &&
      ticklerWorkflowMode === "create" &&
      !existingTickler &&
      Boolean(getManagerCommandReturnContext());
    publishTicklerProjection({
      action: existingTickler ? "Tickler.Update" : "Tickler.Create",
      ticklerRecord: nextTickler,
      sourceWorkspace: "Tickler Workspace",
      previousValue: existingTickler,
    });
    setTicklerForm(emptyTicklerForm);
    setTicklerFormError("");
    setEditingTicklerId("");
    setTicklerWorkflowMode("create");
    if (activeWorkspacePanel !== "tickler") {
      setIsTicklerDialogOpen(false);
    }
    setTicklerSuccessSummary({
      nextAction: nextTickler.ticklerType,
      dueDate: nextTickler.dueDate,
      priority: ticklerWorkspacePriorityDisplay,
      assignedCollector: nextTickler.assignedCollector,
    });
    setTicklerWorkspaceTab("timeline");
    setTicklerWorkspaceMobilePane("account");
    setSelectedTab("ticklers");
    setCallSuccessMessage("");
    setNoteSuccessMessage("");
    setCommunicationSuccessMessage("");
    setTaskSuccessMessage("");
    setPromiseSuccessMessage("");
    setStatementSuccessMessage("");
    setTicklerSuccessMessage(`Follow-up ${existingTickler ? "updated" : "scheduled"} for ${selectedAccount.customerName}. Timeline, Recovery Activity, Follow-up Queue, and Account History updated.`);
    if (shouldReturnToManagerCommandAfterFollowUpCreate) {
      returnToManagerCommand();
    }
  }

  function handleCompleteTickler(tickler: AccountTicklerRecord, completion: ARRecoveryTicklerCompletionFormState) {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Complete Follow-Up")) return;
    const now = new Date().toISOString();
    const actor = actorName();
    const workflowTickler = buildTicklerWorkflowRecordFromExisting(tickler, selectedAccount, now, actor);
    const nextTickler: ARRecoveryTicklerWorkflowRecord = {
      ...workflowTickler,
      status: "Completed",
      completedBy: actor,
      completedAt: now,
      completionNotes: completion.completionNotes.trim(),
      outcome: completion.outcome.trim() || "Follow-up completed",
      updatedBy: actor,
      updatedAt: now,
    };
    publishTicklerProjection({
      action: "Tickler.Complete",
      ticklerRecord: nextTickler,
      sourceWorkspace: "Tickler Center",
      previousValue: workflowTickler,
    });
    setSelectedTab("ticklers");
    setCallSuccessMessage("");
    setNoteSuccessMessage("");
    setCommunicationSuccessMessage("");
    setTaskSuccessMessage("");
    setPromiseSuccessMessage("");
    setStatementSuccessMessage("");
    setTicklerSuccessMessage("Follow-up completed. Tickler Manager, Timeline, Account History, Recovery Activity, and Audit Trail updated.");
  }

  function handleRescheduleTickler(tickler: AccountTicklerRecord, reschedule: ARRecoveryTicklerRescheduleFormState) {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Reschedule Follow-Up")) return;
    const now = new Date().toISOString();
    const actor = actorName();
    const workflowTickler = buildTicklerWorkflowRecordFromExisting(tickler, selectedAccount, now, actor);
    const nextTickler: ARRecoveryTicklerWorkflowRecord = {
      ...workflowTickler,
      dueDate: reschedule.newDueDate,
      dueTime: reschedule.newDueTime,
      status: deriveTicklerStatusFromDate(reschedule.newDueDate),
      updatedBy: actor,
      updatedAt: now,
      rescheduleHistory: [
        ...workflowTickler.rescheduleHistory,
        {
          id: `tickler-reschedule-${workflowTickler.id}-${Date.now()}`,
          previousDueDate: workflowTickler.dueDate,
          newDueDate: reschedule.newDueDate,
          reason: reschedule.reason.trim(),
          updatedBy: actor,
          updatedAt: now,
        },
      ],
    };
    publishTicklerProjection({
      action: "Tickler.Reschedule",
      ticklerRecord: nextTickler,
      sourceWorkspace: "Tickler Center",
      previousValue: workflowTickler,
    });
    setSelectedTab("ticklers");
    setCallSuccessMessage("");
    setNoteSuccessMessage("");
    setCommunicationSuccessMessage("");
    setTaskSuccessMessage("");
    setPromiseSuccessMessage("");
    setStatementSuccessMessage("");
    setTicklerSuccessMessage("Follow-up rescheduled. Previous due date, new due date, reason, Timeline, Account History, and Audit Trail updated.");
  }

  function handleCancelTickler(tickler: AccountTicklerRecord) {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Cancel Follow-Up")) return;
    const now = new Date().toISOString();
    const actor = actorName();
    const workflowTickler = buildTicklerWorkflowRecordFromExisting(tickler, selectedAccount, now, actor);
    const nextTickler: ARRecoveryTicklerWorkflowRecord = {
      ...workflowTickler,
      status: "Cancelled",
      cancelledBy: actor,
      cancelledAt: now,
      updatedBy: actor,
      updatedAt: now,
      outcome: workflowTickler.outcome || "Follow-up cancelled",
    };
    publishTicklerProjection({
      action: "Tickler.Cancel",
      ticklerRecord: nextTickler,
      sourceWorkspace: "Tickler Center",
      previousValue: workflowTickler,
    });
    setSelectedTab("ticklers");
    setCallSuccessMessage("");
    setNoteSuccessMessage("");
    setCommunicationSuccessMessage("");
    setTaskSuccessMessage("");
    setPromiseSuccessMessage("");
    setStatementSuccessMessage("");
    setTicklerSuccessMessage("Follow-up cancelled. Tickler Manager, Timeline, Account History, Recovery Activity, and Audit Trail updated.");
  }

  function handleAddTicklerProgressNote(tickler: AccountTicklerRecord, note: string) {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Follow-Up Progress Note")) return;
    const now = new Date().toISOString();
    const actor = actorName();
    const workflowTickler = buildTicklerWorkflowRecordFromExisting(tickler, selectedAccount, now, actor);
    const nextTickler: ARRecoveryTicklerWorkflowRecord = {
      ...workflowTickler,
      updatedBy: actor,
      updatedAt: now,
      progressNotes: [
        ...workflowTickler.progressNotes,
        {
          id: `tickler-progress-${workflowTickler.id}-${Date.now()}`,
          author: actor,
          note: note.trim(),
          createdAt: now,
        },
      ],
    };
    publishNoteProjection({
      noteType: "Follow-Up Note",
      visibility: "internal",
      content: note,
      outcome: "Follow-up progress noted",
      nextAction: workflowTickler.outcome || workflowTickler.reminderNotes || "",
      sourceWorkspace: "Tickler Center",
      relatedRecordType: "tickler",
      relatedRecordId: workflowTickler.id,
    });
    publishTicklerProjection({
      action: "Tickler.Update",
      ticklerRecord: nextTickler,
      sourceWorkspace: "Tickler Center",
      previousValue: workflowTickler,
    });
    setSelectedTab("ticklers");
    setCallSuccessMessage("");
    setNoteSuccessMessage("");
    setCommunicationSuccessMessage("");
    setTaskSuccessMessage("");
    setPromiseSuccessMessage("");
    setStatementSuccessMessage("");
    setTicklerSuccessMessage("Follow-up progress note saved. Tickler Manager, Timeline, Account History, Recovery Activity, and Audit Trail updated.");
  }

  function openGenerateLetterWorkflow(options: { relatedPromiseId?: string; relatedTaskId?: string; relatedInvoices?: string[]; letterType?: ARRecoveryLetterType } = {}) {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Generate Letter")) return;
    setLetterForm({
      ...emptyLetterForm,
      letterType: options.letterType || "",
      recipient: `${selectedAccount.contactName} <${selectedAccount.contactEmail}>`,
      relatedInvoices: options.relatedInvoices || [],
      relatedPromise: options.relatedPromiseId || "",
      relatedTask: options.relatedTaskId || "",
    });
    setLetterFormError("");
    setLetterSuccessMessage("");
    setLetterSuccessSummary(null);
    setStatementSuccessMessage("");
    setIsStatementDialogOpen(false);
    setIsLetterDialogOpen(true);
    setActiveEnterpriseStudio(null);
    setActiveWorkspacePanel("letter");
    setDocumentExecutionMobilePane("controls");
    setSelectedTab("letters");
  }

  function resetLetterWorkspaceForAnotherLetter() {
    if (!selectedAccount) return;
    setLetterForm({
      ...emptyLetterForm,
      recipient: `${selectedAccount.contactName} <${selectedAccount.contactEmail}>`,
    });
    setLetterFormError("");
    setLetterSuccessMessage("");
    setLetterSuccessSummary(null);
    setDocumentExecutionMobilePane("controls");
  }

  function resetStatementWorkspaceForAnotherStatement() {
    if (!selectedAccount) return;
    setStatementForm({
      ...emptyStatementForm,
      statementDate: new Date().toISOString().slice(0, 10),
      relatedInvoices: buildInvoicePlaceholders(selectedAccount).map((invoice) => invoice.invoiceNumber),
    });
    setStatementFormError("");
    setStatementSuccessMessage("");
    setStatementCompletionSummary(null);
    setStatementFailureSummary(null);
    setDocumentExecutionMobilePane("controls");
  }

  function openSelectedAccountTimelineFromStatementWorkspace() {
    setSelectedTab("history");
    closeWorkspacePanel();
  }

  async function handleGenerateLetter() {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Generate Letter")) return;
    if (!letterForm.letterType) {
      setLetterFormError("Letter Type is required.");
      return;
    }
    if (!letterForm.recipient.trim()) {
      setLetterFormError("Recipient is required.");
      return;
    }
    if (!letterForm.relatedInvoices.length) {
      setLetterFormError("Select at least one related invoice before sending the letter.");
      return;
    }

    const now = new Date().toISOString();
    const actor = actorName();
    const recipient = parseARRecoveryRecipient(letterForm.recipient);
    if (!isValidARRecoveryEmail(recipient.email)) {
      setLetterFormError("Recipient must include a valid email address.");
      return;
    }

    const letterPreview = buildLetterPreviewFromForm({
      form: letterForm,
      account: selectedAccount,
      clientProfile: workspace.clientProfile,
      selectedPromises: selectedAccountPromises,
    });
    if (letterPreview.requiresSpreadsheetAttachment && (!letterPreview.invoiceAttachment || letterPreview.invoiceAttachment.status !== "ready" || !letterPreview.invoiceAttachment.attached)) {
      setLetterFormError(letterPreview.invoiceAttachment?.error || "Invoice spreadsheet is required but could not be generated. Review the selected invoices and retry.");
      return;
    }

    let letterRecord = buildLetterWorkflowRecordFromForm({
      form: letterForm,
      account: selectedAccount,
      companyId: workspace.company.id,
      clientProfile: workspace.clientProfile,
      selectedPromises: selectedAccountPromises,
      now,
      actorName: actor,
    });
    const delivery = await deliverARRecoveryEmail({
      recordType: "letter",
      recipientEmail: recipient.email,
      recipientName: recipient.name,
      subject: `${letterRecord.template} - ${selectedAccount.customerName}`,
      body: `${letterRecord.previewBody}\n\n${letterRecord.signature}`,
      htmlBody: renderARRecoveryLetterDocumentHtml({
        preview: letterPreview,
        clientProfile: workspace.clientProfile,
      }),
      attachments: letterPreview.invoiceAttachment
        ? [{
            name: letterPreview.invoiceAttachment.filename,
            contentType: letterPreview.invoiceAttachment.contentType,
            contentBase64: letterPreview.invoiceAttachment.contentBase64,
          }]
        : undefined,
      accountName: selectedAccount.customerName,
      customerNumber: selectedAccount.customerNumber,
      recordLabel: letterRecord.template,
    });
    const sentAt = delivery.sentAt || new Date().toISOString();

    letterRecord = {
      ...letterRecord,
      status: delivery.status === "sent" ? "Sent" : delivery.status === "failed" ? "Failed" : "Delivery Not Configured",
      sentAt: delivery.status === "sent" ? sentAt : null,
      dateSent: delivery.status === "sent" ? sentAt.slice(0, 10) : null,
      timeSent: delivery.status === "sent" ? formatCapturedTime(sentAt) : null,
      deliveryStatus: delivery.status === "sent" ? "Sent" : delivery.status === "failed" ? "Failed" : "Pending",
      updatedAt: sentAt,
      notes:
        delivery.status === "sent"
          ? `${letterRecord.additionalNotes || letterRecord.letterType} sent to ${recipient.email}. Provider: ${delivery.provider}${delivery.providerMessageId ? ` (${delivery.providerMessageId})` : ""}.${letterPreview.invoiceAttachment ? ` Invoice attachment: ${letterPreview.invoiceAttachment.filename} (${letterPreview.invoiceAttachment.invoiceCount} invoices).` : ""}`
          : delivery.status === "failed"
            ? `${letterRecord.additionalNotes || letterRecord.letterType} generated, but email delivery failed: ${delivery.providerError || delivery.message}${letterPreview.invoiceAttachment ? ` Invoice attachment prepared: ${letterPreview.invoiceAttachment.filename}.` : ""}`
            : `${letterRecord.additionalNotes || letterRecord.letterType} generated. Email delivery is not configured yet; no email was sent.${letterPreview.invoiceAttachment ? ` Invoice attachment prepared: ${letterPreview.invoiceAttachment.filename} (${letterPreview.invoiceAttachment.invoiceCount} invoices).` : ""}`,
      deliveryProvider: delivery.provider,
      providerMessageId: delivery.providerMessageId,
      providerStatus: delivery.providerStatus,
      deliveryError: delivery.providerError,
      sentRecipient: recipient.email,
    };
    publishLetterProjection({
      action: "Letter.Generate",
      letterRecord,
      sourceWorkspace: "Recovery Workbench",
      deliveryStatus: "pending",
    });
    publishLetterProjection({
      action: "Letter.Send",
      letterRecord,
      sourceWorkspace: "Recovery Workbench",
      deliveryStatus: delivery.status === "sent" ? "sent" : delivery.status === "failed" ? "failed" : "not_configured",
    });
    setLetterFormError("");
    setActiveEnterpriseStudio(null);
    if (activeWorkspacePanel !== "letter") {
      setLetterForm(emptyLetterForm);
      setIsLetterDialogOpen(false);
    } else {
      setLetterSuccessSummary({
        letterNumber: letterRecord.id,
        letterType: letterRecord.letterType,
        customer: selectedAccount.customerName,
        deliveryStatus: letterRecord.status,
      });
      setDocumentExecutionMobilePane("controls");
    }
    setSelectedTab("letters");
    setCallSuccessMessage("");
    setNoteSuccessMessage("");
    setCommunicationSuccessMessage("");
    setTaskSuccessMessage("");
    setPromiseSuccessMessage("");
    setTicklerSuccessMessage("");
    setStatementSuccessMessage("");
    setLetterSuccessMessage(
      delivery.status === "sent"
        ? `Letter sent to ${recipient.email}. Letter Manager, Timeline, Account History, Recovery Activity, and Audit Trail updated with Sent status.`
        : delivery.status === "failed"
          ? `Letter generated for ${selectedAccount.customerName}, but email delivery failed. Status set to Failed; no fake Sent status was recorded.`
          : `Letter generated for ${selectedAccount.customerName}. Email delivery is not configured yet, so the letter was not sent. Letter Manager, Timeline, Account History, Recovery Activity, and Audit Trail updated with Delivery Not Configured status.`,
    );
  }

  function handlePrintLetterExecution(outputMode: "print" | "pdf") {
    if (!selectedAccount) return;
    const preview = buildLetterPreviewFromForm({
      form: letterForm,
      account: selectedAccount,
      clientProfile: workspace.clientProfile,
      selectedPromises: selectedAccountPromises,
    });
    const opened = openARRecoveryLetterPrintWindow({
      preview,
      clientProfile: workspace.clientProfile,
      outputMode,
    });

    if (!opened) {
      setWorkspacePanelMessage("Unable to open the customer-facing letter print window. Allow pop-ups for this site and try again.");
      return;
    }

    setWorkspacePanelMessage(
      outputMode === "pdf"
        ? "PDF export uses the same customer-facing rendered letter. Choose Save as PDF in the browser print dialog."
        : "Print preview opened using only the customer-facing rendered letter.",
    );
  }

  function handlePrintStatementExecution(outputMode: "print" | "pdf") {
    if (!selectedAccount) return;
    const preview = buildStatementPreviewFromForm({
      form: statementForm,
      account: selectedAccount,
      clientProfile: workspace.clientProfile,
      selectedPromises: selectedAccountPromises,
    });
    const opened = openARRecoveryStatementPrintWindow({
      preview,
      outputMode,
    });

    if (!opened) {
      setWorkspacePanelMessage("Unable to open the customer-facing statement print window. Allow pop-ups for this site and try again.");
      return;
    }

    setWorkspacePanelMessage(
      outputMode === "pdf"
        ? "PDF export uses the same customer-facing rendered statement. Choose Save as PDF in the browser print dialog."
        : "Browser print dialog opened using the customer-facing rendered statement. Physical printing cannot be confirmed by the application.",
    );
  }

  function openGenerateStatementWorkflow(options: { relatedPromiseId?: string; relatedTaskId?: string; relatedInvoices?: string[]; statementType?: ARRecoveryStatementType } = {}) {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Generate Statement")) return;
    setStatementForm({
      ...emptyStatementForm,
      statementType: options.statementType || "",
      statementDate: new Date().toISOString().slice(0, 10),
      relatedInvoices: options.relatedInvoices || buildInvoicePlaceholders(selectedAccount).map((invoice) => invoice.invoiceNumber),
      relatedPromise: options.relatedPromiseId || "",
      relatedTask: "",
    });
    setStatementFormError("");
    setStatementSuccessMessage("");
    setStatementCompletionSummary(null);
    setStatementFailureSummary(null);
    setLetterSuccessMessage("");
    setIsLetterDialogOpen(false);
    setIsStatementDialogOpen(true);
    setActiveEnterpriseStudio(null);
    setActiveWorkspacePanel("statement");
    setDocumentExecutionMobilePane("controls");
    setSelectedTab("statements");
  }

  async function handleGenerateStatement() {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Generate Statement")) return;
    if (!statementForm.statementType) {
      setStatementFormError("Statement Type is required.");
      return;
    }
    if (!statementForm.statementDate) {
      setStatementFormError("Statement Date is required.");
      return;
    }
    if (!statementForm.relatedInvoices.length) {
      setStatementFormError("Select at least one invoice for the statement.");
      return;
    }
    setStatementFormError("");
    setStatementSuccessMessage("");
    setStatementCompletionSummary(null);
    setStatementFailureSummary(null);

    const now = new Date().toISOString();
    const actor = actorName();
    let statementRecord = buildStatementWorkflowRecordFromForm({
      form: statementForm,
      account: selectedAccount,
      companyId: workspace.company.id,
      clientProfile: workspace.clientProfile,
      existingStatementCount: snapshot.workspace.statements.length + statementWorkflows.length,
      selectedPromises: selectedAccountPromises,
      now,
      actorName: actor,
    });
    const statementPreview = buildStatementPreviewFromForm({
      form: statementForm,
      account: selectedAccount,
      clientProfile: workspace.clientProfile,
      selectedPromises: selectedAccountPromises,
    });
    const recipient = {
      name: selectedAccount.contactName,
      email: selectedAccount.contactEmail,
    };
    let delivery: ARRecoveryEmailDeliveryResult | null = null;

    if (statementForm.deliveryOption === "Email") {
      if (!isValidARRecoveryEmail(recipient.email)) {
        setStatementFormError("Selected account must include a valid contact email for statement delivery.");
        return;
      }

      try {
        delivery = await deliverARRecoveryEmail({
          recordType: "statement",
          recipientEmail: recipient.email,
          recipientName: recipient.name,
          subject: `${statementRecord.statementNumber} - ${selectedAccount.customerName}`,
          body: statementRecord.previewBody,
          htmlBody: renderARRecoveryStatementDocumentHtml({ preview: statementPreview }),
          accountName: selectedAccount.customerName,
          customerNumber: selectedAccount.customerNumber,
          recordLabel: statementRecord.statementNumber,
        });
      } catch (error) {
        setStatementFormError("Statement could not be sent.");
        setStatementFailureSummary({
          reason: error instanceof Error ? error.message : "The statement delivery service did not complete the request.",
          deliveryMethod: statementForm.deliveryOption,
        });
        setDocumentExecutionMobilePane("controls");
        return;
      }
      const sentAt = delivery.sentAt || new Date().toISOString();

      statementRecord = {
        ...statementRecord,
        status: delivery.status === "sent" ? "Sent" : delivery.status === "failed" ? "Failed" : "Delivery Not Configured",
        sentAt: delivery.status === "sent" ? sentAt : null,
        dateSent: delivery.status === "sent" ? sentAt.slice(0, 10) : statementRecord.dateSent,
        timeSent: delivery.status === "sent" ? formatCapturedTime(sentAt) : null,
        deliveryStatus: delivery.status === "sent" ? "Sent" : delivery.status === "failed" ? "Failed" : "Pending",
        updatedAt: sentAt,
        notes:
          delivery.status === "sent"
            ? `${statementRecord.statementType} sent to ${recipient.email}. Provider: ${delivery.provider}${delivery.providerMessageId ? ` (${delivery.providerMessageId})` : ""}.`
            : delivery.status === "failed"
              ? `${statementRecord.statementType} generated, but email delivery failed: ${delivery.providerError || delivery.message}`
              : `${statementRecord.statementType} generated. Email delivery is not configured yet; no email was sent.`,
        deliveryProvider: delivery.provider,
        providerMessageId: delivery.providerMessageId,
        providerStatus: delivery.providerStatus,
        deliveryError: delivery.providerError,
        sentRecipient: recipient.email,
      };
    }
    publishStatementProjection({
      action: "Statement.Generate",
      statementRecord,
      sourceWorkspace: "Recovery Workbench",
      deliveryStatus: "pending",
    });
    if (statementForm.deliveryOption === "Email") {
      publishStatementProjection({
        action: "Statement.Send",
        statementRecord,
        sourceWorkspace: "Recovery Workbench",
        deliveryStatus: delivery?.status === "sent" ? "sent" : delivery?.status === "failed" ? "failed" : "not_configured",
      });
    }
    setActiveEnterpriseStudio(null);
    setSelectedTab("statements");
    setCallSuccessMessage("");
    setNoteSuccessMessage("");
    setCommunicationSuccessMessage("");
    setTaskSuccessMessage("");
    setPromiseSuccessMessage("");
    setTicklerSuccessMessage("");
    setLetterSuccessMessage("");
    if (delivery?.status === "failed" || (statementForm.deliveryOption === "Email" && delivery?.status === "not_configured")) {
      setStatementFormError("Statement could not be sent.");
      setStatementFailureSummary({
        reason: delivery?.providerError || delivery?.message || "Email delivery is not configured for this tenant.",
        deliveryMethod: statementForm.deliveryOption,
      });
      setDocumentExecutionMobilePane("controls");
      return;
    }
    setStatementCompletionSummary({
      statementNumber: statementRecord.statementNumber,
      recipient: recipient.email,
      deliveryMethod: statementForm.deliveryOption,
      sentAt: statementRecord.sentAt || now,
      deliveryStatus: statementRecord.deliveryStatus,
    });
    setStatementSuccessMessage("Statement Sent Successfully");
    setDocumentExecutionMobilePane("controls");
  }

  function openDocumentRequestWorkflow(options: { relatedTaskId?: string; relatedInvoice?: string; requestType?: ARRecoveryDocumentRequestType } = {}) {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Request Document")) return;
    setDocumentRequestForm({
      ...emptyDocumentRequestForm,
      requestType: options.requestType || "",
      requestedFrom: selectedAccount.contactEmail || selectedAccount.contactName || "",
      relatedInvoice: options.relatedInvoice || "",
      relatedTask: options.relatedTaskId || "",
    });
    setDocumentRequestFormError("");
    setDocumentSuccessMessage("");
    setIsDocumentRequestDialogOpen(true);
    setSelectedTab("documents");
  }

  function handleCreateDocumentRequest() {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Request Document")) return;
    if (!documentRequestForm.requestType) {
      setDocumentRequestFormError("Request Type is required.");
      return;
    }
    if (!documentRequestForm.requestedFrom.trim()) {
      setDocumentRequestFormError("Requested From is required.");
      return;
    }
    if (!documentRequestForm.dueDate) {
      setDocumentRequestFormError("Due Date is required.");
      return;
    }

    const now = new Date().toISOString();
    const actor = actorName();
    const documentRecord: ARRecoveryDocumentRequestRecord = {
      id: `document-request-${selectedAccount.id}-${Date.now()}`,
      companyId: workspace.company.id,
      accountId: selectedAccount.id,
      customerName: selectedAccount.customerName,
      customerNumber: selectedAccount.customerNumber,
      requestType: documentRequestForm.requestType,
      requestedFrom: documentRequestForm.requestedFrom.trim(),
      dueDate: documentRequestForm.dueDate,
      priority: documentRequestForm.priority,
      relatedInvoice: documentRequestForm.relatedInvoice.trim(),
      relatedTask: documentRequestForm.relatedTask.trim(),
      requestNotes: documentRequestForm.requestNotes.trim(),
      status: "Requested",
      requestedBy: actor,
      requestedAt: now,
      updatedBy: actor,
      updatedAt: now,
      receivedBy: "",
      receivedAt: "",
      fileName: "",
      receivedNotes: "",
      reviewNotes: "",
      rejectionReason: "",
      additionalInfoNotes: "",
    };
    const nextDocumentRequests = [documentRecord, ...documentRequests];

    saveStoredARRecoveryDocumentRequests(nextDocumentRequests);
    setDocumentRequests(nextDocumentRequests);
    setDocumentRequestForm(emptyDocumentRequestForm);
    setDocumentRequestFormError("");
    setIsDocumentRequestDialogOpen(false);
    setSelectedTab("documents");
    setCallSuccessMessage("");
    setNoteSuccessMessage("");
    setCommunicationSuccessMessage("");
    setTaskSuccessMessage("");
    setPromiseSuccessMessage("");
    setTicklerSuccessMessage("");
    setLetterSuccessMessage("");
    setStatementSuccessMessage("");
    setDocumentSuccessMessage(`Document request created for ${selectedAccount.customerName}. Documents Manager, Timeline, Account History, Recovery Activity, and Audit Trail updated.`);
  }

  function updateDocumentRequest(documentRequestId: string, updater: (record: ARRecoveryDocumentRequestRecord, now: string) => ARRecoveryDocumentRequestRecord, successMessage: string) {
    if (!selectedAccount) return;
    if (blockIfReadOnly("Update Document Request")) return;
    const now = new Date().toISOString();
    const nextDocumentRequests = documentRequests.map((documentRequest) =>
      documentRequest.id === documentRequestId ? updater(documentRequest, now) : documentRequest,
    );

    saveStoredARRecoveryDocumentRequests(nextDocumentRequests);
    setDocumentRequests(nextDocumentRequests);
    setSelectedTab("documents");
    setCallSuccessMessage("");
    setNoteSuccessMessage("");
    setCommunicationSuccessMessage("");
    setTaskSuccessMessage("");
    setPromiseSuccessMessage("");
    setTicklerSuccessMessage("");
    setLetterSuccessMessage("");
    setStatementSuccessMessage("");
    setDocumentSuccessMessage(successMessage);
  }

  function handleReceiveDocument(documentRequestId: string, fulfillment: ARRecoveryDocumentFulfillmentFormState) {
    const actor = actorName();
    updateDocumentRequest(
      documentRequestId,
      (documentRequest, now) => ({
        ...documentRequest,
        status: "Received",
        receivedBy: actor,
        receivedAt: now,
        fileName: fulfillment.fileName.trim(),
        receivedNotes: fulfillment.receivedNotes.trim(),
        updatedBy: actor,
        updatedAt: now,
      }),
      "Document marked received. Documents Manager, Timeline, Account History, Recovery Activity, and Audit Trail updated.",
    );
  }

  function handleApproveDocument(documentRequestId: string, fulfillment: ARRecoveryDocumentFulfillmentFormState) {
    const actor = actorName();
    updateDocumentRequest(
      documentRequestId,
      (documentRequest, now) => ({
        ...documentRequest,
        status: "Approved",
        reviewNotes: fulfillment.reviewNotes.trim(),
        updatedBy: actor,
        updatedAt: now,
      }),
      "Document approved. Documents Manager, Timeline, Account History, Recovery Activity, and Audit Trail updated.",
    );
  }

  function handleRejectDocument(documentRequestId: string, fulfillment: ARRecoveryDocumentFulfillmentFormState) {
    const actor = actorName();
    updateDocumentRequest(
      documentRequestId,
      (documentRequest, now) => ({
        ...documentRequest,
        status: "Rejected",
        rejectionReason: fulfillment.rejectionReason.trim(),
        reviewNotes: fulfillment.reviewNotes.trim(),
        updatedBy: actor,
        updatedAt: now,
      }),
      "Document rejected. Documents Manager, Timeline, Account History, Recovery Activity, and Audit Trail updated.",
    );
  }

  function handleRequestAdditionalInfo(documentRequestId: string, fulfillment: ARRecoveryDocumentFulfillmentFormState) {
    const actor = actorName();
    updateDocumentRequest(
      documentRequestId,
      (documentRequest, now) => ({
        ...documentRequest,
        status: "Waiting",
        additionalInfoNotes: fulfillment.additionalInfoNotes.trim(),
        updatedBy: actor,
        updatedAt: now,
      }),
      "Additional document information requested. Documents Manager, Timeline, Account History, Recovery Activity, and Audit Trail updated.",
    );
  }

  function handleCancelDocumentRequest(documentRequestId: string) {
    const actor = actorName();
    updateDocumentRequest(
      documentRequestId,
      (documentRequest, now) => ({
        ...documentRequest,
        status: "Cancelled",
        updatedBy: actor,
        updatedAt: now,
      }),
      "Document request cancelled. Documents Manager, Timeline, Account History, Recovery Activity, and Audit Trail updated.",
    );
  }

  function renderEscalationDecisionWorkspace() {
    if (!selectedAccount) return null;
    const activePromiseCount = selectedAccountPromises.filter((promise) => promiseMatchesFilter(promise, "active")).length;
    const brokenPromiseCount = selectedAccountPromises.filter((promise) => promise.status === "Broken" || promise.status === "Escalated").length;
    const accountPayments = paymentWorkflows.filter((payment) => payment.accountId === selectedAccount.id);
    const accountEscalations = escalationWorkflows
      .filter((escalation) => escalation.accountId === selectedAccount.id)
      .sort((a, b) => getDateTime(String(b.updatedAt || b.createdAt || "")) - getDateTime(String(a.updatedAt || a.createdAt || "")));
    const currentEscalation = accountEscalations[0];
    const recentChanges = timelineEntries
      .filter((entry) => ["Promise", "Payment", "Statement", "Letter", "Call", "Note", "Escalation", "Broken", "Contact"].some((signal) => `${entry.actionType} ${entry.preview}`.includes(signal)))
      .slice(0, 6);
    const managerNotesRequired = escalationDecisionForm.decision === "Return to Collector" || escalationDecisionForm.decision === "Reject" || escalationDecisionForm.decision === "Escalate Further";
    const escalationOpenedAt = String(currentEscalation?.createdAt || currentEscalation?.updatedAt || new Date().toISOString());
    const daysOpen = Math.max(0, Math.ceil((Date.now() - getDateTime(escalationOpenedAt)) / (24 * 60 * 60 * 1000)));
    const recommendedAction = brokenPromiseCount
      ? "Review broken promise and return with direction or escalate further."
      : selectedAccount.openBalanceCents >= 2500000
        ? "Review high balance exposure and confirm manager ownership."
        : selectedAccount.escalationStatus !== "None"
          ? "Confirm escalation status and assign the next operational decision."
          : "Review account evidence and determine whether escalation should remain open.";
    const evidenceItems: Array<{ label: string; value: string; tab: ARRecoveryAccountTabKey }> = [
      { label: "Timeline", value: `${timelineEntries.length} Events`, tab: "activity" },
      { label: "Communications", value: `${accountCommunications.length} Messages`, tab: "communications" },
      { label: "Promises", value: `${activePromiseCount} Active`, tab: "promises" },
      { label: "Ticklers", value: `${accountTicklers.length} Scheduled`, tab: "ticklers" },
      { label: "Letters", value: `${accountLetters.length} Generated`, tab: "letters" },
      { label: "Statements", value: `${accountStatements.length} Generated`, tab: "statements" },
      { label: "Payments", value: `${accountPayments.length} Recorded`, tab: "history" },
      { label: "Documents", value: `${accountDocumentRequests.length} Available`, tab: "documents" },
    ];
    const selectedDecisionOutcome = escalationDecisionForm.decision || "Pending Decision";
    const selectedReassignCollector = activeInternalUserOptions.find((user) => user.id === escalationDecisionForm.assignedCollectorId) || null;
    const hasValidReassignCollector = escalationDecisionForm.decision !== "Reassign Collector"
      || Boolean(selectedReassignCollector && !isSameCollectorName(selectedReassignCollector.name, selectedAccount.assignedCollector));
    const alternativeCollectorCount = activeInternalUserOptions.filter((user) => !isSameCollectorName(user.name, selectedAccount.assignedCollector)).length;
    const decisionOutcomeItems = [
      "Update Timeline",
      "Update Recovery Activity",
      "Notify Assigned Collector",
      selectedDecisionOutcome === "Close Escalation" ? "Close Escalation" : selectedDecisionOutcome === "Reassign Collector" ? "Update Collector Assignment" : "Update Escalation History",
    ];
    const decisionReadyItems = [
      { label: "Escalation Reviewed", ready: Boolean(escalationDecisionForm.reason), detail: escalationDecisionForm.reason || "Select why this account requires review." },
      { label: "Evidence Reviewed", ready: escalationDecisionForm.evidenceReviewed, detail: escalationDecisionForm.evidenceReviewed ? "Manager confirmed supporting evidence." : "Confirm account evidence before deciding." },
      { label: "Decision Selected", ready: Boolean(escalationDecisionForm.decision), detail: escalationDecisionForm.decision || "Choose the manager decision." },
      ...(escalationDecisionForm.decision === "Reassign Collector" ? [{ label: "Replacement Collector", ready: hasValidReassignCollector, detail: selectedReassignCollector && !isSameCollectorName(selectedReassignCollector.name, selectedAccount.assignedCollector) ? selectedReassignCollector.name : "Select a different eligible active internal user." }] : []),
      { label: "Notes Complete", ready: !managerNotesRequired || Boolean(escalationDecisionForm.decisionNotes.trim()), detail: managerNotesRequired ? (escalationDecisionForm.decisionNotes.trim() ? "Manager notes captured." : "Manager notes are required for this decision.") : "Manager notes optional for this decision." },
      { label: "Ready to Complete", ready: Boolean(escalationDecisionForm.reason && escalationDecisionForm.evidenceReviewed && escalationDecisionForm.decision && hasValidReassignCollector && (!managerNotesRequired || escalationDecisionForm.decisionNotes.trim())), detail: "Publishes to Timeline, Recovery Activity, Manager Command, and Escalation History." },
    ];
    const canCompleteEscalation = decisionReadyItems.every((item) => item.ready);
    const nextEscalatedAccount = queueAccounts.find((account) => account.id !== selectedAccount.id && (account.recoveryStatus === "Escalated" || account.escalationStatus !== "None"));

    return (
      <div className={AR_RECOVERY_ENTERPRISE_PANEL_GRID_CLASS}>
        <section className="min-w-0 rounded-[1.5rem] border border-[#BFEAFF] bg-[#F8FCFF] p-4" aria-label="Escalation decision workspace">
          <div className="flex min-w-0 items-start gap-3">
            <ARLightLineIcon compact icon={<ShieldCheck className="size-5" aria-hidden="true" />} />
            <div className="min-w-0">
              <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Manager Decision</p>
              <h4 className="mt-1 break-words text-xl font-semibold text-[#0A2540]">Review escalation</h4>
              <p className="mt-1 break-words text-sm font-semibold leading-6 text-[#475569]">
                One objective: determine the appropriate manager decision for this escalated account and publish the outcome through the Enterprise Workflow Event Bus.
              </p>
            </div>
          </div>

          {escalationCompletionSummary ? (
            <section className="mt-4 rounded-2xl border border-[rgba(34,197,94,.3)] bg-[rgba(34,197,94,.08)] p-4" aria-live="polite">
              <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#166534]">Escalation Completed Successfully</p>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <DetailMetric label="Decision" value={escalationCompletionSummary.decision} />
                <DetailMetric label="Manager" value={escalationCompletionSummary.manager} />
                <DetailMetric label="Date / Time" value={`${formatCapturedDate(escalationCompletionSummary.completedAt)} | ${formatCapturedTime(escalationCompletionSummary.completedAt)}`} />
                <DetailMetric label="Escalation ID" value={escalationCompletionSummary.escalationId} />
                <DetailMetric label="Timeline" value="Updated" />
                <DetailMetric label="Recovery Activity" value="Updated" />
                <DetailMetric label="Escalation History" value="Updated" />
                <DetailMetric label="Manager Command" value="Updated" />
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button type="button" onClick={closeWorkspacePanel} className="focus-ring min-h-10 rounded-full border border-[#14B8A6] bg-[#14B8A6] px-4 text-sm font-black uppercase tracking-[0.12em] text-white">
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!nextEscalatedAccount) return;
                    const currentCollector = findActiveInternalUserByName(activeInternalUserOptions, nextEscalatedAccount.assignedCollector);
                    setSelectedAccountId(nextEscalatedAccount.id);
                    setEscalationCompletionSummary(null);
                    setEscalationDecisionForm({
                      ...emptyEscalationDecisionForm,
                      reason: getDefaultEscalationReason(nextEscalatedAccount),
                      assignedCollectorId: currentCollector?.id || "",
                      assignedCollector: nextEscalatedAccount.assignedCollector,
                    });
                  }}
                  disabled={!nextEscalatedAccount}
                  className="focus-ring min-h-10 rounded-full border border-[#BFEAFF] bg-white px-4 text-sm font-black uppercase tracking-[0.12em] text-[#0A2540] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Review Next Escalation
                </button>
              </div>
            </section>
          ) : null}

          <div className="mt-4 grid gap-4">
            <div className="rounded-2xl border border-[#BFEAFF] bg-white px-4 py-3">
              <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Review</p>
              <p className="mt-1 break-words text-sm font-semibold leading-6 text-[#475569]">Confirm why this account is escalated, what changed, and what supporting evidence is available.</p>
            </div>

            <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4" aria-label="Escalation summary">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Escalation Summary</p>
                  <h5 className="mt-1 break-words text-base font-semibold text-[#0A2540]">{escalationDecisionForm.reason || currentEscalation?.reason || "Manager review required"}</h5>
                </div>
                <span className="rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-[#0A2540]">
                  {String(currentEscalation?.priority || (selectedAccount.recoveryStatus === "Escalated" ? "Critical" : "High"))}
                </span>
              </div>
              <div className="mt-3 grid min-w-0 gap-3 md:grid-cols-2 xl:grid-cols-3">
                <DetailMetric label="Escalation Reason" value={escalationDecisionForm.reason || String(currentEscalation?.reason || "Not Selected")} />
                <DetailMetric label="Priority" value={String(currentEscalation?.priority || (selectedAccount.recoveryStatus === "Escalated" ? "Critical" : "High"))} />
                <DetailMetric label="Requested By" value={String(currentEscalation?.createdBy || currentEscalation?.actorName || selectedAccount.assignedCollector || "Collector")} />
                <DetailMetric label="Escalation Date" value={formatCapturedDate(escalationOpenedAt)} />
                <DetailMetric label="Days Open" value={`${daysOpen} day${daysOpen === 1 ? "" : "s"}`} />
                <DetailMetric label="Recommended Action" value={recommendedAction} />
              </div>
            </section>

            <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
              <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Escalation Reason</p>
                  <p className="mt-1 break-words text-sm font-semibold leading-6 text-[#475569]">Review the classification captured when this escalation was requested.</p>
                </div>
                <details className="min-w-0">
                  <summary className="focus-ring cursor-pointer rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2 text-xs font-black uppercase tracking-[0.12em] text-[#0A2540]">
                    Edit Classification
                  </summary>
                  <label className="mt-3 grid min-w-0 gap-1.5 rounded-2xl border border-[#BFEAFF] bg-white p-3">
                    <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#475569]">Classification</span>
                    <select
                      value={escalationDecisionForm.reason}
                      onChange={(event) => setEscalationDecisionForm((current) => ({ ...current, reason: event.target.value as ARRecoveryEscalationReason | "" }))}
                      className="focus-ring min-h-11 w-full max-w-full rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
                    >
                      <option value="">Select reason</option>
                      {AR_RECOVERY_ESCALATION_REASONS.map((reason) => <option key={reason} value={reason}>{reason}</option>)}
                    </select>
                  </label>
                </details>
              </div>
              <div className="mt-3 grid min-w-0 gap-3 md:grid-cols-2 xl:grid-cols-4">
                <DetailMetric label="Escalation Reason" value={escalationDecisionForm.reason || String(currentEscalation?.reason || "Not Selected")} />
                <DetailMetric label="Classification" value={String(currentEscalation?.priority || (selectedAccount.recoveryStatus === "Escalated" ? "Critical" : "High"))} />
                <DetailMetric label="Requested By" value={String(currentEscalation?.createdBy || currentEscalation?.actorName || selectedAccount.assignedCollector || "Collector")} />
                <DetailMetric label="Date" value={formatCapturedDate(escalationOpenedAt)} />
              </div>
            </section>

            <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
              <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Recent Changes</p>
              <div className="mt-3 grid gap-2">
                {recentChanges.length ? recentChanges.map((entry) => (
                  <div key={entry.id} className="min-w-0 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] p-3">
                    <p className="break-words text-sm font-semibold text-[#0A2540]">{entry.actionType}</p>
                    <p className="mt-1 break-words text-xs font-semibold leading-5 text-[#475569]">{entry.preview}</p>
                  </div>
                )) : (
                  <ManagerEmptyState title="No recent change signals" description="No meaningful recent activity is available for this account." action="Use Context Workspace tabs to review the full account record." />
                )}
              </div>
            </section>

            <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
              <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Evidence Available</p>
              <p className="mt-1 break-words text-sm font-semibold leading-6 text-[#475569]">Launch the supporting record when deeper review is needed. Evidence is not embedded in this decision workspace.</p>
              <div className="mt-3 grid min-w-0 gap-2 sm:grid-cols-2">
                {evidenceItems.map((item) => (
                  <button
                    key={item.label}
                    type="button"
                    onClick={() => setSelectedTab(item.tab)}
                    className="focus-ring flex min-h-12 min-w-0 items-center justify-between gap-3 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 text-left text-sm font-semibold text-[#0A2540]"
                  >
                    <span className="min-w-0 break-words">{item.label}</span>
                    <span className="shrink-0 rounded-full border border-[#BFEAFF] bg-white px-2 py-0.5 text-xs font-black text-[#0A2540]">{item.value}</span>
                  </button>
                ))}
              </div>
            </section>

            <div className="rounded-2xl border border-[#BFEAFF] bg-white px-4 py-3">
              <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Decision</p>
              <p className="mt-1 break-words text-sm font-semibold leading-6 text-[#475569]">Select the manager outcome, capture required notes when needed, and complete the escalation decision.</p>
            </div>

            <section className="rounded-2xl border border-[#14B8A6] bg-white p-4 shadow-[0_18px_40px_rgba(20,184,166,.08)]">
              <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Manager Decision</p>
                  <h5 className="mt-1 break-words text-base font-semibold text-[#0A2540]">Select the operational decision</h5>
                  <p className="mt-1 break-words text-sm font-semibold leading-6 text-[#475569]">This is the action that completes the manager review and updates escalation history.</p>
                </div>
              </div>
              <div className="mt-3 grid gap-3">
                <label className="grid min-w-0 gap-1.5">
                  <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#475569]">Decision</span>
                  <select
                    value={escalationDecisionForm.decision}
                    onChange={(event) => {
                      const nextDecision = event.target.value as ARRecoveryEscalationDecision | "";
                      const currentCollector = findActiveInternalUserByName(activeInternalUserOptions, selectedAccount.assignedCollector);
                      setEscalationDecisionForm((current) => ({
                        ...current,
                        decision: nextDecision,
                        assignedCollectorId: nextDecision === "Reassign Collector" ? current.assignedCollectorId || currentCollector?.id || "" : current.assignedCollectorId,
                        assignedCollector: nextDecision === "Reassign Collector" ? current.assignedCollector || selectedAccount.assignedCollector : current.assignedCollector,
                      }));
                    }}
                    className="focus-ring min-h-11 w-full max-w-full rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
                  >
                    <option value="">Select decision</option>
                    {AR_RECOVERY_ESCALATION_DECISIONS.map((decision) => <option key={decision} value={decision}>{decision}</option>)}
                  </select>
                </label>
                {escalationDecisionForm.decision === "Reassign Collector" ? (
                  <div className="grid min-w-0 gap-2 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-3">
                    <CollectorAssignmentCombobox
                      options={activeInternalUserOptions}
                      currentCollectorName={selectedAccount.assignedCollector}
                      selectedUserId={escalationDecisionForm.assignedCollectorId}
                      onSelect={(user) => {
                        setEscalationDecisionError("");
                        setEscalationDecisionForm((current) => ({
                          ...current,
                          assignedCollectorId: user.id,
                          assignedCollector: user.name,
                        }));
                      }}
                    />
                    {alternativeCollectorCount ? null : (
                      <p className="rounded-xl border border-dashed border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold text-[#475569]" role="status">
                        No alternative eligible internal user is available for reassignment.
                      </p>
                    )}
                  </div>
                ) : null}
                {escalationDecisionForm.decision === "Request Documentation" ? (
                  <label className="grid min-w-0 gap-1.5">
                    <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#475569]">Documentation Request</span>
                    <input
                      value={escalationDecisionForm.documentationRequest}
                      onChange={(event) => setEscalationDecisionForm((current) => ({ ...current, documentationRequest: event.target.value }))}
                      placeholder="Example: Proof of delivery, remittance detail, dispute support."
                      className="focus-ring min-h-11 w-full max-w-full rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
                    />
                  </label>
                ) : null}
                <label className="grid min-w-0 gap-1.5">
                  <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#475569]">Manager Notes {managerNotesRequired ? "Required" : "Optional"}</span>
                  <textarea
                    value={escalationDecisionForm.decisionNotes}
                    onChange={(event) => setEscalationDecisionForm((current) => ({ ...current, decisionNotes: event.target.value }))}
                    rows={5}
                    placeholder={managerNotesRequired ? "Required: document the manager rationale before completing this decision." : "Optional: add manager rationale, evidence reviewed, or next-step context."}
                    className="focus-ring w-full max-w-full rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold leading-6 text-[#0A2540]"
                  />
                </label>
              </div>
            </section>

            <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Decision Ready</p>
                  <p className="mt-1 text-sm font-semibold leading-6 text-[#475569]">Complete every readiness check before publishing the manager decision.</p>
                </div>
                <label className="focus-within:ring-2 focus-within:ring-[#14B8A6] flex min-h-10 items-center gap-2 rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-3 text-xs font-black uppercase tracking-[0.12em] text-[#0A2540]">
                  <input
                    type="checkbox"
                    checked={escalationDecisionForm.evidenceReviewed}
                    onChange={(event) => setEscalationDecisionForm((current) => ({ ...current, evidenceReviewed: event.target.checked }))}
                    className="size-4"
                  />
                  Evidence Reviewed
                </label>
              </div>
              <div className="mt-3 grid gap-2">
                {decisionReadyItems.map((item) => (
                  <div key={item.label} className="flex min-w-0 items-start gap-2 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] p-3">
                    <span className={`mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border text-xs font-black ${item.ready ? "border-[rgba(34,197,94,.45)] bg-[rgba(34,197,94,.12)] text-[#166534]" : "border-[#BFEAFF] bg-white text-[#64748B]"}`}>
                      {item.ready ? "✓" : "○"}
                    </span>
                    <div className="min-w-0">
                      <p className="break-words text-sm font-semibold text-[#0A2540]">{item.label}</p>
                      <p className="mt-1 break-words text-xs font-semibold leading-5 text-[#475569]">{item.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
              {escalationDecisionError ? (
                <div className="mt-3 rounded-xl border border-[rgba(248,113,113,.28)] bg-[rgba(248,113,113,.08)] px-3 py-2 text-sm font-semibold text-[#B91C1C]" aria-live="assertive">
                  {escalationDecisionError}
                </div>
              ) : null}
              <div className="mt-4 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-3">
                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Decision Outcome</p>
                <p className="mt-1 break-words text-sm font-semibold leading-6 text-[#475569]">This decision will:</p>
                <div className="mt-3 grid gap-2">
                  {decisionOutcomeItems.map((item) => (
                    <div key={item} className="flex min-w-0 items-start gap-2 text-sm font-semibold leading-6 text-[#0A2540]">
                      <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border border-[rgba(34,197,94,.45)] bg-[rgba(34,197,94,.12)] text-xs font-black text-[#166534]">✓</span>
                      <span className="min-w-0 break-words">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleCompleteEscalationDecision}
                  disabled={!canCompleteEscalation || Boolean(escalationCompletionSummary)}
                  className="focus-ring min-h-11 rounded-full border border-[#14B8A6] bg-[#14B8A6] px-5 text-sm font-black uppercase tracking-[0.12em] text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {escalationCompletionSummary ? "Decision Completed" : "Complete Decision"}
                </button>
                <button type="button" onClick={closeWorkspacePanel} className="focus-ring min-h-11 rounded-full border border-[#BFEAFF] bg-white px-5 text-sm font-black uppercase tracking-[0.12em] text-[#0A2540]">
                  Cancel
                </button>
              </div>
            </section>
          </div>
        </section>

        <aside className="min-w-0 rounded-[1.5rem] border border-[#BFEAFF] bg-white p-4" aria-label="Escalation evidence context">
          <div className="flex min-w-0 items-start gap-3">
            <ARLightLineIcon compact icon={<FolderKanban className="size-5" aria-hidden="true" />} />
            <div className="min-w-0">
              <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Context Workspace</p>
              <h4 className="mt-1 break-words text-lg font-semibold text-[#0A2540]">Decision evidence</h4>
              <p className="mt-1 break-words text-sm font-semibold leading-6 text-[#475569]">Compact manager evidence. Use launch actions when deeper workflow work is required.</p>
            </div>
          </div>

          <div className="mt-4 grid gap-3">
            <DetailMetric label="Open Balance" value={formatARRecoveryCurrency(selectedAccount.openBalanceCents)} />
            <DetailMetric label="Aging" value={selectedAccount.agingSummary} />
            <DetailMetric label="Active Promises" value={String(activePromiseCount)} />
            <DetailMetric label="Broken Promises" value={String(brokenPromiseCount)} />
            <DetailMetric label="Open Ticklers" value={String(accountTicklers.filter((tickler) => tickler.status !== "Completed").length)} />
            <DetailMetric label="Current Escalation" value={String(currentEscalation?.status || selectedAccount.escalationStatus || "None")} />
          </div>

          <div className="mt-4 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-3">
            <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Evidence Available</p>
            <div className="mt-3 grid gap-2">
              {evidenceItems.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => setSelectedTab(item.tab)}
                  className="focus-ring flex min-h-10 min-w-0 items-center justify-between gap-3 rounded-xl border border-[#BFEAFF] bg-white px-3 text-left text-sm font-semibold text-[#0A2540]"
                >
                  <span className="min-w-0 break-words">{item.label}</span>
                  <span className="shrink-0 rounded-full border border-[#BFEAFF] bg-[#F8FCFF] px-2 py-0.5 text-xs font-black text-[#0A2540]">{item.value}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 rounded-2xl border border-[#BFEAFF] bg-white p-3">
            <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">State Preservation</p>
            <div className="mt-3 grid gap-2 text-sm font-semibold leading-6 text-[#475569]">
              <p>Selected Customer: {selectedAccount.customerName}</p>
              <p>Selected Account: {editableSelectedAccount?.accountNumber || selectedAccount.customerNumber}</p>
              <p>Context Tab: {selectedTabInfo?.label || selectedTab}</p>
              <p>Queue Position: {selectedAccountQueuePosition} of {queueAccounts.length || snapshot.filteredAccounts.length || 1}</p>
            </div>
          </div>
        </aside>
      </div>
    );
  }

  function renderAccountQuickActions() {
    const primaryActions = [
      {
        label: "Call",
        title: "Capture the call outcome and next touchpoint.",
        onClick: () => launchQuickAction("Call", "call", () => {
          setCallFormError("");
          setCallSuccessMessage("");
          setIsCallDialogOpen(true);
        }),
      },
      {
        label: "Email",
        title: "Open the communications workspace for this account.",
        onClick: () => launchQuickAction("Email", "email", () => {
          if (selectedAccount) {
            setEmailWorkspaceForm({
              ...emptyEmailWorkspaceForm,
              recipient: `${selectedAccount.contactName} <${selectedAccount.contactEmail}>`,
            });
          }
          setEmailWorkspaceError("");
          setEmailWorkspaceValidation(buildEmptyARRecoveryEmailValidationState());
          setEmailWorkspaceDeliverySummary(null);
          setEmailWorkspaceDocumentPreview(null);
          setEmailWorkspaceTab("overview");
          setEmailWorkspaceMobilePane("email");
          setIsEmailWorkspaceOpen(true);
          setCommunicationSuccessMessage("");
          setSelectedTab("communications");
        }),
      },
      { label: "Promise", title: "Document amount, date, and follow-up target.", onClick: () => launchQuickAction("Promise", "promise", openCreatePromiseWorkflow) },
      { label: "Statement", title: "Open the statement execution workspace for this account.", onClick: () => openGenerateStatementWorkflow() },
      { label: "Letter", title: "Open the letter execution workspace for this account.", onClick: () => openGenerateLetterWorkflow() },
      {
        label: "Payment",
        title: "Record and apply a customer payment for this account.",
        onClick: () => launchQuickAction("Payment", "payment", () => {
          const invoices = selectedAccount ? buildInvoicePlaceholders(selectedAccount) : [];
          setPaymentWorkspaceForm({
            ...emptyPaymentWorkspaceForm,
            paymentDate: new Date().toISOString().slice(0, 10),
            appliedInvoiceNumbers: invoices.length === 1 ? [invoices[0].invoiceNumber] : [],
          });
          setPaymentWorkspaceError("");
          setPaymentWorkspaceSuccess(null);
          setPaymentWorkspaceTab("overview");
          setPaymentWorkspaceMobilePane("payment");
          setIsPaymentWorkspaceOpen(true);
        }),
      },
    ];
    const secondaryActions = [
      { label: "Tickler", title: "Create a follow-up reminder for the account.", onClick: () => launchQuickAction("Tickler", "tickler", () => openScheduleFollowUpWorkflow()) },
      {
        label: "Note",
        title: "Record collector notes directly on the account.",
        onClick: () => launchQuickAction("Note", "note", () => {
          setNoteForm(emptyNoteForm);
          setNoteFormError("");
          setNoteSuccessMessage("");
          setNoteCompletionSummary(null);
          setIsNoteDialogOpen(true);
        }),
      },
      {
        label: "Escalate",
        title: "Route the account for manager review.",
        onClick: () => launchQuickAction("Escalate", "escalate", () => {
          openEscalationDecisionWorkspace();
        }),
      },
    ];
    const utilityActions = [
      {
        label: "History",
        title: "Open the selected account history.",
        onClick: () => {
          openWorkspacePanel("history");
          setSelectedTab("history");
        },
      },
      {
        label: "Audit",
        title: "Review immutable account activity and audit signals.",
        onClick: () => {
          openWorkspacePanel("audit");
          setSelectedTab("activity");
        },
      },
    ];

    return (
      <div className="ar-action-toolbar sticky top-4 z-30 min-w-0 overflow-hidden rounded-2xl border p-4" aria-label="Account Recovery Enterprise Action Bar">
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <ARLightLineIcon compact icon={<Bolt className="size-5" aria-hidden="true" />} />
            <div className="min-w-0">
              <p className="ar-action-toolbar-title">Enterprise Action Bar</p>
              <p className="ar-action-toolbar-description mt-1">Start account-level work while preserving the selected customer, account, and timeline context.</p>
            </div>
          </div>
        </div>
        <div className="grid min-w-0 gap-3">
          <div className="flex min-w-0 flex-wrap items-center gap-2" aria-label="Primary account actions">
            <span className="text-[10px] font-black uppercase tracking-[0.14em] text-[#64748B]">Primary</span>
            {primaryActions.map((action) => (
              <CompactActionTool key={action.label} label={action.label} title={action.title} disabled={!canWriteOperationalRecords} onClick={action.onClick} />
            ))}
          </div>
          <div className="flex min-w-0 flex-wrap items-center gap-2" aria-label="Secondary account actions">
            <span className="text-[10px] font-black uppercase tracking-[0.14em] text-[#64748B]">Secondary</span>
            {secondaryActions.map((action) => (
              <CompactActionTool key={action.label} label={action.label} title={action.title} disabled={!canWriteOperationalRecords} onClick={action.onClick} />
            ))}
          </div>
          <div className="flex min-w-0 flex-wrap items-center gap-2" aria-label="Utility account actions">
            <span className="text-[10px] font-black uppercase tracking-[0.14em] text-[#64748B]">Utility</span>
            {utilityActions.map((action) => (
              <CompactActionTool key={action.label} label={action.label} title={action.title} onClick={action.onClick} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  function getWorkspacePanelTitle() {
    const label = {
      call: "Log Call",
      email: "Account Communications",
      promise: "Promise Workflow",
      statement: "Statement Workflow",
      letter: "Letter Workflow",
      payment: "Payment Workspace",
      tickler: "Tickler Workflow",
      note: "Notes Workspace",
      escalate: "Escalation Review",
      "account-info": "Account Information",
      history: "Account History",
      audit: "Account Audit",
    }[activeWorkspacePanel || "history"];
    return selectedAccount ? `${label}: ${selectedAccount.customerName}` : label;
  }

  function getWorkspacePanelEyebrow() {
    return activeWorkspacePanel === "history" || activeWorkspacePanel === "audit"
      ? "Account Recovery Review"
      : "Account Recovery Action";
  }

  const isContextWorkspaceReviewMode = activeWorkspacePanel === "history" || activeWorkspacePanel === "audit";

  function renderCertifiedAccountContextWorkspace() {
    if (!selectedAccount) return null;

    return (
      <>
        <div className={`ar-workspace-tabs-panel sticky top-4 z-20 min-w-0 overflow-hidden rounded-[1.25rem] border p-3 backdrop-blur ${isContextWorkspaceReviewMode ? "xl:col-span-2" : ""}`}>
          <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="ar-workspace-tabs-title flex items-center gap-2">
                <FolderKanban className="size-4 text-[#14B8A6]" aria-hidden="true" />
                Context Workspace
              </p>
              <p className="ar-workspace-tabs-description mt-1">Switch between account information, timeline, notes, documents, promises, and tasks.</p>
            </div>
            <span className="rounded-full border border-[rgba(34,224,208,.18)] px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#22E0D0]">
              {selectedTabInfo?.label || "Timeline"}
            </span>
          </div>
          <div className="flex min-w-0 max-w-full flex-wrap gap-2 overflow-hidden pb-1" role="tablist" aria-label="Selected account workspace tabs">
            {snapshot.tabLabels.map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={selectedTab === tab.key}
                onClick={() => setSelectedTab(tab.key)}
                className={`focus-ring inline-flex min-h-9 max-w-full items-center rounded-full border px-3 text-[11px] font-black uppercase tracking-[0.12em] transition ${
                  selectedTab === tab.key
                    ? "border-[rgba(255,106,43,.5)] bg-[rgba(255,106,43,.16)] text-[#F6F1E8] shadow-[0_12px_28px_rgba(255,106,43,.1)]"
                    : "border-[rgba(34,224,208,.18)] bg-[rgba(255,255,255,.04)] text-[#CBD5E1] hover:border-[rgba(34,224,208,.34)] hover:bg-[rgba(34,224,208,.08)] hover:text-[#F6F1E8]"
                }`}
                title={tab.description}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className={`min-w-0 overflow-hidden rounded-[1.5rem] border border-[rgba(34,224,208,.14)] bg-[rgba(255,255,255,.03)] p-4 ${isContextWorkspaceReviewMode ? "xl:col-span-2" : ""}`}>
          <SectionHeading
            eyebrow={selectedTabInfo?.label || "Workspace Section"}
            title={selectedTabInfo?.label || "Timeline"}
            description={selectedTabInfo?.description || "The selected tab is ready for the next step."}
          />
          {selectedTab === "activity" ? (
            <div className="mt-3">
              <AccountTimelinePanel account={selectedAccount} timelineEntries={timelineEntries} />
            </div>
          ) : selectedTab === "invoices" ? (
            <div className="mt-3">
              <AccountOpenInvoicesPanel account={selectedAccount} promises={selectedAccountPromises} />
            </div>
          ) : selectedTab === "ticklers" ? (
            <div className="mt-3">
              <AccountTicklerPanel
                account={selectedAccount}
                ticklers={accountTicklers}
                activityEvents={snapshot.workspace.recoveryActivityEvents.filter((item) => item.accountId === selectedAccount.id)}
                onCreateTickler={() => openScheduleFollowUpWorkflow()}
                onEditTickler={openEditTicklerWorkflow}
                onCompleteTickler={handleCompleteTickler}
                onRescheduleTickler={handleRescheduleTickler}
                onCancelTickler={handleCancelTickler}
                onAddProgressNote={handleAddTicklerProgressNote}
                onGenerateLetter={(tickler) => openGenerateLetterWorkflow({
                  relatedPromiseId: getTicklerRelatedPromise(tickler),
                  relatedTaskId: getTicklerRelatedTask(tickler),
                  relatedInvoices: getTicklerRelatedInvoice(tickler) ? [getTicklerRelatedInvoice(tickler)] : [],
                  letterType: tickler.ticklerType === "Promise Verification" ? "Promise Confirmation" : "Collection Follow-Up",
                })}
                onGenerateStatement={(tickler) => openGenerateStatementWorkflow({
                  relatedPromiseId: getTicklerRelatedPromise(tickler),
                  relatedTaskId: getTicklerRelatedTask(tickler),
                  relatedInvoices: getTicklerRelatedInvoice(tickler) ? [getTicklerRelatedInvoice(tickler)] : [],
                  statementType: tickler.ticklerType === "Promise Verification" ? "Promise Balance Statement" : "Past Due Statement",
                })}
              />
            </div>
          ) : selectedTab === "promises" ? (
            <div className="mt-3">
              <AccountPromisePanel
                account={selectedAccount}
                promises={selectedAccountPromises}
                historyEntries={promiseHistoryEntries}
                onCreatePromise={openCreatePromiseWorkflow}
                onEditPromise={openEditPromiseWorkflow}
                onCompletePromise={handleCompletePromise}
                onMarkPromiseBroken={handleMarkPromiseBroken}
                onCancelPromise={handleCancelPromise}
                onAddProgressNote={handleAddPromiseProgressNote}
                onGenerateLetter={(promise) => openGenerateLetterWorkflow({
                  relatedPromiseId: promise.id,
                  relatedInvoices: promise.invoiceLabels,
                  letterType: promise.status === "Broken" ? "Broken Promise" : "Promise Confirmation",
                })}
                onGenerateStatement={(promise) => openGenerateStatementWorkflow({
                  relatedPromiseId: promise.id,
                  relatedInvoices: promise.invoiceLabels,
                  statementType: "Promise Balance Statement",
                })}
              />
            </div>
          ) : selectedTab === "notes" ? (
            <div className="mt-3">
              <AccountNotesPanel account={selectedAccount} activityEvents={accountNoteEvents} promises={selectedAccountPromises} />
            </div>
          ) : selectedTab === "communications" ? (
            <div className="mt-3">
              <AccountCommunicationsPanel
                account={selectedAccount}
                communications={accountCommunications}
                onSendCommunication={handleSendCommunication}
                internalUserNames={activeInternalUserNames}
              />
            </div>
          ) : selectedTab === "tasks" ? (
            <div className="mt-3">
              <AccountTasksPanel
                account={selectedAccount}
                tasks={accountTasks}
                promises={selectedAccountPromises}
                letters={accountLetters}
                statements={accountStatements}
                internalUserNames={activeInternalUserNames}
                onCreateTask={handleCreateTask}
                onCompleteTask={handleCompleteTask}
                onAddTaskComment={handleAddTaskComment}
                createTaskLaunchKey={taskCreateLaunchKey}
                onRequestDocument={(task) => openDocumentRequestWorkflow({
                  relatedTaskId: task.id,
                  relatedInvoice: task.relatedInvoice,
                  ...(task.category === "Verify POD" ? { requestType: "Proof of Delivery" as const } : {}),
                })}
              />
            </div>
          ) : selectedTab === "letters" ? (
            <div className="mt-3">
              <AccountLetterPanel
                account={selectedAccount}
                letters={accountLetters}
                allLetters={snapshot.workspace.collectionLetters}
                activityEvents={accountLetterEvents}
                onCreateLetter={() => openGenerateLetterWorkflow()}
                onScheduleFollowUp={() => openScheduleFollowUpWorkflow()}
              />
            </div>
          ) : selectedTab === "statements" ? (
            <div className="mt-3">
              <AccountStatementPanel
                account={selectedAccount}
                statements={accountStatements}
                activityEvents={accountStatementEvents}
                onCreateStatement={() => openGenerateStatementWorkflow()}
              />
            </div>
          ) : selectedTab === "documents" ? (
            <div className="mt-3">
              <AccountDocumentsPanel
                account={selectedAccount}
                documents={buildAccountDocumentRecords(selectedAccount, accountDocumentRequests)}
                onCreateDocumentRequest={() => openDocumentRequestWorkflow()}
                onReceiveDocument={handleReceiveDocument}
                onApproveDocument={handleApproveDocument}
                onRejectDocument={handleRejectDocument}
                onRequestAdditionalInfo={handleRequestAdditionalInfo}
                onCancelDocumentRequest={handleCancelDocumentRequest}
              />
            </div>
          ) : selectedTab === "history" ? (
            <div className="mt-3">
              <AccountHistoryPanel records={buildRecoveryHistory(selectedAccount, selectedAccountActivity, selectedAccountPromises, accountTicklers, accountTasks)} account={selectedAccount} />
            </div>
          ) : (
            <div className="mt-3">
              {renderAccountTab(snapshot, selectedAccount, selectedTab)}
            </div>
          )}
        </div>
      </>
    );
  }

  return (
    <div className="mt-6 grid gap-4">
      {!isSingleAccountWorkspaceActive ? (
      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        {accountCards.map((card) => (
          <article key={card.label} className="rounded-[1.5rem] border border-[#BFEAFF] bg-white p-4 shadow-[0_16px_36px_rgba(15,23,42,.06)]">
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm font-semibold text-[#0A2540]">{card.label}</p>
              <ARLightLineIcon icon={card.icon} />
            </div>
            <p className="mt-2 font-display text-2xl font-bold text-[#0A2540]">{card.value}</p>
            <p className="mt-2 text-sm font-normal leading-6 text-[#475569]">{card.note}</p>
          </article>
        ))}
      </section>
      ) : null}

      <section className="grid min-w-0 gap-3 overflow-hidden">
        {!isSingleAccountWorkspaceActive ? (
        <article className="ar-operational-workspace h-fit min-w-0 overflow-hidden rounded-2xl border p-4">
          <div className="grid min-w-0 gap-2 xl:grid-cols-[minmax(11rem,.35fr)_minmax(18rem,.65fr)] xl:items-center">
            <div className="flex min-w-0 items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex min-w-0 items-start gap-3">
                  <ARLightLineIcon compact icon={<Users className="size-5" aria-hidden="true" />} />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#0A2540]">{queueLabel}</p>
                    <p className="mt-1 text-xs font-normal leading-5 text-[#475569]">Select one account to open its focused Account Recovery Workspace.</p>
                  </div>
                </div>
              </div>
              <span className="shrink-0 rounded-full border border-[#14B8A6] bg-white px-2.5 py-1 text-xs font-semibold text-[#0A2540]">
                {queueAccounts.length}
              </span>
            </div>
            <label className="relative block">
              <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#14B8A6]" aria-hidden="true" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search customer name, account number, invoice, collector, status, or notes"
                className="focus-ring w-full rounded-xl border border-[#BFEAFF] bg-white py-2 pl-10 pr-3 text-sm font-semibold text-[#0A2540] placeholder:text-[#64748B]"
              />
            </label>
          </div>

          <div className="mt-2 flex items-center justify-between gap-2">
            <p className="text-xs font-medium text-[#334155]">{isSearchingAccounts ? "Search Results" : "Recent Portfolio"}</p>
            <span className="rounded-full border border-[#BFEAFF] bg-white px-2.5 py-0.5 text-xs font-semibold text-[#0A2540]">
              {queueAccounts.length} shown
            </span>
          </div>

          <div ref={queueListRef} className="mt-2 grid gap-1.5 md:grid-cols-3">
            {queueAccounts.length ? queueAccounts.map((account) => {
              const active = isSingleAccountWorkspaceActive && account.id === selectedAccount?.id;
              return (
                <button
                  key={account.id}
                  type="button"
                  onClick={() => activateSingleAccountWorkspace(account.id)}
                  className={`min-w-0 rounded-xl border px-3 py-2.5 text-left shadow-[0_8px_20px_rgba(15,23,42,.04)] transition hover:-translate-y-0.5 ${
                    active
                      ? "border-[#14B8A6] bg-white"
                      : "border-[#BFEAFF] bg-white hover:border-[#14B8A6] hover:bg-[#F4FBFF]"
                  }`}
                >
                  <div className="flex min-w-0 items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-[#475569]">{account.customerNumber}</p>
                      <h3 className="mt-0.5 truncate text-sm font-semibold text-[#0A2540]" title={account.customerName}>{account.customerName}</h3>
                    </div>
                    <span className="shrink-0 rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-2 py-1 text-xs font-semibold text-[#0A2540]">
                      {account.recoveryStatus}
                    </span>
                  </div>
                  <div className="mt-2 grid gap-1 text-xs leading-4 text-[#0A2540]">
                    <p className="flex justify-between gap-2"><span className="font-medium text-[#475569]">Balance</span><span className="font-semibold">{formatARRecoveryCurrency(account.openBalanceCents)}</span></p>
                    <p className="flex justify-between gap-2"><span className="font-medium text-[#475569]">Aging</span><span className="font-semibold">{account.agingSummary}</span></p>
                    <p className="flex justify-between gap-2"><span className="font-medium text-[#475569]">Promise</span><span className="font-semibold">{account.promiseToPayCount ? `${account.promiseToPayCount} active` : "None"}</span></p>
                    <p className="flex justify-between gap-2"><span className="font-medium text-[#475569]">Risk</span><span className="font-semibold">{buildRiskScore(account)}</span></p>
                    <p className="flex justify-between gap-2"><span className="font-medium text-[#475569]">Collector</span><span className="min-w-0 break-words text-right font-semibold">{account.assignedCollector}</span></p>
                    <p className="flex justify-between gap-2"><span className="font-medium text-[#475569]">Current Workflow</span><span className="font-semibold">{account.recoveryStatus}</span></p>
                  </div>
                </button>
              );
            }) : (
              <div className="rounded-xl border border-dashed border-[#BFEAFF] bg-white p-3 text-sm font-medium leading-6 text-[#0A2540]">
                No customers match this search. Try a customer name, account number, invoice, collector, status, or note.
              </div>
            )}
          </div>

        </article>
        ) : null}

        {isSingleAccountWorkspaceActive && selectedAccount ? (
        <article className="ar-operational-workspace grid min-w-0 gap-4 overflow-hidden rounded-[1.5rem] border p-4">
          <div ref={accountQuickActionSurfaceRef} tabIndex={-1} className="flex min-w-0 flex-wrap items-start justify-between gap-3 border-b border-[#BFEAFF] pb-4 outline-none">
            <div className="flex min-w-0 items-start gap-3">
              <ARLightLineIcon icon={<FolderKanban className="size-5" aria-hidden="true" />} />
              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#14B8A6]">Current Account</p>
                <h2 className="mt-1 break-words font-display text-2xl font-bold leading-tight text-[#0A2540]">{selectedAccount.customerName}</h2>
                <p className="mt-2 max-w-3xl text-sm font-normal leading-6 text-[#475569]">Everything below is scoped to this selected account: communications, promises, ticklers, letters, statements, payments, documents, notes, activity, and recovery history.</p>
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              {accountRecoveryOrigin.origin !== "direct" && !(accountRecoveryOrigin.origin === "manager-command" && activeWorkspacePanel === "escalate") ? (
                <button
                  type="button"
                  onClick={returnToAccountRecoveryOrigin}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      returnToAccountRecoveryOrigin();
                    }
                  }}
                  className="focus-ring rounded-full border border-[#14B8A6] bg-[#E6FFFB] px-4 py-2 text-xs font-black uppercase tracking-[0.12em] text-[#0A2540]"
                >
                  {accountRecoveryOrigin.label}
                </button>
              ) : null}
              <button
                type="button"
                onClick={returnToAccountQueue}
                className="focus-ring rounded-full border border-[#BFEAFF] bg-white px-4 py-2 text-xs font-black uppercase tracking-[0.12em] text-[#0A2540]"
              >
                Back to Queue
              </button>
            </div>
          </div>

          {selectedAccount ? (
            <>
          <datalist id="ar-internal-users">
                {activeInternalUserNames.map((name) => <option key={name} value={name} />)}
              </datalist>
              <div className="ar-workspace-surface min-w-0 overflow-hidden rounded-2xl border p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex min-w-0 items-start gap-3">
                    <ARLightLineIcon compact icon={<ShieldCheck className="size-5" aria-hidden="true" />} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#0A2540]">Account Governance</p>
                      <p className="mt-1 text-xs font-medium text-[#475569]">
                        Signed In: {currentActor.email || actorName()} | Role: {formatARRecoveryRole(currentActor.role)}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className={`rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.14em] ${
                      canWriteOperationalRecords
                        ? "border-[rgba(34,197,94,.28)] bg-[rgba(34,197,94,.1)] text-[#166534]"
                        : "border-[rgba(248,113,113,.28)] bg-[rgba(248,113,113,.1)] text-[#991B1B]"
                    }`}>
                      Access: {canWriteOperationalRecords ? "Can Edit" : "Read Only"}
                    </span>
                    <span className="rounded-full border border-[rgba(255,106,43,.28)] bg-[rgba(255,106,43,.1)] px-3 py-1 text-[10px] font-black uppercase tracking-[0.14em] text-[#7C5A00]">
                      Override Permission: {canOverrideRecordOwnership ? "Enabled" : "Disabled"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="ar-workspace-surface min-w-0 overflow-hidden rounded-[1.5rem] border p-4">
                <div className="flex items-center gap-3">
                  <ARLightLineIcon compact icon={<IdCard className="size-5" aria-hidden="true" />} />
                  <p className="text-sm font-semibold text-[#0A2540]">Current Account Banner</p>
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-[repeat(9,minmax(0,1fr))]">
                  <DetailMetric label="Customer" value={selectedAccount.customerName} />
                  <DetailMetric label="Account Number" value={editableSelectedAccount?.accountNumber || selectedAccount.customerNumber} />
                  <DetailMetric label="Balance" value={formatARRecoveryCurrency(selectedAccount.openBalanceCents)} />
                  <DetailMetric label="Aging" value={selectedAccount.agingSummary} />
                  <DetailMetric label="Collector" value={selectedAccount.assignedCollector} />
                  <DetailMetric label="Risk" value={`${buildRiskScore(selectedAccount)} | ${selectedAccount.escalationStatus}`} />
                  <DetailMetric label="Promise" value={selectedAccount.promiseToPayCount ? `${selectedAccount.promiseToPayCount} active` : "None"} />
                  <DetailMetric label="Ticklers" value={String(accountTicklers.length)} />
                  <DetailMetric label="Current Status" value={selectedAccount.recoveryStatus} />
                </div>
              </div>

              {renderAccountQuickActions()}

              {!activeWorkspacePanel ? renderCertifiedAccountContextWorkspace() : null}

              {activeWorkspacePanel === "call" && isCallDialogOpen ? (
                <aside
                  ref={workspacePanelRef}
                  tabIndex={-1}
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="ar-recovery-call-workspace-title"
                  className="fixed inset-0 z-[82] flex h-dvh w-full max-w-full flex-col overflow-hidden bg-white outline-none"
                  data-call-workspace-classification="full-enterprise-workspace"
                >
                  <header className="shrink-0 border-b border-[#BFEAFF] bg-white px-4 py-3 lg:px-5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Recovery Workbench</p>
                        <h3 id="ar-recovery-call-workspace-title" className="mt-1 break-words text-2xl font-semibold tracking-tight text-[#0A2540]">
                          Call Workspace
                        </h3>
                        <p className="mt-1 max-w-4xl break-words text-sm font-semibold leading-6 text-[#475569]">
                          Active call execution for {selectedAccount.customerName}. Save call outcomes while keeping full account context visible.
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setCallWorkspaceTab("overview");
                            setCallWorkspaceMobilePane("account");
                          }}
                          className="focus-ring rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2 text-sm font-semibold text-[#0A2540]"
                        >
                          View Full Account
                        </button>
                        <button type="button" onClick={requestCloseCallWorkspace} className="focus-ring rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold text-[#0A2540]">
                          Close
                        </button>
                      </div>
                    </div>

                    <div className="mt-3 grid min-w-0 max-w-full gap-2 sm:grid-cols-2 lg:grid-cols-[repeat(4,minmax(0,1fr))] 2xl:grid-cols-[repeat(9,minmax(0,1fr))]">
                      <WorkspaceField label="Customer" value={selectedAccount.customerName} />
                      <WorkspaceField label="Account Number" value={editableSelectedAccount?.accountNumber || selectedAccount.customerNumber} />
                      <WorkspaceField label="Current Balance" value={formatARRecoveryCurrency(selectedAccount.openBalanceCents)} />
                      <WorkspaceField label="Aging" value={selectedAccount.agingSummary} />
                      <WorkspaceField label="Account Status" value={selectedAccount.recoveryStatus} />
                      <WorkspaceField label="Assigned Collector" value={selectedAccount.assignedCollector} />
                      <WorkspaceField label="Promise Status" value={editableSelectedAccount?.promiseStatus || (selectedAccount.promiseToPayCount ? "Open promise" : "No promise on file")} />
                      <WorkspaceField label="Primary Contact" value={selectedAccount.contactName} />
                      <WorkspaceField label="Phone" value={selectedAccount.contactPhone} />
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-2 lg:hidden" role="tablist" aria-label="Call workspace mobile panes">
                      {[
                        { key: "call" as const, label: "Call Execution" },
                        { key: "account" as const, label: "Context Workspace" },
                      ].map((pane) => (
                        <button
                          key={pane.key}
                          type="button"
                          role="tab"
                          aria-selected={callWorkspaceMobilePane === pane.key}
                          onClick={() => setCallWorkspaceMobilePane(pane.key)}
                          className={`focus-ring min-h-10 rounded-full border px-3 text-xs font-black uppercase tracking-[0.12em] ${
                            callWorkspaceMobilePane === pane.key
                              ? "border-[#14B8A6] bg-[#14B8A6] text-white"
                              : "border-[#BFEAFF] bg-[#F4FBFF] text-[#0A2540]"
                          }`}
                        >
                          {pane.label}
                        </button>
                      ))}
                    </div>
                  </header>

                  <div className={AR_RECOVERY_ENTERPRISE_WORKSPACE_GRID_CLASS}>
                    <section className={`${callWorkspaceMobilePane === "call" ? "block" : "hidden"} min-h-0 min-w-0 border-r border-[#BFEAFF] bg-[#F8FCFF] lg:block`} aria-label="Call Execution">
                      <form
                        className="flex h-full min-h-0 flex-col"
                        onSubmit={(event) => {
                          event.preventDefault();
                          handleSaveCall();
                        }}
                      >
                        <div className="shrink-0 border-b border-[#BFEAFF] p-4">
                          <p className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">
                            <Activity className="size-4" aria-hidden="true" />
                            Call Execution
                          </p>
                          <h4 className="mt-1 break-words text-xl font-semibold text-[#0A2540]">Record active call</h4>
                          <p className="mt-1 text-sm font-semibold leading-6 text-[#475569]">
                            Capture outcome, duration, notes, and next action. Save updates Timeline, Account History, Recovery Activity, and Audit Trail.
                          </p>
                        </div>

                        <div className="min-h-0 flex-1 overflow-y-auto p-4">
                          <div className="grid gap-4">
                            <label className="grid gap-1.5">
                              <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Call Outcome</span>
                              <select
                                value={callForm.outcome}
                                onChange={(event) => setCallForm((current) => ({ ...current, outcome: event.target.value as ARRecoveryCallFormState["outcome"] }))}
                                className="focus-ring min-h-11 max-w-full rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
                              >
                                <option value="">Select outcome</option>
                                <option value="Reached AP Contact">Reached AP Contact</option>
                                <option value="Left Voicemail">Left Voicemail</option>
                                <option value="No Answer">No Answer</option>
                                <option value="Wrong Number">Wrong Number</option>
                                <option value="Dispute Discussed">Dispute Discussed</option>
                                <option value="Payment Discussed">Payment Discussed</option>
                                <option value="Escalation Required">Escalation Required</option>
                              </select>
                            </label>

                            <label className="grid gap-1.5">
                              <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Duration Minutes</span>
                              <input
                                value={callForm.durationMinutes}
                                onChange={(event) => setCallForm((current) => ({ ...current, durationMinutes: event.target.value }))}
                                inputMode="numeric"
                                placeholder="Optional"
                                className="focus-ring min-h-11 max-w-full rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
                              />
                            </label>

                            <label className="grid gap-1.5">
                              <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Next Action</span>
                              <input
                                value={callForm.nextAction}
                                onChange={(event) => setCallForm((current) => ({ ...current, nextAction: event.target.value }))}
                                placeholder="Example: Send statement and follow up tomorrow."
                                className="focus-ring min-h-11 max-w-full rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
                              />
                            </label>

                            <label className="grid gap-1.5">
                              <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Call Notes</span>
                              <textarea
                                value={callForm.notes}
                                onChange={(event) => setCallForm((current) => ({ ...current, notes: event.target.value }))}
                                placeholder="Capture who was reached, what was discussed, and any commitments or blockers."
                                rows={8}
                                className="focus-ring max-w-full rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold leading-6 text-[#0A2540]"
                              />
                            </label>

                            {callFormError ? (
                              <div className="rounded-xl border border-[rgba(248,113,113,.28)] bg-[rgba(248,113,113,.08)] px-3 py-2 text-sm font-semibold text-[#B91C1C]" aria-live="assertive">
                                {callFormError}
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <div className="sticky bottom-0 z-10 shrink-0 border-t border-[#BFEAFF] bg-white/95 p-4 shadow-[0_-12px_30px_rgba(15,23,42,.06)]">
                          <div className="flex flex-wrap justify-end gap-2">
                            <button
                              type="button"
                              onClick={requestCloseCallWorkspace}
                              className="focus-ring min-h-10 rounded-full border border-[#BFEAFF] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#475569]"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              className="focus-ring min-h-10 rounded-full border border-[rgba(255,106,43,.42)] bg-[#FF6A2B] px-4 text-sm font-black uppercase tracking-[0.12em] text-white shadow-[0_14px_30px_rgba(255,106,43,.18)]"
                            >
                              Save Call
                            </button>
                          </div>
                        </div>
                      </form>
                    </section>

                    <section className={`${callWorkspaceMobilePane === "account" ? "block" : "hidden"} min-h-0 min-w-0 bg-[#F4FBFF] lg:block`} aria-label="Context Workspace">
                      <div className="flex h-full min-h-0 flex-col">
                        <div className="shrink-0 border-b border-[#BFEAFF] bg-white p-4">
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Context Workspace</p>
                              <h4 className="mt-1 break-words text-xl font-semibold text-[#0A2540]">{selectedAccount.customerName}</h4>
                            </div>
                          </div>
                          <div className="mt-3 flex max-w-full gap-2 overflow-x-auto overscroll-x-contain pb-1" role="tablist" aria-label="Call account workspace tabs">
                            {[
                              { key: "overview" as const, label: "Overview" },
                              { key: "invoices" as const, label: "Open Invoices" },
                              { key: "timeline" as const, label: "Timeline" },
                              { key: "notes" as const, label: "Notes" },
                              { key: "communications" as const, label: "Communications" },
                              { key: "promises" as const, label: "Promises" },
                              { key: "statements" as const, label: "Statements" },
                            ].map((tab) => (
                              <button
                                key={tab.key}
                                id={`call-workspace-tab-${tab.key}`}
                                type="button"
                                role="tab"
                                aria-selected={callWorkspaceTab === tab.key}
                                aria-controls={`call-workspace-panel-${tab.key}`}
                                onClick={() => setCallWorkspaceTab(tab.key)}
                                className={`focus-ring inline-flex min-h-9 shrink-0 items-center rounded-full border px-3 text-[11px] font-black uppercase tracking-[0.12em] transition ${
                                  callWorkspaceTab === tab.key
                                    ? "border-[#14B8A6] bg-[#14B8A6] text-white"
                                    : "border-[#BFEAFF] bg-[#F4FBFF] text-[#0A2540] hover:border-[#14B8A6]"
                                }`}
                              >
                                {tab.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="min-h-0 flex-1 overflow-y-auto p-4">
                          {callWorkspaceTab === "overview" ? (
                            <div id="call-workspace-panel-overview" role="tabpanel" aria-labelledby="call-workspace-tab-overview" className="grid min-w-0 max-w-full gap-4" data-call-context-layout="summary-primary-list-detail-activity-launch">
                              <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Summary</p>
                                <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                                  <DetailMetric label="Outstanding Balance" value={formatARRecoveryCurrency(selectedAccount.openBalanceCents)} icon={<CircleDollarSign className="size-5" aria-hidden="true" />} />
                                  <DetailMetric label="Open Promises" value={String(selectedAccountPromises.filter((promise) => promiseMatchesFilter(promise, "active")).length)} icon={<Target className="size-5" aria-hidden="true" />} />
                                  <DetailMetric label="Letters Sent" value={String(accountLetters.filter((letter) => letter.status === "Sent" || letter.status === "Delivered" || letter.status === "Viewed").length)} icon={<FileText className="size-5" aria-hidden="true" />} />
                                  <DetailMetric label="Statements Sent" value={String(accountStatements.filter((statement) => statement.status === "Sent" || statement.status === "Delivered" || statement.status === "Opened").length)} icon={<FileText className="size-5" aria-hidden="true" />} />
                                  <DetailMetric label="Risk Level" value={`${buildRiskScore(selectedAccount)} | ${selectedAccount.escalationStatus}`} icon={<ShieldCheck className="size-5" aria-hidden="true" />} />
                                  <DetailMetric label="Recent Activity" value={timelineEntries[0]?.preview || "No recent activity"} icon={<Activity className="size-5" aria-hidden="true" />} />
                                </div>
                              </section>
                              <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Selected Detail</p>
                                <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                                  <WorkspaceField label="Customer Name" value={selectedAccount.customerName} />
                                  <WorkspaceField label="Account Number" value={editableSelectedAccount?.accountNumber || selectedAccount.customerNumber} />
                                  <WorkspaceField label="Client Company" value={editableSelectedAccount?.clientCompany || snapshot.workspace.company.companyName} />
                                  <WorkspaceField label="Assigned Manager" value={editableSelectedAccount?.assignedManager || "Not Captured"} />
                                  <WorkspaceField label="Preferred Contact" value={editableSelectedAccount?.preferredContactMethod || "Email + Phone"} />
                                  <WorkspaceField label="Next Follow-Up" value={editableSelectedAccount?.nextFollowUpDate ? formatCapturedDate(editableSelectedAccount.nextFollowUpDate) : accountNextAction} />
                                  <WorkspaceField label="Hold Status" value={editableSelectedAccount?.holdStatus || "No Hold"} />
                                  <WorkspaceField label="Dispute Status" value={editableSelectedAccount?.disputeStatus || (selectedAccount.escalationStatus === "None" ? "No Dispute" : "Under Review")} />
                                  <WorkspaceField label="Recovery Instructions" value={editableSelectedAccount?.recoveryInstructions || selectedAccount.notes || "Not Captured"} />
                                </div>
                              </section>
                              <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Related Activity</p>
                                <div className="mt-3 grid gap-2">
                                  {timelineEntries.slice(0, 3).map((entry) => (
                                    <div key={entry.id} className="min-w-0 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] p-3">
                                      <p className="break-words text-sm font-semibold text-[#0A2540]">{entry.preview}</p>
                                      <p className="mt-1 text-xs font-semibold uppercase tracking-[0.12em] text-[#475569]">{entry.date} | {entry.actionType}</p>
                                    </div>
                                  ))}
                                  {!timelineEntries.length ? (
                                    <p className="rounded-xl border border-dashed border-[#BFEAFF] bg-[#F4FBFF] p-3 text-sm font-semibold text-[#475569]">No recent activity has been captured for this account.</p>
                                  ) : null}
                                </div>
                              </section>
                            </div>
                          ) : callWorkspaceTab === "invoices" ? (
                            <div id="call-workspace-panel-invoices" role="tabpanel" aria-labelledby="call-workspace-tab-invoices" className="grid min-w-0 max-w-full gap-4" data-call-invoice-context="view-filter-no-payment-execution">
                              <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Summary</p>
                                <div className="mt-3 grid min-w-0 gap-3 sm:grid-cols-2 xl:grid-cols-[repeat(4,minmax(0,1fr))]">
                                  <DetailMetric label="Open Invoices" value={String(callWorkspaceInvoices.length)} />
                                  <DetailMetric label="Oldest" value={callWorkspaceOldestInvoice?.invoiceNumber || "Not Captured"} />
                                  <DetailMetric label="Largest" value={callWorkspaceLargestInvoice ? formatARRecoveryCurrency(callWorkspaceLargestInvoice.currentBalanceCents) : "Not Captured"} />
                                  <DetailMetric label="High Risk" value={String(callWorkspaceHighRiskInvoices.length)} />
                                </div>
                              </section>
                              <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Filters</p>
                                <div className="mt-3 flex flex-wrap gap-2">
                                  {[
                                    { key: "all" as const, label: "All" },
                                    { key: "oldest" as const, label: "Oldest" },
                                    { key: "largest" as const, label: "Largest" },
                                    { key: "high_risk" as const, label: "Highest Risk" },
                                  ].map((filter) => (
                                    <button
                                      key={filter.key}
                                      type="button"
                                      onClick={() => setCallInvoiceFilter(filter.key)}
                                      className={`focus-ring min-h-9 rounded-full border px-3 text-[11px] font-black uppercase tracking-[0.12em] ${
                                        callInvoiceFilter === filter.key ? "border-[#14B8A6] bg-[#14B8A6] text-white" : "border-[#BFEAFF] bg-[#F4FBFF] text-[#0A2540]"
                                      }`}
                                    >
                                      {filter.label}
                                    </button>
                                  ))}
                                </div>
                              </section>
                              <section className="grid min-w-0 max-w-full gap-3">
                                {filteredCallInvoices.map((invoice) => {
                                  const invoicePromise = selectedAccountPromises.find((promise) => promise.invoiceLabels.includes(invoice.invoiceNumber));
                                  const isOldest = invoice.id === callWorkspaceOldestInvoice?.id;
                                  const isLargest = invoice.id === callWorkspaceLargestInvoice?.id;
                                  const isHighRisk = callWorkspaceHighRiskInvoices.some((riskInvoice) => riskInvoice.id === invoice.id);
                                  return (
                                    <article key={invoice.id} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                      <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div className="min-w-0">
                                          <p className="break-words text-lg font-semibold text-[#0A2540]">{invoice.invoiceNumber}</p>
                                          <p className="mt-1 break-words text-sm font-semibold leading-6 text-[#475569]">Due {formatDate(invoice.dueDate)} | {selectedAccount.agingSummary}</p>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          {isOldest ? <span className="rounded-full border border-[rgba(255,106,43,.34)] bg-[rgba(255,106,43,.1)] px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#B45309]">Oldest</span> : null}
                                          {isLargest ? <span className="rounded-full border border-[rgba(248,113,113,.34)] bg-[rgba(248,113,113,.1)] px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#B91C1C]">Largest</span> : null}
                                          {isHighRisk ? <span className="rounded-full border border-[rgba(14,165,233,.28)] bg-[rgba(14,165,233,.1)] px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#0369A1]">Highest Risk</span> : null}
                                        </div>
                                      </div>
                                      <div className="mt-3 grid min-w-0 gap-2 sm:grid-cols-2 xl:grid-cols-[repeat(4,minmax(0,1fr))]">
                                        <WorkspaceField label="Balance" value={formatARRecoveryCurrency(invoice.currentBalanceCents)} />
                                        <WorkspaceField label="Aging" value={selectedAccount.agingSummary} />
                                        <WorkspaceField label="Status" value={invoice.status} />
                                        <WorkspaceField label="Promise" value={invoicePromise?.status || "Not Captured"} />
                                      </div>
                                      <p className="mt-3 break-words text-sm font-semibold leading-6 text-[#475569]">{invoice.notes || "Invoice context available for the active call."}</p>
                                    </article>
                                  );
                                })}
                                {!filteredCallInvoices.length ? (
                                  <ManagerEmptyState
                                    title="No invoices match"
                                    description={`No ${callInvoiceFilter.replace("_", " ")} invoices are currently available for ${selectedAccount.customerName}.`}
                                    action="Select another invoice filter to continue reviewing account context."
                                  />
                                ) : null}
                              </section>
                            </div>
                          ) : callWorkspaceTab === "timeline" ? (
                            <div id="call-workspace-panel-timeline" role="tabpanel" aria-labelledby="call-workspace-tab-timeline" className="grid min-w-0 max-w-full gap-4">
                              <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Filters</p>
                                <div className="mt-3 flex flex-wrap gap-2">
                                  {[
                                    { key: "today" as const, label: "Today" },
                                    { key: "week" as const, label: "This Week" },
                                    { key: "month" as const, label: "This Month" },
                                    { key: "all" as const, label: "All" },
                                  ].map((filter) => (
                                    <button
                                      key={filter.key}
                                      type="button"
                                      onClick={() => setCallTimelineFilter(filter.key)}
                                      className={`focus-ring min-h-9 rounded-full border px-3 text-[11px] font-black uppercase tracking-[0.12em] ${
                                        callTimelineFilter === filter.key ? "border-[#14B8A6] bg-[#14B8A6] text-white" : "border-[#BFEAFF] bg-[#F4FBFF] text-[#0A2540]"
                                      }`}
                                    >
                                      {filter.label}
                                    </button>
                                  ))}
                                </div>
                              </section>
                              <AccountTimelinePanel account={selectedAccount} timelineEntries={callTimelineEntries} />
                            </div>
                          ) : callWorkspaceTab === "notes" ? (
                            <div id="call-workspace-panel-notes" role="tabpanel" aria-labelledby="call-workspace-tab-notes" className="grid min-w-0 max-w-full gap-4">
                              <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Filters</p>
                                <div className="mt-3 flex flex-wrap gap-2">
                                  {[
                                    { key: "all" as const, label: "All" },
                                    { key: "pinned" as const, label: "Pinned Notes" },
                                    { key: "manager" as const, label: "Manager Notes" },
                                    { key: "collector" as const, label: "Collector Notes" },
                                    { key: "system" as const, label: "System Notes" },
                                  ].map((filter) => (
                                    <button
                                      key={filter.key}
                                      type="button"
                                      onClick={() => setCallNotesFilter(filter.key)}
                                      className={`focus-ring min-h-9 rounded-full border px-3 text-[11px] font-black uppercase tracking-[0.12em] ${
                                        callNotesFilter === filter.key ? "border-[#14B8A6] bg-[#14B8A6] text-white" : "border-[#BFEAFF] bg-[#F4FBFF] text-[#0A2540]"
                                      }`}
                                    >
                                      {filter.label}
                                    </button>
                                  ))}
                                </div>
                              </section>
                              <section className="grid min-w-0 max-w-full gap-3">
                                {[...filteredCallNotes].sort((a, b) => {
                                  const pinnedA = callPinnedNotes.some((note) => note.id === a.id) ? 1 : 0;
                                  const pinnedB = callPinnedNotes.some((note) => note.id === b.id) ? 1 : 0;
                                  return pinnedB - pinnedA || getDateTime(b.createdAt) - getDateTime(a.createdAt);
                                }).map((note) => {
                                  const pinned = callPinnedNotes.some((item) => item.id === note.id);
                                  return (
                                    <article key={note.id} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                      <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div className="min-w-0">
                                          <p className="break-words text-sm font-semibold text-[#0A2540]">{note.noteType}</p>
                                          <p className="mt-1 text-xs font-semibold uppercase tracking-[0.12em] text-[#14B8A6]">{formatNoteVisibility(note.visibility)} | {note.collector}</p>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          {pinned ? <span className="rounded-full border border-[rgba(255,106,43,.34)] bg-[rgba(255,106,43,.1)] px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#B45309]">Pinned</span> : null}
                                          <span className="rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-1 text-[11px] font-semibold text-[#475569]">{formatDate(note.createdAt)}</span>
                                        </div>
                                      </div>
                                      <p className="mt-3 break-words text-sm font-semibold leading-6 text-[#475569]">{note.content}</p>
                                      {note.nextAction ? <WorkspaceField label="Next Action" value={note.nextAction} /> : null}
                                    </article>
                                  );
                                })}
                                {!filteredCallNotes.length ? (
                                  <ManagerEmptyState
                                    title="No notes match"
                                    description={`No ${callNotesFilter.replace("_", " ")} notes are currently available for ${selectedAccount.customerName}.`}
                                    action="Continue the active call and save new call notes when ready."
                                  />
                                ) : null}
                              </section>
                            </div>
                          ) : callWorkspaceTab === "communications" ? (
                            <div id="call-workspace-panel-communications" role="tabpanel" aria-labelledby="call-workspace-tab-communications" className="grid min-w-0 max-w-full gap-3">
                              <div className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Summary</p>
                                    <h5 className="mt-1 break-words text-lg font-semibold text-[#0A2540]">Communication context</h5>
                                    <p className="mt-1 text-sm font-semibold leading-6 text-[#475569]">
                                      Review recent customer communications while completing the active call.
                                    </p>
                                  </div>
                                  <span className="rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-[#0A2540]">
                                    {accountCommunications.length} records
                                  </span>
                                </div>
                              </div>
                              <div className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Filters</p>
                                <div className="mt-3 flex flex-wrap gap-2">
                                  {[
                                    { key: "all" as const, label: "All" },
                                    { key: "customer" as const, label: "Customer Communications" },
                                    { key: "internal" as const, label: "Internal Communications" },
                                  ].map((filter) => (
                                    <button
                                      key={filter.key}
                                      type="button"
                                      onClick={() => setCallCommunicationsFilter(filter.key)}
                                      className={`focus-ring min-h-9 rounded-full border px-3 text-[11px] font-black uppercase tracking-[0.12em] ${
                                        callCommunicationsFilter === filter.key ? "border-[#14B8A6] bg-[#14B8A6] text-white" : "border-[#BFEAFF] bg-[#F4FBFF] text-[#0A2540]"
                                      }`}
                                    >
                                      {filter.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <div className="grid min-w-0 max-w-full gap-3">
                                {filteredCallCommunications.length ? filteredCallCommunications.slice(0, 8).map((message) => {
                                  const customerCommunication = isCustomerFacingCommunication(message, selectedAccount);
                                  return (
                                  <article key={message.id} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                      <div className="min-w-0">
                                        <p className="break-words text-sm font-semibold text-[#0A2540]">{message.subject}</p>
                                        <p className="mt-1 break-words text-xs font-semibold uppercase tracking-[0.12em] text-[#14B8A6]">
                                          {message.messageType} | {message.importance}
                                        </p>
                                      </div>
                                      <span className="shrink-0 rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-1 text-[11px] font-semibold text-[#475569]">
                                        {formatDate(message.createdAt)}
                                      </span>
                                    </div>
                                    <span className={`mt-3 inline-flex rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${
                                      customerCommunication
                                        ? "border-[rgba(34,224,208,.3)] bg-[rgba(34,224,208,.1)] text-[#0F766E]"
                                        : "border-[rgba(255,106,43,.28)] bg-[rgba(255,106,43,.1)] text-[#B45309]"
                                    }`}>
                                      {customerCommunication ? "Customer Communication" : "Internal Communication"}
                                    </span>
                                    <div className="mt-3 grid gap-2 md:grid-cols-2">
                                      <WorkspaceField label="Sender" value={message.sender} />
                                      <WorkspaceField label="Recipient" value={message.recipient} />
                                    </div>
                                    <p className="mt-3 break-words text-sm font-semibold leading-6 text-[#475569]">{previewNoteContent(message.message)}</p>
                                  </article>
                                  );
                                }) : (
                                  <ManagerEmptyState
                                    title="No communications captured"
                                    description={`No ${callCommunicationsFilter.replace("_", " ")} communications are currently attached to ${selectedAccount.customerName}.`}
                                    action="Continue the active call and save communication outcomes in the call notes."
                                  />
                                )}
                              </div>
                            </div>
                          ) : callWorkspaceTab === "promises" ? (
                            <div id="call-workspace-panel-promises" role="tabpanel" aria-labelledby="call-workspace-tab-promises" className="grid min-w-0 max-w-full gap-3">
                              <div className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Summary</p>
                                    <h5 className="mt-1 break-words text-lg font-semibold text-[#0A2540]">Promise context</h5>
                                    <p className="mt-1 text-sm font-semibold leading-6 text-[#475569]">
                                      Review promise status and history while the call remains the primary objective.
                                    </p>
                                  </div>
                                  <Link to="/admin/ar-recovery/promise-to-pay" className="focus-ring inline-flex min-h-10 items-center rounded-full border border-[#14B8A6] bg-[#14B8A6] px-4 text-sm font-black uppercase tracking-[0.12em] text-white">
                                    Open Promise Workspace
                                  </Link>
                                </div>
                              </div>
                              <CompactMetricStrip
                                items={[
                                  { label: "Promises", value: String(selectedAccountPromises.length) },
                                  { label: "Active", value: String(selectedAccountPromises.filter((promise) => promiseMatchesFilter(promise, "active")).length) },
                                  { label: "Broken", value: String(selectedAccountPromises.filter((promise) => promise.status === "Broken").length) },
                                  { label: "Completed", value: String(selectedAccountPromises.filter((promise) => promise.status === "Completed").length) },
                                  { label: "Remaining", value: formatARRecoveryCurrency(selectedAccountPromises.reduce((total, promise) => total + promise.remainingBalanceCents, 0)) },
                                ]}
                              />
                              <div className="grid min-w-0 max-w-full gap-3">
                                {selectedAccountPromises.length ? selectedAccountPromises.slice(0, 8).map((promise) => (
                                  <article key={promise.id} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                      <div className="min-w-0">
                                        <p className="break-words text-lg font-semibold text-[#0A2540]">{formatARRecoveryCurrency(promise.amountCents)}</p>
                                        <p className="mt-1 break-words text-sm font-semibold leading-6 text-[#475569]">
                                          Promise date {formatDate(promise.promiseDate)} | {promise.promiseType}
                                        </p>
                                      </div>
                                      <span className={`shrink-0 rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] ${promiseStatusBadgeClass(promise.status)}`}>
                                        {promise.status}
                                      </span>
                                    </div>
                                    <div className="mt-3 grid gap-2 md:grid-cols-3">
                                      <WorkspaceField label="Collector" value={promise.collector || "Not Captured"} />
                                      <WorkspaceField label="Remaining" value={formatARRecoveryCurrency(promise.remainingBalanceCents)} />
                                      <WorkspaceField label="Next Action" value={promise.followUpDate ? `Follow up ${formatDate(promise.followUpDate)}` : "Not Captured"} />
                                    </div>
                                    <p className="mt-3 break-words text-sm font-semibold leading-6 text-[#475569]">{previewNoteContent(promise.notes || promise.collectorNotes || "No promise notes captured.")}</p>
                                  </article>
                                )) : (
                                  <ManagerEmptyState
                                    title="No promises on this account"
                                    description={`${selectedAccount.customerName} does not currently have promise history attached to this account.`}
                                    action="Use Open Promise Workspace when promise management is required."
                                  />
                                )}
                              </div>
                              {promiseHistoryEntries.length ? (
                                <div className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Related Activity</p>
                                  <div className="mt-3 grid gap-2">
                                    {promiseHistoryEntries.slice(0, 4).map((entry) => (
                                      <div key={entry.id} className="min-w-0 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] p-3">
                                        <p className="break-words text-sm font-semibold text-[#0A2540]">{entry.statusChange}</p>
                                        <p className="mt-1 break-words text-xs font-semibold uppercase tracking-[0.12em] text-[#475569]">
                                          {entry.date} | {entry.user} | {entry.status}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          ) : (
                            <div id="call-workspace-panel-statements" role="tabpanel" aria-labelledby="call-workspace-tab-statements" className="grid min-w-0 max-w-full gap-3">
                              <div className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Summary</p>
                                    <h5 className="mt-1 break-words text-lg font-semibold text-[#0A2540]">Statement context</h5>
                                    <p className="mt-1 text-sm font-semibold leading-6 text-[#475569]">
                                      Review statement status, tracking, and recent statement activity without turning the call into statement execution.
                                    </p>
                                  </div>
                                  <Link to="/admin/ar-recovery/statements" className="focus-ring inline-flex min-h-10 items-center rounded-full border border-[#14B8A6] bg-[#14B8A6] px-4 text-sm font-black uppercase tracking-[0.12em] text-white">
                                    Open Statement Workspace
                                  </Link>
                                </div>
                              </div>
                              <CompactMetricStrip
                                items={[
                                  { label: "Statements", value: String(accountStatements.length) },
                                  { label: "Sent", value: String(accountStatements.filter((statement) => statement.status === "Sent").length) },
                                  { label: "Delivered", value: String(accountStatements.filter((statement) => statement.deliveryStatus === "Delivered" || statement.status === "Delivered").length) },
                                  { label: "Opened", value: String(accountStatements.filter((statement) => statement.openedStatus === "Opened" || statement.status === "Opened").length) },
                                  { label: "Outstanding", value: String(accountStatements.filter((statement) => !["Downloaded", "Cancelled"].includes(statement.status)).length) },
                                ]}
                              />
                              <div className="grid min-w-0 max-w-full gap-3">
                                {accountStatements.length ? [...accountStatements]
                                  .sort((a, b) => getDateTime(b.updatedAt || b.createdAt) - getDateTime(a.updatedAt || a.createdAt))
                                  .slice(0, 8)
                                  .map((statement) => (
                                    <article key={statement.id} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                      <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div className="min-w-0">
                                          <p className="break-words text-lg font-semibold text-[#0A2540]">{statement.statementNumber}</p>
                                          <p className="mt-1 break-words text-sm font-semibold leading-6 text-[#475569]">
                                            {statement.dateSent || formatCapturedDate(statement.createdAt)} | {formatARRecoveryCurrency(statement.balanceCents)}
                                          </p>
                                        </div>
                                        <span className={`shrink-0 rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] ${statementStatusBadgeClass(statement.status, statement.deliveryStatus)}`}>
                                          {statement.status}
                                        </span>
                                      </div>
                                      <div className="mt-3 grid gap-2 md:grid-cols-3">
                                        <WorkspaceField label="Delivery" value={statement.deliveryStatus} />
                                        <WorkspaceField label="Opened" value={statement.openedStatus} />
                                        <WorkspaceField label="Collector" value={statement.collector || "Not Captured"} />
                                      </div>
                                      <p className="mt-3 break-words text-sm font-semibold leading-6 text-[#475569]">{previewNoteContent(statement.notes || `${statement.openInvoiceCount} open invoices represented.`)}</p>
                                    </article>
                                  )) : (
                                  <ManagerEmptyState
                                    title="No statements generated"
                                    description={`No statements have been generated or sent for ${selectedAccount.customerName}.`}
                                    action="Use Open Statement Workspace when statement work is required."
                                  />
                                )}
                              </div>
                              {accountStatementEvents.length ? (
                                <div className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Related Activity</p>
                                  <div className="mt-3 grid gap-2">
                                    {accountStatementEvents.slice(0, 4).map((entry) => (
                                      <div key={entry.id} className="min-w-0 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] p-3">
                                        <p className="break-words text-sm font-semibold text-[#0A2540]">{entry.title}</p>
                                        <p className="mt-1 break-words text-xs font-semibold uppercase tracking-[0.12em] text-[#475569]">
                                          {formatDate(entry.createdAt)} | {entry.eventType}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          )}
                        </div>
                      </div>
                    </section>
                  </div>
                </aside>
              ) : activeWorkspacePanel === "promise" && isPromiseDialogOpen ? (
                <aside
                  ref={workspacePanelRef}
                  tabIndex={-1}
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="ar-recovery-promise-workspace-title"
                  className="fixed inset-0 z-[82] flex h-dvh w-full max-w-full flex-col overflow-hidden bg-white outline-none"
                  data-promise-workspace-classification="full-enterprise-workspace"
                >
                  <header className="shrink-0 border-b border-[#BFEAFF] bg-white px-4 py-3 lg:px-5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Recovery Workbench</p>
                        <h3 id="ar-recovery-promise-workspace-title" className="mt-1 break-words text-2xl font-semibold tracking-tight text-[#0A2540]">
                          Promise Workspace
                        </h3>
                        <p className="mt-1 max-w-4xl break-words text-sm font-semibold leading-6 text-[#475569]">
                          Manage customer payment commitments for {selectedAccount.customerName}. Record promise amount, date, related invoices, and follow-up context while keeping full account context visible.
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setPromiseWorkspaceTab("overview");
                            setPromiseWorkspaceMobilePane("account");
                          }}
                          className="focus-ring rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2 text-sm font-semibold text-[#0A2540]"
                        >
                          View Full Account
                        </button>
                        <button type="button" onClick={requestClosePromiseWorkspace} className="focus-ring rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold text-[#0A2540]">
                          Close
                        </button>
                      </div>
                    </div>

                    <div className="mt-3 grid min-w-0 max-w-full gap-2 sm:grid-cols-2 lg:grid-cols-[repeat(4,minmax(0,1fr))] 2xl:grid-cols-[repeat(9,minmax(0,1fr))]">
                      <WorkspaceField label="Customer" value={selectedAccount.customerName} />
                      <WorkspaceField label="Account Number" value={editableSelectedAccount?.accountNumber || selectedAccount.customerNumber} />
                      <WorkspaceField label="Current Balance" value={formatARRecoveryCurrency(selectedAccount.openBalanceCents)} />
                      <WorkspaceField label="Aging" value={selectedAccount.agingSummary} />
                      <WorkspaceField label="Promise Status" value={promiseWorkspaceActivePromise ? promiseWorkspaceActivePromise.status : "No active promise"} />
                      <WorkspaceField label="Active Promise" value={promiseWorkspaceActivePromise ? formatARRecoveryCurrency(promiseWorkspaceActivePromise.remainingBalanceCents) : "None"} />
                      <WorkspaceField label="Assigned Collector" value={selectedAccount.assignedCollector} />
                      <WorkspaceField label="Primary Contact" value={selectedAccount.contactName} />
                      <WorkspaceField label="Phone" value={selectedAccount.contactPhone} />
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-2 lg:hidden" role="tablist" aria-label="Promise workspace mobile panes">
                      {[
                        { key: "promise" as const, label: "Promise Execution" },
                        { key: "account" as const, label: "Context Workspace" },
                      ].map((pane) => (
                        <button
                          key={pane.key}
                          type="button"
                          role="tab"
                          aria-selected={promiseWorkspaceMobilePane === pane.key}
                          onClick={() => setPromiseWorkspaceMobilePane(pane.key)}
                          className={`focus-ring min-h-10 rounded-full border px-3 text-xs font-black uppercase tracking-[0.12em] ${
                            promiseWorkspaceMobilePane === pane.key
                              ? "border-[#14B8A6] bg-[#14B8A6] text-white"
                              : "border-[#BFEAFF] bg-[#F4FBFF] text-[#0A2540]"
                          }`}
                        >
                          {pane.label}
                        </button>
                      ))}
                    </div>
                  </header>

                  <div className={AR_RECOVERY_ENTERPRISE_WORKSPACE_GRID_CLASS}>
                    <section className={`${promiseWorkspaceMobilePane === "promise" ? "block" : "hidden"} min-h-0 min-w-0 border-r border-[#BFEAFF] bg-[#F8FCFF] lg:block`} aria-label="Promise Execution">
                      <form
                        className="flex h-full min-h-0 flex-col"
                        onSubmit={(event) => {
                          event.preventDefault();
                          handleSavePromise();
                        }}
                      >
                        <div className="shrink-0 border-b border-[#BFEAFF] p-4">
                          <p className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">
                            <Target className="size-4" aria-hidden="true" />
                            Promise Execution
                          </p>
                          <h4 className="mt-1 break-words text-xl font-semibold text-[#0A2540]">
                            {promiseWorkflowMode === "edit" ? "Maintain promise" : "Create promise"}
                          </h4>
                          <p className="mt-1 text-sm font-semibold leading-6 text-[#475569]">
                            One objective: manage the customer payment commitment and publish updates to Promise Manager, Timeline, Recovery Activity, and Audit Trail.
                          </p>
                        </div>

                        <div className="min-h-0 flex-1 overflow-y-auto p-4">
                          <div className="grid gap-4">
                            <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4 shadow-[0_18px_40px_rgba(20,184,166,.08)]" aria-label="Live Promise Summary" aria-live="polite">
                              <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Live Promise Summary</p>
                                  <h5 className="mt-1 break-words text-lg font-semibold text-[#0A2540]">{selectedAccount.customerName}</h5>
                                  <p className="mt-1 break-words text-sm font-semibold leading-6 text-[#475569]">{promiseWorkspaceAwareness}</p>
                                </div>
                                <span className="rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-[#0A2540]">
                                  {promiseWorkspaceLifecycleStage}
                                </span>
                              </div>
                              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                                <DetailMetric label="Promise Amount" value={promiseWorkspaceAmountCents > 0 ? formatARRecoveryCurrency(promiseWorkspaceAmountCents) : "Not Captured"} />
                                <DetailMetric label="Promise Date" value={promiseForm.promiseDate ? formatCapturedDate(promiseForm.promiseDate) : "Not Captured"} />
                                <DetailMetric label="Invoices Covered" value={promiseForm.relatedInvoices.length ? `${promiseForm.relatedInvoices.length} selected` : "None selected"} />
                                <DetailMetric label="Remaining Customer Balance" value={formatARRecoveryCurrency(promiseWorkspaceRemainingCustomerBalanceCents)} />
                                <DetailMetric label="Collector" value={selectedAccount.assignedCollector} />
                                <DetailMetric label="Current Promise Status" value={promiseWorkspaceActivePromise?.status || "New Promise"} />
                              </div>
                            </section>

                            <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4 shadow-[0_18px_40px_rgba(20,184,166,.08)]" aria-label="Promise confidence summary" aria-live="polite">
                              <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Promise Confidence</p>
                                  <h5 className="mt-1 break-words text-lg font-semibold text-[#0A2540]">{promiseWorkspaceStatus}</h5>
                                  <p className="mt-1 break-words text-sm font-semibold leading-6 text-[#475569]">
                                    {promiseWorkspaceCanSave ? "All confidence checks are complete." : "Complete the missing confidence checks before final review."}
                                  </p>
                                </div>
                                <span className={`rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] ${promiseWorkspaceMinimumReady ? "border-[rgba(34,197,94,.28)] bg-[rgba(34,197,94,.1)] text-[#166534]" : "border-[rgba(255,106,43,.28)] bg-[rgba(255,106,43,.1)] text-[#9A3412]"}`}>
                                  {promiseWorkspaceMinimumReady ? "Ready to Save" : "Needs Required Fields"}
                                </span>
                              </div>
                              <div className="mt-3 grid gap-2">
                                {promiseWorkspaceReadinessItems.map((item) => (
                                  <div key={item.label} className="flex min-w-0 items-start gap-2 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2">
                                    <span className={`mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-black ${item.ready ? "bg-[#14B8A6] text-white" : "bg-white text-[#64748B]"}`}>
                                      {item.ready ? "✓" : "○"}
                                    </span>
                                    <div className="min-w-0">
                                      <p className="break-words text-sm font-semibold text-[#0A2540]">{item.label}</p>
                                      <p className="mt-0.5 break-words text-xs font-semibold leading-5 text-[#475569]">{item.detail}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </section>

                            {promiseSuccessMessage ? (
                              <div className="rounded-2xl border border-[rgba(34,197,94,.24)] bg-[rgba(34,197,94,.08)] p-4 text-sm font-semibold leading-6 text-[#166534]">
                                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#166534]">Promise Recorded Successfully</p>
                                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                                  <DetailMetric label="Promise Amount" value={promiseSuccessSummary?.amount || "Recorded"} />
                                  <DetailMetric label="Promise Date" value={promiseSuccessSummary?.date || "Recorded"} />
                                  <DetailMetric label="Invoices Covered" value={promiseSuccessSummary?.invoices || "Recorded"} />
                                  <DetailMetric label="Timeline Updated" value="Yes" />
                                  <DetailMetric label="Recovery Activity Updated" value="Yes" />
                                  <DetailMetric label="Promise History Updated" value="Yes" />
                                  <DetailMetric label="Tickler Updated" value={promiseSuccessSummary?.ticklerUpdated || "Not configured"} />
                                </div>
                                <p className="mt-3 break-words">{promiseSuccessMessage}</p>
                                <div className="mt-4 flex flex-wrap gap-2">
                                  <button type="button" onClick={requestClosePromiseWorkspace} className="focus-ring min-h-9 rounded-full border border-[rgba(34,197,94,.32)] bg-white px-4 text-[11px] font-black uppercase tracking-[0.12em] text-[#166534]">Close</button>
                                  <button type="button" onClick={openCreatePromiseWorkflow} className="focus-ring min-h-9 rounded-full border border-[#14B8A6] bg-[#14B8A6] px-4 text-[11px] font-black uppercase tracking-[0.12em] text-white">Record Another Promise</button>
                                </div>
                              </div>
                            ) : null}

                            <div className="grid gap-3 sm:grid-cols-2">
                              <label className="grid gap-1.5">
                                <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Promise Amount</span>
                                <input value={promiseForm.amount} onChange={(event) => setPromiseForm((current) => ({ ...current, amount: event.target.value }))} placeholder="5000" inputMode="decimal" className="focus-ring min-h-11 max-w-full rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]" />
                              </label>

                              <label className="grid gap-1.5">
                                <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Promise Date</span>
                                <input type="date" value={promiseForm.promiseDate} onChange={(event) => setPromiseForm((current) => ({ ...current, promiseDate: event.target.value }))} className="focus-ring min-h-11 max-w-full rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]" />
                              </label>
                            </div>

                            <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                              <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Invoices Covered</p>
                                  <p className="mt-1 break-words text-sm font-semibold leading-6 text-[#475569]">Select the invoices included in this customer promise.</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-semibold text-[#0A2540]">{promiseWorkspaceSelectedInvoices.length} selected</p>
                                  <p className="mt-1 text-xs font-black uppercase tracking-[0.12em] text-[#14B8A6]">{formatARRecoveryCurrency(promiseWorkspaceSelectedInvoiceTotalCents)}</p>
                                </div>
                              </div>
                              <div className="mt-3 grid gap-2">
                                {promiseWorkspaceInvoiceCoverage.map(({ invoice, included, remainingOutsidePromiseCents }) => {
                                  const checked = included;
                                  return (
                                    <label key={invoice.id} className={`flex min-w-0 cursor-pointer items-start gap-3 rounded-xl border p-3 ${checked ? "border-[#14B8A6] bg-[#F4FBFF]" : "border-[#BFEAFF] bg-white"}`}>
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={(event) => setPromiseForm((current) => ({
                                          ...current,
                                          relatedInvoices: event.target.checked
                                            ? [...current.relatedInvoices, invoice.invoiceNumber]
                                            : current.relatedInvoices.filter((number) => number !== invoice.invoiceNumber),
                                        }))}
                                        className="mt-1 size-4 shrink-0"
                                      />
                                      <span className="min-w-0">
                                        <span className="flex flex-wrap items-center gap-2">
                                          <span className="break-words text-sm font-semibold text-[#0A2540]">{invoice.invoiceNumber}</span>
                                          <span className={`rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] ${included ? "border-[#14B8A6] bg-[#14B8A6] text-white" : "border-[#BFEAFF] bg-white text-[#64748B]"}`}>
                                            {included ? "Included In Promise" : "Not Included"}
                                          </span>
                                        </span>
                                        <span className="mt-1 block break-words text-xs font-semibold leading-5 text-[#475569]">
                                          Current Balance {formatARRecoveryCurrency(invoice.currentBalanceCents)} | Remaining Outside Promise {formatARRecoveryCurrency(remainingOutsidePromiseCents)} | Due {formatCapturedDate(invoice.dueDate)}
                                        </span>
                                      </span>
                                    </label>
                                  );
                                })}
                              </div>
                            </section>

                            <div className="grid gap-3 sm:grid-cols-2">
                              <label className="grid gap-1.5">
                                <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Promise Type</span>
                                <select value={promiseForm.promiseType} onChange={(event) => setPromiseForm((current) => ({ ...current, promiseType: event.target.value as ARRecoveryPromiseFormState["promiseType"] }))} className="focus-ring min-h-11 max-w-full rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]">
                                  <option value="">Select promise type</option>
                                  <option value="Single Payment">Single Payment</option>
                                  <option value="Installment Plan">Installment Plan</option>
                                  <option value="Partial Payment">Partial Payment</option>
                                  <option value="Settlement">Settlement</option>
                                  <option value="Other">Other</option>
                                </select>
                              </label>

                              <label className="grid gap-1.5">
                                <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Expected Payment Method</span>
                                <input value={promiseForm.expectedPaymentMethod} onChange={(event) => setPromiseForm((current) => ({ ...current, expectedPaymentMethod: event.target.value }))} placeholder="ACH, check, card, wire" className="focus-ring min-h-11 max-w-full rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]" />
                              </label>
                            </div>

                            <label className="grid gap-1.5">
                              <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Customer Agreement Notes</span>
                              <textarea value={promiseForm.notes} onChange={(event) => setPromiseForm((current) => ({ ...current, notes: event.target.value }))} placeholder="Capture customer commitment, payment expectations, and agreement context." rows={4} className="focus-ring rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold leading-6 text-[#0A2540]" />
                            </label>

                            <label className="grid gap-1.5">
                              <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Collector Notes</span>
                              <textarea value={promiseForm.outcome} onChange={(event) => setPromiseForm((current) => ({ ...current, outcome: event.target.value }))} placeholder="Internal collector context, follow-up cues, or customer risk notes." rows={3} className="focus-ring rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold leading-6 text-[#0A2540]" />
                            </label>

                            <div className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                              <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Reminder Preference</p>
                              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                                <label className="grid gap-1.5">
                                  <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Next Action</span>
                                  <select value={promiseForm.nextActionType} onChange={(event) => setPromiseForm((current) => ({ ...current, nextActionType: event.target.value as ARRecoveryPromiseFormState["nextActionType"] }))} className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]">
                                    <option value="">Select action</option>
                                    {PROMISE_NEXT_ACTION_TYPES.map((action) => <option key={action} value={action}>{action}</option>)}
                                  </select>
                                </label>
                                <label className="grid gap-1.5">
                                  <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Due Date</span>
                                  <input type="date" value={promiseForm.nextActionDueDate} onChange={(event) => setPromiseForm((current) => ({ ...current, nextActionDueDate: event.target.value }))} className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]" />
                                </label>
                                <label className="grid gap-1.5">
                                  <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Assigned To</span>
                                  <input list="ar-internal-users" value={promiseForm.nextActionAssignedTo} onChange={(event) => setPromiseForm((current) => ({ ...current, nextActionAssignedTo: event.target.value }))} placeholder={selectedAccount.assignedCollector || "Collector"} className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]" />
                                </label>
                                <label className="flex min-h-11 items-center gap-2 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 text-sm font-semibold text-[#0A2540]">
                                  <input type="checkbox" checked={promiseForm.reminderRequired} onChange={(event) => setPromiseForm((current) => ({ ...current, reminderRequired: event.target.checked }))} className="size-4" />
                                  Reminder Required
                                </label>
                              </div>
                              <label className="mt-3 grid gap-1.5">
                                <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Reminder Notes</span>
                                <input value={promiseForm.reminderNotes} onChange={(event) => setPromiseForm((current) => ({ ...current, reminderNotes: event.target.value }))} placeholder="Optional reminder notes" className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]" />
                              </label>
                              <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2">
                                <p className="break-words text-xs font-semibold leading-5 text-[#475569]">
                                  A follow-up reminder will be created or updated after this promise is saved when a next action or reminder is configured.
                                </p>
                                <Link to="/admin/ar-recovery/smart-tickler" className="focus-ring inline-flex min-h-9 items-center rounded-full border border-[#14B8A6] bg-white px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#0A2540]">
                                  Open Tickler Workspace
                                </Link>
                              </div>
                            </div>

                            <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4" aria-label="Promise Impact Preview">
                              <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Promise Impact Preview</p>
                              <p className="mt-1 break-words text-sm font-semibold leading-6 text-[#475569]">Saving this promise will update the connected operational record.</p>
                              <div className="mt-3 grid gap-2">
                                {promiseWorkspaceImpactItems.map((item) => (
                                  <div key={item} className="flex min-w-0 items-start gap-2 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2">
                                    <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-[#14B8A6] text-[11px] font-black text-white">✓</span>
                                    <p className="min-w-0 break-words text-sm font-semibold text-[#0A2540]">{item}</p>
                                  </div>
                                ))}
                              </div>
                            </section>

                            {promiseFormError ? (
                              <div className="rounded-xl border border-[rgba(248,113,113,.28)] bg-[rgba(248,113,113,.1)] px-3 py-2 text-sm font-semibold text-[#991B1B]">
                                {promiseFormError}
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <div className="shrink-0 border-t border-[#BFEAFF] bg-white p-4">
                          <div className="flex flex-wrap justify-end gap-2">
                            <button type="button" onClick={requestClosePromiseWorkspace} className="focus-ring min-h-10 rounded-full border border-[#BFEAFF] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#475569]">Cancel</button>
                            <button type="submit" disabled={!promiseWorkspaceMinimumReady} className={`focus-ring min-h-10 rounded-full border px-4 text-sm font-black uppercase tracking-[0.12em] shadow-[0_14px_30px_rgba(255,106,43,.18)] ${promiseWorkspaceMinimumReady ? "border-[rgba(255,106,43,.42)] bg-[#FF6A2B] text-white" : "cursor-not-allowed border-[#BFEAFF] bg-[#E2E8F0] text-[#64748B]"}`}>
                              {promiseWorkflowMode === "edit" ? "Save Promise" : "Save Promise"}
                            </button>
                          </div>
                        </div>
                      </form>
                    </section>

                    <section className={`${promiseWorkspaceMobilePane === "account" ? "block" : "hidden"} min-h-0 min-w-0 bg-[#F4FBFF] lg:block`} aria-label="Context Workspace">
                      <div className="flex h-full min-h-0 flex-col">
                        <div className="shrink-0 border-b border-[#BFEAFF] bg-white p-4">
                          <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Context Workspace</p>
                          <h4 className="mt-1 break-words text-xl font-semibold text-[#0A2540]">{selectedAccount.customerName}</h4>
                          <div className="mt-3 flex max-w-full gap-2 overflow-x-auto overscroll-x-contain pb-1" role="tablist" aria-label="Promise account workspace tabs">
                            {[
                              { key: "overview" as const, label: "Overview" },
                              { key: "invoices" as const, label: "Open Invoices" },
                              { key: "timeline" as const, label: "Timeline" },
                              { key: "notes" as const, label: "Notes" },
                              { key: "communications" as const, label: "Communications" },
                              { key: "payments" as const, label: "Payments" },
                              { key: "statements" as const, label: "Statements" },
                            ].map((tab) => (
                              <button key={tab.key} id={`promise-workspace-tab-${tab.key}`} type="button" role="tab" aria-selected={promiseWorkspaceTab === tab.key} aria-controls={`promise-workspace-panel-${tab.key}`} onClick={() => setPromiseWorkspaceTab(tab.key)} className={`focus-ring inline-flex min-h-9 shrink-0 items-center rounded-full border px-3 text-[11px] font-black uppercase tracking-[0.12em] transition ${promiseWorkspaceTab === tab.key ? "border-[#14B8A6] bg-[#14B8A6] text-white" : "border-[#BFEAFF] bg-[#F4FBFF] text-[#0A2540] hover:border-[#14B8A6]"}`}>{tab.label}</button>
                            ))}
                          </div>
                        </div>
                        <div className="min-h-0 flex-1 overflow-y-auto p-4">
                          {promiseWorkspaceTab === "overview" ? (
                            <div id="promise-workspace-panel-overview" role="tabpanel" aria-labelledby="promise-workspace-tab-overview" className="grid min-w-0 max-w-full gap-4">
                              <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Promise Context</p>
                                <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                                  <DetailMetric label="Outstanding Balance" value={formatARRecoveryCurrency(selectedAccount.openBalanceCents)} />
                                  <DetailMetric label="Open Invoices" value={String(callWorkspaceInvoices.length)} />
                                  <DetailMetric label="Selected Invoice Total" value={formatARRecoveryCurrency(promiseWorkspaceSelectedInvoiceTotalCents)} />
                                  <DetailMetric label="Active Promise" value={promiseWorkspaceActivePromise ? formatARRecoveryCurrency(promiseWorkspaceActivePromise.remainingBalanceCents) : "No active promise"} />
                                  <DetailMetric label="Recent Activity" value={timelineEntries[0]?.preview || "No recent activity"} />
                                  <DetailMetric label="Collector" value={selectedAccount.assignedCollector} />
                                </div>
                              </section>
                            </div>
                          ) : promiseWorkspaceTab === "invoices" ? (
                            <div id="promise-workspace-panel-invoices" role="tabpanel" aria-labelledby="promise-workspace-tab-invoices" className="grid min-w-0 max-w-full gap-3">
                              {callWorkspaceInvoices.map((invoice) => <article key={invoice.id} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4"><p className="break-words text-lg font-semibold text-[#0A2540]">{invoice.invoiceNumber}</p><p className="mt-1 break-words text-sm font-semibold leading-6 text-[#475569]">{formatARRecoveryCurrency(invoice.currentBalanceCents)} | Due {formatDate(invoice.dueDate)} | {invoice.status}</p></article>)}
                            </div>
                          ) : promiseWorkspaceTab === "timeline" ? (
                            <div id="promise-workspace-panel-timeline" role="tabpanel" aria-labelledby="promise-workspace-tab-timeline" className="grid min-w-0 max-w-full gap-4"><AccountTimelinePanel account={selectedAccount} timelineEntries={callTimelineEntries} /></div>
                          ) : promiseWorkspaceTab === "notes" ? (
                            <div id="promise-workspace-panel-notes" role="tabpanel" aria-labelledby="promise-workspace-tab-notes" className="grid min-w-0 max-w-full gap-3">{filteredCallNotes.map((note) => <article key={note.id} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4"><p className="break-words text-sm font-semibold text-[#0A2540]">{note.noteType}</p><p className="mt-3 break-words text-sm font-semibold leading-6 text-[#475569]">{note.content}</p></article>)}</div>
                          ) : promiseWorkspaceTab === "communications" ? (
                            <div id="promise-workspace-panel-communications" role="tabpanel" aria-labelledby="promise-workspace-tab-communications" className="grid min-w-0 max-w-full gap-3">{emailWorkspaceContextEvents.slice(0, 10).map((event) => <article key={event.id} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4"><p className="break-words text-sm font-semibold text-[#0A2540]">{event.title}</p><p className="mt-1 break-words text-xs font-semibold uppercase tracking-[0.12em] text-[#14B8A6]">{event.label}</p><p className="mt-3 break-words text-sm font-semibold leading-6 text-[#475569]">{previewNoteContent(event.detail)}</p></article>)}</div>
                          ) : promiseWorkspaceTab === "statements" ? (
                            <div id="promise-workspace-panel-statements" role="tabpanel" aria-labelledby="promise-workspace-tab-statements" className="grid min-w-0 max-w-full gap-3">{accountStatements.map((statement) => <article key={statement.id} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4"><p className="break-words text-sm font-semibold text-[#0A2540]">{statement.statementNumber}</p><p className="mt-1 break-words text-xs font-semibold uppercase tracking-[0.12em] text-[#14B8A6]">{statement.status} | {formatCapturedDate(statement.createdAt)}</p></article>)}</div>
                          ) : (
                            <div id="promise-workspace-panel-payments" role="tabpanel" aria-labelledby="promise-workspace-tab-payments" className="grid min-w-0 max-w-full gap-3">{paymentWorkflows.filter((payment) => payment.accountId === selectedAccount.id).map((payment) => <article key={payment.id} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4"><p className="break-words text-sm font-semibold text-[#0A2540]">{formatARRecoveryCurrency(Number(payment.amountCents || 0))}</p><p className="mt-1 break-words text-xs font-semibold uppercase tracking-[0.12em] text-[#14B8A6]">{String(payment.lifecycleStatus || "Recorded")} | {formatCapturedDate(String(payment.paymentDate || payment.createdAt || ""))}</p></article>)}</div>
                          )}
                        </div>
                      </div>
                    </section>
                  </div>
                </aside>
              ) : activeWorkspacePanel === "payment" && isPaymentWorkspaceOpen ? (
                <aside
                  ref={workspacePanelRef}
                  tabIndex={-1}
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="ar-recovery-payment-workspace-title"
                  className="fixed inset-0 z-[82] flex h-dvh w-full max-w-full flex-col overflow-hidden bg-white outline-none"
                  data-payment-workspace-classification="full-enterprise-workspace"
                >
                  <header className="shrink-0 border-b border-[#BFEAFF] bg-white px-4 py-3 lg:px-5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Recovery Workbench</p>
                        <h3 id="ar-recovery-payment-workspace-title" className="mt-1 break-words text-2xl font-semibold tracking-tight text-[#0A2540]">Payment Workspace</h3>
                        <p className="mt-1 max-w-4xl break-words text-sm font-semibold leading-6 text-[#475569]">
                          Record and apply a customer payment for {selectedAccount.customerName}. Payment Workspace records account-level payment activity only.
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-wrap items-center gap-2">
                        <button type="button" onClick={() => { setPaymentWorkspaceTab("overview"); setPaymentWorkspaceMobilePane("account"); }} className="focus-ring rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2 text-sm font-semibold text-[#0A2540]">
                          View Full Account
                        </button>
                        <button type="button" onClick={requestClosePaymentWorkspace} className="focus-ring rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold text-[#0A2540]">
                          Close
                        </button>
                      </div>
                    </div>

                    <div className="mt-3 grid min-w-0 max-w-full gap-2 sm:grid-cols-2 lg:grid-cols-[repeat(4,minmax(0,1fr))] 2xl:grid-cols-[repeat(9,minmax(0,1fr))]">
                      <WorkspaceField label="Customer" value={selectedAccount.customerName} />
                      <WorkspaceField label="Account Number" value={editableSelectedAccount?.accountNumber || selectedAccount.customerNumber} />
                      <WorkspaceField label="Current Balance" value={formatARRecoveryCurrency(selectedAccount.openBalanceCents)} />
                      <WorkspaceField label="Aging" value={selectedAccount.agingSummary} />
                      <WorkspaceField label="Account Status" value={selectedAccount.recoveryStatus} />
                      <WorkspaceField label="Assigned Collector" value={selectedAccount.assignedCollector} />
                      <WorkspaceField label="Open Invoices" value={String(callWorkspaceInvoices.length)} />
                      <WorkspaceField label="Active Promise" value={paymentWorkspaceActivePromise ? formatARRecoveryCurrency(paymentWorkspaceActivePromise.remainingBalanceCents) : "No active promise"} />
                      <WorkspaceField label="Payment Activity" value={String(paymentWorkflows.filter((payment) => payment.accountId === selectedAccount.id).length)} />
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-2 lg:hidden" role="tablist" aria-label="Payment workspace mobile panes">
                      {[
                        { key: "payment" as const, label: "Payment Execution" },
                        { key: "account" as const, label: "Context Workspace" },
                      ].map((pane) => (
                        <button
                          key={pane.key}
                          type="button"
                          role="tab"
                          aria-selected={paymentWorkspaceMobilePane === pane.key}
                          onClick={() => setPaymentWorkspaceMobilePane(pane.key)}
                          className={`focus-ring min-h-10 rounded-full border px-3 text-xs font-black uppercase tracking-[0.12em] ${
                            paymentWorkspaceMobilePane === pane.key ? "border-[#14B8A6] bg-[#14B8A6] text-white" : "border-[#BFEAFF] bg-[#F4FBFF] text-[#0A2540]"
                          }`}
                        >
                          {pane.label}
                        </button>
                      ))}
                    </div>
                  </header>

                  <div className={AR_RECOVERY_ENTERPRISE_WORKSPACE_GRID_CLASS}>
                    <section className={`${paymentWorkspaceMobilePane === "payment" ? "block" : "hidden"} min-h-0 min-w-0 border-r border-[#BFEAFF] bg-[#F8FCFF] lg:block`} aria-label="Payment Execution">
                      <form
                        className="flex h-full min-h-0 flex-col"
                        onSubmit={(event) => {
                          event.preventDefault();
                          handleSavePaymentWorkspace();
                        }}
                      >
                        <div className="shrink-0 border-b border-[#BFEAFF] p-4">
                          <p className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]"><CircleDollarSign className="size-4" aria-hidden="true" /> Payment Execution</p>
                          <h4 className="mt-1 break-words text-xl font-semibold text-[#0A2540]">Record customer payment</h4>
                          <p className="mt-1 text-sm font-semibold leading-6 text-[#475569]">Capture payment details, select affected invoices, and review balance impact before saving.</p>
                        </div>
                        <div className="min-h-0 flex-1 overflow-y-auto p-4">
                          <div className="grid gap-4">
                            <div className="grid gap-3 sm:grid-cols-2">
                              <label className="grid gap-1.5">
                                <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Payment Amount</span>
                                <input value={paymentWorkspaceForm.amount} onChange={(event) => { setPaymentWorkspaceError(""); setPaymentWorkspaceSuccess(null); setPaymentWorkspaceForm((current) => ({ ...current, amount: event.target.value, overpaymentConfirmed: false })); }} inputMode="decimal" placeholder="0.00" className="focus-ring min-h-11 max-w-full rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]" aria-describedby="payment-summary-live-region" />
                              </label>
                              <label className="grid gap-1.5">
                                <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Payment Date</span>
                                <input type="date" value={paymentWorkspaceForm.paymentDate} onChange={(event) => { setPaymentWorkspaceError(""); setPaymentWorkspaceSuccess(null); setPaymentWorkspaceForm((current) => ({ ...current, paymentDate: event.target.value })); }} className="focus-ring min-h-11 max-w-full rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]" />
                              </label>
                            </div>

                            <section id="payment-summary-live-region" className="rounded-2xl border border-[#14B8A6] bg-white p-4 shadow-[0_18px_40px_rgba(20,184,166,.08)]" aria-label="Live Payment Summary" aria-live="polite">
                              <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Live Payment Summary</p>
                                  <p className="mt-1 break-words text-xs font-semibold leading-5 text-[#64748B]">Review the financial impact before saving this payment.</p>
                                </div>
                                <span className={`rounded-full border px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em] ${paymentWorkspaceRemainingPaymentCents > 0 ? "border-[#FED7AA] bg-[#FFF7ED] text-[#9A3412]" : "border-[#14B8A6] bg-[rgba(20,184,166,.08)] text-[#0F766E]"}`}>
                                  {paymentWorkspaceRemainingPaymentCents > 0 ? "Remaining Payment" : "Fully Applied"}
                                </span>
                              </div>
                              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                                <WorkspaceField label="Amount Received" value={Number.isFinite(paymentWorkspaceAmountCents) ? formatARRecoveryCurrency(paymentWorkspaceAmountReceivedCents) : "Enter amount"} />
                                <WorkspaceField label="Amount Applied" value={formatARRecoveryCurrency(paymentWorkspaceAmountAppliedCents)} />
                                <WorkspaceField label="Remaining Payment" value={formatARRecoveryCurrency(paymentWorkspaceRemainingPaymentCents)} />
                                <WorkspaceField label="Remaining Customer Balance" value={formatARRecoveryCurrency(paymentWorkspaceRemainingAccountBalanceCents)} />
                                <WorkspaceField label="Selected Invoices" value={String(paymentWorkspaceForm.appliedInvoiceNumbers.length)} />
                                <WorkspaceField label="Promise Impact" value={paymentWorkspaceActivePromise ? paymentWorkspacePromiseInvoiceMatch ? `Remaining promise balance ${formatARRecoveryCurrency(paymentWorkspaceRemainingPromiseBalanceCents)}` : "Active promise not affected by selected invoices" : "No active promise"} />
                              </div>
                              {paymentWorkspaceRemainingPaymentCents > 0 ? (
                                <div className="mt-3 rounded-xl border border-[#FED7AA] bg-[#FFF7ED] p-3 text-sm font-semibold leading-6 text-[#9A3412]">
                                  Unapplied Amount: {formatARRecoveryCurrency(paymentWorkspaceRemainingPaymentCents)} may remain unapplied, be allocated to additional invoices, or be cleared by canceling this payment.
                                </div>
                              ) : null}
                              {paymentWorkspaceOutstandingOverpaymentCents > 0 ? (
                                <div className="mt-3 rounded-xl border border-[#FCA5A5] bg-[rgba(248,113,113,.08)] p-3 text-sm font-semibold leading-6 text-[#B91C1C]">
                                  Outstanding Balance: {formatARRecoveryCurrency(selectedAccount.openBalanceCents)}. Overpayment: {formatARRecoveryCurrency(paymentWorkspaceOutstandingOverpaymentCents)}.
                                </div>
                              ) : null}
                              {paymentWorkspaceOverpaymentRequiresConfirmation ? (
                                <label className="mt-3 flex min-w-0 items-start gap-3 rounded-xl border border-[#FCA5A5] bg-[rgba(248,113,113,.08)] p-3 text-sm font-semibold leading-6 text-[#B91C1C]">
                                  <input type="checkbox" checked={paymentWorkspaceForm.overpaymentConfirmed} onChange={(event) => setPaymentWorkspaceForm((current) => ({ ...current, overpaymentConfirmed: event.target.checked }))} className="mt-1" />
                                  <span>Confirm this payment exceeds the selected invoice balance and leave the remaining amount unapplied.</span>
                                </label>
                              ) : null}
                            </section>

                            <section className="rounded-2xl border border-[#BFEAFF] bg-white p-3">
                              <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Apply Payment</p>
                                  <p className="mt-1 break-words text-xs font-semibold leading-5 text-[#64748B]">Invoices are ordered by promise relevance, age, balance, and recency.</p>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  <span className="rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em] text-[#0A2540]">{paymentWorkspaceForm.appliedInvoiceNumbers.length} Selected</span>
                                  <span className="rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em] text-[#0A2540]">{formatARRecoveryCurrency(paymentWorkspaceAppliedInvoiceTotalCents)} Balance</span>
                                </div>
                              </div>
                              <div className="mt-3 flex flex-wrap gap-2">
                                <button type="button" onClick={() => applyPaymentWorkspaceInvoiceSuggestion("oldest")} disabled={!callWorkspaceOldestInvoice} className="focus-ring rounded-full border border-[#BFEAFF] bg-[#F8FCFF] px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em] text-[#0A2540] disabled:cursor-not-allowed disabled:opacity-50">Oldest</button>
                                <button type="button" onClick={() => applyPaymentWorkspaceInvoiceSuggestion("highest_risk")} disabled={!callWorkspaceHighRiskInvoices.length} className="focus-ring rounded-full border border-[#BFEAFF] bg-[#F8FCFF] px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em] text-[#0A2540] disabled:cursor-not-allowed disabled:opacity-50">Highest Risk</button>
                                <button type="button" onClick={() => applyPaymentWorkspaceInvoiceSuggestion("promise")} disabled={!paymentWorkspaceActivePromise} className="focus-ring rounded-full border border-[#BFEAFF] bg-[#F8FCFF] px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em] text-[#0A2540] disabled:cursor-not-allowed disabled:opacity-50">Promise-Related</button>
                              </div>
                              <div className="mt-3 grid gap-2">
                                {paymentWorkspaceOrderedInvoices.map((invoice) => {
                                  const selected = paymentWorkspaceForm.appliedInvoiceNumbers.includes(invoice.invoiceNumber);
                                  const allocation = paymentWorkspaceAllocationByInvoiceNumber.get(invoice.invoiceNumber);
                                  const appliedCents = allocation?.appliedCents || 0;
                                  const remainingAfterCents = selected ? allocation?.remainingAfterCents ?? invoice.currentBalanceCents : invoice.currentBalanceCents;
                                  return (
                                    <label key={invoice.id} className={`flex min-w-0 items-start gap-3 rounded-xl border p-3 ${selected ? "border-[#14B8A6] bg-[rgba(20,184,166,.08)]" : "border-[#D7F1FF] bg-[#F8FCFF]"}`}>
                                      <input type="checkbox" checked={selected} onChange={() => togglePaymentWorkspaceInvoice(invoice.invoiceNumber)} className="mt-1" aria-label={`Apply payment to invoice ${invoice.invoiceNumber}`} />
                                      <span className="min-w-0 flex-1">
                                        <span className="flex flex-wrap items-center gap-2">
                                          <span className="break-words text-sm font-black text-[#0A2540]">{invoice.invoiceNumber}</span>
                                          {paymentWorkspacePromiseInvoiceNumbers.has(invoice.invoiceNumber) ? <span className="rounded-full border border-[#14B8A6] bg-white px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] text-[#14B8A6]">Promise</span> : null}
                                        </span>
                                        <span className="mt-1 block break-words text-xs font-semibold leading-5 text-[#475569]">{selectedAccount.agingSummary} | Due {formatCapturedDate(invoice.dueDate)} | {invoice.status}</span>
                                        <span className="mt-3 grid gap-2 sm:grid-cols-3">
                                          <span className="min-w-0 rounded-xl border border-[#BFEAFF] bg-white p-2">
                                            <span className="block text-[10px] font-black uppercase tracking-[0.12em] text-[#64748B]">Current Balance</span>
                                            <span className="mt-1 block break-words text-xs font-black text-[#0A2540]">{formatARRecoveryCurrency(invoice.currentBalanceCents)}</span>
                                          </span>
                                          <span className="min-w-0 rounded-xl border border-[#BFEAFF] bg-white p-2">
                                            <span className="block text-[10px] font-black uppercase tracking-[0.12em] text-[#64748B]">Amount Applied</span>
                                            <span className="mt-1 block break-words text-xs font-black text-[#0A2540]">{formatARRecoveryCurrency(appliedCents)}</span>
                                          </span>
                                          <span className="min-w-0 rounded-xl border border-[#BFEAFF] bg-white p-2">
                                            <span className="block text-[10px] font-black uppercase tracking-[0.12em] text-[#64748B]">Remaining Balance</span>
                                            <span className="mt-1 block break-words text-xs font-black text-[#0A2540]">{formatARRecoveryCurrency(remainingAfterCents)}</span>
                                          </span>
                                        </span>
                                      </span>
                                    </label>
                                  );
                                })}
                                {!callWorkspaceInvoices.length ? <ManagerEmptyState title="No open invoices" description="No eligible open invoices are currently available for payment application." action="Record only when payment support is confirmed." /> : null}
                              </div>
                            </section>

                            <section className="rounded-2xl border border-[#BFEAFF] bg-white p-3" aria-label="Payment method">
                              <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Payment Method</p>
                              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                                {AR_RECOVERY_PAYMENT_METHOD_OPTIONS.map((method) => {
                                  const selected = paymentWorkspaceForm.paymentMethod === method.key;
                                  return (
                                    <button
                                      key={method.key}
                                      type="button"
                                      onClick={() => { setPaymentWorkspaceError(""); setPaymentWorkspaceSuccess(null); setPaymentWorkspaceForm((current) => ({ ...current, paymentMethod: method.key })); }}
                                      className={`focus-ring min-w-0 rounded-xl border p-3 text-left ${selected ? "border-[#14B8A6] bg-[rgba(20,184,166,.08)]" : "border-[#BFEAFF] bg-[#F8FCFF]"}`}
                                      aria-pressed={selected}
                                    >
                                      <span className="flex min-w-0 items-center gap-3">
                                        <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-xl border border-[#BFEAFF] bg-white text-[11px] font-black text-[#14B8A6]">{method.code}</span>
                                        <span className="min-w-0">
                                          <span className="block break-words text-sm font-black text-[#0A2540]">{method.label}</span>
                                          <span className="mt-1 block break-words text-xs font-semibold leading-5 text-[#64748B]">{method.description}</span>
                                        </span>
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            </section>

                            <label className="grid gap-1.5">
                              <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Reference / Check Number</span>
                              <input value={paymentWorkspaceForm.referenceNumber} onChange={(event) => { setPaymentWorkspaceError(""); setPaymentWorkspaceSuccess(null); setPaymentWorkspaceForm((current) => ({ ...current, referenceNumber: event.target.value })); }} placeholder="Optional reference" className="focus-ring min-h-11 max-w-full rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]" />
                              {paymentWorkspaceDuplicateReference ? <p className="text-xs font-semibold leading-5 text-[#B91C1C]">This reference already exists for the selected account.</p> : null}
                            </label>

                            <label className="grid gap-1.5">
                              <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Payment Source</span>
                              <select value={paymentWorkspaceForm.paymentSource} onChange={(event) => { setPaymentWorkspaceError(""); setPaymentWorkspaceSuccess(null); setPaymentWorkspaceForm((current) => ({ ...current, paymentSource: event.target.value as ARRecoveryPaymentSourceKey })); }} className="focus-ring min-h-11 max-w-full rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]">
                                {AR_RECOVERY_PAYMENT_SOURCE_OPTIONS.map((source) => <option key={source.key} value={source.key}>{source.label}</option>)}
                              </select>
                            </label>

                            <label className="grid gap-1.5">
                              <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Internal Payment Notes</span>
                              <textarea value={paymentWorkspaceForm.internalNotes} onChange={(event) => { setPaymentWorkspaceError(""); setPaymentWorkspaceSuccess(null); setPaymentWorkspaceForm((current) => ({ ...current, internalNotes: event.target.value })); }} placeholder="Internal only. Capture remittance details or verification context." rows={4} className="focus-ring max-w-full rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold leading-6 text-[#0A2540]" />
                            </label>

                            {paymentWorkspaceActivePromise ? (
                              <section className="rounded-2xl border border-[#BFEAFF] bg-white p-3" aria-label="Promise awareness">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Active Promise</p>
                                    <p className="mt-1 break-words text-sm font-semibold leading-6 text-[#0A2540]">{formatARRecoveryCurrency(paymentWorkspaceActivePromise.remainingBalanceCents)} due {formatCapturedDate(paymentWorkspaceActivePromise.promiseDate)} | {paymentWorkspaceActivePromise.status}</p>
                                    <p className="mt-1 break-words text-xs font-semibold leading-5 text-[#64748B]">{paymentWorkspacePromiseInvoiceMatch ? "Recording this payment will update the active promise context." : "Select promise-related invoices to apply this payment to promise context."}</p>
                                  </div>
                                  <Link to="/admin/ar-recovery/promise-to-pay" className="focus-ring inline-flex min-h-9 items-center rounded-full border border-[#14B8A6] bg-[#14B8A6] px-3 text-[11px] font-black uppercase tracking-[0.12em] text-white">Open Promise Workspace</Link>
                                </div>
                              </section>
                            ) : null}

                            <section className="rounded-2xl border border-[#BFEAFF] bg-white p-3" aria-label="Payment audit preview">
                              <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Payment Audit Preview</p>
                              <div className="mt-3 grid gap-2 text-sm font-semibold leading-6 text-[#0A2540]">
                                {["Reduce Customer Balance", "Update Timeline", "Update Recovery Activity", "Update Payment History", "Update Audit Trail", paymentWorkspaceActivePromise && paymentWorkspacePromiseInvoiceMatch ? "Update Active Promise" : "Preserve Promise Context"].map((item) => (
                                  <p key={item} className="min-w-0 break-words rounded-xl border border-[#D7F1FF] bg-[#F8FCFF] px-3 py-2">{item}</p>
                                ))}
                              </div>
                            </section>

                            <section className="rounded-2xl border border-[#BFEAFF] bg-white p-3" aria-label="Payment readiness">
                              <div className="flex flex-wrap items-center justify-between gap-3">
                                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Payment Ready</p>
                                <span className={`rounded-full border px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em] ${paymentWorkspaceCanSave ? "border-[#14B8A6] bg-[rgba(20,184,166,.08)] text-[#0F766E]" : "border-[#FED7AA] bg-[#FFF7ED] text-[#9A3412]"}`}>{paymentWorkspaceCanSave ? "Ready to Save" : "Needs Review"}</span>
                              </div>
                              <div className="mt-3 grid gap-2">
                                {paymentWorkspaceReadinessItems.map((item) => (
                                  <div key={item.label} className="flex min-w-0 items-start justify-between gap-3 rounded-xl border border-[#D7F1FF] bg-[#F8FCFF] p-3">
                                    <div className="min-w-0">
                                      <p className="break-words text-sm font-black text-[#0A2540]">{item.label}</p>
                                      <p className="mt-1 break-words text-xs font-semibold leading-5 text-[#64748B]">{item.detail}</p>
                                    </div>
                                    <span className={`shrink-0 rounded-full border px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${item.ready ? "border-[#14B8A6] bg-white text-[#0F766E]" : "border-[#FED7AA] bg-white text-[#9A3412]"}`}>{item.ready ? "Ready" : "Needed"}</span>
                                  </div>
                                ))}
                              </div>
                            </section>

                            {paymentWorkspaceError ? <div className="whitespace-pre-wrap rounded-xl border border-[rgba(248,113,113,.28)] bg-[rgba(248,113,113,.08)] px-3 py-2 text-sm font-semibold leading-6 text-[#B91C1C]" aria-live="assertive">{paymentWorkspaceError}</div> : null}
                            {paymentWorkspaceSuccess ? (
                              <section className="rounded-xl border border-[rgba(20,184,166,.28)] bg-[rgba(20,184,166,.08)] p-3 text-sm font-semibold leading-6 text-[#0A2540]" aria-live="polite">
                                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Payment Recorded Successfully</p>
                                <div className="mt-2 grid gap-2">
                                  <p className="break-words"><span className="text-[#64748B]">Amount:</span> {formatARRecoveryCurrency(paymentWorkspaceSuccess.amountCents)}</p>
                                  <p className="break-words"><span className="text-[#64748B]">Invoices Updated:</span> {paymentWorkspaceSuccess.appliedInvoiceNumbers.join(", ") || "Account"}</p>
                                  <p className="break-words"><span className="text-[#64748B]">Unapplied:</span> {formatARRecoveryCurrency(paymentWorkspaceSuccess.unappliedAmountCents)}</p>
                                  <p className="break-words"><span className="text-[#64748B]">Remaining Balance:</span> {formatARRecoveryCurrency(paymentWorkspaceSuccess.remainingBalanceCents)}</p>
                                  <p className="break-words"><span className="text-[#64748B]">Payment Reference:</span> {paymentWorkspaceSuccess.paymentReference}</p>
                                  <p className="break-words"><span className="text-[#64748B]">Updates:</span> Timeline, Recovery Activity, Account History, Payment History, and Audit Trail updated.</p>
                                </div>
                                <div className="mt-3 flex flex-wrap gap-2">
                                  <button type="button" onClick={requestClosePaymentWorkspace} className="focus-ring min-h-9 rounded-full border border-[#14B8A6] bg-[#14B8A6] px-3 text-[11px] font-black uppercase tracking-[0.12em] text-white">Close</button>
                                  <button type="button" onClick={resetPaymentWorkspaceForAnotherPayment} className="focus-ring min-h-9 rounded-full border border-[#BFEAFF] bg-white px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#0A2540]">Record Another Payment</button>
                                </div>
                              </section>
                            ) : null}
                          </div>
                        </div>
                        <div className="sticky bottom-0 z-10 shrink-0 border-t border-[#BFEAFF] bg-white/95 p-4 shadow-[0_-12px_30px_rgba(15,23,42,.06)]">
                          <div className="flex flex-wrap justify-end gap-2">
                            <button type="button" onClick={requestClosePaymentWorkspace} className="focus-ring min-h-10 rounded-full border border-[#BFEAFF] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#475569]">Cancel</button>
                            <button type="submit" disabled={!paymentWorkspaceCanSave || Boolean(paymentWorkspaceSuccess)} className={`focus-ring min-h-10 rounded-full border px-4 text-sm font-black uppercase tracking-[0.12em] shadow-[0_14px_30px_rgba(255,106,43,.18)] ${paymentWorkspaceCanSave && !paymentWorkspaceSuccess ? "border-[rgba(255,106,43,.42)] bg-[#FF6A2B] text-white" : "cursor-not-allowed border-[#BFEAFF] bg-[#E2E8F0] text-[#64748B]"}`}>{paymentWorkspaceSuccess ? "Payment Recorded" : "Save Payment"}</button>
                          </div>
                        </div>
                      </form>
                    </section>

                    <section className={`${paymentWorkspaceMobilePane === "account" ? "block" : "hidden"} min-h-0 min-w-0 bg-[#F4FBFF] lg:block`} aria-label="Context Workspace">
                      <div className="flex h-full min-h-0 flex-col">
                        <div className="shrink-0 border-b border-[#BFEAFF] bg-white p-4">
                          <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Context Workspace</p>
                          <h4 className="mt-1 break-words text-xl font-semibold text-[#0A2540]">{selectedAccount.customerName}</h4>
                          <div className="mt-3 flex max-w-full gap-2 overflow-x-auto overscroll-x-contain pb-1" role="tablist" aria-label="Payment account workspace tabs">
                            {[
                              { key: "overview" as const, label: "Overview" },
                              { key: "invoices" as const, label: "Open Invoices" },
                              { key: "timeline" as const, label: "Timeline" },
                              { key: "notes" as const, label: "Notes" },
                              { key: "communications" as const, label: "Communications" },
                              { key: "promises" as const, label: "Promises" },
                              { key: "statements" as const, label: "Statements" },
                            ].map((tab) => (
                              <button key={tab.key} id={`payment-workspace-tab-${tab.key}`} type="button" role="tab" aria-selected={paymentWorkspaceTab === tab.key} aria-controls={`payment-workspace-panel-${tab.key}`} onClick={() => setPaymentWorkspaceTab(tab.key)} className={`focus-ring inline-flex min-h-9 shrink-0 items-center rounded-full border px-3 text-[11px] font-black uppercase tracking-[0.12em] transition ${paymentWorkspaceTab === tab.key ? "border-[#14B8A6] bg-[#14B8A6] text-white" : "border-[#BFEAFF] bg-[#F4FBFF] text-[#0A2540] hover:border-[#14B8A6]"}`}>{tab.label}</button>
                            ))}
                          </div>
                        </div>
                        <div className="min-h-0 flex-1 overflow-y-auto p-4">
                          {paymentWorkspaceTab === "overview" ? (
                            <div id="payment-workspace-panel-overview" role="tabpanel" aria-labelledby="payment-workspace-tab-overview" className="grid min-w-0 max-w-full gap-4">
                              <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Payment Context</p>
                                <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                                  <DetailMetric label="Outstanding Balance" value={formatARRecoveryCurrency(selectedAccount.openBalanceCents)} />
                                  <DetailMetric label="Open Invoices" value={String(callWorkspaceInvoices.length)} />
                                  <DetailMetric label="Oldest Invoice" value={callWorkspaceOldestInvoice?.invoiceNumber || "Not Captured"} />
                                  <DetailMetric label="Largest Invoice" value={callWorkspaceLargestInvoice ? formatARRecoveryCurrency(callWorkspaceLargestInvoice.currentBalanceCents) : "Not Captured"} />
                                  <DetailMetric label="Active Promise" value={paymentWorkspaceActivePromise ? formatARRecoveryCurrency(paymentWorkspaceActivePromise.remainingBalanceCents) : "No active promise"} />
                                  <DetailMetric label="Recent Activity" value={timelineEntries[0]?.preview || "No recent activity"} />
                                </div>
                              </section>
                            </div>
                          ) : paymentWorkspaceTab === "invoices" ? (
                            <div id="payment-workspace-panel-invoices" role="tabpanel" aria-labelledby="payment-workspace-tab-invoices" className="grid min-w-0 max-w-full gap-3">
                              {callWorkspaceInvoices.map((invoice) => <article key={invoice.id} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4"><p className="break-words text-lg font-semibold text-[#0A2540]">{invoice.invoiceNumber}</p><p className="mt-1 break-words text-sm font-semibold leading-6 text-[#475569]">{formatARRecoveryCurrency(invoice.currentBalanceCents)} | Due {formatDate(invoice.dueDate)} | {invoice.status}</p></article>)}
                            </div>
                          ) : paymentWorkspaceTab === "timeline" ? (
                            <div id="payment-workspace-panel-timeline" role="tabpanel" aria-labelledby="payment-workspace-tab-timeline" className="grid min-w-0 max-w-full gap-4"><AccountTimelinePanel account={selectedAccount} timelineEntries={callTimelineEntries} /></div>
                          ) : paymentWorkspaceTab === "notes" ? (
                            <div id="payment-workspace-panel-notes" role="tabpanel" aria-labelledby="payment-workspace-tab-notes" className="grid min-w-0 max-w-full gap-3">{filteredCallNotes.map((note) => <article key={note.id} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4"><p className="break-words text-sm font-semibold text-[#0A2540]">{note.noteType}</p><p className="mt-3 break-words text-sm font-semibold leading-6 text-[#475569]">{note.content}</p></article>)}</div>
                          ) : paymentWorkspaceTab === "communications" ? (
                            <div id="payment-workspace-panel-communications" role="tabpanel" aria-labelledby="payment-workspace-tab-communications" className="grid min-w-0 max-w-full gap-3">{emailWorkspaceContextEvents.slice(0, 10).map((event) => <article key={event.id} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4"><p className="break-words text-sm font-semibold text-[#0A2540]">{event.title}</p><p className="mt-1 break-words text-xs font-semibold uppercase tracking-[0.12em] text-[#14B8A6]">{event.label}</p><p className="mt-3 break-words text-sm font-semibold leading-6 text-[#475569]">{previewNoteContent(event.detail)}</p></article>)}</div>
                          ) : paymentWorkspaceTab === "promises" ? (
                            <div id="payment-workspace-panel-promises" role="tabpanel" aria-labelledby="payment-workspace-tab-promises" className="grid min-w-0 max-w-full gap-3"><Link to="/admin/ar-recovery/promise-to-pay" className="focus-ring inline-flex min-h-10 w-fit items-center rounded-full border border-[#14B8A6] bg-[#14B8A6] px-4 text-sm font-black uppercase tracking-[0.12em] text-white">Open Promise Workspace</Link>{selectedAccountPromises.map((promise) => <article key={promise.id} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4"><p className="break-words text-sm font-semibold text-[#0A2540]">{formatARRecoveryCurrency(promise.remainingBalanceCents)} remaining</p><p className="mt-1 break-words text-xs font-semibold uppercase tracking-[0.12em] text-[#14B8A6]">{promise.status} | {formatCapturedDate(promise.promiseDate)}</p></article>)}</div>
                          ) : (
                            <div id="payment-workspace-panel-statements" role="tabpanel" aria-labelledby="payment-workspace-tab-statements" className="grid min-w-0 max-w-full gap-3">{accountStatements.map((statement) => <article key={statement.id} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4"><p className="break-words text-sm font-semibold text-[#0A2540]">{statement.statementNumber}</p><p className="mt-1 break-words text-xs font-semibold uppercase tracking-[0.12em] text-[#14B8A6]">{statement.status} | {formatCapturedDate(statement.createdAt)}</p></article>)}</div>
                          )}
                        </div>
                      </div>
                    </section>
                  </div>
                </aside>
              ) : activeWorkspacePanel === "email" && isEmailWorkspaceOpen ? (
                <aside
                  ref={workspacePanelRef}
                  tabIndex={-1}
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="ar-recovery-email-workspace-title"
                  className="fixed inset-0 z-[82] flex h-dvh w-full max-w-full flex-col overflow-hidden bg-white outline-none"
                  data-email-workspace-classification="full-enterprise-workspace"
                >
                  <header className="shrink-0 border-b border-[#BFEAFF] bg-white px-4 py-3 lg:px-5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Recovery Workbench</p>
                        <h3 id="ar-recovery-email-workspace-title" className="mt-1 break-words text-2xl font-semibold tracking-tight text-[#0A2540]">
                          Email Workspace
                        </h3>
                        <p className="mt-1 max-w-4xl break-words text-sm font-semibold leading-6 text-[#475569]">
                          Customer email execution for {selectedAccount.customerName}. Select approved templates, review customer-ready preview, and keep full account context visible.
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEmailWorkspaceTab("overview");
                            setEmailWorkspaceMobilePane("account");
                          }}
                          className="focus-ring rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2 text-sm font-semibold text-[#0A2540]"
                        >
                          View Full Account
                        </button>
                        <button type="button" onClick={requestCloseEmailWorkspace} className="focus-ring rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold text-[#0A2540]">
                          Close
                        </button>
                      </div>
                    </div>

                    <div className="mt-3 grid min-w-0 max-w-full gap-2 sm:grid-cols-2 lg:grid-cols-[repeat(4,minmax(0,1fr))] 2xl:grid-cols-[repeat(9,minmax(0,1fr))]">
                      <WorkspaceField label="Customer" value={selectedAccount.customerName} />
                      <WorkspaceField label="Account Number" value={editableSelectedAccount?.accountNumber || selectedAccount.customerNumber} />
                      <WorkspaceField label="Current Balance" value={formatARRecoveryCurrency(selectedAccount.openBalanceCents)} />
                      <WorkspaceField label="Aging" value={selectedAccount.agingSummary} />
                      <WorkspaceField label="Account Status" value={selectedAccount.recoveryStatus} />
                      <WorkspaceField label="Assigned Collector" value={selectedAccount.assignedCollector} />
                      <WorkspaceField label="Promise Status" value={editableSelectedAccount?.promiseStatus || (selectedAccount.promiseToPayCount ? "Open promise" : "No promise on file")} />
                      <WorkspaceField label="Primary Contact" value={selectedAccount.contactName} />
                      <WorkspaceField label="Phone" value={selectedAccount.contactPhone} />
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-2 lg:hidden" role="tablist" aria-label="Email workspace mobile panes">
                      {[
                        { key: "email" as const, label: "Email Execution" },
                        { key: "account" as const, label: "Context Workspace" },
                      ].map((pane) => (
                        <button
                          key={pane.key}
                          type="button"
                          role="tab"
                          aria-selected={emailWorkspaceMobilePane === pane.key}
                          onClick={() => setEmailWorkspaceMobilePane(pane.key)}
                          className={`focus-ring min-h-10 rounded-full border px-3 text-xs font-black uppercase tracking-[0.12em] ${
                            emailWorkspaceMobilePane === pane.key
                              ? "border-[#14B8A6] bg-[#14B8A6] text-white"
                              : "border-[#BFEAFF] bg-[#F4FBFF] text-[#0A2540]"
                          }`}
                        >
                          {pane.label}
                        </button>
                      ))}
                    </div>
                  </header>

                  <div className={AR_RECOVERY_ENTERPRISE_WORKSPACE_GRID_CLASS}>
                    <section className={`${emailWorkspaceMobilePane === "email" ? "block" : "hidden"} min-h-0 min-w-0 border-r border-[#BFEAFF] bg-[#F8FCFF] lg:block`} aria-label="Email Execution">
                      <form
                        className="flex h-full min-h-0 flex-col"
                        onSubmit={(event) => {
                          event.preventDefault();
                          handleSendEmailWorkspace();
                        }}
                      >
                        <div className="shrink-0 border-b border-[#BFEAFF] p-4">
                          <p className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">
                            <FileText className="size-4" aria-hidden="true" />
                            Email Execution
                          </p>
                          <h4 className="mt-1 break-words text-xl font-semibold text-[#0A2540]">Create customer email</h4>
                          <p className="mt-1 text-sm font-semibold leading-6 text-[#475569]">
                            Select approved templates, choose approved attachments, and preview the customer-facing message before sending.
                          </p>
                        </div>

                        <div className="min-h-0 flex-1 overflow-y-auto p-4">
                          <div className="grid gap-4">
                            <label className="grid gap-1.5">
                              <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Recipient</span>
                              <input
                                ref={emailRecipientInputRef}
                                value={emailWorkspaceForm.recipient}
                                onChange={(event) => {
                                  setEmailWorkspaceError("");
                                  setEmailWorkspaceForm((current) => ({ ...current, recipient: event.target.value }));
                                }}
                                onBlur={handleEmailWorkspaceAddressBlur}
                                aria-invalid={Boolean(emailWorkspaceValidation.recipient.message)}
                                aria-describedby={emailWorkspaceValidation.recipient.message ? "email-recipient-error" : undefined}
                                placeholder={`${selectedAccount.contactName} <${selectedAccount.contactEmail}>`}
                                className={`focus-ring min-h-11 max-w-full rounded-xl border bg-white px-3 text-sm font-semibold text-[#0A2540] ${emailWorkspaceValidation.recipient.message ? "border-[#DC2626] ring-2 ring-[rgba(220,38,38,.14)]" : "border-[#BFEAFF]"}`}
                              />
                              {emailWorkspaceValidation.recipient.message ? (
                                <p id="email-recipient-error" className="flex items-start gap-2 text-xs font-semibold leading-5 text-[#B91C1C]">
                                  <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                                  <span>{emailWorkspaceValidation.recipient.invalidAddresses.length ? `Invalid: ${emailWorkspaceValidation.recipient.invalidAddresses.join(", ")}` : emailWorkspaceValidation.recipient.message}</span>
                                </p>
                              ) : null}
                            </label>

                            <div className="grid gap-3 sm:grid-cols-2">
                              <label className="grid gap-1.5">
                                <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">CC</span>
                                <input
                                  ref={emailCcInputRef}
                                  value={emailWorkspaceForm.cc}
                                  onChange={(event) => {
                                    setEmailWorkspaceError("");
                                    setEmailWorkspaceForm((current) => ({ ...current, cc: event.target.value }));
                                  }}
                                  onBlur={handleEmailWorkspaceAddressBlur}
                                  aria-invalid={Boolean(emailWorkspaceValidation.cc.message)}
                                  aria-describedby={emailWorkspaceValidation.cc.message ? "email-cc-error" : undefined}
                                  placeholder="Optional"
                                  className={`focus-ring min-h-11 max-w-full rounded-xl border bg-white px-3 text-sm font-semibold text-[#0A2540] ${emailWorkspaceValidation.cc.message ? "border-[#DC2626] ring-2 ring-[rgba(220,38,38,.14)]" : "border-[#BFEAFF]"}`}
                                />
                                {emailWorkspaceValidation.cc.message ? (
                                  <p id="email-cc-error" className="flex items-start gap-2 text-xs font-semibold leading-5 text-[#B91C1C]">
                                    <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                                    <span>Invalid: {emailWorkspaceValidation.cc.invalidAddresses.join(", ")}</span>
                                  </p>
                                ) : null}
                              </label>
                              <label className="grid gap-1.5">
                                <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">BCC</span>
                                <input
                                  ref={emailBccInputRef}
                                  value={emailWorkspaceForm.bcc}
                                  onChange={(event) => {
                                    setEmailWorkspaceError("");
                                    setEmailWorkspaceForm((current) => ({ ...current, bcc: event.target.value }));
                                  }}
                                  onBlur={handleEmailWorkspaceAddressBlur}
                                  aria-invalid={Boolean(emailWorkspaceValidation.bcc.message)}
                                  aria-describedby={emailWorkspaceValidation.bcc.message ? "email-bcc-error" : undefined}
                                  placeholder="Optional"
                                  className={`focus-ring min-h-11 max-w-full rounded-xl border bg-white px-3 text-sm font-semibold text-[#0A2540] ${emailWorkspaceValidation.bcc.message ? "border-[#DC2626] ring-2 ring-[rgba(220,38,38,.14)]" : "border-[#BFEAFF]"}`}
                                />
                                {emailWorkspaceValidation.bcc.message ? (
                                  <p id="email-bcc-error" className="flex items-start gap-2 text-xs font-semibold leading-5 text-[#B91C1C]">
                                    <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                                    <span>Invalid: {emailWorkspaceValidation.bcc.invalidAddresses.join(", ")}</span>
                                  </p>
                                ) : null}
                              </label>
                            </div>

                            <label className="grid gap-1.5">
                              <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Approved Email Template</span>
                              <select
                                value={emailWorkspaceForm.template}
                                onChange={(event) => handleSelectEmailTemplate(event.target.value as ARRecoveryEmailWorkspaceTemplate | "")}
                                className="focus-ring min-h-11 max-w-full rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
                              >
                                <option value="">Select approved template</option>
                                {AR_RECOVERY_EMAIL_WORKSPACE_TEMPLATES.map((template) => (
                                  <option key={template.name} value={template.name}>{template.name}</option>
                                ))}
                              </select>
                            </label>

                            <label className="grid gap-1.5">
                              <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Subject</span>
                              <input
                                value={emailWorkspaceForm.subject}
                                onChange={(event) => setEmailWorkspaceForm((current) => ({ ...current, subject: event.target.value }))}
                                className="focus-ring min-h-11 max-w-full rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
                              />
                            </label>

                            <label className="grid gap-1.5">
                              <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Email Body</span>
                              <textarea
                                value={emailWorkspaceForm.body}
                                onChange={(event) => setEmailWorkspaceForm((current) => ({ ...current, body: event.target.value }))}
                                rows={7}
                                className="focus-ring max-w-full rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold leading-6 text-[#0A2540]"
                              />
                            </label>

                            <label className="grid gap-1.5">
                              <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Priority</span>
                              <select
                                value={emailWorkspaceForm.priority}
                                onChange={(event) => setEmailWorkspaceForm((current) => ({ ...current, priority: event.target.value as ARRecoveryEmailWorkspacePriority }))}
                                className="focus-ring min-h-11 max-w-full rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
                              >
                                <option value="Normal">Normal</option>
                                <option value="High">High</option>
                              </select>
                            </label>

                            <section className="rounded-2xl border border-[#BFEAFF] bg-white p-3">
                              <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Supporting Documents</p>
                                  <p className="mt-1 break-words text-xs font-semibold leading-5 text-[#64748B]">
                                    Attach existing certified business documents when they are available. Email can be sent without attachments.
                                  </p>
                                </div>
                                <span className="rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em] text-[#0A2540]">
                                  {emailWorkspacePackageStatus}
                                </span>
                              </div>

                              <div className="mt-3 rounded-xl border border-[#BFEAFF] bg-[#F8FCFF] p-3">
                                <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <p className="text-[11px] font-black uppercase tracking-[0.14em] text-[#14B8A6]">Selected Attachments</p>
                                    <p className="mt-1 break-words text-sm font-black text-[#0A2540]">
                                      {selectedEmailWorkspaceDocumentPackageItems.length
                                        ? `${selectedEmailWorkspaceAttachedDocumentCount} of ${selectedEmailWorkspaceDocumentPackageItems.length} documents attached`
                                        : "Attachments optional"}
                                    </p>
                                    {selectedEmailWorkspaceDocumentPackageItems.length ? (
                                      <p className="mt-1 break-words text-xs font-semibold leading-5 text-[#0F766E]">
                                        Selected documents are ready to send.
                                      </p>
                                    ) : (
                                      <p className="mt-1 break-words text-xs font-semibold leading-5 text-[#64748B]">
                                        No attachments selected. Continue if the approved email does not require supporting documents.
                                      </p>
                                    )}
                                  </div>
                                </div>
                                {!emailWorkspaceDocumentPackageGroups.length ? (
                                  <div className="mt-3 rounded-xl border border-[#BFEAFF] bg-white p-3 text-sm font-semibold leading-6 text-[#475569]">
                                    <p className="text-[11px] font-black uppercase tracking-[0.14em] text-[#14B8A6]">Supporting Documents</p>
                                    <p className="mt-1 break-words">
                                      No supporting documents are currently available.
                                    </p>
                                    <p className="mt-1 break-words text-xs leading-5">
                                      Letters, statements, invoices, and supporting documents will appear here after they have been created or imported.
                                    </p>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                      <button
                                        type="button"
                                        onClick={() => openGenerateLetterWorkflow()}
                                        className="focus-ring rounded-full border border-[#BFEAFF] bg-[#F8FCFF] px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em] text-[#0A2540]"
                                      >
                                        Open Letter Workspace
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => openGenerateStatementWorkflow()}
                                        className="focus-ring rounded-full border border-[#BFEAFF] bg-[#F8FCFF] px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em] text-[#0A2540]"
                                      >
                                        Open Statement Workspace
                                      </button>
                                    </div>
                                  </div>
                                ) : null}
                              </div>

                              <div className="mt-4 grid gap-4">
                                {emailWorkspaceDocumentPackageGroups.map((group) => (
                                  <div key={group.group} className="min-w-0 rounded-xl border border-[#BFEAFF] bg-[#F8FCFF] p-3">
                                    <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">{group.group}</p>
                                    <div className="mt-2 grid gap-2">
                                      {group.items.map((item) => (
                                        <label key={item.attachment.id} className="flex min-w-0 items-start gap-3 rounded-xl border border-[#D7F1FF] bg-white p-3">
                                          <input
                                            type="checkbox"
                                            checked={emailWorkspaceForm.attachmentIds.includes(item.attachment.id)}
                                            onChange={() => toggleEmailWorkspaceAttachment(item.attachment.id)}
                                            className="mt-1"
                                            aria-label={`Include ${item.displayName}`}
                                          />
                                          <span className="min-w-0 flex-1">
                                            <span className="flex min-w-0 flex-wrap items-center gap-2">
                                              <span className="block break-words text-sm font-black text-[#0A2540]">{item.displayName}</span>
                                              <span className={`rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${getARRecoveryEmailDocumentStatusClasses(item.status)}`}>
                                                {item.statusLabel}
                                              </span>
                                            </span>
                                            <span className="mt-1 block break-words text-xs font-semibold leading-5 text-[#475569]">
                                              {item.documentType} | {item.formatLabel} | {item.generatedLabel} | {item.statusDetail}
                                            </span>
                                          </span>
                                        </label>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>

                              {selectedEmailWorkspaceDocumentPackageItems.length ? (
                                <div className="mt-4 grid gap-3">
                                  <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
                                    <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Selected Documents</p>
                                    <p className="break-words text-xs font-black text-[#0A2540]">
                                      {selectedEmailWorkspaceDocumentPackageItems.length} {selectedEmailWorkspaceDocumentPackageItems.length === 1 ? "Document" : "Documents"} Selected
                                    </p>
                                  </div>
                                  {selectedEmailWorkspaceDocumentPackageGroups.map((group) => (
                                    <div key={`selected-${group.group}`} className="grid gap-2">
                                      <p className="text-[10px] font-black uppercase tracking-[0.16em] text-[#64748B]">{group.group}</p>
                                      {group.items.map((item) => (
                                        <article key={`package-${item.attachment.id}`} className="min-w-0 rounded-xl border border-[#BFEAFF] bg-[#F8FCFF] p-3">
                                          <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
                                            <div className="flex min-w-0 gap-3">
                                              <span className="grid size-10 shrink-0 place-items-center rounded-xl border border-[#BFEAFF] bg-white text-[#14B8A6]" aria-hidden="true">
                                                <FileText className="size-5" />
                                              </span>
                                              <div className="min-w-0">
                                                <div className="flex min-w-0 flex-wrap items-center gap-2">
                                                  <p className="break-words text-sm font-black text-[#0A2540]">{item.displayName}</p>
                                                  <span className={`rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${getARRecoveryEmailDocumentStatusClasses(item.status)}`}>
                                                    {item.statusLabel}
                                                  </span>
                                                </div>
                                                <dl className="mt-2 grid gap-1 text-xs font-semibold leading-5 text-[#475569] sm:grid-cols-2">
                                                  <div className="min-w-0"><dt className="inline text-[#64748B]">Type: </dt><dd className="inline break-words">{item.documentType}</dd></div>
                                                  <div className="min-w-0"><dt className="inline text-[#64748B]">Format: </dt><dd className="inline break-words">{item.formatLabel}</dd></div>
                                                  <div className="min-w-0"><dt className="inline text-[#64748B]">Generated: </dt><dd className="inline break-words">{item.generatedLabel}</dd></div>
                                                  <div className="min-w-0"><dt className="inline text-[#64748B]">Status: </dt><dd className="inline break-words">{item.deliveryLabel}</dd></div>
                                                </dl>
                                              </div>
                                            </div>
                                            <div className="flex shrink-0 flex-wrap gap-2">
                                              <button
                                                type="button"
                                                onClick={() => previewEmailWorkspaceAttachment(item)}
                                                disabled={!item.payload}
                                                className={`focus-ring rounded-full border px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em] ${
                                                  item.payload
                                                    ? "border-[#BFEAFF] bg-white text-[#0A2540]"
                                                    : "cursor-not-allowed border-[#E2E8F0] bg-[#F8FAFC] text-[#94A3B8]"
                                                }`}
                                              >
                                                Preview
                                              </button>
                                              <button
                                                type="button"
                                                onClick={() => openEmailWorkspaceAttachment(item)}
                                                disabled={!item.payload}
                                                className={`focus-ring rounded-full border px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em] ${
                                                  item.payload
                                                    ? "border-[#BFEAFF] bg-white text-[#0A2540]"
                                                    : "cursor-not-allowed border-[#E2E8F0] bg-[#F8FAFC] text-[#94A3B8]"
                                                }`}
                                              >
                                                Open
                                              </button>
                                              <button
                                                type="button"
                                                onClick={() => removeEmailWorkspaceAttachment(item.attachment.id)}
                                                className="focus-ring rounded-full border border-[#FCA5A5] bg-white px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em] text-[#B91C1C]"
                                              >
                                                Remove
                                              </button>
                                            </div>
                                          </div>
                                        </article>
                                      ))}
                                    </div>
                                  ))}
                                </div>
                              ) : null}

                              {emailWorkspaceDocumentPreview ? (
                                <section className="mt-4 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] p-3" aria-label="Document preview">
                                  <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Document Preview</p>
                                      <h4 className="mt-1 break-words text-sm font-black text-[#0A2540]">{emailWorkspaceDocumentPreview.name}</h4>
                                      <p className="mt-1 break-words text-xs font-semibold leading-5 text-[#475569]">
                                        {emailWorkspaceDocumentPreview.documentType} | {emailWorkspaceDocumentPreview.formatLabel} | {emailWorkspaceDocumentPreview.generatedLabel} | {emailWorkspaceDocumentPreview.statusLabel}
                                      </p>
                                    </div>
                                    <div className="flex shrink-0 flex-wrap gap-2">
                                      {emailWorkspaceDocumentPreview.sourceUrl ? (
                                        <>
                                          <a
                                            href={emailWorkspaceDocumentPreview.sourceUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="focus-ring rounded-full border border-[#BFEAFF] bg-white px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em] text-[#0A2540]"
                                          >
                                            Open Full Document
                                          </a>
                                          <a
                                            href={emailWorkspaceDocumentPreview.sourceUrl}
                                            download={emailWorkspaceDocumentPreview.name}
                                            className="focus-ring rounded-full border border-[#BFEAFF] bg-white px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em] text-[#0A2540]"
                                          >
                                            Download
                                          </a>
                                        </>
                                      ) : null}
                                      <button
                                        type="button"
                                        onClick={() => setEmailWorkspaceDocumentPreview(null)}
                                        className="focus-ring rounded-full border border-[#BFEAFF] bg-white px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em] text-[#475569]"
                                      >
                                        Close
                                      </button>
                                    </div>
                                  </div>
                                  {emailWorkspaceDocumentPreview.sourceUrl ? (
                                    <iframe
                                      title={`${emailWorkspaceDocumentPreview.name} preview`}
                                      src={emailWorkspaceDocumentPreview.sourceUrl}
                                      className="mt-3 h-[360px] w-full rounded-xl border border-[#BFEAFF] bg-white"
                                    />
                                  ) : (
                                    <div className="mt-3 rounded-xl border border-[#FCA5A5] bg-white p-3 text-sm font-semibold leading-6 text-[#B91C1C]" role="status">
                                      {emailWorkspaceDocumentPreview.message}
                                    </div>
                                  )}
                                </section>
                              ) : null}
                            </section>

                            <label className="grid gap-1.5">
                              <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Internal Notes</span>
                              <textarea
                                value={emailWorkspaceForm.internalNotes}
                                onChange={(event) => setEmailWorkspaceForm((current) => ({ ...current, internalNotes: event.target.value }))}
                                placeholder="Internal only. Never customer visible."
                                rows={4}
                                className="focus-ring max-w-full rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold leading-6 text-[#0A2540]"
                              />
                            </label>

                            <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4" aria-label="Live Email Preview">
                              <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Live Email Preview</p>
                              <div className="mt-3 grid gap-2 text-sm font-semibold leading-6 text-[#0A2540]">
                                <p className="break-words"><span className="text-[#64748B]">From:</span> {snapshot.workspace.company.companyName} Collections</p>
                                <p className="break-words"><span className="text-[#64748B]">To:</span> {emailWorkspaceRecipient || "Recipient required"}</p>
                                <p className="break-words"><span className="text-[#64748B]">Subject:</span> {emailWorkspaceSubject || "Select an approved email template to preview your customer email."}</p>
                              </div>
                              <article className="mt-3 rounded-xl border border-[#BFEAFF] bg-[#F8FCFF] p-4 text-sm font-semibold leading-6 text-[#475569]">
                                <div className="mb-4 flex items-center gap-3 border-b border-[#BFEAFF] pb-3">
                                  <img
                                    src={AR_RECOVERY_LETTER_BRANDING_LOGO_SRC}
                                    alt={`${snapshot.workspace.company.companyName} logo`}
                                    className="h-12 w-auto shrink-0"
                                  />
                                  <div className="min-w-0">
                                    <p className="break-words text-sm font-black text-[#0A2540]">{snapshot.workspace.company.companyName}</p>
                                    <p className="break-words text-xs font-bold text-[#64748B]">Accounts Receivable Department</p>
                                  </div>
                                </div>
                                {emailWorkspaceBody ? (
                                  <div className="whitespace-pre-wrap break-words">
                                    {emailWorkspaceCustomerBody}
                                  </div>
                                ) : (
                                  <p>Select an approved template or enter email body content.</p>
                                )}
                              </article>
                              {selectedEmailWorkspaceAttachments.length ? (
                                <div className="mt-3 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] p-3 text-sm font-semibold leading-6 text-[#0A2540]">
                                  <p className="text-[11px] font-black uppercase tracking-[0.14em] text-[#14B8A6]">Attachments</p>
                                  <p className="mt-1 break-words text-[#475569]">
                                    {selectedEmailWorkspaceAttachments.length} approved {selectedEmailWorkspaceAttachments.length === 1 ? "document will be attached" : "documents will be attached"} to the email.
                                  </p>
                                </div>
                              ) : null}
                            </section>

                            {emailWorkspaceError ? (
                              <div className="whitespace-pre-wrap rounded-xl border border-[rgba(248,113,113,.28)] bg-[rgba(248,113,113,.08)] px-3 py-2 text-sm font-semibold leading-6 text-[#B91C1C]" aria-live="assertive">
                                {emailWorkspaceError}
                              </div>
                            ) : null}
                            {emailWorkspaceDeliverySummary ? (
                              <section className="rounded-xl border border-[rgba(20,184,166,.28)] bg-[rgba(20,184,166,.08)] p-3 text-sm font-semibold leading-6 text-[#0A2540]" aria-live="polite">
                                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Email Sent</p>
                                <div className="mt-2 grid gap-2">
                                  <p className="break-words"><span className="text-[#64748B]">Recipient:</span> {emailWorkspaceDeliverySummary.recipient}</p>
                                  <p className="break-words"><span className="text-[#64748B]">Subject:</span> {emailWorkspaceDeliverySummary.subject}</p>
                                  <p className="break-words"><span className="text-[#64748B]">Attachments:</span> {emailWorkspaceDeliverySummary.attachments.length ? emailWorkspaceDeliverySummary.attachments.join(", ") : "None"}</p>
                                  <p className="break-words"><span className="text-[#64748B]">Sent Time:</span> {formatCapturedDate(emailWorkspaceDeliverySummary.sentAt)} {formatCapturedTime(emailWorkspaceDeliverySummary.sentAt)}</p>
                                </div>
                              </section>
                            ) : null}
                          </div>
                        </div>

                        <div className="sticky bottom-0 z-10 shrink-0 border-t border-[#BFEAFF] bg-white/95 p-4 shadow-[0_-12px_30px_rgba(15,23,42,.06)]">
                          <div className="flex flex-wrap justify-end gap-2">
                            <button
                              type="button"
                              onClick={requestCloseEmailWorkspace}
                              className="focus-ring min-h-10 rounded-full border border-[#BFEAFF] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#475569]"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={handleSaveEmailDraft}
                              className="focus-ring min-h-10 rounded-full border border-[#BFEAFF] bg-white px-4 text-sm font-black uppercase tracking-[0.12em] text-[#0A2540]"
                            >
                              Save Draft
                            </button>
                            <button
                              type="submit"
                              disabled={!emailWorkspaceCanSend || emailWorkspaceSubmitting}
                              className={`focus-ring min-h-10 rounded-full border px-4 text-sm font-black uppercase tracking-[0.12em] shadow-[0_14px_30px_rgba(255,106,43,.18)] ${
                                emailWorkspaceCanSend && !emailWorkspaceSubmitting
                                  ? "border-[rgba(255,106,43,.42)] bg-[#FF6A2B] text-white"
                                  : "cursor-not-allowed border-[#BFEAFF] bg-[#E2E8F0] text-[#64748B]"
                              }`}
                            >
                              {emailWorkspaceSubmitting ? "Sending..." : "Send Email"}
                            </button>
                          </div>
                        </div>
                      </form>
                    </section>

                    <section className={`${emailWorkspaceMobilePane === "account" ? "block" : "hidden"} min-h-0 min-w-0 bg-[#F4FBFF] lg:block`} aria-label="Context Workspace">
                      <div className="flex h-full min-h-0 flex-col">
                        <div className="shrink-0 border-b border-[#BFEAFF] bg-white p-4">
                          <div className="min-w-0">
                            <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Context Workspace</p>
                            <h4 className="mt-1 break-words text-xl font-semibold text-[#0A2540]">{selectedAccount.customerName}</h4>
                          </div>
                          <div className="mt-3 flex max-w-full gap-2 overflow-x-auto overscroll-x-contain pb-1" role="tablist" aria-label="Email account workspace tabs">
                            {[
                              { key: "overview" as const, label: "Overview" },
                              { key: "invoices" as const, label: "Open Invoices" },
                              { key: "timeline" as const, label: "Timeline" },
                              { key: "notes" as const, label: "Notes" },
                              { key: "communications" as const, label: "Communications" },
                              { key: "promises" as const, label: "Promises" },
                              { key: "statements" as const, label: "Statements" },
                            ].map((tab) => (
                              <button
                                key={tab.key}
                                id={`email-workspace-tab-${tab.key}`}
                                type="button"
                                role="tab"
                                aria-selected={emailWorkspaceTab === tab.key}
                                aria-controls={`email-workspace-panel-${tab.key}`}
                                onClick={() => setEmailWorkspaceTab(tab.key)}
                                className={`focus-ring inline-flex min-h-9 shrink-0 items-center rounded-full border px-3 text-[11px] font-black uppercase tracking-[0.12em] transition ${
                                  emailWorkspaceTab === tab.key
                                    ? "border-[#14B8A6] bg-[#14B8A6] text-white"
                                    : "border-[#BFEAFF] bg-[#F4FBFF] text-[#0A2540] hover:border-[#14B8A6]"
                                }`}
                              >
                                {tab.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="min-h-0 flex-1 overflow-y-auto p-4">
                          {emailWorkspaceTab === "overview" ? (
                            <div id="email-workspace-panel-overview" role="tabpanel" aria-labelledby="email-workspace-tab-overview" className="grid min-w-0 max-w-full gap-4" data-email-context-layout="summary-primary-list-detail-activity-launch">
                              <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Summary</p>
                                <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                                  <DetailMetric label="Outstanding Balance" value={formatARRecoveryCurrency(selectedAccount.openBalanceCents)} />
                                  <DetailMetric label="Open Promises" value={String(selectedAccountPromises.filter((promise) => promiseMatchesFilter(promise, "active")).length)} />
                                  <DetailMetric label="Letters Sent" value={String(accountLetters.filter((letter) => letter.status === "Sent" || letter.status === "Delivered" || letter.status === "Viewed").length)} />
                                  <DetailMetric label="Statements Sent" value={String(accountStatements.filter((statement) => statement.status === "Sent" || statement.status === "Delivered" || statement.status === "Opened").length)} />
                                  <DetailMetric label="Risk Level" value={`${buildRiskScore(selectedAccount)} | ${selectedAccount.escalationStatus}`} />
                                  <DetailMetric label="Recent Activity" value={timelineEntries[0]?.preview || "No recent activity"} />
                                </div>
                              </section>
                              <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Selected Detail</p>
                                <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                                  <WorkspaceField label="Customer Name" value={selectedAccount.customerName} />
                                  <WorkspaceField label="Account Number" value={editableSelectedAccount?.accountNumber || selectedAccount.customerNumber} />
                                  <WorkspaceField label="Primary Contact" value={selectedAccount.contactName} />
                                  <WorkspaceField label="Email" value={selectedAccount.contactEmail} />
                                  <WorkspaceField label="Preferred Contact" value={editableSelectedAccount?.preferredContactMethod || "Email + Phone"} />
                                  <WorkspaceField label="Next Follow-Up" value={editableSelectedAccount?.nextFollowUpDate ? formatCapturedDate(editableSelectedAccount.nextFollowUpDate) : accountNextAction} />
                                </div>
                              </section>
                            </div>
                          ) : emailWorkspaceTab === "invoices" ? (
                            <div id="email-workspace-panel-invoices" role="tabpanel" aria-labelledby="email-workspace-tab-invoices" className="grid min-w-0 max-w-full gap-3">
                              <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Open Invoices</p>
                                <div className="mt-3 grid min-w-0 gap-3 sm:grid-cols-2 xl:grid-cols-[repeat(4,minmax(0,1fr))]">
                                  <DetailMetric label="Open Invoices" value={String(callWorkspaceInvoices.length)} />
                                  <DetailMetric label="Oldest" value={callWorkspaceOldestInvoice?.invoiceNumber || "Not Captured"} />
                                  <DetailMetric label="Largest" value={callWorkspaceLargestInvoice ? formatARRecoveryCurrency(callWorkspaceLargestInvoice.currentBalanceCents) : "Not Captured"} />
                                  <DetailMetric label="High Risk" value={String(callWorkspaceHighRiskInvoices.length)} />
                                </div>
                              </section>
                              {callWorkspaceInvoices.map((invoice) => (
                                <article key={invoice.id} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                  <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <p className="break-words text-lg font-semibold text-[#0A2540]">{invoice.invoiceNumber}</p>
                                      <p className="mt-1 break-words text-sm font-semibold leading-6 text-[#475569]">Due {formatDate(invoice.dueDate)} | {selectedAccount.agingSummary}</p>
                                    </div>
                                    <span className="rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-1 text-[11px] font-semibold text-[#475569]">{invoice.status}</span>
                                  </div>
                                  <div className="mt-3 grid min-w-0 gap-2 sm:grid-cols-2 xl:grid-cols-[repeat(4,minmax(0,1fr))]">
                                    <WorkspaceField label="Balance" value={formatARRecoveryCurrency(invoice.currentBalanceCents)} />
                                    <WorkspaceField label="Aging" value={selectedAccount.agingSummary} />
                                    <WorkspaceField label="Status" value={invoice.status} />
                                    <WorkspaceField label="Promise" value={selectedAccount.promiseToPayCount ? "Promise activity exists" : "Not Captured"} />
                                  </div>
                                </article>
                              ))}
                            </div>
                          ) : emailWorkspaceTab === "timeline" ? (
                            <div id="email-workspace-panel-timeline" role="tabpanel" aria-labelledby="email-workspace-tab-timeline" className="grid min-w-0 max-w-full gap-4">
                              <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Filters</p>
                                <div className="mt-3 flex flex-wrap gap-2">
                                  {[
                                    { key: "today" as const, label: "Today" },
                                    { key: "week" as const, label: "This Week" },
                                    { key: "month" as const, label: "This Month" },
                                    { key: "all" as const, label: "All" },
                                  ].map((filter) => (
                                    <button
                                      key={filter.key}
                                      type="button"
                                      onClick={() => setCallTimelineFilter(filter.key)}
                                      className={`focus-ring min-h-9 rounded-full border px-3 text-[11px] font-black uppercase tracking-[0.12em] ${
                                        callTimelineFilter === filter.key ? "border-[#14B8A6] bg-[#14B8A6] text-white" : "border-[#BFEAFF] bg-[#F4FBFF] text-[#0A2540]"
                                      }`}
                                    >
                                      {filter.label}
                                    </button>
                                  ))}
                                </div>
                              </section>
                              <AccountTimelinePanel account={selectedAccount} timelineEntries={callTimelineEntries} />
                            </div>
                          ) : emailWorkspaceTab === "notes" ? (
                            <div id="email-workspace-panel-notes" role="tabpanel" aria-labelledby="email-workspace-tab-notes" className="grid min-w-0 max-w-full gap-3">
                              <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Filters</p>
                                <div className="mt-3 flex flex-wrap gap-2">
                                  {[
                                    { key: "all" as const, label: "All" },
                                    { key: "pinned" as const, label: "Pinned Notes" },
                                    { key: "manager" as const, label: "Manager Notes" },
                                    { key: "collector" as const, label: "Collector Notes" },
                                    { key: "system" as const, label: "System Notes" },
                                  ].map((filter) => (
                                    <button
                                      key={filter.key}
                                      type="button"
                                      onClick={() => setCallNotesFilter(filter.key)}
                                      className={`focus-ring min-h-9 rounded-full border px-3 text-[11px] font-black uppercase tracking-[0.12em] ${
                                        callNotesFilter === filter.key ? "border-[#14B8A6] bg-[#14B8A6] text-white" : "border-[#BFEAFF] bg-[#F4FBFF] text-[#0A2540]"
                                      }`}
                                    >
                                      {filter.label}
                                    </button>
                                  ))}
                                </div>
                              </section>
                              {[...filteredCallNotes].sort((a, b) => {
                                const pinnedA = callPinnedNotes.some((note) => note.id === a.id) ? 1 : 0;
                                const pinnedB = callPinnedNotes.some((note) => note.id === b.id) ? 1 : 0;
                                return pinnedB - pinnedA || getDateTime(b.createdAt) - getDateTime(a.createdAt);
                              }).map((note) => (
                                <article key={note.id} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                  <p className="break-words text-sm font-semibold text-[#0A2540]">{note.noteType}</p>
                                  <p className="mt-1 break-words text-xs font-semibold uppercase tracking-[0.12em] text-[#14B8A6]">{formatNoteVisibility(note.visibility)} | {note.collector}</p>
                                  <p className="mt-3 break-words text-sm font-semibold leading-6 text-[#475569]">{note.content}</p>
                                </article>
                              ))}
                              {!filteredCallNotes.length ? <ManagerEmptyState title="No notes match" description={`No notes are currently available for ${selectedAccount.customerName}.`} action="Continue email review with account context visible." /> : null}
                            </div>
                          ) : emailWorkspaceTab === "communications" ? (
                            <div id="email-workspace-panel-communications" role="tabpanel" aria-labelledby="email-workspace-tab-communications" className="grid min-w-0 max-w-full gap-3">
                              <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Communication Context</p>
                                <p className="mt-1 text-sm font-semibold leading-6 text-[#475569]">Customer Emails, Internal Communications, Call Logs, Letters, and Statements are shown chronologically.</p>
                              </section>
                              {emailWorkspaceContextEvents.slice(0, 10).map((event) => (
                                <article key={event.id} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                  <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <p className="break-words text-sm font-semibold text-[#0A2540]">{event.title}</p>
                                      <p className="mt-1 break-words text-xs font-semibold uppercase tracking-[0.12em] text-[#14B8A6]">{event.label}</p>
                                    </div>
                                    <span className="rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-1 text-[11px] font-semibold text-[#475569]">{formatDate(event.date)}</span>
                                  </div>
                                  <p className="mt-3 break-words text-sm font-semibold leading-6 text-[#475569]">{previewNoteContent(event.detail)}</p>
                                </article>
                              ))}
                              {!emailWorkspaceContextEvents.length ? <ManagerEmptyState title="No communications captured" description={`No communications are currently attached to ${selectedAccount.customerName}.`} action="Send an email to create the first communication record." /> : null}
                            </div>
                          ) : emailWorkspaceTab === "promises" ? (
                            <div id="email-workspace-panel-promises" role="tabpanel" aria-labelledby="email-workspace-tab-promises" className="grid min-w-0 max-w-full gap-3">
                              <div className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Summary</p>
                                    <h5 className="mt-1 break-words text-lg font-semibold text-[#0A2540]">Promise context</h5>
                                  </div>
                                  <Link to="/admin/ar-recovery/promise-to-pay" className="focus-ring inline-flex min-h-10 items-center rounded-full border border-[#14B8A6] bg-[#14B8A6] px-4 text-sm font-black uppercase tracking-[0.12em] text-white">
                                    Open Promise Workspace
                                  </Link>
                                </div>
                              </div>
                              <CompactMetricStrip items={[
                                { label: "Promises", value: String(selectedAccountPromises.length) },
                                { label: "Active", value: String(selectedAccountPromises.filter((promise) => promiseMatchesFilter(promise, "active")).length) },
                                { label: "Broken", value: String(selectedAccountPromises.filter((promise) => promise.status === "Broken").length) },
                                { label: "Remaining", value: formatARRecoveryCurrency(selectedAccountPromises.reduce((total, promise) => total + promise.remainingBalanceCents, 0)) },
                              ]} />
                              {selectedAccountPromises.slice(0, 5).map((promise) => (
                                <article key={promise.id} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                  <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <p className="break-words text-lg font-semibold text-[#0A2540]">{formatARRecoveryCurrency(promise.amountCents)}</p>
                                      <p className="mt-1 break-words text-sm font-semibold leading-6 text-[#475569]">Promise date {formatDate(promise.promiseDate)} | {promise.promiseType}</p>
                                    </div>
                                    <span className={`rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] ${promiseStatusBadgeClass(promise.status)}`}>{promise.status}</span>
                                  </div>
                                  <div className="mt-3 grid gap-2 md:grid-cols-3">
                                    <WorkspaceField label="Collector" value={promise.collector || "Not Captured"} />
                                    <WorkspaceField label="Remaining" value={formatARRecoveryCurrency(promise.remainingBalanceCents)} />
                                    <WorkspaceField label="Next Action" value={promise.followUpDate ? `Follow up ${formatDate(promise.followUpDate)}` : "Not Captured"} />
                                  </div>
                                </article>
                              ))}
                              {promiseHistoryEntries.length ? (
                                <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">History</p>
                                  <div className="mt-3 grid gap-2">
                                    {promiseHistoryEntries.slice(0, 4).map((entry) => (
                                      <div key={entry.id} className="min-w-0 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] p-3">
                                        <p className="break-words text-sm font-semibold text-[#0A2540]">{entry.statusChange}</p>
                                        <p className="mt-1 break-words text-xs font-semibold uppercase tracking-[0.12em] text-[#475569]">{entry.date} | {entry.status}</p>
                                      </div>
                                    ))}
                                  </div>
                                </section>
                              ) : null}
                            </div>
                          ) : (
                            <div id="email-workspace-panel-statements" role="tabpanel" aria-labelledby="email-workspace-tab-statements" className="grid min-w-0 max-w-full gap-3">
                              <div className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Summary</p>
                                    <h5 className="mt-1 break-words text-lg font-semibold text-[#0A2540]">Statement context</h5>
                                  </div>
                                  <Link to="/admin/ar-recovery/statements" className="focus-ring inline-flex min-h-10 items-center rounded-full border border-[#14B8A6] bg-[#14B8A6] px-4 text-sm font-black uppercase tracking-[0.12em] text-white">
                                    Open Statement Workspace
                                  </Link>
                                </div>
                              </div>
                              <CompactMetricStrip items={[
                                { label: "Statements", value: String(accountStatements.length) },
                                { label: "Sent", value: String(accountStatements.filter((statement) => statement.status === "Sent").length) },
                                { label: "Delivered", value: String(accountStatements.filter((statement) => statement.deliveryStatus === "Delivered" || statement.status === "Delivered").length) },
                                { label: "Opened", value: String(accountStatements.filter((statement) => statement.openedStatus === "Opened" || statement.status === "Opened").length) },
                              ]} />
                              {accountStatements.slice(0, 5).map((statement) => (
                                <article key={statement.id} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                  <p className="break-words text-lg font-semibold text-[#0A2540]">{statement.statementNumber}</p>
                                  <p className="mt-1 break-words text-sm font-semibold leading-6 text-[#475569]">{statement.status} | {formatARRecoveryCurrency(statement.balanceCents)}</p>
                                </article>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </section>
                  </div>
                </aside>
              ) : activeWorkspacePanel === "tickler" && isTicklerDialogOpen ? (
                <aside
                  ref={workspacePanelRef}
                  tabIndex={-1}
                  className="fixed inset-0 z-[72] overflow-y-auto bg-[#02072C]/20 px-4 py-4 backdrop-blur-sm sm:px-6"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="tickler-workspace-title"
                >
                  <div className="mx-auto grid min-h-[calc(100vh-2rem)] w-full max-w-[1500px] grid-rows-[auto_1fr] overflow-hidden rounded-[2rem] border border-[#BFEAFF] bg-[#F7FCFF] shadow-[0_30px_90px_rgba(10,37,64,.18)]">
                    <header className="border-b border-[#DFF6FF] bg-white px-5 py-4 sm:px-6">
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="min-w-0">
                          <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#14B8A6]">Tickler Workspace</p>
                          <h3 id="tickler-workspace-title" className="mt-1 break-words text-2xl font-semibold text-[#0A2540]">Enterprise Follow-up Workspace</h3>
                          <p className="mt-1 max-w-3xl break-words text-sm font-semibold leading-6 text-[#475569]">
                            Schedule the next customer follow-up, confirm assignment, and preview the operational impact before saving.
                          </p>
                        </div>
                        <button type="button" onClick={requestCloseTicklerWorkspace} className="focus-ring rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold text-[#0A2540]">
                          Close
                        </button>
                      </div>
                      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                        <DetailMetric label="Customer" value={selectedAccount.customerName} note={selectedAccount.customerNumber} />
                        <DetailMetric label="Current Status" value={ticklerWorkspaceLifecycleStage} note={ticklerWorkspaceStatus} />
                        <DetailMetric label="Assigned Collector" value={ticklerForm.assignedTo || selectedAccount.assignedCollector || "Not Assigned"} note={canOverrideRecordOwnership ? "Manager override enabled" : "Collector workflow"} />
                        <DetailMetric label="Active Follow-ups" value={String(accountTicklers.filter((tickler) => !["Completed", "Cancelled"].includes(tickler.status)).length)} note="Open customer commitments" />
                      </div>
                    </header>

                    <div className="grid min-h-0 min-w-0 gap-0 lg:grid-cols-[minmax(0,0.44fr)_minmax(0,0.56fr)]">
                      <section className={`${ticklerWorkspaceMobilePane === "tickler" ? "block" : "hidden"} min-w-0 overflow-y-auto border-r border-[#DFF6FF] bg-white p-5 lg:block sm:p-6`}>
                        <div className="grid grid-cols-2 gap-2 lg:hidden" role="tablist" aria-label="Tickler workspace mobile panes">
                          {[
                            { key: "tickler", label: "Follow-up" },
                            { key: "account", label: "Context" },
                          ].map((pane) => (
                            <button
                              key={pane.key}
                              type="button"
                              role="tab"
                              aria-selected={ticklerWorkspaceMobilePane === pane.key}
                              onClick={() => setTicklerWorkspaceMobilePane(pane.key as "tickler" | "account")}
                              className={`focus-ring rounded-full border px-3 py-2 text-[11px] font-black uppercase tracking-[0.12em] ${ticklerWorkspaceMobilePane === pane.key ? "border-[#14B8A6] bg-[#14B8A6] text-white" : "border-[#BFEAFF] bg-[#F4FBFF] text-[#0A2540]"}`}
                            >
                              {pane.label}
                            </button>
                          ))}
                        </div>

                        {ticklerSuccessSummary ? (
                          <section className="mt-4 rounded-2xl border border-[rgba(34,197,94,.35)] bg-[rgba(240,253,244,.95)] p-4" role="status" aria-live="polite">
                            <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#166534]">Follow-up Scheduled Successfully</p>
                            <div className="mt-3 grid gap-2 sm:grid-cols-2">
                              <DetailMetric label="Next Action" value={ticklerSuccessSummary.nextAction} />
                              <DetailMetric label="Due Date" value={formatCapturedDate(ticklerSuccessSummary.dueDate)} />
                              <DetailMetric label="Priority" value={ticklerSuccessSummary.priority} />
                              <DetailMetric label="Assigned Collector" value={ticklerSuccessSummary.assignedCollector} />
                              <DetailMetric label="Updates" value="Complete" note="Timeline, Recovery Activity, Follow-up Queue" />
                            </div>
                            <div className="mt-4 flex flex-wrap gap-2">
                              <button type="button" onClick={requestCloseTicklerWorkspace} className="focus-ring min-h-9 rounded-full border border-[rgba(34,197,94,.32)] bg-white px-4 text-[11px] font-black uppercase tracking-[0.12em] text-[#166534]">Close</button>
                              <button type="button" onClick={() => openScheduleFollowUpWorkflow()} className="focus-ring min-h-9 rounded-full border border-[#14B8A6] bg-[#14B8A6] px-4 text-[11px] font-black uppercase tracking-[0.12em] text-white">Schedule Another Follow-up</button>
                            </div>
                          </section>
                        ) : null}

                        <section className="mt-4 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
                          <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Follow-up Summary</p>
                          <div className="mt-3 grid gap-3 sm:grid-cols-2">
                            <DetailMetric label="Customer" value={selectedAccount.customerName} />
                            <DetailMetric label="Next Action" value={ticklerForm.followUpType || "Not Selected"} />
                            <DetailMetric label="Due Date" value={ticklerForm.dueDate ? formatCapturedDate(ticklerForm.dueDate) : "Not Captured"} note={ticklerWorkspaceDueUrgency} />
                            <DetailMetric label="Priority" value={ticklerWorkspacePriorityDisplay} note={ticklerWorkspacePriorityRank} />
                            <DetailMetric label="Reason" value={ticklerWorkspaceReason} />
                            <DetailMetric label="Assigned Collector" value={ticklerForm.assignedTo || selectedAccount.assignedCollector || "Not Assigned"} />
                            <DetailMetric label="Current Status" value={ticklerWorkspaceLifecycleStage} note={ticklerWorkspaceStatus} />
                          </div>
                        </section>

                        <form
                          className="mt-4 grid gap-4"
                          onSubmit={(event) => {
                            event.preventDefault();
                            handleSaveTickler();
                          }}
                        >
                          <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                            <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Next Action</p>
                            <div className="mt-3 grid gap-3">
                              <label className="grid gap-1.5">
                                <span className="text-xs font-black uppercase tracking-[0.14em] text-[#0A2540]">Next Action</span>
                                <select
                                  value={ticklerForm.followUpType}
                                  onChange={(event) => {
                                    setTicklerFormError("");
                                    setTicklerSuccessSummary(null);
                                    setTicklerForm((current) => ({ ...current, followUpType: event.target.value as ARRecoveryTicklerFormState["followUpType"] }));
                                  }}
                                  className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
                                  required
                                >
                                  <option value="">Select follow-up action</option>
                                  {FOLLOW_UP_TYPE_OPTIONS.map((type) => <option key={type} value={type}>{type}</option>)}
                                </select>
                              </label>

                              <div className="grid gap-3 sm:grid-cols-2">
                                <label className="grid gap-1.5">
                                  <span className="text-xs font-black uppercase tracking-[0.14em] text-[#0A2540]">Due Date</span>
                                  <input type="date" value={ticklerForm.dueDate} onChange={(event) => { setTicklerFormError(""); setTicklerSuccessSummary(null); setTicklerForm((current) => ({ ...current, dueDate: event.target.value })); }} className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]" required />
                                  <span className="text-xs font-semibold leading-5 text-[#475569]">{ticklerWorkspaceDueUrgency}</span>
                                </label>
                                <label className="grid gap-1.5">
                                  <span className="text-xs font-black uppercase tracking-[0.14em] text-[#0A2540]">Due Time</span>
                                  <input type="time" value={ticklerForm.dueTime} onChange={(event) => { setTicklerFormError(""); setTicklerSuccessSummary(null); setTicklerForm((current) => ({ ...current, dueTime: event.target.value })); }} className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]" />
                                </label>
                              </div>

                              {ticklerWorkspaceSuggestedDates.length ? (
                                <section className="rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] p-3" aria-label="Smart follow-up date suggestions">
                                  <div className="flex flex-wrap items-start justify-between gap-2">
                                    <div className="min-w-0">
                                      <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Smart Follow-up Suggestions</p>
                                      <p className="mt-1 break-words text-xs font-semibold leading-5 text-[#475569]">Suggestions set the due date only. Collectors can override before saving.</p>
                                    </div>
                                  </div>
                                  <div className="mt-3 flex flex-wrap gap-2">
                                    {ticklerWorkspaceSuggestedDates.map((suggestion) => (
                                      <button
                                        key={`${suggestion.label}-${suggestion.date}`}
                                        type="button"
                                        onClick={() => {
                                          setTicklerFormError("");
                                          setTicklerSuccessSummary(null);
                                          setTicklerForm((current) => ({ ...current, dueDate: suggestion.date }));
                                        }}
                                        className="focus-ring rounded-full border border-[#BFEAFF] bg-white px-3 py-2 text-left text-[11px] font-black uppercase tracking-[0.12em] text-[#0A2540] hover:border-[#14B8A6]"
                                      >
                                        {suggestion.label}: {suggestion.note}
                                      </button>
                                    ))}
                                  </div>
                                </section>
                              ) : null}

                              <label className="grid gap-1.5">
                                <span className="text-xs font-black uppercase tracking-[0.14em] text-[#0A2540]">Priority</span>
                                <select
                                  value={ticklerForm.priority}
                                  onChange={(event) => { setTicklerFormError(""); setTicklerSuccessSummary(null); setTicklerForm((current) => ({ ...current, priority: event.target.value as ARRecoveryTaskPriority })); }}
                                  className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
                                  required
                                >
                                  <option value="Low">1 Low</option>
                                  <option value="Normal">2 Medium</option>
                                  <option value="High">3 High</option>
                                  <option value="Critical">4 Urgent</option>
                                </select>
                              </label>

                              <label className="grid gap-1.5">
                                <span className="text-xs font-black uppercase tracking-[0.14em] text-[#0A2540]">Reason</span>
                                <textarea value={ticklerForm.notes} onChange={(event) => { setTicklerFormError(""); setTicklerSuccessSummary(null); setTicklerForm((current) => ({ ...current, notes: event.target.value })); }} placeholder="Why is this follow-up required?" className="focus-ring min-h-24 rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold leading-6 text-[#0A2540]" />
                              </label>

                              <label className="grid gap-1.5">
                                <span className="text-xs font-black uppercase tracking-[0.14em] text-[#0A2540]">Assigned Collector</span>
                                <input list="ar-internal-users" value={ticklerForm.assignedTo} onChange={(event) => { setTicklerFormError(""); setTicklerSuccessSummary(null); setTicklerForm((current) => ({ ...current, assignedTo: event.target.value })); }} placeholder={selectedAccount.assignedCollector || "Collector"} className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]" required />
                              </label>

                              <div className="grid gap-3 sm:grid-cols-2">
                                <label className="grid gap-1.5">
                                  <span className="text-xs font-black uppercase tracking-[0.14em] text-[#0A2540]">Related Invoice</span>
                                  <select value={ticklerForm.relatedInvoice} onChange={(event) => { setTicklerFormError(""); setTicklerSuccessSummary(null); setTicklerForm((current) => ({ ...current, relatedInvoice: event.target.value })); }} className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]">
                                    <option value="">No invoice selected</option>
                                    {callWorkspaceInvoices.map((invoice) => <option key={invoice.invoiceNumber} value={invoice.invoiceNumber}>{invoice.invoiceNumber} - {formatARRecoveryCurrency(invoice.currentBalanceCents)}</option>)}
                                  </select>
                                </label>
                                <label className="grid gap-1.5">
                                  <span className="text-xs font-black uppercase tracking-[0.14em] text-[#0A2540]">Related Promise</span>
                                  <select value={ticklerForm.relatedPromise} onChange={(event) => { setTicklerFormError(""); setTicklerSuccessSummary(null); setTicklerForm((current) => ({ ...current, relatedPromise: event.target.value })); }} className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]">
                                    <option value="">No promise selected</option>
                                    {selectedAccountPromises.map((promise) => <option key={promise.id} value={promise.id}>{formatARRecoveryCurrency(promise.remainingBalanceCents)} - {promise.status}</option>)}
                                  </select>
                                </label>
                              </div>

                              <label className="grid gap-1.5">
                                <span className="text-xs font-black uppercase tracking-[0.14em] text-[#0A2540]">Customer Notes</span>
                                <textarea value={ticklerForm.reminderNotes} onChange={(event) => { setTicklerFormError(""); setTicklerSuccessSummary(null); setTicklerForm((current) => ({ ...current, reminderNotes: event.target.value })); }} placeholder="Customer-facing context for the next touchpoint." className="focus-ring min-h-20 rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold leading-6 text-[#0A2540]" />
                              </label>

                              <label className="grid gap-1.5">
                                <span className="text-xs font-black uppercase tracking-[0.14em] text-[#0A2540]">Collector Notes</span>
                                <textarea value={ticklerForm.outcome} onChange={(event) => { setTicklerFormError(""); setTicklerSuccessSummary(null); setTicklerForm((current) => ({ ...current, outcome: event.target.value })); }} placeholder="Internal collector notes for this follow-up." className="focus-ring min-h-20 rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold leading-6 text-[#0A2540]" />
                              </label>
                            </div>
                          </section>

                          <section className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Follow-up Ready</p>
                                <p className="mt-1 break-words text-sm font-semibold leading-6 text-[#475569]">{ticklerWorkspaceCanSave ? "All required follow-up checks are complete." : "Complete the missing readiness checks before saving."}</p>
                              </div>
                              <span className={`rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] ${ticklerWorkspaceCanSave ? "border-[rgba(34,197,94,.32)] bg-[rgba(240,253,244,.95)] text-[#166534]" : "border-[rgba(245,158,11,.32)] bg-[rgba(255,251,235,.95)] text-[#92400E]"}`}>
                                {ticklerWorkspaceCanSave ? "Ready" : "Needs Details"}
                              </span>
                            </div>
                            <div className="mt-3 grid gap-2">
                              {ticklerWorkspaceReadinessItems.map((item) => (
                                <div key={item.label} className="flex min-w-0 items-start gap-3 rounded-xl border border-[#BFEAFF] bg-white p-3">
                                  <span className={`mt-0.5 rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] ${item.ready ? "border-[rgba(34,197,94,.32)] text-[#166534]" : "border-[rgba(245,158,11,.32)] text-[#92400E]"}`}>{item.ready ? "OK" : "Needs"}</span>
                                  <div className="min-w-0">
                                    <p className="break-words text-sm font-semibold text-[#0A2540]">{item.label}</p>
                                    <p className="mt-1 break-words text-xs font-semibold leading-5 text-[#475569]">{item.detail}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </section>

                          <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                            <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Follow-up Impact Preview</p>
                            <p className="mt-1 text-sm font-semibold leading-6 text-[#475569]">Saving this follow-up will update:</p>
                            <div className="mt-3 grid gap-2 sm:grid-cols-2">
                              {ticklerWorkspaceImpactItems.map((item) => (
                                <div key={item} className="rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2 text-sm font-semibold text-[#0A2540]">{item}</div>
                              ))}
                            </div>
                          </section>

                          {ticklerFormError ? <div className="rounded-xl border border-[#FCA5A5] bg-[#FEF2F2] p-3 text-sm font-semibold leading-6 text-[#B91C1C]" role="alert">{ticklerFormError}</div> : null}

                          <div className="flex flex-wrap items-center justify-end gap-2">
                            <button type="button" onClick={requestCloseTicklerWorkspace} className="focus-ring min-h-10 rounded-full border border-[#BFEAFF] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#475569]">Cancel</button>
                            <button type="submit" disabled={!ticklerWorkspaceCanSave} className={`focus-ring min-h-10 rounded-full px-5 text-sm font-black uppercase tracking-[0.12em] text-white ${ticklerWorkspaceCanSave ? "bg-[#14B8A6] shadow-[0_14px_30px_rgba(20,184,166,.22)] hover:bg-[#0F766E]" : "cursor-not-allowed bg-[#94A3B8]"}`}>
                              Save Follow-up
                            </button>
                          </div>
                        </form>
                      </section>

                      <section className={`${ticklerWorkspaceMobilePane === "account" ? "block" : "hidden"} min-w-0 overflow-y-auto bg-[#F7FCFF] p-5 lg:block sm:p-6`}>
                        <div className="grid grid-cols-2 gap-2 lg:hidden" role="tablist" aria-label="Tickler workspace mobile panes">
                          {[
                            { key: "tickler", label: "Follow-up" },
                            { key: "account", label: "Context" },
                          ].map((pane) => (
                            <button key={pane.key} type="button" role="tab" aria-selected={ticklerWorkspaceMobilePane === pane.key} onClick={() => setTicklerWorkspaceMobilePane(pane.key as "tickler" | "account")} className={`focus-ring rounded-full border px-3 py-2 text-[11px] font-black uppercase tracking-[0.12em] ${ticklerWorkspaceMobilePane === pane.key ? "border-[#14B8A6] bg-[#14B8A6] text-white" : "border-[#BFEAFF] bg-white text-[#0A2540]"}`}>{pane.label}</button>
                          ))}
                        </div>
                        <div className="mt-4 flex max-w-full gap-2 overflow-x-auto overscroll-x-contain pb-1" role="tablist" aria-label="Tickler account workspace tabs">
                          {ticklerWorkspaceTabs.map((tab) => (
                            <button key={tab.key} id={`tickler-workspace-tab-${tab.key}`} type="button" role="tab" aria-selected={ticklerWorkspaceTab === tab.key} aria-controls={`tickler-workspace-panel-${tab.key}`} onClick={() => setTicklerWorkspaceTab(tab.key)} className={`focus-ring inline-flex min-h-9 shrink-0 items-center rounded-full border px-3 text-[11px] font-black uppercase tracking-[0.12em] transition ${ticklerWorkspaceTab === tab.key ? "border-[#14B8A6] bg-[#14B8A6] text-white" : "border-[#BFEAFF] bg-white text-[#0A2540] hover:border-[#14B8A6]"}`}>{tab.label}</button>
                          ))}
                        </div>
                        <div className="mt-4 min-w-0">
                          {ticklerWorkspaceTab === "overview" ? (
                            <div id="tickler-workspace-panel-overview" role="tabpanel" aria-labelledby="tickler-workspace-tab-overview" className="grid min-w-0 max-w-full gap-4">
                              <CompactMetricStrip items={[
                                { label: "Balance", value: formatARRecoveryCurrency(selectedAccount.openBalanceCents) },
                                { label: "Aging", value: selectedAccount.agingSummary },
                                { label: "Status", value: selectedAccount.recoveryStatus },
                                { label: "Collector", value: selectedAccount.assignedCollector },
                              ]} />
                              {ticklerWorkspaceActiveFollowUp ? (
                                <article className="min-w-0 rounded-2xl border border-[#BFEAFF] bg-white p-4">
                                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Current Follow-up</p>
                                  <h4 className="mt-1 break-words text-lg font-semibold text-[#0A2540]">{ticklerWorkspaceActiveFollowUp.title}</h4>
                                  <p className="mt-2 break-words text-sm font-semibold leading-6 text-[#475569]">{ticklerWorkspaceActiveFollowUp.status} | Due {formatCapturedDate(ticklerWorkspaceActiveFollowUp.dueDate)} | {ticklerWorkspaceActiveFollowUp.assignedCollector}</p>
                                </article>
                              ) : <ManagerEmptyState title="No active follow-up" description="Create a follow-up commitment when the next customer action needs ownership." action="Use Follow-up Workspace to schedule the next action." />}
                            </div>
                          ) : ticklerWorkspaceTab === "invoices" ? (
                            <div id="tickler-workspace-panel-invoices" role="tabpanel" aria-labelledby="tickler-workspace-tab-invoices" className="grid min-w-0 max-w-full gap-3">{callWorkspaceInvoices.map((invoice) => <article key={invoice.invoiceNumber} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4"><p className="break-words text-sm font-semibold text-[#0A2540]">{invoice.invoiceNumber}</p><p className="mt-1 break-words text-xs font-semibold uppercase tracking-[0.12em] text-[#14B8A6]">{formatARRecoveryCurrency(invoice.currentBalanceCents)} | {invoice.status} | Due {formatCapturedDate(invoice.dueDate)}</p></article>)}</div>
                          ) : ticklerWorkspaceTab === "timeline" ? (
                            <div id="tickler-workspace-panel-timeline" role="tabpanel" aria-labelledby="tickler-workspace-tab-timeline" className="grid min-w-0 max-w-full gap-4"><AccountTimelinePanel account={selectedAccount} timelineEntries={callTimelineEntries} /></div>
                          ) : ticklerWorkspaceTab === "notes" ? (
                            <div id="tickler-workspace-panel-notes" role="tabpanel" aria-labelledby="tickler-workspace-tab-notes" className="grid min-w-0 max-w-full gap-3">{filteredCallNotes.map((note) => <article key={note.id} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4"><p className="break-words text-sm font-semibold text-[#0A2540]">{note.noteType}</p><p className="mt-3 break-words text-sm font-semibold leading-6 text-[#475569]">{note.content}</p></article>)}</div>
                          ) : ticklerWorkspaceTab === "communications" ? (
                            <div id="tickler-workspace-panel-communications" role="tabpanel" aria-labelledby="tickler-workspace-tab-communications" className="grid min-w-0 max-w-full gap-3">{emailWorkspaceContextEvents.slice(0, 10).map((event) => <article key={event.id} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4"><p className="break-words text-sm font-semibold text-[#0A2540]">{event.title}</p><p className="mt-1 break-words text-xs font-semibold uppercase tracking-[0.12em] text-[#14B8A6]">{event.label}</p><p className="mt-3 break-words text-sm font-semibold leading-6 text-[#475569]">{previewNoteContent(event.detail)}</p></article>)}</div>
                          ) : ticklerWorkspaceTab === "promises" ? (
                            <div id="tickler-workspace-panel-promises" role="tabpanel" aria-labelledby="tickler-workspace-tab-promises" className="grid min-w-0 max-w-full gap-3">{selectedAccountPromises.map((promise) => <article key={promise.id} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4"><p className="break-words text-sm font-semibold text-[#0A2540]">{formatARRecoveryCurrency(promise.remainingBalanceCents)} remaining</p><p className="mt-1 break-words text-xs font-semibold uppercase tracking-[0.12em] text-[#14B8A6]">{promise.status} | {formatCapturedDate(promise.promiseDate)}</p></article>)}</div>
                          ) : ticklerWorkspaceTab === "statements" ? (
                            <div id="tickler-workspace-panel-statements" role="tabpanel" aria-labelledby="tickler-workspace-tab-statements" className="grid min-w-0 max-w-full gap-3">{accountStatements.map((statement) => <article key={statement.id} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4"><p className="break-words text-sm font-semibold text-[#0A2540]">{statement.statementNumber}</p><p className="mt-1 break-words text-xs font-semibold uppercase tracking-[0.12em] text-[#14B8A6]">{statement.status} | {formatCapturedDate(statement.createdAt)}</p></article>)}</div>
                          ) : (
                            <div id="tickler-workspace-panel-payments" role="tabpanel" aria-labelledby="tickler-workspace-tab-payments" className="grid min-w-0 max-w-full gap-3">{paymentWorkflows.filter((payment) => payment.accountId === selectedAccount.id).map((payment) => <article key={payment.id} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4"><p className="break-words text-sm font-semibold text-[#0A2540]">{formatARRecoveryCurrency(Number(payment.amountCents || 0))}</p><p className="mt-1 break-words text-xs font-semibold uppercase tracking-[0.12em] text-[#14B8A6]">{String(payment.lifecycleStatus || "Recorded")} | {formatCapturedDate(String(payment.paymentDate || payment.createdAt || ""))}</p></article>)}</div>
                          )}
                        </div>
                      </section>
                    </div>
                  </div>
                </aside>
              ) : activeWorkspacePanel === "letter" && isLetterDialogOpen ? (
                <>
                  <ARRecoveryDocumentExecutionShell
                    ref={workspacePanelRef}
                    mode="letter"
                    account={selectedAccount}
                    clientProfile={workspace.clientProfile}
                    queueLabel={queueLabel}
                    queuePosition={selectedAccountQueuePosition}
                    queueCount={queueAccounts.length || snapshot.filteredAccounts.length || 1}
                    letterForm={letterForm}
                    statementForm={statementForm}
                    setLetterForm={setLetterForm}
                    setStatementForm={setStatementForm}
                    selectedAccountPromises={selectedAccountPromises}
                    letterFormError={letterFormError}
                    statementFormError={statementFormError}
                    successMessage={letterSuccessMessage}
                    letterSuccessSummary={letterSuccessSummary}
                    statementCompletionSummary={null}
                    statementFailureSummary={null}
                    workspacePanelMessage={workspacePanelMessage}
                    mobilePane={documentExecutionMobilePane}
                    setMobilePane={setDocumentExecutionMobilePane}
                    isSubmitting={documentExecutionSubmitting === "letter"}
                    historyButtonRef={letterHistoryButtonRef}
                    onHistory={() => setIsLetterHistoryOpen(true)}
                    onPrint={() => handlePrintLetterExecution("print")}
                    onPdf={() => handlePrintLetterExecution("pdf")}
                    onGenerateAnotherLetter={resetLetterWorkspaceForAnotherLetter}
                    onSendAnotherStatement={resetStatementWorkspaceForAnotherStatement}
                    onOpenCustomerTimeline={openSelectedAccountTimelineFromStatementWorkspace}
                    onSend={async () => {
                      if (documentExecutionSubmitting) return;
                      setDocumentExecutionSubmitting("letter");
                      try {
                        await handleGenerateLetter();
                      } finally {
                        setDocumentExecutionSubmitting(null);
                      }
                    }}
                    onClose={closeWorkspacePanel}
                  />
                  {isLetterHistoryOpen ? (
                    <ARRecoveryLetterExecutionHistoryPanel
                      account={selectedAccount}
                      letters={accountLetters}
                      activityEvents={accountLetterEvents}
                      onClose={() => {
                        setIsLetterHistoryOpen(false);
                        window.setTimeout(() => {
                          letterHistoryButtonRef.current?.focus({ preventScroll: true });
                        }, 0);
                      }}
                    />
                  ) : null}
                </>
              ) : activeWorkspacePanel === "statement" && isStatementDialogOpen ? (
                <>
                  <ARRecoveryDocumentExecutionShell
                    ref={workspacePanelRef}
                    mode="statement"
                    account={selectedAccount}
                    clientProfile={workspace.clientProfile}
                    queueLabel={queueLabel}
                    queuePosition={selectedAccountQueuePosition}
                    queueCount={queueAccounts.length || snapshot.filteredAccounts.length || 1}
                    letterForm={letterForm}
                    statementForm={statementForm}
                    setLetterForm={setLetterForm}
                    setStatementForm={setStatementForm}
                    selectedAccountPromises={selectedAccountPromises}
                    letterFormError={letterFormError}
                    statementFormError={statementFormError}
                    successMessage={statementSuccessMessage}
                    letterSuccessSummary={null}
                    statementCompletionSummary={statementCompletionSummary}
                    statementFailureSummary={statementFailureSummary}
                    workspacePanelMessage={workspacePanelMessage}
                    mobilePane={documentExecutionMobilePane}
                    setMobilePane={setDocumentExecutionMobilePane}
                    isSubmitting={documentExecutionSubmitting === "statement"}
                    historyButtonRef={statementHistoryButtonRef}
                    onHistory={() => setIsStatementHistoryOpen(true)}
                    onPrint={() => handlePrintStatementExecution("print")}
                    onPdf={() => handlePrintStatementExecution("pdf")}
                    onGenerateAnotherLetter={resetLetterWorkspaceForAnotherLetter}
                    onSendAnotherStatement={resetStatementWorkspaceForAnotherStatement}
                    onOpenCustomerTimeline={openSelectedAccountTimelineFromStatementWorkspace}
                    onSend={async () => {
                      if (documentExecutionSubmitting) return;
                      setDocumentExecutionSubmitting("statement");
                      try {
                        await handleGenerateStatement();
                      } finally {
                        setDocumentExecutionSubmitting(null);
                      }
                    }}
                    onClose={closeWorkspacePanel}
                  />
                  {isStatementHistoryOpen ? (
                    <ARRecoveryStatementExecutionHistoryPanel
                      account={selectedAccount}
                      statements={accountStatements}
                      activityEvents={accountStatementEvents}
                      onClose={() => {
                        setIsStatementHistoryOpen(false);
                        window.setTimeout(() => {
                          statementHistoryButtonRef.current?.focus({ preventScroll: true });
                        }, 0);
                      }}
                    />
                  ) : null}
                </>
              ) : activeWorkspacePanel === "escalate" ? (
                <ARRecoveryEnterpriseWorkspacePanel
                  ref={workspacePanelRef}
                  eyebrow="Recovery Workbench Decision"
                  title={`Review Escalation: ${selectedAccount.customerName}`}
                  onClose={accountRecoveryLaunchState?.accountRecoveryLaunch?.managerCommandReturnContext || accountRecoveryLaunchState?.managerCommandReturnContext ? returnToManagerCommand : closeWorkspacePanel}
                  closeLabel={accountRecoveryLaunchState?.accountRecoveryLaunch?.managerCommandReturnContext || accountRecoveryLaunchState?.managerCommandReturnContext ? "Return to Manager Command" : "Close"}
                >
                  {renderEscalationDecisionWorkspace()}
                </ARRecoveryEnterpriseWorkspacePanel>
              ) : activeWorkspacePanel ? (
                <ARRecoveryEnterpriseWorkspacePanel
                  ref={workspacePanelRef}
                  contentRef={workspacePanelBodyRef}
                  eyebrow={getWorkspacePanelEyebrow()}
                  title={getWorkspacePanelTitle()}
                  onClose={closeWorkspacePanel}
                >
              <div
                tabIndex={-1}
                className={AR_RECOVERY_ENTERPRISE_PANEL_GRID_CLASS}
              >
              {governanceMessage ? (
                <div className="rounded-2xl border border-[rgba(248,113,113,.24)] bg-[rgba(248,113,113,.08)] px-4 py-3 text-sm font-semibold leading-6 text-[#FCA5A5] xl:col-span-2">
                  {governanceMessage}
                </div>
              ) : null}

              {workspacePanelMessage ? (
                <div className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] px-4 py-3 text-sm font-semibold leading-6 text-[#0A2540] xl:col-span-2">
                  {workspacePanelMessage}
                </div>
              ) : null}

              {accountEditSuccessMessage || callSuccessMessage || noteSuccessMessage || communicationSuccessMessage || taskSuccessMessage || promiseSuccessMessage || ticklerSuccessMessage || letterSuccessMessage || statementSuccessMessage || documentSuccessMessage ? (
                <div className="rounded-2xl border border-[rgba(34,197,94,.24)] bg-[rgba(34,197,94,.08)] px-4 py-3 text-sm font-semibold leading-6 text-[#DCFCE7] xl:col-span-2">
                  {accountEditSuccessMessage || callSuccessMessage || noteSuccessMessage || communicationSuccessMessage || taskSuccessMessage || promiseSuccessMessage || ticklerSuccessMessage || letterSuccessMessage || statementSuccessMessage || documentSuccessMessage}
                </div>
              ) : null}

              {!isManagerCommandNoteWorkspace ? (
              <div className="ar-workspace-surface min-w-0 overflow-hidden rounded-[1.5rem] border p-4 xl:col-span-2">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <ARLightLineIcon compact icon={<IdCard className="size-5" aria-hidden="true" />} />
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-[#0A2540]">Account Information</p>
                      <p className="mt-1 text-xs font-normal text-[#475569]">Shared account record. Updates are attributed to the authenticated AR OS user.</p>
                    </div>
                  </div>
                  {canWriteOperationalRecords ? (
                    <div className="flex flex-wrap gap-2">
                      {isAccountEditMode ? (
                        <>
                          <button type="button" onClick={handleSaveAccountEdit} className="focus-ring min-h-9 rounded-full border border-[rgba(34,197,94,.32)] bg-[rgba(34,197,94,.12)] px-4 text-xs font-black uppercase tracking-[0.12em] text-[#86EFAC]">
                            Save Changes
                          </button>
                          <button type="button" onClick={handleCancelAccountEdit} className="focus-ring min-h-9 rounded-full border border-[rgba(148,163,184,.28)] bg-[rgba(148,163,184,.08)] px-4 text-xs font-black uppercase tracking-[0.12em] text-[#CBD5E1]">
                            Cancel
                          </button>
                        </>
                      ) : (
                        <EditActionButton label="Edit Account" onClick={handleStartAccountEdit} />
                      )}
                    </div>
                  ) : null}
                </div>
                {accountEditError ? (
                  <div className="mt-3 rounded-2xl border border-[rgba(248,113,113,.24)] bg-[rgba(248,113,113,.08)] px-4 py-3 text-sm font-semibold text-[#FCA5A5]">
                    {accountEditError}
                  </div>
                ) : null}
                {isAccountEditMode ? (
                  <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    <AccountInfoEditField label="Customer Name" value={accountEditForm.customerName} onChange={(value) => setAccountEditForm((current) => ({ ...current, customerName: value }))} />
                    <AccountInfoEditField label="Customer Number" value={accountEditForm.customerNumber} onChange={(value) => setAccountEditForm((current) => ({ ...current, customerNumber: value }))} />
                    <AccountInfoEditField label="Account Number" value={accountEditForm.accountNumber} onChange={(value) => setAccountEditForm((current) => ({ ...current, accountNumber: value }))} />
                    <AccountInfoEditField label="Client Company" value={accountEditForm.clientCompany} onChange={(value) => setAccountEditForm((current) => ({ ...current, clientCompany: value }))} />
                    <AccountInfoEditField label="Assigned Collector" value={accountEditForm.assignedCollector} onChange={(value) => setAccountEditForm((current) => ({ ...current, assignedCollector: value }))} options={assignedCollectorOptions} inputRef={assignedCollectorEditInputRef} />
                    <AccountInfoEditField label="Assigned Manager" value={accountEditForm.assignedManager} onChange={(value) => setAccountEditForm((current) => ({ ...current, assignedManager: value }))} list="ar-internal-users" />
                    <AccountInfoEditField label="Account Status" value={accountEditForm.accountStatus} onChange={(value) => setAccountEditForm((current) => ({ ...current, accountStatus: value as ARRecoveryAccountEditFormState["accountStatus"] }))} options={ACCOUNT_STATUS_OPTIONS} />
                    <AccountInfoEditField label="Current Balance" value={accountEditForm.currentBalance} onChange={(value) => setAccountEditForm((current) => ({ ...current, currentBalance: value }))} type="number" />
                    <AccountInfoEditField label="Aging Bucket" value={accountEditForm.agingBucket} onChange={(value) => setAccountEditForm((current) => ({ ...current, agingBucket: value }))} />
                    <AccountInfoEditField label="Promise Status" value={accountEditForm.promiseStatus} onChange={(value) => setAccountEditForm((current) => ({ ...current, promiseStatus: value }))} />
                    <AccountInfoEditField label="Primary Contact" value={accountEditForm.primaryContact} onChange={(value) => setAccountEditForm((current) => ({ ...current, primaryContact: value }))} />
                    <AccountInfoEditField label="Contact Title" value={accountEditForm.contactTitle} onChange={(value) => setAccountEditForm((current) => ({ ...current, contactTitle: value }))} />
                    <AccountInfoEditField label="Phone" value={accountEditForm.phone} onChange={(value) => setAccountEditForm((current) => ({ ...current, phone: value }))} />
                    <AccountInfoEditField label="Email" value={accountEditForm.email} onChange={(value) => setAccountEditForm((current) => ({ ...current, email: value }))} type="email" />
                    <AccountInfoEditField label="Preferred Contact Method" value={accountEditForm.preferredContactMethod} onChange={(value) => setAccountEditForm((current) => ({ ...current, preferredContactMethod: value }))} options={CONTACT_METHOD_OPTIONS} />
                    <AccountInfoEditField label="Additional Contacts" value={accountEditForm.additionalContacts} onChange={(value) => setAccountEditForm((current) => ({ ...current, additionalContacts: value }))} />
                    <AccountInfoEditField label="Collector Owner" value={accountEditForm.collectorOwner} onChange={(value) => setAccountEditForm((current) => ({ ...current, collectorOwner: value }))} list="ar-internal-users" />
                    <AccountInfoEditField label="Hold Status" value={accountEditForm.holdStatus} onChange={(value) => setAccountEditForm((current) => ({ ...current, holdStatus: value }))} options={HOLD_STATUS_OPTIONS} />
                    <AccountInfoEditField label="Dispute Status" value={accountEditForm.disputeStatus} onChange={(value) => setAccountEditForm((current) => ({ ...current, disputeStatus: value }))} options={DISPUTE_STATUS_OPTIONS} />
                    <AccountInfoEditField label="Next Follow-Up Date" value={accountEditForm.nextFollowUpDate} onChange={(value) => setAccountEditForm((current) => ({ ...current, nextFollowUpDate: value }))} type="date" />
                    <div className="md:col-span-2 xl:col-span-3">
                      <AccountInfoEditField label="Recovery Instructions" value={accountEditForm.recoveryInstructions} onChange={(value) => setAccountEditForm((current) => ({ ...current, recoveryInstructions: value }))} multiline />
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    <DetailMetric label="Customer name" value={selectedAccount.customerName} />
                    <DetailMetric label="Customer number" value={selectedAccount.customerNumber} />
                    <DetailMetric label="Account number" value={editableSelectedAccount?.accountNumber || selectedAccount.customerNumber} />
                    <DetailMetric label="Client company" value={editableSelectedAccount?.clientCompany || snapshot.workspace.company.companyName} />
                    <DetailMetric label="Assigned collector" value={selectedAccount.assignedCollector} />
                    <DetailMetric label="Assigned manager" value={editableSelectedAccount?.assignedManager || "Not Captured"} />
                    <DetailMetric label="Account status" value={selectedAccount.recoveryStatus} />
                    <DetailMetric label="Current balance" value={formatARRecoveryCurrency(selectedAccount.openBalanceCents)} />
                    <DetailMetric label="Aging summary" value={selectedAccount.agingSummary} />
                    <DetailMetric label="Promise status" value={editableSelectedAccount?.promiseStatus || (selectedAccount.promiseToPayCount ? "Open promise" : "No promise on file")} />
                    <DetailMetric label="Primary contact" value={selectedAccount.contactName} />
                    <DetailMetric label="Contact title" value={editableSelectedAccount?.contactTitle || "Not Captured"} />
                    <DetailMetric label="Phone" value={selectedAccount.contactPhone} />
                    <DetailMetric label="Email" value={selectedAccount.contactEmail} />
                    <DetailMetric label="Preferred contact" value={editableSelectedAccount?.preferredContactMethod || "Email + Phone"} />
                    <DetailMetric label="Additional contacts" value={editableSelectedAccount?.additionalContacts || "Not Captured"} />
                    <DetailMetric label="Collector owner" value={editableSelectedAccount?.collectorOwner || selectedAccount.assignedCollector} />
                    <DetailMetric label="Hold status" value={editableSelectedAccount?.holdStatus || "No Hold"} />
                    <DetailMetric label="Dispute status" value={editableSelectedAccount?.disputeStatus || (selectedAccount.escalationStatus === "None" ? "No Dispute" : "Under Review")} />
                    <DetailMetric label="Next follow-up" value={editableSelectedAccount?.nextFollowUpDate ? formatCapturedDate(editableSelectedAccount.nextFollowUpDate) : "Not Captured"} />
                    <DetailMetric label="Recovery instructions" value={editableSelectedAccount?.recoveryInstructions || selectedAccount.notes || "Not Captured"} />
                  </div>
                )}
              </div>
              ) : null}

              {isPromiseDialogOpen ? (
                <section className="ar-workflow-surface rounded-[1.5rem] border p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="ar-workflow-label flex items-center gap-2"><Target className="size-4" aria-hidden="true" />Create Promise Workflow</p>
                      <h3 className="ar-workflow-title mt-1 font-display text-2xl font-bold">
                        {promiseWorkflowMode === "edit" ? "Edit promise" : "Create promise"} for {selectedAccount.customerName}
                      </h3>
                      <p className="ar-workflow-description mt-2 text-sm leading-6">
                        Save promise amount, date, payment method, related invoices, and next action. Promise Manager, Timeline, Account History, Recovery Activity, and Audit Trail update immediately.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setIsPromiseDialogOpen(false);
                        setPromiseFormError("");
                        setEditingPromiseId("");
                        setPromiseWorkflowMode("create");
                      }}
                      className="focus-ring rounded-full border border-[rgba(34,224,208,.2)] bg-[rgba(255,255,255,.04)] px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.14em] text-[#22E0D0]"
                    >
                      Close
                    </button>
                  </div>

                  <form
                    className="mt-4 grid gap-3"
                    onSubmit={(event) => {
                      event.preventDefault();
                      handleSavePromise();
                    }}
                  >
                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                      <label className="grid gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Promise Type</span>
                        <select
                          value={promiseForm.promiseType}
                          onChange={(event) => setPromiseForm((current) => ({ ...current, promiseType: event.target.value as ARRecoveryPromiseFormState["promiseType"] }))}
                          className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                        >
                          <option value="">Select promise type</option>
                          <option value="Single Payment">Single Payment</option>
                          <option value="Installment Plan">Installment Plan</option>
                          <option value="Partial Payment">Partial Payment</option>
                          <option value="Settlement">Settlement</option>
                          <option value="Other">Other</option>
                        </select>
                      </label>

                      <label className="grid gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Promise Amount</span>
                        <input
                          value={promiseForm.amount}
                          onChange={(event) => setPromiseForm((current) => ({ ...current, amount: event.target.value }))}
                          placeholder="5000"
                          inputMode="decimal"
                          className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                        />
                      </label>

                      <label className="grid gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Promise Date</span>
                        <input
                          type="date"
                          value={promiseForm.promiseDate}
                          onChange={(event) => setPromiseForm((current) => ({ ...current, promiseDate: event.target.value }))}
                          className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                        />
                      </label>

                      <label className="grid gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Expected Payment Method</span>
                        <input
                          value={promiseForm.expectedPaymentMethod}
                          onChange={(event) => setPromiseForm((current) => ({ ...current, expectedPaymentMethod: event.target.value }))}
                          placeholder="ACH, check, card, wire"
                          className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                        />
                      </label>
                    </div>

                    <label className="grid gap-1.5">
                      <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Optional Related Invoice(s)</span>
                      <select
                        multiple
                        value={promiseForm.relatedInvoices}
                        onChange={(event) => setPromiseForm((current) => ({ ...current, relatedInvoices: Array.from(event.target.selectedOptions).map((option) => option.value) }))}
                        className="focus-ring min-h-28 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 py-2 text-sm font-semibold text-[#F6F1E8]"
                      >
                        {buildInvoicePlaceholders(selectedAccount).map((invoice) => (
                          <option key={invoice.id} value={invoice.invoiceNumber}>
                            {invoice.invoiceNumber} | Due {formatCapturedDate(invoice.dueDate)} | {formatARRecoveryCurrency(invoice.amountCents)} | {invoice.status}
                          </option>
                        ))}
                      </select>
                    </label>

                    <div className="grid gap-3 xl:grid-cols-2">
                      <label className="grid gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Promise Notes</span>
                        <textarea
                          value={promiseForm.notes}
                          onChange={(event) => setPromiseForm((current) => ({ ...current, notes: event.target.value }))}
                          placeholder="Capture promise context, customer commitment, and payment expectations."
                          rows={4}
                          className="focus-ring rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 py-2 text-sm font-semibold leading-6 text-[#F6F1E8]"
                        />
                      </label>

                      <label className="grid gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Outcome</span>
                        <textarea
                          value={promiseForm.outcome}
                          onChange={(event) => setPromiseForm((current) => ({ ...current, outcome: event.target.value }))}
                          placeholder="Optional outcome or expected result."
                          rows={4}
                          className="focus-ring rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 py-2 text-sm font-semibold leading-6 text-[#F6F1E8]"
                        />
                      </label>
                    </div>

                    <div className="rounded-2xl border border-[rgba(34,224,208,.14)] bg-[rgba(255,255,255,.03)] p-3">
                      <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF6A2B]">Next Action</p>
                      <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                        <label className="grid gap-1.5">
                          <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Action Type</span>
                          <select
                            value={promiseForm.nextActionType}
                            onChange={(event) => setPromiseForm((current) => ({ ...current, nextActionType: event.target.value as ARRecoveryPromiseFormState["nextActionType"] }))}
                            className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                          >
                            <option value="">Select action</option>
                            {PROMISE_NEXT_ACTION_TYPES.map((action) => <option key={action} value={action}>{action}</option>)}
                          </select>
                        </label>

                        <label className="grid gap-1.5">
                          <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Due Date</span>
                          <input
                            type="date"
                            value={promiseForm.nextActionDueDate}
                            onChange={(event) => setPromiseForm((current) => ({ ...current, nextActionDueDate: event.target.value }))}
                            className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                          />
                        </label>

                        <label className="grid gap-1.5">
                          <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Due Time</span>
                          <input
                            type="time"
                            value={promiseForm.nextActionDueTime}
                            onChange={(event) => setPromiseForm((current) => ({ ...current, nextActionDueTime: event.target.value }))}
                            className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                          />
                        </label>

                        <label className="grid gap-1.5">
                          <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Priority</span>
                          <select
                            value={promiseForm.nextActionPriority}
                            onChange={(event) => setPromiseForm((current) => ({ ...current, nextActionPriority: event.target.value as ARRecoveryTaskPriority }))}
                            className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                          >
                            <option value="Low">Low</option>
                            <option value="Normal">Normal</option>
                            <option value="High">High</option>
                            <option value="Critical">Critical</option>
                          </select>
                        </label>

                        <label className="grid gap-1.5 md:col-span-2">
                          <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Assigned To</span>
                          <input
                            list="ar-internal-users"
                            value={promiseForm.nextActionAssignedTo}
                            onChange={(event) => setPromiseForm((current) => ({ ...current, nextActionAssignedTo: event.target.value }))}
                            placeholder={selectedAccount.assignedCollector || "Collector"}
                            className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                          />
                        </label>

                        <label className="flex min-h-11 items-center gap-2 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]">
                          <input
                            type="checkbox"
                            checked={promiseForm.reminderRequired}
                            onChange={(event) => setPromiseForm((current) => ({ ...current, reminderRequired: event.target.checked }))}
                            className="size-4"
                          />
                          Reminder Required
                        </label>

                        <label className="grid gap-1.5 md:col-span-2">
                          <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Reminder Notes</span>
                          <input
                            value={promiseForm.reminderNotes}
                            onChange={(event) => setPromiseForm((current) => ({ ...current, reminderNotes: event.target.value }))}
                            placeholder="Optional reminder notes"
                            className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                          />
                        </label>
                      </div>
                      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[rgba(255,106,43,.14)] bg-[rgba(255,106,43,.06)] px-3 py-2">
                        <p className="text-xs font-semibold leading-5 text-[#E8EEF4]">
                          Optional: create a follow-up from this promise using the shared Schedule Follow-Up workflow.
                        </p>
                        <button
                          type="button"
                          onClick={() => openScheduleFollowUpWorkflow(editingPromiseId)}
                          className="focus-ring min-h-9 rounded-full border border-[rgba(34,224,208,.26)] bg-[rgba(34,224,208,.08)] px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#22E0D0]"
                        >
                          Create Follow-Up
                        </button>
                      </div>
                    </div>

                    {promiseFormError ? (
                      <div className="rounded-xl border border-[rgba(248,113,113,.28)] bg-[rgba(248,113,113,.1)] px-3 py-2 text-sm font-semibold text-[#FCA5A5]">
                        {promiseFormError}
                      </div>
                    ) : null}

                    <div className="flex flex-wrap justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setIsPromiseDialogOpen(false);
                          setPromiseFormError("");
                          setEditingPromiseId("");
                          setPromiseWorkflowMode("create");
                        }}
                        className="focus-ring min-h-10 rounded-full border border-[rgba(34,224,208,.18)] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#CBD5E1]"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="focus-ring min-h-10 rounded-full border border-[rgba(255,106,43,.42)] bg-[rgba(255,106,43,.16)] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#F6F1E8]"
                      >
                        {promiseWorkflowMode === "edit" ? "Save Promise" : "Create Promise"}
                      </button>
                    </div>
                  </form>
                </section>
              ) : null}

              {isTicklerDialogOpen ? (
                <section className="ar-workflow-surface rounded-[1.5rem] border p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="ar-workflow-label flex items-center gap-2"><Clock3 className="size-4" aria-hidden="true" />Schedule Follow-Up Workflow</p>
                      <h3 className="ar-workflow-title mt-1 font-display text-2xl font-bold">
                        {ticklerWorkflowMode === "edit" ? "Edit follow-up" : "Schedule follow-up"} for {selectedAccount.customerName}
                      </h3>
                      <p className="ar-workflow-description mt-2 text-sm leading-6">
                        Save an operational follow-up with owner, due date, reminder context, related records, outcome, and next action.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setIsTicklerDialogOpen(false);
                        setTicklerFormError("");
                        setEditingTicklerId("");
                        setTicklerWorkflowMode("create");
                      }}
                      className="focus-ring rounded-full border border-[rgba(34,224,208,.2)] bg-[rgba(255,255,255,.04)] px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.14em] text-[#22E0D0]"
                    >
                      Close
                    </button>
                  </div>

                  <form
                    className="mt-4 grid gap-3"
                    onSubmit={(event) => {
                      event.preventDefault();
                      handleSaveTickler();
                    }}
                  >
                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                      <label className="grid gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Follow-Up Type</span>
                        <select
                          value={ticklerForm.followUpType}
                          onChange={(event) => setTicklerForm((current) => ({ ...current, followUpType: event.target.value as ARRecoveryTicklerFormState["followUpType"] }))}
                          className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                        >
                          <option value="">Select follow-up type</option>
                          {FOLLOW_UP_TYPE_OPTIONS.map((type) => <option key={type} value={type}>{type}</option>)}
                        </select>
                      </label>

                      <label className="grid gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Due Date</span>
                        <input
                          type="date"
                          value={ticklerForm.dueDate}
                          onChange={(event) => setTicklerForm((current) => ({ ...current, dueDate: event.target.value }))}
                          className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                        />
                      </label>

                      <label className="grid gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Due Time</span>
                        <input
                          type="time"
                          value={ticklerForm.dueTime}
                          onChange={(event) => setTicklerForm((current) => ({ ...current, dueTime: event.target.value }))}
                          className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                        />
                      </label>

                      <label className="grid gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Priority</span>
                        <select
                          value={ticklerForm.priority}
                          onChange={(event) => setTicklerForm((current) => ({ ...current, priority: event.target.value as ARRecoveryTaskPriority }))}
                          className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                        >
                          <option value="Low">Low</option>
                          <option value="Normal">Normal</option>
                          <option value="High">High</option>
                          <option value="Critical">Critical</option>
                        </select>
                      </label>

                      <label className="grid gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Assigned To</span>
                        <input
                          list="ar-internal-users"
                          value={ticklerForm.assignedTo}
                          onChange={(event) => setTicklerForm((current) => ({ ...current, assignedTo: event.target.value }))}
                          placeholder={selectedAccount.assignedCollector || "Collector"}
                          className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                        />
                      </label>

                      <label className="flex min-h-11 items-center gap-2 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]">
                        <input
                          type="checkbox"
                          checked={ticklerForm.reminderRequired}
                          onChange={(event) => setTicklerForm((current) => ({ ...current, reminderRequired: event.target.checked }))}
                          className="size-4"
                        />
                        Reminder Required
                      </label>

                      <label className="grid gap-1.5 md:col-span-2">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Reminder Notes</span>
                        <input
                          value={ticklerForm.reminderNotes}
                          onChange={(event) => setTicklerForm((current) => ({ ...current, reminderNotes: event.target.value }))}
                          placeholder="Optional reminder notes"
                          className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                        />
                      </label>
                    </div>

                    <div className="grid gap-3 md:grid-cols-3">
                      <label className="grid gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Optional Related Invoice</span>
                        <select
                          value={ticklerForm.relatedInvoice}
                          onChange={(event) => setTicklerForm((current) => ({ ...current, relatedInvoice: event.target.value }))}
                          className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                        >
                          <option value="">None</option>
                          {buildInvoicePlaceholders(selectedAccount).map((invoice) => (
                            <option key={invoice.id} value={invoice.invoiceNumber}>
                              {invoice.invoiceNumber} | {formatARRecoveryCurrency(invoice.amountCents)}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="grid gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Optional Related Promise</span>
                        <select
                          value={ticklerForm.relatedPromise}
                          onChange={(event) => setTicklerForm((current) => ({ ...current, relatedPromise: event.target.value }))}
                          className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                        >
                          <option value="">None</option>
                          {selectedAccountPromises.map((promise) => (
                            <option key={promise.id} value={promise.id}>
                              {formatARRecoveryCurrency(promise.amountCents)} | {formatCapturedDate(promise.promiseDate)} | {promise.status}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="grid gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Optional Related Task</span>
                        <select
                          value={ticklerForm.relatedTask}
                          onChange={(event) => setTicklerForm((current) => ({ ...current, relatedTask: event.target.value }))}
                          className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                        >
                          <option value="">None</option>
                          {accountTasks.map((task) => (
                            <option key={task.id} value={task.id}>
                              {task.taskTitle} | {task.status}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>

                    <div className="grid gap-3 xl:grid-cols-3">
                      <label className="grid gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Follow-Up Notes</span>
                        <textarea
                          value={ticklerForm.notes}
                          onChange={(event) => setTicklerForm((current) => ({ ...current, notes: event.target.value }))}
                          placeholder="Capture why this future work is needed."
                          rows={4}
                          className="focus-ring rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 py-2 text-sm font-semibold leading-6 text-[#F6F1E8]"
                        />
                      </label>

                      <label className="grid gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Outcome</span>
                        <textarea
                          value={ticklerForm.outcome}
                          onChange={(event) => setTicklerForm((current) => ({ ...current, outcome: event.target.value }))}
                          placeholder="Optional outcome"
                          rows={4}
                          className="focus-ring rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 py-2 text-sm font-semibold leading-6 text-[#F6F1E8]"
                        />
                      </label>

                      <label className="grid gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Next Action</span>
                        <textarea
                          value={ticklerForm.nextAction}
                          onChange={(event) => setTicklerForm((current) => ({ ...current, nextAction: event.target.value }))}
                          placeholder="Optional next action"
                          rows={4}
                          className="focus-ring rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 py-2 text-sm font-semibold leading-6 text-[#F6F1E8]"
                        />
                      </label>
                    </div>

                    {ticklerFormError ? (
                      <div className="rounded-xl border border-[rgba(248,113,113,.28)] bg-[rgba(248,113,113,.1)] px-3 py-2 text-sm font-semibold text-[#FCA5A5]">
                        {ticklerFormError}
                      </div>
                    ) : null}

                    <div className="flex flex-wrap justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setIsTicklerDialogOpen(false);
                          setTicklerFormError("");
                          setEditingTicklerId("");
                          setTicklerWorkflowMode("create");
                        }}
                        className="focus-ring min-h-10 rounded-full border border-[rgba(34,224,208,.18)] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#CBD5E1]"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="focus-ring min-h-10 rounded-full border border-[rgba(255,106,43,.42)] bg-[rgba(255,106,43,.16)] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#F6F1E8]"
                      >
                        {ticklerWorkflowMode === "edit" ? "Save Follow-Up" : "Schedule Follow-Up"}
                      </button>
                    </div>
                  </form>
                </section>
              ) : null}

              {isStatementDialogOpen ? (
                <section className="rounded-[1.5rem] border border-[rgba(255,106,43,.18)] bg-[rgba(7,18,31,.92)] p-4 shadow-[0_18px_42px_rgba(0,0,0,.2)]">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF6A2B]">Statement Production Workflow</p>
                      <h3 className="mt-1 font-display text-2xl font-bold text-[#F6F1E8]">Generate statement for {selectedAccount.customerName}</h3>
                      <p className="mt-2 text-sm font-semibold leading-6 text-[#CBD5E1]">
                        Select invoices, preview totals, and save a permanent statement record. Email delivery is only marked sent when a real provider succeeds.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setIsStatementDialogOpen(false);
                        setStatementFormError("");
                      }}
                      className="focus-ring rounded-full border border-[rgba(34,224,208,.2)] bg-[rgba(255,255,255,.04)] px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.14em] text-[#22E0D0]"
                    >
                      Close
                    </button>
                  </div>

                  <form
                    className="mt-4 grid gap-3"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void handleGenerateStatement();
                    }}
                  >
                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                      <label className="grid gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Statement Type</span>
                        <select
                          value={statementForm.statementType}
                          onChange={(event) => setStatementForm((current) => ({ ...current, statementType: event.target.value as ARRecoveryStatementFormState["statementType"] }))}
                          className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                        >
                          <option value="">Select statement type</option>
                          {STATEMENT_TYPE_OPTIONS.map((statementType) => <option key={statementType} value={statementType}>{statementType}</option>)}
                        </select>
                      </label>

                      <label className="grid gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Statement Date</span>
                        <input
                          type="date"
                          value={statementForm.statementDate}
                          onChange={(event) => setStatementForm((current) => ({ ...current, statementDate: event.target.value }))}
                          className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                        />
                      </label>

                      <label className="grid gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Delivery Option</span>
                        <select
                          value={statementForm.deliveryOption}
                          onChange={(event) => setStatementForm((current) => ({ ...current, deliveryOption: event.target.value as ARRecoveryStatementDeliveryOption }))}
                          className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                        >
                          <option value="Email">Email</option>
                          <option value="Download PDF">Download PDF</option>
                          <option value="Print">Print</option>
                        </select>
                      </label>

                      <label className="grid gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Select All Open Invoices</span>
                        <button
                          type="button"
                          onClick={() => setStatementForm((current) => ({ ...current, relatedInvoices: buildInvoicePlaceholders(selectedAccount).map((invoice) => invoice.invoiceNumber) }))}
                          className="focus-ring min-h-11 rounded-xl border border-[rgba(255,106,43,.28)] bg-[rgba(255,106,43,.1)] px-3 text-sm font-black uppercase tracking-[0.12em] text-[#FF6A2B]"
                        >
                          Select All
                        </button>
                      </label>
                    </div>

                    <div className="grid gap-3 md:grid-cols-[1.2fr_.8fr]">
                      <label className="grid gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Select Invoices</span>
                        <select
                          multiple
                          value={statementForm.relatedInvoices}
                          onChange={(event) => setStatementForm((current) => ({ ...current, relatedInvoices: Array.from(event.target.selectedOptions).map((option) => option.value) }))}
                          className="focus-ring min-h-36 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 py-2 text-sm font-semibold text-[#F6F1E8]"
                        >
                          {buildInvoicePlaceholders(selectedAccount).map((invoice) => (
                            <option key={invoice.id} value={invoice.invoiceNumber}>
                              {invoice.invoiceNumber} | Due {formatCapturedDate(invoice.dueDate)} | {formatARRecoveryCurrency(invoice.amountCents)} | {selectedAccount.agingSummary} | {invoice.status}
                            </option>
                          ))}
                        </select>
                      </label>

                      <div className="grid gap-3">
                        <label className="grid gap-1.5">
                          <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Optional Related Promise</span>
                          <select
                            value={statementForm.relatedPromise}
                            onChange={(event) => setStatementForm((current) => ({ ...current, relatedPromise: event.target.value }))}
                            className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                          >
                            <option value="">None</option>
                            {selectedAccountPromises.map((promise) => (
                              <option key={promise.id} value={promise.id}>
                                {formatARRecoveryCurrency(promise.amountCents)} | {formatCapturedDate(promise.promiseDate)} | {promise.status}
                              </option>
                            ))}
                          </select>
                        </label>

                        <label className="grid gap-1.5">
                          <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Optional Related Task</span>
                          <select
                            value={statementForm.relatedTask}
                            onChange={(event) => setStatementForm((current) => ({ ...current, relatedTask: event.target.value }))}
                            className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                          >
                            <option value="">None</option>
                            {accountTasks.map((task) => (
                              <option key={task.id} value={task.id}>
                                {task.taskTitle} | {task.status}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                    </div>

                    <label className="grid gap-1.5">
                      <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Statement Notes</span>
                      <textarea
                        value={statementForm.statementNotes}
                        onChange={(event) => setStatementForm((current) => ({ ...current, statementNotes: event.target.value }))}
                        placeholder="Optional statement notes for the permanent record."
                        rows={3}
                        className="focus-ring rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 py-2 text-sm font-semibold leading-6 text-[#F6F1E8]"
                      />
                    </label>

                    <div className="rounded-2xl border border-[rgba(34,224,208,.14)] bg-[rgba(255,255,255,.03)] p-3">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF6A2B]">Preview Before Generation</p>
                        <span className="rounded-full border border-[rgba(255,106,43,.28)] bg-[rgba(255,106,43,.08)] px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#FF6A2B]">
                          {statementForm.deliveryOption === "Email" ? "Provider verified on send" : "Manual delivery"}
                        </span>
                      </div>
                      {statementForm.deliveryOption === "Email" ? (
                        <div className="mt-3 rounded-xl border border-[rgba(255,106,43,.2)] bg-[rgba(255,106,43,.08)] px-3 py-2 text-sm font-semibold leading-6 text-[#FF6A2B]">
                          Generating this statement will attempt real email delivery. Status becomes Sent only after provider success; missing configuration becomes Delivery Not Configured and provider errors become Failed.
                        </div>
                      ) : null}
                      <StatementPreviewPanel
                        preview={buildStatementPreviewFromForm({
                          form: statementForm,
                          account: selectedAccount,
                          clientProfile: workspace.clientProfile,
                        })}
                      />
                    </div>

                    {statementFormError ? (
                      <div className="rounded-xl border border-[rgba(248,113,113,.28)] bg-[rgba(248,113,113,.1)] px-3 py-2 text-sm font-semibold text-[#FCA5A5]">
                        {statementFormError}
                      </div>
                    ) : null}

                    <div className="flex flex-wrap justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setIsStatementDialogOpen(false);
                          setStatementFormError("");
                        }}
                        className="focus-ring min-h-10 rounded-full border border-[rgba(34,224,208,.18)] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#CBD5E1]"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="focus-ring min-h-10 rounded-full border border-[rgba(255,106,43,.42)] bg-[rgba(255,106,43,.16)] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#F6F1E8]"
                      >
                        Save Generated Statement
                      </button>
                    </div>
                  </form>
                </section>
              ) : null}

              {isDocumentRequestDialogOpen ? (
                <section className="rounded-[1.5rem] border border-[rgba(255,106,43,.18)] bg-[rgba(7,18,31,.92)] p-4 shadow-[0_18px_42px_rgba(0,0,0,.2)]">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF6A2B]">Document Request Workflow</p>
                      <h3 className="mt-1 font-display text-2xl font-bold text-[#F6F1E8]">Request documents for {selectedAccount.customerName}</h3>
                      <p className="mt-2 text-sm font-semibold leading-6 text-[#CBD5E1]">
                        Create a permanent support-document request. Documents Manager, Timeline, Account History, Recovery Activity, and Audit Trail update immediately.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setIsDocumentRequestDialogOpen(false);
                        setDocumentRequestFormError("");
                      }}
                      className="focus-ring rounded-full border border-[rgba(34,224,208,.2)] bg-[rgba(255,255,255,.04)] px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.14em] text-[#22E0D0]"
                    >
                      Close
                    </button>
                  </div>

                  <form
                    className="mt-4 grid gap-3"
                    onSubmit={(event) => {
                      event.preventDefault();
                      handleCreateDocumentRequest();
                    }}
                  >
                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                      <label className="grid gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Request Type</span>
                        <select
                          value={documentRequestForm.requestType}
                          onChange={(event) => setDocumentRequestForm((current) => ({ ...current, requestType: event.target.value as ARRecoveryDocumentRequestType }))}
                          className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                        >
                          <option value="">Select request type</option>
                          {DOCUMENT_REQUEST_TYPE_OPTIONS.map((requestType) => <option key={requestType} value={requestType}>{requestType}</option>)}
                        </select>
                      </label>

                      <label className="grid gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Requested From</span>
                        <input
                          value={documentRequestForm.requestedFrom}
                          onChange={(event) => setDocumentRequestForm((current) => ({ ...current, requestedFrom: event.target.value }))}
                          placeholder="AP contact, client user, or internal owner"
                          className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                        />
                      </label>

                      <label className="grid gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Due Date</span>
                        <input
                          type="text"
                          value={documentRequestForm.dueDate}
                          onChange={(event) => setDocumentRequestForm((current) => ({ ...current, dueDate: event.target.value }))}
                          placeholder="YYYY-MM-DD"
                          className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                        />
                      </label>

                      <label className="grid gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Priority</span>
                        <select
                          value={documentRequestForm.priority}
                          onChange={(event) => setDocumentRequestForm((current) => ({ ...current, priority: event.target.value as ARRecoveryTaskPriority }))}
                          className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                        >
                          {TASK_PRIORITY_OPTIONS.map((priority) => <option key={priority} value={priority}>{priority}</option>)}
                        </select>
                      </label>

                      <label className="grid gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Optional Related Invoice</span>
                        <select
                          value={documentRequestForm.relatedInvoice}
                          onChange={(event) => setDocumentRequestForm((current) => ({ ...current, relatedInvoice: event.target.value }))}
                          className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                        >
                          <option value="">None</option>
                          {buildInvoicePlaceholders(selectedAccount).map((invoice) => (
                            <option key={invoice.id} value={invoice.invoiceNumber}>
                              {invoice.invoiceNumber} | {formatARRecoveryCurrency(invoice.amountCents)}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="grid gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Optional Related Task</span>
                        <select
                          value={documentRequestForm.relatedTask}
                          onChange={(event) => setDocumentRequestForm((current) => ({ ...current, relatedTask: event.target.value }))}
                          className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                        >
                          <option value="">None</option>
                          {accountTasks.map((task) => (
                            <option key={task.id} value={task.id}>
                              {task.taskTitle} | {task.status}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>

                    <label className="grid gap-1.5">
                      <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Request Notes</span>
                      <textarea
                        value={documentRequestForm.requestNotes}
                        onChange={(event) => setDocumentRequestForm((current) => ({ ...current, requestNotes: event.target.value }))}
                        placeholder="Describe what is needed and why it matters for recovery."
                        rows={3}
                        className="focus-ring rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 py-2 text-sm font-semibold leading-6 text-[#F6F1E8]"
                      />
                    </label>

                    {documentRequestFormError ? (
                      <div className="rounded-xl border border-[rgba(248,113,113,.28)] bg-[rgba(248,113,113,.1)] px-3 py-2 text-sm font-semibold text-[#FCA5A5]">
                        {documentRequestFormError}
                      </div>
                    ) : null}

                    <div className="flex flex-wrap justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setIsDocumentRequestDialogOpen(false);
                          setDocumentRequestFormError("");
                        }}
                        className="focus-ring min-h-10 rounded-full border border-[rgba(34,224,208,.18)] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#CBD5E1]"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="focus-ring min-h-10 rounded-full border border-[rgba(255,106,43,.42)] bg-[rgba(255,106,43,.16)] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#F6F1E8]"
                      >
                        Create Document Request
                      </button>
                    </div>
                  </form>
                </section>
              ) : null}

              {isNoteDialogOpen ? (
                <section ref={noteWorkflowSectionRef} className="ar-workflow-surface rounded-[1.5rem] border p-5 xl:p-6">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="ar-workflow-label flex items-center gap-2"><FileText className="size-4" aria-hidden="true" />Enterprise Business Knowledge Workspace</p>
                      <h3 className="ar-workflow-title mt-1 font-display text-2xl font-bold">Capture account knowledge for {selectedAccount.customerName}</h3>
                      <p className="ar-workflow-description mt-2 text-sm leading-6">
                        Capture what happened, why it matters, who can see it, and what operational history updates after save.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={requestCloseNoteWorkspace}
                      className="focus-ring rounded-full border border-[rgba(34,224,208,.2)] bg-[rgba(255,255,255,.04)] px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.14em] text-[#22E0D0]"
                    >
                      Close
                    </button>
                  </div>

                  <form
                    className="mt-3 grid gap-3"
                    onSubmit={(event) => {
                      event.preventDefault();
                      handleSaveNote();
                    }}
                  >
                    {noteCompletionSummary ? (
                      <div className="rounded-2xl border border-[rgba(34,197,94,.3)] bg-[rgba(34,197,94,.08)] p-4" role="status" aria-live="polite">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#86EFAC]">Note Saved Successfully</p>
                            <h4 className="mt-1 break-words text-lg font-semibold text-[#F6F1E8]">{noteCompletionSummary.customer}</h4>
                            <p className="mt-2 text-sm font-semibold leading-6 text-[#DCFCE7]">
                              Category: {noteCompletionSummary.category} | Visibility: {noteCompletionSummary.visibility} | Saved {formatCapturedDate(noteCompletionSummary.createdAt)} {formatCapturedTime(noteCompletionSummary.createdAt)}
                            </p>
                          </div>
                          <span className="rounded-full border border-[rgba(34,197,94,.3)] bg-[rgba(34,197,94,.12)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-[#86EFAC]">
                            Account History Updated
                          </span>
                        </div>
                        <div className="mt-3 grid gap-2 md:grid-cols-3">
                          {["Timeline Updated", "Recovery Activity Updated", "Account History Updated"].map((item) => (
                            <div key={item} className="rounded-xl border border-[rgba(34,197,94,.22)] bg-[rgba(255,255,255,.04)] px-3 py-2 text-sm font-semibold text-[#DCFCE7]">
                              {item}
                            </div>
                          ))}
                        </div>
                        <div className="mt-4 flex flex-wrap justify-end gap-2">
                          <button
                            type="button"
                            onClick={requestCloseNoteWorkspace}
                            className="focus-ring min-h-10 rounded-full border border-[rgba(34,224,208,.18)] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#CBD5E1]"
                          >
                            Close
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setNoteForm(emptyNoteForm);
                              setNoteFormError("");
                              setNoteSuccessMessage("");
                              setNoteCompletionSummary(null);
                            }}
                            className="focus-ring min-h-10 rounded-full border border-[rgba(34,224,208,.28)] bg-[rgba(34,224,208,.1)] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#22E0D0]"
                          >
                            Create Another Note
                          </button>
                        </div>
                      </div>
                    ) : null}

                    <div className="order-1 grid gap-3 md:grid-cols-2">
                      <label className="grid gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Knowledge Category</span>
                        <select
                          value={noteForm.noteType}
                          onChange={(event) => {
                            setNoteCompletionSummary(null);
                            setNoteSuccessMessage("");
                            setNoteForm((current) => ({ ...current, noteType: event.target.value as ARRecoveryNoteFormState["noteType"] }));
                          }}
                          className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                        >
                          <option value="">Select note type</option>
                          {AR_RECOVERY_NOTE_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.value}</option>
                          ))}
                        </select>
                        <span className="text-xs font-semibold leading-5 text-[#CBD5E1]">{noteWorkspaceCategoryDescription}</span>
                      </label>

                      <label className="grid gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Visibility</span>
                        <select
                          value={noteForm.visibility}
                          onChange={(event) => {
                            setNoteCompletionSummary(null);
                            setNoteSuccessMessage("");
                            setNoteForm((current) => ({ ...current, visibility: event.target.value as ARRecoveryNoteFormState["visibility"] }));
                          }}
                          className="focus-ring min-h-11 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                        >
                          <option value="">Select visibility</option>
                          {AR_RECOVERY_NOTE_VISIBILITY_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        <span className="text-xs font-semibold leading-5 text-[#CBD5E1]">{noteWorkspaceVisibilityDescription}</span>
                      </label>
                    </div>

                    <label className="order-2 grid gap-2 rounded-[1.75rem] border border-[rgba(34,224,208,.22)] bg-[rgba(7,18,31,.78)] p-3 shadow-[0_18px_42px_rgba(2,7,44,.18)] xl:p-4">
                      <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Knowledge Editor</span>
                      <span className="text-base font-semibold leading-7 text-[#F6F1E8]">Document customer account knowledge</span>
                      <textarea
                        ref={noteContentTextareaRef}
                        value={noteForm.content}
                        onChange={(event) => {
                          setNoteCompletionSummary(null);
                          setNoteSuccessMessage("");
                          setNoteForm((current) => ({ ...current, content: event.target.value }));
                        }}
                        placeholder="Capture collector context, customer update, escalation reason, promise context, or follow-up detail."
                        rows={noteEditorRows}
                        className="focus-ring min-h-[18rem] max-h-[34rem] resize-y overflow-y-auto rounded-2xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-5 py-4 text-base font-semibold leading-8 text-[#F6F1E8] placeholder:text-[#94A3B8] md:min-h-[20rem]"
                      />
                    </label>

                    <div className="order-3 rounded-2xl border border-[rgba(34,224,208,.14)] bg-[rgba(255,255,255,.035)] p-3">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Knowledge Status</p>
                        <span className={`rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] ${
                          noteWorkspaceCanSave ? "border-[rgba(34,197,94,.32)] text-[#86EFAC]" : "border-[rgba(255,106,43,.32)] text-[#FFB086]"
                        }`}>
                          {noteWorkspaceCanSave ? "Ready to Save" : "Needs Detail"}
                        </span>
                      </div>
                      <div className="mt-3 grid gap-3 xl:grid-cols-[minmax(0,1.1fr)_minmax(16rem,.9fr)]">
                        <div className="min-w-0 rounded-xl bg-[rgba(7,18,31,.44)] p-3">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Knowledge Summary</p>
                            <span className="rounded-full border border-[rgba(34,224,208,.18)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-[#22E0D0]">
                              {noteForm.content.trim() ? `${noteForm.content.trim().length} Characters` : "Draft"}
                            </span>
                          </div>
                          <p className="mt-2 break-words text-sm font-semibold leading-6 text-[#CBD5E1]">
                            {noteForm.content.trim() ? previewNoteContent(noteForm.content) : "Knowledge summary appears as the collector writes."}
                          </p>
                        </div>

                        <div className="grid min-w-0 gap-2">
                          <div className="rounded-xl bg-[rgba(7,18,31,.44)] p-3">
                            <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Operational Impact</p>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {noteWorkspaceImpactItems.map((item) => (
                                <div key={item} className="rounded-full border border-[rgba(34,224,208,.12)] bg-[rgba(7,18,31,.58)] px-3 py-1.5 text-xs font-black uppercase tracking-[0.12em] text-[#F6F1E8]">
                                  ✓ {item}
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="grid gap-2">
                            {noteWorkspaceReadinessItems.map((item) => (
                              <div key={item.label} className="flex min-w-0 items-start gap-2 rounded-xl bg-[rgba(7,18,31,.45)] px-3 py-2">
                                <span className={`mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full border text-[11px] font-black ${item.ready ? "border-[rgba(34,197,94,.36)] text-[#86EFAC]" : "border-[rgba(255,106,43,.36)] text-[#FFB086]"}`}>
                                  {item.ready ? "✓" : "○"}
                                </span>
                                <div className="min-w-0">
                                  <p className="break-words text-sm font-semibold text-[#F6F1E8]">{item.label}</p>
                                  <p className="mt-0.5 break-words text-xs font-semibold leading-5 text-[#CBD5E1]">{item.detail}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="order-4 min-w-0 max-w-full overflow-hidden rounded-2xl border border-[rgba(34,224,208,.12)] bg-[rgba(7,18,31,.44)] p-3">
                      <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Knowledge Information</p>
                      <div className="mt-2 grid min-w-0 max-w-full grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-[repeat(5,minmax(0,1fr))]">
                        <NoteField label="Knowledge Category" value={noteForm.noteType || "Not Captured"} />
                        <NoteField label="Visibility" value={noteWorkspaceVisibility} />
                        <NoteField label="Collector" value={selectedAccount.assignedCollector || actorName()} />
                        <NoteField label="Status" value={noteWorkspaceCanSave ? "Ready to Save" : "Draft"} />
                        <label className="grid min-w-0 max-w-full gap-1.5 overflow-hidden">
                          <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Next Action</span>
                          <input
                            value={noteForm.nextAction}
                            onChange={(event) => {
                              setNoteCompletionSummary(null);
                              setNoteSuccessMessage("");
                              setNoteForm((current) => ({ ...current, nextAction: event.target.value }));
                            }}
                            placeholder="Optional next action"
                            className="focus-ring box-border min-h-10 w-full min-w-0 max-w-full rounded-xl border border-[rgba(34,224,208,.14)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                          />
                        </label>
                      </div>
                    </div>


                    {noteFormError ? (
                      <div className="order-5 rounded-xl border border-[rgba(248,113,113,.28)] bg-[rgba(248,113,113,.1)] px-3 py-2 text-sm font-semibold text-[#FCA5A5]">
                        {noteFormError}
                      </div>
                    ) : null}

                    <div className="order-6 flex flex-wrap justify-end gap-2">
                      <button
                        type="button"
                        onClick={requestCloseNoteWorkspace}
                        className="focus-ring min-h-10 rounded-full border border-[rgba(34,224,208,.18)] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#CBD5E1]"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={!noteWorkspaceCanSave}
                        className={`focus-ring min-h-10 rounded-full border px-4 text-sm font-black uppercase tracking-[0.12em] ${
                          noteWorkspaceCanSave
                            ? "border-[rgba(255,106,43,.42)] bg-[rgba(255,106,43,.16)] text-[#F6F1E8]"
                            : "cursor-not-allowed border-[rgba(148,163,184,.2)] bg-[rgba(148,163,184,.08)] text-[#94A3B8]"
                        }`}
                      >
                        {noteWorkspaceCanSave ? "Save Note" : "Complete Required Fields"}
                      </button>
                    </div>
                  </form>
                </section>
              ) : null}

              {!isManagerCommandNoteWorkspace ? renderCertifiedAccountContextWorkspace() : null}
              </div>
                </ARRecoveryEnterpriseWorkspacePanel>
              ) : null}
            </>
          ) : (
            <div className="rounded-3xl border border-dashed border-[rgba(34,224,208,.16)] bg-[rgba(255,255,255,.03)] p-5 text-sm font-semibold leading-7 text-[#E8EEF4]">
              No customer account matched the current search. Try a broader search term or reset the filter.
            </div>
          )}
        </article>
        ) : null}
      </section>

      {selectedAccount && activeEnterpriseStudio ? (
        <ARRecoveryEnterpriseStudio
          mode={activeEnterpriseStudio}
          account={selectedAccount}
          clientProfile={workspace.clientProfile}
          letterForm={letterForm}
          statementForm={statementForm}
          setLetterForm={setLetterForm}
          setStatementForm={setStatementForm}
          selectedAccountPromises={selectedAccountPromises}
          accountTasks={accountTasks}
          letterFormError={letterFormError}
          statementFormError={statementFormError}
          studioStatusMessage={studioStatusMessage}
          setStudioStatusMessage={setStudioStatusMessage}
          isDesignerCollapsed={isStudioDesignerCollapsed}
          setIsDesignerCollapsed={setIsStudioDesignerCollapsed}
          activeDesignerSection={activeStudioDesignerSection}
          setActiveDesignerSection={setActiveStudioDesignerSection}
          activeStudioTab={activeStudioTab}
          setActiveStudioTab={setActiveStudioTab}
          onSaveLetter={handleGenerateLetter}
          onSaveStatement={handleGenerateStatement}
          onClose={closeEnterpriseStudio}
        />
      ) : null}
    </div>
  );
}

function PromiseToPaySection({
  workspace,
}: {
  workspace: ReturnType<typeof loadARRecoveryWorkspace>;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const promiseReturnContext = useMemo(() => getPromiseCenterReturnContext(location.search), [location.search]);
  const [promiseWorkspace, setPromiseWorkspace] = useState(workspace);
  const [search, setSearch] = useState(() => promiseReturnContext?.searchQuery || "");
  const [selectedPromiseId, setSelectedPromiseId] = useState(() => promiseReturnContext?.promiseId || workspace.promises[0]?.id || "");
  const [selectedTab, setSelectedTab] = useState<ARRecoveryPromiseTabKey>(() => promiseReturnContext?.activeTab || "overview");
  const [activeQuickAction, setActiveQuickAction] = useState<PromiseQuickActionState | null>(null);
  const promiseConfirmationPendingRef = useRef<PromiseConfirmationType | null>(null);
  const processedPromisePaymentKeysRef = useRef<Set<string>>(new Set());
  const promiseReturnFocusRef = useRef<HTMLDivElement | null>(null);
  const snapshot = useMemo(
    () => buildARRecoveryPromiseCenterSnapshot(promiseWorkspace.company.id, search, selectedPromiseId, selectedTab, promiseWorkspace),
    [search, selectedPromiseId, selectedTab, promiseWorkspace],
  );
  const selectedPromise = snapshot.selectedPromise;
  const selectedPromiseActions = selectedPromise ? buildPromiseWorkflowActions(selectedPromise) : [];

  useEffect(() => {
    setPromiseWorkspace(workspace);
  }, [workspace]);

  useEffect(() => {
    if (!selectedPromiseId && snapshot.filteredPromises[0]) {
      setSelectedPromiseId(snapshot.filteredPromises[0].id);
    }
  }, [selectedPromiseId, snapshot.filteredPromises]);

  useEffect(() => {
    if (selectedPromiseId && !snapshot.filteredPromises.some((promise) => promise.id === selectedPromiseId)) {
      setSelectedPromiseId(snapshot.filteredPromises[0]?.id || "");
    }
  }, [selectedPromiseId, snapshot.filteredPromises]);

  useEffect(() => {
    setActiveQuickAction(null);
  }, [selectedPromiseId]);

  useEffect(() => {
    if (!promiseReturnContext) return;
    setSearch(promiseReturnContext.searchQuery || "");
    setSelectedPromiseId(promiseReturnContext.promiseId);
    setSelectedTab(promiseReturnContext.activeTab || "overview");
    setActiveQuickAction(null);
  }, [promiseReturnContext?.promiseId, promiseReturnContext?.searchQuery, promiseReturnContext?.activeTab, promiseReturnContext?.returnRoute]);

  useLayoutEffect(() => {
    if (!promiseReturnContext) return;
    promiseReturnFocusRef.current?.focus({ preventScroll: true });
  }, [promiseReturnContext?.returnRoute]);

  function routeToPromiseModule(route: string, action: string) {
    if (!selectedPromise) return;
    const params = new URLSearchParams({
      source: "promise-to-pay",
      promiseId: selectedPromise.id,
      accountId: selectedPromise.accountId,
      customerId: selectedPromise.customerNumber,
      customer: selectedPromise.customerName,
      action,
      workflow: action,
    });
    if (search) params.set("promiseSearch", search);
    params.set("promiseTab", selectedTab);
    navigate(`${route}?${params.toString()}`);
  }

  function routeToPromiseAccount() {
    if (!selectedPromise) return;
    if (!selectedPromise.accountId) {
      setActiveQuickAction({
        label: "View Account",
        promiseId: selectedPromise.id,
        title: "Account Link Unavailable",
        description: "This promise is not linked to an available account.",
        status: "Ready",
        primaryActionLabel: "Account Unavailable",
        errorMessage: "This promise is not linked to an available account.",
        details: [
          `Customer: ${selectedPromise.customerName}`,
          `Promise amount: ${formatARRecoveryCurrency(selectedPromise.amountCents)}`,
          `Promise date: ${formatDate(selectedPromise.promiseDate)}`,
          `Status: ${selectedPromise.status}`,
        ],
      });
      return;
    }

    const promiseCenterReturnContext: PromiseCenterReturnContext = {
      origin: "promise-center",
      promiseId: selectedPromise.id,
      accountId: selectedPromise.accountId,
      customerId: selectedPromise.customerNumber,
      searchQuery: search,
      activeTab: selectedTab,
      workflowType: "view-account",
      returnRoute: buildPromiseCenterReturnRoute({
        promiseId: selectedPromise.id,
        accountId: selectedPromise.accountId,
        customerId: selectedPromise.customerNumber,
        searchQuery: search,
        activeTab: selectedTab,
        workflowType: "view-account",
      }),
    };

    navigate("/admin/ar-recovery/customer-accounts", {
      state: {
        accountRecoveryLaunch: {
          origin: "promise-center",
          accountId: selectedPromise.accountId,
          selectedTab: "activity",
          managerAction: "view-account",
          launchToken: `promise-view-account-${selectedPromise.id}-${selectedPromise.accountId}-${Date.now()}`,
          returnRoute: promiseCenterReturnContext.returnRoute,
          promiseCenterReturnContext,
        },
      } satisfies AccountRecoveryLaunchState,
    });
  }

  function openPromiseWorkflow(
    action: ReturnType<typeof buildPromiseWorkflowActions>[number],
    workflow: Omit<PromiseQuickActionState, "label" | "details" | "promiseId" | "routeHref">,
  ) {
    if (!selectedPromise) return;
    const routeHref = workflow.route
      ? `${workflow.route}?${new URLSearchParams({
          source: "promise-to-pay",
          promiseId: selectedPromise.id,
          accountId: selectedPromise.accountId,
          customerId: selectedPromise.customerNumber,
          customer: selectedPromise.customerName,
          action: workflow.routeAction || action.label,
          workflow: workflow.routeAction || action.label,
          ...(search ? { promiseSearch: search } : {}),
          promiseTab: selectedTab,
        }).toString()}`
      : undefined;
    setActiveQuickAction({
      label: action.label,
      promiseId: selectedPromise.id,
      ...workflow,
      routeHref,
      details: [
        `Customer: ${selectedPromise.customerName}`,
        `Account number: ${selectedPromise.customerNumber}`,
        `Account record: ${selectedPromise.accountId}`,
        `Promise amount: ${formatARRecoveryCurrency(selectedPromise.amountCents)}`,
        `Remaining balance: ${formatARRecoveryCurrency(selectedPromise.remainingBalanceCents)}`,
        `Promise date: ${formatDate(selectedPromise.promiseDate)}`,
        `Status: ${selectedPromise.status}`,
      ],
    });
  }

  function handlePromiseQuickAction(action: ReturnType<typeof buildPromiseWorkflowActions>[number]) {
    if (!selectedPromise) return;
    if (action.disabled) return;

    switch (action.label) {
      case "Track Promise":
        setSelectedTab("activity");
        openPromiseWorkflow(action, {
          title: "Promise Tracking Workflow",
          description: "Review activity, history, notes, and promise movement for the selected commitment.",
          status: "Ready",
          primaryActionLabel: "Review Activity",
        });
        break;
      case "Receive Payment":
        if (isPromiseAlreadyClosed(selectedPromise) || selectedPromise.remainingBalanceCents <= 0) {
          openPromiseWorkflow(action, {
            title: "Payment Cannot Be Recorded",
            description: "This promise is already paid or closed. No additional payment can be recorded from Promise Center.",
            status: "Ready",
            primaryActionLabel: "Payment Unavailable",
            errorMessage: "Receive Payment is disabled because this promise is already paid or closed.",
          });
          break;
        }
        if (!selectedPromise.id || !selectedPromise.accountId || !selectedPromise.customerNumber || !promiseWorkspace.accounts.some((account) => account.id === selectedPromise.accountId && account.customerNumber === selectedPromise.customerNumber)) {
          openPromiseWorkflow(action, {
            title: "Payment Cannot Be Recorded",
            description: "This promise cannot receive payment until its customer and account link is valid.",
            status: "Ready",
            primaryActionLabel: "Payment Unavailable",
            errorMessage: "The selected promise is missing a valid customer/account link. Reopen a canonical promise record and try again.",
          });
          break;
        }
        openPromiseWorkflow(action, {
          title: "Receive Payment Workflow",
          description: "Enter payment details, review the balance impact, and confirm one deliberate payment for this promise.",
          status: "Ready",
          primaryActionLabel: "Review Payment",
          confirmationType: "receive-payment",
          confirmLabel: "Review Payment",
          consequence: "Confirming records one canonical payment, applies it to this promise and account, updates Payment History, and publishes the existing payment workflow events. Opening, editing, or cancelling this review does not change financial data.",
          paymentStage: "entry",
          paymentAmount: "",
          paymentDate: new Date().toISOString().slice(0, 10),
          paymentMethod: "",
          paymentReference: "",
          paymentNotes: "",
          paymentIdempotencyKey: `promise-payment-${selectedPromise.id}-${selectedPromise.accountId}-${Date.now()}`,
        });
        break;
      case "Create Follow-Up":
        openPromiseWorkflow(action, {
          title: "Follow-Up Creation Workflow",
          description: "Create a follow-up for the selected promise with customer, balance, and promise context preloaded.",
          status: "Ready",
          primaryActionLabel: "Open Follow-Up Center",
          route: "/admin/ar-recovery/smart-tickler",
          routeAction: "follow-up",
        });
        break;
      case "Create Tickler":
        openPromiseWorkflow(action, {
          title: "Tickler Creation Workflow",
          description: "Create a reminder for this promise with selected promise context preloaded.",
          status: "Ready",
          primaryActionLabel: "Open Tickler Center",
          route: "/admin/ar-recovery/smart-tickler",
          routeAction: "tickler",
        });
        break;
      case "Generate Letter":
        routeToPromiseModule("/admin/ar-recovery/collection-letters", "generate-letter");
        break;
      case "Generate Statement":
        routeToPromiseModule("/admin/ar-recovery/statements", "generate-statement");
        break;
      case "View Account":
        routeToPromiseAccount();
        break;
      case "Approve Promise":
        if (!selectedPromise.id || !selectedPromise.accountId || !selectedPromise.customerNumber || !promiseWorkspace.accounts.some((account) => account.id === selectedPromise.accountId && account.customerNumber === selectedPromise.customerNumber)) {
          openPromiseWorkflow(action, {
            title: "Promise Approval Unavailable",
            description: "This promise cannot be approved until its customer and account link is valid.",
            status: "Ready",
            primaryActionLabel: "Approval Unavailable",
            errorMessage: "The selected promise is missing a valid customer/account link. Reopen a canonical promise record and try again.",
          });
          break;
        }
        openPromiseWorkflow(action, {
          title: "Promise Approval Workflow",
          description: "Review the selected customer, account, promise amount, date, and current status before approving this promise.",
          status: "Ready",
          primaryActionLabel: "Confirm Approval",
          confirmationType: "approve-promise",
          confirmLabel: "Confirm Approval",
          consequence: "Confirming approves the selected promise through the existing Promise.Update workflow event. No payment is recorded and the account balance is unchanged.",
        });
        break;
      case "Close Promise":
        if (isPromiseAlreadyClosed(selectedPromise)) {
          openPromiseWorkflow(action, {
            title: "Promise Already Closed",
            description: "This promise has already been closed. No additional Promise.Cancel event will be published.",
            status: "Ready",
            primaryActionLabel: "Already Closed",
          });
          break;
        }
        if (!selectedPromise.id || !selectedPromise.accountId || !selectedPromise.customerNumber || !promiseWorkspace.accounts.some((account) => account.id === selectedPromise.accountId && account.customerNumber === selectedPromise.customerNumber)) {
          openPromiseWorkflow(action, {
            title: "Close Promise Unavailable",
            description: "This promise cannot be closed until its customer and account link is valid.",
            status: "Ready",
            primaryActionLabel: "Close Unavailable",
            errorMessage: "The selected promise is missing a valid customer/account link. Reopen a canonical promise record and try again.",
          });
          break;
        }
        openPromiseWorkflow(action, {
          title: "Close Promise Workflow",
          description: "Review the selected promise and enter the required closure reason before administratively closing the commitment.",
          status: "Ready",
          primaryActionLabel: "Confirm Close",
          confirmationType: "close-promise",
          confirmLabel: "Confirm Close",
          consequence: "Confirming administratively closes the selected promise through the existing Promise.Cancel workflow event. No payment is recorded, no payment history entry is created, the settlement schedule is preserved, and the account balance is unchanged.",
          resolutionNotes: "",
        });
        break;
      case "Escalate Broken Promise": {
        openPromiseWorkflow(action, {
          title: "Confirm Broken Promise Escalation",
          description: selectedPromise.status === "Broken"
            ? "Review this broken promise before routing it to Manager Command."
            : "Review this promise before routing it to Manager Command for escalation.",
          status: "Ready",
          primaryActionLabel: "Confirm Escalation",
          confirmationType: "escalate-broken-promise",
          confirmLabel: "Confirm Escalation",
          consequence: selectedPromise.status === "Broken"
            ? "Confirming publishes the existing broken-promise escalation event and creates the Manager Command escalation record."
            : "Confirming publishes the existing escalation event and creates the Manager Command escalation record.",
        });
        break;
      }
      default:
        openPromiseWorkflow(action, {
          title: `${action.label} workflow`,
          description: "This operational entry point is ready for the next workflow buildout.",
          status: "Coming Soon",
          primaryActionLabel: "Workflow Pending",
        });
    }
  }

  function updatePromisePaymentField(field: PromisePaymentField, value: string) {
    setActiveQuickAction((current) => (
      current
        ? {
            ...current,
            [field]: value,
            paymentStage: current.paymentStage === "success" ? current.paymentStage : "entry",
            confirmLabel: current.confirmationType === "receive-payment" ? "Review Payment" : current.confirmLabel,
            primaryActionLabel: current.confirmationType === "receive-payment" ? "Review Payment" : current.primaryActionLabel,
            errorMessage: undefined,
            paymentReviewSnapshot: undefined,
          }
        : current
    ));
  }

  function returnPromisePaymentToEntry() {
    setActiveQuickAction((current) => (
      current
        ? {
            ...current,
            paymentStage: "entry",
            confirmLabel: "Review Payment",
            primaryActionLabel: "Review Payment",
            errorMessage: undefined,
            paymentReviewSnapshot: undefined,
          }
        : current
    ));
  }

  function confirmPromiseAction(actionType: PromiseConfirmationType) {
    if (!activeQuickAction || activeQuickAction.confirmationType !== actionType) return;

    const promiseForAction = promiseWorkspace.promises.find((promise) => promise.id === activeQuickAction.promiseId)
      || workspace.promises.find((promise) => promise.id === activeQuickAction.promiseId);
    if (!promiseForAction) {
      setActiveQuickAction({
        ...activeQuickAction,
        errorMessage: "The selected promise could not be found. Reopen the promise and try again.",
      });
      return;
    }

    if (actionType === "receive-payment") {
      const canonicalState = resolveCanonicalPromisePaymentState({
        companyId: promiseWorkspace.company.id,
        promiseId: promiseForAction.id,
        fallbackWorkspace: promiseWorkspace,
      });
      const latestPromiseForAction = canonicalState.promise || promiseForAction;
      const accountForAction = canonicalState.account;
      const validation = validatePromisePaymentAction({
        action: activeQuickAction,
        selectedPromise: latestPromiseForAction,
        account: accountForAction,
        existingPayments: canonicalState.existingPayments,
        processedKeys: processedPromisePaymentKeysRef.current,
      });

      if (validation.errors.length) {
        setActiveQuickAction({
          ...activeQuickAction,
          isPending: false,
          errorMessage: validation.errors.join(" "),
        });
        return;
      }

      if (activeQuickAction.paymentStage !== "review") {
        if (!accountForAction) {
          setActiveQuickAction({
            ...activeQuickAction,
            isPending: false,
            errorMessage: "The selected promise is missing a valid customer/account link. Reopen a canonical promise record and try again.",
          });
          return;
        }
        setActiveQuickAction({
          ...activeQuickAction,
          paymentStage: "review",
          title: "Review Payment",
          description: "Review the payment details and balance impact before recording this payment.",
          primaryActionLabel: "Confirm Payment",
          confirmLabel: "Confirm Payment",
          errorMessage: undefined,
          paymentReviewSnapshot: buildPromisePaymentReviewSnapshot({
            action: activeQuickAction,
            promise: latestPromiseForAction,
            account: accountForAction,
          }),
        });
        return;
      }

      if (!accountForAction) {
        setActiveQuickAction({
          ...activeQuickAction,
          isPending: false,
          paymentStage: "entry",
          primaryActionLabel: "Review Payment",
          confirmLabel: "Review Payment",
          paymentReviewSnapshot: undefined,
          errorMessage: "The selected promise is missing a valid customer/account link. Reopen a canonical promise record and try again.",
        });
        return;
      }
      const freshnessError = validatePromisePaymentReviewFreshness({
        action: activeQuickAction,
        latestPromise: latestPromiseForAction,
        latestAccount: accountForAction,
        existingPayments: canonicalState.existingPayments,
      });
      if (freshnessError) {
        setActiveQuickAction({
          ...activeQuickAction,
          isPending: false,
          paymentStage: "entry",
          title: "Payment Review Needs Refresh",
          description: "Review the current promise and account balances before submitting this payment.",
          primaryActionLabel: "Review Payment",
          confirmLabel: "Review Payment",
          paymentReviewSnapshot: undefined,
          errorMessage: freshnessError,
        });
        return;
      }

      if (promiseConfirmationPendingRef.current) return;
      promiseConfirmationPendingRef.current = actionType;
      setActiveQuickAction({ ...activeQuickAction, isPending: true, errorMessage: undefined });

      const paymentKey = activeQuickAction.paymentIdempotencyKey || "";
      processedPromisePaymentKeysRef.current.add(paymentKey);
      try {
        if (!accountForAction) {
          throw new Error("The selected promise is missing a valid customer/account link. Reopen a canonical promise record and try again.");
        }
        const now = new Date().toISOString();
        const actor = latestPromiseForAction.collector || latestPromiseForAction.manager || "Promise Center";
        const paymentId = paymentKey || `promise-payment-${latestPromiseForAction.id}-${Date.now()}`;
        const previousPromiseBalanceCents = latestPromiseForAction.remainingBalanceCents;
        const previousAccountBalanceCents = accountForAction.openBalanceCents;
        const nextPromiseBalanceCents = Math.max(0, previousPromiseBalanceCents - validation.amountCents);
        const nextAccountBalanceCents = Math.max(0, previousAccountBalanceCents - validation.amountCents);
        const nextPromiseStatus = nextPromiseBalanceCents === 0 ? "Paid" : latestPromiseForAction.status;
        const paymentActivity = {
          id: `promise-act-${latestPromiseForAction.id}-payment-${Date.now()}`,
          kind: "Payment Received" as const,
          title: "Payment received",
          detail: [
            `Amount: ${formatARRecoveryCurrency(validation.amountCents)}`,
            `Payment Date: ${formatCapturedDate(activeQuickAction.paymentDate || "")}`,
            `Method: ${activeQuickAction.paymentMethod}`,
            `Reference: ${validation.reference}`,
            `Applied Account: ${latestPromiseForAction.accountId}`,
            `Applied Promise: ${latestPromiseForAction.id}`,
            `Actor: ${actor}`,
            "Status: Applied",
            `Remaining Promise Balance: ${formatARRecoveryCurrency(nextPromiseBalanceCents)}`,
            `Remaining Account Balance: ${formatARRecoveryCurrency(nextAccountBalanceCents)}`,
            validation.notes ? `Notes: ${validation.notes}` : "",
          ].filter(Boolean).join(" | "),
          createdAt: now,
        };
        const nextPromiseRecord = {
          ...buildPromiseWorkflowRecordFromExisting(latestPromiseForAction, accountForAction, now, actor),
          status: nextPromiseStatus as AccountPromiseRecord["status"],
          remainingBalanceCents: nextPromiseBalanceCents,
          updatedAt: now,
          paymentNotes: [
            latestPromiseForAction.paymentNotes,
            `Payment received ${formatARRecoveryCurrency(validation.amountCents)} on ${formatCapturedDate(activeQuickAction.paymentDate || "")} by ${activeQuickAction.paymentMethod}. Reference: ${validation.reference}.`,
            validation.notes,
          ].filter(Boolean).join("\n"),
          installmentSchedule: applyPromisePaymentToSchedule(latestPromiseForAction.installmentSchedule, validation.amountCents),
          activityFeed: [...latestPromiseForAction.activityFeed, paymentActivity],
        };

        const { promiseWorkflows: projectedPromiseWorkflows } = publishPromiseWorkflowAction({
          tenantId: promiseWorkspace.company.id,
          clientId: promiseWorkspace.company.id,
          customerId: latestPromiseForAction.customerNumber,
          accountId: latestPromiseForAction.accountId,
          customerName: latestPromiseForAction.customerName,
          customerNumber: latestPromiseForAction.customerNumber,
          promiseId: latestPromiseForAction.id,
          promiseRecord: nextPromiseRecord as unknown as Record<string, unknown>,
          action: nextPromiseStatus === "Paid" ? "Promise.Complete" : "Promise.Update",
          actor: {
            actorId: actor,
            actorName: actor,
            actorEmail: "",
            actorRole: "Promise Center",
            permissionLevel: "promise_payment",
          },
          sourceWorkspace: "Promise Center",
          initiatedFromRoute: "/admin/ar-recovery/promise-to-pay",
          previousValue: latestPromiseForAction,
        });
        const validProjectedPromiseWorkflows = projectedPromiseWorkflows
          .map((promise) => promise as unknown)
          .filter(isARRecoveryPromiseWorkflowRecord);
        const legacyPromiseWorkflows = loadStoredARRecoveryPromiseWorkflows();
        const projectedIds = new Set(validProjectedPromiseWorkflows.map((promise) => promise.id));
        const nextPromiseWorkflows = [...validProjectedPromiseWorkflows, ...legacyPromiseWorkflows.filter((promise) => !projectedIds.has(promise.id))]
          .sort((a, b) => getDateTime(b.updatedAt || b.createdAt) - getDateTime(a.updatedAt || a.createdAt));
        saveStoredARRecoveryPromiseWorkflows(nextPromiseWorkflows);
        publishPromiseCenterPaymentEvents({
          selectedPromise: latestPromiseForAction,
          workspace: promiseWorkspace,
          sourceWorkspace: "Promise Center",
          paymentId,
          amountCents: validation.amountCents,
          paymentDate: activeQuickAction.paymentDate || "",
          paymentMethod: activeQuickAction.paymentMethod as ARRecoveryPaymentMethodKey,
          paymentReference: validation.reference,
          paymentNotes: validation.notes,
          actorName: actor,
        });
        setPromiseWorkspace((current) => ({
          ...current,
          promises: current.promises.map((promise) => (promise.id === nextPromiseRecord.id ? nextPromiseRecord : promise)),
          accounts: current.accounts.map((account) => (
            account.id === accountForAction.id
              ? { ...account, openBalanceCents: nextAccountBalanceCents, updatedAt: now }
              : account
          )),
        }));
        setSelectedTab("payments");
        setActiveQuickAction({
          ...activeQuickAction,
          title: "Payment Recorded",
          description: "The payment was recorded once, applied to the promise and account, and synchronized to Payment History, Activity, and Timeline projections.",
          primaryActionLabel: "Payment Recorded",
          confirmationType: undefined,
          confirmLabel: undefined,
          consequence: undefined,
          isPending: false,
          errorMessage: undefined,
          paymentStage: "success",
          paymentResult: {
            paymentId,
            paymentReference: validation.reference,
            amountCents: validation.amountCents,
            paymentDate: activeQuickAction.paymentDate || "",
            paymentMethod: activeQuickAction.paymentMethod || "",
            previousPromiseBalanceCents,
            nextPromiseBalanceCents,
            previousAccountBalanceCents,
            nextAccountBalanceCents,
            nextPromiseStatus,
            recordedAt: now,
          },
        });
        return;
      } catch (error) {
        processedPromisePaymentKeysRef.current.delete(paymentKey);
        setActiveQuickAction({
          ...activeQuickAction,
          isPending: false,
          errorMessage: error instanceof Error ? error.message : "The payment could not be recorded. Review the promise and try again.",
        });
        return;
      } finally {
        promiseConfirmationPendingRef.current = null;
      }
    }

    if (promiseConfirmationPendingRef.current) return;
    promiseConfirmationPendingRef.current = actionType;
    setActiveQuickAction({ ...activeQuickAction, isPending: true, errorMessage: undefined });

    try {
      if (actionType === "approve-promise") {
        const latestPromiseForAction = promiseWorkspace.promises.find((promise) => promise.id === promiseForAction.id) || promiseForAction;
        if (isPromiseAlreadyApproved(latestPromiseForAction)) {
          setSelectedTab("activity");
          setActiveQuickAction({
            ...activeQuickAction,
            title: "Promise Already Approved",
            description: "This promise has already been approved. No additional Promise.Update event was published.",
            primaryActionLabel: "Already Approved",
            confirmationType: undefined,
            confirmLabel: undefined,
            consequence: undefined,
            isPending: false,
            errorMessage: undefined,
          });
          return;
        }

        const accountForAction = promiseWorkspace.accounts.find((account) => account.id === latestPromiseForAction.accountId && account.customerNumber === latestPromiseForAction.customerNumber);
        if (!accountForAction || !latestPromiseForAction.accountId || !latestPromiseForAction.customerNumber) {
          throw new Error("The selected promise is missing a valid customer/account link. Reopen a canonical promise record and try again.");
        }

        const now = new Date().toISOString();
        const approvedPromiseRecord = {
          ...buildPromiseWorkflowRecordFromExisting(latestPromiseForAction, accountForAction, now, latestPromiseForAction.manager || latestPromiseForAction.collector || "Promise Center"),
          status: "Open" as const,
          updatedAt: now,
          activityFeed: [
            ...latestPromiseForAction.activityFeed,
            {
              id: `promise-act-${promiseForAction.id}-approval-${Date.now()}`,
              kind: "Updated" as const,
              title: "Promise approved",
              detail: "Promise approved in Promise Center. No payment or balance change was recorded.",
              createdAt: now,
            },
          ],
        };
        const { promiseWorkflows: projectedPromiseWorkflows } = publishPromiseWorkflowAction({
          tenantId: promiseWorkspace.company.id,
          clientId: promiseWorkspace.company.id,
          customerId: latestPromiseForAction.customerNumber,
          accountId: latestPromiseForAction.accountId,
          customerName: latestPromiseForAction.customerName,
          customerNumber: latestPromiseForAction.customerNumber,
          promiseId: latestPromiseForAction.id,
          promiseRecord: approvedPromiseRecord as unknown as Record<string, unknown>,
          action: "Promise.Update",
          actor: {
            actorId: latestPromiseForAction.manager || latestPromiseForAction.collector || "Promise Center",
            actorName: latestPromiseForAction.manager || latestPromiseForAction.collector || "Promise Center",
            actorEmail: "",
            actorRole: "Promise Center",
            permissionLevel: "promise_approval",
          },
          sourceWorkspace: "Promise Center",
          initiatedFromRoute: "/admin/ar-recovery/promise-to-pay",
          previousValue: latestPromiseForAction,
        });
        const validProjectedPromiseWorkflows = projectedPromiseWorkflows
          .map((promise) => promise as unknown)
          .filter(isARRecoveryPromiseWorkflowRecord);
        const legacyPromiseWorkflows = loadStoredARRecoveryPromiseWorkflows();
        const projectedIds = new Set(validProjectedPromiseWorkflows.map((promise) => promise.id));
        const nextPromiseWorkflows = [...validProjectedPromiseWorkflows, ...legacyPromiseWorkflows.filter((promise) => !projectedIds.has(promise.id))]
          .sort((a, b) => getDateTime(b.updatedAt || b.createdAt) - getDateTime(a.updatedAt || a.createdAt));
        saveStoredARRecoveryPromiseWorkflows(nextPromiseWorkflows);
        setPromiseWorkspace((current) => ({
          ...current,
          promises: current.promises.map((promise) => (promise.id === approvedPromiseRecord.id ? approvedPromiseRecord : promise)),
        }));
        setActiveQuickAction({
          ...activeQuickAction,
          title: "Promise Approved",
          description: "The selected promise was approved and synchronized through the existing promise workflow event pipeline.",
          primaryActionLabel: "Promise Approved",
          confirmationType: undefined,
          confirmLabel: undefined,
          consequence: undefined,
          isPending: false,
          errorMessage: undefined,
        });
        return;
      }

      if (actionType === "close-promise") {
        const latestPromiseForAction = promiseWorkspace.promises.find((promise) => promise.id === promiseForAction.id) || promiseForAction;
        if (isPromiseAlreadyClosed(latestPromiseForAction)) {
          setActiveQuickAction({
            ...activeQuickAction,
            title: "Promise Already Closed",
            description: "This promise has already been closed. No additional Promise.Cancel event was published.",
            primaryActionLabel: "Already Closed",
            confirmationType: undefined,
            confirmLabel: undefined,
            consequence: undefined,
            isPending: false,
            errorMessage: undefined,
            resolutionNotes: undefined,
          });
          return;
        }

        const closureReason = activeQuickAction.resolutionNotes?.trim() || "";
        if (!closureReason) {
          setActiveQuickAction({
            ...activeQuickAction,
            isPending: false,
            errorMessage: "A closure reason is required before closing this promise.",
          });
          return;
        }

        const accountForAction = promiseWorkspace.accounts.find((account) => account.id === latestPromiseForAction.accountId && account.customerNumber === latestPromiseForAction.customerNumber);
        if (!accountForAction || !latestPromiseForAction.accountId || !latestPromiseForAction.customerNumber) {
          throw new Error("The selected promise is missing a valid customer/account link. Reopen a canonical promise record and try again.");
        }

        const now = new Date().toISOString();
        const completedPromiseRecord = {
          ...buildPromiseWorkflowRecordFromExisting(latestPromiseForAction, accountForAction, now, latestPromiseForAction.manager || latestPromiseForAction.collector || "Promise Center"),
          status: "Cancelled" as const,
          completionNotes: closureReason,
          outcome: "Promise cancelled",
          updatedAt: now,
          activityFeed: [
            ...latestPromiseForAction.activityFeed,
            {
              id: `promise-act-${latestPromiseForAction.id}-cancel-${Date.now()}`,
              kind: "Updated" as const,
              title: "Promise cancelled",
              detail: closureReason,
              createdAt: now,
            },
          ],
        };
        const { promiseWorkflows: projectedPromiseWorkflows } = publishPromiseWorkflowAction({
          tenantId: promiseWorkspace.company.id,
          clientId: promiseWorkspace.company.id,
          customerId: latestPromiseForAction.customerNumber,
          accountId: latestPromiseForAction.accountId,
          customerName: latestPromiseForAction.customerName,
          customerNumber: latestPromiseForAction.customerNumber,
          promiseId: latestPromiseForAction.id,
          promiseRecord: completedPromiseRecord as unknown as Record<string, unknown>,
          action: "Promise.Cancel",
          actor: {
            actorId: latestPromiseForAction.manager || latestPromiseForAction.collector || "Promise Center",
            actorName: latestPromiseForAction.manager || latestPromiseForAction.collector || "Promise Center",
            actorEmail: "",
            actorRole: "Promise Center",
            permissionLevel: "promise_closure",
          },
          sourceWorkspace: "Promise Center",
          initiatedFromRoute: "/admin/ar-recovery/promise-to-pay",
          previousValue: latestPromiseForAction,
        });
        const validProjectedPromiseWorkflows = projectedPromiseWorkflows
          .map((promise) => promise as unknown)
          .filter(isARRecoveryPromiseWorkflowRecord);
        const legacyPromiseWorkflows = loadStoredARRecoveryPromiseWorkflows();
        const projectedIds = new Set(validProjectedPromiseWorkflows.map((promise) => promise.id));
        const nextPromiseWorkflows = [...validProjectedPromiseWorkflows, ...legacyPromiseWorkflows.filter((promise) => !projectedIds.has(promise.id))]
          .sort((a, b) => getDateTime(b.updatedAt || b.createdAt) - getDateTime(a.updatedAt || a.createdAt));
        saveStoredARRecoveryPromiseWorkflows(nextPromiseWorkflows);
        setPromiseWorkspace((current) => ({
          ...current,
          promises: current.promises.map((promise) => (promise.id === completedPromiseRecord.id ? completedPromiseRecord : promise)),
        }));
        setActiveQuickAction({
          ...activeQuickAction,
          title: "Promise Closed",
          description: "The selected promise was administratively closed and synchronized through the existing promise cancellation event pipeline.",
          primaryActionLabel: "Promise Closed",
          confirmationType: undefined,
          confirmLabel: undefined,
          consequence: undefined,
          isPending: false,
          errorMessage: undefined,
          resolutionNotes: undefined,
        });
        return;
      }

      publishPromiseCenterEscalationEvent(promiseForAction, workspace, "Promise Center");
      setActiveQuickAction({
        ...activeQuickAction,
        title: "Escalation Recorded",
        description: "The existing broken-promise escalation event was recorded and routed to Manager Command.",
        primaryActionLabel: "Escalation Recorded",
        confirmationType: undefined,
        confirmLabel: undefined,
        consequence: undefined,
        isPending: false,
        errorMessage: undefined,
      });
    } catch (error) {
      setActiveQuickAction({
        ...activeQuickAction,
        isPending: false,
        errorMessage: error instanceof Error ? error.message : "The action could not be completed. Review the promise and try again.",
      });
    } finally {
      promiseConfirmationPendingRef.current = null;
    }
  }

  return (
    <div
      className="ar-workspace-os mt-4 grid min-w-0 max-w-full gap-4 overflow-hidden"
      data-promise-enterprise-fields={PROMISE_CENTER_ENTERPRISE_FIELDS.join(",")}
      data-promise-lifecycle-statuses={PROMISE_CENTER_LIFECYCLE_STATUSES.join(",")}
      data-promise-future-operations={PROMISE_CENTER_FUTURE_OPERATIONS.join(",")}
      data-promise-filter-model={PROMISE_CENTER_FILTER_MODEL.join(",")}
      data-promise-reporting-model={PROMISE_CENTER_REPORTING_MODEL.join(",")}
      data-promise-activity-events={PROMISE_CENTER_ACTIVITY_EVENTS.join(",")}
    >
      <section className="grid min-w-0 max-w-full gap-4 overflow-hidden">
        <article className="min-w-0 overflow-hidden rounded-[1.75rem] border border-[rgba(34,224,208,.16)] bg-white p-5 shadow-[0_20px_48px_rgba(0,0,0,.08)]">
          <SectionHeading
            eyebrow="Promise Queue"
            title="Promise records"
            description="Enterprise promise management built for collectors, managers, and future client portal reuse."
          />
          <label className="relative mt-5 block min-w-0 max-w-full">
            <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#14B8A6]" aria-hidden="true" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search customer, collector, manager, status, notes, or invoice"
              className="focus-ring w-full rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] py-3 pl-11 pr-4 text-sm font-semibold text-[#0A2540] placeholder:text-[#64748B]"
            />
          </label>

          <div className="ar-ia-scroll mt-5 flex w-full max-w-full gap-3 overflow-x-auto overscroll-x-contain pb-2">
            {snapshot.filteredPromises.map((promise) => {
              const active = promise.id === selectedPromise?.id;
              return (
                <button
                  key={promise.id}
                  type="button"
                  onClick={() => {
                    setSelectedPromiseId(promise.id);
                    setSelectedTab("overview");
                  }}
                  className={`min-w-[16rem] max-w-[calc(100vw-4rem)] shrink-0 rounded-3xl border p-4 text-left transition hover:-translate-y-0.5 sm:min-w-[18rem] ${
                    active
                      ? "border-[#14B8A6] bg-[#F4FBFF]"
                      : "border-[#BFEAFF] bg-white hover:border-[#14B8A6] hover:bg-[#F4FBFF]"
                  }`}
                >
                  <div className="flex min-w-0 items-start justify-between gap-3">
                    <div className="flex min-w-0 items-start gap-3">
                      <ARLightLineIcon icon={<ClipboardList className="size-5" aria-hidden="true" />} />
                      <div className="min-w-0">
                        <p className="truncate text-xs font-semibold uppercase tracking-[0.14em] text-[#FF6A2B]">{promise.customerNumber}</p>
                        <h3 className="mt-1 truncate text-lg font-semibold text-[#0A2540]">{promise.customerName}</h3>
                      </div>
                    </div>
                    <span className="shrink-0 rounded-full border border-[#BFEAFF] bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#14B8A6]">
                      {promise.status}
                    </span>
                  </div>
                  <div className="mt-3 grid min-w-0 gap-2 text-sm font-normal leading-6 text-[#475569]">
                    <p>Promise type: {promise.promiseType}</p>
                    <p>Collector: {promise.collector}</p>
                    <p>Manager: {promise.manager}</p>
                    <p>Promise amount: {formatARRecoveryCurrency(promise.amountCents)}</p>
                    <p>Remaining balance: {formatARRecoveryCurrency(promise.remainingBalanceCents)}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </article>

        <article ref={promiseReturnFocusRef} tabIndex={-1} className="min-w-0 overflow-hidden rounded-[1.75rem] border border-[rgba(255,106,43,.16)] bg-white p-5 shadow-[0_20px_48px_rgba(0,0,0,.08)] outline-none">
          <SectionHeading
            eyebrow="Promise Detail"
            title={selectedPromise ? selectedPromise.customerName : "Select a promise"}
            description="Promise information, workflow controls, and related records all stay scoped to the active tenant."
          />

          {selectedPromise ? (
            <div className="mt-5 grid min-w-0 max-w-full gap-5 overflow-hidden">
              <div className="min-w-0 overflow-hidden rounded-[1.5rem] border border-[#BFEAFF] bg-[#F4FBFF] p-4">
                <p className="flex items-center gap-2 text-sm font-semibold text-[#0A2540]">
                  <ARLightLineIcon icon={<ShieldCheck className="size-5" aria-hidden="true" />} />
                  Promise Governance
                </p>
                <div className="mt-3 grid min-w-0 gap-3 md:grid-cols-[repeat(3,minmax(0,1fr))]">
                  <PromiseScopeRow label="Status" value={selectedPromise.status} />
                  <PromiseScopeRow label="Collector" value={selectedPromise.collector} />
                  <PromiseScopeRow label="Manager" value={selectedPromise.manager} />
                </div>
              </div>

              <div className="grid min-w-0 gap-3 md:grid-cols-[repeat(2,minmax(0,1fr))] xl:grid-cols-[repeat(4,minmax(0,1fr))]">
                <DetailMetric label="Promise amount" value={formatARRecoveryCurrency(selectedPromise.amountCents)} icon={<CircleDollarSign className="size-5" aria-hidden="true" />} />
                <DetailMetric label="Remaining balance" value={formatARRecoveryCurrency(selectedPromise.remainingBalanceCents)} icon={<CircleDollarSign className="size-5" aria-hidden="true" />} />
                <DetailMetric label="Promise date" value={formatDate(selectedPromise.promiseDate)} icon={<Clock3 className="size-5" aria-hidden="true" />} />
                <DetailMetric label="Status" value={selectedPromise.status} icon={<ShieldCheck className="size-5" aria-hidden="true" />} />
                <DetailMetric label="Promise type" value={selectedPromise.promiseType} icon={<ClipboardList className="size-5" aria-hidden="true" />} />
                <DetailMetric label="Collector" value={selectedPromise.collector} icon={<Users className="size-5" aria-hidden="true" />} />
                <DetailMetric label="Manager" value={selectedPromise.manager} icon={<ShieldCheck className="size-5" aria-hidden="true" />} />
                <DetailMetric label="Risk score" value={String(buildPromiseRiskScore(selectedPromise))} icon={<Target className="size-5" aria-hidden="true" />} />
              </div>

              <div className="min-w-0 overflow-hidden rounded-[1.5rem] border border-[#BFEAFF] bg-[#F4FBFF] p-4">
                <p className="flex items-center gap-2 text-sm font-semibold text-[#0A2540]">
                  <ARLightLineIcon icon={<Bolt className="size-5" aria-hidden="true" />} />
                  Quick Actions
                </p>
                <div className="mt-3 grid min-w-0 gap-3 md:grid-cols-[repeat(2,minmax(0,1fr))] xl:grid-cols-[repeat(3,minmax(0,1fr))]">
                  {selectedPromiseActions.map((action) => (
                    <PromiseActionCard
                      key={action.label}
                      label={action.label}
                      note={action.description}
                      icon={promiseActionIcon(action.label)}
                      onAction={() => handlePromiseQuickAction(action)}
                      disabled={action.disabled}
                      statusLabel={action.statusLabel}
                      disabledReason={action.disabledReason}
                    />
                  ))}
                </div>
                {activeQuickAction ? (
                  <PromiseQuickActionPanel
                    action={activeQuickAction}
                    onClose={() => setActiveQuickAction(null)}
                    onConfirm={activeQuickAction.confirmationType ? () => confirmPromiseAction(activeQuickAction.confirmationType!) : undefined}
                    onResolutionNotesChange={(resolutionNotes) => setActiveQuickAction((current) => (current ? { ...current, resolutionNotes, errorMessage: undefined } : current))}
                    onPaymentFieldChange={updatePromisePaymentField}
                    onPaymentEdit={returnPromisePaymentToEntry}
                  />
                ) : null}
              </div>

              <div className="flex min-w-0 max-w-full flex-wrap gap-2 overflow-hidden">
                {snapshot.tabLabels.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setSelectedTab(tab.key)}
                    className={`max-w-full rounded-full border px-4 py-2 text-xs font-black uppercase tracking-[0.14em] transition ${
                      selectedTab === tab.key
                        ? "border-[#14B8A6] bg-[#F4FBFF] text-[#0A2540]"
                        : "border-[#BFEAFF] bg-white text-[#475569] hover:border-[#14B8A6] hover:text-[#0A2540]"
                    }`}
                    title={tab.description}
                  >
                    <span className="mr-2 inline-flex align-middle">
                      <ARLightLineIcon compact icon={promiseTabIcon(tab.key)} />
                    </span>
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="min-w-0 overflow-hidden rounded-[1.75rem] border border-[#BFEAFF] bg-white p-4">
                {selectedPromise ? renderPromiseTab(selectedPromise, selectedTab) : null}
              </div>
            </div>
          ) : (
            <div className="mt-5 rounded-3xl border border-dashed border-[#BFEAFF] bg-[#F4FBFF] p-6 text-sm font-semibold leading-7 text-[#0A2540]">
              No promise matched the current search. Broaden the search or clear it to see the active queue.
            </div>
          )}
        </article>
      </section>

    </div>
  );
}

const LETTER_CENTER_ENTERPRISE_FIELDS = ["Letter ID", "Customer", "Account", "Portfolio", "Client", "Collector", "Letter Type", "Template", "Status", "Delivery Method", "Generated Date", "Generated By", "Sent Date", "Delivery Status", "Related Promise", "Related Tickler", "Related Statement", "Related Recovery Activity", "Notes", "Attachments", "Audit History"] as const;
const LETTER_CENTER_LIFECYCLE_STATUSES = ["Draft", "Generated", "Queued", "Sent", "Delivered", "Viewed", "Returned", "Failed", "Cancelled", "Archived"] as const;
const LETTER_CENTER_DOCUMENT_PRODUCTION_MODEL = ["Letter Templates", "Merge Fields", "Individual Generation", "Batch Generation", "PDF Rendering", "Print Queue", "Preview", "Template Versioning", "Approval Workflow"] as const;
const LETTER_CENTER_OPERATIONS_MODEL = ["Generate Letter", "Preview", "Print", "Email", "Download PDF", "Regenerate", "Cancel", "Resend", "Archive", "View History", "Open Customer", "Open Account", "Open Recovery Activity"] as const;
const LETTER_CENTER_FILTER_MODEL = ["Collector", "Client", "Portfolio", "Letter Type", "Template", "Status", "Delivery Status", "Generated Date", "Date Range", "Customer", "Account", "Search"] as const;
const LETTER_CENTER_REPORTING_MODEL = ["Letters Generated", "Letters Sent", "Delivery Success", "Delivery Failures", "Returned Mail", "Collector Activity", "Client Activity", "Portfolio Activity", "Letter Volume"] as const;
const LETTER_CENTER_ACTIVITY_EVENTS = ["Generated", "Printed", "Sent", "Delivered", "Viewed", "Returned", "Failed", "Cancelled", "Archived", "Notes Added"] as const;
const LETTER_CENTER_WORKFLOW_INTEGRATIONS = ["Recovery Workbench", "Promise Center", "Tickler Center", "Statement Center", "Payments", "Customers & Accounts", "Client Management", "Manager Command", "Timeline", "Recovery Activity"] as const;
const LETTER_CENTER_COMMUNICATIONS_ARCHITECTURE = ["Microsoft Outlook", "Microsoft Exchange Online", "Microsoft 365 Mail", "Google Workspace Gmail", "SMTP", "Nextiva", "RingCentral", "Zoom Phone", "Microsoft Teams", "Slack", "Secure Client Portal", "PDF Download", "Email Delivery", "Print Delivery", "Portal Delivery", "Teams Notification", "Manager Notification", "Client Notification"] as const;

function CollectionLetterSection({
  workspace,
}: {
  workspace: ReturnType<typeof loadARRecoveryWorkspace>;
}) {
  const location = useLocation();
  const promiseReturnContext = useMemo(() => getPromiseCenterReturnContext(location.search), [location.search]);
  const [search, setSearch] = useState("");
  const [selectedLetterId, setSelectedLetterId] = useState(workspace.collectionLetters[0]?.id || "");
  const [selectedTab, setSelectedTab] = useState<ARRecoveryLetterTabKey>("overview");
  const [activeLetterAction, setActiveLetterAction] = useState<string | null>(null);
  const snapshot = useMemo(
    () => buildARRecoveryCollectionLetterCenterSnapshot(workspace.company.id, search, selectedLetterId, selectedTab),
    [search, selectedLetterId, selectedTab, workspace.company.id],
  );
  const selectedLetter = snapshot.selectedLetter;
  const activeLetter = selectedLetter ?? snapshot.filteredLetters[0] ?? null;

  useEffect(() => {
    if (!selectedLetterId && snapshot.filteredLetters[0]) {
      setSelectedLetterId(snapshot.filteredLetters[0].id);
    }
  }, [selectedLetterId, snapshot.filteredLetters]);

  useEffect(() => {
    if (selectedLetterId && !snapshot.filteredLetters.some((letter) => letter.id === selectedLetterId)) {
      setSelectedLetterId(snapshot.filteredLetters[0]?.id || "");
    }
  }, [selectedLetterId, snapshot.filteredLetters]);

  function handleLetterAction(action: string) {
    if (activeLetter) {
      const workflowAction = resolveLetterWorkflowAction(action);
      if (workflowAction) {
        publishDocumentWorkflowAction({
          tenantId: workspace.company.id,
          clientId: workspace.company.id,
          customerId: activeLetter.customerNumber,
          accountId: activeLetter.accountId,
          customerName: activeLetter.customerName,
          customerNumber: activeLetter.customerNumber,
          documentId: activeLetter.id,
          documentType: "letter",
          documentRecord: buildLetterDocumentRecordForAction(activeLetter, action),
          action: workflowAction,
          actor: buildLetterCenterActorContext(activeLetter),
          sourceWorkspace: "Letter Center",
          initiatedFromRoute: "/admin/ar-recovery/collection-letters",
          deliveryStatus: resolveLetterDeliveryStatusForAction(action, activeLetter.deliveryStatus),
        });
      }
    }
    setActiveLetterAction(action);
    if (action.includes("Tracking")) setSelectedTab("tracking");
    if (action.includes("Bulk") || action.includes("Batch") || action.includes("Multiple") || action.includes("Export")) setSelectedTab("bulk");
    if (action.includes("Edit")) setSelectedTab("template");
    if (action.includes("Archive")) setSelectedTab("history");
  }

  return (
    <div
      className="ar-workspace-os mt-4 grid min-w-0 max-w-full gap-4 overflow-hidden"
      data-letter-enterprise-fields={LETTER_CENTER_ENTERPRISE_FIELDS.join(",")}
      data-letter-lifecycle-statuses={LETTER_CENTER_LIFECYCLE_STATUSES.join(",")}
      data-letter-document-production-model={LETTER_CENTER_DOCUMENT_PRODUCTION_MODEL.join(",")}
      data-letter-operations-model={LETTER_CENTER_OPERATIONS_MODEL.join(",")}
      data-letter-filter-model={LETTER_CENTER_FILTER_MODEL.join(",")}
      data-letter-reporting-model={LETTER_CENTER_REPORTING_MODEL.join(",")}
      data-letter-activity-events={LETTER_CENTER_ACTIVITY_EVENTS.join(",")}
      data-letter-workflow-integrations={LETTER_CENTER_WORKFLOW_INTEGRATIONS.join(",")}
      data-letter-communications-architecture={LETTER_CENTER_COMMUNICATIONS_ARCHITECTURE.join(",")}
    >
      <PromiseCenterReturnControl context={promiseReturnContext} />

      <section className="grid min-w-0 max-w-full gap-4 md:grid-cols-[repeat(2,minmax(0,1fr))] xl:grid-cols-[repeat(4,minmax(0,1fr))]">
        {snapshot.dashboardCards.map((card) => (
          <article key={card.label} className="min-w-0 max-w-full overflow-hidden rounded-[1.75rem] border border-[rgba(255,106,43,.16)] bg-[rgba(7,18,31,.76)] p-5 shadow-[0_20px_48px_rgba(0,0,0,.18)]">
            <p className="break-words text-xs font-black uppercase tracking-[0.18em] text-[#FF6A2B]">{card.label}</p>
            <p className="mt-2 break-words font-display text-3xl font-bold text-[#F6F1E8]">{card.value}</p>
            <p className="mt-3 break-words text-sm font-semibold leading-7 text-[#E8EEF4]">{card.note}</p>
          </article>
        ))}
      </section>

      <section className="grid min-w-0 max-w-full gap-4 overflow-hidden">
        <article className="min-w-0 max-w-full overflow-hidden rounded-[1.75rem] border border-[rgba(34,224,208,.16)] bg-white p-5 shadow-[0_20px_48px_rgba(0,0,0,.08)]">
          <SectionHeading
            eyebrow="Collection Letter Center"
            title="Letter queue"
            description="Company-branded collection letters with permanent tracking and bulk-ready workflows."
          />
          <label className="relative mt-5 block min-w-0 max-w-full">
            <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#FF6A2B]" aria-hidden="true" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search customer, template, collector, status, or notes"
              className="focus-ring w-full max-w-full min-w-0 rounded-2xl border border-[rgba(255,106,43,.16)] bg-[rgba(255,255,255,.04)] py-3 pl-11 pr-4 text-sm font-semibold text-[#F6F1E8]"
            />
          </label>

          <div className="ar-ia-scroll mt-5 flex w-full max-w-full gap-3 overflow-x-auto overscroll-x-contain pb-2">
            {snapshot.filteredLetters.map((letter) => {
              const active = letter.id === activeLetter?.id;
              return (
                <button
                  key={letter.id}
                  type="button"
                  onClick={() => {
                    setSelectedLetterId(letter.id);
                    setSelectedTab("overview");
                  }}
                  className={`min-w-[16rem] max-w-[calc(100vw-4rem)] shrink-0 overflow-hidden rounded-3xl border p-4 text-left transition hover:-translate-y-0.5 sm:min-w-[18rem] ${
                    active
                      ? "border-[rgba(255,106,43,.42)] bg-[rgba(255,106,43,.12)]"
                      : "border-[rgba(34,224,208,.14)] bg-[rgba(255,255,255,.03)] hover:border-[rgba(34,224,208,.28)]"
                  }`}
                >
                  <div className="flex min-w-0 items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="break-words text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">{letter.customerNumber}</p>
                      <h3 className="mt-1 break-words font-display text-2xl font-bold text-[#F6F1E8]">{letter.customerName}</h3>
                    </div>
                    <span className="shrink-0 rounded-full border border-[rgba(34,224,208,.2)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#22E0D0]">
                      {letter.status}
                    </span>
                  </div>
                  <div className="mt-3 grid min-w-0 gap-2 text-sm font-semibold leading-7 text-[#E8EEF4]">
                    <p className="break-words">Template: {letter.template}</p>
                    <p className="break-words">Collector: {letter.collector}</p>
                    <p className="break-words">Delivery status: {letter.deliveryStatus}</p>
                    <p className="break-words">Balance: {formatARRecoveryCurrency(letter.balanceCents)}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </article>

        <article className="min-w-0 max-w-full overflow-hidden rounded-[1.75rem] border border-[rgba(255,106,43,.16)] bg-white p-5 shadow-[0_20px_48px_rgba(0,0,0,.08)]">
          <SectionHeading
            eyebrow="Letter Detail"
            title={activeLetter ? activeLetter.customerName : "Select a letter"}
            description="Selected correspondence, operational actions, and delivery tracking stay tied to the active letter."
          />

          {activeLetter ? (
            <div className="mt-5 grid min-w-0 max-w-full gap-5 overflow-hidden">
              <div className="grid min-w-0 gap-3 md:grid-cols-[repeat(2,minmax(0,1fr))] xl:grid-cols-[repeat(4,minmax(0,1fr))]">
                <DetailMetric label="Template" value={activeLetter.template} />
                <DetailMetric label="Status" value={activeLetter.status} />
                <DetailMetric label="Sent by" value={activeLetter.sentBy} />
                <DetailMetric label="Sent date" value={activeLetter.dateSent ? formatDate(activeLetter.dateSent) : "Not sent"} />
                <DetailMetric label="Delivery" value={activeLetter.deliveryStatus} />
                <DetailMetric label="Viewed" value={activeLetter.viewedStatus} />
                <DetailMetric label="Downloaded" value={activeLetter.downloadedStatus} />
                <DetailMetric label="Balance" value={formatARRecoveryCurrency(activeLetter.balanceCents)} />
              </div>

              <div className="min-w-0 max-w-full overflow-hidden rounded-[1.5rem] border border-[#BFEAFF] bg-[#F4FBFF] p-4">
                <p className="flex items-center gap-2 text-sm font-semibold text-[#0A2540]">
                  <ARLightLineIcon icon={<Bolt className="size-5" aria-hidden="true" />} />
                  Letter Actions
                </p>
                <div className="mt-4 grid min-w-0 gap-3 md:grid-cols-[repeat(2,minmax(0,1fr))] xl:grid-cols-[repeat(4,minmax(0,1fr))]">
                  {getSingleLetterActions().map((action) => (
                    <LetterActionButton
                      key={action.label}
                      label={action.label}
                      note={action.note}
                      onClick={() => handleLetterAction(action.label)}
                    />
                  ))}
                </div>
              </div>

              <div className="flex min-w-0 max-w-full flex-wrap gap-2">
                {snapshot.tabLabels.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setSelectedTab(tab.key)}
                    className={`rounded-full border px-4 py-2 text-xs font-black uppercase tracking-[0.14em] transition ${
                      selectedTab === tab.key
                        ? "border-[rgba(255,106,43,.46)] bg-[rgba(255,106,43,.14)] text-[#F6F1E8]"
                        : "border-[rgba(34,224,208,.2)] bg-[rgba(255,255,255,.04)] text-[#CBD5E1] hover:border-[rgba(34,224,208,.34)]"
                    }`}
                    title={tab.description}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="min-w-0 max-w-full overflow-hidden rounded-[1.75rem] border border-[#BFEAFF] bg-white p-4">
                {renderCollectionLetterTab(snapshot, activeLetter, selectedTab, handleLetterAction)}
              </div>

              {activeLetterAction ? <LetterActionPanel action={activeLetterAction} letter={activeLetter} onClose={() => setActiveLetterAction(null)} /> : null}
            </div>
          ) : (
            <div className="mt-5 rounded-3xl border border-dashed border-[rgba(34,224,208,.16)] bg-[rgba(255,255,255,.03)] p-6 text-sm font-semibold leading-7 text-[#E8EEF4]">
              No collection letter matched the current search. Broaden the search or clear it to view the queue.
            </div>
          )}
        </article>
      </section>

      <section className="grid min-w-0 max-w-full items-start gap-6 overflow-hidden xl:grid-cols-[minmax(0,1.02fr)_minmax(0,.98fr)]">
        <article className="min-w-0 max-w-full overflow-hidden rounded-[1.75rem] border border-[rgba(255,106,43,.16)] bg-[rgba(7,18,31,.78)] p-5 shadow-[0_20px_48px_rgba(0,0,0,.18)]">
          <SectionHeading
            eyebrow="Bulk Actions"
            title="Send at scale"
            description="Multi-letter actions for high-volume teams and future automation hooks."
          />
          <div className="mt-5 grid min-w-0 gap-3 sm:grid-cols-[repeat(2,minmax(0,1fr))]">
            {getBulkLetterActions().map((action) => (
              <LetterActionButton
                key={action.label}
                label={action.label}
                note={action.note}
                onClick={() => handleLetterAction(action.label)}
              />
            ))}
          </div>
        </article>

        <article className="min-w-0 max-w-full overflow-hidden rounded-[1.75rem] border border-[rgba(255,106,43,.16)] bg-[rgba(7,18,31,.78)] p-5 shadow-[0_20px_48px_rgba(0,0,0,.18)]">
          <SectionHeading
            eyebrow="Activity Feed"
            title="Permanent letter timeline"
            description="Letter generation and send activity are stored as a chronological operational feed."
          />
          <div className="mt-5 grid min-w-0 gap-3">
            {snapshot.activityFeed.map((entry) => (
              <div key={entry.id} className="min-w-0 max-w-full overflow-hidden rounded-2xl border border-[rgba(34,224,208,.14)] bg-[rgba(255,255,255,.03)] p-4">
                <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
                  <p className="break-words text-xs font-black uppercase tracking-[0.16em] text-[#22E0D0]">{formatDate(entry.createdAt)}</p>
                  <span className="shrink-0 rounded-full border border-[rgba(255,106,43,.2)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#FF6A2B]">
                    Letter
                  </span>
                </div>
                <h3 className="mt-2 break-words font-display text-2xl font-bold text-[#F6F1E8]">{entry.title}</h3>
                <p className="mt-2 break-words text-sm font-semibold leading-7 text-[#E8EEF4]">{entry.detail}</p>
              </div>
            ))}
          </div>
        </article>
      </section>
    </div>
  );
}

const STATEMENT_CENTER_ENTERPRISE_FIELDS = ["Statement ID", "Customer", "Account", "Portfolio", "Client", "Collector", "Statement Type", "Template", "Status", "Delivery Method", "Statement Date", "Billing Period", "Generated Date", "Generated By", "Sent Date", "Delivery Status", "Related Promise", "Related Tickler", "Related Letter", "Related Recovery Activity", "Notes", "Attachments", "Audit History"] as const;
const STATEMENT_CENTER_LIFECYCLE_STATUSES = ["Draft", "Generated", "Queued", "Sent", "Delivered", "Viewed", "Returned", "Failed", "Cancelled", "Archived"] as const;
const STATEMENT_CENTER_PRODUCTION_MODEL = ["Statement Templates", "Merge Fields", "Individual Statements", "Batch Statements", "Scheduled Statements", "PDF Generation", "Print Queue", "Preview", "Template Versioning", "Approval Workflow"] as const;
const STATEMENT_CENTER_OPERATIONS_MODEL = ["Generate Statement", "Preview", "Print", "Email", "Download PDF", "Regenerate", "Cancel", "Resend", "Archive", "View History", "Open Customer", "Open Account", "Open Recovery Activity"] as const;
const STATEMENT_CENTER_FILTER_MODEL = ["Collector", "Client", "Portfolio", "Statement Type", "Template", "Status", "Delivery Status", "Statement Date", "Date Range", "Customer", "Account", "Search"] as const;
const STATEMENT_CENTER_REPORTING_MODEL = ["Statements Generated", "Statements Sent", "Delivery Success", "Delivery Failures", "Statement Views", "Collector Activity", "Client Activity", "Portfolio Activity", "Statement Volume"] as const;
const STATEMENT_CENTER_ACTIVITY_EVENTS = ["Generated", "Printed", "Sent", "Delivered", "Viewed", "Returned", "Failed", "Cancelled", "Archived", "Notes Added"] as const;
const STATEMENT_CENTER_WORKFLOW_INTEGRATIONS = ["Recovery Workbench", "Promise Center", "Tickler Center", "Letter Center", "Payments", "Customers & Accounts", "Client Management", "Manager Command", "Timeline", "Recovery Activity"] as const;
const STATEMENT_CENTER_COMMUNICATIONS_ARCHITECTURE = ["Microsoft Outlook", "Microsoft Exchange Online", "Microsoft 365 Mail", "Google Workspace Gmail", "SMTP", "Nextiva", "RingCentral", "Zoom Phone", "Secure Client Portal", "PDF Download", "Email Delivery", "Print Delivery", "Portal Delivery", "Manager Notification", "Client Notification"] as const;
type StatementCenterRecord = ReturnType<typeof loadARRecoveryWorkspace>["statements"][number];
type StatementBulkStep = "select" | "review" | "sending" | "results";
type StatementBulkResult = {
  statementId: string;
  statementNumber: string;
  customerName: string;
  recipient: string;
  status: "success" | "failed" | "skipped";
  reason: string;
  providerMessageId?: string | null;
};
type StatementBulkTimelineEntry = {
  id: string;
  title: string;
  detail: string;
  createdAt: string;
};
type StatementCsvExportStep = "select" | "review" | "generating" | "results";
type StatementCsvExportResult = {
  statementId: string;
  statementNumber: string;
  customerName: string;
  status: "exported" | "skipped";
  reason: string;
};
type StatementPrintBatchStep = "select" | "review" | "generating" | "results";
type StatementPrintBatchResult = {
  statementId: string;
  statementNumber: string;
  customerName: string;
  status: "opened" | "skipped";
  reason: string;
};

function StatementCenterSection({
  workspace,
}: {
  workspace: ReturnType<typeof loadARRecoveryWorkspace>;
}) {
  const location = useLocation();
  const promiseReturnContext = useMemo(() => getPromiseCenterReturnContext(location.search), [location.search]);
  const [search, setSearch] = useState("");
  const [selectedStatementId, setSelectedStatementId] = useState(workspace.statements[0]?.id || "");
  const [selectedTab, setSelectedTab] = useState<ARRecoveryLetterTabKey>("overview");
  const [activeStatementAction, setActiveStatementAction] = useState<string | null>(null);
  const [statementActor, setStatementActor] = useState<ARRecoveryActor>(() => buildFallbackARRecoveryActor());
  const [statementOverrides, setStatementOverrides] = useState<Record<string, StatementCenterRecord>>({});
  const [bulkTimelineEntries, setBulkTimelineEntries] = useState<StatementBulkTimelineEntry[]>([]);
  const snapshot = useMemo(
    () => buildARRecoveryStatementCenterSnapshot(workspace.company.id, search, selectedStatementId, selectedTab, workspace),
    [search, selectedStatementId, selectedTab, workspace],
  );
  const filteredStatements = useMemo(
    () => snapshot.filteredStatements.map((statement) => statementOverrides[statement.id] || statement),
    [snapshot.filteredStatements, statementOverrides],
  );
  const selectedStatement = filteredStatements.find((statement) => statement.id === selectedStatementId) || null;
  const activeStatement = selectedStatement ?? filteredStatements[0] ?? null;
  const statementActivityFeed = useMemo(
    () => [...bulkTimelineEntries, ...snapshot.activityFeed].sort((a, b) => getDateTime(b.createdAt) - getDateTime(a.createdAt)),
    [bulkTimelineEntries, snapshot.activityFeed],
  );

  useEffect(() => {
    let cancelled = false;
    getSession()
      .then((session) => {
        if (cancelled) return;
        const user = session.user;
        setStatementActor({
          email: user?.email || "",
          name: user?.email || "Authenticated User",
          role: normalizeARRecoveryRole(user?.role as UserRole | undefined),
        });
      })
      .catch(() => {
        if (!cancelled) setStatementActor(buildFallbackARRecoveryActor());
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedStatementId && snapshot.filteredStatements[0]) {
      setSelectedStatementId(snapshot.filteredStatements[0].id);
    }
  }, [selectedStatementId, snapshot.filteredStatements]);

  useEffect(() => {
    if (selectedStatementId && !snapshot.filteredStatements.some((statement) => statement.id === selectedStatementId)) {
      setSelectedStatementId(snapshot.filteredStatements[0]?.id || "");
    }
  }, [selectedStatementId, snapshot.filteredStatements]);

  function handleStatementAction(action: string) {
    if (action === "Send Bulk Statements") {
      setActiveStatementAction(action);
      setSelectedTab("bulk");
      return;
    }

    if (activeStatement) {
      const workflowAction = resolveStatementWorkflowAction(action);
      if (workflowAction) {
        publishDocumentWorkflowAction({
          tenantId: workspace.company.id,
          clientId: workspace.company.id,
          customerId: activeStatement.customerNumber,
          accountId: activeStatement.accountId,
          customerName: activeStatement.customerName,
          customerNumber: activeStatement.customerNumber,
          documentId: activeStatement.id,
          documentType: "statement",
          documentRecord: buildStatementDocumentRecordForAction(activeStatement, action),
          action: workflowAction,
          actor: buildStatementCenterActorContext(activeStatement),
          sourceWorkspace: "Statement Center",
          initiatedFromRoute: "/admin/ar-recovery/statements",
          deliveryStatus: resolveStatementDeliveryStatusForAction(action, activeStatement.deliveryStatus),
        });
      }
    }
    setActiveStatementAction(action);
    if (action.includes("Tracking")) setSelectedTab("tracking");
    if (action.includes("Bulk") || action.includes("Batch") || action.includes("Export")) setSelectedTab("bulk");
    if (action.includes("Archive")) setSelectedTab("history");
  }

  return (
    <div
      className="ar-workspace-os mt-4 grid min-w-0 max-w-full gap-4 overflow-hidden"
      data-statement-enterprise-fields={STATEMENT_CENTER_ENTERPRISE_FIELDS.join(",")}
      data-statement-lifecycle-statuses={STATEMENT_CENTER_LIFECYCLE_STATUSES.join(",")}
      data-statement-production-model={STATEMENT_CENTER_PRODUCTION_MODEL.join(",")}
      data-statement-operations-model={STATEMENT_CENTER_OPERATIONS_MODEL.join(",")}
      data-statement-filter-model={STATEMENT_CENTER_FILTER_MODEL.join(",")}
      data-statement-reporting-model={STATEMENT_CENTER_REPORTING_MODEL.join(",")}
      data-statement-activity-events={STATEMENT_CENTER_ACTIVITY_EVENTS.join(",")}
      data-statement-workflow-integrations={STATEMENT_CENTER_WORKFLOW_INTEGRATIONS.join(",")}
      data-statement-communications-architecture={STATEMENT_CENTER_COMMUNICATIONS_ARCHITECTURE.join(",")}
    >
      <PromiseCenterReturnControl context={promiseReturnContext} />

      <section className="grid min-w-0 max-w-full gap-4 md:grid-cols-[repeat(2,minmax(0,1fr))] xl:grid-cols-[repeat(4,minmax(0,1fr))]">
        {snapshot.dashboardCards.map((card) => (
          <article key={card.label} className="min-w-0 max-w-full overflow-hidden rounded-[1.75rem] border border-[rgba(212,175,55,.16)] bg-[rgba(7,18,31,.76)] p-5 shadow-[0_20px_48px_rgba(0,0,0,.18)]">
            <p className="break-words text-xs font-black uppercase tracking-[0.18em] text-[#F4D77A]">{card.label}</p>
            <p className="mt-2 break-words font-display text-3xl font-bold text-[#F6F1E8]">{card.value}</p>
            <p className="mt-3 break-words text-sm font-semibold leading-7 text-[#E8EEF4]">{card.note}</p>
          </article>
        ))}
      </section>

      <section className="grid min-w-0 max-w-full gap-4 overflow-hidden">
        <article className="min-w-0 max-w-full overflow-hidden rounded-[1.75rem] border border-[rgba(34,224,208,.16)] bg-white p-5 shadow-[0_20px_48px_rgba(0,0,0,.08)]">
          <SectionHeading
            eyebrow="Statement Center"
            title="Statement queue"
            description="Company-branded statements with delivery, open, and download tracking."
          />
          <label className="relative mt-5 block">
            <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#F4D77A]" aria-hidden="true" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search customer, statement, collector, status, or notes"
              className="focus-ring w-full rounded-2xl border border-[rgba(212,175,55,.16)] bg-[rgba(255,255,255,.04)] py-3 pl-11 pr-4 text-sm font-semibold text-[#F6F1E8]"
            />
          </label>

          <div className="statement-center-queue-grid mt-5 w-full max-w-full gap-3">
            {filteredStatements.map((statement) => {
              const active = statement.id === activeStatement?.id;
              return (
                <button
                  key={statement.id}
                  type="button"
                  onClick={() => {
                    setSelectedStatementId(statement.id);
                    setSelectedTab("overview");
                  }}
                  aria-current={active ? "true" : undefined}
                  className={`ar-interaction-control min-w-0 max-w-full overflow-hidden rounded-3xl border p-4 text-left transition hover:-translate-y-0.5 ${
                    active
                      ? "ar-interaction-selected"
                      : "border-[rgba(34,224,208,.14)] bg-[rgba(255,255,255,.03)] hover:border-[rgba(34,224,208,.28)]"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="break-words text-xs font-black uppercase tracking-[0.16em] text-[#F4D77A]">{statement.statementNumber}</p>
                      <h3 className="mt-1 break-words font-display text-2xl font-bold text-[#F6F1E8]">{statement.customerName}</h3>
                    </div>
                    <span className="shrink-0 rounded-full border border-[rgba(34,224,208,.2)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#22E0D0]">
                      {statement.status}
                    </span>
                    {active ? <span className="ar-interaction-selected-marker shrink-0">Selected</span> : null}
                  </div>
                  <div className="mt-3 grid gap-2 text-sm font-semibold leading-7 text-[#E8EEF4]">
                    <p className="break-words">Collector: {statement.collector}</p>
                    <p className="break-words">Delivery status: {statement.deliveryStatus}</p>
                    <p className="break-words">Opened status: {statement.openedStatus}</p>
                    <p className="break-words">Balance: {formatARRecoveryCurrency(statement.balanceCents)}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </article>

        <article className="min-w-0 max-w-full overflow-hidden rounded-[1.75rem] border border-[rgba(212,175,55,.16)] bg-white p-5 shadow-[0_20px_48px_rgba(0,0,0,.08)]">
          <SectionHeading
            eyebrow="Statement Detail"
            title={activeStatement ? activeStatement.customerName : "Select a statement"}
            description="Selected statement actions and delivery tracking stay tied to the active statement."
          />

          {activeStatement ? (
            <div className="mt-5 grid min-w-0 max-w-full gap-5 overflow-hidden">
              <div className="grid min-w-0 gap-3 md:grid-cols-[repeat(2,minmax(0,1fr))] xl:grid-cols-[repeat(4,minmax(0,1fr))]">
                <DetailMetric label="Statement #" value={activeStatement.statementNumber} />
                <DetailMetric label="Status" value={activeStatement.status} />
                <DetailMetric label="Sent by" value={activeStatement.sentBy} />
                <DetailMetric label="Due date" value={formatDate(activeStatement.dueDate)} />
                <DetailMetric label="Delivery" value={activeStatement.deliveryStatus} />
                <DetailMetric label="Opened" value={activeStatement.openedStatus} />
                <DetailMetric label="Downloaded" value={activeStatement.downloadedStatus} />
                <DetailMetric label="Balance" value={formatARRecoveryCurrency(activeStatement.balanceCents)} />
              </div>

              <div className="min-w-0 max-w-full overflow-hidden rounded-[1.5rem] border border-[#BFEAFF] bg-[#F4FBFF] p-4">
                <p className="flex items-center gap-2 text-sm font-semibold text-[#0A2540]">
                  <ARLightLineIcon icon={<Bolt className="size-5" aria-hidden="true" />} />
                  Statement Actions
                </p>
                <div className="mt-4 grid min-w-0 gap-3 md:grid-cols-[repeat(2,minmax(0,1fr))] xl:grid-cols-[repeat(3,minmax(0,1fr))]">
                  {getSingleStatementActions().map((action) => (
                    <StatementActionButton
                      key={action.label}
                      label={action.label}
                      note={action.note}
                      onClick={() => handleStatementAction(action.label)}
                    />
                  ))}
                </div>
              </div>

              <div className="flex min-w-0 max-w-full flex-wrap gap-2" role="tablist" aria-label="Statement detail sections">
                {snapshot.tabLabels.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={selectedTab === tab.key}
                    onClick={() => setSelectedTab(tab.key)}
                    className={`ar-interaction-control rounded-full border px-4 py-2 text-xs font-black uppercase tracking-[0.14em] transition ${
                      selectedTab === tab.key
                        ? "ar-interaction-selected"
                        : "border-[rgba(34,224,208,.2)] bg-[rgba(255,255,255,.04)] text-[#CBD5E1] hover:border-[rgba(34,224,208,.34)]"
                    }`}
                    title={tab.description}
                  >
                    {tab.label}{selectedTab === tab.key ? <span className="sr-only"> selected</span> : null}
                  </button>
                ))}
              </div>

              <div className="min-w-0 max-w-full overflow-hidden rounded-[1.75rem] border border-[#BFEAFF] bg-white p-4">
                {renderStatementTab(snapshot, activeStatement, selectedTab, handleStatementAction)}
              </div>

              {activeStatementAction === "Send Bulk Statements" ? (
                <StatementBulkSendWorkflow
                  statements={filteredStatements}
                  actor={statementActor}
                  companyId={workspace.company.id}
                  onClose={() => setActiveStatementAction(null)}
                  onStatementsUpdated={(updates) => setStatementOverrides((current) => ({ ...current, ...updates }))}
                  onTimelineEntries={(entries) => setBulkTimelineEntries((current) => [...entries, ...current])}
                />
              ) : activeStatementAction === "Export Statements" ? (
                <StatementCsvExportWorkflow
                  statements={filteredStatements}
                  actor={statementActor}
                  companyId={workspace.company.id}
                  onClose={() => setActiveStatementAction(null)}
                  onViewHistory={() => setSelectedTab("history")}
                  onTimelineEntries={(entries) => setBulkTimelineEntries((current) => [...entries, ...current])}
                />
              ) : activeStatementAction === "Print Batch" ? (
                <StatementPrintBatchWorkflow
                  statements={filteredStatements}
                  actor={statementActor}
                  companyId={workspace.company.id}
                  clientProfile={workspace.clientProfile}
                  onClose={() => setActiveStatementAction(null)}
                  onViewHistory={() => setSelectedTab("history")}
                  onTimelineEntries={(entries) => setBulkTimelineEntries((current) => [...entries, ...current])}
                />
              ) : activeStatementAction ? (
                <StatementActionPanel action={activeStatementAction} statement={activeStatement} onClose={() => setActiveStatementAction(null)} />
              ) : null}
            </div>
          ) : (
            <div className="mt-5 rounded-3xl border border-dashed border-[rgba(34,224,208,.16)] bg-[rgba(255,255,255,.03)] p-6 text-sm font-semibold leading-7 text-[#E8EEF4]">
              No statement matched the current search. Broaden the search or clear it to view the queue.
            </div>
          )}
        </article>
      </section>

      <section className="flex min-w-0 max-w-full flex-col gap-6 overflow-hidden">
        <article className="min-w-0 max-w-full overflow-hidden rounded-[1.75rem] border border-[rgba(212,175,55,.16)] bg-[rgba(7,18,31,.78)] p-5 shadow-[0_20px_48px_rgba(0,0,0,.18)]">
          <SectionHeading
            eyebrow="Bulk Actions"
            title="Send at scale"
            description="Multi-statement actions for high-volume teams and repeatable delivery rules."
          />
          <div className="mt-5 grid min-w-0 gap-3 sm:grid-cols-[repeat(2,minmax(0,1fr))]">
            {getBulkStatementActions().map((action) => (
              <StatementActionButton
                key={action.label}
                label={action.label}
                note={action.note}
                onClick={() => handleStatementAction(action.label)}
                selected={activeStatementAction === action.label}
              />
            ))}
          </div>
        </article>

        <article className="min-w-0 max-w-full overflow-hidden rounded-[1.75rem] border border-[rgba(212,175,55,.16)] bg-[rgba(7,18,31,.78)] p-5 shadow-[0_20px_48px_rgba(0,0,0,.18)]">
          <SectionHeading
            eyebrow="Activity Feed"
            title="Permanent statement timeline"
            description="Statement generation and send activity are stored as a chronological operational feed."
          />
          <div className="mt-5 grid min-w-0 gap-3">
            {statementActivityFeed.map((entry) => (
              <div key={entry.id} className="min-w-0 max-w-full overflow-hidden rounded-2xl border border-[rgba(34,224,208,.14)] bg-[rgba(255,255,255,.03)] p-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="break-words text-xs font-black uppercase tracking-[0.16em] text-[#22E0D0]">{formatDate(entry.createdAt)}</p>
                  <span className="shrink-0 rounded-full border border-[rgba(212,175,55,.2)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#F4D77A]">
                    Statement
                  </span>
                </div>
                <h3 className="mt-2 break-words font-display text-2xl font-bold text-[#F6F1E8]">{entry.title}</h3>
                <p className="mt-2 break-words text-sm font-semibold leading-7 text-[#E8EEF4]">{entry.detail}</p>
              </div>
            ))}
          </div>
        </article>
      </section>
    </div>
  );
}

function DocumentCenterSection({
  workspace,
  documentDeliveries,
  documentRequests,
  onChangeDocumentDeliveries,
}: {
  workspace: ReturnType<typeof loadARRecoveryWorkspace>;
  documentDeliveries: ARRecoveryDocumentDeliveryRecord[];
  documentRequests: ARRecoveryDocumentRequestRecord[];
  onChangeDocumentDeliveries: (records: ARRecoveryDocumentDeliveryRecord[]) => void;
}) {
  const safeClientProfile = workspace.clientProfile;
  const [documentActor, setDocumentActor] = useState<ARRecoveryActor>(() => buildFallbackARRecoveryActor());
  const safeLetterDefaults = safeClientProfile.letterDefaults || {
    businessName: safeClientProfile.businessName || workspace.company.companyName,
    address: safeClientProfile.addressLine1 || "Not captured",
    phone: safeClientProfile.phone || "Not captured",
    email: safeClientProfile.email || "Not captured",
    website: safeClientProfile.website || "Not captured",
    contactName: safeClientProfile.collectionDepartmentContact || "Not captured",
  };
  const safeBranding = workspace.branding || {
    displayName: safeClientProfile.dbaName || safeClientProfile.businessName || workspace.company.companyName,
    headline: "Enterprise editable template system",
    primaryColor: "#071A2F",
    accentColor: "#22E0D0",
    surfaceColor: "#101826",
  };
  const previewAccount = workspace.accounts[0] || null;
  const safeStatementDefaults = {
    title: `${safeClientProfile.businessName || workspace.company.companyName} Statement`,
    agingSummary: previewAccount?.agingSummary || "Current",
    balance: previewAccount ? formatARRecoveryCurrency(previewAccount.openBalanceCents) : formatARRecoveryCurrency(0),
    dueDate: previewAccount ? formatCapturedDate(getNextActionDate(previewAccount)) : "Not captured",
  };
  const safePaymentInstructions = {
    title: safeClientProfile.accountingContact || "Payment instructions not configured",
    note: safeClientProfile.footerContent || "Payment guidance will appear here when the profile is finalized.",
    support: safeClientProfile.emailSignature || "Not configured",
  };
  const [documentTemplates, setDocumentTemplates] = useState<ARRecoveryDocumentTemplateRecord[]>(() => loadStoredARRecoveryDocumentTemplates(workspace));
  const [selectedWorkspaceTab, setSelectedWorkspaceTab] = useState<DocumentWorkspaceTabKey>(() => loadStoredARRecoveryDocumentWorkspaceTab(workspace.company.id));

  useEffect(() => {
    let cancelled = false;
    getSession()
      .then((session) => {
        if (cancelled) return;
        const user = session.user;
        setDocumentActor({
          email: user?.email || "",
          name: user?.email || "Authenticated User",
          role: (user?.role as UserRole | undefined) || "",
        });
      })
      .catch(() => {
        if (!cancelled) setDocumentActor(buildFallbackARRecoveryActor());
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    saveStoredARRecoveryDocumentTemplates(workspace, documentTemplates);
  }, [documentTemplates, workspace]);

  useEffect(() => {
    setSelectedWorkspaceTab((currentTab) => (isDocumentWorkspaceTabKey(currentTab) ? currentTab : "overview"));
    saveStoredARRecoveryDocumentWorkspaceTab(workspace.company.id, selectedWorkspaceTab);
  }, [selectedWorkspaceTab, workspace.company.id]);

  const letterTemplates = useMemo(() => documentTemplates.filter((template) => template.family === "letter"), [documentTemplates]);
  const statementTemplates = useMemo(() => documentTemplates.filter((template) => template.family === "statement"), [documentTemplates]);
  const documentHistoryEntries = useMemo(
    () => buildARRecoveryDocumentTemplateHistoryEntries(documentRequests, documentTemplates, documentDeliveries),
    [documentRequests, documentTemplates, documentDeliveries],
  );
  const assignedAccountCount = documentTemplates.reduce((sum, template) => sum + template.assignedAccountIds.length, 0);
  const publishedVersionCount = documentTemplates.filter((template) => template.status === "Published").length;
  const mergeFieldCount = AR_RECOVERY_DOCUMENT_TEMPLATE_FIELD_GROUPS.reduce((sum, group) => sum + group.fields.length, 0);
  const sortedDeliveries = useMemo(
    () => [...documentDeliveries].sort((a, b) => getDateTime(b.updatedAt || b.generatedAt) - getDateTime(a.updatedAt || a.generatedAt)),
    [documentDeliveries],
  );
  const selectedWorkspaceTabInfo = DOCUMENT_WORKSPACE_TABS.find((tab) => tab.key === selectedWorkspaceTab) || DOCUMENT_WORKSPACE_TABS[0];
  const deliveryCounts = {
    generated: documentDeliveries.length,
    sent: documentDeliveries.filter((delivery) => delivery.deliveryStatus === "sent").length,
    failed: documentDeliveries.filter((delivery) => delivery.deliveryStatus === "failed").length,
    pending: documentDeliveries.filter((delivery) => delivery.deliveryStatus === "pending").length,
  };

  const previewContext = useMemo(
    () => ({
      ...buildARRecoveryDocumentTemplatePreviewContext(workspace),
      TemplateName: "Enterprise Document Center",
      StatementTitle: safeStatementDefaults.title,
      PaymentInstructions: safePaymentInstructions.title,
      PortalUrl: safeClientProfile.portalUrl || "Not configured",
      ClientName: safeLetterDefaults.businessName,
      ClientDBA: safeClientProfile.dbaName || safeLetterDefaults.businessName,
      ClientAddress: safeLetterDefaults.address,
      ClientPhone: safeLetterDefaults.phone,
      ClientEmail: safeLetterDefaults.email,
      ClientWebsite: safeLetterDefaults.website,
      ClientContactName: safeLetterDefaults.contactName,
      AccountName: previewAccount?.customerName || "Not captured",
      AccountNumber: previewAccount?.customerNumber || "Not captured",
      CustomerName: previewAccount?.customerName || "Not captured",
      CustomerNumber: previewAccount?.customerNumber || "Not captured",
      CurrentBalance: previewAccount ? formatARRecoveryCurrency(previewAccount.openBalanceCents) : formatARRecoveryCurrency(0),
      AgingBucket: previewAccount?.agingSummary || "Current",
      DueDate: safeStatementDefaults.dueDate,
      OpenInvoiceCount: previewAccount ? `${previewAccount.openInvoiceCount}` : "0",
      OpenBalance: previewAccount ? formatARRecoveryCurrency(previewAccount.openBalanceCents) : formatARRecoveryCurrency(0),
      AssignedCollector: previewAccount?.assignedCollector || workspace.collectors[0]?.collectorName || "Collector Desk",
      CollectorName: workspace.collectors[0]?.collectorName || safeClientProfile.collectionDepartmentContact || "Collector Desk",
      ManagerName: safeClientProfile.escalationContact || "Manager Desk",
      PromiseStatus: previewAccount?.recoveryStatus || "Not captured",
      NextAction: previewAccount?.notes || "Review account",
    }),
    [previewAccount, safeClientProfile, safeLetterDefaults, safePaymentInstructions.title, safeStatementDefaults.dueDate, safeStatementDefaults.title, workspace],
  );

  return (
    <>
      <section className="space-y-6">
      <article className="rounded-[1.75rem] border border-slate-200 bg-white p-4 text-slate-900 shadow-[0_16px_36px_rgba(15,23,42,.06)]">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="grid gap-3">
            <div className="inline-flex size-12 items-center justify-center rounded-[1.1rem] border border-teal-100 bg-teal-50 text-teal-700 shadow-sm">
              <FileText className="size-6" aria-hidden="true" />
            </div>
            <div className="grid gap-2">
              <p className="text-[11px] font-black uppercase tracking-[0.2em] text-teal-700">Documents</p>
              <h2 className="font-display text-3xl font-bold tracking-[-0.03em] text-slate-900">Documents</h2>
              <p className="max-w-4xl text-sm font-medium leading-7 text-slate-600">
                Create, generate, deliver, and manage customer correspondence in one focused workspace.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setSelectedWorkspaceTab("generate")}
            className="inline-flex min-h-10 items-center gap-2 rounded-full border border-teal-500 bg-teal-600 px-3.5 text-sm font-black uppercase tracking-[0.12em] text-white shadow-[0_12px_28px_rgba(20,184,166,.18)] transition hover:bg-teal-700 focus-ring"
          >
            Generate Document
            <ArrowRight className="size-4" aria-hidden="true" />
          </button>
        </div>

        <div className="sticky top-4 z-20 mt-5 min-w-0 max-w-full overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white/95 p-2.5 shadow-[0_12px_28px_rgba(15,23,42,.06)] backdrop-blur">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.18em] text-teal-700">Workspace tabs</p>
              <p className="mt-1 text-sm font-medium leading-6 text-slate-600">{selectedWorkspaceTabInfo.note}</p>
            </div>
            <span className="rounded-full border border-teal-200 bg-teal-50 px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-teal-700">
              {selectedWorkspaceTabInfo.label}
            </span>
          </div>
          <div className="mt-3 flex w-full min-w-0 max-w-full gap-2 overflow-x-auto pb-1" role="tablist" aria-label="Document workspace tabs">
            {DOCUMENT_WORKSPACE_TABS.map((tab) => (
              <DocumentWorkspaceTabButton key={tab.key} tab={tab} active={selectedWorkspaceTab === tab.key} onClick={() => setSelectedWorkspaceTab(tab.key)} />
            ))}
          </div>
        </div>
      </article>

      {selectedWorkspaceTab === "overview" ? (
        <div className="space-y-6">
          <article className="ar-action-toolbar rounded-[1.75rem] border p-4">
            <p className="ar-action-toolbar-title flex items-center gap-2">
              <Bolt className="ar-quick-action-bolt size-4" aria-hidden="true" />
              Quick Actions
            </p>
            <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
              <DocumentWorkspaceQuickLink
                label="Generate Letter"
                note="Create a new letter from an approved template."
                icon={<FileText className="size-4" aria-hidden="true" />}
                onClick={() => setSelectedWorkspaceTab("generate")}
              />
              <DocumentWorkspaceQuickLink
                label="Generate Statement"
                note="Create a new statement from an approved template."
                icon={<FileText className="size-4" aria-hidden="true" />}
                onClick={() => setSelectedWorkspaceTab("generate")}
              />
              <DocumentWorkspaceQuickLink
                label="Open Templates"
                note="Jump into letter and statement template work."
                icon={<FileText className="size-4" aria-hidden="true" />}
                onClick={() => setSelectedWorkspaceTab("letter-templates")}
              />
              <DocumentWorkspaceQuickLink
                label="Open History"
                note="Review generated documents and template events."
                icon={<Clock3 className="size-4" aria-hidden="true" />}
                onClick={() => setSelectedWorkspaceTab("document-history")}
              />
              <DocumentWorkspaceQuickLink
                label="Review Branding"
                note="Check portal foundation and branding defaults."
                icon={<Building2 className="size-4" aria-hidden="true" />}
                onClick={() => setSelectedWorkspaceTab("branding")}
              />
              <DocumentWorkspaceQuickLink
                label="Review Permissions"
                note="Inspect role visibility and access guidance."
                icon={<ShieldCheck className="size-4" aria-hidden="true" />}
                onClick={() => setSelectedWorkspaceTab("permissions")}
              />
            </div>
          </article>

          <article className="rounded-[1.75rem] border border-slate-200 bg-white p-4 shadow-[0_10px_24px_rgba(15,23,42,.04)]">
            <p className="text-[11px] font-black uppercase tracking-[0.18em] text-teal-700">Workspace summary</p>
            <div className="mt-3 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <DocumentWorkspaceMetricCard label="Letter Templates" value={letterTemplates.length.toString()} note="Approved letter templates available to the workspace." />
              <DocumentWorkspaceMetricCard label="Statement Templates" value={statementTemplates.length.toString()} note="Approved statement templates available to the workspace." />
              <DocumentWorkspaceMetricCard label="Published Versions" value={publishedVersionCount.toString()} note="Published template versions preserved in history." />
              <DocumentWorkspaceMetricCard label="Assigned Accounts" value={assignedAccountCount.toString()} note="Client-scoped assignments remain attached to templates." />
              <DocumentWorkspaceMetricCard label="Generated Documents" value={deliveryCounts.generated.toString()} note="Letters and statements created from approved templates." />
              <DocumentWorkspaceMetricCard label="Delivery Status" value={`${deliveryCounts.sent} sent`} note={`${deliveryCounts.failed} failed, ${deliveryCounts.pending} pending, and history stays available.`} />
              <DocumentWorkspaceMetricCard label="Merge Fields" value={mergeFieldCount.toString()} note="Client, customer, account, invoice, and recovery tokens." />
              <DocumentWorkspaceMetricCard label="Document History" value={documentHistoryEntries.length.toString()} note="Template and document activity remains visible." />
            </div>
          </article>

          <article className="rounded-[1.75rem] border border-slate-200 bg-white p-4 shadow-[0_10px_24px_rgba(15,23,42,.04)]">
            <p className="text-[11px] font-black uppercase tracking-[0.18em] text-teal-700">Recent activity</p>
            <div className="mt-3">
              {documentHistoryEntries.length ? (
                <DocumentWorkspaceTimeline entries={documentHistoryEntries.slice(0, 6)} />
              ) : (
                <DocumentWorkspaceEmptyState
                  title="No document history yet"
                  description="The first generated letter, statement, or request will appear here once the workflow creates one."
                  action="Use Generate to seed the history lane."
                />
              )}
            </div>
          </article>
        </div>
      ) : selectedWorkspaceTab === "generate" ? (
        <DocumentWorkspacePanel
          eyebrow="Generate"
          title="Focused generation flow"
          description="Choose an account, approved template, and delivery action without leaving the workspace."
          action={
            <button
              type="button"
              onClick={() => setSelectedWorkspaceTab("document-history")}
              className="inline-flex min-h-10 items-center gap-2 rounded-full border border-teal-200 bg-white px-4 text-sm font-black uppercase tracking-[0.12em] text-teal-700 transition hover:border-teal-300 hover:bg-teal-50 focus-ring"
            >
              View History
              <ArrowRight className="size-4" aria-hidden="true" />
            </button>
          }
        >
          <DocumentDeliveryConsole
            workspace={workspace}
            documentTemplates={documentTemplates}
            documentDeliveries={documentDeliveries}
            onChangeDocumentDeliveries={onChangeDocumentDeliveries}
            safeBranding={safeBranding}
            safeClientProfile={safeClientProfile}
            safeLetterDefaults={safeLetterDefaults}
            safePaymentInstructions={safePaymentInstructions}
            safeStatementDefaults={safeStatementDefaults}
            currentActorName={documentActor.name}
            currentActorRole={documentActor.role}
          />
        </DocumentWorkspacePanel>
      ) : selectedWorkspaceTab === "letter-templates" ? (
        <DocumentWorkspacePanel
          eyebrow="Letter templates"
          title="Letter template workspace"
          description="Friendly reminders, notices, demand letters, promise confirmations, and custom letters stay editable with version history."
        >
          <DocumentTemplateManager
            family="letter"
            title="Letter Template Manager"
            description="Friendly reminders, notices, demand letters, promise confirmations, and custom letters stay editable with version history."
            templates={letterTemplates}
            onChangeTemplates={setDocumentTemplates}
            workspace={workspace}
            safeBranding={safeBranding}
            safeClientProfile={safeClientProfile}
            safeLetterDefaults={safeLetterDefaults}
            safePaymentInstructions={safePaymentInstructions}
            safeStatementDefaults={safeStatementDefaults}
            previewContext={previewContext}
            currentActorName={documentActor.name}
            currentActorRole={documentActor.role}
          />
        </DocumentWorkspacePanel>
      ) : selectedWorkspaceTab === "statement-templates" ? (
        <DocumentWorkspacePanel
          eyebrow="Statement templates"
          title="Statement template workspace"
          description="Standard, monthly, past due, aging, executive, and custom statements stay aligned to the active company profile."
        >
          <DocumentTemplateManager
            family="statement"
            title="Statement Template Manager"
            description="Standard, monthly, past due, aging, executive, and custom statements stay aligned to the active company profile."
            templates={statementTemplates}
            onChangeTemplates={setDocumentTemplates}
            workspace={workspace}
            safeBranding={safeBranding}
            safeClientProfile={safeClientProfile}
            safeLetterDefaults={safeLetterDefaults}
            safePaymentInstructions={safePaymentInstructions}
            safeStatementDefaults={safeStatementDefaults}
            previewContext={previewContext}
            currentActorName={documentActor.name}
            currentActorRole={documentActor.role}
          />
        </DocumentWorkspacePanel>
      ) : selectedWorkspaceTab === "document-history" ? (
        <DocumentWorkspacePanel
          eyebrow="Document history"
          title="Template version history and document activity"
          description="Template saves, published versions, and document requests stay visible in one operational lane."
        >
          {documentHistoryEntries.length ? (
            <DocumentWorkspaceTimeline entries={documentHistoryEntries.slice(0, 8)} />
          ) : (
            <DocumentWorkspaceEmptyState
              title="No document history yet"
              description="The first generated letter, statement, or request will appear here once the workflow creates one."
              action="Use Generate to seed the history lane."
            />
          )}
        </DocumentWorkspacePanel>
      ) : selectedWorkspaceTab === "delivery-history" ? (
        <DocumentWorkspacePanel
          eyebrow="Delivery history"
          title="Sent, failed, printed, and downloaded document activity"
          description="Every document delivery event stays visible with the latest action, recipient, and audit metadata."
        >
          <div className="grid gap-3">
            {sortedDeliveries.length ? (
              sortedDeliveries.slice(0, 8).map((delivery) => (
                <article key={delivery.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-[11px] font-black uppercase tracking-[0.18em] text-teal-700">
                        {delivery.documentType} · {delivery.family === "letter" ? "Letter" : "Statement"}
                      </p>
                      <h3 className="mt-1 font-display text-xl font-bold text-slate-900">{delivery.customerName}</h3>
                    </div>
                    <span className="rounded-full border border-teal-200 bg-white px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-teal-700">
                      {delivery.deliveryStatus}
                    </span>
                  </div>
                  <p className="mt-2 text-sm font-medium leading-6 text-slate-600">
                    Template: {delivery.templateName} · Generated by {delivery.generatedBy} · {formatCapturedDate(delivery.generatedAt)}
                  </p>
                  <p className="mt-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Recipient: {delivery.recipientName} {delivery.recipientEmail ? `· ${delivery.recipientEmail}` : ""}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {delivery.actions.slice(-3).map((action) => (
                      <span key={`${delivery.id}-${action.action}-${action.at}`} className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-slate-600">
                        {action.action}
                      </span>
                    ))}
                  </div>
                </article>
              ))
            ) : (
              <DocumentWorkspaceEmptyState
                title="No delivery history yet"
                description="Generate a letter or statement from an approved template to create delivery history."
                action="Use Generate to seed the delivery lane."
              />
            )}
          </div>
        </DocumentWorkspacePanel>
      ) : selectedWorkspaceTab === "branding" ? (
        <DocumentWorkspacePanel
          eyebrow="Branding"
          title="Portal foundation and merge-field defaults"
          description="The client profile keeps letterhead, footer, payment guidance, and portal information aligned with the company brand."
        >
          <div className="grid gap-5 xl:grid-cols-[1.04fr_.96fr]">
            <div className="grid gap-3">
              <DocumentWorkspaceKeyValueRow label="Business Name" value={safeLetterDefaults.businessName} />
              <DocumentWorkspaceKeyValueRow label="Address" value={safeLetterDefaults.address} />
              <DocumentWorkspaceKeyValueRow label="Phone" value={safeLetterDefaults.phone} />
              <DocumentWorkspaceKeyValueRow label="Email" value={safeLetterDefaults.email} />
              <DocumentWorkspaceKeyValueRow label="Website" value={safeLetterDefaults.website} />
              <DocumentWorkspaceKeyValueRow label="Contact Name" value={safeLetterDefaults.contactName} />
              <DocumentWorkspaceKeyValueRow label="Portal URL" value={safeClientProfile.portalUrl || "Not captured"} />
              <DocumentWorkspaceKeyValueRow label="Email Signature" value={safeClientProfile.emailSignature || "Not captured"} />
              <DocumentWorkspaceKeyValueRow label="Letterhead" value={safeClientProfile.letterhead || "Not captured"} />
              <DocumentWorkspaceKeyValueRow label="Footer Content" value={safeClientProfile.footerContent || "Not captured"} />
              <DocumentWorkspaceKeyValueRow label="Statement Title" value={safeStatementDefaults.title} />
              <DocumentWorkspaceKeyValueRow label="Statement Balance" value={safeStatementDefaults.balance} />
              <DocumentWorkspaceKeyValueRow label="Statement Aging" value={safeStatementDefaults.agingSummary} />
              <DocumentWorkspaceKeyValueRow label="Payment Instructions" value={safePaymentInstructions.title} />
              <DocumentWorkspaceKeyValueRow label="Payment Notes" value={safePaymentInstructions.note} />
              <DocumentWorkspaceKeyValueRow label="Signature" value={safeClientProfile.emailSignature || "Not captured"} />
            </div>

            <article className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-teal-700">Live preview</p>
                  <h3 className="mt-1 font-display text-2xl font-bold text-slate-900">{safeBranding.displayName}</h3>
                </div>
                <div className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-white px-3 py-1 text-[11px] font-black uppercase tracking-[0.16em] text-teal-700">
                  Brand colors
                  <span className="size-3 rounded-full" style={{ backgroundColor: safeBranding.primaryColor }} aria-hidden="true" />
                  <span className="size-3 rounded-full" style={{ backgroundColor: safeBranding.accentColor }} aria-hidden="true" />
                </div>
              </div>
              <p className="mt-3 text-sm font-medium leading-7 text-slate-600">{safeBranding.headline}</p>
              <div className="mt-4 rounded-[1.35rem] border border-slate-200 bg-white p-4 shadow-[0_12px_28px_rgba(15,23,42,.06)]">
                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-teal-700">Sample rendered output</p>
                <div
                  className="mt-3 grid gap-3 text-sm leading-7 text-slate-900"
                  dangerouslySetInnerHTML={{
                    __html: renderARRecoveryDocumentTemplatePreview(
                      letterTemplates[0]?.bodyHtml || statementTemplates[0]?.bodyHtml || "<p>No template selected.</p>",
                      previewContext,
                    ),
                  }}
                />
              </div>
            </article>
          </div>
        </DocumentWorkspacePanel>
      ) : selectedWorkspaceTab === "merge-fields" ? (
        <DocumentWorkspacePanel
          eyebrow="Merge fields"
          title="Reusable document field references"
          description="Tokens remain grouped by business context so template authors can find the right placeholders quickly."
        >
          <div className="grid gap-4">
            {AR_RECOVERY_DOCUMENT_TEMPLATE_FIELD_GROUPS.map((group) => (
              <article key={group.label} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-teal-700">{group.label}</p>
                  <p className="mt-1 text-sm font-medium leading-6 text-slate-600">{group.note}</p>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {group.fields.map((field) => (
                    <span
                      key={field.token}
                      title={`${field.token} · ${field.note}`}
                      className="inline-flex items-center rounded-full border border-teal-200 bg-white px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-teal-700"
                    >
                      {field.label}
                    </span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </DocumentWorkspacePanel>
      ) : selectedWorkspaceTab === "permissions" ? (
        <DocumentWorkspacePanel
          eyebrow="Permissions"
          title="User-facing template access matrix"
          description="Role names remain unchanged while the workspace explains which groups can review, edit, and publish templates."
        >
          <div className="grid gap-3">
            {AR_RECOVERY_DOCUMENT_TEMPLATE_ROLE_OPTIONS.map((role) => {
              const writeEnabled = new Set(["platform_admin", "kreatethedate_admin", "admin", "manager", "supervisor", "team_member", "collector"]).has(role.key);
              return (
                <div key={role.key} className={`rounded-2xl border p-4 ${writeEnabled ? "border-teal-200 bg-teal-50" : "border-slate-200 bg-slate-50"}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-black text-slate-900">{role.label}</p>
                      <p className="mt-1 text-xs font-medium leading-5 text-slate-600">{role.note}</p>
                    </div>
                    <span className={`rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] ${writeEnabled ? "border-teal-200 bg-white text-teal-700" : "border-slate-200 bg-white text-slate-500"}`}>
                      {writeEnabled ? "Review + Edit" : "Review Only"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </DocumentWorkspacePanel>
      ) : (
        <DocumentWorkspacePanel
          eyebrow="Document history"
          title="Template version history and document activity"
          description="Template saves, published versions, and document requests stay visible in one operational lane."
        >
          {documentHistoryEntries.length ? (
            <OperationalHistoryRows entries={documentHistoryEntries.slice(0, 8)} />
          ) : (
            <DocumentWorkspaceEmptyState
              title="No document history yet"
              description="The first generated letter, statement, or request will appear here once the workflow creates one."
              action="Use Generate to seed the history lane."
            />
          )}
        </DocumentWorkspacePanel>
      )}
      </section>
    </>
  );
}

function DocumentDeliveryConsole({
  workspace,
  documentTemplates,
  documentDeliveries,
  onChangeDocumentDeliveries,
  safeBranding,
  safeClientProfile,
  safeLetterDefaults,
  safePaymentInstructions,
  safeStatementDefaults,
  currentActorName,
  currentActorRole,
}: {
  workspace: ReturnType<typeof loadARRecoveryWorkspace>;
  documentTemplates: ARRecoveryDocumentTemplateRecord[];
  documentDeliveries: ARRecoveryDocumentDeliveryRecord[];
  onChangeDocumentDeliveries: (records: ARRecoveryDocumentDeliveryRecord[]) => void;
  safeBranding: ReturnType<typeof loadARRecoveryWorkspace>["branding"];
  safeClientProfile: ReturnType<typeof loadARRecoveryWorkspace>["clientProfile"];
  safeLetterDefaults: ReturnType<typeof loadARRecoveryWorkspace>["clientProfile"]["letterDefaults"];
  safePaymentInstructions: { title: string; note: string; support: string };
  safeStatementDefaults: { title: string; agingSummary: string; balance: string; dueDate: string };
  currentActorName: string;
  currentActorRole: string;
}) {
  const approvedLetterTemplates = useMemo(
    () => documentTemplates.filter((template) => template.family === "letter" && template.status === "Published"),
    [documentTemplates],
  );
  const approvedStatementTemplates = useMemo(
    () => documentTemplates.filter((template) => template.family === "statement" && template.status === "Published"),
    [documentTemplates],
  );
  const [selectedFamily, setSelectedFamily] = useState<ARRecoveryDocumentTemplateFamily>("letter");
  const [selectedAccountId, setSelectedAccountId] = useState<string>(() => workspace.accounts[0]?.id || "");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(() => approvedLetterTemplates[0]?.id || approvedStatementTemplates[0]?.id || "");
  const [selectedDeliveryId, setSelectedDeliveryId] = useState<string>(() => documentDeliveries[0]?.id || "");
  const [selectedPromiseId, setSelectedPromiseId] = useState<string>("");
  const [previewZoom, setPreviewZoom] = useState<80 | 90 | 100 | 110 | 125 | 150>(100);
  const [deliveryActionError, setDeliveryActionError] = useState("");

  const familyTemplates = selectedFamily === "letter" ? approvedLetterTemplates : approvedStatementTemplates;
  const selectedAccount = useMemo(
    () => workspace.accounts.find((account) => account.id === selectedAccountId) || workspace.accounts[0] || null,
    [selectedAccountId, workspace.accounts],
  );
  const selectedAccountPromises = useMemo(
    () => (selectedAccount ? workspace.promises.filter((promise) => promise.accountId === selectedAccount.id) : []),
    [selectedAccount, workspace.promises],
  );
  const selectedTemplate = useMemo(
    () => familyTemplates.find((template) => template.id === selectedTemplateId) || familyTemplates[0] || null,
    [familyTemplates, selectedTemplateId],
  );
  const selectedDelivery = useMemo(
    () => documentDeliveries.find((delivery) => delivery.id === selectedDeliveryId) || documentDeliveries[0] || null,
    [documentDeliveries, selectedDeliveryId],
  );

  useEffect(() => {
    if (!familyTemplates.length) {
      setSelectedTemplateId("");
      return;
    }
    if (!selectedTemplateId || !familyTemplates.some((template) => template.id === selectedTemplateId)) {
      setSelectedTemplateId(familyTemplates[0].id);
    }
  }, [familyTemplates, selectedTemplateId]);

  useEffect(() => {
    if (!selectedAccountId || !workspace.accounts.some((account) => account.id === selectedAccountId)) {
      setSelectedAccountId(workspace.accounts[0]?.id || "");
    }
  }, [selectedAccountId, workspace.accounts]);

  useEffect(() => {
    if (!selectedPromiseId || !selectedAccountPromises.some((promise) => promise.id === selectedPromiseId)) {
      setSelectedPromiseId(selectedAccountPromises[0]?.id || "");
    }
  }, [selectedAccountPromises, selectedPromiseId]);

  useEffect(() => {
    if (selectedDeliveryId && !documentDeliveries.some((delivery) => delivery.id === selectedDeliveryId)) {
      setSelectedDeliveryId(documentDeliveries[0]?.id || "");
    }
  }, [documentDeliveries, selectedDeliveryId]);

  useEffect(() => {
    const preferredTemplate = familyTemplates.find((template) => template.assignedAccountIds.includes(selectedAccountId)) || familyTemplates[0] || null;
    if (preferredTemplate && preferredTemplate.id !== selectedTemplateId) {
      setSelectedTemplateId(preferredTemplate.id);
    }
  }, [familyTemplates, selectedAccountId, selectedTemplateId]);

  const selectedPromise = useMemo(
    () => selectedAccountPromises.find((promise) => promise.id === selectedPromiseId) || selectedAccountPromises[0] || null,
    [selectedAccountPromises, selectedPromiseId],
  );

  const previewContext = useMemo(
    () => buildARRecoveryDocumentDeliveryPreviewContext(workspace, selectedAccount, selectedTemplate, selectedFamily),
    [selectedAccount, selectedFamily, selectedTemplate, workspace],
  );
  const previewHtml = selectedTemplate
    ? renderARRecoveryDocumentTemplatePreview(selectedTemplate.bodyHtml, previewContext)
    : "<p>No approved template selected.</p>";
  const previewText = previewHtml.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim() || "No preview available.";
  const previewDelivery = selectedDelivery || null;

  const generationMetrics = useMemo(
    () => [
      { label: "Generated", value: documentDeliveries.length.toString(), note: "Approved documents saved to history." },
      { label: "Previewed", value: documentDeliveries.reduce((sum, delivery) => sum + delivery.previewCount, 0).toString(), note: "Preview passes recorded." },
      { label: "Printed", value: documentDeliveries.reduce((sum, delivery) => sum + delivery.printCount, 0).toString(), note: "Print actions retained for audit." },
      { label: "Emailed", value: documentDeliveries.reduce((sum, delivery) => sum + delivery.emailCount, 0).toString(), note: "Delivery attempts tracked." },
    ],
    [documentDeliveries],
  );

  const currentDocumentHistory = useMemo(
    () => [...documentDeliveries].sort((a, b) => getDateTime(b.updatedAt || b.generatedAt) - getDateTime(a.updatedAt || a.generatedAt)),
    [documentDeliveries],
  );

  function persistDelivery(nextDelivery: ARRecoveryDocumentDeliveryRecord) {
    onChangeDocumentDeliveries([nextDelivery, ...documentDeliveries.filter((delivery) => delivery.id !== nextDelivery.id)]);
    setSelectedDeliveryId(nextDelivery.id);
  }

  function recordDeliveryAction(
    action: ARRecoveryDocumentDeliveryAction,
    detail: string,
    overrides: Partial<Pick<ARRecoveryDocumentDeliveryRecord, "deliveryStatus" | "deliveryProvider" | "providerMessageId" | "providerStatus" | "deliveryError" | "previewHtml">> = {},
  ) {
    if (!selectedTemplate || !selectedAccount) {
      setDeliveryActionError("Select an account and approved template before generating a document.");
      return null;
    }
    setDeliveryActionError("");
    const activeDelivery = previewDelivery || createARRecoveryDocumentDeliveryRecord({
      workspace,
      family: selectedFamily,
      template: selectedTemplate,
      account: selectedAccount,
      actorName: currentActorName,
      previewContext,
      relatedPromiseId: selectedPromise?.id || "",
      relatedTaskId: "",
    });
    const nextDelivery = updateARRecoveryDocumentDeliveryRecord(activeDelivery, action, currentActorName, detail, overrides);
    persistDelivery(nextDelivery);
    return nextDelivery;
  }

  function handleGenerateDocument() {
    if (!selectedTemplate || !selectedAccount) {
      setDeliveryActionError("Select an account and approved template before generating a document.");
      return;
    }
    const nextDelivery = createARRecoveryDocumentDeliveryRecord({
      workspace,
      family: selectedFamily,
      template: selectedTemplate,
      account: selectedAccount,
      actorName: currentActorName,
      previewContext,
      relatedPromiseId: selectedPromise?.id || "",
      relatedTaskId: "",
    });
    persistDelivery(nextDelivery);
  }

  function handlePreviewDocument() {
    const nextDelivery = recordDeliveryAction(
      "Previewed",
      `Previewed ${selectedTemplate?.templateName || "approved document"} for ${selectedAccount?.customerName || "selected account"}.`,
    );
    if (nextDelivery && typeof window !== "undefined") {
      window.setTimeout(() => {
        const previewAnchor = document.getElementById(`document-delivery-preview-${nextDelivery.id}`);
        previewAnchor?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
    }
  }

  function handlePrintDocument() {
    const nextDelivery = recordDeliveryAction(
      "Printed",
      `Printed ${selectedTemplate?.templateName || "approved document"} for ${selectedAccount?.customerName || "selected account"}.`,
    );
    const html = nextDelivery?.previewHtml || previewHtml;
    if (typeof window === "undefined") return;
    const printWindow = window.open("", "_blank", "noopener,noreferrer,width=1024,height=1200");
    const printMarkup = `<!doctype html><html><head><title>${escapeHtml(selectedTemplate?.templateName || "Document")}</title><style>body{font-family:Inter,Arial,sans-serif;margin:0;padding:40px;color:#111827;background:#fffdf8;} .page{max-width:900px;margin:0 auto;padding:32px;background:#fff;border:1px solid #e5d5aa;border-radius:24px;box-shadow:0 20px 45px rgba(17,24,39,.08);} h1,h2,h3,p{margin:0 0 16px 0;} strong{font-weight:800;}</style></head><body><div class="page">${html}</div></body></html>`;
    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(printMarkup);
      printWindow.document.close();
      printWindow.focus();
      window.setTimeout(() => printWindow.print(), 250);
      window.setTimeout(() => printWindow.close(), 750);
      return;
    }
    window.print();
  }

  function handleDownloadDocument() {
    const nextDelivery = recordDeliveryAction(
      "Downloaded",
      `Downloaded ${selectedTemplate?.templateName || "approved document"} for ${selectedAccount?.customerName || "selected account"}.`,
    );
    const html = nextDelivery?.previewHtml || previewHtml;
    if (typeof window === "undefined") return;
    const printWindow = window.open("", "_blank", "noopener,noreferrer,width=1024,height=1200");
    const printMarkup = `<!doctype html><html><head><title>${escapeHtml(selectedTemplate?.templateName || "Document")}</title><style>body{font-family:Inter,Arial,sans-serif;margin:0;padding:40px;color:#111827;background:#fffdf8;} .page{max-width:900px;margin:0 auto;padding:32px;background:#fff;border:1px solid #e5d5aa;border-radius:24px;box-shadow:0 20px 45px rgba(17,24,39,.08);} h1,h2,h3,p{margin:0 0 16px 0;} strong{font-weight:800;}</style></head><body><div class="page">${html}</div></body></html>`;
    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(printMarkup);
      printWindow.document.close();
      printWindow.focus();
      window.setTimeout(() => printWindow.print(), 250);
      window.setTimeout(() => printWindow.close(), 750);
      return;
    }
    window.print();
  }

  async function handleEmailDocument() {
    if (!selectedTemplate || !selectedAccount) {
      setDeliveryActionError("Select an account and approved template before sending email.");
      return;
    }
    if (!selectedAccount.contactEmail || !isValidARRecoveryEmail(selectedAccount.contactEmail)) {
      setDeliveryActionError("The selected account must include a valid email address before delivery.");
      return;
    }
    const activeDelivery = previewDelivery || createARRecoveryDocumentDeliveryRecord({
      workspace,
      family: selectedFamily,
      template: selectedTemplate,
      account: selectedAccount,
      actorName: currentActorName,
      previewContext,
      relatedPromiseId: selectedPromise?.id || "",
      relatedTaskId: "",
    });
    const delivery = await deliverARRecoveryEmail({
      recordType: selectedFamily,
      recipientEmail: selectedAccount.contactEmail,
      recipientName: selectedAccount.contactName || selectedAccount.customerName,
      subject: activeDelivery.subjectLine,
      body: `${previewText}\n\n${safePaymentInstructions.title}\n${safePaymentInstructions.support}`,
      htmlBody: activeDelivery.previewHtml,
      accountName: selectedAccount.customerName,
      customerNumber: selectedAccount.customerNumber,
      recordLabel: activeDelivery.templateName,
    });
    const sentAt = delivery.sentAt || new Date().toISOString();
    const nextDelivery = updateARRecoveryDocumentDeliveryRecord(
      activeDelivery,
      delivery.status === "sent" ? "Emailed" : "Failed",
      currentActorName,
      delivery.status === "sent"
        ? `Emailed ${activeDelivery.templateName} to ${selectedAccount.contactEmail}. Provider: ${delivery.provider}${delivery.providerMessageId ? ` (${delivery.providerMessageId})` : ""}.`
        : `Email delivery failed for ${activeDelivery.templateName}: ${delivery.providerError || delivery.message}`,
      {
        deliveryStatus: delivery.status === "sent" ? "sent" : delivery.status === "failed" ? "failed" : "not_configured",
        deliveryProvider: delivery.provider,
        providerMessageId: delivery.providerMessageId,
        providerStatus: delivery.providerStatus,
        deliveryError: delivery.providerError,
        previewHtml: activeDelivery.previewHtml,
      },
    );
    nextDelivery.updatedAt = sentAt;
    persistDelivery(nextDelivery);
  }

  const selectedDeliveryAction = selectedDelivery?.actions[selectedDelivery.actions.length - 1]?.action || "Generated";
  const canGenerate = Boolean(selectedTemplate && selectedAccount);
  const deliveryCounts = {
    generated: documentDeliveries.length,
    sent: documentDeliveries.filter((delivery) => delivery.deliveryStatus === "sent").length,
    failed: documentDeliveries.filter((delivery) => delivery.deliveryStatus === "failed").length,
    pending: documentDeliveries.filter((delivery) => delivery.deliveryStatus === "pending").length,
  };

  return (
    <section className="rounded-[1.6rem] border border-slate-200 bg-white p-5 shadow-[0_20px_48px_rgba(15,23,42,.06)]">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="grid gap-3">
          <DocumentWorkspaceSectionHeading
            eyebrow="Document generation"
            title="Generate, preview, print, download, and email approved documents"
            description="Collectors can use approved templates to produce tenant-branded letters and statements with automatic delivery tracking."
          />
          <div className="flex flex-wrap gap-2 text-[11px] font-black uppercase tracking-[0.16em]">
            <span className="rounded-full border border-teal-200 bg-teal-50 px-3 py-1 text-teal-700">Document generation engine</span>
            <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-amber-800">{formatARRecoveryRole(currentActorRole)}</span>
            <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-slate-700">{selectedAccount?.customerName || "No account selected"}</span>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {generationMetrics.map((metric) => (
            <DocumentWorkspaceDetailMetric key={metric.label} label={metric.label} value={metric.value} note={metric.note} />
          ))}
        </div>
      </div>

      <div className="mt-5 grid gap-4 xl:grid-cols-[1fr_.92fr]">
        <div className="grid gap-4">
          <section className="rounded-[1.35rem] border border-slate-200 bg-white p-4 shadow-[0_10px_24px_rgba(15,23,42,.04)]">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-teal-700">1. Choose family</p>
                <p className="mt-1 text-sm font-semibold leading-6 text-slate-600">Use approved letter or statement templates for the current account.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {(["letter", "statement"] as ARRecoveryDocumentTemplateFamily[]).map((family) => (
                  <button
                    key={family}
                    type="button"
                    onClick={() => setSelectedFamily(family)}
                    className={`rounded-full border px-4 py-2 text-xs font-black uppercase tracking-[0.14em] transition ${
                      selectedFamily === family
                      ? "border-amber-300 bg-amber-50 text-amber-900"
                      : "border-teal-200 bg-white text-slate-600 hover:border-teal-300 hover:bg-teal-50 hover:text-teal-900"
                    }`}
                  >
                    {family === "letter" ? "Letter" : "Statement"}
                  </button>
                ))}
              </div>
            </div>
          </section>

          <section className="rounded-[1.35rem] border border-slate-200 bg-white p-4 shadow-[0_10px_24px_rgba(15,23,42,.04)]">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-teal-700">2. Choose customer and account</p>
                <p className="mt-1 text-sm font-semibold leading-6 text-slate-600">The selected account drives merge fields, balance data, and delivery recipients.</p>
              </div>
              <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-amber-800">
                {selectedAccountPromises.length ? `${selectedAccountPromises.length} promise${selectedAccountPromises.length === 1 ? "" : "s"} linked` : "No promise linked"}
              </span>
            </div>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <label className="grid gap-2">
                <span className="text-[11px] font-black uppercase tracking-[0.14em] text-amber-800">Customer / Account</span>
                <select
                  value={selectedAccount?.id || ""}
                  onChange={(event) => setSelectedAccountId(event.target.value)}
                  className="focus-ring rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-900 outline-none transition focus:border-teal-400"
                >
                  {workspace.accounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.customerName} · {account.customerNumber}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grid gap-2">
                <span className="text-[11px] font-black uppercase tracking-[0.14em] text-amber-800">Approved template</span>
                <select
                  value={selectedTemplate?.id || ""}
                  onChange={(event) => setSelectedTemplateId(event.target.value)}
                  className="focus-ring rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-900 outline-none transition focus:border-teal-400"
                >
                  {(familyTemplates.length ? familyTemplates : documentTemplates.filter((template) => template.family === selectedFamily)).map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.templateName}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grid gap-2 md:col-span-2">
                <span className="text-[11px] font-black uppercase tracking-[0.14em] text-amber-800">Promise link (optional)</span>
                <select
                  value={selectedPromise?.id || ""}
                  onChange={(event) => setSelectedPromiseId(event.target.value)}
                  className="focus-ring rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-900 outline-none transition focus:border-teal-400"
                >
                  <option value="">No promise linked</option>
                  {selectedAccountPromises.map((promise) => (
                    <option key={promise.id} value={promise.id}>
                      {promise.promiseType} · {promise.amountCents ? formatARRecoveryCurrency(promise.amountCents) : "No amount"} · {promise.promiseDate ? formatCapturedDate(promise.promiseDate) : "No date"}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </section>

          <section className="rounded-[1.35rem] border border-[rgba(34,224,208,.16)] bg-[rgba(255,255,255,.03)] p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">3. Generate and deliver</p>
                <p className="mt-1 text-sm font-semibold leading-6 text-[#E8EEF4]">Every action updates delivery history, timeline events, account history, and recovery activity.</p>
              </div>
              <span className="rounded-full border border-[rgba(255,106,43,.2)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#FF6A2B]">
                {selectedDeliveryAction}
              </span>
            </div>
            {deliveryActionError ? (
              <p className="mt-3 rounded-2xl border border-[rgba(239,68,68,.26)] bg-[rgba(239,68,68,.08)] px-4 py-3 text-sm font-semibold leading-6 text-[#FCA5A5]">
                {deliveryActionError}
              </p>
            ) : null}
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" onClick={handleGenerateDocument} disabled={!canGenerate} className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[rgba(34,224,208,.26)] bg-[rgba(7,18,31,.62)] px-4 text-sm font-extrabold text-[#F6F1E8] transition hover:border-[rgba(34,224,208,.46)] hover:bg-[rgba(34,224,208,.12)] focus-ring disabled:cursor-not-allowed disabled:opacity-40">
                Generate {selectedFamily === "letter" ? "Letter" : "Statement"}
              </button>
              <button type="button" onClick={handlePreviewDocument} disabled={!canGenerate} className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[rgba(255,106,43,.26)] bg-[rgba(7,18,31,.62)] px-4 text-sm font-extrabold text-[#F6F1E8] transition hover:border-[rgba(255,106,43,.46)] hover:bg-[rgba(255,106,43,.1)] focus-ring disabled:cursor-not-allowed disabled:opacity-40">
                Preview
              </button>
              <button type="button" onClick={handlePrintDocument} disabled={!canGenerate} className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[rgba(34,224,208,.26)] bg-[rgba(7,18,31,.62)] px-4 text-sm font-extrabold text-[#F6F1E8] transition hover:border-[rgba(34,224,208,.46)] hover:bg-[rgba(34,224,208,.12)] focus-ring disabled:cursor-not-allowed disabled:opacity-40">
                Print
              </button>
              <button type="button" onClick={handleDownloadDocument} disabled={!canGenerate} className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[rgba(255,106,43,.26)] bg-[rgba(7,18,31,.62)] px-4 text-sm font-extrabold text-[#F6F1E8] transition hover:border-[rgba(255,106,43,.46)] hover:bg-[rgba(255,106,43,.1)] focus-ring disabled:cursor-not-allowed disabled:opacity-40">
                Download PDF
              </button>
              <button type="button" onClick={() => void handleEmailDocument()} disabled={!canGenerate || !selectedAccount?.contactEmail} className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[rgba(34,224,208,.26)] bg-[rgba(7,18,31,.62)] px-4 text-sm font-extrabold text-[#F6F1E8] transition hover:border-[rgba(34,224,208,.46)] hover:bg-[rgba(34,224,208,.12)] focus-ring disabled:cursor-not-allowed disabled:opacity-40">
                Email
              </button>
            </div>
          </section>
        </div>

        <article className="rounded-[1.35rem] border border-[rgba(34,224,208,.16)] bg-[rgba(255,255,255,.03)] p-4 shadow-[0_20px_48px_rgba(0,0,0,.18)]">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Live preview</p>
              <h3 className="mt-1 font-display text-2xl font-bold text-[#F6F1E8]">{selectedTemplate?.templateName || "Approved document"}</h3>
              <p className="mt-1 text-sm font-semibold leading-6 text-[#E8EEF4]">
                {selectedAccount?.customerName || "Select an account"} · {selectedFamily === "letter" ? "Letter" : "Statement"} · {selectedTemplate?.status || "Draft"}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-[rgba(255,106,43,.2)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#FF6A2B]">
                Zoom {previewZoom}%
              </span>
              {[80, 90, 100, 110, 125, 150].map((zoom) => (
                <button
                  key={zoom}
                  type="button"
                  onClick={() => setPreviewZoom(zoom as 80 | 90 | 100 | 110 | 125 | 150)}
                  className={`rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.14em] transition ${
                    previewZoom === zoom
                      ? "border-[rgba(255,106,43,.42)] bg-[rgba(255,106,43,.14)] text-[#F6F1E8]"
                      : "border-[rgba(34,224,208,.2)] bg-[rgba(255,255,255,.04)] text-[#CBD5E1] hover:border-[rgba(34,224,208,.34)]"
                  }`}
                >
                  {zoom}%
                </button>
              ))}
            </div>
          </div>
          <div className="mt-4 rounded-[1.5rem] border border-[rgba(255,106,43,.14)] bg-[#fffdf8] p-4 text-[#111827] shadow-[0_18px_40px_rgba(17,24,39,.14)]">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">Rendered preview</p>
                <p className="mt-1 text-sm font-semibold text-[#334155]">
                  {selectedTemplate ? "Preview matches the approved template, merged with live account data." : "Select a published template to preview output."}
                </p>
              </div>
              <span className="rounded-full border border-[#FF6A2B]/24 bg-white px-3 py-1 text-[11px] font-black uppercase tracking-[0.16em] text-[#D94A16]">
                Page 1 / 1
              </span>
            </div>
            <div className="mt-4 rounded-[1.35rem] border border-[#e5d5aa] bg-white p-4 shadow-[0_12px_28px_rgba(17,24,39,.08)]">
              <div
                id={previewDelivery ? `document-delivery-preview-${previewDelivery.id}` : undefined}
                className="origin-top-left"
                style={{ transform: `scale(${previewZoom / 100})`, transformOrigin: "top left", width: `${10000 / previewZoom}%` }}
                dangerouslySetInnerHTML={{ __html: previewDelivery?.previewHtml || previewHtml }}
              />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button type="button" className="rounded-full border border-[rgba(255,106,43,.2)] bg-[#fffdf8] px-3 py-2 text-xs font-black uppercase tracking-[0.14em] text-[#D94A16] opacity-60" disabled>
                Previous Page
              </button>
              <button type="button" className="rounded-full border border-[rgba(255,106,43,.2)] bg-[#fffdf8] px-3 py-2 text-xs font-black uppercase tracking-[0.14em] text-[#D94A16] opacity-60" disabled>
                Next Page
              </button>
              <button type="button" onClick={handlePreviewDocument} className="rounded-full border border-[rgba(34,224,208,.22)] bg-[#fffdf8] px-3 py-2 text-xs font-black uppercase tracking-[0.14em] text-[#0a7480] transition hover:border-[rgba(34,224,208,.44)]">
                Refresh Preview
              </button>
            </div>
          </div>

          <div className="mt-4 grid gap-3">
            <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Delivery history</p>
            {currentDocumentHistory.length ? (
              currentDocumentHistory.slice(0, 6).map((delivery) => (
                <button
                  key={delivery.id}
                  type="button"
                  onClick={() => {
                    setSelectedDeliveryId(delivery.id);
                    setSelectedFamily(delivery.family);
                    setSelectedTemplateId(delivery.templateId);
                    setSelectedAccountId(delivery.accountId || workspace.accounts[0]?.id || "");
                  }}
                  className={`rounded-2xl border p-4 text-left transition focus-ring ${
                    delivery.id === selectedDelivery?.id
                      ? "border-[rgba(255,106,43,.42)] bg-[rgba(255,106,43,.12)]"
                      : "border-[rgba(34,224,208,.14)] bg-[rgba(255,255,255,.03)] hover:border-[rgba(34,224,208,.3)] hover:bg-[rgba(255,255,255,.06)]"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">
                        {delivery.documentType} · {delivery.family === "letter" ? "Letter" : "Statement"}
                      </p>
                      <h4 className="mt-1 font-display text-xl font-bold text-[#F6F1E8]">{delivery.customerName}</h4>
                    </div>
                    <span className="rounded-full border border-[rgba(255,106,43,.18)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#FF6A2B]">
                      {delivery.deliveryStatus}
                    </span>
                  </div>
                  <p className="mt-2 text-sm font-semibold leading-6 text-[#E8EEF4]">
                    Template: {delivery.templateName} · Generated by {delivery.generatedBy} · {formatCapturedDate(delivery.generatedAt)}
                  </p>
                  <p className="mt-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#CBD5E1]">
                    Recipient: {delivery.recipientName} {delivery.recipientEmail ? `· ${delivery.recipientEmail}` : ""}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {delivery.actions.slice(-3).map((action) => (
                      <span key={`${delivery.id}-${action.action}-${action.at}`} className="rounded-full border border-[rgba(34,224,208,.14)] px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#CBD5E1]">
                        {action.action}
                      </span>
                    ))}
                  </div>
                </button>
              ))
            ) : (
              <ManagerEmptyState
                title="No document deliveries yet"
                description="Generate a letter or statement from an approved template to create delivery history."
                action="Use the controls above to seed the delivery lane."
              />
            )}
          </div>
        </article>
      </div>
    </section>
  );
}

function DocumentTemplateManager({
  family,
  title,
  description,
  templates,
  onChangeTemplates,
  workspace,
  safeBranding,
  safeClientProfile,
  safeLetterDefaults,
  safePaymentInstructions,
  safeStatementDefaults,
  previewContext,
  currentActorName,
  currentActorRole,
}: {
  family: ARRecoveryDocumentTemplateFamily;
  title: string;
  description: string;
  templates: ARRecoveryDocumentTemplateRecord[];
  onChangeTemplates: (nextTemplates: ARRecoveryDocumentTemplateRecord[]) => void;
  workspace: ReturnType<typeof loadARRecoveryWorkspace>;
  safeBranding: ReturnType<typeof loadARRecoveryWorkspace>["branding"];
  safeClientProfile: ReturnType<typeof loadARRecoveryWorkspace>["clientProfile"];
  safeLetterDefaults: ReturnType<typeof loadARRecoveryWorkspace>["clientProfile"]["letterDefaults"];
  safePaymentInstructions: { title: string; note: string; support: string };
  safeStatementDefaults: { title: string; agingSummary: string; balance: string; dueDate: string };
  previewContext: Record<string, string>;
  currentActorName: string;
  currentActorRole: string;
}) {
  const familyLabel = family === "letter" ? "Letter Template Manager" : "Statement Template Manager";
  const familyTemplates = templates.filter((template) => template.family === family);
  const [selectedTemplateId, setSelectedTemplateId] = useState(familyTemplates[0]?.id || "");
  const selectedTemplate = familyTemplates.find((template) => template.id === selectedTemplateId) || familyTemplates[0] || null;
  const [draft, setDraft] = useState<ARRecoveryDocumentTemplateDraftState>(() => createARRecoveryDocumentTemplateDraft(selectedTemplate || createARRecoveryBlankDocumentTemplate(workspace, family)));
  const [editorHtml, setEditorHtml] = useState(selectedTemplate?.bodyHtml || createARRecoveryBlankDocumentTemplate(workspace, family).bodyHtml);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const editorSelectionRef = useRef<Range | null>(null);
  const normalizedActorRole = normalizeARRecoveryRole(currentActorRole);
  const canManageTemplates = new Set(["platform_admin", "kreatethedate_admin", "company_admin", "admin", "manager"]).has(normalizedActorRole);
  const canEditTemplates = canManageTemplates || (normalizedActorRole === "supervisor" && draft.allowedRoles.includes("supervisor"));
  const [selectedAccountId, setSelectedAccountId] = useState<string>(() => selectedTemplate?.assignedAccountIds[0] || workspace.accounts[0]?.id || "");

  useEffect(() => {
    if (!selectedTemplateId && familyTemplates[0]) {
      setSelectedTemplateId(familyTemplates[0].id);
    }
  }, [familyTemplates, selectedTemplateId]);

  useEffect(() => {
    if (!selectedTemplate) return;
    setDraft(createARRecoveryDocumentTemplateDraft(selectedTemplate));
    setEditorHtml(selectedTemplate.bodyHtml);
  }, [selectedTemplate?.id, selectedTemplate?.updatedAt]);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== editorHtml) {
      editorRef.current.innerHTML = editorHtml;
    }
  }, [editorHtml]);

  useEffect(() => {
    if (!selectedTemplate && familyTemplates[0]) {
      setSelectedTemplateId(familyTemplates[0].id);
    }
  }, [familyTemplates, selectedTemplate]);

  useEffect(() => {
    const preferredAccountId = selectedTemplate?.assignedAccountIds[0] || workspace.accounts[0]?.id || "";
    if (!preferredAccountId) return;
    setSelectedAccountId((current) => {
      if (current && workspace.accounts.some((account) => account.id === current)) {
        return current;
      }
      return preferredAccountId;
    });
  }, [selectedTemplate?.id, selectedTemplate?.assignedAccountIds, workspace.accounts]);

  useEffect(() => {
    if (!selectedAccountId) return;
    const assignedTemplate = familyTemplates.find((template) => template.assignedAccountIds.includes(selectedAccountId));
    if (assignedTemplate && assignedTemplate.id !== selectedTemplate?.id) {
      setSelectedTemplateId(assignedTemplate.id);
    }
  }, [familyTemplates, selectedAccountId, selectedTemplate?.id]);

  const metrics = useMemo(
    () => [
      { label: "Templates", value: familyTemplates.length.toString(), note: "Seeded enterprise templates are ready." },
      { label: "Published", value: familyTemplates.filter((template) => template.status === "Published").length.toString(), note: "Published versions remain visible." },
      { label: "Assigned Accounts", value: familyTemplates.reduce((sum, template) => sum + template.assignedAccountIds.length, 0).toString(), note: "Client-specific assignments are preserved." },
      { label: "Allowed Roles", value: draft.allowedRoles.length.toString(), note: "Write-enabled roles can review and publish." },
    ],
    [draft.allowedRoles.length, familyTemplates],
  );

  const assignableAccounts = workspace.accounts.slice(0, 6);
  const selectedAccount = useMemo(
    () => workspace.accounts.find((account) => account.id === selectedAccountId) || workspace.accounts[0] || null,
    [selectedAccountId, workspace.accounts],
  );
  const versionCards = useMemo(
    () =>
      (selectedTemplate?.versionHistory || []).map((version, index) => {
        const versionNumber = selectedTemplate ? selectedTemplate.versionHistory.length - index : index + 1;
        const modifiedAt = version.modifiedAt || version.createdAt;
        const modifiedBy = version.modifiedBy || version.createdBy;
        const publishedAt = version.publishedAt || (version.status === "Published" ? version.createdAt : null);
        return {
          id: version.id,
          versionNumber,
          createdBy: version.createdBy,
          modifiedBy,
          createdAt: version.createdAt,
          modifiedAt,
          publishedAt,
          publishedBy: version.publishedBy || version.createdBy,
          status: version.status,
          note: version.note,
          bodyHtml: version.bodyHtml,
        };
      }),
    [selectedTemplate],
  );
  const templatePreviewHtml = renderARRecoveryDocumentTemplatePreview(selectedTemplate?.bodyHtml || editorHtml, {
    ...previewContext,
    TemplateName: draft.templateName || selectedTemplate?.templateName || title,
    StatementTitle: draft.subjectLine || selectedTemplate?.subjectLine || safeStatementDefaults.title,
    PaymentInstructions: safePaymentInstructions.title,
    PortalUrl: safeClientProfile.portalUrl || "Not configured",
    ClientName: safeLetterDefaults.businessName,
    ClientDBA: safeClientProfile.dbaName || safeLetterDefaults.businessName,
    ClientAddress: safeLetterDefaults.address,
    ClientPhone: safeLetterDefaults.phone,
    ClientEmail: safeLetterDefaults.email,
    ClientWebsite: safeLetterDefaults.website,
    ClientContactName: safeLetterDefaults.contactName,
    AccountName: selectedAccount?.customerName || "Not captured",
    AccountNumber: selectedAccount?.customerNumber || "Not captured",
    CustomerName: selectedAccount?.customerName || "Not captured",
    CustomerNumber: selectedAccount?.customerNumber || "Not captured",
    CurrentBalance: selectedAccount ? formatARRecoveryCurrency(selectedAccount.openBalanceCents) : formatARRecoveryCurrency(0),
    AgingBucket: selectedAccount?.agingSummary || "Current",
    DueDate: safeStatementDefaults.dueDate,
    OpenInvoiceCount: selectedAccount ? `${selectedAccount.openInvoiceCount}` : "0",
    OpenBalance: selectedAccount ? formatARRecoveryCurrency(selectedAccount.openBalanceCents) : formatARRecoveryCurrency(0),
    AssignedCollector: selectedAccount?.assignedCollector || workspace.collectors[0]?.collectorName || "Collector Desk",
    CollectorName: workspace.collectors[0]?.collectorName || safeClientProfile.collectionDepartmentContact || "Collector Desk",
    ManagerName: safeClientProfile.escalationContact || "Manager Desk",
    PromiseStatus: selectedAccount?.recoveryStatus || "Not captured",
    NextAction: selectedAccount?.notes || "Review account",
  });

  function createDraftTokenSummary() {
    return Array.from(new Set((editorHtml.match(/{{\s*([^}]+)\s*}}/g) || []).map((match) => match.replace(/{{|}}/g, "").trim()))).filter(Boolean);
  }

  function handleTemplateSelect(templateId: string) {
    setSelectedTemplateId(templateId);
    const nextTemplate = familyTemplates.find((template) => template.id === templateId);
    const nextAssignedAccountId = nextTemplate?.assignedAccountIds[0];
    if (nextAssignedAccountId) {
      setSelectedAccountId(nextAssignedAccountId);
    }
  }

  function handleAccountSelect(accountId: string) {
    setSelectedAccountId(accountId);
    const nextTemplate = familyTemplates.find((template) => template.assignedAccountIds.includes(accountId));
    if (nextTemplate) {
      setSelectedTemplateId(nextTemplate.id);
    }
  }

  function saveEditorSelection() {
    if (typeof window === "undefined") return;
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      editorSelectionRef.current = selection.getRangeAt(0).cloneRange();
    }
  }

  function restoreEditorSelection() {
    if (typeof window === "undefined") return false;
    const editor = editorRef.current;
    const selection = window.getSelection();
    const range = editorSelectionRef.current;
    if (!editor || !selection || !range) return false;
    selection.removeAllRanges();
    selection.addRange(range);
    editor.focus();
    return true;
  }

  function insertHtmlAtSelection(html: string) {
    const editor = editorRef.current;
    if (!editor || !canEditTemplates) return;
    restoreEditorSelection();
    document.execCommand("insertHTML", false, html);
    setEditorHtml(editor.innerHTML);
    saveEditorSelection();
  }

  function execEditorCommand(command: string, value?: string) {
    const editor = editorRef.current;
    if (!editor || !canEditTemplates) return;
    restoreEditorSelection();
    document.execCommand(command, false, value);
    setEditorHtml(editor.innerHTML);
    saveEditorSelection();
  }

  function updateDraft<K extends keyof ARRecoveryDocumentTemplateDraftState>(key: K, value: ARRecoveryDocumentTemplateDraftState[K]) {
    if (!canEditTemplates) return;
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function updateDraftRoles(roleKey: ARRecoveryDocumentTemplateRole) {
    if (!canEditTemplates) return;
    setDraft((current) => ({
      ...current,
      allowedRoles: current.allowedRoles.includes(roleKey) ? current.allowedRoles.filter((role) => role !== roleKey) : [...current.allowedRoles, roleKey],
    }));
  }

  function updateDraftAccount(accountId: string) {
    if (!canEditTemplates) return;
    setDraft((current) => ({
      ...current,
      assignedAccountIds: current.assignedAccountIds.includes(accountId) ? current.assignedAccountIds.filter((id) => id !== accountId) : [...current.assignedAccountIds, accountId],
    }));
  }

  function insertMergeField(token: string) {
    if (!canEditTemplates) return;
    insertHtmlAtSelection(token);
  }

  function handleEditorInput() {
    setEditorHtml(editorRef.current?.innerHTML || "");
    saveEditorSelection();
  }

  function handleEditorFormat(command: "bold" | "italic" | "underline" | "insertUnorderedList" | "insertOrderedList" | "justifyLeft" | "justifyCenter" | "justifyRight" | "justifyFull" | "insertHorizontalRule") {
    if (!canEditTemplates) return;
    execEditorCommand(command);
  }

  function handleEditorStyle(command: "fontName" | "fontSize" | "foreColor" | "hiliteColor", value: string) {
    if (!canEditTemplates) return;
    execEditorCommand(command, value);
  }

  function handleInsertLink() {
    if (!canEditTemplates) return;
    const url = window.prompt("Enter the hyperlink URL", "https://");
    if (!url) return;
    execEditorCommand("createLink", url.trim());
  }

  function handleInsertTable() {
    if (!canEditTemplates) return;
    insertHtmlAtSelection(`
      <table style="width:100%;border-collapse:collapse;margin:16px 0;font-size:14px;">
        <thead>
          <tr>
            <th style="border:1px solid #c9d5e5;padding:8px;text-align:left;">Column 1</th>
            <th style="border:1px solid #c9d5e5;padding:8px;text-align:left;">Column 2</th>
            <th style="border:1px solid #c9d5e5;padding:8px;text-align:left;">Column 3</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="border:1px solid #c9d5e5;padding:8px;">Row item</td>
            <td style="border:1px solid #c9d5e5;padding:8px;">Row item</td>
            <td style="border:1px solid #c9d5e5;padding:8px;">Row item</td>
          </tr>
        </tbody>
      </table>
    `);
  }

  function handleInsertLogo() {
    if (!canEditTemplates) return;
    const logoDataUri = createARRecoveryBrandLogoDataUri(safeBranding.displayName, safeBranding.primaryColor, safeBranding.accentColor);
    insertHtmlAtSelection(`
      <figure style="display:inline-flex;align-items:center;gap:12px;margin:12px 0;padding:12px 16px;border:1px solid #e5d5aa;border-radius:18px;background:#fffdf8;">
        <img src="${logoDataUri}" alt="${escapeHtml(safeBranding.displayName)} logo" style="display:block;max-width:220px;height:auto;" />
        <figcaption style="font-size:12px;line-height:1.6;color:#334155;font-weight:600;">Brand logo only</figcaption>
      </figure>
    `);
  }

  function handleInsertSignatureBlock() {
    if (!canEditTemplates) return;
    insertHtmlAtSelection(`
      <div style="margin:24px 0 8px 0;padding-top:16px;border-top:1px solid #cbd5e1;">
        <p style="margin:0 0 12px 0;">Respectfully,</p>
        <p style="margin:0;font-weight:800;">${escapeHtml(currentActorName || "Authenticated User")}</p>
        <p style="margin:4px 0 0 0;font-size:12px;color:#334155;">${escapeHtml(formatARRecoveryRole(currentActorRole))}</p>
      </div>
    `);
  }

  function handleSaveTemplate(nextStatus: ARRecoveryDocumentTemplateStatus = draft.status) {
    if (!canEditTemplates) return;
    const now = new Date().toISOString();
    const activeTemplate = selectedTemplate || createARRecoveryBlankDocumentTemplate(workspace, family);
    const versionLabel = `Version ${activeTemplate.versionHistory.length + 1}`;
    const nextTemplate: ARRecoveryDocumentTemplateRecord = {
      ...activeTemplate,
      templateName: draft.templateName.trim() || activeTemplate.templateName,
      summary: draft.summary.trim() || activeTemplate.summary,
      subjectLine: draft.subjectLine.trim() || activeTemplate.subjectLine,
      bodyHtml: editorHtml || activeTemplate.bodyHtml,
      brandMode: draft.brandMode,
      status: nextStatus,
      allowedRoles: draft.allowedRoles,
      assignedAccountIds: draft.assignedAccountIds,
      assignedCustomerIds: draft.assignedCustomerIds,
      mergeFields: Array.from(new Set(createDraftTokenSummary())),
      updatedAt: now,
      publishedAt: nextStatus === "Published" ? now : activeTemplate.publishedAt,
      previewNote: nextStatus === "Published" ? "Published version ready for operational use." : "Draft saved for future refinement.",
      versionHistory: [
        {
          id: `${activeTemplate.id}-${versionLabel.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${Date.now().toString(36)}`,
          label: versionLabel,
          note: nextStatus === "Published" ? "Template published for company-scoped use." : "Template draft saved.",
          bodyHtml: editorHtml || activeTemplate.bodyHtml,
          createdAt: now,
          createdBy: currentActorName || "Authenticated User",
          modifiedAt: now,
          modifiedBy: currentActorName || "Authenticated User",
          publishedAt: nextStatus === "Published" ? now : null,
          publishedBy: nextStatus === "Published" ? currentActorName || "Authenticated User" : undefined,
          status: nextStatus === "Published" ? "Published" : "Draft",
        },
        ...activeTemplate.versionHistory,
      ],
    };
    const nextTemplates = templates.some((template) => template.id === nextTemplate.id)
      ? templates.map((template) => (template.id === nextTemplate.id ? nextTemplate : template))
      : [nextTemplate, ...templates];
    onChangeTemplates(nextTemplates);
    setSelectedTemplateId(nextTemplate.id);
    setDraft(createARRecoveryDocumentTemplateDraft(nextTemplate));
    setEditorHtml(nextTemplate.bodyHtml);
  }

  function handleCreateTemplate() {
    const nextTemplate = createARRecoveryBlankDocumentTemplate(workspace, family);
    onChangeTemplates([nextTemplate, ...templates]);
    setSelectedTemplateId(nextTemplate.id);
    setDraft(createARRecoveryDocumentTemplateDraft(nextTemplate));
    setEditorHtml(nextTemplate.bodyHtml);
    setSelectedAccountId(nextTemplate.assignedAccountIds[0] || workspace.accounts[0]?.id || "");
  }

  function handleSaveAsTemplate() {
    if (!canEditTemplates) return;
    const activeTemplate = selectedTemplate || createARRecoveryBlankDocumentTemplate(workspace, family);
    const now = new Date().toISOString();
    const copyTemplate: ARRecoveryDocumentTemplateRecord = {
      ...activeTemplate,
      id: `${activeTemplate.id}-copy-${Date.now().toString(36)}`,
      templateName: `${draft.templateName.trim() || activeTemplate.templateName} Copy`,
      summary: draft.summary.trim() || activeTemplate.summary,
      subjectLine: draft.subjectLine.trim() || activeTemplate.subjectLine,
      bodyHtml: editorHtml || activeTemplate.bodyHtml,
      brandMode: draft.brandMode,
      status: "Draft",
      allowedRoles: [...draft.allowedRoles],
      assignedAccountIds: [...draft.assignedAccountIds],
      assignedCustomerIds: [...draft.assignedCustomerIds],
      mergeFields: Array.from(new Set(createDraftTokenSummary())),
      previewNote: "Saved as a new working copy.",
      versionHistory: [
        {
          id: `${activeTemplate.id}-copy-${Date.now().toString(36)}`,
          label: "Version 1",
          note: "Template copied into a new working draft.",
          bodyHtml: editorHtml || activeTemplate.bodyHtml,
          createdAt: now,
          createdBy: currentActorName || "Authenticated User",
          modifiedAt: now,
          modifiedBy: currentActorName || "Authenticated User",
          status: "Draft",
        },
      ],
      createdAt: now,
      updatedAt: now,
      publishedAt: null,
    };
    const nextTemplates = [copyTemplate, ...templates];
    onChangeTemplates(nextTemplates);
    setSelectedTemplateId(copyTemplate.id);
    setDraft(createARRecoveryDocumentTemplateDraft(copyTemplate));
    setEditorHtml(copyTemplate.bodyHtml);
    setSelectedAccountId(copyTemplate.assignedAccountIds[0] || workspace.accounts[0]?.id || "");
  }

  function handleRestoreVersion(versionId: string) {
    if (!canEditTemplates || !selectedTemplate) return;
    const targetVersion = selectedTemplate.versionHistory.find((version) => version.id === versionId);
    if (!targetVersion) return;
    const restoredHtml = targetVersion.bodyHtml;
    setEditorHtml(restoredHtml);
    setDraft((current) => ({
      ...current,
      status: "Draft",
      templateName: current.templateName || selectedTemplate.templateName,
      summary: current.summary || selectedTemplate.summary,
      subjectLine: current.subjectLine || selectedTemplate.subjectLine,
    }));
    if (editorRef.current) {
      editorRef.current.innerHTML = restoredHtml;
    }
  }

  function handleDeleteTemplate() {
    if (!canEditTemplates || !selectedTemplate) return;
    if (typeof window !== "undefined" && !window.confirm(`Delete template "${selectedTemplate.templateName}"? This cannot be undone.`)) {
      return;
    }
    const remainingTemplates = templates.filter((template) => template.id !== selectedTemplate.id);
    const nextTemplates = remainingTemplates.length ? remainingTemplates : [createARRecoveryBlankDocumentTemplate(workspace, family)];
    onChangeTemplates(nextTemplates);
    const nextSelection = nextTemplates[0];
    setSelectedTemplateId(nextSelection.id);
    setDraft(createARRecoveryDocumentTemplateDraft(nextSelection));
    setEditorHtml(nextSelection.bodyHtml);
    setSelectedAccountId(nextSelection.assignedAccountIds[0] || workspace.accounts[0]?.id || "");
  }

  function handleArchiveTemplate() {
    if (!canEditTemplates) return;
    handleSaveTemplate("Archived");
  }

  function handlePrintTemplate() {
    const templateName = draft.templateName || selectedTemplate?.templateName || title;
    const previewHtml = templatePreviewHtml;
    if (typeof window === "undefined") return;
    const printWindow = window.open("", "_blank", "noopener,noreferrer,width=1024,height=1200");
    const printMarkup = `<!doctype html><html><head><title>${escapeHtml(templateName)}</title><style>body{font-family:Inter,Arial,sans-serif;margin:0;padding:40px;color:#111827;background:#fffdf8;} .page{max-width:900px;margin:0 auto;padding:32px;background:#fff;border:1px solid #e5d5aa;border-radius:24px;box-shadow:0 20px 45px rgba(17,24,39,.08);} h1,h2,h3,p{margin:0 0 16px 0;} strong{font-weight:800;}</style></head><body><div class="page">${previewHtml}</div></body></html>`;
    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(printMarkup);
      printWindow.document.close();
      printWindow.focus();
      window.setTimeout(() => printWindow.print(), 250);
      window.setTimeout(() => printWindow.close(), 750);
      return;
    }
    window.print();
  }

  function handlePreviewTemplate() {
    previewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    previewRef.current?.focus?.({ preventScroll: true });
  }

  const familySelectedTemplateCount = familyTemplates.length;

  return (
    <article className="rounded-[1.5rem] border border-[rgba(34,224,208,.16)] bg-[rgba(255,255,255,.03)] p-5 shadow-[0_20px_48px_rgba(0,0,0,.18)]">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="grid gap-3">
          <SectionHeading eyebrow={family === "letter" ? "Professional Letter Center" : "Professional Statement Center"} title={title} description={description} />
          <div className="flex flex-wrap gap-2 text-[11px] font-black uppercase tracking-[0.16em]">
            <span className="rounded-full border border-[rgba(34,224,208,.18)] bg-[rgba(34,224,208,.08)] px-3 py-1 text-[#22E0D0]">Split-screen designer</span>
            <span className="rounded-full border border-[rgba(255,106,43,.18)] bg-[rgba(255,106,43,.08)] px-3 py-1 text-[#FF6A2B]">{formatARRecoveryRole(currentActorRole)}</span>
            <span className="rounded-full border border-[rgba(255,106,43,.12)] bg-[rgba(7,18,31,.46)] px-3 py-1 text-[#E8EEF4]">{selectedAccount?.customerName || workspace.company.companyName}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canEditTemplates ? (
            <>
              <button type="button" onClick={handleCreateTemplate} className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[rgba(34,224,208,.26)] bg-[rgba(7,18,31,.62)] px-4 text-sm font-extrabold text-[#F6F1E8] transition hover:border-[rgba(34,224,208,.46)] hover:bg-[rgba(34,224,208,.12)] focus-ring">
                <FileText className="size-4" aria-hidden="true" />
                New Template
              </button>
              <button type="button" onClick={() => handleSaveTemplate("Draft")} className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[rgba(255,106,43,.26)] bg-[rgba(7,18,31,.62)] px-4 text-sm font-extrabold text-[#F6F1E8] transition hover:border-[rgba(255,106,43,.46)] hover:bg-[rgba(255,106,43,.1)] focus-ring">
                <Clock3 className="size-4" aria-hidden="true" />
                Save Draft
              </button>
              <button type="button" onClick={handleSaveAsTemplate} className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[rgba(255,106,43,.22)] bg-[rgba(7,18,31,.62)] px-4 text-sm font-extrabold text-[#F6F1E8] transition hover:border-[rgba(255,106,43,.42)] hover:bg-[rgba(255,106,43,.1)] focus-ring">
                <Target className="size-4" aria-hidden="true" />
                Save As
              </button>
              <button type="button" onClick={() => handleSaveTemplate("Published")} className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[rgba(34,224,208,.26)] bg-[rgba(7,18,31,.62)] px-4 text-sm font-extrabold text-[#F6F1E8] transition hover:border-[rgba(34,224,208,.46)] hover:bg-[rgba(34,224,208,.12)] focus-ring">
                <Sparkles className="size-4" aria-hidden="true" />
                Publish Version
              </button>
              <button type="button" onClick={handleArchiveTemplate} className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[rgba(148,163,184,.26)] bg-[rgba(7,18,31,.62)] px-4 text-sm font-extrabold text-[#F6F1E8] transition hover:border-[rgba(148,163,184,.46)] hover:bg-[rgba(148,163,184,.1)] focus-ring">
                Archive
              </button>
              <button type="button" onClick={handleDeleteTemplate} className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[rgba(239,68,68,.28)] bg-[rgba(7,18,31,.62)] px-4 text-sm font-extrabold text-[#F6F1E8] transition hover:border-[rgba(239,68,68,.48)] hover:bg-[rgba(239,68,68,.12)] focus-ring">
                Delete
              </button>
            </>
          ) : (
            <div className="inline-flex items-center rounded-full border border-[rgba(34,224,208,.2)] bg-[rgba(7,18,31,.62)] px-4 py-2 text-[11px] font-black uppercase tracking-[0.16em] text-[#CBD5E1]">
              Preview-only mode for {formatARRecoveryRole(currentActorRole)}
            </div>
          )}
          <button type="button" onClick={handlePrintTemplate} className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[rgba(255,106,43,.26)] bg-[rgba(7,18,31,.62)] px-4 text-sm font-extrabold text-[#F6F1E8] transition hover:border-[rgba(255,106,43,.46)] hover:bg-[rgba(255,106,43,.1)] focus-ring">
            <ArrowRight className="size-4 rotate-[-45deg]" aria-hidden="true" />
            Download PDF
          </button>
          <button type="button" onClick={handlePrintTemplate} className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[rgba(34,224,208,.26)] bg-[rgba(7,18,31,.62)] px-4 text-sm font-extrabold text-[#F6F1E8] transition hover:border-[rgba(34,224,208,.46)] hover:bg-[rgba(34,224,208,.12)] focus-ring">
            <ArrowRight className="size-4" aria-hidden="true" />
            Print
          </button>
        </div>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {metrics.map((metric) => (
          <DetailMetric key={metric.label} label={metric.label} value={metric.value} />
        ))}
      </div>

      <div className="mt-5 grid gap-4 xl:grid-cols-[.88fr_1.12fr]">
        <div className="grid gap-4">
          <section className="rounded-[1.35rem] border border-[rgba(34,224,208,.16)] bg-[rgba(7,18,31,.72)] p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Client selector</p>
                <p className="mt-1 text-sm font-semibold leading-6 text-[#E8EEF4]">Switch the selected client and the designer will load matching assigned templates automatically.</p>
              </div>
              <span className="rounded-full border border-[rgba(255,106,43,.2)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#FF6A2B]">
                {selectedAccount ? "Assigned client" : "No client selected"}
              </span>
            </div>
            <label className="mt-3 grid gap-2 rounded-2xl border border-[rgba(255,106,43,.12)] bg-[rgba(255,255,255,.03)] p-3">
              <span className="text-[11px] font-black uppercase tracking-[0.14em] text-[#FF6A2B]">Active Client</span>
              <select
                value={selectedAccount?.id || ""}
                onChange={(event) => handleAccountSelect(event.target.value)}
                className="focus-ring rounded-2xl border border-[rgba(255,106,43,.16)] bg-[rgba(7,18,31,.72)] px-3 py-2.5 text-sm font-semibold text-[#F6F1E8] outline-none transition focus:border-[rgba(34,224,208,.42)]"
              >
                {(assignableAccounts.length ? assignableAccounts : workspace.accounts).map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.customerName} · {account.customerNumber}
                  </option>
                ))}
              </select>
            </label>
          </section>

          <section className="rounded-[1.35rem] border border-[rgba(34,224,208,.16)] bg-[rgba(7,18,31,.72)] p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Template Library</p>
                <p className="mt-1 text-sm font-semibold leading-6 text-[#E8EEF4]">{familySelectedTemplateCount} seeded template{familySelectedTemplateCount === 1 ? "" : "s"} ready for editing.</p>
              </div>
              <span className="rounded-full border border-[rgba(255,106,43,.2)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#FF6A2B]">
                {family === "letter" ? "Letter" : "Statement"}
              </span>
            </div>
            <div className="mt-3 grid gap-2">
              {familyTemplates.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => handleTemplateSelect(template.id)}
                  className={`rounded-2xl border px-4 py-3 text-left transition focus-ring ${
                    template.id === selectedTemplate?.id
                      ? "border-[rgba(34,224,208,.52)] bg-[rgba(34,224,208,.12)]"
                      : "border-[rgba(34,224,208,.14)] bg-[rgba(255,255,255,.03)] hover:border-[rgba(34,224,208,.3)] hover:bg-[rgba(255,255,255,.06)]"
                  }`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">{template.family === "letter" ? "Letter" : "Statement"}</p>
                      <h3 className="mt-1 font-display text-xl font-bold text-[#F6F1E8]">{template.templateName}</h3>
                    </div>
                    <span className="rounded-full border border-[rgba(255,106,43,.18)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#FF6A2B]">
                      {template.status}
                    </span>
                  </div>
                  <p className="mt-2 text-sm font-semibold leading-6 text-[#E8EEF4]">{template.summary}</p>
                  <p className="mt-2 text-[11px] font-black uppercase tracking-[0.14em] text-[#94A3B8]">
                    {template.versionHistory.length} version{template.versionHistory.length === 1 ? "" : "s"} | {template.assignedAccountIds.length} account{template.assignedAccountIds.length === 1 ? "" : "s"}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="rounded-full border border-[rgba(34,224,208,.14)] px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#CBD5E1]">
                      Version {template.versionHistory.length}
                    </span>
                    <span className="rounded-full border border-[rgba(255,106,43,.14)] px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#CBD5E1]">
                      Modified {formatCapturedDate(template.updatedAt)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </section>

          <section className="rounded-[1.35rem] border border-[rgba(34,224,208,.16)] bg-[rgba(7,18,31,.72)] p-4">
            <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Merge field engine</p>
            <p className="mt-1 text-sm font-semibold leading-6 text-[#E8EEF4]">Insert client, customer, account, invoice, and recovery fields into the active template.</p>
            <div className="mt-3 grid gap-3">
              {AR_RECOVERY_DOCUMENT_TEMPLATE_FIELD_GROUPS.map((group) => (
                <div key={group.label} className="rounded-2xl border border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.03)] p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-[11px] font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{group.label}</p>
                      <p className="mt-1 text-xs font-semibold leading-5 text-[#CBD5E1]">{group.note}</p>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {group.fields.map((field) => (
                      <button
                        key={field.token}
                        type="button"
                        title={`${field.token} · ${field.note}`}
                        onClick={() => insertMergeField(field.token)}
                        className="inline-flex min-h-8 items-center rounded-full border border-[rgba(34,224,208,.18)] bg-[rgba(34,224,208,.07)] px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#22E0D0] transition hover:border-[rgba(34,224,208,.38)] hover:bg-[rgba(34,224,208,.12)] hover:text-[#F6F1E8] focus-ring"
                      >
                        {field.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-[1.35rem] border border-[rgba(34,224,208,.16)] bg-[rgba(7,18,31,.72)] p-4">
            <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Template permissions</p>
            <p className="mt-1 text-sm font-semibold leading-6 text-[#E8EEF4]">Platform Admin, Company Admin, Admin, and Manager can create and publish. Supervisor can be enabled per template. Collector, Viewer, and Read Only stay review-only.</p>
            <div className="mt-3 grid gap-2">
              {AR_RECOVERY_DOCUMENT_TEMPLATE_ROLE_OPTIONS.map((role) => (
                <label
                  key={role.key}
                  className={`flex items-start gap-3 rounded-2xl border px-3 py-3 text-sm font-semibold transition ${
                    draft.allowedRoles.includes(role.key)
                      ? "border-[rgba(34,224,208,.3)] bg-[rgba(34,224,208,.1)]"
                      : "border-[rgba(255,106,43,.12)] bg-[rgba(255,255,255,.03)]"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={draft.allowedRoles.includes(role.key)}
                    onChange={() => updateDraftRoles(role.key)}
                    disabled={!canEditTemplates}
                    className="mt-1 size-4 accent-[#22E0D0]"
                  />
                  <span className="grid gap-1">
                    <span className="text-[#F6F1E8]">{role.label}</span>
                    <span className="text-xs font-semibold leading-5 text-[#CBD5E1]">{role.note}</span>
                  </span>
                </label>
              ))}
            </div>
          </section>

          <section className="rounded-[1.35rem] border border-[rgba(34,224,208,.16)] bg-[rgba(7,18,31,.72)] p-4">
            <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Client template assignment</p>
            <p className="mt-1 text-sm font-semibold leading-6 text-[#E8EEF4]">Assign the current template to one or more current accounts for future client-scoped use.</p>
            <div className="mt-3 grid gap-2">
              {assignableAccounts.length ? (
                assignableAccounts.map((account) => {
                  const checked = draft.assignedAccountIds.includes(account.id);
                  return (
                    <label
                      key={account.id}
                      className={`flex items-start gap-3 rounded-2xl border px-3 py-3 text-sm font-semibold transition ${
                        checked ? "border-[rgba(255,106,43,.28)] bg-[rgba(255,106,43,.08)]" : "border-[rgba(255,106,43,.12)] bg-[rgba(255,255,255,.03)]"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => updateDraftAccount(account.id)}
                        disabled={!canEditTemplates}
                        className="mt-1 size-4 accent-[#FF6A2B]"
                      />
                      <span className="grid gap-1">
                        <span className="text-[#F6F1E8]">{account.customerName}</span>
                        <span className="text-xs font-semibold leading-5 text-[#CBD5E1]">
                          {account.customerNumber} | {account.assignedCollector}
                        </span>
                      </span>
                    </label>
                  );
                })
              ) : (
                <ManagerEmptyState
                  title="No accounts available"
                  description="The template manager will still function and the first account assignments can be added once the portfolio contains accounts."
                  action="Assignments are stored per template."
                />
              )}
            </div>
          </section>
        </div>

        <div className="grid gap-4">
          <article className="rounded-[1.35rem] border border-[rgba(34,224,208,.16)] bg-[rgba(7,18,31,.72)] p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Template editor</p>
                <h3 className="mt-1 font-display text-2xl font-bold text-[#F6F1E8]">{draft.templateName || selectedTemplate?.templateName || "Untitled Template"}</h3>
                <p className="mt-2 text-sm font-semibold leading-6 text-[#E8EEF4]">{draft.summary || selectedTemplate?.summary || "Template summary."}</p>
              </div>
              <div className="grid gap-2">
                <div className="rounded-2xl border border-[rgba(255,106,43,.12)] bg-[rgba(255,255,255,.03)] p-3 text-xs font-semibold leading-6 text-[#CBD5E1]">
                  <div className="flex flex-wrap gap-3">
                    <span className="rounded-full border border-[rgba(34,224,208,.14)] px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#22E0D0]">
                      Version {selectedTemplate ? selectedTemplate.versionHistory.length : 1}
                    </span>
                    <span className="rounded-full border border-[rgba(255,106,43,.14)] px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#FF6A2B]">
                      Created {formatCapturedDate(selectedTemplate?.createdAt || new Date().toISOString())}
                    </span>
                    <span className="rounded-full border border-[rgba(255,106,43,.14)] px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#FF6A2B]">
                      Modified {formatCapturedDate(selectedTemplate?.updatedAt || new Date().toISOString())}
                    </span>
                    <span className="rounded-full border border-[rgba(34,224,208,.14)] px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#CBD5E1]">
                      Published {selectedTemplate?.publishedAt ? formatCapturedDate(selectedTemplate.publishedAt) : "Draft"}
                    </span>
                    <span className="rounded-full border border-[rgba(34,224,208,.14)] px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#CBD5E1]">
                      Client {selectedAccount?.customerName || "Not selected"}
                    </span>
                  </div>
                  <p className="mt-2">
                    Created by {selectedTemplate?.versionHistory[0]?.createdBy || currentActorName || "Authenticated User"} · Modified by {selectedTemplate?.versionHistory[0]?.modifiedBy || selectedTemplate?.versionHistory[0]?.createdBy || currentActorName || "Authenticated User"} · Role {formatARRecoveryRole(currentActorRole)}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => handleEditorFormat("bold")} disabled={!canEditTemplates} className="inline-flex min-h-9 items-center rounded-full border border-[rgba(34,224,208,.18)] bg-[rgba(255,255,255,.04)] px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#22E0D0] transition hover:border-[rgba(34,224,208,.38)] hover:bg-[rgba(34,224,208,.12)] focus-ring disabled:cursor-not-allowed disabled:opacity-40">
                    Bold
                  </button>
                  <button type="button" onClick={() => handleEditorFormat("italic")} disabled={!canEditTemplates} className="inline-flex min-h-9 items-center rounded-full border border-[rgba(34,224,208,.18)] bg-[rgba(255,255,255,.04)] px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#22E0D0] transition hover:border-[rgba(34,224,208,.38)] hover:bg-[rgba(34,224,208,.12)] focus-ring disabled:cursor-not-allowed disabled:opacity-40">
                    Italic
                  </button>
                  <button type="button" onClick={() => handleEditorFormat("underline")} disabled={!canEditTemplates} className="inline-flex min-h-9 items-center rounded-full border border-[rgba(34,224,208,.18)] bg-[rgba(255,255,255,.04)] px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#22E0D0] transition hover:border-[rgba(34,224,208,.38)] hover:bg-[rgba(34,224,208,.12)] focus-ring disabled:cursor-not-allowed disabled:opacity-40">
                    Underline
                  </button>
                  <button type="button" onClick={() => handleEditorFormat("insertUnorderedList")} disabled={!canEditTemplates} className="inline-flex min-h-9 items-center rounded-full border border-[rgba(34,224,208,.18)] bg-[rgba(255,255,255,.04)] px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#22E0D0] transition hover:border-[rgba(34,224,208,.38)] hover:bg-[rgba(34,224,208,.12)] focus-ring disabled:cursor-not-allowed disabled:opacity-40">
                    Bullets
                  </button>
                  <button type="button" onClick={() => handleEditorFormat("insertOrderedList")} disabled={!canEditTemplates} className="inline-flex min-h-9 items-center rounded-full border border-[rgba(34,224,208,.18)] bg-[rgba(255,255,255,.04)] px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#22E0D0] transition hover:border-[rgba(34,224,208,.38)] hover:bg-[rgba(34,224,208,.12)] focus-ring disabled:cursor-not-allowed disabled:opacity-40">
                    Numbered
                  </button>
                  <button type="button" onClick={() => handleEditorFormat("justifyLeft")} disabled={!canEditTemplates} className="inline-flex min-h-9 items-center rounded-full border border-[rgba(34,224,208,.18)] bg-[rgba(255,255,255,.04)] px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#22E0D0] transition hover:border-[rgba(34,224,208,.38)] hover:bg-[rgba(34,224,208,.12)] focus-ring disabled:cursor-not-allowed disabled:opacity-40">
                    Left
                  </button>
                  <button type="button" onClick={() => handleEditorFormat("justifyCenter")} disabled={!canEditTemplates} className="inline-flex min-h-9 items-center rounded-full border border-[rgba(34,224,208,.18)] bg-[rgba(255,255,255,.04)] px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#22E0D0] transition hover:border-[rgba(34,224,208,.38)] hover:bg-[rgba(34,224,208,.12)] focus-ring disabled:cursor-not-allowed disabled:opacity-40">
                    Center
                  </button>
                  <button type="button" onClick={() => handleEditorFormat("justifyRight")} disabled={!canEditTemplates} className="inline-flex min-h-9 items-center rounded-full border border-[rgba(34,224,208,.18)] bg-[rgba(255,255,255,.04)] px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#22E0D0] transition hover:border-[rgba(34,224,208,.38)] hover:bg-[rgba(34,224,208,.12)] focus-ring disabled:cursor-not-allowed disabled:opacity-40">
                    Right
                  </button>
                  <button type="button" onClick={() => handleEditorFormat("justifyFull")} disabled={!canEditTemplates} className="inline-flex min-h-9 items-center rounded-full border border-[rgba(34,224,208,.18)] bg-[rgba(255,255,255,.04)] px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#22E0D0] transition hover:border-[rgba(34,224,208,.38)] hover:bg-[rgba(34,224,208,.12)] focus-ring disabled:cursor-not-allowed disabled:opacity-40">
                    Justify
                  </button>
                </div>
                <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
                  <label className="grid gap-1">
                    <span className="text-[10px] font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Font Family</span>
                    <select
                      defaultValue="Inter"
                      onChange={(event) => handleEditorStyle("fontName", event.target.value)}
                      disabled={!canEditTemplates}
                      className="focus-ring rounded-2xl border border-[rgba(255,106,43,.16)] bg-[rgba(7,18,31,.72)] px-3 py-2 text-sm font-semibold text-[#F6F1E8] outline-none transition disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <option value="Inter">Inter</option>
                      <option value="Arial">Arial</option>
                      <option value="Georgia">Georgia</option>
                      <option value="Times New Roman">Times New Roman</option>
                      <option value="Courier New">Courier New</option>
                    </select>
                  </label>
                  <label className="grid gap-1">
                    <span className="text-[10px] font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Font Size</span>
                    <select
                      defaultValue="3"
                      onChange={(event) => handleEditorStyle("fontSize", event.target.value)}
                      disabled={!canEditTemplates}
                      className="focus-ring rounded-2xl border border-[rgba(255,106,43,.16)] bg-[rgba(7,18,31,.72)] px-3 py-2 text-sm font-semibold text-[#F6F1E8] outline-none transition disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <option value="1">Small</option>
                      <option value="2">Regular Small</option>
                      <option value="3">Regular</option>
                      <option value="4">Large</option>
                      <option value="5">XL</option>
                      <option value="6">2XL</option>
                      <option value="7">3XL</option>
                    </select>
                  </label>
                  <label className="grid gap-1">
                    <span className="text-[10px] font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Text Color</span>
                    <select
                      defaultValue="#111827"
                      onChange={(event) => handleEditorStyle("foreColor", event.target.value)}
                      disabled={!canEditTemplates}
                      className="focus-ring rounded-2xl border border-[rgba(255,106,43,.16)] bg-[rgba(7,18,31,.72)] px-3 py-2 text-sm font-semibold text-[#F6F1E8] outline-none transition disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <option value="#111827">Ink</option>
                      <option value="#0f172a">Slate</option>
                      <option value="#D94A16">Gold</option>
                      <option value="#0f766e">Teal</option>
                      <option value="#d97706">Amber</option>
                    </select>
                  </label>
                  <label className="grid gap-1">
                    <span className="text-[10px] font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Highlight</span>
                    <select
                      defaultValue="#fffdf8"
                      onChange={(event) => handleEditorStyle("hiliteColor", event.target.value)}
                      disabled={!canEditTemplates}
                      className="focus-ring rounded-2xl border border-[rgba(255,106,43,.16)] bg-[rgba(7,18,31,.72)] px-3 py-2 text-sm font-semibold text-[#F6F1E8] outline-none transition disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <option value="#fffdf8">Soft White</option>
                      <option value="#dbeafe">Blue Wash</option>
                      <option value="#dcfce7">Green Wash</option>
                      <option value="#fef3c7">Gold Wash</option>
                    </select>
                  </label>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={handleInsertLink} disabled={!canEditTemplates} className="inline-flex min-h-9 items-center rounded-full border border-[rgba(255,106,43,.18)] bg-[rgba(255,255,255,.04)] px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#FF6A2B] transition hover:border-[rgba(255,106,43,.38)] hover:bg-[rgba(255,106,43,.12)] focus-ring disabled:cursor-not-allowed disabled:opacity-40">
                    Link
                  </button>
                  <button type="button" onClick={handleInsertTable} disabled={!canEditTemplates} className="inline-flex min-h-9 items-center rounded-full border border-[rgba(255,106,43,.18)] bg-[rgba(255,255,255,.04)] px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#FF6A2B] transition hover:border-[rgba(255,106,43,.38)] hover:bg-[rgba(255,106,43,.12)] focus-ring disabled:cursor-not-allowed disabled:opacity-40">
                    Table
                  </button>
                  <button type="button" onClick={() => handleEditorFormat("insertHorizontalRule")} disabled={!canEditTemplates} className="inline-flex min-h-9 items-center rounded-full border border-[rgba(255,106,43,.18)] bg-[rgba(255,255,255,.04)] px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#FF6A2B] transition hover:border-[rgba(255,106,43,.38)] hover:bg-[rgba(255,106,43,.12)] focus-ring disabled:cursor-not-allowed disabled:opacity-40">
                    Rule
                  </button>
                  <button type="button" onClick={handleInsertLogo} disabled={!canEditTemplates} className="inline-flex min-h-9 items-center rounded-full border border-[rgba(34,224,208,.18)] bg-[rgba(255,255,255,.04)] px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#22E0D0] transition hover:border-[rgba(34,224,208,.38)] hover:bg-[rgba(34,224,208,.12)] focus-ring disabled:cursor-not-allowed disabled:opacity-40">
                    Logo
                  </button>
                  <button type="button" onClick={handleInsertSignatureBlock} disabled={!canEditTemplates} className="inline-flex min-h-9 items-center rounded-full border border-[rgba(34,224,208,.18)] bg-[rgba(255,255,255,.04)] px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#22E0D0] transition hover:border-[rgba(34,224,208,.38)] hover:bg-[rgba(34,224,208,.12)] focus-ring disabled:cursor-not-allowed disabled:opacity-40">
                    Signature Block
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <label className="grid gap-2 rounded-2xl border border-[rgba(255,106,43,.12)] bg-[rgba(255,255,255,.03)] p-3">
                <span className="text-[11px] font-black uppercase tracking-[0.14em] text-[#FF6A2B]">Template Name</span>
                <input
                  value={draft.templateName}
                  onChange={(event) => updateDraft("templateName", event.target.value)}
                  disabled={!canEditTemplates}
                  className="focus-ring rounded-2xl border border-[rgba(255,106,43,.16)] bg-[rgba(7,18,31,.72)] px-3 py-2.5 text-sm font-semibold text-[#F6F1E8] outline-none transition placeholder:text-[#64748B] focus:border-[rgba(34,224,208,.42)]"
                />
              </label>
              <label className="grid gap-2 rounded-2xl border border-[rgba(255,106,43,.12)] bg-[rgba(255,255,255,.03)] p-3">
                <span className="text-[11px] font-black uppercase tracking-[0.14em] text-[#FF6A2B]">Subject Line</span>
                <input
                  value={draft.subjectLine}
                  onChange={(event) => updateDraft("subjectLine", event.target.value)}
                  disabled={!canEditTemplates}
                  className="focus-ring rounded-2xl border border-[rgba(255,106,43,.16)] bg-[rgba(7,18,31,.72)] px-3 py-2.5 text-sm font-semibold text-[#F6F1E8] outline-none transition placeholder:text-[#64748B] focus:border-[rgba(34,224,208,.42)]"
                />
              </label>
              <label className="grid gap-2 rounded-2xl border border-[rgba(255,106,43,.12)] bg-[rgba(255,255,255,.03)] p-3 md:col-span-2">
                <span className="text-[11px] font-black uppercase tracking-[0.14em] text-[#FF6A2B]">Template Summary</span>
                <textarea
                  value={draft.summary}
                  onChange={(event) => updateDraft("summary", event.target.value)}
                  disabled={!canEditTemplates}
                  className="focus-ring min-h-24 rounded-2xl border border-[rgba(255,106,43,.16)] bg-[rgba(7,18,31,.72)] px-3 py-2.5 text-sm font-semibold leading-7 text-[#F6F1E8] outline-none transition placeholder:text-[#64748B] focus:border-[rgba(34,224,208,.42)]"
                />
              </label>
              <label className="grid gap-2 rounded-2xl border border-[rgba(255,106,43,.12)] bg-[rgba(255,255,255,.03)] p-3">
                <span className="text-[11px] font-black uppercase tracking-[0.14em] text-[#FF6A2B]">Brand Mode</span>
                <select
                  value={draft.brandMode}
                  onChange={(event) => updateDraft("brandMode", event.target.value as ARRecoveryDocumentTemplateDraftState["brandMode"])}
                  disabled={!canEditTemplates}
                  className="focus-ring rounded-2xl border border-[rgba(255,106,43,.16)] bg-[rgba(7,18,31,.72)] px-3 py-2.5 text-sm font-semibold text-[#F6F1E8] outline-none transition focus:border-[rgba(34,224,208,.42)]"
                >
                  <option value="company">Company Brand</option>
                  <option value="client">Client Brand</option>
                </select>
              </label>
              <label className="grid gap-2 rounded-2xl border border-[rgba(255,106,43,.12)] bg-[rgba(255,255,255,.03)] p-3">
                <span className="text-[11px] font-black uppercase tracking-[0.14em] text-[#FF6A2B]">Template Status</span>
                <select
                  value={draft.status}
                  onChange={(event) => updateDraft("status", event.target.value as ARRecoveryDocumentTemplateStatus)}
                  disabled={!canEditTemplates}
                  className="focus-ring rounded-2xl border border-[rgba(255,106,43,.16)] bg-[rgba(7,18,31,.72)] px-3 py-2.5 text-sm font-semibold text-[#F6F1E8] outline-none transition focus:border-[rgba(34,224,208,.42)]"
                >
                  <option value="Draft">Draft</option>
                  <option value="Published">Published</option>
                  <option value="Archived">Archived</option>
                </select>
              </label>
            </div>

            <div className="mt-4 rounded-[1.35rem] border border-[rgba(34,224,208,.14)] bg-[rgba(255,255,255,.03)] p-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.14em] text-[#22E0D0]">Rich text body</p>
                  <p className="mt-1 text-xs font-semibold leading-5 text-[#CBD5E1]">Use the toolbar and merge-field chips to shape the final letter or statement language.</p>
                </div>
                <span className="rounded-full border border-[rgba(255,106,43,.18)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#FF6A2B]">
                  {draft.allowedRoles.length} roles
                </span>
              </div>
              <div
                ref={editorRef}
                contentEditable={canEditTemplates}
                spellCheck
                suppressContentEditableWarning
                onInput={handleEditorInput}
                onMouseUp={saveEditorSelection}
                onKeyUp={saveEditorSelection}
                onFocus={saveEditorSelection}
                className={`focus-ring mt-3 min-h-[16rem] rounded-2xl border border-[rgba(255,106,43,.16)] bg-[rgba(7,18,31,.72)] px-4 py-4 text-sm font-semibold leading-7 text-[#F6F1E8] outline-none transition ${canEditTemplates ? "" : "cursor-default opacity-95"}`}
              />
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handlePreviewTemplate}
                className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[rgba(34,224,208,.26)] bg-[rgba(7,18,31,.62)] px-4 text-sm font-extrabold text-[#F6F1E8] transition hover:border-[rgba(34,224,208,.46)] hover:bg-[rgba(34,224,208,.12)] focus-ring"
              >
                Preview
              </button>
              <button
                type="button"
                onClick={handlePrintTemplate}
                className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[rgba(255,106,43,.26)] bg-[rgba(7,18,31,.62)] px-4 text-sm font-extrabold text-[#F6F1E8] transition hover:border-[rgba(255,106,43,.46)] hover:bg-[rgba(255,106,43,.1)] focus-ring"
              >
                Download PDF
              </button>
              <button
                type="button"
                onClick={handlePrintTemplate}
                className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[rgba(34,224,208,.26)] bg-[rgba(7,18,31,.62)] px-4 text-sm font-extrabold text-[#F6F1E8] transition hover:border-[rgba(34,224,208,.46)] hover:bg-[rgba(34,224,208,.12)] focus-ring"
              >
                Print
              </button>
            </div>
          </article>

          <article ref={previewRef} tabIndex={-1} className="rounded-[1.35rem] border border-[rgba(34,224,208,.16)] bg-[#fffdf8] p-4 text-[#111827] shadow-[0_18px_40px_rgba(17,24,39,.14)]">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#D94A16]">Live preview</p>
                <h4 className="mt-1 font-display text-2xl font-bold text-[#111827]">{draft.templateName || selectedTemplate?.templateName || title}</h4>
                <p className="mt-2 text-sm font-semibold leading-6 text-[#334155]">{safeBranding.displayName} · {draft.brandMode === "company" ? "Company brand" : "Client brand"}</p>
              </div>
              <div className="rounded-full border border-[#FF6A2B]/24 bg-white px-3 py-1 text-[11px] font-black uppercase tracking-[0.16em] text-[#D94A16]">
                {draft.status}
              </div>
            </div>
            <div className="mt-4 rounded-[1.35rem] border border-[#e5d5aa] bg-white p-4 shadow-[0_12px_28px_rgba(17,24,39,.08)]">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#D94A16]">Rendered output</p>
                  <p className="mt-1 text-sm font-semibold leading-6 text-[#334155]">
                    {family === "letter" ? safePaymentInstructions.support : safeStatementDefaults.title}
                  </p>
                </div>
                <div className="inline-flex items-center gap-2 rounded-full border border-[#FF6A2B]/18 px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#D94A16]">
                  {safeBranding.primaryColor}
                  <span className="size-3 rounded-full" style={{ backgroundColor: safeBranding.primaryColor }} aria-hidden="true" />
                  <span className="size-3 rounded-full" style={{ backgroundColor: safeBranding.accentColor }} aria-hidden="true" />
                </div>
              </div>
              <div
                className="mt-4 grid gap-3 text-sm leading-7 text-[#111827]"
                dangerouslySetInnerHTML={{
                  __html: templatePreviewHtml,
                }}
              />
            </div>
          </article>

          <article className="rounded-[1.35rem] border border-[rgba(34,224,208,.16)] bg-[rgba(7,18,31,.72)] p-4">
            <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Version history</p>
            <p className="mt-1 text-sm font-semibold leading-6 text-[#E8EEF4]">The latest publication is stored with the current template so review history stays intact.</p>
            <div className="mt-4">
              {versionCards.length ? (
                <div className="grid gap-3">
                  {versionCards.map((version) => (
                    <div key={version.id} className="rounded-2xl border border-[rgba(34,224,208,.14)] bg-[rgba(255,255,255,.03)] p-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Version {version.versionNumber}</p>
                          <h4 className="mt-1 font-display text-xl font-bold text-[#F6F1E8]">{version.note}</h4>
                          <p className="mt-2 text-sm font-semibold leading-6 text-[#E8EEF4]">
                            Created by {version.createdBy} · Modified by {version.modifiedBy} · Published {version.publishedAt ? formatCapturedDate(version.publishedAt) : "Draft"}
                          </p>
                          <p className="mt-1 text-xs font-semibold leading-5 text-[#CBD5E1]">
                            Created {formatCapturedDate(version.createdAt)} · Modified {formatCapturedDate(version.modifiedAt)} · Status {version.status}
                          </p>
                        </div>
                        {canEditTemplates ? (
                          <button
                            type="button"
                            onClick={() => handleRestoreVersion(version.id)}
                            className="inline-flex min-h-10 items-center rounded-full border border-[rgba(255,106,43,.22)] bg-[rgba(7,18,31,.62)] px-3 text-xs font-black uppercase tracking-[0.12em] text-[#FF6A2B] transition hover:border-[rgba(255,106,43,.42)] hover:bg-[rgba(255,106,43,.1)] focus-ring"
                          >
                            Restore Previous Version
                          </button>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <ManagerEmptyState
                  title="No versions yet"
                  description="Publish or save the current draft to begin tracking template versions."
                />
              )}
            </div>
          </article>
        </div>
      </div>
    </article>
  );
}

function ClientPortalSection({
  workspace,
}: {
  workspace: ReturnType<typeof loadARRecoveryWorkspace>;
}) {
  const [portalSearch, setPortalSearch] = useState("");
  const [portfolioFilter, setPortfolioFilter] = useState("All Portfolios");
  const [accountStatusFilter, setAccountStatusFilter] = useState("All Statuses");
  const [dateRangeFilter, setDateRangeFilter] = useState("All Dates");
  const safeClientProfile = workspace.clientProfile;
  const safeLetterDefaults = safeClientProfile.letterDefaults || {
    businessName: safeClientProfile.businessName || workspace.company.companyName,
    contactName: safeClientProfile.collectionDepartmentContact || "Not captured",
  };
  const activationFoundation = useMemo(() => loadPortfolioActivationEngineFoundation(workspace.company.id), [workspace.company.id]);
  const activatedPortfolio = activationFoundation.activation.status === "Activated" ? activationFoundation.activationSummary : null;
  const normalizedPortalSearch = portalSearch.trim().toLowerCase();
  const visibleAccounts = activatedPortfolio ? workspace.accounts.filter((account) => {
    const matchesSearch = normalizedPortalSearch.length === 0
      || [account.customerName, account.customerNumber, account.agingSummary, account.recoveryStatus].some((value) => value.toLowerCase().includes(normalizedPortalSearch));
    const matchesStatus = accountStatusFilter === "All Statuses" || account.recoveryStatus === accountStatusFilter;
    return matchesSearch && matchesStatus;
  }) : [];
  const deliveredLetters = activatedPortfolio ? workspace.collectionLetters.filter((letter) => ["Sent", "Delivered", "Viewed", "Downloaded"].includes(letter.status)).length : 0;
  const deliveredStatements = activatedPortfolio ? workspace.statements.filter((statement) => ["Sent", "Delivered", "Viewed", "Opened", "Downloaded"].includes(statement.status)).length : 0;
  const paymentsPosted = activatedPortfolio ? workspace.activity.filter((activity) => activity.kind === "Payment").length : 0;
  const outstandingBalanceCents = visibleAccounts.reduce((sum, account) => sum + account.openBalanceCents, 0);
  const promiseSummary = activatedPortfolio ? workspace.promises.reduce<Record<string, number>>((summary, promise) => {
    summary[promise.status] = (summary[promise.status] || 0) + 1;
    return summary;
  }, {}) : {};
  const recoveryProgress = activatedPortfolio
    ? Math.round((workspace.accounts.filter((account) => account.recoveryStatus === "Resolved" || account.recoveryStatus === "Payment Plan").length / Math.max(workspace.accounts.length, 1)) * 100)
    : 0;
  const clientSafeTimeline = activatedPortfolio ? [
    {
      id: "portfolio-activated",
      kind: "Portfolio",
      title: "Portfolio Activated",
      detail: "Portfolio is active and visible in the client portal.",
      createdAt: activationFoundation.activationSummary.activationTime || activationFoundation.activation.requestedAt,
    },
    ...workspace.collectionLetters
      .filter((letter) => ["Generated", "Sent", "Delivered", "Viewed", "Downloaded"].includes(letter.status))
      .map((letter) => ({
        id: `letter-${letter.id}`,
        kind: "Letter",
        title: "Letter Generated",
        detail: `${letter.template} for ${letter.customerName}.`,
        createdAt: letter.sentAt || letter.updatedAt || letter.createdAt,
      })),
    ...workspace.statements
      .filter((statement) => ["Generated", "Sent", "Delivered", "Viewed", "Opened", "Downloaded"].includes(statement.status))
      .map((statement) => ({
        id: `statement-${statement.id}`,
        kind: "Statement",
        title: "Statement Generated",
        detail: `${statement.statementNumber} for ${statement.customerName}.`,
        createdAt: statement.sentAt || statement.updatedAt || statement.createdAt,
      })),
    ...workspace.activity
      .filter((activity) => ["Payment", "Promise"].includes(activity.kind))
      .map((activity) => ({
        id: `activity-${activity.id}`,
        kind: activity.kind,
        title: activity.kind === "Payment" ? "Payment Posted" : "Promise Status",
        detail: `Client-visible ${activity.kind.toLowerCase()} update for ${activity.customerName}.`,
        createdAt: activity.createdAt,
      })),
  ].filter((entry) => clientPortalDateMatchesRange(entry.createdAt, dateRangeFilter)).sort((a, b) => getDateTime(b.createdAt) - getDateTime(a.createdAt)).slice(0, 8) : [];
  const portfolioOptions = ["All Portfolios", activatedPortfolio?.portfolio || "Activated Portfolio"].filter(Boolean);
  const accountStatusOptions = ["All Statuses", ...Array.from(new Set(workspace.accounts.map((account) => account.recoveryStatus)))];
  const portfolioVisible = Boolean(activatedPortfolio) && (portfolioFilter === "All Portfolios" || portfolioFilter === activatedPortfolio?.portfolio);
  const visibleAccountCount = portfolioVisible ? visibleAccounts.length : 0;

  return (
    <section className="space-y-6">
      <SectionHeading
        eyebrow="Client Portal"
        title="Operational visibility for activated portfolios"
        description="Clients can view activated portfolio status, account summaries, document delivery, payments, promises, and safe activity without seeing internal AR operations."
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <DetailMetric label="Assigned Portfolios" value={activatedPortfolio ? "1 Active" : "0 Active"} note="Only activated portfolios are visible." icon={<FolderKanban className="size-5" aria-hidden="true" />} />
        <DetailMetric label="Assigned Accounts" value={`${visibleAccountCount} accounts`} note="Client-scoped account summaries." icon={<Users className="size-5" aria-hidden="true" />} />
        <DetailMetric label="Outstanding Balance" value={formatARRecoveryCurrency(outstandingBalanceCents)} note="Visible open balance across shown accounts." icon={<CircleDollarSign className="size-5" aria-hidden="true" />} />
        <DetailMetric label="Recovery Progress" value={`${recoveryProgress}%`} note="Resolved or payment-plan accounts." icon={<Activity className="size-5" aria-hidden="true" />} />
      </div>

      <article className="rounded-[1.5rem] border border-[#BFEAFF] bg-white p-5 shadow-[0_20px_48px_rgba(15,23,42,.06)]">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-[#BFEAFF] pb-4">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">Client Dashboard</p>
            <h2 className="mt-1 font-display text-2xl font-bold text-[#0A2540]">Activated portfolio visibility</h2>
            <p className="mt-2 max-w-3xl text-sm leading-7 text-[#475569]">Draft, uploaded, validating, mapping, review, import, and assignment-pending portfolios remain internal.</p>
          </div>
          <ClientPortalVisibilityBadge label={activatedPortfolio ? "Activated" : "No Activated Portfolio"} />
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          <ClientPortalSearchField value={portalSearch} onChange={setPortalSearch} />
          <ClientPortalSelect label="Portfolio" value={portfolioFilter} options={portfolioOptions} onChange={setPortfolioFilter} />
          <ClientPortalSelect label="Status" value={accountStatusFilter} options={accountStatusOptions} onChange={setAccountStatusFilter} />
          <ClientPortalSelect label="Date Range" value={dateRangeFilter} options={["All Dates", "Last 7 Days", "Last 30 Days", "This Quarter"]} onChange={setDateRangeFilter} />
          <ClientPortalSelect label="Search Scope" value="Client Scope Only" options={["Client Scope Only"]} onChange={() => undefined} />
        </div>

        {activatedPortfolio && portfolioVisible ? (
          <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,0.36fr)_minmax(0,0.64fr)]">
            <div className="min-w-0 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
              <div className="rounded-2xl border border-[#14B8A6] bg-white p-4 shadow-[0_12px_28px_rgba(20,184,166,.12)]">
                <p className="text-[11px] font-black uppercase tracking-[0.14em] text-[#14B8A6]">{activatedPortfolio.client}</p>
                <h3 className="mt-1 break-words font-display text-xl font-bold text-[#0A2540]">{activatedPortfolio.portfolio}</h3>
                <div className="mt-4 grid gap-3">
                  <ScopeRow label="Portfolio Status" value={activatedPortfolio.activationStatus} />
                  <ScopeRow label="Portfolio Health" value={activationFoundation.activationSummary.blockedRecordCount === 0 ? "Healthy" : "Needs Review"} />
                  <ScopeRow label="Visible Records" value={`${activatedPortfolio.eligibleRecordCount} eligible records`} />
                  <ScopeRow label="Activation Time" value={activatedPortfolio.activationTime ? formatDate(activatedPortfolio.activationTime) : "Active"} />
                </div>
              </div>
            </div>
            <div className="min-w-0 rounded-2xl border border-[#BFEAFF] bg-white p-4">
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                <DetailMetric label="Letters Sent" value={deliveredLetters.toString()} note="Client-visible letter delivery." />
                <DetailMetric label="Statements Sent" value={deliveredStatements.toString()} note="Client-visible statement delivery." />
                <DetailMetric label="Payments Posted" value={paymentsPosted.toString()} note="Posted payment updates." />
                <DetailMetric label="Open Promises" value={(promiseSummary.Open || promiseSummary.Pending || 0).toString()} note="Visible promise status count." />
                <DetailMetric label="Portfolio Health" value={activationFoundation.activationSummary.blockedRecordCount === 0 ? "Healthy" : "Needs Review"} note="Client-safe operational summary." />
                <DetailMetric label="Recent Activity" value={`${clientSafeTimeline.length} events`} note="Internal notes are hidden." />
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-5 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-5">
            <p className="font-display text-xl font-bold text-[#0A2540]">No activated portfolios are visible.</p>
            <p className="mt-2 text-sm leading-7 text-[#475569]">Client portal visibility begins only after the Client Exchange activation gate completes.</p>
          </div>
        )}
      </article>

      <div className="grid gap-4 xl:grid-cols-[1fr_.95fr]">
        <article className="rounded-[1.5rem] border border-[#BFEAFF] bg-white p-5 shadow-[0_20px_48px_rgba(15,23,42,.06)]">
          <SectionHeading
            eyebrow="Assigned Accounts"
            title="Client-safe account summaries"
            description="Accounts show customer, status, aging, invoice count, and balance only. Collector ownership, manager notes, internal comments, and workflow rules remain hidden."
          />
          <div className="mt-4 overflow-x-auto overscroll-x-contain">
            <table className="w-full min-w-[42rem] border-separate border-spacing-y-2 text-left text-sm">
              <thead>
                <tr className="text-xs font-black uppercase tracking-[0.12em] text-[#475569]">
                  <th className="px-3 py-2">Account</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Aging</th>
                  <th className="px-3 py-2">Invoices</th>
                  <th className="px-3 py-2">Open Balance</th>
                </tr>
              </thead>
              <tbody>
                {visibleAccounts.map((account) => (
                  <tr key={account.id} className="rounded-2xl bg-[#F4FBFF]">
                    <td className="rounded-l-2xl border-y border-l border-[#BFEAFF] px-3 py-3">
                      <p className="font-bold text-[#0A2540]">{account.customerName}</p>
                      <p className="text-xs font-semibold text-[#475569]">{account.customerNumber}</p>
                    </td>
                    <td className="border-y border-[#BFEAFF] px-3 py-3"><ClientPortalVisibilityBadge label={account.recoveryStatus} /></td>
                    <td className="border-y border-[#BFEAFF] px-3 py-3 font-semibold text-[#0A2540]">{account.agingSummary}</td>
                    <td className="border-y border-[#BFEAFF] px-3 py-3 font-semibold text-[#0A2540]">{account.openInvoiceCount}</td>
                    <td className="rounded-r-2xl border-y border-r border-[#BFEAFF] px-3 py-3 font-bold text-[#0A2540]">{formatARRecoveryCurrency(account.openBalanceCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {visibleAccounts.length === 0 ? (
              <div className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4 text-sm font-semibold text-[#0A2540]">No client-visible accounts match the current filters.</div>
            ) : null}
          </div>
        </article>

        <article className="rounded-[1.5rem] border border-[#BFEAFF] bg-white p-5 shadow-[0_20px_48px_rgba(15,23,42,.06)]">
          <SectionHeading
            eyebrow="Security Boundary"
            title="Client-safe access rules"
            description="Portal visibility is limited to activated portfolios and client-visible projections. Internal operations remain available only to AR workspaces."
          />
          <div className="mt-4 grid gap-3">
            <ScopeRow label="Company" value={workspace.company.companyName} />
            <ScopeRow label="Portal URL" value={workspace.clientProfile.portalUrl || "Not captured"} />
            <ScopeRow label="Letter Defaults" value={`${safeLetterDefaults.businessName} / ${safeLetterDefaults.contactName}`} />
            <ScopeRow label="Draft Portfolios" value="Hidden" />
            <ScopeRow label="Internal Notes" value="Hidden" />
            <ScopeRow label="Collector / Manager Details" value="Hidden" />
            <ScopeRow label="Cross-Company Access" value="Blocked" />
          </div>
          <div className="mt-4 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4 text-sm font-semibold leading-7 text-[#0A2540]">
            Manager Command continues to consume the complete operational event stream. The Client Portal consumes only approved client-visible projections.
          </div>
        </article>
      </div>

      <div className="grid gap-4 xl:grid-cols-[.95fr_1fr]">
        <article className="rounded-[1.5rem] border border-[#BFEAFF] bg-white p-5 shadow-[0_20px_48px_rgba(15,23,42,.06)]">
          <SectionHeading
            eyebrow="Recovery Visibility"
            title="Documents, payments, and promise status"
            description="Client-visible recovery progress is summarized without exposing assignment history or internal workflow rules."
          />
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <ActionPill label="Letters Sent" note={`${deliveredLetters} letters visible to the client.`} icon={<FileText className="size-5" aria-hidden="true" />} />
            <ActionPill label="Statements Sent" note={`${deliveredStatements} statements visible to the client.`} icon={<FileText className="size-5" aria-hidden="true" />} />
            <ActionPill label="Payments Posted" note={`${paymentsPosted} payment updates visible.`} icon={<CircleDollarSign className="size-5" aria-hidden="true" />} />
            <ActionPill label="Promise Status" note={Object.entries(promiseSummary).map(([status, count]) => `${status}: ${count}`).join(" | ") || "No visible promises yet."} icon={<Target className="size-5" aria-hidden="true" />} />
          </div>
        </article>

        <article className="rounded-[1.5rem] border border-[#BFEAFF] bg-white p-5 shadow-[0_20px_48px_rgba(15,23,42,.06)]">
          <SectionHeading
            eyebrow="Timeline"
            title="Client-safe recent activity"
            description="The certified timeline is reused with internal event details filtered out before client display."
          />
          <div className="mt-4 grid gap-3">
            {clientSafeTimeline.map((entry) => (
              <div key={entry.id} className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="break-words font-display text-lg font-bold text-[#0A2540]">{entry.title}</p>
                    <p className="mt-1 text-sm leading-6 text-[#475569]">{entry.detail}</p>
                  </div>
                  <ClientPortalVisibilityBadge label={entry.kind} />
                </div>
                <p className="mt-3 text-xs font-semibold text-[#475569]">{formatDate(entry.createdAt)}</p>
              </div>
            ))}
            {clientSafeTimeline.length === 0 ? (
              <div className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4 text-sm font-semibold text-[#0A2540]">No client-visible activity is available yet.</div>
            ) : null}
          </div>
        </article>
      </div>
    </section>
  );
}

function ClientPortalSearchField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <label className="min-w-0 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-3">
      <span className="text-xs font-semibold text-[#334155]">Search</span>
      <div className="mt-2 flex min-w-0 items-center gap-2 rounded-xl border border-[#BFEAFF] bg-white px-3 py-2">
        <Search className="size-4 shrink-0 text-[#14B8A6]" aria-hidden="true" />
        <input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="Portfolio, account, status"
          className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-[#0A2540] outline-none placeholder:text-[#94A3B8]"
        />
      </div>
    </label>
  );
}

function ClientPortalSelect({ label, value, options, onChange }: { label: string; value: string; options: string[]; onChange: (value: string) => void }) {
  const uniqueOptions = Array.from(new Set(options));
  return (
    <label className="min-w-0 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-3">
      <span className="text-xs font-semibold text-[#334155]">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-2 w-full min-w-0 rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold text-[#0A2540] outline-none focus:border-[#14B8A6]"
      >
        {uniqueOptions.map((option) => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
    </label>
  );
}

function ClientPortalVisibilityBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex max-w-full items-center rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-[#0F766E]">
      <span className="min-w-0 break-words">{label}</span>
    </span>
  );
}

function clientPortalDateMatchesRange(value: string, range: string) {
  if (range === "All Dates") return true;
  const timestamp = getDateTime(value);
  if (!Number.isFinite(timestamp)) return true;
  const now = Date.now();
  if (range === "Last 7 Days") return now - timestamp <= 7 * 24 * 60 * 60 * 1000;
  if (range === "Last 30 Days") return now - timestamp <= 30 * 24 * 60 * 60 * 1000;
  if (range === "This Quarter") {
    const date = new Date(now);
    const quarterStartMonth = Math.floor(date.getMonth() / 3) * 3;
    const quarterStart = new Date(date.getFullYear(), quarterStartMonth, 1).getTime();
    return timestamp >= quarterStart;
  }
  return true;
}

function CommercializationSection({
  workspace,
}: {
  workspace: ReturnType<typeof loadARRecoveryWorkspace>;
}) {
  const [selectedTab, setSelectedTab] = useState<ARRecoveryCommercializationTabKey>("overview");
  const snapshot = useMemo(
    () => buildARRecoveryCommercializationSnapshot(workspace.company.id, selectedTab),
    [selectedTab, workspace.company.id],
  );

  return (
    <div className="ar-workspace-os mt-4 grid gap-4">
      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {snapshot.readinessCards.map((card) => (
          <article key={card.label} className="rounded-[1.75rem] border border-[rgba(255,106,43,.16)] bg-[rgba(7,18,31,.76)] p-5 shadow-[0_20px_48px_rgba(0,0,0,.18)]">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF6A2B]">{card.label}</p>
            <p className="mt-2 font-display text-3xl font-bold text-[#F6F1E8]">{card.value}</p>
            <p className="mt-3 text-sm font-semibold leading-7 text-[#E8EEF4]">{card.note}</p>
          </article>
        ))}
      </section>

      <section className="rounded-[1.75rem] border border-[rgba(34,224,208,.16)] bg-[rgba(7,18,31,.78)] p-5 shadow-[0_20px_48px_rgba(0,0,0,.18)]">
        <SectionHeading
          eyebrow="Business Platform"
          title="Business Platform"
          description="This workspace consolidates client onboarding, white-label deployment, packaging, support, documentation, training, and automation readiness."
        />
        <div className="mt-5 flex flex-wrap gap-2">
          {snapshot.tabLabels.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setSelectedTab(tab.key)}
              className={`rounded-full border px-4 py-2 text-xs font-black uppercase tracking-[0.14em] transition ${
                selectedTab === tab.key
                  ? "border-[rgba(255,106,43,.46)] bg-[rgba(255,106,43,.14)] text-[#F6F1E8]"
                  : "border-[rgba(34,224,208,.2)] bg-[rgba(255,255,255,.04)] text-[#CBD5E1] hover:border-[rgba(34,224,208,.34)]"
              }`}
              title={tab.description}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="mt-6 grid gap-6">
          {renderCommercializationTab(snapshot, selectedTab)}
        </div>
      </section>
    </div>
  );
}

function renderCommercializationTab(
  snapshot: ReturnType<typeof buildARRecoveryCommercializationSnapshot>,
  selectedTab: ARRecoveryCommercializationTabKey,
) {
  switch (selectedTab) {
    case "overview":
      return (
        <>
          <section className="grid gap-6 xl:grid-cols-[1fr_.92fr]">
            <article className="rounded-[1.75rem] border border-[rgba(255,106,43,.16)] bg-[rgba(255,255,255,.03)] p-5">
              <SectionHeading eyebrow="Readiness Score" title={`${snapshot.readinessScore}% commercial readiness`} description="A practical readiness proxy based on live recovery records, workflows, and deployment scaffolding." />
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {snapshot.targetIndustries.map((industry) => (
                  <div key={industry.label} className="rounded-2xl border border-[rgba(34,224,208,.14)] bg-[rgba(7,18,31,.72)] p-4">
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-[#22E0D0]">{industry.label}</p>
                    <p className="mt-2 text-sm font-semibold leading-7 text-[#E8EEF4]">{industry.reason}</p>
                  </div>
                ))}
              </div>
            </article>

            <article className="rounded-[1.75rem] border border-[rgba(255,106,43,.16)] bg-[rgba(255,255,255,.03)] p-5">
              <SectionHeading eyebrow="Launch Checklist" title="Business platform gate" description="This list captures the launch-readiness items we can verify from the current codebase." />
              <div className="mt-5 grid gap-3">
                {snapshot.launchChecklist.map((item) => (
                  <div key={item.label} className="rounded-2xl border border-[rgba(34,224,208,.14)] bg-[rgba(7,18,31,.72)] p-4">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">{item.label}</p>
                      <span className={`rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] ${item.completed ? "border-[rgba(34,224,208,.24)] text-[#22E0D0]" : "border-[rgba(255,107,53,.24)] text-[#FF6B35]"}`}>
                        {item.completed ? "Complete" : "Needs Review"}
                      </span>
                    </div>
                    <p className="mt-2 text-sm font-semibold leading-7 text-[#E8EEF4]">{item.note}</p>
                  </div>
                ))}
              </div>
            </article>
          </section>
        </>
      );
    case "onboarding":
      return (
        <div className="grid gap-6 xl:grid-cols-2">
          {snapshot.launchSequence.map((sequence) => (
            <article key={sequence.title} className="rounded-[1.75rem] border border-[rgba(255,106,43,.16)] bg-[rgba(255,255,255,.03)] p-5">
              <SectionHeading eyebrow="Onboarding" title={sequence.title} description={sequence.detail} />
              <div className="mt-5 grid gap-3">
                {sequence.steps.map((step, index) => (
                  <div key={step} className="flex items-center justify-between gap-4 rounded-2xl border border-[rgba(34,224,208,.14)] bg-[rgba(7,18,31,.72)] px-4 py-3">
                    <div className="flex items-center gap-3">
                      <span className="grid size-8 place-items-center rounded-full border border-[rgba(255,106,43,.2)] text-xs font-black text-[#FF6A2B]">{index + 1}</span>
                      <span className="text-sm font-semibold text-[#F6F1E8]">{step}</span>
                    </div>
                    <span className="rounded-full border border-[rgba(34,224,208,.2)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#22E0D0]">Ready</span>
                  </div>
                ))}
              </div>
            </article>
          ))}
        </div>
      );
    case "deployment":
      return (
        <section className="grid gap-6 xl:grid-cols-[1fr_.94fr]">
          <article className="rounded-[1.75rem] border border-[rgba(255,106,43,.16)] bg-[rgba(255,255,255,.03)] p-5">
            <SectionHeading eyebrow="White-Label Deployment" title="Branding and domain controls" description="Tenant branding and domain settings are ready to be mapped per company." />
            <div className="mt-5 grid gap-3">
              {snapshot.deploymentControls.map((control) => (
                <div key={control.title} className="rounded-2xl border border-[rgba(34,224,208,.14)] bg-[rgba(7,18,31,.72)] p-4">
                  <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">{control.title}</p>
                  <p className="mt-2 font-display text-2xl font-bold text-[#F6F1E8]">{control.value}</p>
                  <p className="mt-2 text-sm font-semibold leading-7 text-[#E8EEF4]">{control.note}</p>
                </div>
              ))}
            </div>
          </article>
          <article className="rounded-[1.75rem] border border-[rgba(255,106,43,.16)] bg-[rgba(255,255,255,.03)] p-5">
            <SectionHeading eyebrow="Portal Branding" title="Communication assets" description="Email, letter, statement, and notification branding can all inherit the same tenant layer." />
            <div className="mt-5 grid gap-3">
              <ScopeRow label="Email Signature" value={snapshot.workspace.clientProfile.emailSignature} />
              <ScopeRow label="Letterhead" value={snapshot.workspace.clientProfile.letterhead} />
              <ScopeRow label="Footer" value={snapshot.workspace.clientProfile.footerContent} />
              <ScopeRow label="Communication Preference" value={snapshot.workspace.clientProfile.communicationPreference} />
            </div>
          </article>
        </section>
      );
    case "packages":
      return (
        <section className="grid gap-6 xl:grid-cols-3">
          {snapshot.packageCatalog.map((pkg) => (
            <article key={pkg.name} className="rounded-[1.75rem] border border-[rgba(255,106,43,.16)] bg-[rgba(255,255,255,.03)] p-5">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">{pkg.category}</p>
                <span className={`rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] ${pkg.status === "Active" ? "border-[rgba(34,224,208,.24)] text-[#22E0D0]" : "border-[rgba(255,106,43,.2)] text-[#FF6A2B]"}`}>{pkg.status}</span>
              </div>
              <h3 className="mt-3 font-display text-3xl font-bold text-[#F6F1E8]">{pkg.name}</h3>
              <p className="mt-2 text-lg font-extrabold text-[#22E0D0]">{pkg.pricing}</p>
              <p className="mt-2 text-sm font-semibold leading-7 text-[#E8EEF4]">{pkg.limits}</p>
              <div className="mt-4 grid gap-2">
                {pkg.features.map((feature) => (
                  <div key={feature} className="rounded-2xl border border-[rgba(34,224,208,.14)] bg-[rgba(7,18,31,.72)] px-3 py-2 text-sm font-semibold text-[#F6F1E8]">{feature}</div>
                ))}
              </div>
              <p className="mt-4 text-sm font-semibold uppercase tracking-[0.14em] text-[#CBD5E1]">Active clients: {pkg.activeClients}</p>
            </article>
          ))}
        </section>
      );
    case "success":
      return (
        <section className="grid gap-6 xl:grid-cols-2">
          <article className="rounded-[1.75rem] border border-[rgba(255,106,43,.16)] bg-[rgba(255,255,255,.03)] p-5">
            <SectionHeading eyebrow="Client Success" title="Health and renewal view" description="Client health, go-live, and renewal signals are represented from the current tenant model." />
            <div className="mt-5 grid gap-3">
              {snapshot.clientSuccessMetrics.map((metric) => (
                <div key={metric.label} className="rounded-2xl border border-[rgba(34,224,208,.14)] bg-[rgba(7,18,31,.72)] p-4">
                  <p className="text-xs font-black uppercase tracking-[0.16em] text-[#22E0D0]">{metric.label}</p>
                  <p className="mt-2 font-display text-3xl font-bold text-[#F6F1E8]">{metric.value}</p>
                  <p className="mt-2 text-sm font-semibold leading-7 text-[#E8EEF4]">{metric.note}</p>
                </div>
              ))}
            </div>
          </article>
          <article className="rounded-[1.75rem] border border-[rgba(255,106,43,.16)] bg-[rgba(255,255,255,.03)] p-5">
            <SectionHeading eyebrow="Onboarding Outcomes" title="Operational success targets" description="The success center keeps onboarding, adoption, and renewal milestones visible." />
            <div className="mt-5 grid gap-3">
              <ScopeRow label="Support Requests" value={`${snapshot.clientSuccessMetrics.find((metric) => metric.label === "Support Requests")?.value || 0}`} />
              <ScopeRow label="Health Score" value={`${snapshot.clientSuccessMetrics.find((metric) => metric.label === "Health Score")?.value || "0%"}`} />
              <ScopeRow label="Renewal Date" value={`${snapshot.clientSuccessMetrics.find((metric) => metric.label === "Renewal Date")?.value || ""}`} />
              <ScopeRow label="Launch Status" value="Ready" />
            </div>
          </article>
        </section>
      );
    case "support":
      return (
        <section className="grid gap-6 xl:grid-cols-2">
          <article className="rounded-[1.75rem] border border-[rgba(255,106,43,.16)] bg-[rgba(255,255,255,.03)] p-5">
            <SectionHeading eyebrow="Support Center" title="Issues and escalations" description="Support records link directly to clients, tenants, and operational workflows." />
            <div className="mt-5 grid gap-3">
              {snapshot.supportQueue.map((item) => (
                <div key={item.title} className="rounded-2xl border border-[rgba(34,224,208,.14)] bg-[rgba(7,18,31,.72)] p-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">{item.title}</p>
                    <span className="rounded-full border border-[rgba(255,106,43,.2)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#22E0D0]">{item.severity}</span>
                  </div>
                  <p className="mt-2 text-sm font-semibold leading-7 text-[#E8EEF4]">{item.detail}</p>
                  <p className="mt-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#CBD5E1]">{item.linkedTo}</p>
                </div>
              ))}
            </div>
          </article>
          <article className="rounded-[1.75rem] border border-[rgba(255,106,43,.16)] bg-[rgba(255,255,255,.03)] p-5">
            <SectionHeading eyebrow="Support Links" title="Record relationships" description="Every support record can be tied back to a client, tenant, user, or account." />
            <div className="mt-5 grid gap-3">
              <ScopeRow label="Client" value={snapshot.workspace.clientProfile.businessName} />
              <ScopeRow label="Tenant" value={snapshot.workspace.company.companyName} />
              <ScopeRow label="User" value={snapshot.workspace.clientProfile.collectionDepartmentContact} />
              <ScopeRow label="Account" value={snapshot.workspace.accounts[0]?.customerName || "No account"} />
            </div>
          </article>
        </section>
      );
    case "documentation":
      return (
        <section className="grid gap-6 xl:grid-cols-2">
          <article className="rounded-[1.75rem] border border-[rgba(255,106,43,.16)] bg-[rgba(255,255,255,.03)] p-5">
            <SectionHeading eyebrow="Documentation Center" title="Knowledge base library" description="Guides and training content can ship with the platform from day one." />
            <div className="mt-5 grid gap-3">
              {snapshot.documentationLibrary.map((doc) => (
                <div key={doc.title} className="rounded-2xl border border-[rgba(34,224,208,.14)] bg-[rgba(7,18,31,.72)] p-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">{doc.title}</p>
                    <span className="rounded-full border border-[rgba(255,106,43,.2)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#22E0D0]">{doc.status}</span>
                  </div>
                  <p className="mt-2 text-sm font-semibold leading-7 text-[#E8EEF4]">{doc.description}</p>
                  <p className="mt-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#CBD5E1]">{doc.category}</p>
                </div>
              ))}
            </div>
          </article>
          <article className="rounded-[1.75rem] border border-[rgba(255,106,43,.16)] bg-[rgba(255,255,255,.03)] p-5">
            <SectionHeading eyebrow="Library Coverage" title="Customer-ready materials" description="User, client, admin, and white-label content can all live in the same documentation center." />
            <div className="mt-5 grid gap-3">
              <ScopeRow label="User Guides" value="Ready" />
              <ScopeRow label="Client Guides" value="Ready" />
              <ScopeRow label="Admin Guides" value="Ready" />
              <ScopeRow label="White Label Guides" value="Ready" />
              <ScopeRow label="FAQ Library" value="Ready" />
              <ScopeRow label="Knowledge Base" value="Ready" />
            </div>
          </article>
        </section>
      );
    case "training":
      return (
        <section className="grid gap-6 xl:grid-cols-2">
          <article className="rounded-[1.75rem] border border-[rgba(255,106,43,.16)] bg-[rgba(255,255,255,.03)] p-5">
            <SectionHeading eyebrow="Training Center" title="Training tracks" description="Collector, manager, client, and software user training can be tracked centrally." />
            <div className="mt-5 grid gap-3">
              {snapshot.trainingPrograms.map((program) => (
                <div key={program.title} className="rounded-2xl border border-[rgba(34,224,208,.14)] bg-[rgba(7,18,31,.72)] p-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">{program.title}</p>
                    <span className="rounded-full border border-[rgba(255,106,43,.2)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#22E0D0]">{program.certification}</span>
                  </div>
                  <p className="mt-2 text-sm font-semibold leading-7 text-[#E8EEF4]">{program.audience}</p>
                  <p className="mt-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#CBD5E1]">Completion {program.completion}</p>
                </div>
              ))}
            </div>
          </article>
          <article className="rounded-[1.75rem] border border-[rgba(255,106,43,.16)] bg-[rgba(255,255,255,.03)] p-5">
            <SectionHeading eyebrow="Certification" title="Training completion" description="Training completion and certification status can be used for onboarding gates later." />
            <div className="mt-5 grid gap-3">
              <ScopeRow label="Collector Training" value="Ready" />
              <ScopeRow label="Manager Training" value="Ready" />
              <ScopeRow label="Client Training" value="Ready" />
              <ScopeRow label="Software User Training" value="Ready" />
            </div>
          </article>
        </section>
      );
    case "automation":
      return (
        <section className="grid gap-6 xl:grid-cols-2">
          <article className="rounded-[1.75rem] border border-[rgba(255,106,43,.16)] bg-[rgba(255,255,255,.03)] p-5">
            <SectionHeading eyebrow="Automation Audit" title="Workflow trigger readiness" description="The automation framework is reusable across tenants, with one item left for hardening." />
            <div className="mt-5 grid gap-3">
              {snapshot.automationAudit.map((item) => (
                <div key={item.trigger} className="rounded-2xl border border-[rgba(34,224,208,.14)] bg-[rgba(7,18,31,.72)] p-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">{item.trigger}</p>
                    <span className={`rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] ${item.status === "Ready" || item.status === "Complete" ? "border-[rgba(34,224,208,.24)] text-[#22E0D0]" : "border-[rgba(255,107,53,.24)] text-[#FF6B35]"}`}>{item.status}</span>
                  </div>
                  <p className="mt-2 text-sm font-semibold leading-7 text-[#E8EEF4]">{item.detail}</p>
                </div>
              ))}
            </div>
          </article>
          <article className="rounded-[1.75rem] border border-[rgba(255,106,43,.16)] bg-[rgba(255,255,255,.03)] p-5">
            <SectionHeading eyebrow="Audit Summary" title="Ready for production hooks" description="The only remaining hardening item is aging-threshold automation, which can be layered without changing the current architecture." />
            <div className="mt-5 grid gap-3">
              <ScopeRow label="New Client" value="Ready" />
              <ScopeRow label="New User" value="Ready" />
              <ScopeRow label="New Account" value="Ready" />
              <ScopeRow label="New Promise" value="Ready" />
              <ScopeRow label="Broken Promise" value="Ready" />
              <ScopeRow label="Escalation" value="Ready" />
              <ScopeRow label="Letter Sent" value="Ready" />
              <ScopeRow label="Statement Sent" value="Ready" />
              <ScopeRow label="Tickler Created" value="Ready" />
              <ScopeRow label="Aging Threshold Reached" value="Needs Review" />
            </div>
          </article>
        </section>
      );
    case "readiness":
      return (
        <section className="grid gap-6 xl:grid-cols-2">
          <article className="rounded-[1.75rem] border border-[rgba(255,106,43,.16)] bg-[rgba(255,255,255,.03)] p-5">
            <SectionHeading eyebrow="Readiness Review" title="Go-to-market summary" description="This is the practical launch stance for services, software licensing, and white-label deployment." />
            <div className="mt-5 grid gap-3">
              <ScopeRow label="Launch Sequence" value="Mapped" />
              <ScopeRow label="Commercial Readiness" value={`${snapshot.readinessScore}%`} />
              <ScopeRow label="Revenue Readiness" value="Ready" />
              <ScopeRow label="SaaS Readiness" value="Ready" />
              <ScopeRow label="Business Platform" value="Ready" />
            </div>
          </article>
          <article className="rounded-[1.75rem] border border-[rgba(255,106,43,.16)] bg-[rgba(255,255,255,.03)] p-5">
            <SectionHeading eyebrow="Recommended Launch" title="Sequence and target industries" description="Recommended launch order and initial verticals for the business platform." />
            <div className="mt-5 grid gap-3">
              {snapshot.targetIndustries.map((industry) => (
                <div key={industry.label} className="rounded-2xl border border-[rgba(34,224,208,.14)] bg-[rgba(7,18,31,.72)] p-4">
                  <p className="text-xs font-black uppercase tracking-[0.16em] text-[#22E0D0]">{industry.label}</p>
                  <p className="mt-2 text-sm font-semibold leading-7 text-[#E8EEF4]">{industry.reason}</p>
                </div>
              ))}
            </div>
          </article>
        </section>
      );
    default:
      return null;
  }
}

type TicklerWorkspaceTabKey = "details" | "activity" | "history";

type TicklerQuickActionState = {
  label: string;
  title: string;
  description: string;
  status: "Ready" | "Coming Soon";
  primaryActionLabel: string;
  details: string[];
  routeHref?: string;
};

const TICKLER_CENTER_ENTERPRISE_FIELDS = ["Tickler ID", "Customer", "Account", "Invoice(s)", "Portfolio", "Client", "Collector", "Priority", "Due Date", "Due Time", "Status", "Reminder Type", "Assigned User", "Related Promise", "Related Payment", "Related Letter", "Related Statement", "Related Recovery Activity", "Notes", "Created Date", "Created By", "Last Updated"] as const;
const TICKLER_CENTER_LIFECYCLE_STATUSES = ["Pending", "Scheduled", "Due Today", "Upcoming", "Overdue", "Completed", "Cancelled", "Escalated", "Deferred", "Recurring", "Missed", "Archived"] as const;
const TICKLER_CENTER_FUTURE_OPERATIONS = ["Create Tickler", "Edit Tickler", "Complete Tickler", "Reschedule", "Assign Collector", "Reassign", "Cancel", "Escalate", "Add Notes", "Open Customer", "Open Account", "Open Promise", "Open Recovery Activity"] as const;
const TICKLER_CENTER_FILTER_MODEL = ["Collector", "Client", "Portfolio", "Priority", "Status", "Due Today", "Overdue", "Upcoming", "Date Range", "Reminder Type", "Customer", "Account", "Search"] as const;
const TICKLER_CENTER_REPORTING_MODEL = ["Total Ticklers", "Completed Ticklers", "Overdue Ticklers", "Due Today", "Completion Rate", "Average Completion Time", "Collector Performance", "Client Performance", "Portfolio Performance", "Escalated Ticklers"] as const;
const TICKLER_CENTER_ACTIVITY_EVENTS = ["Created", "Updated", "Completed", "Rescheduled", "Cancelled", "Escalated", "Assigned", "Reassigned", "Deferred", "Notes Added", "Reminder Triggered"] as const;
const TICKLER_CENTER_WORKFLOW_INTEGRATIONS = ["Promise Center", "Recovery Workbench", "Letters", "Statements", "Payments", "Customer Accounts", "Client Management", "Recovery Activity", "Manager Command", "Timeline"] as const;

function SmartTicklerSection({
  workspace,
}: {
  workspace: ReturnType<typeof loadARRecoveryWorkspace>;
}) {
  const location = useLocation();
  const promiseReturnContext = useMemo(() => getPromiseCenterReturnContext(location.search), [location.search]);
  const [selectedTicklerId, setSelectedTicklerId] = useState(() =>
    workspace.ticklers.find((tickler) =>
      tickler.accountId === promiseReturnContext?.accountId ||
      getTicklerRelatedPromise(tickler) === promiseReturnContext?.promiseId
    )?.id || workspace.ticklers[0]?.id || "",
  );
  const [workspaceTab, setWorkspaceTab] = useState<TicklerWorkspaceTabKey>("details");
  const [activeQuickAction, setActiveQuickAction] = useState<TicklerQuickActionState | null>(null);
  const snapshot = useMemo(() => buildARRecoveryTicklerDashboardSnapshot(workspace.company.id), [workspace.company.id]);
  const actionQueue = [...snapshot.dueToday, ...snapshot.overdue, ...snapshot.critical, ...snapshot.upcoming].filter(
    (tickler, index, queue) => queue.findIndex((item) => item.id === tickler.id) === index,
  );
  const selectedTickler = actionQueue.find((tickler) => tickler.id === selectedTicklerId) || actionQueue[0] || workspace.ticklers[0];
  const selectedAccount = selectedTickler ? workspace.accounts.find((account) => account.id === selectedTickler.accountId) : null;
  const selectedPromise = selectedTickler ? workspace.promises.find((promise) => promise.accountId === selectedTickler.accountId) : null;
  const selectedActivity = selectedTickler
    ? workspace.activity.filter((entry) => entry.accountId === selectedTickler.accountId).slice(0, 8)
    : [];
  const summaryCards = [
    { label: "Due Today", value: snapshot.dueToday.length, note: "Tasks the team must clear today." },
    { label: "Overdue", value: snapshot.overdue.length, note: "Missed reminders needing attention." },
    { label: "Critical", value: snapshot.critical.length, note: "Highest-risk reminders." },
    { label: "Assigned To Me", value: snapshot.assignedToMe.length, note: "Primary collector queue." },
  ];
  const workspaceTabs: Array<{ key: TicklerWorkspaceTabKey; label: string; note: string }> = [
    { key: "details", label: "Details", note: "Selected tickler and account context." },
    { key: "activity", label: "Activity", note: "Recent activity tied to the selected account." },
    { key: "history", label: "History", note: "Operational history and completion context." },
  ];

  useEffect(() => {
    if (!selectedTicklerId && actionQueue[0]) {
      setSelectedTicklerId(actionQueue[0].id);
    }
  }, [actionQueue, selectedTicklerId]);

  useEffect(() => {
    if (!promiseReturnContext) return;
    const contextualTickler = actionQueue.find((tickler) =>
      tickler.accountId === promiseReturnContext.accountId ||
      getTicklerRelatedPromise(tickler) === promiseReturnContext.promiseId
    );
    if (contextualTickler) setSelectedTicklerId(contextualTickler.id);
  }, [actionQueue, promiseReturnContext?.accountId, promiseReturnContext?.promiseId]);

  useEffect(() => {
    setActiveQuickAction(null);
    setWorkspaceTab("details");
  }, [selectedTicklerId]);

  function openTicklerAction(label: string, description: string, status: TicklerQuickActionState["status"], route?: string, routeAction?: string) {
    if (!selectedTickler) return;
    if (label === "Escalate") {
      const now = new Date().toISOString();
      const escalationId = `escalation-${selectedTickler.id}-${Date.now()}`;
      publishEscalationWorkflowAction({
        tenantId: workspace.company.id,
        clientId: workspace.company.id,
        customerId: selectedAccount?.customerNumber || selectedTickler.accountId,
        accountId: selectedTickler.accountId,
        customerName: selectedTickler.customerName,
        customerNumber: selectedAccount?.customerNumber || selectedTickler.accountId,
        escalationId,
        escalationRecord: {
          id: escalationId,
          companyId: workspace.company.id,
          accountId: selectedTickler.accountId,
          customerName: selectedTickler.customerName,
          customerNumber: selectedAccount?.customerNumber || selectedTickler.accountId,
          status: "Created",
          priority: selectedTickler.priority || "High",
          reason: `${selectedTickler.ticklerType} escalated for manager review`,
          assignedTo: "Manager Command",
          createdAt: now,
          updatedAt: now,
          createdBy: selectedTickler.assignedCollector,
          actorName: selectedTickler.assignedCollector,
          sourceTrigger: "Overdue Tickler",
          sourceEventType: "TicklerEscalated",
          sourceRecordType: "tickler",
          sourceRecordId: selectedTickler.id,
          lifecycle: ["Created", "Assigned", "In Review", "Resolved", "Closed", "Reopened"],
        },
        action: "Escalation.Create",
        actor: {
          actorId: selectedTickler.assignedCollector,
          actorName: selectedTickler.assignedCollector,
          actorEmail: "",
          actorRole: "Collector",
          permissionLevel: "collector_write",
        },
        sourceWorkspace: "Tickler Center",
        initiatedFromRoute: "/admin/ar-recovery/smart-tickler",
      });
    }
    const routeHref = route
      ? `${route}?${new URLSearchParams({
          source: "smart-tickler",
          ticklerId: selectedTickler.id,
          accountId: selectedTickler.accountId,
          customer: selectedTickler.customerName,
          action: routeAction || label,
        }).toString()}`
      : undefined;
    setActiveQuickAction({
      label,
      title: `${label} Workflow`,
      description,
      status,
      primaryActionLabel: route ? `Open ${label}` : status === "Ready" ? "Workflow Open" : "Workflow Pending",
      routeHref,
      details: [
        `Customer: ${selectedTickler.customerName}`,
        `Tickler type: ${selectedTickler.ticklerType}`,
        `Due date: ${formatDate(selectedTickler.dueDate)}`,
        `Priority: ${selectedTickler.priority}`,
        `Assigned collector: ${selectedTickler.assignedCollector}`,
      ],
    });
  }

  return (
    <div
      className="ar-workspace-os mt-4 grid min-w-0 max-w-full gap-4 overflow-hidden"
      data-tickler-enterprise-fields={TICKLER_CENTER_ENTERPRISE_FIELDS.join(",")}
      data-tickler-lifecycle-statuses={TICKLER_CENTER_LIFECYCLE_STATUSES.join(",")}
      data-tickler-future-operations={TICKLER_CENTER_FUTURE_OPERATIONS.join(",")}
      data-tickler-filter-model={TICKLER_CENTER_FILTER_MODEL.join(",")}
      data-tickler-reporting-model={TICKLER_CENTER_REPORTING_MODEL.join(",")}
      data-tickler-activity-events={TICKLER_CENTER_ACTIVITY_EVENTS.join(",")}
      data-tickler-workflow-integrations={TICKLER_CENTER_WORKFLOW_INTEGRATIONS.join(",")}
    >
      <PromiseCenterReturnControl context={promiseReturnContext} />

      <section className="grid min-w-0 max-w-full gap-4 md:grid-cols-[repeat(2,minmax(0,1fr))] xl:grid-cols-[repeat(4,minmax(0,1fr))]">
        {summaryCards.map((card) => (
          <DetailMetric key={card.label} label={card.label} value={String(card.value)} note={card.note} icon={ticklerMetricIcon(card.label)} />
        ))}
      </section>

      <section className="min-w-0 max-w-full overflow-hidden rounded-[1.75rem] border border-[#BFEAFF] bg-white p-5 shadow-[0_18px_38px_rgba(10,37,64,.08)]">
        <SectionHeading
          eyebrow="Daily Action Queue"
          title="Follow-up work that must happen today"
          description="Select a tickler, review the required work, and complete the next collector action without configuration panels."
        />
        <div className="ar-ia-scroll mt-5 flex w-full max-w-full gap-3 overflow-x-auto overscroll-x-contain pb-2">
          {actionQueue.length ? actionQueue.map((tickler) => {
            const active = selectedTickler?.id === tickler.id;
            const account = workspace.accounts.find((record) => record.id === tickler.accountId);
            return (
              <button
                key={tickler.id}
                type="button"
                onClick={() => setSelectedTicklerId(tickler.id)}
                className={`min-w-[16rem] max-w-[calc(100vw-4rem)] shrink-0 overflow-hidden rounded-3xl border p-4 text-left transition hover:-translate-y-0.5 sm:min-w-[19rem] ${
                  active ? "border-[#14B8A6] bg-[#F4FBFF]" : "border-[#BFEAFF] bg-white hover:border-[#14B8A6] hover:bg-[#F4FBFF]"
                }`}
              >
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <ARLightLineIcon icon={ticklerMetricIcon(tickler.ticklerType)} />
                    <div className="min-w-0">
                      <p className="break-words text-xs font-semibold uppercase tracking-[0.14em] text-[#FF6A2B]">{tickler.ticklerType}</p>
                      <h3 className="mt-1 break-words text-base font-semibold text-[#0A2540]">{tickler.title}</h3>
                    </div>
                  </div>
                  <span className="shrink-0 rounded-full border border-[#BFEAFF] bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#14B8A6]">
                    {tickler.priority}
                  </span>
                </div>
                <div className="mt-3 grid min-w-0 gap-2 text-sm font-normal leading-6 text-[#475569]">
                  <p className="min-w-0 break-words"><span className="font-semibold text-[#0A2540]">Customer:</span> {tickler.customerName}</p>
                  <p className="min-w-0 break-words"><span className="font-semibold text-[#0A2540]">Account:</span> {account?.customerNumber || tickler.accountId}</p>
                  <p className="min-w-0 break-words"><span className="font-semibold text-[#0A2540]">Reminder:</span> {tickler.ticklerType}</p>
                  <p className="min-w-0 break-words"><span className="font-semibold text-[#0A2540]">Collector:</span> {tickler.assignedCollector}</p>
                </div>
                <div className="mt-3 grid min-w-0 gap-2 rounded-2xl border border-[#BFEAFF] bg-white p-3 text-xs font-semibold uppercase tracking-[0.11em] text-[#0A2540]">
                  <p className="break-words">Due {formatDate(tickler.dueDate)} • {tickler.status}</p>
                </div>
              </button>
            );
          }) : (
            <div className="rounded-2xl border border-dashed border-[#BFEAFF] bg-[#F4FBFF] p-4 text-sm font-semibold leading-7 text-[#0A2540]">
              No ticklers are currently queued for action.
            </div>
          )}
        </div>
      </section>

      {selectedTickler ? (
        <section className="grid min-w-0 max-w-full gap-4 overflow-hidden">
          <article className="min-w-0 max-w-full overflow-hidden rounded-[1.75rem] border border-[#BFEAFF] bg-white p-5 shadow-[0_18px_38px_rgba(10,37,64,.08)]">
            <SectionHeading
              eyebrow="Selected Tickler"
              title={selectedTickler.title}
              description="Selected follow-up context, account details, and the collector action surface."
            />
            <div className="mt-5 grid min-w-0 gap-3 md:grid-cols-[repeat(2,minmax(0,1fr))] xl:grid-cols-[repeat(4,minmax(0,1fr))]">
              <DetailMetric label="Customer" value={selectedTickler.customerName} icon={<Users className="size-5" aria-hidden="true" />} />
              <DetailMetric label="Customer number" value={selectedAccount?.customerNumber || "Not captured"} icon={<IdCard className="size-5" aria-hidden="true" />} />
              <DetailMetric label="Account" value={selectedAccount?.customerNumber || selectedTickler.accountId} icon={<IdCard className="size-5" aria-hidden="true" />} />
              <DetailMetric label="Collector" value={selectedTickler.assignedCollector} icon={<Users className="size-5" aria-hidden="true" />} />
              <DetailMetric label="Due date" value={formatDate(selectedTickler.dueDate)} icon={<Clock3 className="size-5" aria-hidden="true" />} />
              <DetailMetric label="Priority" value={selectedTickler.priority} icon={<Target className="size-5" aria-hidden="true" />} />
              <DetailMetric label="Reminder type" value={selectedTickler.ticklerType} icon={<ClipboardList className="size-5" aria-hidden="true" />} />
              <DetailMetric label="Reason" value={getTicklerWorkReason(selectedTickler, selectedAccount)} icon={<ShieldCheck className="size-5" aria-hidden="true" />} />
              <DetailMetric label="Related promise" value={selectedPromise ? `${formatARRecoveryCurrency(selectedPromise.amountCents)} • ${selectedPromise.status}` : "Not linked"} icon={<CircleDollarSign className="size-5" aria-hidden="true" />} />
              <DetailMetric label="Related invoice" value={selectedPromise?.invoiceLabels[0] || "Not linked"} icon={<FileText className="size-5" aria-hidden="true" />} />
              <DetailMetric label="Current status" value={selectedTickler.status} icon={<ShieldCheck className="size-5" aria-hidden="true" />} />
              <DetailMetric label="Last contact" value={selectedActivity[0] ? `${formatDate(selectedActivity[0].createdAt)} • ${selectedActivity[0].kind}` : "No contact captured"} icon={<Clock3 className="size-5" aria-hidden="true" />} />
              <DetailMetric label="Notes" value={selectedAccount?.notes || "No notes captured"} icon={<ClipboardList className="size-5" aria-hidden="true" />} />
              <DetailMetric label="Timeline summary" value={selectedActivity[0] ? `${selectedActivity[0].title} • ${formatDate(selectedActivity[0].createdAt)}` : "No recent timeline activity"} icon={<Activity className="size-5" aria-hidden="true" />} />
            </div>
          </article>

          <article className="min-w-0 max-w-full overflow-hidden rounded-[1.75rem] border border-[#BFEAFF] bg-[#F4FBFF] p-5">
            <p className="flex items-center gap-2 text-sm font-semibold text-[#0A2540]">
              <ARLightLineIcon icon={<Bolt className="size-5" aria-hidden="true" />} />
              Work Actions
            </p>
            <div className="mt-3 grid min-w-0 gap-3 md:grid-cols-[repeat(2,minmax(0,1fr))] xl:grid-cols-[repeat(4,minmax(0,1fr))]">
              <TicklerQuickActionButton label="Call Customer" note="Launch outreach." icon={<Users className="size-5" aria-hidden="true" />} onClick={() => openTicklerAction("Call Customer", "Open the communication workflow for this customer follow-up.", "Ready", "/admin/ar-recovery/recovery-activity", "call-customer")} />
              <TicklerQuickActionButton label="Send Email" note="Open email." icon={<FileText className="size-5" aria-hidden="true" />} onClick={() => openTicklerAction("Send Email", "Open the email workflow with this customer context preloaded.", "Coming Soon")} />
              <TicklerQuickActionButton label="Generate Letter" note="Open letters." icon={<FileText className="size-5" aria-hidden="true" />} onClick={() => openTicklerAction("Generate Letter", "Open Letter Center with the selected account context preloaded.", "Ready", "/admin/ar-recovery/collection-letters", "generate-letter")} />
              <TicklerQuickActionButton label="Generate Statement" note="Open statements." icon={<FileText className="size-5" aria-hidden="true" />} onClick={() => openTicklerAction("Generate Statement", "Open Statement Center with the selected account context preloaded.", "Ready", "/admin/ar-recovery/statements", "generate-statement")} />
              <TicklerQuickActionButton label="Add Note" note="Open note." icon={<ClipboardList className="size-5" aria-hidden="true" />} onClick={() => openTicklerAction("Add Note", "Capture a collector note for this tickler and account context.", "Coming Soon")} />
              <TicklerQuickActionButton label="Snooze Reminder" note="Open snooze." icon={<Clock3 className="size-5" aria-hidden="true" />} onClick={() => openTicklerAction("Snooze Reminder", "Open a snooze workflow for this reminder.", "Coming Soon")} />
              <TicklerQuickActionButton label="Complete Tickler" note="Complete reminder." icon={<ShieldCheck className="size-5" aria-hidden="true" />} onClick={() => openTicklerAction("Complete Tickler", "Complete this tickler after the required follow-up work is done.", "Coming Soon")} />
              <TicklerQuickActionButton label="Escalate" note="Open escalation." icon={<ShieldCheck className="size-5" aria-hidden="true" />} onClick={() => openTicklerAction("Escalate", "Open escalation context for manager review without changing workflow rules.", "Ready", "/admin/ar-recovery/manager-command-center", "escalate")} />
              <TicklerQuickActionButton label="Review Promise" note="Open promise." icon={<CircleDollarSign className="size-5" aria-hidden="true" />} onClick={() => openTicklerAction("Review Promise", "Open Promise Center with the related promise context preloaded.", "Ready", "/admin/ar-recovery/promise-to-pay", "review-promise")} />
            </div>
            {activeQuickAction ? <TicklerQuickActionPanel action={activeQuickAction} onClose={() => setActiveQuickAction(null)} /> : null}
          </article>

          <article className="min-w-0 max-w-full overflow-hidden rounded-[1.75rem] border border-[#BFEAFF] bg-white p-5 shadow-[0_18px_38px_rgba(10,37,64,.08)]">
            <div className="flex min-w-0 max-w-full flex-wrap gap-2">
              {workspaceTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setWorkspaceTab(tab.key)}
                  className={`rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] transition ${
                    workspaceTab === tab.key
                      ? "border-[#14B8A6] bg-[#F4FBFF] text-[#0A2540]"
                      : "border-[#BFEAFF] bg-white text-[#475569] hover:border-[#14B8A6] hover:text-[#0A2540]"
                  }`}
                  title={tab.note}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="mt-4 min-w-0 max-w-full overflow-hidden">
              {workspaceTab === "details" ? <TicklerDetailSurface tickler={selectedTickler} account={selectedAccount} /> : null}
              {workspaceTab === "activity" ? <TicklerActivitySurface activity={selectedActivity} tickler={selectedTickler} /> : null}
              {workspaceTab === "history" ? <TicklerHistorySurface tickler={selectedTickler} /> : null}
            </div>
          </article>
        </section>
      ) : null}
    </div>
  );
}
type ManagerWorkspaceTabKey = "queue" | "team" | "activity";

const MANAGER_COMMAND_EXECUTIVE_KPI_MODEL = ["Operational KPIs", "Collector KPIs", "Client KPIs", "Portfolio KPIs", "Financial KPIs", "SLA KPIs"] as const;
const MANAGER_COMMAND_WORKFORCE_MODEL = ["Collector Workload", "Team Workload", "Assignments", "Capacity", "Availability", "Productivity", "Performance"] as const;
const MANAGER_COMMAND_RECOVERY_OPERATIONS_MODEL = ["Recovery Workbench", "Promise Center", "Tickler Center", "Letter Center", "Statement Center", "Payments", "Escalations", "Recovery Activity", "Timeline"] as const;
const MANAGER_COMMAND_REPORTING_MODEL = ["Daily", "Weekly", "Monthly", "Collector", "Client", "Portfolio", "Team", "Operational Health", "Recovery Performance"] as const;
const MANAGER_COMMAND_ALERT_MODEL = ["Critical Alerts", "SLA Breaches", "Overdue Promises", "Overdue Ticklers", "Failed Statement Delivery", "Failed Letter Delivery", "Queue Health", "Operational Health"] as const;
const MANAGER_COMMAND_RECOVERY_ACTIVITY_MODEL = ["Activity Volume", "Collector Activity", "Client Activity", "Operational Timeline", "Recovery Health"] as const;
const MANAGER_COMMAND_COMMUNICATIONS_ARCHITECTURE = ["Microsoft Outlook", "Microsoft Exchange Online", "Microsoft 365", "Microsoft Teams", "Nextiva", "Email Notifications", "SMS", "Internal Notifications", "Manager Notifications"] as const;
const MANAGER_COMMAND_ENTERPRISE_READINESS_MODEL = ["Executive Audit", "Forecasting", "AI Insights", "Operational Health", "Capacity Planning", "Trend Analysis", "Compliance", "Retention"] as const;

function ManagerCommandCenterSection({
  workspace,
  escalationWorkflows,
}: {
  workspace: ReturnType<typeof loadARRecoveryWorkspace>;
  escalationWorkflows: ARWorkflowEscalationProjection[];
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const managerCommandReturnContext = (location.state as AccountRecoveryLaunchState | null)?.managerCommandReturnContext;
  const initialManagerTab = managerCommandReturnContext?.workspaceTab || "queue";
  const initialSelectedOperationalCaseId = managerCommandReturnContext?.selectedOperationalCaseId || workspace.accounts[0]?.id || "";
  const [workspaceTab, setWorkspaceTab] = useState<ManagerWorkspaceTabKey>(initialManagerTab);
  const [selectedOperationalCaseId, setSelectedOperationalCaseId] = useState(initialSelectedOperationalCaseId);
  const managerActivityHeadingRef = useRef<HTMLDivElement | null>(null);
  const dashboard = useMemo(() => buildARRecoveryDashboardSnapshot(workspace.company.id), [workspace.company.id]);
  const ticklerSnapshot = useMemo(() => buildARRecoveryTicklerDashboardSnapshot(workspace.company.id), [workspace.company.id]);
  const managerCollectorQueue = useMemo(() => {
    const collectorNames = Array.from(new Set([
      ...workspace.collectors.map((collector) => collector.collectorName),
      ...workspace.accounts.map((account) => account.assignedCollector),
    ].filter(Boolean))).sort((a, b) => a.localeCompare(b));

    return collectorNames.map((collectorName) => {
      const assignedAccounts = workspace.accounts.filter((account) => account.assignedCollector === collectorName);
      const assignedAccountIds = new Set(assignedAccounts.map((account) => account.id));
      return {
        collectorName,
        dueAccounts: assignedAccounts.length,
        overdueAccounts: assignedAccounts.filter((account) => account.agingSummary.includes("90") || account.recoveryStatus === "Escalated").length,
        todayCalls: workspace.activity.filter((item) => assignedAccountIds.has(item.accountId) && item.kind === "Call").length,
        todayEmails: workspace.activity.filter((item) => assignedAccountIds.has(item.accountId) && item.kind === "Email").length,
        todayPromises: workspace.promises.filter((promise) => assignedAccountIds.has(promise.accountId)).length,
        todayFollowUps: workspace.ticklers.filter((tickler) => assignedAccountIds.has(tickler.accountId) && tickler.status !== "Completed").length,
        todayEscalations: assignedAccounts.filter((account) => account.escalationStatus !== "None").length,
      };
    });
  }, [workspace.accounts, workspace.activity, workspace.collectors, workspace.promises, workspace.ticklers]);
  const attentionQueue = [...workspace.accounts]
    .sort((a, b) => {
      const priorityScore = (account: (typeof workspace.accounts)[number]) => {
        let score = 0;
        if (account.recoveryStatus === "Escalated") score += 40;
        if (account.escalationStatus === "Director Review") score += 30;
        if (account.escalationStatus === "Escalated") score += 20;
        if (account.promiseToPayCount > 0) score += 10;
        score += Math.min(Math.round(account.openBalanceCents / 100000), 50);
        return score;
      };
      return priorityScore(b) - priorityScore(a);
    })
    .slice(0, 8);
  const selectedOperationalCase = attentionQueue.find((account) => account.id === selectedOperationalCaseId) || attentionQueue[0] || workspace.accounts[0];
  const selectedCasePromises = selectedOperationalCase ? workspace.promises.filter((promise) => promise.accountId === selectedOperationalCase.id) : [];
  const selectedCaseTicklers = selectedOperationalCase ? workspace.ticklers.filter((tickler) => tickler.accountId === selectedOperationalCase.id) : [];
  const selectedCaseActivity = selectedOperationalCase ? workspace.activity.filter((item) => item.accountId === selectedOperationalCase.id) : [];
  const selectedCaseEscalation = selectedOperationalCase
    ? escalationWorkflows
      .filter((escalation) => escalation.accountId === selectedOperationalCase.id)
      .sort((a, b) => getDateTime(String(b.updatedAt || b.createdAt || "")) - getDateTime(String(a.updatedAt || a.createdAt || "")))[0]
    : null;
  const brokenPromiseCount = selectedCasePromises.filter((promise) => promise.status === "Broken" || promise.status === "Escalated").length;
  const followUpsDueCount = selectedCaseTicklers.filter((tickler) => tickler.status !== "Completed").length;
  const latestContact = selectedCaseActivity[0];
  const completedEscalationDecision = selectedCaseEscalation?.completedAt && selectedCaseEscalation?.decision ? String(selectedCaseEscalation.decision) : "";
  const unresolvedEscalationStatus = String(selectedCaseEscalation?.status || selectedOperationalCase?.escalationStatus || "None");
  const requiresManagerEscalationReview = Boolean(selectedOperationalCase)
    && !completedEscalationDecision
    && (unresolvedEscalationStatus === "Director Review" || unresolvedEscalationStatus === "Escalated");
  const requiresEscalationDocumentation = completedEscalationDecision === "Request Documentation";
  const requiresEscalationContinuation = completedEscalationDecision === "Escalate Further";
  const selectedCaseStatus = selectedOperationalCase
    ? requiresManagerEscalationReview
      ? "Director Review"
      : requiresEscalationContinuation || selectedOperationalCase.escalationStatus === "Escalated"
        ? "Escalated"
        : selectedOperationalCase.recoveryStatus === "At Risk"
          ? "At Risk"
          : selectedOperationalCase.escalationStatus === "Watch"
            ? "Watch"
            : "Healthy"
    : "Watch";
  const selectedCaseIntro = selectedOperationalCase
    ? "Review the selected account's operating posture, attention drivers, and next manager action."
    : "Select an operational case to review manager context and next action guidance.";
  const selectedCaseAttentionReasons = selectedOperationalCase
    ? [
        selectedOperationalCase.openBalanceCents > 1000000 ? "High Balance" : null,
        selectedOperationalCase.agingSummary.includes("90") ? "91+ Days" : null,
        brokenPromiseCount ? "Broken Promise" : null,
        followUpsDueCount ? "Follow-Up Overdue" : null,
        requiresManagerEscalationReview || requiresEscalationContinuation ? unresolvedEscalationStatus : null,
        requiresEscalationDocumentation ? "Documentation Requested" : null,
        selectedOperationalCase.promiseToPayCount ? "Promise Activity" : null,
      ].filter(Boolean) as string[]
    : [];
  const selectedCaseRecommendation = selectedOperationalCase
    ? requiresManagerEscalationReview || requiresEscalationContinuation
      ? {
          label: "Review Escalation",
          note: requiresEscalationContinuation ? "Escalation remains active after the prior decision and needs manager follow-through." : "Resolve the active manager-review escalation before other account work.",
          action: "review-escalation" as ManagerCommandAccountAction,
          selectedTab: "activity" as ARRecoveryAccountTabKey,
          icon: <ShieldCheck className="size-4" aria-hidden="true" />,
          assessment: `${selectedOperationalCase.customerName} remains in manager review because ${selectedCaseAttentionReasons.length ? selectedCaseAttentionReasons.slice(0, 3).join(", ").toLowerCase() : "the account requires collector oversight"}. Complete the active escalation review before moving to lower-priority operational work.`,
        }
      : requiresEscalationDocumentation
        ? {
            label: "Request Document",
            note: "Capture the documentation requested by the completed manager decision.",
            action: "request-document" as ManagerCommandAccountAction,
            selectedTab: "documents" as ARRecoveryAccountTabKey,
            icon: <FileText className="size-4" aria-hidden="true" />,
            assessment: `${selectedOperationalCase.customerName} has a completed manager decision requesting documentation. The next unresolved step is to capture the requested supporting document before returning the account to ordinary follow-up activity.`,
          }
        : followUpsDueCount
          ? {
              label: "Follow-Up",
              note: "Schedule or complete the overdue customer follow-up.",
              action: "create-follow-up" as ManagerCommandAccountAction,
              selectedTab: "ticklers" as ARRecoveryAccountTabKey,
              icon: <Clock3 className="size-4" aria-hidden="true" />,
              assessment: `${selectedOperationalCase.customerName} no longer has an unresolved manager-review decision. The highest remaining operational condition is ${followUpsDueCount} open follow-up${followUpsDueCount === 1 ? "" : "s"} that should be worked before the account is considered current.`,
            }
          : brokenPromiseCount || selectedOperationalCase.promiseToPayCount
            ? {
                label: "Promise",
                note: "Confirm active promise status and next commitment.",
                action: "promise" as ManagerCommandAccountAction,
                selectedTab: "promises" as ARRecoveryAccountTabKey,
                icon: <Target className="size-4" aria-hidden="true" />,
                assessment: `${selectedOperationalCase.customerName} no longer has an unresolved manager-review decision. Promise activity remains the next operational priority and should be confirmed before ownership or risk posture changes.`,
              }
            : selectedOperationalCase.agingSummary.includes("90") || selectedOperationalCase.openBalanceCents > 1000000
              ? {
                  label: "Log Call",
                  note: "Capture the next customer touchpoint for the high-risk balance.",
                  action: "log-call" as ManagerCommandAccountAction,
                  selectedTab: "activity" as ARRecoveryAccountTabKey,
                  icon: <Activity className="size-4" aria-hidden="true" />,
                  assessment: `${selectedOperationalCase.customerName} no longer has an unresolved manager-review decision. The remaining priority is customer contact for the aged balance and current account exposure.`,
                }
              : {
                  label: "View Account",
                  note: "Monitor account context and continue normal recovery oversight.",
                  action: "view-account" as ManagerCommandAccountAction,
                  selectedTab: "activity" as ARRecoveryAccountTabKey,
                  icon: <FileText className="size-4" aria-hidden="true" />,
                  assessment: `${selectedOperationalCase.customerName} has no unresolved manager-review decision. Continue monitoring the account and use the certified Account Recovery workspace when more context is needed.`,
                }
    : null;
  const selectedCaseAssessment = selectedOperationalCase
    ? selectedCaseRecommendation?.assessment || `${selectedOperationalCase.customerName} requires manager review based on current operational conditions.`
    : "Select a case to load the manager assessment.";
  const selectedCaseSummaryFacts = selectedOperationalCase
    ? [
        { label: "Collector", value: selectedOperationalCase.assignedCollector },
        { label: "Risk", value: selectedOperationalCase.recoveryStatus },
        { label: "Balance", value: formatARRecoveryCurrency(selectedOperationalCase.openBalanceCents) },
        { label: "Aging", value: selectedOperationalCase.agingSummary },
      ]
    : [];
  const formatManagerTimelineType = (kind: ReturnType<typeof loadARRecoveryWorkspace>["activity"][number]["kind"]) => {
    if (kind === "Call" || kind === "Email" || kind === "Task") return "Collector Activity";
    if (kind === "Promise") return "Promise Activity";
    if (kind === "Escalation") return "Manager Review";
    if (kind === "Payment") return "Payment Activity";
    if (kind === "Letter" || kind === "Statement") return "Document Activity";
    return "Account Activity";
  };
  const recentResponses = [...workspace.activity].filter((item) => ["Call", "Email", "Payment", "Promise", "Escalation"].includes(item.kind)).slice(0, 10);
  const workspaceTabs: Array<{ key: ManagerWorkspaceTabKey; label: string; note: string }> = [
    { key: "queue", label: "Queue", note: "Accounts needing immediate collector attention." },
    { key: "team", label: "Team View", note: "Portfolio health and collector workload." },
    { key: "activity", label: "Activity", note: "Recent responses and operational actions." },
  ];

  useEffect(() => {
    if (!managerCommandReturnContext) return;
    setWorkspaceTab(managerCommandReturnContext.workspaceTab);
    setSelectedOperationalCaseId(managerCommandReturnContext.selectedOperationalCaseId);
    window.setTimeout(() => {
      window.scrollTo({ top: managerCommandReturnContext.scrollY, left: 0, behavior: "auto" });
    }, 0);
  }, [managerCommandReturnContext]);

  function openSelectedCaseAccountRecovery(action: ManagerCommandAccountAction = "view-account", selectedTab: ARRecoveryAccountTabKey = "activity") {
    if (!selectedOperationalCase) return;
    navigate("/admin/ar-recovery/customer-accounts", {
      state: {
        accountRecoveryLaunch: {
          origin: "manager-command",
          accountId: selectedOperationalCase.id,
          selectedTab,
          managerAction: action,
          launchToken: `${action}-${selectedOperationalCase.id}-${Date.now()}`,
          returnRoute: "/admin/ar-recovery/manager-command-center",
          managerCommandReturnContext: {
            selectedOperationalCaseId: selectedOperationalCase.id,
            workspaceTab,
            scrollY: window.scrollY,
          },
        },
      } satisfies AccountRecoveryLaunchState,
    });
  }

  function openSelectedCaseActivity() {
    setWorkspaceTab("activity");
    window.setTimeout(() => {
      managerActivityHeadingRef.current?.focus({ preventScroll: false });
    }, 0);
  }

  const managerActionCards: Array<{ label: string; note: string; icon: ReactNode; action: ManagerCommandAccountAction; selectedTab?: ARRecoveryAccountTabKey }> = [
    { label: "Assign Collector", note: "Open certified account assignment fields.", icon: <Users className="size-4" aria-hidden="true" />, action: "assign-collector", selectedTab: "overview" },
    { label: "Review Escalation", note: "Open escalation review workflow.", icon: <ShieldCheck className="size-4" aria-hidden="true" />, action: "review-escalation", selectedTab: "activity" },
    { label: "Add Manager Note", note: "Capture manager-only account direction.", icon: <ClipboardList className="size-4" aria-hidden="true" />, action: "add-manager-note", selectedTab: "notes" },
    { label: "Create Follow-Up", note: "Prepare next check-in.", icon: <Clock3 className="size-4" aria-hidden="true" />, action: "create-follow-up", selectedTab: "ticklers" },
    { label: "View Account", note: "Open account context.", icon: <FileText className="size-4" aria-hidden="true" />, action: "view-account", selectedTab: "activity" },
  ];

  const nextActionGuidanceCards: Array<{ label: string; note: string; action: ManagerCommandAccountAction; selectedTab?: ARRecoveryAccountTabKey }> = [
    { label: "Log Call", note: "Capture the latest collector touchpoint.", action: "log-call", selectedTab: "activity" },
    { label: "Add Note", note: "Record the outcome or internal direction.", action: "add-manager-note", selectedTab: "notes" },
    { label: "Assign Task", note: "Hand off a follow-up or escalation step.", action: "assign-task", selectedTab: "tasks" },
    { label: "Promise", note: "Track the next commitment date and amount.", action: "promise", selectedTab: "promises" },
    { label: "Follow-Up", note: "Schedule the next check-in or reminder.", action: "create-follow-up", selectedTab: "ticklers" },
    { label: "Generate Letter", note: "Prepare a collection letter.", action: "generate-letter", selectedTab: "letters" },
    { label: "Generate Statement", note: "Prepare a statement.", action: "generate-statement", selectedTab: "statements" },
    { label: "Request Document", note: "Capture needed supporting documents.", action: "request-document", selectedTab: "documents" },
    { label: "Escalate", note: "Route a serious account for manager review.", action: "escalate", selectedTab: "activity" },
  ];

  return (
    <div
      className="ar-workspace-os grid gap-4"
      data-manager-executive-kpi-model={MANAGER_COMMAND_EXECUTIVE_KPI_MODEL.join(",")}
      data-manager-workforce-model={MANAGER_COMMAND_WORKFORCE_MODEL.join(",")}
      data-manager-recovery-operations-model={MANAGER_COMMAND_RECOVERY_OPERATIONS_MODEL.join(",")}
      data-manager-reporting-model={MANAGER_COMMAND_REPORTING_MODEL.join(",")}
      data-manager-alert-model={MANAGER_COMMAND_ALERT_MODEL.join(",")}
      data-manager-recovery-activity-model={MANAGER_COMMAND_RECOVERY_ACTIVITY_MODEL.join(",")}
      data-manager-communications-architecture={MANAGER_COMMAND_COMMUNICATIONS_ARCHITECTURE.join(",")}
      data-manager-enterprise-readiness-model={MANAGER_COMMAND_ENTERPRISE_READINESS_MODEL.join(",")}
    >
      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {[
          { label: "Accounts Requiring Attention", value: dashboard.accountsRequiringAction, note: "Items that need a collector touchpoint today." },
          { label: "Follow-Ups Due", value: dashboard.followUpsDueToday, note: "Promises and reminders that are due now." },
          { label: "Broken Promises", value: dashboard.brokenPromises, note: "Missed commitments that may need escalation." },
          { label: "Escalations Needed", value: dashboard.escalationsNeeded, note: "Manager review queue for higher-risk accounts." },
        ].map((card) => (
          <article key={card.label} className="rounded-[1.75rem] border border-[rgba(255,106,43,.16)] bg-[rgba(7,18,31,.78)] p-5 shadow-[0_20px_48px_rgba(0,0,0,.18)]">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF6A2B]">{card.label}</p>
            <p className="mt-2 font-display text-3xl font-bold text-[#F6F1E8]">{card.value}</p>
            <p className="mt-3 text-sm font-semibold leading-7 text-[#E8EEF4]">{card.note}</p>
          </article>
        ))}
      </section>

      <section className="rounded-[1.75rem] border border-[rgba(34,224,208,.18)] bg-white p-4 shadow-[0_18px_38px_rgba(10,37,64,.08)]">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.14em] text-[#C9A743]">Manager Workspace</p>
            <h3 className="mt-1 text-xl font-black text-[#071A2F]">Focused operational command view</h3>
            <p className="mt-1 text-sm font-semibold leading-6 text-[#0D2B4D]">
              Move between priority queue, team oversight, and activity context without turning the command center into a long report.
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2" role="tablist" aria-label="Manager Command views">
          {workspaceTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={workspaceTab === tab.key}
              onClick={() => setWorkspaceTab(tab.key)}
              className={`ar-interaction-control rounded-full border px-4 py-2 text-xs font-black uppercase tracking-[0.12em] transition ${
                workspaceTab === tab.key
                  ? "ar-interaction-selected"
                  : "border-[rgba(20,184,166,.24)] bg-[#F2FEFC] text-[#0A2540] hover:border-[#14B8A6]"
              }`}
              title={tab.note}
            >
              {tab.label}{workspaceTab === tab.key ? <span className="sr-only"> selected</span> : null}
            </button>
          ))}
        </div>
      </section>

      {workspaceTab === "queue" ? (
      <div className="grid min-w-0 gap-6">
      <section className="grid min-w-0 grid-cols-1 items-start gap-6 2xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
        <article
          className="flex h-fit min-w-0 max-w-full self-start rounded-[1.75rem] border border-[rgba(34,224,208,.16)] bg-[rgba(7,18,31,.78)] p-5 shadow-[0_20px_48px_rgba(0,0,0,.18)] 2xl:flex-col"
          style={{ alignSelf: "start" }}
        >
          <SectionHeading
            eyebrow="Daily Work Queue"
            title="Accounts needing collector attention"
            description="Prioritized by escalation risk, promise activity, and open balance so collectors can work the day in order."
          />
          <div className="mt-5 border-t border-[rgba(34,224,208,.14)] pt-4">
            <div className="min-w-0 max-w-full pr-1">
              <div className="flex flex-col gap-4">
                {attentionQueue.length ? attentionQueue.map((account) => (
                  <button
                    key={account.id}
                    type="button"
                    onClick={() => setSelectedOperationalCaseId(account.id)}
                    aria-current={selectedOperationalCase?.id === account.id ? "true" : undefined}
                    className={`ar-interaction-control min-w-0 max-w-full rounded-2xl border border-l-4 px-5 py-4 text-left transition hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#14B8A6] focus-visible:ring-offset-2 focus-visible:ring-offset-[#071A2F] ${
                      selectedOperationalCase?.id === account.id
                        ? "ar-interaction-selected-dark border-l-[#FF6A2B]"
                        : "border-[rgba(255,106,43,.14)] border-l-transparent bg-[rgba(255,255,255,.04)] hover:border-[rgba(34,224,208,.34)]"
                    }`}
                  >
                    <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">{account.customerNumber}</p>
                        <h3 className="mt-1 min-w-0 break-words font-display text-xl font-bold leading-7 text-[#F6F1E8] sm:text-2xl">{account.customerName}</h3>
                      </div>
                      {selectedOperationalCase?.id === account.id ? <span className="ar-interaction-selected-marker shrink-0">Selected</span> : null}
                      <span className="shrink-0 rounded-full border border-[rgba(34,224,208,.2)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#22E0D0]">{account.recoveryStatus}</span>
                    </div>
                    <div className="mt-4 grid min-w-0 gap-2 text-sm font-semibold leading-6 text-[#E8EEF4] sm:grid-cols-[repeat(2,minmax(0,1fr))]">
                      <p className="min-w-0 break-words"><span className="text-[#FF6A2B]">Collector:</span> {account.assignedCollector}</p>
                      <p className="min-w-0 break-words"><span className="text-[#FF6A2B]">Aging:</span> {account.agingSummary}</p>
                      <p className="min-w-0 break-words"><span className="text-[#FF6A2B]">Open Balance:</span> {formatARRecoveryCurrency(account.openBalanceCents)}</p>
                      <p className="min-w-0 break-words"><span className="text-[#FF6A2B]">Promises:</span> {account.promiseToPayCount}</p>
                    </div>
                    <div className="mt-3 flex min-w-0 flex-wrap gap-2">
                      <span className="rounded-full border border-[rgba(34,224,208,.18)] bg-[rgba(34,224,208,.08)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-[#22E0D0]">{account.escalationStatus}</span>
                      <span className="rounded-full border border-[rgba(255,106,43,.18)] bg-[rgba(255,106,43,.08)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-[#FF6A2B]">{account.openInvoiceCount} invoices</span>
                    </div>
                  </button>
                )) : (
                  <div className="rounded-2xl border border-dashed border-[rgba(255,106,43,.18)] bg-[rgba(255,255,255,.03)] p-4 text-sm font-semibold leading-7 text-[#E8EEF4]">No accounts are currently flagged for attention.</div>
                )}
              </div>
            </div>
          </div>
        </article>

        <article className="min-w-0 max-w-full overflow-hidden rounded-[1.75rem] border border-[#BFEAFF] bg-white p-5 shadow-[0_18px_38px_rgba(10,37,64,.08)]">
          <SectionHeading
            eyebrow="Selected Operational Case"
            title={selectedOperationalCase ? selectedOperationalCase.customerName : "Select a case"}
            description={selectedCaseIntro}
          />
          {selectedOperationalCase ? (
            <div className="mt-5 grid min-w-0 gap-5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full border border-[#BFEAFF] bg-white px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#0A2540]">{selectedOperationalCase.customerNumber}</span>
                <span className="rounded-full border border-[#14B8A6] bg-[#E6FFFB] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#0F766E]">{selectedCaseStatus}</span>
                {selectedOperationalCase.escalationStatus !== selectedCaseStatus ? (
                  <span className="rounded-full border border-[#FF6A2B] bg-[#FFF8DB] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#D94A16]">{selectedOperationalCase.escalationStatus}</span>
                ) : null}
              </div>
              <div className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
                <p className="flex items-center gap-2 text-sm font-semibold text-[#0A2540]">
                  <ARLightLineIcon icon={<FileText className="size-5" aria-hidden="true" />} />
                  Operational Summary
                </p>
                <div className="mt-3 grid min-w-0 gap-2 sm:grid-cols-[repeat(2,minmax(0,1fr))]">
                  {selectedCaseSummaryFacts.map((fact) => (
                    <div key={fact.label} className="min-w-0 rounded-xl border border-[#BFEAFF] bg-white px-3 py-2">
                      <p className="text-[11px] font-black uppercase tracking-[0.12em] text-[#0F766E]">{fact.label}</p>
                      <p className="mt-1 min-w-0 break-words text-sm font-semibold leading-6 text-[#0A2540]">{fact.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-[#FF6A2B] bg-[#FFFDF2] p-4">
                <p className="flex items-center gap-2 text-sm font-semibold text-[#0A2540]">
                  <ARLightLineIcon icon={<Target className="size-5" aria-hidden="true" />} />
                  Reason for Attention
                </p>
                <div className="mt-3 flex min-w-0 flex-wrap gap-2">
                  {(selectedCaseAttentionReasons.length ? selectedCaseAttentionReasons : ["Manager Review"]).map((reason) => (
                    <span key={reason} className="rounded-full border border-[#FF6A2B] bg-white px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-[#D94A16]">{reason}</span>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                <p className="flex items-center gap-2 text-sm font-semibold text-[#0A2540]">
                  <ARLightLineIcon icon={<Activity className="size-5" aria-hidden="true" />} />
                  Risk Snapshot
                </p>
              <div className="mt-3 grid min-w-0 gap-3 md:grid-cols-[repeat(2,minmax(0,1fr))]">
                <DetailMetric label="Assigned collector" value={selectedOperationalCase.assignedCollector} icon={<Users className="size-5" aria-hidden="true" />} />
                <DetailMetric label="Risk status" value={selectedOperationalCase.recoveryStatus} icon={<ShieldCheck className="size-5" aria-hidden="true" />} />
                <DetailMetric label="Aging bucket" value={selectedOperationalCase.agingSummary} icon={<Clock3 className="size-5" aria-hidden="true" />} />
                <DetailMetric label="Open balance" value={formatARRecoveryCurrency(selectedOperationalCase.openBalanceCents)} icon={<CircleDollarSign className="size-5" aria-hidden="true" />} />
                <DetailMetric label="Open invoices" value={selectedOperationalCase.openInvoiceCount.toString()} icon={<FileText className="size-5" aria-hidden="true" />} />
                <DetailMetric label="Promise count" value={selectedOperationalCase.promiseToPayCount.toString()} icon={<Target className="size-5" aria-hidden="true" />} />
                <DetailMetric label="Follow-ups due" value={followUpsDueCount.toString()} icon={<ClipboardList className="size-5" aria-hidden="true" />} />
                <DetailMetric label="Broken promises" value={brokenPromiseCount.toString()} icon={<Activity className="size-5" aria-hidden="true" />} />
                <DetailMetric label="Last contact" value={latestContact ? formatCapturedDate(latestContact.createdAt) : "Not captured"} icon={<Clock3 className="size-5" aria-hidden="true" />} />
                <DetailMetric label="Next recommended action" value={selectedCaseRecommendation?.label || formatCapturedDate(getNextActionDate(selectedOperationalCase))} icon={<ArrowRight className="size-5" aria-hidden="true" />} />
              </div>
              </div>
            </div>
          ) : (
            <div className="mt-5 rounded-2xl border border-dashed border-[#BFEAFF] bg-[#F4FBFF] p-4 text-sm font-semibold leading-7 text-[#0A2540]">
              Select an item from the Daily Work Queue to load the operational case.
            </div>
          )}
        </article>
      </section>
      {selectedOperationalCase ? (
        <section className="grid min-w-0 gap-5">
          <div className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
            <p className="flex items-center gap-2 text-sm font-semibold text-[#0A2540]">
              <ARLightLineIcon icon={<ShieldCheck className="size-5" aria-hidden="true" />} />
              Recommended Action
            </p>
            {selectedCaseRecommendation ? (
              <h4 className="mt-3 text-lg font-black text-[#071A2F]">{selectedCaseRecommendation.label}</h4>
            ) : null}
            <p className="mt-3 text-sm font-medium leading-7 text-[#0A2540]">{selectedCaseAssessment}</p>
            {selectedCaseRecommendation ? (
              <div className="mt-3 max-w-xl">
                <ActionPill
                  label={selectedCaseRecommendation.label}
                  note={selectedCaseRecommendation.note}
                  icon={selectedCaseRecommendation.icon}
                  onClick={() => openSelectedCaseAccountRecovery(selectedCaseRecommendation.action, selectedCaseRecommendation.selectedTab)}
                />
              </div>
            ) : null}
          </div>

          <div className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
            <p className="flex items-center gap-2 text-sm font-semibold text-[#0A2540]">
              <ARLightLineIcon icon={<Bolt className="size-5" aria-hidden="true" />} />
              Manager Actions
            </p>
            <div className="mt-3 grid min-w-0 gap-2 sm:grid-cols-[repeat(2,minmax(0,1fr))] xl:grid-cols-[repeat(3,minmax(0,1fr))]">
              {managerActionCards.map((action) => (
                <ActionPill
                  key={action.label}
                  label={action.label}
                  note={action.note}
                  icon={action.icon}
                  onClick={() => openSelectedCaseAccountRecovery(action.action, action.selectedTab || "activity")}
                />
              ))}
              <ActionPill label="Open Activity" note="Review selected account activity." icon={<Activity className="size-4" aria-hidden="true" />} onClick={openSelectedCaseActivity} />
            </div>
          </div>

          <div className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
            <p className="flex items-center gap-2 text-sm font-semibold text-[#0A2540]">
              <ARLightLineIcon icon={<Clock3 className="size-5" aria-hidden="true" />} />
              Operational Timeline
            </p>
            <div className="mt-3 grid gap-2">
              {selectedCaseActivity.slice(0, 3).length ? selectedCaseActivity.slice(0, 3).map((item) => (
                <div key={item.id} className="rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-3">
                  <p className="text-xs font-black uppercase tracking-[0.12em] text-[#0F766E]">{formatCapturedDate(item.createdAt)}</p>
                  <p className="mt-1 text-sm font-semibold leading-6 text-[#0A2540]">{item.title}</p>
                  <div className="mt-2 grid min-w-0 gap-2 text-xs font-medium leading-5 text-[#475569] sm:grid-cols-[repeat(3,minmax(0,1fr))]">
                    <p className="min-w-0 break-words"><span className="font-semibold text-[#0A2540]">Collector:</span> {selectedOperationalCase.assignedCollector}</p>
                    <p className="min-w-0 break-words"><span className="font-semibold text-[#0A2540]">Status:</span> {selectedCaseStatus}</p>
                    <p className="min-w-0 break-words"><span className="font-semibold text-[#0A2540]">Type:</span> {formatManagerTimelineType(item.kind)}</p>
                  </div>
                </div>
              )) : (
                <p className="rounded-xl border border-dashed border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2 text-sm font-medium leading-6 text-[#0A2540]">Operational history will appear here as manager activity is captured.</p>
              )}
            </div>
          </div>
        </section>
      ) : null}
      </div>
      ) : null}

      {workspaceTab === "team" ? (
      <section className="grid gap-6 xl:grid-cols-2">
        <div className="grid gap-6 xl:col-span-2 xl:grid-cols-2">
          <article className="rounded-[1.75rem] border border-[rgba(255,106,43,.16)] bg-[rgba(7,18,31,.78)] p-5 shadow-[0_20px_48px_rgba(0,0,0,.18)]">
            <SectionHeading eyebrow="Manager Oversight" title="Portfolio health and team load" description="Use this view to watch workload, broken promises, open AR, and activity compliance without leaving the operating workspace." />
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {ticklerSnapshot.managerMetrics.map((metric) => (
                <div key={metric.label} className="rounded-2xl border border-[rgba(34,224,208,.14)] bg-[rgba(255,255,255,.04)] p-4">
                  <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">{metric.label}</p>
                  <p className="mt-2 font-display text-3xl font-bold text-[#F6F1E8]">{metric.value}</p>
                  <p className="mt-2 text-sm font-semibold leading-7 text-[#E8EEF4]">{metric.note}</p>
                </div>
              ))}
            </div>
          </article>

          <article className="rounded-[1.75rem] border border-[rgba(255,106,43,.16)] bg-[rgba(7,18,31,.78)] p-5 shadow-[0_20px_48px_rgba(0,0,0,.18)]">
            <SectionHeading eyebrow="Collector Performance" title="Workload by collector" description="Workload, calls, emails, and recovery dollars are summarized per collector so managers can rebalance quickly." />
            <div className="mt-5 grid gap-3">
              {managerCollectorQueue.map((collector) => (
                <div key={collector.collectorName} className="rounded-2xl border border-[rgba(34,224,208,.14)] bg-[rgba(255,255,255,.04)] p-4">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="font-display text-2xl font-bold text-[#F6F1E8]">{collector.collectorName}</h3>
                    <span className="rounded-full border border-[rgba(34,224,208,.2)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#22E0D0]">{collector.dueAccounts} assigned</span>
                  </div>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    <MiniStat label="Overdue" value={collector.overdueAccounts} note="Past-due touches waiting." />
                    <MiniStat label="Calls" value={collector.todayCalls} note="Collector call load." />
                    <MiniStat label="Emails" value={collector.todayEmails} note="Collector email load." />
                    <MiniStat label="Promises" value={collector.todayPromises} note="Promises needing confirmation." />
                    <MiniStat label="Follow Ups" value={collector.todayFollowUps} note="Reminder queue load." />
                    <MiniStat label="Escalations" value={collector.todayEscalations} note="Escalation work in motion." />
                  </div>
                </div>
              ))}
            </div>
          </article>
        </div>
      </section>
      ) : null}

      {workspaceTab === "activity" ? (
      <section className="grid gap-6 xl:grid-cols-[1.02fr_.98fr]">
        <article className="rounded-[1.75rem] border border-[rgba(255,106,43,.16)] bg-[rgba(7,18,31,.78)] p-5 shadow-[0_20px_48px_rgba(0,0,0,.18)]">
          <div ref={managerActivityHeadingRef} tabIndex={-1} className="outline-none">
            <SectionHeading eyebrow="Recent Customer Responses" title="Latest activity feed" description="Calls, emails, payments, and promise changes stay visible so the next action is always obvious." />
          </div>
          <div className="mt-5 grid gap-3">
            {(selectedCaseActivity.length ? selectedCaseActivity : recentResponses).length ? (selectedCaseActivity.length ? selectedCaseActivity : recentResponses).map((item) => (
              <div key={item.id} className="rounded-2xl border border-[rgba(34,224,208,.14)] bg-[rgba(255,255,255,.04)] p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">{item.kind}</p>
                    <h3 className="mt-1 font-display text-2xl font-bold text-[#F6F1E8]">{item.title}</h3>
                    <p className="mt-2 text-sm font-semibold leading-7 text-[#E8EEF4]">{item.customerName}</p>
                  </div>
                  <span className="rounded-full border border-[rgba(34,224,208,.2)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#22E0D0]">{formatDate(item.createdAt)}</span>
                </div>
                <p className="mt-2 text-sm font-semibold leading-7 text-[#CBD5E1]">{item.detail}</p>
              </div>
            )) : (
              <div className="rounded-2xl border border-dashed border-[rgba(255,106,43,.18)] bg-[rgba(255,255,255,.03)] p-4 text-sm font-semibold leading-7 text-[#E8EEF4]">No recent customer responses are available yet.</div>
            )}
          </div>
        </article>

        <article className="rounded-[1.75rem] border border-[rgba(255,106,43,.16)] bg-[rgba(7,18,31,.78)] p-5 shadow-[0_20px_48px_rgba(0,0,0,.18)]">
          <SectionHeading eyebrow="Operational Workflow" title="Next action guidance" description="These are the actions the workspace is already structured to support without introducing new workflow concepts." />
          <div className="mt-5 grid gap-3">
            {nextActionGuidanceCards.map((action) => (
              <button
                key={action.label}
                type="button"
                onClick={() => openSelectedCaseAccountRecovery(action.action, action.selectedTab || "activity")}
                className="focus-ring rounded-2xl border border-[rgba(34,224,208,.14)] bg-[rgba(255,255,255,.04)] p-4 text-left transition hover:border-[rgba(34,224,208,.42)] hover:bg-[rgba(255,255,255,.08)]"
              >
                <p className="text-xs font-black uppercase tracking-[0.16em] text-[#22E0D0]">{action.label}</p>
                <p className="mt-2 text-sm font-semibold leading-7 text-[#E8EEF4]">{action.note}</p>
              </button>
            ))}
          </div>
        </article>
      </section>
      ) : null}
    </div>
  );
}
function TicklerQueue({
  title,
  items,
}: {
  title: string;
  items: ReturnType<typeof buildARRecoveryTicklerDashboardSnapshot>["dueToday"];
}) {
  return (
    <div className="rounded-[1.5rem] border border-[rgba(34,224,208,.14)] bg-[rgba(255,255,255,.03)] p-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-display text-2xl font-bold text-[#F6F1E8]">{title}</h3>
        <span className="rounded-full border border-[rgba(255,106,43,.2)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#FF6A2B]">
          {items.length}
        </span>
      </div>
      <div className="ar-ia-scroll mt-4 grid gap-3">
        {items.length ? (
          items.map((item) => (
            <div key={item.id} className="rounded-2xl border border-[rgba(255,106,43,.12)] bg-[rgba(7,18,31,.72)] p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">{item.ticklerType}</p>
                  <h4 className="mt-1 font-display text-xl font-bold text-[#F6F1E8]">{item.title}</h4>
                </div>
                <span className="rounded-full border border-[rgba(34,224,208,.2)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#22E0D0]">
                  {item.priority}
                </span>
              </div>
              <p className="mt-2 text-sm font-semibold leading-7 text-[#E8EEF4]">
                {item.customerName} • {item.assignedCollector}
              </p>
              <p className="mt-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#CBD5E1]">
                Due {formatDate(item.dueDate)} • {item.status}
              </p>
            </div>
          ))
        ) : (
          <div className="rounded-2xl border border-dashed border-[rgba(255,106,43,.18)] bg-[rgba(255,255,255,.02)] p-4 text-sm font-semibold leading-7 text-[#E8EEF4]">
            No items in this queue.
          </div>
        )}
      </div>
    </div>
  );
}

function ticklerMetricIcon(label: string) {
  if (label.includes("Overdue") || label.includes("Critical") || label.includes("Escalation")) return <ShieldCheck className="size-5" aria-hidden="true" />;
  if (label.includes("Assigned") || label.includes("Customer")) return <Users className="size-5" aria-hidden="true" />;
  if (label.includes("Statement") || label.includes("Letter")) return <FileText className="size-5" aria-hidden="true" />;
  if (label.includes("Promise") || label.includes("Follow") || label.includes("Callback")) return <Clock3 className="size-5" aria-hidden="true" />;
  return <ClipboardList className="size-5" aria-hidden="true" />;
}

function getTicklerWorkReason(
  tickler: ReturnType<typeof buildARRecoveryTicklerDashboardSnapshot>["dueToday"][number],
  account?: ReturnType<typeof loadARRecoveryWorkspace>["accounts"][number] | null,
) {
  if (tickler.status === "Overdue") return "Reminder is overdue.";
  if (tickler.priority === "Critical") return "Critical priority follow-up.";
  if (tickler.ticklerType === "Promise Follow-Up" || tickler.ticklerType === "Promise Verification") return "Promise requires verification.";
  if (tickler.ticklerType === "Payment Verification") return "Payment requires confirmation.";
  if (tickler.ticklerType === "Statement Follow-Up") return "Statement follow-up is due.";
  if (tickler.ticklerType === "Letter Follow-Up") return "Letter response check is due.";
  if (account?.recoveryStatus === "Escalated") return "Account is escalated.";
  return "Scheduled collector follow-up.";
}

function getTicklerNextActionLabel(
  tickler: ReturnType<typeof buildARRecoveryTicklerDashboardSnapshot>["dueToday"][number],
) {
  if (tickler.ticklerType === "Promise Follow-Up" || tickler.ticklerType === "Promise Verification") return "Review promise";
  if (tickler.ticklerType === "Payment Verification") return "Verify payment";
  if (tickler.ticklerType === "Statement Follow-Up") return "Generate statement";
  if (tickler.ticklerType === "Letter Follow-Up") return "Send letter";
  if (tickler.ticklerType === "Escalation" || tickler.priority === "Critical") return "Escalate";
  if (tickler.ticklerType === "Callback" || tickler.ticklerType === "Customer Call") return "Call customer";
  return "Complete follow-up";
}

function TicklerQuickActionButton({
  label,
  note,
  icon,
  onClick,
}: {
  label: string;
  note: string;
  icon: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group min-w-0 max-w-full overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-3 text-left transition hover:border-[#14B8A6] hover:bg-[#F4FBFF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#14B8A6] focus-visible:ring-offset-2 focus-visible:ring-offset-white"
    >
      <div className="flex min-w-0 items-start justify-between gap-3">
        <p className="flex min-w-0 items-center gap-2 text-[0.8125rem] font-semibold text-[#0A2540]">
          <ARLightLineIcon compact icon={icon} />
          <span className="min-w-0 break-words">{label}</span>
        </p>
        <ArrowRight className="size-4 shrink-0 text-[#14B8A6] opacity-70 transition group-hover:translate-x-0.5 group-hover:opacity-100" aria-hidden="true" />
      </div>
      <p className="mt-1.5 min-w-0 break-words text-xs font-normal leading-5 text-[#475569]">{note}</p>
    </button>
  );
}

function TicklerQuickActionPanel({
  action,
  onClose,
}: {
  action: TicklerQuickActionState;
  onClose: () => void;
}) {
  return (
    <div className="mt-3 min-w-0 max-w-full overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <ARLightLineIcon icon={ticklerMetricIcon(action.label)} />
          <div className="min-w-0">
            <p className="break-words text-xs font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">{action.label}</p>
            <h4 className="mt-1 break-words text-base font-semibold text-[#0A2540]">{action.title}</h4>
            <p className="mt-1 break-words text-sm font-normal leading-6 text-[#475569]">{action.description}</p>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <span className="rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#0A2540]">
            {action.status}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-[#BFEAFF] bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#14B8A6] transition hover:border-[#14B8A6] hover:bg-[#F4FBFF]"
          >
            Dismiss
          </button>
        </div>
      </div>
      <div className="mt-4 grid min-w-0 gap-2 md:grid-cols-[repeat(2,minmax(0,1fr))]">
        {action.details.map((detail) => (
          <div key={detail} className="min-w-0 break-words rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2 text-xs font-semibold leading-5 text-[#0A2540]">
            {detail}
          </div>
        ))}
      </div>
      <div className="mt-4">
        {action.routeHref ? (
          <Link
            to={action.routeHref}
            className="inline-flex rounded-full border border-[#14B8A6] bg-[#14B8A6] px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-white transition hover:bg-[#0F766E]"
          >
            {action.primaryActionLabel}
          </Link>
        ) : (
          <span className="inline-flex rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#0A2540]">
            {action.primaryActionLabel}
          </span>
        )}
      </div>
    </div>
  );
}

function TicklerDetailSurface({
  tickler,
  account,
}: {
  tickler: ReturnType<typeof buildARRecoveryTicklerDashboardSnapshot>["dueToday"][number];
  account: ReturnType<typeof loadARRecoveryWorkspace>["accounts"][number] | null | undefined;
}) {
  return (
    <div className="grid min-w-0 max-w-full gap-3 md:grid-cols-[repeat(2,minmax(0,1fr))]">
      <DetailMetric label="Tickler type" value={tickler.ticklerType} icon={<ClipboardList className="size-5" aria-hidden="true" />} />
      <DetailMetric label="Assigned collector" value={tickler.assignedCollector} icon={<Users className="size-5" aria-hidden="true" />} />
      <DetailMetric label="Account status" value={account?.recoveryStatus || "Not captured"} icon={<ShieldCheck className="size-5" aria-hidden="true" />} />
      <DetailMetric label="Open balance" value={account ? formatARRecoveryCurrency(account.openBalanceCents) : "Not captured"} icon={<CircleDollarSign className="size-5" aria-hidden="true" />} />
    </div>
  );
}

function TicklerActivitySurface({
  activity,
  tickler,
}: {
  activity: ReturnType<typeof loadARRecoveryWorkspace>["activity"];
  tickler: ReturnType<typeof buildARRecoveryTicklerDashboardSnapshot>["dueToday"][number];
}) {
  return (
    <div className="grid min-w-0 max-w-full gap-3">
      {activity.length ? activity.map((entry) => (
        <div key={entry.id} className="min-w-0 max-w-full overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4">
          <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
            <p className="flex min-w-0 items-center gap-2 break-words text-xs font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">
              <ARLightLineIcon compact icon={<Activity className="size-4" aria-hidden="true" />} />
              {entry.kind}
            </p>
            <span className="rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#0A2540]">
              {formatDate(entry.createdAt)}
            </span>
          </div>
          <h3 className="mt-2 break-words text-base font-semibold text-[#0A2540]">{entry.title}</h3>
          <p className="mt-2 break-words text-sm font-normal leading-7 text-[#475569]">{entry.detail}</p>
        </div>
      )) : (
        <div className="rounded-2xl border border-dashed border-[#BFEAFF] bg-[#F4FBFF] p-4 text-sm font-semibold leading-7 text-[#0A2540]">
          No activity has been captured yet for {tickler.customerName}.
        </div>
      )}
    </div>
  );
}

function TicklerHistorySurface({
  tickler,
}: {
  tickler: ReturnType<typeof buildARRecoveryTicklerDashboardSnapshot>["dueToday"][number];
}) {
  const history = [
    { label: "Created", value: `${tickler.ticklerType} opened for ${tickler.customerName}` },
    { label: "Assigned", value: tickler.assignedCollector },
    { label: "Current Status", value: `${tickler.status} • ${tickler.priority}` },
  ];
  return (
    <div className="grid min-w-0 max-w-full gap-3">
      {history.map((entry) => (
        <div key={entry.label} className="min-w-0 max-w-full overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4">
          <p className="break-words text-xs font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">{entry.label}</p>
          <p className="mt-2 break-words text-sm font-normal leading-7 text-[#475569]">{entry.value}</p>
        </div>
      ))}
    </div>
  );
}

function ScoringRow({
  label,
  count,
  note,
}: {
  label: string;
  count: number;
  note: string;
}) {
  return (
    <div className="rounded-2xl border border-[#BFEAFF] bg-white p-4 shadow-[0_10px_24px_rgba(15,23,42,.04)]">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-medium text-[#334155]">{label}</p>
        <span className="font-display text-3xl font-bold text-[#0A2540]">{count}</span>
      </div>
      <p className="mt-2 text-sm font-normal leading-7 text-[#475569]">{note}</p>
    </div>
  );
}

function DetailMetric({ label, value, note, icon }: { label: string; value: string; note?: string; icon?: ReactNode }) {
  return (
    <div className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-3 shadow-[0_8px_20px_rgba(15,23,42,.035)]" title={value}>
      <div className="flex items-start justify-between gap-3">
        <p className="truncate text-xs font-medium text-[#475569]">{label}</p>
        {icon ? <ARLightLineIcon icon={icon} /> : null}
      </div>
      <p className="mt-1.5 min-w-0 break-words text-[0.95rem] font-semibold leading-6 text-[#0F172A]">{value}</p>
      {note ? <p className="mt-2 text-xs font-normal leading-6 text-[#64748B]">{note}</p> : null}
    </div>
  );
}

function GovernanceStatusItem({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="min-w-0 rounded-2xl border border-[#BFEAFF] bg-white p-3 shadow-[0_8px_20px_rgba(15,23,42,.035)]" title={`${label}: ${value}`}>
      <div className="flex items-center gap-2">
        <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] text-[#14B8A6]">
          {icon}
        </span>
        <div className="min-w-0">
          <p className="truncate text-xs font-medium text-[#475569]">{label}</p>
          <p className="mt-0.5 truncate text-sm font-semibold text-[#0A2540]">{value}</p>
        </div>
      </div>
    </div>
  );
}

function AccountInfoEditField({
  label,
  value,
  onChange,
  type = "text",
  options,
  list,
  multiline = false,
  inputRef,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: "text" | "email" | "number" | "date";
  options?: string[];
  list?: string;
  multiline?: boolean;
  inputRef?: RefObject<HTMLInputElement | HTMLSelectElement | null>;
}) {
  const controlClass = "focus-ring mt-1.5 min-w-0 w-full max-w-full rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold text-[#0A2540] outline-none transition placeholder:text-[#7EE7E0] focus:border-[#14B8A6]";
  const assignControlRef = (node: HTMLInputElement | HTMLSelectElement | null) => {
    if (inputRef) {
      inputRef.current = node;
    }
  };
  return (
    <label className="block min-w-0 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-3">
      <span className="block truncate text-xs font-medium text-[#334155]">{label}</span>
      {options ? (
        <select ref={assignControlRef} value={value} onChange={(event) => onChange(event.target.value)} className={controlClass}>
          {options.map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
      ) : multiline ? (
        <textarea value={value} onChange={(event) => onChange(event.target.value)} rows={3} className={`${controlClass} resize-y leading-6`} />
      ) : (
        <input ref={assignControlRef} value={value} onChange={(event) => onChange(event.target.value)} type={type} list={list} step={type === "number" ? "0.01" : undefined} className={controlClass} />
      )}
    </label>
  );
}

function CollectorAssignmentCombobox({
  options,
  currentCollectorName,
  selectedUserId,
  onSelect,
}: {
  options: ARRecoveryActiveInternalUserOption[];
  currentCollectorName: string;
  selectedUserId: string;
  onSelect: (user: ARRecoveryActiveInternalUserOption) => void;
}) {
  const comboboxId = useId();
  const listboxId = `${comboboxId}-listbox`;
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const selectedUser = options.find((user) => user.id === selectedUserId) || null;
  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return options;
    return options.filter((user) => {
      return user.name.toLowerCase().includes(normalizedQuery)
        || user.role.toLowerCase().includes(normalizedQuery)
        || user.title.toLowerCase().includes(normalizedQuery);
    });
  }, [options, query]);
  const activeOption = filteredOptions[activeIndex] || null;
  const selectedLabel = selectedUser?.name || "";
  const displayValue = isOpen ? query : selectedLabel;

  useEffect(() => {
    if (activeIndex > Math.max(filteredOptions.length - 1, 0)) {
      setActiveIndex(0);
    }
  }, [activeIndex, filteredOptions.length]);

  function selectOption(user: ARRecoveryActiveInternalUserOption) {
    onSelect(user);
    setQuery("");
    setIsOpen(false);
  }

  function handleKeyDown(event: ReactKeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setIsOpen(true);
      setActiveIndex((current) => Math.min(current + 1, Math.max(filteredOptions.length - 1, 0)));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setIsOpen(true);
      setActiveIndex((current) => Math.max(current - 1, 0));
    } else if (event.key === "Enter") {
      if (!isOpen) return;
      event.preventDefault();
      if (activeOption && !isSameCollectorName(activeOption.name, currentCollectorName)) {
        selectOption(activeOption);
      }
    } else if (event.key === "Escape") {
      event.preventDefault();
      setIsOpen(false);
      setQuery("");
    }
  }

  return (
    <div className="grid min-w-0 gap-1.5">
      <label htmlFor={comboboxId} className="text-[11px] font-black uppercase tracking-[0.16em] text-[#475569]">Assign New Collector</label>
      <div className="relative min-w-0">
        <input
          id={comboboxId}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-activedescendant={activeOption ? `${listboxId}-${activeOption.id}` : undefined}
          value={displayValue}
          onChange={(event) => {
            setQuery(event.target.value);
            setIsOpen(true);
            setActiveIndex(0);
          }}
          onFocus={() => {
            setIsOpen(true);
            setQuery("");
          }}
          onKeyDown={handleKeyDown}
          placeholder="Search eligible internal users"
          className="focus-ring min-h-11 w-full max-w-full rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540] placeholder:text-[#7EE7E0]"
        />
        {isOpen ? (
          <div className="absolute z-30 mt-2 max-h-64 w-full min-w-0 overflow-y-auto rounded-2xl border border-[#BFEAFF] bg-white p-2 shadow-[0_18px_42px_rgba(15,23,42,.14)]">
            {filteredOptions.length ? (
              <ul id={listboxId} role="listbox" aria-label="Eligible collectors" className="grid gap-2">
                {filteredOptions.map((user, index) => {
                  const isCurrent = isSameCollectorName(user.name, currentCollectorName);
                  const isSelected = user.id === selectedUserId;
                  return (
                    <li
                      key={user.id}
                      id={`${listboxId}-${user.id}`}
                      role="option"
                      aria-selected={isSelected}
                      className="min-w-0"
                    >
                      <button
                        type="button"
                        disabled={isCurrent}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => selectOption(user)}
                        onFocus={() => setActiveIndex(index)}
                        className={`focus-ring w-full min-w-0 rounded-xl border px-3 py-2 text-left transition ${isSelected ? "border-[#14B8A6] bg-[#ECFEFF]" : "border-[#BFEAFF] bg-[#F8FCFF]"} ${isCurrent ? "cursor-not-allowed opacity-70" : "hover:border-[#14B8A6] hover:bg-[#F4FBFF]"}`}
                      >
                        <span className="flex min-w-0 flex-wrap items-center justify-between gap-2">
                          <span className="min-w-0 break-words text-sm font-black text-[#0A2540]">{user.name}</span>
                          {isCurrent ? <span className="rounded-full border border-[#BFEAFF] bg-white px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] text-[#14B8A6]">Current</span> : null}
                        </span>
                        <span className="mt-1 block min-w-0 break-words text-xs font-semibold leading-5 text-[#475569]">{user.title || user.role}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="rounded-xl border border-dashed border-[#BFEAFF] bg-[#F4FBFF] px-3 py-3 text-sm font-semibold text-[#475569]" role="status">No eligible internal users found.</p>
            )}
          </div>
        ) : null}
      </div>
      {selectedUser ? (
        <p className="text-xs font-semibold leading-5 text-[#475569]">
          Selected: <span className="font-black text-[#0A2540]">{selectedUser.name}</span>{isSameCollectorName(selectedUser.name, currentCollectorName) ? " (current collector)" : ""}
        </p>
      ) : (
        <p className="text-xs font-semibold leading-5 text-[#475569]">Select a valid active internal user before completing reassignment.</p>
      )}
    </div>
  );
}

function ActionPill({ label, note, icon, onClick }: { label: string; note: string; icon?: ReactNode; onClick?: () => void }) {
  const content = (
    <>
      <p className="flex items-center gap-2 text-[0.8125rem] font-semibold text-[#0A2540]">
        {icon ? <ARLightLineIcon compact icon={icon} /> : null}
        {label}
      </p>
      <p className="mt-1 text-xs font-normal leading-5 text-[#475569]">{note}</p>
    </>
  );

  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
      className="focus-ring min-w-0 rounded-xl border border-[#BFEAFF] bg-white p-2.5 text-left transition hover:border-[#14B8A6] hover:bg-[#F4FBFF]"
    >
      {content}
    </button>
  ) : (
    <div className="min-w-0 rounded-xl border border-[#BFEAFF] bg-white p-2.5">
      {content}
    </div>
  );
}

function getSingleLetterActions() {
  return [
    { label: "Send Letter", note: "Send selected letter." },
    { label: "Preview Letter", note: "Open selected preview." },
    { label: "Edit Letter", note: "Open selected template." },
    { label: "Update Tracking", note: "Open selected tracking." },
    { label: "Archive Letter", note: "Open archive action." },
    { label: "Print Letter", note: "Print selected letter." },
    { label: "Download PDF", note: "Download selected letter." },
  ];
}

function getBulkLetterActions() {
  return [
    { label: "Send Bulk Letters", note: "Send filtered letter batches." },
    { label: "Generate Reminder Letters", note: "Generate reminder batches." },
    { label: "Archive Multiple Letters", note: "Archive selected batches." },
    { label: "Export Letters", note: "Export the letter queue." },
    { label: "Schedule Batch Delivery", note: "Schedule batch delivery." },
    { label: "Print Batch", note: "Print selected batches." },
  ];
}

function resolveLetterWorkflowAction(action: string): ARWorkflowDocumentAction | null {
  if (action.includes("Send")) return "Letter.Send";
  if (action.includes("Preview") || action.includes("Edit")) return "Letter.Preview";
  if (action.includes("Print")) return "Letter.Print";
  if (action.includes("Download") || action.includes("Export")) return "Letter.Download";
  if (action.includes("Archive")) return "Letter.Archive";
  if (action.includes("Generate") || action.includes("Reminder")) return "Letter.Generate";
  return null;
}

function resolveLetterDeliveryStatusForAction(action: string, deliveryStatus: string): "delivered" | "failed" | "not_configured" | "pending" | "sent" {
  const normalizedDelivery = deliveryStatus.toLowerCase();
  if (action.includes("Send") && normalizedDelivery.includes("delivered")) return "delivered";
  if (action.includes("Send") && normalizedDelivery.includes("failed")) return "failed";
  if (action.includes("Send")) return "sent";
  if (normalizedDelivery.includes("failed")) return "failed";
  if (normalizedDelivery.includes("delivered")) return "delivered";
  if (normalizedDelivery.includes("not configured")) return "not_configured";
  return "pending";
}

function buildLetterDocumentRecordForAction(
  letter: NonNullable<ReturnType<typeof buildARRecoveryCollectionLetterCenterSnapshot>["selectedLetter"]>,
  action: string,
): Record<string, unknown> {
  const now = new Date().toISOString();
  const workflowAction = resolveLetterWorkflowAction(action);
  return {
    ...letter,
    status: workflowAction === "Letter.Archive" ? "Archived" : letter.status,
    updatedAt: now,
    generatedBy: letter.sentBy || letter.collector || "Letter Center",
    generatedAt: letter.createdAt || now,
    letterType: letter.template,
    recipient: letter.customerName,
    relatedInvoices: [],
    relatedPromise: "",
    relatedTask: "",
    additionalNotes: letter.notes || action,
    previewBody: letter.notes || "",
    signature: letter.clientCompany || "CollabRecover AR Solutions",
  };
}

function buildLetterCenterActorContext(letter: NonNullable<ReturnType<typeof buildARRecoveryCollectionLetterCenterSnapshot>["selectedLetter"]>) {
  return {
    actorId: letter.sentBy || letter.collector || "letter-center",
    actorName: letter.sentBy || letter.collector || "Letter Center",
    actorEmail: "",
    actorRole: "Collector",
    permissionLevel: "collector_write",
  };
}

function getSingleStatementActions() {
  return [
    { label: "Send Statement", note: "Send selected statement." },
    { label: "Preview Statement", note: "Open selected preview." },
    { label: "Print Statement", note: "Print selected statement." },
    { label: "Download PDF", note: "Download selected statement." },
    { label: "Update Tracking", note: "Open selected tracking." },
    { label: "Archive Statement", note: "Open archive action." },
  ];
}

function getBulkStatementActions() {
  return [
    { label: "Send Bulk Statements", note: "Send filtered statement batches." },
    { label: "Schedule Batch Delivery", note: "Schedule statement batches." },
    { label: "Export Statements", note: "Export the statement queue." },
    { label: "Print Batch", note: "Print selected batches." },
  ];
}

function resolveStatementWorkflowAction(action: string): ARWorkflowDocumentAction | null {
  if (action.includes("Send") || action.includes("Schedule")) return "Statement.Send";
  if (action.includes("Preview") || action.includes("Tracking")) return "Statement.Preview";
  if (action.includes("Print")) return "Statement.Print";
  if (action.includes("Download") || action.includes("Export")) return "Statement.Download";
  if (action.includes("Archive")) return "Statement.Archive";
  if (action.includes("Generate")) return "Statement.Generate";
  return null;
}

function resolveStatementDeliveryStatusForAction(action: string, deliveryStatus: string): "delivered" | "failed" | "not_configured" | "pending" | "sent" {
  const normalizedDelivery = deliveryStatus.toLowerCase();
  if ((action.includes("Send") || action.includes("Schedule")) && normalizedDelivery.includes("delivered")) return "delivered";
  if ((action.includes("Send") || action.includes("Schedule")) && normalizedDelivery.includes("failed")) return "failed";
  if (action.includes("Send") || action.includes("Schedule")) return "sent";
  if (normalizedDelivery.includes("failed")) return "failed";
  if (normalizedDelivery.includes("delivered")) return "delivered";
  if (normalizedDelivery.includes("not configured")) return "not_configured";
  return "pending";
}

function buildStatementDocumentRecordForAction(
  statement: NonNullable<ReturnType<typeof buildARRecoveryStatementCenterSnapshot>["selectedStatement"]>,
  action: string,
): Record<string, unknown> {
  const now = new Date().toISOString();
  const workflowAction = resolveStatementWorkflowAction(action);
  return {
    ...statement,
    status: workflowAction === "Statement.Archive" ? "Archived" : statement.status,
    updatedAt: now,
    generatedBy: statement.sentBy || statement.collector || "Statement Center",
    generatedAt: statement.createdAt || now,
    statementType: statement.statementNumber,
    relatedInvoices: statement.lineItems?.map((item) => item.invoiceNumber) || [],
    relatedPromise: "",
    relatedTask: "",
    statementNotes: statement.notes || action,
    deliveryOption: "Statement Center",
    previewBody: statement.notes || "",
  };
}

function buildStatementCenterActorContext(statement: NonNullable<ReturnType<typeof buildARRecoveryStatementCenterSnapshot>["selectedStatement"]>) {
  return {
    actorId: statement.sentBy || statement.collector || "statement-center",
    actorName: statement.sentBy || statement.collector || "Statement Center",
    actorEmail: "",
    actorRole: "Collector",
    permissionLevel: "collector_write",
  };
}

function LetterActionButton({ label, note, onClick }: { label: string; note: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="statement-center-action-card group min-w-0 max-w-full overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-3 text-left shadow-[0_8px_20px_rgba(15,23,42,.035)] transition hover:-translate-y-0.5 hover:border-[#14B8A6] hover:bg-[#F4FBFF] hover:shadow-[0_12px_26px_rgba(20,184,166,.12)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#14B8A6] focus-visible:ring-offset-2 focus-visible:ring-offset-white"
    >
      <div className="flex min-w-0 items-start justify-between gap-3">
        <p className="flex min-w-0 items-center gap-2 text-[0.8125rem] font-semibold text-[#0A2540]">
          <ARLightLineIcon compact icon={resolveLetterActionIcon(label)} />
          <span className="break-words">{label}</span>
        </p>
        <ArrowRight className="size-4 shrink-0 text-[#14B8A6] opacity-70 transition group-hover:translate-x-1 group-hover:opacity-100" aria-hidden="true" />
      </div>
      <p className="mt-1.5 break-words text-xs font-normal leading-5 text-[#475569]">{note}</p>
    </button>
  );
}

function StatementActionButton({ label, note, onClick, selected = false }: { label: string; note: string; onClick: () => void; selected?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`ar-interaction-control group min-w-0 max-w-full overflow-hidden rounded-2xl border p-3 text-left shadow-[0_8px_20px_rgba(15,23,42,.035)] transition hover:-translate-y-0.5 hover:border-[#14B8A6] hover:bg-[#F4FBFF] hover:shadow-[0_12px_26px_rgba(20,184,166,.12)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#14B8A6] focus-visible:ring-offset-2 focus-visible:ring-offset-white ${selected ? "ar-interaction-selected" : "border-[#BFEAFF] bg-white"}`}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="flex min-w-0 items-center gap-2 text-[0.8125rem] font-semibold text-[#0A2540]">
          <ARLightLineIcon compact icon={resolveStatementActionIcon(label)} />
          <span className="break-words">{label}</span>
        </p>
        {selected ? <span className="ar-interaction-selected-marker shrink-0">Selected</span> : <ArrowRight className="size-4 shrink-0 text-[#14B8A6] opacity-70 transition group-hover:translate-x-1 group-hover:opacity-100" aria-hidden="true" />}
      </div>
      <p className="mt-1.5 break-words text-xs font-normal leading-5 text-[#475569]">{note}</p>
    </button>
  );
}

function resolveLetterActionIcon(label: string) {
  if (label.includes("Bulk")) return <Users className="size-4" aria-hidden="true" />;
  if (label.includes("Tracking") || label.includes("Track")) return <Activity className="size-4" aria-hidden="true" />;
  if (label.includes("Archive")) return <ShieldCheck className="size-4" aria-hidden="true" />;
  if (label.includes("Reminder")) return <Clock3 className="size-4" aria-hidden="true" />;
  if (label.includes("Custom") || label.includes("Edit") || label.includes("Preview") || label.includes("Print") || label.includes("Download") || label.includes("Export")) return <FileText className="size-4" aria-hidden="true" />;
  return <Bolt className="size-4" aria-hidden="true" />;
}

function resolveStatementActionIcon(label: string) {
  if (label.includes("Bulk") || label.includes("Batch")) return <Users className="size-4" aria-hidden="true" />;
  if (label.includes("Tracking") || label.includes("Track")) return <Activity className="size-4" aria-hidden="true" />;
  if (label.includes("Archive")) return <ShieldCheck className="size-4" aria-hidden="true" />;
  if (label.includes("Preview") || label.includes("Print") || label.includes("Download") || label.includes("Export")) return <FileText className="size-4" aria-hidden="true" />;
  return <Bolt className="size-4" aria-hidden="true" />;
}

function LetterActionPanel({
  action,
  letter,
  onClose,
}: {
  action: string;
  letter: NonNullable<ReturnType<typeof buildARRecoveryCollectionLetterCenterSnapshot>["selectedLetter"]>;
  onClose: () => void;
}) {
  const actionContext = action.includes("Bulk")
    ? "Bulk letter action opened for the active letter queue."
    : action.includes("Tracking")
      ? "Tracking action opened for the selected letter."
      : action.includes("Archive")
        ? "Archive action opened for the selected letter."
      : action.includes("Reminder")
          ? "Reminder letter action opened with the selected account context."
          : action.includes("Custom")
            ? "Custom letter action opened with the selected account context."
            : "Letter action opened for the selected account.";

  return (
    <div className="min-w-0 max-w-full overflow-hidden rounded-[1.5rem] border border-[#BFEAFF] bg-[#F4FBFF] p-4 shadow-[0_14px_30px_rgba(15,23,42,.06)]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-2 break-words text-sm font-semibold text-[#0A2540]">
            <ARLightLineIcon icon={resolveLetterActionIcon(action)} />
            {action}
          </p>
          <p className="mt-1 break-words text-xs font-normal leading-5 text-[#475569]">{actionContext}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-full border border-[#BFEAFF] bg-white px-3 py-1.5 text-xs font-semibold text-[#0A2540] transition hover:border-[#14B8A6] hover:text-[#0F766E] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#14B8A6] focus-visible:ring-offset-2 focus-visible:ring-offset-[#F4FBFF]"
        >
          Close
        </button>
      </div>
      <div className="mt-4 grid min-w-0 gap-3 md:grid-cols-[repeat(2,minmax(0,1fr))] xl:grid-cols-[repeat(4,minmax(0,1fr))]">
        <DetailMetric label="Selected Letter" value={letter.template} />
        <DetailMetric label="Customer" value={letter.customerName} />
        <DetailMetric label="Related Account" value={letter.customerNumber} />
        <DetailMetric label="Current Status" value={letter.status} />
      </div>
    </div>
  );
}

function StatementActionPanel({
  action,
  statement,
  onClose,
}: {
  action: string;
  statement: NonNullable<ReturnType<typeof buildARRecoveryStatementCenterSnapshot>["selectedStatement"]>;
  onClose: () => void;
}) {
  const actionContext = action.includes("Bulk") || action.includes("Batch")
    ? "Bulk statement action opened for the active statement queue."
    : action.includes("Tracking")
      ? "Tracking action opened for the selected statement."
      : action.includes("Archive")
        ? "Archive action opened for the selected statement."
        : "Statement action opened for the selected account.";

  return (
    <div className="min-w-0 max-w-full overflow-hidden rounded-[1.5rem] border border-[#BFEAFF] bg-[#F4FBFF] p-4 shadow-[0_14px_30px_rgba(15,23,42,.06)]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-2 break-words text-sm font-semibold text-[#0A2540]">
            <ARLightLineIcon icon={resolveStatementActionIcon(action)} />
            {action}
          </p>
          <p className="mt-1 break-words text-xs font-normal leading-5 text-[#475569]">{actionContext}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-full border border-[#BFEAFF] bg-white px-3 py-1.5 text-xs font-semibold text-[#0A2540] transition hover:border-[#14B8A6] hover:text-[#0F766E] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#14B8A6] focus-visible:ring-offset-2 focus-visible:ring-offset-[#F4FBFF]"
        >
          Close
        </button>
      </div>
      <div className="mt-4 grid min-w-0 gap-3 md:grid-cols-[repeat(2,minmax(0,1fr))] xl:grid-cols-[repeat(4,minmax(0,1fr))]">
        <DetailMetric label="Selected Statement" value={statement.statementNumber} />
        <DetailMetric label="Customer" value={statement.customerName} />
        <DetailMetric label="Current Status" value={statement.status} />
        <DetailMetric label="Balance" value={formatARRecoveryCurrency(statement.balanceCents)} />
      </div>
    </div>
  );
}

function StatementBulkSendWorkflow({
  statements,
  actor,
  companyId,
  onClose,
  onStatementsUpdated,
  onTimelineEntries,
}: {
  statements: StatementCenterRecord[];
  actor: ARRecoveryActor;
  companyId: string;
  onClose: () => void;
  onStatementsUpdated: (updates: Record<string, StatementCenterRecord>) => void;
  onTimelineEntries: (entries: StatementBulkTimelineEntry[]) => void;
}) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [step, setStep] = useState<StatementBulkStep>("select");
  const [isSending, setIsSending] = useState(false);
  const [batchId, setBatchId] = useState("");
  const [completedAt, setCompletedAt] = useState("");
  const [results, setResults] = useState<StatementBulkResult[]>([]);
  const selectedStatements = statements.filter((statement) => selectedIds.includes(statement.id));
  const validations = selectedStatements.map((statement) => validateBulkStatementEligibility(statement, actor));
  const eligibleStatements = validations.filter((validation) => validation.eligible).map((validation) => validation.statement);
  const ineligibleStatements = validations.filter((validation) => !validation.eligible);
  const selectedBalance = selectedStatements.reduce((sum, statement) => sum + statement.balanceCents, 0);
  const customersRepresented = new Set(selectedStatements.map((statement) => statement.customerName)).size;
  const recipientCount = new Set(eligibleStatements.map((statement) => getStatementBulkRecipient(statement))).size;
  const eligibleQueue = statements.filter((statement) => validateBulkStatementEligibility(statement, actor).eligible);
  const resultCounts = results.reduce(
    (counts, result) => ({
      success: counts.success + (result.status === "success" ? 1 : 0),
      failed: counts.failed + (result.status === "failed" ? 1 : 0),
      skipped: counts.skipped + (result.status === "skipped" ? 1 : 0),
    }),
    { success: 0, failed: 0, skipped: 0 },
  );
  const failedRetryIds = results
    .filter((result) => result.status === "failed")
    .map((result) => result.statementId);
  const summaryMetrics = step === "results"
    ? [
        { label: "Successful", value: String(resultCounts.success) },
        { label: "Failed", value: String(resultCounts.failed) },
        { label: "Skipped", value: String(resultCounts.skipped) },
      ]
    : [
        { label: "Selected", value: String(selectedStatements.length) },
        { label: "Eligible", value: String(eligibleStatements.length) },
        { label: "Ineligible", value: String(ineligibleStatements.length) },
      ];

  function toggleStatement(statementId: string) {
    setSelectedIds((current) => current.includes(statementId) ? current.filter((id) => id !== statementId) : [...current, statementId]);
  }

  function selectAllEligible() {
    setSelectedIds(eligibleQueue.map((statement) => statement.id));
  }

  function clearSelection() {
    setSelectedIds([]);
    setResults([]);
    setBatchId("");
    setCompletedAt("");
    setStep("select");
  }

  async function sendBatch() {
    if (!eligibleStatements.length || isSending) return;
    setIsSending(true);
    setStep("sending");
    const nextBatchId = `statement-batch-${Date.now()}`;
    const finishedAt = new Date().toISOString();
    const batchResults: StatementBulkResult[] = ineligibleStatements.map((validation) => ({
      statementId: validation.statement.id,
      statementNumber: validation.statement.statementNumber,
      customerName: validation.statement.customerName,
      recipient: getStatementBulkRecipient(validation.statement) || "Not Captured",
      status: "skipped",
      reason: validation.reason || "Statement was not eligible for bulk send.",
    }));
    const statementUpdates: Record<string, StatementCenterRecord> = {};
    const timelineEntries: StatementBulkTimelineEntry[] = [
      {
        id: `${nextBatchId}-timeline`,
        title: `Bulk statement batch ${nextBatchId} completed`,
        detail: `${eligibleStatements.length} eligible, ${ineligibleStatements.length} ineligible, ${selectedStatements.length} selected.`,
        createdAt: finishedAt,
      },
    ];
    let storedStatementWorkflows = loadStoredARRecoveryStatementWorkflows();

    for (const statement of eligibleStatements) {
      const recipient = getStatementBulkRecipient(statement);
      const statementLabel = statement.statementNumber;
      const delivery = await deliverARRecoveryEmail({
        recordType: "statement",
        recipientEmail: recipient,
        recipientName: statement.mergeFields.contactName || statement.customerName,
        subject: `${statement.statementNumber} - ${statement.customerName}`,
        body: buildStatementBulkPlainText(statement, nextBatchId),
        htmlBody: buildStatementBulkHtml(statement, nextBatchId),
        accountName: statement.customerName,
        customerNumber: statement.customerNumber,
        recordLabel: statementLabel,
      });
      const sentAt = delivery.sentAt || new Date().toISOString();
      const deliveryFailed = delivery.status !== "sent";
      const updatedStatement: StatementCenterRecord = {
        ...statement,
        status: delivery.status === "sent" ? "Sent" : delivery.status === "not_configured" ? "Delivery Not Configured" : "Failed",
        sentAt: delivery.status === "sent" ? sentAt : statement.sentAt,
        dateSent: delivery.status === "sent" ? sentAt.slice(0, 10) : statement.dateSent,
        timeSent: delivery.status === "sent" ? formatCapturedTime(sentAt) : statement.timeSent,
        sentBy: actor.name || statement.sentBy || "Statement Center",
        deliveryStatus: delivery.status === "sent" ? "Sent" : "Failed",
        updatedAt: sentAt,
        notes: delivery.status === "sent"
          ? `Bulk batch ${nextBatchId} accepted for ${recipient}. Provider: ${delivery.provider}${delivery.providerMessageId ? ` (${delivery.providerMessageId})` : ""}.`
          : `Bulk batch ${nextBatchId} failed for ${recipient}: ${delivery.providerError || delivery.message}`,
      };
      statementUpdates[statement.id] = updatedStatement;
      batchResults.push({
        statementId: statement.id,
        statementNumber: statement.statementNumber,
        customerName: statement.customerName,
        recipient,
        status: deliveryFailed ? "failed" : "success",
        reason: deliveryFailed ? delivery.providerError || delivery.message || "Statement delivery failed." : "Statement delivery accepted by provider.",
        providerMessageId: delivery.providerMessageId,
      });
      timelineEntries.push({
        id: `${nextBatchId}-${statement.id}`,
        title: `${statement.statementNumber} ${deliveryFailed ? "failed" : "sent"}`,
        detail: `${statement.customerName} • ${deliveryFailed ? delivery.providerError || delivery.message : `sent to ${recipient}`}`,
        createdAt: sentAt,
      });
      const { documentWorkflows } = publishDocumentWorkflowAction({
        tenantId: companyId,
        clientId: companyId,
        customerId: statement.customerNumber,
        accountId: statement.accountId,
        customerName: statement.customerName,
        customerNumber: statement.customerNumber,
        documentId: statement.id,
        documentType: "statement",
        documentRecord: {
          ...buildStatementDocumentRecordForAction(updatedStatement, "Send Bulk Statements"),
          batchId: nextBatchId,
          recipient,
          providerMessageId: delivery.providerMessageId,
          deliveryError: delivery.providerError,
        },
        action: "Statement.Send",
        actor: buildStatementBulkActorContext(actor, statement),
        sourceWorkspace: "Statement Center",
        initiatedFromRoute: "/admin/ar-recovery/statements",
        deliveryStatus: deliveryFailed ? "failed" : "sent",
      });
      const projectedStatements = documentWorkflows
        .map((record) => record as unknown)
        .filter(isARRecoveryStatementWorkflowRecord);
      const projectedIds = new Set(projectedStatements.map((record) => record.id));
      storedStatementWorkflows = [...projectedStatements, ...storedStatementWorkflows.filter((record) => !projectedIds.has(record.id))]
        .sort((a, b) => getDateTime(b.updatedAt || b.createdAt) - getDateTime(a.updatedAt || a.createdAt));
    }

    saveStoredARRecoveryStatementWorkflows(storedStatementWorkflows);
    onStatementsUpdated(statementUpdates);
    onTimelineEntries(timelineEntries);
    setBatchId(nextBatchId);
    setCompletedAt(finishedAt);
    setResults(batchResults);
    setStep("results");
    setIsSending(false);
  }

  function retryFailed() {
    setSelectedIds(failedRetryIds);
    setResults([]);
    setBatchId("");
    setCompletedAt("");
    setStep("review");
  }

  return (
    <div className="min-w-0 max-w-full overflow-hidden rounded-[1.5rem] border border-[#BFEAFF] bg-[#F4FBFF] p-4 shadow-[0_14px_30px_rgba(15,23,42,.06)]" aria-live={isSending ? "polite" : "off"}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-2 break-words text-sm font-semibold text-[#0A2540]">
            <ARLightLineIcon icon={resolveStatementActionIcon("Send Bulk Statements")} />
            Send Bulk Statements
          </p>
          <p className="mt-1 break-words text-xs font-normal leading-5 text-[#475569]">
            Select eligible statements, review the batch, then send through the existing AR Recovery statement delivery service.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={isSending}
          className="rounded-full border border-[#BFEAFF] bg-white px-3 py-1.5 text-xs font-semibold text-[#0A2540] transition hover:border-[#14B8A6] hover:text-[#0F766E] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#14B8A6] focus-visible:ring-offset-2 focus-visible:ring-offset-[#F4FBFF] disabled:cursor-not-allowed disabled:opacity-70"
        >
          Close
        </button>
      </div>

      <div className="mt-4 grid min-w-0 gap-3 md:grid-cols-[repeat(3,minmax(0,1fr))]">
        {summaryMetrics.map((metric) => (
          <DetailMetric key={metric.label} label={metric.label} value={metric.value} />
        ))}
      </div>

      {step === "select" ? (
        <div className="mt-4 grid min-w-0 gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={selectAllEligible} className="rounded-full border border-[#14B8A6] bg-white px-3 py-1.5 text-xs font-semibold text-[#0A2540] transition hover:bg-[#E6FFFB] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#14B8A6]">Select all eligible</button>
            <button type="button" onClick={clearSelection} className="rounded-full border border-[#BFEAFF] bg-white px-3 py-1.5 text-xs font-semibold text-[#0A2540] transition hover:border-[#14B8A6] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#14B8A6]">Clear selection</button>
          </div>
          <div className="grid min-w-0 gap-2">
            {statements.map((statement) => {
              const selected = selectedIds.includes(statement.id);
              const validation = validateBulkStatementEligibility(statement, actor);
              return (
                <label
                  key={statement.id}
                  className={`grid min-w-0 cursor-pointer gap-3 rounded-2xl border p-3 transition md:grid-cols-[auto_minmax(0,1fr)_auto] ${selected ? "ar-interaction-selected" : "border-[#BFEAFF] bg-white hover:border-[#14B8A6]"}`}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => toggleStatement(statement.id)}
                    className="mt-1 size-4 accent-[#14B8A6]"
                    aria-label={`Select ${statement.statementNumber}`}
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-[#0A2540]">{statement.statementNumber} • {statement.customerName}</span>
                    <span className="mt-1 block text-xs font-semibold leading-5 text-[#475569]">
                      {statement.customerNumber} • {formatARRecoveryCurrency(statement.balanceCents)} • {getStatementBulkRecipient(statement) || "No recipient"} • {statement.deliveryStatus}
                    </span>
                    {!validation.eligible ? <span className="mt-1 block text-xs font-semibold leading-5 text-[#A33A12]">{validation.reason}</span> : null}
                  </span>
                  <span className={`self-start rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] ${validation.eligible ? "border-[#14B8A6] bg-[#E6FFFB] text-[#0A2540]" : "border-[#FED7AA] bg-[#FFF7ED] text-[#A33A12]"}`}>
                    {validation.eligible ? "Eligible" : "Ineligible"}
                  </span>
                </label>
              );
            })}
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-full border border-[#BFEAFF] bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#0A2540]">Cancel</button>
            <button
              type="button"
              onClick={() => setStep("review")}
              disabled={!selectedStatements.length}
              className="rounded-full border border-[#14B8A6] bg-[#14B8A6] px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-white transition hover:bg-[#0F766E] disabled:cursor-not-allowed disabled:border-[#BFEAFF] disabled:bg-white disabled:text-[#64748B]"
            >
              Review Batch
            </button>
          </div>
        </div>
      ) : null}

      {step === "review" ? (
        <div className="mt-4 grid min-w-0 gap-3">
          <div className="grid min-w-0 gap-3 md:grid-cols-[repeat(3,minmax(0,1fr))]">
            <DetailMetric label="Customers" value={String(customersRepresented)} />
            <DetailMetric label="Total Balance" value={formatARRecoveryCurrency(selectedBalance)} />
            <DetailMetric label="Unique recipients" value={String(recipientCount)} />
            <DetailMetric label="Delivery Method" value="Email" />
            <DetailMetric label="Batch Eligibility" value={`${eligibleStatements.length} eligible / ${ineligibleStatements.length} ineligible`} />
            <DetailMetric label="Duplicate Send Protection" value="Enabled" />
          </div>
          {ineligibleStatements.length ? (
            <div className="rounded-2xl border border-[#FED7AA] bg-[#FFF7ED] p-3">
              <p className="text-xs font-black uppercase tracking-[0.14em] text-[#A33A12]">Ineligible statements</p>
              <div className="mt-2 grid gap-2">
                {ineligibleStatements.map((validation) => (
                  <p key={validation.statement.id} className="text-sm font-semibold leading-6 text-[#7C2D12]">
                    {validation.statement.statementNumber}: {validation.reason}
                  </p>
                ))}
              </div>
            </div>
          ) : null}
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" onClick={() => setStep("select")} className="rounded-full border border-[#BFEAFF] bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#0A2540]">Back</button>
            <button type="button" onClick={onClose} className="rounded-full border border-[#BFEAFF] bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#0A2540]">Cancel</button>
            <button
              type="button"
              onClick={sendBatch}
              disabled={!eligibleStatements.length || isSending}
              className="rounded-full border border-[#14B8A6] bg-[#14B8A6] px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-white transition hover:bg-[#0F766E] disabled:cursor-not-allowed disabled:border-[#BFEAFF] disabled:bg-white disabled:text-[#64748B]"
            >
              Confirm Send
            </button>
          </div>
        </div>
      ) : null}

      {step === "sending" ? (
        <div className="mt-4 rounded-2xl border border-[#BFEAFF] bg-white p-4 text-sm font-semibold leading-6 text-[#0A2540]" role="status">
          Sending statements... Duplicate submissions are disabled while this batch is processing.
        </div>
      ) : null}

      {step === "results" ? (
        <div className="mt-4 grid min-w-0 gap-3">
          <div className="rounded-2xl border border-[#BFEAFF] bg-white p-3">
            <p className="text-xs font-black uppercase tracking-[0.14em] text-[#14B8A6]">Batch results</p>
            <p className="mt-1 text-sm font-semibold text-[#0A2540]">Batch ID: {batchId}</p>
            <p className="text-sm font-semibold text-[#0A2540]">Completed: {completedAt ? `${formatCapturedDate(completedAt)} ${formatCapturedTime(completedAt)}` : "Not Captured"}</p>
          </div>
          <div className="grid min-w-0 gap-2">
            {results.map((result) => (
              <div key={`${result.statementId}-${result.status}`} className="rounded-2xl border border-[#BFEAFF] bg-white p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <p className="text-sm font-semibold text-[#0A2540]">{result.statementNumber} • {result.customerName}</p>
                  <span className={`rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] ${result.status === "success" ? "border-[#14B8A6] bg-[#E6FFFB] text-[#0A2540]" : result.status === "failed" ? "border-[#FED7AA] bg-[#FFF7ED] text-[#A33A12]" : "border-[#BFEAFF] bg-[#F8FAFC] text-[#475569]"}`}>
                    {result.status}
                  </span>
                </div>
                <p className="mt-1 text-xs font-semibold leading-5 text-[#475569]">Recipient: {result.recipient}</p>
                <p className="mt-1 text-xs font-semibold leading-5 text-[#475569]">{result.reason}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-full border border-[#BFEAFF] bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#0A2540]">Close</button>
            <button type="button" onClick={() => setStep("select")} className="rounded-full border border-[#BFEAFF] bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#0A2540]">View Statement History</button>
            <button
              type="button"
              onClick={retryFailed}
              disabled={!failedRetryIds.length}
              className="rounded-full border border-[#14B8A6] bg-[#14B8A6] px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-white transition hover:bg-[#0F766E] disabled:cursor-not-allowed disabled:border-[#BFEAFF] disabled:bg-white disabled:text-[#64748B]"
            >
              Retry Failed Statements
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function validateBulkStatementEligibility(statement: StatementCenterRecord, actor: ARRecoveryActor) {
  const recipient = getStatementBulkRecipient(statement);
  const normalizedStatus = statement.status.toLowerCase();
  const normalizedDelivery = statement.deliveryStatus.toLowerCase();
  let reason = "";
  if (!statement.id) reason = "Statement record is missing.";
  else if (!["created", "queued", "generated", "failed", "delivery not configured"].includes(normalizedStatus)) reason = `Status is ${statement.status}; only created, queued, generated, failed, or delivery-not-configured statements can be sent in bulk.`;
  else if (["sent", "delivered", "opened", "downloaded"].includes(normalizedDelivery)) reason = `Delivery status is ${statement.deliveryStatus}; already-issued statements are not selected automatically.`;
  else if (!isValidARRecoveryEmail(recipient)) reason = "A valid recipient email is required.";
  else if (actor.role && !AR_RECOVERY_WRITE_ROLES.has(actor.role)) reason = "Current user does not have statement send permission.";
  return { statement, eligible: !reason, reason };
}

function StatementCsvExportWorkflow({
  statements,
  actor,
  companyId,
  onClose,
  onViewHistory,
  onTimelineEntries,
}: {
  statements: StatementCenterRecord[];
  actor: ARRecoveryActor;
  companyId: string;
  onClose: () => void;
  onViewHistory: () => void;
  onTimelineEntries: (entries: StatementBulkTimelineEntry[]) => void;
}) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [step, setStep] = useState<StatementCsvExportStep>("select");
  const [isGenerating, setIsGenerating] = useState(false);
  const [exportId, setExportId] = useState("");
  const [completedAt, setCompletedAt] = useState("");
  const [generatedFilename, setGeneratedFilename] = useState("");
  const [csvContent, setCsvContent] = useState("");
  const [results, setResults] = useState<StatementCsvExportResult[]>([]);
  const selectedStatements = statements.filter((statement) => selectedIds.includes(statement.id));
  const validations = selectedStatements.map((statement) => validateStatementCsvExportEligibility(statement, actor));
  const exportableStatements = validations.filter((validation) => validation.eligible).map((validation) => validation.statement);
  const unavailableStatements = validations.filter((validation) => !validation.eligible);
  const exportableQueue = statements.filter((statement) => validateStatementCsvExportEligibility(statement, actor).eligible);
  const selectedBalance = selectedStatements.reduce((sum, statement) => sum + statement.balanceCents, 0);
  const customersRepresented = new Set(selectedStatements.map((statement) => statement.customerName)).size;
  const estimatedFilename = buildStatementCsvExportFilename(new Date());

  function toggleStatement(statementId: string) {
    setSelectedIds((current) => current.includes(statementId) ? current.filter((id) => id !== statementId) : [...current, statementId]);
  }

  function selectAllExportable() {
    setSelectedIds(exportableQueue.map((statement) => statement.id));
  }

  function clearSelection() {
    setSelectedIds([]);
    setResults([]);
    setExportId("");
    setCompletedAt("");
    setGeneratedFilename("");
    setCsvContent("");
    setStep("select");
  }

  function downloadCsv(content: string, filename: string) {
    if (typeof window === "undefined" || typeof document === "undefined") return false;
    const blob = new Blob([`\uFEFF${content}`], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
    return true;
  }

  function generateExport() {
    if (!exportableStatements.length || isGenerating) return;
    setIsGenerating(true);
    setStep("generating");
    const nextExportId = `statement-export-${Date.now()}`;
    const finishedAt = new Date().toISOString();
    const filename = buildStatementCsvExportFilename(new Date(finishedAt));
    const csv = buildStatementCsvExportContent(exportableStatements);
    const exportedResults: StatementCsvExportResult[] = exportableStatements.map((statement) => ({
      statementId: statement.id,
      statementNumber: statement.statementNumber,
      customerName: statement.customerName,
      status: "exported",
      reason: "Statement metadata included in CSV export.",
    }));
    const skippedResults: StatementCsvExportResult[] = unavailableStatements.map((validation) => ({
      statementId: validation.statement.id,
      statementNumber: validation.statement.statementNumber,
      customerName: validation.statement.customerName,
      status: "skipped",
      reason: validation.reason || "Statement was not exportable.",
    }));
    let storedStatementWorkflows = loadStoredARRecoveryStatementWorkflows();
    const timelineEntries: StatementBulkTimelineEntry[] = [
      {
        id: `${nextExportId}-timeline`,
        title: `Statement CSV export ${nextExportId} generated`,
        detail: `${exportableStatements.length} exported, ${unavailableStatements.length} skipped, ${selectedStatements.length} selected. File: ${filename}.`,
        createdAt: finishedAt,
      },
    ];

    for (const statement of exportableStatements) {
      const { documentWorkflows } = publishDocumentWorkflowAction({
        tenantId: companyId,
        clientId: companyId,
        customerId: statement.customerNumber,
        accountId: statement.accountId,
        customerName: statement.customerName,
        customerNumber: statement.customerNumber,
        documentId: statement.id,
        documentType: "statement",
        documentRecord: {
          ...buildStatementDocumentRecordForAction(statement, "Export Statements"),
          exportId: nextExportId,
          exportFormat: "CSV",
          exportFilename: filename,
        },
        action: "Statement.Download",
        actor: buildStatementBulkActorContext(actor, statement),
        sourceWorkspace: "Statement Center",
        initiatedFromRoute: "/admin/ar-recovery/statements",
        deliveryStatus: resolveStatementDeliveryStatusForAction("Export Statements", statement.deliveryStatus),
      });
      const projectedStatements = documentWorkflows
        .map((record) => record as unknown)
        .filter(isARRecoveryStatementWorkflowRecord);
      const projectedIds = new Set(projectedStatements.map((record) => record.id));
      storedStatementWorkflows = [...projectedStatements, ...storedStatementWorkflows.filter((record) => !projectedIds.has(record.id))]
        .sort((a, b) => getDateTime(b.updatedAt || b.createdAt) - getDateTime(a.updatedAt || a.createdAt));
    }

    saveStoredARRecoveryStatementWorkflows(storedStatementWorkflows);
    onTimelineEntries(timelineEntries);
    downloadCsv(csv, filename);
    setExportId(nextExportId);
    setCompletedAt(finishedAt);
    setGeneratedFilename(filename);
    setCsvContent(csv);
    setResults([...exportedResults, ...skippedResults]);
    setStep("results");
    setIsGenerating(false);
  }

  function downloadAgain() {
    if (!csvContent || !generatedFilename) return;
    downloadCsv(csvContent, generatedFilename);
  }

  return (
    <div className="min-w-0 max-w-full overflow-hidden rounded-[1.5rem] border border-[#BFEAFF] bg-[#F4FBFF] p-4 shadow-[0_14px_30px_rgba(15,23,42,.06)]" aria-live={isGenerating ? "polite" : "off"}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-2 break-words text-sm font-semibold text-[#0A2540]">
            <ARLightLineIcon icon={resolveStatementActionIcon("Export Statements")} />
            Export Statements
          </p>
          <p className="mt-1 break-words text-xs font-normal leading-5 text-[#475569]">
            Select statements, review the CSV export scope, then download a UTF-8 statement metadata export.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={isGenerating}
          className="rounded-full border border-[#BFEAFF] bg-white px-3 py-1.5 text-xs font-semibold text-[#0A2540] transition hover:border-[#14B8A6] hover:text-[#0F766E] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#14B8A6] focus-visible:ring-offset-2 focus-visible:ring-offset-[#F4FBFF] disabled:cursor-not-allowed disabled:opacity-70"
        >
          Close
        </button>
      </div>

      <div className="mt-4 grid min-w-0 gap-3 md:grid-cols-[repeat(3,minmax(0,1fr))]">
        <DetailMetric label="Selected" value={String(selectedStatements.length)} />
        <DetailMetric label="Exportable" value={String(exportableStatements.length)} />
        <DetailMetric label="Unavailable" value={String(unavailableStatements.length)} />
      </div>

      <div className="mt-4 grid min-w-0 gap-3 md:grid-cols-[repeat(2,minmax(0,1fr))]">
        <label className="ar-interaction-control ar-interaction-selected rounded-2xl border p-3">
          <span className="flex items-start gap-3">
            <input type="radio" checked readOnly className="mt-1 size-4 accent-[#14B8A6]" aria-label="CSV Data export format selected" />
            <span>
              <span className="block text-sm font-semibold text-[#0A2540]">CSV Data</span>
              <span className="mt-1 block text-xs font-semibold leading-5 text-[#475569]">Operational statement metadata export.</span>
            </span>
          </span>
        </label>
        <label className="rounded-2xl border border-[#BFEAFF] bg-white p-3 opacity-70">
          <span className="flex items-start gap-3">
            <input type="radio" disabled className="mt-1 size-4" aria-label="ZIP of Statement PDFs unavailable" />
            <span>
              <span className="block text-sm font-semibold text-[#0A2540]">ZIP of Statement PDFs</span>
              <span className="mt-1 block text-xs font-semibold leading-5 text-[#475569]">Document export service required.</span>
            </span>
          </span>
        </label>
      </div>

      {step === "select" ? (
        <div className="mt-4 grid min-w-0 gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={selectAllExportable} className="rounded-full border border-[#14B8A6] bg-white px-3 py-1.5 text-xs font-semibold text-[#0A2540] transition hover:bg-[#E6FFFB] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#14B8A6]">Select all exportable</button>
            <button type="button" onClick={clearSelection} className="rounded-full border border-[#BFEAFF] bg-white px-3 py-1.5 text-xs font-semibold text-[#0A2540] transition hover:border-[#14B8A6] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#14B8A6]">Clear selection</button>
          </div>
          <div className="grid min-w-0 gap-2">
            {statements.map((statement) => {
              const selected = selectedIds.includes(statement.id);
              const validation = validateStatementCsvExportEligibility(statement, actor);
              return (
                <label
                  key={statement.id}
                  className={`grid min-w-0 cursor-pointer gap-3 rounded-2xl border p-3 transition md:grid-cols-[auto_minmax(0,1fr)_auto] ${selected ? "ar-interaction-selected" : "border-[#BFEAFF] bg-white hover:border-[#14B8A6]"}`}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => toggleStatement(statement.id)}
                    className="mt-1 size-4 accent-[#14B8A6]"
                    aria-label={`Select ${statement.statementNumber} for CSV export`}
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-[#0A2540]">{statement.statementNumber} - {statement.customerName}</span>
                    <span className="mt-1 block text-xs font-semibold leading-5 text-[#475569]">
                      {statement.customerNumber} - {formatARRecoveryCurrency(statement.balanceCents)} - {getStatementBulkRecipient(statement) || "No recipient"} - {statement.deliveryStatus}
                    </span>
                    {!validation.eligible ? <span className="mt-1 block text-xs font-semibold leading-5 text-[#A33A12]">{validation.reason}</span> : null}
                  </span>
                  <span className={`self-start rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] ${validation.eligible ? "border-[#14B8A6] bg-[#E6FFFB] text-[#0A2540]" : "border-[#FED7AA] bg-[#FFF7ED] text-[#A33A12]"}`}>
                    {validation.eligible ? "Exportable" : "Unavailable"}
                  </span>
                </label>
              );
            })}
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-full border border-[#BFEAFF] bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#0A2540]">Cancel</button>
            <button
              type="button"
              onClick={() => setStep("review")}
              disabled={!selectedStatements.length}
              className="rounded-full border border-[#14B8A6] bg-[#14B8A6] px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-white transition hover:bg-[#0F766E] disabled:cursor-not-allowed disabled:border-[#BFEAFF] disabled:bg-white disabled:text-[#64748B]"
            >
              Review CSV Export
            </button>
          </div>
        </div>
      ) : null}

      {step === "review" ? (
        <div className="mt-4 grid min-w-0 gap-3">
          <div className="grid min-w-0 gap-3 md:grid-cols-[repeat(3,minmax(0,1fr))]">
            <DetailMetric label="Customers" value={String(customersRepresented)} />
            <DetailMetric label="Total Balance" value={formatARRecoveryCurrency(selectedBalance)} />
            <DetailMetric label="Selected Format" value="CSV" />
            <DetailMetric label="Estimated Filename" value={estimatedFilename} />
            <DetailMetric label="Exportable" value={String(exportableStatements.length)} />
            <DetailMetric label="Unavailable" value={String(unavailableStatements.length)} />
          </div>
          {unavailableStatements.length ? (
            <div className="rounded-2xl border border-[#FED7AA] bg-[#FFF7ED] p-3">
              <p className="text-xs font-black uppercase tracking-[0.14em] text-[#A33A12]">Unavailable statements</p>
              <div className="mt-2 grid gap-2">
                {unavailableStatements.map((validation) => (
                  <p key={validation.statement.id} className="text-sm font-semibold leading-6 text-[#7C2D12]">
                    {validation.statement.statementNumber}: {validation.reason}
                  </p>
                ))}
              </div>
            </div>
          ) : null}
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" onClick={() => setStep("select")} className="rounded-full border border-[#BFEAFF] bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#0A2540]">Back</button>
            <button type="button" onClick={onClose} className="rounded-full border border-[#BFEAFF] bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#0A2540]">Cancel</button>
            <button
              type="button"
              onClick={generateExport}
              disabled={!exportableStatements.length || isGenerating}
              className="rounded-full border border-[#14B8A6] bg-[#14B8A6] px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-white transition hover:bg-[#0F766E] disabled:cursor-not-allowed disabled:border-[#BFEAFF] disabled:bg-white disabled:text-[#64748B]"
            >
              Generate Export
            </button>
          </div>
        </div>
      ) : null}

      {step === "generating" ? (
        <div className="mt-4 rounded-2xl border border-[#BFEAFF] bg-white p-4 text-sm font-semibold leading-6 text-[#0A2540]" role="status">
          Generating CSV export... Duplicate submissions are disabled while this export is processing.
        </div>
      ) : null}

      {step === "results" ? (
        <div className="mt-4 grid min-w-0 gap-3">
          <div className="rounded-2xl border border-[#BFEAFF] bg-white p-3">
            <p className="text-xs font-black uppercase tracking-[0.14em] text-[#14B8A6]">CSV export results</p>
            <p className="mt-1 text-sm font-semibold text-[#0A2540]">Export ID: {exportId}</p>
            <p className="text-sm font-semibold text-[#0A2540]">File: {generatedFilename}</p>
            <p className="text-sm font-semibold text-[#0A2540]">Completed: {completedAt ? `${formatCapturedDate(completedAt)} ${formatCapturedTime(completedAt)}` : "Not Captured"}</p>
          </div>
          <div className="grid min-w-0 gap-3 md:grid-cols-[repeat(2,minmax(0,1fr))]">
            <DetailMetric label="Exported" value={String(results.filter((result) => result.status === "exported").length)} />
            <DetailMetric label="Skipped or Failed" value={String(results.filter((result) => result.status === "skipped").length)} />
          </div>
          <div className="grid min-w-0 gap-2">
            {results.map((result) => (
              <div key={`${result.statementId}-${result.status}`} className="rounded-2xl border border-[#BFEAFF] bg-white p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <p className="text-sm font-semibold text-[#0A2540]">{result.statementNumber} - {result.customerName}</p>
                  <span className={`rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] ${result.status === "exported" ? "border-[#14B8A6] bg-[#E6FFFB] text-[#0A2540]" : "border-[#FED7AA] bg-[#FFF7ED] text-[#A33A12]"}`}>
                    {result.status}
                  </span>
                </div>
                <p className="mt-1 text-xs font-semibold leading-5 text-[#475569]">{result.reason}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" onClick={downloadAgain} disabled={!csvContent} className="rounded-full border border-[#14B8A6] bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#0A2540] transition hover:bg-[#E6FFFB] disabled:cursor-not-allowed disabled:border-[#BFEAFF] disabled:text-[#64748B]">Download Again</button>
            <button type="button" onClick={onViewHistory} className="rounded-full border border-[#BFEAFF] bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#0A2540]">View Statement History</button>
            <button type="button" onClick={onClose} className="rounded-full border border-[#BFEAFF] bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#0A2540]">Close</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function validateStatementCsvExportEligibility(statement: StatementCenterRecord, actor: ARRecoveryActor) {
  let reason = "";
  if (!statement.id) reason = "Statement record is missing.";
  else if (actor.role && !AR_RECOVERY_WRITE_ROLES.has(actor.role)) reason = "Current user does not have statement export permission.";
  return { statement, eligible: !reason, reason };
}

function buildStatementCsvExportContent(statements: StatementCenterRecord[]) {
  const columns = [
    "Statement ID",
    "Customer Name",
    "Customer Number",
    "Account Number",
    "Statement Date",
    "Statement Period",
    "Current Balance",
    "Aging",
    "Recipient Email",
    "Delivery Method",
    "Delivery Status",
    "Statement Status",
    "Created Date",
    "Last Updated Date",
  ];
  const rows = statements.map((statement) => [
    statement.id,
    statement.customerName,
    statement.customerNumber,
    statement.accountId,
    statement.dateSent || statement.createdAt.slice(0, 10),
    buildStatementCsvPeriod(statement),
    formatARRecoveryCurrency(statement.balanceCents),
    buildStatementCsvAging(statement),
    getStatementBulkRecipient(statement),
    "Email",
    statement.deliveryStatus,
    statement.status,
    statement.createdAt,
    statement.updatedAt,
  ]);
  return [columns, ...rows].map((row) => row.map(escapeStatementCsvCell).join(",")).join("\r\n");
}

function escapeStatementCsvCell(value: string | number | null | undefined) {
  const normalized = String(value ?? "").replace(/\r?\n/g, "\n");
  return `"${normalized.replace(/"/g, '""')}"`;
}

function buildStatementCsvPeriod(statement: StatementCenterRecord) {
  const dueDates = statement.lineItems
    .map((item) => item.dueDate)
    .filter(Boolean)
    .sort();
  if (!dueDates.length) return statement.dueDate || statement.createdAt.slice(0, 10);
  return `${dueDates[0]} through ${dueDates[dueDates.length - 1]}`;
}

function buildStatementCsvAging(statement: StatementCenterRecord) {
  const statuses = Array.from(new Set(statement.lineItems.map((item) => item.status))).filter(Boolean);
  if (statuses.length) return statuses.join(" / ");
  return statement.dueDate ? `Due ${formatDate(statement.dueDate)}` : "Not Captured";
}

function buildStatementCsvExportFilename(date: Date) {
  const timestamp = [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-") + "-" + [
    String(date.getHours()).padStart(2, "0"),
    String(date.getMinutes()).padStart(2, "0"),
  ].join("");
  return `statement-export-${timestamp}.csv`;
}

function StatementPrintBatchWorkflow({
  statements,
  actor,
  companyId,
  clientProfile,
  onClose,
  onViewHistory,
  onTimelineEntries,
}: {
  statements: StatementCenterRecord[];
  actor: ARRecoveryActor;
  companyId: string;
  clientProfile: ReturnType<typeof loadARRecoveryWorkspace>["clientProfile"];
  onClose: () => void;
  onViewHistory: () => void;
  onTimelineEntries: (entries: StatementBulkTimelineEntry[]) => void;
}) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [step, setStep] = useState<StatementPrintBatchStep>("select");
  const [isGenerating, setIsGenerating] = useState(false);
  const [batchId, setBatchId] = useState("");
  const [completedAt, setCompletedAt] = useState("");
  const [printError, setPrintError] = useState("");
  const [lastPrintableIds, setLastPrintableIds] = useState<string[]>([]);
  const [results, setResults] = useState<StatementPrintBatchResult[]>([]);
  const selectedStatements = statements.filter((statement) => selectedIds.includes(statement.id));
  const validations = selectedStatements.map((statement) => validateStatementPrintBatchEligibility(statement, actor));
  const printableStatements = validations.filter((validation) => validation.eligible).map((validation) => validation.statement);
  const unavailableStatements = validations.filter((validation) => !validation.eligible);
  const printableQueue = statements.filter((statement) => validateStatementPrintBatchEligibility(statement, actor).eligible);
  const selectedBalance = selectedStatements.reduce((sum, statement) => sum + statement.balanceCents, 0);
  const customersRepresented = new Set(selectedStatements.map((statement) => statement.customerName)).size;
  const estimatedPageCount = printableStatements.length;

  function toggleStatement(statementId: string) {
    setSelectedIds((current) => current.includes(statementId) ? current.filter((id) => id !== statementId) : [...current, statementId]);
  }

  function selectAllPrintable() {
    setSelectedIds(printableQueue.map((statement) => statement.id));
  }

  function clearSelection() {
    setSelectedIds([]);
    setResults([]);
    setBatchId("");
    setCompletedAt("");
    setPrintError("");
    setLastPrintableIds([]);
    setStep("select");
  }

  function openBatchPrint(targetStatements: StatementCenterRecord[], existingBatchId?: string) {
    const nextBatchId = existingBatchId || `statement-print-batch-${Date.now()}`;
    const previews = targetStatements.map((statement) => buildStatementPreviewFromStatementCenterRecord(statement, clientProfile));
    return openARRecoveryStatementBatchPrintWindow({ previews, batchId: nextBatchId });
  }

  function publishPrintHistory(targetStatements: StatementCenterRecord[], nextBatchId: string, openedAt: string) {
    let storedStatementWorkflows = loadStoredARRecoveryStatementWorkflows();
    const timelineEntries: StatementBulkTimelineEntry[] = [
      {
        id: `${nextBatchId}-timeline`,
        title: `Statement print batch ${nextBatchId} opened`,
        detail: `${targetStatements.length} print dialogs opened, ${unavailableStatements.length} skipped, ${selectedStatements.length} selected across ${customersRepresented} customer${customersRepresented === 1 ? "" : "s"}.`,
        createdAt: openedAt,
      },
    ];

    for (const statement of targetStatements) {
      const { documentWorkflows } = publishDocumentWorkflowAction({
        tenantId: companyId,
        clientId: companyId,
        customerId: statement.customerNumber,
        accountId: statement.accountId,
        customerName: statement.customerName,
        customerNumber: statement.customerNumber,
        documentId: statement.id,
        documentType: "statement",
        documentRecord: {
          ...buildStatementDocumentRecordForAction(statement, "Print Batch"),
          printBatchId: nextBatchId,
          printResult: "Print dialog opened",
        },
        action: "Statement.Print",
        actor: buildStatementBulkActorContext(actor, statement),
        sourceWorkspace: "Statement Center",
        initiatedFromRoute: "/admin/ar-recovery/statements",
        deliveryStatus: resolveStatementDeliveryStatusForAction("Print Batch", statement.deliveryStatus),
      });
      const projectedStatements = documentWorkflows
        .map((record) => record as unknown)
        .filter(isARRecoveryStatementWorkflowRecord);
      const projectedIds = new Set(projectedStatements.map((record) => record.id));
      storedStatementWorkflows = [...projectedStatements, ...storedStatementWorkflows.filter((record) => !projectedIds.has(record.id))]
        .sort((a, b) => getDateTime(b.updatedAt || b.createdAt) - getDateTime(a.updatedAt || a.createdAt));
    }

    saveStoredARRecoveryStatementWorkflows(storedStatementWorkflows);
    onTimelineEntries(timelineEntries);
  }

  function generatePrintBatch() {
    if (!printableStatements.length || isGenerating) return;
    setIsGenerating(true);
    setPrintError("");
    setStep("generating");
    const nextBatchId = `statement-print-batch-${Date.now()}`;
    const opened = openBatchPrint(printableStatements, nextBatchId);
    if (!opened) {
      setPrintError("Print window could not be opened. Allow popups for this site and try again.");
      setStep("review");
      setIsGenerating(false);
      return;
    }

    const openedAt = new Date().toISOString();
    publishPrintHistory(printableStatements, nextBatchId, openedAt);
    setBatchId(nextBatchId);
    setCompletedAt(openedAt);
    setLastPrintableIds(printableStatements.map((statement) => statement.id));
    setResults([
      ...printableStatements.map((statement): StatementPrintBatchResult => ({
        statementId: statement.id,
        statementNumber: statement.statementNumber,
        customerName: statement.customerName,
        status: "opened",
        reason: "Combined print document opened in one browser print dialog.",
      })),
      ...unavailableStatements.map((validation): StatementPrintBatchResult => ({
        statementId: validation.statement.id,
        statementNumber: validation.statement.statementNumber,
        customerName: validation.statement.customerName,
        status: "skipped",
        reason: validation.reason || "Statement was not printable.",
      })),
    ]);
    setStep("results");
    setIsGenerating(false);
  }

  function openAgain() {
    const statementsToPrint = statements.filter((statement) => lastPrintableIds.includes(statement.id));
    if (!statementsToPrint.length || !batchId) return;
    const opened = openBatchPrint(statementsToPrint, batchId);
    if (!opened) {
      setPrintError("Print window could not be reopened. Allow popups for this site and try again.");
    }
  }

  return (
    <div className="min-w-0 max-w-full overflow-hidden rounded-[1.5rem] border border-[#BFEAFF] bg-[#F4FBFF] p-4 shadow-[0_14px_30px_rgba(15,23,42,.06)]" aria-live={isGenerating || printError ? "polite" : "off"}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-2 break-words text-sm font-semibold text-[#0A2540]">
            <ARLightLineIcon icon={resolveStatementActionIcon("Print Batch")} />
            Print Batch
          </p>
          <p className="mt-1 break-words text-xs font-normal leading-5 text-[#475569]">
            Select printable statements, review the batch, then open one combined browser print document.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={isGenerating}
          className="rounded-full border border-[#BFEAFF] bg-white px-3 py-1.5 text-xs font-semibold text-[#0A2540] transition hover:border-[#14B8A6] hover:text-[#0F766E] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#14B8A6] focus-visible:ring-offset-2 focus-visible:ring-offset-[#F4FBFF] disabled:cursor-not-allowed disabled:opacity-70"
        >
          Close
        </button>
      </div>

      <div className="mt-4 grid min-w-0 gap-3 md:grid-cols-[repeat(3,minmax(0,1fr))]">
        <DetailMetric label="Selected" value={String(selectedStatements.length)} />
        <DetailMetric label="Printable" value={String(printableStatements.length)} />
        <DetailMetric label="Unavailable" value={String(unavailableStatements.length)} />
      </div>

      {printError ? (
        <div className="mt-4 rounded-2xl border border-[#FED7AA] bg-[#FFF7ED] p-3 text-sm font-semibold leading-6 text-[#7C2D12]" role="alert">
          {printError}
        </div>
      ) : null}

      {step === "select" ? (
        <div className="mt-4 grid min-w-0 gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={selectAllPrintable} className="rounded-full border border-[#14B8A6] bg-white px-3 py-1.5 text-xs font-semibold text-[#0A2540] transition hover:bg-[#E6FFFB] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#14B8A6]">Select all printable</button>
            <button type="button" onClick={clearSelection} className="rounded-full border border-[#BFEAFF] bg-white px-3 py-1.5 text-xs font-semibold text-[#0A2540] transition hover:border-[#14B8A6] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#14B8A6]">Clear selection</button>
          </div>
          <div className="grid min-w-0 gap-2">
            {statements.map((statement) => {
              const selected = selectedIds.includes(statement.id);
              const validation = validateStatementPrintBatchEligibility(statement, actor);
              return (
                <label
                  key={statement.id}
                  className={`grid min-w-0 cursor-pointer gap-3 rounded-2xl border p-3 transition md:grid-cols-[auto_minmax(0,1fr)_auto] ${selected ? "ar-interaction-selected" : "border-[#BFEAFF] bg-white hover:border-[#14B8A6]"}`}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => toggleStatement(statement.id)}
                    className="mt-1 size-4 accent-[#14B8A6]"
                    aria-label={`Select ${statement.statementNumber} for batch printing`}
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-[#0A2540]">{statement.statementNumber} - {statement.customerName}</span>
                    <span className="mt-1 block text-xs font-semibold leading-5 text-[#475569]">
                      {statement.customerNumber} - {formatCapturedDate(statement.dateSent || statement.createdAt)} - {formatARRecoveryCurrency(statement.balanceCents)} - {statement.status}
                    </span>
                    {!validation.eligible ? <span className="mt-1 block text-xs font-semibold leading-5 text-[#A33A12]">{validation.reason}</span> : null}
                  </span>
                  <span className={`self-start rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] ${validation.eligible ? "border-[#14B8A6] bg-[#E6FFFB] text-[#0A2540]" : "border-[#FED7AA] bg-[#FFF7ED] text-[#A33A12]"}`}>
                    {validation.eligible ? "Printable" : "Unavailable"}
                  </span>
                </label>
              );
            })}
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-full border border-[#BFEAFF] bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#0A2540]">Cancel</button>
            <button
              type="button"
              onClick={() => setStep("review")}
              disabled={!selectedStatements.length}
              className="rounded-full border border-[#14B8A6] bg-[#14B8A6] px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-white transition hover:bg-[#0F766E] disabled:cursor-not-allowed disabled:border-[#BFEAFF] disabled:bg-white disabled:text-[#64748B]"
            >
              Review Batch
            </button>
          </div>
        </div>
      ) : null}

      {step === "review" ? (
        <div className="mt-4 grid min-w-0 gap-3">
          <div className="grid min-w-0 gap-3 md:grid-cols-[repeat(3,minmax(0,1fr))]">
            <DetailMetric label="Customers" value={String(customersRepresented)} />
            <DetailMetric label="Total Balance" value={formatARRecoveryCurrency(selectedBalance)} />
            <DetailMetric label="Estimated Pages" value={String(estimatedPageCount)} />
            <DetailMetric label="Print Format" value="One statement per page" />
            <DetailMetric label="Printable" value={String(printableStatements.length)} />
            <DetailMetric label="Unavailable" value={String(unavailableStatements.length)} />
          </div>
          {unavailableStatements.length ? (
            <div className="rounded-2xl border border-[#FED7AA] bg-[#FFF7ED] p-3">
              <p className="text-xs font-black uppercase tracking-[0.14em] text-[#A33A12]">Unavailable statements</p>
              <div className="mt-2 grid gap-2">
                {unavailableStatements.map((validation) => (
                  <p key={validation.statement.id} className="text-sm font-semibold leading-6 text-[#7C2D12]">
                    {validation.statement.statementNumber}: {validation.reason}
                  </p>
                ))}
              </div>
            </div>
          ) : null}
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" onClick={() => setStep("select")} className="rounded-full border border-[#BFEAFF] bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#0A2540]">Back</button>
            <button type="button" onClick={onClose} className="rounded-full border border-[#BFEAFF] bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#0A2540]">Cancel</button>
            <button
              type="button"
              onClick={generatePrintBatch}
              disabled={!printableStatements.length || isGenerating}
              className="rounded-full border border-[#14B8A6] bg-[#14B8A6] px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-white transition hover:bg-[#0F766E] disabled:cursor-not-allowed disabled:border-[#BFEAFF] disabled:bg-white disabled:text-[#64748B]"
            >
              Open Print Batch
            </button>
          </div>
        </div>
      ) : null}

      {step === "generating" ? (
        <div className="mt-4 rounded-2xl border border-[#BFEAFF] bg-white p-4 text-sm font-semibold leading-6 text-[#0A2540]" role="status">
          Generating combined print document... Duplicate activations are disabled while the print batch is opening.
        </div>
      ) : null}

      {step === "results" ? (
        <div className="mt-4 grid min-w-0 gap-3">
          <div className="rounded-2xl border border-[#BFEAFF] bg-white p-3">
            <p className="text-xs font-black uppercase tracking-[0.14em] text-[#14B8A6]">Print batch results</p>
            <p className="mt-1 text-sm font-semibold text-[#0A2540]">Batch ID: {batchId}</p>
            <p className="text-sm font-semibold text-[#0A2540]">Completed: {completedAt ? `${formatCapturedDate(completedAt)} ${formatCapturedTime(completedAt)}` : "Not Captured"}</p>
          </div>
          <div className="grid min-w-0 gap-3 md:grid-cols-[repeat(3,minmax(0,1fr))]">
            <DetailMetric label="Print Dialog Opened" value={String(results.filter((result) => result.status === "opened").length)} />
            <DetailMetric label="Skipped or Unavailable" value={String(results.filter((result) => result.status === "skipped").length)} />
            <DetailMetric label="Statement Count" value={String(lastPrintableIds.length)} />
          </div>
          <div className="grid min-w-0 gap-2">
            {results.map((result) => (
              <div key={`${result.statementId}-${result.status}`} className="rounded-2xl border border-[#BFEAFF] bg-white p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <p className="text-sm font-semibold text-[#0A2540]">{result.statementNumber} - {result.customerName}</p>
                  <span className={`rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] ${result.status === "opened" ? "border-[#14B8A6] bg-[#E6FFFB] text-[#0A2540]" : "border-[#FED7AA] bg-[#FFF7ED] text-[#A33A12]"}`}>
                    {result.status}
                  </span>
                </div>
                <p className="mt-1 text-xs font-semibold leading-5 text-[#475569]">{result.reason}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" onClick={openAgain} disabled={!lastPrintableIds.length} className="rounded-full border border-[#14B8A6] bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#0A2540] transition hover:bg-[#E6FFFB] disabled:cursor-not-allowed disabled:border-[#BFEAFF] disabled:text-[#64748B]">Open Print Batch Again</button>
            <button type="button" onClick={onViewHistory} className="rounded-full border border-[#BFEAFF] bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#0A2540]">View Statement History</button>
            <button type="button" onClick={onClose} className="rounded-full border border-[#BFEAFF] bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#0A2540]">Close</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function validateStatementPrintBatchEligibility(statement: StatementCenterRecord, actor: ARRecoveryActor) {
  let reason = "";
  if (!statement.id) reason = "Statement record is missing.";
  else if (!statement.customerName || !statement.customerNumber || !statement.accountId) reason = "Customer and account context are required for printing.";
  else if (!statement.statementNumber) reason = "Statement number is required for printing.";
  else if (!Array.isArray(statement.lineItems)) reason = "Statement invoice content is not available.";
  else if (actor.role && !AR_RECOVERY_WRITE_ROLES.has(actor.role)) reason = "Current user does not have statement print permission.";
  return { statement, eligible: !reason, reason };
}

function buildStatementPreviewFromStatementCenterRecord(
  statement: StatementCenterRecord,
  clientProfile: ReturnType<typeof loadARRecoveryWorkspace>["clientProfile"],
): StatementPreview {
  const branding: LetterBrandingBlock = {
    logoSrc: AR_RECOVERY_LETTER_BRANDING_LOGO_SRC,
    logoAlt: "CollabRecover AR Solutions",
    companyName: clientProfile.letterDefaults.businessName || clientProfile.businessName,
    addressLines: buildARRecoveryLetterAddressLines(clientProfile.letterDefaults.address || clientProfile.addressLine1 || ""),
  };
  const statementDate = statement.dateSent || statement.createdAt.slice(0, 10);
  const totalBalanceCents = statement.balanceCents || statement.lineItems.reduce((sum, item) => sum + item.amountCents, 0);
  const lineItems = statement.lineItems.map((item) => ({
    invoiceNumber: item.invoiceNumber,
    invoiceDate: formatCapturedDate(item.dueDate),
    dueDate: formatCapturedDate(item.dueDate),
    originalAmount: formatARRecoveryCurrency(item.amountCents),
    openBalance: formatARRecoveryCurrency(item.amountCents),
    balance: formatARRecoveryCurrency(item.amountCents),
    aging: item.status,
    status: item.status,
  }));
  const summaryParagraphs = [
    `This statement summarizes the open invoices currently associated with your account as of ${formatCapturedDate(statementDate)}.`,
    "Please review the invoice details below. If payment has already been submitted, please provide the payment date and remittance reference so the account can be updated.",
  ];
  const footerContactItems: LetterFooterContactItem[] = [
    { label: "Email" as const, value: clientProfile.letterDefaults.email },
    { label: "Phone" as const, value: clientProfile.letterDefaults.phone },
    { label: "Website" as const, value: clientProfile.letterDefaults.website },
  ].filter(
    (item, index, items): item is LetterFooterContactItem =>
      Boolean(item.value) && items.findIndex((candidate) => candidate.value === item.value) === index,
  );
  const body = [
    ...summaryParagraphs,
    `Selected invoices: ${lineItems.length}. Total due: ${formatARRecoveryCurrency(totalBalanceCents)}.`,
  ].join("\n\n");

  return {
    branding,
    documentTitle: "Account Statement",
    customer: statement.customerName,
    client: statement.clientCompany || clientProfile.businessName || clientProfile.dbaName || "AR Recovery Client",
    account: statement.customerNumber,
    statementDate: formatCapturedDate(statementDate),
    recipientContactName: statement.mergeFields.contactName || statement.customerName,
    recipientEmail: statement.mergeFields.email,
    totalBalance: formatARRecoveryCurrency(totalBalanceCents),
    invoiceCount: String(lineItems.length),
    agingSummary: buildStatementCsvAging(statement),
    notes: statement.notes,
    deliveryOption: "Print",
    lineItems,
    summaryParagraphs,
    selectedPromise: null,
    totals: {
      selectedInvoiceCount: String(lineItems.length),
      totalOriginalAmount: formatARRecoveryCurrency(totalBalanceCents),
      totalOpenBalance: formatARRecoveryCurrency(totalBalanceCents),
      currentDue: formatARRecoveryCurrency(totalBalanceCents),
      pastDue: formatARRecoveryCurrency(0),
      totalDue: formatARRecoveryCurrency(totalBalanceCents),
    },
    remittanceContact: {
      name: clientProfile.letterDefaults.contactName,
      phone: clientProfile.letterDefaults.phone,
      email: clientProfile.letterDefaults.email,
    },
    footerContactItems,
    body,
  };
}

function getStatementBulkRecipient(statement: StatementCenterRecord) {
  return (statement.mergeFields.email || "").trim();
}

function buildStatementBulkActorContext(actor: ARRecoveryActor, statement: StatementCenterRecord) {
  return {
    actorId: actor.email || statement.sentBy || statement.collector || "statement-center",
    actorName: actor.name || actor.email || statement.sentBy || statement.collector || "Statement Center",
    actorEmail: actor.email || "",
    actorRole: actor.role || "Collector",
    permissionLevel: "collector_write",
  };
}

function buildStatementBulkPlainText(statement: StatementCenterRecord, batchId: string) {
  const lineItems = statement.lineItems
    .map((item) => `${item.invoiceNumber}: ${formatARRecoveryCurrency(item.amountCents)} due ${formatDate(item.dueDate)} (${item.status})`)
    .join("\n");
  return [
    `Statement ${statement.statementNumber}`,
    `Batch: ${batchId}`,
    `Customer: ${statement.customerName}`,
    `Account: ${statement.customerNumber}`,
    `Total balance: ${formatARRecoveryCurrency(statement.balanceCents)}`,
    "",
    "Open invoices:",
    lineItems || "No invoice line items captured.",
    "",
    statement.notes,
  ].filter(Boolean).join("\n");
}

function buildStatementBulkHtml(statement: StatementCenterRecord, batchId: string) {
  const rows = statement.lineItems
    .map((item) => `<tr><td style="padding:8px;border-bottom:1px solid #dbeafe;">${escapeARRecoveryEmailHtml(item.invoiceNumber)}</td><td style="padding:8px;border-bottom:1px solid #dbeafe;">${formatDate(item.dueDate)}</td><td style="padding:8px;border-bottom:1px solid #dbeafe;">${formatARRecoveryCurrency(item.amountCents)}</td><td style="padding:8px;border-bottom:1px solid #dbeafe;">${escapeARRecoveryEmailHtml(item.status)}</td></tr>`)
    .join("");
  return `
    <div style="font-family: Arial, sans-serif; color:#0A2540; line-height:1.55; max-width:720px; margin:0 auto;">
      <p style="margin:0 0 8px;color:#14B8A6;font-size:12px;font-weight:800;text-transform:uppercase;letter-spacing:.14em;">CollabRecover AR Solutions</p>
      <h1 style="margin:0 0 12px;font-size:24px;color:#0A2540;">Account Statement ${escapeARRecoveryEmailHtml(statement.statementNumber)}</h1>
      <p><strong>Batch:</strong> ${escapeARRecoveryEmailHtml(batchId)}</p>
      <p><strong>Customer:</strong> ${escapeARRecoveryEmailHtml(statement.customerName)}</p>
      <p><strong>Account:</strong> ${escapeARRecoveryEmailHtml(statement.customerNumber)}</p>
      <p><strong>Total balance:</strong> ${formatARRecoveryCurrency(statement.balanceCents)}</p>
      <table style="width:100%;border-collapse:collapse;margin-top:18px;">
        <thead><tr><th align="left" style="padding:8px;border-bottom:2px solid #14B8A6;">Invoice</th><th align="left" style="padding:8px;border-bottom:2px solid #14B8A6;">Due Date</th><th align="left" style="padding:8px;border-bottom:2px solid #14B8A6;">Amount</th><th align="left" style="padding:8px;border-bottom:2px solid #14B8A6;">Status</th></tr></thead>
        <tbody>${rows || `<tr><td colspan="4" style="padding:8px;">No invoice line items captured.</td></tr>`}</tbody>
      </table>
      <p style="margin-top:18px;">${escapeARRecoveryEmailHtml(statement.notes)}</p>
    </div>
  `;
}

function escapeARRecoveryEmailHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function ARLightLineIcon({ icon, compact = false }: { icon: ReactNode; compact?: boolean }) {
  return (
    <span
      className={`${compact ? "size-4" : "size-5"} inline-flex shrink-0 items-center justify-center text-[#22E0D0] [&>svg]:size-full [&>svg]:stroke-[1.8]`}
      aria-hidden="true"
    >
      {icon}
    </span>
  );
}

function ManagerEmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: string;
}) {
  return (
    <div className="rounded-xl border border-dashed border-[#BFEAFF] bg-[#F4FBFF] p-4 text-sm font-semibold leading-6 text-[#0A2540]">
      <p className="font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{title}</p>
      <p className="mt-2">{description}</p>
      {action ? <p className="mt-1 text-[#14B8A6]">{action}</p> : null}
    </div>
  );
}

function CompactActionTool({ label, title, disabled = false, onClick }: { label: string; title: string; disabled?: boolean; onClick?: () => void }) {
  const iconMap: Record<string, ReactNode> = {
    Call: <Activity className="size-3.5" aria-hidden="true" />,
    "Log Call": <Activity className="size-3.5" aria-hidden="true" />,
    Note: <FileText className="size-3.5" aria-hidden="true" />,
    "Add Note": <FileText className="size-3.5" aria-hidden="true" />,
    Email: <Users className="size-3.5" aria-hidden="true" />,
    Task: <FolderKanban className="size-3.5" aria-hidden="true" />,
    Promise: <Target className="size-3.5" aria-hidden="true" />,
    Payment: <CircleDollarSign className="size-3.5" aria-hidden="true" />,
    Tickler: <Clock3 className="size-3.5" aria-hidden="true" />,
    Letter: <FileText className="size-3.5" aria-hidden="true" />,
    Statement: <FileText className="size-3.5" aria-hidden="true" />,
    Document: <FolderKanban className="size-3.5" aria-hidden="true" />,
    Communications: <Users className="size-3.5" aria-hidden="true" />,
    "Assign Task": <FolderKanban className="size-3.5" aria-hidden="true" />,
    "Create Promise": <Target className="size-3.5" aria-hidden="true" />,
    "Follow-Up": <Clock3 className="size-3.5" aria-hidden="true" />,
    "Generate Letter": <FileText className="size-3.5" aria-hidden="true" />,
    "Generate Statement": <FileText className="size-3.5" aria-hidden="true" />,
    "Request Document": <FolderKanban className="size-3.5" aria-hidden="true" />,
    Escalate: <ShieldCheck className="size-3.5" aria-hidden="true" />,
    History: <Clock3 className="size-3.5" aria-hidden="true" />,
    Audit: <ClipboardList className="size-3.5" aria-hidden="true" />,
  };
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className="ar-compact-action focus-ring inline-flex min-h-8 shrink-0 items-center gap-1.5 rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#14B8A6] transition hover:border-[#14B8A6] hover:bg-[#14B8A6] hover:text-white disabled:cursor-not-allowed disabled:border-[#BFEAFF] disabled:bg-[#F4FBFF] disabled:text-[#7EE7E0]"
    >
      <span className="inline-flex size-4 shrink-0 items-center justify-center text-[#22E0D0]" aria-hidden="true">
        {iconMap[label] || <Bolt className="size-3.5" aria-hidden="true" />}
      </span>
      {label}
    </button>
  );
}

const ARRecoveryDocumentExecutionShell = forwardRef<HTMLElement, {
  mode: "letter" | "statement";
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  clientProfile: ReturnType<typeof loadARRecoveryWorkspace>["clientProfile"];
  queueLabel: string;
  queuePosition: number;
  queueCount: number;
  letterForm: ARRecoveryLetterFormState;
  statementForm: ARRecoveryStatementFormState;
  setLetterForm: Dispatch<SetStateAction<ARRecoveryLetterFormState>>;
  setStatementForm: Dispatch<SetStateAction<ARRecoveryStatementFormState>>;
  selectedAccountPromises: Array<{ id: string; amountCents: number; promiseDate: string; status: string }>;
  letterFormError: string;
  statementFormError: string;
  successMessage: string;
  letterSuccessSummary: ARRecoveryLetterSuccessSummary;
  statementCompletionSummary: ARRecoveryStatementCompletionSummary;
  statementFailureSummary: ARRecoveryStatementFailureSummary;
  workspacePanelMessage: string;
  mobilePane: "controls" | "preview";
  setMobilePane: Dispatch<SetStateAction<"controls" | "preview">>;
  isSubmitting: boolean;
  historyButtonRef?: RefObject<HTMLButtonElement | null>;
  onHistory: () => void;
  onPrint: () => void;
  onPdf: () => void;
  onGenerateAnotherLetter: () => void;
  onSendAnotherStatement: () => void;
  onOpenCustomerTimeline: () => void;
  onSend: () => Promise<void>;
  onClose: () => void;
}>(
  ({
    mode,
    account,
    clientProfile,
    queueLabel,
    queuePosition,
    queueCount,
    letterForm,
    statementForm,
    setLetterForm,
    setStatementForm,
    selectedAccountPromises,
    letterFormError,
    statementFormError,
    successMessage,
    letterSuccessSummary,
    statementCompletionSummary,
    statementFailureSummary,
    workspacePanelMessage,
    mobilePane,
    setMobilePane,
    isSubmitting,
    historyButtonRef,
    onHistory,
    onPrint,
    onPdf,
    onGenerateAnotherLetter,
    onSendAnotherStatement,
    onOpenCustomerTimeline,
    onSend,
    onClose,
  }, ref) => {
    const isLetter = mode === "letter";
    const title = isLetter ? "Enterprise Correspondence Workspace" : "Statement Execution";
    const documentLabel = isLetter ? "Certified Letter" : "Statement";
    const errorMessage = isLetter ? letterFormError : statementFormError;
    const letterPreview = buildLetterPreviewFromForm({ form: letterForm, account, clientProfile, selectedPromises: selectedAccountPromises });
    const statementPreview = buildStatementPreviewFromForm({ form: statementForm, account, clientProfile, selectedPromises: selectedAccountPromises });
    const letterReadiness = isLetter ? buildLetterDocumentReadiness(letterForm, account, selectedAccountPromises, letterPreview) : [];
    const statementReadiness = !isLetter ? buildStatementDocumentReadiness(statementForm, account, clientProfile, statementPreview) : [];
    const canSendDocument = isLetter
      ? letterReadiness.every((item) => item.ready || item.optional)
      : statementReadiness.every((item) => item.ready || item.optional);
    const letterLifecycleStatus = letterSuccessSummary?.deliveryStatus || (canSendDocument ? "Ready" : "Draft");
    const letterImpactItems = [
      "Create Certified Letter",
      "Update Timeline",
      "Update Recovery Activity",
      "Save Letter History",
      "Notify Customer when delivery is configured",
    ];
    const statementSentAtLabel = statementCompletionSummary
      ? `${formatCapturedDate(statementCompletionSummary.sentAt.slice(0, 10))} ${formatCapturedTime(statementCompletionSummary.sentAt)}`
      : "";
    const statementVerifiedItems: ARRecoveryDocumentReadinessItem[] = [
      {
        label: "Customer",
        ready: Boolean(account.customerName && account.contactEmail),
        message: account.customerName ? `${account.customerName} | ${account.contactEmail}` : "Customer context is required.",
      },
      {
        label: "Balance",
        ready: account.openBalanceCents >= 0,
        message: formatARRecoveryCurrency(account.openBalanceCents),
      },
      {
        label: "Statement Content",
        ready: Boolean(statementForm.statementType && statementForm.statementDate && statementForm.relatedInvoices.length),
        message: statementForm.relatedInvoices.length
          ? `${statementForm.relatedInvoices.length} invoice${statementForm.relatedInvoices.length === 1 ? "" : "s"} included.`
          : "Select invoices before sending.",
      },
      {
        label: "Delivery Method",
        ready: Boolean(statementForm.deliveryOption),
        message: statementForm.deliveryOption || "Select a delivery method.",
      },
    ];
    const statementImpactItems = [
      "Generate Certified Statement",
      "Update Timeline",
      "Update Recovery Activity",
      "Update Statement History",
      "Deliver to Customer",
    ];

    return (
      <div
        className={`pointer-events-none fixed inset-0 z-[82] flex bg-[#02072C]/12 ${isLetter ? "justify-end" : "items-center justify-center p-3 sm:p-4"}`}
        role="presentation"
        data-recovery-document-execution={mode}
      >
        <aside
          ref={ref}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-labelledby="ar-recovery-document-execution-title"
          className={isLetter
            ? "pointer-events-auto flex h-dvh w-full max-w-full flex-col overflow-hidden border-l border-[#BFEAFF] bg-white shadow-[0_24px_90px_rgba(15,23,42,.24)] outline-none"
            : "pointer-events-auto flex h-[44rem] max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-[96rem] flex-col overflow-hidden rounded-[1.35rem] border border-[#BFEAFF] bg-white shadow-[0_24px_90px_rgba(15,23,42,.24)] outline-none"}
        >
          <header className="shrink-0 border-b border-[#BFEAFF] bg-white px-4 py-3 lg:px-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Recovery Workbench</p>
                <h3 id="ar-recovery-document-execution-title" className="mt-1 break-words text-2xl font-semibold tracking-tight text-[#0A2540]">
                  {title}
                </h3>
                <p className="mt-1 text-sm font-semibold leading-6 text-[#475569]">
                  {account.customerName} | Account {account.customerNumber} | {documentLabel} operational workflow
                </p>
              </div>
              <button type="button" onClick={onClose} className="focus-ring shrink-0 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2 text-sm font-semibold text-[#0A2540]">
                Close
              </button>
            </div>
          </header>

          <div
            className={isLetter ? "grid min-h-0 flex-1 grid-cols-1 overflow-hidden lg:grid-cols-[minmax(17rem,3fr)_minmax(0,5fr)]" : "min-h-0 flex-1 overflow-y-auto bg-[#F4FBFF]"}
            data-statement-execution-scroll-owner={isLetter ? undefined : "workflow"}
          >
            <div className={isLetter ? "contents" : "grid grid-cols-1 lg:h-[32rem] lg:grid-cols-[minmax(17rem,3fr)_minmax(0,5fr)] lg:overflow-hidden"}>
            <section className={isLetter ? `${mobilePane === "controls" ? "block" : "hidden"} min-h-0 min-w-0 border-r border-[#BFEAFF] bg-[#F8FCFF] lg:block` : "min-h-0 min-w-0 border-b border-[#BFEAFF] bg-[#F8FCFF] lg:border-b-0 lg:border-r"}>
              <div className="flex h-full min-h-0 flex-col">
                <div className="shrink-0 border-b border-[#BFEAFF] p-4">
                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Operational Controls</p>
                  <p className="mt-1 text-sm font-semibold leading-6 text-[#475569]">
                    {isLetter
                      ? "Select approved operational options. Template editing remains outside Recovery Workbench."
                      : "Prepare and deliver an account-level customer statement without leaving Recovery Workbench."}
                  </p>
                </div>
                <div
                  className="min-h-0 flex-1 overflow-y-auto p-4"
                  data-statement-execution-scroll-owner={isLetter ? undefined : "controls"}
                >
                  <div className="grid gap-4">
                    <div className="rounded-2xl border border-[#BFEAFF] bg-white p-3">
                      <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Workbench Context</p>
                      <div className="mt-3 grid gap-2">
                        <WorkspaceField label="Queue" value={`${queueLabel}: ${queuePosition} of ${Math.max(queueCount, 1)}`} />
                        <WorkspaceField label="Customer" value={account.customerName} />
                        <WorkspaceField label="Account" value={account.customerNumber} />
                        <WorkspaceField label="Status" value={account.recoveryStatus} />
                        <WorkspaceField label="Balance" value={formatARRecoveryCurrency(account.openBalanceCents)} />
                      </div>
                    </div>

                    {isLetter ? (
                      <ARRecoveryLetterExecutionControls
                        account={account}
                        letterForm={letterForm}
                        setLetterForm={setLetterForm}
                        selectedAccountPromises={selectedAccountPromises}
                        preview={letterPreview}
                      />
                    ) : (
                      <ARRecoveryStatementExecutionControls
                        account={account}
                        statementForm={statementForm}
                        setStatementForm={setStatementForm}
                        selectedAccountPromises={selectedAccountPromises}
                      />
                    )}

                    {workspacePanelMessage ? (
                      <div className="rounded-2xl border border-[#BFEAFF] bg-white px-4 py-3 text-sm font-semibold leading-6 text-[#0A2540]" aria-live="polite">
                        {workspacePanelMessage}
                      </div>
                    ) : null}
                    {!isLetter && isSubmitting ? (
                      <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4" aria-live="polite" aria-label="Statement sending status">
                        <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Sending Statement...</p>
                        <p className="mt-1 text-sm font-semibold leading-6 text-[#475569]">
                          Statement delivery is processing. Keep this workspace open until confirmation appears.
                        </p>
                      </section>
                    ) : null}
                    {!isLetter && statementCompletionSummary ? (
                      <section className="rounded-2xl border border-[rgba(34,197,94,.28)] bg-[rgba(34,197,94,.08)] p-4" aria-live="polite" aria-label="Statement send result">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#166534]">Statement Sent Successfully</p>
                            <p className="mt-1 text-sm font-semibold leading-6 text-[#166534]">
                              The statement remains visible for review. Choose the next action when ready.
                            </p>
                          </div>
                          <span className="rounded-full border border-[rgba(34,197,94,.28)] bg-white px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#166534]">
                            {statementCompletionSummary.deliveryStatus}
                          </span>
                        </div>
                        <div className="mt-3 grid gap-2 sm:grid-cols-2">
                          <WorkspaceField label="Statement Number" value={statementCompletionSummary.statementNumber} />
                          <WorkspaceField label="Recipient" value={statementCompletionSummary.recipient} />
                          <WorkspaceField label="Delivery Method" value={statementCompletionSummary.deliveryMethod} />
                          <WorkspaceField label="Date/Time Sent" value={statementSentAtLabel} />
                          <WorkspaceField label="Timeline" value="Updated" />
                          <WorkspaceField label="Recovery Activity" value="Updated" />
                          <WorkspaceField label="Statement History" value="Updated" />
                        </div>
                        <div className="mt-4 flex flex-wrap gap-2">
                          <button type="button" onClick={onClose} className="focus-ring min-h-9 rounded-full border border-[#BFEAFF] bg-white px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#0A2540]">
                            Close
                          </button>
                          <button type="button" onClick={onSendAnotherStatement} className="focus-ring min-h-9 rounded-full border border-[#14B8A6] bg-white px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#0F766E]">
                            Send Another Statement
                          </button>
                          <button type="button" onClick={onHistory} className="focus-ring min-h-9 rounded-full border border-[#BFEAFF] bg-white px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#0A2540]">
                            View Statement History
                          </button>
                          <button type="button" onClick={onOpenCustomerTimeline} className="focus-ring min-h-9 rounded-full border border-[#BFEAFF] bg-white px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#0A2540]">
                            Open Customer Timeline
                          </button>
                        </div>
                      </section>
                    ) : null}
                    {!isLetter && statementFailureSummary ? (
                      <section className="rounded-2xl border border-[rgba(248,113,113,.28)] bg-[rgba(248,113,113,.08)] p-4" aria-live="assertive" aria-label="Statement send failure">
                        <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#B91C1C]">Statement could not be sent.</p>
                        <div className="mt-3 grid gap-2">
                          <WorkspaceField label="Reason" value={statementFailureSummary.reason} />
                          <WorkspaceField label="Delivery Method" value={statementFailureSummary.deliveryMethod} />
                        </div>
                        <div className="mt-4 flex flex-wrap gap-2">
                          <button
                            type="button"
                            disabled={isSubmitting}
                            onClick={() => void onSend()}
                            className="focus-ring min-h-9 rounded-full border border-[rgba(255,106,43,.42)] bg-white px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#7C2D12] disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            Retry
                          </button>
                          <button type="button" onClick={onClose} className="focus-ring min-h-9 rounded-full border border-[#BFEAFF] bg-white px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#0A2540]">
                            Cancel
                          </button>
                        </div>
                      </section>
                    ) : null}
                    {isLetter && letterSuccessSummary ? (
                      <section className="rounded-2xl border border-[rgba(34,197,94,.28)] bg-[rgba(34,197,94,.08)] p-4" aria-live="polite" aria-label="Certified letter generation result">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#166534]">Certified Letter Generated Successfully</p>
                            <p className="mt-1 text-sm font-semibold leading-6 text-[#166534]">
                              The certified correspondence record is ready for follow-up without leaving Recovery Workbench.
                            </p>
                          </div>
                          <span className="rounded-full border border-[rgba(34,197,94,.28)] bg-white px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#166534]">
                            {letterSuccessSummary.deliveryStatus}
                          </span>
                        </div>
                        <div className="mt-3 grid gap-2 sm:grid-cols-2">
                          <WorkspaceField label="Letter Number" value={letterSuccessSummary.letterNumber} />
                          <WorkspaceField label="Letter Type" value={letterSuccessSummary.letterType} />
                          <WorkspaceField label="Customer" value={letterSuccessSummary.customer} />
                          <WorkspaceField label="Timeline" value="Updated" />
                          <WorkspaceField label="Recovery Activity" value="Updated" />
                          <WorkspaceField label="Letter History" value="Updated" />
                        </div>
                        <div className="mt-4 flex flex-wrap gap-2">
                          <button type="button" onClick={onClose} className="focus-ring min-h-9 rounded-full border border-[#BFEAFF] bg-white px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#0A2540]">
                            Close
                          </button>
                          <button type="button" onClick={onGenerateAnotherLetter} className="focus-ring min-h-9 rounded-full border border-[#14B8A6] bg-white px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#0F766E]">
                            Generate Another Letter
                          </button>
                        </div>
                      </section>
                    ) : successMessage && (isLetter || (!statementCompletionSummary && !statementFailureSummary)) ? (
                      <div className="rounded-2xl border border-[rgba(34,197,94,.24)] bg-[rgba(34,197,94,.08)] px-4 py-3 text-sm font-semibold leading-6 text-[#166534]" aria-live="polite">
                        {successMessage}
                      </div>
                    ) : null}
                    {errorMessage && (isLetter || !statementFailureSummary) ? (
                      <div className="rounded-2xl border border-[rgba(248,113,113,.28)] bg-[rgba(248,113,113,.08)] px-4 py-3 text-sm font-semibold leading-6 text-[#B91C1C]" aria-live="assertive">
                        {errorMessage}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </section>

            <section className={isLetter ? `${mobilePane === "preview" ? "block" : "hidden"} min-h-0 min-w-0 bg-[#F4FBFF] lg:block` : "min-h-0 min-w-0 bg-[#F4FBFF]"}>
              <div className="flex h-full min-h-0 flex-col">
                <div className="shrink-0 border-b border-[#BFEAFF] bg-white p-4">
                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">{isLetter ? "Live Letter Preview" : "Live Statement Preview"}</p>
                  <p className="mt-1 text-sm font-semibold leading-6 text-[#475569]">
                    {isLetter ? "Customer-facing correspondence content for preview, print, PDF, and delivery." : "Customer-facing statement for preview, print, PDF, and send."}
                  </p>
                </div>
                <div
                  className="min-h-0 flex-1 overflow-y-auto p-4 lg:p-6"
                  aria-label={`${documentLabel} live document preview`}
                  data-statement-execution-scroll-owner={isLetter ? undefined : "preview"}
                >
                  {isLetter ? (
                    <ARRecoveryLetterExecutionPreview preview={letterPreview} clientProfile={clientProfile} />
                  ) : (
                    <ARRecoveryStatementExecutionPreview preview={statementPreview} deliveryMethod={statementForm.deliveryOption} />
                  )}
                </div>
              </div>
            </section>
            </div>
            {!isLetter ? (
              <div className="grid gap-3 border-t border-[#BFEAFF] bg-white px-4 py-3 lg:px-5">
                <ARRecoveryStatementReadinessPanel readiness={statementReadiness} />
                <ARRecoveryStatementVerifiedPanel items={statementVerifiedItems} />
                <ARRecoveryStatementImpactPreview items={statementImpactItems} />
              </div>
            ) : null}
          </div>

          <footer className="shrink-0 border-t border-[#BFEAFF] bg-white px-4 py-3 lg:px-5">
            {isLetter ? (
              <>
                <ARRecoveryLetterReadinessPanel readiness={letterReadiness} lifecycleStatus={letterLifecycleStatus} />
                <ARRecoveryLetterImpactPreview items={letterImpactItems} />
              </>
            ) : null}
            {isLetter ? (
              <div className="mb-3 grid grid-cols-2 gap-2 lg:hidden">
                <button
                  type="button"
                  onClick={() => setMobilePane("controls")}
                  className={`focus-ring rounded-full border px-3 py-2 text-xs font-black uppercase tracking-[0.12em] ${mobilePane === "controls" ? "border-[#14B8A6] bg-[#F4FBFF] text-[#0A2540]" : "border-[#BFEAFF] text-[#475569]"}`}
                >
                  Controls
                </button>
                <button
                  type="button"
                  onClick={() => setMobilePane("preview")}
                  className={`focus-ring rounded-full border px-3 py-2 text-xs font-black uppercase tracking-[0.12em] ${mobilePane === "preview" ? "border-[#14B8A6] bg-[#F4FBFF] text-[#0A2540]" : "border-[#BFEAFF] text-[#475569]"}`}
                >
                  Preview
                </button>
              </div>
            ) : null}
            <div className="flex flex-wrap items-center justify-end gap-2">
              <button type="button" ref={historyButtonRef} onClick={onHistory} className="focus-ring min-h-10 rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-4 text-xs font-black uppercase tracking-[0.12em] text-[#0A2540]">
                History
              </button>
              <button type="button" onClick={onPrint} className="focus-ring min-h-10 rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-4 text-xs font-black uppercase tracking-[0.12em] text-[#0A2540]">
                Print
              </button>
              <button type="button" onClick={onPdf} className="focus-ring min-h-10 rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-4 text-xs font-black uppercase tracking-[0.12em] text-[#0A2540]">
                PDF
              </button>
              <button
                type="button"
                disabled={isSubmitting || !canSendDocument || (!isLetter && Boolean(statementCompletionSummary))}
                onClick={() => void onSend()}
                className="focus-ring min-h-10 rounded-full border border-[rgba(255,106,43,.42)] bg-[rgba(255,106,43,.16)] px-5 text-xs font-black uppercase tracking-[0.12em] text-[#7C2D12] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting
                  ? (isLetter ? "Generating" : "Sending Statement...")
                  : !isLetter && statementCompletionSummary
                    ? "Statement Sent"
                    : isLetter
                      ? "Generate Certified Letter"
                      : "Send Statement"}
              </button>
              <button type="button" onClick={onClose} className="focus-ring min-h-10 rounded-full border border-[#BFEAFF] bg-white px-4 text-xs font-black uppercase tracking-[0.12em] text-[#475569]">
                Close
              </button>
            </div>
          </footer>
        </aside>
      </div>
    );
  },
);
ARRecoveryDocumentExecutionShell.displayName = "ARRecoveryDocumentExecutionShell";

type ARRecoveryDocumentReadinessItem = {
  label: string;
  ready: boolean;
  optional?: boolean;
  message: string;
};

function buildLetterDocumentReadiness(
  form: ARRecoveryLetterFormState,
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>,
  selectedAccountPromises: Array<{ id: string; amountCents: number; promiseDate: string; status: string }>,
  preview: LetterPreview,
): ARRecoveryDocumentReadinessItem[] {
  const recipient = parseARRecoveryRecipient(form.recipient || `${account.contactName} <${account.contactEmail}>`);
  const selectedPromise = selectedAccountPromises.find((promise) => promise.id === form.relatedPromise);
  const readiness: ARRecoveryDocumentReadinessItem[] = [
    {
      label: "Approved Template Selected",
      ready: Boolean(form.letterType),
      message: form.letterType ? form.letterType : "Select an approved letter template.",
    },
    {
      label: "Recipient Available",
      ready: Boolean(form.recipient.trim()) && isValidARRecoveryEmail(recipient.email),
      message: isValidARRecoveryEmail(recipient.email) ? recipient.email : "A valid account contact email is required.",
    },
    {
      label: "Invoices Selected",
      ready: form.relatedInvoices.length > 0,
      message: form.relatedInvoices.length ? `${form.relatedInvoices.length} invoice${form.relatedInvoices.length === 1 ? "" : "s"} selected.` : "Select at least one invoice.",
    },
    {
      label: "Merge Fields Complete",
      ready: Boolean(account.customerName && account.customerNumber && account.openBalanceCents >= 0),
      message: account.customerName && account.customerNumber ? "Customer, account, balance, and aging fields are available." : "Customer and account fields are required.",
    },
    {
      label: "Promise Applied",
      ready: Boolean(selectedPromise),
      optional: !form.relatedPromise,
      message: selectedPromise
        ? `${selectedPromise.status} promise for ${formatARRecoveryCurrency(selectedPromise.amountCents)} on ${formatCapturedDate(selectedPromise.promiseDate)}.`
        : "No promise selected. Promise language will be omitted.",
    },
  ];

  if (preview.requiresSpreadsheetAttachment) {
    readiness.splice(4, 0,
      {
        label: "Invoice Spreadsheet Generated",
        ready: preview.invoiceAttachment?.status === "ready",
        message: preview.invoiceAttachment?.status === "ready"
          ? `${preview.invoiceAttachment.filename} generated for ${preview.invoiceAttachment.invoiceCount} invoices.`
          : preview.invoiceAttachment?.error || "Generate the invoice spreadsheet before sending.",
      },
      {
        label: "Invoice Spreadsheet Attached",
        ready: preview.invoiceAttachment?.attached === true,
        message: preview.invoiceAttachment?.attached
          ? "Detailed invoice spreadsheet will be attached to the outbound communication."
          : "Attach the invoice spreadsheet before sending.",
      },
    );
  }

  return readiness;
}

function ARRecoveryLetterReadinessPanel({ readiness, lifecycleStatus }: { readiness: ARRecoveryDocumentReadinessItem[]; lifecycleStatus: string }) {
  const blockingIssues = readiness.filter((item) => !item.ready && !item.optional);
  return (
    <section className="mb-3 rounded-2xl border border-[#BFEAFF] bg-[#F8FCFF] p-3" aria-label="Letter readiness" aria-live="polite">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Letter Ready</p>
          <p className="mt-1 text-xs font-semibold leading-5 text-[#64748B]">
            Generate Certified Letter is available when required checks are complete.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="rounded-full border border-[#BFEAFF] bg-white px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#0A2540]">
            {lifecycleStatus}
          </span>
          <span className={`rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${
            blockingIssues.length
              ? "border-[rgba(248,113,113,.28)] bg-[rgba(248,113,113,.08)] text-[#B91C1C]"
              : "border-[rgba(34,197,94,.28)] bg-[rgba(34,197,94,.1)] text-[#166534]"
          }`}>
            {blockingIssues.length ? `${blockingIssues.length} Required` : "Ready to Generate"}
          </span>
        </div>
      </div>
      <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-5">
        {readiness.map((item) => (
          <div key={item.label} className="rounded-xl border border-[#BFEAFF] bg-white p-2">
            <p className={`text-xs font-black ${item.ready ? "text-[#166534]" : item.optional ? "text-[#475569]" : "text-[#B91C1C]"}`}>
              {item.ready ? "Ready" : item.optional ? "Optional" : "Required"}: {item.label}
            </p>
            <p className="mt-1 text-xs font-semibold leading-5 text-[#64748B]">{item.message}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function ARRecoveryLetterImpactPreview({ items }: { items: string[] }) {
  return (
    <section className="mb-3 rounded-2xl border border-[#BFEAFF] bg-white p-3" aria-label="Correspondence impact preview">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Correspondence Impact Preview</p>
          <p className="mt-1 text-xs font-semibold leading-5 text-[#64748B]">
            Generating this letter will create the operational correspondence record and update the shared account history.
          </p>
        </div>
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-5">
        {items.map((item) => (
          <div key={item} className="rounded-xl border border-[#BFEAFF] bg-[#F8FCFF] p-2">
            <p className="text-xs font-black text-[#0A2540]">{item}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function buildStatementDocumentReadiness(
  form: ARRecoveryStatementFormState,
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>,
  clientProfile: ReturnType<typeof loadARRecoveryWorkspace>["clientProfile"],
  preview: StatementPreview,
): ARRecoveryDocumentReadinessItem[] {
  const selectedPromise = preview.selectedPromise;
  return [
    {
      label: "Approved Statement Template Selected",
      ready: Boolean(form.statementType),
      message: form.statementType ? form.statementType : "Select an approved statement template.",
    },
    {
      label: "Recipient Available",
      ready: Boolean(account.contactEmail && isValidARRecoveryEmail(account.contactEmail)),
      message: isValidARRecoveryEmail(account.contactEmail) ? account.contactEmail : "A valid account contact email is required.",
    },
    {
      label: "At Least One Invoice Selected",
      ready: form.relatedInvoices.length > 0,
      message: form.relatedInvoices.length ? `${form.relatedInvoices.length} invoice${form.relatedInvoices.length === 1 ? "" : "s"} selected.` : "Select at least one invoice.",
    },
    {
      label: "Statement Date Available",
      ready: Boolean(form.statementDate),
      message: form.statementDate ? formatCapturedDate(form.statementDate) : "Select a statement date.",
    },
    {
      label: "Merge Fields Complete",
      ready: Boolean(account.customerName && account.customerNumber && preview.branding.companyName),
      message: account.customerName && account.customerNumber ? "Customer, account, branding, and invoice fields are available." : "Customer and account fields are required.",
    },
    {
      label: "Remittance Contact Available",
      ready: Boolean(clientProfile.letterDefaults.contactName && clientProfile.letterDefaults.phone && clientProfile.letterDefaults.email),
      message: clientProfile.letterDefaults.contactName && clientProfile.letterDefaults.phone && clientProfile.letterDefaults.email
        ? `${clientProfile.letterDefaults.contactName} | ${clientProfile.letterDefaults.phone} | ${clientProfile.letterDefaults.email}`
        : "Remittance contact name, phone, and email are required.",
    },
    {
      label: "Promise Applied",
      ready: Boolean(selectedPromise),
      optional: !form.relatedPromise,
      message: selectedPromise
        ? `${selectedPromise.status} promise for ${selectedPromise.amount} on ${selectedPromise.promiseDate}.`
        : "No promise selected. Promise language will be omitted.",
    },
  ];
}

function ARRecoveryStatementReadinessPanel({ readiness }: { readiness: ARRecoveryDocumentReadinessItem[] }) {
  const blockingIssues = readiness.filter((item) => !item.ready && !item.optional);
  return (
    <section className="mb-3 rounded-2xl border border-[#BFEAFF] bg-[#F8FCFF] p-3" aria-label="Statement readiness" aria-live="polite">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Statement Ready</p>
          <p className="mt-1 text-xs font-semibold leading-5 text-[#64748B]">
            Send Statement is available when required checks are complete.
          </p>
        </div>
        <span className={`rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${
          blockingIssues.length
            ? "border-[rgba(248,113,113,.28)] bg-[rgba(248,113,113,.08)] text-[#B91C1C]"
            : "border-[rgba(34,197,94,.28)] bg-[rgba(34,197,94,.1)] text-[#166534]"
        }`}>
          {blockingIssues.length ? `${blockingIssues.length} Required` : "Ready to Send"}
        </span>
      </div>
      <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-6">
        {readiness.map((item) => (
          <div key={item.label} className="rounded-xl border border-[#BFEAFF] bg-white p-2">
            <p className={`text-xs font-black ${item.ready ? "text-[#166534]" : item.optional ? "text-[#475569]" : "text-[#B91C1C]"}`}>
              {item.ready ? "Ready" : item.optional ? "Optional" : "Required"}: {item.label}
            </p>
            <p className="mt-1 text-xs font-semibold leading-5 text-[#64748B]">{item.message}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function ARRecoveryStatementVerifiedPanel({ items }: { items: ARRecoveryDocumentReadinessItem[] }) {
  const blockingIssues = items.filter((item) => !item.ready && !item.optional);
  return (
    <section className="mb-3 rounded-2xl border border-[#BFEAFF] bg-white p-3" aria-label="Statement verified" aria-live="polite">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Statement Verified</p>
          <p className="mt-1 text-xs font-semibold leading-5 text-[#64748B]">
            Collector review confirms the customer, balance, content, and delivery method before sending.
          </p>
        </div>
        <span className={`rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${
          blockingIssues.length
            ? "border-[rgba(248,113,113,.28)] bg-[rgba(248,113,113,.08)] text-[#B91C1C]"
            : "border-[rgba(34,197,94,.28)] bg-[rgba(34,197,94,.1)] text-[#166534]"
        }`}>
          {blockingIssues.length ? "Review Required" : "Verified"}
        </span>
      </div>
      <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-4">
        {items.map((item) => (
          <div key={item.label} className="rounded-xl border border-[#BFEAFF] bg-[#F8FCFF] p-2">
            <p className={`text-xs font-black ${item.ready ? "text-[#166534]" : "text-[#B91C1C]"}`}>
              {item.ready ? "Verified" : "Review"}: {item.label}
            </p>
            <p className="mt-1 break-words text-xs font-semibold leading-5 text-[#64748B]">{item.message}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function ARRecoveryStatementImpactPreview({ items }: { items: string[] }) {
  return (
    <section className="mb-3 rounded-2xl border border-[#BFEAFF] bg-white p-3" aria-label="Statement delivery impact preview">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Delivery Impact</p>
          <p className="mt-1 text-xs font-semibold leading-5 text-[#64748B]">
            Sending this statement will create the official statement record and update shared account history.
          </p>
        </div>
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-5">
        {items.map((item) => (
          <div key={item} className="rounded-xl border border-[#BFEAFF] bg-[#F8FCFF] p-2">
            <p className="text-xs font-black text-[#0A2540]">{item}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

type LetterExecutionInvoiceRecord = ReturnType<typeof buildInvoicePlaceholders>[number];
type LetterInvoiceQuickFilter = "all" | "open" | "past_due" | "promise" | "30" | "60" | "90" | "selected";
type LetterInvoiceSearchCategory = "invoice" | "reference" | "amount" | "date" | "aging" | "status";

type LetterInvoiceSearchField = {
  category: LetterInvoiceSearchCategory;
  label: string;
  value: string;
  exactPriority: number;
  partialPriority: number;
};

type RankedLetterInvoice = {
  invoice: LetterExecutionInvoiceRecord;
  originalIndex: number;
  rank: number;
  matchedField?: LetterInvoiceSearchField;
  matchType?: "exact" | "startsWith" | "contains" | "partial";
};

const LETTER_INVOICE_QUICK_FILTER_GROUPS: Array<{ label: string; filters: Array<{ key: LetterInvoiceQuickFilter; label: string }> }> = [
  {
    label: "Status",
    filters: [
      { key: "all", label: "All" },
      { key: "open", label: "Open" },
      { key: "past_due", label: "Past Due" },
      { key: "promise", label: "Promise" },
    ],
  },
  {
    label: "Aging",
    filters: [
      { key: "30", label: "30+" },
      { key: "60", label: "60+" },
      { key: "90", label: "90+" },
    ],
  },
  {
    label: "Selection",
    filters: [
      { key: "selected", label: "Selected" },
    ],
  },
];

const LETTER_INVOICE_QUICK_FILTERS = LETTER_INVOICE_QUICK_FILTER_GROUPS.flatMap((group) => group.filters);

function normalizeLetterInvoiceSearchValue(value: string) {
  return value
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

function compactLetterInvoiceSearchValue(value: string) {
  return normalizeLetterInvoiceSearchValue(value).replace(/\s+/g, "");
}

function formatLetterInvoiceLongSearchDate(value: string) {
  if (!value || !Number.isFinite(new Date(value).getTime())) return "";
  return new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric", year: "numeric" }).format(new Date(value));
}

function formatLetterInvoiceShortSearchDate(value: string) {
  if (!value || !Number.isFinite(new Date(value).getTime())) return "";
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(new Date(value));
}

function formatLetterInvoiceNumericSearchDate(value: string) {
  const date = new Date(value);
  if (!value || !Number.isFinite(date.getTime())) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${month}/${day}/${date.getFullYear()}`;
}

function formatLetterInvoiceMonthDaySearchDate(value: string) {
  const date = new Date(value);
  if (!value || !Number.isFinite(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(date);
}

function normalizeLetterInvoiceAmountValue(value: string) {
  const cleaned = value.replace(/[^0-9.]/g, "");
  if (!cleaned) return "";
  const numericValue = Number(cleaned);
  return Number.isFinite(numericValue) ? numericValue.toFixed(2) : cleaned;
}

function buildLetterInvoiceAmountValues(cents: number) {
  const amount = cents / 100;
  return [
    formatARRecoveryCurrency(cents),
    amount.toFixed(2),
    amount.toFixed(0),
  ];
}

function buildLetterInvoiceDateValues(value: string) {
  return [
    value,
    value.slice(0, 10),
    formatCapturedDate(value),
    formatLetterInvoiceLongSearchDate(value),
    formatLetterInvoiceShortSearchDate(value),
    formatLetterInvoiceNumericSearchDate(value),
    formatLetterInvoiceMonthDaySearchDate(value),
  ].filter(Boolean);
}

function buildLetterInvoiceSearchFields(invoice: LetterExecutionInvoiceRecord, agingSummary: string): LetterInvoiceSearchField[] {
  const fields: LetterInvoiceSearchField[] = [
    { category: "invoice", label: "invoice number", value: invoice.invoiceNumber, exactPriority: 1, partialPriority: 2 },
    { category: "amount", label: "open balance", value: formatARRecoveryCurrency(invoice.currentBalanceCents), exactPriority: 4, partialPriority: 4 },
    { category: "amount", label: "original amount", value: formatARRecoveryCurrency(invoice.originalAmountCents), exactPriority: 4, partialPriority: 4 },
    { category: "date", label: "due date", value: formatCapturedDate(invoice.dueDate), exactPriority: 5, partialPriority: 5 },
    { category: "date", label: "invoice date", value: formatCapturedDate(invoice.invoiceDate), exactPriority: 6, partialPriority: 6 },
    { category: "aging", label: "aging", value: agingSummary, exactPriority: 7, partialPriority: 7 },
    { category: "status", label: "status", value: invoice.status, exactPriority: 8, partialPriority: 8 },
  ];

  if (invoice.purchaseOrder) {
    fields.push({ category: "reference", label: "PO/reference", value: invoice.purchaseOrder, exactPriority: 3, partialPriority: 3 });
  }
  if (invoice.referenceNumber) {
    fields.push({ category: "reference", label: "PO/reference", value: invoice.referenceNumber, exactPriority: 3, partialPriority: 3 });
  }

  buildLetterInvoiceAmountValues(invoice.currentBalanceCents).forEach((value) => {
    fields.push({ category: "amount", label: "open balance", value, exactPriority: 4, partialPriority: 4 });
  });
  buildLetterInvoiceAmountValues(invoice.originalAmountCents).forEach((value) => {
    fields.push({ category: "amount", label: "original amount", value, exactPriority: 4, partialPriority: 4 });
  });
  buildLetterInvoiceDateValues(invoice.invoiceDate).forEach((value) => {
    fields.push({ category: "date", label: "invoice date", value, exactPriority: 6, partialPriority: 6 });
  });
  buildLetterInvoiceDateValues(invoice.dueDate).forEach((value) => {
    fields.push({ category: "date", label: "due date", value, exactPriority: 5, partialPriority: 5 });
  });

  return fields;
}

function rankLetterInvoiceSearchMatch(
  invoice: LetterExecutionInvoiceRecord,
  agingSummary: string,
  search: string,
  originalIndex: number,
): RankedLetterInvoice | null {
  const normalizedSearch = normalizeLetterInvoiceSearchValue(search);
  if (!normalizedSearch) return { invoice, originalIndex, rank: originalIndex + 1000 };

  const compactSearch = compactLetterInvoiceSearchValue(search);
  const amountSearch = normalizeLetterInvoiceAmountValue(search);
  const fields = buildLetterInvoiceSearchFields(invoice, agingSummary);
  let bestMatch: RankedLetterInvoice | null = null;

  fields.forEach((field) => {
    const normalizedValue = normalizeLetterInvoiceSearchValue(field.value);
    const compactValue = compactLetterInvoiceSearchValue(field.value);
    const amountValue = field.category === "amount" ? normalizeLetterInvoiceAmountValue(field.value) : "";
    let rank = Number.POSITIVE_INFINITY;
    let matchType: RankedLetterInvoice["matchType"] | undefined;

    if (field.category === "amount" && amountSearch && amountValue === amountSearch) {
      rank = field.exactPriority;
      matchType = "exact";
    } else if (normalizedValue === normalizedSearch || (compactSearch && compactValue === compactSearch)) {
      rank = field.exactPriority;
      matchType = "exact";
    } else if (field.category === "invoice" && compactSearch && compactValue.startsWith(compactSearch)) {
      rank = 2;
      matchType = "startsWith";
    } else if (field.category === "invoice" && compactSearch && compactValue.includes(compactSearch)) {
      rank = field.partialPriority;
      matchType = "contains";
    } else if (normalizedValue.includes(normalizedSearch) || (compactSearch && compactValue.includes(compactSearch))) {
      rank = field.partialPriority;
      matchType = "partial";
    }

    if (!Number.isFinite(rank)) return;
    if (!bestMatch || rank < bestMatch.rank) {
      bestMatch = { invoice, originalIndex, rank, matchedField: field, matchType };
    }
  });

  return bestMatch;
}

function getLetterInvoiceAgingNumber(agingSummary: string) {
  const numbers = agingSummary.match(/\d+/g)?.map(Number).filter(Number.isFinite) || [];
  return numbers.length ? Math.max(...numbers) : 0;
}

function invoiceMatchesLetterQuickFilter(
  invoice: LetterExecutionInvoiceRecord,
  agingSummary: string,
  filter: LetterInvoiceQuickFilter,
  selectedInvoiceNumbers: Set<string>,
  promiseInvoiceNumbers: Set<string>,
) {
  if (filter === "all") return true;
  if (filter === "selected") return selectedInvoiceNumbers.has(invoice.invoiceNumber);
  if (filter === "open") return invoice.status.toLowerCase().includes("open");
  if (filter === "past_due") return invoice.status.toLowerCase().includes("past due");
  if (filter === "promise") return promiseInvoiceNumbers.has(invoice.invoiceNumber);
  const agingNumber = getLetterInvoiceAgingNumber(agingSummary);
  if (filter === "30") return agingNumber >= 30;
  if (filter === "60") return agingNumber >= 60;
  if (filter === "90") return agingNumber >= 90;
  return true;
}

function renderInvoiceSearchHighlight(value: string, search: string) {
  const trimmedSearch = search.trim();
  if (!trimmedSearch) return value;
  const directIndex = value.toLowerCase().indexOf(trimmedSearch.toLowerCase());
  const normalizedValue = normalizeLetterInvoiceSearchValue(value);
  const normalizedSearch = normalizeLetterInvoiceSearchValue(search);
  const compactValue = compactLetterInvoiceSearchValue(value);
  const compactSearch = compactLetterInvoiceSearchValue(search);
  const fieldMatches = normalizedSearch
    ? normalizedValue.includes(normalizedSearch) || (Boolean(compactSearch) && compactValue.includes(compactSearch))
    : false;

  if (directIndex >= 0) {
    const before = value.slice(0, directIndex);
    const match = value.slice(directIndex, directIndex + trimmedSearch.length);
    const after = value.slice(directIndex + trimmedSearch.length);
    return (
      <>
        {before}
        <mark className="rounded bg-[rgba(255,106,43,.18)] px-0.5 text-[#7C2D12]">{match}</mark>
        {after}
      </>
    );
  }

  if (!fieldMatches) return value;
  return <mark className="rounded bg-[rgba(255,106,43,.14)] px-0.5 text-[#0A2540]">{value}</mark>;
}

function ARRecoveryLetterExecutionControls({
  account,
  letterForm,
  setLetterForm,
  selectedAccountPromises,
  preview,
}: {
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  letterForm: ARRecoveryLetterFormState;
  setLetterForm: Dispatch<SetStateAction<ARRecoveryLetterFormState>>;
  selectedAccountPromises: Array<{ id: string; amountCents: number; promiseDate: string; status: string; invoiceLabels?: string[] }>;
  preview: LetterPreview;
}) {
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const [invoiceQuickFilter, setInvoiceQuickFilter] = useState<LetterInvoiceQuickFilter>("all");
  const invoices = useMemo(() => buildInvoicePlaceholders(account), [account]);
  const selectedInvoiceNumbers = useMemo(() => new Set(letterForm.relatedInvoices), [letterForm.relatedInvoices]);
  const promiseInvoiceNumbers = useMemo(() => new Set(selectedAccountPromises.flatMap((promise) => promise.invoiceLabels || [])), [selectedAccountPromises]);
  const activeSearch = invoiceSearch.trim().length > 0;
  const rankedInvoices = useMemo(() => {
    return invoices
      .map((invoice, originalIndex) => rankLetterInvoiceSearchMatch(invoice, account.agingSummary, invoiceSearch, originalIndex))
      .filter((result): result is RankedLetterInvoice => Boolean(result))
      .filter((result) => invoiceMatchesLetterQuickFilter(result.invoice, account.agingSummary, invoiceQuickFilter, selectedInvoiceNumbers, promiseInvoiceNumbers))
      .sort((left, right) => left.rank - right.rank || left.originalIndex - right.originalIndex);
  }, [account.agingSummary, invoiceQuickFilter, invoiceSearch, invoices, promiseInvoiceNumbers, selectedInvoiceNumbers]);
  const visibleInvoices = rankedInvoices.map((result) => result.invoice);
  const selectedInvoices = invoices.filter((invoice) => selectedInvoiceNumbers.has(invoice.invoiceNumber));
  const selectedInvoiceTotalCents = selectedInvoices.reduce((total, invoice) => total + invoice.amountCents, 0);
  const activeFilter = invoiceQuickFilter !== "all";
  const activeFilterLabel = LETTER_INVOICE_QUICK_FILTERS.find((filter) => filter.key === invoiceQuickFilter)?.label || "All";
  const selectedSummary = `${selectedInvoices.length} invoice${selectedInvoices.length === 1 ? "" : "s"} | ${formatARRecoveryCurrency(selectedInvoiceTotalCents)}`;
  const correspondenceState = letterForm.letterType && selectedInvoices.length ? "Ready" : "Draft";
  const searchResultSummary = `${invoices.length} invoices • ${visibleInvoices.length} matching • ${selectedInvoices.length} selected • ${formatARRecoveryCurrency(selectedInvoiceTotalCents)} selected`;

  function toggleInvoice(invoiceNumber: string) {
    setLetterForm((current) => {
      const selected = new Set(current.relatedInvoices);
      if (selected.has(invoiceNumber)) {
        selected.delete(invoiceNumber);
      } else {
        selected.add(invoiceNumber);
      }
      return { ...current, relatedInvoices: Array.from(selected) };
    });
  }

  return (
    <div className="grid gap-4">
      <label className="grid gap-1.5">
        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Letter Type / Approved Template</span>
        <select
          value={letterForm.letterType}
          onChange={(event) => setLetterForm((current) => ({ ...current, letterType: event.target.value as ARRecoveryLetterFormState["letterType"] }))}
          className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
        >
          <option value="">Select approved template</option>
          {LETTER_TYPE_OPTIONS.map((letterType) => <option key={letterType} value={letterType}>{letterType}</option>)}
        </select>
      </label>

      <section className="rounded-2xl border border-[#BFEAFF] bg-white p-3" aria-label="Approved template confidence">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Template Confidence</p>
            <p className="mt-1 text-xs font-semibold leading-5 text-[#64748B]">
              Collectors and Team Leads generate only approved correspondence templates from Recovery Workbench.
            </p>
          </div>
          <span className={`rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${
            letterForm.letterType
              ? "border-[rgba(34,197,94,.28)] bg-[rgba(34,197,94,.08)] text-[#166534]"
              : "border-[rgba(248,113,113,.28)] bg-[rgba(248,113,113,.08)] text-[#B91C1C]"
          }`}>
            {letterForm.letterType ? "Approved Enterprise Template" : "Template Required"}
          </span>
        </div>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <WorkspaceField label="Selected Template" value={letterForm.letterType || "Not Selected"} />
          <WorkspaceField label="Editing" value="Managed in Letter Center" />
        </div>
      </section>

      <div className="rounded-2xl border border-[#BFEAFF] bg-white p-3">
        <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Recipient</p>
        <p className="mt-2 break-words text-sm font-semibold leading-6 text-[#0A2540]">{letterForm.recipient || `${account.contactName} <${account.contactEmail}>`}</p>
        <p className="mt-2 text-xs font-semibold leading-5 text-[#64748B]">Recipient is populated from the selected account contact.</p>
      </div>

      <section className="rounded-2xl border border-[#BFEAFF] bg-white p-3" aria-label="Correspondence summary">
        <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Correspondence Summary</p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <WorkspaceField label="Letter Type" value={letterForm.letterType || "Not Selected"} />
          <WorkspaceField label="Recipient" value={letterForm.recipient || `${account.contactName} <${account.contactEmail}>`} />
          <WorkspaceField label="Selected Invoices" value={selectedSummary} />
          <WorkspaceField label="Lifecycle State" value={correspondenceState} />
          <WorkspaceField label="Attachments" value={preview.invoiceAttachment ? preview.invoiceAttachment.filename : preview.requiresSpreadsheetAttachment ? "Required" : "Not Required"} />
          <WorkspaceField label="Related Promise" value={preview.selectedPromise ? `${preview.selectedPromise.status} | ${preview.selectedPromise.amount}` : "None"} />
        </div>
      </section>

      <section className="rounded-2xl border border-[#BFEAFF] bg-white p-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Related Invoices</p>
            <p className="mt-1 text-xs font-semibold leading-5 text-[#64748B]">
              Find and select every invoice that should appear in the letter context.
            </p>
          </div>
          <span className="rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.12em] text-[#0A2540]" aria-live="polite">
            Selected: {selectedSummary}
          </span>
        </div>
        <div className="mt-3 grid gap-2">
          <label className="grid gap-1.5" htmlFor="letter-invoice-search">
            <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Find Invoices</span>
            <span className="relative block min-w-0">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#14B8A6]" aria-hidden="true" />
              <input
                id="letter-invoice-search"
                value={invoiceSearch}
                onChange={(event) => setInvoiceSearch(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Escape" && invoiceSearch.trim()) {
                    event.stopPropagation();
                    setInvoiceSearch("");
                  }
                }}
                placeholder="Find invoice by number, amount, due date, aging, or status..."
                className="focus-ring min-h-10 w-full rounded-xl border border-[#BFEAFF] bg-white py-2 pl-9 pr-20 text-sm font-semibold text-[#0A2540] placeholder:text-[#64748B]"
              />
              {activeSearch ? (
                <button
                  type="button"
                  onClick={() => setInvoiceSearch("")}
                  className="focus-ring absolute right-2 top-1/2 min-h-7 -translate-y-1/2 rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-2 text-[10px] font-black uppercase tracking-[0.12em] text-[#0A2540]"
                  aria-label="Clear invoice search"
                >
                  Clear
                </button>
              ) : null}
            </span>
            <span className="text-xs font-semibold leading-5 text-[#64748B]">
              Type what you know. The finder checks invoice number, reference, amount, dates, aging, and status.
            </span>
          </label>
          <div className="rounded-xl border border-[#BFEAFF] bg-[#F8FCFF] p-2" aria-label="Invoice quick filters">
            {LETTER_INVOICE_QUICK_FILTER_GROUPS.map((group) => (
              <div key={group.label} className="mb-2 flex min-w-0 flex-wrap items-center gap-2 last:mb-0">
                <span className="text-[10px] font-black uppercase tracking-[0.14em] text-[#64748B]">{group.label}</span>
                {group.filters.map((filter) => {
                  const active = invoiceQuickFilter === filter.key;
                  return (
                    <button
                      key={filter.key}
                      type="button"
                      onClick={() => setInvoiceQuickFilter(filter.key)}
                      className={`focus-ring min-h-7 rounded-full border px-2.5 text-[10px] font-black uppercase tracking-[0.12em] transition ${
                        active
                          ? "border-[#14B8A6] bg-[#14B8A6] text-white"
                          : "border-[#BFEAFF] bg-white text-[#0A2540] hover:border-[#14B8A6]"
                      }`}
                      aria-pressed={active}
                    >
                      {filter.label}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-[#BFEAFF] bg-[#F8FCFF] px-3 py-2 text-xs font-semibold leading-5 text-[#475569]" aria-live="polite">
            <span>
              {searchResultSummary}
              {activeFilter ? ` • Filter: ${activeFilterLabel}` : ""}
            </span>
          </div>
        </div>
        <div className="mt-3 grid max-h-64 gap-2 overflow-y-auto pr-1">
          {rankedInvoices.map((result) => {
            const { invoice, matchedField } = result;
            const selected = selectedInvoiceNumbers.has(invoice.invoiceNumber);
            return (
              <label
                key={invoice.id}
                className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition ${
                  selected ? "border-[#14B8A6] bg-[#F4FBFF]" : "border-[#BFEAFF] bg-white hover:border-[#14B8A6]"
                }`}
              >
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => toggleInvoice(invoice.invoiceNumber)}
                  className="mt-1 size-4 accent-[#14B8A6]"
                />
                <span className="grid min-w-0 flex-1 gap-1">
                  <span className="flex min-w-0 flex-wrap items-start justify-between gap-2">
                    <span className="min-w-0 break-words text-base font-black leading-5 text-[#0A2540]">{renderInvoiceSearchHighlight(invoice.invoiceNumber, invoiceSearch)}</span>
                    <span className="shrink-0 rounded-full border border-[#BFEAFF] bg-[#F8FCFF] px-2.5 py-1 text-sm font-black text-[#0A2540]">
                      {renderInvoiceSearchHighlight(formatARRecoveryCurrency(invoice.amountCents), invoiceSearch)}
                    </span>
                  </span>
                  {activeSearch && matchedField ? (
                    <span className="text-[10px] font-black uppercase tracking-[0.12em] text-[#0F766E]">
                      Matched {matchedField.label}
                    </span>
                  ) : null}
                  <span className="grid gap-1 text-xs font-semibold leading-5 text-[#475569] sm:grid-cols-2">
                    {invoice.purchaseOrder || invoice.referenceNumber ? (
                      <span>Reference: {renderInvoiceSearchHighlight(invoice.purchaseOrder || invoice.referenceNumber, invoiceSearch)}</span>
                    ) : null}
                    <span>Aging: {renderInvoiceSearchHighlight(account.agingSummary, invoiceSearch)}</span>
                    <span>Due: {renderInvoiceSearchHighlight(formatCapturedDate(invoice.dueDate), invoiceSearch)}</span>
                    <span>Invoice Date: {renderInvoiceSearchHighlight(formatCapturedDate(invoice.invoiceDate), invoiceSearch)}</span>
                    <span>Original: {renderInvoiceSearchHighlight(formatARRecoveryCurrency(invoice.originalAmountCents), invoiceSearch)}</span>
                    <span>Status: {renderInvoiceSearchHighlight(invoice.status, invoiceSearch)}</span>
                  </span>
                </span>
              </label>
            );
          })}
          {!visibleInvoices.length ? (
            <div className="rounded-xl border border-dashed border-[#BFEAFF] bg-[#F4FBFF] p-3 text-sm font-semibold leading-6 text-[#475569]" role="status" aria-live="polite">
              <p>No invoices found.</p>
              {activeSearch || activeFilter ? (
                <p className="mt-1 text-xs leading-5">Search or filters may be limiting results.</p>
              ) : null}
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setInvoiceSearch("")}
                  className="focus-ring min-h-8 rounded-full border border-[#14B8A6] bg-white px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#0F766E]"
                >
                  Clear Search
                </button>
                <button
                  type="button"
                  onClick={() => setInvoiceQuickFilter("all")}
                  className="focus-ring min-h-8 rounded-full border border-[#BFEAFF] bg-white px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#0A2540]"
                >
                  Reset Filters
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </section>

      <section className="rounded-2xl border border-[#BFEAFF] bg-white p-3" aria-live="polite">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Invoice Attachment</p>
            <p className="mt-1 text-xs font-semibold leading-5 text-[#64748B]">
              More than {AR_RECOVERY_LETTER_INLINE_INVOICE_LIMIT} selected invoices automatically require a detailed invoice spreadsheet.
            </p>
          </div>
          <span className={`rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${
            preview.requiresSpreadsheetAttachment
              ? "border-[rgba(255,106,43,.28)] bg-[rgba(255,106,43,.1)] text-[#7C2D12]"
              : "border-[rgba(34,197,94,.28)] bg-[rgba(34,197,94,.08)] text-[#166534]"
          }`}>
            {preview.requiresSpreadsheetAttachment ? "Attachment Required" : "Inline Table"}
          </span>
        </div>
        {preview.invoiceAttachment ? (
          <div className="mt-3 rounded-xl border border-[#BFEAFF] bg-[#F8FCFF] p-3">
            <p className="break-words text-sm font-bold text-[#0A2540]">{preview.invoiceAttachment.filename}</p>
            <p className="mt-1 text-xs font-semibold leading-5 text-[#475569]">
              {preview.invoiceAttachment.invoiceCount} invoices | {preview.invoiceAttachment.openBalanceTotal} selected open balance | {preview.invoiceAttachment.status === "ready" ? "Generated and attached" : preview.invoiceAttachment.error}
            </p>
            <a
              href={preview.invoiceAttachment.dataUri}
              download={preview.invoiceAttachment.filename}
              className="focus-ring mt-3 inline-flex min-h-9 items-center rounded-full border border-[#14B8A6] bg-white px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#0F766E]"
            >
              Download Spreadsheet
            </a>
          </div>
        ) : (
          <div className="mt-3 rounded-xl border border-[#BFEAFF] bg-[#F8FCFF] p-3 text-sm font-semibold leading-6 text-[#475569]">
            Selected invoice count is within the letter display limit. No invoice spreadsheet is required.
          </div>
        )}
      </section>

      <label className="grid gap-1.5">
        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Related Promise</span>
        <select
          value={letterForm.relatedPromise}
          onChange={(event) => setLetterForm((current) => ({ ...current, relatedPromise: event.target.value }))}
          className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
        >
          <option value="">None</option>
          {selectedAccountPromises.map((promise) => (
            <option key={promise.id} value={promise.id}>
              {formatARRecoveryCurrency(promise.amountCents)} | {formatCapturedDate(promise.promiseDate)} | {promise.status}
            </option>
          ))}
        </select>
      </label>

      <label className="grid gap-1.5">
        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Internal Operational Notes</span>
        <textarea
          value={letterForm.additionalNotes}
          onChange={(event) => setLetterForm((current) => ({ ...current, additionalNotes: event.target.value }))}
          placeholder="Optional internal generation note."
          rows={4}
          className="focus-ring rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold leading-6 text-[#0A2540]"
        />
        <span className="text-xs font-semibold leading-5 text-[#64748B]">
          Internal only. These notes never appear in customer-facing letters, print output, PDF export, or template merge fields.
        </span>
      </label>
    </div>
  );
}

function ARRecoveryStatementExecutionControls({
  account,
  statementForm,
  setStatementForm,
  selectedAccountPromises,
}: {
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  statementForm: ARRecoveryStatementFormState;
  setStatementForm: Dispatch<SetStateAction<ARRecoveryStatementFormState>>;
  selectedAccountPromises: Array<{ id: string; amountCents: number; promiseDate: string; status: string }>;
}) {
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const invoices = useMemo(() => buildInvoicePlaceholders(account), [account]);
  const selectedInvoiceNumbers = useMemo(() => new Set(statementForm.relatedInvoices), [statementForm.relatedInvoices]);
  const rankedInvoices = useMemo(() => {
    return invoices
      .map((invoice, originalIndex) => rankLetterInvoiceSearchMatch(invoice, account.agingSummary, invoiceSearch, originalIndex))
      .filter((result): result is RankedLetterInvoice => Boolean(result))
      .sort((left, right) => left.rank - right.rank || left.originalIndex - right.originalIndex);
  }, [account.agingSummary, invoiceSearch, invoices]);
  const visibleInvoices = rankedInvoices.map((result) => result.invoice);
  const selectedInvoices = invoices.filter((invoice) => selectedInvoiceNumbers.has(invoice.invoiceNumber));
  const selectedInvoiceTotalCents = selectedInvoices.reduce((total, invoice) => total + invoice.currentBalanceCents, 0);
  const searchActive = invoiceSearch.trim().length > 0;
  const statementFlowState = statementForm.statementType && statementForm.statementDate && statementForm.relatedInvoices.length ? "Ready" : "Draft";
  const selectedInvoiceSummary = `${selectedInvoices.length} invoice${selectedInvoices.length === 1 ? "" : "s"} | ${formatARRecoveryCurrency(selectedInvoiceTotalCents)}`;
  const deliveryMethodDescriptions: Record<ARRecoveryStatementDeliveryOption, string> = {
    Email: "Send immediately to the customer contact email.",
    Print: "Open the browser print dialog for the customer-facing statement.",
    "Download PDF": "Save a certified PDF from the browser print dialog.",
  };
  const searchResultSummary = `${invoices.length} invoices • ${visibleInvoices.length} matching • ${selectedInvoices.length} selected • ${formatARRecoveryCurrency(selectedInvoiceTotalCents)} selected`;

  function toggleInvoice(invoiceNumber: string) {
    setStatementForm((current) => {
      const selected = new Set(current.relatedInvoices);
      if (selected.has(invoiceNumber)) {
        selected.delete(invoiceNumber);
      } else {
        selected.add(invoiceNumber);
      }
      return { ...current, relatedInvoices: Array.from(selected) };
    });
  }

  return (
    <div className="grid gap-4">
      <label className="grid gap-1.5">
        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Statement Type / Approved Template</span>
        <select
          value={statementForm.statementType}
          onChange={(event) => setStatementForm((current) => ({ ...current, statementType: event.target.value as ARRecoveryStatementFormState["statementType"] }))}
          className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
        >
          <option value="">Select approved template</option>
          {STATEMENT_TYPE_OPTIONS.map((statementType) => <option key={statementType} value={statementType}>{statementType}</option>)}
        </select>
      </label>

      <section className="rounded-2xl border border-[#BFEAFF] bg-white p-3" aria-label="Statement preparation summary">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Statement Preparation Summary</p>
            <p className="mt-1 text-xs font-semibold leading-5 text-[#64748B]">
              Review the official account statement details before sending.
            </p>
          </div>
          <span className="rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#0A2540]">
            {statementFlowState}
          </span>
        </div>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <WorkspaceField label="Statement Type" value={statementForm.statementType || "Not Selected"} />
          <WorkspaceField label="Approved Template" value={statementForm.statementType || "Not Selected"} />
          <WorkspaceField label="Customer" value={account.customerName} />
          <WorkspaceField label="Recipient" value={account.contactEmail} />
          <WorkspaceField label="Delivery Method" value={statementForm.deliveryOption} />
          <WorkspaceField label="Selected Balance" value={selectedInvoiceSummary} />
        </div>
      </section>

      <div className="rounded-2xl border border-[#BFEAFF] bg-white p-3">
        <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Recipient</p>
        <div className="mt-2 grid gap-1 text-sm font-semibold leading-6 text-[#0A2540]">
          <p className="break-words">{account.contactName || account.customerName}</p>
          <p className="break-words text-[#475569]">{account.contactEmail}</p>
        </div>
        <p className="mt-2 text-xs font-semibold leading-5 text-[#64748B]">Recipient is populated from the selected account contact.</p>
      </div>

      <label className="grid gap-1.5">
        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Statement Date</span>
        <input
          type="date"
          value={statementForm.statementDate}
          onChange={(event) => setStatementForm((current) => ({ ...current, statementDate: event.target.value }))}
          className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
        />
      </label>

      <label className="grid gap-1.5">
        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Delivery Method</span>
        <select
          value={statementForm.deliveryOption}
          onChange={(event) => setStatementForm((current) => ({ ...current, deliveryOption: event.target.value as ARRecoveryStatementDeliveryOption }))}
          className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
        >
          <option value="Email">Email</option>
          <option value="Download PDF">Download PDF</option>
          <option value="Print">Print</option>
        </select>
        <span className="text-xs font-semibold leading-5 text-[#64748B]">{deliveryMethodDescriptions[statementForm.deliveryOption]}</span>
      </label>

      <section className="rounded-2xl border border-[#BFEAFF] bg-white p-3" aria-label="Delivery method guidance">
        <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Delivery Methods</p>
        <div className="mt-3 grid gap-2">
          {(["Email", "Print", "Download PDF"] as ARRecoveryStatementDeliveryOption[]).map((method) => {
            const selected = statementForm.deliveryOption === method;
            return (
              <div key={method} className={`rounded-xl border p-2 ${selected ? "border-[#14B8A6] bg-[#F4FBFF]" : "border-[#BFEAFF] bg-[#F8FCFF]"}`}>
                <p className="text-xs font-black text-[#0A2540]">{method}{selected ? " | Selected" : ""}</p>
                <p className="mt-1 text-xs font-semibold leading-5 text-[#64748B]">{deliveryMethodDescriptions[method]}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="rounded-2xl border border-[#BFEAFF] bg-white p-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Statement Options</p>
            <p className="mt-1 text-xs font-semibold leading-5 text-[#64748B]">
              Select the open invoices that should appear on this customer statement.
            </p>
          </div>
          <span className="rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.12em] text-[#0A2540]" aria-live="polite">
            {selectedInvoices.length} Selected
          </span>
        </div>
        <label className="mt-3 grid gap-1.5" htmlFor="statement-invoice-search">
          <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Find Invoices</span>
          <span className="relative block min-w-0">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#14B8A6]" aria-hidden="true" />
            <input
              id="statement-invoice-search"
              value={invoiceSearch}
              onChange={(event) => setInvoiceSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape" && invoiceSearch.trim()) {
                  event.stopPropagation();
                  setInvoiceSearch("");
                }
              }}
              placeholder="Find invoice by number, due date, balance, aging, or status..."
              className="focus-ring min-h-10 w-full rounded-xl border border-[#BFEAFF] bg-white py-2 pl-9 pr-20 text-sm font-semibold text-[#0A2540] placeholder:text-[#64748B]"
            />
            {searchActive ? (
              <button
                type="button"
                onClick={() => setInvoiceSearch("")}
                className="focus-ring absolute right-2 top-1/2 min-h-7 -translate-y-1/2 rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-2 text-[10px] font-black uppercase tracking-[0.12em] text-[#0A2540]"
                aria-label="Clear statement invoice search"
              >
                Clear
              </button>
            ) : null}
          </span>
        </label>
        <div className="mt-3 rounded-xl border border-[#BFEAFF] bg-[#F8FCFF] px-3 py-2 text-xs font-semibold leading-5 text-[#475569]" aria-live="polite">
          {searchResultSummary}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setStatementForm((current) => ({ ...current, relatedInvoices: invoices.map((invoice) => invoice.invoiceNumber) }))}
            className="focus-ring min-h-9 rounded-full border border-[rgba(255,106,43,.28)] bg-[rgba(255,106,43,.1)] px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#7C2D12]"
          >
            Select All Open Invoices
          </button>
          <button
            type="button"
            onClick={() => setStatementForm((current) => ({ ...current, relatedInvoices: [] }))}
            className="focus-ring min-h-9 rounded-full border border-[#BFEAFF] bg-white px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#0A2540]"
          >
            Clear Selection
          </button>
        </div>
        <div className="mt-3 grid max-h-72 gap-2 overflow-y-auto pr-1">
          {rankedInvoices.map((result) => {
            const { invoice, matchedField } = result;
            const selected = selectedInvoiceNumbers.has(invoice.invoiceNumber);
            return (
              <label
                key={invoice.id}
                className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition ${
                  selected ? "border-[#14B8A6] bg-[#F4FBFF]" : "border-[#BFEAFF] bg-white hover:border-[#14B8A6]"
                }`}
              >
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => toggleInvoice(invoice.invoiceNumber)}
                  className="mt-1 size-4 accent-[#14B8A6]"
                  aria-label={`Include invoice ${invoice.invoiceNumber} on statement`}
                />
                <span className="grid min-w-0 flex-1 gap-1">
                  <span className="flex min-w-0 flex-wrap items-start justify-between gap-2">
                    <span className="min-w-0 break-words text-base font-black leading-5 text-[#0A2540]">{renderInvoiceSearchHighlight(invoice.invoiceNumber, invoiceSearch)}</span>
                    <span className="shrink-0 rounded-full border border-[#BFEAFF] bg-[#F8FCFF] px-2.5 py-1 text-sm font-black text-[#0A2540]">
                      {renderInvoiceSearchHighlight(formatARRecoveryCurrency(invoice.currentBalanceCents), invoiceSearch)}
                    </span>
                  </span>
                  {searchActive && matchedField ? (
                    <span className="text-[10px] font-black uppercase tracking-[0.12em] text-[#0F766E]">
                      Matched {matchedField.label}
                    </span>
                  ) : null}
                  <span className="grid gap-1 text-xs font-semibold leading-5 text-[#475569] sm:grid-cols-2">
                    <span>Due Date: {renderInvoiceSearchHighlight(formatCapturedDate(invoice.dueDate), invoiceSearch)}</span>
                    <span>Open Balance: {renderInvoiceSearchHighlight(formatARRecoveryCurrency(invoice.currentBalanceCents), invoiceSearch)}</span>
                    <span>Aging: {renderInvoiceSearchHighlight(account.agingSummary, invoiceSearch)}</span>
                    <span>Status: {renderInvoiceSearchHighlight(invoice.status, invoiceSearch)}</span>
                  </span>
                </span>
              </label>
            );
          })}
          {!visibleInvoices.length ? (
            <div className="rounded-xl border border-dashed border-[#BFEAFF] bg-[#F4FBFF] p-3 text-sm font-semibold leading-6 text-[#475569]" role="status" aria-live="polite">
              <p>No invoices found.</p>
              {searchActive ? <p className="mt-1 text-xs leading-5">Search may be limiting results.</p> : null}
            </div>
          ) : null}
        </div>
      </section>

      <label className="grid gap-1.5">
        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Related Promise</span>
        <select
          value={statementForm.relatedPromise}
          onChange={(event) => setStatementForm((current) => ({ ...current, relatedPromise: event.target.value }))}
          className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
        >
          <option value="">None</option>
          {selectedAccountPromises.map((promise) => (
            <option key={promise.id} value={promise.id}>
              {formatARRecoveryCurrency(promise.amountCents)} | {formatCapturedDate(promise.promiseDate)} | {promise.status}
            </option>
          ))}
        </select>
      </label>

      <label className="grid gap-1.5">
        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Internal Operational Note</span>
        <textarea
          value={statementForm.statementNotes}
          onChange={(event) => setStatementForm((current) => ({ ...current, statementNotes: event.target.value }))}
          placeholder="Optional internal note for the operational record."
          rows={4}
          className="focus-ring rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold leading-6 text-[#0A2540]"
        />
        <span className="text-xs font-semibold leading-5 text-[#64748B]">
          Internal only. Never included in the customer statement, print, PDF, or email.
        </span>
      </label>

    </div>
  );
}

function ARRecoveryLetterExecutionPreview({
  preview,
  clientProfile,
}: {
  preview: LetterPreview;
  clientProfile: ReturnType<typeof loadARRecoveryWorkspace>["clientProfile"];
}) {
  return (
    <article className="mx-auto min-h-[64rem] max-w-[58rem] rounded-[1.25rem] border border-slate-200 bg-white p-8 shadow-[0_24px_70px_rgba(15,23,42,.12)]" data-ar-letter-document="customer-facing">
      <header className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 pb-6">
        <div className="flex min-w-0 items-start gap-4">
          <img
            src={preview.branding.logoSrc}
            alt={preview.branding.logoAlt}
            className="h-20 w-20 shrink-0 object-contain"
          />
          <div className="min-w-0">
            <h4 className="text-2xl font-bold text-[#0A2540]">{preview.branding.companyName}</h4>
            <div className="mt-2 max-w-md text-sm font-semibold leading-6 text-[#475569]">
              {preview.branding.addressLines.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] p-3 text-right text-sm font-semibold leading-6 text-[#475569]">
          <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Letter Date</p>
          <p>{preview.date}</p>
        </div>
      </header>
      <section className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,1fr)_16rem]">
        <div className="min-w-0 text-sm font-semibold leading-6 text-[#475569]">
          <h5 className="break-words text-xl font-bold text-[#0A2540]">{preview.customer}</h5>
          <p className="mt-2 break-words">{preview.recipientContactName}</p>
          <p className="break-words">{preview.recipientEmail}</p>
        </div>
        <div className="rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] p-3 text-sm font-semibold leading-6 text-[#475569]">
          <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Account Summary</p>
          <dl className="mt-3 grid gap-2">
            <div>
              <dt className="text-[10px] font-black uppercase tracking-[0.12em] text-[#64748B]">Account Number</dt>
              <dd className="mt-0.5 break-words font-bold text-[#0A2540]">{preview.account}</dd>
            </div>
            <div>
              <dt className="text-[10px] font-black uppercase tracking-[0.12em] text-[#64748B]">Current Balance</dt>
              <dd className="mt-0.5 break-words font-bold text-[#0A2540]">{preview.balance}</dd>
            </div>
            <div>
              <dt className="text-[10px] font-black uppercase tracking-[0.12em] text-[#64748B]">Account Aging</dt>
              <dd className="mt-0.5 break-words font-bold text-[#0A2540]">{preview.agingStatus}</dd>
            </div>
          </dl>
        </div>
      </section>
      <section className="mt-10">
        <p className="text-sm font-bold uppercase tracking-[0.14em] text-[#0F766E]">RE: {preview.subject}</p>
        <h5 className="mt-3 text-2xl font-bold text-[#0A2540]">{preview.documentTitle}</h5>
        <div className="mt-6 grid gap-4">
          <p className="text-base font-semibold leading-8 text-[#0A2540]">{preview.greeting}</p>
          {preview.reasonParagraphs.map((paragraph) => (
            <p key={paragraph} className="text-base font-semibold leading-8 text-[#0A2540]">{paragraph}</p>
          ))}
        </div>
        <div className="mt-6 grid gap-3 rounded-xl border border-[#BFEAFF] bg-[#F8FCFF] p-4 md:grid-cols-2 xl:grid-cols-3">
          {preview.accountSummary.map((item) => (
            <div key={item.label} className="min-w-0">
              <p className="text-[10px] font-black uppercase tracking-[0.12em] text-[#14B8A6]">{item.label}</p>
              <p className="mt-1 break-words text-sm font-bold text-[#0A2540]">{item.value}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 grid gap-3">
          <section className="overflow-hidden rounded-xl border border-[#BFEAFF]">
            <div className="flex flex-wrap items-center justify-between gap-3 bg-[#F4FBFF] px-4 py-3">
              <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Invoices Requiring Attention</p>
              <p className="text-xs font-black uppercase tracking-[0.12em] text-[#0A2540]">
                {preview.invoiceSummary.invoiceCount} selected | {preview.selectedInvoiceTotal}
              </p>
            </div>
            {preview.requiresSpreadsheetAttachment ? (
              <div className="grid gap-3 p-4">
                <p className="text-sm font-semibold leading-6 text-[#0A2540]">
                  The selected invoices requiring attention are included in the attached invoice detail file for readability.
                </p>
                <div className="grid gap-3 rounded-xl border border-[#BFEAFF] bg-[#F8FCFF] p-3 sm:grid-cols-2 xl:grid-cols-5">
                  <WorkspaceField label="Invoice Count" value={String(preview.invoiceSummary.invoiceCount)} />
                  <WorkspaceField label="Selected Balance" value={preview.invoiceSummary.openBalanceTotal} />
                  <WorkspaceField label="Oldest Due Date" value={preview.invoiceSummary.oldestDueDate} />
                  <WorkspaceField label="Highest Aging" value={preview.invoiceSummary.highestAgingBucket} />
                  <WorkspaceField label="Account" value={preview.invoiceSummary.accountNumber} />
                </div>
                {preview.invoiceAttachment ? (
                  <p className="text-xs font-semibold leading-5 text-[#475569]">
                    Attachment: {preview.invoiceAttachment.filename} | {preview.invoiceAttachment.invoiceCount} invoices
                  </p>
                ) : null}
              </div>
            ) : preview.inlineInvoices.length ? (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[38rem] border-collapse text-left text-xs font-semibold text-[#0A2540]">
                  <thead className="text-[10px] font-black uppercase tracking-[0.12em] text-[#14B8A6]">
                    <tr>
                      <th className="px-4 py-2">Invoice</th>
                      <th className="px-4 py-2">Invoice Date</th>
                      <th className="px-4 py-2">Due Date</th>
                      <th className="px-4 py-2">Original Amount</th>
                      <th className="px-4 py-2">Open Balance</th>
                      <th className="px-4 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.inlineInvoices.map((invoice) => (
                      <tr key={invoice.invoiceNumber} className="border-t border-[#BFEAFF]">
                        <td className="px-4 py-2">{invoice.invoiceNumber}</td>
                        <td className="px-4 py-2">{invoice.invoiceDate}</td>
                        <td className="px-4 py-2">{invoice.dueDate}</td>
                        <td className="px-4 py-2 tabular-nums">{invoice.originalAmount}</td>
                        <td className="px-4 py-2 tabular-nums">{invoice.openBalance}</td>
                        <td className="px-4 py-2">{invoice.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="px-4 py-3 text-sm font-semibold leading-6 text-[#64748B]">No invoices selected for this letter preview.</p>
            )}
          </section>
          {preview.selectedPromise ? (
            <section className="rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
              <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">{preview.selectedPromise.heading}</p>
              <div className="mt-2 grid gap-2 text-sm font-semibold leading-6 text-[#0A2540] sm:grid-cols-3">
                <p>Status: {preview.selectedPromise.status}</p>
                <p>Amount: {preview.selectedPromise.amount}</p>
                <p>Date: {preview.selectedPromise.promiseDate}</p>
              </div>
              <p className="mt-3 text-sm font-semibold leading-6 text-[#0A2540]">{preview.selectedPromise.body}</p>
            </section>
          ) : null}
        </div>
        <section className="mt-8">
          <h5 className="text-xl font-bold text-[#0A2540]">Requested Action</h5>
          <div className="mt-3 grid gap-4">
            {preview.requestedActionParagraphs.map((paragraph) => (
              <p key={paragraph} className="text-base font-semibold leading-8 text-[#0A2540]">{paragraph}</p>
            ))}
          </div>
        </section>
      </section>
      <footer className="mt-12 grid gap-5 border-t border-slate-200 pt-6">
        <section aria-label="Closing">
          <p className="text-base font-semibold leading-7 text-[#0A2540]">{preview.closing}</p>
        </section>
        <section aria-label="Signature">
          <p className="text-base font-semibold leading-7 text-[#0A2540]">{preview.signatureBlock.salutation}</p>
          <div className="mt-3 grid gap-1">
            {preview.signatureLines.map((line, index) => (
              <p key={line} className={`${index === 0 ? "text-base font-bold text-[#0A2540]" : "text-sm font-semibold text-[#475569]"} leading-6`}>
                {line}
              </p>
            ))}
          </div>
        </section>
        {preview.paymentInstructions.length ? (
          <section aria-label="Payment Instructions">
            <div className="mt-2 grid gap-2">
              {preview.paymentInstructions.map((item) => (
                <p key={item} className="text-sm font-semibold leading-6 text-[#475569]">{item}</p>
              ))}
            </div>
          </section>
        ) : null}
        {preview.complianceNotices.length ? (
          <section aria-label="Compliance">
            <div className="mt-2 grid gap-2">
              {preview.complianceNotices.map((item) => (
                <p key={item} className="text-sm font-semibold leading-6 text-[#475569]">{item}</p>
              ))}
            </div>
          </section>
        ) : null}
        <section aria-label="Contact Information">
          <div className="mt-2 grid gap-2">
            {preview.footerContactItems.map((item) => (
              <div key={`${item.label}-${item.value}`} className="grid gap-1 text-sm font-semibold leading-6 text-[#475569] sm:grid-cols-[5rem_minmax(0,1fr)]">
                <span className="text-[10px] font-black uppercase tracking-[0.12em] text-[#14B8A6]">{item.label}</span>
                <span className="break-words text-[#0A2540]">{item.value}</span>
              </div>
            ))}
          </div>
        </section>
      </footer>
    </article>
  );
}

function ARRecoveryStatementExecutionPreview({
  preview,
  deliveryMethod,
}: {
  preview: StatementPreview;
  deliveryMethod: ARRecoveryStatementDeliveryOption;
}) {
  return (
    <article className="mx-auto min-h-[64rem] max-w-[62rem] rounded-[1.25rem] border border-slate-200 bg-white p-8 shadow-[0_24px_70px_rgba(15,23,42,.12)]" data-ar-statement-document="customer-facing">
      <header className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 pb-6">
        <div className="flex min-w-0 items-start gap-4">
          <img
            src={preview.branding.logoSrc}
            alt={preview.branding.logoAlt}
            className="h-20 w-20 shrink-0 object-contain"
          />
          <div className="min-w-0">
            <h4 className="text-2xl font-bold text-[#0A2540]">{preview.branding.companyName}</h4>
            <div className="mt-2 max-w-md text-sm font-semibold leading-6 text-[#475569]">
              {preview.branding.addressLines.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] p-3 text-right text-sm font-semibold leading-6 text-[#475569]">
          <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Statement Date</p>
          <p className="font-bold text-[#0A2540]">{preview.statementDate}</p>
          <p className="mt-2 text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Delivery Method</p>
          <p className="font-bold text-[#0A2540]">{deliveryMethod}</p>
          <p className="mt-2 text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Account Statement</p>
        </div>
      </header>
      <section className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,1fr)_18rem]">
        <div className="min-w-0 text-sm font-semibold leading-6 text-[#475569]">
          <h5 className="break-words text-xl font-bold text-[#0A2540]">{preview.customer}</h5>
          <p className="mt-2 break-words">{preview.recipientContactName}</p>
          <p className="break-words">{preview.recipientEmail}</p>
        </div>
        <div className="rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] p-3 text-sm font-semibold leading-6 text-[#475569]">
          <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Account Summary</p>
          <dl className="mt-3 grid gap-2">
            {[
              ["Customer", preview.customer],
              ["Account Number", preview.account],
              ["Account Aging", preview.agingSummary],
              ["Total Due", preview.totalBalance],
            ].map(([label, value]) => (
              <div key={label}>
                <dt className="text-[10px] font-black uppercase tracking-[0.12em] text-[#64748B]">{label}</dt>
                <dd className="mt-0.5 break-words font-bold text-[#0A2540]">{value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>
      <section className="mt-8 rounded-xl border border-[#BFEAFF] bg-[#F8FCFF] p-4">
        <h5 className="text-lg font-bold text-[#0A2540]">{preview.documentTitle}</h5>
        <div className="mt-3 grid gap-3">
          {preview.summaryParagraphs.map((paragraph) => (
            <p key={paragraph} className="text-sm font-semibold leading-7 text-[#0A2540]">{paragraph}</p>
          ))}
        </div>
      </section>
      {preview.selectedPromise ? (
        <section className="mt-4 rounded-xl border border-[#BFEAFF] bg-white p-4">
          <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Promise Context</p>
          <div className="mt-2 grid gap-2 text-sm font-semibold leading-6 text-[#0A2540] sm:grid-cols-3">
            <p>Status: {preview.selectedPromise.status}</p>
            <p>Amount: {preview.selectedPromise.amount}</p>
            <p>Date: {preview.selectedPromise.promiseDate}</p>
          </div>
          <p className="mt-3 text-sm font-semibold leading-6 text-[#0A2540]">{preview.selectedPromise.body}</p>
        </section>
      ) : null}
      <section className="mt-8 overflow-hidden rounded-xl border border-[#BFEAFF]">
        <table className="w-full table-fixed border-collapse text-left text-[11px] font-semibold text-[#0A2540] sm:text-xs">
          <thead className="bg-[#F4FBFF] text-[9px] font-black uppercase tracking-[0.1em] text-[#14B8A6] sm:text-[10px]">
            <tr>
              <th className="w-[18%] px-2 py-3">Invoice Number</th>
              <th className="hidden w-[14%] px-2 py-3 md:table-cell">Invoice Date</th>
              <th className="w-[14%] px-2 py-3">Due Date</th>
              <th className="hidden w-[16%] px-2 py-3 text-right lg:table-cell">Original Amount</th>
              <th className="w-[16%] px-2 py-3 text-right">Open Balance</th>
              <th className="w-[14%] px-2 py-3">Aging</th>
              <th className="w-[12%] px-2 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {preview.lineItems.map((item) => (
              <tr key={item.invoiceNumber} className="border-t border-[#BFEAFF]">
                <td className="break-words px-2 py-3">{item.invoiceNumber}</td>
                <td className="hidden px-2 py-3 md:table-cell">{item.invoiceDate}</td>
                <td className="px-2 py-3">{item.dueDate}</td>
                <td className="hidden px-2 py-3 text-right tabular-nums lg:table-cell">{item.originalAmount}</td>
                <td className="px-2 py-3 text-right tabular-nums">{item.openBalance}</td>
                <td className="px-2 py-3">{item.aging}</td>
                <td className="px-2 py-3">{item.status}</td>
              </tr>
            ))}
            {!preview.lineItems.length ? (
              <tr className="border-t border-[#BFEAFF]">
                <td className="px-3 py-4 text-sm text-[#64748B]" colSpan={7}>No invoices selected for this statement.</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </section>
      <section className="mt-6 grid gap-3 rounded-xl border border-[#BFEAFF] bg-[#F8FCFF] p-4 sm:grid-cols-2 xl:grid-cols-3">
        {[
          ["Number of Selected Invoices", preview.totals.selectedInvoiceCount],
          ["Total Original Amount", preview.totals.totalOriginalAmount],
          ["Total Open Balance", preview.totals.totalOpenBalance],
          ["Current Due", preview.totals.currentDue],
          ["Past Due", preview.totals.pastDue],
          ["Total Due", preview.totals.totalDue],
        ].map(([label, value]) => (
          <div key={label} className="min-w-0">
            <p className="text-[10px] font-black uppercase tracking-[0.12em] text-[#14B8A6]">{label}</p>
            <p className="mt-1 break-words text-sm font-bold text-[#0A2540]">{value}</p>
          </div>
        ))}
      </section>
      <section className="mt-8 rounded-xl border border-[#BFEAFF] bg-white p-4">
        <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Remittance Questions</p>
        <p className="mt-2 text-sm font-semibold leading-6 text-[#475569]">For payment or remittance assistance, contact:</p>
        <div className="mt-3 grid gap-1 text-sm font-semibold leading-6 text-[#0A2540]">
          <p>{preview.remittanceContact.name}</p>
          <p>{preview.remittanceContact.phone}</p>
          <p>{preview.remittanceContact.email}</p>
        </div>
      </section>
      <footer className="mt-12 grid gap-1 border-t border-slate-200 pt-6 text-sm font-semibold leading-6 text-[#475569]">
        <p className="font-bold text-[#0A2540]">{preview.branding.companyName}</p>
        <p>Accounts Receivable Department</p>
        {preview.footerContactItems.map((item) => (
          <p key={`${item.label}-${item.value}`} className="break-words">{item.value}</p>
        ))}
      </footer>
    </article>
  );
}

const ARRecoveryEnterpriseWorkspacePanel = forwardRef<HTMLElement, {
  eyebrow: string;
  title: string;
  onClose: () => void;
  closeLabel?: string;
  children: ReactNode;
  contentRef?: RefObject<HTMLDivElement | null>;
}>(
  ({ eyebrow, title, onClose, closeLabel = "Close", children, contentRef }, ref) => (
    <div className="pointer-events-none fixed inset-0 z-[80] flex bg-[#0A2540]/10" role="presentation">
      <aside
        ref={ref}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ar-recovery-enterprise-workspace-panel-title"
        className="pointer-events-auto flex h-full w-full max-w-full flex-col border-l border-[#BFEAFF] bg-white shadow-[0_24px_80px_rgba(15,23,42,.22)] outline-none"
      >
        <div className="border-b border-[#BFEAFF] p-5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">{eyebrow}</p>
          <div className="mt-2 flex items-start justify-between gap-4">
            <h3 id="ar-recovery-enterprise-workspace-panel-title" className="min-w-0 break-words text-2xl font-semibold tracking-tight text-[#0A2540]">
              {title}
            </h3>
            <button
              type="button"
              onClick={onClose}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onClose();
                }
              }}
              className="focus-ring shrink-0 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2 text-sm font-semibold text-[#0A2540]"
            >
              {closeLabel}
            </button>
          </div>
        </div>
        <div ref={contentRef} className="min-h-0 flex-1 overflow-y-auto p-5">
          {children}
        </div>
      </aside>
    </div>
  ),
);
ARRecoveryEnterpriseWorkspacePanel.displayName = "ARRecoveryEnterpriseWorkspacePanel";

function ARRecoveryEnterpriseStudio({
  mode,
  account,
  clientProfile,
  letterForm,
  statementForm,
  setLetterForm,
  setStatementForm,
  selectedAccountPromises,
  accountTasks,
  letterFormError,
  statementFormError,
  studioStatusMessage,
  setStudioStatusMessage,
  isDesignerCollapsed,
  setIsDesignerCollapsed,
  activeDesignerSection,
  setActiveDesignerSection,
  activeStudioTab,
  setActiveStudioTab,
  onSaveLetter,
  onSaveStatement,
  onClose,
}: {
  mode: Exclude<ARRecoveryEnterpriseStudioMode, null>;
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  clientProfile: ReturnType<typeof loadARRecoveryWorkspace>["clientProfile"];
  letterForm: ARRecoveryLetterFormState;
  statementForm: ARRecoveryStatementFormState;
  setLetterForm: Dispatch<SetStateAction<ARRecoveryLetterFormState>>;
  setStatementForm: Dispatch<SetStateAction<ARRecoveryStatementFormState>>;
  selectedAccountPromises: Array<{ id: string; amountCents: number; promiseDate: string; status: string }>;
  accountTasks: Array<{ id: string; taskTitle: string; status: string }>;
  letterFormError: string;
  statementFormError: string;
  studioStatusMessage: string;
  setStudioStatusMessage: Dispatch<SetStateAction<string>>;
  isDesignerCollapsed: boolean;
  setIsDesignerCollapsed: Dispatch<SetStateAction<boolean>>;
  activeDesignerSection: ARRecoveryStudioDesignerSection;
  setActiveDesignerSection: Dispatch<SetStateAction<ARRecoveryStudioDesignerSection>>;
  activeStudioTab: ARRecoveryStudioDocumentTab;
  setActiveStudioTab: Dispatch<SetStateAction<ARRecoveryStudioDocumentTab>>;
  onSaveLetter: () => Promise<void>;
  onSaveStatement: () => Promise<void>;
  onClose: () => void;
}) {
  if (mode === "letter") {
    return (
      <ARRecoveryLetterStudioWorkspace
        account={account}
        clientProfile={clientProfile}
        letterForm={letterForm}
        setLetterForm={setLetterForm}
        selectedAccountPromises={selectedAccountPromises}
        accountTasks={accountTasks}
        letterFormError={letterFormError}
        studioStatusMessage={studioStatusMessage}
        setStudioStatusMessage={setStudioStatusMessage}
        activeDesignerSection={activeDesignerSection}
        setActiveDesignerSection={setActiveDesignerSection}
        activeStudioTab={activeStudioTab}
        setActiveStudioTab={setActiveStudioTab}
        onSaveLetter={onSaveLetter}
        onClose={onClose}
      />
    );
  }

  const studioTitle = "Statement Studio";
  const preview = buildStatementPreviewFromForm({ form: statementForm, account, clientProfile });
  const availableTabs: Array<{ key: ARRecoveryStudioDocumentTab; label: string }> = [
    { key: "preview", label: "Preview" },
    { key: "invoices", label: "Invoices" },
    { key: "aging", label: "Aging" },
    { key: "history", label: "History" },
    { key: "attachments", label: "Attachments" },
    { key: "versions", label: "Versions" },
  ];
  const selectedTab = availableTabs.some((tab) => tab.key === activeStudioTab) ? activeStudioTab : "preview";

  function handlePreviewClick(section: ARRecoveryStudioDesignerSection) {
    setIsDesignerCollapsed(false);
    setActiveDesignerSection(section);
  }

  function handleUtilityAction(action: string) {
    if (action === "Preview") {
      setActiveStudioTab("preview");
      setStudioStatusMessage("Live Preview is already active and synchronized with the Document Designer.");
      return;
    }
    if (action === "Print") {
      setStudioStatusMessage("Print requested. Browser print controls can be used for this Studio preview.");
      window.print();
      return;
    }
    setStudioStatusMessage(`${action} prepared for ${studioTitle}. Full provider execution remains governed by the document workflow.`);
  }

  return (
    <div
      className="fixed inset-0 z-[95] flex flex-col overflow-hidden bg-[#02072C] text-[#0A2540]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ar-enterprise-studio-title"
      data-enterprise-studio="letter-studio statement-studio"
    >
      <header className="shrink-0 border-b border-[rgba(34,224,208,.24)] bg-[#071A3D] px-4 py-3 text-[#F6F1E8] shadow-[0_18px_48px_rgba(2,7,44,.28)] lg:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#22E0D0]">Enterprise Studio</p>
            <h2 id="ar-enterprise-studio-title" className="mt-1 break-words text-2xl font-bold tracking-tight">
              {studioTitle}
            </h2>
            <p className="mt-1 text-sm font-semibold leading-6 text-[#CBD5E1]">
              {account.customerName} | Account {account.customerNumber} | Full workspace document creation and review.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => void onSaveStatement()} className="focus-ring min-h-10 rounded-full border border-[rgba(255,106,43,.5)] bg-[#FF6A2B] px-4 text-sm font-black uppercase tracking-[0.12em] text-white">
              Save
            </button>
            {["Save Draft", "Preview", "Print", "PDF", "Send Test"].map((action) => (
              <button
                key={action}
                type="button"
                onClick={() => handleUtilityAction(action)}
                className="focus-ring min-h-10 rounded-full border border-[rgba(34,224,208,.24)] bg-[rgba(255,255,255,.06)] px-3 text-xs font-black uppercase tracking-[0.12em] text-[#E8EEF4] transition hover:border-[#22E0D0] hover:bg-[rgba(34,224,208,.12)]"
              >
                {action}
              </button>
            ))}
            <button type="button" onClick={onClose} className="focus-ring min-h-10 rounded-full border border-[rgba(34,224,208,.28)] bg-white px-4 text-sm font-black uppercase tracking-[0.12em] text-[#0A2540]">
              Close
            </button>
          </div>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-hidden p-3 lg:p-5">
        <div className={`grid h-full min-h-0 gap-4 ${isDesignerCollapsed ? "lg:grid-cols-[minmax(0,1fr)]" : "lg:grid-cols-[minmax(18rem,35%)_minmax(0,65%)] xl:grid-cols-[minmax(22rem,35%)_minmax(0,65%)]"}`}>
          {isDesignerCollapsed ? null : (
            <aside className="min-h-0 min-w-0 overflow-hidden rounded-[1.5rem] border border-[#BFEAFF] bg-white shadow-[0_24px_64px_rgba(2,7,44,.2)]">
              <div className="flex items-start justify-between gap-3 border-b border-[#BFEAFF] p-4">
                <div className="min-w-0">
                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Document Designer</p>
                  <p className="mt-1 text-sm font-semibold leading-6 text-[#475569]">Configure the document without losing Live Preview.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsDesignerCollapsed(true)}
                  className="focus-ring shrink-0 rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em] text-[#0A2540]"
                >
                  Hide Designer
                </button>
              </div>
              <div className="h-[calc(100%-5.75rem)] overflow-y-auto p-4">
                <ARRecoveryStudioDesigner
                  mode={mode}
                  account={account}
                  letterForm={letterForm}
                  statementForm={statementForm}
                  setLetterForm={setLetterForm}
                  setStatementForm={setStatementForm}
                  selectedAccountPromises={selectedAccountPromises}
                  accountTasks={accountTasks}
                  activeDesignerSection={activeDesignerSection}
                  setActiveDesignerSection={setActiveDesignerSection}
                />
              </div>
            </aside>
          )}

          <main className="min-h-0 min-w-0 overflow-hidden rounded-[1.5rem] border border-[#BFEAFF] bg-[#F4FBFF] shadow-[0_24px_64px_rgba(2,7,44,.16)]">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#BFEAFF] bg-white p-4">
              <div className="min-w-0">
                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Live Preview</p>
                <p className="mt-1 text-sm font-semibold leading-6 text-[#475569]">Interactive preview stays visible and synchronized while editing.</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {isDesignerCollapsed ? (
                  <button
                    type="button"
                    onClick={() => setIsDesignerCollapsed(false)}
                    className="focus-ring rounded-full border border-[#14B8A6] bg-[#F4FBFF] px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em] text-[#0A2540]"
                  >
                    Show Designer
                  </button>
                ) : null}
                <div className="flex max-w-full gap-2 overflow-x-auto pb-1" role="tablist" aria-label={`${studioTitle} document tabs`}>
                  {availableTabs.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      role="tab"
                      aria-selected={selectedTab === tab.key}
                      onClick={() => setActiveStudioTab(tab.key)}
                      className={`focus-ring shrink-0 rounded-full border px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em] transition ${
                        selectedTab === tab.key
                          ? "border-[#FF6A2B] bg-[rgba(255,106,43,.12)] text-[#0A2540]"
                          : "border-[#BFEAFF] bg-white text-[#475569] hover:border-[#14B8A6]"
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="h-[calc(100%-5.75rem)] overflow-y-auto p-4 lg:p-6">
              {studioStatusMessage ? (
                <div className="mb-4 rounded-2xl border border-[#BFEAFF] bg-white px-4 py-3 text-sm font-semibold leading-6 text-[#0A2540]">
                  {studioStatusMessage}
                </div>
              ) : null}
              <ARRecoveryStatementStudioPreview preview={preview} account={account} onActivateDesignerSection={handlePreviewClick} activeTab={selectedTab} />
              {statementFormError ? (
                <div className="mt-4 rounded-2xl border border-[rgba(248,113,113,.28)] bg-[rgba(248,113,113,.08)] px-4 py-3 text-sm font-semibold text-[#B91C1C]">
                  {statementFormError}
                </div>
              ) : null}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

function ARRecoveryLetterStudioWorkspace({
  account,
  clientProfile,
  letterForm,
  setLetterForm,
  selectedAccountPromises,
  accountTasks,
  letterFormError,
  studioStatusMessage,
  setStudioStatusMessage,
  activeDesignerSection,
  setActiveDesignerSection,
  activeStudioTab,
  setActiveStudioTab,
  onSaveLetter,
  onClose,
}: {
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  clientProfile: ReturnType<typeof loadARRecoveryWorkspace>["clientProfile"];
  letterForm: ARRecoveryLetterFormState;
  setLetterForm: Dispatch<SetStateAction<ARRecoveryLetterFormState>>;
  selectedAccountPromises: Array<{ id: string; amountCents: number; promiseDate: string; status: string }>;
  accountTasks: Array<{ id: string; taskTitle: string; status: string }>;
  letterFormError: string;
  studioStatusMessage: string;
  setStudioStatusMessage: Dispatch<SetStateAction<string>>;
  activeDesignerSection: ARRecoveryStudioDesignerSection;
  setActiveDesignerSection: Dispatch<SetStateAction<ARRecoveryStudioDesignerSection>>;
  activeStudioTab: ARRecoveryStudioDocumentTab;
  setActiveStudioTab: Dispatch<SetStateAction<ARRecoveryStudioDocumentTab>>;
  onSaveLetter: () => Promise<void>;
  onClose: () => void;
}) {
  const preview = buildLetterPreviewFromForm({ form: letterForm, account, clientProfile });
  const availableTabs: Array<{ key: ARRecoveryStudioDocumentTab; label: string }> = [
    { key: "preview", label: "Live Letter" },
    { key: "merge-fields", label: "Merge Fields" },
    { key: "variables", label: "Variables" },
    { key: "history", label: "History" },
    { key: "versions", label: "Versions" },
    { key: "audit", label: "Audit" },
  ];
  const selectedTab = availableTabs.some((tab) => tab.key === activeStudioTab) ? activeStudioTab : "preview";

  function activateDesignerSection(section: ARRecoveryStudioDesignerSection) {
    setActiveDesignerSection(section);
  }

  function handleLetterUtilityAction(action: string) {
    if (action === "Print") {
      setStudioStatusMessage("Print requested. Browser print controls can be used for the live letter.");
      window.print();
      return;
    }
    if (action === "PDF") {
      setStudioStatusMessage("PDF export prepared for the selected letter.");
      return;
    }
    if (action === "Send Test") {
      setStudioStatusMessage("Test send prepared. Delivery remains governed by the document workflow.");
      return;
    }
    setStudioStatusMessage(`${action} prepared for Letter Studio.`);
  }

  return (
    <div
      className="fixed inset-0 z-[95] flex flex-col overflow-hidden bg-[#02072C] text-[#0A2540]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ar-letter-studio-title"
      data-letter-studio="enterprise-letter-studio"
    >
      <header className="shrink-0 border-b border-[rgba(34,224,208,.24)] bg-[#071A3D] px-4 py-3 text-[#F6F1E8] shadow-[0_18px_48px_rgba(2,7,44,.3)] lg:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#22E0D0]">Enterprise Studio</p>
            <h2 id="ar-letter-studio-title" className="mt-1 break-words text-2xl font-bold tracking-tight">
              Letter Studio
            </h2>
            <p className="mt-1 max-w-3xl text-sm font-semibold leading-6 text-[#CBD5E1]">
              {account.customerName} | Account {account.customerNumber} | Live letter composition workspace.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => void onSaveLetter()} className="focus-ring min-h-10 rounded-full border border-[rgba(255,106,43,.5)] bg-[#FF6A2B] px-4 text-sm font-black uppercase tracking-[0.12em] text-white shadow-[0_12px_30px_rgba(255,106,43,.28)] transition hover:-translate-y-0.5 hover:shadow-[0_16px_36px_rgba(255,106,43,.34)]">
              Save
            </button>
            {["Save Draft", "Print", "PDF", "Send Test"].map((action) => (
              <button
                key={action}
                type="button"
                onClick={() => handleLetterUtilityAction(action)}
                className="focus-ring min-h-10 rounded-full border border-[rgba(34,224,208,.24)] bg-[rgba(255,255,255,.06)] px-3 text-xs font-black uppercase tracking-[0.12em] text-[#E8EEF4] transition hover:border-[#22E0D0] hover:bg-[rgba(34,224,208,.12)]"
              >
                {action}
              </button>
            ))}
            <button type="button" onClick={onClose} className="focus-ring min-h-10 rounded-full border border-[rgba(34,224,208,.28)] bg-white px-4 text-sm font-black uppercase tracking-[0.12em] text-[#0A2540]">
              Close
            </button>
          </div>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-hidden p-3 lg:p-5">
        <div className="grid h-full min-h-0 grid-cols-1 gap-4 lg:grid-cols-[minmax(20rem,35%)_minmax(0,65%)]">
          <aside className="min-h-0 min-w-0 overflow-hidden rounded-[1.5rem] border border-[#BFEAFF] bg-white shadow-[0_24px_64px_rgba(2,7,44,.2)]">
            <div className="border-b border-[#BFEAFF] p-4">
              <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Letter Designer</p>
              <p className="mt-1 text-sm font-semibold leading-6 text-[#475569]">
                Configure the active letter while the live letter remains visible.
              </p>
            </div>
            <div className="h-[calc(100%-5.75rem)] overflow-y-auto p-4">
              <ARRecoveryLetterDesigner
                account={account}
                letterForm={letterForm}
                setLetterForm={setLetterForm}
                selectedAccountPromises={selectedAccountPromises}
                accountTasks={accountTasks}
                activeDesignerSection={activeDesignerSection}
                setActiveDesignerSection={setActiveDesignerSection}
              />
            </div>
          </aside>

          <main className="min-h-0 min-w-0 overflow-hidden rounded-[1.5rem] border border-[#BFEAFF] bg-[#F4FBFF] shadow-[0_24px_64px_rgba(2,7,44,.16)]">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#BFEAFF] bg-white p-4">
              <div className="min-w-0">
                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Live Letter</p>
                <p className="mt-1 text-sm font-semibold leading-6 text-[#475569]">
                  Click letter regions to open their matching designer controls.
                </p>
              </div>
              <div className="flex max-w-full gap-2 overflow-x-auto pb-1" role="tablist" aria-label="Letter Studio document tabs">
                {availableTabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={selectedTab === tab.key}
                    onClick={() => setActiveStudioTab(tab.key)}
                    className={`focus-ring shrink-0 rounded-full border px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em] transition ${
                      selectedTab === tab.key
                        ? "border-[#FF6A2B] bg-[rgba(255,106,43,.12)] text-[#0A2540]"
                        : "border-[#BFEAFF] bg-white text-[#475569] hover:border-[#14B8A6]"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="h-[calc(100%-5.75rem)] overflow-y-auto p-4 lg:p-6">
              {studioStatusMessage ? (
                <div className="mb-4 rounded-2xl border border-[#BFEAFF] bg-white px-4 py-3 text-sm font-semibold leading-6 text-[#0A2540]">
                  {studioStatusMessage}
                </div>
              ) : null}
              {selectedTab === "preview" ? (
                <ARRecoveryLetterStudioLiveLetter
                  preview={preview}
                  account={account}
                  onActivateDesignerSection={activateDesignerSection}
                />
              ) : (
                <ARRecoveryStudioTabPlaceholder tab={selectedTab} mode="letter" />
              )}
              {letterFormError ? (
                <div className="mt-4 rounded-2xl border border-[rgba(248,113,113,.28)] bg-[rgba(248,113,113,.08)] px-4 py-3 text-sm font-semibold text-[#B91C1C]">
                  {letterFormError}
                </div>
              ) : null}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

function ARRecoveryLetterDesigner({
  account,
  letterForm,
  setLetterForm,
  selectedAccountPromises,
  accountTasks,
  activeDesignerSection,
  setActiveDesignerSection,
}: {
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  letterForm: ARRecoveryLetterFormState;
  setLetterForm: Dispatch<SetStateAction<ARRecoveryLetterFormState>>;
  selectedAccountPromises: Array<{ id: string; amountCents: number; promiseDate: string; status: string }>;
  accountTasks: Array<{ id: string; taskTitle: string; status: string }>;
  activeDesignerSection: ARRecoveryStudioDesignerSection;
  setActiveDesignerSection: Dispatch<SetStateAction<ARRecoveryStudioDesignerSection>>;
}) {
  const sections: Array<{ key: ARRecoveryStudioDesignerSection; label: string; note: string }> = [
    { key: "template", label: "Template", note: "Select the approved letter type." },
    { key: "recipient", label: "Recipient", note: "Confirm who receives this letter." },
    { key: "branding", label: "Branding", note: "Logo, sender identity, and letterhead." },
    { key: "merge-fields", label: "Merge Fields", note: "Customer, account, balance, and collector fields." },
    { key: "formatting", label: "Formatting", note: "Typography and spacing controls." },
    { key: "layout", label: "Layout", note: "Header, body, footer, and page structure." },
    { key: "sections", label: "Sections", note: "Letter body and operational message." },
    { key: "attachments", label: "Attachments", note: "Supporting documents and account records." },
    { key: "delivery", label: "Delivery", note: "Related promise, task, and delivery handling." },
    { key: "advanced", label: "Advanced", note: "Version, audit, and workflow metadata." },
  ];

  return (
    <div className="grid gap-3">
      {sections.map((section) => {
        const expanded = activeDesignerSection === section.key;
        return (
          <section key={section.key} className="overflow-hidden rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF]">
            <button
              type="button"
              className="focus-ring flex w-full items-start justify-between gap-3 bg-white px-4 py-3 text-left"
              aria-expanded={expanded}
              onClick={() => setActiveDesignerSection(section.key)}
            >
              <span className="min-w-0">
                <span className="block text-sm font-bold text-[#0A2540]">{section.label}</span>
                <span className="mt-1 block text-xs font-semibold leading-5 text-[#64748B]">{section.note}</span>
              </span>
              <span className="shrink-0 rounded-full border border-[#BFEAFF] px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#14B8A6]">
                {expanded ? "Open" : "Edit"}
              </span>
            </button>
            {expanded ? (
              <div className="grid gap-3 border-t border-[#BFEAFF] p-4">
                {renderARRecoveryLetterDesignerSection({
                  section: section.key,
                  account,
                  letterForm,
                  setLetterForm,
                  selectedAccountPromises,
                  accountTasks,
                })}
              </div>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}

function renderARRecoveryLetterDesignerSection({
  section,
  account,
  letterForm,
  setLetterForm,
  selectedAccountPromises,
  accountTasks,
}: {
  section: ARRecoveryStudioDesignerSection;
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  letterForm: ARRecoveryLetterFormState;
  setLetterForm: Dispatch<SetStateAction<ARRecoveryLetterFormState>>;
  selectedAccountPromises: Array<{ id: string; amountCents: number; promiseDate: string; status: string }>;
  accountTasks: Array<{ id: string; taskTitle: string; status: string }>;
}) {
  if (section === "template") {
    return (
      <label className="grid gap-1.5">
        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Letter Type</span>
        <select
          value={letterForm.letterType}
          onChange={(event) => setLetterForm((current) => ({ ...current, letterType: event.target.value as ARRecoveryLetterFormState["letterType"] }))}
          className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
        >
          <option value="">Select letter type</option>
          {LETTER_TYPE_OPTIONS.map((letterType) => <option key={letterType} value={letterType}>{letterType}</option>)}
        </select>
      </label>
    );
  }

  if (section === "recipient") {
    return (
      <div className="grid gap-3">
        <label className="grid gap-1.5">
          <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Recipient</span>
          <input
            value={letterForm.recipient}
            onChange={(event) => setLetterForm((current) => ({ ...current, recipient: event.target.value }))}
            placeholder="Customer contact or department"
            className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
          />
        </label>
        <WorkspaceField label="Customer" value={account.customerName} />
        <WorkspaceField label="Account" value={account.customerNumber} />
      </div>
    );
  }

  if (section === "branding") {
    return (
      <div className="grid gap-2 text-sm font-semibold leading-6 text-[#475569]">
        <WorkspaceField label="Brand" value="CollabRecover AR Solutions" />
        <WorkspaceField label="Accent" value="Hot Orange / Recovery Teal" />
        <WorkspaceField label="Sender" value={account.assignedCollector} />
        <WorkspaceField label="Contact" value={account.contactEmail} />
      </div>
    );
  }

  if (section === "merge-fields") {
    return (
      <div className="grid gap-3">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <WorkspaceField label="Customer Name" value={account.customerName} />
          <WorkspaceField label="Account Number" value={account.customerNumber} />
          <WorkspaceField label="Balance" value={formatARRecoveryCurrency(account.openBalanceCents)} />
          <WorkspaceField label="Aging" value={account.agingSummary} />
        </div>
        <div className="flex flex-wrap gap-2">
          {["CustomerName", "AccountNumber", "OpenBalance", "AgingBucket", "Collector", "Manager"].map((field) => (
            <span key={field} className="rounded-full border border-[#BFEAFF] bg-white px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-[#0F766E]">
              {field}
            </span>
          ))}
        </div>
      </div>
    );
  }

  if (section === "formatting") {
    return (
      <div className="grid gap-2">
        {["Document typography", "Paragraph spacing", "Address block spacing", "Signature spacing"].map((item) => (
          <div key={item} className="rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold text-[#475569]">
            {item}
          </div>
        ))}
      </div>
    );
  }

  if (section === "layout") {
    return (
      <div className="grid gap-2">
        {["Letterhead", "Recipient block", "Body", "Signature", "Footer"].map((item) => (
          <div key={item} className="flex items-center justify-between gap-3 rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold text-[#475569]">
            <span>{item}</span>
            <span className="rounded-full border border-[#BFEAFF] px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#14B8A6]">Active</span>
          </div>
        ))}
      </div>
    );
  }

  if (section === "sections") {
    return (
      <label className="grid gap-1.5">
        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Additional Notes</span>
        <textarea
          value={letterForm.additionalNotes}
          onChange={(event) => setLetterForm((current) => ({ ...current, additionalNotes: event.target.value }))}
          placeholder="Optional generation notes."
          rows={5}
          className="focus-ring rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold leading-6 text-[#0A2540]"
        />
      </label>
    );
  }

  if (section === "attachments") {
    return (
      <div className="grid gap-2">
        {["Open invoices", "Account statement", "Promise record"].map((item) => (
          <div key={item} className="rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold text-[#475569]">
            {item}
          </div>
        ))}
      </div>
    );
  }

  if (section === "delivery") {
    return (
      <div className="grid gap-3">
        <label className="grid gap-1.5">
          <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Related Promise</span>
          <select
            value={letterForm.relatedPromise}
            onChange={(event) => setLetterForm((current) => ({ ...current, relatedPromise: event.target.value }))}
            className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
          >
            <option value="">None</option>
            {selectedAccountPromises.map((promise) => (
              <option key={promise.id} value={promise.id}>
                {formatARRecoveryCurrency(promise.amountCents)} | {formatCapturedDate(promise.promiseDate)} | {promise.status}
              </option>
            ))}
          </select>
        </label>
        <label className="grid gap-1.5">
          <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Related Task</span>
          <select
            value={letterForm.relatedTask}
            onChange={(event) => setLetterForm((current) => ({ ...current, relatedTask: event.target.value }))}
            className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
          >
            <option value="">None</option>
            {accountTasks.map((task) => (
              <option key={task.id} value={task.id}>
                {task.taskTitle} | {task.status}
              </option>
            ))}
          </select>
        </label>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[#BFEAFF] bg-white p-3 text-sm font-semibold leading-6 text-[#475569]">
      Version, audit, and workflow metadata remain governed by the document workflow.
    </div>
  );
}

function ARRecoveryLetterStudioLiveLetter({
  preview,
  account,
  onActivateDesignerSection,
}: {
  preview: LetterPreview;
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  onActivateDesignerSection: (section: ARRecoveryStudioDesignerSection) => void;
}) {
  return (
    <div className="mx-auto max-w-[64rem]">
      <article className="min-h-[68rem] rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-[0_24px_70px_rgba(15,23,42,.12)] sm:p-8 lg:p-12">
        <button
          type="button"
          onClick={() => onActivateDesignerSection("branding")}
          className="focus-ring flex w-full flex-wrap items-start justify-between gap-4 rounded-xl border border-transparent p-3 text-left transition hover:border-[#BFEAFF] hover:bg-[#F4FBFF]"
          aria-label="Edit company logo and branding"
        >
          <span className="min-w-0">
            <span className="block text-[11px] font-black uppercase tracking-[0.18em] text-[#14B8A6]">Company Logo</span>
            <span className="mt-2 block text-2xl font-bold text-[#0A2540]">CollabRecover AR Solutions</span>
          </span>
          <span className="rounded-full border border-[#BFEAFF] px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-[#0F766E]">
            Edit Branding
          </span>
        </button>

        <div className="mt-8 grid gap-6 border-t border-slate-200 pt-8 lg:grid-cols-[minmax(0,1fr)_14rem]">
          <button
            type="button"
            onClick={() => onActivateDesignerSection("merge-fields")}
            className="focus-ring rounded-xl border border-transparent p-3 text-left transition hover:border-[#BFEAFF] hover:bg-[#F4FBFF]"
            aria-label="Edit customer name and merge fields"
          >
            <p className="text-sm font-semibold leading-6 text-[#475569]">{preview.address}</p>
            <p className="text-sm font-semibold leading-6 text-[#475569]">Account {preview.account}</p>
            <p className="mt-4 text-sm font-semibold text-[#475569]">Customer Name</p>
            <h3 className="mt-1 break-words text-2xl font-bold text-[#0A2540]">{preview.customer}</h3>
          </button>
          <div className="rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] p-4 text-sm font-semibold leading-6 text-[#475569]">
            <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Letter Context</p>
            <p className="mt-2">Date: {formatCapturedDate(new Date().toISOString())}</p>
            <p>Balance: {formatARRecoveryCurrency(account.openBalanceCents)}</p>
            <p>Aging: {account.agingSummary}</p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => onActivateDesignerSection("sections")}
          className="focus-ring mt-10 w-full rounded-xl border border-transparent p-3 text-left transition hover:border-[#BFEAFF] hover:bg-[#F4FBFF]"
          aria-label="Edit letter body sections"
        >
          <p className="text-sm font-bold uppercase tracking-[0.14em] text-[#0F766E]">RE: Collection Letter</p>
          <div className="mt-8 whitespace-pre-line text-base font-semibold leading-8 text-[#0A2540]">
            {preview.body}
          </div>
        </button>

        <div className="mt-10 grid gap-8">
          <div className="rounded-xl border border-transparent p-3">
            <p className="text-sm font-semibold leading-7 text-[#475569]">{preview.signature}</p>
          </div>
          <button
            type="button"
            onClick={() => onActivateDesignerSection("layout")}
            className="focus-ring w-full rounded-xl border border-transparent p-3 text-left transition hover:border-[#BFEAFF] hover:bg-[#F4FBFF]"
            aria-label="Edit footer and page layout"
          >
            <p className="border-t border-slate-200 pt-4 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
              Footer
            </p>
            <p className="mt-2 text-xs font-semibold leading-5 text-slate-500">
              CollabRecover AR Solutions | Recovery correspondence prepared for {account.customerName}.
            </p>
          </button>
        </div>
      </article>
    </div>
  );
}

function ARRecoveryStudioDesigner({
  mode,
  account,
  letterForm,
  statementForm,
  setLetterForm,
  setStatementForm,
  selectedAccountPromises,
  accountTasks,
  activeDesignerSection,
  setActiveDesignerSection,
}: {
  mode: Exclude<ARRecoveryEnterpriseStudioMode, null>;
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  letterForm: ARRecoveryLetterFormState;
  statementForm: ARRecoveryStatementFormState;
  setLetterForm: Dispatch<SetStateAction<ARRecoveryLetterFormState>>;
  setStatementForm: Dispatch<SetStateAction<ARRecoveryStatementFormState>>;
  selectedAccountPromises: Array<{ id: string; amountCents: number; promiseDate: string; status: string }>;
  accountTasks: Array<{ id: string; taskTitle: string; status: string }>;
  activeDesignerSection: ARRecoveryStudioDesignerSection;
  setActiveDesignerSection: Dispatch<SetStateAction<ARRecoveryStudioDesignerSection>>;
}) {
  const sections: Array<{ key: ARRecoveryStudioDesignerSection; label: string; note: string }> = [
    { key: "template", label: "Template", note: "Document type and approved template selection." },
    { key: "branding", label: "Branding", note: "Company identity, header, footer, and logo context." },
    { key: "merge-fields", label: "Merge Fields", note: "Customer, account, promise, task, and invoice fields." },
    { key: "formatting", label: "Formatting", note: "Spacing, typography, and address layout guidance." },
    { key: "sections", label: "Sections", note: "Document body, invoice listing, and summary blocks." },
    { key: "attachments", label: "Attachments", note: "Supporting files and related records." },
    { key: "delivery", label: "Delivery", note: "Email, print, PDF, and test-send controls." },
    { key: "advanced", label: "Advanced", note: "Audit, versions, and workflow metadata." },
  ];

  return (
    <div className="grid gap-3">
      {sections.map((section) => {
        const expanded = activeDesignerSection === section.key;
        return (
          <section key={section.key} className="overflow-hidden rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF]">
            <button
              type="button"
              className="focus-ring flex w-full items-start justify-between gap-3 bg-white px-4 py-3 text-left"
              aria-expanded={expanded}
              onClick={() => setActiveDesignerSection(section.key)}
            >
              <span className="min-w-0">
                <span className="block text-sm font-bold text-[#0A2540]">{section.label}</span>
                <span className="mt-1 block text-xs font-semibold leading-5 text-[#64748B]">{section.note}</span>
              </span>
              <span className="shrink-0 rounded-full border border-[#BFEAFF] px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#14B8A6]">
                {expanded ? "Open" : "Edit"}
              </span>
            </button>
            {expanded ? (
              <div className="grid gap-3 border-t border-[#BFEAFF] p-4">
                {renderARRecoveryStudioDesignerSection({
                  section: section.key,
                  mode,
                  account,
                  letterForm,
                  statementForm,
                  setLetterForm,
                  setStatementForm,
                  selectedAccountPromises,
                  accountTasks,
                })}
              </div>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}

function renderARRecoveryStudioDesignerSection({
  section,
  mode,
  account,
  letterForm,
  statementForm,
  setLetterForm,
  setStatementForm,
  selectedAccountPromises,
  accountTasks,
}: {
  section: ARRecoveryStudioDesignerSection;
  mode: Exclude<ARRecoveryEnterpriseStudioMode, null>;
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  letterForm: ARRecoveryLetterFormState;
  statementForm: ARRecoveryStatementFormState;
  setLetterForm: Dispatch<SetStateAction<ARRecoveryLetterFormState>>;
  setStatementForm: Dispatch<SetStateAction<ARRecoveryStatementFormState>>;
  selectedAccountPromises: Array<{ id: string; amountCents: number; promiseDate: string; status: string }>;
  accountTasks: Array<{ id: string; taskTitle: string; status: string }>;
}) {
  if (section === "template") {
    return mode === "letter" ? (
      <label className="grid gap-1.5">
        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Letter Type</span>
        <select
          value={letterForm.letterType}
          onChange={(event) => setLetterForm((current) => ({ ...current, letterType: event.target.value as ARRecoveryLetterFormState["letterType"] }))}
          className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
        >
          <option value="">Select letter type</option>
          {LETTER_TYPE_OPTIONS.map((letterType) => <option key={letterType} value={letterType}>{letterType}</option>)}
        </select>
      </label>
    ) : (
      <div className="grid gap-3">
        <label className="grid gap-1.5">
          <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Statement Type</span>
          <select
            value={statementForm.statementType}
            onChange={(event) => setStatementForm((current) => ({ ...current, statementType: event.target.value as ARRecoveryStatementFormState["statementType"] }))}
            className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
          >
            <option value="">Select statement type</option>
            {STATEMENT_TYPE_OPTIONS.map((statementType) => <option key={statementType} value={statementType}>{statementType}</option>)}
          </select>
        </label>
        <label className="grid gap-1.5">
          <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Statement Date</span>
          <input
            type="date"
            value={statementForm.statementDate}
            onChange={(event) => setStatementForm((current) => ({ ...current, statementDate: event.target.value }))}
            className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
          />
        </label>
      </div>
    );
  }

  if (section === "merge-fields") {
    return (
      <div className="grid gap-3">
        {mode === "letter" ? (
          <label className="grid gap-1.5">
            <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Recipient</span>
            <input
              value={letterForm.recipient}
              onChange={(event) => setLetterForm((current) => ({ ...current, recipient: event.target.value }))}
              placeholder="Customer contact or department"
              className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
            />
          </label>
        ) : (
          <label className="grid gap-1.5">
            <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Select Invoices</span>
            <select
              multiple
              value={statementForm.relatedInvoices}
              onChange={(event) => setStatementForm((current) => ({ ...current, relatedInvoices: Array.from(event.target.selectedOptions).map((option) => option.value) }))}
              className="focus-ring min-h-36 rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold text-[#0A2540]"
            >
              {buildInvoicePlaceholders(account).map((invoice) => (
                <option key={invoice.id} value={invoice.invoiceNumber}>
                  {invoice.invoiceNumber} | Due {formatCapturedDate(invoice.dueDate)} | {formatARRecoveryCurrency(invoice.amountCents)}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="flex flex-wrap gap-2">
          {["CustomerName", "AccountNumber", "OpenBalance", "AgingBucket", "Collector", "Manager"].map((field) => (
            <span key={field} className="rounded-full border border-[#BFEAFF] bg-white px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-[#0F766E]">
              {field}
            </span>
          ))}
        </div>
      </div>
    );
  }

  if (section === "sections") {
    return mode === "letter" ? (
      <label className="grid gap-1.5">
        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Additional Notes</span>
        <textarea
          value={letterForm.additionalNotes}
          onChange={(event) => setLetterForm((current) => ({ ...current, additionalNotes: event.target.value }))}
          placeholder="Optional internal generation notes."
          rows={4}
          className="focus-ring rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold leading-6 text-[#0A2540]"
        />
      </label>
    ) : (
      <label className="grid gap-1.5">
        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Statement Notes</span>
        <textarea
          value={statementForm.statementNotes}
          onChange={(event) => setStatementForm((current) => ({ ...current, statementNotes: event.target.value }))}
          placeholder="Optional statement notes for the permanent record."
          rows={4}
          className="focus-ring rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold leading-6 text-[#0A2540]"
        />
      </label>
    );
  }

  if (section === "delivery") {
    return (
      <div className="grid gap-3">
        {mode === "statement" ? (
          <label className="grid gap-1.5">
            <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Delivery Option</span>
            <select
              value={statementForm.deliveryOption}
              onChange={(event) => setStatementForm((current) => ({ ...current, deliveryOption: event.target.value as ARRecoveryStatementDeliveryOption }))}
              className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
            >
              <option value="Email">Email</option>
              <option value="Download PDF">Download PDF</option>
              <option value="Print">Print</option>
            </select>
          </label>
        ) : null}
        <label className="grid gap-1.5">
          <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Related Promise</span>
          <select
            value={mode === "letter" ? letterForm.relatedPromise : statementForm.relatedPromise}
            onChange={(event) => mode === "letter"
              ? setLetterForm((current) => ({ ...current, relatedPromise: event.target.value }))
              : setStatementForm((current) => ({ ...current, relatedPromise: event.target.value }))}
            className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
          >
            <option value="">None</option>
            {selectedAccountPromises.map((promise) => (
              <option key={promise.id} value={promise.id}>
                {formatARRecoveryCurrency(promise.amountCents)} | {formatCapturedDate(promise.promiseDate)} | {promise.status}
              </option>
            ))}
          </select>
        </label>
        <label className="grid gap-1.5">
          <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Related Task</span>
          <select
            value={mode === "letter" ? letterForm.relatedTask : statementForm.relatedTask}
            onChange={(event) => mode === "letter"
              ? setLetterForm((current) => ({ ...current, relatedTask: event.target.value }))
              : setStatementForm((current) => ({ ...current, relatedTask: event.target.value }))}
            className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
          >
            <option value="">None</option>
            {accountTasks.map((task) => (
              <option key={task.id} value={task.id}>
                {task.taskTitle} | {task.status}
              </option>
            ))}
          </select>
        </label>
      </div>
    );
  }

  if (section === "branding") {
    return (
      <div className="grid gap-2 text-sm font-semibold leading-6 text-[#475569]">
        <WorkspaceField label="Business Name" value="CollabRecover AR Solutions" />
        <WorkspaceField label="Contact" value={account.contactName} />
        <WorkspaceField label="Email" value={account.contactEmail} />
        <WorkspaceField label="Phone" value={account.contactPhone} />
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[#BFEAFF] bg-white p-3 text-sm font-semibold leading-6 text-[#475569]">
      This section is prepared for {section.replace("-", " ")} controls without changing the underlying document workflow.
    </div>
  );
}

function ARRecoveryLetterStudioPreview({
  preview,
  account,
  onActivateDesignerSection,
  activeTab,
}: {
  preview: LetterPreview;
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  onActivateDesignerSection: (section: ARRecoveryStudioDesignerSection) => void;
  activeTab: ARRecoveryStudioDocumentTab;
}) {
  if (activeTab !== "preview") {
    return <ARRecoveryStudioTabPlaceholder tab={activeTab} mode="letter" />;
  }

  return (
    <article className="mx-auto min-h-[48rem] max-w-[58rem] rounded-[1.5rem] border border-slate-200 bg-white p-8 shadow-[0_24px_70px_rgba(15,23,42,.12)]">
      <button type="button" onClick={() => onActivateDesignerSection("branding")} className="focus-ring w-full rounded-xl border border-transparent p-3 text-left transition hover:border-[#BFEAFF] hover:bg-[#F4FBFF]">
        <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#14B8A6]">Company Logo</p>
        <h3 className="mt-1 text-2xl font-bold text-[#0A2540]">CollabRecover AR Solutions</h3>
      </button>
      <button type="button" onClick={() => onActivateDesignerSection("formatting")} className="focus-ring mt-6 rounded-xl border border-transparent p-3 text-left transition hover:border-[#BFEAFF] hover:bg-[#F4FBFF]">
        <p className="text-sm font-semibold leading-6 text-[#475569]">{preview.address}</p>
        <p className="text-sm font-semibold leading-6 text-[#475569]">Account {preview.account}</p>
      </button>
      <button type="button" onClick={() => onActivateDesignerSection("merge-fields")} className="focus-ring mt-8 rounded-xl border border-transparent p-3 text-left transition hover:border-[#BFEAFF] hover:bg-[#F4FBFF]">
        <p className="text-sm font-semibold text-[#475569]">Customer Name</p>
        <h4 className="mt-1 text-xl font-bold text-[#0A2540]">{preview.customer}</h4>
      </button>
      <button type="button" onClick={() => onActivateDesignerSection("sections")} className="focus-ring mt-8 w-full rounded-xl border border-transparent p-3 text-left transition hover:border-[#BFEAFF] hover:bg-[#F4FBFF]">
        <p className="whitespace-pre-line text-base font-semibold leading-8 text-[#0A2540]">{preview.body}</p>
      </button>
      <button type="button" onClick={() => onActivateDesignerSection("advanced")} className="focus-ring mt-10 w-full rounded-xl border border-transparent p-3 text-left transition hover:border-[#BFEAFF] hover:bg-[#F4FBFF]">
        <p className="text-sm font-semibold leading-7 text-[#475569]">{preview.signature}</p>
        <p className="mt-6 border-t border-slate-200 pt-4 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Footer</p>
      </button>
    </article>
  );
}

function ARRecoveryStatementStudioPreview({
  preview,
  account,
  onActivateDesignerSection,
  activeTab,
}: {
  preview: StatementPreview;
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  onActivateDesignerSection: (section: ARRecoveryStudioDesignerSection) => void;
  activeTab: ARRecoveryStudioDocumentTab;
}) {
  if (activeTab !== "preview") {
    return <ARRecoveryStudioTabPlaceholder tab={activeTab} mode="statement" />;
  }

  return (
    <article className="mx-auto min-h-[48rem] max-w-[64rem] rounded-[1.5rem] border border-slate-200 bg-white p-8 shadow-[0_24px_70px_rgba(15,23,42,.12)]">
      <button type="button" onClick={() => onActivateDesignerSection("branding")} className="focus-ring w-full rounded-xl border border-transparent p-3 text-left transition hover:border-[#BFEAFF] hover:bg-[#F4FBFF]">
        <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#14B8A6]">Company Logo</p>
        <h3 className="mt-1 text-2xl font-bold text-[#0A2540]">{preview.client}</h3>
      </button>
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <button type="button" onClick={() => onActivateDesignerSection("merge-fields")} className="focus-ring rounded-xl border border-transparent p-3 text-left transition hover:border-[#BFEAFF] hover:bg-[#F4FBFF]">
          <p className="text-sm font-semibold text-[#475569]">Customer Name</p>
          <h4 className="mt-1 text-xl font-bold text-[#0A2540]">{preview.customer}</h4>
          <p className="mt-1 text-sm font-semibold text-[#475569]">Account {preview.account}</p>
        </button>
        <button type="button" onClick={() => onActivateDesignerSection("template")} className="focus-ring rounded-xl border border-transparent p-3 text-left transition hover:border-[#BFEAFF] hover:bg-[#F4FBFF]">
          <p className="text-sm font-semibold text-[#475569]">Statement Date</p>
          <h4 className="mt-1 text-xl font-bold text-[#0A2540]">{preview.statementDate}</h4>
          <p className="mt-1 text-sm font-semibold text-[#475569]">{preview.deliveryOption}</p>
        </button>
      </div>
      <button type="button" onClick={() => onActivateDesignerSection("sections")} className="focus-ring mt-8 w-full rounded-xl border border-transparent p-3 text-left transition hover:border-[#BFEAFF] hover:bg-[#F4FBFF]">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[38rem] text-left text-sm text-[#0A2540]">
            <thead className="border-b border-slate-200 text-[11px] font-black uppercase tracking-[0.14em] text-[#14B8A6]">
              <tr>
                <th className="py-2">Invoice</th>
                <th className="py-2">Due Date</th>
                <th className="py-2 text-right">Balance</th>
                <th className="py-2">Aging</th>
                <th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {preview.lineItems.map((item) => (
                <tr key={item.invoiceNumber} className="border-b border-slate-100">
                  <td className="py-3 font-semibold">{item.invoiceNumber}</td>
                  <td className="py-3">{item.dueDate}</td>
                  <td className="py-3 text-right font-semibold">{item.balance}</td>
                  <td className="py-3">{item.aging}</td>
                  <td className="py-3">{item.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </button>
      <button type="button" onClick={() => onActivateDesignerSection("advanced")} className="focus-ring mt-8 w-full rounded-xl border border-transparent p-3 text-left transition hover:border-[#BFEAFF] hover:bg-[#F4FBFF]">
        <p className="whitespace-pre-line text-base font-semibold leading-8 text-[#0A2540]">{preview.body}</p>
        <p className="mt-6 border-t border-slate-200 pt-4 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Footer</p>
      </button>
    </article>
  );
}

function ARRecoveryStudioTabPlaceholder({ tab, mode }: { tab: ARRecoveryStudioDocumentTab; mode: "letter" | "statement" }) {
  return (
    <article className="mx-auto max-w-[54rem] rounded-[1.5rem] border border-[#BFEAFF] bg-white p-6 shadow-[0_18px_46px_rgba(15,23,42,.08)]">
      <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#14B8A6]">{mode === "letter" ? "Letter Studio" : "Statement Studio"}</p>
      <h3 className="mt-2 text-2xl font-bold text-[#0A2540]">{tab.replace("-", " ")}</h3>
      <p className="mt-3 text-sm font-semibold leading-7 text-[#475569]">
        This Studio tab is reserved inside the Version 1 Enterprise Studio foundation and uses the same full-screen document workspace standard.
      </p>
    </article>
  );
}

function renderAccountTab(
  snapshot: ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>,
  selectedAccount: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>,
  selectedTab: ARRecoveryAccountTabKey,
) {
  const accountActivity = snapshot.workspace.activity.filter((item) => item.accountId === selectedAccount.id);
  const accountPromises = snapshot.workspace.promises.filter((item) => item.accountId === selectedAccount.id);
  const accountTicklers = snapshot.workspace.ticklers.filter((item) => item.accountId === selectedAccount.id);
  const accountRecoveryEvents = snapshot.workspace.recoveryActivityEvents.filter((item) => item.accountId === selectedAccount.id);

  switch (selectedTab) {
    case "overview":
      return (
        <div className="grid gap-4 xl:grid-cols-[1fr_.92fr]">
          <div className="grid gap-3">
            <SectionHeading eyebrow="Overview" title="Account summary" description="Current risk, balance, and next action in one view." />
            <div className="grid gap-3 md:grid-cols-2">
              <DetailMetric label="Current balance" value={formatARRecoveryCurrency(selectedAccount.openBalanceCents)} />
              <DetailMetric label="Risk score" value={String(buildRiskScore(selectedAccount))} />
              <DetailMetric label="Recovery status" value={selectedAccount.recoveryStatus} />
              <DetailMetric label="Next action date" value={snapshot.nextActionDate ? formatDate(snapshot.nextActionDate) : "Not set"} />
              <DetailMetric label="Promise status" value={selectedAccount.promiseToPayCount ? "Open promise" : "No active promise"} />
              <DetailMetric label="Last activity" value={formatDate(latestAccountActivityDate(accountActivity))} />
            </div>
          </div>
          <div className="rounded-3xl border border-[rgba(34,224,208,.14)] bg-[rgba(7,18,31,.72)] p-4">
            <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Single Account Actions</p>
            <div className="mt-4 grid gap-3">
              <ActionPill label="Add Note" note="Capture internal or customer-facing context." />
              <ActionPill label="Add Tickler" note="Create a follow-up, escalation, or promise reminder." />
              <ActionPill label="Create Promise" note="Record amount, date, and outcome for the account." />
              <ActionPill label="Send Letter" note="Queue a collection letter placeholder." />
              <ActionPill label="Escalate" note="Route the account for manager review." />
            </div>
          </div>
        </div>
      );
    case "activity":
      return <AccountTimelinePanel account={selectedAccount} timelineEntries={buildAccountTimelineEntries(selectedAccount, accountActivity)} />;
    case "invoices":
      return <AccountOpenInvoicesPanel account={selectedAccount} promises={accountPromises} />;
    case "notes":
      return <AccountNotesPanel account={selectedAccount} activityEvents={accountRecoveryEvents} promises={accountPromises} />;
    case "communications":
      return (
        <ManagerEmptyState
          title="Communications manager unavailable"
          description="The active customer account workspace mounts communications through the selected-account workflow."
          action="Return to the account workspace and select the Communications tab."
        />
      );
    case "tasks":
      return (
        <ManagerEmptyState
          title="Task manager unavailable"
          description="The active customer account workspace mounts assignments through the selected-account workflow."
          action="Return to the account workspace and select the Tasks tab."
        />
      );
    case "letters":
      return (
        <ManagerEmptyState
          title="Letter manager unavailable"
          description="The active customer account workspace mounts letter production through the selected-account workflow."
          action="Return to the account workspace and select the Letter Area tab."
        />
      );
    case "statements":
      return <AccountStatementPanel account={selectedAccount} statements={snapshot.workspace.statements.filter((item) => item.accountId === selectedAccount.id)} activityEvents={accountRecoveryEvents} />;
    case "promises":
      return <AccountRecordList title="Promises" records={accountPromises.map((promise) => ({ id: promise.id, primary: `${formatARRecoveryCurrency(promise.amountCents)} on ${formatDate(promise.promiseDate)}`, secondary: promise.status, meta: promise.notes }))} />;
    case "ticklers":
      return (
        <ManagerEmptyState
          title="Tickler manager unavailable"
          description="The active customer account workspace mounts follow-ups through the selected-account workflow."
          action="Return to the account workspace and select the Tickler Area tab."
        />
      );
    case "documents":
      return <AccountRecordList title="Documents" records={buildDocumentPlaceholders(selectedAccount).map((record) => ({ id: record.id, primary: record.title, secondary: record.documentType, meta: `${record.status} • ${record.notes}` }))} />;
    case "history":
      return <AccountRecordList title="Recovery History" records={buildRecoveryHistory(selectedAccount, accountActivity, accountPromises, accountTicklers, []).map((record) => ({ id: record.id, primary: record.title, secondary: record.kind, meta: `${formatDate(record.createdAt)} • ${record.detail}` }))} />;
    default:
      return null;
  }
}

function AccountTimelinePanel({
  account,
  timelineEntries,
}: {
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  timelineEntries: Array<{
    id: string;
    actionType: string;
    date: string;
    time: string;
    user: string;
    status: string;
    preview: string;
    outcome: string;
    nextAction: string;
    relatedAccountNumber: string;
    relatedAccountName: string;
    relatedAccountBalance: string;
    createdBy: string;
    createdDate: string;
    updatedBy: string;
    updatedDate: string;
  }>;
}) {
  const [selectedFilter, setSelectedFilter] = useState<AccountTimelineFilterKey>("all");
  const [selectedTimelineId, setSelectedTimelineId] = useState(timelineEntries[0]?.id || "");
  const filteredTimelineEntries = timelineEntries.filter((entry) => timelineEntryMatchesFilter(entry, selectedFilter));
  const selectedEntry =
    filteredTimelineEntries.find((entry) => entry.id === selectedTimelineId) ||
    filteredTimelineEntries[0] ||
    null;

  useEffect(() => {
    if (!filteredTimelineEntries.length) {
      setSelectedTimelineId("");
      return;
    }
    if (!filteredTimelineEntries.some((entry) => entry.id === selectedTimelineId)) {
      setSelectedTimelineId(filteredTimelineEntries[0].id);
    }
  }, [filteredTimelineEntries, selectedTimelineId]);

  const summaryItems = [
    { label: "Total Activity", value: String(timelineEntries.length) },
    { label: "Calls", value: String(countTimelineEntries(timelineEntries, "calls")) },
    { label: "Notes", value: String(countTimelineEntries(timelineEntries, "notes")) },
    { label: "Comms", value: String(countTimelineEntries(timelineEntries, "communications")) },
    { label: "Promises", value: String(countTimelineEntries(timelineEntries, "promises")) },
  ];

  return (
    <div className="min-w-0 max-w-full overflow-hidden rounded-[1.5rem] border border-[#BFEAFF] bg-[#F4FBFF] p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <ARLightLineIcon compact icon={<ClipboardList className="size-5" aria-hidden="true" />} />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[#0A2540]">Timeline Manager</p>
            <p className="mt-1 text-sm font-normal leading-6 text-[#475569]">Activity feed and selected event detail for {account.customerName}.</p>
          </div>
        </div>
        <span className="rounded-full border border-[#14B8A6] bg-white px-3 py-1 text-xs font-semibold text-[#0A2540]">
          {filteredTimelineEntries.length} shown
        </span>
      </div>

      <div className="mt-3">
        <CompactMetricStrip items={summaryItems} />
      </div>

      <div className="mt-3 rounded-xl border border-[#BFEAFF] bg-white p-3">
        <p className="mb-2 text-xs font-medium text-[#475569]">Activity Filters</p>
        <div className="flex flex-wrap gap-2">
          {TIMELINE_FILTERS.map((filter) => {
            const active = selectedFilter === filter.key;
            return (
              <button
                key={filter.key}
                type="button"
                onClick={() => setSelectedFilter(filter.key)}
                className={`focus-ring rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                  active
                    ? "border-[#14B8A6] bg-[#14B8A6] text-white"
                    : "border-[#BFEAFF] bg-[#F4FBFF] text-[#0A2540] hover:border-[#14B8A6]"
                }`}
              >
                {filter.label} {countTimelineEntries(timelineEntries, filter.key)}
              </button>
            );
          })}
        </div>
      </div>

      {timelineEntries.length ? (
        <div className="mt-3 grid min-w-0 max-w-full gap-3 overflow-hidden xl:grid-cols-[minmax(0,40fr)_minmax(0,60fr)]">
          <section className="min-h-0 min-w-0 max-w-full overflow-hidden rounded-xl border border-[#BFEAFF] bg-white">
            <div className="flex min-w-0 flex-wrap items-center justify-between gap-3 border-b border-[#BFEAFF] px-3 py-2">
              <div className="flex min-w-0 items-center gap-2">
                <ARLightLineIcon compact icon={<Activity className="size-5" aria-hidden="true" />} />
                <p className="min-w-0 break-words text-sm font-semibold text-[#0A2540]">Activity Feed</p>
              </div>
              <span className="max-w-full rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-2 py-1 text-xs font-semibold text-[#0A2540]">
                {filteredTimelineEntries.length} records
              </span>
            </div>
            <div className="max-h-[24rem] min-w-0 max-w-full overflow-y-auto overflow-x-hidden p-2 xl:max-h-[31rem]">
              {filteredTimelineEntries.length ? (
                <div className="grid min-w-0 max-w-full gap-2">
                  {filteredTimelineEntries.map((entry) => {
                    const active = selectedEntry?.id === entry.id;
                    return (
                      <button
                        key={entry.id}
                        type="button"
                        onClick={() => setSelectedTimelineId(entry.id)}
                        className={`focus-ring min-w-0 max-w-full overflow-hidden rounded-xl border p-2.5 text-left transition ${
                          active
                            ? "border-[#14B8A6] bg-[#F4FBFF]"
                            : "border-[#BFEAFF] bg-white hover:border-[#14B8A6]"
                        }`}
                      >
                        <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
                          <div className="min-w-0 max-w-full">
                            <p className="max-w-full break-words text-sm font-semibold text-[#0A2540]">{entry.actionType}</p>
                            <p className="mt-1 max-w-full break-words text-xs font-medium leading-5 text-[#475569]">
                              {entry.date} at {entry.time} by {entry.user}
                            </p>
                          </div>
                          <span className={`max-w-full rounded-full border px-2 py-1 text-xs font-semibold ${timelineStatusBadgeClass(entry.status, entry.actionType)}`}>
                            {entry.status}
                          </span>
                        </div>
                        <p className="mt-2 max-w-full overflow-hidden break-words text-sm font-normal leading-5 text-[#475569] [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
                          {entry.preview}
                        </p>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <ManagerEmptyState
                  title="No activity matches"
                  description={`No activity matches the selected filter for ${account.customerName}.`}
                  action="Select another filter to review account activity."
                />
              )}
            </div>
          </section>

          <section className="min-h-0 min-w-0 max-w-full overflow-hidden rounded-xl border border-[#BFEAFF] bg-white">
            <div className="flex min-w-0 flex-wrap items-center justify-between gap-3 border-b border-[#BFEAFF] px-3 py-2">
              <div className="flex min-w-0 items-center gap-2">
                <ARLightLineIcon compact icon={<FileText className="size-5" aria-hidden="true" />} />
                <p className="min-w-0 break-words text-sm font-semibold text-[#0A2540]">Selected Activity Detail</p>
              </div>
              {selectedEntry ? (
                <span className={`max-w-full break-words rounded-full border px-2 py-1 text-xs font-semibold ${timelineStatusBadgeClass(selectedEntry.status, selectedEntry.actionType)}`}>
                  {selectedEntry.actionType}
                </span>
              ) : null}
            </div>
            <div className="max-h-[24rem] min-w-0 max-w-full overflow-y-auto overflow-x-hidden p-3 xl:max-h-[31rem]">
              {selectedEntry ? (
                <div className="grid min-w-0 max-w-full gap-3">
                  <div className="grid min-w-0 max-w-full gap-2 md:grid-cols-[repeat(2,minmax(0,1fr))] xl:grid-cols-[repeat(3,minmax(0,1fr))]">
                    <WorkspaceField label="Activity Type" value={selectedEntry.actionType} />
                    <WorkspaceField label="Date" value={selectedEntry.date} />
                    <WorkspaceField label="Time" value={selectedEntry.time} />
                    <WorkspaceField label="User" value={selectedEntry.user} />
                    <WorkspaceField label="Status" value={selectedEntry.status} />
                  </div>

                  <RelatedAccountSummary
                    accountNumber={selectedEntry.relatedAccountNumber}
                    accountName={selectedEntry.relatedAccountName}
                    currentBalance={selectedEntry.relatedAccountBalance}
                  />

                  <div className="grid min-w-0 max-w-full gap-2 xl:grid-cols-[repeat(2,minmax(0,1fr))]">
                    <WorkspaceField label="Outcome" value={selectedEntry.outcome} />
                    <WorkspaceField label="Next Action" value={selectedEntry.nextAction} />
                  </div>

                  <AuditStrip
                    createdBy={selectedEntry.createdBy}
                    createdDate={selectedEntry.createdDate}
                    createdTime={selectedEntry.time}
                    updatedBy={selectedEntry.updatedBy}
                    updatedDate={selectedEntry.updatedDate}
                    updatedTime={selectedEntry.time}
                  />
                </div>
              ) : (
                <ManagerEmptyState
                  title="No activity selected"
                  description="No activity matches the current filter."
                  action="Select another filter or clear filters to view activity."
                />
              )}
            </div>
          </section>
        </div>
      ) : (
        <div className="mt-3">
          <ManagerEmptyState
            title="No activity available"
            description={`No account activity is available yet for ${account.customerName}.`}
            action="Activity will appear here as calls, notes, promises, ticklers, letters, statements, and status changes are captured."
          />
        </div>
      )}
    </div>
  );
}

type AccountTimelineEntry = {
  id: string;
  actionType: string;
  date: string;
  time: string;
  user: string;
  status: string;
  preview: string;
  outcome: string;
  nextAction: string;
  relatedAccountNumber: string;
  relatedAccountName: string;
  relatedAccountBalance: string;
  createdBy: string;
  createdDate: string;
  updatedBy: string;
  updatedDate: string;
};
type AccountTimelineFilterKey = "all" | "calls" | "notes" | "communications" | "promises" | "ticklers" | "letters" | "statements" | "escalations" | "payments" | "follow_ups";

const TIMELINE_FILTERS: Array<{ key: AccountTimelineFilterKey; label: string }> = [
  { key: "all", label: "All Activity" },
  { key: "calls", label: "Calls" },
  { key: "notes", label: "Notes" },
  { key: "communications", label: "Communications" },
  { key: "promises", label: "Promises" },
  { key: "ticklers", label: "Ticklers" },
  { key: "letters", label: "Letters" },
  { key: "statements", label: "Statements" },
  { key: "escalations", label: "Escalations" },
  { key: "payments", label: "Payments" },
  { key: "follow_ups", label: "Follow-Ups" },
];

function timelineEntryMatchesFilter(entry: AccountTimelineEntry, filter: AccountTimelineFilterKey) {
  if (filter === "all") return true;
  const normalized = `${entry.actionType} ${entry.preview} ${entry.outcome} ${entry.nextAction}`.toLowerCase();
  if (filter === "calls") return normalized.includes("call");
  if (filter === "notes") return normalized.includes("note");
  if (filter === "communications") return normalized.includes("team message") || normalized.includes("team reply") || normalized.includes("message type") || normalized.includes("communication");
  if (filter === "promises") return normalized.includes("promise");
  if (filter === "ticklers") return normalized.includes("tickler") || normalized.includes("reminder");
  if (filter === "letters") return normalized.includes("letter");
  if (filter === "statements") return normalized.includes("statement");
  if (filter === "escalations") return normalized.includes("escalation") || normalized.includes("review");
  if (filter === "payments") return normalized.includes("payment") || normalized.includes("paid");
  return normalized.includes("follow-up") || normalized.includes("follow up");
}

function countTimelineEntries(entries: AccountTimelineEntry[], filter: AccountTimelineFilterKey) {
  return entries.filter((entry) => timelineEntryMatchesFilter(entry, filter)).length;
}

function timelineStatusBadgeClass(status: string, actionType: string) {
  const normalized = `${status} ${actionType}`.toLowerCase();
  if (normalized.includes("escalation") || normalized.includes("overdue") || normalized.includes("broken")) return "border-[rgba(248,113,113,.34)] bg-[rgba(248,113,113,.12)] text-[#FCA5A5]";
  if (normalized.includes("payment") || normalized.includes("completed") || normalized.includes("resolved")) return "border-[rgba(34,197,94,.32)] bg-[rgba(34,197,94,.12)] text-[#86EFAC]";
  if (normalized.includes("promise") || normalized.includes("follow")) return "border-[rgba(255,106,43,.34)] bg-[rgba(255,106,43,.12)] text-[#FF6A2B]";
  return "border-[rgba(34,224,208,.3)] bg-[rgba(34,224,208,.1)] text-[#22E0D0]";
}

function AccountOpenInvoicesPanel({
  account,
  promises,
}: {
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  promises: ReturnType<typeof loadARRecoveryWorkspace>["promises"];
}) {
  const invoices = useMemo(() => buildInvoicePlaceholders(account), [account]);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(invoices[0]?.id || "");
  const selectedInvoice = invoices.find((invoice) => invoice.id === selectedInvoiceId) || invoices[0] || null;
  const oldestInvoice = [...invoices].sort((a, b) => getDateTime(a.dueDate) - getDateTime(b.dueDate))[0];
  const largestInvoice = [...invoices].sort((a, b) => b.amountCents - a.amountCents)[0];
  const mostOverdueInvoice = oldestInvoice;
  const selectedPromise = selectedInvoice
    ? promises.find((promise) => promise.invoiceLabels.includes(selectedInvoice.invoiceNumber))
    : undefined;

  useEffect(() => {
    if (selectedInvoiceId && invoices.some((invoice) => invoice.id === selectedInvoiceId)) return;
    setSelectedInvoiceId(invoices[0]?.id || "");
  }, [invoices, selectedInvoiceId]);

  return (
    <div className="rounded-[1.5rem] border border-[rgba(255,106,43,.16)] bg-[rgba(255,255,255,.03)] p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF6A2B]">Open Invoices</p>
        <span className="rounded-full border border-[rgba(34,224,208,.2)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#22E0D0]">
          {invoices.length} open
        </span>
      </div>

      <div className="mt-3 grid gap-3 md:grid-cols-3">
        <DetailMetric label="Oldest invoice" value={oldestInvoice?.invoiceNumber || "Not Captured"} />
        <DetailMetric label="Largest balance" value={largestInvoice ? formatARRecoveryCurrency(largestInvoice.amountCents) : "Not Captured"} />
        <DetailMetric label="Most overdue" value={mostOverdueInvoice?.invoiceNumber || "Not Captured"} />
      </div>

      <div className="mt-3 grid gap-3 xl:grid-cols-[minmax(0,68fr)_minmax(13rem,32fr)]">
        <div className="h-[24rem] overflow-hidden rounded-xl border border-[rgba(34,224,208,.14)] bg-[rgba(7,18,31,.72)]">
          <div className="h-full overflow-auto">
            <table className="w-full min-w-[62rem] border-collapse text-left">
              <thead className="sticky top-0 z-10 bg-[#07121f] text-[10px] font-black uppercase tracking-[0.14em] text-[#22E0D0] shadow-[0_1px_0_rgba(34,224,208,.14)]">
                <tr>
                  <th className="px-3 py-2">Invoice Number</th>
                  <th className="px-3 py-2">Invoice Date</th>
                  <th className="px-3 py-2">Due Date</th>
                  <th className="px-3 py-2 text-right">Balance</th>
                  <th className="px-3 py-2">Aging</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Promise</th>
                  <th className="px-3 py-2">Collector</th>
                </tr>
              </thead>
              <tbody className="text-sm font-semibold text-[#E8EEF4]">
                {invoices.map((invoice, index) => {
                  const active = selectedInvoice?.id === invoice.id;
                  const invoicePromise = promises.find((promise) => promise.invoiceLabels.includes(invoice.invoiceNumber));
                  return (
                    <tr
                      key={invoice.id}
                      onClick={() => setSelectedInvoiceId(invoice.id)}
                      className={`cursor-pointer border-b border-[rgba(34,224,208,.08)] transition ${
                        active
                          ? "bg-[rgba(255,106,43,.14)] text-[#F6F1E8]"
                          : index % 2 === 0
                            ? "bg-[rgba(255,255,255,.025)] hover:bg-[rgba(34,224,208,.08)]"
                            : "bg-[rgba(7,18,31,.36)] hover:bg-[rgba(34,224,208,.08)]"
                      }`}
                    >
                      <td className="whitespace-nowrap px-3 py-2 font-black text-[#F6F1E8]">{invoice.invoiceNumber}</td>
                      <td className="px-3 py-2">{formatDate(invoice.invoiceDate)}</td>
                      <td className="px-3 py-2">{formatDate(invoice.dueDate)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatARRecoveryCurrency(invoice.amountCents)}</td>
                      <td className="px-3 py-2">{account.agingSummary}</td>
                      <td className="px-3 py-2">{invoice.status}</td>
                      <td className="px-3 py-2">{invoicePromise?.status || "Not Captured"}</td>
                      <td className="px-3 py-2">{account.assignedCollector || "Not Captured"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <aside className="h-[24rem] overflow-y-auto rounded-xl border border-[rgba(255,106,43,.12)] bg-[rgba(7,18,31,.72)] p-3">
          <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Invoice Detail</p>
            {selectedInvoice ? (
              <div className="mt-3 grid gap-2">
                <WorkspaceField label="Invoice Number" value={selectedInvoice.invoiceNumber} />
              <WorkspaceField label="Invoice Date" value={formatDate(selectedInvoice.invoiceDate)} />
              <WorkspaceField label="Balance" value={formatARRecoveryCurrency(selectedInvoice.amountCents)} />
              <WorkspaceField label="Original Amount" value={formatARRecoveryCurrency(selectedInvoice.originalAmountCents)} />
              <WorkspaceField label="Current Balance" value={formatARRecoveryCurrency(selectedInvoice.currentBalanceCents)} />
              <WorkspaceField label="Due Date" value={formatDate(selectedInvoice.dueDate)} />
              <WorkspaceField label="Purchase Order" value={selectedInvoice.purchaseOrder || "Not Captured"} />
              <WorkspaceField label="Reference Number" value={selectedInvoice.referenceNumber || "Not Captured"} />
              <WorkspaceField label="Aging" value={account.agingSummary} />
              <WorkspaceField label="Status" value={selectedInvoice.status} />
              <WorkspaceField label="Promise Information" value={selectedPromise ? `${selectedPromise.status} | ${formatARRecoveryCurrency(selectedPromise.amountCents)}` : "Not Captured"} />
              <WorkspaceField label="Customer" value={account.customerName || "Not Captured"} />
              <WorkspaceField label="Collector" value={account.assignedCollector || "Not Captured"} />
                <WorkspaceField label="Notes / Context" value={selectedInvoice.notes || "Not Captured"} />
              </div>
            ) : (
              <div className="mt-3">
                <ManagerEmptyState
                  title="No invoice selected"
                  description="Select an invoice row to review invoice detail."
                  action="Invoice context will appear here without leaving the account workspace."
                />
              </div>
            )}
        </aside>
      </div>
    </div>
  );
}

function AccountNotesPanel({
  account,
  activityEvents,
  promises,
}: {
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  activityEvents: ReturnType<typeof loadARRecoveryWorkspace>["recoveryActivityEvents"];
  promises: ReturnType<typeof loadARRecoveryWorkspace>["promises"];
}) {
  const noteRecords = useMemo(() => buildAccountNoteRecords(account, activityEvents, promises), [account, activityEvents, promises]);
  const [selectedFilter, setSelectedFilter] = useState<AccountNoteFilterKey>("all");
  const filteredNoteRecords = useMemo(
    () => noteRecords.filter((note) => noteMatchesFilter(note, selectedFilter)),
    [noteRecords, selectedFilter],
  );
  const [selectedNoteId, setSelectedNoteId] = useState(noteRecords[0]?.id || "");
  const selectedNote = filteredNoteRecords.find((note) => note.id === selectedNoteId) || filteredNoteRecords[0] || null;
  const totalNotes = noteRecords.length;
  const internalNotes = noteRecords.filter((note) => note.visibility === "internal" || note.visibility === "manager_only").length;
  const customerNotes = noteRecords.filter((note) => note.visibility === "client_visible" || note.noteType === "Customer Note").length;
  const escalationNotes = noteRecords.filter((note) => note.noteType.toLowerCase().includes("escalation")).length;
  const notesAddedToday = noteRecords.filter((note) => isToday(note.createdAt)).length;

  useEffect(() => {
    if (selectedNoteId && filteredNoteRecords.some((note) => note.id === selectedNoteId)) return;
    setSelectedNoteId(filteredNoteRecords[0]?.id || "");
  }, [filteredNoteRecords, selectedNoteId]);

  return (
    <div className="grid gap-3">
      <CompactMetricStrip
        items={[
          { label: "Total Notes", value: String(totalNotes) },
          { label: "Internal", value: String(internalNotes) },
          { label: "Customer", value: String(customerNotes) },
          { label: "Escalation", value: String(escalationNotes) },
          { label: "Today", value: String(notesAddedToday) },
        ]}
      />

      <div className="rounded-xl border border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.025)] p-2">
        <p className="mb-2 text-[10px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Note Filters</p>
        <div className="flex flex-wrap gap-2">
        {NOTE_FILTERS.map((filter) => {
          const active = selectedFilter === filter.key;
          return (
            <button
              key={filter.key}
              type="button"
              onClick={() => setSelectedFilter(filter.key)}
              className={`rounded-full border px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.14em] transition ${
                active
                  ? "border-[rgba(255,106,43,.46)] bg-[rgba(255,106,43,.14)] text-[#F6F1E8]"
                  : "border-[rgba(34,224,208,.2)] bg-[rgba(255,255,255,.04)] text-[#CBD5E1] hover:border-[rgba(34,224,208,.34)]"
              }`}
            >
              {filter.label} {countNotesForFilter(noteRecords, filter.key)}
            </button>
          );
        })}
        </div>
      </div>

      <div className="grid min-w-0 max-w-full gap-3 lg:grid-cols-[minmax(16rem,40fr)_minmax(0,60fr)]">
        <section className="h-[34rem] min-w-0 max-w-full overflow-hidden rounded-xl border border-[rgba(255,106,43,.12)] bg-[rgba(7,18,31,.72)]">
          <div className="flex items-center justify-between gap-3 border-b border-[rgba(255,106,43,.1)] px-3 py-2">
            <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Notes List</p>
            <span className="rounded-full border border-[rgba(34,224,208,.18)] px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#22E0D0]">
              {filteredNoteRecords.length} shown
            </span>
          </div>
          <div className="h-[calc(34rem-2.75rem)] min-w-0 max-w-full overflow-y-auto overflow-x-hidden p-2">
            {filteredNoteRecords.length ? (
              <div className="grid min-w-0 max-w-full gap-2">
                {filteredNoteRecords.map((note) => {
                  const active = selectedNote?.id === note.id;
                  return (
                    <button
                      key={note.id}
                      type="button"
                      onClick={() => setSelectedNoteId(note.id)}
                      className={`rounded-xl border p-2.5 text-left transition ${
                        active
                          ? "border-[rgba(255,106,43,.42)] bg-[rgba(255,106,43,.12)]"
                          : "border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.03)] hover:border-[rgba(34,224,208,.3)]"
                      } min-w-0 max-w-full overflow-hidden`}
                    >
                      <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0 max-w-full">
                          <p className="max-w-full break-words text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{note.noteType}</p>
                          <p className="mt-1 max-w-full break-words text-sm font-semibold leading-5 text-[#F6F1E8]">
                            {formatCapturedDate(note.createdAt)} | {formatCapturedTime(note.createdAt)} | {note.createdBy}
                          </p>
                        </div>
                        <div className="flex min-w-0 max-w-full flex-wrap items-start justify-end gap-1 sm:flex-col sm:items-end">
                          <span className={`rounded-full border px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${noteVisibilityBadgeClass(note.visibility)}`}>
                            {formatNoteVisibility(note.visibility)}
                          </span>
                          <span className={`rounded-full border px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${noteCategoryBadgeClass(note)}`}>
                            {noteCategoryLabel(note)}
                          </span>
                        </div>
                      </div>
                      <p className="mt-2 max-w-full overflow-hidden break-words text-sm font-semibold leading-5 text-[#CBD5E1] [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
                        {previewNoteContent(note.content)}
                      </p>
                    </button>
                  );
                })}
              </div>
            ) : (
              <ManagerEmptyState
                title="No notes match"
                description={`No notes match the selected filter for ${account.customerName}.`}
                action="Select another filter to separate internal, client-visible, escalation, promise, and system notes."
              />
            )}
          </div>
        </section>

        <section className="h-[34rem] min-w-0 max-w-full overflow-hidden rounded-xl border border-[rgba(255,106,43,.12)] bg-[rgba(7,18,31,.72)]">
          <div className="flex items-center justify-between gap-3 border-b border-[rgba(255,106,43,.1)] px-3 py-2">
            <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Selected Note Detail</p>
            {selectedNote ? (
              <span className={`rounded-full border px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${noteCategoryBadgeClass(selectedNote)}`}>
                {noteCategoryLabel(selectedNote)}
              </span>
            ) : null}
          </div>
          <div className="h-[calc(34rem-2.75rem)] min-w-0 max-w-full overflow-y-auto overflow-x-hidden p-3">
          {selectedNote ? (
            <div className="grid gap-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 max-w-full">
                  <p className="max-w-full break-words text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">{selectedNote.noteType}</p>
                  <p className="mt-1 max-w-full break-words text-sm font-semibold leading-5 text-[#F6F1E8]">{formatCapturedDate(selectedNote.createdAt)} | {formatCapturedTime(selectedNote.createdAt)} | {selectedNote.createdBy}</p>
                </div>
                <span className={`rounded-full border px-2.5 py-1 text-[11px] font-black uppercase tracking-[0.12em] ${noteVisibilityBadgeClass(selectedNote.visibility)}`}>
                  {formatNoteVisibility(selectedNote.visibility)}
                </span>
              </div>

              <div className="rounded-xl border border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.03)] p-3">
                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Full Note Content</p>
                <p className="mt-2 max-w-full break-words text-sm font-semibold leading-6 text-[#E8EEF4]">{selectedNote.content}</p>
              </div>

              <div className="grid gap-2 xl:grid-cols-2">
                <NoteField label="Outcome" value={selectedNote.outcome} />
                <NoteField label="Next Action" value={selectedNote.nextAction} />
              </div>

              <AuditStrip
                createdBy={selectedNote.createdBy}
                createdDate={formatCapturedDate(selectedNote.createdAt)}
                createdTime={formatCapturedTime(selectedNote.createdAt)}
                updatedBy={selectedNote.updatedBy}
                updatedDate={formatCapturedDate(selectedNote.updatedAt)}
                updatedTime={formatCapturedTime(selectedNote.updatedAt)}
                containText
              />
            </div>
          ) : (
            <ManagerEmptyState
              title="No note selected"
              description="Select a note to view its details."
              action="Only one note expands at a time in the detail panel."
            />
          )}
          </div>
        </section>
      </div>
    </div>
  );
}

function AccountLetterPanel({
  account,
  letters,
  allLetters,
  activityEvents,
  onCreateLetter,
  onScheduleFollowUp,
}: {
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  letters: ReturnType<typeof loadARRecoveryWorkspace>["collectionLetters"];
  allLetters: ReturnType<typeof loadARRecoveryWorkspace>["collectionLetters"];
  activityEvents: ReturnType<typeof loadARRecoveryWorkspace>["recoveryActivityEvents"];
  onCreateLetter: () => void;
  onScheduleFollowUp: () => void;
}) {
  const accountLetters = [...letters].sort((a, b) => getDateTime(b.updatedAt || b.createdAt) - getDateTime(a.updatedAt || a.createdAt));
  const [selectedFilter, setSelectedFilter] = useState<AccountLetterFilterKey>("all");
  const filteredLetters = useMemo(
    () => accountLetters.filter((letter) => letterMatchesFilter(letter, selectedFilter)),
    [accountLetters, selectedFilter],
  );
  const [selectedLetterId, setSelectedLetterId] = useState(accountLetters[0]?.id || "");
  const selectedLetter = filteredLetters.find((letter) => letter.id === selectedLetterId) || filteredLetters[0] || null;
  const selectedLetterHistoryEntries = selectedLetter
    ? buildAccountLetterHistoryEntries([selectedLetter], activityEvents.filter((event) => event.relatedRecordId === selectedLetter.id))
    : [];
  const availableTemplates = new Set(allLetters.map((letter) => letter.template)).size;
  const draftLetters = accountLetters.filter((letter) => letter.status === "Draft").length;
  const generatedLetters = accountLetters.length;
  const sentLetters = accountLetters.filter((letter) => letter.status === "Sent").length;
  const deliveredLetters = accountLetters.filter((letter) => letter.deliveryStatus === "Delivered" || letter.status === "Delivered").length;
  const viewedLetters = accountLetters.filter((letter) => letter.viewedStatus === "Viewed" || letter.status === "Viewed").length;
  const failedLetters = accountLetters.filter((letter) => letterMatchesFilter(letter, "failed")).length;
  const deliveryNotConfiguredLetters = accountLetters.filter((letter) => letter.status === "Delivery Not Configured").length;

  useEffect(() => {
    if (selectedLetterId && filteredLetters.some((letter) => letter.id === selectedLetterId)) return;
    setSelectedLetterId(filteredLetters[0]?.id || "");
  }, [filteredLetters, selectedLetterId]);

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[rgba(255,106,43,.12)] bg-[rgba(255,255,255,.025)] p-3">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Letter Production</p>
          <p className="mt-1 text-sm font-semibold text-[#CBD5E1]">Preview, generate, and track collection letters without leaving the account.</p>
        </div>
        <button
          type="button"
          onClick={onCreateLetter}
          className="focus-ring min-h-10 rounded-full border border-[rgba(255,106,43,.42)] bg-[rgba(255,106,43,.16)] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#F6F1E8]"
        >
          New Letter
        </button>
      </div>

      <CompactMetricStrip
        items={[
          { label: "Templates", value: String(availableTemplates) },
          { label: "Draft", value: String(draftLetters) },
          { label: "Generated", value: String(generatedLetters) },
          { label: "Sent", value: String(sentLetters) },
          { label: "Delivered", value: String(deliveredLetters) },
          { label: "Viewed", value: String(viewedLetters) },
          { label: "Failed", value: String(failedLetters) },
          { label: "Delivery Config", value: deliveryNotConfiguredLetters ? "Needed" : "Ready" },
        ]}
      />

      <div className="rounded-xl border border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.025)] p-2">
        <p className="mb-2 text-[10px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Letter Filters</p>
        <div className="flex flex-wrap gap-2">
          {LETTER_FILTERS.map((filter) => {
            const active = selectedFilter === filter.key;
            return (
              <button
                key={filter.key}
                type="button"
                onClick={() => setSelectedFilter(filter.key)}
                className={`rounded-full border px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.14em] transition ${
                  active
                    ? "border-[rgba(255,106,43,.46)] bg-[rgba(255,106,43,.14)] text-[#F6F1E8]"
                    : "border-[rgba(34,224,208,.2)] bg-[rgba(255,255,255,.04)] text-[#CBD5E1] hover:border-[rgba(34,224,208,.34)]"
                }`}
              >
                {filter.label} {countLettersForFilter(accountLetters, filter.key)}
              </button>
            );
          })}
        </div>
      </div>

      {accountLetters.length ? (
        <div className="grid gap-3 lg:grid-cols-[minmax(18rem,.9fr)_minmax(0,1.5fr)]">
          <section className="h-[34rem] overflow-hidden rounded-xl border border-[rgba(255,106,43,.12)] bg-[rgba(7,18,31,.72)]">
            <div className="flex items-center justify-between gap-3 border-b border-[rgba(255,106,43,.1)] px-3 py-2">
              <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Letter List</p>
              <span className="rounded-full border border-[rgba(34,224,208,.18)] px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#22E0D0]">
                {filteredLetters.length} shown
              </span>
            </div>
            <div className="h-[calc(34rem-2.75rem)] overflow-y-auto p-2">
              {filteredLetters.length ? (
                <div className="grid gap-2">
                  {filteredLetters.map((letter) => {
                    const active = selectedLetter?.id === letter.id;
                    return (
                      <button
                        key={letter.id}
                        type="button"
                        onClick={() => setSelectedLetterId(letter.id)}
                        className={`rounded-xl border p-2.5 text-left transition ${
                          active
                            ? "border-[rgba(255,106,43,.42)] bg-[rgba(255,106,43,.12)]"
                            : "border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.03)] hover:border-[rgba(34,224,208,.3)]"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="truncate text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{letter.template}</p>
                            <p className="mt-1 text-sm font-semibold leading-5 text-[#F6F1E8]">
                              {letter.dateSent || formatCapturedDate(letter.createdAt)} | {letter.sentBy || letter.collector || "Not Captured"}
                            </p>
                          </div>
                          <span className={`shrink-0 rounded-full border px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${letterStatusBadgeClass(letter.status, letter.deliveryStatus)}`}>
                            {letter.status}
                          </span>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2 text-[11px] font-black uppercase tracking-[0.12em] text-[#CBD5E1]">
                          <span>{letter.deliveryStatus}</span>
                          <span>|</span>
                          <span>{letter.viewedStatus}</span>
                          <span>|</span>
                          <span>{formatARRecoveryCurrency(letter.balanceCents)}</span>
                        </div>
                        <p className="mt-2 overflow-hidden text-sm font-semibold leading-5 text-[#CBD5E1] [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
                          {letter.notes || "Not Captured"}
                        </p>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <ManagerEmptyState
                  title="No letters match"
                  description={`No letters match the selected filter for ${account.customerName}.`}
                  action="Select another filter to review generated, draft, sent, delivered, viewed, or failed letters."
                />
              )}
            </div>
          </section>

          <section className="h-[34rem] overflow-hidden rounded-xl border border-[rgba(255,106,43,.12)] bg-[rgba(7,18,31,.72)]">
            <div className="flex items-center justify-between gap-3 border-b border-[rgba(255,106,43,.1)] px-3 py-2">
              <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Selected Letter Detail</p>
              {selectedLetter ? (
                <span className={`rounded-full border px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${letterStatusBadgeClass(selectedLetter.status, selectedLetter.deliveryStatus)}`}>
                  {selectedLetter.deliveryStatus}
                </span>
              ) : null}
            </div>
            <div className="h-[calc(34rem-2.75rem)] overflow-y-auto p-3">
              {selectedLetter ? (
                  <div className="grid gap-3">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={onScheduleFollowUp}
                      className="focus-ring rounded-full border border-[rgba(34,224,208,.24)] bg-[rgba(34,224,208,.08)] px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em] text-[#22E0D0]"
                    >
                      Schedule Follow-Up
                    </button>
                  </div>

                  <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                    <LetterField label="Letter Type" value={selectedLetter.template} />
                    <LetterField label="Template Used" value={selectedLetter.template} />
                    <LetterField label="Status" value={selectedLetter.status} />
                    <LetterField label="Delivery Method" value={selectedLetter.status === "Delivery Not Configured" ? "Email delivery not configured" : "Email"} />
                    <LetterField label="Delivery Status" value={selectedLetter.deliveryStatus} />
                    <LetterField label="Recipient" value={(selectedLetter as ARRecoveryLetterWorkflowRecord).sentRecipient || (selectedLetter as ARRecoveryLetterWorkflowRecord).recipient || "Not Captured"} />
                    <LetterField label="Provider" value={(selectedLetter as ARRecoveryLetterWorkflowRecord).deliveryProvider || "Not Captured"} />
                    <LetterField label="Provider Reference" value={(selectedLetter as ARRecoveryLetterWorkflowRecord).providerMessageId || "Not Captured"} />
                    <LetterField label="Viewed Status" value={selectedLetter.viewedStatus} />
                    <LetterField label="Downloaded Status" value={selectedLetter.downloadedStatus} />
                    <LetterField label="Collector" value={selectedLetter.collector || "Not Captured"} />
                    <LetterField label="Customer" value={selectedLetter.customerName || account.customerName || "Not Captured"} />
                  </div>

                  <div className="grid gap-2 xl:grid-cols-2">
                    <LetterField label="Outcome" value={selectedLetter.status === "Delivery Not Configured" ? "Email delivery is not configured yet. Letter generated but no email was sent." : selectedLetter.status === "Failed" ? (selectedLetter as ARRecoveryLetterWorkflowRecord).deliveryError || selectedLetter.notes || "Email delivery failed." : selectedLetter.notes || selectedLetter.deliveryStatus || "Not Captured"} />
                    <LetterField label="Next Action" value={buildLetterNextAction(selectedLetter)} />
                  </div>

                  <AuditStrip
                    createdBy={selectedLetter.sentBy || selectedLetter.collector || "Not Captured"}
                    createdDate={formatCapturedDate(selectedLetter.createdAt)}
                    createdTime={formatCapturedTime(selectedLetter.createdAt)}
                    updatedBy="Not Captured"
                    updatedDate={formatCapturedDate(selectedLetter.updatedAt)}
                    updatedTime={formatCapturedTime(selectedLetter.updatedAt)}
                  />

                  <div className="grid gap-2">
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-[#22E0D0]">Selected Letter History</p>
                    {selectedLetterHistoryEntries.length ? (
                      <OperationalHistoryRows entries={selectedLetterHistoryEntries} />
                    ) : (
                      <ManagerEmptyState
                        title="No letter history"
                        description="No history is available for this selected letter."
                        action="Letter lifecycle events will appear here when generated, sent, delivered, viewed, or updated."
                      />
                    )}
                  </div>
                </div>
              ) : (
                <ManagerEmptyState
                  title="No letter selected"
                  description="Select a letter to view its details."
                  action="Template, delivery, outcome, next action, and audit information will appear here."
                />
              )}
            </div>
          </section>
        </div>
      ) : (
        <ManagerEmptyState
          title="No letters available"
          description={`No collection letters are available for ${account.customerName}.`}
          action="Letter records will appear here when they exist for this account."
        />
      )}
    </div>
  );
}

type AccountLetterRecord = ReturnType<typeof loadARRecoveryWorkspace>["collectionLetters"][number];

function ARRecoveryLetterExecutionHistoryPanel({
  account,
  letters,
  activityEvents,
  onClose,
}: {
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  letters: ReturnType<typeof loadARRecoveryWorkspace>["collectionLetters"];
  activityEvents: ReturnType<typeof loadARRecoveryWorkspace>["recoveryActivityEvents"];
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLElement | null>(null);
  const sortedLetters = [...letters].sort((a, b) => getDateTime(b.updatedAt || b.createdAt) - getDateTime(a.updatedAt || a.createdAt));
  const historyEntries = buildAccountLetterHistoryEntries(sortedLetters, activityEvents);

  useEffect(() => {
    window.setTimeout(() => {
      panelRef.current?.focus({ preventScroll: true });
    }, 0);
  }, []);

  return (
    <div className="pointer-events-none fixed inset-0 z-[84] flex justify-end" role="presentation" data-recovery-letter-history-panel="true">
      <aside
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="false"
        aria-labelledby="ar-recovery-letter-history-title"
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.stopPropagation();
            onClose();
          }
        }}
        className="pointer-events-auto flex h-dvh w-full max-w-full flex-col overflow-hidden border-l border-[#BFEAFF] bg-white shadow-[0_24px_90px_rgba(15,23,42,.24)] outline-none lg:w-[30vw] lg:min-w-[24rem] lg:max-w-[34rem]"
      >
        <header className="shrink-0 border-b border-[#BFEAFF] bg-white px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Recovery Workbench</p>
              <h3 id="ar-recovery-letter-history-title" className="mt-1 break-words text-xl font-semibold tracking-tight text-[#0A2540]">
                Letter History
              </h3>
              <p className="mt-1 text-sm font-semibold leading-6 text-[#475569]">
                {account.customerName} | Account {account.customerNumber}
              </p>
            </div>
            <button type="button" onClick={onClose} className="focus-ring shrink-0 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2 text-sm font-semibold text-[#0A2540]">
              Close
            </button>
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto bg-[#F8FCFF] p-4">
          {sortedLetters.length ? (
            <div className="grid gap-4">
              {sortedLetters.map((letter) => {
                const workflowLetter = letter as AccountLetterRecord & Partial<ARRecoveryLetterWorkflowRecord>;
                const relatedInvoices = Array.isArray(workflowLetter.relatedInvoices) && workflowLetter.relatedInvoices.length
                  ? workflowLetter.relatedInvoices.join(", ")
                  : "Not Captured";
                const attachmentLabel = getLetterSpreadsheetAttachmentLabel(letter);
                return (
                  <article key={letter.id} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4 shadow-[0_10px_24px_rgba(15,23,42,.04)]">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">{workflowLetter.letterType || letter.template}</p>
                        <h4 className="mt-1 break-words text-base font-semibold text-[#0A2540]">{letter.template}</h4>
                      </div>
                      <span className={`shrink-0 rounded-full border px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${letterStatusBadgeClass(letter.status, letter.deliveryStatus)}`}>
                        {letter.deliveryStatus || letter.status}
                      </span>
                    </div>

                    <div className="mt-4 grid gap-2">
                      <WorkspaceField label="Created Date" value={`${formatCapturedDate(letter.createdAt)} | ${formatCapturedTime(letter.createdAt)}`} />
                      <WorkspaceField label="Sent Date" value={letter.sentAt ? `${formatCapturedDate(letter.sentAt)} | ${letter.timeSent || formatCapturedTime(letter.sentAt)}` : "Not Sent"} />
                      <WorkspaceField label="Recipient" value={workflowLetter.sentRecipient || workflowLetter.recipient || "Not Captured"} />
                      <WorkspaceField label="Delivery Method" value={letter.status === "Delivery Not Configured" ? "Email delivery not configured" : workflowLetter.deliveryProvider || "Email"} />
                      <WorkspaceField label="Delivery Status" value={letter.deliveryStatus || letter.status} />
                      <WorkspaceField label="Sent By" value={letter.sentBy || workflowLetter.generatedBy || letter.collector || "Not Captured"} />
                      <WorkspaceField label="Related Invoices" value={relatedInvoices} />
                      <WorkspaceField label="Related Promise" value={workflowLetter.relatedPromise || "Not Captured"} />
                      <WorkspaceField label="Tracking Result" value={`Viewed: ${letter.viewedStatus || "Not Captured"} | Downloaded: ${letter.downloadedStatus || "Not Captured"}`} />
                      <WorkspaceField label="Failure Reason" value={workflowLetter.deliveryError || (letter.status === "Failed" ? letter.notes : "None Recorded")} />
                      <WorkspaceField label="Document / PDF Reference" value={workflowLetter.providerMessageId || letter.id} />
                      <WorkspaceField label="Spreadsheet Attachment" value={attachmentLabel} />
                    </div>
                  </article>
                );
              })}

              {historyEntries.length ? (
                <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Lifecycle Events</p>
                  <div className="mt-3">
                    <OperationalHistoryRows entries={historyEntries} />
                  </div>
                </section>
              ) : null}
            </div>
          ) : (
            <div role="status" aria-live="polite">
              <ManagerEmptyState
                title="No letter history"
                description="No letters have been generated or sent for this account."
                action="Letter records will appear here when generated, sent, delivered, viewed, or updated."
              />
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

function ARRecoveryStatementExecutionHistoryPanel({
  account,
  statements,
  activityEvents,
  onClose,
}: {
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  statements: ReturnType<typeof loadARRecoveryWorkspace>["statements"];
  activityEvents: ReturnType<typeof loadARRecoveryWorkspace>["recoveryActivityEvents"];
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLElement | null>(null);
  const sortedStatements = [...statements].sort((a, b) => getDateTime(b.updatedAt || b.createdAt) - getDateTime(a.updatedAt || a.createdAt));
  const historyEntries = buildAccountStatementHistoryEntries(sortedStatements, activityEvents);

  useEffect(() => {
    window.setTimeout(() => {
      panelRef.current?.focus({ preventScroll: true });
    }, 0);
  }, []);

  return (
    <div className="pointer-events-none fixed inset-0 z-[84] flex justify-end" role="presentation" data-recovery-statement-history-panel="true">
      <aside
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="false"
        aria-labelledby="ar-recovery-statement-history-title"
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.stopPropagation();
            onClose();
          }
        }}
        className="pointer-events-auto flex h-dvh w-full max-w-full flex-col overflow-hidden border-l border-[#BFEAFF] bg-white shadow-[0_24px_90px_rgba(15,23,42,.24)] outline-none lg:w-[30vw] lg:min-w-[24rem] lg:max-w-[34rem]"
      >
        <header className="shrink-0 border-b border-[#BFEAFF] bg-white px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Recovery Workbench</p>
              <h3 id="ar-recovery-statement-history-title" className="mt-1 break-words text-xl font-semibold tracking-tight text-[#0A2540]">
                Statement History
              </h3>
              <p className="mt-1 text-sm font-semibold leading-6 text-[#475569]">
                {account.customerName} | Account {account.customerNumber}
              </p>
            </div>
            <button type="button" onClick={onClose} className="focus-ring shrink-0 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2 text-sm font-semibold text-[#0A2540]">
              Close
            </button>
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto bg-[#F8FCFF] p-4">
          {sortedStatements.length ? (
            <div className="grid gap-4">
              {sortedStatements.map((statement) => {
                const workflowStatement = statement as ReturnType<typeof loadARRecoveryWorkspace>["statements"][number] & Partial<ARRecoveryStatementWorkflowRecord>;
                const relatedInvoices = Array.isArray(workflowStatement.relatedInvoices) && workflowStatement.relatedInvoices.length
                  ? workflowStatement.relatedInvoices.join(", ")
                  : statement.lineItems.map((item) => item.invoiceNumber).join(", ") || "Not Captured";
                return (
                  <article key={statement.id} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4 shadow-[0_10px_24px_rgba(15,23,42,.04)]">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">{workflowStatement.statementType || "Account Statement"}</p>
                        <h4 className="mt-1 break-words text-base font-semibold text-[#0A2540]">{statement.statementNumber}</h4>
                      </div>
                      <span className={`shrink-0 rounded-full border px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${statementStatusBadgeClass(statement.status, statement.deliveryStatus)}`}>
                        {statement.deliveryStatus || statement.status}
                      </span>
                    </div>

                    <div className="mt-4 grid gap-2">
                      <WorkspaceField label="Created Date" value={`${formatCapturedDate(statement.createdAt)} | ${formatCapturedTime(statement.createdAt)}`} />
                      <WorkspaceField label="Statement Date" value={formatCapturedDate(statement.dateSent || statement.createdAt)} />
                      <WorkspaceField label="Recipient" value={workflowStatement.sentRecipient || account.contactEmail || "Not Captured"} />
                      <WorkspaceField label="Delivery Method" value={workflowStatement.deliveryOption || "Email"} />
                      <WorkspaceField label="Delivery Status" value={statement.deliveryStatus || statement.status} />
                      <WorkspaceField label="Generated By" value={workflowStatement.generatedBy || statement.sentBy || statement.collector || "Not Captured"} />
                      <WorkspaceField label="Related Invoices" value={relatedInvoices} />
                      <WorkspaceField label="Related Promise" value={workflowStatement.relatedPromise || "Not Captured"} />
                      <WorkspaceField label="Tracking Result" value={`Opened: ${statement.openedStatus || "Not Captured"} | Downloaded: ${statement.downloadedStatus || "Not Captured"}`} />
                      <WorkspaceField label="Failure Reason" value={workflowStatement.deliveryError || (statement.status === "Failed" ? statement.notes : "None Recorded")} />
                      <WorkspaceField label="Document / PDF Reference" value={workflowStatement.providerMessageId || statement.id} />
                    </div>
                  </article>
                );
              })}

              {historyEntries.length ? (
                <section className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Lifecycle Events</p>
                  <div className="mt-3">
                    <OperationalHistoryRows entries={historyEntries} />
                  </div>
                </section>
              ) : null}
            </div>
          ) : (
            <div role="status" aria-live="polite">
              <ManagerEmptyState
                title="No statement history"
                description="No statements have been generated or sent for this account."
                action="Statement records will appear here when generated, sent, delivered, opened, downloaded, or updated."
              />
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

function getLetterSpreadsheetAttachmentLabel(letter: AccountLetterRecord) {
  const notes = letter.notes || "";
  const attachmentMatch = notes.match(/Invoice attachment(?: prepared)?:\s*([^.(]+?\.csv)(?:\s*\((\d+)\s+invoices?\))?/i);
  if (!attachmentMatch) return "No spreadsheet attachment recorded.";
  const filename = attachmentMatch[1].trim();
  const invoiceCount = attachmentMatch[2]?.trim();
  return invoiceCount ? `${filename} (${invoiceCount} invoices)` : filename;
}

type AccountLetterFilterKey = "all" | "generated" | "draft" | "sent" | "delivered" | "viewed" | "failed";

const AR_RECOVERY_LETTER_INLINE_INVOICE_LIMIT = 10;

type LetterInvoiceDetail = {
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  originalAmount: string;
  openBalance: string;
  aging: string;
  status: string;
};

type LetterInvoiceAttachment = {
  filename: string;
  contentType: string;
  contentBase64: string;
  dataUri: string;
  invoiceCount: number;
  openBalanceTotal: string;
  status: "ready" | "failed";
  attached: boolean;
  error?: string;
};

type LetterFooterSignatureBlock = {
  salutation: string;
  companyName: string;
  businessDivision: string;
  department: string;
};

type LetterFooterContactItem = {
  label: "Email" | "Phone" | "Website";
  value: string;
};

type LetterBrandingBlock = {
  logoSrc: string;
  logoAlt: string;
  companyName: string;
  addressLines: string[];
};

type LetterPreview = {
  branding: LetterBrandingBlock;
  documentTitle: string;
  date: string;
  customer: string;
  address: string;
  recipientContactName: string;
  recipientEmail: string;
  account: string;
  balance: string;
  agingStatus: string;
  subject: string;
  greeting: string;
  openingParagraph: string;
  reasonParagraphs: string[];
  accountSummary: Array<{ label: string; value: string }>;
  selectedInvoices: LetterInvoiceDetail[];
  inlineInvoices: LetterInvoiceDetail[];
  inlineInvoiceLimit: number;
  selectedInvoiceTotal: string;
  requiresSpreadsheetAttachment: boolean;
  invoiceSummary: {
    invoiceCount: number;
    openBalanceTotal: string;
    oldestDueDate: string;
    highestAgingBucket: string;
    accountNumber: string;
  };
  invoiceAttachment: LetterInvoiceAttachment | null;
  selectedPromise: { heading: string; amount: string; promiseDate: string; status: string; body: string } | null;
  requestedAction: string;
  requestedActionParagraphs: string[];
  closing: string;
  contactLine: string;
  signatureBlock: LetterFooterSignatureBlock;
  signatureLines: string[];
  footerContactItems: LetterFooterContactItem[];
  paymentInstructions: string[];
  complianceNotices: string[];
  mergeFields: Array<{ label: string; value: string }>;
  body: string;
  signature: string;
};

const LETTER_TYPE_OPTIONS: ARRecoveryLetterType[] = [
  "Courtesy Reminder",
  "Past Due Reminder",
  "Demand Letter",
  "Final Demand",
  "Promise Confirmation",
  "Settlement Offer",
  "Broken Promise",
  "Collection Follow-Up",
  "Custom",
];

function mapLetterTypeToTemplate(letterType: ARRecoveryLetterType | ""): AccountLetterRecord["template"] {
  if (letterType === "Courtesy Reminder") return "Friendly Reminder";
  if (letterType === "Past Due Reminder") return "Past Due Notice";
  if (letterType === "Demand Letter") return "Demand Letter";
  if (letterType === "Final Demand") return "Final Notice";
  if (letterType === "Promise Confirmation") return "Promise Reminder";
  if (letterType === "Settlement Offer") return "Settlement Offer";
  if (letterType === "Broken Promise") return "Broken Promise Letter";
  if (letterType === "Collection Follow-Up") return "Account Review Notice";
  return "Custom Letter Builder";
}

function getARRecoveryLetterTemplateContent(letterType: ARRecoveryLetterType | "") {
  if (letterType === "Courtesy Reminder") {
    return {
      documentTitle: "Courtesy Reminder",
      subject: "Courtesy reminder regarding outstanding invoices",
      opening: "We are writing as a courtesy regarding the open invoices listed below. Our records show that these invoices remain outstanding on your account, and we would appreciate your review.",
      requestedAction: "Please remit payment for the listed invoices at your earliest convenience, or contact our office with payment details, questions, or documentation requests.",
    };
  }
  if (letterType === "Past Due Reminder") {
    return {
      documentTitle: "Past Due Reminder",
      subject: "Past due invoices requiring attention",
      opening: "Our records indicate that the invoices listed below remain unpaid beyond agreed terms. We are requesting your prompt attention so the account can be brought current or any account concern can be reviewed.",
      requestedAction: "Please arrange payment for the listed invoices or contact our office to confirm payment timing, dispute details, or any documentation needed for processing.",
    };
  }
  if (letterType === "Demand Letter") {
    return {
      documentTitle: "Payment Request",
      subject: "Formal request for payment on outstanding invoices",
      opening: "This notice is a formal request for attention to the invoices listed below. The balance remains open in our records, and we ask that your team review the account and respond with payment status or any supporting information needed for resolution.",
      requestedAction: "Please contact our office with remittance details or provide a written update regarding the listed invoices.",
    };
  }
  if (letterType === "Final Demand") {
    return {
      documentTitle: "Final Account Notice",
      subject: "Final notice regarding unresolved outstanding invoices",
      opening: "This account remains unresolved after previous communication. Please review the listed invoices and contact our office promptly so the balance can be addressed or any account discrepancy can be reviewed before further action is considered.",
      requestedAction: "Please provide payment status, remittance confirmation, or documentation regarding the listed invoices as soon as possible.",
    };
  }
  if (letterType === "Promise Confirmation") {
    return {
      documentTitle: "Promise Follow-up",
      subject: "Follow-up regarding payment commitment",
      opening: "We are following up regarding the payment commitment associated with this account. Please review the account and invoice information below and contact us if the commitment details need to be confirmed or updated.",
      requestedAction: "Please fulfill the payment commitment or contact our office if payment timing, documentation, or account details need review.",
    };
  }
  if (letterType === "Settlement Offer") {
    return {
      documentTitle: "Settlement Communication",
      subject: "Account resolution discussion",
      opening: "We are contacting you regarding the open account balance and available resolution options. Please review the listed invoices and contact our office to discuss a practical path toward resolving the account.",
      requestedAction: "Please contact our office to discuss account resolution options or provide payment details for the listed invoices.",
    };
  }
  if (letterType === "Broken Promise") {
    return {
      documentTitle: "Broken Promise Follow-up",
      subject: "Follow-up regarding missed payment commitment",
      opening: "We are contacting you because a payment commitment associated with this account requires attention. Please review the details below and contact our office with payment status, updated timing, or any information needed to resolve the matter.",
      requestedAction: "Please provide payment confirmation or contact our office with an updated payment plan for the listed invoices.",
    };
  }
  if (letterType === "Collection Follow-Up") {
    return {
      documentTitle: "Collection Follow-up",
      subject: "Follow-up on unresolved account balance",
      opening: "We are following up on the open account balance shown below. Please review the listed invoices and contact our office with payment status, documentation needs, or any account questions.",
      requestedAction: "Please respond with payment status or contact our office to resolve the listed invoices.",
    };
  }
  return {
    documentTitle: "Account Notice",
    subject: "Outstanding account balance requiring attention",
    opening: "We are contacting you regarding open invoices on your account. Please review the information below and contact our office if payment has already been submitted or if additional documentation is needed.",
    requestedAction: "Please remit payment for the listed invoices or contact our office with questions, dispute information, or payment details.",
  };
}

function buildARRecoveryPromiseLetterSection(promise: { amountCents: number; promiseDate: string; status: string }) {
  const status = promise.status.toLowerCase();
  const amount = formatARRecoveryCurrency(promise.amountCents);
  const promiseDate = formatCapturedDate(promise.promiseDate);
  if (status.includes("broken") || status.includes("missed") || status.includes("failed")) {
    return {
      heading: "Payment Commitment Requires Attention",
      amount,
      promiseDate,
      status: promise.status,
      body: `Our records show a payment commitment of ${amount} due on ${promiseDate}. That commitment now requires attention because the expected fulfillment has not been recorded. Please contact our office with payment confirmation or updated timing.`,
    };
  }
  if (status.includes("upcoming") || status.includes("pending") || status.includes("active") || status.includes("open")) {
    return {
      heading: "Payment Commitment on File",
      amount,
      promiseDate,
      status: promise.status,
      body: `Our records show a payment commitment of ${amount} due on ${promiseDate}. Please ensure the payment is submitted as agreed or contact our office if any details have changed.`,
    };
  }
  return {
    heading: "Related Payment Commitment",
    amount,
    promiseDate,
    status: promise.status,
    body: `A payment commitment of ${amount} dated ${promiseDate} is associated with this account. Please contact our office if the status or timing requires review.`,
  };
}

function sanitizeARRecoveryAttachmentFilename(value: string) {
  return value.replace(/[<>:"/\\|?*\x00-\x1F]/g, "_").replace(/\s+/g, "_").replace(/_+/g, "_").replace(/^_+|_+$/g, "").slice(0, 120) || "Invoice_Detail";
}

function escapeCsvCell(value: string | number) {
  const text = String(value ?? "");
  return /[",\n\r]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

function encodeTextToBase64(value: string) {
  if (typeof btoa === "function" && typeof TextEncoder !== "undefined") {
    const bytes = new TextEncoder().encode(value);
    let binary = "";
    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });
    return btoa(binary);
  }
  return "";
}

function buildARRecoveryInvoiceSpreadsheetAttachment({
  account,
  invoices,
}: {
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  invoices: ReturnType<typeof buildInvoicePlaceholders>;
}): LetterInvoiceAttachment {
  try {
    const letterDate = new Date().toISOString().slice(0, 10);
    const selectedTotalCents = invoices.reduce((total, invoice) => total + invoice.currentBalanceCents, 0);
    const headers = [
      "Customer Name",
      "Account Number",
      "Letter Date",
      "Collector",
      "Invoice Number",
      "Invoice Date",
      "Due Date",
      "Original Amount",
      "Open Balance",
      "Aging",
      "Status",
      "Selected Invoice Total",
    ];
    const rows = invoices.map((invoice) => [
      account.customerName,
      account.customerNumber,
      formatCapturedDate(letterDate),
      account.assignedCollector,
      invoice.invoiceNumber,
      formatCapturedDate(invoice.invoiceDate),
      formatCapturedDate(invoice.dueDate),
      formatARRecoveryCurrency(invoice.originalAmountCents),
      formatARRecoveryCurrency(invoice.currentBalanceCents),
      account.agingSummary,
      invoice.status,
      formatARRecoveryCurrency(selectedTotalCents),
    ]);
    const csv = [headers, ...rows].map((row) => row.map(escapeCsvCell).join(",")).join("\r\n");
    const contentBase64 = encodeTextToBase64(csv);
    if (!contentBase64) {
      return {
        filename: "Invoice_Detail.csv",
        contentType: "text/csv",
        contentBase64: "",
        dataUri: "",
        invoiceCount: invoices.length,
        openBalanceTotal: formatARRecoveryCurrency(selectedTotalCents),
        status: "failed",
        attached: false,
        error: "Invoice spreadsheet could not be encoded in this browser.",
      };
    }
    const filename = `${sanitizeARRecoveryAttachmentFilename(account.customerName)}_${sanitizeARRecoveryAttachmentFilename(account.customerNumber)}_Invoice_Detail_${letterDate}.csv`;
    return {
      filename,
      contentType: "text/csv",
      contentBase64,
      dataUri: `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`,
      invoiceCount: invoices.length,
      openBalanceTotal: formatARRecoveryCurrency(selectedTotalCents),
      status: "ready",
      attached: true,
    };
  } catch (error) {
    return {
      filename: "Invoice_Detail.csv",
      contentType: "text/csv",
      contentBase64: "",
      dataUri: "",
      invoiceCount: invoices.length,
      openBalanceTotal: formatARRecoveryCurrency(invoices.reduce((total, invoice) => total + invoice.currentBalanceCents, 0)),
      status: "failed",
      attached: false,
      error: error instanceof Error ? error.message : "Invoice spreadsheet generation failed.",
    };
  }
}

function buildLetterPreviewFromForm({
  form,
  account,
  clientProfile,
  selectedPromises = [],
}: {
  form: ARRecoveryLetterFormState;
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  clientProfile: ReturnType<typeof loadARRecoveryWorkspace>["clientProfile"];
  selectedPromises?: Array<{ id: string; amountCents: number; promiseDate: string; status: string }>;
}): LetterPreview {
  const template = getARRecoveryLetterTemplateContent(form.letterType);
  const recipient = form.recipient.trim() || `${account.contactName} <${account.contactEmail}>`;
  const parsedRecipient = parseARRecoveryRecipient(recipient);
  const greeting = buildARRecoveryLetterGreeting(parsedRecipient.name || account.contactName);
  const invoiceLookup = new Set(form.relatedInvoices);
  const selectedInvoiceRecords = buildInvoicePlaceholders(account).filter((invoice) => invoiceLookup.has(invoice.invoiceNumber));
  const selectedInvoiceTotalCents = selectedInvoiceRecords.reduce((total, invoice) => total + invoice.amountCents, 0);
  const requiresSpreadsheetAttachment = selectedInvoiceRecords.length > AR_RECOVERY_LETTER_INLINE_INVOICE_LIMIT;
  const oldestDueDateMs = selectedInvoiceRecords.reduce((oldest, invoice) => Math.min(oldest, new Date(invoice.dueDate).getTime()), Number.POSITIVE_INFINITY);
  const selectedInvoiceDetails: LetterInvoiceDetail[] = selectedInvoiceRecords.map((invoice) => ({
    invoiceNumber: invoice.invoiceNumber,
    invoiceDate: formatCapturedDate(invoice.invoiceDate),
    dueDate: formatCapturedDate(invoice.dueDate),
    originalAmount: formatARRecoveryCurrency(invoice.originalAmountCents),
    openBalance: formatARRecoveryCurrency(invoice.currentBalanceCents),
    aging: account.agingSummary,
    status: invoice.status,
  }));
  const invoiceAttachment = requiresSpreadsheetAttachment
    ? buildARRecoveryInvoiceSpreadsheetAttachment({ account, invoices: selectedInvoiceRecords })
    : null;
  const selectedPromiseRecord = selectedPromises.find((promise) => promise.id === form.relatedPromise) || null;
  const selectedPromise = selectedPromiseRecord ? buildARRecoveryPromiseLetterSection(selectedPromiseRecord) : null;
  const contactLine = `${clientProfile.letterDefaults.phone} | ${clientProfile.letterDefaults.email}${clientProfile.letterDefaults.website ? ` | ${clientProfile.letterDefaults.website}` : ""}`;
  const branding: LetterBrandingBlock = {
    logoSrc: AR_RECOVERY_LETTER_BRANDING_LOGO_SRC,
    logoAlt: "CollabRecover AR Solutions",
    companyName: clientProfile.letterDefaults.businessName || clientProfile.businessName,
    addressLines: buildARRecoveryLetterAddressLines(clientProfile.letterDefaults.address || clientProfile.addressLine1 || ""),
  };
  const signatureBlock: LetterFooterSignatureBlock = {
    salutation: "Sincerely,",
    companyName: clientProfile.letterDefaults.businessName || clientProfile.businessName,
    businessDivision: clientProfile.dbaName || clientProfile.businessName || clientProfile.letterDefaults.businessName,
    department: "Accounts Receivable Department",
  };
  const signatureDisplayLines = dedupeARRecoverySignatureLines([
    signatureBlock.companyName,
    signatureBlock.businessDivision,
    signatureBlock.department,
  ]);
  const footerContactItems: LetterFooterContactItem[] = [
    { label: "Email" as const, value: clientProfile.letterDefaults.email },
    { label: "Phone" as const, value: clientProfile.letterDefaults.phone },
    { label: "Website" as const, value: clientProfile.letterDefaults.website },
  ].filter(
    (item, index, items): item is LetterFooterContactItem =>
      Boolean(item.value) && items.findIndex((candidate) => candidate.value === item.value) === index,
  );
  const footerLines = [
    signatureBlock.salutation,
    "",
    ...signatureDisplayLines,
    "",
    ...footerContactItems.map((item) => `${item.label}: ${item.value}`),
  ];
  const reasonParagraphs = [
    template.opening,
    `This communication concerns the outstanding invoices listed below for ${account.customerName}. The selected invoices reflect an open balance of ${formatARRecoveryCurrency(selectedInvoiceTotalCents)} and require attention.`,
    "If payment has already been sent, it may still be in transit or pending application. Please contact our office with remittance details, questions, or any documentation request so the account can be updated accurately.",
  ];
  const bodyLines = [greeting, ...reasonParagraphs];
  if (requiresSpreadsheetAttachment) {
    bodyLines.push("For readability, a detailed listing of the outstanding invoices referenced in this communication has been included as an attachment.");
  }
  if (selectedPromise) {
    bodyLines.push(selectedPromise.body);
  }
  const requestedActionParagraphs = [
    template.requestedAction,
    "If payment has already been submitted, please advise us of the payment date, amount, and remittance reference so we can update the account.",
  ];
  bodyLines.push(...requestedActionParagraphs);
  const body = bodyLines.join("\n\n");

  return {
    branding,
    documentTitle: template.documentTitle,
    date: formatCapturedDate(new Date().toISOString()),
    customer: account.customerName,
    address: recipient,
    recipientContactName: parsedRecipient.name || account.contactName || account.customerName,
    recipientEmail: parsedRecipient.email || account.contactEmail,
    account: account.customerNumber,
    balance: formatARRecoveryCurrency(account.openBalanceCents),
    agingStatus: account.agingSummary,
    subject: template.subject,
    greeting,
    openingParagraph: template.opening,
    reasonParagraphs,
    accountSummary: [
      { label: "Customer", value: account.customerName },
      { label: "Account Number", value: account.customerNumber },
      { label: "Current Balance", value: formatARRecoveryCurrency(account.openBalanceCents) },
      { label: "Selected Invoice Count", value: String(selectedInvoiceRecords.length) },
      { label: "Selected Balance", value: formatARRecoveryCurrency(selectedInvoiceTotalCents) },
      { label: "Account Aging", value: account.agingSummary },
    ],
    selectedInvoices: selectedInvoiceDetails,
    inlineInvoices: requiresSpreadsheetAttachment ? [] : selectedInvoiceDetails,
    inlineInvoiceLimit: AR_RECOVERY_LETTER_INLINE_INVOICE_LIMIT,
    selectedInvoiceTotal: formatARRecoveryCurrency(selectedInvoiceTotalCents),
    requiresSpreadsheetAttachment,
    invoiceSummary: {
      invoiceCount: selectedInvoiceRecords.length,
      openBalanceTotal: formatARRecoveryCurrency(selectedInvoiceTotalCents),
      oldestDueDate: Number.isFinite(oldestDueDateMs) ? formatCapturedDate(new Date(oldestDueDateMs).toISOString()) : "Not Captured",
      highestAgingBucket: account.agingSummary,
      accountNumber: account.customerNumber,
    },
    invoiceAttachment,
    selectedPromise,
    requestedAction: template.requestedAction,
    requestedActionParagraphs,
    closing: "Thank you for your prompt attention. We appreciate your cooperation and look forward to resolving this account.",
    contactLine,
    signatureBlock,
    signatureLines: signatureDisplayLines,
    footerContactItems,
    paymentInstructions: [],
    complianceNotices: [],
    mergeFields: [
      { label: "Business", value: clientProfile.letterDefaults.businessName },
      { label: "DBA", value: clientProfile.dbaName },
      { label: "Contact", value: clientProfile.letterDefaults.contactName },
      { label: "Phone", value: clientProfile.letterDefaults.phone },
      { label: "Email", value: clientProfile.letterDefaults.email },
      { label: "Selected Invoice Count", value: String(selectedInvoiceRecords.length) },
      { label: "Selected Invoice Balance", value: formatARRecoveryCurrency(selectedInvoiceTotalCents) },
      { label: "Related Invoices", value: selectedInvoiceRecords.map((invoice) => invoice.invoiceNumber).join(", ") || "Not Captured" },
      { label: "Promise Status", value: selectedPromiseRecord?.status || "Not Captured" },
      { label: "Promise Amount", value: selectedPromiseRecord ? formatARRecoveryCurrency(selectedPromiseRecord.amountCents) : "Not Captured" },
      { label: "Promise Date", value: selectedPromiseRecord ? formatCapturedDate(selectedPromiseRecord.promiseDate) : "Not Captured" },
    ],
    body,
    signature: footerLines.join("\n"),
  };
}

function buildLetterWorkflowRecordFromForm({
  form,
  account,
  companyId,
  clientProfile,
  selectedPromises = [],
  now,
  actorName,
}: {
  form: ARRecoveryLetterFormState;
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  companyId: string;
  clientProfile: ReturnType<typeof loadARRecoveryWorkspace>["clientProfile"];
  selectedPromises?: Array<{ id: string; amountCents: number; promiseDate: string; status: string }>;
  now: string;
  actorName: string;
}): ARRecoveryLetterWorkflowRecord {
  const preview = buildLetterPreviewFromForm({ form, account, clientProfile, selectedPromises });
  const letterType = form.letterType || "Custom";
  return {
    id: `letter-${account.id}-${Date.now()}`,
    companyId,
    accountId: account.id,
    customerName: account.customerName,
    customerNumber: account.customerNumber,
    clientCompany: clientProfile.businessName || "AR Recovery Client",
    template: mapLetterTypeToTemplate(letterType),
    status: "Delivery Not Configured",
    sentAt: null,
    dateSent: null,
    timeSent: null,
    sentBy: actorName || account.assignedCollector || "System",
    deliveryStatus: "Pending",
    viewedStatus: "Pending",
    downloadedStatus: "Pending",
    agingBucket: account.agingSummary,
    balanceCents: account.openBalanceCents,
    collector: actorName || account.assignedCollector || "System",
    manager: "Not Captured",
    notes: form.additionalNotes.trim() || `${letterType} generated from Customer Account workspace. Email delivery is not configured yet; no email was sent.`,
    mergeFields: {
      businessName: clientProfile.letterDefaults.businessName,
      dbaName: clientProfile.dbaName,
      address: clientProfile.letterDefaults.address,
      phone: clientProfile.letterDefaults.phone,
      email: clientProfile.letterDefaults.email,
      website: clientProfile.letterDefaults.website,
      contactName: clientProfile.letterDefaults.contactName,
    },
    createdAt: now,
    updatedAt: now,
    letterType,
    recipient: form.recipient.trim(),
    relatedInvoices: form.relatedInvoices,
    relatedPromise: form.relatedPromise,
    relatedTask: form.relatedTask,
    additionalNotes: form.additionalNotes.trim(),
    generatedBy: actorName || account.assignedCollector || "System",
    generatedAt: now,
    previewBody: preview.body,
    signature: preview.signature,
  };
}

function renderARRecoveryLetterDocumentHtml({
  preview,
  clientProfile,
}: {
  preview: LetterPreview;
  clientProfile: ReturnType<typeof loadARRecoveryWorkspace>["clientProfile"];
}) {
  const summaryRows = preview.accountSummary
    .map((item) => `<div class="summary-item"><span>${escapeHtml(item.label)}</span><strong>${escapeHtml(item.value)}</strong></div>`)
    .join("");
  const reasonParagraphs = preview.reasonParagraphs
    .map((paragraph) => `<p>${escapeHtml(paragraph)}</p>`)
    .join("");
  const requestedActionParagraphs = preview.requestedActionParagraphs
    .map((paragraph) => `<p>${escapeHtml(paragraph)}</p>`)
    .join("");
  const signatureRows = preview.signatureLines
    .map((line, index) => index === 0 ? `<strong>${escapeHtml(line)}</strong>` : `<p>${escapeHtml(line)}</p>`)
    .join("");
  const footerContactRows = preview.footerContactItems
    .map((item) => `<p><span>${escapeHtml(item.label)}</span><strong>${escapeHtml(item.value)}</strong></p>`)
    .join("");
  const brandingLogoSrc = resolveARRecoveryLetterAssetSrc(preview.branding.logoSrc);
  const brandingAddressRows = preview.branding.addressLines
    .map((line) => `<p>${escapeHtml(line)}</p>`)
    .join("");
  const paymentInstructionsSection = preview.paymentInstructions.length
    ? `<section class="footer-section"><h2>Payment Instructions</h2>${preview.paymentInstructions.map((item) => `<p>${escapeHtml(item)}</p>`).join("")}</section>`
    : "";
  const complianceSection = preview.complianceNotices.length
    ? `<section class="footer-section"><h2>Compliance</h2>${preview.complianceNotices.map((item) => `<p>${escapeHtml(item)}</p>`).join("")}</section>`
    : "";
  const invoiceRows = preview.selectedInvoices.length
    ? preview.inlineInvoices
        .map((invoice) => `
          <tr>
            <td>${escapeHtml(invoice.invoiceNumber)}</td>
            <td>${escapeHtml(invoice.invoiceDate)}</td>
            <td>${escapeHtml(invoice.dueDate)}</td>
            <td>${escapeHtml(invoice.originalAmount)}</td>
            <td>${escapeHtml(invoice.openBalance)}</td>
            <td>${escapeHtml(invoice.status)}</td>
          </tr>
        `)
        .join("")
    : `<tr><td colspan="6">No invoices selected for this notice.</td></tr>`;
  const invoiceSection = preview.requiresSpreadsheetAttachment
    ? `
      <section class="notice-section invoice-section">
        <div class="section-heading">
          <h2>Invoices Requiring Attention</h2>
          <p>${escapeHtml(String(preview.invoiceSummary.invoiceCount))} selected | ${escapeHtml(preview.invoiceSummary.openBalanceTotal)}</p>
        </div>
        <p>The selected invoices requiring attention are included in the attached invoice detail file for readability.</p>
        <div class="summary-grid">
          <div class="summary-item"><span>Invoice Count</span><strong>${escapeHtml(String(preview.invoiceSummary.invoiceCount))}</strong></div>
          <div class="summary-item"><span>Selected Balance</span><strong>${escapeHtml(preview.invoiceSummary.openBalanceTotal)}</strong></div>
          <div class="summary-item"><span>Oldest Due Date</span><strong>${escapeHtml(preview.invoiceSummary.oldestDueDate)}</strong></div>
          <div class="summary-item"><span>Highest Aging</span><strong>${escapeHtml(preview.invoiceSummary.highestAgingBucket)}</strong></div>
          <div class="summary-item"><span>Account</span><strong>${escapeHtml(preview.invoiceSummary.accountNumber)}</strong></div>
        </div>
        ${preview.invoiceAttachment ? `<p class="attachment-note">Attachment: ${escapeHtml(preview.invoiceAttachment.filename)} | ${escapeHtml(String(preview.invoiceAttachment.invoiceCount))} invoices</p>` : ""}
      </section>
    `
    : `
      <section class="notice-section invoice-section">
        <div class="section-heading">
          <h2>Invoices Requiring Attention</h2>
          <p>${escapeHtml(String(preview.inlineInvoices.length))} selected | ${escapeHtml(preview.selectedInvoiceTotal)}</p>
        </div>
        <table>
          <thead>
            <tr>
              <th>Invoice</th>
              <th>Invoice Date</th>
              <th>Due Date</th>
              <th>Original Amount</th>
              <th>Open Balance</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>${invoiceRows}</tbody>
        </table>
      </section>
    `;
  const promiseSection = preview.selectedPromise
    ? `
      <section class="notice-section promise-section">
        <h2>${escapeHtml(preview.selectedPromise.heading)}</h2>
        <div class="promise-grid">
          <p><span>Status</span><strong>${escapeHtml(preview.selectedPromise.status)}</strong></p>
          <p><span>Amount</span><strong>${escapeHtml(preview.selectedPromise.amount)}</strong></p>
          <p><span>Date</span><strong>${escapeHtml(preview.selectedPromise.promiseDate)}</strong></p>
        </div>
        <p>${escapeHtml(preview.selectedPromise.body)}</p>
      </section>
    `
    : "";

  return `
    <article class="letter-document">
      <header class="letter-header">
        <div class="brand-lockup">
          <img class="brand-logo" src="${escapeHtml(brandingLogoSrc)}" alt="${escapeHtml(preview.branding.logoAlt)}" />
          <div>
            <h1>${escapeHtml(preview.branding.companyName)}</h1>
            <div class="brand-address">${brandingAddressRows}</div>
          </div>
        </div>
        <div class="document-meta">
          <p class="overline">Letter Date</p>
          <p>${escapeHtml(preview.date)}</p>
        </div>
      </header>

      <section class="recipient-block">
        <div>
          <h2>${escapeHtml(preview.customer)}</h2>
          <p>${escapeHtml(preview.recipientContactName)}</p>
          <p>${escapeHtml(preview.recipientEmail)}</p>
        </div>
        <div class="account-card">
          <p class="overline">Account Summary</p>
          <dl>
            <div>
              <dt>Account Number</dt>
              <dd>${escapeHtml(preview.account)}</dd>
            </div>
            <div>
              <dt>Current Balance</dt>
              <dd>${escapeHtml(preview.balance)}</dd>
            </div>
            <div>
              <dt>Account Aging</dt>
              <dd>${escapeHtml(preview.agingStatus)}</dd>
            </div>
          </dl>
        </div>
      </section>

      <section class="notice-section">
        <p class="subject">RE: ${escapeHtml(preview.subject)}</p>
        <h2>${escapeHtml(preview.documentTitle)}</h2>
        <p>${escapeHtml(preview.greeting)}</p>
        ${reasonParagraphs}
        <div class="summary-grid">${summaryRows}</div>
      </section>

      ${invoiceSection}

      ${promiseSection}

      <section class="notice-section">
        <h2>Requested Action</h2>
        ${requestedActionParagraphs}
      </section>

      <footer class="letter-footer">
        <section class="footer-section closing-section" aria-label="Closing">
          <p>${escapeHtml(preview.closing)}</p>
        </section>
        <section class="footer-section signature-section" aria-label="Signature">
          <p>${escapeHtml(preview.signatureBlock.salutation)}</p>
          ${signatureRows}
        </section>
        ${paymentInstructionsSection}
        ${complianceSection}
        <section class="footer-section contact-section" aria-label="Contact Information">
          ${footerContactRows}
        </section>
      </footer>
    </article>
  `.trim();
}

function renderARRecoveryLetterPrintPage({
  preview,
  clientProfile,
  outputMode,
}: {
  preview: LetterPreview;
  clientProfile: ReturnType<typeof loadARRecoveryWorkspace>["clientProfile"];
  outputMode: "print" | "pdf";
}) {
  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${escapeHtml(preview.documentTitle)} - ${escapeHtml(preview.customer)}</title>
    <style>
      @page { size: letter; margin: 0.55in; }
      * { box-sizing: border-box; }
      body {
        margin: 0;
        background: #ffffff;
        color: #0A2540;
        font-family: Georgia, "Times New Roman", serif;
        font-size: 12px;
        line-height: 1.55;
      }
      .letter-document {
        width: 100%;
        max-width: 7.4in;
        margin: 0 auto;
        background: #ffffff;
      }
      .letter-header,
      .recipient-block {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 1.75in;
        gap: 24px;
      }
      .letter-header {
        border-bottom: 1px solid #dbeafe;
        padding-bottom: 18px;
      }
      .brand-lockup {
        display: flex;
        align-items: flex-start;
        gap: 16px;
        min-width: 0;
      }
      .brand-logo {
        width: 0.8in;
        height: 0.8in;
        object-fit: contain;
        flex: 0 0 auto;
      }
      .brand-address {
        margin-top: 8px;
        color: #475569;
        font-family: Arial, sans-serif;
        font-size: 11px;
        font-weight: 700;
        line-height: 1.45;
      }
      h1, h2, p { margin: 0; }
      h1 {
        font-size: 23px;
        line-height: 1.15;
      }
      h2 {
        margin-bottom: 8px;
        font-size: 16px;
        line-height: 1.25;
      }
      .overline,
      .subject,
      th {
        color: #0f766e;
        font-family: Arial, sans-serif;
        font-size: 9px;
        font-weight: 800;
        letter-spacing: .12em;
        text-transform: uppercase;
      }
      .document-meta,
      .account-card {
        border: 1px solid #BFEAFF;
        border-radius: 12px;
        background: #F8FCFF;
        padding: 12px;
        text-align: right;
      }
      .account-card dl,
      .account-card dt,
      .account-card dd {
        margin: 0;
      }
      .account-card dl {
        display: grid;
        gap: 8px;
        margin-top: 10px;
      }
      .account-card dt {
        color: #64748B;
        font-family: Arial, sans-serif;
        font-size: 8px;
        font-weight: 800;
        letter-spacing: .1em;
        text-transform: uppercase;
      }
      .account-card dd {
        margin-top: 2px;
        color: #0A2540;
        font-weight: 700;
        overflow-wrap: anywhere;
      }
      .recipient-block,
      .notice-section {
        margin-top: 24px;
      }
      .summary-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 8px;
        margin-top: 14px;
        border: 1px solid #BFEAFF;
        border-radius: 12px;
        background: #F8FCFF;
        padding: 12px;
      }
      .summary-item span,
      .promise-grid span {
        display: block;
        color: #0f766e;
        font-family: Arial, sans-serif;
        font-size: 8px;
        font-weight: 800;
        letter-spacing: .1em;
        text-transform: uppercase;
      }
      .summary-item strong,
      .promise-grid strong {
        display: block;
        margin-top: 3px;
        overflow-wrap: anywhere;
      }
      .section-heading {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 16px;
      }
      table {
        width: 100%;
        margin-top: 10px;
        border-collapse: collapse;
        page-break-inside: auto;
      }
      tr { page-break-inside: avoid; page-break-after: auto; }
      th, td {
        border-top: 1px solid #BFEAFF;
        padding: 7px 6px;
        text-align: left;
        vertical-align: top;
      }
      .promise-section {
        border: 1px solid #BFEAFF;
        border-radius: 12px;
        background: #F8FCFF;
        padding: 14px;
      }
      .promise-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 10px;
        margin: 10px 0;
      }
      .letter-footer {
        margin-top: 36px;
        border-top: 1px solid #dbeafe;
        padding-top: 16px;
      }
      .footer-section + .footer-section {
        margin-top: 16px;
      }
      .footer-section h2 {
        margin-bottom: 6px;
        color: #0f766e;
        font-family: Arial, sans-serif;
        font-size: 9px;
        font-weight: 800;
        letter-spacing: .12em;
        text-transform: uppercase;
      }
      .signature-section strong {
        display: block;
        margin: 8px 0 2px;
      }
      .contact-section p {
        display: grid;
        grid-template-columns: 0.85in minmax(0, 1fr);
        gap: 10px;
        margin-top: 5px;
      }
      .contact-section span {
        color: #0f766e;
        font-family: Arial, sans-serif;
        font-size: 8px;
        font-weight: 800;
        letter-spacing: .1em;
        text-transform: uppercase;
      }
      .contact-section strong {
        overflow-wrap: anywhere;
      }
      .attachment-note {
        margin-top: 10px;
        color: #475569;
        font-size: 11px;
      }
      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .letter-document { box-shadow: none; }
      }
    </style>
  </head>
  <body>
    ${renderARRecoveryLetterDocumentHtml({ preview, clientProfile })}
  </body>
</html>`;
}

function openARRecoveryLetterPrintWindow({
  preview,
  clientProfile,
  outputMode,
}: {
  preview: LetterPreview;
  clientProfile: ReturnType<typeof loadARRecoveryWorkspace>["clientProfile"];
  outputMode: "print" | "pdf";
}) {
  if (typeof window === "undefined") return false;
  const printWindow = window.open("", "_blank", "width=960,height=720");
  if (!printWindow) return false;
  printWindow.document.open();
  printWindow.document.write(renderARRecoveryLetterPrintPage({ preview, clientProfile, outputMode }));
  printWindow.document.close();
  printWindow.focus();
  window.setTimeout(() => {
    printWindow.print();
  }, 250);
  return true;
}

function renderARRecoveryStatementDocumentHtml({ preview }: { preview: StatementPreview }) {
  const brandingLogoSrc = resolveARRecoveryLetterAssetSrc(preview.branding.logoSrc);
  const brandingAddressRows = preview.branding.addressLines
    .map((line) => `<p>${escapeHtml(line)}</p>`)
    .join("");
  const summaryParagraphs = preview.summaryParagraphs
    .map((paragraph) => `<p>${escapeHtml(paragraph)}</p>`)
    .join("");
  const promiseSection = preview.selectedPromise
    ? `
      <section class="statement-section promise-section">
        <h2>Promise Context</h2>
        <div class="promise-grid">
          <p><span>Status</span><strong>${escapeHtml(preview.selectedPromise.status)}</strong></p>
          <p><span>Amount</span><strong>${escapeHtml(preview.selectedPromise.amount)}</strong></p>
          <p><span>Date</span><strong>${escapeHtml(preview.selectedPromise.promiseDate)}</strong></p>
        </div>
        <p>${escapeHtml(preview.selectedPromise.body)}</p>
      </section>
    `
    : "";
  const invoiceRows = preview.lineItems.length
    ? preview.lineItems.map((item) => `
        <tr>
          <td>${escapeHtml(item.invoiceNumber)}</td>
          <td>${escapeHtml(item.invoiceDate)}</td>
          <td>${escapeHtml(item.dueDate)}</td>
          <td>${escapeHtml(item.originalAmount)}</td>
          <td>${escapeHtml(item.openBalance)}</td>
          <td>${escapeHtml(item.aging)}</td>
          <td>${escapeHtml(item.status)}</td>
        </tr>
      `).join("")
    : `<tr><td colspan="7">No invoices selected for this statement.</td></tr>`;
  const totalsRows = [
    ["Number of Selected Invoices", preview.totals.selectedInvoiceCount],
    ["Total Original Amount", preview.totals.totalOriginalAmount],
    ["Total Open Balance", preview.totals.totalOpenBalance],
    ["Current Due", preview.totals.currentDue],
    ["Past Due", preview.totals.pastDue],
    ["Total Due", preview.totals.totalDue],
  ].map(([label, value]) => `<div class="summary-item"><span>${escapeHtml(label)}</span><strong>${escapeHtml(value)}</strong></div>`).join("");
  const footerContactRows = preview.footerContactItems
    .map((item) => `<p>${escapeHtml(item.value)}</p>`)
    .join("");

  return `
    <article class="statement-document">
      <header class="statement-header">
        <div class="brand-lockup">
          <img class="brand-logo" src="${escapeHtml(brandingLogoSrc)}" alt="${escapeHtml(preview.branding.logoAlt)}" />
          <div>
            <h1>${escapeHtml(preview.branding.companyName)}</h1>
            <div class="brand-address">${brandingAddressRows}</div>
          </div>
        </div>
        <div class="document-meta">
          <p class="overline">Statement Date</p>
          <p>${escapeHtml(preview.statementDate)}</p>
          <p class="overline title-overline">Account Statement</p>
        </div>
      </header>
      <section class="recipient-block">
        <div>
          <h2>${escapeHtml(preview.customer)}</h2>
          <p>${escapeHtml(preview.recipientContactName)}</p>
          <p>${escapeHtml(preview.recipientEmail)}</p>
        </div>
        <div class="account-card">
          <p class="overline">Account Summary</p>
          <dl>
            <div><dt>Customer</dt><dd>${escapeHtml(preview.customer)}</dd></div>
            <div><dt>Account Number</dt><dd>${escapeHtml(preview.account)}</dd></div>
            <div><dt>Account Aging</dt><dd>${escapeHtml(preview.agingSummary)}</dd></div>
            <div><dt>Total Due</dt><dd>${escapeHtml(preview.totalBalance)}</dd></div>
          </dl>
        </div>
      </section>
      <section class="statement-section">
        <h2>${escapeHtml(preview.documentTitle)}</h2>
        ${summaryParagraphs}
      </section>
      ${promiseSection}
      <section class="statement-section invoice-section">
        <table>
          <thead>
            <tr>
              <th>Invoice Number</th>
              <th>Invoice Date</th>
              <th>Due Date</th>
              <th>Original Amount</th>
              <th>Open Balance</th>
              <th>Aging</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>${invoiceRows}</tbody>
        </table>
      </section>
      <section class="summary-grid">${totalsRows}</section>
      <section class="statement-section remittance-section">
        <h2>Remittance Questions</h2>
        <p>For payment or remittance assistance, contact:</p>
        <p><strong>${escapeHtml(preview.remittanceContact.name)}</strong></p>
        <p>${escapeHtml(preview.remittanceContact.phone)}</p>
        <p>${escapeHtml(preview.remittanceContact.email)}</p>
      </section>
      <footer class="statement-footer">
        <p><strong>${escapeHtml(preview.branding.companyName)}</strong></p>
        <p>Accounts Receivable Department</p>
        ${footerContactRows}
      </footer>
    </article>
  `.trim();
}

function renderARRecoveryStatementPrintPage({
  preview,
  outputMode,
}: {
  preview: StatementPreview;
  outputMode: "print" | "pdf";
}) {
  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${escapeHtml(preview.documentTitle)} - ${escapeHtml(preview.customer)}</title>
    <style>
      @page { size: letter; margin: 0.5in; }
      * { box-sizing: border-box; }
      body {
        margin: 0;
        background: #ffffff;
        color: #0A2540;
        font-family: Georgia, "Times New Roman", serif;
        font-size: 11px;
        line-height: 1.45;
      }
      .statement-document {
        width: 100%;
        max-width: 7.5in;
        margin: 0 auto;
        background: #ffffff;
      }
      .statement-header,
      .recipient-block {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 1.85in;
        gap: 22px;
      }
      .statement-header {
        border-bottom: 1px solid #dbeafe;
        padding-bottom: 16px;
      }
      .brand-lockup { display: flex; align-items: flex-start; gap: 16px; min-width: 0; }
      .brand-logo { width: 0.78in; height: 0.78in; object-fit: contain; flex: 0 0 auto; }
      .brand-address {
        margin-top: 8px;
        color: #475569;
        font-family: Arial, sans-serif;
        font-size: 10px;
        font-weight: 700;
        line-height: 1.4;
      }
      h1, h2, p, dl, dt, dd { margin: 0; }
      h1 { font-size: 22px; line-height: 1.15; }
      h2 { margin-bottom: 8px; font-size: 15px; line-height: 1.25; }
      .overline,
      th,
      .summary-item span,
      .account-card dt {
        color: #0f766e;
        font-family: Arial, sans-serif;
        font-size: 8px;
        font-weight: 800;
        letter-spacing: .1em;
        text-transform: uppercase;
      }
      .title-overline { margin-top: 10px; }
      .document-meta,
      .account-card {
        border: 1px solid #BFEAFF;
        border-radius: 12px;
        background: #F8FCFF;
        padding: 11px;
        text-align: right;
      }
      .account-card dl { display: grid; gap: 7px; margin-top: 9px; }
      .account-card dd { margin-top: 2px; font-weight: 700; overflow-wrap: anywhere; }
      .recipient-block,
      .statement-section,
      .summary-grid {
        margin-top: 22px;
      }
      .statement-section p + p { margin-top: 8px; }
      table {
        width: 100%;
        margin-top: 8px;
        border-collapse: collapse;
        table-layout: fixed;
        page-break-inside: auto;
      }
      thead { display: table-header-group; }
      tr { page-break-inside: avoid; page-break-after: auto; }
      th, td {
        border-top: 1px solid #BFEAFF;
        padding: 6px 4px;
        text-align: left;
        vertical-align: top;
        overflow-wrap: anywhere;
      }
      th:nth-child(4), th:nth-child(5),
      td:nth-child(4), td:nth-child(5) {
        text-align: right;
      }
      .summary-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 8px;
        border: 1px solid #BFEAFF;
        border-radius: 12px;
        background: #F8FCFF;
        padding: 12px;
        page-break-inside: avoid;
      }
      .summary-item strong {
        display: block;
        margin-top: 3px;
        overflow-wrap: anywhere;
      }
      .promise-section,
      .remittance-section {
        border: 1px solid #BFEAFF;
        border-radius: 12px;
        background: #F8FCFF;
        padding: 12px;
        page-break-inside: avoid;
      }
      .promise-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 8px;
        margin: 8px 0;
      }
      .promise-grid span {
        display: block;
        color: #0f766e;
        font-family: Arial, sans-serif;
        font-size: 8px;
        font-weight: 800;
        letter-spacing: .1em;
        text-transform: uppercase;
      }
      .statement-footer {
        margin-top: 28px;
        border-top: 1px solid #dbeafe;
        padding-top: 14px;
        color: #475569;
        font-family: Arial, sans-serif;
        font-size: 10px;
        font-weight: 700;
        line-height: 1.45;
      }
      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .statement-document { box-shadow: none; }
      }
    </style>
  </head>
  <body data-output-mode="${escapeHtml(outputMode)}">
    ${renderARRecoveryStatementDocumentHtml({ preview })}
  </body>
</html>`;
}

function openARRecoveryStatementPrintWindow({
  preview,
  outputMode,
}: {
  preview: StatementPreview;
  outputMode: "print" | "pdf";
}) {
  if (typeof window === "undefined") return false;
  const printWindow = window.open("", "_blank", "width=960,height=720");
  if (!printWindow) return false;
  printWindow.document.open();
  printWindow.document.write(renderARRecoveryStatementPrintPage({ preview, outputMode }));
  printWindow.document.close();
  printWindow.focus();
  window.setTimeout(() => {
    printWindow.print();
  }, 250);
  return true;
}

function renderARRecoveryStatementBatchPrintPage({
  previews,
  batchId,
}: {
  previews: StatementPreview[];
  batchId: string;
}) {
  const documents = previews
    .map((preview, index) => `
      <section class="statement-batch-page${index === previews.length - 1 ? " statement-batch-page--last" : ""}">
        ${renderARRecoveryStatementDocumentHtml({ preview })}
      </section>
    `)
    .join("");

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>Statement Print Batch - ${escapeHtml(batchId)}</title>
    <style>
      @page { size: letter; margin: 0.5in; }
      * { box-sizing: border-box; }
      body {
        margin: 0;
        background: #ffffff;
        color: #0A2540;
        font-family: Georgia, "Times New Roman", serif;
        font-size: 11px;
        line-height: 1.45;
      }
      .statement-batch-page {
        break-after: page;
        page-break-after: always;
      }
      .statement-batch-page--last {
        break-after: auto;
        page-break-after: auto;
      }
      .statement-document {
        width: 100%;
        max-width: 7.5in;
        margin: 0 auto;
        background: #ffffff;
      }
      .statement-header,
      .recipient-block {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 1.85in;
        gap: 22px;
      }
      .statement-header {
        border-bottom: 1px solid #dbeafe;
        padding-bottom: 16px;
      }
      .brand-lockup { display: flex; align-items: flex-start; gap: 16px; min-width: 0; }
      .brand-logo { width: 0.78in; height: 0.78in; object-fit: contain; flex: 0 0 auto; }
      .brand-address {
        margin-top: 8px;
        color: #475569;
        font-family: Arial, sans-serif;
        font-size: 10px;
        font-weight: 700;
        line-height: 1.4;
      }
      h1, h2, p, dl, dt, dd { margin: 0; }
      h1 { font-size: 22px; line-height: 1.15; }
      h2 { margin-bottom: 8px; font-size: 15px; line-height: 1.25; }
      .overline,
      th,
      .summary-item span,
      .account-card dt {
        color: #0f766e;
        font-family: Arial, sans-serif;
        font-size: 8px;
        font-weight: 800;
        letter-spacing: .1em;
        text-transform: uppercase;
      }
      .title-overline { margin-top: 10px; }
      .document-meta,
      .account-card {
        border: 1px solid #BFEAFF;
        border-radius: 12px;
        background: #F8FCFF;
        padding: 11px;
        text-align: right;
      }
      .account-card dl { display: grid; gap: 7px; margin-top: 9px; }
      .account-card dd { margin-top: 2px; font-weight: 700; overflow-wrap: anywhere; }
      .recipient-block,
      .statement-section,
      .summary-grid {
        margin-top: 22px;
      }
      .statement-section,
      .summary-grid,
      .recipient-block,
      .statement-header,
      .promise-section,
      .remittance-section {
        break-inside: avoid;
        page-break-inside: avoid;
      }
      .statement-section p + p { margin-top: 8px; }
      table {
        width: 100%;
        margin-top: 8px;
        border-collapse: collapse;
        table-layout: fixed;
        page-break-inside: auto;
      }
      thead { display: table-header-group; }
      tr { page-break-inside: avoid; page-break-after: auto; }
      th, td {
        border-top: 1px solid #BFEAFF;
        padding: 6px 4px;
        text-align: left;
        vertical-align: top;
        overflow-wrap: anywhere;
      }
      th:nth-child(4), th:nth-child(5),
      td:nth-child(4), td:nth-child(5) {
        text-align: right;
      }
      .summary-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 8px;
        border: 1px solid #BFEAFF;
        border-radius: 12px;
        background: #F8FCFF;
        padding: 12px;
        page-break-inside: avoid;
      }
      .summary-item strong {
        display: block;
        margin-top: 3px;
        overflow-wrap: anywhere;
      }
      .promise-section,
      .remittance-section {
        border: 1px solid #BFEAFF;
        border-radius: 12px;
        background: #F8FCFF;
        padding: 12px;
        page-break-inside: avoid;
      }
      .promise-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 8px;
        margin: 8px 0;
      }
      .promise-grid span {
        display: block;
        color: #0f766e;
        font-family: Arial, sans-serif;
        font-size: 8px;
        font-weight: 800;
        letter-spacing: .1em;
        text-transform: uppercase;
      }
      .statement-footer {
        margin-top: 28px;
        border-top: 1px solid #dbeafe;
        padding-top: 14px;
        color: #475569;
        font-family: Arial, sans-serif;
        font-size: 10px;
        font-weight: 700;
        line-height: 1.45;
      }
      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .statement-document { box-shadow: none; }
      }
    </style>
  </head>
  <body data-output-mode="batch-print" data-batch-id="${escapeHtml(batchId)}">
    ${documents}
  </body>
</html>`;
}

function openARRecoveryStatementBatchPrintWindow({
  previews,
  batchId,
}: {
  previews: StatementPreview[];
  batchId: string;
}) {
  if (typeof window === "undefined") return false;
  const printWindow = window.open("", "_blank", "width=960,height=720");
  if (!printWindow) return false;
  printWindow.document.open();
  printWindow.document.write(renderARRecoveryStatementBatchPrintPage({ previews, batchId }));
  printWindow.document.close();
  printWindow.focus();
  window.setTimeout(() => {
    printWindow.print();
  }, 250);
  return true;
}

function LetterPreviewPanel({ preview }: { preview: LetterPreview }) {
  return (
    <div className="mt-3 grid gap-3 xl:grid-cols-[.9fr_1.1fr]">
      <div className="grid gap-2">
        <WorkspaceField label="Customer" value={preview.customer} />
        <WorkspaceField label="Address" value={preview.address} />
        <WorkspaceField label="Account" value={preview.account} />
        <WorkspaceField label="Balance" value={preview.balance} />
        <div className="grid gap-2 md:grid-cols-2">
          {preview.mergeFields.map((field) => (
            <WorkspaceField key={field.label} label={field.label} value={field.value} />
          ))}
        </div>
      </div>
      <div className="rounded-xl border border-[rgba(255,106,43,.12)] bg-[rgba(7,18,31,.72)] p-3">
        <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Body</p>
        <p className="mt-2 whitespace-pre-line text-sm font-semibold leading-6 text-[#E8EEF4]">{preview.body}</p>
        <div className="mt-3 rounded-lg border border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.03)] px-3 py-2">
          <p className="text-[10px] font-black uppercase tracking-[0.14em] text-[#22E0D0]">Signature</p>
          <p className="mt-1 text-sm font-semibold text-[#F6F1E8]">{preview.signature}</p>
        </div>
      </div>
    </div>
  );
}

type StatementPreview = {
  branding: LetterBrandingBlock;
  documentTitle: string;
  customer: string;
  client: string;
  account: string;
  statementDate: string;
  recipientContactName: string;
  recipientEmail: string;
  totalBalance: string;
  invoiceCount: string;
  agingSummary: string;
  notes: string;
  deliveryOption: string;
  lineItems: Array<{
    invoiceNumber: string;
    invoiceDate: string;
    dueDate: string;
    originalAmount: string;
    openBalance: string;
    balance: string;
    aging: string;
    status: string;
  }>;
  summaryParagraphs: string[];
  selectedPromise: { amount: string; promiseDate: string; status: string; body: string } | null;
  totals: {
    selectedInvoiceCount: string;
    totalOriginalAmount: string;
    totalOpenBalance: string;
    currentDue: string;
    pastDue: string;
    totalDue: string;
  };
  remittanceContact: {
    name: string;
    phone: string;
    email: string;
  };
  footerContactItems: LetterFooterContactItem[];
  body: string;
};

const STATEMENT_TYPE_OPTIONS: ARRecoveryStatementType[] = [
  "Current Statement",
  "Past Due Statement",
  "Final Statement",
  "Promise Balance Statement",
  "Settlement Statement",
  "Custom Statement",
];

function buildStatementPreviewFromForm({
  form,
  account,
  clientProfile,
  selectedPromises = [],
}: {
  form: ARRecoveryStatementFormState;
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  clientProfile: ReturnType<typeof loadARRecoveryWorkspace>["clientProfile"];
  selectedPromises?: Array<{ id: string; amountCents: number; promiseDate: string; status: string }>;
}): StatementPreview {
  const allInvoices = buildInvoicePlaceholders(account);
  const selectedInvoiceNumbers = new Set(form.relatedInvoices);
  const selectedInvoices = allInvoices.filter((invoice) => selectedInvoiceNumbers.has(invoice.invoiceNumber));
  const totalOriginalAmountCents = selectedInvoices.reduce((total, invoice) => total + invoice.originalAmountCents, 0);
  const totalBalanceCents = selectedInvoices.reduce((total, invoice) => total + invoice.currentBalanceCents, 0);
  const pastDueCents = selectedInvoices
    .filter((invoice) => invoice.status.toLowerCase().includes("past due") || new Date(invoice.dueDate).getTime() < Date.now())
    .reduce((total, invoice) => total + invoice.currentBalanceCents, 0);
  const currentDueCents = Math.max(totalBalanceCents - pastDueCents, 0);
  const statementType = form.statementType || "Account Statement";
  const statementDate = form.statementDate || new Date().toISOString().slice(0, 10);
  const selectedPromiseRecord = selectedPromises.find((promise) => promise.id === form.relatedPromise) || null;
  const selectedPromise = selectedPromiseRecord
    ? {
        amount: formatARRecoveryCurrency(selectedPromiseRecord.amountCents),
        promiseDate: formatCapturedDate(selectedPromiseRecord.promiseDate),
        status: selectedPromiseRecord.status,
        body: `A ${selectedPromiseRecord.status.toLowerCase()} promise of ${formatARRecoveryCurrency(selectedPromiseRecord.amountCents)} dated ${formatCapturedDate(selectedPromiseRecord.promiseDate)} is associated with this account and has been reflected in this statement context.`,
      }
    : null;
  const branding: LetterBrandingBlock = {
    logoSrc: AR_RECOVERY_LETTER_BRANDING_LOGO_SRC,
    logoAlt: "CollabRecover AR Solutions",
    companyName: clientProfile.letterDefaults.businessName || clientProfile.businessName,
    addressLines: buildARRecoveryLetterAddressLines(clientProfile.letterDefaults.address || clientProfile.addressLine1 || ""),
  };
  const summaryParagraphs = [
    `This statement summarizes the open invoices currently associated with your account as of ${formatCapturedDate(statementDate)}.`,
    "Please review the invoice details below. If payment has already been submitted, please provide the payment date and remittance reference so the account can be updated.",
  ];
  const footerContactItems: LetterFooterContactItem[] = [
    { label: "Email" as const, value: clientProfile.letterDefaults.email },
    { label: "Phone" as const, value: clientProfile.letterDefaults.phone },
    { label: "Website" as const, value: clientProfile.letterDefaults.website },
  ].filter(
    (item, index, items): item is LetterFooterContactItem =>
      Boolean(item.value) && items.findIndex((candidate) => candidate.value === item.value) === index,
  );
  const body = [
    ...summaryParagraphs,
    selectedPromise?.body || "",
    `Selected invoices: ${selectedInvoices.length}. Total due: ${formatARRecoveryCurrency(totalBalanceCents)}.`,
  ].filter(Boolean).join("\n\n");

  return {
    branding,
    documentTitle: statementType || "Account Statement",
    customer: account.customerName,
    client: clientProfile.businessName || clientProfile.dbaName || "AR Recovery Client",
    account: account.customerNumber,
    statementDate: formatCapturedDate(statementDate),
    recipientContactName: account.contactName || account.customerName,
    recipientEmail: account.contactEmail,
    totalBalance: formatARRecoveryCurrency(totalBalanceCents),
    invoiceCount: String(selectedInvoices.length),
    agingSummary: account.agingSummary,
    notes: form.statementNotes.trim(),
    deliveryOption: form.deliveryOption,
    lineItems: selectedInvoices.map((invoice) => ({
      invoiceNumber: invoice.invoiceNumber,
      invoiceDate: formatCapturedDate(invoice.invoiceDate),
      dueDate: formatCapturedDate(invoice.dueDate),
      originalAmount: formatARRecoveryCurrency(invoice.originalAmountCents),
      openBalance: formatARRecoveryCurrency(invoice.currentBalanceCents),
      balance: formatARRecoveryCurrency(invoice.currentBalanceCents),
      aging: account.agingSummary,
      status: invoice.status,
    })),
    summaryParagraphs,
    selectedPromise,
    totals: {
      selectedInvoiceCount: String(selectedInvoices.length),
      totalOriginalAmount: formatARRecoveryCurrency(totalOriginalAmountCents),
      totalOpenBalance: formatARRecoveryCurrency(totalBalanceCents),
      currentDue: formatARRecoveryCurrency(currentDueCents),
      pastDue: formatARRecoveryCurrency(pastDueCents),
      totalDue: formatARRecoveryCurrency(totalBalanceCents),
    },
    remittanceContact: {
      name: clientProfile.letterDefaults.contactName,
      phone: clientProfile.letterDefaults.phone,
      email: clientProfile.letterDefaults.email,
    },
    footerContactItems,
    body,
  };
}

function buildStatementWorkflowRecordFromForm({
  form,
  account,
  companyId,
  clientProfile,
  existingStatementCount,
  selectedPromises = [],
  now,
  actorName,
}: {
  form: ARRecoveryStatementFormState;
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  companyId: string;
  clientProfile: ReturnType<typeof loadARRecoveryWorkspace>["clientProfile"];
  existingStatementCount: number;
  selectedPromises?: Array<{ id: string; amountCents: number; promiseDate: string; status: string }>;
  now: string;
  actorName: string;
}): ARRecoveryStatementWorkflowRecord {
  const allInvoices = buildInvoicePlaceholders(account);
  const selectedInvoiceNumbers = new Set(form.relatedInvoices);
  const selectedInvoices = allInvoices.filter((invoice) => selectedInvoiceNumbers.has(invoice.invoiceNumber));
  const preview = buildStatementPreviewFromForm({ form, account, clientProfile, selectedPromises });
  const totalBalanceCents = selectedInvoices.reduce((total, invoice) => total + invoice.currentBalanceCents, 0);
  const statementType = form.statementType || "Custom Statement";
  const statementNumber = `ST-${String(existingStatementCount + 1001).padStart(4, "0")}`;
  const deliveryNotConfigured = form.deliveryOption === "Email";

  return {
    id: `statement-${account.id}-${Date.now()}`,
    companyId,
    accountId: account.id,
    customerName: account.customerName,
    customerNumber: account.customerNumber,
    clientCompany: clientProfile.businessName || clientProfile.dbaName || "AR Recovery Client",
    statementNumber,
    status: deliveryNotConfigured ? "Delivery Not Configured" : "Generated",
    sentAt: null,
    dateSent: form.statementDate,
    timeSent: null,
    sentBy: actorName || account.assignedCollector || "System",
    deliveryStatus: "Pending",
    openedStatus: "Pending",
    downloadedStatus: "Pending",
    balanceCents: totalBalanceCents,
    openInvoiceCount: selectedInvoices.length,
    dueDate: form.statementDate,
    collector: actorName || account.assignedCollector || "System",
    manager: "Not Captured",
    notes: form.statementNotes.trim() || (deliveryNotConfigured ? `${statementType} generated. Email delivery is not configured yet; no email was sent.` : `${statementType} generated for ${form.deliveryOption.toLowerCase()} delivery.`),
    lineItems: selectedInvoices.map((invoice) => ({
      invoiceNumber: invoice.invoiceNumber,
      dueDate: invoice.dueDate,
      amountCents: invoice.currentBalanceCents,
      status: invoice.status as "Open" | "Past Due" | "Paid" | "Partial",
    })),
    mergeFields: {
      businessName: clientProfile.letterDefaults.businessName,
      dbaName: clientProfile.dbaName,
      address: clientProfile.letterDefaults.address,
      phone: clientProfile.letterDefaults.phone,
      email: clientProfile.letterDefaults.email,
      website: clientProfile.letterDefaults.website,
      contactName: clientProfile.letterDefaults.contactName,
    },
    createdAt: now,
    updatedAt: now,
    statementType,
    relatedInvoices: selectedInvoices.map((invoice) => invoice.invoiceNumber),
    relatedPromise: form.relatedPromise,
    relatedTask: "",
    statementNotes: form.statementNotes.trim(),
    generatedBy: actorName || account.assignedCollector || "System",
    generatedAt: now,
    deliveryOption: form.deliveryOption,
    previewBody: preview.body,
  };
}

function StatementPreviewPanel({ preview }: { preview: StatementPreview }) {
  return (
    <div className="mt-3 grid gap-3 xl:grid-cols-[.85fr_1.15fr]">
      <div className="grid gap-2">
        <div className="grid gap-2 md:grid-cols-2">
          <WorkspaceField label="Customer" value={preview.customer} />
          <WorkspaceField label="Client" value={preview.client} />
          <WorkspaceField label="Account" value={preview.account} />
          <WorkspaceField label="Statement Date" value={preview.statementDate} />
          <WorkspaceField label="Total Balance" value={preview.totalBalance} />
          <WorkspaceField label="Invoice Count" value={preview.invoiceCount} />
          <WorkspaceField label="Aging Summary" value={preview.agingSummary} />
          <WorkspaceField label="Delivery" value={preview.deliveryOption} />
        </div>
        <WorkspaceField label="Statement Notes" value={preview.notes} />
      </div>
      <div className="rounded-xl border border-[rgba(255,106,43,.12)] bg-[rgba(7,18,31,.72)] p-3">
        <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Invoice Listing</p>
        <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-[rgba(34,224,208,.12)]">
          <table className="w-full min-w-[34rem] text-left text-xs font-semibold text-[#E8EEF4]">
            <thead className="sticky top-0 bg-[#07121f] text-[10px] font-black uppercase tracking-[0.14em] text-[#22E0D0]">
              <tr>
                <th className="px-3 py-2">Invoice</th>
                <th className="px-3 py-2">Due Date</th>
                <th className="px-3 py-2 text-right">Balance</th>
                <th className="px-3 py-2">Aging</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {preview.lineItems.map((item) => (
                <tr key={item.invoiceNumber} className="border-t border-[rgba(34,224,208,.08)]">
                  <td className="px-3 py-2">{item.invoiceNumber}</td>
                  <td className="px-3 py-2">{item.dueDate}</td>
                  <td className="px-3 py-2 text-right">{item.balance}</td>
                  <td className="px-3 py-2">{item.aging}</td>
                  <td className="px-3 py-2">{item.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3 rounded-lg border border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.03)] px-3 py-2">
          <p className="text-[10px] font-black uppercase tracking-[0.14em] text-[#22E0D0]">Preview Body</p>
          <p className="mt-1 whitespace-pre-line text-sm font-semibold leading-6 text-[#F6F1E8]">{preview.body}</p>
        </div>
      </div>
    </div>
  );
}

const LETTER_FILTERS: Array<{ key: AccountLetterFilterKey; label: string }> = [
  { key: "all", label: "All" },
  { key: "generated", label: "Generated" },
  { key: "draft", label: "Draft" },
  { key: "sent", label: "Sent" },
  { key: "delivered", label: "Delivered" },
  { key: "viewed", label: "Viewed" },
  { key: "failed", label: "Failed" },
];

function letterMatchesFilter(letter: AccountLetterRecord, filter: AccountLetterFilterKey) {
  if (filter === "all") return true;
  if (filter === "generated") return letter.status === "Generated" || letter.status === "Delivery Not Configured" || letter.status === "Queued" || letter.status === "Downloaded";
  if (filter === "draft") return letter.status === "Draft";
  if (filter === "sent") return letter.status === "Sent";
  if (filter === "delivered") return letter.status === "Delivered" || letter.deliveryStatus === "Delivered";
  if (filter === "viewed") return letter.status === "Viewed" || letter.viewedStatus === "Viewed";
  return letter.status === "Failed" || letter.status === "Delivery Not Configured" || letter.notes.toLowerCase().includes("failed") || letter.deliveryStatus.toLowerCase().includes("failed");
}

function countLettersForFilter(letters: AccountLetterRecord[], filter: AccountLetterFilterKey) {
  return letters.filter((letter) => letterMatchesFilter(letter, filter)).length;
}

function letterStatusBadgeClass(status: AccountLetterRecord["status"], deliveryStatus: AccountLetterRecord["deliveryStatus"]) {
  if (status === "Cancelled" || status === "Failed") return "border-[rgba(248,113,113,.34)] bg-[rgba(248,113,113,.12)] text-[#FCA5A5]";
  if (status === "Delivery Not Configured") return "border-[rgba(255,106,43,.34)] bg-[rgba(255,106,43,.12)] text-[#FF6A2B]";
  if (status === "Delivered" || deliveryStatus === "Delivered" || status === "Viewed" || status === "Downloaded") return "border-[rgba(34,197,94,.32)] bg-[rgba(34,197,94,.12)] text-[#86EFAC]";
  if (status === "Sent" || status === "Queued" || status === "Generated") return "border-[rgba(255,106,43,.34)] bg-[rgba(255,106,43,.12)] text-[#FF6A2B]";
  return "border-[rgba(34,224,208,.3)] bg-[rgba(34,224,208,.1)] text-[#22E0D0]";
}

function buildLetterNextAction(letter: AccountLetterRecord) {
  if (letter.status === "Delivery Not Configured") return "Configure email delivery before marking this letter sent.";
  if (letter.status === "Draft" || letter.status === "Queued") return "Review and prepare for delivery.";
  if (letter.deliveryStatus === "Pending") return "Confirm letter delivery.";
  if (letter.viewedStatus === "Viewed") return "Review response and determine next collection step.";
  if (letter.status === "Cancelled") return "No next action captured.";
  return "Monitor delivery and customer response.";
}

function AccountStatementPanel({
  account,
  statements,
  activityEvents,
  onCreateStatement,
}: {
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  statements: ReturnType<typeof loadARRecoveryWorkspace>["statements"];
  activityEvents: ReturnType<typeof loadARRecoveryWorkspace>["recoveryActivityEvents"];
  onCreateStatement?: () => void;
}) {
  const accountStatements = useMemo(
    () => [...statements].sort((a, b) => getDateTime(b.updatedAt || b.createdAt) - getDateTime(a.updatedAt || a.createdAt)),
    [statements],
  );
  const [selectedFilter, setSelectedFilter] = useState<AccountStatementFilterKey>("all");
  const filteredStatements = useMemo(
    () => accountStatements.filter((statement) => statementMatchesFilter(statement, selectedFilter)),
    [accountStatements, selectedFilter],
  );
  const [selectedStatementId, setSelectedStatementId] = useState(accountStatements[0]?.id || "");
  const selectedStatement = filteredStatements.find((statement) => statement.id === selectedStatementId) || filteredStatements[0] || null;
  const selectedStatementHistoryEntries = selectedStatement
    ? buildAccountStatementHistoryEntries([selectedStatement], activityEvents.filter((event) => event.relatedRecordId === selectedStatement.id))
    : [];
  const generatedStatements = accountStatements.length;
  const sentStatements = accountStatements.filter((statement) => statement.status === "Sent").length;
  const deliveredStatements = accountStatements.filter((statement) => statement.deliveryStatus === "Delivered" || statement.status === "Delivered").length;
  const viewedStatements = accountStatements.filter((statement) => statement.openedStatus === "Opened" || statement.status === "Opened").length;
  const outstandingStatements = accountStatements.filter((statement) => !["Downloaded", "Cancelled"].includes(statement.status)).length;

  useEffect(() => {
    if (selectedStatementId && filteredStatements.some((statement) => statement.id === selectedStatementId)) return;
    setSelectedStatementId(filteredStatements[0]?.id || "");
  }, [filteredStatements, selectedStatementId]);

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[rgba(255,106,43,.12)] bg-[rgba(255,255,255,.025)] p-3">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Statement Production</p>
          <p className="mt-1 text-sm font-semibold text-[#CBD5E1]">Create, preview, generate, and track account statements without leaving the customer workspace.</p>
        </div>
        {onCreateStatement ? (
          <button
            type="button"
            onClick={onCreateStatement}
            className="focus-ring min-h-10 rounded-full border border-[rgba(255,106,43,.42)] bg-[rgba(255,106,43,.16)] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#F6F1E8]"
          >
            New Statement
          </button>
        ) : null}
      </div>

      <CompactMetricStrip
        items={[
          { label: "Generated", value: String(generatedStatements) },
          { label: "Sent", value: String(sentStatements) },
          { label: "Delivered", value: String(deliveredStatements) },
          { label: "Viewed", value: String(viewedStatements) },
          { label: "Outstanding", value: String(outstandingStatements) },
        ]}
      />

      <div className="rounded-xl border border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.025)] p-2">
        <p className="mb-2 text-[10px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Statement Filters</p>
        <div className="flex flex-wrap gap-2">
          {STATEMENT_FILTERS.map((filter) => {
            const active = selectedFilter === filter.key;
            return (
              <button
                key={filter.key}
                type="button"
                onClick={() => setSelectedFilter(filter.key)}
                className={`rounded-full border px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.14em] transition ${
                  active
                    ? "border-[rgba(255,106,43,.46)] bg-[rgba(255,106,43,.14)] text-[#F6F1E8]"
                    : "border-[rgba(34,224,208,.2)] bg-[rgba(255,255,255,.04)] text-[#CBD5E1] hover:border-[rgba(34,224,208,.34)]"
                }`}
              >
                {filter.label} {countStatementsForFilter(accountStatements, filter.key)}
              </button>
            );
          })}
        </div>
      </div>

      {accountStatements.length ? (
        <div className="grid gap-3 lg:grid-cols-[minmax(18rem,.9fr)_minmax(0,1.5fr)]">
          <section className="h-[34rem] overflow-hidden rounded-xl border border-[rgba(255,106,43,.12)] bg-[rgba(7,18,31,.72)]">
            <div className="flex items-center justify-between gap-3 border-b border-[rgba(255,106,43,.1)] px-3 py-2">
              <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Statement List</p>
              <span className="rounded-full border border-[rgba(34,224,208,.18)] px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#22E0D0]">
                {filteredStatements.length} shown
              </span>
            </div>
            <div className="h-[calc(34rem-2.75rem)] overflow-y-auto p-2">
              {filteredStatements.length ? (
                <div className="grid gap-2">
                  {filteredStatements.map((statement) => {
                    const active = selectedStatement?.id === statement.id;
                    return (
                      <button
                        key={statement.id}
                        type="button"
                        onClick={() => setSelectedStatementId(statement.id)}
                        className={`rounded-xl border p-2.5 text-left transition ${
                          active
                            ? "border-[rgba(255,106,43,.42)] bg-[rgba(255,106,43,.12)]"
                            : "border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.03)] hover:border-[rgba(34,224,208,.3)]"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{statement.statementNumber}</p>
                            <p className="mt-1 text-sm font-semibold leading-5 text-[#F6F1E8]">
                              {statement.dateSent || formatCapturedDate(statement.createdAt)} | {formatARRecoveryCurrency(statement.balanceCents)}
                            </p>
                          </div>
                          <span className={`shrink-0 rounded-full border px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${statementStatusBadgeClass(statement.status, statement.deliveryStatus)}`}>
                            {statement.status}
                          </span>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2 text-[11px] font-black uppercase tracking-[0.12em] text-[#CBD5E1]">
                          <span>{statement.deliveryStatus}</span>
                          <span>|</span>
                          <span>{statement.openedStatus}</span>
                          <span>|</span>
                          <span>{statement.collector || "Not Captured"}</span>
                        </div>
                        <p className="mt-2 overflow-hidden text-sm font-semibold leading-5 text-[#CBD5E1] [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
                          {statement.notes || `${statement.openInvoiceCount} open invoices represented.`}
                        </p>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <ManagerEmptyState
                  title="No statements match"
                  description={`No statements match the selected filter for ${account.customerName}.`}
                  action="Select another filter to review generated, sent, delivered, viewed, outstanding, or draft statements."
                />
              )}
            </div>
          </section>

          <section className="h-[34rem] overflow-hidden rounded-xl border border-[rgba(255,106,43,.12)] bg-[rgba(7,18,31,.72)]">
            <div className="flex items-center justify-between gap-3 border-b border-[rgba(255,106,43,.1)] px-3 py-2">
              <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Selected Statement Detail</p>
              {selectedStatement ? (
                <span className={`rounded-full border px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${statementStatusBadgeClass(selectedStatement.status, selectedStatement.deliveryStatus)}`}>
                  {selectedStatement.deliveryStatus}
                </span>
              ) : null}
            </div>
            <div className="h-[calc(34rem-2.75rem)] overflow-y-auto p-3">
              {selectedStatement ? (
                <div className="grid gap-3">
                  <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                    <StatementField label="Statement Number" value={selectedStatement.statementNumber} />
                    <StatementField label="Statement Date" value={selectedStatement.dateSent || formatCapturedDate(selectedStatement.createdAt)} />
                    <StatementField label="Statement Period" value="Not Captured" />
                    <StatementField label="Balance" value={formatARRecoveryCurrency(selectedStatement.balanceCents)} />
                    <StatementField label="Status" value={selectedStatement.status} />
                    <StatementField label="Delivery Status" value={selectedStatement.deliveryStatus} />
                    <StatementField label="Recipient" value={(selectedStatement as ARRecoveryStatementWorkflowRecord).sentRecipient || "Not Captured"} />
                    <StatementField label="Provider" value={(selectedStatement as ARRecoveryStatementWorkflowRecord).deliveryProvider || "Not Captured"} />
                    <StatementField label="Provider Reference" value={(selectedStatement as ARRecoveryStatementWorkflowRecord).providerMessageId || "Not Captured"} />
                    <StatementField label="Viewed Status" value={selectedStatement.openedStatus} />
                    <StatementField label="Collector" value={selectedStatement.collector || "Not Captured"} />
                    <StatementField label="Customer" value={selectedStatement.customerName || account.customerName || "Not Captured"} />
                  </div>

                  <div className="grid gap-2 xl:grid-cols-2">
                    <StatementField label="Outcome" value={selectedStatement.status === "Failed" ? (selectedStatement as ARRecoveryStatementWorkflowRecord).deliveryError || selectedStatement.notes || "Email delivery failed." : selectedStatement.notes || selectedStatement.deliveryStatus || "Not Captured"} />
                    <StatementField label="Next Action" value={buildStatementNextAction(selectedStatement)} />
                  </div>

                  <AuditStrip
                    createdBy={selectedStatement.sentBy || selectedStatement.collector || "Not Captured"}
                    createdDate={formatCapturedDate(selectedStatement.createdAt)}
                    createdTime={formatCapturedTime(selectedStatement.createdAt)}
                    updatedBy="Not Captured"
                    updatedDate={formatCapturedDate(selectedStatement.updatedAt)}
                    updatedTime={formatCapturedTime(selectedStatement.updatedAt)}
                  />

                  <div className="grid gap-2">
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-[#22E0D0]">Selected Statement History</p>
                    {selectedStatementHistoryEntries.length ? (
                      <OperationalHistoryRows entries={selectedStatementHistoryEntries} />
                    ) : (
                      <ManagerEmptyState
                        title="No statement history"
                        description="No history is available for this selected statement."
                        action="Statement activity will appear here when generated, sent, delivered, viewed, or updated."
                      />
                    )}
                  </div>
                </div>
              ) : (
                <ManagerEmptyState
                  title="No statement selected"
                  description="Select a statement to view its details."
                  action="Delivery status, outcome, next action, and audit information will appear here."
                />
              )}
            </div>
          </section>
        </div>
      ) : (
        <ManagerEmptyState
          title="No statements available"
          description={`No statements are available for ${account.customerName}.`}
          action="Statement records will appear here when they exist for this account."
        />
      )}
    </div>
  );
}

type AccountStatementRecord = ReturnType<typeof loadARRecoveryWorkspace>["statements"][number];
type AccountStatementFilterKey = "all" | "generated" | "sent" | "delivered" | "viewed" | "outstanding" | "draft";

const STATEMENT_FILTERS: Array<{ key: AccountStatementFilterKey; label: string }> = [
  { key: "all", label: "All" },
  { key: "generated", label: "Generated" },
  { key: "sent", label: "Sent" },
  { key: "delivered", label: "Delivered" },
  { key: "viewed", label: "Viewed" },
  { key: "outstanding", label: "Outstanding" },
  { key: "draft", label: "Draft" },
];

function statementMatchesFilter(statement: AccountStatementRecord, filter: AccountStatementFilterKey) {
  if (filter === "all") return true;
  if (filter === "generated") return statement.status === "Created" || statement.status === "Queued" || statement.status === "Generated" || statement.status === "Delivery Not Configured";
  if (filter === "sent") return statement.status === "Sent";
  if (filter === "delivered") return statement.status === "Delivered" || statement.deliveryStatus === "Delivered";
  if (filter === "viewed") return statement.status === "Viewed" || statement.status === "Opened" || statement.openedStatus === "Opened";
  if (filter === "outstanding") return !["Downloaded", "Cancelled", "Failed"].includes(statement.status);
  return statement.status === "Draft" || statement.status === "Created" || statement.status === "Queued";
}

function countStatementsForFilter(statements: AccountStatementRecord[], filter: AccountStatementFilterKey) {
  return statements.filter((statement) => statementMatchesFilter(statement, filter)).length;
}

function statementStatusBadgeClass(status: AccountStatementRecord["status"], deliveryStatus: AccountStatementRecord["deliveryStatus"]) {
  if (status === "Cancelled" || status === "Failed") return "border-[rgba(248,113,113,.34)] bg-[rgba(248,113,113,.12)] text-[#FCA5A5]";
  if (status === "Delivery Not Configured") return "border-[rgba(255,106,43,.34)] bg-[rgba(255,106,43,.12)] text-[#FF6A2B]";
  if (status === "Downloaded" || deliveryStatus === "Downloaded") return "border-[rgba(34,197,94,.32)] bg-[rgba(34,197,94,.12)] text-[#86EFAC]";
  if (status === "Delivered" || deliveryStatus === "Delivered" || status === "Viewed" || status === "Opened") return "border-[rgba(34,224,208,.3)] bg-[rgba(34,224,208,.1)] text-[#22E0D0]";
  if (status === "Sent" || status === "Generated") return "border-[rgba(255,106,43,.34)] bg-[rgba(255,106,43,.12)] text-[#FF6A2B]";
  return "border-[rgba(203,213,225,.18)] bg-[rgba(203,213,225,.08)] text-[#CBD5E1]";
}

function buildStatementNextAction(statement: AccountStatementRecord) {
  if (statement.status === "Delivery Not Configured") return "Configure email delivery before marking this statement sent.";
  if (statement.status === "Generated") return "Download, print, or configure delivery before marking sent.";
  if (statement.status === "Created" || statement.status === "Queued") return "Review and send statement.";
  if (statement.status === "Sent" || statement.deliveryStatus === "Pending") return "Confirm delivery status.";
  if (statement.deliveryStatus === "Delivered" && statement.openedStatus !== "Opened") return "Monitor for client/customer view.";
  if (statement.openedStatus === "Opened") return "Follow up on statement response.";
  if (statement.status === "Downloaded") return "Review downloaded statement outcome.";
  if (statement.status === "Cancelled") return "No next action captured.";
  return "Continue monitoring statement activity.";
}

function AccountCommunicationsPanel({
  account,
  communications,
  onSendCommunication,
  internalUserNames,
}: {
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  communications: ARRecoveryCommunicationRecord[];
  onSendCommunication: (draft: ARRecoveryCommunicationDraft) => void;
  internalUserNames: string[];
}) {
  const [selectedFilter, setSelectedFilter] = useState<AccountCommunicationFilterKey>("all");
  const [selectedConversationId, setSelectedConversationId] = useState("");
  const [newMessageForm, setNewMessageForm] = useState<ARRecoveryCommunicationFormState>(emptyCommunicationForm);
  const [replyForm, setReplyForm] = useState<ARRecoveryCommunicationFormState>(emptyCommunicationForm);
  const [newMessageError, setNewMessageError] = useState("");
  const [replyError, setReplyError] = useState("");
  const sortedCommunications = useMemo(
    () => [...communications].sort((a, b) => getDateTime(b.createdAt) - getDateTime(a.createdAt)),
    [communications],
  );
  const conversationSummaries = useMemo(() => buildCommunicationConversationSummaries(sortedCommunications), [sortedCommunications]);
  const filteredConversations = useMemo(
    () => conversationSummaries.filter((conversation) => communicationConversationMatchesFilter(conversation, selectedFilter)),
    [conversationSummaries, selectedFilter],
  );
  const selectedConversation =
    filteredConversations.find((conversation) => conversation.conversationId === selectedConversationId) ||
    filteredConversations[0] ||
    null;
  const selectedMessages = selectedConversation
    ? sortedCommunications
        .filter((message) => message.conversationId === selectedConversation.conversationId)
        .sort((a, b) => getDateTime(a.createdAt) - getDateTime(b.createdAt))
    : [];
  const latestSelectedMessage = selectedMessages[selectedMessages.length - 1] || null;

  useEffect(() => {
    if (!filteredConversations.length) {
      setSelectedConversationId("");
      return;
    }
    if (!filteredConversations.some((conversation) => conversation.conversationId === selectedConversationId)) {
      setSelectedConversationId(filteredConversations[0].conversationId);
    }
  }, [filteredConversations, selectedConversationId]);

  useEffect(() => {
    if (!latestSelectedMessage) {
      setReplyForm(emptyCommunicationForm);
      return;
    }

    setReplyForm({
      recipient: getSuggestedCommunicationReplyRecipient(latestSelectedMessage, account.assignedCollector || "System"),
      subject: latestSelectedMessage.subject,
      messageType: latestSelectedMessage.messageType,
      message: "",
      importance: latestSelectedMessage.importance,
    });
  }, [account.assignedCollector, latestSelectedMessage]);

  function submitNewMessage() {
    if (!newMessageForm.recipient.trim()) {
      setNewMessageError("Recipient is required.");
      return;
    }
    if (!newMessageForm.message.trim()) {
      setNewMessageError("Message is required.");
      return;
    }

    onSendCommunication(newMessageForm);
    setNewMessageForm(emptyCommunicationForm);
    setNewMessageError("");
  }

  function submitReply() {
    if (!selectedConversation || !latestSelectedMessage) return;
    if (!replyForm.recipient.trim()) {
      setReplyError("Recipient is required.");
      return;
    }
    if (!replyForm.message.trim()) {
      setReplyError("Reply message is required.");
      return;
    }

    onSendCommunication({
      ...replyForm,
      conversationId: selectedConversation.conversationId,
      parentMessageId: latestSelectedMessage.id,
      subject: replyForm.subject || latestSelectedMessage.subject,
    });
    setReplyForm((current) => ({ ...current, message: "" }));
    setReplyError("");
  }

  return (
    <div className="grid gap-3">
      <datalist id="ar-communications-users">
        {internalUserNames.map((name) => <option key={name} value={name} />)}
      </datalist>
      <CompactMetricStrip
        items={[
          { label: "Conversations", value: String(conversationSummaries.length) },
          { label: "Messages", value: String(sortedCommunications.length) },
          { label: "Manager Review", value: String(countCommunicationMessages(sortedCommunications, "manager_review")) },
          { label: "Escalations", value: String(countCommunicationMessages(sortedCommunications, "escalation")) },
          { label: "High Importance", value: String(countCommunicationMessages(sortedCommunications, "high")) },
        ]}
      />

      <section className="rounded-xl border border-[rgba(255,106,43,.14)] bg-[rgba(7,18,31,.72)] p-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF6A2B]">Start Internal Conversation</p>
            <p className="mt-1 text-sm font-semibold leading-6 text-[#CBD5E1]">
              Internal only. This does not create a recovery note or expose messages to clients.
            </p>
          </div>
          <span className="rounded-full border border-[rgba(34,224,208,.2)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#22E0D0]">
            Related Account: {account.customerNumber}
          </span>
        </div>

        <form
          className="mt-3 grid gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            submitNewMessage();
          }}
        >
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <CommunicationField label="Recipient">
              <input
                list="ar-communications-users"
                value={newMessageForm.recipient}
                onChange={(event) => setNewMessageForm((current) => ({ ...current, recipient: event.target.value }))}
                placeholder="Manager or teammate"
                className="focus-ring min-h-10 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
              />
            </CommunicationField>
            <CommunicationField label="Subject">
              <input
                value={newMessageForm.subject}
                onChange={(event) => setNewMessageForm((current) => ({ ...current, subject: event.target.value }))}
                placeholder="Optional"
                className="focus-ring min-h-10 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
              />
            </CommunicationField>
            <CommunicationField label="Message Type">
              <select
                value={newMessageForm.messageType}
                onChange={(event) => setNewMessageForm((current) => ({ ...current, messageType: event.target.value as ARRecoveryCommunicationType }))}
                className="focus-ring min-h-10 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
              >
                {COMMUNICATION_TYPE_OPTIONS.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
            </CommunicationField>
            <CommunicationField label="Importance">
              <select
                value={newMessageForm.importance}
                onChange={(event) => setNewMessageForm((current) => ({ ...current, importance: event.target.value as ARRecoveryCommunicationImportance }))}
                className="focus-ring min-h-10 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
              >
                <option value="Normal">Normal</option>
                <option value="High">High</option>
              </select>
            </CommunicationField>
          </div>
          <CommunicationField label="Message">
            <textarea
              value={newMessageForm.message}
              onChange={(event) => setNewMessageForm((current) => ({ ...current, message: event.target.value }))}
              rows={3}
              placeholder="Ask for review, share research, verify payment context, or coordinate documentation."
              className="focus-ring rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 py-2 text-sm font-semibold leading-6 text-[#F6F1E8]"
            />
          </CommunicationField>
          {newMessageError ? (
            <div className="rounded-xl border border-[rgba(248,113,113,.28)] bg-[rgba(248,113,113,.1)] px-3 py-2 text-sm font-semibold text-[#FCA5A5]">
              {newMessageError}
            </div>
          ) : null}
          <div className="flex justify-end">
            <button
              type="submit"
              className="focus-ring min-h-10 rounded-full border border-[rgba(255,106,43,.42)] bg-[rgba(255,106,43,.16)] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#F6F1E8]"
            >
              Send Message
            </button>
          </div>
        </form>
      </section>

      <div className="rounded-xl border border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.025)] p-2">
        <p className="mb-2 text-[10px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Communication Filters</p>
        <div className="flex flex-wrap gap-2">
          {COMMUNICATION_FILTERS.map((filter) => {
            const active = selectedFilter === filter.key;
            return (
              <button
                key={filter.key}
                type="button"
                onClick={() => setSelectedFilter(filter.key)}
                className={`rounded-full border px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.14em] transition ${
                  active
                    ? "border-[rgba(255,106,43,.46)] bg-[rgba(255,106,43,.14)] text-[#F6F1E8]"
                    : "border-[rgba(34,224,208,.2)] bg-[rgba(255,255,255,.04)] text-[#CBD5E1] hover:border-[rgba(34,224,208,.34)]"
                }`}
              >
                {filter.label} {countCommunicationConversations(conversationSummaries, filter.key)}
              </button>
            );
          })}
        </div>
      </div>

      {conversationSummaries.length ? (
        <div className="grid gap-3 lg:grid-cols-[minmax(16rem,38fr)_minmax(0,62fr)]">
          <section className="h-[34rem] overflow-hidden rounded-xl border border-[rgba(255,106,43,.12)] bg-[rgba(7,18,31,.72)]">
            <div className="flex items-center justify-between gap-3 border-b border-[rgba(255,106,43,.1)] px-3 py-2">
              <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Conversation List</p>
              <span className="rounded-full border border-[rgba(34,224,208,.18)] px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#22E0D0]">
                {filteredConversations.length} shown
              </span>
            </div>
            <div className="h-[calc(34rem-2.75rem)] overflow-y-auto p-2">
              {filteredConversations.length ? (
                <div className="grid gap-2">
                  {filteredConversations.map((conversation) => {
                    const active = selectedConversation?.conversationId === conversation.conversationId;
                    const latest = conversation.latestMessage;
                    return (
                      <button
                        key={conversation.conversationId}
                        type="button"
                        onClick={() => setSelectedConversationId(conversation.conversationId)}
                        className={`rounded-xl border p-2.5 text-left transition ${
                          active
                            ? "border-[rgba(255,106,43,.42)] bg-[rgba(255,106,43,.12)]"
                            : "border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.03)] hover:border-[rgba(34,224,208,.3)]"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="truncate text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">
                              {latest.subject || latest.messageType}
                            </p>
                            <p className="mt-1 text-sm font-semibold leading-5 text-[#F6F1E8]">
                              {formatCapturedDate(latest.createdAt)} | {formatCapturedTime(latest.createdAt)} | {latest.sender}
                            </p>
                          </div>
                          <span className={`shrink-0 rounded-full border px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${communicationBadgeClass(latest)}`}>
                            {latest.messageType}
                          </span>
                        </div>
                        <p className="mt-2 overflow-hidden text-sm font-semibold leading-5 text-[#CBD5E1] [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
                          {latest.message}
                        </p>
                        <p className="mt-2 text-[11px] font-black uppercase tracking-[0.12em] text-[#94A3B8]">
                          {conversation.messageCount} message{conversation.messageCount === 1 ? "" : "s"} | To: {latest.recipient}
                        </p>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <ManagerEmptyState
                  title="No communications match"
                  description={`No internal conversations match the selected filter for ${account.customerName}.`}
                  action="Select another filter or start a new internal conversation."
                />
              )}
            </div>
          </section>

          <section className="h-[34rem] overflow-hidden rounded-xl border border-[rgba(255,106,43,.12)] bg-[rgba(7,18,31,.72)]">
            <div className="flex items-center justify-between gap-3 border-b border-[rgba(255,106,43,.1)] px-3 py-2">
              <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Selected Conversation Detail</p>
              {latestSelectedMessage ? (
                <span className={`rounded-full border px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${communicationBadgeClass(latestSelectedMessage)}`}>
                  Internal Only
                </span>
              ) : null}
            </div>
            <div className="h-[calc(34rem-2.75rem)] overflow-y-auto p-3">
              {selectedConversation && latestSelectedMessage ? (
                <div className="grid gap-3">
                  <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                    <WorkspaceField label="Subject" value={latestSelectedMessage.subject || "Not Captured"} />
                    <WorkspaceField label="Message Type" value={latestSelectedMessage.messageType} />
                    <WorkspaceField label="Importance" value={latestSelectedMessage.importance} />
                    <WorkspaceField label="Sender" value={latestSelectedMessage.sender} />
                    <WorkspaceField label="Recipient" value={latestSelectedMessage.recipient} />
                    <WorkspaceField label="Related Account" value={`${account.customerNumber} | ${account.customerName}`} />
                  </div>

                  <div className="grid gap-2">
                    {selectedMessages.map((message) => (
                      <article key={message.id} className="rounded-xl border border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.03)] p-3">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <p className="text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{message.messageType}</p>
                            <p className="mt-1 text-sm font-semibold leading-5 text-[#F6F1E8]">
                              {formatCapturedDate(message.createdAt)} | {formatCapturedTime(message.createdAt)} | {message.sender} to {message.recipient}
                            </p>
                          </div>
                          <span className={`rounded-full border px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${communicationBadgeClass(message)}`}>
                            {message.importance}
                          </span>
                        </div>
                        <p className="mt-2 text-sm font-semibold leading-6 text-[#E8EEF4]">{message.message}</p>
                      </article>
                    ))}
                  </div>

                  <AuditStrip
                    createdBy={latestSelectedMessage.sender}
                    createdDate={formatCapturedDate(latestSelectedMessage.createdAt)}
                    createdTime={formatCapturedTime(latestSelectedMessage.createdAt)}
                    updatedBy={latestSelectedMessage.sender}
                    updatedDate={formatCapturedDate(latestSelectedMessage.createdAt)}
                    updatedTime={formatCapturedTime(latestSelectedMessage.createdAt)}
                  />

                  <form
                    className="rounded-xl border border-[rgba(255,106,43,.14)] bg-[rgba(255,255,255,.025)] p-3"
                    onSubmit={(event) => {
                      event.preventDefault();
                      submitReply();
                    }}
                  >
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Reply Internally</p>
                    <div className="mt-3 grid gap-3 md:grid-cols-3">
                      <CommunicationField label="Recipient">
                        <input
                          list="ar-communications-users"
                          value={replyForm.recipient}
                          onChange={(event) => setReplyForm((current) => ({ ...current, recipient: event.target.value }))}
                          className="focus-ring min-h-10 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                        />
                      </CommunicationField>
                      <CommunicationField label="Message Type">
                        <select
                          value={replyForm.messageType}
                          onChange={(event) => setReplyForm((current) => ({ ...current, messageType: event.target.value as ARRecoveryCommunicationType }))}
                          className="focus-ring min-h-10 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                        >
                          {COMMUNICATION_TYPE_OPTIONS.map((type) => <option key={type} value={type}>{type}</option>)}
                        </select>
                      </CommunicationField>
                      <CommunicationField label="Importance">
                        <select
                          value={replyForm.importance}
                          onChange={(event) => setReplyForm((current) => ({ ...current, importance: event.target.value as ARRecoveryCommunicationImportance }))}
                          className="focus-ring min-h-10 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                        >
                          <option value="Normal">Normal</option>
                          <option value="High">High</option>
                        </select>
                      </CommunicationField>
                    </div>
                    <CommunicationField label="Reply Message">
                      <textarea
                        value={replyForm.message}
                        onChange={(event) => setReplyForm((current) => ({ ...current, message: event.target.value }))}
                        rows={3}
                        className="focus-ring mt-3 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 py-2 text-sm font-semibold leading-6 text-[#F6F1E8]"
                      />
                    </CommunicationField>
                    {replyError ? (
                      <div className="mt-3 rounded-xl border border-[rgba(248,113,113,.28)] bg-[rgba(248,113,113,.1)] px-3 py-2 text-sm font-semibold text-[#FCA5A5]">
                        {replyError}
                      </div>
                    ) : null}
                    <div className="mt-3 flex justify-end">
                      <button
                        type="submit"
                        className="focus-ring min-h-10 rounded-full border border-[rgba(255,106,43,.42)] bg-[rgba(255,106,43,.16)] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#F6F1E8]"
                      >
                        Send Reply
                      </button>
                    </div>
                  </form>
                </div>
              ) : (
                <ManagerEmptyState
                  title="No conversation selected"
                  description="No internal conversation matches the current filter."
                  action="Start a new conversation or select another filter to review account-based team communications."
                />
              )}
            </div>
          </section>
        </div>
      ) : (
        <ManagerEmptyState
          title="No internal communications yet"
          description={`No internal AR team conversations have been sent for ${account.customerName}.`}
          action="Start a conversation above to coordinate manager review, documentation, research, or payment verification without adding a recovery note."
        />
      )}
    </div>
  );
}

function CommunicationField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <label className="grid gap-1.5">
      <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">{label}</span>
      {children}
    </label>
  );
}

type AccountCommunicationFilterKey =
  | "all"
  | "general"
  | "manager_review"
  | "escalation"
  | "customer_research"
  | "documentation_request"
  | "payment_verification"
  | "internal_question"
  | "high";

type AccountCommunicationConversationSummary = {
  conversationId: string;
  latestMessage: ARRecoveryCommunicationRecord;
  messageCount: number;
};

const COMMUNICATION_TYPE_OPTIONS: ARRecoveryCommunicationType[] = [
  "General",
  "Manager Review",
  "Escalation",
  "Customer Research",
  "Documentation Request",
  "Payment Verification",
  "Internal Question",
];

const COMMUNICATION_FILTERS: Array<{ key: AccountCommunicationFilterKey; label: string }> = [
  { key: "all", label: "All" },
  { key: "general", label: "General" },
  { key: "manager_review", label: "Manager Review" },
  { key: "escalation", label: "Escalation" },
  { key: "customer_research", label: "Research" },
  { key: "documentation_request", label: "Documentation" },
  { key: "payment_verification", label: "Payment Verification" },
  { key: "internal_question", label: "Internal Question" },
  { key: "high", label: "High Importance" },
];

function buildCommunicationConversationSummaries(messages: ARRecoveryCommunicationRecord[]): AccountCommunicationConversationSummary[] {
  const conversations = new Map<string, ARRecoveryCommunicationRecord[]>();

  messages.forEach((message) => {
    const existing = conversations.get(message.conversationId) || [];
    conversations.set(message.conversationId, [...existing, message]);
  });

  return Array.from(conversations.entries())
    .map(([conversationId, conversationMessages]) => {
      const sorted = [...conversationMessages].sort((a, b) => getDateTime(b.createdAt) - getDateTime(a.createdAt));
      return {
        conversationId,
        latestMessage: sorted[0],
        messageCount: conversationMessages.length,
      };
    })
    .sort((a, b) => getDateTime(b.latestMessage.createdAt) - getDateTime(a.latestMessage.createdAt));
}

function communicationConversationMatchesFilter(conversation: AccountCommunicationConversationSummary, filter: AccountCommunicationFilterKey) {
  if (filter === "all") return true;
  if (filter === "high") return conversation.latestMessage.importance === "High";
  return normalizeCommunicationType(conversation.latestMessage.messageType) === filter;
}

function countCommunicationConversations(conversations: AccountCommunicationConversationSummary[], filter: AccountCommunicationFilterKey) {
  return conversations.filter((conversation) => communicationConversationMatchesFilter(conversation, filter)).length;
}

function countCommunicationMessages(messages: ARRecoveryCommunicationRecord[], filter: AccountCommunicationFilterKey) {
  if (filter === "high") return messages.filter((message) => message.importance === "High").length;
  return messages.filter((message) => normalizeCommunicationType(message.messageType) === filter).length;
}

function normalizeCommunicationType(type: ARRecoveryCommunicationType): AccountCommunicationFilterKey {
  if (type === "Manager Review") return "manager_review";
  if (type === "Escalation") return "escalation";
  if (type === "Customer Research") return "customer_research";
  if (type === "Documentation Request") return "documentation_request";
  if (type === "Payment Verification") return "payment_verification";
  if (type === "Internal Question") return "internal_question";
  return "general";
}

function isCustomerFacingCommunication(
  message: ARRecoveryCommunicationRecord,
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>,
) {
  const customerEmail = account.contactEmail.toLowerCase();
  const customerName = account.customerName.toLowerCase();
  const contactName = account.contactName.toLowerCase();
  const sender = message.sender.toLowerCase();
  const recipient = message.recipient.toLowerCase();
  if (message.messageType === "Internal Question" || message.messageType === "Manager Review") return false;
  if (customerEmail && (recipient.includes(customerEmail) || sender.includes(customerEmail))) return true;
  return Boolean(
    customerName && (recipient.includes(customerName) || sender.includes(customerName)) ||
    contactName && (recipient.includes(contactName) || sender.includes(contactName)),
  );
}

function getSuggestedCommunicationReplyRecipient(message: ARRecoveryCommunicationRecord, currentSender: string) {
  if (message.sender && message.sender !== currentSender) return message.sender;
  return message.recipient || "";
}

function communicationBadgeClass(message: ARRecoveryCommunicationRecord) {
  if (message.importance === "High" || message.messageType === "Escalation") return "border-[rgba(248,113,113,.34)] bg-[rgba(248,113,113,.12)] text-[#FCA5A5]";
  if (message.messageType === "Manager Review" || message.messageType === "Documentation Request") return "border-[rgba(255,106,43,.34)] bg-[rgba(255,106,43,.12)] text-[#FF6A2B]";
  return "border-[rgba(34,224,208,.3)] bg-[rgba(34,224,208,.1)] text-[#22E0D0]";
}

function AccountTasksPanel({
  account,
  tasks,
  promises,
  letters,
  statements,
  onCreateTask,
  onCompleteTask,
  onAddTaskComment,
  onRequestDocument,
  internalUserNames,
  createTaskLaunchKey = "",
}: {
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  tasks: ARRecoveryTaskAssignmentRecord[];
  promises: ReturnType<typeof loadARRecoveryWorkspace>["promises"];
  letters: ReturnType<typeof loadARRecoveryWorkspace>["collectionLetters"];
  statements: ReturnType<typeof loadARRecoveryWorkspace>["statements"];
  onCreateTask: (form: ARRecoveryTaskAssignmentFormState) => void;
  onCompleteTask: (taskId: string, completion: ARRecoveryTaskCompletionFormState) => void;
  onAddTaskComment: (taskId: string, commentText: string) => void;
  onRequestDocument?: (task: ARRecoveryTaskAssignmentRecord) => void;
  internalUserNames: string[];
  createTaskLaunchKey?: string;
}) {
  const invoices = useMemo(() => buildInvoicePlaceholders(account), [account]);
  const sortedTasks = useMemo(() => [...tasks].sort((a, b) => getDateTime(b.updatedAt || b.createdAt) - getDateTime(a.updatedAt || a.createdAt)), [tasks]);
  const [selectedFilter, setSelectedFilter] = useState<AccountTaskFilterKey>("all");
  const [selectedTaskId, setSelectedTaskId] = useState(sortedTasks[0]?.id || "");
  const [taskForm, setTaskForm] = useState<ARRecoveryTaskAssignmentFormState>(emptyTaskAssignmentForm);
  const [taskFormError, setTaskFormError] = useState("");
  const [completionForm, setCompletionForm] = useState<ARRecoveryTaskCompletionFormState>(emptyTaskCompletionForm);
  const [completionError, setCompletionError] = useState("");
  const [commentText, setCommentText] = useState("");
  const [commentError, setCommentError] = useState("");
  const taskAssignmentFormRef = useRef<HTMLFormElement | null>(null);
  const taskTitleInputRef = useRef<HTMLInputElement | null>(null);
  const relatedLetterStatementValue = taskForm.relatedLetter
    ? `letter:${taskForm.relatedLetter}`
    : taskForm.relatedStatement
      ? `statement:${taskForm.relatedStatement}`
      : "";
  const filteredTasks = useMemo(() => sortedTasks.filter((task) => taskMatchesFilter(task, selectedFilter)), [sortedTasks, selectedFilter]);
  const selectedTask = filteredTasks.find((task) => task.id === selectedTaskId) || filteredTasks[0] || null;

  useEffect(() => {
    if (!filteredTasks.length) {
      setSelectedTaskId("");
      return;
    }
    if (!filteredTasks.some((task) => task.id === selectedTaskId)) {
      setSelectedTaskId(filteredTasks[0].id);
    }
  }, [filteredTasks, selectedTaskId]);

  useEffect(() => {
    if (!createTaskLaunchKey) return;
    setTaskForm((current) => ({
      ...current,
      assignedTo: current.assignedTo || account.assignedCollector || "",
    }));
    setTaskFormError("");
    window.setTimeout(() => {
      taskAssignmentFormRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
      taskTitleInputRef.current?.focus({ preventScroll: true });
    }, 0);
  }, [account.assignedCollector, createTaskLaunchKey]);

  function submitTask() {
    if (!taskForm.taskTitle.trim()) {
      setTaskFormError("Task title is required.");
      return;
    }
    if (!taskForm.category) {
      setTaskFormError("Task category is required.");
      return;
    }
    if (!taskForm.assignedTo.trim()) {
      setTaskFormError("Assigned To is required.");
      return;
    }
    if (!taskForm.dueDate) {
      setTaskFormError("Due date is required.");
      return;
    }
    if (!taskForm.description.trim()) {
      setTaskFormError("Description is required.");
      return;
    }

    onCreateTask(taskForm);
    setTaskForm(emptyTaskAssignmentForm);
    setTaskFormError("");
  }

  function submitCompletion() {
    if (!selectedTask) return;
    if (!completionForm.completionNotes.trim()) {
      setCompletionError("Completion notes are required.");
      return;
    }
    if (!completionForm.outcome.trim()) {
      setCompletionError("Outcome is required.");
      return;
    }

    onCompleteTask(selectedTask.id, completionForm);
    setCompletionForm(emptyTaskCompletionForm);
    setCompletionError("");
  }

  function submitComment() {
    if (!selectedTask) return;
    if (!commentText.trim()) {
      setCommentError("Task comment is required.");
      return;
    }

    onAddTaskComment(selectedTask.id, commentText);
    setCommentText("");
    setCommentError("");
  }

  return (
    <div className="grid gap-3">
      <datalist id="ar-task-users">
        {internalUserNames.map((name) => <option key={name} value={name} />)}
      </datalist>
      <CompactMetricStrip
        items={[
          { label: "Tasks", value: String(tasks.length) },
          { label: "Open", value: String(countTasksForFilter(tasks, "open")) },
          { label: "Due Today", value: String(countTasksForFilter(tasks, "due_today")) },
          { label: "Overdue", value: String(countTasksForFilter(tasks, "overdue")) },
          { label: "Completed", value: String(countTasksForFilter(tasks, "completed")) },
        ]}
      />

      <section className="rounded-xl border border-[rgba(255,106,43,.14)] bg-[rgba(7,18,31,.72)] p-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF6A2B]">Assign Operational Task</p>
            <p className="mt-1 text-sm font-semibold leading-6 text-[#CBD5E1]">
              Account-scoped work assignment for AR users. This is separate from Notes and Communications.
            </p>
          </div>
          <span className="rounded-full border border-[rgba(34,224,208,.2)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#22E0D0]">
            Related Account: {account.customerNumber}
          </span>
        </div>

        <form
          ref={taskAssignmentFormRef}
          className="mt-3 grid gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            submitTask();
          }}
        >
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <CommunicationField label="Task Title">
              <input
                ref={taskTitleInputRef}
                value={taskForm.taskTitle}
                onChange={(event) => setTaskForm((current) => ({ ...current, taskTitle: event.target.value }))}
                placeholder="Verify remittance details"
                className="focus-ring min-h-10 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
              />
            </CommunicationField>
            <CommunicationField label="Task Category">
              <select
                value={taskForm.category}
                onChange={(event) => setTaskForm((current) => ({ ...current, category: event.target.value as ARRecoveryTaskAssignmentFormState["category"] }))}
                className="focus-ring min-h-10 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
              >
                <option value="">Select category</option>
                {TASK_CATEGORY_OPTIONS.map((category) => <option key={category} value={category}>{category}</option>)}
              </select>
            </CommunicationField>
            <CommunicationField label="Assigned To">
              <input
                list="ar-task-users"
                value={taskForm.assignedTo}
                onChange={(event) => setTaskForm((current) => ({ ...current, assignedTo: event.target.value }))}
                placeholder="Manager or AR teammate"
                className="focus-ring min-h-10 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
              />
            </CommunicationField>
            <CommunicationField label="Priority">
              <select
                value={taskForm.priority}
                onChange={(event) => setTaskForm((current) => ({ ...current, priority: event.target.value as ARRecoveryTaskPriority }))}
                className="focus-ring min-h-10 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
              >
                {TASK_PRIORITY_OPTIONS.map((priority) => <option key={priority} value={priority}>{priority}</option>)}
              </select>
            </CommunicationField>
            <CommunicationField label="Due Date">
              <input
                type="date"
                value={taskForm.dueDate}
                onChange={(event) => setTaskForm((current) => ({ ...current, dueDate: event.target.value }))}
                className="focus-ring min-h-10 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
              />
            </CommunicationField>
            <CommunicationField label="Related Invoice">
              <select
                value={taskForm.relatedInvoice}
                onChange={(event) => setTaskForm((current) => ({ ...current, relatedInvoice: event.target.value }))}
                className="focus-ring min-h-10 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
              >
                <option value="">Optional</option>
                {invoices.map((invoice) => <option key={invoice.id} value={invoice.invoiceNumber}>{invoice.invoiceNumber}</option>)}
              </select>
            </CommunicationField>
            <CommunicationField label="Related Promise">
              <select
                value={taskForm.relatedPromise}
                onChange={(event) => setTaskForm((current) => ({ ...current, relatedPromise: event.target.value }))}
                className="focus-ring min-h-10 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
              >
                <option value="">Optional</option>
                {promises.map((promise) => <option key={promise.id} value={promise.id}>{formatARRecoveryCurrency(promise.amountCents)} | {promise.status}</option>)}
              </select>
            </CommunicationField>
            <CommunicationField label="Related Letter/Statement">
              <select
                value={relatedLetterStatementValue}
                onChange={(event) => {
                  const value = event.target.value;
                  setTaskForm((current) => ({
                    ...current,
                    relatedLetter: value.startsWith("letter:") ? value.replace("letter:", "") : "",
                    relatedStatement: value.startsWith("statement:") ? value.replace("statement:", "") : "",
                  }));
                }}
                className="focus-ring min-h-10 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
              >
                <option value="">Optional</option>
                <option value="" disabled>Letters</option>
                {letters.length ? (
                  letters.map((letter) => <option key={letter.id} value={`letter:${letter.id}`}>{formatRelatedLetterLabel(letter)}</option>)
                ) : (
                  <option value="" disabled>No Letters Available</option>
                )}
                <option value="" disabled>Statements</option>
                {statements.length ? (
                  statements.map((statement) => <option key={statement.id} value={`statement:${statement.id}`}>{formatRelatedStatementLabel(statement)}</option>)
                ) : (
                  <option value="" disabled>No Statements Available</option>
                )}
              </select>
            </CommunicationField>
          </div>
          <CommunicationField label="Description">
            <textarea
              value={taskForm.description}
              onChange={(event) => setTaskForm((current) => ({ ...current, description: event.target.value }))}
              rows={3}
              placeholder="Describe the exact AR work that needs to be completed."
              className="focus-ring rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 py-2 text-sm font-semibold leading-6 text-[#F6F1E8]"
            />
          </CommunicationField>
          {taskFormError ? (
            <div className="rounded-xl border border-[rgba(248,113,113,.28)] bg-[rgba(248,113,113,.1)] px-3 py-2 text-sm font-semibold text-[#FCA5A5]">
              {taskFormError}
            </div>
          ) : null}
          <div className="flex justify-end">
            <button
              type="submit"
              className="focus-ring min-h-10 rounded-full border border-[rgba(255,106,43,.42)] bg-[rgba(255,106,43,.16)] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#F6F1E8]"
            >
              Assign Task
            </button>
          </div>
        </form>
      </section>

      <div className="rounded-xl border border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.025)] p-2">
        <p className="mb-2 text-[10px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Task Filters</p>
        <div className="flex flex-wrap gap-2">
          {TASK_FILTERS.map((filter) => {
            const active = selectedFilter === filter.key;
            return (
              <button
                key={filter.key}
                type="button"
                onClick={() => setSelectedFilter(filter.key)}
                className={`rounded-full border px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.14em] transition ${
                  active
                    ? "border-[rgba(255,106,43,.46)] bg-[rgba(255,106,43,.14)] text-[#F6F1E8]"
                    : "border-[rgba(34,224,208,.2)] bg-[rgba(255,255,255,.04)] text-[#CBD5E1] hover:border-[rgba(34,224,208,.34)]"
                }`}
              >
                {filter.label} {countTasksForFilter(tasks, filter.key)}
              </button>
            );
          })}
        </div>
      </div>

      {tasks.length ? (
        <div className="grid gap-3 lg:grid-cols-[minmax(16rem,38fr)_minmax(0,62fr)]">
          <section className="h-[34rem] overflow-hidden rounded-xl border border-[rgba(255,106,43,.12)] bg-[rgba(7,18,31,.72)]">
            <div className="flex items-center justify-between gap-3 border-b border-[rgba(255,106,43,.1)] px-3 py-2">
              <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Task List</p>
              <span className="rounded-full border border-[rgba(34,224,208,.18)] px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#22E0D0]">
                {filteredTasks.length} shown
              </span>
            </div>
            <div className="h-[calc(34rem-2.75rem)] overflow-y-auto p-2">
              {filteredTasks.length ? (
                <div className="grid gap-2">
                  {filteredTasks.map((task) => {
                    const active = selectedTask?.id === task.id;
                    return (
                      <button
                        key={task.id}
                        type="button"
                        onClick={() => setSelectedTaskId(task.id)}
                        className={`rounded-xl border p-2.5 text-left transition ${
                          active
                            ? "border-[rgba(255,106,43,.42)] bg-[rgba(255,106,43,.12)]"
                            : "border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.03)] hover:border-[rgba(34,224,208,.3)]"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="truncate text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{task.taskTitle}</p>
                            <p className="mt-1 text-sm font-semibold leading-5 text-[#F6F1E8]">
                              {formatCapturedDate(task.dueDate)} | {task.assignedTo}
                            </p>
                          </div>
                          <span className={`shrink-0 rounded-full border px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${taskStatusBadgeClass(task)}`}>
                            {task.status}
                          </span>
                        </div>
                        <p className="mt-2 overflow-hidden text-sm font-semibold leading-5 text-[#CBD5E1] [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
                          {task.category} | {task.priority} | {task.description}
                        </p>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <ManagerEmptyState
                  title="No tasks match"
                  description={`No operational assignments match the selected filter for ${account.customerName}.`}
                  action="Select another filter or assign a new task."
                />
              )}
            </div>
          </section>

          <section className="h-[34rem] overflow-hidden rounded-xl border border-[rgba(255,106,43,.12)] bg-[rgba(7,18,31,.72)]">
            <div className="flex items-center justify-between gap-3 border-b border-[rgba(255,106,43,.1)] px-3 py-2">
              <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Selected Task Detail</p>
              {selectedTask ? (
                <span className={`rounded-full border px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${taskStatusBadgeClass(selectedTask)}`}>
                  {selectedTask.priority}
                </span>
              ) : null}
            </div>
            <div className="h-[calc(34rem-2.75rem)] overflow-y-auto p-3">
              {selectedTask ? (
                <div className="grid gap-3">
                  <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                    <WorkspaceField label="Task Title" value={selectedTask.taskTitle} />
                    <WorkspaceField label="Category" value={selectedTask.category} />
                    <WorkspaceField label="Status" value={selectedTask.status} />
                    <WorkspaceField label="Assigned To" value={selectedTask.assignedTo} />
                    <WorkspaceField label="Assigned By" value={selectedTask.assignedBy} />
                    <WorkspaceField label="Due Date" value={formatCapturedDate(selectedTask.dueDate)} />
                    <WorkspaceField label="Priority" value={selectedTask.priority} />
                    <WorkspaceField label="Related Invoice" value={selectedTask.relatedInvoice || "Not Captured"} />
                    <WorkspaceField label="Related Promise" value={selectedTask.relatedPromise || "Not Captured"} />
                    <WorkspaceField label="Related Letter" value={resolveRelatedLetterLabel(selectedTask.relatedLetter, letters)} />
                    <WorkspaceField label="Related Statement" value={resolveRelatedStatementLabel(selectedTask.relatedStatement, statements)} />
                    <WorkspaceField label="Related Account" value={`${account.customerNumber} | ${account.customerName}`} />
                  </div>

                  <WorkspaceField label="Description" value={selectedTask.description} />

                  {onRequestDocument ? (
                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.025)] p-3">
                      <div>
                        <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Task Document Support</p>
                        <p className="mt-1 text-sm font-semibold leading-6 text-[#CBD5E1]">
                          Request supporting documents without leaving this task or customer account.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => onRequestDocument(selectedTask)}
                        className="focus-ring min-h-10 rounded-full border border-[rgba(34,224,208,.28)] bg-[rgba(34,224,208,.1)] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#22E0D0]"
                      >
                        Request Document
                      </button>
                    </div>
                  ) : null}

                  <AuditStrip
                    createdBy={selectedTask.assignedBy}
                    createdDate={formatCapturedDate(selectedTask.createdAt)}
                    createdTime={formatCapturedTime(selectedTask.createdAt)}
                    updatedBy={selectedTask.completedBy || selectedTask.assignedBy}
                    updatedDate={formatCapturedDate(selectedTask.updatedAt)}
                    updatedTime={formatCapturedTime(selectedTask.updatedAt)}
                  />

                  {selectedTask.status === "Completed" ? (
                    <div className="grid gap-2 xl:grid-cols-2">
                      <WorkspaceField label="Completed By" value={selectedTask.completedBy || "Not Captured"} />
                      <WorkspaceField label="Completed At" value={`${formatCapturedDate(selectedTask.completedAt)} | ${formatCapturedTime(selectedTask.completedAt)}`} />
                      <WorkspaceField label="Outcome" value={selectedTask.outcome || "Not Captured"} />
                      <WorkspaceField label="Next Action" value={selectedTask.nextAction || "Not Captured"} />
                      <WorkspaceField label="Completion Notes" value={selectedTask.completionNotes || "Not Captured"} />
                    </div>
                  ) : (
                    <form
                      className="rounded-xl border border-[rgba(255,106,43,.14)] bg-[rgba(255,255,255,.025)] p-3"
                      onSubmit={(event) => {
                        event.preventDefault();
                        submitCompletion();
                      }}
                    >
                      <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Complete Task</p>
                      <div className="mt-3 grid gap-3 xl:grid-cols-2">
                        <CommunicationField label="Outcome">
                          <input
                            value={completionForm.outcome}
                            onChange={(event) => setCompletionForm((current) => ({ ...current, outcome: event.target.value }))}
                            placeholder="Verification completed, issue found, etc."
                            className="focus-ring min-h-10 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                          />
                        </CommunicationField>
                        <CommunicationField label="Next Action">
                          <input
                            value={completionForm.nextAction}
                            onChange={(event) => setCompletionForm((current) => ({ ...current, nextAction: event.target.value }))}
                            placeholder="Optional next action"
                            className="focus-ring min-h-10 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                          />
                        </CommunicationField>
                      </div>
                      <CommunicationField label="Completion Notes">
                        <textarea
                          value={completionForm.completionNotes}
                          onChange={(event) => setCompletionForm((current) => ({ ...current, completionNotes: event.target.value }))}
                          rows={3}
                          placeholder="Capture what was completed and what changed."
                          className="focus-ring mt-3 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 py-2 text-sm font-semibold leading-6 text-[#F6F1E8]"
                        />
                      </CommunicationField>
                      {completionError ? (
                        <div className="mt-3 rounded-xl border border-[rgba(248,113,113,.28)] bg-[rgba(248,113,113,.1)] px-3 py-2 text-sm font-semibold text-[#FCA5A5]">
                          {completionError}
                        </div>
                      ) : null}
                      <div className="mt-3 flex justify-end">
                        <button
                          type="submit"
                          className="focus-ring min-h-10 rounded-full border border-[rgba(255,106,43,.42)] bg-[rgba(255,106,43,.16)] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#F6F1E8]"
                        >
                          Mark Completed
                        </button>
                      </div>
                    </form>
                  )}

                  <form
                    className="rounded-xl border border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.025)] p-3"
                    onSubmit={(event) => {
                      event.preventDefault();
                      submitComment();
                    }}
                  >
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Task Discussion</p>
                    <div className="mt-3 grid gap-2">
                      {selectedTask.comments.length ? selectedTask.comments.map((comment) => (
                        <article key={comment.id} className="rounded-xl border border-[rgba(34,224,208,.1)] bg-[rgba(255,255,255,.03)] p-2.5">
                          <p className="text-xs font-black uppercase tracking-[0.14em] text-[#22E0D0]">{comment.author}</p>
                          <p className="mt-1 text-sm font-semibold leading-5 text-[#F6F1E8]">{formatCapturedDate(comment.createdAt)} | {formatCapturedTime(comment.createdAt)}</p>
                          <p className="mt-2 text-sm font-semibold leading-6 text-[#E8EEF4]">{comment.comment}</p>
                        </article>
                      )) : (
                        <ManagerEmptyState
                          title="No task comments"
                          description="No task-specific comments are attached to this assignment yet."
                          action="Use comments for assignment-specific discussion. Use Communications for general collaboration."
                        />
                      )}
                    </div>
                    <CommunicationField label="Add Task Comment">
                      <textarea
                        value={commentText}
                        onChange={(event) => setCommentText(event.target.value)}
                        rows={2}
                        placeholder="Add assignment-specific context."
                        className="focus-ring mt-3 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 py-2 text-sm font-semibold leading-6 text-[#F6F1E8]"
                      />
                    </CommunicationField>
                    {commentError ? (
                      <div className="mt-3 rounded-xl border border-[rgba(248,113,113,.28)] bg-[rgba(248,113,113,.1)] px-3 py-2 text-sm font-semibold text-[#FCA5A5]">
                        {commentError}
                      </div>
                    ) : null}
                    <div className="mt-3 flex justify-end">
                      <button
                        type="submit"
                        className="focus-ring min-h-10 rounded-full border border-[rgba(34,224,208,.3)] bg-[rgba(34,224,208,.1)] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#F6F1E8]"
                      >
                        Add Comment
                      </button>
                    </div>
                  </form>
                </div>
              ) : (
                <ManagerEmptyState
                  title="No task selected"
                  description="No operational task matches the current filter."
                  action="Assign a task or select another filter to review account-based work."
                />
              )}
            </div>
          </section>
        </div>
      ) : (
        <ManagerEmptyState
          title="No operational tasks yet"
          description={`No AR team assignments have been created for ${account.customerName}.`}
          action="Assign a task above to make verification, research, review, or follow-up work accountable."
        />
      )}
    </div>
  );
}

type AccountTaskFilterKey = "all" | "open" | "in_progress" | "waiting" | "due_today" | "overdue" | "completed" | "cancelled" | "critical";

const TASK_CATEGORY_OPTIONS: ARRecoveryTaskCategory[] = [
  "Payment Verification",
  "Research Account",
  "Verify POD",
  "Request Documentation",
  "Manager Review",
  "Credit Review",
  "Dispute Assistance",
  "Customer Follow-Up",
  "Internal Review",
  "Custom",
];

const TASK_PRIORITY_OPTIONS: ARRecoveryTaskPriority[] = ["Low", "Normal", "High", "Critical"];

const TASK_FILTERS: Array<{ key: AccountTaskFilterKey; label: string }> = [
  { key: "all", label: "All" },
  { key: "open", label: "Open" },
  { key: "in_progress", label: "In Progress" },
  { key: "waiting", label: "Waiting" },
  { key: "due_today", label: "Due Today" },
  { key: "overdue", label: "Overdue" },
  { key: "completed", label: "Completed" },
  { key: "cancelled", label: "Cancelled" },
  { key: "critical", label: "Critical" },
];

function formatRelatedLetterLabel(letter: ReturnType<typeof loadARRecoveryWorkspace>["collectionLetters"][number]) {
  const date = letter.dateSent || formatCapturedDate(letter.createdAt);
  return `Letter | ${letter.template} | ${date}`;
}

function formatRelatedStatementLabel(statement: ReturnType<typeof loadARRecoveryWorkspace>["statements"][number]) {
  const date = statement.dateSent || formatCapturedDate(statement.createdAt);
  return `Statement | ${statement.statementNumber} | ${date} | ${formatARRecoveryCurrency(statement.balanceCents)}`;
}

function resolveRelatedLetterLabel(letterId: string, letters: ReturnType<typeof loadARRecoveryWorkspace>["collectionLetters"]) {
  if (!letterId) return "Not Captured";
  const letter = letters.find((item) => item.id === letterId);
  return letter ? formatRelatedLetterLabel(letter) : letterId;
}

function resolveRelatedStatementLabel(statementId: string, statements: ReturnType<typeof loadARRecoveryWorkspace>["statements"]) {
  if (!statementId) return "Not Captured";
  const statement = statements.find((item) => item.id === statementId);
  return statement ? formatRelatedStatementLabel(statement) : statementId;
}

function taskMatchesFilter(task: ARRecoveryTaskAssignmentRecord, filter: AccountTaskFilterKey) {
  if (filter === "all") return true;
  if (filter === "open") return task.status === "Open";
  if (filter === "in_progress") return task.status === "In Progress";
  if (filter === "waiting") return task.status === "Waiting";
  if (filter === "completed") return task.status === "Completed";
  if (filter === "cancelled") return task.status === "Cancelled";
  if (filter === "critical") return task.priority === "Critical";
  if (filter === "due_today") return isToday(task.dueDate) && task.status !== "Completed" && task.status !== "Cancelled";
  if (filter === "overdue") return getDateTime(task.dueDate) < startOfTodayTime() && task.status !== "Completed" && task.status !== "Cancelled";
  return true;
}

function countTasksForFilter(tasks: ARRecoveryTaskAssignmentRecord[], filter: AccountTaskFilterKey) {
  return tasks.filter((task) => taskMatchesFilter(task, filter)).length;
}

function taskStatusBadgeClass(task: ARRecoveryTaskAssignmentRecord) {
  if (task.status === "Cancelled") return "border-[rgba(148,163,184,.24)] bg-[rgba(148,163,184,.1)] text-[#CBD5E1]";
  if (task.status === "Completed") return "border-[rgba(34,197,94,.32)] bg-[rgba(34,197,94,.12)] text-[#86EFAC]";
  if (task.priority === "Critical" || getDateTime(task.dueDate) < startOfTodayTime()) return "border-[rgba(248,113,113,.34)] bg-[rgba(248,113,113,.12)] text-[#FCA5A5]";
  if (task.priority === "High" || task.status === "Waiting") return "border-[rgba(255,106,43,.34)] bg-[rgba(255,106,43,.12)] text-[#FF6A2B]";
  return "border-[rgba(34,224,208,.3)] bg-[rgba(34,224,208,.1)] text-[#22E0D0]";
}

function AccountPromisePanel({
  account,
  promises,
  historyEntries,
  onCreatePromise,
  onEditPromise,
  onCompletePromise,
  onMarkPromiseBroken,
  onCancelPromise,
  onAddProgressNote,
  onGenerateLetter,
  onGenerateStatement,
}: {
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  promises: ReturnType<typeof loadARRecoveryWorkspace>["promises"];
  historyEntries: Array<{
    id: string;
    statusChange: string;
    date: string;
    time: string;
    user: string;
    status: string;
    outcome: string;
    nextAction: string;
  }>;
  onCreatePromise: () => void;
  onEditPromise: (promise: AccountPromiseRecord) => void;
  onCompletePromise: (promise: AccountPromiseRecord, completion: ARRecoveryPromiseCompletionFormState) => void;
  onMarkPromiseBroken: (promise: AccountPromiseRecord, broken: ARRecoveryPromiseBrokenFormState) => void;
  onCancelPromise: (promise: AccountPromiseRecord) => void;
  onAddProgressNote: (promise: AccountPromiseRecord, note: string) => void;
  onGenerateLetter: (promise: AccountPromiseRecord) => void;
  onGenerateStatement: (promise: AccountPromiseRecord) => void;
}) {
  const sortedPromises = useMemo(
    () => [...promises].sort((a, b) => getDateTime(b.updatedAt || b.createdAt) - getDateTime(a.updatedAt || a.createdAt)),
    [promises],
  );
  const [selectedFilter, setSelectedFilter] = useState<AccountPromiseFilterKey>("all");
  const filteredPromises = useMemo(
    () => sortedPromises.filter((promise) => promiseMatchesFilter(promise, selectedFilter)),
    [sortedPromises, selectedFilter],
  );
  const [selectedPromiseId, setSelectedPromiseId] = useState(sortedPromises[0]?.id || "");
  const [completionForm, setCompletionForm] = useState<ARRecoveryPromiseCompletionFormState>(emptyPromiseCompletionForm);
  const [completionError, setCompletionError] = useState("");
  const [brokenForm, setBrokenForm] = useState<ARRecoveryPromiseBrokenFormState>(emptyPromiseBrokenForm);
  const [brokenError, setBrokenError] = useState("");
  const [progressNote, setProgressNote] = useState("");
  const [progressError, setProgressError] = useState("");
  const selectedPromise = filteredPromises.find((promise) => promise.id === selectedPromiseId) || filteredPromises[0] || null;
  const selectedHistoryEntries = selectedPromise ? buildSelectedPromiseHistoryEntries(selectedPromise) : [];
  const activePromises = sortedPromises.filter((promise) => promiseMatchesFilter(promise, "active")).length;
  const dueToday = sortedPromises.filter((promise) => promiseMatchesFilter(promise, "due_today")).length;
  const overdue = sortedPromises.filter((promise) => promiseMatchesFilter(promise, "overdue")).length;
  const completed = sortedPromises.filter((promise) => promiseMatchesFilter(promise, "completed")).length;
  const remainingBalanceCents = sortedPromises.reduce((total, promise) => total + promise.remainingBalanceCents, 0);

  useEffect(() => {
    if (selectedPromiseId && filteredPromises.some((promise) => promise.id === selectedPromiseId)) return;
    setSelectedPromiseId(filteredPromises[0]?.id || "");
  }, [filteredPromises, selectedPromiseId]);

  function submitCompletePromise() {
    if (!selectedPromise) return;
    if (!completionForm.completionNotes.trim()) {
      setCompletionError("Completion notes are required.");
      return;
    }
    onCompletePromise(selectedPromise, completionForm);
    setCompletionForm(emptyPromiseCompletionForm);
    setCompletionError("");
  }

  function submitBrokenPromise() {
    if (!selectedPromise) return;
    if (!brokenForm.reason.trim()) {
      setBrokenError("Broken promise reason is required.");
      return;
    }
    if (!brokenForm.outcome.trim()) {
      setBrokenError("Outcome is required.");
      return;
    }
    onMarkPromiseBroken(selectedPromise, brokenForm);
    setBrokenForm(emptyPromiseBrokenForm);
    setBrokenError("");
  }

  function submitProgressNote() {
    if (!selectedPromise) return;
    if (!progressNote.trim()) {
      setProgressError("Progress note is required.");
      return;
    }
    onAddProgressNote(selectedPromise, progressNote);
    setProgressNote("");
    setProgressError("");
  }

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[rgba(255,106,43,.12)] bg-[rgba(255,255,255,.025)] p-3">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Promise Workflow</p>
          <p className="mt-1 text-sm font-semibold text-[#CBD5E1]">Create and manage promise-to-pay records without leaving the account.</p>
        </div>
        <button
          type="button"
          onClick={onCreatePromise}
          className="focus-ring min-h-10 rounded-full border border-[rgba(255,106,43,.42)] bg-[rgba(255,106,43,.16)] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#F6F1E8]"
        >
          Create Promise
        </button>
      </div>

      <CompactMetricStrip
        items={[
          { label: "Active Promises", value: String(activePromises) },
          { label: "Due Today", value: String(dueToday) },
          { label: "Overdue", value: String(overdue) },
          { label: "Completed", value: String(completed) },
          { label: "Remaining", value: formatARRecoveryCurrency(remainingBalanceCents) },
        ]}
      />

      <div className="rounded-xl border border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.025)] p-2">
        <p className="mb-2 text-[10px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Promise Filters</p>
        <div className="flex flex-wrap gap-2">
          {PROMISE_FILTERS.map((filter) => {
            const active = selectedFilter === filter.key;
            return (
              <button
                key={filter.key}
                type="button"
                onClick={() => setSelectedFilter(filter.key)}
                className={`rounded-full border px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.14em] transition ${
                  active
                    ? "border-[rgba(255,106,43,.46)] bg-[rgba(255,106,43,.14)] text-[#F6F1E8]"
                    : "border-[rgba(34,224,208,.2)] bg-[rgba(255,255,255,.04)] text-[#CBD5E1] hover:border-[rgba(34,224,208,.34)]"
                }`}
              >
                {filter.label} {countPromisesForFilter(sortedPromises, filter.key)}
              </button>
            );
          })}
        </div>
      </div>

      {sortedPromises.length ? (
        <div className="grid gap-3 lg:grid-cols-[minmax(18rem,.9fr)_minmax(0,1.5fr)]">
          <section className="h-[34rem] overflow-hidden rounded-xl border border-[rgba(255,106,43,.12)] bg-[rgba(7,18,31,.72)]">
            <div className="flex items-center justify-between gap-3 border-b border-[rgba(255,106,43,.1)] px-3 py-2">
              <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Promise List</p>
              <span className="rounded-full border border-[rgba(34,224,208,.18)] px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#22E0D0]">
                {filteredPromises.length} shown
              </span>
            </div>
            <div className="h-[calc(34rem-2.75rem)] overflow-y-auto p-2">
              {filteredPromises.length ? (
                <div className="grid gap-2">
                  {filteredPromises.map((promise) => {
                    const active = selectedPromise?.id === promise.id;
                    return (
                      <button
                        key={promise.id}
                        type="button"
                        onClick={() => setSelectedPromiseId(promise.id)}
                        className={`rounded-xl border p-2.5 text-left transition ${
                          active
                            ? "border-[rgba(255,106,43,.42)] bg-[rgba(255,106,43,.12)]"
                            : "border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.03)] hover:border-[rgba(34,224,208,.3)]"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">
                              {formatARRecoveryCurrency(promise.amountCents)}
                            </p>
                            <p className="mt-1 text-sm font-semibold leading-5 text-[#F6F1E8]">
                              Promise {formatDate(promise.promiseDate)} | Follow-up {promise.followUpDate ? formatDate(promise.followUpDate) : "Not Captured"}
                            </p>
                          </div>
                          <span className={`shrink-0 rounded-full border px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${promiseStatusBadgeClass(promise.status)}`}>
                            {promise.status}
                          </span>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2 text-[11px] font-black uppercase tracking-[0.12em] text-[#CBD5E1]">
                          <span>{promise.collector || "Not Captured"}</span>
                          <span>|</span>
                          <span>{promise.promiseType}</span>
                        </div>
                        <p className="mt-2 overflow-hidden text-sm font-semibold leading-5 text-[#CBD5E1] [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
                          {previewNoteContent(promise.notes || promise.collectorNotes || "Not Captured")}
                        </p>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <ManagerEmptyState
                  title="No promises match"
                  description={`No promises match the selected filter for ${account.customerName}.`}
                  action="Select another filter to review active, due, overdue, completed, broken, or payment-plan promises."
                />
              )}
            </div>
          </section>

          <section className="h-[34rem] overflow-hidden rounded-xl border border-[rgba(255,106,43,.12)] bg-[rgba(7,18,31,.72)]">
            <div className="flex items-center justify-between gap-3 border-b border-[rgba(255,106,43,.1)] px-3 py-2">
              <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Selected Promise Detail</p>
              {selectedPromise ? (
                <span className={`rounded-full border px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${promiseStatusBadgeClass(selectedPromise.status)}`}>
                  {selectedPromise.status}
                </span>
              ) : null}
            </div>
            <div className="h-[calc(34rem-2.75rem)] overflow-y-auto p-3">
              {selectedPromise ? (
                <div className="grid gap-3">
                  <div className="flex flex-wrap gap-2">
                    <EditActionButton label="Edit Promise" onClick={() => onEditPromise(selectedPromise)} />
                    <button
                      type="button"
                      onClick={() => onCancelPromise(selectedPromise)}
                      className="focus-ring rounded-full border border-[rgba(148,163,184,.24)] bg-[rgba(148,163,184,.08)] px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em] text-[#CBD5E1]"
                    >
                      Cancel Promise
                    </button>
                    <button
                      type="button"
                      onClick={() => onGenerateLetter(selectedPromise)}
                      className="focus-ring rounded-full border border-[rgba(255,106,43,.28)] bg-[rgba(255,106,43,.1)] px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em] text-[#FF6A2B]"
                    >
                      Generate Letter
                    </button>
                    <button
                      type="button"
                      onClick={() => onGenerateStatement(selectedPromise)}
                      className="focus-ring rounded-full border border-[rgba(255,106,43,.28)] bg-[rgba(255,106,43,.1)] px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em] text-[#FF6A2B]"
                    >
                      Generate Statement
                    </button>
                  </div>

                  <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                    <WorkspaceField label="Promise Amount" value={formatARRecoveryCurrency(selectedPromise.amountCents)} />
                    <WorkspaceField label="Promise Date" value={formatDate(selectedPromise.promiseDate)} />
                    <WorkspaceField label="Follow-Up Date" value={selectedPromise.followUpDate ? formatDate(selectedPromise.followUpDate) : "Not Captured"} />
                    <WorkspaceField label="Promise Status" value={selectedPromise.status} />
                    <WorkspaceField label="Promise Type" value={selectedPromise.promiseType} />
                    <WorkspaceField label="Collector" value={selectedPromise.collector || "Not Captured"} />
                    <WorkspaceField label="Remaining Balance" value={formatARRecoveryCurrency(selectedPromise.remainingBalanceCents)} />
                    <WorkspaceField label="Related Invoices" value={selectedPromise.invoiceLabels.length ? selectedPromise.invoiceLabels.join(", ") : "Not Captured"} />
                    <WorkspaceField label="Expected Payment Method" value={getPromiseExpectedPaymentMethod(selectedPromise)} />
                    <WorkspaceField label="Outcome" value={selectedPromise.status || "Not Captured"} />
                  </div>

                  <div className="grid gap-2 xl:grid-cols-2">
                    <div className="rounded-xl border border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.03)] p-3">
                      <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Promise Notes</p>
                      <p className="mt-2 text-sm font-semibold leading-6 text-[#E8EEF4]">{selectedPromise.notes || "Not Captured"}</p>
                    </div>
                    <div className="rounded-xl border border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.03)] p-3">
                      <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Next Action</p>
                      <p className="mt-2 text-sm font-semibold leading-6 text-[#E8EEF4]">{buildPromiseNextAction(selectedPromise)}</p>
                    </div>
                  </div>

                  <AuditStrip
                    createdBy={selectedPromise.collector || "Not Captured"}
                    createdDate={formatCapturedDate(selectedPromise.createdAt)}
                    createdTime={formatCapturedTime(selectedPromise.createdAt)}
                    updatedBy={selectedPromise.collector || selectedPromise.manager || "Not Captured"}
                    updatedDate={formatCapturedDate(selectedPromise.updatedAt)}
                    updatedTime={formatCapturedTime(selectedPromise.updatedAt)}
                  />

                  <div className="grid gap-2">
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-[#22E0D0]">Selected Promise History</p>
                    {selectedHistoryEntries.length ? (
                      <OperationalHistoryRows entries={selectedHistoryEntries} />
                    ) : (
                      <ManagerEmptyState
                        title="No promise history"
                        description="No history is available for this selected promise."
                        action="Promise lifecycle events will appear here when captured."
                      />
                    )}
                  </div>

                  <div className="grid gap-3 xl:grid-cols-3">
                    <div className="rounded-xl border border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.03)] p-3">
                      <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Complete Promise</p>
                      <textarea
                        value={completionForm.completionNotes}
                        onChange={(event) => setCompletionForm((current) => ({ ...current, completionNotes: event.target.value }))}
                        placeholder="Completion notes"
                        rows={3}
                        className="focus-ring mt-2 w-full rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 py-2 text-sm font-semibold text-[#F6F1E8]"
                      />
                      <input
                        value={completionForm.outcome}
                        onChange={(event) => setCompletionForm((current) => ({ ...current, outcome: event.target.value }))}
                        placeholder="Outcome"
                        className="focus-ring mt-2 min-h-10 w-full rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                      />
                      {completionError ? <p className="mt-2 text-xs font-bold text-[#FCA5A5]">{completionError}</p> : null}
                      <button type="button" onClick={submitCompletePromise} className="focus-ring mt-2 min-h-9 rounded-full border border-[rgba(34,197,94,.32)] bg-[rgba(34,197,94,.1)] px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#86EFAC]">
                        Complete
                      </button>
                    </div>

                    <div className="rounded-xl border border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.03)] p-3">
                      <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Mark Broken</p>
                      <input
                        value={brokenForm.reason}
                        onChange={(event) => setBrokenForm((current) => ({ ...current, reason: event.target.value }))}
                        placeholder="Reason"
                        className="focus-ring mt-2 min-h-10 w-full rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                      />
                      <input
                        value={brokenForm.outcome}
                        onChange={(event) => setBrokenForm((current) => ({ ...current, outcome: event.target.value }))}
                        placeholder="Outcome"
                        className="focus-ring mt-2 min-h-10 w-full rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                      />
                      <input
                        value={brokenForm.nextAction}
                        onChange={(event) => setBrokenForm((current) => ({ ...current, nextAction: event.target.value }))}
                        placeholder="Next action"
                        className="focus-ring mt-2 min-h-10 w-full rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                      />
                      {brokenError ? <p className="mt-2 text-xs font-bold text-[#FCA5A5]">{brokenError}</p> : null}
                      <button type="button" onClick={submitBrokenPromise} className="focus-ring mt-2 min-h-9 rounded-full border border-[rgba(248,113,113,.32)] bg-[rgba(248,113,113,.1)] px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#FCA5A5]">
                        Mark Broken
                      </button>
                    </div>

                    <div className="rounded-xl border border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.03)] p-3">
                      <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Progress Note</p>
                      <textarea
                        value={progressNote}
                        onChange={(event) => setProgressNote(event.target.value)}
                        placeholder="Add progress note"
                        rows={4}
                        className="focus-ring mt-2 w-full rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 py-2 text-sm font-semibold text-[#F6F1E8]"
                      />
                      {progressError ? <p className="mt-2 text-xs font-bold text-[#FCA5A5]">{progressError}</p> : null}
                      <button type="button" onClick={submitProgressNote} className="focus-ring mt-2 min-h-9 rounded-full border border-[rgba(34,224,208,.24)] bg-[rgba(34,224,208,.08)] px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#22E0D0]">
                        Add Note
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <ManagerEmptyState
                  title="No promise selected"
                  description="Select a promise to view its details."
                  action="Amount, due date, follow-up, outcome, next action, and audit information will appear here."
                />
              )}
            </div>
          </section>
        </div>
      ) : (
        <ManagerEmptyState
          title="No promise records"
          description="No promise-to-pay record is available for this account yet."
          action="Promise records will appear here when they exist for this account."
        />
      )}
    </div>
  );
}

type AccountPromiseRecord = ReturnType<typeof loadARRecoveryWorkspace>["promises"][number];
type AccountPromiseFilterKey = "all" | "active" | "due_today" | "overdue" | "completed" | "broken" | "payment_plan";

const PROMISE_FILTERS: Array<{ key: AccountPromiseFilterKey; label: string }> = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "due_today", label: "Due Today" },
  { key: "overdue", label: "Overdue" },
  { key: "completed", label: "Completed" },
  { key: "broken", label: "Broken" },
  { key: "payment_plan", label: "Payment Plan" },
];

const PROMISE_NEXT_ACTION_TYPES: ARRecoveryPromiseNextActionType[] = [
  "Call Customer",
  "Send Letter",
  "Send Statement",
  "Verify Payment",
  "Verify Promise",
  "Follow-Up",
  "Manager Review",
  "Request Documents",
  "Assign Task",
  "Research Account",
  "Escalate Account",
  "Other",
];

function parsePromiseAmountCents(value: string) {
  const normalized = value.replace(/[$,]/g, "").trim();
  const amount = Number(normalized);
  return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
}

function mapPromiseFormTypeToRecordType(type: ARRecoveryPromiseFormType | ""): AccountPromiseRecord["promiseType"] {
  if (type === "Settlement") return "Settlement Agreement";
  if (type === "Other") return "Custom Promise";
  if (type === "Installment Plan") return "Installment Plan";
  if (type === "Partial Payment") return "Partial Payment";
  return "Single Payment";
}

function mapPromiseRecordTypeToFormType(type: AccountPromiseRecord["promiseType"]): ARRecoveryPromiseFormType {
  if (type === "Settlement Agreement") return "Settlement";
  if (type === "Custom Promise") return "Other";
  return type;
}

function derivePromiseStatusFromDate(promiseDate: string): AccountPromiseRecord["status"] {
  if (isToday(promiseDate)) return "Due Today";
  if (getDateTime(promiseDate) > startOfTodayTime()) return "Upcoming";
  return "Open";
}

function buildPromiseFormFromRecord(promise: AccountPromiseRecord): ARRecoveryPromiseFormState {
  const workflowPromise = promise as Partial<ARRecoveryPromiseWorkflowRecord>;
  return {
    promiseType: mapPromiseRecordTypeToFormType(promise.promiseType),
    amount: String(Math.round(promise.amountCents / 100)),
    promiseDate: promise.promiseDate,
    expectedPaymentMethod: workflowPromise.expectedPaymentMethod || "",
    relatedInvoices: promise.invoiceLabels || [],
    notes: promise.notes || "",
    outcome: workflowPromise.outcome || "",
    nextActionType: workflowPromise.nextActionType || "",
    nextActionDueDate: workflowPromise.nextActionDueDate || promise.followUpDate || "",
    nextActionDueTime: workflowPromise.nextActionDueTime || "",
    nextActionPriority: workflowPromise.nextActionPriority || "Normal",
    nextActionAssignedTo: workflowPromise.nextActionAssignedTo || promise.collector || "",
    reminderRequired: Boolean(workflowPromise.reminderRequired),
    reminderNotes: workflowPromise.reminderNotes || "",
  };
}

function buildPromiseWorkflowRecordFromForm({
  form,
  account,
  companyId,
  existingPromise,
  amountCents,
  now,
  actorName,
}: {
  form: ARRecoveryPromiseFormState;
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  companyId: string;
  existingPromise?: AccountPromiseRecord;
  amountCents: number;
  now: string;
  actorName: string;
}): ARRecoveryPromiseWorkflowRecord {
  const existingWorkflow = existingPromise ? buildPromiseWorkflowRecordFromExisting(existingPromise, account, now, actorName) : null;
  const promiseId = existingPromise?.id || `promise-${account.id}-${Date.now()}`;
  const promiseType = mapPromiseFormTypeToRecordType(form.promiseType);
  const status = existingPromise && isClosedPromiseLifecycleStatus(existingPromise.status) ? existingPromise.status : derivePromiseStatusFromDate(form.promiseDate);
  const invoiceLabels = form.relatedInvoices.length ? form.relatedInvoices : existingPromise?.invoiceLabels || [];
  const baseActivityFeed = existingWorkflow?.activityFeed || [];
  const activityFeed = existingPromise
    ? [
        ...baseActivityFeed,
        {
          id: `promise-act-${promiseId}-${Date.now()}`,
          kind: "Updated" as const,
          title: "Promise updated",
          detail: form.notes.trim() || "Promise details updated.",
          createdAt: now,
        },
      ]
    : [
        {
          id: `promise-act-${promiseId}-${Date.now()}`,
          kind: "Created" as const,
          title: "Promise created",
          detail: form.notes.trim() || "Promise-to-pay created from account workspace.",
          createdAt: now,
        },
      ];

  return {
    id: promiseId,
    companyId,
    accountId: account.id,
    customerName: account.customerName,
    customerNumber: account.customerNumber,
    clientCompany: existingPromise?.clientCompany || "AR Recovery Client",
    invoiceLabels,
    remainingBalanceCents: Math.max(0, account.openBalanceCents - amountCents),
    promiseDate: form.promiseDate,
    amountCents,
    status,
    promiseType,
    followUpDate: form.nextActionDueDate || existingPromise?.followUpDate || null,
    collector: existingPromise?.collector || actorName || account.assignedCollector || "System",
    manager: existingPromise?.manager || "Not Captured",
    createdAt: existingPromise?.createdAt || now,
    updatedAt: now,
    notes: form.notes.trim(),
    collectorNotes: existingPromise?.collectorNotes || "",
    customerNotes: existingPromise?.customerNotes || "",
    managerNotes: existingPromise?.managerNotes || "",
    escalationNotes: existingPromise?.escalationNotes || "",
    paymentNotes: existingPromise?.paymentNotes || "",
    installmentSchedule: existingPromise?.installmentSchedule || [],
    activityFeed,
    expectedPaymentMethod: form.expectedPaymentMethod.trim(),
    outcome: form.outcome.trim(),
    nextActionType: form.nextActionType,
    nextActionDueDate: form.nextActionDueDate,
    nextActionDueTime: form.nextActionDueTime,
    nextActionPriority: form.nextActionPriority,
    nextActionAssignedTo: form.nextActionAssignedTo.trim() || actorName || account.assignedCollector || "System",
    reminderRequired: form.reminderRequired,
    reminderNotes: form.reminderNotes.trim(),
    completionNotes: existingWorkflow?.completionNotes || "",
    brokenReason: existingWorkflow?.brokenReason || "",
    progressNotes: existingWorkflow?.progressNotes || [],
  };
}

function buildPromiseWorkflowRecordFromExisting(
  promise: AccountPromiseRecord,
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>,
  now: string,
  actorName?: string,
): ARRecoveryPromiseWorkflowRecord {
  const workflowPromise = promise as Partial<ARRecoveryPromiseWorkflowRecord>;
  return {
    ...promise,
    expectedPaymentMethod: workflowPromise.expectedPaymentMethod || "",
    outcome: workflowPromise.outcome || "",
    nextActionType: workflowPromise.nextActionType || "",
    nextActionDueDate: workflowPromise.nextActionDueDate || promise.followUpDate || "",
    nextActionDueTime: workflowPromise.nextActionDueTime || "",
    nextActionPriority: workflowPromise.nextActionPriority || "Normal",
    nextActionAssignedTo: workflowPromise.nextActionAssignedTo || promise.collector || actorName || account.assignedCollector || "System",
    reminderRequired: Boolean(workflowPromise.reminderRequired),
    reminderNotes: workflowPromise.reminderNotes || "",
    completionNotes: workflowPromise.completionNotes || "",
    brokenReason: workflowPromise.brokenReason || "",
    progressNotes: workflowPromise.progressNotes || [],
    updatedAt: promise.updatedAt || now,
  };
}

function upsertPromiseWorkflowRecord(records: ARRecoveryPromiseWorkflowRecord[], promise: ARRecoveryPromiseWorkflowRecord) {
  const exists = records.some((record) => record.id === promise.id);
  return exists
    ? records.map((record) => (record.id === promise.id ? promise : record))
    : [promise, ...records];
}

function getPromiseExpectedPaymentMethod(promise: AccountPromiseRecord) {
  return (promise as Partial<ARRecoveryPromiseWorkflowRecord>).expectedPaymentMethod || "Not Captured";
}

function formatPromiseNextActionSummary(promise: Pick<ARRecoveryPromiseWorkflowRecord, "nextActionType" | "nextActionDueDate" | "nextActionDueTime" | "nextActionAssignedTo" | "reminderRequired" | "reminderNotes">) {
  const parts = [
    promise.nextActionType || "",
    promise.nextActionDueDate ? `due ${formatCapturedDate(promise.nextActionDueDate)}` : "",
    promise.nextActionDueTime ? `at ${promise.nextActionDueTime}` : "",
    promise.nextActionAssignedTo ? `assigned to ${promise.nextActionAssignedTo}` : "",
    promise.reminderRequired ? "reminder required" : "",
    promise.reminderNotes || "",
  ].filter(Boolean);
  return parts.length ? parts.join(" | ") : "Not Captured";
}

function promiseMatchesFilter(promise: AccountPromiseRecord, filter: AccountPromiseFilterKey) {
  const closedStatuses = ["Paid", "Completed", "Resolved", "Cancelled"];
  if (filter === "all") return true;
  if (filter === "active") return !closedStatuses.includes(promise.status);
  if (filter === "due_today") return promise.status === "Due Today" || isToday(promise.promiseDate);
  if (filter === "overdue") return getDateTime(promise.promiseDate) < startOfTodayTime() && !closedStatuses.includes(promise.status);
  if (filter === "completed") return ["Paid", "Completed", "Resolved"].includes(promise.status);
  if (filter === "broken") return promise.status === "Broken" || promise.status === "Escalated";
  return promise.promiseType === "Installment Plan";
}

function countPromisesForFilter(promises: AccountPromiseRecord[], filter: AccountPromiseFilterKey) {
  return promises.filter((promise) => promiseMatchesFilter(promise, filter)).length;
}

function promiseStatusBadgeClass(status: AccountPromiseRecord["status"]) {
  if (status === "Broken" || status === "Escalated") return "border-[rgba(248,113,113,.34)] bg-[rgba(248,113,113,.12)] text-[#FCA5A5]";
  if (status === "Paid" || status === "Completed" || status === "Resolved") return "border-[rgba(34,197,94,.32)] bg-[rgba(34,197,94,.12)] text-[#86EFAC]";
  if (status === "Due Today" || status === "Partial") return "border-[rgba(255,106,43,.34)] bg-[rgba(255,106,43,.12)] text-[#FF6A2B]";
  return "border-[rgba(34,224,208,.3)] bg-[rgba(34,224,208,.1)] text-[#22E0D0]";
}

function buildPromiseNextAction(promise: AccountPromiseRecord) {
  if (promise.followUpDate) return `Follow up ${formatDate(promise.followUpDate)}.`;
  if (promise.status === "Broken" || promise.status === "Escalated") return "Escalate for review.";
  if (promise.status === "Partial") return "Monitor remaining balance and confirm next payment.";
  if (promise.status === "Paid" || promise.status === "Completed" || promise.status === "Resolved") return "No next action captured.";
  return "Monitor commitment and confirm payment.";
}

function buildSelectedPromiseHistoryEntries(promise: AccountPromiseRecord) {
  return [...promise.activityFeed]
    .sort((a, b) => getDateTime(b.createdAt) - getDateTime(a.createdAt))
    .map((entry) => ({
      id: entry.id,
      eventType: entry.kind,
      date: formatCapturedDate(entry.createdAt),
      time: formatCapturedTime(entry.createdAt),
      user: promise.collector || promise.manager || "Not Captured",
      status: promise.status,
      outcome: entry.detail || entry.title || "Not Captured",
      nextAction: buildPromiseNextAction(promise),
    }));
}

function startOfTodayTime() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today.getTime();
}

interface AccountNoteRecord {
  id: string;
  noteType: string;
  visibility: "internal" | "client_visible" | "manager_only";
  content: string;
  outcome: string;
  nextAction: string;
  createdBy: string;
  createdAt?: string;
  updatedBy: string;
  updatedAt?: string;
}

type AccountNoteFilterKey = "all" | "internal" | "client_visible" | "escalation" | "promise" | "system";

const NOTE_FILTERS: Array<{ key: AccountNoteFilterKey; label: string }> = [
  { key: "all", label: "All Notes" },
  { key: "internal", label: "Internal" },
  { key: "client_visible", label: "Client Visible" },
  { key: "escalation", label: "Escalation" },
  { key: "promise", label: "Promise" },
  { key: "system", label: "System" },
];

function buildAccountNoteRecords(
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>,
  activityEvents: ReturnType<typeof loadARRecoveryWorkspace>["recoveryActivityEvents"],
  promises: ReturnType<typeof loadARRecoveryWorkspace>["promises"],
): AccountNoteRecord[] {
  const records: AccountNoteRecord[] = [];

  if (account.notes.trim()) {
    records.push({
      id: `${account.id}-account-note`,
      noteType: "Internal Note",
      visibility: "internal",
      content: account.notes,
      outcome: "Not Captured",
      nextAction: "Not Captured",
      createdBy: "Not Captured",
      createdAt: account.createdAt,
      updatedBy: "Not Captured",
      updatedAt: account.updatedAt,
    });
  }

  activityEvents
    .filter((event) => event.notes || event.eventType === "note_added")
    .forEach((event) => {
      records.push({
        id: event.id,
        noteType: buildActivityNoteType(event.relatedRecordType, event.eventType, event.metadata),
        visibility: event.visibility,
        content: event.notes || event.detail || "Not Captured",
        outcome: event.outcome || event.detail || "Not Captured",
        nextAction: typeof event.metadata?.nextAction === "string" && event.metadata.nextAction.trim() ? event.metadata.nextAction : "Not Captured",
        createdBy: event.actorName || "Not Captured",
        createdAt: event.createdAt,
        updatedBy: "Not Captured",
      });
    });

  promises.forEach((promise) => {
    [
      { key: "promise", noteType: "Promise Note", content: promise.notes, createdBy: promise.collector || "Not Captured" },
      { key: "collector", noteType: "Collection Note", content: promise.collectorNotes, createdBy: promise.collector || "Not Captured" },
      { key: "customer", noteType: "Customer Note", content: promise.customerNotes, createdBy: "Not Captured" },
      { key: "manager", noteType: "Internal Note", content: promise.managerNotes, createdBy: promise.manager || "Not Captured" },
      { key: "escalation", noteType: "Escalation Note", content: promise.escalationNotes, createdBy: promise.manager || promise.collector || "Not Captured" },
      { key: "payment", noteType: "Promise Note", content: promise.paymentNotes, createdBy: promise.collector || "Not Captured" },
    ].forEach((note) => {
      if (!note.content.trim()) return;
      records.push({
        id: `${promise.id}-${note.key}-note`,
        noteType: note.noteType,
        visibility: note.key === "customer" ? "client_visible" : note.key === "manager" || note.key === "escalation" ? "manager_only" : "internal",
        content: note.content,
        outcome: promise.status,
        nextAction: promise.followUpDate ? `Follow up ${formatDate(promise.followUpDate)}.` : "Not Captured",
        createdBy: note.createdBy,
        createdAt: promise.createdAt,
        updatedBy: note.createdBy,
        updatedAt: promise.updatedAt,
      });
    });
  });

  return records.sort((a, b) => getDateTime(b.updatedAt || b.createdAt) - getDateTime(a.updatedAt || a.createdAt));
}

function noteMatchesFilter(note: AccountNoteRecord, filter: AccountNoteFilterKey) {
  if (filter === "all") return true;
  if (filter === "internal") return note.visibility === "internal" || note.visibility === "manager_only";
  if (filter === "client_visible") return note.visibility === "client_visible" || note.noteType === "Customer Note";
  if (filter === "escalation") return note.noteType.toLowerCase().includes("escalation");
  if (filter === "promise") return note.noteType.toLowerCase().includes("promise");
  return note.noteType.toLowerCase().includes("system") || note.createdBy === "System";
}

function countNotesForFilter(notes: AccountNoteRecord[], filter: AccountNoteFilterKey) {
  return notes.filter((note) => noteMatchesFilter(note, filter)).length;
}

function previewNoteContent(content: string) {
  const normalized = content.trim();
  return normalized.length > 120 ? `${normalized.slice(0, 120)}...` : normalized || "Not Captured";
}

function noteVisibilityBadgeClass(visibility: AccountNoteRecord["visibility"]) {
  if (visibility === "client_visible") return "border-[rgba(34,224,208,.3)] bg-[rgba(34,224,208,.1)] text-[#22E0D0]";
  if (visibility === "manager_only") return "border-[rgba(255,106,43,.3)] bg-[rgba(255,106,43,.1)] text-[#FF6A2B]";
  return "border-[rgba(203,213,225,.18)] bg-[rgba(203,213,225,.08)] text-[#CBD5E1]";
}

function noteCategoryLabel(note: AccountNoteRecord) {
  const noteType = note.noteType.toLowerCase();
  if (noteType.includes("promise")) return "Promise";
  if (noteType.includes("escalation")) return "Escalation";
  if (noteType.includes("system") || note.createdBy === "System") return "System";
  return formatNoteVisibility(note.visibility);
}

function noteCategoryBadgeClass(note: AccountNoteRecord) {
  const label = noteCategoryLabel(note);
  if (label === "Promise") return "border-[rgba(34,224,208,.32)] bg-[rgba(34,224,208,.12)] text-[#22E0D0]";
  if (label === "Escalation") return "border-[rgba(248,113,113,.34)] bg-[rgba(248,113,113,.12)] text-[#FCA5A5]";
  if (label === "System") return "border-[rgba(148,163,184,.28)] bg-[rgba(148,163,184,.1)] text-[#CBD5E1]";
  return noteVisibilityBadgeClass(note.visibility);
}

function buildAccountNoteHistoryEntries(noteRecords: AccountNoteRecord[]) {
  return noteRecords.map((note) => ({
    id: `${note.id}-history`,
    eventType: `${note.noteType} Added`,
    date: formatCapturedDate(note.createdAt),
    time: formatCapturedTime(note.createdAt),
    user: note.createdBy,
    status: formatNoteVisibility(note.visibility),
    outcome: note.outcome,
    nextAction: note.nextAction,
  }));
}

function buildAccountLetterHistoryEntries(
  letters: ReturnType<typeof loadARRecoveryWorkspace>["collectionLetters"],
  activityEvents: ReturnType<typeof loadARRecoveryWorkspace>["recoveryActivityEvents"],
) {
  const letterEventRows = activityEvents
    .filter((event) => event.relatedRecordType === "letter" || event.eventType === "letter_generated")
    .map((event) => ({
      id: event.id,
      eventType: formatTicklerEventType(event.eventType),
      date: formatCapturedDate(event.createdAt),
      time: formatCapturedTime(event.createdAt),
      user: event.actorName || "Not Captured",
      status: event.nextStatus || event.previousStatus || "Not Captured",
      outcome: event.outcome || event.detail || "Not Captured",
      nextAction: typeof event.metadata?.nextAction === "string" && event.metadata.nextAction.trim() ? event.metadata.nextAction : "Not Captured",
      sortAt: getDateTime(event.createdAt),
    }));

  const letterRows = letters.flatMap((letter) => [
    {
      id: `${letter.id}-created`,
      eventType: "Letter Created",
      date: formatCapturedDate(letter.createdAt),
      time: formatCapturedTime(letter.createdAt),
      user: letter.sentBy || letter.collector || "Not Captured",
      status: letter.status,
      outcome: letter.notes || "Not Captured",
      nextAction: "Not Captured",
      sortAt: getDateTime(letter.createdAt),
    },
    ...(letter.sentAt
      ? [
          {
            id: `${letter.id}-sent`,
            eventType: "Letter Sent",
            date: formatCapturedDate(letter.sentAt),
            time: letter.timeSent || formatCapturedTime(letter.sentAt),
            user: letter.sentBy || "Not Captured",
            status: letter.deliveryStatus,
            outcome: letter.notes || "Not Captured",
            nextAction: "Not Captured",
            sortAt: getDateTime(letter.sentAt),
          },
        ]
      : []),
    {
      id: `${letter.id}-updated`,
      eventType: "Letter Updated",
      date: formatCapturedDate(letter.updatedAt),
      time: formatCapturedTime(letter.updatedAt),
      user: "Not Captured",
      status: letter.status,
      outcome: letter.notes || "Not Captured",
      nextAction: "Not Captured",
      sortAt: getDateTime(letter.updatedAt),
    },
  ]);

  return [...letterEventRows, ...letterRows].sort((a, b) => b.sortAt - a.sortAt);
}

function buildAccountStatementHistoryEntries(
  statements: ReturnType<typeof loadARRecoveryWorkspace>["statements"],
  activityEvents: ReturnType<typeof loadARRecoveryWorkspace>["recoveryActivityEvents"],
) {
  const statementEventRows = activityEvents
    .filter((event) => event.relatedRecordType === "statement" || event.eventType === "statement_generated")
    .map((event) => ({
      id: event.id,
      eventType: formatTicklerEventType(event.eventType),
      date: formatCapturedDate(event.createdAt),
      time: formatCapturedTime(event.createdAt),
      user: event.actorName || "Not Captured",
      status: event.nextStatus || event.previousStatus || "Not Captured",
      outcome: event.outcome || event.detail || "Not Captured",
      nextAction: typeof event.metadata?.nextAction === "string" && event.metadata.nextAction.trim() ? event.metadata.nextAction : "Not Captured",
      sortAt: getDateTime(event.createdAt),
    }));

  const statementRows = statements.flatMap((statement) => [
    {
      id: `${statement.id}-created`,
      eventType: "Statement Created",
      date: formatCapturedDate(statement.createdAt),
      time: formatCapturedTime(statement.createdAt),
      user: statement.sentBy || statement.collector || "Not Captured",
      status: statement.status,
      outcome: statement.notes || "Not Captured",
      nextAction: "Not Captured",
      sortAt: getDateTime(statement.createdAt),
    },
    ...(statement.sentAt
      ? [
          {
            id: `${statement.id}-sent`,
            eventType: "Statement Sent",
            date: formatCapturedDate(statement.sentAt),
            time: statement.timeSent || formatCapturedTime(statement.sentAt),
            user: statement.sentBy || "Not Captured",
            status: statement.deliveryStatus,
            outcome: statement.notes || "Not Captured",
            nextAction: "Not Captured",
            sortAt: getDateTime(statement.sentAt),
          },
        ]
      : []),
    {
      id: `${statement.id}-updated`,
      eventType: "Statement Updated",
      date: formatCapturedDate(statement.updatedAt),
      time: formatCapturedTime(statement.updatedAt),
      user: "Not Captured",
      status: statement.status,
      outcome: statement.notes || "Not Captured",
      nextAction: "Not Captured",
      sortAt: getDateTime(statement.updatedAt),
    },
  ]);

  return [...statementEventRows, ...statementRows].sort((a, b) => b.sortAt - a.sortAt);
}

function buildActivityNoteType(
  relatedRecordType: ReturnType<typeof loadARRecoveryWorkspace>["recoveryActivityEvents"][number]["relatedRecordType"],
  eventType: ReturnType<typeof loadARRecoveryWorkspace>["recoveryActivityEvents"][number]["eventType"],
  metadata?: Record<string, unknown>,
) {
  if (typeof metadata?.noteType === "string" && metadata.noteType.trim()) return metadata.noteType;
  if (relatedRecordType === "promise") return "Promise Note";
  if (relatedRecordType === "escalation") return "Escalation Note";
  if (relatedRecordType === "message" || relatedRecordType === "document") return "Customer Note";
  if (eventType === "note_added") return "Collection Note";
  return `${formatTicklerEventType(eventType)} Note`;
}

function formatNoteVisibility(value: AccountNoteRecord["visibility"]) {
  if (value === "client_visible") return "Customer Visible";
  if (value === "manager_only") return "Manager Only";
  return "Internal";
}

function getNoteVisibilityDescription(value: ARRecoveryNoteVisibility) {
  if (value === "client_visible") return "Safe for customer-facing account context.";
  if (value === "manager_only") return "Restricted to managers and authorized oversight users.";
  return "Visible to internal recovery users only.";
}

function formatCapturedDate(value?: string) {
  if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(new Date(year, month - 1, day));
  }
  return value && Number.isFinite(new Date(value).getTime()) ? formatDate(value) : "Not Captured";
}

function formatCapturedTime(value?: string) {
  if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) return "Not Captured";
  return value && Number.isFinite(new Date(value).getTime()) ? formatTime(value) : "Not Captured";
}

function timestampOffsetHours(hours: number) {
  return new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
}

function getNextActionDate(account: ReturnType<typeof loadARRecoveryWorkspace>["accounts"][number]) {
  const safeAccount = account as unknown as Record<string, string | undefined>;
  return (
    safeAccount.nextActionDate ||
    safeAccount.followUpDate ||
    safeAccount.promiseDate ||
    safeAccount.dueDate ||
    safeAccount.createdAt ||
    safeAccount.updatedAt ||
    new Date().toISOString()
  );
}

function getDateTime(value?: string) {
  const time = value ? new Date(value).getTime() : Number.NaN;
  return Number.isFinite(time) ? time : 0;
}

function isToday(value?: string) {
  if (!value) return false;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return false;
  const today = new Date();
  return date.toDateString() === today.toDateString();
}

function buildAccountTimelineEntries(
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>,
  activity: ReturnType<typeof loadARRecoveryWorkspace>["activity"],
) {
  return [...activity]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .map((item) => {
      const actor = getTimelineActivityActor(item, account.assignedCollector || "System");
      return {
        id: item.id,
        actionType: formatTimelineActionType(item.kind),
        date: formatDate(item.createdAt),
        time: formatTime(item.createdAt),
        user: actor,
        status: formatTimelineActionType(item.kind),
        preview: item.title || item.detail || "Activity recorded.",
        outcome: buildTimelineOutcome(item),
        nextAction: buildTimelineNextAction(item),
        relatedAccountNumber: account.customerNumber,
        relatedAccountName: account.customerName,
        relatedAccountBalance: formatARRecoveryCurrency(account.openBalanceCents),
        createdBy: actor,
        createdDate: formatDate(item.createdAt),
        updatedBy: actor,
        updatedDate: formatDate(item.createdAt),
      };
    });
}

function formatTimelineActionType(kind: string) {
  const normalized = kind.toLowerCase();
  if (normalized.includes("call")) return "Call Made";
  if (normalized.includes("note")) return "Collection Note";
  if (normalized.includes("letter")) return "Letter Sent";
  if (normalized.includes("statement")) return "Statement Generated";
  if (normalized.includes("promise")) return "Promise Created";
  if (normalized.includes("payment")) return "Payment Received";
  if (normalized.includes("escalation")) return "Account Escalated";
  if (normalized.includes("task")) return "Follow-up Task";
  if (normalized.includes("email")) return "Email Sent";
  return kind || "Activity Recorded";
}

function getTimelineActivityActor(
  item: ReturnType<typeof loadARRecoveryWorkspace>["activity"][number],
  fallback: string,
) {
  const actorName = (item as { actorName?: string }).actorName;
  return actorName?.trim() || fallback;
}

function buildAccountPromiseHistoryEntries(
  promises: ReturnType<typeof loadARRecoveryWorkspace>["promises"],
) {
  return [...promises]
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .map((promise) => ({
      id: promise.id,
      statusChange:
        promise.status === "Paid" || promise.status === "Completed" || promise.status === "Resolved"
          ? "Promise Kept"
          : promise.status === "Partial"
            ? "Promise Partial Payment"
            : promise.status === "Broken" || promise.status === "Escalated"
              ? "Promise Broken"
              : promise.status === "Due Today" || promise.status === "Upcoming"
                ? "Promise Created"
                : "Promise Modified",
      date: formatDate(promise.updatedAt),
      time: formatTime(promise.updatedAt),
      user: promise.collector || promise.manager || "System",
      status: promise.status,
      outcome: promise.notes || `${formatARRecoveryCurrency(promise.amountCents)} commitment recorded.`,
      nextAction:
        promise.followUpDate
          ? `Follow up ${formatDate(promise.followUpDate)}.`
          : promise.status === "Broken" || promise.status === "Escalated"
            ? "Escalate for review."
            : promise.status === "Partial"
              ? "Monitor remainder and confirm payment."
              : "Monitor the commitment and wait for the due date.",
    }));
}

function buildTimelineOutcome(item: ReturnType<typeof loadARRecoveryWorkspace>["activity"][number]) {
  return item.detail || item.title;
}

function buildTimelineNextAction(item: ReturnType<typeof loadARRecoveryWorkspace>["activity"][number]) {
  const capturedNextAction = extractNextActionFromDetail(item.detail);
  if (capturedNextAction) return capturedNextAction;

  switch (item.kind) {
    case "Call":
      return "Review the call outcome and set the next follow-up.";
    case "Email":
      return "Monitor for a response and update the account if needed.";
    case "Note":
      return "Continue with the next recovery step.";
    case "Statement":
      return "Confirm statement delivery and wait for reply.";
    case "Letter":
      return "Track delivery and prepare the next notice if needed.";
    case "Promise":
      return "Follow up on the promised date and amount.";
    case "Escalation":
      return "Route for review and manager action.";
    case "Payment":
      return "Apply the payment and review the remaining balance.";
    default:
      return "Continue monitoring the account.";
  }
}

function formatTime(value: string) {
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}

const TICKLER_PRIORITY_ORDER: Record<"Critical" | "High" | "Normal" | "Medium" | "Low", number> = {
  Critical: 0,
  High: 1,
  Normal: 2,
  Medium: 3,
  Low: 4,
};

function buildTicklerHistoryEntries(
  activityEvents: ReturnType<typeof loadARRecoveryWorkspace>["recoveryActivityEvents"],
  accountTicklerIds: Set<string>,
  strictRecordMatch = false,
) {
  return [...activityEvents]
    .filter((event) => {
      const matchesSelectedTickler = event.relatedRecordId ? accountTicklerIds.has(event.relatedRecordId) : false;
      if (strictRecordMatch) return matchesSelectedTickler;
      return (
        event.relatedRecordType === "tickler" ||
        matchesSelectedTickler ||
        event.eventType === "follow_up_created" ||
        event.eventType === "follow_up_completed" ||
        event.eventType === "escalation_created" ||
        event.eventType === "escalation_approved" ||
        event.eventType === "escalation_resolved"
      );
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .map((event) => ({
      id: event.id,
      eventType: formatTicklerEventType(event.eventType),
      date: formatDate(event.createdAt),
      time: formatTime(event.createdAt),
      user: event.actorName || "Not Captured",
      status: event.nextStatus || event.previousStatus || "Not Captured",
      outcome: event.outcome || event.detail || "Not Captured",
      nextAction: typeof event.metadata?.nextAction === "string" && event.metadata.nextAction.trim() ? event.metadata.nextAction : "Not Captured",
    }));
}

function formatTicklerEventType(eventType: string) {
  return eventType
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function AccountRecordList({
  title,
  records,
}: {
  title: string;
  records: Array<{ id: string; primary: string; secondary: string; meta: string }>;
}) {
  return (
    <div className="grid gap-3">
      <SectionHeading eyebrow={title} title={`${title} records`} description="Action-ready record history scoped to this account." />
      {records.length ? (
        records.map((record) => (
          <div key={record.id} className="rounded-2xl border border-[rgba(34,224,208,.14)] bg-[rgba(255,255,255,.03)] p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">{record.secondary}</p>
                <h3 className="mt-1 font-display text-2xl font-bold text-[#F6F1E8]">{record.primary}</h3>
              </div>
            </div>
            <p className="mt-2 text-sm font-semibold leading-7 text-[#E8EEF4]">{record.meta}</p>
          </div>
        ))
      ) : (
        <ManagerEmptyState
          title="No records available"
          description="No records exist for this account tab yet."
          action="Records will appear here when they are available for the selected account."
        />
      )}
    </div>
  );
}

function AccountTicklerPanel({
  account,
  ticklers,
  activityEvents,
  onCreateTickler,
  onEditTickler,
  onCompleteTickler,
  onRescheduleTickler,
  onCancelTickler,
  onAddProgressNote,
  onGenerateLetter,
  onGenerateStatement,
}: {
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  ticklers: ReturnType<typeof loadARRecoveryWorkspace>["ticklers"];
  activityEvents: ReturnType<typeof loadARRecoveryWorkspace>["recoveryActivityEvents"];
  onCreateTickler: () => void;
  onEditTickler: (tickler: AccountTicklerRecord) => void;
  onCompleteTickler: (tickler: AccountTicklerRecord, completion: ARRecoveryTicklerCompletionFormState) => void;
  onRescheduleTickler: (tickler: AccountTicklerRecord, reschedule: ARRecoveryTicklerRescheduleFormState) => void;
  onCancelTickler: (tickler: AccountTicklerRecord) => void;
  onAddProgressNote: (tickler: AccountTicklerRecord, note: string) => void;
  onGenerateLetter: (tickler: AccountTicklerRecord) => void;
  onGenerateStatement: (tickler: AccountTicklerRecord) => void;
}) {
  const accountTicklers = useMemo(
    () =>
      [...ticklers]
        .filter((item) => item.accountId === account.id)
        .sort((a, b) => {
          const dueDateDelta = getDateTime(a.dueDate) - getDateTime(b.dueDate);
          if (dueDateDelta !== 0) return dueDateDelta;
          return TICKLER_PRIORITY_ORDER[a.priority] - TICKLER_PRIORITY_ORDER[b.priority];
        }),
    [account.id, ticklers],
  );
  const [selectedFilter, setSelectedFilter] = useState<AccountTicklerFilterKey>("all");
  const [completionForm, setCompletionForm] = useState<ARRecoveryTicklerCompletionFormState>(emptyTicklerCompletionForm);
  const [completionError, setCompletionError] = useState("");
  const [rescheduleForm, setRescheduleForm] = useState<ARRecoveryTicklerRescheduleFormState>(emptyTicklerRescheduleForm);
  const [rescheduleError, setRescheduleError] = useState("");
  const [progressNote, setProgressNote] = useState("");
  const [progressError, setProgressError] = useState("");
  const filteredTicklers = useMemo(
    () => accountTicklers.filter((tickler) => ticklerMatchesFilter(tickler, selectedFilter)),
    [accountTicklers, selectedFilter],
  );
  const [selectedTicklerId, setSelectedTicklerId] = useState(accountTicklers[0]?.id || "");
  const selectedTickler = filteredTicklers.find((tickler) => tickler.id === selectedTicklerId) || filteredTicklers[0] || null;
  const selectedTicklerHistoryEntries = selectedTickler
    ? buildTicklerHistoryEntries(activityEvents, new Set([selectedTickler.id]), true)
    : [];
  const selectedLatestHistory = selectedTicklerHistoryEntries[0] || null;
  const openTicklers = accountTicklers.filter((item) => item.status !== "Completed").length;
  const dueToday = accountTicklers.filter((item) => item.status === "Due Today").length;
  const overdue = accountTicklers.filter((item) => item.status === "Overdue").length;
  const upcoming = accountTicklers.filter((item) => item.status === "Upcoming").length;
  const completed = accountTicklers.filter((item) => item.status === "Completed").length;

  useEffect(() => {
    if (selectedTicklerId && filteredTicklers.some((tickler) => tickler.id === selectedTicklerId)) return;
    setSelectedTicklerId(filteredTicklers[0]?.id || "");
  }, [filteredTicklers, selectedTicklerId]);

  function submitCompleteTickler() {
    if (!selectedTickler) return;
    if (!completionForm.completionNotes.trim()) {
      setCompletionError("Completion notes are required.");
      return;
    }
    if (!completionForm.outcome.trim()) {
      setCompletionError("Outcome is required.");
      return;
    }
    onCompleteTickler(selectedTickler, completionForm);
    setCompletionForm(emptyTicklerCompletionForm);
    setCompletionError("");
  }

  function submitRescheduleTickler() {
    if (!selectedTickler) return;
    if (!rescheduleForm.newDueDate) {
      setRescheduleError("New Due Date is required.");
      return;
    }
    if (!rescheduleForm.reason.trim()) {
      setRescheduleError("Reschedule reason is required.");
      return;
    }
    onRescheduleTickler(selectedTickler, rescheduleForm);
    setRescheduleForm(emptyTicklerRescheduleForm);
    setRescheduleError("");
  }

  function submitProgressNote() {
    if (!selectedTickler) return;
    if (!progressNote.trim()) {
      setProgressError("Progress note is required.");
      return;
    }
    onAddProgressNote(selectedTickler, progressNote);
    setProgressNote("");
    setProgressError("");
  }

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[rgba(255,106,43,.12)] bg-[rgba(255,255,255,.025)] p-3">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Follow-Up Workflow</p>
          <p className="mt-1 text-sm font-semibold text-[#CBD5E1]">Schedule and manage operational follow-ups without leaving the account.</p>
        </div>
        <button
          type="button"
          onClick={onCreateTickler}
          className="focus-ring min-h-10 rounded-full border border-[rgba(255,106,43,.42)] bg-[rgba(255,106,43,.16)] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#F6F1E8]"
        >
          New Follow-Up
        </button>
      </div>

      <CompactMetricStrip
        items={[
          { label: "Open", value: String(openTicklers) },
          { label: "Due Today", value: String(dueToday) },
          { label: "Overdue", value: String(overdue) },
          { label: "Upcoming", value: String(upcoming) },
          { label: "Completed", value: String(completed) },
        ]}
      />

      <div className="rounded-xl border border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.025)] p-2">
        <p className="mb-2 text-[10px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Tickler Filters</p>
        <div className="flex flex-wrap gap-2">
          {TICKLER_FILTERS.map((filter) => {
            const active = selectedFilter === filter.key;
            return (
              <button
                key={filter.key}
                type="button"
                onClick={() => setSelectedFilter(filter.key)}
                className={`rounded-full border px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.14em] transition ${
                  active
                    ? "border-[rgba(255,106,43,.46)] bg-[rgba(255,106,43,.14)] text-[#F6F1E8]"
                    : "border-[rgba(34,224,208,.2)] bg-[rgba(255,255,255,.04)] text-[#CBD5E1] hover:border-[rgba(34,224,208,.34)]"
                }`}
              >
                {filter.label} {countTicklersForFilter(accountTicklers, filter.key)}
              </button>
            );
          })}
        </div>
      </div>

      {accountTicklers.length ? (
        <div className="grid gap-3 lg:grid-cols-[minmax(18rem,.9fr)_minmax(0,1.5fr)]">
          <section className="h-[34rem] overflow-hidden rounded-xl border border-[rgba(255,106,43,.12)] bg-[rgba(7,18,31,.72)]">
            <div className="flex items-center justify-between gap-3 border-b border-[rgba(255,106,43,.1)] px-3 py-2">
              <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Tickler List</p>
              <span className="rounded-full border border-[rgba(34,224,208,.18)] px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#22E0D0]">
                {filteredTicklers.length} shown
              </span>
            </div>
            <div className="h-[calc(34rem-2.75rem)] overflow-y-auto p-2">
              {filteredTicklers.length ? (
                <div className="grid gap-2">
                  {filteredTicklers.map((tickler) => {
                    const active = selectedTickler?.id === tickler.id;
                    return (
                      <button
                        key={tickler.id}
                        type="button"
                        onClick={() => setSelectedTicklerId(tickler.id)}
                        className={`rounded-xl border p-2.5 text-left transition ${
                          active
                            ? "border-[rgba(255,106,43,.42)] bg-[rgba(255,106,43,.12)]"
                            : "border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.03)] hover:border-[rgba(34,224,208,.3)]"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{tickler.ticklerType}</p>
                            <p className="mt-1 text-sm font-semibold leading-5 text-[#F6F1E8]">{tickler.title}</p>
                          </div>
                          <span className={`shrink-0 rounded-full border px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${ticklerStatusBadgeClass(tickler.status, tickler.priority)}`}>
                            {tickler.status}
                          </span>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2 text-[11px] font-black uppercase tracking-[0.12em] text-[#CBD5E1]">
                          <span>{formatDate(tickler.dueDate)}</span>
                          <span>|</span>
                          <span>{tickler.priority}</span>
                          <span>|</span>
                          <span>{tickler.assignedCollector || "Not Captured"}</span>
                        </div>
                        <p className="mt-2 overflow-hidden text-sm font-semibold leading-5 text-[#CBD5E1] [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
                          {tickler.title || "Not Captured"}
                        </p>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <ManagerEmptyState
                  title="No ticklers match"
                  description={`No ticklers match the selected filter for ${account.customerName}.`}
                  action="Select another filter to review open, due today, overdue, upcoming, completed, or critical ticklers."
                />
              )}
            </div>
          </section>

          <section className="h-[34rem] overflow-hidden rounded-xl border border-[rgba(255,106,43,.12)] bg-[rgba(7,18,31,.72)]">
            <div className="flex items-center justify-between gap-3 border-b border-[rgba(255,106,43,.1)] px-3 py-2">
              <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Selected Tickler Detail</p>
              {selectedTickler ? (
                <span className={`rounded-full border px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${ticklerStatusBadgeClass(selectedTickler.status, selectedTickler.priority)}`}>
                  {selectedTickler.priority}
                </span>
              ) : null}
            </div>
            <div className="h-[calc(34rem-2.75rem)] overflow-y-auto p-3">
              {selectedTickler ? (
                <div className="grid gap-3">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => onEditTickler(selectedTickler)}
                      className="focus-ring rounded-full border border-[rgba(34,224,208,.24)] bg-[rgba(34,224,208,.08)] px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em] text-[#22E0D0]"
                    >
                      Edit Follow-Up
                    </button>
                    <button
                      type="button"
                      onClick={() => onCancelTickler(selectedTickler)}
                      className="focus-ring rounded-full border border-[rgba(148,163,184,.24)] bg-[rgba(148,163,184,.08)] px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em] text-[#CBD5E1]"
                    >
                      Cancel Follow-Up
                    </button>
                    <button
                      type="button"
                      onClick={() => onGenerateLetter(selectedTickler)}
                      className="focus-ring rounded-full border border-[rgba(255,106,43,.28)] bg-[rgba(255,106,43,.1)] px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em] text-[#FF6A2B]"
                    >
                      Generate Letter
                    </button>
                    <button
                      type="button"
                      onClick={() => onGenerateStatement(selectedTickler)}
                      className="focus-ring rounded-full border border-[rgba(255,106,43,.28)] bg-[rgba(255,106,43,.1)] px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em] text-[#FF6A2B]"
                    >
                      Generate Statement
                    </button>
                  </div>

                  <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                    <TicklerField label="Tickler Type" value={selectedTickler.ticklerType} />
                    <TicklerField label="Due Date" value={formatDate(selectedTickler.dueDate)} />
                    <TicklerField label="Due Time" value={getTicklerDueTime(selectedTickler)} />
                    <TicklerField label="Priority" value={selectedTickler.priority} />
                    <TicklerField label="Status" value={selectedTickler.status} />
                    <TicklerField label="Assigned User" value={selectedTickler.assignedCollector || "Not Captured"} />
                  </div>

                  <div className="grid gap-2 xl:grid-cols-3">
                    <TicklerField label="Reminder Notes" value={getTicklerReminderNotes(selectedTickler)} />
                    <TicklerField label="Outcome" value={getTicklerOutcome(selectedTickler) || selectedLatestHistory?.outcome || "Not Captured"} />
                    <TicklerField label="Next Action" value={getTicklerNextAction(selectedTickler) || selectedLatestHistory?.nextAction || "Not Captured"} />
                  </div>

                  <AuditStrip
                    createdBy={getTicklerCreatedBy(selectedTickler)}
                    createdDate={formatCapturedDate(getTicklerCreatedAt(selectedTickler))}
                    createdTime={formatCapturedTime(getTicklerCreatedAt(selectedTickler))}
                    updatedBy={getTicklerUpdatedBy(selectedTickler)}
                    updatedDate={formatCapturedDate(getTicklerUpdatedAt(selectedTickler))}
                    updatedTime={formatCapturedTime(getTicklerUpdatedAt(selectedTickler))}
                  />

                  <div className="grid gap-2">
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-[#22E0D0]">Selected Tickler History</p>
                    {selectedTicklerHistoryEntries.length ? (
                      <OperationalHistoryRows entries={selectedTicklerHistoryEntries} />
                    ) : (
                      <ManagerEmptyState
                        title="No tickler history"
                        description="No history is available for this selected tickler."
                        action="Follow-up lifecycle events will appear here when captured."
                      />
                    )}
                  </div>

                  <div className="grid gap-3 xl:grid-cols-3">
                    <div className="rounded-xl border border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.03)] p-3">
                      <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Complete Follow-Up</p>
                      <textarea
                        value={completionForm.completionNotes}
                        onChange={(event) => setCompletionForm((current) => ({ ...current, completionNotes: event.target.value }))}
                        placeholder="Completion notes"
                        rows={3}
                        className="focus-ring mt-2 w-full rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 py-2 text-sm font-semibold text-[#F6F1E8]"
                      />
                      <input
                        value={completionForm.outcome}
                        onChange={(event) => setCompletionForm((current) => ({ ...current, outcome: event.target.value }))}
                        placeholder="Outcome"
                        className="focus-ring mt-2 min-h-10 w-full rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                      />
                      {completionError ? <p className="mt-2 text-xs font-bold text-[#FCA5A5]">{completionError}</p> : null}
                      <button type="button" onClick={submitCompleteTickler} className="focus-ring mt-2 min-h-9 rounded-full border border-[rgba(34,197,94,.32)] bg-[rgba(34,197,94,.1)] px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#86EFAC]">
                        Complete
                      </button>
                    </div>

                    <div className="rounded-xl border border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.03)] p-3">
                      <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Reschedule</p>
                      <input
                        type="date"
                        value={rescheduleForm.newDueDate}
                        onChange={(event) => setRescheduleForm((current) => ({ ...current, newDueDate: event.target.value }))}
                        className="focus-ring mt-2 min-h-10 w-full rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                      />
                      <input
                        type="time"
                        value={rescheduleForm.newDueTime}
                        onChange={(event) => setRescheduleForm((current) => ({ ...current, newDueTime: event.target.value }))}
                        className="focus-ring mt-2 min-h-10 w-full rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                      />
                      <input
                        value={rescheduleForm.reason}
                        onChange={(event) => setRescheduleForm((current) => ({ ...current, reason: event.target.value }))}
                        placeholder="Reason"
                        className="focus-ring mt-2 min-h-10 w-full rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                      />
                      {rescheduleError ? <p className="mt-2 text-xs font-bold text-[#FCA5A5]">{rescheduleError}</p> : null}
                      <button type="button" onClick={submitRescheduleTickler} className="focus-ring mt-2 min-h-9 rounded-full border border-[rgba(255,106,43,.32)] bg-[rgba(255,106,43,.1)] px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#FF6A2B]">
                        Reschedule
                      </button>
                    </div>

                    <div className="rounded-xl border border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.03)] p-3">
                      <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Progress Note</p>
                      <textarea
                        value={progressNote}
                        onChange={(event) => setProgressNote(event.target.value)}
                        placeholder="Add progress note"
                        rows={4}
                        className="focus-ring mt-2 w-full rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 py-2 text-sm font-semibold text-[#F6F1E8]"
                      />
                      {progressError ? <p className="mt-2 text-xs font-bold text-[#FCA5A5]">{progressError}</p> : null}
                      <button type="button" onClick={submitProgressNote} className="focus-ring mt-2 min-h-9 rounded-full border border-[rgba(34,224,208,.24)] bg-[rgba(34,224,208,.08)] px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#22E0D0]">
                        Add Note
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <ManagerEmptyState
                  title="No tickler selected"
                  description="Select a tickler to view its details."
                  action="Due date, priority, owner, outcome, next action, and audit information will appear here."
                />
              )}
            </div>
          </section>
        </div>
      ) : (
        <ManagerEmptyState
          title="No ticklers available"
          description="No ticklers are available for this account."
          action="Follow-up reminders will appear here when they exist for this account."
        />
      )}
    </div>
  );
}

type AccountTicklerRecord = ReturnType<typeof loadARRecoveryWorkspace>["ticklers"][number];
type AccountTicklerFilterKey = "all" | "open" | "due_today" | "overdue" | "upcoming" | "completed" | "critical";

const FOLLOW_UP_TYPE_OPTIONS: ARRecoveryFollowUpType[] = [
  "Customer Call",
  "Promise Verification",
  "Payment Verification",
  "Statement Follow-Up",
  "Letter Follow-Up",
  "Manager Review",
  "Documentation Follow-Up",
  "Research",
  "Internal Follow-Up",
  "Other",
];

const TICKLER_FILTERS: Array<{ key: AccountTicklerFilterKey; label: string }> = [
  { key: "all", label: "All" },
  { key: "open", label: "Open" },
  { key: "due_today", label: "Due Today" },
  { key: "overdue", label: "Overdue" },
  { key: "upcoming", label: "Upcoming" },
  { key: "completed", label: "Completed" },
  { key: "critical", label: "Critical" },
];

function ticklerMatchesFilter(tickler: AccountTicklerRecord, filter: AccountTicklerFilterKey) {
  if (filter === "all") return true;
  if (filter === "open") return tickler.status !== "Completed" && tickler.status !== "Cancelled";
  if (filter === "due_today") return tickler.status === "Due Today" || isToday(tickler.dueDate);
  if (filter === "overdue") return tickler.status === "Overdue";
  if (filter === "upcoming") return tickler.status === "Upcoming";
  if (filter === "completed") return tickler.status === "Completed";
  return tickler.priority === "Critical";
}

function countTicklersForFilter(ticklers: AccountTicklerRecord[], filter: AccountTicklerFilterKey) {
  return ticklers.filter((tickler) => ticklerMatchesFilter(tickler, filter)).length;
}

function ticklerStatusBadgeClass(status: AccountTicklerRecord["status"], priority: AccountTicklerRecord["priority"]) {
  if (status === "Cancelled") return "border-[rgba(148,163,184,.24)] bg-[rgba(148,163,184,.1)] text-[#CBD5E1]";
  if (priority === "Critical" || status === "Overdue") return "border-[rgba(248,113,113,.34)] bg-[rgba(248,113,113,.12)] text-[#FCA5A5]";
  if (status === "Completed") return "border-[rgba(34,197,94,.32)] bg-[rgba(34,197,94,.12)] text-[#86EFAC]";
  if (status === "Due Today") return "border-[rgba(255,106,43,.34)] bg-[rgba(255,106,43,.12)] text-[#FF6A2B]";
  return "border-[rgba(34,224,208,.3)] bg-[rgba(34,224,208,.1)] text-[#22E0D0]";
}

function deriveTicklerStatusFromDate(dueDate: string): AccountTicklerRecord["status"] {
  if (isToday(dueDate)) return "Due Today";
  if (getDateTime(dueDate) < startOfTodayTime()) return "Overdue";
  return "Upcoming";
}

function buildTicklerFormFromRecord(tickler: AccountTicklerRecord): ARRecoveryTicklerFormState {
  const workflowTickler = tickler as Partial<ARRecoveryTicklerWorkflowRecord>;
  return {
    followUpType: mapTicklerTypeToFollowUpType(tickler.ticklerType),
    dueDate: tickler.dueDate,
    dueTime: workflowTickler.dueTime || "",
    priority: normalizeTicklerPriority(tickler.priority),
    assignedTo: tickler.assignedCollector || "",
    reminderRequired: Boolean(workflowTickler.reminderRequired),
    reminderNotes: workflowTickler.reminderNotes || "",
    relatedInvoice: workflowTickler.relatedInvoice || "",
    relatedPromise: workflowTickler.relatedPromise || "",
    relatedTask: workflowTickler.relatedTask || "",
    notes: workflowTickler.notes || tickler.title || "",
    outcome: workflowTickler.outcome || "",
    nextAction: workflowTickler.nextAction || "",
  };
}

function buildTicklerWorkflowRecordFromForm({
  form,
  account,
  companyId,
  existingTickler,
  now,
  actorName,
}: {
  form: ARRecoveryTicklerFormState;
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  companyId: string;
  existingTickler?: AccountTicklerRecord;
  now: string;
  actorName: string;
}): ARRecoveryTicklerWorkflowRecord {
  const existingWorkflow = existingTickler ? buildTicklerWorkflowRecordFromExisting(existingTickler, account, now, actorName) : null;
  const ticklerId = existingTickler?.id || `tickler-${account.id}-${Date.now()}`;
  return {
    id: ticklerId,
    companyId,
    accountId: account.id,
    customerName: account.customerName,
    title: `${form.followUpType || "Follow-Up"} - ${account.customerName}`,
    ticklerType: form.followUpType || "Other",
    dueDate: form.dueDate,
    status: existingTickler?.status === "Completed" || existingTickler?.status === "Cancelled" ? existingTickler.status : deriveTicklerStatusFromDate(form.dueDate),
    priority: form.priority,
    assignedCollector: form.assignedTo.trim(),
    dueTime: form.dueTime,
    reminderRequired: form.reminderRequired,
    reminderNotes: form.reminderNotes.trim(),
    relatedInvoice: form.relatedInvoice,
    relatedPromise: form.relatedPromise,
    relatedTask: form.relatedTask,
    notes: form.notes.trim(),
    outcome: form.outcome.trim(),
    nextAction: form.nextAction.trim(),
    createdBy: existingWorkflow?.createdBy || actorName || account.assignedCollector || "System",
    createdAt: existingWorkflow?.createdAt || now,
    updatedBy: actorName || account.assignedCollector || "System",
    updatedAt: now,
    completedBy: existingWorkflow?.completedBy || "",
    completedAt: existingWorkflow?.completedAt || "",
    completionNotes: existingWorkflow?.completionNotes || "",
    cancelledBy: existingWorkflow?.cancelledBy || "",
    cancelledAt: existingWorkflow?.cancelledAt || "",
    rescheduleHistory: existingWorkflow?.rescheduleHistory || [],
    progressNotes: existingWorkflow?.progressNotes || [],
  };
}

function buildTicklerWorkflowRecordFromExisting(
  tickler: AccountTicklerRecord,
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>,
  now: string,
  actorName?: string,
): ARRecoveryTicklerWorkflowRecord {
  const workflowTickler = tickler as Partial<ARRecoveryTicklerWorkflowRecord>;
  return {
    ...tickler,
    dueTime: workflowTickler.dueTime || "",
    reminderRequired: Boolean(workflowTickler.reminderRequired),
    reminderNotes: workflowTickler.reminderNotes || "",
    relatedInvoice: workflowTickler.relatedInvoice || "",
    relatedPromise: workflowTickler.relatedPromise || "",
    relatedTask: workflowTickler.relatedTask || "",
    notes: workflowTickler.notes || tickler.title || "",
    outcome: workflowTickler.outcome || "",
    nextAction: workflowTickler.nextAction || "",
    createdBy: workflowTickler.createdBy || tickler.assignedCollector || actorName || account.assignedCollector || "System",
    createdAt: workflowTickler.createdAt || tickler.dueDate || now,
    updatedBy: workflowTickler.updatedBy || tickler.assignedCollector || actorName || account.assignedCollector || "System",
    updatedAt: workflowTickler.updatedAt || now,
    completedBy: workflowTickler.completedBy || "",
    completedAt: workflowTickler.completedAt || "",
    completionNotes: workflowTickler.completionNotes || "",
    cancelledBy: workflowTickler.cancelledBy || "",
    cancelledAt: workflowTickler.cancelledAt || "",
    rescheduleHistory: workflowTickler.rescheduleHistory || [],
    progressNotes: workflowTickler.progressNotes || [],
  };
}

function upsertTicklerWorkflowRecord(records: ARRecoveryTicklerWorkflowRecord[], tickler: ARRecoveryTicklerWorkflowRecord) {
  const exists = records.some((record) => record.id === tickler.id);
  return exists
    ? records.map((record) => (record.id === tickler.id ? tickler : record))
    : [tickler, ...records];
}

function mapTicklerTypeToFollowUpType(type: AccountTicklerRecord["ticklerType"]): ARRecoveryFollowUpType {
  if (type === "Callback" || type === "Customer Touchpoint") return "Customer Call";
  if (type === "Promise Follow-Up") return "Promise Verification";
  if (type === "Escalation") return "Manager Review";
  if (type === "Custom") return "Other";
  if (type === "Other") return "Other";
  return type;
}

function normalizeTicklerPriority(priority: AccountTicklerRecord["priority"]): ARRecoveryTaskPriority {
  if (priority === "Medium") return "Normal";
  return priority;
}

function getTicklerDueTime(tickler: AccountTicklerRecord) {
  return (tickler as Partial<ARRecoveryTicklerWorkflowRecord>).dueTime || "Not Captured";
}

function getTicklerReminderNotes(tickler: AccountTicklerRecord) {
  return (tickler as Partial<ARRecoveryTicklerWorkflowRecord>).reminderNotes || "Not Captured";
}

function getTicklerRelatedInvoice(tickler: AccountTicklerRecord) {
  return (tickler as Partial<ARRecoveryTicklerWorkflowRecord>).relatedInvoice || "";
}

function getTicklerRelatedPromise(tickler: AccountTicklerRecord) {
  return (tickler as Partial<ARRecoveryTicklerWorkflowRecord>).relatedPromise || "";
}

function getTicklerRelatedTask(tickler: AccountTicklerRecord) {
  return (tickler as Partial<ARRecoveryTicklerWorkflowRecord>).relatedTask || "";
}

function getTicklerOutcome(tickler: AccountTicklerRecord) {
  return (tickler as Partial<ARRecoveryTicklerWorkflowRecord>).outcome || "";
}

function getTicklerNextAction(tickler: AccountTicklerRecord) {
  return (tickler as Partial<ARRecoveryTicklerWorkflowRecord>).nextAction || "";
}

function getTicklerCreatedBy(tickler: AccountTicklerRecord) {
  return (tickler as Partial<ARRecoveryTicklerWorkflowRecord>).createdBy || "Not Captured";
}

function getTicklerCreatedAt(tickler: AccountTicklerRecord) {
  return (tickler as Partial<ARRecoveryTicklerWorkflowRecord>).createdAt || "";
}

function getTicklerUpdatedBy(tickler: AccountTicklerRecord) {
  return (tickler as Partial<ARRecoveryTicklerWorkflowRecord>).updatedBy || "Not Captured";
}

function getTicklerUpdatedAt(tickler: AccountTicklerRecord) {
  return (tickler as Partial<ARRecoveryTicklerWorkflowRecord>).updatedAt || "";
}

function AccountDocumentsPanel({
  account,
  documents,
  onCreateDocumentRequest,
  onReceiveDocument,
  onApproveDocument,
  onRejectDocument,
  onRequestAdditionalInfo,
  onCancelDocumentRequest,
}: {
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  documents: AccountDocumentRecord[];
  onCreateDocumentRequest: () => void;
  onReceiveDocument: (documentRequestId: string, fulfillment: ARRecoveryDocumentFulfillmentFormState) => void;
  onApproveDocument: (documentRequestId: string, fulfillment: ARRecoveryDocumentFulfillmentFormState) => void;
  onRejectDocument: (documentRequestId: string, fulfillment: ARRecoveryDocumentFulfillmentFormState) => void;
  onRequestAdditionalInfo: (documentRequestId: string, fulfillment: ARRecoveryDocumentFulfillmentFormState) => void;
  onCancelDocumentRequest: (documentRequestId: string) => void;
}) {
  const [selectedFilter, setSelectedFilter] = useState<AccountDocumentFilterKey>("all");
  const [fulfillmentForm, setFulfillmentForm] = useState<ARRecoveryDocumentFulfillmentFormState>(emptyDocumentFulfillmentForm);
  const [fulfillmentError, setFulfillmentError] = useState("");
  const filteredDocuments = useMemo(
    () => documents.filter((document) => documentMatchesFilter(document, selectedFilter)),
    [documents, selectedFilter],
  );
  const [selectedDocumentId, setSelectedDocumentId] = useState(documents[0]?.id || "");
  const selectedDocument = filteredDocuments.find((document) => document.id === selectedDocumentId) || filteredDocuments[0] || null;
  const selectedRequestId = selectedDocument?.requestId || "";

  useEffect(() => {
    if (selectedDocumentId && filteredDocuments.some((document) => document.id === selectedDocumentId)) return;
    setSelectedDocumentId(filteredDocuments[0]?.id || "");
  }, [filteredDocuments, selectedDocumentId]);

  useEffect(() => {
    setFulfillmentForm(emptyDocumentFulfillmentForm);
    setFulfillmentError("");
  }, [selectedDocumentId]);

  function submitReceived() {
    if (!selectedRequestId) return;
    if (!fulfillmentForm.fileName.trim()) {
      setFulfillmentError("File name is required when marking a document received.");
      return;
    }
    onReceiveDocument(selectedRequestId, fulfillmentForm);
    setFulfillmentForm(emptyDocumentFulfillmentForm);
    setFulfillmentError("");
  }

  function submitApproved() {
    if (!selectedRequestId) return;
    onApproveDocument(selectedRequestId, fulfillmentForm);
    setFulfillmentForm(emptyDocumentFulfillmentForm);
    setFulfillmentError("");
  }

  function submitRejected() {
    if (!selectedRequestId) return;
    if (!fulfillmentForm.rejectionReason.trim()) {
      setFulfillmentError("Rejection reason is required.");
      return;
    }
    onRejectDocument(selectedRequestId, fulfillmentForm);
    setFulfillmentForm(emptyDocumentFulfillmentForm);
    setFulfillmentError("");
  }

  function submitAdditionalInfo() {
    if (!selectedRequestId) return;
    if (!fulfillmentForm.additionalInfoNotes.trim()) {
      setFulfillmentError("Additional information notes are required.");
      return;
    }
    onRequestAdditionalInfo(selectedRequestId, fulfillmentForm);
    setFulfillmentForm(emptyDocumentFulfillmentForm);
    setFulfillmentError("");
  }

  return (
    <div className="grid gap-3">
      <CompactMetricStrip
        items={[
          { label: "Documents", value: String(documents.length) },
          { label: "Statements", value: String(countDocumentsForFilter(documents, "statements")) },
          { label: "Letters", value: String(countDocumentsForFilter(documents, "letters")) },
          { label: "Attachments", value: String(countDocumentsForFilter(documents, "attachments")) },
          { label: "Contracts", value: String(countDocumentsForFilter(documents, "contracts")) },
        ]}
      />

      <div className="rounded-xl border border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.025)] p-2">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <p className="text-[10px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">Document Filters</p>
          <button
            type="button"
            onClick={onCreateDocumentRequest}
            className="focus-ring min-h-9 rounded-full border border-[rgba(255,106,43,.36)] bg-[rgba(255,106,43,.12)] px-3 text-[11px] font-black uppercase tracking-[0.12em] text-[#FF6A2B]"
          >
            New Document Request
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {DOCUMENT_FILTERS.map((filter) => {
            const active = selectedFilter === filter.key;
            return (
              <button
                key={filter.key}
                type="button"
                onClick={() => setSelectedFilter(filter.key)}
                className={`rounded-full border px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.14em] transition ${
                  active
                    ? "border-[rgba(255,106,43,.46)] bg-[rgba(255,106,43,.14)] text-[#F6F1E8]"
                    : "border-[rgba(34,224,208,.2)] bg-[rgba(255,255,255,.04)] text-[#CBD5E1] hover:border-[rgba(34,224,208,.34)]"
                }`}
              >
                {filter.label} {countDocumentsForFilter(documents, filter.key)}
              </button>
            );
          })}
        </div>
      </div>

      {documents.length ? (
        <div className="grid gap-3 lg:grid-cols-[minmax(18rem,.9fr)_minmax(0,1.5fr)]">
          <section className="h-[34rem] overflow-hidden rounded-xl border border-[rgba(255,106,43,.12)] bg-[rgba(7,18,31,.72)]">
            <div className="flex items-center justify-between gap-3 border-b border-[rgba(255,106,43,.1)] px-3 py-2">
              <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Document List</p>
              <span className="rounded-full border border-[rgba(34,224,208,.18)] px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#22E0D0]">
                {filteredDocuments.length} shown
              </span>
            </div>
            <div className="h-[calc(34rem-2.75rem)] overflow-y-auto p-2">
              {filteredDocuments.length ? (
                <div className="grid gap-2">
                  {filteredDocuments.map((document) => {
                    const active = selectedDocument?.id === document.id;
                    return (
                      <button
                        key={document.id}
                        type="button"
                        onClick={() => setSelectedDocumentId(document.id)}
                        className={`rounded-xl border p-2.5 text-left transition ${
                          active
                            ? "border-[rgba(255,106,43,.42)] bg-[rgba(255,106,43,.12)]"
                            : "border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.03)] hover:border-[rgba(34,224,208,.3)]"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="truncate text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{document.title}</p>
                            <p className="mt-1 text-sm font-semibold leading-5 text-[#F6F1E8]">{document.documentType} | {account.customerNumber}</p>
                          </div>
                          <span className={`shrink-0 rounded-full border px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${documentStatusBadgeClass(document.status)}`}>
                            {document.status}
                          </span>
                        </div>
                        <p className="mt-2 overflow-hidden text-sm font-semibold leading-5 text-[#CBD5E1] [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
                          {document.notes || "Not Captured"}
                        </p>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <ManagerEmptyState
                  title="No documents match"
                  description="No documents match the current filter."
                  action="Select another filter to review statements, letters, attachments, proof of delivery, contracts, or other account files."
                />
              )}
            </div>
          </section>

          <section className="h-[34rem] overflow-hidden rounded-xl border border-[rgba(255,106,43,.12)] bg-[rgba(7,18,31,.72)]">
            <div className="flex items-center justify-between gap-3 border-b border-[rgba(255,106,43,.1)] px-3 py-2">
              <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Selected Document Detail</p>
              {selectedDocument ? (
                <span className={`rounded-full border px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${documentStatusBadgeClass(selectedDocument.status)}`}>
                  {selectedDocument.status}
                </span>
              ) : null}
            </div>
            <div className="h-[calc(34rem-2.75rem)] overflow-y-auto p-3">
              {selectedDocument ? (
                <div className="grid gap-3">
                  <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                    <WorkspaceField label="Document Name" value={selectedDocument.title} />
                    <WorkspaceField label="Document Type" value={selectedDocument.documentType} />
                    <WorkspaceField label="Status" value={selectedDocument.status} />
                    <WorkspaceField label="Related Account" value={`${account.customerNumber} | ${account.customerName}`} />
                    <WorkspaceField label="Client Company" value={account.customerName || "Not Captured"} />
                    <WorkspaceField label="Collector" value={account.assignedCollector || "Not Captured"} />
                    <WorkspaceField label="Requested From" value={selectedDocument.requestedFrom || "Not Captured"} />
                    <WorkspaceField label="Due Date" value={formatCapturedDate(selectedDocument.dueDate)} />
                    <WorkspaceField label="Priority" value={selectedDocument.priority || "Not Captured"} />
                    <WorkspaceField label="Related Invoice" value={selectedDocument.relatedInvoice || "Not Captured"} />
                    <WorkspaceField label="Related Task" value={selectedDocument.relatedTask || "Not Captured"} />
                    <WorkspaceField label="File Name" value={selectedDocument.fileName || "Not Captured"} />
                  </div>

                  <div className="grid gap-2 xl:grid-cols-2">
                    <WorkspaceField label="Request Notes" value={selectedDocument.requestNotes || selectedDocument.notes || "Not Captured"} />
                    <WorkspaceField label="Received Notes" value={selectedDocument.receivedNotes || "Not Captured"} />
                    <WorkspaceField label="Review Notes" value={selectedDocument.reviewNotes || "Not Captured"} />
                    <WorkspaceField label="Outcome" value={buildDocumentOutcome(selectedDocument)} />
                    <WorkspaceField label="Next Action" value={buildDocumentNextAction(selectedDocument)} />
                  </div>

                  <AuditStrip
                    createdBy={selectedDocument.requestedBy || "Not Captured"}
                    createdDate={formatCapturedDate(selectedDocument.requestedAt)}
                    createdTime={formatCapturedTime(selectedDocument.requestedAt)}
                    updatedBy={selectedDocument.updatedBy || "Not Captured"}
                    updatedDate={formatCapturedDate(selectedDocument.updatedAt)}
                    updatedTime={formatCapturedTime(selectedDocument.updatedAt)}
                  />

                  {selectedRequestId ? (
                    <section className="rounded-xl border border-[rgba(255,106,43,.14)] bg-[rgba(255,255,255,.025)] p-3">
                      <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Fulfillment Controls</p>
                      <div className="mt-3 grid gap-3 md:grid-cols-2">
                        <CommunicationField label="File Name / Upload Reference">
                          <input
                            value={fulfillmentForm.fileName}
                            onChange={(event) => setFulfillmentForm((current) => ({ ...current, fileName: event.target.value }))}
                            placeholder="POD-ABC-1002.pdf"
                            className="focus-ring min-h-10 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                          />
                        </CommunicationField>
                        <CommunicationField label="Received Notes">
                          <input
                            value={fulfillmentForm.receivedNotes}
                            onChange={(event) => setFulfillmentForm((current) => ({ ...current, receivedNotes: event.target.value }))}
                            placeholder="Document received from client AP."
                            className="focus-ring min-h-10 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                          />
                        </CommunicationField>
                        <CommunicationField label="Review Notes">
                          <input
                            value={fulfillmentForm.reviewNotes}
                            onChange={(event) => setFulfillmentForm((current) => ({ ...current, reviewNotes: event.target.value }))}
                            placeholder="Document is usable for collection follow-up."
                            className="focus-ring min-h-10 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                          />
                        </CommunicationField>
                        <CommunicationField label="Rejection Reason">
                          <input
                            value={fulfillmentForm.rejectionReason}
                            onChange={(event) => setFulfillmentForm((current) => ({ ...current, rejectionReason: event.target.value }))}
                            placeholder="Missing signature, wrong invoice, unreadable file..."
                            className="focus-ring min-h-10 rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 text-sm font-semibold text-[#F6F1E8]"
                          />
                        </CommunicationField>
                        <div className="md:col-span-2">
                          <CommunicationField label="Additional Information Notes">
                            <textarea
                              value={fulfillmentForm.additionalInfoNotes}
                              onChange={(event) => setFulfillmentForm((current) => ({ ...current, additionalInfoNotes: event.target.value }))}
                              rows={2}
                              placeholder="Describe what else is needed."
                              className="focus-ring rounded-xl border border-[rgba(34,224,208,.16)] bg-[#07121f] px-3 py-2 text-sm font-semibold leading-6 text-[#F6F1E8]"
                            />
                          </CommunicationField>
                        </div>
                      </div>

                      {fulfillmentError ? (
                        <div className="mt-3 rounded-xl border border-[rgba(248,113,113,.28)] bg-[rgba(248,113,113,.1)] px-3 py-2 text-sm font-semibold text-[#FCA5A5]">
                          {fulfillmentError}
                        </div>
                      ) : null}

                      <div className="mt-3 flex flex-wrap justify-end gap-2">
                        <button type="button" onClick={submitReceived} className="focus-ring min-h-10 rounded-full border border-[rgba(34,224,208,.28)] bg-[rgba(34,224,208,.1)] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#22E0D0]">
                          Mark Received
                        </button>
                        <button type="button" onClick={submitApproved} className="focus-ring min-h-10 rounded-full border border-[rgba(34,197,94,.3)] bg-[rgba(34,197,94,.12)] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#86EFAC]">
                          Approve
                        </button>
                        <button type="button" onClick={submitRejected} className="focus-ring min-h-10 rounded-full border border-[rgba(248,113,113,.32)] bg-[rgba(248,113,113,.12)] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#FCA5A5]">
                          Reject
                        </button>
                        <button type="button" onClick={submitAdditionalInfo} className="focus-ring min-h-10 rounded-full border border-[rgba(255,106,43,.32)] bg-[rgba(255,106,43,.12)] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#FF6A2B]">
                          Request Info
                        </button>
                        <button type="button" onClick={() => onCancelDocumentRequest(selectedRequestId)} className="focus-ring min-h-10 rounded-full border border-[rgba(148,163,184,.28)] bg-[rgba(148,163,184,.08)] px-4 text-sm font-black uppercase tracking-[0.12em] text-[#CBD5E1]">
                          Cancel Request
                        </button>
                      </div>
                    </section>
                  ) : null}
                </div>
              ) : (
                <ManagerEmptyState
                  title="No document selected"
                  description="Select a document to view its details."
                  action="Document status, context, outcome, next action, and audit information will appear here."
                />
              )}
            </div>
          </section>
        </div>
      ) : (
        <ManagerEmptyState
          title="No documents available"
          description={`No documents are available for ${account.customerName}.`}
          action="Documents will appear here when they exist for this account."
        />
      )}
    </div>
  );
}

type AccountDocumentFilterKey = "all" | "statements" | "letters" | "attachments" | "pod" | "contracts" | "other";

const DOCUMENT_FILTERS: Array<{ key: AccountDocumentFilterKey; label: string }> = [
  { key: "all", label: "All" },
  { key: "statements", label: "Statements" },
  { key: "letters", label: "Letters" },
  { key: "attachments", label: "Attachments" },
  { key: "pod", label: "Proof of Delivery" },
  { key: "contracts", label: "Contracts" },
  { key: "other", label: "Other" },
];

function documentMatchesFilter(document: AccountDocumentRecord, filter: AccountDocumentFilterKey) {
  const normalized = `${document.title} ${document.documentType} ${document.status} ${document.notes}`.toLowerCase();
  if (filter === "all") return true;
  if (filter === "statements") return normalized.includes("statement");
  if (filter === "letters") return normalized.includes("letter");
  if (filter === "attachments") return normalized.includes("attachment") || normalized.includes("file") || normalized.includes("packet");
  if (filter === "pod") return normalized.includes("pod") || normalized.includes("proof of delivery");
  if (filter === "contracts") return normalized.includes("contract");
  return !["statement", "letter", "attachment", "file", "packet", "pod", "proof of delivery", "contract"].some((term) => normalized.includes(term));
}

function countDocumentsForFilter(documents: AccountDocumentRecord[], filter: AccountDocumentFilterKey) {
  return documents.filter((document) => documentMatchesFilter(document, filter)).length;
}

function documentStatusBadgeClass(status: AccountDocumentRecord["status"]) {
  if (["Archived", "Approved", "Received"].includes(status)) return "border-[rgba(34,197,94,.32)] bg-[rgba(34,197,94,.12)] text-[#86EFAC]";
  if (["Requested", "Waiting", "Under Review"].includes(status) || status.toLowerCase().includes("pending")) return "border-[rgba(255,106,43,.34)] bg-[rgba(255,106,43,.12)] text-[#FF6A2B]";
  if (["Rejected", "Cancelled", "Failed"].includes(status)) return "border-[rgba(248,113,113,.34)] bg-[rgba(248,113,113,.12)] text-[#FCA5A5]";
  return "border-[rgba(34,224,208,.3)] bg-[rgba(34,224,208,.1)] text-[#22E0D0]";
}

function buildDocumentOutcome(document: AccountDocumentRecord) {
  if (document.status === "Approved") return document.reviewNotes || "Document approved for recovery use.";
  if (document.status === "Rejected") return document.rejectionReason || "Document rejected.";
  if (document.status === "Received") return document.receivedNotes || document.fileName || "Document received.";
  if (document.status === "Waiting") return document.additionalInfoNotes || "Waiting for additional information.";
  if (document.status === "Cancelled") return "Document request cancelled.";
  return document.notes || document.status || "Not Captured";
}

function buildDocumentNextAction(document: AccountDocumentRecord) {
  if (document.status === "Requested") return `Follow up by ${formatCapturedDate(document.dueDate)}.`;
  if (document.status === "Waiting") return "Wait for the requester to provide the additional information.";
  if (document.status === "Received" || document.status === "Under Review") return "Review the document and approve or reject it.";
  if (document.status === "Rejected") return document.additionalInfoNotes || "Request corrected documentation.";
  if (document.status === "Cancelled") return "No further document action required.";
  if (document.status === "Archived" || document.status === "Approved") return "Use as account reference when needed.";
  return "Review document status and determine next action.";
}

function AccountHistoryPanel({
  account,
  records,
}: {
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>;
  records: ReturnType<typeof buildRecoveryHistory>;
}) {
  const [selectedFilter, setSelectedFilter] = useState<AccountHistoryFilterKey>("all");
  const filteredRecords = useMemo(
    () => records.filter((record) => historyMatchesFilter(record, selectedFilter)),
    [records, selectedFilter],
  );
  const [selectedHistoryId, setSelectedHistoryId] = useState(records[0]?.id || "");
  const selectedHistory = filteredRecords.find((record) => record.id === selectedHistoryId) || filteredRecords[0] || null;

  useEffect(() => {
    if (selectedHistoryId && filteredRecords.some((record) => record.id === selectedHistoryId)) return;
    setSelectedHistoryId(filteredRecords[0]?.id || "");
  }, [filteredRecords, selectedHistoryId]);

  return (
    <div className="grid gap-3">
      <CompactMetricStrip
        items={[
          { label: "History Records", value: String(records.length) },
          { label: "Calls", value: String(countHistoryForFilter(records, "calls")) },
          { label: "Notes", value: String(countHistoryForFilter(records, "notes")) },
          { label: "Comms", value: String(countHistoryForFilter(records, "communications")) },
          { label: "Promises", value: String(countHistoryForFilter(records, "promises")) },
        ]}
      />

      <div className="rounded-xl border border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.025)] p-2">
        <p className="mb-2 text-[10px] font-black uppercase tracking-[0.16em] text-[#22E0D0]">History Filters</p>
        <div className="flex flex-wrap gap-2">
          {HISTORY_FILTERS.map((filter) => {
            const active = selectedFilter === filter.key;
            return (
              <button
                key={filter.key}
                type="button"
                onClick={() => setSelectedFilter(filter.key)}
                className={`rounded-full border px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.14em] transition ${
                  active
                    ? "border-[rgba(255,106,43,.46)] bg-[rgba(255,106,43,.14)] text-[#F6F1E8]"
                    : "border-[rgba(34,224,208,.2)] bg-[rgba(255,255,255,.04)] text-[#CBD5E1] hover:border-[rgba(34,224,208,.34)]"
                }`}
              >
                {filter.label} {countHistoryForFilter(records, filter.key)}
              </button>
            );
          })}
        </div>
      </div>

      {records.length ? (
        <div className="grid min-w-0 max-w-full gap-3">
          <section className="min-w-0 max-w-full rounded-xl border border-[rgba(255,106,43,.12)] bg-[rgba(7,18,31,.72)]">
            <div className="flex items-center justify-between gap-3 border-b border-[rgba(255,106,43,.1)] px-3 py-2">
              <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">History List</p>
              <span className="rounded-full border border-[rgba(34,224,208,.18)] px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#22E0D0]">
                {filteredRecords.length} shown
              </span>
            </div>
            <div className="max-h-[26rem] overflow-y-auto p-2">
              {filteredRecords.length ? (
                <div className="grid min-w-0 gap-2 lg:grid-cols-[repeat(2,minmax(0,1fr))] 2xl:grid-cols-[repeat(3,minmax(0,1fr))]">
                  {filteredRecords.map((record) => {
                    const active = selectedHistory?.id === record.id;
                    return (
                      <button
                        key={record.id}
                        type="button"
                        onClick={() => setSelectedHistoryId(record.id)}
                        className={`min-w-0 max-w-full rounded-xl border p-2.5 text-left transition ${
                          active
                            ? "border-[rgba(255,106,43,.42)] bg-[rgba(255,106,43,.12)]"
                            : "border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.03)] hover:border-[rgba(34,224,208,.3)]"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="truncate text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{record.title}</p>
                            <p className="mt-1 text-sm font-semibold leading-5 text-[#F6F1E8]">
                              {formatCapturedDate(record.createdAt)} | {formatCapturedTime(record.createdAt)}
                            </p>
                          </div>
                          <span className={`shrink-0 rounded-full border px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${timelineStatusBadgeClass(record.kind, record.title)}`}>
                            {record.kind}
                          </span>
                        </div>
                        <p className="mt-2 overflow-hidden text-sm font-semibold leading-5 text-[#CBD5E1] [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
                          {record.detail || "Not Captured"}
                        </p>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <ManagerEmptyState
                  title="No history records match"
                  description="No history records match the current filter."
                  action="Select another filter to review calls, notes, promises, ticklers, letters, statements, escalations, or payments."
                />
              )}
            </div>
          </section>

          <section className="min-w-0 max-w-full rounded-xl border border-[rgba(255,106,43,.12)] bg-[rgba(7,18,31,.72)]">
            <div className="flex items-center justify-between gap-3 border-b border-[rgba(255,106,43,.1)] px-3 py-2">
              <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Selected History Detail</p>
              {selectedHistory ? (
                <span className={`rounded-full border px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${timelineStatusBadgeClass(selectedHistory.kind, selectedHistory.title)}`}>
                  {selectedHistory.kind}
                </span>
              ) : null}
            </div>
            <div className="p-3">
              {selectedHistory ? (
                <div className="grid min-w-0 gap-3">
                  <div className="grid min-w-0 gap-2 sm:grid-cols-[repeat(2,minmax(0,1fr))] xl:grid-cols-[repeat(3,minmax(0,1fr))]">
                    <WorkspaceField label="Event Type" value={selectedHistory.kind} />
                    <WorkspaceField label="Date" value={formatCapturedDate(selectedHistory.createdAt)} />
                    <WorkspaceField label="Time" value={formatCapturedTime(selectedHistory.createdAt)} />
                    <WorkspaceField label="User" value={selectedHistory.actorName || account.assignedCollector || "System"} />
                    <WorkspaceField label="Related Account" value={`${account.customerNumber} | ${account.customerName}`} />
                    <WorkspaceField label="Status" value={selectedHistory.kind} />
                  </div>

                  <div className="grid min-w-0 gap-2 xl:grid-cols-[repeat(2,minmax(0,1fr))]">
                    <WorkspaceField label="Outcome" value={selectedHistory.detail || selectedHistory.title || "Not Captured"} />
                    <WorkspaceField label="Next Action" value={buildHistoryNextAction(selectedHistory)} />
                  </div>

                  <AuditStrip
                    createdBy={selectedHistory.actorName || account.assignedCollector || "System"}
                    createdDate={formatCapturedDate(selectedHistory.createdAt)}
                    createdTime={formatCapturedTime(selectedHistory.createdAt)}
                    updatedBy="Not Captured"
                    updatedDate="Not Captured"
                    updatedTime="Not Captured"
                  />
                </div>
              ) : (
                <ManagerEmptyState
                  title="No history selected"
                  description="Select a history record to view its details."
                  action="Outcome, next action, related account, and audit information will appear here."
                />
              )}
            </div>
          </section>
        </div>
      ) : (
        <ManagerEmptyState
          title="No history available"
          description={`No account history is available for ${account.customerName}.`}
          action="History records will appear here when activity, promise, or tickler records exist for this account."
        />
      )}
    </div>
  );
}

type AccountHistoryRecord = ReturnType<typeof buildRecoveryHistory>[number];
type AccountHistoryFilterKey = "all" | "calls" | "notes" | "communications" | "promises" | "ticklers" | "letters" | "statements" | "escalations" | "payments";

const HISTORY_FILTERS: Array<{ key: AccountHistoryFilterKey; label: string }> = [
  { key: "all", label: "All" },
  { key: "calls", label: "Calls" },
  { key: "notes", label: "Notes" },
  { key: "communications", label: "Communications" },
  { key: "promises", label: "Promises" },
  { key: "ticklers", label: "Ticklers" },
  { key: "letters", label: "Letters" },
  { key: "statements", label: "Statements" },
  { key: "escalations", label: "Escalations" },
  { key: "payments", label: "Payments" },
];

function historyMatchesFilter(record: AccountHistoryRecord, filter: AccountHistoryFilterKey) {
  const normalized = `${record.kind} ${record.title} ${record.detail}`.toLowerCase();
  if (filter === "all") return true;
  if (filter === "calls") return normalized.includes("call");
  if (filter === "notes") return normalized.includes("note");
  if (filter === "communications") return normalized.includes("team message") || normalized.includes("team reply") || normalized.includes("message type") || normalized.includes("communication");
  if (filter === "promises") return normalized.includes("promise");
  if (filter === "ticklers") return normalized.includes("tickler") || normalized.includes("callback") || normalized.includes("follow-up");
  if (filter === "letters") return normalized.includes("letter");
  if (filter === "statements") return normalized.includes("statement");
  if (filter === "escalations") return normalized.includes("escalation") || normalized.includes("review");
  return normalized.includes("payment") || normalized.includes("paid");
}

function countHistoryForFilter(records: AccountHistoryRecord[], filter: AccountHistoryFilterKey) {
  return records.filter((record) => historyMatchesFilter(record, filter)).length;
}

function buildHistoryNextAction(record: AccountHistoryRecord) {
  const normalized = `${record.kind} ${record.title} ${record.detail}`.toLowerCase();
  const capturedNextAction = extractNextActionFromDetail(record.detail);
  if (capturedNextAction) return capturedNextAction;

  if (normalized.includes("team message") || normalized.includes("team reply") || normalized.includes("message type")) return "Review internal conversation and coordinate next AR action.";
  if (normalized.includes("promise")) return "Review promise status and follow-up timing.";
  if (normalized.includes("overdue") || normalized.includes("escalation")) return "Review risk and determine escalation action.";
  if (normalized.includes("payment") || normalized.includes("paid")) return "Confirm remaining balance and next account action.";
  return "Review account context and determine the next collector action.";
}

function TicklerField({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return <WorkspaceField label={label} value={value} />;
}

function NoteField({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return <WorkspaceField label={label} value={value} />;
}

function LetterField({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return <WorkspaceField label={label} value={value} />;
}

function StatementField({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return <WorkspaceField label={label} value={value} />;
}

function WorkspaceField({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  const displayValue = value || "Not Captured";

  return (
    <div className="min-w-0 overflow-hidden rounded-xl border border-[#BFEAFF] bg-white p-3 shadow-[0_8px_20px_rgba(15,23,42,.03)]" title={displayValue}>
      <p className="truncate text-xs font-medium text-[#475569]">{label}</p>
      <p className="mt-1 min-w-0 break-words text-sm font-semibold leading-6 text-[#0A2540]">{displayValue}</p>
    </div>
  );
}

function RelatedAccountSummary({
  accountNumber,
  accountName,
  currentBalance,
}: {
  accountNumber: string;
  accountName: string;
  currentBalance: string;
}) {
  return (
    <div className="min-w-0 max-w-full overflow-hidden rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] p-3">
      <p className="break-words text-xs font-medium text-[#475569]">Related Account</p>
      <div className="mt-2 grid min-w-0 max-w-full gap-2 md:grid-cols-[repeat(3,minmax(0,1fr))]">
        <div className="min-w-0 max-w-full rounded-xl border border-[#BFEAFF] bg-white p-3">
          <p className="text-xs font-medium text-[#475569]">Account Number</p>
          <p className="mt-1 max-w-full break-words text-sm font-semibold leading-6 text-[#0A2540]" title={accountNumber}>{accountNumber || "Not Captured"}</p>
        </div>
        <div className="min-w-0 max-w-full rounded-xl border border-[#BFEAFF] bg-white p-3">
          <p className="text-xs font-medium text-[#475569]">Account Name</p>
          <p className="mt-1 max-w-full break-words text-sm font-semibold leading-6 text-[#0A2540]" title={accountName}>{accountName || "Not Captured"}</p>
        </div>
        <div className="min-w-0 max-w-full rounded-xl border border-[#BFEAFF] bg-white p-3">
          <p className="text-xs font-medium text-[#475569]">Current Balance</p>
          <p className="mt-1 max-w-full break-words text-sm font-semibold leading-6 text-[#0A2540]" title={currentBalance}>{currentBalance || "Not Captured"}</p>
        </div>
      </div>
    </div>
  );
}

function CompactMetricStrip({
  items,
}: {
  items: Array<{ label: string; value: string }>;
}) {
  return (
    <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-5">
      {items.map((item) => (
        <div key={item.label} className="min-w-0 overflow-hidden rounded-xl border border-[#BFEAFF] bg-white px-3 py-2" title={`${item.label}: ${item.value}`}>
          <p className="text-xs font-medium text-[#475569]">{item.label}</p>
          <p className="mt-0.5 min-w-0 truncate text-sm font-semibold leading-5 text-[#0A2540]">{item.value}</p>
        </div>
      ))}
    </div>
  );
}

function AuditStrip({
  createdBy,
  createdDate,
  createdTime,
  updatedBy,
  updatedDate,
  updatedTime,
  containText = false,
}: {
  createdBy: string;
  createdDate: string;
  createdTime: string;
  updatedBy: string;
  updatedDate: string;
  updatedTime: string;
  containText?: boolean;
}) {
  const auditCardClass = containText
    ? "box-border h-auto w-full min-w-0 max-w-full overflow-visible rounded-xl border border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.03)] px-3 py-2"
    : "min-w-0 max-w-full overflow-hidden rounded-xl border border-[rgba(34,224,208,.12)] bg-[rgba(255,255,255,.03)] px-3 py-2";
  const auditValueClass = containText
    ? "mt-1 block w-full min-w-0 max-w-full whitespace-normal break-all text-sm font-semibold leading-5 text-[#E8EEF4]"
    : "mt-1 min-w-0 max-w-full break-words text-sm font-semibold leading-5 text-[#E8EEF4]";
  const auditDateClass = containText
    ? "mt-1 block min-w-0 max-w-full whitespace-normal break-words text-xs font-semibold leading-5 text-[#CBD5E1]"
    : auditValueClass;
  const auditValueStyle: CSSProperties | undefined = containText ? { overflowWrap: "anywhere", wordBreak: "break-all" } : undefined;

  return (
    <div className={containText ? "mt-2 grid w-full min-w-0 max-w-full grid-cols-1 items-start gap-2" : "mt-2 grid min-w-0 max-w-full gap-2 xl:grid-cols-[repeat(2,minmax(0,1fr))]"}>
      <div className={auditCardClass}>
        <p className="text-[10px] font-black uppercase tracking-[0.14em] text-[#22E0D0]">Created</p>
        {containText ? (
          <>
            <p className={auditValueClass} style={auditValueStyle}>{createdBy || "Not Captured"}</p>
            <p className={auditDateClass} style={auditValueStyle}>{createdDate || "Not Captured"} • {createdTime || "Not Captured"}</p>
          </>
        ) : (
          <p className={auditValueClass}>{createdBy || "Not Captured"} | {createdDate || "Not Captured"} | {createdTime || "Not Captured"}</p>
        )}
      </div>
      <div className={auditCardClass}>
        <p className="text-[10px] font-black uppercase tracking-[0.14em] text-[#22E0D0]">Updated</p>
        {containText ? (
          <>
            <p className={auditValueClass} style={auditValueStyle}>{updatedBy || "Not Captured"}</p>
            <p className={auditDateClass} style={auditValueStyle}>{updatedDate || "Not Captured"} • {updatedTime || "Not Captured"}</p>
          </>
        ) : (
          <p className={auditValueClass}>{updatedBy || "Not Captured"} | {updatedDate || "Not Captured"} | {updatedTime || "Not Captured"}</p>
        )}
      </div>
    </div>
  );
}

function OperationalHistoryRows({
  entries,
}: {
  entries: Array<{
    id: string;
    eventType: string;
    date: string;
    time: string;
    user: string;
    status: string;
    outcome: string;
    nextAction: string;
  }>;
}) {
  return (
    <div className="grid gap-2">
      {entries.map((entry) => (
        <article key={entry.id} className="rounded-xl border border-[rgba(255,106,43,.12)] bg-[rgba(7,18,31,.72)] px-3 py-2.5">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{entry.eventType}</p>
              <p className="mt-1 text-sm font-semibold leading-5 text-[#F6F1E8]">
                {entry.date} | {entry.time} | {entry.user}
              </p>
            </div>
            <span className="rounded-full border border-[rgba(34,224,208,.18)] bg-[rgba(34,224,208,.08)] px-2.5 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-[#22E0D0]">
              {entry.status}
            </span>
          </div>

          <div className="mt-2 grid gap-2 xl:grid-cols-2">
            <div className="rounded-lg border border-[rgba(34,224,208,.1)] bg-[rgba(255,255,255,.025)] px-3 py-2">
              <p className="text-[10px] font-black uppercase tracking-[0.14em] text-[#22E0D0]">Outcome</p>
              <p className="mt-1 text-sm font-semibold leading-5 text-[#E8EEF4]">{entry.outcome}</p>
            </div>
            <div className="rounded-lg border border-[rgba(34,224,208,.1)] bg-[rgba(255,255,255,.025)] px-3 py-2">
              <p className="text-[10px] font-black uppercase tracking-[0.14em] text-[#22E0D0]">Next Action</p>
              <p className="mt-1 text-sm font-semibold leading-5 text-[#E8EEF4]">{entry.nextAction}</p>
            </div>
          </div>
        </article>
      ))}
    </div>
  );
}

function renderCollectionLetterTab(
  snapshot: ReturnType<typeof buildARRecoveryCollectionLetterCenterSnapshot>,
  selectedLetter: NonNullable<ReturnType<typeof buildARRecoveryCollectionLetterCenterSnapshot>["selectedLetter"]>,
  selectedTab: ARRecoveryLetterTabKey,
  onLetterAction: (action: string) => void,
) {
  const selectedHistory = buildLetterHistory(selectedLetter, snapshot.activityFeed);

  switch (selectedTab) {
    case "overview":
      return (
        <div className="grid min-w-0 max-w-full gap-4 overflow-hidden">
          <div className="grid min-w-0 gap-3">
            <SectionHeading eyebrow="Overview" title="Letter summary" description="Selected correspondence, delivery state, and current operational context." />
            <div className="grid min-w-0 gap-3 md:grid-cols-[repeat(2,minmax(0,1fr))]">
              <DetailMetric label="Template" value={selectedLetter.template} />
              <DetailMetric label="Status" value={selectedLetter.status} />
              <DetailMetric label="Sent by" value={selectedLetter.sentBy} />
              <DetailMetric label="Delivery" value={selectedLetter.deliveryStatus} />
              <DetailMetric label="Viewed" value={selectedLetter.viewedStatus} />
              <DetailMetric label="Downloaded" value={selectedLetter.downloadedStatus} />
            </div>
          </div>
        </div>
      );
    case "template":
      return (
        <div className="grid min-w-0 max-w-full gap-4 overflow-hidden xl:grid-cols-[minmax(0,1fr)_minmax(0,.92fr)]">
          <div className="grid min-w-0 gap-3">
            <SectionHeading eyebrow="Templates" title="Merge fields" description="Letter defaults are injected from the active client profile without manual edits." />
            <div className="grid min-w-0 gap-3 md:grid-cols-[repeat(2,minmax(0,1fr))]">
              {snapshot.mergePreview.map((field) => (
                <DetailMetric key={field.label} label={field.label} value={field.value} />
              ))}
            </div>
          </div>
          <div className="min-w-0 max-w-full overflow-hidden rounded-3xl border border-[rgba(34,224,208,.14)] bg-[rgba(7,18,31,.72)] p-4">
            <p className="break-words text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Template coverage</p>
            <div className="mt-4 grid min-w-0 gap-3">
              {snapshot.templates.slice(0, 5).map((template) => (
                <div key={template.label} className="min-w-0 max-w-full overflow-hidden rounded-2xl border border-[rgba(34,224,208,.14)] bg-[rgba(255,255,255,.03)] p-4">
                  <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
                    <p className="break-words text-xs font-black uppercase tracking-[0.16em] text-[#22E0D0]">{template.label}</p>
                    <span className="shrink-0 rounded-full border border-[rgba(255,106,43,.2)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{template.count}</span>
                  </div>
                  <p className="mt-2 break-words text-sm font-semibold leading-7 text-[#E8EEF4]">{template.note}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    case "tracking":
      return (
        <div className="grid min-w-0 max-w-full gap-3 overflow-hidden md:grid-cols-[repeat(2,minmax(0,1fr))] xl:grid-cols-[repeat(3,minmax(0,1fr))]">
          <DetailMetric label="Delivery Status" value={selectedLetter.deliveryStatus} />
          <DetailMetric label="Viewed Status" value={selectedLetter.viewedStatus} />
          <DetailMetric label="Downloaded Status" value={selectedLetter.downloadedStatus} />
          <DetailMetric label="Sent Date" value={selectedLetter.dateSent ? formatDate(selectedLetter.dateSent) : "Not sent"} />
          <DetailMetric label="Collector" value={selectedLetter.collector} />
          <DetailMetric label="Manager" value={selectedLetter.manager} />
          <DetailMetric label="Related Account" value={selectedLetter.customerNumber} />
        </div>
      );
    case "bulk":
      return (
        <div className="grid min-w-0 max-w-full gap-3 overflow-hidden md:grid-cols-[repeat(2,minmax(0,1fr))]">
          {getBulkLetterActions().map((action) => (
            <LetterActionButton key={action.label} label={action.label} note={action.note} onClick={() => onLetterAction(action.label)} />
          ))}
        </div>
      );
    case "activity":
      return (
        <div className="grid min-w-0 max-w-full gap-3 overflow-hidden">
          {snapshot.activityFeed
            .filter((entry) => entry.title.toLowerCase().includes(selectedLetter.customerName.toLowerCase()))
            .slice(0, 10)
            .map((entry) => (
              <div key={entry.id} className="min-w-0 max-w-full overflow-hidden rounded-2xl border border-[rgba(34,224,208,.14)] bg-[rgba(7,18,31,.72)] p-4">
                <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
                  <p className="break-words text-xs font-black uppercase tracking-[0.16em] text-[#22E0D0]">{formatDate(entry.createdAt)}</p>
                  <span className="shrink-0 rounded-full border border-[rgba(255,106,43,.2)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#FF6A2B]">Letter</span>
                </div>
                <h3 className="mt-2 break-words font-display text-2xl font-bold text-[#F6F1E8]">{entry.title}</h3>
                <p className="mt-2 break-words text-sm font-semibold leading-7 text-[#E8EEF4]">{entry.detail}</p>
              </div>
            ))}
        </div>
      );
    case "account":
      return (
        <div className="grid min-w-0 max-w-full gap-3 overflow-hidden md:grid-cols-[repeat(2,minmax(0,1fr))]">
          <ScopeRow label="Business Name" value={selectedLetter.mergeFields.businessName} />
          <ScopeRow label="DBA Name" value={selectedLetter.mergeFields.dbaName} />
          <ScopeRow label="Address" value={selectedLetter.mergeFields.address} />
          <ScopeRow label="Phone" value={selectedLetter.mergeFields.phone} />
          <ScopeRow label="Email" value={selectedLetter.mergeFields.email} />
          <ScopeRow label="Website" value={selectedLetter.mergeFields.website} />
          <ScopeRow label="Customer" value={selectedLetter.customerName} />
          <ScopeRow label="Collector" value={selectedLetter.collector} />
        </div>
      );
    case "history":
      return <AccountRecordList title="Letter History" records={selectedHistory} />;
    default:
      return null;
  }
}

function renderStatementTab(
  snapshot: ReturnType<typeof buildARRecoveryStatementCenterSnapshot>,
  selectedStatement: NonNullable<ReturnType<typeof buildARRecoveryStatementCenterSnapshot>["selectedStatement"]>,
  selectedTab: ARRecoveryLetterTabKey,
  onStatementAction: (action: string) => void,
) {
  const selectedHistory = buildStatementHistory(selectedStatement, snapshot.activityFeed);

  switch (selectedTab) {
    case "overview":
      return (
        <div className="grid min-w-0 max-w-full gap-4 overflow-hidden">
          <div className="grid min-w-0 gap-3">
            <SectionHeading eyebrow="Overview" title="Statement summary" description="Selected statement delivery, balance context, and current operational status." />
            <div className="grid min-w-0 gap-3 md:grid-cols-[repeat(2,minmax(0,1fr))]">
              <DetailMetric label="Statement #" value={selectedStatement.statementNumber} />
              <DetailMetric label="Status" value={selectedStatement.status} />
              <DetailMetric label="Sent by" value={selectedStatement.sentBy} />
              <DetailMetric label="Delivery" value={selectedStatement.deliveryStatus} />
              <DetailMetric label="Opened" value={selectedStatement.openedStatus} />
              <DetailMetric label="Downloaded" value={selectedStatement.downloadedStatus} />
            </div>
          </div>
        </div>
      );
    case "template":
      return (
        <div className="grid min-w-0 max-w-full gap-4 overflow-hidden xl:grid-cols-[minmax(0,1fr)_minmax(0,.92fr)]">
          <div className="grid min-w-0 gap-3">
            <SectionHeading eyebrow="Layouts" title="Merge fields" description="Statements inherit the active client profile and maintain the same brand defaults." />
            <div className="grid min-w-0 gap-3 md:grid-cols-[repeat(2,minmax(0,1fr))]">
              {Object.entries(selectedStatement.mergeFields).map(([label, value]) => (
                <DetailMetric
                  key={label}
                  label={label === "businessName" ? "Business Name" : label === "dbaName" ? "DBA Name" : label === "contactName" ? "Contact Name" : label === "address" ? "Address" : label === "phone" ? "Phone" : label === "email" ? "Email" : "Website"}
                  value={value}
                />
              ))}
            </div>
          </div>
          <div className="min-w-0 max-w-full overflow-hidden rounded-3xl border border-[rgba(34,224,208,.14)] bg-[rgba(7,18,31,.72)] p-4">
            <p className="break-words text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Line items</p>
            <div className="mt-4 grid min-w-0 gap-3">
              {snapshot.lineItemPreview.map((item) => (
                <div key={item.invoiceNumber} className="min-w-0 max-w-full overflow-hidden rounded-2xl border border-[rgba(34,224,208,.14)] bg-[rgba(255,255,255,.03)] p-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="min-w-0 break-words text-xs font-black uppercase tracking-[0.16em] text-[#22E0D0]">{item.invoiceNumber}</p>
                    <span className="shrink-0 rounded-full border border-[rgba(255,106,43,.2)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{item.status}</span>
                  </div>
                  <p className="mt-2 break-words text-sm font-semibold leading-7 text-[#E8EEF4]">
                    {formatARRecoveryCurrency(item.amountCents)} • due {formatDate(item.dueDate)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    case "tracking":
      return (
        <div className="grid min-w-0 max-w-full gap-3 overflow-hidden md:grid-cols-[repeat(2,minmax(0,1fr))] xl:grid-cols-[repeat(3,minmax(0,1fr))]">
          <DetailMetric label="Delivery Status" value={selectedStatement.deliveryStatus} />
          <DetailMetric label="Opened Status" value={selectedStatement.openedStatus} />
          <DetailMetric label="Downloaded Status" value={selectedStatement.downloadedStatus} />
          <DetailMetric label="Sent Date" value={selectedStatement.dateSent ? formatDate(selectedStatement.dateSent) : "Not sent"} />
          <DetailMetric label="Collector" value={selectedStatement.collector} />
          <DetailMetric label="Manager" value={selectedStatement.manager} />
          <DetailMetric label="Related Account" value={selectedStatement.customerNumber} />
          <DetailMetric label="Open Invoices" value={selectedStatement.openInvoiceCount.toString()} />
          <DetailMetric label="Balance" value={formatARRecoveryCurrency(selectedStatement.balanceCents)} />
        </div>
      );
    case "bulk":
      return (
        <div className="grid min-w-0 max-w-full gap-3 overflow-hidden md:grid-cols-[repeat(2,minmax(0,1fr))]">
          {getBulkStatementActions().map((action) => (
            <StatementActionButton key={action.label} label={action.label} note={action.note} onClick={() => onStatementAction(action.label)} />
          ))}
        </div>
      );
    case "activity":
      return (
        <div className="grid min-w-0 max-w-full gap-3 overflow-hidden">
          {snapshot.activityFeed
            .filter((entry) => entry.title.toLowerCase().includes(selectedStatement.customerName.toLowerCase()))
            .slice(0, 10)
            .map((entry) => (
              <div key={entry.id} className="min-w-0 max-w-full overflow-hidden rounded-2xl border border-[rgba(34,224,208,.14)] bg-[rgba(7,18,31,.72)] p-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="break-words text-xs font-black uppercase tracking-[0.16em] text-[#22E0D0]">{formatDate(entry.createdAt)}</p>
                  <span className="shrink-0 rounded-full border border-[rgba(255,106,43,.2)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#FF6A2B]">Statement</span>
                </div>
                <h3 className="mt-2 break-words font-display text-2xl font-bold text-[#F6F1E8]">{entry.title}</h3>
                <p className="mt-2 break-words text-sm font-semibold leading-7 text-[#E8EEF4]">{entry.detail}</p>
              </div>
            ))}
        </div>
      );
    case "account":
      return (
        <div className="grid min-w-0 max-w-full gap-3 overflow-hidden md:grid-cols-[repeat(2,minmax(0,1fr))]">
          <ScopeRow label="Business Name" value={selectedStatement.mergeFields.businessName} />
          <ScopeRow label="DBA Name" value={selectedStatement.mergeFields.dbaName} />
          <ScopeRow label="Address" value={selectedStatement.mergeFields.address} />
          <ScopeRow label="Phone" value={selectedStatement.mergeFields.phone} />
          <ScopeRow label="Email" value={selectedStatement.mergeFields.email} />
          <ScopeRow label="Website" value={selectedStatement.mergeFields.website} />
          <ScopeRow label="Customer" value={selectedStatement.customerName} />
          <ScopeRow label="Collector" value={selectedStatement.collector} />
        </div>
      );
    case "history":
      return <AccountRecordList title="Statement History" records={selectedHistory} />;
    default:
      return null;
  }
}

function buildLetterHistory(
  selectedLetter: NonNullable<ReturnType<typeof buildARRecoveryCollectionLetterCenterSnapshot>["selectedLetter"]>,
  activityFeed: ReturnType<typeof buildARRecoveryCollectionLetterCenterSnapshot>["activityFeed"],
) {
  return [
    { id: `${selectedLetter.id}-history-1`, primary: "Created", secondary: selectedLetter.template, meta: `${formatDate(selectedLetter.createdAt)} • ${selectedLetter.notes}` },
    { id: `${selectedLetter.id}-history-2`, primary: "Sent", secondary: selectedLetter.deliveryStatus, meta: `${selectedLetter.sentAt ? formatDate(selectedLetter.sentAt) : "Not sent"} • ${selectedLetter.sentBy}` },
    { id: `${selectedLetter.id}-history-3`, primary: "Tracking Updated", secondary: selectedLetter.viewedStatus, meta: `${formatDate(selectedLetter.updatedAt)} • ${selectedLetter.downloadedStatus}` },
    ...activityFeed
      .filter((entry) => entry.title.toLowerCase().includes(selectedLetter.customerName.toLowerCase()))
      .slice(0, 4)
      .map((entry) => ({ id: entry.id, primary: entry.title, secondary: "Activity", meta: `${formatDate(entry.createdAt)} • ${entry.detail}` })),
  ];
}

function buildStatementHistory(
  selectedStatement: NonNullable<ReturnType<typeof buildARRecoveryStatementCenterSnapshot>["selectedStatement"]>,
  activityFeed: ReturnType<typeof buildARRecoveryStatementCenterSnapshot>["activityFeed"],
) {
  return [
    { id: `${selectedStatement.id}-history-1`, primary: "Created", secondary: selectedStatement.statementNumber, meta: `${formatDate(selectedStatement.createdAt)} • ${selectedStatement.notes}` },
    { id: `${selectedStatement.id}-history-2`, primary: "Sent", secondary: selectedStatement.deliveryStatus, meta: `${selectedStatement.sentAt ? formatDate(selectedStatement.sentAt) : "Not sent"} • ${selectedStatement.sentBy}` },
    { id: `${selectedStatement.id}-history-3`, primary: "Tracking Updated", secondary: selectedStatement.openedStatus, meta: `${formatDate(selectedStatement.updatedAt)} • ${selectedStatement.downloadedStatus}` },
    ...activityFeed
      .filter((entry) => entry.title.toLowerCase().includes(selectedStatement.customerName.toLowerCase()))
      .slice(0, 4)
      .map((entry) => ({ id: entry.id, primary: entry.title, secondary: "Activity", meta: `${formatDate(entry.createdAt)} • ${entry.detail}` })),
  ];
}

function promiseTabIcon(tab: ARRecoveryPromiseTabKey) {
  switch (tab) {
    case "overview":
      return <IdCard className="size-4" aria-hidden="true" />;
    case "account":
      return <Building2 className="size-4" aria-hidden="true" />;
    case "notes":
      return <ClipboardList className="size-4" aria-hidden="true" />;
    case "activity":
      return <Activity className="size-4" aria-hidden="true" />;
    case "payments":
      return <CircleDollarSign className="size-4" aria-hidden="true" />;
    case "letters":
    case "statements":
    case "documents":
      return <FileText className="size-4" aria-hidden="true" />;
    case "ticklers":
    case "history":
      return <Clock3 className="size-4" aria-hidden="true" />;
    default:
      return <ClipboardList className="size-4" aria-hidden="true" />;
  }
}

function promiseActionIcon(label: string) {
  if (label.includes("Payment")) return <CircleDollarSign className="size-5" aria-hidden="true" />;
  if (label.includes("Follow") || label.includes("Tickler") || label.includes("Track")) return <Clock3 className="size-5" aria-hidden="true" />;
  if (label.includes("Letter") || label.includes("Statement")) return <FileText className="size-5" aria-hidden="true" />;
  if (label.includes("Escalate") || label.includes("Approve") || label.includes("Close")) return <ShieldCheck className="size-5" aria-hidden="true" />;
  return <Target className="size-5" aria-hidden="true" />;
}

function PromiseScopeRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 items-start justify-between gap-4 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white px-4 py-3">
      <span className="shrink-0 text-xs font-medium text-[#334155]">{label}</span>
      <span className="min-w-0 max-w-[65%] break-words text-right text-sm font-semibold leading-6 text-[#0A2540]">{value}</span>
    </div>
  );
}

type PromiseQuickActionState = {
  label: string;
  promiseId: string;
  title: string;
  description: string;
  status: "Ready" | "Coming Soon";
  primaryActionLabel: string;
  details: string[];
  confirmationType?: PromiseConfirmationType;
  confirmLabel?: string;
  consequence?: string;
  isPending?: boolean;
  errorMessage?: string;
  resolutionNotes?: string;
  paymentStage?: "entry" | "review" | "success";
  paymentAmount?: string;
  paymentDate?: string;
  paymentMethod?: ARRecoveryPaymentMethodKey | "";
  paymentReference?: string;
  paymentNotes?: string;
  paymentIdempotencyKey?: string;
  paymentReviewSnapshot?: PromisePaymentReviewSnapshot;
  paymentResult?: {
    paymentId: string;
    paymentReference: string;
    amountCents: number;
    paymentDate: string;
    paymentMethod: string;
    previousPromiseBalanceCents: number;
    nextPromiseBalanceCents: number;
    previousAccountBalanceCents: number;
    nextAccountBalanceCents: number;
    nextPromiseStatus: string;
    recordedAt: string;
  };
  route?: string;
  routeAction?: string;
  routeHref?: string;
};

type PromisePaymentReviewSnapshot = {
  promiseId: string;
  accountId: string;
  customerNumber: string;
  promiseStatus: string;
  promiseRemainingBalanceCents: number;
  accountOpenBalanceCents: number;
  paymentReference: string;
  paymentIdempotencyKey: string;
  reviewedAt: string;
};

type PromiseConfirmationType = "approve-promise" | "close-promise" | "receive-payment" | "escalate-broken-promise";
type PromisePaymentField = "paymentAmount" | "paymentDate" | "paymentMethod" | "paymentReference" | "paymentNotes";
type PromisePaymentValidationResult = {
  amountCents: number;
  errors: string[];
  reference: string;
  notes: string;
};

function isCurrencyInputScaleValid(value: string) {
  return /^\$?\s*\d+(?:,\d{3})*(?:\.\d{1,2})?$|^\$?\s*\d+(?:\.\d{1,2})?$/.test(value.trim());
}

function applyPromisePaymentToSchedule(
  schedule: AccountPromiseRecord["installmentSchedule"],
  amountCents: number,
): AccountPromiseRecord["installmentSchedule"] {
  let unappliedCents = amountCents;
  return schedule.map((step) => {
    if (step.status === "Paid" || unappliedCents <= 0) return step;
    if (unappliedCents >= step.amountCents) {
      unappliedCents -= step.amountCents;
      return { ...step, status: "Paid" as const };
    }
    unappliedCents = 0;
    return step;
  });
}

function validatePromisePaymentAction(input: {
  action: PromiseQuickActionState;
  selectedPromise: NonNullable<ReturnType<typeof buildARRecoveryPromiseCenterSnapshot>["selectedPromise"]>;
  account?: ReturnType<typeof loadARRecoveryWorkspace>["accounts"][number];
  existingPayments: ARWorkflowPaymentProjection[];
  processedKeys: Set<string>;
}): PromisePaymentValidationResult {
  const errors: string[] = [];
  const amountInput = input.action.paymentAmount?.trim() || "";
  const amountCents = parseCurrencyInputToCents(amountInput);
  const reference = input.action.paymentReference?.trim() || "";
  const notes = input.action.paymentNotes?.trim() || "";
  const paymentDate = input.action.paymentDate || "";
  const paymentMethod = input.action.paymentMethod || "";
  const idempotencyKey = input.action.paymentIdempotencyKey || "";

  if (!input.account || !input.selectedPromise.accountId || !input.selectedPromise.customerNumber) {
    errors.push("The selected promise is missing a valid customer/account link. Reopen a canonical promise record and try again.");
  }
  if (isPromiseAlreadyClosed(input.selectedPromise) || input.selectedPromise.remainingBalanceCents <= 0) {
    errors.push("This promise is already paid or closed. No additional payment can be recorded from Promise Center.");
  }
  if (!amountInput) {
    errors.push("Payment amount is required.");
  } else if (!isCurrencyInputScaleValid(amountInput) || !Number.isFinite(amountCents)) {
    errors.push("Enter a valid payment amount using dollars and cents only.");
  } else if (amountCents <= 0) {
    errors.push("Payment amount must be greater than zero.");
  } else if (amountCents > input.selectedPromise.remainingBalanceCents) {
    errors.push(`Payment amount cannot exceed the remaining promise balance of ${formatARRecoveryCurrency(input.selectedPromise.remainingBalanceCents)}.`);
  }
  if (!paymentDate) {
    errors.push("Payment date is required.");
  } else if (!Number.isFinite(getDateTime(paymentDate))) {
    errors.push("Enter a valid payment date.");
  } else if (getDateTime(paymentDate) > Date.now()) {
    errors.push("Future-dated payments cannot be recorded in Promise Center.");
  }
  if (!paymentMethod || !AR_RECOVERY_PAYMENT_METHOD_OPTIONS.some((method) => method.key === paymentMethod)) {
    errors.push("Select a supported payment method.");
  }
  if (!reference) {
    errors.push("Payment reference is required before recording a Promise Center payment.");
  }
  if (reference && input.existingPayments.some((payment) =>
    payment.accountId === input.selectedPromise.accountId
      && String(payment.externalPaymentReference || "").trim().toLowerCase() === reference.toLowerCase()
      && payment.lifecycleStatus !== "Voided",
  )) {
    errors.push("This payment reference already exists for the selected account. Verify the reference before saving.");
  }
  if (!idempotencyKey || input.processedKeys.has(idempotencyKey)) {
    errors.push("This payment confirmation has already been processed. Start a new Receive Payment workflow for another payment.");
  }

  return { amountCents, errors, reference, notes };
}

function getPromisePaymentProjectionIdentity(payment: ARWorkflowPaymentProjection) {
  return String(payment.id || payment.paymentId || payment.idempotencyKey || "").trim();
}

function isActivePromisePaymentProjection(payment: ARWorkflowPaymentProjection) {
  return payment.lifecycleStatus !== "Voided";
}

function hasDuplicatePromisePaymentReference(input: {
  payments: ARWorkflowPaymentProjection[];
  accountId: string;
  reference: string;
}) {
  const normalizedReference = input.reference.trim().toLowerCase();
  if (!normalizedReference) return false;
  return input.payments.some((payment) =>
    payment.accountId === input.accountId
      && String(payment.externalPaymentReference || payment.paymentReference || "").trim().toLowerCase() === normalizedReference
      && isActivePromisePaymentProjection(payment),
  );
}

function hasProcessedPromisePaymentIdentity(input: {
  payments: ARWorkflowPaymentProjection[];
  idempotencyKey: string;
}) {
  const normalizedKey = input.idempotencyKey.trim();
  if (!normalizedKey) return false;
  return input.payments.some((payment) =>
    getPromisePaymentProjectionIdentity(payment) === normalizedKey
      || String(payment.idempotencyKey || "").trim() === normalizedKey,
  );
}

function resolveCanonicalPromisePaymentState(input: {
  companyId: string;
  promiseId: string;
  fallbackWorkspace: ReturnType<typeof loadARRecoveryWorkspace>;
}) {
  const seedWorkspace = loadARRecoveryWorkspace(input.companyId);
  const projectedPromises = loadProjectedARRecoveryPromiseWorkflows();
  const existingPayments = loadProjectedARRecoveryPaymentWorkflows();
  const projectedPromise = projectedPromises.find((promise) => promise.id === input.promiseId) as ReturnType<typeof loadARRecoveryWorkspace>["promises"][number] | undefined;
  const fallbackPromise = input.fallbackWorkspace.promises.find((promise) => promise.id === input.promiseId)
    || seedWorkspace.promises.find((promise) => promise.id === input.promiseId);
  const promise = projectedPromise || fallbackPromise;
  const seedAccount = promise
    ? seedWorkspace.accounts.find((account) => account.id === promise.accountId && account.customerNumber === promise.customerNumber)
    : undefined;
  const fallbackAccount = promise
    ? input.fallbackWorkspace.accounts.find((account) => account.id === promise.accountId && account.customerNumber === promise.customerNumber)
    : undefined;
  const accountBase = seedAccount || fallbackAccount;
  const appliedAccountPaymentCents = accountBase
    ? existingPayments
        .filter((payment) =>
          payment.accountId === accountBase.id
            && payment.applicationScope === "account"
            && isActivePromisePaymentProjection(payment),
        )
        .reduce((sum, payment) => sum + payment.amountCents, 0)
    : 0;
  const account = accountBase
    ? {
        ...accountBase,
        openBalanceCents: Math.max(0, accountBase.openBalanceCents - appliedAccountPaymentCents),
      }
    : undefined;

  return {
    promise,
    account,
    existingPayments,
  };
}

function buildPromisePaymentReviewSnapshot(input: {
  action: PromiseQuickActionState;
  promise: NonNullable<ReturnType<typeof resolveCanonicalPromisePaymentState>["promise"]>;
  account: NonNullable<ReturnType<typeof resolveCanonicalPromisePaymentState>["account"]>;
}): PromisePaymentReviewSnapshot {
  return {
    promiseId: input.promise.id,
    accountId: input.account.id,
    customerNumber: input.promise.customerNumber,
    promiseStatus: input.promise.status,
    promiseRemainingBalanceCents: input.promise.remainingBalanceCents,
    accountOpenBalanceCents: input.account.openBalanceCents,
    paymentReference: input.action.paymentReference?.trim() || "",
    paymentIdempotencyKey: input.action.paymentIdempotencyKey || "",
    reviewedAt: new Date().toISOString(),
  };
}

function validatePromisePaymentReviewFreshness(input: {
  action: PromiseQuickActionState;
  latestPromise: NonNullable<ReturnType<typeof resolveCanonicalPromisePaymentState>["promise"]>;
  latestAccount: NonNullable<ReturnType<typeof resolveCanonicalPromisePaymentState>["account"]>;
  existingPayments: ARWorkflowPaymentProjection[];
}) {
  const snapshot = input.action.paymentReviewSnapshot;
  const currentReference = input.action.paymentReference?.trim() || "";
  const currentIdempotencyKey = input.action.paymentIdempotencyKey || "";
  const staleMessage = "Payment details changed. Refresh the payment review before submitting. The account or promise changed after this review was opened.";

  if (!snapshot) return staleMessage;
  if (
    snapshot.promiseId !== input.latestPromise.id
    || snapshot.accountId !== input.latestAccount.id
    || snapshot.customerNumber !== input.latestPromise.customerNumber
    || snapshot.promiseStatus !== input.latestPromise.status
    || snapshot.promiseRemainingBalanceCents !== input.latestPromise.remainingBalanceCents
    || snapshot.accountOpenBalanceCents !== input.latestAccount.openBalanceCents
    || snapshot.paymentReference !== currentReference
    || snapshot.paymentIdempotencyKey !== currentIdempotencyKey
  ) {
    return staleMessage;
  }
  if (isPromiseAlreadyClosed(input.latestPromise) || input.latestPromise.remainingBalanceCents <= 0) {
    return "This promise is already paid or closed. Refresh the promise before recording another payment.";
  }
  if (hasDuplicatePromisePaymentReference({
    payments: input.existingPayments,
    accountId: input.latestPromise.accountId,
    reference: currentReference,
  })) {
    return "This payment reference already exists for the selected account. Refresh the payment review before submitting.";
  }
  if (hasProcessedPromisePaymentIdentity({
    payments: input.existingPayments,
    idempotencyKey: currentIdempotencyKey,
  })) {
    return "This payment confirmation has already been processed. Start a new Receive Payment workflow for another payment.";
  }
  return "";
}

function PromiseActionCard({
  label,
  note,
  icon,
  onAction,
  disabled = false,
  statusLabel = "Open workflow",
  disabledReason,
}: {
  label: string;
  note: string;
  icon: ReactNode;
  onAction: () => void;
  disabled?: boolean;
  statusLabel?: string;
  disabledReason?: string;
}) {
  return (
    <button
      type="button"
      onClick={onAction}
      disabled={disabled}
      title={disabled ? disabledReason : undefined}
      className="group min-w-0 max-w-full cursor-pointer overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-3 text-left transition hover:-translate-y-0.5 hover:border-[#14B8A6] hover:bg-[#F4FBFF] hover:shadow-[0_12px_26px_rgba(20,184,166,.12)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#14B8A6] focus-visible:ring-offset-2 focus-visible:ring-offset-white active:translate-y-0 disabled:cursor-not-allowed disabled:border-[#BFEAFF] disabled:bg-[#F8FAFC] disabled:opacity-80 disabled:hover:translate-y-0 disabled:hover:shadow-none"
      aria-label={disabled ? `${label}: ${disabledReason || statusLabel}` : `Open ${label}`}
      aria-disabled={disabled ? "true" : undefined}
    >
      <div className="flex min-w-0 items-start justify-between gap-3">
        <p className="flex min-w-0 items-center gap-2 text-[0.8125rem] font-semibold text-[#0A2540]">
          <ARLightLineIcon compact icon={icon} />
          <span className="truncate">{label}</span>
        </p>
        <ArrowRight className="size-4 shrink-0 text-[#14B8A6] opacity-70 transition group-hover:translate-x-1 group-hover:opacity-100 group-disabled:translate-x-0 group-disabled:opacity-40" aria-hidden="true" />
      </div>
      <p className="mt-1.5 text-xs font-normal leading-5 text-[#475569]">{note}</p>
      <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#14B8A6]">{statusLabel}</p>
    </button>
  );
}

function PromiseCenterReturnControl({
  context,
  label = "Return to Promise Center",
}: {
  context: PromiseCenterReturnContext | null;
  label?: string;
}) {
  if (!context) return null;

  return (
    <div className="min-w-0 max-w-full overflow-hidden rounded-[1.5rem] border border-[#BFEAFF] bg-[#F4FBFF] p-4">
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Promise Center Origin</p>
          <p className="mt-1 break-words text-sm font-semibold leading-6 text-[#0A2540]">
            Return restores promise {context.promiseId}{context.accountId ? ` for account ${context.accountId}` : ""}.
          </p>
        </div>
        <Link
          to={context.returnRoute}
          className="focus-ring inline-flex min-h-11 shrink-0 items-center gap-2 rounded-full border border-[#14B8A6] bg-[#14B8A6] px-4 text-sm font-black uppercase tracking-[0.12em] text-white transition hover:bg-[#0F766E]"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          {label}
        </Link>
      </div>
    </div>
  );
}

function PromiseQuickActionPanel({
  action,
  onClose,
  onConfirm,
  onResolutionNotesChange,
  onPaymentFieldChange,
  onPaymentEdit,
}: {
  action: PromiseQuickActionState;
  onClose: () => void;
  onConfirm?: () => void;
  onResolutionNotesChange?: (value: string) => void;
  onPaymentFieldChange?: (field: PromisePaymentField, value: string) => void;
  onPaymentEdit?: () => void;
}) {
  const titleId = useId();
  const descriptionId = useId();
  const isConfirmation = Boolean(action.confirmationType);
  const requiresResolutionNotes = action.confirmationType === "close-promise";
  const resolutionNotesId = useId();
  const paymentAmountId = useId();
  const paymentDateId = useId();
  const paymentReferenceId = useId();
  const paymentNotesId = useId();
  const isPaymentWorkflow = action.confirmationType === "receive-payment";
  const paymentStage = action.paymentStage || "entry";
  const paymentAmountCents = parseCurrencyInputToCents(action.paymentAmount || "");
  const confirmDisabled = action.isPending || !onConfirm || paymentStage === "success" || (requiresResolutionNotes && !action.resolutionNotes?.trim());

  return (
    <div
      className="mt-3 min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4"
      role={isConfirmation ? "dialog" : undefined}
      aria-labelledby={isConfirmation ? titleId : undefined}
      aria-describedby={isConfirmation ? descriptionId : undefined}
      aria-busy={action.isPending ? "true" : undefined}
      onKeyDown={(event) => {
        if (event.key === "Escape" && !action.isPending) onClose();
      }}
    >
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <ARLightLineIcon icon={promiseActionIcon(action.label)} />
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">{action.label}</p>
            <h4 id={titleId} className="mt-1 text-base font-semibold text-[#0A2540]">{action.title}</h4>
            <p id={descriptionId} className="mt-1 text-sm font-normal leading-6 text-[#475569]">{action.description}</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#0A2540]">
            {action.status}
          </span>
          <button
            type="button"
            onClick={onClose}
            disabled={action.isPending}
            className="rounded-full border border-[#BFEAFF] bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#14B8A6] transition hover:border-[#14B8A6] hover:bg-[#F4FBFF]"
          >
            {isConfirmation ? "Cancel" : "Dismiss"}
          </button>
        </div>
      </div>
      {isPaymentWorkflow ? (
        <div className="mt-4 grid min-w-0 gap-4 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
          {paymentStage === "review" ? (
            <div className="min-w-0 rounded-xl border border-[#BFEAFF] bg-white px-3 py-3">
              <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Payment Review</p>
                <button
                  type="button"
                  onClick={onPaymentEdit}
                  disabled={action.isPending}
                  className="rounded-full border border-[#BFEAFF] bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#14B8A6] transition hover:border-[#14B8A6] hover:bg-[#F4FBFF] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Edit Payment
                </button>
              </div>
              <div className="mt-3 grid min-w-0 gap-2 md:grid-cols-[repeat(2,minmax(0,1fr))]">
                <PromiseScopeRow label="Amount" value={Number.isFinite(paymentAmountCents) ? formatARRecoveryCurrency(paymentAmountCents) : "Not valid"} />
                <PromiseScopeRow label="Payment date" value={action.paymentDate ? formatCapturedDate(action.paymentDate) : "Not selected"} />
                <PromiseScopeRow label="Method" value={action.paymentMethod || "Not selected"} />
                <PromiseScopeRow label="Reference" value={action.paymentReference || "Required"} />
                <PromiseScopeRow label="Notes" value={action.paymentNotes?.trim() || "No notes entered"} />
              </div>
            </div>
          ) : null}
          {action.paymentResult ? (
            <div className="min-w-0 rounded-xl border border-[#BFEAFF] bg-white px-3 py-3">
              <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Payment Result</p>
              <div className="mt-3 grid min-w-0 gap-2 md:grid-cols-[repeat(2,minmax(0,1fr))]">
                <PromiseScopeRow label="Payment ID" value={action.paymentResult.paymentId} />
                <PromiseScopeRow label="Reference" value={action.paymentResult.paymentReference} />
                <PromiseScopeRow label="Amount" value={formatARRecoveryCurrency(action.paymentResult.amountCents)} />
                <PromiseScopeRow label="Promise balance" value={`${formatARRecoveryCurrency(action.paymentResult.previousPromiseBalanceCents)} -> ${formatARRecoveryCurrency(action.paymentResult.nextPromiseBalanceCents)}`} />
                <PromiseScopeRow label="Account balance" value={`${formatARRecoveryCurrency(action.paymentResult.previousAccountBalanceCents)} -> ${formatARRecoveryCurrency(action.paymentResult.nextAccountBalanceCents)}`} />
                <PromiseScopeRow label="Promise status" value={action.paymentResult.nextPromiseStatus} />
              </div>
            </div>
          ) : null}
          {paymentStage !== "success" ? (
            <>
              <div className="grid min-w-0 gap-3 md:grid-cols-[repeat(2,minmax(0,1fr))]">
                <label htmlFor={paymentAmountId} className="block min-w-0">
                  <span className="block text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Payment Amount</span>
                  <input
                    id={paymentAmountId}
                    inputMode="decimal"
                    value={action.paymentAmount || ""}
                    onChange={(event) => onPaymentFieldChange?.("paymentAmount", event.target.value)}
                    disabled={action.isPending || paymentStage === "review"}
                    className="mt-2 w-full min-w-0 rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold text-[#0A2540] outline-none transition focus:border-[#14B8A6] focus:ring-2 focus:ring-[#14B8A6]/20 disabled:cursor-not-allowed disabled:bg-[#E2E8F0] disabled:text-[#64748B]"
                    placeholder="$0.00"
                  />
                </label>
                <label htmlFor={paymentDateId} className="block min-w-0">
                  <span className="block text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Payment Date</span>
                  <input
                    id={paymentDateId}
                    type="date"
                    value={action.paymentDate || ""}
                    onChange={(event) => onPaymentFieldChange?.("paymentDate", event.target.value)}
                    disabled={action.isPending || paymentStage === "review"}
                    className="mt-2 w-full min-w-0 rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold text-[#0A2540] outline-none transition focus:border-[#14B8A6] focus:ring-2 focus:ring-[#14B8A6]/20 disabled:cursor-not-allowed disabled:bg-[#E2E8F0] disabled:text-[#64748B]"
                  />
                </label>
              </div>
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Payment Method</p>
                <div className="mt-2 grid min-w-0 gap-2 md:grid-cols-[repeat(2,minmax(0,1fr))]">
                  {AR_RECOVERY_PAYMENT_METHOD_OPTIONS.map((method) => {
                    const selected = action.paymentMethod === method.key;
                    return (
                      <button
                        key={method.key}
                        type="button"
                        onClick={() => onPaymentFieldChange?.("paymentMethod", method.key)}
                        disabled={action.isPending || paymentStage === "review"}
                        className={`min-w-0 rounded-xl border px-3 py-2 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#14B8A6] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${
                          selected ? "border-[#14B8A6] bg-white text-[#0A2540]" : "border-[#BFEAFF] bg-white text-[#475569] hover:border-[#14B8A6]"
                        }`}
                        aria-pressed={selected}
                      >
                        <span className="block text-xs font-black uppercase tracking-[0.12em]">{method.label}</span>
                        <span className="mt-1 block text-xs font-normal leading-5">{method.description}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <label htmlFor={paymentReferenceId} className="block min-w-0">
                <span className="block text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Payment Reference</span>
                <input
                  id={paymentReferenceId}
                  value={action.paymentReference || ""}
                  onChange={(event) => onPaymentFieldChange?.("paymentReference", event.target.value)}
                  disabled={action.isPending || paymentStage === "review"}
                  className="mt-2 w-full min-w-0 rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold text-[#0A2540] outline-none transition focus:border-[#14B8A6] focus:ring-2 focus:ring-[#14B8A6]/20 disabled:cursor-not-allowed disabled:bg-[#E2E8F0] disabled:text-[#64748B]"
                  placeholder="Check number, ACH trace, wire reference, or receipt"
                />
              </label>
              <label htmlFor={paymentNotesId} className="block min-w-0">
                <span className="block text-[11px] font-black uppercase tracking-[0.16em] text-[#14B8A6]">Notes Optional</span>
                <textarea
                  id={paymentNotesId}
                  value={action.paymentNotes || ""}
                  onChange={(event) => onPaymentFieldChange?.("paymentNotes", event.target.value)}
                  disabled={action.isPending || paymentStage === "review"}
                  rows={3}
                  className="mt-2 w-full min-w-0 resize-y rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold leading-6 text-[#0A2540] outline-none transition focus:border-[#14B8A6] focus:ring-2 focus:ring-[#14B8A6]/20 disabled:cursor-not-allowed disabled:bg-[#E2E8F0] disabled:text-[#64748B]"
                  placeholder="Internal payment notes"
                />
              </label>
            </>
          ) : null}
        </div>
      ) : null}
      <div className="mt-4 grid min-w-0 gap-2 md:grid-cols-[repeat(2,minmax(0,1fr))]">
        {action.details.map((detail) => (
          <div key={detail} className="min-w-0 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2 text-xs font-semibold leading-5 text-[#0A2540]">
            {detail}
          </div>
        ))}
      </div>
      {action.consequence ? (
        <div className="mt-4 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2 text-xs font-semibold leading-5 text-[#0A2540]">
          <span className="block text-[11px] uppercase tracking-[0.12em] text-[#14B8A6]">Operational consequence</span>
          <span className="mt-1 block font-normal text-[#475569]">{action.consequence}</span>
        </div>
      ) : null}
      {requiresResolutionNotes ? (
        <label htmlFor={resolutionNotesId} className="mt-4 block min-w-0">
          <span className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-[#14B8A6]">Closure reason</span>
          <textarea
            id={resolutionNotesId}
            value={action.resolutionNotes || ""}
            onChange={(event) => onResolutionNotesChange?.(event.target.value)}
            disabled={action.isPending}
            rows={3}
            className="mt-2 w-full min-w-0 resize-y rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2 text-sm font-semibold leading-6 text-[#0A2540] outline-none transition focus:border-[#14B8A6] focus:bg-white focus:ring-2 focus:ring-[#14B8A6]/20 disabled:cursor-not-allowed disabled:opacity-60"
            placeholder="Enter the reason this promise is being administratively closed."
          />
          <span className="mt-1 block text-xs font-normal leading-5 text-[#475569]">Required. This reason becomes the Promise.Cancel activity detail.</span>
        </label>
      ) : null}
      {action.errorMessage ? (
        <div className="mt-4 rounded-xl border border-[#FCA5A5] bg-[#FEF2F2] px-3 py-2 text-xs font-semibold leading-5 text-[#7F1D1D]" role="alert">
          {action.errorMessage}
        </div>
      ) : null}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {isConfirmation ? (
          <>
            <button
              type="button"
              onClick={onClose}
              disabled={action.isPending}
              className="rounded-full border border-[#BFEAFF] bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#14B8A6] transition hover:border-[#14B8A6] hover:bg-[#F4FBFF] disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={confirmDisabled}
              title={requiresResolutionNotes && !action.resolutionNotes?.trim() ? "A closure reason is required before closing this promise." : undefined}
              className="rounded-full border border-[#14B8A6] bg-[#14B8A6] px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-white transition hover:bg-[#0F766E] disabled:cursor-not-allowed disabled:border-[#BFEAFF] disabled:bg-[#E2E8F0] disabled:text-[#64748B]"
            >
              {action.isPending ? "Processing..." : action.confirmLabel || action.primaryActionLabel}
            </button>
          </>
        ) : action.routeHref ? (
          <Link
            to={action.routeHref}
            className="rounded-full border border-[#14B8A6] bg-[#14B8A6] px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-white transition hover:bg-[#0F766E]"
          >
            {action.primaryActionLabel}
          </Link>
        ) : (
          <span className="rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#0A2540]">
            {action.primaryActionLabel}
          </span>
        )}
      </div>
    </div>
  );
}

function publishPromiseCenterPaymentEvents(input: {
  selectedPromise: NonNullable<ReturnType<typeof buildARRecoveryPromiseCenterSnapshot>["selectedPromise"]>,
  workspace: ReturnType<typeof loadARRecoveryWorkspace>,
  sourceWorkspace: string,
  paymentId: string;
  amountCents: number;
  paymentDate: string;
  paymentMethod: ARRecoveryPaymentMethodKey;
  paymentReference: string;
  paymentNotes: string;
  actorName: string;
}) {
  const now = new Date().toISOString();
  const correlationId = input.paymentId;
  const basePaymentRecord = {
    id: input.paymentId,
    companyId: input.workspace.company.id,
    accountId: input.selectedPromise.accountId,
    customerName: input.selectedPromise.customerName,
    customerNumber: input.selectedPromise.customerNumber,
    amountCents: input.amountCents,
    paymentMethod: input.paymentMethod,
    status: "Recorded",
    lifecycleStatus: "Recorded",
    paymentDate: input.paymentDate,
    recordedAt: now,
    updatedAt: now,
    recordedBy: input.actorName,
    actorName: input.actorName,
    appliedToPromiseId: input.selectedPromise.id,
    appliedToAccountId: input.selectedPromise.accountId,
    externalPaymentReference: input.paymentReference,
    accountingProvider: "Not Configured",
    accountingSyncStatus: "not_configured",
    notes: input.paymentNotes,
    paymentReference: input.paymentReference,
    idempotencyKey: correlationId,
  };
  const actor = {
    actorId: input.actorName,
    actorName: input.actorName,
    actorEmail: "",
    actorRole: "Collector",
    permissionLevel: "collector_write",
  };
  const sharedInput = {
    tenantId: input.workspace.company.id,
    clientId: input.workspace.company.id,
    customerId: input.selectedPromise.customerNumber,
    accountId: input.selectedPromise.accountId,
    promiseId: input.selectedPromise.id,
    customerName: input.selectedPromise.customerName,
    customerNumber: input.selectedPromise.customerNumber,
    paymentId: input.paymentId,
    actor,
    sourceWorkspace: input.sourceWorkspace,
    initiatedFromRoute: "/admin/ar-recovery/promise-to-pay",
  };

  publishPaymentWorkflowAction({
    ...sharedInput,
    action: "Payment.Record",
    paymentRecord: basePaymentRecord,
  });
  publishPaymentWorkflowAction({
    ...sharedInput,
    action: "Payment.ApplyToPromise",
    paymentRecord: {
      ...basePaymentRecord,
      status: "Applied",
      lifecycleStatus: "Applied",
      applicationScope: "promise",
    },
    previousValue: basePaymentRecord,
  });
  publishPaymentWorkflowAction({
    ...sharedInput,
    action: "Payment.ApplyToAccount",
    paymentRecord: {
      ...basePaymentRecord,
      status: "Applied",
      lifecycleStatus: "Applied",
      applicationScope: "account",
    },
    previousValue: basePaymentRecord,
  });
  return projectPaymentEventsToPaymentWorkflows(loadStoredARWorkflowEvents());
}

function publishPromiseCenterEscalationEvent(
  selectedPromise: NonNullable<ReturnType<typeof buildARRecoveryPromiseCenterSnapshot>["selectedPromise"]>,
  workspace: ReturnType<typeof loadARRecoveryWorkspace>,
  sourceWorkspace: string,
) {
  const now = new Date().toISOString();
  const escalationId = `escalation-${selectedPromise.id}-${Date.now()}`;

  publishEscalationWorkflowAction({
    tenantId: workspace.company.id,
    clientId: workspace.company.id,
    customerId: selectedPromise.customerNumber,
    accountId: selectedPromise.accountId,
    customerName: selectedPromise.customerName,
    customerNumber: selectedPromise.customerNumber,
    escalationId,
    escalationRecord: {
      id: escalationId,
      companyId: workspace.company.id,
      accountId: selectedPromise.accountId,
      customerName: selectedPromise.customerName,
      customerNumber: selectedPromise.customerNumber,
      status: "Created",
      priority: "Critical",
      reason: selectedPromise.status === "Broken" ? "Broken promise requires manager review" : "Promise escalated for manager review",
      assignedTo: selectedPromise.manager || "Manager Command",
      createdAt: now,
      updatedAt: now,
      createdBy: selectedPromise.collector,
      actorName: selectedPromise.collector,
      sourceTrigger: "Broken Promise",
      sourceEventType: "PromiseEscalated",
      sourceRecordType: "promise",
      sourceRecordId: selectedPromise.id,
      lifecycle: ["Created", "Assigned", "In Review", "Resolved", "Closed", "Reopened"],
    },
    action: "Escalation.Create",
    actor: {
      actorId: selectedPromise.collector,
      actorName: selectedPromise.collector,
      actorEmail: "",
      actorRole: "Collector",
      permissionLevel: "collector_write",
    },
    sourceWorkspace,
    initiatedFromRoute: "/admin/ar-recovery/promise-to-pay",
  });
}

function renderPromiseTab(
  selectedPromise: NonNullable<ReturnType<typeof buildARRecoveryPromiseCenterSnapshot>["selectedPromise"]>,
  selectedTab: ARRecoveryPromiseTabKey,
) {
  const promiseActivity = selectedPromise.activityFeed;
  const promiseLetters = buildPromiseLetters(selectedPromise);
  const promiseStatements = buildPromiseStatements(selectedPromise);
  const promiseTicklers = buildPromiseTicklers(selectedPromise);
  const promiseDocuments = buildPromiseDocuments(selectedPromise);
  const promiseHistory = buildPromiseHistory(selectedPromise);
  const promisePaymentEntries = promiseActivity.filter((entry) => entry.kind === "Payment Received");

  switch (selectedTab) {
    case "overview":
      return (
        <div className="grid min-w-0 max-w-full gap-4 overflow-hidden">
          <div className="grid min-w-0 max-w-full gap-3 overflow-hidden">
            <SectionHeading eyebrow="Overview" title="Promise summary" description="Current promise risk, balances, and next actions in one operational view." />
            <div className="grid min-w-0 gap-3 md:grid-cols-[repeat(2,minmax(0,1fr))]">
              <DetailMetric label="Promise amount" value={formatARRecoveryCurrency(selectedPromise.amountCents)} icon={<CircleDollarSign className="size-5" aria-hidden="true" />} />
              <DetailMetric label="Remaining balance" value={formatARRecoveryCurrency(selectedPromise.remainingBalanceCents)} icon={<CircleDollarSign className="size-5" aria-hidden="true" />} />
              <DetailMetric label="Promise date" value={formatDate(selectedPromise.promiseDate)} icon={<Clock3 className="size-5" aria-hidden="true" />} />
              <DetailMetric label="Follow-up date" value={selectedPromise.followUpDate ? formatDate(selectedPromise.followUpDate) : "Not set"} icon={<Clock3 className="size-5" aria-hidden="true" />} />
              <DetailMetric label="Promise type" value={selectedPromise.promiseType} icon={<ClipboardList className="size-5" aria-hidden="true" />} />
              <DetailMetric label="Status" value={selectedPromise.status} icon={<ShieldCheck className="size-5" aria-hidden="true" />} />
            </div>
          </div>
          <div className="min-w-0 overflow-hidden rounded-3xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
            <p className="flex items-center gap-2 text-sm font-semibold text-[#0A2540]">
              <ARLightLineIcon icon={<ShieldCheck className="size-5" aria-hidden="true" />} />
              Promise status context
            </p>
            <div className="mt-4 grid min-w-0 gap-3 md:grid-cols-[repeat(2,minmax(0,1fr))] xl:grid-cols-[repeat(3,minmax(0,1fr))]">
              <PromiseScopeRow label="Customer" value={selectedPromise.customerName} />
              <PromiseScopeRow label="Customer number" value={selectedPromise.customerNumber} />
              <PromiseScopeRow label="Company" value={selectedPromise.clientCompany} />
              <PromiseScopeRow label="Promise amount" value={formatARRecoveryCurrency(selectedPromise.amountCents)} />
              <PromiseScopeRow label="Remaining balance" value={formatARRecoveryCurrency(selectedPromise.remainingBalanceCents)} />
              <PromiseScopeRow label="Current status" value={selectedPromise.status} />
              <PromiseScopeRow label="Due date" value={selectedPromise.followUpDate ? formatDate(selectedPromise.followUpDate) : formatDate(selectedPromise.promiseDate)} />
              <PromiseScopeRow label="Collector" value={selectedPromise.collector} />
              <PromiseScopeRow label="Manager" value={selectedPromise.manager} />
              <PromiseScopeRow label="Related invoice" value={selectedPromise.invoiceLabels[0] || "Not linked"} />
              <PromiseScopeRow label="Related letter" value={promiseLetters[0]?.primary || "Not generated"} />
              <PromiseScopeRow label="Related statement" value={promiseStatements[0]?.primary || "Not generated"} />
              <PromiseScopeRow label="Related tickler" value={promiseTicklers[0]?.primary || "Not scheduled"} />
            </div>
          </div>
        </div>
      );
    case "account":
      return (
        <div className="grid min-w-0 gap-3 md:grid-cols-[repeat(2,minmax(0,1fr))]">
          {[
            { category: "Customer Notes", text: selectedPromise.customerNotes },
            { category: "Collector Notes", text: selectedPromise.collectorNotes },
            { category: "Manager Notes", text: selectedPromise.managerNotes },
            { category: "Escalation Notes", text: selectedPromise.escalationNotes },
            { category: "Payment Notes", text: selectedPromise.paymentNotes },
            { category: "Invoice Link", text: selectedPromise.invoiceLabels.length ? selectedPromise.invoiceLabels.join(" • ") : "No invoices linked." },
          ].map((note) => (
            <div key={note.category} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4">
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">
                <ARLightLineIcon compact icon={<ClipboardList className="size-4" aria-hidden="true" />} />
                {note.category}
              </p>
              <p className="mt-2 text-sm font-normal leading-7 text-[#475569]">{note.text}</p>
            </div>
          ))}
        </div>
      );
    case "notes":
      return (
        <div className="grid min-w-0 gap-3 md:grid-cols-[repeat(2,minmax(0,1fr))]">
          {[
            { category: "Internal Notes", text: selectedPromise.notes },
            { category: "Collector Notes", text: selectedPromise.collectorNotes },
            { category: "Customer Notes", text: selectedPromise.customerNotes },
            { category: "Manager Notes", text: selectedPromise.managerNotes },
            { category: "Escalation Notes", text: selectedPromise.escalationNotes },
            { category: "Payment Notes", text: selectedPromise.paymentNotes },
          ].map((note) => (
            <div key={note.category} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4">
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">
                <ARLightLineIcon compact icon={<ClipboardList className="size-4" aria-hidden="true" />} />
                {note.category}
              </p>
              <p className="mt-2 text-sm font-normal leading-7 text-[#475569]">{note.text}</p>
            </div>
          ))}
        </div>
      );
    case "activity":
      return (
        <div className="grid min-w-0 gap-3">
          {promiseActivity.map((entry) => (
            <div key={entry.id} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4">
              <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">
                  <ARLightLineIcon compact icon={<Activity className="size-4" aria-hidden="true" />} />
                  {entry.kind}
                </p>
                <span className="rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#0A2540]">
                  {formatDate(entry.createdAt)}
                </span>
              </div>
              <h3 className="mt-2 text-lg font-semibold text-[#0A2540]">{entry.title}</h3>
              <p className="mt-2 text-sm font-normal leading-7 text-[#475569]">{entry.detail}</p>
            </div>
          ))}
        </div>
      );
    case "payments":
      return (
        <div className="grid min-w-0 gap-3">
          {promisePaymentEntries.length ? (
            <div className="grid min-w-0 gap-3">
              <SectionHeading eyebrow="Payment History" title="Received payments" description="Canonical Promise Center payment records applied to this promise." />
              {promisePaymentEntries.map((entry) => (
                <div key={entry.id} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4">
                  <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
                    <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">
                      <ARLightLineIcon compact icon={<CircleDollarSign className="size-4" aria-hidden="true" />} />
                      {entry.kind}
                    </p>
                    <span className="rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#0A2540]">
                      {formatDate(entry.createdAt)}
                    </span>
                  </div>
                  <h3 className="mt-2 text-lg font-semibold text-[#0A2540]">{entry.title}</h3>
                  <p className="mt-2 text-sm font-normal leading-7 text-[#475569]">{entry.detail}</p>
                </div>
              ))}
            </div>
          ) : null}
          <SectionHeading eyebrow="Settlement Schedule" title="Installments" description="Scheduled promise installments and payment status." />
          {selectedPromise.installmentSchedule.map((step, index) => (
            <div key={`${selectedPromise.id}-payment-${index}`} className="min-w-0 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-white p-4">
              <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                  <ARLightLineIcon icon={<CircleDollarSign className="size-5" aria-hidden="true" />} />
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#FF6A2B]">{step.label}</p>
                    <h3 className="mt-1 text-lg font-semibold text-[#0A2540]">{formatARRecoveryCurrency(step.amountCents)}</h3>
                  </div>
                </div>
                <span className="rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#14B8A6]">
                  {step.status}
                </span>
              </div>
              <p className="mt-2 text-sm font-normal leading-7 text-[#475569]">Due {formatDate(step.dueDate)}</p>
            </div>
          ))}
        </div>
      );
    case "letters":
      return <PromiseRecordList title="Related Letters" icon={<FileText className="size-5" aria-hidden="true" />} records={promiseLetters} />;
    case "statements":
      return <PromiseRecordList title="Related Statements" icon={<FileText className="size-5" aria-hidden="true" />} records={promiseStatements} />;
    case "ticklers":
      return <PromiseRecordList title="Related Ticklers" icon={<Clock3 className="size-5" aria-hidden="true" />} records={promiseTicklers} />;
    case "documents":
      return <PromiseRecordList title="Related Documents" icon={<FileText className="size-5" aria-hidden="true" />} records={promiseDocuments} />;
    case "history":
      return <PromiseRecordList title="Promise History" icon={<Activity className="size-5" aria-hidden="true" />} records={promiseHistory} />;
    default:
      return null;
  }
}

function buildPromiseWorkflowActions(
  selectedPromise: NonNullable<ReturnType<typeof buildARRecoveryPromiseCenterSnapshot>["selectedPromise"]>,
) {
  const hasAccountLink = Boolean(selectedPromise.id && selectedPromise.accountId && selectedPromise.customerNumber);
  const promiseAlreadyApproved = isPromiseAlreadyApproved(selectedPromise);
  const promiseAlreadyClosed = isPromiseAlreadyClosed(selectedPromise);
  const promisePaymentUnavailable = promiseAlreadyClosed || selectedPromise.remainingBalanceCents <= 0;
  const baseActions = [
    ...(hasAccountLink ? [{ label: "View Account", description: "Open related account.", priority: "High" as const }] : []),
    {
      label: "Approve Promise",
      description: promiseAlreadyApproved ? "This promise has already been approved." : "Open approval.",
      priority: "High" as const,
      disabled: promiseAlreadyApproved,
      statusLabel: promiseAlreadyApproved ? "Already approved" : "Open workflow",
      disabledReason: promiseAlreadyApproved ? "This promise has already been approved." : undefined,
    },
    { label: "Track Promise", description: "Review activity.", priority: "Medium" as const },
    {
      label: "Receive Payment",
      description: promisePaymentUnavailable ? "This promise is already paid or closed." : "Record payment.",
      priority: "Critical" as const,
      disabled: promisePaymentUnavailable,
      statusLabel: promisePaymentUnavailable ? "Payment unavailable" : "Open workflow",
      disabledReason: "This promise is already paid or closed.",
    },
    {
      label: "Close Promise",
      description: promiseAlreadyClosed ? "This promise has already been closed." : "Close commitment.",
      priority: "Medium" as const,
      disabled: promiseAlreadyClosed,
      statusLabel: promiseAlreadyClosed ? "Already closed" : "Open workflow",
      disabledReason: promiseAlreadyClosed ? "This promise has already been closed." : undefined,
    },
    { label: "Escalate Broken Promise", description: "Open escalation.", priority: "Critical" as const },
    { label: "Create Follow-Up", description: "Create follow-up.", priority: "High" as const },
    { label: "Create Tickler", description: "Create tickler.", priority: "High" as const },
    { label: "Generate Letter", description: "Open Letter Center.", priority: "Medium" as const },
    { label: "Generate Statement", description: "Open Statement Center.", priority: "Medium" as const },
  ];

  if (selectedPromise.status === "Broken") {
    return baseActions.map((action) =>
      action.label === "Escalate Broken Promise"
        ? { ...action, description: "Escalate now." }
        : action,
    );
  }

  if (selectedPromise.status === "Paid" || selectedPromise.status === "Resolved" || selectedPromise.status === "Completed") {
    return baseActions.map((action) =>
      action.label === "Close Promise"
        ? { ...action, description: "Close promise." }
        : action,
    );
  }

  return baseActions;
}

function hasPromiseApprovalActivity(
  selectedPromise: NonNullable<ReturnType<typeof buildARRecoveryPromiseCenterSnapshot>["selectedPromise"]>,
) {
  return selectedPromise.activityFeed.some((entry) => entry.title.trim().toLowerCase() === "promise approved");
}

function isPromiseAlreadyApproved(
  selectedPromise: NonNullable<ReturnType<typeof buildARRecoveryPromiseCenterSnapshot>["selectedPromise"]>,
) {
  return hasPromiseApprovalActivity(selectedPromise) || selectedPromise.status === "Open" || isPromiseAlreadyClosed(selectedPromise);
}

function hasPromiseCompletionActivity(
  selectedPromise: NonNullable<ReturnType<typeof buildARRecoveryPromiseCenterSnapshot>["selectedPromise"]>,
) {
  return selectedPromise.activityFeed.some((entry) => entry.title.trim().toLowerCase() === "promise completed");
}

function isPromiseAlreadyClosed(
  selectedPromise: NonNullable<ReturnType<typeof buildARRecoveryPromiseCenterSnapshot>["selectedPromise"]>,
) {
  return hasPromiseCompletionActivity(selectedPromise) || selectedPromise.status === "Paid" || selectedPromise.status === "Resolved" || selectedPromise.status === "Completed" || selectedPromise.status === "Cancelled";
}

function buildPromiseRiskScore(
  selectedPromise: NonNullable<ReturnType<typeof buildARRecoveryPromiseCenterSnapshot>["selectedPromise"]>,
) {
  let score = 25;
  if (selectedPromise.status === "Broken" || selectedPromise.status === "Escalated") score += 35;
  if (selectedPromise.status === "Partial") score += 15;
  if (selectedPromise.status === "Due Today") score += 20;
  if (selectedPromise.promiseType === "Installment Plan") score += 10;
  if (selectedPromise.remainingBalanceCents > selectedPromise.amountCents) score += 10;
  if (selectedPromise.remainingBalanceCents === 0) score -= 10;
  return Math.max(0, Math.min(score, 100));
}

function buildPromiseLetters(
  selectedPromise: NonNullable<ReturnType<typeof buildARRecoveryPromiseCenterSnapshot>["selectedPromise"]>,
) {
  return [
    {
      id: `${selectedPromise.id}-letter-1`,
      primary: "Friendly Promise Reminder",
      secondary: "Generated",
      meta: `${selectedPromise.promiseType} • ${formatDate(selectedPromise.promiseDate)} • Promise follow-up ready`,
    },
    {
      id: `${selectedPromise.id}-letter-2`,
      primary: "Escalation Notice",
      secondary: "Draft",
      meta: `${selectedPromise.status} • ${selectedPromise.collector} • Manager approval path`,
    },
  ];
}

function buildPromiseStatements(
  selectedPromise: NonNullable<ReturnType<typeof buildARRecoveryPromiseCenterSnapshot>["selectedPromise"]>,
) {
  return [
    {
      id: `${selectedPromise.id}-statement-1`,
      primary: "Promise Statement",
      secondary: "Email",
      meta: `${formatARRecoveryCurrency(selectedPromise.amountCents)} • ${selectedPromise.customerName}`,
    },
    {
      id: `${selectedPromise.id}-statement-2`,
      primary: "Balance Reminder",
      secondary: "PDF Export",
      meta: `${formatARRecoveryCurrency(selectedPromise.remainingBalanceCents)} • ${selectedPromise.clientCompany}`,
    },
  ];
}

function buildPromiseTicklers(
  selectedPromise: NonNullable<ReturnType<typeof buildARRecoveryPromiseCenterSnapshot>["selectedPromise"]>,
) {
  return [
    {
      id: `${selectedPromise.id}-tickler-1`,
      primary: "Promise Follow-Up",
      secondary: selectedPromise.followUpDate ? formatDate(selectedPromise.followUpDate) : "Not set",
      meta: `${selectedPromise.collector} • ${selectedPromise.promiseType} • Follow-up reminder`,
    },
    {
      id: `${selectedPromise.id}-tickler-2`,
      primary: "Broken Promise Review",
      secondary: selectedPromise.status,
      meta: `${selectedPromise.manager} • Escalation-ready reminder`,
    },
  ];
}

function buildPromiseDocuments(
  selectedPromise: NonNullable<ReturnType<typeof buildARRecoveryPromiseCenterSnapshot>["selectedPromise"]>,
) {
  return [
    {
      id: `${selectedPromise.id}-doc-1`,
      primary: "Promise Agreement",
      secondary: "Approved",
      meta: `${selectedPromise.customerName} • ${selectedPromise.invoiceLabels.join(", ") || "No invoices linked"}`,
    },
    {
      id: `${selectedPromise.id}-doc-2`,
      primary: "Payment Evidence Packet",
      secondary: "Archived",
      meta: `${formatARRecoveryCurrency(selectedPromise.amountCents)} • ${selectedPromise.collector}`,
    },
  ];
}

function buildPromiseHistory(
  selectedPromise: NonNullable<ReturnType<typeof buildARRecoveryPromiseCenterSnapshot>["selectedPromise"]>,
) {
  return [
    {
      id: `${selectedPromise.id}-history-1`,
      primary: "Promise created",
      secondary: "Created",
      meta: `${formatDate(selectedPromise.createdAt)} • ${selectedPromise.notes}`,
    },
    {
      id: `${selectedPromise.id}-history-2`,
      primary: "Promise updated",
      secondary: "Updated",
      meta: `${formatDate(selectedPromise.updatedAt)} • ${selectedPromise.collectorNotes}`,
    },
    {
      id: `${selectedPromise.id}-history-3`,
      primary: "Follow-up scheduled",
      secondary: "Reminder",
      meta: `${selectedPromise.followUpDate ? formatDate(selectedPromise.followUpDate) : "Not set"} • ${selectedPromise.managerNotes}`,
    },
  ];
}

function PromiseRecordList({
  title,
  icon,
  records,
}: {
  title: string;
  icon: ReactNode;
  records: Array<{ id: string; primary: string; secondary: string; meta: string }>;
}) {
  return (
    <div className="grid gap-3">
      <div className="border-b border-[#BFEAFF] pb-4">
        <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">
          <ARLightLineIcon icon={icon} />
          {title}
        </p>
        <h2 className="mt-1 text-2xl font-semibold text-[#0A2540]">{title} records</h2>
        <p className="mt-2 max-w-4xl text-sm font-normal leading-7 text-[#475569]">Action-ready record history scoped to this promise.</p>
      </div>
      {records.length ? (
        records.map((record) => (
          <div key={record.id} className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-3">
                <ARLightLineIcon compact icon={icon} />
                <div className="min-w-0">
                  <p className="truncate text-xs font-semibold uppercase tracking-[0.14em] text-[#FF6A2B]">{record.secondary}</p>
                  <h3 className="mt-1 text-lg font-semibold text-[#0A2540]">{record.primary}</h3>
                </div>
              </div>
            </div>
            <p className="mt-2 text-sm font-normal leading-7 text-[#475569]">{record.meta}</p>
          </div>
        ))
      ) : (
        <ManagerEmptyState
          title="No records available"
          description="No records exist for this promise tab yet."
          action="Records will appear here when they are available for the selected promise."
        />
      )}
    </div>
  );
}

function buildRiskScore(account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>) {
  let score = 20;
  if (account.recoveryStatus === "Escalated") score += 40;
  if (account.recoveryStatus === "At Risk") score += 20;
  if (account.escalationStatus === "Director Review") score += 30;
  if (account.promiseToPayCount > 0) score += 10;
  if (account.agingSummary.includes("90")) score += 20;
  return Math.min(score, 100);
}

function latestAccountActivityDate(records: ReturnType<typeof loadARRecoveryWorkspace>["activity"]) {
  return records.length ? records[0].createdAt : "";
}

function buildInvoicePlaceholders(account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>) {
  const count = Math.max(account.openInvoiceCount, 1);
  return Array.from({ length: count }, (_, index) => {
    const originalAmountCents = Math.round(account.openBalanceCents / count);
    const currentBalanceCents = originalAmountCents;
    const invoiceDate = new Date(Date.now() - (index * 14 + 21) * 86400000).toISOString();
    const dueDate = new Date(Date.now() - (index * 14 + 7) * 86400000).toISOString();
    return {
      id: `${account.id}-invoice-${index + 1}`,
      invoiceNumber: `${account.customerNumber}-INV-${String(index + 1).padStart(2, "0")}`,
      invoiceDate,
      originalAmountCents,
      currentBalanceCents,
      status: index === 0 ? "Open" : index === count - 1 ? "Past Due" : "Open",
      amountCents: currentBalanceCents,
      dueDate,
      purchaseOrder: index === 0 ? `${account.customerNumber}-PO-1` : "",
      referenceNumber: `${account.customerNumber}-REF-${String(index + 1).padStart(2, "0")}`,
      notes: index === 0 ? "Primary open invoice in the recovery queue." : "Linked invoice placeholder for the account tab.",
    };
  });
}

function buildLetterPlaceholders(account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>) {
  return [
    { id: `${account.id}-letter-1`, title: "Friendly Reminder", template: "Friendly Reminder", status: "Generated", createdAt: new Date().toISOString(), notes: "Ready for collector review." },
    { id: `${account.id}-letter-2`, title: "Past Due Reminder", template: "Past Due Reminder", status: "Sent", createdAt: new Date(Date.now() - 3 * 86400000).toISOString(), notes: "Delivery placeholder only." },
  ];
}

function buildStatementPlaceholders(account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>) {
  return [
    { id: `${account.id}-statement-1`, title: "Monthly Statement", deliveryMethod: "Email", status: "Sent", createdAt: new Date(Date.now() - 2 * 86400000).toISOString(), notes: "Awaiting view confirmation." },
    { id: `${account.id}-statement-2`, title: "Resent Statement", deliveryMethod: "PDF Export", status: "Created", createdAt: new Date(Date.now() - 8 * 86400000).toISOString(), notes: "For future workflow expansion." },
  ];
}

function buildDocumentPlaceholders(account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>) {
  return [
    { id: `${account.id}-doc-1`, title: "Aging Export", documentType: "Reference", status: "Archived", notes: "Linked to the latest account review." },
    { id: `${account.id}-doc-2`, title: "Collector Notes Packet", documentType: "Brief", status: "Approved", notes: "Prepared for internal use." },
  ];
}

type PlatformAdministrationTabKey =
  | "company"
  | "users"
  | "roles"
  | "permissions"
  | "teams"
  | "branding"
  | "domains"
  | "notifications"
  | "integrations"
  | "licensing";

const PLATFORM_ADMINISTRATION_TABS: Array<{ key: PlatformAdministrationTabKey; label: string; description: string; icon: ReactNode }> = [
  { key: "company", label: "Company", description: "Tenant profile and platform ownership.", icon: <Building2 className="size-4" aria-hidden="true" /> },
  { key: "users", label: "Users", description: "Directory, access status, and invitation readiness.", icon: <Users className="size-4" aria-hidden="true" /> },
  { key: "teams", label: "Teams", description: "Operational team and manager grouping.", icon: <FolderKanban className="size-4" aria-hidden="true" /> },
  { key: "roles", label: "Roles", description: "Default platform and AR Recovery role catalog.", icon: <ShieldCheck className="size-4" aria-hidden="true" /> },
  { key: "permissions", label: "Permissions", description: "Module-level action permissions.", icon: <IdCard className="size-4" aria-hidden="true" /> },
  { key: "branding", label: "Branding", description: "Tenant brand controls.", icon: <Sparkles className="size-4" aria-hidden="true" /> },
  { key: "domains", label: "Domains", description: "Custom domain management.", icon: <Target className="size-4" aria-hidden="true" /> },
  { key: "integrations", label: "Integrations", description: "System connection registry.", icon: <Bolt className="size-4" aria-hidden="true" /> },
  { key: "notifications", label: "Notifications", description: "Notification governance review.", icon: <Activity className="size-4" aria-hidden="true" /> },
  { key: "licensing", label: "Licensing", description: "Product licensing and tenant entitlements.", icon: <ClipboardList className="size-4" aria-hidden="true" /> },
];

const PLATFORM_ADMINISTRATION_ROLES = [
  { role: "Platform Owner", description: "Manages Universal Circuit Hub platform services and enterprise administration.", assignedUsers: 1, permissionCount: 96, status: "Active", visibility: "internal" },
  { role: "Tenant Admin", description: "Manages organization administration, users, roles, teams, and platform settings.", assignedUsers: 2, permissionCount: 78, status: "Active", visibility: "tenant" },
  { role: "AR Administrator", description: "Manages CollabRecover AR Solutions configuration and operational administration.", assignedUsers: 2, permissionCount: 64, status: "Active", visibility: "tenant" },
  { role: "AR Manager", description: "Oversees collectors, assignments, escalations, approvals, and recovery operations.", assignedUsers: 3, permissionCount: 52, status: "Active", visibility: "tenant" },
  { role: "Collector", description: "Manages assigned recovery accounts and daily collection activities.", assignedUsers: 12, permissionCount: 34, status: "Active", visibility: "tenant" },
  { role: "Read Only", description: "Provides view-only access to assigned records, reports, and operational dashboards.", assignedUsers: 4, permissionCount: 16, status: "Active", visibility: "tenant" },
  { role: "Client User", description: "Provides secure access to the Client Portal and assigned portfolio information.", assignedUsers: 0, permissionCount: 8, status: "Active", visibility: "tenant", showStatusBadge: false },
];

const PLATFORM_ADMINISTRATION_PERMISSION_MODULES = [
  "Recovery Workbench",
  "Customers & Accounts",
  "Promises",
  "Ticklers",
  "Letters",
  "Statements",
  "Payments",
  "Manager Command",
  "Client Management",
  "Reports",
  "Analytics",
  "Client Portal",
  "Administration",
];

const PLATFORM_ADMINISTRATION_PERMISSION_ACTIONS = ["View", "Create", "Edit", "Delete", "Approve", "Assign", "Export", "Activate"];
const PLATFORM_ADMINISTRATION_FUTURE_PERMISSION_ACTIONS = ["Manage", "Configure"] as const;
const PLATFORM_ADMINISTRATION_ROLE_SELECTOR_OPTIONS = ["Tenant Admin", "AR Administrator", "AR Manager", "Collector", "Read Only", "Client User"] as const;

const PLATFORM_ADMINISTRATION_PERMISSION_GROUPS = [
  { label: "Recovery Operations", modules: ["Recovery Workbench", "Customers & Accounts", "Promises", "Ticklers", "Letters", "Statements", "Payments"] },
  { label: "Management & Insight", modules: ["Manager Command", "Reports", "Analytics"] },
  { label: "Platform", modules: ["Client Portal", "Administration"] },
] as const;

const PLATFORM_ADMINISTRATION_ROLE_PERMISSION_GROUPS = [
  "Recovery Workbench",
  "Recovery Operations",
  "Promises",
  "Ticklers",
  "Letters",
  "Statements",
  "Payments",
  "Recovery Activity",
  "Timeline",
  "Manager Command",
  "Reporting",
  "Administration",
] as const;

const PLATFORM_ADMINISTRATION_FUTURE_MODULES = ["Audit", "Security", "API Keys", "Automation", "Platform Logs", "System Health"] as const;

const platformAdministrationTextWrapStyle = { overflowWrap: "anywhere", wordBreak: "break-word" } as const;

const PLATFORM_ADMINISTRATION_LICENSED_PRODUCTS = ["CollabRecover AR Solutions"] as const;
const PLATFORM_ADMINISTRATION_FUTURE_ROLE_METRICS = ["Workspace Access"] as const;
const PLATFORM_ADMINISTRATION_FUTURE_USER_FILTERS = ["Search Users", "Role Filter", "Team Filter", "Status Filter"] as const;
const PLATFORM_ADMINISTRATION_FUTURE_TEAM_CAPACITY_METRICS = ["Open Accounts", "Outstanding Balance", "Collector Capacity", "Work Queue Size"] as const;
const PLATFORM_ADMINISTRATION_FUTURE_DOMAIN_CAPABILITIES = ["SSL Certificate", "DNS Verification", "Domain Verification", "Verification History", "Routing Rules", "Audit Trail", "Domain Ownership", "Verification Tokens"] as const;
const PLATFORM_ADMINISTRATION_FUTURE_INTEGRATION_CATEGORIES = ["Email Providers", "SMS Providers", "Phone Systems", "Document Generation", "Statement Delivery", "Letter Delivery", "Payment Providers", "Stripe", "ACH", "Banking APIs", "Microsoft 365", "Google Workspace", "Azure AD / Entra ID", "Single Sign-On", "API Keys", "OAuth", "Webhooks", "Event Subscriptions", "Cloud Storage", "Cloudflare R2", "SharePoint", "Google Drive", "Reporting Integrations", "AI Services"] as const;
const PLATFORM_ADMINISTRATION_FUTURE_INTEGRATION_ACTIONS = ["Connect", "Disconnect", "Configure", "Reconnect", "Test Connection", "Sync Now", "Refresh Credentials", "View Logs", "View History"] as const;
const PLATFORM_ADMINISTRATION_FUTURE_INTEGRATION_STATUSES = ["Connected", "Available", "Pending", "Disconnected", "Disabled", "Requires Setup", "Authentication Failed", "Sync Failed", "Maintenance"] as const;
const PLATFORM_ADMINISTRATION_NOTIFICATION_STATUSES = ["Connected", "Ready", "Future Ready", "Available", "Future", "Inactive"] as const;
const PLATFORM_ADMINISTRATION_NOTIFICATION_CATEGORIES = [
  {
    category: "Platform Alerts",
    purpose: "Platform health, security, infrastructure, and service monitoring.",
    status: "Ready",
    audience: ["Platform Owner", "Tenant Administrator"],
    examples: ["Platform Availability", "Scheduled Maintenance", "Authentication Issues", "SSL Certificate Status", "Domain Verification", "Integration Failures", "License Status"],
    capability: "",
  },
  {
    category: "Workflow Notifications",
    purpose: "Operational notifications generated by Industry Operating Systems.",
    status: "Future Ready",
    audience: ["Managers", "Team Leads", "Collectors"],
    examples: ["Promise Due", "Tickler Due", "Letter Generated", "Statement Generated", "Payment Posted", "Assignment Updated", "Escalation Created", "Manager Approval Required", "Recovery Activity Recorded"],
    capability: "",
  },
  {
    category: "Tenant Service Messages",
    purpose: "Platform communication and tenant lifecycle messages.",
    status: "Ready",
    audience: ["Platform Owner", "Tenant Administrator"],
    examples: ["Product Updates", "Release Notes", "Security Advisories", "Subscription Messages", "Feature Announcements", "Documentation Updates"],
    capability: "",
  },
  {
    category: "Client Notifications",
    purpose: "Future Client Portal communications.",
    status: "Future",
    audience: ["Client Portal Users"],
    examples: ["Secure Message Received", "Statement Available", "Letter Available", "Document Shared", "Portal Invitation", "Payment Confirmation", "Account Updates"],
    capability: "Future Capability, Version 2+",
  },
] as const;

const PLATFORM_ADMINISTRATION_RESPONSIBILITY_GROUPS = [
  { label: "Identity & Access", services: ["Users", "Teams", "Roles", "Permissions"] },
  { label: "Organization", services: ["Company", "Branding", "Domains"] },
  { label: "Platform Services", services: ["Integrations", "Notifications", "Licensing"] },
] as const;

const PLATFORM_ADMINISTRATION_USERS_MODULE_KEY = "ar_recovery_platform_administration_users";
const PLATFORM_ADMINISTRATION_USERS_SCOPE = { scopeKey: "collabrecover-administration", scopeType: "platform" as const };
const PLATFORM_ADMINISTRATION_TEAMS_MODULE_KEY = "ar_recovery_platform_administration_teams";
const PLATFORM_ADMINISTRATION_TEAMS_SCOPE = { scopeKey: "collabrecover-administration-teams", scopeType: "platform" as const };
const PLATFORM_ADMINISTRATION_CANONICAL_USER_ROLES = new Set<BusinessCenterUserRecord["role"] | string>([
  "company_admin",
  "company_manager",
  "company_supervisor",
  "company_team_member",
  "customer_client_user",
  "viewer",
]);

interface PlatformAdministrationUserRecord {
  id: string;
  displayName: string;
  email: string;
  role: string;
  status: string;
  team: string;
  lastLogin: string;
  lastLoginAt?: string;
  invitationStatus: string;
  defaultLandingWorkspace: string;
}

interface PlatformAdministrationUserFormState {
  displayName: string;
  email: string;
  role: string;
  team: string;
  defaultLandingWorkspace: string;
}

interface PlatformAdministrationTeamFormState {
  teamName: string;
  description: string;
  manager: string;
  teamLead: string;
  collectorIds: string[];
  portfolioIds: string[];
  status: string;
}

interface PlatformAdministrationTeamRecord {
  teamName: string;
  description: string;
  manager: string;
  teamLead: string;
  collectors: number;
  collectorIds: string[];
  portfolioCount: number;
  portfolioIds: string[];
  activityHistory: Array<{ id: string; title: string; detail: string; createdAt: string }>;
  status: string;
  assignmentStatus: string;
  capacitySummary: string;
  workloadSummary: string;
}

interface PlatformAdministrationPortfolioOption {
  id: string;
  portfolioName: string;
  client: string;
  status: string;
}

type PlatformAdministrationWorkflow = "invite" | "deactivate" | "delete" | "access-code" | "role" | null;
type PlatformAdministrationTeamWorkflow = "summary" | "create" | "edit" | "manager" | null;
type PlatformAdministrationTeamPanelTab = "overview" | "members" | "portfolios" | "activity" | "history";
type PlatformAdministrationAssignmentSectionKey = "manager" | "teamLead" | "collectors" | "portfolios";
type PlatformAdministrationRoleRecord = (typeof PLATFORM_ADMINISTRATION_ROLES)[number];
type PlatformAdministrationPermissionGroupRecord = (typeof PLATFORM_ADMINISTRATION_PERMISSION_GROUPS)[number];
type PlatformAdministrationRolePanelMode = "summary" | "assigned-users" | null;

interface PlatformAdministrationNotification {
  message: string;
  tone: "success" | "error" | "info";
}

const blankPlatformAdministrationUserForm: PlatformAdministrationUserFormState = {
  displayName: "",
  email: "",
  role: "Collector",
  team: "Recovery Operations",
  defaultLandingWorkspace: "recovery-workbench",
};

const blankPlatformAdministrationTeamForm: PlatformAdministrationTeamFormState = {
  teamName: "",
  description: "",
  manager: "",
  teamLead: "",
  collectorIds: [],
  portfolioIds: [],
  status: "Active",
};

function PlatformAdministrationSection({
  workspace,
}: {
  workspace: ReturnType<typeof loadARRecoveryWorkspace>;
}) {
  const [selectedTab, setSelectedTab] = useState<PlatformAdministrationTabKey>("users");
  const [selectedPermissionRole, setSelectedPermissionRole] = useState<(typeof PLATFORM_ADMINISTRATION_ROLE_SELECTOR_OPTIONS)[number]>("AR Manager");
  const seededUserRows = useMemo(() => buildPlatformAdministrationUsers(workspace), [workspace]);
  const seededTeamRows = useMemo(() => buildPlatformAdministrationTeams(workspace), [workspace]);
  const [userRows, setUserRows] = useState<PlatformAdministrationUserRecord[]>(seededUserRows);
  const [teamRows, setTeamRows] = useState<PlatformAdministrationTeamRecord[]>(seededTeamRows);
  const [inviteRequestId, setInviteRequestId] = useState(0);
  const tenantUserRows = userRows.filter((user) => user.role !== "Platform Owner");
  const activeUsers = tenantUserRows.filter((user) => user.status === "Active").length;
  const licensedProducts = 1;

  useEffect(() => {
    let cancelled = false;
    async function hydrateUserDirectory() {
      try {
        const response = await loadBusinessCenterOperationalState<PlatformAdministrationUserRecord[]>(
          PLATFORM_ADMINISTRATION_USERS_MODULE_KEY,
          PLATFORM_ADMINISTRATION_USERS_SCOPE,
        );
        if (cancelled) return;
        const persistedUsers = Array.isArray(response.state) ? response.state.map(normalizePlatformAdministrationUser).filter(Boolean) : [];
        const canonicalUsers = await refreshBusinessCenterCompanyUsers();
        if (cancelled) return;
        const hydratedUsers = mergePlatformAdministrationUsersWithCanonicalUsers(
          workspace,
          persistedUsers.length ? persistedUsers : seededUserRows,
          canonicalUsers,
        );
        setUserRows(hydratedUsers);
        if (hasPlatformAdministrationUserDirectoryMismatch(persistedUsers, hydratedUsers)) {
          await saveBusinessCenterOperationalState(
            PLATFORM_ADMINISTRATION_USERS_MODULE_KEY,
            hydratedUsers,
            PLATFORM_ADMINISTRATION_USERS_SCOPE,
          );
        }
      } catch (error) {
        console.warn("User Directory hydration failed; displaying seeded fallback until persistence is reachable.", {
          errorType: error instanceof Error ? error.name : typeof error,
        });
        if (!cancelled) setUserRows(seededUserRows);
      }
    }
    void hydrateUserDirectory();
    return () => {
      cancelled = true;
    };
  }, [seededUserRows, workspace]);

  useEffect(() => {
    let cancelled = false;
    loadBusinessCenterOperationalState<PlatformAdministrationTeamRecord[]>(
      PLATFORM_ADMINISTRATION_TEAMS_MODULE_KEY,
      PLATFORM_ADMINISTRATION_TEAMS_SCOPE,
    )
      .then((response) => {
        if (cancelled) return;
        const persistedTeams = Array.isArray(response.state) ? response.state.map(normalizePlatformAdministrationTeam).filter(Boolean) : [];
        setTeamRows(persistedTeams.length ? persistedTeams : seededTeamRows);
      })
      .catch(() => {
        if (!cancelled) setTeamRows(seededTeamRows);
      });
    return () => {
      cancelled = true;
    };
  }, [seededTeamRows]);

  async function savePlatformAdministrationUsers(nextUsers: PlatformAdministrationUserRecord[]) {
    const normalizedUsers = nextUsers.map(normalizePlatformAdministrationUser);
    await saveBusinessCenterOperationalState(
      PLATFORM_ADMINISTRATION_USERS_MODULE_KEY,
      normalizedUsers,
      PLATFORM_ADMINISTRATION_USERS_SCOPE,
    );
    setUserRows(normalizedUsers);
    return normalizedUsers;
  }

  async function savePlatformAdministrationTeams(nextTeams: PlatformAdministrationTeamRecord[]) {
    const normalizedTeams = nextTeams.map(normalizePlatformAdministrationTeam);
    await saveBusinessCenterOperationalState(
      PLATFORM_ADMINISTRATION_TEAMS_MODULE_KEY,
      normalizedTeams,
      PLATFORM_ADMINISTRATION_TEAMS_SCOPE,
    );
    setTeamRows(normalizedTeams);
    return normalizedTeams;
  }

  return (
    <section className="ar-workspace-os mt-4 grid gap-4">
      <header className="rounded-[1.75rem] border border-[#BFEAFF] bg-white p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">Universal Circuit Hub</p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-[#0A2540] sm:text-3xl">Administration</h1>
            <p className="mt-3 max-w-4xl text-sm leading-7 text-[#475569]">
              Manage the platform services that CollabRecover AR Solutions consumes: company administration, users, roles, permissions, teams, and future tenant controls.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                setSelectedTab("users");
                setInviteRequestId((current) => current + 1);
              }}
              className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-[#14B8A6] px-4 text-sm font-semibold text-white transition hover:bg-[#0F9F94] focus-ring"
            >
              <Users className="size-4" aria-hidden="true" />
              Invite User
            </button>
            <button type="button" className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-4 text-sm font-semibold text-[#0A2540] transition hover:border-[#14B8A6] focus-ring">
              <ShieldCheck className="size-4 text-[#14B8A6]" aria-hidden="true" />
              Review Roles
            </button>
          </div>
        </div>
      </header>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MiniStat label="Active Users" value={tenantUserRows.length} note={`${activeUsers} active platform users.`} icon={<Users className="size-5" aria-hidden="true" />} />
        <MiniStat label="Platform Roles" value={PLATFORM_ADMINISTRATION_ROLES.length} note="Default platform role catalog." icon={<ShieldCheck className="size-5" aria-hidden="true" />} />
        <MiniStat label="Permission Groups" value={PLATFORM_ADMINISTRATION_PERMISSION_MODULES.length} note="Grouped by operating module." icon={<IdCard className="size-5" aria-hidden="true" />} />
        <MiniStat label="Licensed Products" value={licensedProducts} note="Industry Operating Systems licensed for this organization." icon={<ClipboardList className="size-5" aria-hidden="true" />} />
      </div>

      <section className="rounded-[1.75rem] border border-[#BFEAFF] bg-white p-4">
        <div className="flex gap-2 overflow-x-auto pb-1">
          {PLATFORM_ADMINISTRATION_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setSelectedTab(tab.key)}
              className={`inline-flex min-h-11 shrink-0 items-center gap-2 rounded-xl border px-4 text-sm font-semibold transition focus-ring ${
                selectedTab === tab.key
                  ? "border-[#14B8A6] bg-[#E8FCFF] text-[#0A2540]"
                  : "border-[#BFEAFF] bg-white text-[#334155] hover:border-[#14B8A6] hover:bg-[#F4FBFF]"
              }`}
              title={tab.description}
            >
              <span className="text-[#14B8A6]">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </section>

      {selectedTab === "users" ? (
        <PlatformAdministrationUsersTab
          workspace={workspace}
          userRows={tenantUserRows}
          onSaveUsers={savePlatformAdministrationUsers}
          inviteRequestId={inviteRequestId}
        />
      ) : renderPlatformAdministrationTab(selectedTab, workspace, userRows, teamRows, selectedPermissionRole, setSelectedPermissionRole, savePlatformAdministrationTeams)}
    </section>
  );
}

function buildPlatformAdministrationUsers(workspace: ReturnType<typeof loadARRecoveryWorkspace>): PlatformAdministrationUserRecord[] {
  const collectorNames = Array.from(new Set(workspace.accounts.map((account) => account.assignedCollector).filter(Boolean))).slice(0, 4);
  const managerNames = Array.from(new Set(workspace.promises.map((promise) => promise.manager).filter(Boolean))).slice(0, 2);
  const tenantEmail = formatPlatformAdministrationBusinessEmail(workspace.clientProfile.email, "admin@futurearrecovery.com");
  const directory = [
    { id: "platform-owner", displayName: "Milton Platform Owner", email: "platform@collabrecover.com", role: "Platform Owner", status: "Active", team: "Platform Services", lastLogin: "Today", invitationStatus: "Accepted", defaultLandingWorkspace: "business-center-os" },
    { id: "tenant-admin", displayName: workspace.clientProfile.collectionDepartmentContact || "Tenant Administrator", email: tenantEmail, role: "Tenant Admin", status: "Active", team: "Administration", lastLogin: "Today", invitationStatus: "Accepted", defaultLandingWorkspace: "business-center-os" },
    ...managerNames.map((name, index) => ({ id: `manager-${index + 1}`, displayName: name || "Profile Incomplete", email: index === 0 ? "manager@futurearrecovery.com" : `manager${index + 1}@futurearrecovery.com`, role: "AR Manager", status: "Active", team: "Recovery Management", lastLogin: index === 0 ? "Today" : "Yesterday", invitationStatus: "Accepted", defaultLandingWorkspace: "manager-command" })),
    ...collectorNames.map((name, index) => ({ id: `collector-${index + 1}`, displayName: name || "Profile Incomplete", email: index === 0 ? "collector@futurearrecovery.com" : `collector${index + 1}@futurearrecovery.com`, role: "Collector", status: "Active", team: "Recovery Operations", lastLogin: index < 2 ? "Today" : "This week", invitationStatus: "Accepted", defaultLandingWorkspace: "recovery-workbench" })),
    { id: "client-user", displayName: workspace.clientProfile.accountingContact || "Client Portal Contact", email: formatPlatformAdministrationBusinessEmail(workspace.clientProfile.email, "client.user@futurearrecovery.com"), role: "Client User", status: "Active", team: "Client Portal", lastLogin: "Profile Incomplete", invitationStatus: "Accepted", defaultLandingWorkspace: "client-portal" },
  ];
  return directory.slice(0, 10);
}

function formatPlatformAdministrationBusinessEmail(value: string | undefined, fallback: string) {
  const email = value?.trim();
  if (!email || email.toLowerCase().endsWith(".local") || email.toLowerCase().includes("@truevance.local")) return fallback;
  return email;
}

function buildPlatformAdministrationTeams(workspace: ReturnType<typeof loadARRecoveryWorkspace>): PlatformAdministrationTeamRecord[] {
  const portfolioCount = Math.max(workspace.accounts.length ? 1 : 0, 1);
  return [
    { teamName: "Recovery Operations", description: "Daily recovery queue ownership and collector coordination.", manager: workspace.promises[0]?.manager || "AR Manager", teamLead: "", collectors: Math.max(new Set(workspace.accounts.map((account) => account.assignedCollector)).size, 1), collectorIds: [], portfolioIds: ["foundation"], activityHistory: [], status: "Active", portfolioCount, assignmentStatus: "Connected", capacitySummary: "Collector capacity aligned to active portfolio coverage.", workloadSummary: `${workspace.accounts.length} account records available in the recovery workspace.` },
    { teamName: "Manager Command", description: "Management oversight for escalations, promise activity, and recovery prioritization.", manager: workspace.promises[1]?.manager || "Recovery Director", teamLead: "", collectors: 3, collectorIds: [], portfolioIds: [], activityHistory: [], status: "Active", portfolioCount: Math.max(portfolioCount - 1, 0), assignmentStatus: "Ready", capacitySummary: "Management coverage available for active recovery operations.", workloadSummary: `${workspace.promises.length} promise records available for manager review.` },
    { teamName: "Client Success", description: "Client-facing recovery support and portal coordination.", manager: workspace.clientProfile.collectionDepartmentContact || "Tenant Admin", teamLead: "", collectors: 0, collectorIds: [], portfolioIds: ["foundation"], activityHistory: [], status: "Active", portfolioCount: 1, assignmentStatus: "Ready", capacitySummary: "Client support team does not carry collector capacity.", workloadSummary: "Client portal coverage is ready for assigned portfolio support." },
  ];
}

function normalizePlatformAdministrationTeam(team: PlatformAdministrationTeamRecord): PlatformAdministrationTeamRecord {
  const teamName = team.teamName?.trim() || "Profile Incomplete";
  return {
    teamName,
    description: team.description?.trim() || "Team description not configured.",
    manager: team.manager?.trim() || "Unassigned",
    teamLead: team.teamLead?.trim() || "",
    collectorIds: Array.isArray(team.collectorIds) ? [...new Set(team.collectorIds.filter(Boolean))] : [],
    portfolioIds: Array.isArray(team.portfolioIds) ? [...new Set(team.portfolioIds.filter(Boolean))] : [],
    activityHistory: Array.isArray(team.activityHistory) ? team.activityHistory : [],
    collectors: Array.isArray(team.collectorIds) && team.collectorIds.length ? team.collectorIds.length : Number.isFinite(team.collectors) ? Math.max(team.collectors, 0) : 0,
    status: team.status === "Inactive" || team.status === "Archived" ? team.status : "Active",
    portfolioCount: Array.isArray(team.portfolioIds) && team.portfolioIds.length ? team.portfolioIds.length : Number.isFinite(team.portfolioCount) ? Math.max(team.portfolioCount, 0) : 0,
    assignmentStatus: team.assignmentStatus || "Ready",
    capacitySummary: team.capacitySummary?.trim() || "Capacity summary not configured.",
    workloadSummary: team.workloadSummary?.trim() || "Current workload summary not configured.",
  };
}

function buildPlatformAdministrationPortfolioOptions(workspace: ReturnType<typeof loadARRecoveryWorkspace>): PlatformAdministrationPortfolioOption[] {
  const activationFoundation = loadPortfolioActivationEngineFoundation(workspace.company.id);
  const activationSummary = activationFoundation.activationSummary;
  return [
    {
      id: activationFoundation.activation.portfolioId || "foundation",
      portfolioName: activationSummary.portfolio || "Portfolio Assignment Engine Foundation",
      client: activationSummary.client || workspace.company.companyName,
      status: activationFoundation.activation.status || activationSummary.activationStatus || "Ready",
    },
  ];
}

function PlatformAdministrationUsersTab({
  workspace,
  userRows,
  onSaveUsers,
  inviteRequestId,
}: {
  workspace: ReturnType<typeof loadARRecoveryWorkspace>;
  userRows: PlatformAdministrationUserRecord[];
  onSaveUsers: (users: PlatformAdministrationUserRecord[]) => Promise<PlatformAdministrationUserRecord[]>;
  inviteRequestId: number;
}) {
  const [selectedUserId, setSelectedUserId] = useState(userRows[0]?.id || "");
  const [sessionUserEmail, setSessionUserEmail] = useState("");
  const [sessionUserRole, setSessionUserRole] = useState("");
  const [activeWorkflow, setActiveWorkflow] = useState<PlatformAdministrationWorkflow>(null);
  const [notification, setNotification] = useState<PlatformAdministrationNotification | null>(null);
  const [saving, setSaving] = useState(false);
  const [sendingAccessCode, setSendingAccessCode] = useState(false);
  const [inviteForm, setInviteForm] = useState<PlatformAdministrationUserFormState>(blankPlatformAdministrationUserForm);
  const [roleDraft, setRoleDraft] = useState("Collector");
  const [landingWorkspaceDraft, setLandingWorkspaceDraft] = useState("");
  const [deleteAssessment, setDeleteAssessment] = useState<BusinessCenterPermanentUserDeletionResult | null>(null);
  const [deleteConfirmationEmail, setDeleteConfirmationEmail] = useState("");
  const [deleteAssessmentLoading, setDeleteAssessmentLoading] = useState(false);
  const selectedUser = userRows.find((user) => user.id === selectedUserId) || null;
  const sessionDirectoryUser = userRows.find((user) => normalizePlatformAdministrationEmail(user.email) === normalizePlatformAdministrationEmail(sessionUserEmail)) || null;
  const validRoles = PLATFORM_ADMINISTRATION_ROLES.map((role) => role.role).filter((role) => role !== "Platform Owner");
  const canDeactivateSelectedUser = Boolean(selectedUser && selectedUser.status === "Active" && normalizePlatformAdministrationEmail(selectedUser.email) !== normalizePlatformAdministrationEmail(sessionUserEmail));
  const canSendAccessCode = Boolean(selectedUser && selectedUser.status === "Active" && selectedUser.role !== "Client User" && selectedUser.email.trim());
  const canUsePermanentDelete = Boolean(
    ["kreatethedate_admin", "admin"].includes(sessionUserRole)
    || (sessionDirectoryUser && ["Platform Owner", "Tenant Admin", "AR Administrator"].includes(sessionDirectoryUser.role)),
  );
  const canPermanentlyDeleteSelectedUser = Boolean(canUsePermanentDelete && selectedUser && normalizePlatformAdministrationEmail(selectedUser.email) !== normalizePlatformAdministrationEmail(sessionUserEmail));

  useEffect(() => {
    if (!selectedUserId || !userRows.some((user) => user.id === selectedUserId)) {
      setSelectedUserId(userRows[0]?.id || "");
    }
  }, [selectedUserId, userRows]);

  useEffect(() => {
    let cancelled = false;
    getSession()
      .then((session) => {
        if (!cancelled) {
          setSessionUserEmail(session.user?.email || "");
          setSessionUserRole(session.user?.role || "");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSessionUserEmail("");
          setSessionUserRole("");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (inviteRequestId > 0) openInviteForm();
  }, [inviteRequestId]);

  useEffect(() => {
    if (!notification) return undefined;
    const timeoutId = window.setTimeout(() => setNotification(null), notification.tone === "error" ? 6500 : 4200);
    return () => window.clearTimeout(timeoutId);
  }, [notification]);

  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        closeWorkflow();
      }
    }

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, []);

  function openInviteForm() {
    setActiveWorkflow("invite");
    setNotification(null);
    setInviteForm(blankPlatformAdministrationUserForm);
  }

  function openRoleEditor() {
    if (!selectedUser) {
      showNotification("Select a user before assigning a role.", "error");
      return;
    }
    setRoleDraft(validRoles.includes(selectedUser.role) ? selectedUser.role : "Collector");
    setLandingWorkspaceDraft(normalizeDefaultLandingWorkspace(selectedUser.defaultLandingWorkspace));
    setActiveWorkflow("role");
    setNotification(null);
  }

  function openDeactivateConfirmation() {
    if (!selectedUser) {
      showNotification("Select a user before deactivating access.", "error");
      return;
    }
    if (!canDeactivateSelectedUser) {
      showNotification("Only another active user can be deactivated.", "error");
      return;
    }
    setActiveWorkflow("deactivate");
    setNotification(null);
  }

  function openPermanentDeleteWorkflow() {
    if (!selectedUser) {
      showNotification("Select a user before permanently deleting a record.", "error");
      return;
    }
    if (!canUsePermanentDelete) {
      showNotification("Only authorized administrators can permanently delete unused user records.", "error");
      return;
    }
    if (!canPermanentlyDeleteSelectedUser) {
      showNotification("You cannot permanently delete your own active administrator record.", "error");
      return;
    }
    setActiveWorkflow("delete");
    setNotification(null);
    setDeleteAssessment(null);
    setDeleteConfirmationEmail("");
    setDeleteAssessmentLoading(true);
    assessPermanentBusinessCenterUserDeletion(selectedUser.id)
      .then((assessment) => setDeleteAssessment(assessment))
      .catch((error) => {
        const message = error instanceof Error ? error.message : "Unable to assess permanent deletion eligibility.";
        setDeleteAssessment({
          eligible: false,
          deleted: false,
          userId: selectedUser.id,
          displayName: selectedUser.displayName,
          email: selectedUser.email,
          dependencies: [{ moduleKey: "eligibility_check", scopeKey: "User Directory", count: 1, reason: message }],
          cleanup: {
            canonicalUsersRemoved: 0,
            platformUsersRemoved: 0,
            invitationsRemoved: 0,
            teamAssignmentsRemoved: 0,
            sessionsRevoked: 0,
            accessCodesInvalidated: 0,
            loginUsersRemoved: 0,
          },
          message,
        });
      })
      .finally(() => setDeleteAssessmentLoading(false));
  }

  function openAccessCodeConfirmation() {
    if (!selectedUser) {
      showNotification("Select a user before sending an access code.", "error");
      return;
    }
    if (!canSendAccessCode) {
      showNotification("Select an active internal user before sending an access code.", "error");
      return;
    }
    setActiveWorkflow("access-code");
    setNotification(null);
  }

  function closeWorkflow() {
    setActiveWorkflow(null);
    setDeleteAssessment(null);
    setDeleteConfirmationEmail("");
    setDeleteAssessmentLoading(false);
  }

  function showNotification(message: string, tone: PlatformAdministrationNotification["tone"] = "success") {
    setNotification({ message, tone });
  }

  async function handleInviteSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const displayName = inviteForm.displayName.trim();
    const email = normalizePlatformAdministrationEmail(inviteForm.email);
    const role = inviteForm.role;
    const team = inviteForm.team.trim();
    if (!displayName || !email || !role || !team) {
      showNotification("Display name, email, role, and team are required before inviting a user.", "error");
      return;
    }
    if (!validRoles.includes(role)) {
      showNotification("Choose a supported Administration role before inviting a user.", "error");
      return;
    }
    const existingUser = userRows.find((user) => normalizePlatformAdministrationEmail(user.email) === email);
    if (existingUser) {
      if (existingUser.invitationStatus === "Accepted") {
        showNotification("This user has already accepted access. Use Send Access Code to sign in.", "info");
        return;
      } else {
        showNotification("Replacing the pending invitation and invalidating the previous link.", "info");
      }
    }

    setSaving(true);
    setNotification(null);
    try {
      const now = new Date().toISOString();
      const companyId = workspace.company.id || "company-future-ar-recovery";
      const nextUser = normalizePlatformAdministrationUser({
        id: existingUser?.id || `ar-admin-user-${crypto.randomUUID()}`,
        displayName,
        email,
        role,
        team,
        defaultLandingWorkspace: inviteForm.defaultLandingWorkspace,
        status: "Active",
        lastLogin: existingUser?.lastLogin || "Invite Pending",
        lastLoginAt: existingUser?.lastLoginAt,
        invitationStatus: "Pending",
      });
      const companyUser: BusinessCenterUserRecord = {
        id: nextUser.id,
        companyId,
        parentCompanyId: null,
        firstName: displayName.split(" ")[0] || displayName,
        lastName: displayName.split(" ").slice(1).join(" "),
        displayName,
        fullName: displayName,
        email,
        jobTitle: role,
        department: "Recovery Operations",
        phone: "",
        team,
        notificationPreference: "email",
        role: mapPlatformAdministrationRoleToCompanyUserRole(role),
        status: "invited",
        portalAccessStatus: "pending",
        defaultLandingWorkspace: normalizeDefaultLandingWorkspace(nextUser.defaultLandingWorkspace),
        invitationStatus: "pending",
        invitationMessage: "Welcome to the CollabRecover AR Solutions recovery operations portal.",
        assignedClientIds: [companyId],
        assignedProjectIds: [],
        assignedTaskIds: [],
        assignedDocumentIds: [],
        createdAt: now,
        updatedAt: now,
      };
      const invitation = await inviteBusinessCenterUser({
        user: companyUser,
        companyName: workspace.company.companyName || "CollabRecover AR Solutions",
        invitationMessage: companyUser.invitationMessage,
      });
      const nextUsers = await onSaveUsers(existingUser
        ? userRows.map((user) => (user.id === existingUser.id ? nextUser : user))
        : [nextUser, ...userRows]);
      setSelectedUserId(nextUser.id);
      closeWorkflow();
      setInviteForm(blankPlatformAdministrationUserForm);
      showNotification(
        `Invitation email sent to ${nextUser.displayName}. Invitation Status: Pending.${invitation.deliveryMessageId ? ` Provider reference: ${invitation.deliveryMessageId}` : ""}`,
      );
      if (!nextUsers.some((user) => user.id === nextUser.id)) showNotification("Invitation saved, but the refreshed directory did not include the new user.", "error");
    } catch (error) {
      showNotification(error instanceof Error ? error.message : "Unable to invite the user.", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeactivateSelectedUser() {
    if (!selectedUser) return;
    if (normalizePlatformAdministrationEmail(selectedUser.email) === normalizePlatformAdministrationEmail(sessionUserEmail)) {
      closeWorkflow();
      showNotification("You cannot deactivate your own administrator account from this workflow.", "error");
      return;
    }

    setSaving(true);
    setNotification(null);
    try {
      const nextUser = normalizePlatformAdministrationUser({
        ...selectedUser,
        status: "Deactivated",
        invitationStatus: "Revoked",
        lastLogin: selectedUser.lastLogin || "Profile Incomplete",
      });
      const nextUsers = await onSaveUsers(userRows.map((user) => (user.id === selectedUser.id ? nextUser : user)));
      closeWorkflow();
      setSelectedUserId(nextUser.id);
      showNotification(`${nextUser.displayName} was deactivated.`);
      if (!nextUsers.find((user) => user.id === nextUser.id && user.status === "Deactivated")) showNotification("Deactivation saved, but the refreshed directory did not show the updated status.", "error");
    } catch (error) {
      showNotification(error instanceof Error ? error.message : "Unable to deactivate the selected user.", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handlePermanentDeleteSelectedUser() {
    if (!selectedUser) return;
    const expectedEmail = normalizePlatformAdministrationEmail(selectedUser.email);
    if (normalizePlatformAdministrationEmail(deleteConfirmationEmail) !== expectedEmail) {
      showNotification("Type the selected user's email address to confirm permanent deletion.", "error");
      return;
    }

    setSaving(true);
    setNotification(null);
    try {
      const result = await permanentlyDeleteBusinessCenterUser({
        userId: selectedUser.id,
        confirmationEmail: deleteConfirmationEmail,
      });
      const nextUsers = Array.isArray(result.platformUsers) && result.platformUsers.length
        ? result.platformUsers.map(normalizePlatformAdministrationUser)
        : userRows.filter((user) => user.id !== selectedUser.id);
      await onSaveUsers(nextUsers);
      closeWorkflow();
      setSelectedUserId(nextUsers[0]?.id || "");
      showNotification(result.message || `${selectedUser.displayName} was permanently deleted.`);
    } catch (error) {
      showNotification(error instanceof Error ? error.message : "Unable to permanently delete the selected user.", "error");
      if (selectedUser) {
        setDeleteAssessmentLoading(true);
        assessPermanentBusinessCenterUserDeletion(selectedUser.id)
          .then((assessment) => setDeleteAssessment(assessment))
          .catch(() => undefined)
          .finally(() => setDeleteAssessmentLoading(false));
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleSendAccessCode() {
    if (!selectedUser || !canSendAccessCode) {
      showNotification("Select an active internal user before sending an access code.", "error");
      return;
    }

    setSendingAccessCode(true);
    setNotification(null);
    try {
      await syncPlatformAdministrationUserForAccess(workspace, selectedUser);
      const delivery = await login(selectedUser.email, true);
      closeWorkflow();
      showNotification(
        `Access code sent to ${selectedUser.email}.${delivery.deliveryMessageId ? ` Provider reference: ${delivery.deliveryMessageId}` : ""}`,
      );
    } catch (error) {
      showNotification(error instanceof Error ? error.message : "Unable to send an access code for the selected user.", "error");
    } finally {
      setSendingAccessCode(false);
    }
  }

  async function handleRoleSave() {
    if (!selectedUser) return;
    if (!validRoles.includes(roleDraft)) {
      showNotification("Choose a supported Administration role before saving.", "error");
      return;
    }

    setSaving(true);
    setNotification(null);
    try {
      const nextUser = normalizePlatformAdministrationUser({
        ...selectedUser,
        role: roleDraft,
        defaultLandingWorkspace: landingWorkspaceDraft,
      });
      const nextUsers = await onSaveUsers(userRows.map((user) => (user.id === selectedUser.id ? nextUser : user)));
      await syncPlatformAdministrationUserForAccess(workspace, nextUser);
      closeWorkflow();
      setSelectedUserId(nextUser.id);
      showNotification(`${nextUser.displayName} was assigned the ${nextUser.role} role.`);
      if (!nextUsers.find((user) => user.id === nextUser.id && user.role === nextUser.role)) showNotification("Role saved, but the refreshed directory did not show the updated role.", "error");
    } catch (error) {
      showNotification(error instanceof Error ? error.message : "Unable to assign the selected role.", "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="relative rounded-[1.75rem] border border-[#BFEAFF] bg-white p-5">
      <SectionHeading eyebrow="Users" title="User Directory" description="Manage platform user access, role assignments, team membership, and invitation status for this tenant." />
      <div className="mt-5 grid gap-3 xl:grid-cols-4">
        <button type="button" onClick={openInviteForm} disabled={saving} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-4 text-sm font-semibold text-[#0A2540] transition hover:border-[#14B8A6] focus-ring disabled:cursor-not-allowed disabled:opacity-60">
          <ARLightLineIcon icon={<Bolt className="size-4" aria-hidden="true" />} compact />
          Invite User
        </button>
        <button type="button" onClick={openDeactivateConfirmation} disabled={!canDeactivateSelectedUser || saving} title={!selectedUser ? "Select a user first." : !canDeactivateSelectedUser ? "Only another active user can be deactivated." : undefined} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-4 text-sm font-semibold text-[#0A2540] transition hover:border-[#14B8A6] focus-ring disabled:cursor-not-allowed disabled:opacity-60">
          <ARLightLineIcon icon={<Bolt className="size-4" aria-hidden="true" />} compact />
          Deactivate User
        </button>
        <button type="button" onClick={openAccessCodeConfirmation} disabled={!canSendAccessCode || sendingAccessCode} title={!selectedUser ? "Select a user first." : !canSendAccessCode ? "Select an active internal user." : undefined} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-4 text-sm font-semibold text-[#0A2540] transition hover:border-[#14B8A6] focus-ring disabled:cursor-not-allowed disabled:opacity-60">
          <ARLightLineIcon icon={<Bolt className="size-4" aria-hidden="true" />} compact />
          {sendingAccessCode ? "Sending..." : "Send Access Code"}
        </button>
        <button type="button" onClick={openRoleEditor} disabled={!selectedUser || saving} title={!selectedUser ? "Select a user first." : undefined} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-4 text-sm font-semibold text-[#0A2540] transition hover:border-[#14B8A6] focus-ring disabled:cursor-not-allowed disabled:opacity-60">
          <ARLightLineIcon icon={<Bolt className="size-4" aria-hidden="true" />} compact />
          Assign Role
        </button>
        {canUsePermanentDelete ? (
          <button type="button" onClick={openPermanentDeleteWorkflow} disabled={!canPermanentlyDeleteSelectedUser || saving} title={!selectedUser ? "Select a user first." : !canPermanentlyDeleteSelectedUser ? "Only another eligible user can be permanently deleted." : undefined} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 text-sm font-semibold text-red-900 transition hover:border-red-300 hover:bg-red-100 focus-ring disabled:cursor-not-allowed disabled:opacity-60">
            <ARLightLineIcon icon={<AlertTriangle className="size-4" aria-hidden="true" />} compact />
            Permanently Delete User
          </button>
        ) : null}
      </div>

      <div className="mt-4 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] px-4 py-3" aria-live="polite">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">Selected User</p>
        <p className="mt-1 text-sm font-semibold text-[#0A2540]">
          {selectedUser ? `${selectedUser.displayName} - ${selectedUser.email} - ${selectedUser.role}` : "No user selected"}
        </p>
      </div>

      {notification ? (
        <div
          role={notification.tone === "error" ? "alert" : "status"}
          className={`fixed right-4 top-4 z-[90] max-w-md rounded-2xl border px-4 py-3 text-sm font-semibold shadow-[0_18px_48px_rgba(15,23,42,.18)] ${
            notification.tone === "error"
              ? "border-red-200 bg-red-50 text-red-900"
              : "border-[#BFEAFF] bg-white text-[#0A2540]"
          }`}
        >
          {notification.message}
        </div>
      ) : null}

      <div className="mt-5 max-w-full overflow-x-auto" data-future-user-filters={PLATFORM_ADMINISTRATION_FUTURE_USER_FILTERS.join(",")}>
        <table className="min-w-full table-fixed text-left text-sm">
          <thead className="text-xs uppercase tracking-[0.12em] text-[#14B8A6]">
            <tr className="border-b border-[#BFEAFF]">
              {["Display Name", "Email", "Role", "Status", "Team", "Default Landing", "Last Login", "Invitation Status"].map((heading) => (
                <th key={heading} className="min-w-0 px-3 py-3 font-semibold">{heading}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {userRows.map((user) => {
              const isSelected = selectedUserId === user.id;
              return (
                <tr
                  key={user.id}
                  tabIndex={0}
                  aria-selected={isSelected}
                  onClick={() => setSelectedUserId(user.id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setSelectedUserId(user.id);
                    }
                  }}
                  className={`cursor-pointer border-b border-[#E8FCFF] outline-none transition focus-visible:ring-2 focus-visible:ring-[#14B8A6] ${
                    isSelected ? "bg-[#E8FCFF] shadow-[inset_4px_0_0_#14B8A6]" : "hover:bg-[#F4FBFF]"
                  }`}
                >
                  <td className="min-w-0 px-3 py-2.5 font-semibold text-[#0A2540]"><span className="block max-w-full min-w-0 break-words leading-snug" style={platformAdministrationTextWrapStyle}>{user.displayName || "Profile Incomplete"}</span></td>
                  <td className="min-w-0 px-3 py-2.5 text-[#475569]"><span className="block max-w-full min-w-0 break-words leading-snug" style={platformAdministrationTextWrapStyle}>{user.email}</span></td>
                  <td className="min-w-0 px-3 py-2.5 text-[#0A2540]"><span className="block max-w-full min-w-0 break-words leading-snug" style={platformAdministrationTextWrapStyle}>{user.role}</span></td>
                  <td className="min-w-0 px-3 py-2.5"><PlatformAdministrationStatusPill status={user.status} /></td>
                  <td className="min-w-0 px-3 py-2.5 text-[#475569]"><span className="block max-w-full min-w-0 break-words leading-snug" style={platformAdministrationTextWrapStyle}>{user.team || "Profile Incomplete"}</span></td>
                  <td className="min-w-0 px-3 py-2.5 text-[#475569]"><span className="block max-w-full min-w-0 break-words leading-snug" style={platformAdministrationTextWrapStyle}>{getDefaultLandingWorkspaceLabel(user.defaultLandingWorkspace)}</span></td>
                  <td className="min-w-0 px-3 py-2.5 text-[#475569]"><span className="block max-w-full min-w-0 break-words leading-snug" style={platformAdministrationTextWrapStyle}>{formatPlatformAdministrationLastLogin(user)}</span></td>
                  <td className="min-w-0 px-3 py-2.5"><PlatformAdministrationInvitationStatusBadge status={user.invitationStatus} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <PlatformAdministrationManagementPanel
        activeWorkflow={activeWorkflow}
        selectedUser={selectedUser}
        inviteForm={inviteForm}
        setInviteForm={setInviteForm}
        validRoles={validRoles}
        roleDraft={roleDraft}
        setRoleDraft={setRoleDraft}
        landingWorkspaceDraft={landingWorkspaceDraft}
        setLandingWorkspaceDraft={setLandingWorkspaceDraft}
        saving={saving}
        sendingAccessCode={sendingAccessCode}
        onClose={closeWorkflow}
        onInviteSubmit={handleInviteSubmit}
        onDeactivate={() => void handleDeactivateSelectedUser()}
        deleteAssessment={deleteAssessment}
        deleteConfirmationEmail={deleteConfirmationEmail}
        setDeleteConfirmationEmail={setDeleteConfirmationEmail}
        deleteAssessmentLoading={deleteAssessmentLoading}
        onPermanentDelete={() => void handlePermanentDeleteSelectedUser()}
        onSendAccessCode={() => void handleSendAccessCode()}
        onRoleSave={() => void handleRoleSave()}
      />
    </section>
  );
}

function PlatformAdministrationInput({
  label,
  value,
  onChange,
  type = "text",
  required = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: "text" | "email";
  required?: boolean;
}) {
  return (
    <label className="grid gap-2 text-sm font-semibold text-[#0A2540]">
      {label}
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        type={type}
        required={required}
        className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
      />
    </label>
  );
}

function PlatformAdministrationManagementPanel({
  activeWorkflow,
  selectedUser,
  inviteForm,
  setInviteForm,
  validRoles,
  roleDraft,
  setRoleDraft,
  landingWorkspaceDraft,
  setLandingWorkspaceDraft,
  saving,
  sendingAccessCode,
  onClose,
  onInviteSubmit,
  onDeactivate,
  deleteAssessment,
  deleteConfirmationEmail,
  setDeleteConfirmationEmail,
  deleteAssessmentLoading,
  onPermanentDelete,
  onSendAccessCode,
  onRoleSave,
}: {
  activeWorkflow: PlatformAdministrationWorkflow;
  selectedUser: PlatformAdministrationUserRecord | null;
  inviteForm: PlatformAdministrationUserFormState;
  setInviteForm: Dispatch<SetStateAction<PlatformAdministrationUserFormState>>;
  validRoles: string[];
  roleDraft: string;
  setRoleDraft: Dispatch<SetStateAction<string>>;
  landingWorkspaceDraft: string;
  setLandingWorkspaceDraft: Dispatch<SetStateAction<string>>;
  saving: boolean;
  sendingAccessCode: boolean;
  onClose: () => void;
  onInviteSubmit: (event: FormEvent<HTMLFormElement>) => void | Promise<void>;
  onDeactivate: () => void;
  deleteAssessment: BusinessCenterPermanentUserDeletionResult | null;
  deleteConfirmationEmail: string;
  setDeleteConfirmationEmail: Dispatch<SetStateAction<string>>;
  deleteAssessmentLoading: boolean;
  onPermanentDelete: () => void;
  onSendAccessCode: () => void;
  onRoleSave: () => void;
}) {
  if (!activeWorkflow) return null;

  const title = activeWorkflow === "invite"
    ? "Invite User"
    : activeWorkflow === "deactivate"
      ? "Deactivate User"
      : activeWorkflow === "delete"
        ? "Permanently Delete User"
        : activeWorkflow === "access-code"
          ? "Send Access Code"
          : "Assign Role";

  return (
    <div className="fixed inset-0 z-[80] flex justify-end bg-[#0A2540]/20" role="presentation">
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="platform-administration-management-title"
        className="flex h-full w-full max-w-[440px] flex-col border-l border-[#BFEAFF] bg-white shadow-[0_24px_80px_rgba(15,23,42,.22)]"
      >
        <div className="border-b border-[#BFEAFF] p-5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">User Management</p>
          <div className="mt-2 flex items-start justify-between gap-4">
            <h3 id="platform-administration-management-title" className="text-2xl font-semibold tracking-tight text-[#0A2540]">{title}</h3>
            <button type="button" onClick={onClose} className="focus-ring rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2 text-sm font-semibold text-[#0A2540]">
              Close
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          {activeWorkflow === "invite" ? (
            <form onSubmit={(event) => void onInviteSubmit(event)} className="grid gap-4">
              <PlatformAdministrationInput label="Display Name" value={inviteForm.displayName} onChange={(value) => setInviteForm((current) => ({ ...current, displayName: value }))} required />
              <PlatformAdministrationInput label="Email" value={inviteForm.email} onChange={(value) => setInviteForm((current) => ({ ...current, email: value }))} type="email" required />
              <label className="grid gap-2 text-sm font-semibold text-[#0A2540]">
                Role
                <select value={inviteForm.role} onChange={(event) => setInviteForm((current) => ({ ...current, role: event.target.value }))} className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]" required>
                  {validRoles.map((role) => <option key={role} value={role}>{role}</option>)}
                </select>
              </label>
              <PlatformAdministrationInput label="Team" value={inviteForm.team} onChange={(value) => setInviteForm((current) => ({ ...current, team: value }))} required />
              <label className="grid gap-2 text-sm font-semibold text-[#0A2540]">
                Default Landing Workspace
                <select value={inviteForm.defaultLandingWorkspace} onChange={(event) => setInviteForm((current) => ({ ...current, defaultLandingWorkspace: event.target.value }))} className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]">
                  <option value="">Role-based fallback</option>
                  {defaultLandingWorkspaceOptions.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
                </select>
              </label>
              <div className="flex flex-wrap gap-2 pt-2">
                <button type="submit" disabled={saving} className="inline-flex min-h-11 items-center rounded-xl bg-[#14B8A6] px-4 text-sm font-semibold text-white transition hover:bg-[#0F9F94] focus-ring disabled:opacity-60">{saving ? "Inviting..." : "Submit Invitation"}</button>
                <button type="button" onClick={onClose} disabled={saving} className="inline-flex min-h-11 items-center rounded-xl border border-[#BFEAFF] bg-white px-4 text-sm font-semibold text-[#0A2540] transition hover:border-[#14B8A6] focus-ring disabled:opacity-60">Cancel</button>
              </div>
            </form>
          ) : null}

          {activeWorkflow === "role" && selectedUser ? (
            <div className="grid gap-4">
              <PlatformAdministrationUserSummary user={selectedUser} />
              <label className="grid gap-2 text-sm font-semibold text-[#0A2540]">
                Role
                <select value={roleDraft} onChange={(event) => setRoleDraft(event.target.value)} className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]">
                  {validRoles.map((role) => <option key={role} value={role}>{role}</option>)}
                </select>
              </label>
              <label className="grid gap-2 text-sm font-semibold text-[#0A2540]">
                Default Landing Workspace
                <select value={landingWorkspaceDraft} onChange={(event) => setLandingWorkspaceDraft(event.target.value)} className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]">
                  <option value="">Role-based fallback</option>
                  {defaultLandingWorkspaceOptions.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
                </select>
              </label>
              <div className="flex flex-wrap gap-2 pt-2">
                <button type="button" onClick={onRoleSave} disabled={saving} className="inline-flex min-h-11 items-center rounded-xl bg-[#14B8A6] px-4 text-sm font-semibold text-white transition hover:bg-[#0F9F94] focus-ring disabled:opacity-60">{saving ? "Saving..." : "Save Role"}</button>
                <button type="button" onClick={onClose} disabled={saving} className="inline-flex min-h-11 items-center rounded-xl border border-[#BFEAFF] bg-white px-4 text-sm font-semibold text-[#0A2540] transition hover:border-[#14B8A6] focus-ring disabled:opacity-60">Cancel</button>
              </div>
            </div>
          ) : null}

          {activeWorkflow === "deactivate" && selectedUser ? (
            <div className="grid gap-4">
              <PlatformAdministrationUserSummary user={selectedUser} />
              <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold leading-7 text-red-900">
                Confirm that you want to deactivate this user and revoke active access for the Administration workspace.
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={onDeactivate} disabled={saving} className="inline-flex min-h-11 items-center rounded-xl bg-[#14B8A6] px-4 text-sm font-semibold text-white transition hover:bg-[#0F9F94] focus-ring disabled:opacity-60">{saving ? "Deactivating..." : "Confirm"}</button>
                <button type="button" onClick={onClose} disabled={saving} className="inline-flex min-h-11 items-center rounded-xl border border-[#BFEAFF] bg-white px-4 text-sm font-semibold text-[#0A2540] transition hover:border-[#14B8A6] focus-ring disabled:opacity-60">Cancel</button>
              </div>
            </div>
          ) : null}

          {activeWorkflow === "delete" && selectedUser ? (
            <div className="grid gap-4">
              <PlatformAdministrationUserSummary user={selectedUser} />
              <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold leading-7 text-red-900">
                Permanent deletion is only available for unused duplicate, QA, or mistaken user records. Operational history blocks deletion; use Deactivate User instead.
              </div>
              <div className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">Dependency Assessment</p>
                {deleteAssessmentLoading ? (
                  <p className="mt-2 text-sm font-semibold text-[#475569]" role="status">Checking operational dependencies...</p>
                ) : deleteAssessment ? (
                  <div className="mt-3 grid gap-3">
                    <p className="text-sm font-semibold text-[#0A2540]">
                      {deleteAssessment.eligible
                        ? "This user is eligible for permanent deletion."
                        : "Permanent deletion is blocked for this user."}
                    </p>
                    <div className="grid gap-2 text-sm text-[#0A2540]">
                      <PlatformAdministrationFieldRow label="Operational dependency count" value={String(deleteAssessment.dependencies.reduce((total, dependency) => total + dependency.count, 0))} />
                      <PlatformAdministrationFieldRow label="Invitation records to remove" value={String(deleteAssessment.cleanup.invitationsRemoved)} />
                      <PlatformAdministrationFieldRow label="Sessions to revoke" value={String(deleteAssessment.cleanup.sessionsRevoked)} />
                      <PlatformAdministrationFieldRow label="Access codes to invalidate" value={String(deleteAssessment.cleanup.accessCodesInvalidated)} />
                    </div>
                    {deleteAssessment.dependencies.length ? (
                      <div className="grid gap-2" role="status" aria-live="polite">
                        {deleteAssessment.dependencies.map((dependency) => (
                          <div key={`${dependency.moduleKey}-${dependency.scopeKey}`} className="rounded-xl border border-red-200 bg-white p-3 text-sm font-semibold leading-6 text-red-900">
                            <span className="block text-[11px] uppercase tracking-[0.12em] text-red-700">{dependency.moduleKey}</span>
                            {dependency.reason}
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <p className="mt-2 text-sm font-semibold text-[#475569]">Select a user to assess permanent deletion eligibility.</p>
                )}
              </div>
              <label className="grid gap-2 text-sm font-semibold text-[#0A2540]">
                Type the user's email address to permanently delete this user
                <input
                  value={deleteConfirmationEmail}
                  onChange={(event) => setDeleteConfirmationEmail(event.target.value)}
                  type="email"
                  className="focus-ring min-h-11 rounded-xl border border-red-200 bg-white px-3 text-sm font-semibold text-[#0A2540]"
                  disabled={!deleteAssessment?.eligible || saving}
                  aria-describedby="platform-administration-delete-help"
                />
              </label>
              <p id="platform-administration-delete-help" className="text-xs font-semibold leading-6 text-[#64748B]">
                This does not replace Deactivate User. If dependencies exist, permanent deletion stays blocked and historical attribution remains preserved.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={onPermanentDelete}
                  disabled={!deleteAssessment?.eligible || saving || normalizePlatformAdministrationEmail(deleteConfirmationEmail) !== normalizePlatformAdministrationEmail(selectedUser.email)}
                  className="inline-flex min-h-11 items-center rounded-xl bg-red-600 px-4 text-sm font-semibold text-white transition hover:bg-red-700 focus-ring disabled:opacity-60"
                >
                  {saving ? "Deleting..." : "Permanently Delete User"}
                </button>
                <button type="button" onClick={onClose} disabled={saving} className="inline-flex min-h-11 items-center rounded-xl border border-[#BFEAFF] bg-white px-4 text-sm font-semibold text-[#0A2540] transition hover:border-[#14B8A6] focus-ring disabled:opacity-60">Cancel</button>
              </div>
            </div>
          ) : null}

          {activeWorkflow === "access-code" && selectedUser ? (
            <div className="grid gap-4">
              <PlatformAdministrationUserSummary user={selectedUser} />
              <div className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4 text-sm font-semibold leading-7 text-[#0A2540]">
                Send a one-time access code to this user's email using the existing secure login workflow.
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={onSendAccessCode} disabled={sendingAccessCode} className="inline-flex min-h-11 items-center rounded-xl bg-[#14B8A6] px-4 text-sm font-semibold text-white transition hover:bg-[#0F9F94] focus-ring disabled:opacity-60">{sendingAccessCode ? "Sending..." : "Send Access Code"}</button>
                <button type="button" onClick={onClose} disabled={sendingAccessCode} className="inline-flex min-h-11 items-center rounded-xl border border-[#BFEAFF] bg-white px-4 text-sm font-semibold text-[#0A2540] transition hover:border-[#14B8A6] focus-ring disabled:opacity-60">Cancel</button>
              </div>
            </div>
          ) : null}
        </div>
      </aside>
    </div>
  );
}

function PlatformAdministrationUserSummary({ user }: { user: PlatformAdministrationUserRecord }) {
  return (
    <div className="grid gap-3 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
      <PlatformAdministrationFieldRow label="Display Name" value={user.displayName} />
      <PlatformAdministrationFieldRow label="Email" value={user.email} />
      <PlatformAdministrationFieldRow label="Current Role" value={user.role} />
      <PlatformAdministrationFieldRow label="Status" value={user.status} />
      <PlatformAdministrationFieldRow label="Team" value={user.team} />
      <PlatformAdministrationFieldRow label="Default Landing Workspace" value={getDefaultLandingWorkspaceLabel(user.defaultLandingWorkspace)} />
    </div>
  );
}

function normalizePlatformAdministrationUser(user: PlatformAdministrationUserRecord): PlatformAdministrationUserRecord {
  const email = normalizePlatformAdministrationEmail(user.email);
  const lastLoginAt = user.lastLoginAt || "";
  const invitationStatus =
    user.invitationStatus === "Pending" || user.invitationStatus === "Sent" || user.invitationStatus === "Revoked" || user.invitationStatus === "Expired"
      ? user.invitationStatus
      : "Accepted";
  return {
    id: user.id || `ar-admin-user-${email || crypto.randomUUID()}`,
    displayName: user.displayName || "Profile Incomplete",
    email: email || "user@example.com",
    role: user.role || "Collector",
    status: user.status === "Deactivated" || user.status === "Archived" ? user.status : "Active",
    team: user.team || "Recovery Operations",
    lastLogin: lastLoginAt ? formatPlatformAdministrationLastLoginAt(lastLoginAt) : user.lastLogin || "Profile Incomplete",
    lastLoginAt,
    invitationStatus,
    defaultLandingWorkspace: normalizeDefaultLandingWorkspace(user.defaultLandingWorkspace),
  };
}

function mergePlatformAdministrationUsersWithCanonicalUsers(
  workspace: ReturnType<typeof loadARRecoveryWorkspace>,
  directoryUsers: PlatformAdministrationUserRecord[],
  canonicalUsers: BusinessCenterUserRecord[],
) {
  const tenantCompanyIds = buildPlatformAdministrationTenantCompanyIds(workspace);
  const mergedUsers = [...directoryUsers.map(normalizePlatformAdministrationUser)];
  const directoryIds = new Set(mergedUsers.map((user) => user.id).filter(Boolean));
  const directoryEmails = new Set(mergedUsers.map((user) => normalizePlatformAdministrationEmail(user.email)).filter(Boolean));

  canonicalUsers
    .filter((user) => isCanonicalUserVisibleInPlatformAdministration(user, tenantCompanyIds))
    .map(mapCanonicalUserToPlatformAdministrationUser)
    .forEach((canonicalUser) => {
      const existingIndex = mergedUsers.findIndex((user) =>
        user.id === canonicalUser.id
        || normalizePlatformAdministrationEmail(user.email) === normalizePlatformAdministrationEmail(canonicalUser.email),
      );
      if (existingIndex >= 0) {
        const existingUser = mergedUsers[existingIndex];
        mergedUsers[existingIndex] = normalizePlatformAdministrationUser({
          ...existingUser,
          id: existingUser.id || canonicalUser.id,
          displayName: existingUser.displayName || canonicalUser.displayName,
          email: existingUser.email || canonicalUser.email,
          role: canonicalUser.role,
          status: canonicalUser.status,
          team: canonicalUser.team || existingUser.team,
          invitationStatus: canonicalUser.invitationStatus,
          lastLogin: canonicalUser.lastLogin,
          lastLoginAt: canonicalUser.lastLoginAt,
          defaultLandingWorkspace: canonicalUser.defaultLandingWorkspace || existingUser.defaultLandingWorkspace,
        });
        return;
      }
      if (directoryIds.has(canonicalUser.id) || directoryEmails.has(normalizePlatformAdministrationEmail(canonicalUser.email))) return;
      mergedUsers.unshift(canonicalUser);
      directoryIds.add(canonicalUser.id);
      directoryEmails.add(normalizePlatformAdministrationEmail(canonicalUser.email));
    });

  return mergedUsers.map(normalizePlatformAdministrationUser);
}

function buildPlatformAdministrationTenantCompanyIds(workspace: ReturnType<typeof loadARRecoveryWorkspace>) {
  const ids = new Set<string>();
  const add = (value: string | null | undefined) => {
    const normalized = String(value || "").trim();
    if (normalized) ids.add(normalized);
  };
  add(workspace.company.id);
  add(workspace.company.companySlug);
  add(workspace.company.brand);
  const slugBase = workspace.company.companySlug.replace(/-recovery$/, "");
  add(slugBase);
  add(`company-${slugBase}`);
  add(workspace.company.id.replace(/_/g, "-"));
  add(`company-${workspace.company.id.replace(/_/g, "-")}`);
  return ids;
}

function isCanonicalUserVisibleInPlatformAdministration(user: BusinessCenterUserRecord, tenantCompanyIds: Set<string>) {
  const userCompanyIds = [
    user.companyId,
    user.parentCompanyId || "",
    ...(Array.isArray(user.assignedClientIds) ? user.assignedClientIds : []),
  ].filter(Boolean);
  return Boolean(
    user.id
    && normalizePlatformAdministrationEmail(user.email)
    && PLATFORM_ADMINISTRATION_CANONICAL_USER_ROLES.has(user.role)
    && userCompanyIds.some((companyId) => tenantCompanyIds.has(companyId)),
  );
}

function mapCanonicalUserToPlatformAdministrationUser(user: BusinessCenterUserRecord): PlatformAdministrationUserRecord {
  const invitationStatus = mapCanonicalInvitationStatus(user.invitationStatus || user.portalAccessStatus || user.status);
  const lastLoginAt = user.lastLoginAt || "";
  return normalizePlatformAdministrationUser({
    id: user.id,
    displayName: user.displayName || user.fullName || "Profile Incomplete",
    email: user.email,
    role: mapCompanyUserRoleToPlatformAdministrationRole(user.role),
    status: mapCanonicalUserStatusToPlatformAdministrationStatus(user.status),
    team: user.team || user.department || "Recovery Operations",
    lastLogin: lastLoginAt ? formatPlatformAdministrationLastLoginAt(lastLoginAt) : invitationStatus === "Pending" ? "Invite Pending" : "Not recorded",
    lastLoginAt,
    invitationStatus,
    defaultLandingWorkspace: normalizeDefaultLandingWorkspace(user.defaultLandingWorkspace),
  });
}

function mapCompanyUserRoleToPlatformAdministrationRole(role: BusinessCenterUserRecord["role"] | string) {
  switch (role) {
    case "company_admin":
      return "Tenant Admin";
    case "company_manager":
      return "AR Manager";
    case "company_supervisor":
      return "Team Lead";
    case "company_team_member":
      return "Collector";
    case "customer_client_user":
      return "Client User";
    case "viewer":
      return "Read Only";
    default:
      return "Read Only";
  }
}

function mapCanonicalUserStatusToPlatformAdministrationStatus(status: BusinessCenterUserRecord["status"] | string) {
  switch (status) {
    case "deactivated":
      return "Deactivated";
    case "archived":
      return "Archived";
    default:
      return "Active";
  }
}

function mapCanonicalInvitationStatus(status: string) {
  switch (status.toLowerCase()) {
    case "accepted":
    case "active":
      return "Accepted";
    case "revoked":
    case "suspended":
      return "Revoked";
    case "expired":
      return "Expired";
    case "sent":
      return "Sent";
    default:
      return "Pending";
  }
}

function hasPlatformAdministrationUserDirectoryMismatch(
  persistedUsers: PlatformAdministrationUserRecord[],
  hydratedUsers: PlatformAdministrationUserRecord[],
) {
  if (persistedUsers.length !== hydratedUsers.length) return true;
  const persistedById = new Map(persistedUsers.map((user) => [user.id, normalizePlatformAdministrationUser(user)]));
  return hydratedUsers.some((user) => {
    const persistedUser = persistedById.get(user.id);
    if (!persistedUser) return true;
    return persistedUser.email !== user.email
      || persistedUser.role !== user.role
      || persistedUser.status !== user.status
      || persistedUser.team !== user.team
      || persistedUser.invitationStatus !== user.invitationStatus
      || persistedUser.defaultLandingWorkspace !== user.defaultLandingWorkspace
      || persistedUser.lastLoginAt !== user.lastLoginAt;
  });
}

function formatPlatformAdministrationLastLogin(user: PlatformAdministrationUserRecord) {
  if (user.lastLoginAt) return formatPlatformAdministrationLastLoginAt(user.lastLoginAt);
  if (user.invitationStatus === "Accepted" && user.lastLogin === "Invite Pending") return "Not recorded";
  return user.lastLogin || "Profile Incomplete";
}

function formatPlatformAdministrationLastLoginAt(isoValue: string) {
  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) return "Not recorded";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function normalizePlatformAdministrationEmail(value: string) {
  return value.trim().toLowerCase();
}

function mapPlatformAdministrationRoleToCompanyUserRole(role: string): BusinessCenterUserRecord["role"] {
  switch (role) {
    case "Tenant Admin":
      return "company_admin";
    case "AR Manager":
      return "company_manager";
    case "Collector":
      return "company_team_member";
    case "Client User":
      return "customer_client_user";
    default:
      return "viewer";
  }
}

async function syncPlatformAdministrationUserForAccess(
  workspace: ReturnType<typeof loadARRecoveryWorkspace>,
  user: PlatformAdministrationUserRecord,
) {
  if (user.role === "Client User" || user.status !== "Active") return;
  const now = new Date().toISOString();
  const companyId = workspace.company.id || "company-future-ar-recovery";
  await upsertBusinessCenterCompanyUser({
    id: user.id,
    companyId,
    parentCompanyId: null,
    firstName: user.displayName.split(" ")[0] || user.displayName,
    lastName: user.displayName.split(" ").slice(1).join(" "),
    displayName: user.displayName,
    fullName: user.displayName,
    email: user.email,
    jobTitle: user.role,
    department: "Recovery Operations",
    phone: "",
    team: user.team,
    notificationPreference: "email",
    role: mapPlatformAdministrationRoleToCompanyUserRole(user.role),
    status: "active",
    portalAccessStatus: "active",
    defaultLandingWorkspace: normalizeDefaultLandingWorkspace(user.defaultLandingWorkspace),
    assignedClientIds: [companyId],
    assignedProjectIds: [],
    assignedTaskIds: [],
    assignedDocumentIds: [],
    createdAt: now,
    updatedAt: now,
  });
}

function renderPlatformAdministrationTab(
  selectedTab: PlatformAdministrationTabKey,
  workspace: ReturnType<typeof loadARRecoveryWorkspace>,
  userRows: ReturnType<typeof buildPlatformAdministrationUsers>,
  teamRows: ReturnType<typeof buildPlatformAdministrationTeams>,
  selectedPermissionRole: (typeof PLATFORM_ADMINISTRATION_ROLE_SELECTOR_OPTIONS)[number],
  setSelectedPermissionRole: Dispatch<SetStateAction<(typeof PLATFORM_ADMINISTRATION_ROLE_SELECTOR_OPTIONS)[number]>>,
  onSaveTeams: (teams: PlatformAdministrationTeamRecord[]) => Promise<PlatformAdministrationTeamRecord[]>,
) {
  if (selectedTab === "company") {
    return (
      <section className="grid gap-5 xl:grid-cols-[1fr_.86fr]">
        <article className="rounded-[1.75rem] border border-[#BFEAFF] bg-white p-5">
          <SectionHeading eyebrow="Company" title={workspace.company.companyName} description="Company administration remains the tenant anchor for Universal Circuit Hub platform services." />
          <div className="mt-5 grid gap-4">
            <PlatformAdministrationFieldGroup title="Organization">
              <PlatformAdministrationFieldRow label="Organization Name" value={workspace.company.companyName} />
              <PlatformAdministrationFieldRow label="Platform" value="Universal Circuit Hub" />
              <PlatformAdministrationFieldRow label="Licensed Product(s)" value={<PlatformAdministrationProductList products={[...PLATFORM_ADMINISTRATION_LICENSED_PRODUCTS]} />} />
              <PlatformAdministrationFieldRow label="Subscription / Plan" value={<PlatformAdministrationSubscriptionSummary plan="Enterprise" status="Active" />} />
              <PlatformAdministrationFieldRow label="Tenant Status" value={<PlatformAdministrationTenantStatusBadge status={workspace.company.status} />} />
            </PlatformAdministrationFieldGroup>
            <PlatformAdministrationFieldGroup title="Primary Contact">
              <PlatformAdministrationFieldRow label="Primary Contact" value={workspace.clientProfile.collectionDepartmentContact || "Not configured"} />
              <PlatformAdministrationFieldRow label="Support Email" value={workspace.clientProfile.email || "Not configured"} />
              <PlatformAdministrationFieldRow label="Primary Phone" value={workspace.clientProfile.phone || "Not configured"} />
            </PlatformAdministrationFieldGroup>
            <PlatformAdministrationFieldGroup title="Administration">
              <PlatformAdministrationFieldRow label="Created Date" value={formatCapturedDate(workspace.company.createdAt)} />
              <PlatformAdministrationFieldRow label="Last Updated" value={formatCapturedDate(workspace.company.updatedAt)} />
            </PlatformAdministrationFieldGroup>
          </div>
        </article>
        <PlatformAdministrationServiceCard
          eyebrow="Platform Responsibilities"
          title="Platform Responsibilities"
          description="Universal Circuit Hub manages the shared platform services used by this organization."
          groups={PLATFORM_ADMINISTRATION_RESPONSIBILITY_GROUPS}
          footer="Industry Operating Systems consume these platform services without owning them."
        />
      </section>
    );
  }

  if (selectedTab === "users") {
    return (
      <section className="rounded-[1.75rem] border border-[#BFEAFF] bg-white p-5">
        <SectionHeading eyebrow="Users" title="User Directory" description="Manage platform user access, role assignments, team membership, and invitation status for this tenant." />
        <div className="mt-5 grid gap-3 xl:grid-cols-4">
          {["Invite User", "Deactivate User", "Send Access Code", "Assign Role"].map((action) => (
            <button key={action} type="button" className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-4 text-sm font-semibold text-[#0A2540] transition hover:border-[#14B8A6] focus-ring">
              <ARLightLineIcon icon={<Bolt className="size-4" aria-hidden="true" />} compact />
              {action}
            </button>
          ))}
        </div>
        <div className="mt-5 max-w-full overflow-x-auto" data-future-user-filters={PLATFORM_ADMINISTRATION_FUTURE_USER_FILTERS.join(",")}>
          <table className="min-w-full table-fixed text-left text-sm">
            <thead className="text-xs uppercase tracking-[0.12em] text-[#14B8A6]">
              <tr className="border-b border-[#BFEAFF]">
                {["Display Name", "Email", "Role", "Status", "Team", "Default Landing", "Last Login", "Invitation Status"].map((heading) => (
                  <th key={heading} className="min-w-0 px-3 py-3 font-semibold">{heading}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {userRows.map((user) => (
                <tr key={`${user.email}-${user.role}`} className="border-b border-[#E8FCFF]">
                  <td className="min-w-0 px-3 py-2.5 font-semibold text-[#0A2540]"><span className="block max-w-full min-w-0 break-words leading-snug" style={platformAdministrationTextWrapStyle}>{user.displayName || "Profile Incomplete"}</span></td>
                  <td className="min-w-0 px-3 py-2.5 text-[#475569]"><span className="block max-w-full min-w-0 break-words leading-snug" style={platformAdministrationTextWrapStyle}>{user.email}</span></td>
                  <td className="min-w-0 px-3 py-2.5 text-[#0A2540]"><span className="block max-w-full min-w-0 break-words leading-snug" style={platformAdministrationTextWrapStyle}>{user.role}</span></td>
                  <td className="min-w-0 px-3 py-2.5"><PlatformAdministrationStatusPill status={user.status} /></td>
                  <td className="min-w-0 px-3 py-2.5 text-[#475569]"><span className="block max-w-full min-w-0 break-words leading-snug" style={platformAdministrationTextWrapStyle}>{user.team || "Profile Incomplete"}</span></td>
                  <td className="min-w-0 px-3 py-2.5 text-[#475569]"><span className="block max-w-full min-w-0 break-words leading-snug" style={platformAdministrationTextWrapStyle}>{getDefaultLandingWorkspaceLabel(user.defaultLandingWorkspace)}</span></td>
                  <td className="min-w-0 px-3 py-2.5 text-[#475569]"><span className="block max-w-full min-w-0 break-words leading-snug" style={platformAdministrationTextWrapStyle}>{formatPlatformAdministrationLastLogin(user)}</span></td>
                  <td className="min-w-0 px-3 py-2.5"><PlatformAdministrationInvitationStatusBadge status={user.invitationStatus} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    );
  }

  if (selectedTab === "roles") {
    return <PlatformAdministrationRolesTab userRows={userRows} />;
  }

  if (selectedTab === "permissions") {
    return (
      <PlatformAdministrationPermissionsTab
        selectedPermissionRole={selectedPermissionRole}
        setSelectedPermissionRole={setSelectedPermissionRole}
      />
    );
  }

  if (selectedTab === "teams") {
    return <PlatformAdministrationTeamsTab teamRows={teamRows} userRows={userRows} portfolioOptions={buildPlatformAdministrationPortfolioOptions(workspace)} onSaveTeams={onSaveTeams} />;
  }

  if (selectedTab === "branding") {
    const productTagline = "Accounts, Documents, Promises & Recovery";
    const brandingSummaryRows = [
      { label: "Brand", value: workspace.branding.displayName },
      { label: "Theme", value: "Enterprise" },
      { label: "Primary Color", value: <PlatformAdministrationColorValue color={workspace.branding.primaryColor} /> },
      { label: "Accent", value: <PlatformAdministrationColorValue color={workspace.branding.accentColor} /> },
      { label: "Surface", value: <PlatformAdministrationColorValue color={workspace.branding.surfaceColor} /> },
    ];
    const brandingIdentityRows = [
      { label: "Brand Name", value: workspace.branding.displayName },
      { label: "Product Name", value: workspace.branding.displayName },
      { label: "Logo", value: "CR tenant badge" },
      { label: "Product Tagline", value: productTagline },
    ];
    const brandingColorRows = [
      { label: "Primary", value: <PlatformAdministrationColorValue color={workspace.branding.primaryColor} /> },
      { label: "Accent", value: <PlatformAdministrationColorValue color={workspace.branding.accentColor} /> },
      { label: "Surface", value: <PlatformAdministrationColorValue color={workspace.branding.surfaceColor} /> },
    ];
    const brandingThemeRows = [
      { label: "Light Theme", value: "Administration review surfaces" },
      { label: "Enterprise Theme", value: "Active" },
    ];
    const brandingDesignSystemRows = [
      { label: "Border Radius", value: "Rounded enterprise cards" },
      { label: "Default Card Style", value: "White surface, light aqua border" },
      { label: "Default Button Style", value: "Rounded action controls" },
      { label: "Icon Style", value: "Enterprise micro-icons" },
    ];

    return (
      <section className="rounded-[1.75rem] border border-[#BFEAFF] bg-white p-5">
        <SectionHeading eyebrow="Branding" title="Branding" description="Review the visible product identity used across this tenant experience." />
        <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,.9fr)_minmax(0,1.1fr)]">
          <article className="min-w-0 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">Brand Preview</p>
            <div className="mt-4 overflow-hidden rounded-2xl border border-[#BFEAFF] bg-[#F8FCFF] shadow-[0_18px_45px_rgba(10,37,64,0.08)]" aria-label={`${workspace.branding.displayName} tenant experience preview`}>
              <div className="flex min-w-0 items-center gap-3 px-4 py-3" style={{ backgroundColor: workspace.branding.surfaceColor }}>
                <div className="flex min-w-0 items-center gap-3">
                  <span className="grid size-10 shrink-0 place-items-center rounded-2xl text-sm font-black text-white shadow-[0_10px_25px_rgba(255,106,43,0.24)]" style={{ backgroundColor: workspace.branding.primaryColor }}>
                    CR
                  </span>
                  <div className="min-w-0">
                    <p className="max-w-full min-w-0 break-words text-sm font-semibold leading-tight text-[#F8FAFC]" style={platformAdministrationTextWrapStyle}>
                      {workspace.branding.displayName}
                    </p>
                  </div>
                </div>
              </div>
              <div className="grid gap-4 p-4">
                <div className="rounded-2xl border border-[#D7F2FF] bg-white p-4 shadow-[0_10px_28px_rgba(10,37,64,0.05)]">
                  <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">Product Identity</p>
                      <p className="mt-2 max-w-full min-w-0 break-words text-lg font-bold leading-tight text-[#0A2540]" style={platformAdministrationTextWrapStyle}>
                        {workspace.branding.displayName}
                      </p>
                      <p className="mt-1 max-w-full min-w-0 break-words text-sm font-semibold leading-6 text-[#475569]" style={platformAdministrationTextWrapStyle}>
                        {productTagline}
                      </p>
                    </div>
                    <span className="w-fit shrink-0 rounded-full border border-[#14B8A6]/30 bg-[#E8FCFF] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#0A766F]">
                      Active
                    </span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex min-h-10 items-center rounded-full px-4 text-xs font-semibold text-white shadow-[0_10px_25px_rgba(255,106,43,0.18)]" style={{ backgroundColor: workspace.branding.primaryColor }}>
                    Primary Button
                  </span>
                  <span className="inline-flex min-h-10 items-center rounded-full border bg-white px-4 text-xs font-semibold text-[#0A2540]" style={{ borderColor: workspace.branding.accentColor }}>
                    Secondary Button
                  </span>
                  <span className="inline-flex min-h-10 items-center rounded-full border px-4 text-xs font-semibold" style={{ borderColor: workspace.branding.accentColor, color: workspace.branding.accentColor, backgroundColor: `${workspace.branding.accentColor}14` }}>
                    Accent Button
                  </span>
                </div>
                <div className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
                  <div className="flex min-w-0 items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">Example Card</p>
                      <p className="mt-1 max-w-full min-w-0 break-words text-sm font-semibold text-[#0A2540]" style={platformAdministrationTextWrapStyle}>
                        Tenant brand preview
                      </p>
                      <p className="mt-1 max-w-full min-w-0 break-words text-xs font-semibold leading-5 text-[#64748B]" style={platformAdministrationTextWrapStyle}>
                        A readable sample of card, text, and status styling.
                      </p>
                    </div>
                    <span className="shrink-0 rounded-full border border-[#14B8A6]/30 bg-[#E8FCFF] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#0A766F]">
                      Status Chip
                    </span>
                  </div>
                </div>
                <div className="grid gap-2 sm:grid-cols-3">
                  {brandingColorRows.map((row) => (
                    <div key={row.label} className="rounded-xl border border-[#BFEAFF] bg-white px-3 py-2">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#14B8A6]">{row.label}</p>
                      <div className="mt-1 text-xs font-semibold text-[#0A2540]">{row.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </article>
          <article className="min-w-0 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">Brand Summary</p>
            <div className="mt-3 grid gap-3">
              {brandingSummaryRows.map((row) => (
                <PlatformAdministrationFieldRow key={row.label} label={row.label} value={row.value} />
              ))}
            </div>
            <div className="mt-4 grid gap-4">
              <PlatformAdministrationFieldGroup title="Identity">
                {brandingIdentityRows.map((row) => (
                  <PlatformAdministrationFieldRow key={row.label} label={row.label} value={row.value} />
                ))}
              </PlatformAdministrationFieldGroup>
              <PlatformAdministrationFieldGroup title="Colors">
                {brandingColorRows.map((row) => (
                  <PlatformAdministrationFieldRow key={row.label} label={row.label} value={row.value} />
                ))}
              </PlatformAdministrationFieldGroup>
              <PlatformAdministrationFieldGroup title="Theme">
                {brandingThemeRows.map((row) => (
                  <PlatformAdministrationFieldRow key={row.label} label={row.label} value={row.value} />
                ))}
              </PlatformAdministrationFieldGroup>
              <PlatformAdministrationFieldGroup title="Design System">
                {brandingDesignSystemRows.map((row) => (
                  <PlatformAdministrationFieldRow key={row.label} label={row.label} value={row.value} />
                ))}
              </PlatformAdministrationFieldGroup>
              <div className="rounded-2xl border border-[#BFEAFF] bg-white p-4 text-sm font-semibold leading-7 text-[#0A2540]">
                Branding is read-only for Version 1. Visual identity, theme, and design-token changes remain governed by a future platform branding workflow.
              </div>
            </div>
          </article>
        </div>
      </section>
    );
  }

  if (selectedTab === "domains") {
    const tenantDomainSlug = workspace.company.companyName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "tenant";
    const portalDomain = workspace.clientProfile.portalUrl?.replace(/^https?:\/\//, "").replace(/\/$/, "") || `portal.${tenantDomainSlug}.com`;
    const primaryDomain = portalDomain.replace(/^portal\./, "");
    const domainRows = [
      { label: "Primary Domain", value: primaryDomain, status: "Active" },
      { label: "Portal Domain", value: portalDomain, status: "Verified" },
      { label: "Tenant Routing", value: "Portal route mapped to tenant workspace", status: "Ready" },
      { label: "SSL Status", value: "Certificate coverage prepared", status: "Ready" },
      { label: "DNS Status", value: "Domain records ready for verification", status: "Ready" },
      { label: "Redirects", value: "Primary portal redirect policy prepared", status: "Configured" },
    ];

    return (
      <section className="grid gap-5">
        <article className="min-w-0 rounded-[1.75rem] border border-[#BFEAFF] bg-white p-5" data-future-domain-capabilities={PLATFORM_ADMINISTRATION_FUTURE_DOMAIN_CAPABILITIES.join(",")}>
          <SectionHeading eyebrow="Domains" title="Domain Management" description="Review tenant portal domains, routing readiness, verification status, and future domain-governance support." />
          <div className="mt-5 grid gap-4 lg:grid-cols-2">
            {domainRows.map((row) => (
              <div key={row.label} className="min-w-0 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">{row.label}</p>
                    <p className="mt-2 max-w-full min-w-0 break-words text-sm font-semibold leading-6 text-[#0A2540]" style={platformAdministrationTextWrapStyle}>{row.value}</p>
                  </div>
                  <PlatformAdministrationDomainStatusBadge status={row.status} />
                </div>
              </div>
            ))}
          </div>
        </article>

        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,.9fr)]">
          <PlatformAdministrationServiceCard
            eyebrow="Domain Architecture"
            title="Enterprise Domain Model"
            description="The Domains workspace is structured for tenant routing, custom domains, white-label domains, verification states, SSL status, DNS status, redirects, and subdomain governance."
            items={["Primary and portal domains", "Custom and white-label domains", "Verification, SSL, DNS, and redirect readiness"]}
          />
          <PlatformAdministrationServiceCard
            eyebrow="Future Readiness"
            title="Verification Expansion"
            description="The current read-only structure can support certificate management, DNS verification, routing rules, ownership checks, verification tokens, history, and audit trail without changing the workspace architecture."
            items={["SSL certificate lifecycle", "DNS and ownership verification", "Domain history and audit trail"]}
          />
        </div>
      </section>
    );
  }

  if (selectedTab === "integrations") {
    const integrationRows = [
      {
        name: "Document Generation",
        category: "Documents",
        description: "Document workflow generation for letters, statements, and future client notices.",
        status: "Connected",
        connectionState: "Operational",
        lastSync: "Today",
        lastUpdated: "Today",
        connectedBy: "Tenant Admin",
        version: "v1",
        authenticationType: "Platform service",
      },
      {
        name: "Workflow Events",
        category: "Event Subscriptions",
        description: "Shared workflow event projections for activity, timeline, reporting, and notifications.",
        status: "Connected",
        connectionState: "Operational",
        lastSync: "Today",
        lastUpdated: "Today",
        connectedBy: "Platform Service",
        version: "v1",
        authenticationType: "Internal event bus",
      },
      {
        name: "Recovery Activity Projections",
        category: "Reporting Integrations",
        description: "Projection bridge for recovery activity, analytics, manager review, and audit surfaces.",
        status: "Available",
        connectionState: "Ready for setup",
        lastSync: "Not connected",
        lastUpdated: "Profile Incomplete",
        connectedBy: "Profile Incomplete",
        version: "v1",
        authenticationType: "Platform authorization",
      },
    ];

    return (
      <section className="grid gap-5">
        <article className="min-w-0 rounded-[1.75rem] border border-[#BFEAFF] bg-white p-5" data-future-integration-categories={PLATFORM_ADMINISTRATION_FUTURE_INTEGRATION_CATEGORIES.join(",")} data-future-integration-statuses={PLATFORM_ADMINISTRATION_FUTURE_INTEGRATION_STATUSES.join(",")} data-future-integration-actions={PLATFORM_ADMINISTRATION_FUTURE_INTEGRATION_ACTIONS.join(",")}>
          <SectionHeading eyebrow="Integrations" title="Integration Registry" description="Review enterprise integration readiness for providers, platform services, authentication models, connection state, and future operational actions." />
          <div className="mt-5 grid gap-4">
            {integrationRows.map((integration) => (
              <article key={integration.name} className="min-w-0 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
                <div className="flex min-w-0 flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">{integration.category}</p>
                    <h3 className="mt-1 max-w-full min-w-0 break-words text-lg font-semibold text-[#0A2540]" style={platformAdministrationTextWrapStyle}>{integration.name}</h3>
                    <p className="mt-2 max-w-full min-w-0 break-words text-sm leading-7 text-[#475569]" style={platformAdministrationTextWrapStyle}>{integration.description}</p>
                  </div>
                  <PlatformAdministrationIntegrationStatusBadge status={integration.status} />
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <PlatformAdministrationFieldRow label="Connection State" value={integration.connectionState} />
                  <PlatformAdministrationFieldRow label="Last Sync" value={integration.lastSync} />
                  <PlatformAdministrationFieldRow label="Last Updated" value={integration.lastUpdated} />
                  <PlatformAdministrationFieldRow label="Connected By" value={integration.connectedBy} />
                  <PlatformAdministrationFieldRow label="Version" value={integration.version} />
                  <PlatformAdministrationFieldRow label="Authentication Type" value={integration.authenticationType} />
                </div>
              </article>
            ))}
          </div>
        </article>

        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,.9fr)]">
          <PlatformAdministrationServiceCard
            eyebrow="Integration Architecture"
            title="Enterprise Integration Model"
            description="The Integrations workspace is structured for provider categories, authentication types, connection state, sync health, versioning, connected-by ownership, and future operational history."
            items={["Providers, webhooks, OAuth, API keys, and event subscriptions", "Email, SMS, phone, document, payment, storage, reporting, and AI services", "Microsoft 365, Google Workspace, Azure AD / Entra ID, Stripe, ACH, and banking APIs"]}
          />
          <PlatformAdministrationServiceCard
            eyebrow="Future Actions"
            title="Connection Operations"
            description="The current registry can support connect, disconnect, configure, reconnect, test connection, sync now, credential refresh, logs, and history without changing the workspace architecture."
            items={["Connection lifecycle actions", "Credential and authentication operations", "Logs, sync history, and audit support"]}
          />
        </div>
      </section>
    );
  }

  if (selectedTab === "notifications") {
    const notificationSummary = [
      { label: "Categories", value: PLATFORM_ADMINISTRATION_NOTIFICATION_CATEGORIES.length.toString() },
      { label: "Workflow Sources", value: "9" },
      { label: "Audience Groups", value: "6" },
      { label: "Read-Only Model", value: "Certified" },
    ];

    return (
      <section className="grid gap-5">
        <article className="min-w-0 rounded-[1.75rem] border border-[#BFEAFF] bg-white p-5" data-notification-statuses={PLATFORM_ADMINISTRATION_NOTIFICATION_STATUSES.join(",")}>
          <SectionHeading eyebrow="Notifications" title="Notification Center" description="Review platform notification categories, audiences, governance boundaries, and future delivery readiness without configuring notification delivery." />
          <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {notificationSummary.map((item) => (
              <div key={item.label} className="min-w-0 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] px-4 py-3">
                <p className="max-w-full min-w-0 break-words text-[10px] font-semibold uppercase tracking-[0.12em] text-[#14B8A6]" style={platformAdministrationTextWrapStyle}>{item.label}</p>
                <p className="mt-1 max-w-full min-w-0 break-words text-lg font-semibold text-[#0A2540]" style={platformAdministrationTextWrapStyle}>{item.value}</p>
              </div>
            ))}
          </div>
          <div className="mt-5 grid gap-4 xl:grid-cols-2">
            {PLATFORM_ADMINISTRATION_NOTIFICATION_CATEGORIES.map((notificationCategory) => (
              <article key={notificationCategory.category} className="min-w-0 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
                <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">Notification Category</p>
                    <h3 className="mt-1 max-w-full min-w-0 break-words text-lg font-semibold text-[#0A2540]" style={platformAdministrationTextWrapStyle}>{notificationCategory.category}</h3>
                    {notificationCategory.capability ? (
                      <p className="mt-2 max-w-full min-w-0 break-words text-xs font-semibold uppercase tracking-[0.12em] text-[#D94A16]" style={platformAdministrationTextWrapStyle}>{notificationCategory.capability}</p>
                    ) : null}
                    <p className="mt-2 max-w-full min-w-0 break-words text-sm leading-7 text-[#475569]" style={platformAdministrationTextWrapStyle}>{notificationCategory.purpose}</p>
                  </div>
                  <PlatformAdministrationNotificationStatusBadge status={notificationCategory.status} />
                </div>
                <div className="mt-4 grid gap-3 lg:grid-cols-[minmax(0,.75fr)_minmax(0,1.25fr)]">
                  <div className="min-w-0 rounded-xl border border-[#BFEAFF] bg-white p-3">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#14B8A6]">Audience</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {notificationCategory.audience.map((audience) => (
                        <span key={audience} className="max-w-full rounded-full border border-[#BFEAFF] bg-[#F8FCFF] px-3 py-1 text-xs font-semibold text-[#0A2540]">
                          <span className="block min-w-0 break-words" style={platformAdministrationTextWrapStyle}>{audience}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="min-w-0 rounded-xl border border-[#BFEAFF] bg-white p-3">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#14B8A6]">Examples</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {notificationCategory.examples.map((example) => (
                        <span key={example} className="max-w-full rounded-full border border-[#BFEAFF] bg-[#F8FCFF] px-3 py-1 text-xs font-semibold text-[#334155]">
                          <span className="block min-w-0 break-words" style={platformAdministrationTextWrapStyle}>{example}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </article>

        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,.9fr)]">
          <PlatformAdministrationServiceCard
            eyebrow="Governance Model"
            title="Notification Architecture"
            description="Universal Circuit Hub separates notifications into four categories so Industry Operating Systems inherit consistent notification behavior without redesigning notification architecture."
            items={["Platform Alerts", "Workflow Notifications", "Tenant Service Messages", "Client Notifications"]}
          />
          <PlatformAdministrationServiceCard
            eyebrow="Read-Only Boundary"
            title="Governance Review Only"
            description="The Notification Center is aligned with Branding, Domains, and Integrations as an Administration review surface. It does not send notifications or change delivery behavior."
            items={["No Save, Cancel, Edit, Enable, Disable, Configure, or Delete controls", "Status indicators describe readiness only", "Future Client Portal notifications are clearly labeled as Version 2+"]}
          />
        </div>
      </section>
    );
  }

  const tab = PLATFORM_ADMINISTRATION_TABS.find((item) => item.key === selectedTab);
  const serviceTitle = tab?.label || "Platform Service";
  const serviceDescription = tab?.description || "This platform service is part of Universal Circuit Hub Administration.";
  const serviceExamples: Partial<Record<PlatformAdministrationTabKey, string[]>> = {
    licensing: ["CollabRecover AR Solutions", "Business Platform", "Client Portal"],
  };

  return (
    <PlatformAdministrationServiceCard
      eyebrow={serviceTitle}
      title={serviceTitle}
      description={`${serviceDescription} Configuration controls will become available as the service is enabled for this tenant.`}
      items={serviceExamples[selectedTab]}
    />
  );
}

function PlatformAdministrationRolesTab({ userRows }: { userRows: PlatformAdministrationUserRecord[] }) {
  const [selectedRoleName, setSelectedRoleName] = useState("");
  const [panelMode, setPanelMode] = useState<PlatformAdministrationRolePanelMode>(null);
  const selectedRole = PLATFORM_ADMINISTRATION_ROLES.find((role) => role.role === selectedRoleName) || null;
  const assignedRoleUsers = selectedRole ? userRows.filter((user) => user.role === selectedRole.role) : [];

  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setPanelMode(null);
      }
    }

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, []);

  function selectRole(role: PlatformAdministrationRoleRecord) {
    setSelectedRoleName(role.role);
    setPanelMode("summary");
  }

  return (
    <section className="relative rounded-[1.75rem] border border-[#BFEAFF] bg-white p-5">
      <SectionHeading eyebrow="Roles" title="Role Management" description="Review role ownership, assigned users, permission groups, and visibility through the shared Administration management pattern." />

      <div className="mt-4 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] px-4 py-3" aria-live="polite">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">Selected Role</p>
        <p className="mt-1 text-sm font-semibold text-[#0A2540]">
          {selectedRole ? `${selectedRole.role} - ${selectedRole.assignedUsers} Users - ${PLATFORM_ADMINISTRATION_ROLE_PERMISSION_GROUPS.length} Permission Groups` : "No role selected"}
        </p>
      </div>

      <div className="mt-5 grid items-stretch gap-4 xl:grid-cols-2">
        {PLATFORM_ADMINISTRATION_ROLES.map((role) => {
          const isSelected = selectedRoleName === role.role;
          return (
            <button
              key={role.role}
              type="button"
              aria-selected={isSelected}
              onClick={() => selectRole(role)}
              className={`flex min-w-0 flex-col rounded-[1.5rem] border p-5 text-left outline-none transition focus-visible:ring-2 focus-visible:ring-[#14B8A6] ${
                isSelected
                  ? "border-[#14B8A6] bg-[#E8FCFF] shadow-[inset_4px_0_0_#14B8A6]"
                  : "border-[#BFEAFF] bg-white hover:border-[#14B8A6] hover:bg-[#F4FBFF]"
              }`}
              data-role-visibility={role.visibility}
              data-future-role-metrics={PLATFORM_ADMINISTRATION_FUTURE_ROLE_METRICS.join(",")}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">Role</p>
                  <h3 className="mt-1 max-w-full min-w-0 break-words text-xl font-semibold text-[#0A2540]" style={platformAdministrationTextWrapStyle}>{role.role}</h3>
                </div>
                {role.showStatusBadge !== false && role.status === "Active" ? <PlatformAdministrationStatusPill status={role.status} /> : null}
              </div>
              <p className="mt-3 max-w-full min-w-0 flex-1 break-words text-sm leading-7 text-[#475569]" style={platformAdministrationTextWrapStyle}>{role.description}</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-2">
                <PlatformAdministrationFieldRow label="Assigned Users" value={`${role.assignedUsers} Users`} />
                <PlatformAdministrationFieldRow label="Permission Groups" value={`${PLATFORM_ADMINISTRATION_ROLE_PERMISSION_GROUPS.length} Groups`} />
                <PlatformAdministrationFieldRow label="Total Permissions" value={`${role.permissionCount}`} />
                <PlatformAdministrationFieldRow label="Visibility" value={formatPlatformAdministrationRoleVisibility(role.visibility)} />
              </div>
            </button>
          );
        })}
      </div>

      <PlatformAdministrationRoleManagementPanel
        selectedRole={selectedRole}
        panelMode={panelMode}
        assignedUsers={assignedRoleUsers}
        onViewAssignedUsers={() => setPanelMode("assigned-users")}
        onBackToSummary={() => setPanelMode("summary")}
        onClose={() => setPanelMode(null)}
      />
    </section>
  );
}

function PlatformAdministrationRoleManagementPanel({
  selectedRole,
  panelMode,
  assignedUsers,
  onViewAssignedUsers,
  onBackToSummary,
  onClose,
}: {
  selectedRole: PlatformAdministrationRoleRecord | null;
  panelMode: PlatformAdministrationRolePanelMode;
  assignedUsers: PlatformAdministrationUserRecord[];
  onViewAssignedUsers: () => void;
  onBackToSummary: () => void;
  onClose: () => void;
}) {
  if (!panelMode || !selectedRole) return null;

  if (panelMode === "assigned-users") {
    return (
      <PlatformAdministrationEnterprisePanel title="Assigned Users" eyebrow="Role Management" onClose={onClose}>
        <div className="grid gap-4">
          <div className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">Assigned Users</p>
            <h4 className="mt-1 max-w-full min-w-0 break-words text-xl font-semibold text-[#0A2540]" style={platformAdministrationTextWrapStyle}>{selectedRole.role}</h4>
            <p className="mt-2 max-w-full min-w-0 break-words text-sm leading-7 text-[#475569]" style={platformAdministrationTextWrapStyle}>
              Review-only list of users assigned to this role. Role reassignment is managed through the Users workspace.
            </p>
          </div>
          <div className="grid gap-3">
            {assignedUsers.length ? (
              assignedUsers.map((user) => (
                <article key={`${user.email}-${user.role}`} className="min-w-0 rounded-2xl border border-[#BFEAFF] bg-white p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="max-w-full min-w-0 break-words text-base font-semibold text-[#0A2540]" style={platformAdministrationTextWrapStyle}>{user.displayName || "Profile Incomplete"}</p>
                      <p className="mt-1 max-w-full min-w-0 break-words text-xs font-semibold uppercase tracking-[0.12em] text-[#14B8A6]" style={platformAdministrationTextWrapStyle}>{user.role}</p>
                    </div>
                    <PlatformAdministrationStatusPill status={user.status} />
                  </div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <PlatformAdministrationFieldRow label="Email" value={user.email} />
                    <PlatformAdministrationFieldRow label="Team" value={user.team || "Profile Incomplete"} />
                    <PlatformAdministrationFieldRow label="Last Login" value={formatPlatformAdministrationLastLogin(user)} />
                    <PlatformAdministrationFieldRow label="Invitation Status" value={<PlatformAdministrationInvitationStatusBadge status={user.invitationStatus} />} />
                  </div>
                </article>
              ))
            ) : (
              <div className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4 text-sm font-semibold leading-7 text-[#0A2540]">
                No assigned users are visible in the tenant directory for this role.
              </div>
            )}
          </div>
          <button type="button" onClick={onBackToSummary} className="inline-flex min-h-11 w-fit items-center rounded-xl border border-[#BFEAFF] bg-white px-4 text-sm font-semibold text-[#0A2540] transition hover:border-[#14B8A6] focus-ring">
            Back to Role Summary
          </button>
        </div>
      </PlatformAdministrationEnterprisePanel>
    );
  }

  return (
    <PlatformAdministrationEnterprisePanel title="Role Review" eyebrow="Role Management" onClose={onClose}>
      <div className="grid gap-4">
        <div className="grid gap-3 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">Role Summary</p>
          <PlatformAdministrationFieldRow label="Role" value={selectedRole.role} />
          <PlatformAdministrationFieldRow label="Description" value={selectedRole.description} />
          <PlatformAdministrationFieldRow label="Status" value={<PlatformAdministrationStatusPill status={selectedRole.status} />} />
          <PlatformAdministrationFieldRow label="Visibility" value={formatPlatformAdministrationRoleVisibility(selectedRole.visibility)} />
          <PlatformAdministrationFieldRow label="Assigned Users" value={`${selectedRole.assignedUsers} Users`} />
          <PlatformAdministrationFieldRow label="Permission Groups" value={`${PLATFORM_ADMINISTRATION_ROLE_PERMISSION_GROUPS.length} Groups`} />
        </div>
        <div className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">Permission Groups</p>
              <p className="mt-1 max-w-full min-w-0 break-words text-sm font-semibold text-[#0A2540]" style={platformAdministrationTextWrapStyle}>
                {PLATFORM_ADMINISTRATION_ROLE_PERMISSION_GROUPS.length} Permission Groups - {selectedRole.permissionCount} Total Permissions
              </p>
            </div>
            <button type="button" onClick={onViewAssignedUsers} className="inline-flex min-h-11 shrink-0 items-center justify-center rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-4 text-sm font-semibold text-[#0A2540] transition hover:border-[#14B8A6] focus-ring">
              View Assigned Users
            </button>
          </div>
          <div className="mt-4 grid gap-2">
            {PLATFORM_ADMINISTRATION_ROLE_PERMISSION_GROUPS.map((group) => (
              <div key={group} className="min-w-0 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2 text-sm font-semibold text-[#0A2540]" style={platformAdministrationTextWrapStyle}>
                {group}
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4 text-sm font-semibold leading-7 text-[#0A2540]">
          Review-only role management is certified for Version 1. Permission editing belongs to the Permissions workspace.
        </div>
      </div>
    </PlatformAdministrationEnterprisePanel>
  );
}

function PlatformAdministrationPermissionsTab({
  selectedPermissionRole,
  setSelectedPermissionRole,
}: {
  selectedPermissionRole: (typeof PLATFORM_ADMINISTRATION_ROLE_SELECTOR_OPTIONS)[number];
  setSelectedPermissionRole: Dispatch<SetStateAction<(typeof PLATFORM_ADMINISTRATION_ROLE_SELECTOR_OPTIONS)[number]>>;
}) {
  const [selectedGroupLabel, setSelectedGroupLabel] = useState("");
  const [panelOpen, setPanelOpen] = useState(false);
  const selectedGroup = PLATFORM_ADMINISTRATION_PERMISSION_GROUPS.find((group) => group.label === selectedGroupLabel) || null;
  const permissionActions = PLATFORM_ADMINISTRATION_PERMISSION_ACTIONS;
  const permissionModuleCount = PLATFORM_ADMINISTRATION_PERMISSION_GROUPS.reduce((total, group) => total + group.modules.length, 0);
  const allowedPermissions = PLATFORM_ADMINISTRATION_PERMISSION_GROUPS.reduce(
    (total, group) => total + group.modules.reduce((groupTotal, module) => groupTotal + permissionActions.filter((action) => isPlatformAdministrationPermissionAllowed(selectedPermissionRole, module, action)).length, 0),
    0,
  );
  const permissionGroupCount = PLATFORM_ADMINISTRATION_PERMISSION_GROUPS.length;
  const restrictedPermissions = 0;
  const roleCount = PLATFORM_ADMINISTRATION_ROLES.length;

  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setPanelOpen(false);
      }
    }

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, []);

  function selectGroup(group: PlatformAdministrationPermissionGroupRecord) {
    setSelectedGroupLabel(group.label);
    setPanelOpen(true);
  }

  return (
    <section className="relative rounded-[1.75rem] border border-[#BFEAFF] bg-white p-5">
      <SectionHeading eyebrow="Permissions" title="Permission Management" description="Review module-level action permissions through the shared Administration management pattern." />
      <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(260px,.45fr)_minmax(0,1fr)]">
        <label className="min-w-0 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
          <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">Selected Role</span>
          <select
            value={selectedPermissionRole}
            onChange={(event) => setSelectedPermissionRole(event.target.value as (typeof PLATFORM_ADMINISTRATION_ROLE_SELECTOR_OPTIONS)[number])}
            className="focus-ring mt-2 w-full rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold text-[#0A2540]"
          >
            {PLATFORM_ADMINISTRATION_ROLE_SELECTOR_OPTIONS.map((role) => (
              <option key={role} value={role}>{role}</option>
            ))}
          </select>
        </label>
        <div className="grid min-w-0 gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <PlatformAdministrationPermissionSummaryMetric label="Role" value={selectedPermissionRole} />
          <PlatformAdministrationPermissionSummaryMetric label="Granted Permission Groups" value={`${permissionGroupCount}`} />
          <PlatformAdministrationPermissionSummaryMetric label="Granted Permissions" value={`${allowedPermissions}`} />
          <PlatformAdministrationPermissionSummaryMetric label="Restricted Permissions" value={`${restrictedPermissions}`} />
          <PlatformAdministrationPermissionSummaryMetric label="Modules" value={`${permissionModuleCount}`} />
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] px-4 py-3" aria-live="polite">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">Selected Permission Group</p>
        <p className="mt-1 text-sm font-semibold text-[#0A2540]">
          {selectedGroup ? `${selectedGroup.label} - ${selectedGroup.modules.length} Modules - ${selectedGroup.modules.length * permissionActions.length} Permissions - ${roleCount} Roles` : "No permission group selected"}
        </p>
      </div>

      <div className="mt-5 grid gap-3 lg:grid-cols-3">
        {PLATFORM_ADMINISTRATION_PERMISSION_GROUPS.map((group) => {
          const isSelected = selectedGroupLabel === group.label;
          return (
            <button
              key={group.label}
              type="button"
              aria-selected={isSelected}
              aria-label={`Review ${group.label} permission group`}
              onClick={() => selectGroup(group)}
              className={`min-w-0 rounded-2xl border p-4 text-left outline-none transition focus-visible:ring-2 focus-visible:ring-[#14B8A6] ${
                isSelected
                  ? "border-[#14B8A6] bg-[#E8FCFF] shadow-[inset_4px_0_0_#14B8A6]"
                  : "border-[#BFEAFF] bg-[#F4FBFF] hover:border-[#14B8A6]"
              }`}
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">Permission Group</p>
              <h3 className="mt-1 max-w-full min-w-0 break-words text-lg font-semibold text-[#0A2540]" style={platformAdministrationTextWrapStyle}>{group.label}</h3>
              <p className="mt-2 text-sm font-semibold text-[#475569]">{group.modules.length} modules</p>
            </button>
          );
        })}
      </div>

      <div className="mt-5 max-w-full overflow-x-auto overscroll-x-contain">
        <table className="min-w-[980px] text-left text-sm">
          <thead className="text-xs uppercase tracking-[0.12em] text-[#14B8A6]">
            <tr className="border-b border-[#BFEAFF]">
              <th className="sticky left-0 z-20 min-w-[220px] border-r border-[#BFEAFF] bg-white px-3 py-3 font-semibold">Module</th>
              {permissionActions.map((action) => (
                <th key={action} className="min-w-[104px] px-3 py-3 text-center font-semibold">{formatPlatformAdministrationPermissionActionLabel(action)}</th>
              ))}
            </tr>
          </thead>
          {PLATFORM_ADMINISTRATION_PERMISSION_GROUPS.map((group) => (
            <tbody key={group.label}>
              <tr className={`border-b border-[#BFEAFF] ${selectedGroupLabel === group.label ? "bg-[#E8FCFF]" : "bg-[#F4FBFF]"}`}>
                <td colSpan={permissionActions.length + 1} className="px-3 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">
                  {group.label}
                </td>
              </tr>
              {group.modules.map((module) => (
                <tr key={module} className="border-b border-[#E8FCFF]">
                  <td className="sticky left-0 z-10 min-w-[220px] border-r border-[#E8FCFF] bg-white px-3 py-3 font-semibold text-[#0A2540]"><span className="block max-w-full min-w-0 break-words leading-snug" style={platformAdministrationTextWrapStyle}>{module}</span></td>
                  {permissionActions.map((action) => {
                    const allowed = isPlatformAdministrationPermissionAllowed(selectedPermissionRole, module, action);
                    return (
                      <td key={`${module}-${action}`} className="px-3 py-2 text-center align-middle">
                        <PlatformAdministrationPermissionIndicator allowed={allowed} />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      </div>

      <PlatformAdministrationPermissionManagementPanel
        selectedGroup={selectedGroup}
        selectedPermissionRole={selectedPermissionRole}
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
      />
    </section>
  );
}

function PlatformAdministrationPermissionManagementPanel({
  selectedGroup,
  selectedPermissionRole,
  open,
  onClose,
}: {
  selectedGroup: PlatformAdministrationPermissionGroupRecord | null;
  selectedPermissionRole: (typeof PLATFORM_ADMINISTRATION_ROLE_SELECTOR_OPTIONS)[number];
  open: boolean;
  onClose: () => void;
}) {
  if (!open || !selectedGroup) return null;

  const permissionActionLabels = PLATFORM_ADMINISTRATION_PERMISSION_ACTIONS.map(formatPlatformAdministrationPermissionActionLabel);
  const rolesUsingGroup = PLATFORM_ADMINISTRATION_ROLES.map((role) => role.role);
  const totalPermissions = selectedGroup.modules.length * PLATFORM_ADMINISTRATION_PERMISSION_ACTIONS.length;

  return (
    <PlatformAdministrationEnterprisePanel title="Permission Group Review" eyebrow="Permission Management" onClose={onClose}>
      <div className="grid gap-4">
        <div className="grid gap-3 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">Permission Group Summary</p>
          <PlatformAdministrationFieldRow label="Permission Group" value={selectedGroup.label} />
          <PlatformAdministrationFieldRow label="Description" value={getPlatformAdministrationPermissionGroupDescription(selectedGroup.label)} />
          <PlatformAdministrationFieldRow label="Status" value={<PlatformAdministrationStatusPill status="Active" />} />
          <PlatformAdministrationFieldRow label="Selected Role" value={selectedPermissionRole} />
        </div>
        <div className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">Summary Statistics</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <PlatformAdministrationFieldRow label="Modules" value={`${selectedGroup.modules.length}`} />
            <PlatformAdministrationFieldRow label="Permission Groups" value="1" />
            <PlatformAdministrationFieldRow label="Total Permissions" value={`${totalPermissions}`} />
            <PlatformAdministrationFieldRow label="Assigned Roles" value={`${rolesUsingGroup.length}`} />
          </div>
        </div>
        <div className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">Modules Included</p>
          <div className="mt-3 grid gap-2">
            {selectedGroup.modules.map((module) => (
              <div key={module} className="min-w-0 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2 text-sm font-semibold text-[#0A2540]" style={platformAdministrationTextWrapStyle}>
                {module}
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">Permission Actions</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {permissionActionLabels.map((action) => (
              <span key={action} className="min-w-0 rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-1 text-xs font-semibold text-[#0A2540]" style={platformAdministrationTextWrapStyle}>
                {action}
              </span>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">Roles Using This Group</p>
          <div className="mt-3 grid gap-2">
            {rolesUsingGroup.map((role) => (
              <div key={role} className="min-w-0 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2 text-sm font-semibold text-[#0A2540]" style={platformAdministrationTextWrapStyle}>
                {role}
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4 text-sm font-semibold leading-7 text-[#0A2540]">
          Permission Management is review-only for Version 1. Permission assignments remain governed by the certified role and permission model.
        </div>
      </div>
    </PlatformAdministrationEnterprisePanel>
  );
}

function PlatformAdministrationEnterprisePanel({
  eyebrow,
  title,
  onClose,
  children,
}: {
  eyebrow: string;
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-[80] flex justify-end bg-[#0A2540]/20" role="presentation">
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="platform-administration-enterprise-management-title"
        className="flex h-full w-full max-w-[460px] flex-col border-l border-[#BFEAFF] bg-white shadow-[0_24px_80px_rgba(15,23,42,.22)]"
      >
        <div className="border-b border-[#BFEAFF] p-5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">{eyebrow}</p>
          <div className="mt-2 flex items-start justify-between gap-4">
            <h3 id="platform-administration-enterprise-management-title" className="text-2xl font-semibold tracking-tight text-[#0A2540]">{title}</h3>
            <button type="button" onClick={onClose} className="focus-ring rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2 text-sm font-semibold text-[#0A2540]">
              Close
            </button>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          {children}
        </div>
      </aside>
    </div>
  );
}

function PlatformAdministrationTeamsTab({
  teamRows,
  userRows,
  portfolioOptions,
  onSaveTeams,
}: {
  teamRows: PlatformAdministrationTeamRecord[];
  userRows: PlatformAdministrationUserRecord[];
  portfolioOptions: PlatformAdministrationPortfolioOption[];
  onSaveTeams: (teams: PlatformAdministrationTeamRecord[]) => Promise<PlatformAdministrationTeamRecord[]>;
}) {
  const [selectedTeamName, setSelectedTeamName] = useState("");
  const [sessionUserRole, setSessionUserRole] = useState("");
  const [sessionUserEmail, setSessionUserEmail] = useState("");
  const [activeWorkflow, setActiveWorkflow] = useState<PlatformAdministrationTeamWorkflow>(null);
  const [notification, setNotification] = useState<PlatformAdministrationNotification | null>(null);
  const [saving, setSaving] = useState(false);
  const [teamForm, setTeamForm] = useState<PlatformAdministrationTeamFormState>(blankPlatformAdministrationTeamForm);
  const selectedTeam = teamRows.find((team) => team.teamName === selectedTeamName) || null;
  const managerOptions = Array.from(new Set(userRows.filter((user) => user.status === "Active" && ["Platform Owner", "Tenant Admin", "AR Administrator", "AR Manager"].includes(user.role)).map((user) => user.displayName).filter(Boolean)));
  const teamLeadOptions = Array.from(new Set(userRows.filter((user) => user.status === "Active" && user.role === "Team Lead").map((user) => user.displayName).filter(Boolean)));
  const collectorOptions = userRows.filter((user) => user.status === "Active" && user.role === "Collector");
  const sessionDirectoryUser = userRows.find((user) => normalizePlatformAdministrationEmail(user.email) === normalizePlatformAdministrationEmail(sessionUserEmail)) || null;
  const canCreateTeams = isPlatformAdministrationTeamLeadershipRole(sessionUserRole);
  const canManageSelectedTeam = Boolean(selectedTeam && isPlatformAdministrationTeamManagementAllowed(sessionUserRole, selectedTeam, sessionDirectoryUser));
  const teamsSummary = {
    teams: teamRows.length,
    managers: new Set(teamRows.map((team) => team.manager).filter(Boolean)).size,
    collectors: teamRows.reduce((total, team) => total + team.collectors, 0),
    assignedPortfolios: teamRows.reduce((total, team) => total + team.portfolioCount, 0),
  };
  const managerTeamCounts = teamRows.reduce<Record<string, number>>((counts, team) => {
    const managerName = normalizePlatformAdministrationName(team.manager);
    if (!managerName) return counts;
    counts[managerName] = (counts[managerName] || 0) + 1;
    return counts;
  }, {});

  useEffect(() => {
    let cancelled = false;
    getSession()
      .then((session) => {
        if (!cancelled) {
          setSessionUserRole(session.user?.role || "");
          setSessionUserEmail(session.user?.email || "");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSessionUserRole("");
          setSessionUserEmail("");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (selectedTeamName && !teamRows.some((team) => team.teamName === selectedTeamName)) {
      setSelectedTeamName("");
      setActiveWorkflow(null);
    }
  }, [selectedTeamName, teamRows]);

  useEffect(() => {
    if (!notification) return undefined;
    const timeoutId = window.setTimeout(() => setNotification(null), notification.tone === "error" ? 6500 : 4200);
    return () => window.clearTimeout(timeoutId);
  }, [notification]);

  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setActiveWorkflow(null);
      }
    }

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, []);

  function selectTeam(team: PlatformAdministrationTeamRecord, workflow: PlatformAdministrationTeamWorkflow = "summary") {
    setSelectedTeamName(team.teamName);
    setActiveWorkflow(workflow);
    setNotification(null);
  }

  function openTeamWorkflow(workflow: PlatformAdministrationTeamWorkflow) {
    if (workflow === "create") {
      if (!canCreateTeams) {
        setNotification({ message: "Only Owners and Managers can create teams.", tone: "error" });
        return;
      }
      setTeamForm({
        ...blankPlatformAdministrationTeamForm,
        manager: managerOptions[0] || "",
      });
      setActiveWorkflow("create");
      setNotification(null);
      return;
    }

    if (!selectedTeam) {
      setNotification({ message: "Select a team before opening this management action.", tone: "error" });
      return;
    }

    if (!canManageSelectedTeam) {
      setNotification({ message: "Only Owners and assigned Managers can update this team.", tone: "error" });
      return;
    }

    setTeamForm({
      teamName: selectedTeam.teamName,
      description: selectedTeam.description,
      manager: selectedTeam.manager,
      teamLead: selectedTeam.teamLead,
      collectorIds: selectedTeam.collectorIds,
      portfolioIds: selectedTeam.portfolioIds,
      status: selectedTeam.status,
    });
    setActiveWorkflow(workflow);
    setNotification(null);
  }

  const selectedTeamRequiredTitle = selectedTeam ? undefined : "Select a team first.";
  const createTeamTitle = canCreateTeams ? undefined : "Only Owners and Managers can create teams.";
  const manageTeamTitle = !selectedTeam ? "Select a team first." : !canManageSelectedTeam ? "Only Owners and assigned Managers can update this team." : undefined;

  async function handleTeamSave(event?: FormEvent<HTMLFormElement>) {
    event?.preventDefault();
    const teamName = teamForm.teamName.trim();
    const manager = teamForm.manager.trim();
    if (!teamName || !manager) {
      setNotification({ message: "Team Name and Assigned Manager are required.", tone: "error" });
      return;
    }
    if (activeWorkflow === "create" && !canCreateTeams) {
      setNotification({ message: "Only Owners and Managers can create teams.", tone: "error" });
      return;
    }
    if (activeWorkflow !== "create" && !canManageSelectedTeam) {
      setNotification({ message: "Only Owners and assigned Managers can update this team.", tone: "error" });
      return;
    }
    if (activeWorkflow === "create" && teamRows.some((team) => team.teamName.toLowerCase() === teamName.toLowerCase())) {
      setNotification({ message: "A team with that name already exists.", tone: "error" });
      return;
    }
    const hasOperationalWarning = teamForm.collectorIds.length === 0 || teamForm.portfolioIds.length === 0;
    const wasCreateWorkflow = activeWorkflow === "create";

    setSaving(true);
    setNotification(null);
    try {
      const nextTeam = normalizePlatformAdministrationTeam({
        teamName,
        description: teamForm.description,
        manager,
        teamLead: teamForm.teamLead,
        collectors: teamForm.collectorIds.length,
        collectorIds: teamForm.collectorIds,
        status: teamForm.status,
        portfolioCount: teamForm.portfolioIds.length,
        portfolioIds: teamForm.portfolioIds,
        activityHistory: buildPlatformAdministrationTeamActivityHistory(activeWorkflow === "create" ? "Team Created" : activeWorkflow === "manager" ? "Manager Assigned" : "Team Updated", teamName, manager, selectedTeam?.activityHistory),
        assignmentStatus: hasOperationalWarning ? "Pending" : activeWorkflow === "create" ? "Ready" : selectedTeam?.assignmentStatus || "Ready",
        capacitySummary: teamForm.collectorIds.length ? `${teamForm.collectorIds.length} collectors assigned.` : "Collector capacity has not been assigned yet.",
        workloadSummary: teamForm.portfolioIds.length ? `${teamForm.portfolioIds.length} portfolios assigned to this team.` : "No active workload has been assigned to this team.",
      });
      const nextTeams = activeWorkflow === "create"
        ? [nextTeam, ...teamRows]
        : teamRows.map((team) => (team.teamName === selectedTeam?.teamName ? nextTeam : team));
      await onSaveTeams(nextTeams);
      publishPlatformAdministrationTeamWorkflowEvents({
        team: nextTeam,
        previousTeam: activeWorkflow === "create" ? null : selectedTeam,
        action: activeWorkflow === "create" ? "create" : activeWorkflow === "manager" ? "assign-manager" : "update",
        actorRole: sessionUserRole,
        actorEmail: sessionUserEmail,
      });
      setSelectedTeamName(nextTeam.teamName);
      setActiveWorkflow(wasCreateWorkflow ? "summary" : null);
      setNotification({
        message: hasOperationalWarning
          ? `${nextTeam.teamName} was ${wasCreateWorkflow ? "created" : "updated"}, but is not yet fully operational until Collectors and Portfolios are assigned.`
          : `${nextTeam.teamName} was ${wasCreateWorkflow ? "created" : "updated"}.`,
        tone: hasOperationalWarning ? "info" : "success",
      });
    } catch (error) {
      setNotification({ message: error instanceof Error ? error.message : "Unable to save the team.", tone: "error" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="relative rounded-[1.75rem] border border-[#BFEAFF] bg-white p-5">
      <SectionHeading eyebrow="Teams" title="Team Management Center" description="Manage team selection, ownership, collector capacity, and portfolio coverage without leaving Administration." />

      <div className="mt-5 grid gap-3 md:grid-cols-4">
        <PlatformAdministrationTeamSummaryMetric label="Teams" value={teamsSummary.teams} />
        <PlatformAdministrationTeamSummaryMetric label="Managers" value={teamsSummary.managers} />
        <PlatformAdministrationTeamSummaryMetric label="Collectors" value={teamsSummary.collectors} />
        <PlatformAdministrationTeamSummaryMetric label="Assigned Portfolios" value={teamsSummary.assignedPortfolios} />
      </div>

      <div className="mt-5 grid gap-3 xl:grid-cols-5">
        <button type="button" onClick={() => openTeamWorkflow("create")} disabled={!canCreateTeams} title={createTeamTitle} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-4 text-sm font-semibold text-[#0A2540] transition hover:border-[#14B8A6] focus-ring disabled:cursor-not-allowed disabled:opacity-60">
          <ARLightLineIcon icon={<Bolt className="size-4" aria-hidden="true" />} compact />
          Create Team
        </button>
        <button type="button" onClick={() => openTeamWorkflow("edit")} disabled={!selectedTeam || !canManageSelectedTeam} title={manageTeamTitle} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-4 text-sm font-semibold text-[#0A2540] transition hover:border-[#14B8A6] focus-ring disabled:cursor-not-allowed disabled:opacity-60">
          <ARLightLineIcon icon={<Bolt className="size-4" aria-hidden="true" />} compact />
          Edit Team
        </button>
        <button type="button" onClick={() => openTeamWorkflow("manager")} disabled={!selectedTeam || !isPlatformAdministrationTeamOwnerRole(sessionUserRole)} title={!selectedTeam ? selectedTeamRequiredTitle : !isPlatformAdministrationTeamOwnerRole(sessionUserRole) ? "Only Owners can assign managers." : undefined} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-4 text-sm font-semibold text-[#0A2540] transition hover:border-[#14B8A6] focus-ring disabled:cursor-not-allowed disabled:opacity-60">
          <ARLightLineIcon icon={<Bolt className="size-4" aria-hidden="true" />} compact />
          Assign Manager
        </button>
      </div>

      <div className="mt-4 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] px-4 py-3" aria-live="polite">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">Selected Team</p>
        <p className="mt-1 text-sm font-semibold text-[#0A2540]">
          {selectedTeam ? `${selectedTeam.teamName} - ${selectedTeam.manager} - ${selectedTeam.teamLead || "No Team Lead Assigned"} - ${selectedTeam.collectors} collectors` : "No team selected"}
        </p>
      </div>

      {notification ? (
        <div
          role={notification.tone === "error" ? "alert" : "status"}
          className={`fixed right-4 top-4 z-[90] max-w-md rounded-2xl border px-4 py-3 text-sm font-semibold shadow-[0_18px_48px_rgba(15,23,42,.18)] ${
            notification.tone === "error"
              ? "border-red-200 bg-red-50 text-red-900"
              : "border-[#BFEAFF] bg-white text-[#0A2540]"
          }`}
        >
          {notification.message}
        </div>
      ) : null}

      <div className="mt-5 grid gap-4 lg:grid-cols-3">
        {teamRows.map((team) => {
          const isSelected = selectedTeamName === team.teamName;
          return (
            <button
              key={team.teamName}
              type="button"
              aria-selected={isSelected}
              onClick={() => selectTeam(team)}
              className={`flex min-w-0 flex-col rounded-[1.5rem] border p-4 text-left outline-none transition focus-visible:ring-2 focus-visible:ring-[#14B8A6] ${
                isSelected
                  ? "border-[#14B8A6] bg-[#E8FCFF] shadow-[inset_4px_0_0_#14B8A6]"
                  : "border-[#BFEAFF] bg-[#F4FBFF] hover:border-[#14B8A6]"
              }`}
              data-future-team-capacity-metrics={PLATFORM_ADMINISTRATION_FUTURE_TEAM_CAPACITY_METRICS.join(",")}
            >
              <div className="flex items-start justify-between gap-3">
                <h3 className="max-w-full min-w-0 break-words text-lg font-semibold text-[#0A2540]" style={platformAdministrationTextWrapStyle}>{team.teamName}</h3>
                <PlatformAdministrationStatusPill status={team.status} />
              </div>
              <div className="mt-4 grid flex-1 gap-3">
                <PlatformAdministrationFieldRow label="Manager" value={team.manager} />
                <PlatformAdministrationFieldRow label="Team Lead" value={team.teamLead || "No Team Lead Assigned"} />
                <PlatformAdministrationFieldRow label="Collectors" value={`${team.collectors}`} />
                <PlatformAdministrationFieldRow label="Assigned Portfolios" value={`${team.portfolioCount}`} />
                <PlatformAdministrationFieldRow label="Assignment Status" value={<PlatformAdministrationAssignmentStatusBadge status={team.assignmentStatus} />} />
              </div>
            </button>
          );
        })}
      </div>

      <PlatformAdministrationTeamManagementPanel
        activeWorkflow={activeWorkflow}
        selectedTeam={selectedTeam}
        teamForm={teamForm}
        setTeamForm={setTeamForm}
        managerOptions={managerOptions}
        teamLeadOptions={teamLeadOptions}
        managerTeamCounts={managerTeamCounts}
        collectorOptions={collectorOptions}
        portfolioOptions={portfolioOptions}
        saving={saving}
        onClose={() => setActiveWorkflow(null)}
        onSave={handleTeamSave}
      />
    </section>
  );
}

function PlatformAdministrationTeamManagementPanel({
  activeWorkflow,
  selectedTeam,
  teamForm,
  setTeamForm,
  managerOptions,
  teamLeadOptions,
  managerTeamCounts,
  collectorOptions,
  portfolioOptions,
  saving,
  onClose,
  onSave,
}: {
  activeWorkflow: PlatformAdministrationTeamWorkflow;
  selectedTeam: PlatformAdministrationTeamRecord | null;
  teamForm: PlatformAdministrationTeamFormState;
  setTeamForm: Dispatch<SetStateAction<PlatformAdministrationTeamFormState>>;
  managerOptions: string[];
  teamLeadOptions: string[];
  managerTeamCounts: Record<string, number>;
  collectorOptions: PlatformAdministrationUserRecord[];
  portfolioOptions: PlatformAdministrationPortfolioOption[];
  saving: boolean;
  onClose: () => void;
  onSave: (event?: FormEvent<HTMLFormElement>) => void | Promise<void>;
}) {
  const [portfolioSearch, setPortfolioSearch] = useState("");
  const [selectedTab, setSelectedTab] = useState<PlatformAdministrationTeamPanelTab>("overview");
  const [expandedAssignments, setExpandedAssignments] = useState<Partial<Record<PlatformAdministrationAssignmentSectionKey, boolean>>>({});
  if (!activeWorkflow) return null;

  const title =
    activeWorkflow === "create"
      ? "Create Team"
      : activeWorkflow === "edit"
        ? "Edit Team"
        : activeWorkflow === "manager"
          ? "Assign Manager"
          : "Team Management";
  const selectedCollectors = collectorOptions.filter((collector) => teamForm.collectorIds.includes(collector.id));
  const selectedPortfolios = portfolioOptions.filter((portfolio) => teamForm.portfolioIds.includes(portfolio.id));

  function isAssignmentExpanded(section: PlatformAdministrationAssignmentSectionKey, hasSelection: boolean) {
    return expandedAssignments[section] ?? !hasSelection;
  }

  function openAssignmentSection(section: PlatformAdministrationAssignmentSectionKey) {
    setExpandedAssignments((current) => ({ ...current, [section]: true }));
  }

  return (
    <div className="fixed inset-0 z-[80] flex justify-end bg-[#0A2540]/20" role="presentation">
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="platform-administration-team-management-title"
        className="flex h-full w-full max-w-[460px] flex-col border-l border-[#BFEAFF] bg-white shadow-[0_24px_80px_rgba(15,23,42,.22)]"
      >
        <div className="border-b border-[#BFEAFF] p-5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">Team Management</p>
          <div className="mt-2 flex items-start justify-between gap-4">
            <h3 id="platform-administration-team-management-title" className="text-2xl font-semibold tracking-tight text-[#0A2540]">{title}</h3>
            <button type="button" onClick={onClose} className="focus-ring rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2 text-sm font-semibold text-[#0A2540]">
              Close
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          {activeWorkflow === "create" || activeWorkflow === "edit" || activeWorkflow === "manager" ? (
            <form onSubmit={(event) => void onSave(event)} className="grid gap-4">
              {activeWorkflow !== "create" && selectedTeam ? <PlatformAdministrationTeamSummary team={selectedTeam} /> : null}
              {activeWorkflow === "manager" ? null : (
                <>
                  <PlatformAdministrationInput label="Team Name" value={teamForm.teamName} onChange={(value) => setTeamForm((current) => ({ ...current, teamName: value }))} required />
                  <label className="grid gap-2 text-sm font-semibold text-[#0A2540]">
                    Description
                    <textarea
                      value={teamForm.description}
                      onChange={(event) => setTeamForm((current) => ({ ...current, description: event.target.value }))}
                      className="focus-ring min-h-28 rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold text-[#0A2540]"
                    />
                  </label>
                </>
              )}
              <PlatformAdministrationAssignmentSection
                title="Manager"
                isExpanded={isAssignmentExpanded("manager", Boolean(teamForm.manager))}
                onChange={() => openAssignmentSection("manager")}
                summary={teamForm.manager ? (
                  <PlatformAdministrationAssignmentSummary
                    title={teamForm.manager}
                    detail="AR Manager"
                    meta={`Managing ${managerTeamCounts[normalizePlatformAdministrationName(teamForm.manager)] || 1} Teams`}
                  />
                ) : null}
              >
                <label className="grid gap-2 text-sm font-semibold text-[#0A2540]">
                  Assigned Manager
                  <select
                    value={teamForm.manager}
                    onChange={(event) => {
                      setTeamForm((current) => ({ ...current, manager: event.target.value }));
                      if (event.target.value) setExpandedAssignments((current) => ({ ...current, manager: false }));
                    }}
                    className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
                    required
                  >
                    <option value="">Select manager</option>
                    {[...new Set([teamForm.manager, ...managerOptions].filter(Boolean))].map((manager) => (
                      <option key={manager} value={manager}>{manager}</option>
                    ))}
                  </select>
                </label>
              </PlatformAdministrationAssignmentSection>
              {activeWorkflow === "manager" ? null : (
                <label className="grid gap-2 text-sm font-semibold text-[#0A2540]">
                  Status
                  <select
                    value={teamForm.status}
                    onChange={(event) => setTeamForm((current) => ({ ...current, status: event.target.value }))}
                    className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </label>
              )}
              {activeWorkflow === "manager" ? null : (
                <>
                  <PlatformAdministrationAssignmentSection
                    title="Team Lead"
                    isExpanded={isAssignmentExpanded("teamLead", Boolean(teamForm.teamLead))}
                    onChange={() => openAssignmentSection("teamLead")}
                    summary={teamForm.teamLead ? (
                      <PlatformAdministrationAssignmentSummary
                        title={teamForm.teamLead}
                        detail="Team Lead"
                        meta={teamForm.status === "Active" ? "Current Team Active" : "Current Team Inactive"}
                      />
                    ) : null}
                  >
                    <label className="grid gap-2 text-sm font-semibold text-[#0A2540]">
                      Assigned Team Lead
                      <select
                        value={teamForm.teamLead}
                        onChange={(event) => {
                          setTeamForm((current) => ({ ...current, teamLead: event.target.value }));
                          if (event.target.value) setExpandedAssignments((current) => ({ ...current, teamLead: false }));
                        }}
                        className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
                      >
                        <option value="">No Team Lead Assigned</option>
                        {[...new Set([teamForm.teamLead, ...teamLeadOptions].filter(Boolean))].map((teamLead) => (
                          <option key={teamLead} value={teamLead}>{teamLead}</option>
                        ))}
                      </select>
                    </label>
                  </PlatformAdministrationAssignmentSection>
                  <PlatformAdministrationAssignmentSection
                    title="Collectors"
                    isExpanded
                    onChange={() => openAssignmentSection("collectors")}
                    summary={teamForm.collectorIds.length ? (
                      <div className="grid gap-2">
                        {selectedCollectors.map((collector) => (
                          <PlatformAdministrationAssignmentSummary
                            key={collector.id}
                            title={collector.displayName}
                            detail={collector.role}
                            meta={collector.team ? `Current Team: ${collector.team}` : "Current Team: Unassigned"}
                          />
                        ))}
                      </div>
                    ) : null}
                  >
                    <PlatformAdministrationCollectorAssignmentField
                      collectorOptions={collectorOptions}
                      selectedCollectorIds={teamForm.collectorIds}
                      onChange={(collectorIds) => {
                        setTeamForm((current) => ({ ...current, collectorIds }));
                      }}
                    />
                  </PlatformAdministrationAssignmentSection>
                  <PlatformAdministrationAssignmentSection
                    title="Portfolios"
                    isExpanded={isAssignmentExpanded("portfolios", teamForm.portfolioIds.length > 0)}
                    onChange={() => openAssignmentSection("portfolios")}
                    summary={teamForm.portfolioIds.length ? (
                      <div className="grid gap-2">
                        {selectedPortfolios.map((portfolio) => (
                          <PlatformAdministrationAssignmentSummary
                            key={portfolio.id}
                            title={portfolio.portfolioName}
                            detail={portfolio.client}
                            meta={portfolio.status}
                          />
                        ))}
                      </div>
                    ) : null}
                  >
                    <PlatformAdministrationPortfolioAssignmentField
                      portfolioOptions={portfolioOptions}
                      selectedPortfolioIds={teamForm.portfolioIds}
                    searchTerm={portfolioSearch}
                    onSearchChange={setPortfolioSearch}
                    onChange={(portfolioIds) => {
                      setTeamForm((current) => ({ ...current, portfolioIds }));
                      if (portfolioIds.length) setExpandedAssignments((current) => ({ ...current, portfolios: false }));
                    }}
                    />
                  </PlatformAdministrationAssignmentSection>
                  <PlatformAdministrationTeamSetupProgress teamForm={teamForm} />
                </>
              )}
              <div className="flex flex-wrap gap-2 pt-2">
                <button type="submit" disabled={saving} className="inline-flex min-h-11 items-center rounded-xl bg-[#14B8A6] px-4 text-sm font-semibold text-white transition hover:bg-[#0F9F94] focus-ring disabled:opacity-60">{saving ? "Saving..." : activeWorkflow === "create" ? "Create Team" : "Save Changes"}</button>
                <button type="button" onClick={onClose} disabled={saving} className="inline-flex min-h-11 items-center rounded-xl border border-[#BFEAFF] bg-white px-4 text-sm font-semibold text-[#0A2540] transition hover:border-[#14B8A6] focus-ring disabled:opacity-60">Cancel</button>
              </div>
            </form>
          ) : null}

          {activeWorkflow === "summary" && selectedTeam ? (
            <div className="grid gap-4">
              <div className="flex gap-2 overflow-x-auto pb-1">
                {(["overview", "members", "portfolios", "activity", "history"] as PlatformAdministrationTeamPanelTab[]).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setSelectedTab(tab)}
                    className={`focus-ring inline-flex min-h-10 shrink-0 items-center rounded-xl border px-3 text-xs font-semibold capitalize ${
                      selectedTab === tab ? "border-[#14B8A6] bg-[#E8FCFF] text-[#0A2540]" : "border-[#BFEAFF] bg-white text-[#475569]"
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
              <PlatformAdministrationTeamPanelTabContent
                team={selectedTeam}
                selectedTab={selectedTab}
                collectorOptions={collectorOptions}
                portfolioOptions={portfolioOptions}
              />
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={onClose} className="inline-flex min-h-11 items-center rounded-xl border border-[#BFEAFF] bg-white px-4 text-sm font-semibold text-[#0A2540] transition hover:border-[#14B8A6] focus-ring">Cancel</button>
              </div>
            </div>
          ) : null}
        </div>
      </aside>
    </div>
  );
}

function PlatformAdministrationAssignmentSection({
  title,
  isExpanded,
  summary,
  onChange,
  children,
}: {
  title: string;
  isExpanded: boolean;
  summary: ReactNode;
  onChange: () => void;
  children: ReactNode;
}) {
  return (
    <section className="grid gap-3 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4" aria-label={`${title} assignment`}>
      <div className="flex min-w-0 items-start justify-between gap-3">
        <p className="min-w-0 break-words text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]" style={platformAdministrationTextWrapStyle}>{title}</p>
        {!isExpanded && summary ? (
          <button type="button" onClick={onChange} className="focus-ring shrink-0 rounded-lg border border-[#BFEAFF] bg-white px-2 py-1 text-xs font-semibold text-[#0A2540]">
            Change
          </button>
        ) : null}
      </div>
      {!isExpanded && summary ? summary : children}
    </section>
  );
}

function PlatformAdministrationAssignmentSummary({
  title,
  detail,
  meta,
}: {
  title: string;
  detail: string;
  meta: string;
}) {
  return (
    <article className="min-w-0 rounded-xl border border-[#BFEAFF] bg-white px-3 py-2">
      <p className="max-w-full min-w-0 break-words text-sm font-semibold text-[#0A2540]" style={platformAdministrationTextWrapStyle}>{title}</p>
      <p className="mt-1 max-w-full min-w-0 break-words text-xs font-semibold text-[#475569]" style={platformAdministrationTextWrapStyle}>{detail}</p>
      <p className="mt-1 max-w-full min-w-0 break-words text-[11px] font-semibold uppercase tracking-[0.12em] text-[#14B8A6]" style={platformAdministrationTextWrapStyle}>{meta}</p>
    </article>
  );
}

function PlatformAdministrationTeamSetupProgress({ teamForm }: { teamForm: PlatformAdministrationTeamFormState }) {
  const progressItems = [
    { label: "Team Name", complete: Boolean(teamForm.teamName.trim()), detail: teamForm.teamName.trim() || "Required" },
    { label: "Manager Assigned", complete: Boolean(teamForm.manager.trim()), detail: teamForm.manager.trim() || "Required" },
    { label: "Team Lead (Optional)", complete: Boolean(teamForm.teamLead.trim()), detail: teamForm.teamLead.trim() || "Optional" },
    { label: `${teamForm.collectorIds.length} Collectors Assigned`, complete: teamForm.collectorIds.length > 0, detail: teamForm.collectorIds.length ? "Recommended complete" : "Recommended" },
    { label: `${teamForm.portfolioIds.length} Portfolio Assigned`, complete: teamForm.portfolioIds.length > 0, detail: teamForm.portfolioIds.length ? "Recommended complete" : "Recommended" },
  ];

  return (
    <section className="grid gap-3 rounded-2xl border border-[#BFEAFF] bg-white p-4" aria-label="Team setup progress">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">Team Setup</p>
        <p className="mt-1 text-sm font-semibold text-[#475569]">Review operational readiness before saving.</p>
      </div>
      <div className="grid gap-2">
        {progressItems.map((item) => (
          <div key={item.label} className="flex min-w-0 items-start gap-3 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2">
            <span className={`mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold ${item.complete ? "border-[#14B8A6]/30 bg-[#E8FCFF] text-[#0A766F]" : "border-[#CBD5E1] bg-white text-[#475569]"}`} aria-hidden="true">
              {item.complete ? "OK" : "--"}
            </span>
            <span className="min-w-0">
              <span className="block max-w-full min-w-0 break-words text-sm font-semibold text-[#0A2540]" style={platformAdministrationTextWrapStyle}>{item.label}</span>
              <span className="block max-w-full min-w-0 break-words text-xs font-semibold text-[#475569]" style={platformAdministrationTextWrapStyle}>{item.detail}</span>
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

function PlatformAdministrationCollectorAssignmentField({
  collectorOptions,
  selectedCollectorIds,
  onChange,
}: {
  collectorOptions: PlatformAdministrationUserRecord[];
  selectedCollectorIds: string[];
  onChange: (collectorIds: string[]) => void;
}) {
  const selectedCollectors = collectorOptions.filter((collector) => selectedCollectorIds.includes(collector.id));
  const [collectorSearch, setCollectorSearch] = useState("");
  const normalizedSearch = collectorSearch.trim().toLowerCase();
  const availableCollectors = collectorOptions.filter((collector) => {
    if (selectedCollectorIds.includes(collector.id)) return false;
    if (!normalizedSearch) return true;
    return [collector.displayName, collector.role, collector.email, collector.team]
      .some((value) => value.toLowerCase().includes(normalizedSearch));
  });

  function toggleCollector(collectorId: string) {
    onChange(selectedCollectorIds.includes(collectorId)
      ? selectedCollectorIds.filter((id) => id !== collectorId)
      : [...selectedCollectorIds, collectorId]);
  }

  return (
    <div className="grid gap-3 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">Collectors</p>
        <p className="mt-1 text-sm font-semibold text-[#475569]">Add or remove collectors assigned to this team.</p>
      </div>
      <label className="grid gap-2 text-sm font-semibold text-[#0A2540]">
        Search Collectors
        <input
          value={collectorSearch}
          onChange={(event) => setCollectorSearch(event.target.value)}
          className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
          placeholder="Search by name, role, email, or team"
        />
      </label>
      <div className="grid gap-2" aria-label="Available collectors">
        {availableCollectors.length ? availableCollectors.map((collector) => (
          <button
            key={collector.id}
            type="button"
            onClick={() => toggleCollector(collector.id)}
            className="focus-ring grid min-w-0 gap-1 rounded-xl border border-[#BFEAFF] bg-white px-3 py-3 text-left text-sm font-semibold text-[#0A2540] transition hover:border-[#14B8A6]"
            aria-label={`Assign ${collector.displayName}`}
          >
            <span className="max-w-full min-w-0 break-words text-sm font-semibold text-[#0A2540]" style={platformAdministrationTextWrapStyle}>{collector.displayName}</span>
            <span className="max-w-full min-w-0 break-words text-xs font-semibold text-[#475569]" style={platformAdministrationTextWrapStyle}>{collector.role}</span>
            <span className="max-w-full min-w-0 break-words text-xs font-semibold text-[#475569]" style={platformAdministrationTextWrapStyle}>{collector.email}</span>
            <span className="max-w-full min-w-0 break-words text-[11px] font-semibold uppercase tracking-[0.12em] text-[#14B8A6]" style={platformAdministrationTextWrapStyle}>Eligible for Assignment</span>
          </button>
        )) : (
          <p className="rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold text-[#475569]">
            {collectorOptions.length === selectedCollectorIds.length ? "All available collectors are assigned." : "No collectors match this search."}
          </p>
        )}
      </div>
      <div className="grid gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">Selected Collectors</p>
        {selectedCollectors.length ? selectedCollectors.map((collector) => (
          <PlatformAdministrationCollectorAssignmentCard
            key={collector.id}
            collector={collector}
            onRemove={() => toggleCollector(collector.id)}
          />
        )) : (
          <p className="rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold text-[#475569]">No collectors selected.</p>
        )}
      </div>
    </div>
  );
}

function PlatformAdministrationCollectorAssignmentCard({
  collector,
  onRemove,
}: {
  collector: PlatformAdministrationUserRecord;
  onRemove: () => void;
}) {
  return (
    <article className="grid min-w-0 gap-3 rounded-xl border border-[#BFEAFF] bg-white px-3 py-3">
      <div className="flex min-w-0 items-start gap-3">
        <ARLightLineIcon icon={<Users className="size-4" aria-hidden="true" />} compact />
        <div className="min-w-0">
          <p className="max-w-full min-w-0 break-words text-sm font-semibold text-[#0A2540]" style={platformAdministrationTextWrapStyle}>{collector.displayName}</p>
          <p className="mt-1 max-w-full min-w-0 break-words text-xs font-semibold text-[#475569]" style={platformAdministrationTextWrapStyle}>{collector.role}</p>
          <p className="mt-1 max-w-full min-w-0 break-words text-xs font-semibold text-[#475569]" style={platformAdministrationTextWrapStyle}>{collector.email}</p>
        </div>
      </div>
      <div className="grid gap-1 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2">
        <p className="text-xs font-semibold text-[#475569]">Current Team</p>
        <p className="max-w-full min-w-0 break-words text-sm font-semibold text-[#0A2540]" style={platformAdministrationTextWrapStyle}>{collector.team || "No Team Assigned"}</p>
      </div>
      <div>
        <button type="button" onClick={onRemove} className="focus-ring rounded-lg border border-[#BFEAFF] bg-white px-3 py-2 text-xs font-semibold text-[#0A2540] transition hover:border-[#14B8A6]" aria-label={`Remove ${collector.displayName} from selected collectors`}>
          Remove
        </button>
      </div>
    </article>
  );
}

function PlatformAdministrationPortfolioAssignmentField({
  portfolioOptions,
  selectedPortfolioIds,
  searchTerm,
  onSearchChange,
  onChange,
}: {
  portfolioOptions: PlatformAdministrationPortfolioOption[];
  selectedPortfolioIds: string[];
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onChange: (portfolioIds: string[]) => void;
}) {
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const visiblePortfolios = portfolioOptions.filter((portfolio) =>
    !normalizedSearch
    || [portfolio.portfolioName, portfolio.client, portfolio.status].some((value) => value.toLowerCase().includes(normalizedSearch)),
  );
  const selectedPortfolios = portfolioOptions.filter((portfolio) => selectedPortfolioIds.includes(portfolio.id));

  function togglePortfolio(portfolioId: string) {
    onChange(selectedPortfolioIds.includes(portfolioId)
      ? selectedPortfolioIds.filter((id) => id !== portfolioId)
      : [...selectedPortfolioIds, portfolioId]);
  }

  return (
    <div className="grid gap-3 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">Portfolio Assignment</p>
        <p className="mt-1 text-sm font-semibold text-[#475569]">Assign existing Version 1 client portfolios.</p>
      </div>
      <label className="grid gap-2 text-sm font-semibold text-[#0A2540]">
        Search
        <input
          value={searchTerm}
          onChange={(event) => onSearchChange(event.target.value)}
          className="focus-ring min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-semibold text-[#0A2540]"
          placeholder="Portfolio, client, or status"
        />
      </label>
      <div className="grid gap-2">
        {visiblePortfolios.map((portfolio) => (
          <label key={portfolio.id} className="grid min-w-0 gap-2 rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold text-[#0A2540]">
            <span className="flex min-w-0 items-center gap-3">
              <input
                type="checkbox"
                className="size-4 shrink-0 accent-[#14B8A6]"
                checked={selectedPortfolioIds.includes(portfolio.id)}
                onChange={() => togglePortfolio(portfolio.id)}
              />
              <span className="min-w-0 break-words" style={platformAdministrationTextWrapStyle}>{portfolio.portfolioName}</span>
            </span>
            <span className="ml-7 min-w-0 break-words text-xs text-[#475569]" style={platformAdministrationTextWrapStyle}>{portfolio.client} - {portfolio.status}</span>
          </label>
        ))}
      </div>
      <div className="grid gap-2">
        {selectedPortfolios.length ? selectedPortfolios.map((portfolio) => (
          <div key={portfolio.id} className="flex min-w-0 items-center justify-between gap-3 rounded-xl border border-[#BFEAFF] bg-white px-3 py-2">
            <span className="min-w-0 break-words text-sm font-semibold text-[#0A2540]" style={platformAdministrationTextWrapStyle}>{portfolio.portfolioName}</span>
            <button type="button" onClick={() => togglePortfolio(portfolio.id)} className="focus-ring rounded-lg border border-[#BFEAFF] px-2 py-1 text-xs font-semibold text-[#0A2540]">Remove</button>
          </div>
        )) : (
          <p className="rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold text-[#475569]">No portfolios selected.</p>
        )}
      </div>
    </div>
  );
}

function PlatformAdministrationTeamPanelTabContent({
  team,
  selectedTab,
  collectorOptions,
  portfolioOptions,
}: {
  team: PlatformAdministrationTeamRecord;
  selectedTab: PlatformAdministrationTeamPanelTab;
  collectorOptions: PlatformAdministrationUserRecord[];
  portfolioOptions: PlatformAdministrationPortfolioOption[];
}) {
  const assignedCollectors = collectorOptions.filter((collector) => team.collectorIds.includes(collector.id));
  const assignedPortfolios = portfolioOptions.filter((portfolio) => team.portfolioIds.includes(portfolio.id));

  if (selectedTab === "members") {
    return (
      <div className="grid gap-3 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
        <PlatformAdministrationFieldRow label="Assigned Manager" value={team.manager} />
        <PlatformAdministrationFieldRow label="Assigned Team Lead" value={team.teamLead || "No Team Lead Assigned"} />
        <PlatformAdministrationFieldRow label="Collector Count" value={`${team.collectors}`} />
        {assignedCollectors.length ? assignedCollectors.map((collector) => (
          <PlatformAdministrationFieldRow key={collector.id} label="Collector" value={collector.displayName} />
        )) : <PlatformAdministrationFieldRow label="Collectors" value="No collectors assigned." />}
      </div>
    );
  }

  if (selectedTab === "portfolios") {
    return (
      <div className="grid gap-3 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
        <PlatformAdministrationFieldRow label="Assigned Portfolio Count" value={`${team.portfolioCount}`} />
        {assignedPortfolios.length ? assignedPortfolios.map((portfolio) => (
          <div key={portfolio.id} className="rounded-xl border border-[#BFEAFF] bg-white p-3">
            <p className="text-sm font-semibold text-[#0A2540]">{portfolio.portfolioName}</p>
            <p className="mt-1 text-xs font-semibold text-[#475569]">{portfolio.client} - {portfolio.status}</p>
          </div>
        )) : <PlatformAdministrationFieldRow label="Portfolios" value="No portfolios assigned." />}
      </div>
    );
  }

  if (selectedTab === "activity" || selectedTab === "history") {
    return (
      <div className="grid gap-3 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
        {team.activityHistory.length ? team.activityHistory.map((event) => (
          <div key={event.id} className="rounded-xl border border-[#BFEAFF] bg-white p-3">
            <p className="text-sm font-semibold text-[#0A2540]">{event.title}</p>
            <p className="mt-1 text-xs font-semibold text-[#475569]">{event.detail}</p>
            <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#14B8A6]">{formatDate(event.createdAt)}</p>
          </div>
        )) : <PlatformAdministrationFieldRow label={selectedTab === "activity" ? "Activity" : "History"} value="No team activity recorded yet." />}
      </div>
    );
  }

  return <PlatformAdministrationTeamSummary team={team} />;
}

function PlatformAdministrationTeamSummary({ team }: { team: PlatformAdministrationTeamRecord }) {
  return (
    <div className="grid gap-3 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
      <PlatformAdministrationFieldRow label="Team Name" value={team.teamName} />
      <PlatformAdministrationFieldRow label="Description" value={team.description} />
      <PlatformAdministrationFieldRow label="Status" value={team.status} />
      <PlatformAdministrationFieldRow label="Assigned Manager" value={team.manager} />
      <PlatformAdministrationFieldRow label="Assigned Team Lead" value={team.teamLead || "No Team Lead Assigned"} />
      <PlatformAdministrationFieldRow label="Assigned Collectors" value={`${team.collectors}`} />
      <PlatformAdministrationFieldRow label="Assigned Portfolios" value={`${team.portfolioCount}`} />
      <PlatformAdministrationFieldRow label="Capacity Summary" value={team.capacitySummary} />
      <PlatformAdministrationFieldRow label="Current Workload" value={team.workloadSummary} />
      {team.activityHistory.length ? (
        <div className="md:col-span-2">
          <p className="text-xs font-semibold text-[#475569]">History</p>
          <div className="mt-2 grid gap-2">
            {team.activityHistory.slice(0, 4).map((event) => (
              <div key={event.id} className="rounded-xl border border-[#BFEAFF] bg-white px-3 py-2">
                <p className="text-sm font-semibold text-[#0A2540]">{event.title}</p>
                <p className="mt-1 text-xs font-semibold text-[#475569]">{event.detail}</p>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function buildPlatformAdministrationTeamActivityHistory(title: string, teamName: string, manager: string, existingHistory: PlatformAdministrationTeamRecord["activityHistory"] = []) {
  const createdAt = new Date().toISOString();
  return [
    {
      id: `team-activity-${crypto.randomUUID()}`,
      title,
      detail: `${teamName} assigned to ${manager}.`,
      createdAt,
    },
    ...existingHistory,
  ];
}

function publishPlatformAdministrationTeamWorkflowEvents({
  team,
  previousTeam,
  action,
  actorRole,
  actorEmail,
}: {
  team: PlatformAdministrationTeamRecord;
  previousTeam: PlatformAdministrationTeamRecord | null;
  action: "create" | "update" | "assign-manager";
  actorRole: string;
  actorEmail: string;
}) {
  const actor = {
    actorId: actorEmail || "team-management",
    actorName: actorEmail || "Team Management",
    actorEmail,
    actorRole: actorRole || "admin",
    permissionLevel: "manager_override",
  };
  const baseInput = {
    tenantId: "collabrecover-administration",
    entityId: team.teamName,
    entityType: "manager_review" as const,
    actor,
    sourceWorkspace: "Administration",
    initiatedFromRoute: "/admin/ar-recovery/white-label-settings",
        metadata: {
      teamName: team.teamName,
      manager: team.manager,
      teamLead: team.teamLead,
      collectorIds: team.collectorIds,
      portfolioIds: team.portfolioIds,
    },
  };

  const events: Array<{ eventType: ARWorkflowManagerEventType; title: string; summary: string; entityType?: "manager_review" | "portfolio_assignment"; entityId?: string }> = [];
  if (action === "create") {
    events.push({ eventType: "TeamCreated", title: "Team Created", summary: `${team.teamName} was created.` });
  } else {
    events.push({ eventType: "TeamUpdated", title: "Team Updated", summary: `${team.teamName} was updated.` });
  }
  if (action === "create" || previousTeam?.manager !== team.manager || action === "assign-manager") {
    events.push({ eventType: "ManagerAssigned", title: "Manager Assigned", summary: `${team.manager} was assigned to ${team.teamName}.` });
  }

  const previousCollectors = new Set(previousTeam?.collectorIds || []);
  const nextCollectors = new Set(team.collectorIds);
  team.collectorIds.filter((id) => !previousCollectors.has(id)).forEach((collectorId) => {
    events.push({ eventType: "CollectorAssigned", title: "Collector Assigned", summary: `${collectorId} was assigned to ${team.teamName}.` });
  });
  (previousTeam?.collectorIds || []).filter((id) => !nextCollectors.has(id)).forEach((collectorId) => {
    events.push({ eventType: "CollectorRemoved", title: "Collector Removed", summary: `${collectorId} was removed from ${team.teamName}.` });
  });

  const previousPortfolios = new Set(previousTeam?.portfolioIds || []);
  const nextPortfolios = new Set(team.portfolioIds);
  team.portfolioIds.filter((id) => !previousPortfolios.has(id)).forEach((portfolioId) => {
    events.push({ eventType: "PortfolioAssigned", title: "Portfolio Assigned", summary: `${portfolioId} was assigned to ${team.teamName}.`, entityType: "portfolio_assignment", entityId: portfolioId });
  });
  (previousTeam?.portfolioIds || []).filter((id) => !nextPortfolios.has(id)).forEach((portfolioId) => {
    events.push({ eventType: "PortfolioRemoved", title: "Portfolio Removed", summary: `${portfolioId} was removed from ${team.teamName}.`, entityType: "portfolio_assignment", entityId: portfolioId });
  });

  events.forEach((event) => {
    publishManagerWorkflowAction({
      ...baseInput,
      entityId: event.entityId || baseInput.entityId,
      entityType: event.entityType || baseInput.entityType,
      eventType: event.eventType,
      title: event.title,
      summary: event.summary,
      managerAction: event.title,
      newValue: team,
      previousValue: previousTeam || undefined,
    });
  });
}

function normalizePlatformAdministrationSessionRole(role: string) {
  return role.trim().toLowerCase();
}

function isPlatformAdministrationTeamOwnerRole(role: string) {
  return new Set(["platform_admin", "kreatethedate_admin", "admin", "company_admin"]).has(normalizePlatformAdministrationSessionRole(role));
}

function isPlatformAdministrationTeamLeadershipRole(role: string) {
  return isPlatformAdministrationTeamOwnerRole(role) || new Set(["manager", "company_manager"]).has(normalizePlatformAdministrationSessionRole(role));
}

function isPlatformAdministrationTeamManagementAllowed(role: string, team: PlatformAdministrationTeamRecord, sessionUser: PlatformAdministrationUserRecord | null) {
  if (isPlatformAdministrationTeamOwnerRole(role)) return true;
  if (!new Set(["manager", "company_manager"]).has(normalizePlatformAdministrationSessionRole(role))) return false;
  if (!sessionUser) return false;
  return normalizePlatformAdministrationName(team.manager) === normalizePlatformAdministrationName(sessionUser.displayName);
}

function normalizePlatformAdministrationName(value: string) {
  return value.trim().toLowerCase();
}

function formatPlatformAdministrationRoleVisibility(value: string) {
  const normalized = value.trim().toLowerCase().replace(/[_-]+/g, " ");
  const labels: Record<string, string> = {
    tenant: "Tenant",
    platform: "Platform",
    "client portal": "Client Portal",
    internal: "Internal",
  };
  return labels[normalized] || normalized.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function getPlatformAdministrationPermissionGroupDescription(groupLabel: string) {
  const descriptions: Record<string, string> = {
    "Recovery Operations": "Operational permissions for account recovery workspaces, documents, promises, ticklers, statements, and payments.",
    "Management & Insight": "Oversight and reporting permissions for manager review, operational performance, and recovery analytics.",
    Platform: "Platform-facing permissions for client portal visibility and Administration service review.",
  };
  return descriptions[groupLabel] || "Permission group review for the selected platform service area.";
}

function PlatformAdministrationStatusPill({ status }: { status: string }) {
  const isActive = status === "Active" || status === "Accepted";
  return (
    <span className={`inline-flex rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] ${isActive ? "border-[#14B8A6]/30 bg-[#E8FCFF] text-[#0A766F]" : "border-[#FF6A2B]/35 bg-[#FFF3EC] text-[#D94A16]"}`}>
      {status}
    </span>
  );
}

function PlatformAdministrationInvitationStatusBadge({ status }: { status: string }) {
  const normalized = status.toLowerCase();
  const badge =
    normalized === "pending"
      ? { label: "Pending", classes: "border-[#FF6A2B]/35 bg-[#FFF3EC] text-[#D94A16]" }
      : normalized === "invited"
        ? { label: "Invited", classes: "border-[#BFEAFF] bg-[#F4FBFF] text-[#0A2540]" }
        : normalized === "expired"
          ? { label: "Expired", classes: "border-[#CBD5E1] bg-[#F8FAFC] text-[#475569]" }
          : normalized === "revoked"
            ? { label: "Revoked", classes: "border-[#FCA5A5] bg-[#FEF2F2] text-[#991B1B]" }
            : { label: "Accepted", classes: "border-[#14B8A6]/30 bg-[#E8FCFF] text-[#0A766F]" };

  return (
    <span className={`inline-flex max-w-full rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] ${badge.classes}`}>
      <span className="min-w-0 break-words" style={platformAdministrationTextWrapStyle}>{badge.label}</span>
    </span>
  );
}

function PlatformAdministrationDomainStatusBadge({ status }: { status: string }) {
  const normalized = status.toLowerCase();
  const badge =
    normalized === "active" || normalized === "verified" || normalized === "ready" || normalized === "configured"
      ? { label: status, dot: "bg-[#14B8A6]", classes: "border-[#14B8A6]/30 bg-[#E8FCFF] text-[#0A766F]" }
      : { label: status, dot: "bg-[#FF6A2B]", classes: "border-[#FF6A2B]/35 bg-[#FFF3EC] text-[#D94A16]" };

  return (
    <span className={`inline-flex max-w-full shrink-0 items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] ${badge.classes}`}>
      <span className={`size-2 shrink-0 rounded-full ${badge.dot}`} aria-hidden="true" />
      <span className="min-w-0 break-words" style={platformAdministrationTextWrapStyle}>{badge.label}</span>
    </span>
  );
}

function PlatformAdministrationIntegrationStatusBadge({ status }: { status: string }) {
  const normalized = status.toLowerCase();
  const badge =
    normalized === "connected"
      ? { label: status, dot: "bg-[#14B8A6]", classes: "border-[#14B8A6]/30 bg-[#E8FCFF] text-[#0A766F]" }
      : normalized === "available" || normalized === "pending" || normalized === "requires setup" || normalized === "maintenance"
        ? { label: status, dot: "bg-[#FF6A2B]", classes: "border-[#FF6A2B]/35 bg-[#FFF3EC] text-[#D94A16]" }
        : { label: status, dot: "bg-[#CBD5E1]", classes: "border-[#CBD5E1] bg-[#F8FAFC] text-[#475569]" };

  return (
    <span className={`inline-flex max-w-full shrink-0 items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] ${badge.classes}`}>
      <span className={`size-2 shrink-0 rounded-full ${badge.dot}`} aria-hidden="true" />
      <span className="min-w-0 break-words" style={platformAdministrationTextWrapStyle}>{badge.label}</span>
    </span>
  );
}

function PlatformAdministrationNotificationStatusBadge({ status }: { status: string }) {
  const normalized = status.toLowerCase();
  const badge =
    normalized === "connected" || normalized === "ready"
      ? { label: status, dot: "bg-[#14B8A6]", classes: "border-[#14B8A6]/30 bg-[#E8FCFF] text-[#0A766F]" }
      : normalized === "available" || normalized === "future ready"
        ? { label: status, dot: "bg-[#FF6A2B]", classes: "border-[#FF6A2B]/35 bg-[#FFF3EC] text-[#D94A16]" }
        : normalized === "future"
          ? { label: status, dot: "bg-[#64748B]", classes: "border-[#CBD5E1] bg-[#F8FAFC] text-[#475569]" }
          : { label: status || "Inactive", dot: "bg-[#94A3B8]", classes: "border-[#CBD5E1] bg-[#F8FAFC] text-[#475569]" };

  return (
    <span className={`inline-flex max-w-full shrink-0 items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] ${badge.classes}`}>
      <span className={`size-2 shrink-0 rounded-full ${badge.dot}`} aria-hidden="true" />
      <span className="min-w-0 break-words" style={platformAdministrationTextWrapStyle}>{badge.label}</span>
    </span>
  );
}

function PlatformAdministrationTeamSummaryMetric({ label, value }: { label: string; value: number }) {
  return (
    <div className="min-w-0 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] px-4 py-3">
      <p className="max-w-full min-w-0 break-words text-xs font-medium text-[#334155]" style={platformAdministrationTextWrapStyle}>{label}</p>
      <p className="mt-1 text-2xl font-semibold text-[#0A2540]">{value}</p>
    </div>
  );
}

function PlatformAdministrationAssignmentStatusBadge({ status }: { status: string }) {
  const normalized = status.toLowerCase();
  const badge =
    normalized === "pending"
      ? { label: "Pending", classes: "border-[#FF6A2B]/35 bg-[#FFF3EC] text-[#D94A16]" }
      : normalized === "not configured"
        ? { label: "Not Configured", classes: "border-[#CBD5E1] bg-[#F8FAFC] text-[#475569]" }
        : normalized === "connected"
          ? { label: "Connected", classes: "border-[#14B8A6]/30 bg-[#E8FCFF] text-[#0A766F]" }
          : { label: "Ready", classes: "border-[#BFEAFF] bg-white text-[#0A2540]" };

  return (
    <span className={`inline-flex max-w-full rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] ${badge.classes}`}>
      <span className="min-w-0 break-words" style={platformAdministrationTextWrapStyle}>{badge.label}</span>
    </span>
  );
}

function PlatformAdministrationPermissionSummaryMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] px-4 py-3">
      <p className="max-w-full min-w-0 break-words text-xs font-medium text-[#334155]" style={platformAdministrationTextWrapStyle}>{label}</p>
      <p className="mt-1 max-w-full min-w-0 break-words text-lg font-semibold text-[#0A2540]" style={platformAdministrationTextWrapStyle}>{value}</p>
    </div>
  );
}

function formatPlatformAdministrationPermissionActionLabel(action: string) {
  return action === "Approve" ? "Approve Workflow" : action;
}

function isPlatformAdministrationPermissionAllowed(
  selectedRole: (typeof PLATFORM_ADMINISTRATION_ROLE_SELECTOR_OPTIONS)[number],
  moduleName: string,
  action: string,
) {
  void selectedRole;
  void moduleName;
  void action;
  return true;
}

function PlatformAdministrationPermissionIndicator({ allowed }: { allowed: boolean }) {
  return (
    <span
      aria-label={allowed ? "Granted" : "Restricted"}
      title={allowed ? "Granted" : "Restricted"}
      className={`inline-flex size-7 items-center justify-center rounded-full border text-sm font-semibold ${
        allowed ? "border-[#14B8A6]/30 bg-[#E8FCFF] text-[#0A766F]" : "border-[#CBD5E1] bg-[#F8FAFC] text-[#475569]"
      }`}
    >
      {allowed ? "✓" : "—"}
    </span>
  );
}

function PlatformAdministrationFieldGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="min-w-0 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">{title}</p>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        {children}
      </div>
    </div>
  );
}

function PlatformAdministrationFieldRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0 rounded-2xl border border-[#BFEAFF] bg-white px-4 py-3">
      <span className="block min-w-0 text-left text-xs font-medium text-[#334155]">{label}</span>
      <span className="mt-1 block max-w-full min-w-0 break-words text-left text-sm font-semibold leading-6 text-[#0A2540]" style={platformAdministrationTextWrapStyle}>{value}</span>
    </div>
  );
}

function PlatformAdministrationColorValue({ color }: { color: string }) {
  return (
    <span className="inline-flex max-w-full min-w-0 items-center gap-2">
      <span className="size-3 shrink-0 rounded-full border border-[#BFEAFF]" style={{ backgroundColor: color }} aria-hidden="true" />
      <span className="min-w-0 break-words" style={platformAdministrationTextWrapStyle}>{color}</span>
    </span>
  );
}

function PlatformAdministrationProductList({ products }: { products: string[] }) {
  return (
    <span className="flex min-w-0 max-w-full flex-wrap gap-2">
      {products.map((product) => (
        <span key={product} className="min-w-0 rounded-full border border-[#BFEAFF] bg-[#E8FCFF] px-3 py-1 text-xs font-semibold text-[#0A766F]" style={platformAdministrationTextWrapStyle}>
          {product}
        </span>
      ))}
    </span>
  );
}

function PlatformAdministrationSubscriptionSummary({ plan, status }: { plan: string; status: "Active" | "Trial" | "Suspended" | "Archived" }) {
  return (
    <span className="grid min-w-0 gap-2 sm:grid-cols-2">
      <span className="min-w-0 break-words" style={platformAdministrationTextWrapStyle}>Plan: {plan}</span>
      <span className="min-w-0 break-words" style={platformAdministrationTextWrapStyle}>Status: {status}</span>
    </span>
  );
}

function PlatformAdministrationTenantStatusBadge({ status }: { status: string }) {
  const normalized = status.toLowerCase();
  const badge =
    normalized === "trial"
      ? { label: "Trial", dot: "bg-[#FF6A2B]", classes: "border-[#FF6A2B]/35 bg-[#FFF3EC] text-[#D94A16]" }
      : normalized === "suspended"
        ? { label: "Suspended", dot: "bg-[#DC2626]", classes: "border-[#FCA5A5] bg-[#FEF2F2] text-[#991B1B]" }
        : normalized === "archived"
          ? { label: "Archived", dot: "bg-[#CBD5E1]", classes: "border-[#CBD5E1] bg-[#F8FAFC] text-[#475569]" }
          : { label: "Active", dot: "bg-[#14B8A6]", classes: "border-[#14B8A6]/30 bg-[#E8FCFF] text-[#0A766F]" };

  return (
    <span className={`inline-flex max-w-full items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${badge.classes}`}>
      <span className={`size-2 shrink-0 rounded-full ${badge.dot}`} aria-hidden="true" />
      <span className="min-w-0 break-words" style={platformAdministrationTextWrapStyle}>{badge.label}</span>
    </span>
  );
}

function PlatformAdministrationServiceCard({ eyebrow, title, description, items, groups, footer }: { eyebrow: string; title: string; description: string; items?: string[]; groups?: ReadonlyArray<{ label: string; services: readonly string[] }>; footer?: string }) {
  return (
    <article className="min-w-0 rounded-[1.75rem] border border-[#BFEAFF] bg-white p-5" data-future-platform-modules={PLATFORM_ADMINISTRATION_FUTURE_MODULES.join(",")}>
      <SectionHeading eyebrow={eyebrow} title={title} description={description} />
      {groups?.length ? (
        <div className="mt-5 grid gap-4">
          {groups.map((group) => (
            <section key={group.label} className="min-w-0 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">{group.label}</p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {group.services.map((service) => (
                  <p key={service} className="min-w-0 rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold leading-6 text-[#0A2540]" style={platformAdministrationTextWrapStyle}>{service}</p>
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : null}
      {items?.length ? (
        <div className="mt-5 grid gap-3 md:grid-cols-3">
          {items.map((item) => (
            <div key={item} className="min-w-0 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] px-4 py-3">
              <p className="max-w-full min-w-0 break-words text-sm font-semibold leading-6 text-[#0A2540]" style={platformAdministrationTextWrapStyle}>{item}</p>
            </div>
          ))}
        </div>
      ) : null}
      {footer ? <p className="mt-5 max-w-full min-w-0 break-words text-sm leading-7 text-[#475569]" style={platformAdministrationTextWrapStyle}>{footer}</p> : null}
    </article>
  );
}

function buildAccountDocumentRecords(
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>,
  documentRequests: ARRecoveryDocumentRequestRecord[],
): AccountDocumentRecord[] {
  const requestDocuments = documentRequests.map((documentRequest) => ({
    id: documentRequest.id,
    title: `${documentRequest.requestType} Request`,
    documentType: documentRequest.requestType,
    status: documentRequest.status,
    notes: documentRequest.requestNotes || `Requested from ${documentRequest.requestedFrom}`,
    requestId: documentRequest.id,
    requestType: documentRequest.requestType,
    requestedFrom: documentRequest.requestedFrom,
    dueDate: documentRequest.dueDate,
    priority: documentRequest.priority,
    relatedInvoice: documentRequest.relatedInvoice,
    relatedTask: documentRequest.relatedTask,
    requestNotes: documentRequest.requestNotes,
    requestedBy: documentRequest.requestedBy,
    requestedAt: documentRequest.requestedAt,
    updatedBy: documentRequest.updatedBy,
    updatedAt: documentRequest.updatedAt,
    receivedBy: documentRequest.receivedBy,
    receivedAt: documentRequest.receivedAt,
    fileName: documentRequest.fileName,
    receivedNotes: documentRequest.receivedNotes,
    reviewNotes: documentRequest.reviewNotes,
    rejectionReason: documentRequest.rejectionReason,
    additionalInfoNotes: documentRequest.additionalInfoNotes,
  }));

  return [...requestDocuments, ...buildDocumentPlaceholders(account)];
}

function buildRecoveryHistory(
  account: NonNullable<ReturnType<typeof buildARRecoveryCustomerAccountsSnapshot>["selectedAccount"]>,
  activity: ReturnType<typeof loadARRecoveryWorkspace>["activity"],
  promises: ReturnType<typeof loadARRecoveryWorkspace>["promises"],
  ticklers: ReturnType<typeof loadARRecoveryWorkspace>["ticklers"],
  tasks: ARRecoveryTaskAssignmentRecord[],
) {
  const history = [
    ...activity
      .filter((item) => item.accountId === account.id)
      .map((item) => ({ id: item.id, createdAt: item.createdAt, title: item.title, kind: item.kind, detail: item.detail, actorName: (item as { actorName?: string }).actorName })),
    ...promises
      .filter((item) => item.accountId === account.id)
      .map((item) => ({ id: item.id, createdAt: item.promiseDate, title: `Promise ${item.status}`, kind: "Promise", detail: item.notes, actorName: item.collector || account.assignedCollector || "System" })),
    ...ticklers
      .filter((item) => item.accountId === account.id)
      .map((item) => ({ id: item.id, createdAt: item.dueDate, title: item.title, kind: item.status, detail: `${item.ticklerType} • ${item.priority}`, actorName: item.assignedCollector || account.assignedCollector || "System" })),
    ...tasks
      .filter((item) => item.accountId === account.id)
      .flatMap((task) => {
        const records = [
          { id: `${task.id}-assigned-history`, createdAt: task.createdAt, title: `Task Assigned - ${task.taskTitle}`, kind: "Task", detail: buildTaskAssignmentActivityDetail(task), actorName: task.assignedBy },
        ];

        task.comments.forEach((comment) => {
          records.push({ id: `${comment.id}-history`, createdAt: comment.createdAt, title: `Task Comment - ${task.taskTitle}`, kind: "Task", detail: `Comment By: ${comment.author} | Comment: ${comment.comment}`, actorName: comment.author });
        });

        if (task.status === "Completed" && task.completedAt) {
          records.push({ id: `${task.id}-completed-history`, createdAt: task.completedAt, title: `Task Completed - ${task.taskTitle}`, kind: "Task", detail: buildTaskCompletionDetail(task), actorName: task.completedBy || task.assignedBy });
        }

        return records;
      }),
  ]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  return history;
}

function PlaceholderSection({
  workspace,
  section,
}: {
  workspace: ReturnType<typeof loadARRecoveryWorkspace>;
  section: Exclude<ARRecoverySectionKey, "dashboard" | "client-management" | "customer-account-management" | "customer-accounts" | "smart-tickler">;
}) {
  const sectionNotes: Record<Exclude<ARRecoverySectionKey, "dashboard">, string[]> = {
    "customer-accounts": ["Customer profile fields, collector ownership, and account history.", "Company-scoped filtering only.", "Promise, ticket, and activity links can attach here."],
    "client-management": ["Paying-client records, contacts, assignments, lifecycle, and operational settings."],
    "client-exchange": ["Portfolio intake, validation, mapping, review, import, assignment, and activation.", "Reuses the enterprise portfolio engines.", "Projects onboarding status to Recovery Activity, Timeline, Manager Command, and Reporting."],
    "customer-account-management": ["Client-bound customer and account master records."],
    "aging-management": ["Aging uploads, prioritization, and bucket-based filtering.", "Future balance tracking and customer search.", "Workflow-ready for multi-tenant imports."],
    "promise-to-pay": ["Promise date, amount, status, and follow-up actions.", "Kept, broken, partial, and completed states.", "Feeds the smart tickler engine."],
    "smart-tickler": ["Daily follow-up queue and overdue reminders.", "Priority scoring for critical accounts.", "Escalation-ready reminder architecture."],
    "collection-letters": ["Editable professional letters with merge fields.", "Brand-safe delivery paths for single or bulk sends.", "History tracking for created, queued, and sent letters."],
    "document-center": ["Professional letter and statement history in one operating view.", "Merge field references, branding, and client portal foundations.", "Document history for every request, delivery, and review step."],
    statements: ["Statement queue, selected statement detail, and statement summary.", "Statement Actions and Bulk Actions remain scoped to the dedicated Statement Center.", "Permanent Statement Timeline preserves statement generation and send history."],
    "recovery-activity": ["Chronological calls, emails, notes, statements, and payments.", "Account-linked history views.", "Reusable timeline for reporting surfaces."],
    "recovery-reporting": ["Collector performance and recovery reporting.", "Trend-aware operational metrics.", "White-label reporting layer foundation."],
    "recovery-analytics": ["Aging and recovery trend charts.", "Promise success and collector activity analytics.", "Executive command-center visual language."],
    "manager-command-center": ["Assignment oversight and escalation control.", "Managerial queue management.", "Future team performance and workload balancing."],
    "client-portal": ["Client portal foundation for document navigation and record access.", "Read-only company-scoped visibility only.", "No cross-company access."],
    "white-label-settings": ["Universal Circuit Hub platform services.", "Users, roles, permissions, and teams foundation.", "Future tenant administration controls."],
    "commercialization-center": ["Client onboarding, white-label deployment, package management, and support.", "Documentation, training, and automation readiness.", "Launch-ready business platform hub."],
  };

  const actionLinks = [
    { label: "Open Account Recovery", note: "Work account queues and account-level actions.", href: "/admin/ar-recovery/customer-accounts", icon: <FolderKanban className="size-4" aria-hidden="true" /> },
    { label: "Review Ticklers", note: "Inspect reminders and follow-up pressure.", href: "/admin/ar-recovery/smart-tickler", icon: <Clock3 className="size-4" aria-hidden="true" /> },
    { label: "Open Documents", note: "Generate and review letters or statements.", href: "/admin/ar-recovery/document-center", icon: <FileText className="size-4" aria-hidden="true" /> },
    { label: "Manager Command", note: "Review escalation and oversight queues.", href: "/admin/ar-recovery/manager-command-center", icon: <ShieldCheck className="size-4" aria-hidden="true" /> },
  ];

  return (
    <section className="ar-workspace-os mt-4 grid gap-4">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MiniStat label="Accounts" value={workspace.accounts.length} note="Company-scoped account records." />
        <MiniStat label="Open Balance" value={formatARRecoveryCurrency(workspace.accounts.reduce((sum, account) => sum + account.openBalanceCents, 0))} note="Open receivables in this workspace." />
        <MiniStat label="Promises" value={workspace.promises.length} note="Promise records available." />
        <MiniStat label="Activity" value={workspace.activity.length} note="Timeline events available." />
      </div>

      <article className="ar-action-toolbar rounded-[1.75rem] border p-4">
        <p className="ar-action-toolbar-title flex items-center gap-2">
          <Bolt className="ar-quick-action-bolt size-4" aria-hidden="true" />
          Quick Actions
        </p>
        <p className="ar-action-toolbar-description mt-1">Use these shortcuts to reach the active recovery workspaces instead of treating this page like reference documentation.</p>
        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {actionLinks.map((action) => (
            <Link key={action.label} to={action.href} className="flex items-start gap-3 rounded-2xl border border-[#BFEAFF] bg-white px-4 py-3 text-left transition hover:border-[#14B8A6] hover:bg-[#F4FBFF] focus-ring">
              <span className="mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-full border border-[#BFEAFF] bg-[#F4FBFF] text-[#14B8A6]">{action.icon}</span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-[#0A2540]">{action.label}</span>
                <span className="mt-1 block text-xs font-normal leading-5 text-[#475569]">{action.note}</span>
              </span>
            </Link>
          ))}
        </div>
      </article>

      <section className="grid gap-6 xl:grid-cols-[1.08fr_.92fr]">
        <article className="ar-ia-panel rounded-[1.75rem] border p-5">
          <SectionHeading
            eyebrow="Primary Workspace"
            title={sectionTitles[section]}
            description={sectionDescriptions[section]}
          />
          <div className="mt-5 grid gap-3 md:grid-cols-3">
            {sectionNotes[section].map((note) => (
              <div key={note} className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4 text-sm font-normal leading-7 text-[#475569]">
                {note}
              </div>
            ))}
          </div>
          {section === "recovery-activity" ? (
            <div className="ar-ia-scroll mt-5 grid gap-2">
              {workspace.activity.slice(0, 10).map((activity) => (
                <div key={activity.id} className="rounded-xl border border-[#BFEAFF] bg-white p-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-[#0A2540]">{activity.title}</p>
                      <p className="mt-1 text-xs font-medium text-[#334155]">
                        {activity.customerName} | {formatCapturedDate(activity.createdAt)} | {formatCapturedTime(activity.createdAt)}
                      </p>
                    </div>
                    <span className="rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-2 py-1 text-[10px] font-semibold text-[#14B8A6]">
                      {activity.kind}
                    </span>
                  </div>
                  <p className="mt-2 text-sm font-normal leading-6 text-[#475569]">{activity.detail}</p>
                </div>
              ))}
            </div>
          ) : null}
        </article>

        <article className="ar-ia-panel rounded-[1.75rem] border p-5">
          <SectionHeading
            eyebrow="Supporting Context"
            title="Company isolation"
            description="The page keeps the active tenant scoped to one company while operational work stays first."
          />
          <div className="mt-5 grid gap-3">
            <ScopeRow label="Company" value={workspace.company.companyName} />
            <ScopeRow label="Brand" value={workspace.branding.displayName} />
            <ScopeRow label="Loaded records" value={`${workspace.accounts.length} accounts`} />
            <ScopeRow label="Ticklers" value={`${workspace.ticklers.length} open reminders`} />
            <ScopeRow label="Promises" value={`${workspace.promises.length} promise records`} />
            <ScopeRow label="Activity" value={`${workspace.activity.length} timeline events`} />
          </div>
        </article>
      </section>
    </section>
  );
}

function ARRecoveryHeader() {
  const navigate = useNavigate();

  async function handleLogout() {
    try {
      await logout();
    } finally {
      localStorage.removeItem("ktd_demo_auth");
      navigate("/admin/login", { replace: true });
    }
  }

  return (
    <header className="sticky top-0 z-40 border-b border-[rgba(255,106,43,.16)] bg-[rgba(7,26,61,.92)] px-4 py-4 backdrop-blur sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-7xl flex-col gap-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <Link to="/admin/truevance-business-center" className="focus-ring inline-flex max-w-full items-center gap-4 rounded-2xl">
            <span className="grid size-12 shrink-0 place-items-center overflow-hidden rounded-2xl bg-[#FF6A2B] text-sm font-black text-white shadow-[0_18px_45px_rgba(255,106,43,.24)]">
              <img
                src="/assets/collabrecover-cr-logo.png"
                alt="CollabRecover AR Solutions"
                className="h-full w-full rounded-[inherit] object-cover"
              />
            </span>
            <span className="min-w-0 leading-tight">
              <span className="block max-w-full break-words font-display text-2xl font-bold leading-tight text-[#F6F1E8] sm:text-[2rem]">CollabRecover AR Solutions</span>
              <span className="block text-xs font-bold uppercase tracking-[0.14em] text-[#CBD5E1]">Accounts, Documents, Promises & Recovery</span>
            </span>
          </Link>

          <div className="flex flex-wrap items-center gap-2">
            <span
              className="inline-flex size-11 items-center justify-center rounded-full border border-[rgba(34,224,208,.24)] bg-[rgba(255,255,255,.05)] text-sm font-black text-[#F6F1E8] transition hover:border-[rgba(34,224,208,.45)] hover:bg-[rgba(34,224,208,.1)]"
              aria-label="Document workspace profile"
              title="Document workspace profile"
            >
              AR
            </span>
            <button
              type="button"
              onClick={() => void handleLogout()}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-[rgba(34,224,208,.24)] bg-[rgba(255,255,255,.04)] px-4 text-sm font-extrabold text-[#F6F1E8] transition hover:border-[rgba(34,224,208,.45)] hover:bg-[rgba(34,224,208,.1)] focus-ring"
            >
              <LogOut className="size-4" aria-hidden="true" />
              Logout
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}

function MiniStat({ label, value, note, icon }: { label: string; value: string | number; note: string; icon?: ReactNode }) {
  return (
    <div className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs font-medium text-[#334155]">{label}</p>
        {icon ? <ARLightLineIcon icon={icon} /> : null}
      </div>
      <p className="mt-2 font-display text-3xl font-bold text-[#0A2540]">{value}</p>
      <p className="mt-2 text-sm font-normal leading-6 text-[#475569]">{note}</p>
    </div>
  );
}

function ScopeRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 flex-wrap items-start justify-between gap-4 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] px-4 py-3">
      <span className="min-w-0 break-words text-xs font-medium text-[#334155]">{label}</span>
      <span className="min-w-0 max-w-full break-words text-right text-sm font-semibold leading-6 text-[#0A2540] sm:max-w-[60%]">{value}</span>
    </div>
  );
}

function SectionHeading({
  eyebrow,
  title,
  description,
}: {
  eyebrow: string;
  title: string;
  description: string;
}) {
  return (
    <div className="border-b border-[#BFEAFF] pb-4">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#14B8A6]">{eyebrow}</p>
      <h2 className="mt-1 font-display text-3xl font-bold text-[#0A2540]">{title}</h2>
      <p className="mt-3 max-w-4xl text-sm font-normal leading-7 text-[#475569]">{description}</p>
    </div>
  );
}

function navIcon(section: ARRecoverySectionKey) {
  const iconClass = "size-4";
  switch (section) {
    case "dashboard":
      return <BarChart3 className={iconClass} aria-hidden="true" />;
    case "client-management":
      return <Building2 className={iconClass} aria-hidden="true" />;
    case "client-exchange":
      return <FolderKanban className={iconClass} aria-hidden="true" />;
    case "customer-account-management":
      return <Users className={iconClass} aria-hidden="true" />;
    case "customer-accounts":
      return <Users className={iconClass} aria-hidden="true" />;
    case "aging-management":
      return <FolderKanban className={iconClass} aria-hidden="true" />;
    case "promise-to-pay":
      return <Target className={iconClass} aria-hidden="true" />;
    case "smart-tickler":
      return <Clock3 className={iconClass} aria-hidden="true" />;
    case "collection-letters":
      return <FileText className={iconClass} aria-hidden="true" />;
    case "document-center":
      return <FileText className={iconClass} aria-hidden="true" />;
    case "statements":
      return <FileText className={iconClass} aria-hidden="true" />;
    case "recovery-activity":
      return <Activity className={iconClass} aria-hidden="true" />;
    case "recovery-reporting":
      return <BarChart3 className={iconClass} aria-hidden="true" />;
    case "recovery-analytics":
      return <BarChart3 className={iconClass} aria-hidden="true" />;
    case "manager-command-center":
      return <ShieldCheck className={iconClass} aria-hidden="true" />;
    case "client-portal":
      return <Users className={iconClass} aria-hidden="true" />;
    case "white-label-settings":
      return <ShieldCheck className={iconClass} aria-hidden="true" />;
    case "commercialization-center":
      return <Sparkles className={iconClass} aria-hidden="true" />;
    default:
      return <BarChart3 className={iconClass} aria-hidden="true" />;
  }
}

function arRecoverySidebarLabel(section: ARRecoverySectionKey) {
  const labels: Record<ARRecoverySectionKey, string> = {
    dashboard: "Dashboard",
    "client-management": "Client Management",
    "client-exchange": "Client Exchange",
    "customer-account-management": "Customers & Accounts",
    "customer-accounts": "Account Recovery",
    "aging-management": "Aging Management",
    "promise-to-pay": "Promises",
    "smart-tickler": "Ticklers",
    "collection-letters": "Letters",
    "document-center": "Document Center",
    statements: "Statements",
    "recovery-activity": "Recovery Activity",
    "recovery-reporting": "Reports",
    "recovery-analytics": "Analytics",
    "manager-command-center": "Manager Command",
    "client-portal": "Client Portal Foundation",
    "white-label-settings": "Administration",
    "commercialization-center": "Business Platform",
  };
  return labels[section];
}

function useARRecoveryBranding(title: string, description: string) {
  useEffect(() => {
    const fullTitle = `${title} | CollabRecover AR Solutions`;
    document.title = fullTitle;
    document.querySelector('meta[name="description"]')?.setAttribute("content", description);

    const themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const previousTheme = themeMeta?.getAttribute("content") || "";
    if (themeMeta) themeMeta.setAttribute("content", "#02072C");

    const faviconHref = "/assets/collabrecover-favicon.svg";
    const iconSelectors = ['link[rel="icon"]', 'link[rel="shortcut icon"]'] as const;
    const icons = iconSelectors.map((selector) => document.querySelector<HTMLLinkElement>(selector)).filter((link): link is HTMLLinkElement => Boolean(link));
    const previousIcons = icons.map((link) => link.getAttribute("href"));
    icons.forEach((link) => {
      link.setAttribute("href", faviconHref);
      link.setAttribute("type", "image/svg+xml");
    });

    return () => {
      if (themeMeta && previousTheme) themeMeta.setAttribute("content", previousTheme);
      icons.forEach((link, index) => {
        const previousHref = previousIcons[index];
        if (previousHref) link.setAttribute("href", previousHref);
      });
    };
  }, [description, title]);
}

const sectionTitles: Record<ARRecoverySectionKey, string> = {
  dashboard: "Recovery Dashboard",
  "client-management": "Client Management",
  "client-exchange": "Client Exchange Center",
  "customer-account-management": "Customers & Accounts",
  "customer-accounts": "Account Recovery Workspace",
  "aging-management": "Aging Management Center",
  "promise-to-pay": "Promise-To-Pay Center",
  "smart-tickler": "Smart Tickler Center",
  "collection-letters": "Professional Letter Center",
  "document-center": "Enterprise Document Center",
  statements: "Professional Statement Center",
  "recovery-activity": "Recovery Activity Timeline",
  "recovery-reporting": "Recovery Reporting",
  "recovery-analytics": "Recovery Analytics",
  "manager-command-center": "Manager Command Center",
  "client-portal": "Client Portal Foundation",
  "white-label-settings": "Administration",
  "commercialization-center": "Business Platform",
};

const sectionDescriptions: Record<ARRecoverySectionKey, string> = {
  dashboard: "A modern recovery command center for open AR, follow-ups, promises, and collector action.",
  "client-management": "The operational headquarters for companies that hire Future AR Recovery, from onboarding through portfolio readiness.",
  "client-exchange": "The operational command center for onboarding client portfolios into AR Recovery OS.",
  "customer-account-management": "Build the mandatory Client → Customer → Account hierarchy that prepares receivables for invoice management.",
  "customer-accounts": "Manage one selected customer account, communications, promises, documents, payments, and recovery actions from a focused operational workspace.",
  "aging-management": "Aging buckets, uploads, prioritization, and queue preparation for every account.",
  "promise-to-pay": "Track promise dates, amounts, statuses, and follow-up actions from one workflow.",
  "smart-tickler": "The reminder engine that keeps callbacks, escalations, and follow-ups visible.",
  "collection-letters": "Template-driven collection letters, bulk delivery, and history scaffolding.",
  "document-center": "The Enterprise Document Center brings letters, statements, templates, merge fields, branding, and history together.",
  statements: "Dedicated Statement Center for statement queue, selected statement actions, bulk actions, tabs, and permanent statement timeline.",
  "recovery-activity": "A timeline of calls, emails, letters, promises, escalations, and payment updates.",
  "recovery-reporting": "Collector performance and recovery reporting ready for future company-level analytics.",
  "recovery-analytics": "Executive recovery charts and trend analysis built for white-label deployment.",
  "manager-command-center": "Workload balancing, team oversight, and escalation management for recovery leaders.",
  "client-portal": "A client portal foundation for document navigation, read-only visibility, and company-scoped access.",
  "white-label-settings": "Universal Circuit Hub administration for users, roles, permissions, teams, and platform service foundations.",
  "commercialization-center": "Client onboarding, white-label deployment, packaging, support, documentation, training, and automation readiness.",
};


